// src/components/QuotationApp.js
import React, {
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  useState,
} from "react";
import "./MobileResponsive.css";
import {
  BellRing,
  CalendarDays,
  Save,
  Scissors,
  Download,
  Trash2,
  Plus,
  RotateCcw,
  Check,
  Menu,
  ChevronDown,
  ChevronUp,
  Columns3,
  GripVertical,
  LayoutGrid,
  Mail,
  ExternalLink,
  FileArchive,
  FileImage,
  FileText,
  Link2,
  Phone,
  Paperclip,
  Settings2,
  Users,
  UserCog,
  Unlink,
  UploadCloud,
  X,
  CheckCircle2,
} from "lucide-react";
import Sidebar from "./Sidebar";
import Button from "./ui/Button";
import GeneralQuotationEditor from "./GeneralQuotationEditor";
import useViewport from "./useViewport";
import { useAccount } from "../context/AccountContext";
import {
  amountToWords,
  buildBusinessDisplayName,
  calcGeneralGrandTotal,
  calcGeneralSubtotal,
  calculateGeneralQuoteTotals,
  createGeneralQuoteImage,
  createClientId,
  createDefaultGeneralQuote,
  CURTAIN_QUOTE_TYPE,
  GENERAL_QUOTE_IMAGE_TTL_DAYS,
  getGeneralItemBaseAmount,
  getGeneralItemAmount,
  getGeneralItemTaxAmount,
  GENERAL_QUOTE_TYPE,
  normalizeGeneralDiscount,
  normalizeGeneralDiscountType,
  normalizeGeneralQuoteItemSpecifications,
  normalizeGeneralQuoteItemImages,
  normalizeGeneralQuote,
  extractFirebaseStorageFullPathFromUrl,
  hasFirebaseStorageDownloadToken,
  isFirebaseStorageRestUrl,
  isProtectedFirebaseStorageRestUrl,
  getTodayLocalDateString,
  syncGeneralQuoteItemPrimaryImageFields,
  resolveQuoteDateForDisplay,
  resolveQuoteDateForInput,
  todayISO,
} from "./generalQuotationUtils";
import {
  buildOwnerWorkspaceProfile,
  DEFAULT_STAFF_LIMIT,
  DEFAULT_TEAM_PERMISSIONS,
  OWNER_TEAM_PERMISSIONS,
  TEAM_PERMISSION_DEFINITIONS,
  countOccupiedStaffSlots,
  createTeamMemberDraft,
  findMatchingTeamMember,
  getTeamMemberStatusLabel,
  hasFeatureAccess,
  isActiveTeamMember,
  normalizeStaffLimit,
  normalizeEmailValue,
  normalizePermissionSet,
  normalizeTeamMembers,
  normalizeWorkspaceProfile,
} from "../utils/teamAccess";
import {
  buildCrmContactFromQuote,
  cloneChecklistTree,
  CRM_REMINDER_REPEAT_OPTIONS,
  CRM_REMINDER_STATUS_ACTIVE,
  CRM_REMINDER_STATUS_DISABLED,
  CRM_REMINDER_STATUS_DONE,
  CRM_REMINDER_TYPE_INSTALLATION,
  CRM_REMINDER_TYPE_TASK_DUE,
  CRM_FOLLOW_UP_PRIORITY_HIGH,
  CRM_FOLLOW_UP_PRIORITY_MEDIUM,
  CRM_FOLLOW_UP_PRIORITY_OPTIONS,
  CRM_FOLLOW_UP_REPEAT_OPTIONS,
  CRM_FOLLOW_UP_STATUS_DONE,
  CRM_FOLLOW_UP_STATUS_PENDING,
  DEFAULT_CRM_REMINDER_TIME,
  createChecklistChildDraft,
  createChecklistFromStage,
  createDealChecklistItem,
  createExecutionSpecItem,
  createExecutionTemplateFieldDraft,
  createExecutionTemplateDraft,
  createChecklistTemplateDraft,
  createStageDraft,
  CRM_CHECKLIST_MAX_DEPTH,
  CRM_PIPELINE_TYPES,
  DEFAULT_CRM_PIPELINES,
  deleteChecklistNodeById,
  getChecklistProgress,
  getCrmFollowUpRepeatMinutes,
  getPrimaryPendingCrmFollowUp,
  getCrmReminderRepeatMinutes,
  getCrmReminderTypeLabel,
  getChecklistNodeProgress,
  getCrmPipeline,
  getCrmStage,
  getChecklistSummary,
  getDefaultCrmStageId,
  getFollowUpSummary,
  getInstallationSummary,
  getQuoteTypeLabel,
  insertChecklistChildNode,
  moveChecklistNodeById,
  normalizeChecklistTree,
  normalizeCrmAttachments,
  normalizeCrmFollowUps,
  normalizeCrmReminderConfigs,
  normalizeExecutionSpecs,
  normalizeExecutionSpecAttachments,
  normalizeExecutionTemplateDrafts,
  normalizeExecutionTemplateItemType,
  normalizeExecutionTemplates,
  preserveChecklistDraftTree,
  getStageFilterOptions,
  getStageSelectOptions,
  normalizeCrmPipelines,
  normalizeDealChecklist,
  toggleChecklistNodeById,
  updateChecklistNodeById,
} from "./crmUtils";




import { ref, getDownloadURL, deleteObject, getMetadata } from "firebase/storage";
import {
  app as firebaseApp,
  auth,
  db,
  functions,
  storage,
} from "../firebase";
import {
  clearFirestoreReadBackoffState,
  getDocCached,
  getDocsCached,
  invalidateFirestoreReadCache,
  invalidateFirestoreReadCacheByPrefix,
  isFirestoreResourceExhausted,
  recordFirestoreRead,
  trackedGetDoc,
  trackedGetDocs,
  trackedOnSnapshot,
} from "../utils/firestoreReadCache";
import {
  buildFabricCatalogueDisplayCode,
  buildFabricCatalogueRowPatch,
  buildSrPriceRuleDocId,
  buildSrPriceRulePayload,
  getSrPriceRuleLabel,
  normalizeCatalogueKey,
  normalizeFabricCatalogue,
  normalizeSrNumber,
  normalizeSrPriceRule,
  parseFabricCatalogueInput,
  resolveFabricCatalogueRrp,
} from "../utils/fabricCatalogue";
import {
  buildCustomerSearchIndexRecord,
  buildCustomerSearchIndexId,
  searchCrmLeads as globalSearchCrmLeads,
  searchCustomerPeople as globalSearchCustomerPeople,
  searchCustomers as globalSearchCustomers,
  searchQuotations as globalSearchQuotations,
  searchTaskHistory as globalSearchTaskHistory,
} from "../utils/globalSearch";
import {
  archiveManagedStorageFile,
  formatStorageBytes,
  uploadManagedImage,
} from "../utils/managedImageUpload";
import { IS_DEV_UI_ENABLED } from "../utils/devUi";
import {
  getChunkLoadDebugContext,
  handleChunkLoadErrorRecovery,
  isChunkLoadError,
  resetChunkLoadReloadGuard,
} from "../utils/chunkLoadRecovery";
import {
  DEFAULT_PRODUCT_CONFIGS,
  PRODUCT_CALCULATION_LABELS,
  PRODUCT_CALCULATION_TYPES,
  PRODUCT_CATEGORIES,
  PRODUCT_CATEGORY_LABELS,
  PRODUCT_CONFIG_COLLECTION,
  CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
  buildDefaultProductConfigs,
  buildProductConfigSnapshot,
  calculateBlindSqft,
  calculateCurtainPieces,
  calculateProductRow,
  createProductConfigDraft,
  mergeProductConfigsWithDefaults,
  isAccessoryProductConfig,
  isAccessoryCalculationType,
  normalizeProductConfig,
  normalizeBlindSqftRoundingMode,
  normalizeProductName,
  normalizeStitchingKey,
  resolveBlindSqftRoundingMode,
  getRowDiscountScope,
  resolveCurtainFullness,
  resolveFabricRulesForRow,
  resolveProductConfig,
  resolveSelectedStitchingStyle,
  productSupportsLining,
  shouldUseLegacyProductCalculation,
  validateProductConfig,
} from "../utils/productConfigs";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendSignInLinkToEmail,
  signOut,
  updatePassword,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import {
  collection,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  doc,
  arrayUnion,
  query as firestoreQuery,
  limit as firestoreLimit,
  where as firestoreWhere,
  orderBy as firestoreOrderBy,
  startAfter as firestoreStartAfter,
  documentId as firestoreDocumentId,
  serverTimestamp,
  Timestamp,
  increment,
  runTransaction,
  writeBatch,
} from "firebase/firestore";
import {
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  BarChart,
  Bar,
  AreaChart,
  Area,
} from "recharts";
import CountUp from "react-countup";

const SAVED_QUOTES_PAGE_SIZE = 24;
const SAVED_QUOTES_INITIAL_READ_LIMIT = 24;
const SAVED_QUOTES_DELETED_READ_LIMIT = 25;
const CRM_LEADS_PAGE_SIZE = 20;
const CRM_DEALS_PAGE_SIZE = 20;
const CRM_LEADS_INITIAL_READ_LIMIT = 30;
const CUSTOMER_LEDGER_INITIAL_READ_LIMIT = 25;
const CUSTOMER_LEDGER_SELECTED_QUOTE_LIMIT = 25;
const CUSTOMER_LEDGER_SELECTED_CRM_LIMIT = 25;
const GLOBAL_REMINDER_DUE_READ_LIMIT = 20;
const GLOBAL_REMINDER_LOOKAHEAD_MS = 10 * 60 * 1000;
const GLOBAL_REMINDER_CHECK_TTL_MS = 5 * 60 * 1000;
const ACCOUNT_SECURITY_SESSION_LIMIT = 20;
const ACCOUNT_SECURITY_ACTIVITY_LIMIT = 20;
const ACCOUNT_SECURITY_HEARTBEAT_MS = 5 * 60 * 1000;
const ACCOUNT_SECURITY_SESSION_PREFIX = "fabriquote:sessionId:";
const CRM_TASK_HISTORY_INITIAL_READ_LIMIT = 50;
const WORKSPACE_BOOTSTRAP_TIMEOUT_MS = 15 * 1000;
const DASHBOARD_PREVIEW_READ_LIMIT = 8;
const DASHBOARD_TOP_CLIENT_LIMIT = 10;
const DASHBOARD_INSTALLATION_MONTH_LIMIT = 25;
const DASHBOARD_MONTHLY_CHART_MONTHS = 6;
const FABRIC_STOCK_READ_LIMIT = 200;
const STOCK_HISTORY_READ_LIMIT = 100;
const CRM_OVERVIEW_PREVIEW_LIMIT = 5;
const LOCAL_DRAFT_PREFIX = "fabriquote:draft";
const LOCAL_DRAFT_VERSION = 1;
const LOCAL_DRAFT_INTERVAL_MS = 8000;
const isDevRuntime = IS_DEV_UI_ENABLED;
const PRODUCTION_INVITE_ORIGIN = "https://quote.fabriquote.in";
const getDoc = (ref, options = {}) =>
  trackedGetDoc(ref, { label: options.label || "QuotationApp.getDoc", ...options });
const getDocs = (ref, options = {}) =>
  trackedGetDocs(ref, { label: options.label || "QuotationApp.getDocs", ...options });
const waitFor = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const logAppStartupTrace = (payload = {}) => {
  if (!IS_DEV_UI_ENABLED) return;
  try {
    console.log("APP_STARTUP_TRACE", {
      at: new Date().toISOString(),
      ...payload,
    });
  } catch {
    // Startup tracing must never block rendering.
  }
};
const buildWorkspaceBootstrapDebug = ({
  authUid = "",
  email = "",
  workspaceUid = "",
  loadingStep = "",
  lastCompletedStep = "",
  errorCode = "",
  errorMessage = "",
} = {}) => ({
  authUid,
  email,
  workspaceUid,
  loadingStep,
  lastCompletedStep,
  errorCode,
  errorMessage,
});
const getInviteOrigin = () => {
  if (typeof window === "undefined") return PRODUCTION_INVITE_ORIGIN;
  const origin = window.location?.origin || "";
  const hostname = window.location?.hostname || "";
  const isLocalhost =
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1";
  return isDevRuntime && isLocalhost && origin ? origin : PRODUCTION_INVITE_ORIGIN;
};
const getInviteEmailErrorMessage = (error) => {
  const code = String(error?.code || "").toLowerCase();
  if (code.includes("invalid-email")) return "Please enter a valid email address.";
  if (code.includes("unauthorized-continue-uri")) {
    return "Invite link domain is not authorized in Firebase.";
  }
  if (code.includes("operation-not-allowed")) {
    return "Email link sign-in is not enabled in Firebase Auth.";
  }
  if (code.includes("too-many-requests")) {
    return "Too many invite attempts. Please try again later.";
  }
  if (code.includes("network-request-failed")) {
    return "Network issue while sending invite email.";
  }
  if (code.includes("permission-denied")) {
    return "Invite email sent, but updating the invite record was blocked.";
  }
  return "Could not send invite email.";
};
const validateInviteActionCodeSettings = ({
  email,
  accountId,
  workspaceUid,
  inviteId,
  actionCodeSettings,
}) => {
  if (!normalizeEmailValue(email)) throw new Error("Please enter a valid email address.");
  if (!String(accountId || workspaceUid || "").trim()) {
    throw new Error("Invite account id is missing.");
  }
  if (!String(inviteId || "").trim()) throw new Error("Invite id is missing.");
  const url = String(actionCodeSettings?.url || "");
  if (!url || url.includes("undefined") || url.includes("null")) {
    throw new Error("Invite link is invalid.");
  }
  if (!isDevRuntime && !url.startsWith("https://")) {
    throw new Error("Invite link must use HTTPS in production.");
  }
};
const isFirebaseStorageBillingError = (error) => {
  const message = String(error?.message || "").toLowerCase();
  const code = String(error?.code || "").toLowerCase();
  return code.includes("402") || message.includes("402") || message.includes("payment required");
};
const isLocalStorageKillSwitchEnabled = (key) => {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
};

const getSecuritySessionStorageKey = (uid = "") =>
  `${ACCOUNT_SECURITY_SESSION_PREFIX}${String(uid || "").trim()}`;

const createSecuritySessionId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `session_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
};

const getOrCreateSecuritySessionId = (uid = "") => {
  if (typeof window === "undefined" || !uid) return "";
  const key = getSecuritySessionStorageKey(uid);
  try {
    const existing = window.localStorage.getItem(key);
    if (existing) return existing;
    const next = createSecuritySessionId();
    window.localStorage.setItem(key, next);
    return next;
  } catch {
    return createSecuritySessionId();
  }
};

const clearSecuritySessionId = (uid = "") => {
  if (typeof window === "undefined" || !uid) return;
  try {
    window.localStorage.removeItem(getSecuritySessionStorageKey(uid));
  } catch {
    // Best effort only.
  }
};

const hashClientText = async (value = "") => {
  const text = String(value || "");
  if (!text) return "";
  try {
    if (typeof crypto === "undefined" || !crypto.subtle || typeof TextEncoder === "undefined") {
      return "";
    }
    const buffer = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
    return Array.from(new Uint8Array(buffer))
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("")
      .slice(0, 64);
  } catch {
    return "";
  }
};

const detectBrowserInfo = (userAgent = "") => {
  const ua = String(userAgent || "");
  if (/Edg\//.test(ua)) return { browser: "Microsoft Edge", version: ua.match(/Edg\/(\d+)/)?.[1] || "" };
  if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) return { browser: "Chrome", version: ua.match(/Chrome\/(\d+)/)?.[1] || "" };
  if (/Firefox\//.test(ua)) return { browser: "Firefox", version: ua.match(/Firefox\/(\d+)/)?.[1] || "" };
  if (/Safari\//.test(ua) && /Version\//.test(ua)) return { browser: "Safari", version: ua.match(/Version\/(\d+)/)?.[1] || "" };
  return { browser: "Browser", version: "" };
};

const detectOsInfo = (userAgent = "") => {
  const ua = String(userAgent || "");
  if (/Windows NT/.test(ua)) return "Windows";
  if (/Mac OS X/.test(ua)) return "macOS";
  if (/Android/.test(ua)) return "Android";
  if (/iPhone|iPad|iPod/.test(ua)) return "iOS";
  if (/Linux/.test(ua)) return "Linux";
  return "Unknown OS";
};

const detectDeviceType = (userAgent = "") => {
  const ua = String(userAgent || "");
  if (/iPad|Tablet/.test(ua)) return "tablet";
  if (/Mobi|Android|iPhone|iPod/.test(ua)) return "mobile";
  if (ua) return "desktop";
  return "unknown";
};

const getSafeSessionDeviceInfo = async () => {
  const userAgent =
    typeof navigator !== "undefined" ? String(navigator.userAgent || "") : "";
  const browser = detectBrowserInfo(userAgent);
  const os = detectOsInfo(userAgent);
  return {
    browser: browser.browser,
    browserVersionMajor: browser.version,
    os,
    deviceType: detectDeviceType(userAgent),
    userAgentHash: await hashClientText(userAgent),
    displayLabel: `${browser.browser}${browser.version ? ` ${browser.version}` : ""} on ${os}`,
  };
};

const normalizeSecurityTimestampMillis = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const formatSecurityRelativeTime = (value) => {
  const millis = normalizeSecurityTimestampMillis(value);
  if (!millis) return "Not available";
  const diffMs = Date.now() - millis;
  const absMs = Math.abs(diffMs);
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (absMs < minute) return "Active now";
  if (absMs < hour) return `${Math.max(1, Math.round(absMs / minute))} min ago`;
  if (absMs < day) return `${Math.max(1, Math.round(absMs / hour))} hr ago`;
  if (absMs < 2 * day) return "Yesterday";
  return new Date(millis).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const getPasswordStrength = (password = "") => {
  const value = String(password || "");
  let score = 0;
  if (value.length >= 8) score += 1;
  if (value.length >= 12) score += 1;
  if (/[A-Z]/.test(value) && /[a-z]/.test(value)) score += 1;
  if (/\d/.test(value)) score += 1;
  if (/[^A-Za-z0-9]/.test(value)) score += 1;
  if (!value) return { score: 0, label: "Not started", color: "#94a3b8" };
  if (score <= 2) return { score, label: "Weak", color: "#dc2626" };
  if (score <= 4) return { score, label: "Good", color: "#f59e0b" };
  return { score, label: "Strong", color: "#16a34a" };
};

const getSecurityAuthErrorMessage = (error) => {
  const code = String(error?.code || "").toLowerCase();
  const message = String(error?.message || "").toLowerCase();
  if (code.includes("wrong-password") || code.includes("invalid-credential")) {
    return "Current password is incorrect.";
  }
  if (code.includes("requires-recent-login") || message.includes("sign in again")) {
    return "Please sign in again to continue.";
  }
  if (code.includes("weak-password")) return "Please choose a stronger password.";
  if (code.includes("network-request-failed")) return "Network error. Please try again.";
  if (code.includes("permission-denied")) return "This session was signed out for security.";
  if (code.includes("unauthenticated")) return "Please sign in again.";
  return "Security action failed. Please try again.";
};

const getSecurityProviderSummary = (authUser) => {
  const providers = Array.isArray(authUser?.providerData) ? authUser.providerData : [];
  const providerIds = providers.map((provider) => provider.providerId).filter(Boolean);
  const hasPassword = providerIds.includes("password");
  const labels = providerIds.map((providerId) => {
    if (providerId === "password") return "Email/Password";
    if (providerId === "google.com") return "Google";
    if (providerId === "phone") return "Phone";
    return providerId;
  });
  return {
    hasPassword,
    labels: labels.length ? labels : ["Unknown"],
    label: labels.length ? labels.join(" + ") : "Unknown",
  };
};

const invalidateWorkspaceCollectionCache = (workspaceUid, collectionName) => {
  if (!workspaceUid || !collectionName) return;
  invalidateFirestoreReadCacheByPrefix(`users/${workspaceUid}/${collectionName}`);
  if (collectionName === "quote_index") {
    invalidateFirestoreReadCacheByPrefix(`savedQuotes:recent:${workspaceUid}:`);
    invalidateFirestoreReadCacheByPrefix(`savedQuotes:recycleBin:${workspaceUid}:`);
    invalidateFirestoreReadCacheByPrefix(`customerLedger:quotes:${workspaceUid}:`);
  }
  if (collectionName === "customers") {
    invalidateFirestoreReadCacheByPrefix(`customerLedger:recent:${workspaceUid}:`);
  }
  if (collectionName === "crm_leads") {
    invalidateFirestoreReadCacheByPrefix(`crmDeals:recent:${workspaceUid}:`);
    invalidateFirestoreReadCacheByPrefix(`customerLedger:crmDeals:${workspaceUid}:`);
  }
  if (collectionName === "product_configs") {
    invalidateFirestoreReadCache(`productConfigs:${workspaceUid}`);
  }
};
const LOCAL_DRAFT_MODULES = {
  GENERAL_QUOTE: "generalQuote",
  CURTAIN_QUOTE: "curtainQuote",
  CRM_LEAD: "crmLead",
  EXECUTION_DETAILS: "executionDetails",
  EXECUTION_TEMPLATE: "executionTemplate",
};

const TASK_HISTORY_STATUS_COMPLETED = "completed";
const TASK_HISTORY_TYPE_DEAL_ACTION = "deal_action";
const TASK_HISTORY_TYPE_REMINDER = "reminder";
const TASK_HISTORY_TYPE_CHECKLIST = "checklist";
const TASK_HISTORY_TYPE_INSTALLATION = "installation";
const TASK_HISTORY_SOURCE_CRM = "crm";
const TASK_HISTORY_SOURCE_INSTALLATION = "installation";
const TASK_HISTORY_SOURCE_EXECUTION_DETAILS = "execution_details";
const CRM_REMINDER_TYPE_FOLLOW_UP = "follow_up";
const DUE_REMINDER_STATUS_PENDING = "pending";
const DUE_REMINDER_STATUS_COMPLETED = "completed";
const DUE_REMINDER_STATUS_CANCELLED = "cancelled";
const DUE_REMINDER_TYPE_FOLLOW_UP = "followup";
const DUE_REMINDER_TYPE_INSTALLATION = "installation";
const DUE_REMINDER_TYPE_TASK = "task";
const DUE_REMINDER_ID_UNSAFE_CHARS = new RegExp("[/\\\\#?\\[\\]]+", "g");
const DAILY_TASK_SOURCE_FOLLOW_UP = "followup";
const DAILY_TASK_SOURCE_INSTALLATION = "installation";
const DAILY_TASK_SOURCE_DEAL_ACTION = "deal_action";
const DAILY_TASK_SOURCE_CHECKLIST = "checklist";

const DAILY_TASK_TYPE_LABELS = {
  [DAILY_TASK_SOURCE_FOLLOW_UP]: "Follow-up",
  [DAILY_TASK_SOURCE_INSTALLATION]: "Installation",
  [DAILY_TASK_SOURCE_DEAL_ACTION]: "Deal Action",
  [DAILY_TASK_SOURCE_CHECKLIST]: "Checklist",
};

const normalizeSearchText = (value = "") =>
  String(value ?? "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");

const normalizeSearchCompact = (value = "") =>
  normalizeSearchText(value).replace(/[^a-z0-9]+/g, "");

const doesSearchBlobMatch = (blob = "", query = "") => {
  const normalizedBlob = normalizeSearchText(blob);
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return true;

  if (normalizedBlob.includes(normalizedQuery)) return true;

  const compactBlob = normalizeSearchCompact(normalizedBlob);
  const compactQuery = normalizeSearchCompact(normalizedQuery);
  return Boolean(compactQuery && compactBlob.includes(compactQuery));
};

const SEARCH_TEXT_MAX_CHARS = 20000;
const SEARCH_KEYWORDS_MAX = 300;
const SEARCH_KEYWORD_MAX_CHARS = 60;
const SEARCH_PIECE_MAX_CHARS = 160;
const CUSTOMER_SEARCH_TEXT_MAX_CHARS = 3000;
const CUSTOMER_SEARCH_KEYWORDS_MAX = 100;
const CUSTOMER_SEARCH_PREFIXES_MAX = 200;
const CUSTOMER_SEARCH_RESULT_LIMIT = 15;
const CUSTOMER_SEARCH_TTL_MS = 5 * 60 * 1000;
const CUSTOMER_SEARCH_DEBOUNCE_MS = 500;
const GLOBAL_SEARCH_RESULT_LIMIT = 24;
const GLOBAL_SEARCH_DEBOUNCE_MS = 450;
const OPTIMIZER_QUOTE_SEARCH_LIMIT = 12;
const OPTIMIZER_QUOTE_SEARCH_FALLBACK_LIMIT = 40;
const OPTIMIZER_QUOTE_SEARCH_CACHE_TTL_MS = 5 * 60 * 1000;
const OPTIMIZER_QUOTE_SEARCH_DEBOUNCE_MS = 450;

const safeSearchPiece = (value, maxLen = SEARCH_PIECE_MAX_CHARS) => {
  if (value == null) return "";
  if (value instanceof Date) return value.toISOString().slice(0, maxLen);
  if (typeof value?.toDate === "function") {
    const dateValue = value.toDate();
    return dateValue instanceof Date && !Number.isNaN(dateValue.getTime())
      ? dateValue.toISOString().slice(0, maxLen)
      : "";
  }
  if (typeof value === "object") return "";

  return String(value)
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, maxLen);
};

const pushSafeSearchPiece = (pieces, value, maxLen = SEARCH_PIECE_MAX_CHARS) => {
  if (Array.isArray(value)) {
    value.forEach((entry) => pushSafeSearchPiece(pieces, entry, maxLen));
    return;
  }
  const piece = safeSearchPiece(value, maxLen);
  if (piece) pieces.push(piece);
};

const normalizeCustomerSearchText = (value = "") =>
  String(value ?? "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s@.]/g, " ")
    .replace(/\s+/g, " ");

const normalizeCustomerSearchPhone = (value = "") => {
  const digits = String(value || "").replace(/\D+/g, "");
  return digits.length > 10 ? digits.slice(-10) : digits;
};

const addCustomerSearchPrefixes = (prefixSet, value, maxPrefixLength = 24) => {
  const normalized = normalizeCustomerSearchText(value);
  if (!normalized) return;

  normalized
    .split(/\s+/)
    .map((token) => token.trim())
    .filter((token) => token.length > 0)
    .forEach((token) => {
      const capped = token.slice(0, maxPrefixLength);
      for (let index = 1; index <= capped.length; index += 1) {
        prefixSet.add(capped.slice(0, index));
        if (prefixSet.size >= CUSTOMER_SEARCH_PREFIXES_MAX) return;
      }
    });
};

const addCustomerPhonePrefixes = (prefixSet, value) => {
  const phone = normalizeCustomerSearchPhone(value);
  if (!phone) return;
  for (let index = 1; index <= phone.length; index += 1) {
    prefixSet.add(phone.slice(0, index));
    if (prefixSet.size >= CUSTOMER_SEARCH_PREFIXES_MAX) return;
  }
};

const buildSafeSearchResult = (pieces = []) => {
  const normalizedPieces = [];
  const seen = new Set();

  (Array.isArray(pieces) ? pieces : []).forEach((piece) => {
    const safePiece = safeSearchPiece(piece);
    const normalized = normalizeSearchText(safePiece);
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    normalizedPieces.push(normalized);
  });

  let searchText = normalizedPieces.join(" ");
  if (searchText.length > SEARCH_TEXT_MAX_CHARS) {
    if (isDevRuntime) {
      console.warn("Search index text truncated", {
        originalLength: searchText.length,
        maxLength: SEARCH_TEXT_MAX_CHARS,
      });
    }
    searchText = searchText.slice(0, SEARCH_TEXT_MAX_CHARS);
  }

  const keywordSet = new Set();
  searchText
    .split(/\s+/)
    .map((value) => value.trim())
    .filter((value) => value.length >= 2)
    .forEach((value) => keywordSet.add(value.slice(0, SEARCH_KEYWORD_MAX_CHARS)));

  return {
    searchText,
    searchKeywords: Array.from(keywordSet).slice(0, SEARCH_KEYWORDS_MAX),
  };
};

const addSafeLinkedQuotePieces = (pieces, linkedQuotes = []) => {
  (Array.isArray(linkedQuotes) ? linkedQuotes : []).forEach((quote) => {
    pushSafeSearchPiece(pieces, quote?.quoteNo || quote?.quoteNumber);
    pushSafeSearchPiece(pieces, quote?.customerName);
    pushSafeSearchPiece(pieces, quote?.type || quote?.quoteType);
    pushSafeSearchPiece(pieces, quote?.value);
  });
};

const addSafeSiteLocationPieces = (pieces, locations = []) => {
  (Array.isArray(locations) ? locations : []).forEach((location) => {
    pushSafeSearchPiece(pieces, location?.label);
    pushSafeSearchPiece(pieces, location?.addressText);
    pushSafeSearchPiece(pieces, location?.notes, 120);
  });
};

const addSafeQuoteRowPieces = (pieces, rows = []) => {
  (Array.isArray(rows) ? rows : []).slice(0, 120).forEach((row) => {
    pushSafeSearchPiece(pieces, row?.item || row?.name || row?.room);
    pushSafeSearchPiece(pieces, row?.product || row?.productType || row?.type);
    pushSafeSearchPiece(pieces, row?.fabric || row?.fabricName || row?.fabricLabel);
    pushSafeSearchPiece(pieces, row?.material || row?.itemFabric);
    pushSafeSearchPiece(pieces, row?.description, 120);
  });
};

const buildSafeDealSearchIndex = (deal = {}, customer = null) => {
  const linkedQuotes = normalizeCrmLinkedQuotes(deal);
  const primaryLinkedQuote =
    deal.primaryLinkedQuote || getPrimaryCrmLinkedQuote(linkedQuotes) || {};
  const contact = deal.crmContact || deal.contact || deal.crm?.contact || {};
  const crm = deal.crm || {};
  const customerRecord = customer || {};
  const pieces = [];

  [
    deal.searchText,
    deal.searchKeywords,
    deal.customerName,
    deal.customerLabel,
    deal.name,
    deal.crmTitle,
    deal.title,
    deal.dealTitle,
    deal.projectTitle,
    deal.companyOrProject,
    deal.companyName,
    deal.projectName,
    deal.project,
    contact.name,
    contact.phone,
    contact.whatsapp,
    contact.email,
    contact.address,
    deal.phone,
    deal.whatsapp,
    deal.email,
    deal.address,
    deal.location,
    deal.source,
    contact.source,
    crm.source,
    deal.notes,
    deal.crmNotes,
    deal.description,
    deal.status,
    deal.dealStatus,
    deal.crmStageId,
    deal.stageId,
    deal.crmStageLabel,
    deal.stageNameSnapshot,
    deal.pipelineType,
    deal.quoteType,
    deal.quoteTypeResolved,
    deal.assignedTo,
    deal.assignedToName,
    deal.assignedToNameSnapshot,
    deal.assignedUser,
    deal.assignedUserName,
    deal.quoteNo,
    deal.primaryQuoteNo,
    deal.linkedQuoteNo,
    deal.quoteId,
    deal.linkedQuoteId,
    deal.customerId,
    deal.customerPhone,
    deal.customerEmail,
    deal.customerAddress,
    deal.customerCompanyName,
    crm.customerName,
    crm.customerPhone,
    crm.customerWhatsapp,
    crm.customerEmail,
    crm.customerAddress,
    crm.customerCompanyName,
    crm.quoteNo,
    crm.quoteId,
    primaryLinkedQuote.quoteNo,
    primaryLinkedQuote.quoteId,
    primaryLinkedQuote.customerName,
    customerRecord.id,
    customerRecord.customerId,
    customerRecord.customerName,
    customerRecord.phone,
    customerRecord.whatsapp,
    customerRecord.email,
    customerRecord.address,
    customerRecord.companyName,
    customerRecord.source,
    customerRecord.tags,
  ].forEach((value) => pushSafeSearchPiece(pieces, value));

  addSafeLinkedQuotePieces(pieces, linkedQuotes);
  addSafeLinkedQuotePieces(pieces, normalizeCrmLinkedQuotes(crm));
  addSafeSiteLocationPieces(pieces, deal.siteLocations || crm.siteLocations || []);
  addSafeSiteLocationPieces(pieces, customerRecord.siteLocations || []);

  return buildSafeSearchResult(pieces);
};

const buildDealSearchBlob = (deal = {}, customer = null) => {
  const { searchText } = buildSafeDealSearchIndex(deal, customer);
  const compactBlob = normalizeSearchCompact(searchText);
  return compactBlob && compactBlob !== searchText
    ? `${searchText} ${compactBlob}`.slice(0, SEARCH_TEXT_MAX_CHARS)
    : searchText;
};

const buildCrmSearchIndexFields = (deal = {}, customer = null) => {
  const index = buildSafeDealSearchIndex(deal, customer);
  const keywordSet = new Set(index.searchKeywords);
  const compactPhone = normalizeSearchCompact(
    [
      deal?.phone,
      deal?.whatsapp,
      deal?.customerPhone,
      deal?.contact?.phone,
      deal?.contact?.whatsapp,
      deal?.crmContact?.phone,
      deal?.crmContact?.whatsapp,
    ]
      .map((value) => safeSearchPiece(value, 80))
      .filter(Boolean)
      .join(" ")
  );
  if (compactPhone) keywordSet.add(compactPhone.slice(0, SEARCH_KEYWORD_MAX_CHARS));

  return {
    searchText: index.searchText,
    searchKeywords: Array.from(keywordSet).slice(0, SEARCH_KEYWORDS_MAX),
    searchTokens: Array.from(keywordSet).slice(0, SEARCH_KEYWORDS_MAX),
    customerNameLower: normalizeSearchText(
      deal?.customerName ||
        deal?.contact?.name ||
        deal?.crmContact?.name ||
        deal?.title ||
        deal?.companyOrProject ||
        customer?.customerName ||
        ""
    ),
    normalizedPhone: normalizeSearchCompact(
      deal?.customerPhone ||
        deal?.phone ||
        deal?.contact?.phone ||
        deal?.contact?.whatsapp ||
        deal?.crmContact?.phone ||
        deal?.crmContact?.whatsapp ||
        customer?.phone ||
        customer?.whatsapp ||
        ""
    ),
    projectNameLower: normalizeSearchText(
      deal?.projectName ||
        deal?.projectTitle ||
        deal?.companyOrProject ||
        deal?.crmTitle ||
        deal?.title ||
        ""
    ),
    stageLower: normalizeSearchText(
      deal?.crmStageLabel || deal?.stageNameSnapshot || deal?.stageId || deal?.crmStageId || ""
    ),
    sourceLower: normalizeSearchText(deal?.source || deal?.contact?.source || deal?.crmContact?.source || ""),
    quoteNo: String(
      deal?.quoteNo ||
        deal?.primaryQuoteNo ||
        getPrimaryCrmLinkedQuote(normalizeCrmLinkedQuotes(deal))?.quoteNo ||
        ""
    ).trim(),
    primaryQuoteNo: String(
      deal?.primaryQuoteNo ||
        getPrimaryCrmLinkedQuote(normalizeCrmLinkedQuotes(deal))?.quoteNo ||
        deal?.quoteNo ||
        ""
    ).trim(),
  };
};

const buildSafeQuoteSearchIndex = (quote = {}) => {
  const pieces = [];
  [
    quote.quoteNo,
    quote.customerName,
    quote.customerPhone,
    quote.customerWhatsapp,
    quote.customerEmail,
    quote.source,
    quote.quoteType,
    quote.quoteTypeResolved,
    quote.status,
    quote.workStage,
    quote.crm?.stageId,
    quote.crm?.stageName,
    quote.generalQuote?.quoteFor?.name,
    quote.generalQuote?.quoteFor?.company,
    quote.generalQuote?.quoteFor?.phone,
    quote.generalQuote?.quoteFor?.email,
  ].forEach((value) => pushSafeSearchPiece(pieces, value));
  addSafeQuoteRowPieces(pieces, quote.rows || []);
  addSafeQuoteRowPieces(pieces, quote.generalQuote?.items || []);
  addSafeSiteLocationPieces(pieces, quote.crm?.siteLocations || []);
  return buildSafeSearchResult(pieces);
};

const buildQuoteSearchBlob = (quote = {}) => {
  const { searchText } = buildSafeQuoteSearchIndex(quote);
  const compactBlob = normalizeSearchCompact(searchText);
  return compactBlob && compactBlob !== searchText
    ? `${searchText} ${compactBlob}`.slice(0, SEARCH_TEXT_MAX_CHARS)
    : searchText;
};

const buildQuoteLookupFields = (quote = {}) => ({
  quoteNoString: String(quote.quoteNo || quote.id || "").trim(),
  customerNameLower: normalizeSearchText(
    quote.customerName ||
      quote.crm?.customerName ||
      quote.generalQuote?.quoteFor?.name ||
      quote.generalQuote?.quoteFor?.company ||
      ""
  ),
  projectNameLower: normalizeSearchText(
    quote.projectName ||
      quote.projectTitle ||
      quote.title ||
      quote.crm?.projectTitle ||
      ""
  ),
  normalizedPhone: normalizeSearchCompact(
    quote.customerPhone ||
      quote.customerWhatsapp ||
      quote.crm?.customerPhone ||
      quote.crm?.contact?.phone ||
      quote.generalQuote?.quoteFor?.phone ||
    ""
  ),
});

const buildQuoteIndexPayload = (quote = {}, quoteIdValue = "", workspaceUidValue = "") => {
  const resolveIndexNumber = (...candidates) => {
    for (const candidate of candidates) {
      if (candidate == null || candidate === "") continue;
      const numericValue = Number(candidate);
      if (Number.isFinite(numericValue)) return numericValue;
    }
    return 0;
  };
  const explicitSourceQuoteId = String(
    quote.sourceQuoteId ||
      quote.quotationId ||
      quote.sourceId ||
      quote.fullQuoteId ||
      ""
  ).trim();
  const quoteId = String(
    quoteIdValue || quote.quoteId || quote.id || explicitSourceQuoteId || ""
  ).trim();
  const sourceQuoteId = String(
    quote.sourceQuoteId ||
      quote.quotationId ||
      quote.sourceId ||
      quote.fullQuoteId ||
      quote.quoteId ||
      quote.id ||
      quoteId ||
      ""
  ).trim();
  const sourceCollection = String(quote.sourceCollection || "quotations").trim() || "quotations";
  const sourcePath = String(
    quote.sourcePath ||
      (workspaceUidValue && sourceQuoteId
        ? `users/${workspaceUidValue}/${sourceCollection}/${sourceQuoteId}`
        : "")
  ).trim();
  const quoteTypeResolved = quote.quoteTypeResolved || resolveQuoteType(quote);
  const quoteNo = String(quote.quoteNo || quote.quoteNoString || quoteId || "").trim();
  const quoteDate = resolveQuoteDateForInput(quote);
  const customerName = String(
    quote.customerName ||
      quote.quoteFor?.name ||
      quote.quoteFor?.company ||
      quote.generalQuote?.quoteFor?.name ||
      quote.generalQuote?.quoteFor?.company ||
      quote.crm?.customerName ||
      ""
  ).trim();
  const customerPhone = String(
    quote.customerPhone ||
      quote.customerWhatsapp ||
      quote.quoteFor?.phone ||
      quote.generalQuote?.quoteFor?.phone ||
      quote.crm?.customerPhone ||
      quote.crm?.contact?.phone ||
      ""
  ).trim();
  const projectName = String(
    quote.projectName ||
      quote.projectTitle ||
      quote.title ||
      quote.crm?.projectTitle ||
      ""
  ).trim();
  const subtotalAmount = resolveIndexNumber(
    quote.subtotal,
    quote.subTotal,
    quote.taxableAmount,
    quote.totalAmount,
    quote.grandTotal
  );
  const totalAmount = resolveIndexNumber(
    quote.grandTotal,
    quote.total,
    quote.finalTotal,
    quote.finalAmount,
    quote.totalAmount,
    quote.amount,
    subtotalAmount
  );
  const taxableAmount = resolveIndexNumber(
    quote.taxableAmount,
    quote.taxableTotal,
    quote.netAmount,
    subtotalAmount
  );
  const totalGst = resolveIndexNumber(
    quote.totalGst,
    quote.totalGstAmount,
    quote.gstTotal,
    quote.gstAmount,
    quote.taxAmount
  );
  const lightweightSearchSource = {
    id: quoteId,
    quoteNo,
    customerName,
    customerPhone,
    customerWhatsapp: quote.customerWhatsapp || "",
    customerEmail: quote.customerEmail || "",
    projectName,
    projectTitle: projectName,
    quoteType: quoteTypeResolved,
    quoteTypeResolved,
    status: quote.status || "draft",
    workStage: quote.workStage || "quote_given",
  };
  const quoteSearchIndex = buildSafeQuoteSearchIndex(lightweightSearchSource);
  const lookupFields = buildQuoteLookupFields(lightweightSearchSource);

  return {
    quoteId,
    id: quoteId,
    sourceQuoteId,
    sourceCollection,
    sourcePath,
    quoteNo,
    quoteNoString: lookupFields.quoteNoString,
    customerId: quote.customerId || quote.crm?.customerId || "",
    customerKey:
      quote.customerKey ||
      quote.crm?.customerKey ||
      quote.customerId ||
      quote.crm?.customerId ||
      lookupFields.normalizedPhone ||
      normalizeSearchCompact(customerName) ||
      "",
    customerName,
    customerNameLower: lookupFields.customerNameLower,
    customerPhone,
    customerWhatsapp: quote.customerWhatsapp || "",
    normalizedPhone: lookupFields.normalizedPhone,
    projectName,
    projectNameLower: lookupFields.projectNameLower,
    quoteType: quoteTypeResolved,
    quoteTypeResolved,
    category: quote.category || quoteTypeResolved,
    status: quote.status || "draft",
    workStage: quote.workStage || "quote_given",
    leadId: quote.leadId || quote.crm?.leadId || "",
    crmLeadId: quote.crmLeadId || quote.crm?.leadId || "",
    subtotal: Number.isFinite(subtotalAmount) ? subtotalAmount : 0,
    taxableAmount: Number.isFinite(taxableAmount) ? taxableAmount : 0,
    totalGst: Number.isFinite(totalGst) ? totalGst : 0,
    gstAmount: Number.isFinite(totalGst) ? totalGst : 0,
    totalAmount: Number.isFinite(totalAmount) ? totalAmount : 0,
    grandTotal: Number.isFinite(totalAmount) ? totalAmount : 0,
    total: Number.isFinite(totalAmount) ? totalAmount : 0,
    finalTotal: Number.isFinite(totalAmount) ? totalAmount : 0,
    finalAmount: Number.isFinite(totalAmount) ? totalAmount : 0,
    quoteDate,
    date: quoteDate,
    quotationDate: quoteDate,
    createdAt: quote.createdAt || quote.createdOn || quote.createdDate || quoteDate || null,
    updatedAt: quote.updatedAt || quote.updatedOn || quote.modifiedAt || null,
    deleted: quote.deleted === true,
    deletedAt: quote.deletedAt || null,
    searchText: quoteSearchIndex.searchText,
    searchKeywords: quoteSearchIndex.searchKeywords,
    searchTokens: quoteSearchIndex.searchKeywords,
  };
};

const buildQuoteIndexSavePayload = (quote = {}, quoteIdValue = "", workspaceUidValue = "") =>
  sanitizeCrmFirestoreValue({
    ...buildQuoteIndexPayload(quote, quoteIdValue, workspaceUidValue),
    updatedAt: serverTimestamp(),
  });

const compactCustomerSearchIndexPayload = (payload = {}) =>
  Object.entries(payload || {}).reduce((result, [key, value]) => {
    if (typeof value === "undefined" || typeof value === "function") return result;
    if (typeof value === "number" && !Number.isFinite(value)) return result;
    result[key] = value;
    return result;
  }, {});

const buildCustomerSearchIndexWrite = (record = {}, options = {}) => {
  const sourceType = String(options.sourceType || record.sourceType || "customer").trim();
  const indexRecord = buildCustomerSearchIndexRecord(record, {
    sourceType,
    workspaceUid: options.workspaceUid || record.workspaceUid || "",
  });
  const personId = buildCustomerSearchIndexId(indexRecord);
  if (!personId || !indexRecord.displayName) return null;

  const customerId = String(indexRecord.customerId || record.customerId || record.customerKey || "").trim();
  const crmLeadId = String(indexRecord.crmLeadId || record.crmLeadId || record.leadId || "").trim();
  const quoteId = String(indexRecord.quoteId || record.quoteId || record.id || "").trim();
  const payload = compactCustomerSearchIndexPayload({
    ...indexRecord,
    id: personId,
    personId,
    workspaceUid: options.workspaceUid || indexRecord.workspaceUid || "",
    sourceTypes: arrayUnion(sourceType),
    ...(customerId ? { customerIds: arrayUnion(customerId), customerId, customerKey: customerId } : {}),
    ...(crmLeadId ? { crmLeadIds: arrayUnion(crmLeadId), crmLeadId, leadId: crmLeadId } : {}),
    ...(quoteId
      ? {
          quoteIds: arrayUnion(quoteId),
          latestQuoteId: indexRecord.latestQuoteId || quoteId,
          latestQuoteNo: indexRecord.latestQuoteNo || indexRecord.quoteNo || "",
        }
      : {}),
    updatedAt: serverTimestamp(),
  });

  return {
    personId,
    payload,
    localRecord: {
      ...indexRecord,
      id: personId,
      personId,
      sourceTypes: uniqueValues([sourceType, ...(indexRecord.sourceTypes || [])]),
    },
  };
};

const normalizeQuoteIndexRecordForList = (id, raw = {}, normalizeTs = (value) => value) => {
  const quoteIndexId = String(raw.quoteIndexId || raw.indexId || id || "").trim();
  const sourceQuoteId = String(
    raw.sourceQuoteId ||
      raw.quotationId ||
      raw.sourceId ||
      raw.fullQuoteId ||
      raw.quoteId ||
      id ||
      ""
  ).trim();
  const quoteId = String(raw.quoteId || sourceQuoteId || id || "").trim();
  const indexPayload = buildQuoteIndexPayload(
    {
      ...raw,
      id: quoteId,
      quoteId,
      quoteIndexId,
      indexId: quoteIndexId,
      sourceQuoteId,
      sourceCollection: raw.sourceCollection || "quotations",
    },
    quoteId
  );
  return {
    ...indexPayload,
    id: quoteId,
    quoteId,
    indexId: quoteIndexId,
    quoteIndexId,
    indexRecordRaw: raw,
    indexRecordRawKeys: Object.keys(raw || {}),
    sourceQuoteId: sourceQuoteId || quoteId,
    sourceCollection: raw.sourceCollection || indexPayload.sourceCollection || "quotations",
    sourcePath: raw.sourcePath || indexPayload.sourcePath || "",
    staleSource: raw.staleSource === true,
    sourceMissingAt: normalizeTs(raw.sourceMissingAt),
    quoteType: indexPayload.quoteType,
    quoteTypeResolved: indexPayload.quoteTypeResolved,
    rows: [],
    effectiveRows: [],
    generalQuote: null,
    generalQuoteData: null,
    lineItems: undefined,
    items: undefined,
    subtotal: Number(indexPayload.subtotal || 0),
    grandTotal: Number(indexPayload.grandTotal || 0),
    totalAmount: Number(indexPayload.grandTotal || 0),
    total: Number(indexPayload.grandTotal || 0),
    finalTotal: Number(indexPayload.grandTotal || 0),
    finalAmount: Number(indexPayload.grandTotal || 0),
    taxableAmount: Number(indexPayload.taxableAmount || 0),
    totalGst: Number(indexPayload.totalGst || 0),
    gstAmount: Number(indexPayload.gstAmount || 0),
    quoteDate: indexPayload.quoteDate,
    date: indexPayload.date,
    quotationDate: indexPayload.quotationDate,
    createdAt: normalizeTs(raw.createdAt || raw.createdOn || raw.createdDate || indexPayload.quoteDate || raw.date),
    updatedAt: normalizeTs(raw.updatedAt || raw.updatedOn || raw.modifiedAt),
    deletedAt: normalizeTs(raw.deletedAt),
  };
};

const normalizeSavedQuoteSourcePath = (workspaceUidValue = "", sourcePath = "") => {
  const workspace = String(workspaceUidValue || "").trim();
  const path = String(sourcePath || "").trim();
  if (!workspace || !path) return "";
  const prefix = `users/${workspace}/quotations/`;
  if (!path.startsWith(prefix)) return "";
  const quoteId = path.slice(prefix.length).trim();
  if (!quoteId || quoteId.includes("/")) return "";
  return `${prefix}${quoteId}`;
};

const getSavedQuoteOpenCandidates = (quoteInput, workspaceUidValue = "") => {
  const quoteIndex =
    quoteInput && typeof quoteInput === "object" ? quoteInput : { id: quoteInput };
  const indexId = String(
    quoteIndex.indexId || quoteIndex.quoteIndexId || quoteIndex.indexDocId || ""
  ).trim();
  const sourceQuoteId = String(
    quoteIndex.sourceQuoteId ||
      quoteIndex.quotationId ||
      quoteIndex.sourceId ||
      quoteIndex.fullQuoteId ||
      ""
  ).trim();
  const quotationId = String(
    quoteIndex.quotationId ||
      quoteIndex.quotationID ||
      quoteIndex.quotationDocId ||
      ""
  ).trim();
  const quoteId = String(quoteIndex.quoteId || quoteIndex.id || "").trim();
  const displayId = String(quoteIndex.id || "").trim();
  const candidatePaths = [];
  const pushPath = (path) => {
    const normalizedPath = normalizeSavedQuoteSourcePath(workspaceUidValue, path);
    if (normalizedPath && !candidatePaths.includes(normalizedPath)) {
      candidatePaths.push(normalizedPath);
    }
  };
  const pushId = (id) => {
    const value = String(id || "").trim();
    if (!value || value.includes("/")) return;
    pushPath(`users/${workspaceUidValue}/quotations/${value}`);
  };

  pushPath(quoteIndex.sourcePath);
  pushId(sourceQuoteId);
  pushId(quoteId);
  pushId(quotationId);
  pushId(displayId);
  pushId(indexId);

  return {
    quoteIndex,
    indexId,
    sourceQuoteId,
    quotationId,
    quoteId,
    displayId,
    candidatePaths,
  };
};

const getFirstArrayField = (...candidates) =>
  candidates.find((candidate) => Array.isArray(candidate)) || [];

const isGeneralQuoteTypeHint = (value = "") => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  return (
    normalized.includes("general") ||
    normalized.includes("custom") ||
    normalized.includes("noncurtain")
  );
};

const normalizeGeneralQuoteForEditor = (rawQuote = {}, indexRecord = {}) => {
  const embedded =
    rawQuote.generalQuote && typeof rawQuote.generalQuote === "object"
      ? rawQuote.generalQuote
      : rawQuote.generalQuoteData && typeof rawQuote.generalQuoteData === "object"
      ? rawQuote.generalQuoteData
      : {};
  const resolvedQuoteNo = String(
    rawQuote.quoteNo ||
      rawQuote.quoteNumber ||
      rawQuote.quotationNumber ||
      embedded.quoteNo ||
      indexRecord.quoteNo ||
      indexRecord.quoteNoString ||
      rawQuote.id ||
      ""
  ).trim();
  const resolvedCustomerName = String(
    rawQuote.customerName ||
      rawQuote.customer ||
      rawQuote.clientName ||
      rawQuote.billToName ||
      rawQuote.quoteFor?.name ||
      rawQuote.quoteFor?.company ||
      embedded.customerName ||
      embedded.quoteFor?.name ||
      embedded.quoteFor?.company ||
      indexRecord.customerName ||
      ""
  ).trim();
  const resolvedDate =
    resolveQuoteDateForInput({
      ...rawQuote,
      generalQuote: embedded,
      quoteDate:
        rawQuote.quoteDate ||
        rawQuote.date ||
        rawQuote.quotationDate ||
        embedded.quoteDate ||
        indexRecord.quoteDate ||
        indexRecord.date ||
        "",
    }) ||
    resolveQuoteDateForInput(indexRecord) ||
    "";
  const lineItems = getFirstArrayField(
    embedded.lineItems,
    embedded.items,
    embedded.rows,
    rawQuote.lineItems,
    rawQuote.items,
    rawQuote.rows,
    rawQuote.quoteRows,
    rawQuote.generalRows
  );
  const subtotal = Number(
    rawQuote.subtotal ??
      embedded.subtotal ??
      indexRecord.subtotal ??
      indexRecord.totalAmount ??
      0
  );
  const grandTotal = Number(
    rawQuote.grandTotal ??
      rawQuote.finalTotal ??
      rawQuote.total ??
      rawQuote.totalAmount ??
      rawQuote.finalAmount ??
      embedded.grandTotal ??
      embedded.finalTotal ??
      embedded.total ??
      indexRecord.grandTotal ??
      indexRecord.totalAmount ??
      0
  );
  const warnings = [];
  if (!lineItems.length && grandTotal > 0) {
    warnings.push("No item rows found; preserving saved total as legacy summary item.");
  }
  const resolvedItems = lineItems.length
    ? lineItems
    : grandTotal > 0
    ? [
        {
          id: "legacy-general-summary-item",
          name: "Legacy saved quotation total",
          itemName: "Legacy saved quotation total",
          description: "Original item rows were not available in this old saved quote.",
          quantity: "1",
          unit: "lot",
          rate: String(grandTotal),
          amount: String(grandTotal),
          gstPercent: 0,
          gstMode: "inclusive",
          images: [],
          specifications: [],
        },
      ]
    : [];

  if (isDevRuntime) {
    console.log("GENERAL_QUOTE_NORMALIZE_TRACE", {
      quoteNo: resolvedQuoteNo,
      rawKeys: Object.keys(rawQuote || {}),
      resolvedCustomerName,
      resolvedQuoteNo,
      resolvedDate,
      rowCount: resolvedItems.length,
      subtotal: Number.isFinite(subtotal) ? subtotal : 0,
      grandTotal: Number.isFinite(grandTotal) ? grandTotal : 0,
      warnings,
    });
  }

  return {
    ...embedded,
    id: embedded.id || rawQuote.id || indexRecord.sourceQuoteId || indexRecord.quoteId || "",
    quoteNo: resolvedQuoteNo,
    quoteDate: resolvedDate,
    date: resolvedDate,
    quotationDate: resolvedDate,
    customerId: embedded.customerId || rawQuote.customerId || indexRecord.customerId || "",
    customerName: embedded.customerName || resolvedCustomerName,
    customerPhone:
      embedded.customerPhone ||
      rawQuote.customerPhone ||
      rawQuote.phone ||
      indexRecord.customerPhone ||
      "",
    customerWhatsapp:
      embedded.customerWhatsapp ||
      rawQuote.customerWhatsapp ||
      rawQuote.whatsapp ||
      indexRecord.customerWhatsapp ||
      "",
    customerEmail:
      embedded.customerEmail ||
      rawQuote.customerEmail ||
      rawQuote.email ||
      indexRecord.customerEmail ||
      "",
    customerAddress:
      embedded.customerAddress ||
      rawQuote.customerAddress ||
      rawQuote.address ||
      "",
    quoteFor: {
      ...(embedded.quoteFor || rawQuote.quoteFor || {}),
      name:
        embedded.quoteFor?.name ||
        rawQuote.quoteFor?.name ||
        resolvedCustomerName,
      company:
        embedded.quoteFor?.company ||
        rawQuote.quoteFor?.company ||
        rawQuote.companyName ||
        "",
      phone:
        embedded.quoteFor?.phone ||
        rawQuote.quoteFor?.phone ||
        rawQuote.customerPhone ||
        "",
      email:
        embedded.quoteFor?.email ||
        rawQuote.quoteFor?.email ||
        rawQuote.customerEmail ||
        rawQuote.email ||
        "",
      address:
        embedded.quoteFor?.address ||
        rawQuote.quoteFor?.address ||
        rawQuote.customerAddress ||
        rawQuote.address ||
        "",
    },
    lineItems: resolvedItems,
    items: resolvedItems,
    subtotal: Number.isFinite(subtotal) ? subtotal : 0,
    grandTotal: Number.isFinite(grandTotal) ? grandTotal : 0,
    finalTotal: Number.isFinite(grandTotal) ? grandTotal : 0,
    total: Number.isFinite(grandTotal) ? grandTotal : 0,
    taxableAmount: Number(rawQuote.taxableAmount ?? embedded.taxableAmount ?? 0) || 0,
    gstAmount: Number(rawQuote.gstAmount ?? embedded.gstAmount ?? 0) || 0,
  };
};

const normalizeSavedQuoteForEditor = (rawQuote = {}, quoteIndex = {}) => {
  const id = String(
    rawQuote.id ||
      rawQuote.quoteId ||
      quoteIndex.sourceQuoteId ||
      quoteIndex.quoteId ||
      quoteIndex.id ||
      ""
  ).trim();
  const explicitQuoteType =
    rawQuote.quoteType ||
    rawQuote.quoteTypeResolved ||
    quoteIndex.quoteType ||
    quoteIndex.quoteTypeResolved ||
    "";
  const quoteType =
    explicitQuoteType && isGeneralQuoteTypeHint(explicitQuoteType)
      ? GENERAL_QUOTE_TYPE
      : explicitQuoteType
      ? resolveQuoteType({ quoteType: explicitQuoteType })
      : resolveQuoteType(rawQuote);
  const quoteNo = String(
    rawQuote.quoteNo ||
      rawQuote.quoteNumber ||
      rawQuote.no ||
      quoteIndex.quoteNo ||
      quoteIndex.quoteNoString ||
      id ||
      ""
  ).trim();
  const quoteDate =
    resolveQuoteDateForInput(rawQuote) ||
    resolveQuoteDateForInput(quoteIndex) ||
    "";
  const customerName = String(
    rawQuote.customerName ||
      rawQuote.customer ||
      rawQuote.clientName ||
      rawQuote.quoteFor?.name ||
      rawQuote.quoteFor?.company ||
      rawQuote.generalQuote?.quoteFor?.name ||
      rawQuote.generalQuote?.quoteFor?.company ||
      quoteIndex.customerName ||
      ""
  ).trim();
  const customerPhone = String(
    rawQuote.customerPhone ||
      rawQuote.customerWhatsapp ||
      rawQuote.phone ||
      rawQuote.whatsapp ||
      rawQuote.quoteFor?.phone ||
      rawQuote.generalQuote?.quoteFor?.phone ||
      quoteIndex.customerPhone ||
      quoteIndex.customerWhatsapp ||
      ""
  ).trim();
  const subtotal = Number(rawQuote.subtotal ?? quoteIndex.subtotal ?? 0);
  const total = Number(
    rawQuote.grandTotal ??
      rawQuote.finalTotal ??
      rawQuote.total ??
      rawQuote.totalAmount ??
      rawQuote.finalAmount ??
      quoteIndex.grandTotal ??
      quoteIndex.totalAmount ??
      0
  );
  const normalized = {
    ...rawQuote,
    id,
    quoteId: rawQuote.quoteId || id,
    quoteType,
    quoteTypeResolved: quoteType,
    quoteNo,
    quoteNoString: rawQuote.quoteNoString || quoteNo,
    customerName,
    customerPhone,
    customerWhatsapp: rawQuote.customerWhatsapp || quoteIndex.customerWhatsapp || customerPhone,
    customerEmail: rawQuote.customerEmail || rawQuote.email || quoteIndex.customerEmail || "",
    customerId: rawQuote.customerId || quoteIndex.customerId || "",
    customerKey: rawQuote.customerKey || quoteIndex.customerKey || "",
    quoteDate,
    date: quoteDate,
    quotationDate: quoteDate,
    subtotal: Number.isFinite(subtotal) ? subtotal : 0,
    grandTotal: Number.isFinite(total) ? total : 0,
    finalTotal: Number.isFinite(total) ? total : 0,
  };

  if (quoteType === GENERAL_QUOTE_TYPE) {
    const embedded = normalizeGeneralQuoteForEditor(normalized, quoteIndex);
    const items = getFirstArrayField(
      embedded.lineItems,
      embedded.items,
      embedded.rows,
      rawQuote.lineItems,
      rawQuote.items,
      rawQuote.generalRows,
      rawQuote.quoteRows,
      rawQuote.generalItems
    );
    return {
      ...normalized,
      generalQuote: {
        ...embedded,
        id: embedded.id || id,
        quoteNo: embedded.quoteNo || quoteNo,
        quoteFor: {
          ...(embedded.quoteFor || {}),
          name: embedded.quoteFor?.name || customerName,
          phone: embedded.quoteFor?.phone || customerPhone,
        },
        items,
        lineItems: items,
      },
      rows: Array.isArray(rawQuote.rows) ? rawQuote.rows : [],
    };
  }

  const rows = getFirstArrayField(
    rawQuote.rows,
    rawQuote.quoteRows,
    rawQuote.curtainRows,
    rawQuote.measurementRows,
    rawQuote.items
  );
  return {
    ...normalized,
    rows,
  };
};

const classifySavedQuoteOpenFailure = ({
  readError = null,
  exists = false,
  normalizeError = null,
  wrongLoader = false,
  attemptedPaths = [],
} = {}) => {
  const error = normalizeError || readError;
  const code = String(error?.code || "");
  if (wrongLoader) {
    return {
      result: "wrong-loader",
      message: "This quote exists, but was routed to the wrong quotation editor.",
      tone: "error",
    };
  }
  if (normalizeError) {
    return {
      result: "schema-error",
      message: "This old quotation exists but uses an older format that could not be loaded.",
      tone: "error",
    };
  }
  if (code === "resource-exhausted") {
    return {
      result: "resource-exhausted",
      message: "Firebase read limit reached while opening this saved quote.",
      tone: "warning",
    };
  }
  if (code === "permission-denied") {
    return {
      result: "permission-denied",
      message:
        "The quote index was found, but your account could not read the full quote document.",
      tone: "error",
    };
  }
  if (!exists && attemptedPaths.length > 0 && !readError) {
    return {
      result: "not-found",
      message:
        "This saved quote search result is stale. The source quotation document was not found.",
      tone: "warning",
    };
  }
  if (code === "not-found") {
    return {
      result: "not-found",
      message:
        "This saved quote index exists, but the source quotation document was not found.",
      tone: "warning",
    };
  }
  if (code === "unavailable" || code === "deadline-exceeded") {
    return {
      result: "network",
      message: "Network issue while opening this saved quote. Please try again.",
      tone: "warning",
    };
  }
  return {
    result: "unknown",
    message: "Could not open this quotation. Check console trace.",
    tone: "error",
  };
};

const buildSafeCustomerSearchIndex = (customer = {}) => {
  const pieces = [];
  [
    customer.customerName,
    customer.name,
    customer.phone,
    customer.whatsapp,
    customer.email,
    customer.companyName,
    customer.source,
    customer.address,
    customer.tags,
  ].forEach((value) => pushSafeSearchPiece(pieces, value));
  addSafeSiteLocationPieces(pieces, customer.siteLocations || []);
  const baseResult = buildSafeSearchResult(pieces);
  const searchText = baseResult.searchText.slice(0, CUSTOMER_SEARCH_TEXT_MAX_CHARS);
  const searchKeywords = baseResult.searchKeywords.slice(
    0,
    CUSTOMER_SEARCH_KEYWORDS_MAX
  );
  const searchPrefixSet = new Set();
  const phonePrefixSet = new Set();

  [
    customer.customerName,
    customer.name,
    customer.companyName,
    customer.source,
    customer.email,
    customer.normalizedName,
  ].forEach((value) => addCustomerSearchPrefixes(searchPrefixSet, value));
  [
    customer.phone,
    customer.whatsapp,
    customer.customerPhone,
    customer.customerWhatsapp,
    customer.normalizedPhone,
    customer.normalizedWhatsapp,
  ].forEach((value) => {
    addCustomerPhonePrefixes(phonePrefixSet, value);
    addCustomerSearchPrefixes(searchPrefixSet, normalizeCustomerSearchPhone(value));
  });

  return {
    searchText,
    searchKeywords,
    searchPrefixes: Array.from(searchPrefixSet).slice(
      0,
      CUSTOMER_SEARCH_PREFIXES_MAX
    ),
    phonePrefixes: Array.from(phonePrefixSet).slice(
      0,
      CUSTOMER_SEARCH_PREFIXES_MAX
    ),
  };
};

const MeasuredChartFrame = ({
  active = true,
  children,
  fallback = null,
  height = 220,
  style = {},
}) => {
  const frameRef = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!active) return undefined;

    const node = frameRef.current;
    if (!node) return undefined;

    let animationFrame = 0;
    const measure = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        const nextWidth = Math.floor(node.getBoundingClientRect().width || 0);
        setWidth((prevWidth) =>
          Math.abs(prevWidth - nextWidth) > 1 ? nextWidth : prevWidth
        );
      });
    };

    measure();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(measure);
      observer.observe(node);
      window.addEventListener("resize", measure);
      return () => {
        window.cancelAnimationFrame(animationFrame);
        window.removeEventListener("resize", measure);
        observer.disconnect();
      };
    }

    window.addEventListener("resize", measure);
    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", measure);
    };
  }, [active]);

  return (
    <div
      ref={frameRef}
      className="fabriquote-chart-shell"
      style={{
        ...style,
        width: "100%",
        minWidth: 0,
        height,
        overflow: "hidden",
        position: "relative",
      }}
    >
      {active && width > 12
        ? children({ width, height })
        : fallback}
    </div>
  );
};

const getCrmReminderFollowUpKeys = (reminder = {}) => {
  const values = [
    reminder?.followUpId,
    reminder?.sourceTaskId,
    reminder?.reminderId,
  ];
  const idSuffix = String(reminder?.id || "").split("__").pop();
  if (idSuffix && idSuffix !== String(reminder?.id || "")) {
    values.push(idSuffix);
  }

  return new Set(values.map((value) => String(value || "").trim()).filter(Boolean));
};

const doesCrmReminderMatchFollowUp = (reminder = {}, followUp = {}) => {
  const keys = getCrmReminderFollowUpKeys(reminder);
  const followUpKeys = [
    followUp?.id,
    followUp?.reminderId,
    followUp?.sourceTaskId,
  ].map((value) => String(value || "").trim());

  return followUpKeys.some((value) => value && keys.has(value));
};

const TASK_HISTORY_TYPE_LABELS = {
  [TASK_HISTORY_TYPE_DEAL_ACTION]: "Deal Action",
  [TASK_HISTORY_TYPE_REMINDER]: "Reminder",
  [TASK_HISTORY_TYPE_CHECKLIST]: "Checklist",
  [TASK_HISTORY_TYPE_INSTALLATION]: "Installation",
};

const TASK_HISTORY_SOURCE_LABELS = {
  [TASK_HISTORY_SOURCE_CRM]: "CRM",
  [TASK_HISTORY_SOURCE_INSTALLATION]: "Installation",
  [TASK_HISTORY_SOURCE_EXECUTION_DETAILS]: "Execution Details",
};

let pdfRuntimePromise = null;
let zipRuntimePromise = null;

const loadPdfRuntime = async () => {
  if (!pdfRuntimePromise) {
    pdfRuntimePromise = Promise.all([
      import("jspdf"),
      import("jspdf-autotable"),
    ])
      .then(([jspdfModule, autoTableModule]) => ({
        jsPDF: jspdfModule.default || jspdfModule.jsPDF || jspdfModule,
        autoTable: autoTableModule.default || autoTableModule,
      }))
      .catch((error) => {
        pdfRuntimePromise = null;
        throw error;
      });
  }

  return pdfRuntimePromise;
};

const loadZipRuntime = async () => {
  if (!zipRuntimePromise) {
    zipRuntimePromise = Promise.all([
      import("jszip"),
      import("file-saver"),
    ])
      .then(([zipModule, fileSaverModule]) => ({
        JSZip: zipModule.default || zipModule,
        saveAs: fileSaverModule.saveAs || fileSaverModule.default,
      }))
      .catch((error) => {
        zipRuntimePromise = null;
        throw error;
      });
  }

  return zipRuntimePromise;
};

const preloadWorkshopZipRuntime = () => {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return Promise.resolve(false);
  }

  return Promise.all([loadPdfRuntime(), loadZipRuntime()])
    .then(() => {
      resetChunkLoadReloadGuard();
      return true;
    })
    .catch((error) => {
      if (isChunkLoadError(error)) {
        handleChunkLoadErrorRecovery(error, {
          source: "preloadWorkshopZipRuntime",
          autoReload: false,
        });
      }
      return false;
    });
};

const buildLocalDraftKey = (workspaceUid, module, entityId = "new") =>
  `${LOCAL_DRAFT_PREFIX}:${String(workspaceUid || "anonymous").trim()}:${String(
    module || "unknown"
  ).trim()}:${String(entityId || "new").trim() || "new"}`;

const sanitizeLocalDraftValue = (value, seen = new WeakSet()) => {
  if (value === undefined || typeof value === "function") return undefined;
  if (value === null) return null;
  if (typeof File !== "undefined" && value instanceof File) return undefined;
  if (typeof Blob !== "undefined" && value instanceof Blob) return undefined;
  if (value instanceof Date) return value.toISOString();
  if (value && typeof value.toDate === "function") {
    const dateValue = value.toDate();
    return dateValue instanceof Date && !Number.isNaN(dateValue.getTime())
      ? dateValue.toISOString()
      : null;
  }
  if (typeof value !== "object") return value;
  if (seen.has(value)) return undefined;
  seen.add(value);

  if (Array.isArray(value)) {
    return value
      .map((entry) => sanitizeLocalDraftValue(entry, seen))
      .filter((entry) => entry !== undefined);
  }

  const output = {};
  Object.entries(value).forEach(([key, entryValue]) => {
    const sanitized = sanitizeLocalDraftValue(entryValue, seen);
    if (sanitized !== undefined) {
      output[key] = sanitized;
    }
  });
  return output;
};

const sanitizeGeneralQuoteImageMetadata = (image = {}) => {
  const normalized = createGeneralQuoteImage(image);
  const storagePath = getGeneralQuoteImageStoragePath(normalized);
  const fileName = String(normalized.filename || normalized.name || "").trim();
  const downloadURL = getGeneralQuoteImageDisplayUrl(normalized);

  return {
    id: String(normalized.id || createClientId("general-image")),
    imageId: String(normalized.imageId || normalized.id || "").trim(),
    fileId: String(normalized.fileId || "").trim(),
    name: fileName,
    filename: fileName,
    originalFilename: String(normalized.originalFilename || fileName).trim(),
    safeFilename: String(normalized.safeFilename || fileName).trim(),
    contentType: String(normalized.contentType || normalized.type || "").trim(),
    sizeBytes: Number.isFinite(Number(normalized.sizeBytes ?? normalized.size))
      ? Number(normalized.sizeBytes ?? normalized.size)
      : null,
    originalSizeBytes: Number.isFinite(Number(normalized.originalSizeBytes))
      ? Number(normalized.originalSizeBytes)
      : null,
    optimizedSizeBytes: Number.isFinite(Number(normalized.optimizedSizeBytes))
      ? Number(normalized.optimizedSizeBytes)
      : null,
    thumbnailSizeBytes: Number.isFinite(Number(normalized.thumbnailSizeBytes))
      ? Number(normalized.thumbnailSizeBytes)
      : null,
    compressionRatio: Number.isFinite(Number(normalized.compressionRatio))
      ? Number(normalized.compressionRatio)
      : null,
    width: Number.isFinite(Number(normalized.width)) ? Number(normalized.width) : null,
    height: Number.isFinite(Number(normalized.height)) ? Number(normalized.height) : null,
    originalWidth: Number.isFinite(Number(normalized.originalWidth))
      ? Number(normalized.originalWidth)
      : null,
    originalHeight: Number.isFinite(Number(normalized.originalHeight))
      ? Number(normalized.originalHeight)
      : null,
    fullPath: storagePath,
    downloadURL,
    thumbnailFullPath: String(normalized.thumbnailFullPath || "").trim(),
    thumbnailDownloadURL: String(normalized.thumbnailDownloadURL || "").trim(),
    bucket: String(normalized.storageBucket || normalized.bucket || "").trim(),
    storageBucket: String(normalized.storageBucket || "").trim(),
    url: downloadURL,
    previewUrl: String(normalized.thumbnailDownloadURL || normalized.previewUrl || downloadURL).trim(),
    path: storagePath,
    storagePath,
    size: Number.isFinite(Number(normalized.sizeBytes ?? normalized.size))
      ? Number(normalized.sizeBytes ?? normalized.size)
      : null,
    type: String(normalized.contentType || normalized.type || "").trim(),
    uploadedAt: String(normalized.uploadedAt || "").trim(),
    uploadedBy: String(normalized.uploadedBy || normalized.uploadedByUid || "").trim(),
    uploadStatus: String(normalized.uploadStatus || "").trim(),
    compressionStatus: String(normalized.compressionStatus || "").trim(),
    thumbnailStatus: String(normalized.thumbnailStatus || "").trim(),
    ownerType: String(normalized.ownerType || "").trim(),
    ownerId: String(normalized.ownerId || "").trim(),
    workspaceUid: String(normalized.workspaceUid || "").trim(),
    quoteId: String(normalized.quoteId || "").trim(),
    itemId: String(normalized.itemId || "").trim(),
    rowId: String(normalized.rowId || "").trim(),
    leadId: String(normalized.leadId || "").trim(),
    removedFromRecord: normalized.removedFromRecord === true,
    archived: normalized.archived === true,
    expiresAt: String(normalized.expiresAt || "").trim(),
    legacyUrl: String(normalized.legacyUrl || "").trim(),
    legacyPath: String(normalized.legacyPath || "").trim(),
    legacyFullPath: String(normalized.legacyFullPath || "").trim(),
    legacyMissingImage: normalized.legacyMissingImage || null,
    replacedMissingImage: normalized.replacedMissingImage === true,
    repairedFromMissing: normalized.repairedFromMissing === true,
    replacedAt: String(normalized.replacedAt || "").trim(),
    repairedFromPath: String(normalized.repairedFromPath || "").trim(),
    repairMethod: String(normalized.repairMethod || "").trim(),
    repairedAt: String(normalized.repairedAt || "").trim(),
    repairedBy: String(normalized.repairedBy || "").trim(),
    removedFromQuote: normalized.removedFromQuote === true,
    removedAt: String(normalized.removedAt || "").trim(),
    removedBy: String(normalized.removedBy || "").trim(),
    removalReason: String(normalized.removalReason || "").trim(),
    cleanupEligibleAt: String(normalized.cleanupEligibleAt || "").trim(),
    cleanupRetentionDays: Number.isFinite(Number(normalized.cleanupRetentionDays))
      ? Number(normalized.cleanupRetentionDays)
      : null,
    cleanupStatus: String(normalized.cleanupStatus || "").trim(),
    removedFromQuoteId: String(normalized.removedFromQuoteId || "").trim(),
    removedFromQuoteNo: String(normalized.removedFromQuoteNo || "").trim(),
    removedFromItemId: String(normalized.removedFromItemId || "").trim(),
    removedFromItemName: String(normalized.removedFromItemName || "").trim(),
  };
};

const sanitizeGeneralQuoteFirestoreValue = (value, seen = new WeakSet()) => {
  if (value === undefined || typeof value === "function") return undefined;
  if (value === null) return null;
  if (typeof File !== "undefined" && value instanceof File) return undefined;
  if (typeof Blob !== "undefined" && value instanceof Blob) return undefined;
  if (typeof Number.isNaN === "function" && typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }
  if (value instanceof Date) return value.toISOString();
  if (typeof value?.toDate === "function") {
    const dateValue = value.toDate();
    return dateValue instanceof Date && !Number.isNaN(dateValue.getTime())
      ? dateValue.toISOString()
      : null;
  }
  if (typeof value !== "object") return value;
  if (seen.has(value)) return undefined;
  seen.add(value);

  if (Array.isArray(value)) {
    return value
      .map((entry) => sanitizeGeneralQuoteFirestoreValue(entry, seen))
      .filter((entry) => entry !== undefined);
  }

  return Object.entries(value).reduce((output, [key, entryValue]) => {
    const sanitized = sanitizeGeneralQuoteFirestoreValue(entryValue, seen);
    if (sanitized !== undefined) {
      output[key] = sanitized;
    }
    return output;
  }, {});
};

const sanitizeGeneralQuoteItemForFirestore = (item = {}) => {
  const images = normalizeGeneralQuoteItemImages(item).map(
    sanitizeGeneralQuoteImageMetadata
  );
  const removedImages = Array.isArray(item.removedImages)
    ? item.removedImages.map(sanitizeGeneralQuoteImageMetadata)
    : [];
  return sanitizeGeneralQuoteFirestoreValue(
    syncGeneralQuoteItemPrimaryImageFields({
      ...item,
      images,
      removedImages,
    })
  );
};

const sanitizeGeneralQuoteForFirestore = (quote = {}) => {
  const lineItems = Array.isArray(quote.lineItems)
    ? quote.lineItems
    : Array.isArray(quote.items)
    ? quote.items
    : [];
  const removedImages = Array.isArray(quote.removedImages)
    ? quote.removedImages.map(sanitizeGeneralQuoteImageMetadata)
    : [];
  return sanitizeGeneralQuoteFirestoreValue({
    ...quote,
    lineItems: lineItems.map(sanitizeGeneralQuoteItemForFirestore),
    removedImages,
    items: undefined,
  });
};

const sanitizeGeneralQuotePayload = (payload = {}) =>
  sanitizeGeneralQuoteFirestoreValue({
    ...payload,
    generalQuote: payload.generalQuote
      ? sanitizeGeneralQuoteForFirestore(payload.generalQuote)
      : payload.generalQuote,
    generalQuoteData: payload.generalQuoteData
      ? sanitizeGeneralQuoteForFirestore(payload.generalQuoteData)
      : payload.generalQuoteData,
  });

const safeDebugPreview = (payload = {}, maxLength = 6000) => {
  try {
    const safePayload = sanitizeGeneralQuotePayload(payload);
    const text = JSON.stringify(safePayload, null, 2);
    return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
  } catch (error) {
    return `Unable to build debug preview: ${error?.message || error}`;
  }
};

const estimateJsonSizeBytes = (payload = {}) => {
  try {
    return new Blob([JSON.stringify(sanitizeGeneralQuotePayload(payload))]).size;
  } catch (error) {
    try {
      return JSON.stringify(sanitizeGeneralQuotePayload(payload)).length;
    } catch {
      return 0;
    }
  }
};

const inspectGeneralQuotePayload = (value, path = "payload", seen = new WeakSet()) => {
  const result = {
    undefinedPaths: [],
    fileBlobPaths: [],
    invalidNumberPaths: [],
    functionPaths: [],
    circularPaths: [],
  };

  const visit = (entry, entryPath) => {
    if (typeof entry === "undefined") {
      result.undefinedPaths.push(entryPath);
      return;
    }
    if (typeof entry === "function") {
      result.functionPaths.push(entryPath);
      return;
    }
    if (typeof File !== "undefined" && entry instanceof File) {
      result.fileBlobPaths.push(entryPath);
      return;
    }
    if (typeof Blob !== "undefined" && entry instanceof Blob) {
      result.fileBlobPaths.push(entryPath);
      return;
    }
    if (typeof entry === "number" && !Number.isFinite(entry)) {
      result.invalidNumberPaths.push(entryPath);
      return;
    }
    if (!entry || typeof entry !== "object") return;
    if (seen.has(entry)) {
      result.circularPaths.push(entryPath);
      return;
    }
    seen.add(entry);

    if (Array.isArray(entry)) {
      entry.forEach((child, index) => visit(child, `${entryPath}[${index}]`));
      return;
    }

    Object.entries(entry).forEach(([key, child]) =>
      visit(child, `${entryPath}.${key}`)
    );
  };

  visit(value, path);

  return {
    ...result,
    hasUndefinedFields: result.undefinedPaths.length > 0,
    hasFileBlobObjects: result.fileBlobPaths.length > 0,
    hasInvalidNumbers: result.invalidNumberPaths.length > 0,
    hasFunctions: result.functionPaths.length > 0,
    hasCircularReferences: result.circularPaths.length > 0,
  };
};

const countGeneralQuoteImages = (quoteLike = {}) => {
  const generalQuote = quoteLike.generalQuote || quoteLike.generalQuoteData || quoteLike;
  const items = Array.isArray(generalQuote?.lineItems)
    ? generalQuote.lineItems
    : Array.isArray(generalQuote?.items)
    ? generalQuote.items
    : Array.isArray(quoteLike?.items)
    ? quoteLike.items
    : [];

  return items.reduce(
    (count, item) => count + normalizeGeneralQuoteItemImages(item).length,
    0
  );
};

const validateGeneralQuotePayloadForFirestore = (payload = {}) => {
  const diagnostics = inspectGeneralQuotePayload(payload);
  const invalidPaths = [
    ...diagnostics.undefinedPaths.map((path) => `undefined:${path}`),
    ...diagnostics.fileBlobPaths.map((path) => `file_or_blob:${path}`),
    ...diagnostics.invalidNumberPaths.map((path) => `invalid_number:${path}`),
    ...diagnostics.functionPaths.map((path) => `function:${path}`),
    ...diagnostics.circularPaths.map((path) => `circular:${path}`),
  ];

  const searchTextLength = String(payload.searchText || "").length;
  if (searchTextLength > SEARCH_TEXT_MAX_CHARS) {
    invalidPaths.push(`oversized_searchText:${searchTextLength}`);
  }

  const searchKeywordsCount = Array.isArray(payload.searchKeywords)
    ? payload.searchKeywords.length
    : 0;
  if (searchKeywordsCount > SEARCH_KEYWORDS_MAX) {
    invalidPaths.push(`oversized_searchKeywords:${searchKeywordsCount}`);
  }

  if (
    Array.isArray(payload.searchKeywords) &&
    payload.searchKeywords.some(
      (keyword) => String(keyword || "").length > SEARCH_KEYWORD_MAX_CHARS
    )
  ) {
    invalidPaths.push("oversized_searchKeyword");
  }

  return {
    valid: invalidPaths.length === 0,
    invalidPaths,
    diagnostics,
    searchTextLength,
    searchKeywordsCount,
  };
};

const readLocalDraftRecord = (key) => {
  if (!key || typeof window === "undefined" || !window.localStorage) return null;

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (error) {
    console.warn("Failed to read local draft:", error);
    return null;
  }
};

const writeLocalDraftRecord = (key, payload) => {
  if (!key || typeof window === "undefined" || !window.localStorage) return;

  try {
    window.localStorage.setItem(key, JSON.stringify(sanitizeLocalDraftValue(payload)));
  } catch (error) {
    console.warn("Failed to write local draft:", error);
  }
};

const clearLocalDraftRecord = (key) => {
  if (!key || typeof window === "undefined" || !window.localStorage) return;

  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    console.warn("Failed to clear local draft:", error);
  }
};

const saveLocalDraft = (key, payload) => writeLocalDraftRecord(key, payload);
const loadLocalDraft = (key) => readLocalDraftRecord(key);
const clearLocalDraft = (key) => clearLocalDraftRecord(key);
const getDraftMeta = (key) => {
  const record = readLocalDraftRecord(key);
  if (!record) return null;
  return {
    module: record.module || "",
    workspaceUid: record.workspaceUid || "",
    quoteId: record.entityId || record.metadata?.quoteId || "",
    savedAt: record.updatedAt || record.savedAt || "",
    appVersion: record.appVersion || "",
    payloadVersion: record.payloadVersion || record.version || LOCAL_DRAFT_VERSION,
    serverUpdatedAt: Number(record.serverUpdatedAt || 0),
  };
};

const uniqueValues = (values = []) =>
  Array.from(new Set((Array.isArray(values) ? values : []).filter(Boolean)));

const CRM_LEGACY_QUOTE_LINK_FIELDS = [
  "linkedQuoteId",
  "quoteId",
  "primaryQuoteId",
  "linkedQuoteIds",
  "linkedQuoteNo",
  "primaryQuoteNo",
  "quoteNo",
  "linkedQuoteType",
  "linkedQuoteValue",
  "linkedQuoteCustomerName",
  "linkedQuoteDate",
];

const CRM_CLOSED_DEAL_KEYWORDS = [
  "cancel",
  "lost",
  "installed",
  "completed",
  "closed",
];

const logQuoteToDealLinkDecision = (details = {}) => {
  if (!IS_DEV_UI_ENABLED) return;
  console.info("[FabriQuote quote-to-deal]", {
    quoteId: details.quoteId || "",
    customerId: details.customerId || "",
    matchedCustomerId: details.matchedCustomerId || "",
    existingOpenDealIds: Array.isArray(details.existingOpenDealIds)
      ? details.existingOpenDealIds
      : [],
    actionSelected: details.actionSelected || "",
    finalLinkedLeadId: details.finalLinkedLeadId || "",
  });
};

const stripLegacyCrmQuoteLinkFields = (record = {}) => {
  const nextRecord = { ...(record || {}) };
  CRM_LEGACY_QUOTE_LINK_FIELDS.forEach((field) => {
    delete nextRecord[field];
  });
  return nextRecord;
};

const createLegacyCrmQuoteLinkDeletePatch = () =>
  CRM_LEGACY_QUOTE_LINK_FIELDS.reduce((patch, field) => {
    patch[field] = deleteField();
    return patch;
  }, {});

const createLegacyQuoteCrmLinkDeletePatch = () =>
  CRM_LEGACY_QUOTE_LINK_FIELDS.reduce((patch, field) => {
    patch[`crm.${field}`] = deleteField();
    return patch;
  }, {});

const normalizeCrmLinkedQuoteEntry = (entry = {}, index = 0) => {
  const quoteId = String(
    entry?.quoteId ||
      entry?.id ||
      entry?.primaryQuoteId ||
      entry?.linkedQuoteId ||
      ""
  ).trim();
  const quoteNo = String(
    entry?.quoteNo ||
      entry?.quoteNumber ||
      entry?.primaryQuoteNo ||
      entry?.linkedQuoteNo ||
      ""
  ).trim();

  if (!quoteId && !quoteNo) return null;

  return {
    quoteId,
    quoteNo,
    type: String(
      entry?.type || entry?.quoteType || entry?.linkedQuoteType || entry?.pipelineType || ""
    ).trim(),
    value: Number(entry?.value ?? entry?.totalValue ?? entry?.linkedQuoteValue ?? 0) || 0,
    isPrimary: entry?.isPrimary === true || index === 0,
  };
};

const normalizeCrmLinkedQuotes = (leadLike = {}, overrides = {}) => {
  const hasLinkedQuotesOverride = Object.prototype.hasOwnProperty.call(
    overrides,
    "linkedQuotes"
  );
  const sourceLinkedQuotes = hasLinkedQuotesOverride
    ? overrides.linkedQuotes
    : leadLike.linkedQuotes;
  const entries = (Array.isArray(sourceLinkedQuotes) ? sourceLinkedQuotes : [])
    .map((entry, index) => normalizeCrmLinkedQuoteEntry(entry, index))
    .filter(Boolean);

  if (!hasLinkedQuotesOverride) {
    const legacyIds = uniqueValues([
      leadLike.primaryQuoteId,
      leadLike.linkedQuoteId,
      leadLike.quoteId,
      ...(Array.isArray(leadLike.linkedQuoteIds) ? leadLike.linkedQuoteIds : []),
    ]);
    const legacyQuoteNo = String(
      leadLike.primaryQuoteNo || leadLike.linkedQuoteNo || leadLike.quoteNo || ""
    ).trim();

    legacyIds.forEach((quoteId, index) => {
      entries.push(
        normalizeCrmLinkedQuoteEntry({
          quoteId,
          quoteNo: index === 0 ? legacyQuoteNo : "",
          type: leadLike.linkedQuoteType || leadLike.pipelineType || leadLike.quoteType || "",
          value: Number(leadLike.linkedQuoteValue || leadLike.finalValue || 0),
          isPrimary: quoteId === leadLike.primaryQuoteId || index === 0,
        })
      );
    });

    if (!legacyIds.length && legacyQuoteNo) {
      entries.push(
        normalizeCrmLinkedQuoteEntry({
          quoteNo: legacyQuoteNo,
          type: leadLike.linkedQuoteType || leadLike.pipelineType || leadLike.quoteType || "",
          value: Number(leadLike.linkedQuoteValue || leadLike.finalValue || 0),
          isPrimary: true,
        })
      );
    }
  }

  const seen = new Set();
  const deduped = entries.filter(Boolean).filter((entry) => {
    const key = entry.quoteId ? `id:${entry.quoteId}` : `no:${entry.quoteNo}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  const primaryIndex = deduped.findIndex((entry) => entry.isPrimary === true);
  const resolvedPrimaryIndex = primaryIndex >= 0 ? primaryIndex : 0;

  return deduped.map((entry, index) => ({
    quoteId: entry.quoteId,
    quoteNo: entry.quoteNo,
    type: entry.type,
    value: Number(entry.value || 0),
    isPrimary: index === resolvedPrimaryIndex,
  }));
};

const getPrimaryCrmLinkedQuote = (linkedQuotes = []) =>
  (Array.isArray(linkedQuotes) ? linkedQuotes : []).find((entry) => entry?.isPrimary) ||
  (Array.isArray(linkedQuotes) ? linkedQuotes[0] : null) ||
  null;

const QUOTE_DEAL_LINKS_COLLECTION = "quote_deal_links";
const ARCHIVED_DUPLICATE_STATUS = "archived_duplicate";

const sanitizeFirestoreDocIdSegment = (value = "") => {
  const sanitized = String(value || "")
    .trim()
    .replace(/[/.#[\]]+/g, "_")
    .replace(/[^a-zA-Z0-9_-]+/g, "_")
    .replace(/^_+|_+$/g, "");

  return sanitized || "unknown_quote";
};

const getQuoteDealLinkDocId = (quoteId = "") =>
  sanitizeFirestoreDocIdSegment(quoteId);

const buildQuoteCreatedLeadId = (quoteId = "") =>
  `quote_${sanitizeFirestoreDocIdSegment(quoteId)}`;

const getCrmLeadQuoteReferenceSummary = (leadLike = {}) => {
  const linkedQuotes = normalizeCrmLinkedQuotes(leadLike);
  const primaryLinkedQuote =
    getPrimaryCrmLinkedQuote(linkedQuotes) || leadLike.primaryLinkedQuote || {};
  const crm = leadLike.crm || {};
  const crmLinkedQuotes = normalizeCrmLinkedQuotes(crm);
  const allLinkedQuotes = [
    ...linkedQuotes,
    ...crmLinkedQuotes,
    leadLike.linkedQuote,
    leadLike.quote,
    primaryLinkedQuote,
    crm.linkedQuote,
    crm.quote,
  ].filter(Boolean);

  const quoteIds = uniqueValues([
    leadLike.quoteId,
    leadLike.linkedQuoteId,
    leadLike.primaryQuoteId,
    leadLike.id && leadLike.crmRecordType === "quote" ? leadLike.id : "",
    crm.quoteId,
    crm.linkedQuoteId,
    crm.primaryQuoteId,
    primaryLinkedQuote.quoteId,
    ...(Array.isArray(leadLike.linkedQuoteIds) ? leadLike.linkedQuoteIds : []),
    ...(Array.isArray(crm.linkedQuoteIds) ? crm.linkedQuoteIds : []),
    ...allLinkedQuotes.map((entry) => entry?.quoteId || entry?.id),
  ])
    .map((value) => String(value || "").trim())
    .filter(Boolean);

  const quoteNos = uniqueValues([
    leadLike.quoteNo,
    leadLike.linkedQuoteNo,
    leadLike.primaryQuoteNo,
    leadLike.primaryQuoteNumber,
    crm.quoteNo,
    crm.linkedQuoteNo,
    crm.primaryQuoteNo,
    primaryLinkedQuote.quoteNo,
    ...allLinkedQuotes.map(
      (entry) => entry?.quoteNo || entry?.quoteNumber || entry?.linkedQuoteNo
    ),
  ])
    .map((value) => String(value || "").trim())
    .filter(Boolean);

  return {
    linkedQuotes: normalizeCrmLinkedQuotes(
      {},
      { linkedQuotes: allLinkedQuotes.filter((entry) => entry?.quoteId || entry?.quoteNo) }
    ),
    quoteIds,
    quoteNos,
    normalizedQuoteNos: quoteNos.map((value) => normalizeSearchCompact(value)),
    primaryQuoteId: String(primaryLinkedQuote.quoteId || quoteIds[0] || "").trim(),
    primaryQuoteNo: String(primaryLinkedQuote.quoteNo || quoteNos[0] || "").trim(),
  };
};

const doesCrmLeadReferenceQuote = (leadLike = {}, quoteId = "", quoteNo = "") => {
  const referenceSummary = getCrmLeadQuoteReferenceSummary(leadLike);
  const targetQuoteId = String(quoteId || "").trim();
  const targetQuoteNo = String(quoteNo || "").trim();
  const normalizedTargetQuoteNo = normalizeSearchCompact(targetQuoteNo);

  return Boolean(
    (targetQuoteId && referenceSummary.quoteIds.includes(targetQuoteId)) ||
      (targetQuoteNo && referenceSummary.quoteNos.includes(targetQuoteNo)) ||
      (normalizedTargetQuoteNo &&
        referenceSummary.normalizedQuoteNos.includes(normalizedTargetQuoteNo))
  );
};

const getCanonicalDealKey = (dealLike = {}) => {
  const referenceSummary = getCrmLeadQuoteReferenceSummary(dealLike);
  const quoteId =
    referenceSummary.primaryQuoteId ||
    referenceSummary.quoteIds.find(Boolean) ||
    "";
  if (quoteId) return `quote:${quoteId}`;

  const quoteNo =
    referenceSummary.primaryQuoteNo ||
    referenceSummary.quoteNos.find(Boolean) ||
    "";
  const normalizedQuoteNo = normalizeSearchCompact(quoteNo);
  if (normalizedQuoteNo) return `quoteNo:${normalizedQuoteNo}`;

  return `lead:${String(dealLike.id || "unknown_lead").trim()}`;
};

const isArchivedDuplicateCrmLead = (leadLike = {}) =>
  leadLike?.isArchivedDuplicate === true ||
  String(leadLike?.status || leadLike?.dealStatus || "")
    .toLowerCase()
    .includes(ARCHIVED_DUPLICATE_STATUS);

const getCrmLeadSortMillis = (leadLike = {}, key = "created") => {
  const value =
    key === "updated"
      ? leadLike.updatedAt || leadLike.createdAt
      : leadLike.createdAt || leadLike.updatedAt;
  return Number(safeTimeMillis(value) || 0);
};

const chooseCanonicalQuoteDeal = (leads = [], preferredLeadId = "") => {
  const normalizedPreferredId = String(preferredLeadId || "").trim();
  const candidates = (Array.isArray(leads) ? leads : [])
    .filter((lead) => lead?.id)
    .filter(
      (lead, index, source) =>
        source.findIndex((item) => String(item.id) === String(lead.id)) === index
    );

  if (!candidates.length) return null;

  const preferred = normalizedPreferredId
    ? candidates.find((lead) => String(lead.id) === normalizedPreferredId)
    : null;
  if (preferred && !isArchivedDuplicateCrmLead(preferred)) return preferred;

  const activeCandidates = candidates.filter(
    (lead) => !isArchivedDuplicateCrmLead(lead)
  );
  const sortable = activeCandidates.length ? activeCandidates : candidates;

  return [...sortable].sort((a, b) => {
    const aRecordScore = a.crmRecordType === "quote" ? 1 : 0;
    const bRecordScore = b.crmRecordType === "quote" ? 1 : 0;
    if (aRecordScore !== bRecordScore) return aRecordScore - bRecordScore;

    const aClosed = a.isCrmClosed === true || a.isCrmCancelled === true ? 1 : 0;
    const bClosed = b.isCrmClosed === true || b.isCrmCancelled === true ? 1 : 0;
    if (aClosed !== bClosed) return aClosed - bClosed;

    const createdDelta = getCrmLeadSortMillis(a, "created") - getCrmLeadSortMillis(b, "created");
    if (createdDelta !== 0) return createdDelta;

    return String(a.id || "").localeCompare(String(b.id || ""));
  })[0];
};

const dedupeCrmDealsByQuote = (deals = []) => {
  const groups = new Map();
  (Array.isArray(deals) ? deals : []).forEach((deal) => {
    const key = getCanonicalDealKey(deal);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(deal);
  });

  return Array.from(groups.values()).map((group) => {
    if (group.length === 1) return group[0];
    return chooseCanonicalQuoteDeal(group) || group[0];
  });
};

const buildQuoteDealDuplicateGroups = (leads = []) => {
  const groups = new Map();
  (Array.isArray(leads) ? leads : [])
    .filter((lead) => lead?.id)
    .forEach((lead) => {
      const referenceSummary = getCrmLeadQuoteReferenceSummary(lead);
      const key = getCanonicalDealKey(lead);
      if (key.startsWith("lead:")) return;
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          quoteId: referenceSummary.primaryQuoteId || "",
          quoteNo: referenceSummary.primaryQuoteNo || "",
          leads: [],
        });
      }
      groups.get(key).leads.push(lead);
    });

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      canonicalLead: chooseCanonicalQuoteDeal(group.leads),
      duplicateLeads: group.leads.filter(
        (lead) => lead.id !== chooseCanonicalQuoteDeal(group.leads)?.id
      ),
    }))
    .filter((group) => group.leads.length > 1);
};

/**
 * Atomically gets the next quote number and increments it.
 * This prevents duplicates even with autosave / multiple tabs.
 */




// ================= FABRIC KEY NORMALIZATION =================
const normalizeFabricKey = (name) => {
  return String(name || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[^a-z0-9 ]/g, "");
};

const WORKSHOP_EMPTY_VALUE = "—";

const DEFAULT_HALF_PIECE_REUSE_SETTINGS = {
  heightMatchMode: "can_trim_down",
  heightToleranceInches: 1,
  allowLargerPieceToBeTrimmed: true,
};

const HALF_PIECE_EPSILON = 0.000001;

const formatRepeatNumber = (value) => {
  if (value === true || value === false) return "";
  const parsed =
    typeof value === "number"
      ? value
      : Number.parseFloat(String(value ?? "").replace(/[^\d.-]/g, ""));
  if (!Number.isFinite(parsed) || parsed <= 0) return "";
  return String(Number(parsed.toFixed(2)));
};

const hasOwnRepeatKey = (source = {}, keys = []) =>
  keys.some((key) => Object.prototype.hasOwnProperty.call(source, key));

const isRepeatToggleEnabled = (source = {}, keys = []) =>
  keys.some((key) => {
    const value = source?.[key];
    return value === true || value === "true" || value === 1 || value === "1";
  });

const formatRepeatSettings = (repeatSettings = {}) => {
  if (!repeatSettings || typeof repeatSettings !== "object" || Array.isArray(repeatSettings)) {
    return WORKSHOP_EMPTY_VALUE;
  }

  const parts = [];

  const verticalValue = formatRepeatNumber(
    repeatSettings.verticalRepeatValue ??
      repeatSettings.verticalRepeatInch ??
      repeatSettings.verticalRepeatIn ??
      repeatSettings.vr ??
      repeatSettings.verticalRepeat
  );
  const horizontalValue = formatRepeatNumber(
    repeatSettings.horizontalRepeatValue ??
      repeatSettings.horizontalRepeatInch ??
      repeatSettings.horizontalRepeatIn ??
      repeatSettings.hr ??
      repeatSettings.horizontalRepeat
  );

  const verticalToggleKeys = ["verticalEnabled", "verticalRepeatEnabled"];
  const horizontalToggleKeys = ["horizontalEnabled", "horizontalRepeatEnabled"];
  const verticalHasExplicitToggle = hasOwnRepeatKey(repeatSettings, verticalToggleKeys);
  const horizontalHasExplicitToggle = hasOwnRepeatKey(repeatSettings, horizontalToggleKeys);
  const verticalEnabled =
    isRepeatToggleEnabled(repeatSettings, verticalToggleKeys) ||
    (!verticalHasExplicitToggle &&
      (repeatSettings.verticalRepeat === true ||
        repeatSettings.verticalRepeat === "true" ||
        Boolean(verticalValue)));
  const horizontalEnabled =
    isRepeatToggleEnabled(repeatSettings, horizontalToggleKeys) ||
    (!horizontalHasExplicitToggle &&
      (repeatSettings.horizontalRepeat === true ||
        repeatSettings.horizontalRepeat === "true" ||
        Boolean(horizontalValue)));

  if (verticalEnabled && verticalValue > 0) {
    parts.push(`V: ${verticalValue} in`);
  }
  if (horizontalEnabled && horizontalValue > 0) {
    parts.push(`H: ${horizontalValue} in`);
  }
  return parts.length ? parts.join(", ") : WORKSHOP_EMPTY_VALUE;
};

const getRepeatSummaryDisplay = (summary = {}) => {
  const candidate =
    summary?.repeatSettings && typeof summary.repeatSettings === "object"
      ? summary.repeatSettings
      : summary?.repeatSummary;
  if (candidate && typeof candidate === "object") {
    return formatRepeatSettings(candidate);
  }
  const text = String(candidate ?? "").trim();
  if (!text || text === "[object Object]") return WORKSHOP_EMPTY_VALUE;
  return text;
};

const normalizeCurtainRepeatEntry = (repeat = {}) => {
  const source = repeat && typeof repeat === "object" ? repeat : {};
  return {
    verticalEnabled:
      source.verticalEnabled === true ||
      source.verticalEnabled === "true" ||
      source.verticalRepeatEnabled === true ||
      source.verticalRepeatEnabled === "true",
    verticalRepeatInch:
      source.verticalRepeatInch ??
      source.verticalRepeatIn ??
      source.verticalRepeat ??
      "",
    horizontalEnabled:
      source.horizontalEnabled === true ||
      source.horizontalEnabled === "true" ||
      source.horizontalRepeatEnabled === true ||
      source.horizontalRepeatEnabled === "true",
    horizontalRepeatInch:
      source.horizontalRepeatInch ??
      source.horizontalRepeatIn ??
      source.horizontalRepeat ??
      "",
  };
};

const normalizeCurtainRepeatSettingsMap = (settings = {}) => {
  if (!settings || typeof settings !== "object" || Array.isArray(settings)) return {};
  return Object.fromEntries(
    Object.entries(settings)
      .map(([rawKey, repeat]) => {
        const normalizedKey = normalizeFabricKey(rawKey);
        return normalizedKey
          ? [normalizedKey, normalizeCurtainRepeatEntry(repeat)]
          : null;
      })
      .filter(Boolean)
  );
};

const hasMeaningfulCurtainRepeatEntry = (repeat = {}) => {
  const normalized = normalizeCurtainRepeatEntry(repeat);
  return (
    normalized.verticalEnabled ||
    normalized.horizontalEnabled ||
    Number(normalized.verticalRepeatInch || 0) > 0 ||
    Number(normalized.horizontalRepeatInch || 0) > 0
  );
};

const getCurtainRepeatSignature = (repeat = {}) => {
  const normalized = normalizeCurtainRepeatEntry(repeat);
  const verticalValue = Number(normalized.verticalRepeatInch || 0);
  const horizontalValue = Number(normalized.horizontalRepeatInch || 0);
  return [
    normalized.verticalEnabled && verticalValue > 0 ? `v:${round2(verticalValue)}` : "v:off",
    normalized.horizontalEnabled && horizontalValue > 0 ? `h:${round2(horizontalValue)}` : "h:off",
  ].join("|");
};

const applyHorizontalRepeatToFabricRules = (
  fabricRules = {},
  repeatSettings = {}
) => {
  const horizontalEnabled =
    repeatSettings.horizontalEnabled === true ||
    repeatSettings.horizontalEnabled === "true";
  const horizontalRepeatInch = Number(repeatSettings.horizontalRepeatInch || 0);
  const fabricWidthIn = Math.max(1, Number(fabricRules.fabricWidthIn || 0));
  const baseUsableWidthIn = Math.max(
    1,
    Number(
      fabricRules.usableFabricWidthIn ??
        fabricWidthIn - Number(fabricRules.usableWidthDeductionIn || 0)
    )
  );

  if (!horizontalEnabled || horizontalRepeatInch <= 0 || baseUsableWidthIn <= 0) {
    return {
      ...fabricRules,
      horizontalRepeatAdjusted: false,
      repeatSettings: normalizeCurtainRepeatEntry(repeatSettings),
    };
  }

  const repeatAlignedUsableWidth =
    Math.floor(baseUsableWidthIn / horizontalRepeatInch) * horizontalRepeatInch;
  const adjustedUsableWidthIn =
    repeatAlignedUsableWidth > 0
      ? Math.min(baseUsableWidthIn, repeatAlignedUsableWidth)
      : baseUsableWidthIn;

  return {
    ...fabricRules,
    usableFabricWidthIn: round2(adjustedUsableWidthIn),
    usableWidthDeductionIn: round2(Math.max(0, fabricWidthIn - adjustedUsableWidthIn)),
    horizontalRepeatAdjusted: adjustedUsableWidthIn < baseUsableWidthIn,
    baseUsableFabricWidthIn: round2(baseUsableWidthIn),
    horizontalRepeatInch,
    repeatSettings: normalizeCurtainRepeatEntry(repeatSettings),
  };
};

const sanitizeEditableText = (value) => {
  const text = String(value ?? "").trim();
  const lowered = text.toLowerCase();
  if (["—", "-", "n/a", "na"].includes(text) || ["n/a", "na"].includes(lowered)) {
    return "";
  }
  return text;
};

const normalizeWorkshopText = (value) => {
  if (value == null) return "";
  const text = sanitizeEditableText(value);
  if (!text) return "";

  const lowered = text.toLowerCase();
  if (["n/a", "na", "not selected", "fabric pending", "undefined", "null"].includes(lowered)) {
    return "";
  }

  return text;
};

const isWorkshopResolvedLabel = (value) => {
  const text = String(value ?? "").trim();
  const normalized = text.toLowerCase();
  if (!text) return false;
  if (text === WORKSHOP_EMPTY_VALUE || text === "\u2014" || text === "\u2013" || text === "-") {
    return false;
  }
  if (["n/a", "na", "not selected", "undefined", "null"].includes(normalized)) {
    return false;
  }
  return true;
};

const getRawEditableTextValue = (value) => {
  if (value == null) return "";
  if (typeof value === "object") {
    return getWorkshopFieldLabel(value);
  }
  const text = String(value);
  const trimmed = text.trim();
  const lowered = trimmed.toLowerCase();
  if (["â€”", "\u2014", "\u2013", "-", "n/a", "na"].includes(trimmed)) return "";
  if (["n/a", "na", "undefined", "null"].includes(lowered)) return "";
  return text;
};

const getInputValue = (value) => (value == null ? "" : String(value));

const isSrPricingRuleDisplayText = (value = "") => {
  const text = String(value ?? "").trim();
  if (!text) return false;
  return /\bSR\b.*\bTO\b/i.test(text) || /\bto\b/i.test(text) || /[,;]/.test(text);
};

const getSpecificRowSrNumber = (row = {}) => {
  const srNumber = normalizeSrNumber(row?.srNumber || "");
  if (!srNumber) return "";
  if (/TO/i.test(srNumber) || /[,;]/.test(srNumber)) return "";
  if (isSrPricingRuleDisplayText(row?.srLabel || "")) return "";
  return srNumber;
};

const normalizeFabricBrand = (brand = {}, fallbackId = "") => {
  const name = sanitizeEditableText(
    brand.name || brand.brandName || brand.label || ""
  );
  const id = sanitizeEditableText(brand.id || brand.brandId || fallbackId || "");
  return {
    id,
    name,
    nameLower: name.toLowerCase(),
    supplier: sanitizeEditableText(brand.supplier || brand.vendor || ""),
    notes: sanitizeEditableText(brand.notes || ""),
    archived: brand.archived === true,
    updatedAt: brand.updatedAt || null,
    createdAt: brand.createdAt || null,
  };
};

const normalizeFabricBrands = (brands = []) =>
  (Array.isArray(brands) ? brands : [])
    .map((brand, index) => normalizeFabricBrand(brand, brand.id || `brand-${index}`))
    .filter((brand) => brand.id && brand.name && !brand.archived)
    .sort((a, b) => a.name.localeCompare(b.name));

const normalizeFabricSummaryMeta = (meta = {}) => {
  if (!meta || typeof meta !== "object" || Array.isArray(meta)) return {};
  return Object.fromEntries(
    Object.entries(meta)
      .map(([rawKey, rawValue]) => {
        const normalizedKey = normalizeFabricKey(rawKey);
        if (!normalizedKey) return null;
        const value =
          rawValue && typeof rawValue === "object" && !Array.isArray(rawValue)
            ? rawValue
            : {};
        const normalized = {
          fabricCode: sanitizeEditableText(value.fabricCode || value.code || rawKey),
          brandId: sanitizeEditableText(value.brandId || value.fabricBrandId || ""),
          brandName: sanitizeEditableText(value.brandName || value.fabricBrandName || ""),
          catalogueId: sanitizeEditableText(value.catalogueId || ""),
          catalogueName: sanitizeEditableText(value.catalogueName || ""),
          catalogueKey: sanitizeEditableText(value.catalogueKey || ""),
          repeatSettings: normalizeCurtainRepeatEntry(value.repeatSettings || value.repeat || {}),
          notes: sanitizeEditableText(value.notes || ""),
        };
        return [normalizedKey, normalized];
      })
      .filter(Boolean)
  );
};

const normalizeFabricSummaryMetaWithRepeatSettings = (
  meta = {},
  repeatSettingsMap = {}
) => {
  const normalizedMeta = normalizeFabricSummaryMeta(meta);
  const normalizedRepeatSettings = normalizeCurtainRepeatSettingsMap(repeatSettingsMap);
  return Object.fromEntries(
    Array.from(
      new Set([
        ...Object.keys(normalizedMeta),
        ...Object.keys(normalizedRepeatSettings),
      ])
    ).map((fabricKey) => {
      const settingsRepeat = normalizedRepeatSettings[fabricKey];
      const metaRepeat = normalizedMeta[fabricKey]?.repeatSettings;
      const resolvedRepeat = hasMeaningfulCurtainRepeatEntry(settingsRepeat)
        ? settingsRepeat
        : hasMeaningfulCurtainRepeatEntry(metaRepeat)
        ? metaRepeat
        : normalizeCurtainRepeatEntry(settingsRepeat || metaRepeat || {});
      return [
        fabricKey,
        {
          ...(normalizedMeta[fabricKey] || { fabricCode: fabricKey }),
          repeatSettings: resolvedRepeat,
        },
      ];
    })
  );
};

const mergeRepeatSettingsFromFabricSummaryMeta = (
  repeatSettingsMap = {},
  meta = {}
) => {
  const normalizedRepeatSettings = normalizeCurtainRepeatSettingsMap(repeatSettingsMap);
  const normalizedMeta = normalizeFabricSummaryMeta(meta);
  Object.entries(normalizedMeta).forEach(([fabricKey, value]) => {
    const metaRepeat = normalizeCurtainRepeatEntry(value.repeatSettings || {});
    const hasMetaRepeat =
      metaRepeat.verticalEnabled ||
      metaRepeat.horizontalEnabled ||
      Number(metaRepeat.verticalRepeatInch || 0) > 0 ||
      Number(metaRepeat.horizontalRepeatInch || 0) > 0;
    if (
      hasMetaRepeat &&
      !hasMeaningfulCurtainRepeatEntry(normalizedRepeatSettings[fabricKey])
    ) {
      normalizedRepeatSettings[fabricKey] = metaRepeat;
    }
  });
  return normalizedRepeatSettings;
};

const getWorkshopObjectLabel = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return "";

  const primary =
    normalizeWorkshopText(value.name) ||
    normalizeWorkshopText(value.label) ||
    normalizeWorkshopText(value.title) ||
    normalizeWorkshopText(value.displayName) ||
    normalizeWorkshopText(value.fabricName) ||
    normalizeWorkshopText(value.fabricLabel) ||
    normalizeWorkshopText(value.materialName) ||
    normalizeWorkshopText(value.fabricKey);
  const detail =
    normalizeWorkshopText(value.code) ||
    normalizeWorkshopText(value.sku) ||
    normalizeWorkshopText(value.itemCode) ||
    normalizeWorkshopText(value.shade) ||
    normalizeWorkshopText(value.color);

  if (primary && detail && !primary.toLowerCase().includes(detail.toLowerCase())) {
    return `${primary} (${detail})`;
  }

  return primary || detail;
};

const getWorkshopFieldLabel = (value) => {
  if (value == null) return "";
  if (typeof value === "object") {
    if (Array.isArray(value)) {
      return value.map((entry) => getWorkshopFieldLabel(entry)).find(Boolean) || "";
    }
    return getWorkshopObjectLabel(value);
  }
  return normalizeWorkshopText(value);
};

const getWorkshopRowStableId = (row = {}) =>
  normalizeWorkshopText(
    row?.id ||
      row?.rowId ||
      row?.clientRowId ||
      row?.sourceRowId ||
      row?.sourceId ||
      row?.originalRowId
  );

const getWorkshopRowItemLabel = (row = {}) =>
  normalizeWorkshopText(row?.item || row?.name || row?.windowName || row?.label);

const getWorkshopRowProductLabel = (row = {}) =>
  normalizeWorkshopText(row?.productType || row?.product || row?.type);

const normalizeWorkshopProductLabel = (value) =>
  normalizeWorkshopText(value).toLowerCase().replace(/\s+/g, " ");

const getWorkshopRowWidth = (row = {}) =>
  Number(row?.width ?? row?.windowWidth ?? row?.w ?? row?.measurement?.width ?? 0);

const getWorkshopRowHeight = (row = {}) =>
  Number(row?.height ?? row?.drop ?? row?.windowHeight ?? row?.h ?? row?.measurement?.height ?? 0);

const closeWorkshopNumber = (a, b, tolerance = 0.05) => {
  const nA = Number(a || 0);
  const nB = Number(b || 0);
  return Math.abs(nA - nB) <= tolerance;
};

const isSameWorkshopRowIdentity = (row = {}, candidate = {}) => {
  if (!row || !candidate) return false;

  const rowId = getWorkshopRowStableId(row);
  const candidateId = getWorkshopRowStableId(candidate);
  if (rowId && candidateId) return rowId === candidateId;

  const item = getWorkshopRowItemLabel(row).toLowerCase();
  const candidateItem = getWorkshopRowItemLabel(candidate).toLowerCase();
  const product = normalizeWorkshopProductLabel(getWorkshopRowProductLabel(row));
  const candidateProduct = normalizeWorkshopProductLabel(getWorkshopRowProductLabel(candidate));
  const widthMatches = closeWorkshopNumber(getWorkshopRowWidth(row), getWorkshopRowWidth(candidate));
  const heightMatches = closeWorkshopNumber(getWorkshopRowHeight(row), getWorkshopRowHeight(candidate));

  return Boolean(
    item &&
      candidateItem &&
      item === candidateItem &&
      product &&
      candidateProduct &&
      product === candidateProduct &&
      widthMatches &&
      heightMatches
  );
};

const CURTAIN_EDITABLE_FABRIC_FIELDS = [
  "fabric",
  "fabricName",
  "fabricLabel",
  "selectedFabric",
  "fabricDetails",
  "material",
  "materialCode",
  "materialName",
  "colorCode",
  "itemFabric",
  "fabricCode",
  "fabricSku",
  "fabricKey",
];

const sanitizeEditableFabricValue = (value) => {
  if (value == null || typeof value === "string" || typeof value === "number") {
    return sanitizeEditableText(value);
  }
  if (Array.isArray(value)) {
    return value.map((entry) => sanitizeEditableFabricValue(entry));
  }
  if (typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, entryValue]) => [
        key,
        sanitizeEditableFabricValue(entryValue),
      ])
    );
  }
  return value;
};

const curtainRowSupportsLining = (row = {}, calc = {}) => {
  const productConfig = row?.productConfigSnapshot || null;
  const productName =
    row?.productName ||
    row?.productType ||
    row?.product ||
    calc?.productName ||
    "";
  return productSupportsLining(productConfig, {
    ...row,
    productName,
    productType: productName,
    category:
      row?.category ||
      (calc?.isCurtain ? "Curtain" : calc?.isRoman ? "Roman Blind" : ""),
    calculationType: calc?.calculationType || row?.calculationType,
    usesFabric: Boolean(
      row?.usesFabric === true || calc?.isCurtain === true || calc?.isRoman === true
    ),
    supportsLining: row?.supportsLining,
    isCurtain: calc?.isCurtain === true,
    isRoman: calc?.isRoman === true,
  });
};

const sanitizeCurtainRowEditableTextFields = (row = {}) => {
  const nextRow = { ...(row || {}) };
  CURTAIN_EDITABLE_FABRIC_FIELDS.forEach((fieldName) => {
    if (Object.prototype.hasOwnProperty.call(nextRow, fieldName)) {
      nextRow[fieldName] = sanitizeEditableFabricValue(nextRow[fieldName]);
    }
  });
  nextRow.lining = normalizeCurtainRowLining(nextRow.lining || {}, {
    calculatedMeters: Number(nextRow.customerBillableFabricMeters || nextRow.fabricMeters || 0),
    sqft: Number(
      nextRow.billableSqft ??
        nextRow.adjSqft ??
        ((Number(nextRow.width || 0) * Number(nextRow.height || 0)) / 144)
    ),
    applicable: curtainRowSupportsLining(nextRow),
  });
  return nextRow;
};

const CURTAIN_RATE_AMOUNT_FIELDS = [
  "sqftRate",
  "ratePerSqft",
  "rate",
  "rateAmount",
  "manualAmount",
  "ratePerFeet",
  "runningFeetRate",
  "length",
  "lengthUnit",
  "ratePerLength",
  "unit",
  "unitRate",
  "ratePerPiece",
  "pieceRate",
  "amount",
  "lineAmount",
];

const normalizeRowProductConfigForPricing = (row = {}, productConfig = null) => {
  if (productConfig) return normalizeProductConfig(productConfig);
  if (row?.productConfigSnapshot) return normalizeProductConfig(row.productConfigSnapshot);
  if (row?.calculationType || row?.requiredFields || row?.pricingFields || row?.displayFields) {
    return normalizeProductConfig({
      id: row.productId || row.productName || row.productType || row.product || "row-product",
      name: row.productName || row.productType || row.product || "Row Product",
      calculationType: row.calculationType || "sqft_only",
      requiredFields: row.requiredFields || {},
      pricingFields: row.pricingFields || {},
      displayFields: row.displayFields || {},
      sqftRules: row.sqftRules || {},
      runningFeetRules: row.runningFeetRules || {},
      quantityRules: row.quantityRules || {},
      fabricRules: row.fabricRules || {},
      stitchingRules: row.stitchingRules || {},
      trackRules: row.trackRules || {},
    });
  }
  return null;
};

const shouldProductUseRateAmountField = (row = {}, productConfig = null) => {
  const config = normalizeRowProductConfigForPricing(row, productConfig);
  if (config) {
    const calculationType = config.calculationType;
    if (calculationType === "fabric_meter" && config.pricingFields?.rateAffectsPrice !== true) {
      return false;
    }
    if (
      calculationType === "sqft_only" ||
      calculationType === "sqft_plus_fabric" ||
      calculationType === "running_length_rate" ||
      calculationType === "quantity_rate" ||
      calculationType === "manual_amount"
    ) {
      return true;
    }
    if (
      config.sqftRules?.enabled ||
      config.runningFeetRules?.enabled ||
      config.quantityRules?.enabled ||
      config.requiredFields?.ratePerSqft ||
      config.requiredFields?.ratePerFeet ||
      config.requiredFields?.ratePerLength ||
      config.requiredFields?.unitRate ||
      config.requiredFields?.manualAmount ||
      config.requiredFields?.quantity
    ) {
      return true;
    }
    return config.displayFields?.showRate === true && config.pricingFields?.rateAffectsPrice === true;
  }

  const calculationType = String(row?.calculationType || "").trim();
  if (calculationType === "fabric_meter") return false;
  if (
    ["sqft_only", "sqft_plus_fabric", "running_length_rate", "quantity_rate", "manual_amount"].includes(
      calculationType
    )
  ) {
    return true;
  }

  const productName = normalizeProductName(row?.productType || row?.productName || row?.product || "");
  if (productName.includes("curtain")) return false;
  if (productName.includes("blind") || productName.includes("roman")) return true;
  return true;
};

const sanitizeRowPricingFieldsByProduct = (row = {}, productConfig = null) => {
  const nextRow = { ...(row || {}) };
  if (shouldProductUseRateAmountField(nextRow, productConfig)) return nextRow;

  CURTAIN_RATE_AMOUNT_FIELDS.forEach((fieldName) => {
    if (Object.prototype.hasOwnProperty.call(nextRow, fieldName)) {
      nextRow[fieldName] = "";
    }
  });

  return nextRow;
};

const sanitizeCurtainRowsEditableTextFields = (sourceRows = []) =>
  (Array.isArray(sourceRows) ? sourceRows : []).map((row) =>
    sanitizeRowPricingFieldsByProduct(sanitizeCurtainRowEditableTextFields(row))
  );

const CURTAIN_LINING_TYPES = [
  { type: "satin", label: "Satin" },
  { type: "dimout", label: "Dimout" },
  { type: "blackout", label: "Blackout" },
  { type: "custom", label: "Custom" },
];

const DEFAULT_CURTAIN_LINING = {
  enabled: false,
  type: "satin",
  label: "Satin",
  customLabel: "",
  chargeMethod: "meter",
  ratePerMeter: "",
  ratePerSqft: "",
  fabricCode: "",
  brandId: "",
  brandName: "",
  metersMode: "same_as_curtain",
  calculatedMeters: 0,
  meters: 0,
  manualMeters: null,
  overrideMeters: "",
  billableMeters: 0,
  sqft: 0,
  includedInSqftRate: false,
  notes: "",
  amount: 0,
};

const getCurtainLiningTypeMeta = (type = "") =>
  CURTAIN_LINING_TYPES.find((entry) => entry.type === type) ||
  CURTAIN_LINING_TYPES[0];

const parseNonNegativeNumberInput = (value, fallback = 0) => {
  if (value === "" || value == null) return fallback;
  const numeric = Number(String(value).replace(/,/g, "").trim());
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(0, numeric);
};

const getCurtainLiningLabel = (lining = {}) => {
  const type = String(lining?.type || "satin").trim().toLowerCase();
  if (type === "custom") {
    return sanitizeEditableText(lining?.customLabel || lining?.label) || "Custom lining";
  }
  return getCurtainLiningTypeMeta(type).label;
};

const normalizeCurtainRowLining = (
  lining = {},
  { calculatedMeters = 0, sqft = 0, applicable = true } = {}
) => {
  const safeCalculatedMeters = round2(Math.max(0, Number(calculatedMeters || 0)));
  const safeSqft = round2(Math.max(0, Number(sqft || 0)));
  const rawType = String(lining?.type || "satin").trim().toLowerCase();
  const type = CURTAIN_LINING_TYPES.some((entry) => entry.type === rawType)
    ? rawType
    : "satin";
  const chargeMethod = lining?.chargeMethod === "sqft" ? "sqft" : "meter";
  const metersMode =
    lining?.metersMode === "custom_override" || lining?.manualMeters != null
      ? "custom_override"
      : "same_as_curtain";
  const rawMeterRate =
    lining?.ratePerMeter ?? lining?.rate ?? lining?.defaultRatePerMeter ?? "";
  const rawSqftRate =
    lining?.ratePerSqft ?? lining?.sqftRate ?? lining?.defaultRatePerSqft ?? "";
  const rawOverride =
    lining?.manualMeters ?? lining?.overrideMeters ?? lining?.customMeters ?? "";
  const ratePerMeter = parseNonNegativeNumberInput(rawMeterRate, 0);
  const ratePerSqft = parseNonNegativeNumberInput(rawSqftRate, 0);
  const overrideMeters = parseNonNegativeNumberInput(rawOverride, 0);
  const enabled = applicable && lining?.enabled === true;
  const billableMeters =
    enabled && metersMode === "custom_override"
      ? round2(overrideMeters)
      : enabled
      ? safeCalculatedMeters
      : 0;

  const amount = enabled
    ? chargeMethod === "sqft"
      ? round2(safeSqft * ratePerSqft)
      : round2(billableMeters * ratePerMeter)
    : 0;

  const normalized = {
    ...DEFAULT_CURTAIN_LINING,
    ...lining,
    enabled,
    type,
    label: getCurtainLiningLabel({ ...lining, type }),
    customLabel: sanitizeEditableText(lining?.customLabel || ""),
    chargeMethod,
    ratePerMeter: rawMeterRate === null || rawMeterRate === undefined ? "" : rawMeterRate,
    ratePerSqft: rawSqftRate === null || rawSqftRate === undefined ? "" : rawSqftRate,
    fabricCode: sanitizeEditableText(lining?.fabricCode || lining?.fabric || ""),
    brandId: sanitizeEditableText(lining?.brandId || lining?.fabricBrandId || ""),
    brandName: sanitizeEditableText(lining?.brandName || lining?.fabricBrandName || ""),
    metersMode,
    calculatedMeters: safeCalculatedMeters,
    meters: billableMeters,
    manualMeters: metersMode === "custom_override" ? billableMeters : null,
    overrideMeters: rawOverride === null || rawOverride === undefined ? "" : rawOverride,
    billableMeters,
    sqft: safeSqft,
    includedInSqftRate: lining?.includedInSqftRate === true,
    notes: sanitizeEditableText(lining?.notes || ""),
    amount,
  };

  return normalized;
};

const calculateCurtainRowLining = (
  row = {},
  billableMeters = 0,
  applicable = true,
  options = {}
) =>
  normalizeCurtainRowLining(row?.lining || {}, {
    calculatedMeters: billableMeters,
    sqft: options.sqft ?? options.adjSqft ?? row?.adjSqft ?? 0,
    applicable,
  });

const getEditableFabricValue = (row = {}, originalRow = {}) => {
  const hasValidOriginalRow =
    originalRow &&
    Object.keys(originalRow).length > 0 &&
    isSameWorkshopRowIdentity(row, originalRow);
  const sources = [
    row,
    hasValidOriginalRow ? originalRow : null,
  ].filter(Boolean);

  for (const source of sources) {
    const catalogueName = sanitizeEditableText(source?.catalogueName || "");
    const srNumber = getSpecificRowSrNumber(source);
    if (catalogueName && srNumber) {
      return buildFabricCatalogueDisplayCode({ catalogueName, srNumber });
    }
    if (
      catalogueName &&
      (isSrPricingRuleDisplayText(source?.fabricCodeRaw || "") ||
        isSrPricingRuleDisplayText(source?.fabric || "") ||
        isSrPricingRuleDisplayText(source?.fabricCode || ""))
    ) {
      return catalogueName;
    }

    for (const fieldName of CURTAIN_EDITABLE_FABRIC_FIELDS) {
      const value = getRawEditableTextValue(source?.[fieldName]);
      if (value !== "") return value;
    }
  }

  return "";
};

const getDisplayFabricLabel = (row = {}, originalRow = {}) =>
  normalizeWorkshopText(getEditableFabricValue(row, originalRow)) || WORKSHOP_EMPTY_VALUE;

const getCurtainRowFabricLabel = (row = {}, originalRow = {}) =>
  getDisplayFabricLabel(row, originalRow);

const getCurtainRowFabricKey = (row = {}, originalRow = {}) => {
  const label = getEditableFabricValue(row, originalRow);
  return label ? normalizeFabricKey(label) : "";
};

const hslToRgbArray = (hue = 0, saturation = 0, lightness = 0) => {
  const h = (((Number(hue) || 0) % 360) + 360) % 360;
  const s = Math.max(0, Math.min(100, Number(saturation) || 0)) / 100;
  const l = Math.max(0, Math.min(100, Number(lightness) || 0)) / 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;

  if (h < 60) {
    r = c;
    g = x;
  } else if (h < 120) {
    r = x;
    g = c;
  } else if (h < 180) {
    g = c;
    b = x;
  } else if (h < 240) {
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }

  return [r, g, b].map((channel) =>
    Math.round((channel + m) * 255)
  );
};

const getWorkshopPdfFabricTintByIndex = (index = 0) =>
  hslToRgbArray((Number(index) || 0) * 137.508, 68, 92);

const getWorkshopFabricColorKey = (row = {}, fallbackLabel = "") => {
  const srNumber =
    getSpecificRowSrNumber(row) ||
    sanitizeEditableText(row?.srNumber || row?.srNo || row?.sr || "");
  const parts = [
    sanitizeEditableText(
      row?.brandId ||
        row?.fabricBrandId ||
        row?.brandName ||
        row?.fabricBrandName ||
        ""
    ),
    sanitizeEditableText(
      row?.catalogueId ||
        row?.catalogueKey ||
        row?.catalogueName ||
        row?.collectionName ||
        ""
    ),
    srNumber ? `sr ${srNumber}` : "",
    sanitizeEditableText(
      row?.fabricCodeRaw ||
        row?.fabricCode ||
        row?.materialCode ||
        row?.displayName ||
        row?.fabricLabel ||
        getEditableFabricValue(row) ||
        fallbackLabel
    ),
  ].filter(Boolean);

  return (
    normalizeFabricKey(parts.join(" ")) ||
    getCurtainRowFabricKey(row) ||
    normalizeFabricKey(fallbackLabel)
  );
};

const findCurtainRowStitchConfig = (row = {}, stitchingStyles = {}) => {
  const styles = stitchingStyles && typeof stitchingStyles === "object" ? stitchingStyles : {};
  const entries = Object.entries(styles);
  if (!entries.length) return null;

  const candidates = [
    row?.stitchingStyleId,
    row?.stitchingId,
    row?.stitchId,
    typeof row?.stitchingStyle === "string" ? row.stitchingStyle : "",
    row?.stitchingStyle?.id,
    row?.selectedStitch?.id,
    row?.selectedStitching?.id,
    row?.stitchingStyle && typeof row.stitchingStyle !== "string"
      ? row.stitchingStyle
      : "",
    row?.stitch,
    row?.stitchType,
    row?.stitching,
    row?.stitchingType,
  ]
    .map((value) => getWorkshopFieldLabel(value))
    .filter(Boolean);

  for (const candidate of candidates) {
    if (styles[candidate]) {
      return { key: candidate, config: styles[candidate] };
    }

    const normalizedCandidate = normalizeWorkshopText(candidate).toLowerCase();
    const matched = entries.find(([key, config]) => {
      const possibleLabels = [
        key,
        config?.id,
        config?.name,
        config?.label,
        config?.title,
        config?.displayName,
      ]
        .map((value) => normalizeWorkshopText(value).toLowerCase())
        .filter(Boolean);
      return possibleLabels.includes(normalizedCandidate);
    });
    if (matched) {
      return { key: matched[0], config: matched[1] };
    }
  }

  return null;
};

const getCurtainRowStitchLabel = (row = {}, stitchingStyles = {}, productConfig = null) => {
  const selectedStyleProbe = {
    stitchingStyleId: row?.stitchingStyleId,
    stitchingId: row?.stitchingId,
    stitchId: row?.stitchId,
    stitchingStyle: row?.stitchingStyle,
  };
  const hasSelectedStyle = [
    selectedStyleProbe.stitchingStyleId,
    selectedStyleProbe.stitchingId,
    selectedStyleProbe.stitchId,
    selectedStyleProbe.stitchingStyle,
  ].some((value) => isWorkshopResolvedLabel(getWorkshopFieldLabel(value)));

  if (hasSelectedStyle) {
    const selectedStyleConfig = findCurtainRowStitchConfig(
      selectedStyleProbe,
      stitchingStyles
    );
    if (selectedStyleConfig) {
      const selectedStyleLabel =
        getWorkshopFieldLabel(selectedStyleConfig.config) ||
        getWorkshopFieldLabel(selectedStyleConfig.key);
      if (isWorkshopResolvedLabel(selectedStyleLabel)) return selectedStyleLabel;
    }

    const selectedStyleText = getWorkshopFieldLabel(row?.stitchingStyle);
    if (isWorkshopResolvedLabel(selectedStyleText)) return selectedStyleText;
  }

  const directLabel = [
    row?.stitchLabel,
    row?.stitchingLabel,
    row?.stitch,
    row?.stitchType,
    row?.stitching,
    row?.stitchingType,
    row?.selectedStitch,
    row?.selectedStitching,
    row?.stitchingStyle && typeof row.stitchingStyle === "object"
      ? row.stitchingStyle
      : "",
  ]
    .map((value) => getWorkshopFieldLabel(value))
    .find((label) => isWorkshopResolvedLabel(label)) || "";

  if (directLabel) return directLabel;

  const styleConfig = findCurtainRowStitchConfig(row, stitchingStyles);
  if (styleConfig) {
    const styleLabel =
      getWorkshopFieldLabel(styleConfig.config) ||
      getWorkshopFieldLabel(styleConfig.key);
    return isWorkshopResolvedLabel(styleLabel) ? styleLabel : WORKSHOP_EMPTY_VALUE;
  }

  const styleText = getWorkshopFieldLabel(row?.stitchingStyle);
  if (isWorkshopResolvedLabel(styleText)) return styleText;

  const defaultStyleId = productConfig?.stitchingRules?.defaultStitchingStyleId || "";
  if (defaultStyleId) {
    const defaultConfig = findCurtainRowStitchConfig(
      { stitchingStyleId: defaultStyleId },
      stitchingStyles
    );
    if (defaultConfig) {
      const defaultLabel =
        getWorkshopFieldLabel(defaultConfig.config) || getWorkshopFieldLabel(defaultConfig.key);
      return isWorkshopResolvedLabel(defaultLabel) ? defaultLabel : WORKSHOP_EMPTY_VALUE;
    }
  }

  return WORKSHOP_EMPTY_VALUE;
};

const getCurtainRowWorkshopStitchLabel = (
  row = {},
  stitchingStyles = {},
  productConfig = null
) => {
  const stitchConfigMatch = findCurtainRowStitchConfig(row, stitchingStyles);
  const resolvedStitch = getCurtainRowStitchLabel(row, stitchingStyles, productConfig);
  const resolvedTrack =
    getCurtainRowDisplay(row?.trackType || row?.trackName || row?.track, "") ||
    getCurtainRowDisplay(stitchConfigMatch?.config?.trackType, "");
  const hasStitch = isWorkshopResolvedLabel(resolvedStitch);
  const hasTrack = Boolean(resolvedTrack);
  const stitchIncludesTrack =
    hasStitch &&
    hasTrack &&
    normalizeWorkshopText(resolvedStitch)
      .toLowerCase()
      .includes(normalizeWorkshopText(resolvedTrack).toLowerCase());

  if (hasStitch && hasTrack && !stitchIncludesTrack) {
    return `${resolvedStitch} - ${resolvedTrack}`;
  }
  if (hasStitch) return resolvedStitch;
  return resolvedTrack || WORKSHOP_EMPTY_VALUE;
};

const buildWorkshopStitchTraceRow = (
  row = {},
  stitchingStyles = {},
  productConfig = null,
  extra = {}
) => ({
  ...extra,
  id: row?.id || row?.rowId || row?.sourceRowId || "",
  item: row?.item || row?.name || "",
  product: row?.productType || row?.productName || row?.product || "",
  stitch: row?.stitch,
  stitchType: row?.stitchType,
  stitching: row?.stitching,
  stitchingType: row?.stitchingType,
  stitchingStyle: row?.stitchingStyle,
  stitchingStyleId: row?.stitchingStyleId,
  selectedStitch: row?.selectedStitch,
  selectedStitching: row?.selectedStitching,
  stitchLabel: row?.stitchLabel,
  stitchingLabel: row?.stitchingLabel,
  track: row?.track,
  trackType: row?.trackType,
  trackName: row?.trackName,
  resolved: getCurtainRowWorkshopStitchLabel(row, stitchingStyles, productConfig),
});

const getCurtainRowDisplay = (value, fallback = WORKSHOP_EMPTY_VALUE) =>
  normalizeWorkshopText(value) || fallback;

const WORKSHOP_PRICING_KEY_TERMS = [
  "mrp",
  "rate",
  "price",
  "amount",
  "total",
  "subtotal",
  "discount",
  "gst",
  "tax",
  "taxable",
  "value",
  "grandtotal",
  "finaltotal",
  "dealvalue",
  "quotevalue",
  "expectedvalue",
  "finalvalue",
  "cost",
  "billing",
  "payment",
  "advance",
  "balance",
  "margin",
];

const WORKSHOP_PRICING_TEXT_PATTERN =
  /(rs\.|₹|\brate\b|\bmrp\b|\bamount\b|\btotal\b|\bsubtotal\b|\bdiscount\b|\bgst\b|\btaxable\b|\bgrand\s*total\b|\bfinal\s*total\b|\bquotation\s*value\b|\bdeal\s*value\b)/i;

const isWorkshopPricingKey = (key = "") => {
  const normalized = String(key || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
  return WORKSHOP_PRICING_KEY_TERMS.some((term) => normalized.includes(term));
};

const sanitizeWorkshopRow = (value, seen = new WeakSet()) => {
  if (value == null || typeof value !== "object") return value;
  if (seen.has(value)) return undefined;
  seen.add(value);

  if (Array.isArray(value)) {
    return value
      .map((entry) => sanitizeWorkshopRow(entry, seen))
      .filter((entry) => entry !== undefined);
  }

  const output = {};
  Object.entries(value).forEach(([key, entryValue]) => {
    if (isWorkshopPricingKey(key)) return;
    const sanitized = sanitizeWorkshopRow(entryValue, seen);
    if (sanitized !== undefined) {
      output[key] = sanitized;
    }
  });
  return output;
};

const assertNoPricingInWorkshopPdfPayload = (payload, context = {}) => {
  if (!isDevRuntime) return [];

  const leaks = [];
  const visit = (value, path = "", rowContext = {}) => {
    if (value == null) return;
    if (typeof value === "string" || typeof value === "number") {
      if (typeof value === "string" && WORKSHOP_PRICING_TEXT_PATTERN.test(value)) {
        leaks.push({ path, value, ...rowContext });
      }
      return;
    }
    if (typeof value !== "object") return;

    if (Array.isArray(value)) {
      value.forEach((entry, index) =>
        visit(entry, `${path}[${index}]`, rowContext)
      );
      return;
    }

    const nextRowContext = {
      product: value.product || value.productType || rowContext.product || context.product || "",
      rowItem: value.item || value.name || rowContext.rowItem || context.rowItem || "",
    };

    Object.entries(value).forEach(([key, entryValue]) => {
      const keyPath = path ? `${path}.${key}` : key;
      if (isWorkshopPricingKey(key)) {
        leaks.push({ path: keyPath, value: entryValue, ...nextRowContext });
      }
      visit(entryValue, keyPath, nextRowContext);
    });
  };

  visit(payload, context.label || "workshopPayload", context);

  if (leaks.length) {
    console.error("WORKSHOP_PDF_PRICING_LEAK_DETECTED", {
      ...context,
      leaks,
    });
  }

  return leaks;
};

const assertNoPricingInWorkshopPdfText = (values, context = {}) => {
  if (!isDevRuntime) return [];
  const flattened = [];
  const visit = (value, path = "") => {
    if (value == null) return;
    if (Array.isArray(value)) {
      value.forEach((entry, index) => visit(entry, `${path}[${index}]`));
      return;
    }
    if (typeof value === "object") {
      Object.entries(value).forEach(([key, entryValue]) =>
        visit(entryValue, path ? `${path}.${key}` : key)
      );
      return;
    }
    const text = String(value);
    if (WORKSHOP_PRICING_TEXT_PATTERN.test(text)) {
      flattened.push({ path, value: text });
    }
  };
  visit(values, context.label || "workshopText");
  if (flattened.length) {
    console.error("WORKSHOP_PDF_TEXT_PRICING_LEAK", {
      ...context,
      leaks: flattened,
    });
  }
  return flattened;
};

const findOriginalWorkshopRow = (row = {}, rowIndex = -1, sourceRows = []) => {
  const candidates = Array.isArray(sourceRows) ? sourceRows : [];
  const idCandidates = [
    row?.sourceRowId,
    row?.rowId,
    row?.id,
    row?.sourceId,
    row?.originalRowId,
  ]
    .map((value) => normalizeWorkshopText(value))
    .filter(Boolean);

  if (idCandidates.length) {
    const byId = candidates.find((candidate) =>
      [
        candidate?.sourceRowId,
        candidate?.rowId,
        candidate?.id,
        candidate?.sourceId,
        candidate?.originalRowId,
      ]
        .map((value) => normalizeWorkshopText(value))
        .some((value) => value && idCandidates.includes(value))
    );
    if (byId) return byId;
  }

  const sourceIndex = Number(row?.sourceIndex ?? row?.originalIndex ?? row?.rowIndex);
  if (
    Number.isInteger(sourceIndex) &&
    candidates[sourceIndex] &&
    isSameWorkshopRowIdentity(row, candidates[sourceIndex])
  ) {
    if (IS_DEV_UI_ENABLED && !idCandidates.length) {
      console.warn("Workshop export used guarded sourceIndex fallback", {
        sourceIndex,
        row,
        candidate: candidates[sourceIndex],
      });
    }
    return candidates[sourceIndex];
  }

  if (
    Number.isInteger(rowIndex) &&
    candidates[rowIndex] &&
    isSameWorkshopRowIdentity(row, candidates[rowIndex])
  ) {
    if (IS_DEV_UI_ENABLED && !idCandidates.length) {
      console.warn("Workshop export used guarded rowIndex fallback", {
        rowIndex,
        row,
        candidate: candidates[rowIndex],
      });
    }
    return candidates[rowIndex];
  }

  const byExactIdentity = candidates.find((candidate) =>
    isSameWorkshopRowIdentity(row, candidate)
  );
  if (byExactIdentity) return byExactIdentity;

  return {};
};

const createWorkshopSourceRow = (
  row = {},
  rowIndex = 0,
  sourceRows = [],
  options = {}
) => {
  const originalRow = findOriginalWorkshopRow(row, rowIndex, sourceRows);
  const resolvedFabricLabel = getCurtainRowFabricLabel(row, originalRow);
  const stitchingStyles = options.stitchingStyles || {};
  const productConfig = options.productConfig || null;
  const resolvedStitchLabel = getCurtainRowWorkshopStitchLabel(
    { ...originalRow, ...row },
    stitchingStyles,
    productConfig
  );
  const resolvedStitchFields = isWorkshopResolvedLabel(resolvedStitchLabel)
    ? {
        stitch: resolvedStitchLabel,
        stitchLabel: resolvedStitchLabel,
        stitchingLabel: resolvedStitchLabel,
        resolvedStitchLabel,
      }
    : {};
  const rowId =
    normalizeWorkshopText(row?.id) ||
    normalizeWorkshopText(row?.rowId) ||
    normalizeWorkshopText(row?.clientRowId) ||
    normalizeWorkshopText(row?.sourceRowId) ||
    normalizeWorkshopText(originalRow?.id) ||
    normalizeWorkshopText(originalRow?.rowId) ||
    normalizeWorkshopText(originalRow?.clientRowId) ||
    `workshop-row-${rowIndex + 1}`;

  return {
    ...originalRow,
    ...row,
    sourceIndex: Number.isInteger(Number(row?.sourceIndex ?? row?.originalIndex))
      ? Number(row?.sourceIndex ?? row?.originalIndex)
      : rowIndex,
    id: normalizeWorkshopText(row?.id) || rowId,
    rowId: normalizeWorkshopText(row?.rowId) || rowId,
    clientRowId: normalizeWorkshopText(row?.clientRowId) || rowId,
    sourceRowId: rowId,
    originalRowSnapshot: originalRow && Object.keys(originalRow).length ? originalRow : null,
    fabric: getWorkshopFieldLabel(row?.fabric) || getWorkshopFieldLabel(originalRow?.fabric) || resolvedFabricLabel,
    fabricName:
      getWorkshopFieldLabel(row?.fabricName) ||
      getWorkshopFieldLabel(originalRow?.fabricName) ||
      resolvedFabricLabel,
    fabricLabel:
      getWorkshopFieldLabel(row?.fabricLabel) ||
      getWorkshopFieldLabel(originalRow?.fabricLabel) ||
      resolvedFabricLabel,
    resolvedFabricLabel,
    ...resolvedStitchFields,
  };
};

const createCurtainQuoteRowId = () => createClientId("curtain-row");

const DEFAULT_CURTAIN_PRODUCT_CONFIG =
  DEFAULT_PRODUCT_CONFIGS.find((config) => config.name === "Curtains") ||
  DEFAULT_PRODUCT_CONFIGS[0];

const withCurtainRowId = (row = {}, rowIndex = 0) => {
  const rowId =
    getWorkshopRowStableId(row) ||
    normalizeWorkshopText(row?.sourceRowId) ||
    createCurtainQuoteRowId();

  return {
    ...row,
    id: normalizeWorkshopText(row?.id) || rowId,
    rowId: normalizeWorkshopText(row?.rowId) || rowId,
    clientRowId: normalizeWorkshopText(row?.clientRowId) || rowId,
    sourceRowId: normalizeWorkshopText(row?.sourceRowId) || rowId,
    sourceIndex: Number.isInteger(Number(row?.sourceIndex ?? row?.originalIndex))
      ? Number(row?.sourceIndex ?? row?.originalIndex)
      : rowIndex,
  };
};

const QUOTE_RAW_FIELD_MUTATION_SOURCES = {
  USER_INPUT: "USER_INPUT",
  PRODUCT_CHANGE: "PRODUCT_CHANGE",
  PRODUCT_DEFAULT_APPLY: "PRODUCT_DEFAULT_APPLY",
  AUTOSAVE_RESTORE: "AUTOSAVE_RESTORE",
  AUTOSAVE_SAVE: "AUTOSAVE_SAVE",
  SERVER_LOAD: "SERVER_LOAD",
  SAVE_READBACK: "SAVE_READBACK",
  CALCULATION_RECOMPUTE: "CALCULATION_RECOMPUTE",
  CUSTOMER_LOOKUP: "CUSTOMER_LOOKUP",
  FABRIC_LOOKUP: "FABRIC_LOOKUP",
  PDF_EXPORT: "PDF_EXPORT",
  WORKSHOP_EXPORT: "WORKSHOP_EXPORT",
  HALF_PIECE_CALC: "HALF_PIECE_CALC",
  BULK_REPLACE: "BULK_REPLACE",
  UNKNOWN: "UNKNOWN",
};

const QUOTE_PROTECTED_RAW_FIELDS = [
  "item",
  "product",
  "productType",
  "productName",
  "fabric",
  "fabricName",
  "material",
  "materialCode",
  "fabricWidthIn",
  "width",
  "height",
  "mrp",
  "mrpPerMeter",
  "discount",
  "sqftRate",
  "rate",
  "ratePerFeet",
  "ratePerLength",
  "unitRate",
  "manualAmount",
  "amount",
  "qty",
  "quantity",
  "unit",
  "length",
  "lengthUnit",
  "description",
  "modelCode",
  "stitchingStyle",
  "stitchingStyleId",
  "customStitchRate",
  "gstRate",
  "lining",
];

const QUOTE_ALLOWED_RAW_MUTATION_SOURCES = new Set([
  QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT,
  QUOTE_RAW_FIELD_MUTATION_SOURCES.PRODUCT_CHANGE,
  QUOTE_RAW_FIELD_MUTATION_SOURCES.PRODUCT_DEFAULT_APPLY,
  QUOTE_RAW_FIELD_MUTATION_SOURCES.AUTOSAVE_RESTORE,
  QUOTE_RAW_FIELD_MUTATION_SOURCES.SERVER_LOAD,
  QUOTE_RAW_FIELD_MUTATION_SOURCES.BULK_REPLACE,
]);

const isQuoteMutationAuditDevMode = () =>
  IS_DEV_UI_ENABLED;

const isPermittedDecimalInput = (value) =>
  value === "" || /^-?\d*(?:\.\d*)?$/.test(String(value));

const roundRawDecimalString = (value, decimals = 4) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "";
  const factor = 10 ** decimals;
  return String(Math.round((numericValue + Number.EPSILON) * factor) / factor);
};

const getDimensionInputValueForUnit = (storedValue, selectedUnit = "inch") => {
  if (storedValue === "" || storedValue == null) return "";
  if (selectedUnit !== "cm") return String(storedValue);
  const numericValue = Number(storedValue);
  if (!Number.isFinite(numericValue)) return String(storedValue);
  return roundRawDecimalString(numericValue * 2.54, 2);
};

const getStoredDimensionValueFromInput = (inputValue, selectedUnit = "inch") => {
  if (inputValue === "") return "";
  if (!isPermittedDecimalInput(inputValue)) return null;
  if (selectedUnit !== "cm") return String(inputValue);
  return roundRawDecimalString(Number(inputValue) / 2.54, 4);
};

const getCurtainRowsSignature = (sourceRows = []) =>
  (Array.isArray(sourceRows) ? sourceRows : [])
    .map((row, index) => {
      const stableRow = withCurtainRowId(row || {}, index);
      return [
        getWorkshopRowStableId(stableRow),
        stableRow.item ?? "",
        stableRow.productType ?? stableRow.product ?? "",
        stableRow.fabric ?? stableRow.fabricName ?? "",
        stableRow.width ?? "",
        stableRow.height ?? "",
        stableRow.mrp ?? "",
        stableRow.sqftRate ?? stableRow.rate ?? stableRow.unitRate ?? stableRow.manualAmount ?? "",
        stableRow.stitchingStyle ?? "",
      ].join("|");
    })
    .join("||");

const auditQuotationRowMutation = (previousRows = [], nextRows = [], source = "UNKNOWN") => {
  const previousById = new Map(
    (Array.isArray(previousRows) ? previousRows : []).map((row, index) => {
      const stableRow = withCurtainRowId(row || {}, index);
      return [getWorkshopRowStableId(stableRow), stableRow];
    })
  );
  const events = [];
  const timestamp = new Date().toISOString();
  const stack = isQuoteMutationAuditDevMode() ? new Error().stack : "";

  (Array.isArray(nextRows) ? nextRows : []).forEach((row, index) => {
    const nextRow = withCurtainRowId(row || {}, index);
    const rowId = getWorkshopRowStableId(nextRow);
    const previousRow = previousById.get(rowId);
    if (!previousRow) return;

    QUOTE_PROTECTED_RAW_FIELDS.forEach((field) => {
      const previousValue = previousRow?.[field];
      const nextValue = nextRow?.[field];
      if (Object.is(previousValue, nextValue)) return;
      const suspicious = !QUOTE_ALLOWED_RAW_MUTATION_SOURCES.has(source);
      const event = {
        source,
        rowId,
        item: nextRow.item || previousRow.item || "",
        field,
        previousValue,
        nextValue,
        suspicious,
        stack,
        timestamp,
      };
      events.push(event);

      if (isQuoteMutationAuditDevMode()) {
        console.warn(suspicious ? "UNAUTHORIZED_RAW_FIELD_CHANGE" : "QUOTE_FIELD_MUTATION", event);
      }
    });
  });

  if (isQuoteMutationAuditDevMode() && typeof window !== "undefined") {
    window.__FQ_LAST_QUOTE_FIELD_MUTATIONS__ = [
      ...(window.__FQ_LAST_QUOTE_FIELD_MUTATIONS__ || []),
      ...events,
    ].slice(-100);
  }

  return events;
};

const createDefaultCurtainQuoteRow = (item = "W1") =>
  withCurtainRowId({
    lineType: "measurement",
    item,
    productId: DEFAULT_CURTAIN_PRODUCT_CONFIG?.id || "",
    productName: DEFAULT_CURTAIN_PRODUCT_CONFIG?.name || "Curtains",
    product: DEFAULT_CURTAIN_PRODUCT_CONFIG?.name || "Curtains",
    productType: "Curtains",
    calculationType: DEFAULT_CURTAIN_PRODUCT_CONFIG?.calculationType || "fabric_meter",
    fabric: "",
    fabricCodeRaw: "",
    brandId: "",
    brandName: "",
    fabricBrandId: "",
    fabricBrandName: "",
    brandSource: "none",
    catalogueId: "",
    catalogueName: "",
    catalogueKey: "",
    srNumber: "",
    srLabel: "",
    srPriceRuleId: "",
    srPriceRuleType: "none",
    srPriceRuleLabel: "",
    rrpPerMeter: null,
    rrpSource: "unmatched",
    fabricWidthIn: 54,
    width: "",
    height: "",
    mrp: "",
    discount: "",
    sqftRate: "",
    stitchingStyle: "",
    customStitchRate: "",
    lining: { ...DEFAULT_CURTAIN_LINING },
  });

const DEFAULT_ACCESSORY_PRODUCT_CONFIG =
  DEFAULT_PRODUCT_CONFIGS.find((config) => isAccessoryProductConfig(config)) || null;

const createDefaultCurtainAccessoryRow = (item = "Accessory") =>
  withCurtainRowId({
    lineType: "accessory",
    item,
    productId: DEFAULT_ACCESSORY_PRODUCT_CONFIG?.id || "",
    productName: DEFAULT_ACCESSORY_PRODUCT_CONFIG?.name || "Accessory",
    product: DEFAULT_ACCESSORY_PRODUCT_CONFIG?.name || "Accessory",
    productType: DEFAULT_ACCESSORY_PRODUCT_CONFIG?.name || "Accessory",
    category: DEFAULT_ACCESSORY_PRODUCT_CONFIG?.category || "accessory",
    calculationType: DEFAULT_ACCESSORY_PRODUCT_CONFIG?.calculationType || "quantity_rate",
    productConfigSnapshot: DEFAULT_ACCESSORY_PRODUCT_CONFIG
      ? buildProductConfigSnapshot(DEFAULT_ACCESSORY_PRODUCT_CONFIG)
      : null,
    description: "",
    modelCode: "",
    qty: 1,
    quantity: 1,
    unit: DEFAULT_ACCESSORY_PRODUCT_CONFIG?.quantityRules?.defaultUnit || "pcs",
    unitRate: "",
    length: "",
    lengthUnit: DEFAULT_ACCESSORY_PRODUCT_CONFIG?.runningFeetRules?.defaultUnit || "ft",
    ratePerLength: "",
    manualAmount: "",
    discount: "",
    gstRate: "",
    notes: "",
    fabric: "",
    fabricMeters: 0,
    orderedFabricMeters: 0,
    cutFabricMeters: 0,
    lining: { ...DEFAULT_CURTAIN_LINING },
  });

const isCurtainAccessoryRowStatic = (row = {}) =>
  row?.lineType === "accessory" ||
  isAccessoryCalculationType(row?.calculationType || row?.productConfigSnapshot?.calculationType);

const isDeletedWorkshopExportRow = (row = {}) =>
  row?.deleted === true ||
  row?.isDeleted === true ||
  row?.archived === true ||
  row?.isArchived === true ||
  row?.active === false ||
  row?.isActive === false;

const hasMeaningfulWorkshopExportData = (row = {}) => {
  const product = getWorkshopRowProductLabel(row);
  const fabric = getCurtainRowFabricLabel(row);
  const hasFabric = Boolean(fabric && fabric !== WORKSHOP_EMPTY_VALUE);
  const widthValue = Number(row?.width || 0);
  const heightValue = Number(row?.height || 0);
  const hasMeasurements =
    widthValue > 0 ||
    heightValue > 0;
  const hasQuantity =
    Number(row?.quantity || row?.qty || 0) > 0;
  const hasNotes = Boolean(
    normalizeWorkshopText(row?.remarks || row?.remark || row?.notes || row?.note)
  );

  return Boolean(product && (hasFabric || hasMeasurements || hasQuantity || hasNotes));
};

const normalizeCurtainRowsForExport = (sourceRows = []) =>
  (Array.isArray(sourceRows) ? sourceRows : [])
    .map((row, index) => withCurtainRowId(row, index))
    .filter((row) => !isDeletedWorkshopExportRow(row))
    .filter(hasMeaningfulWorkshopExportData)
    .map((row, index) => createWorkshopSourceRow(row, index, []));

const serializeCurtainRowsForDraft = (sourceRows = []) =>
  (Array.isArray(sourceRows) ? sourceRows : []).map((row, index) =>
    sanitizeLocalDraftValue(
      sanitizeCurtainRowEditableTextFields(withCurtainRowId(row || {}, index))
    )
  );

const getActiveCurtainRowsForExport = (sourceRows = []) =>
  normalizeCurtainRowsForExport(sourceRows);

const summarizeWorkshopExportRow = (row = {}, index = 0) => ({
  index: index + 1,
  id: getWorkshopRowStableId(row),
  sourceRowId: row?.sourceRowId || "",
  sourceIndex: row?.sourceIndex ?? "",
  item: getWorkshopRowItemLabel(row),
  product: getWorkshopRowProductLabel(row),
  fabric: getDisplayFabricLabel(row),
  fabricWidth: Number(row?.fabricWidthIn || 0),
  width: Number(row?.width || 0),
  height: Number(row?.height || 0),
  stitch: getCurtainRowStitchLabel(row, {}, null),
  fabricMeters: Number(row?.fabricMeters || row?.fabricMeter || row?.meters || 0),
  deleted: Boolean(isDeletedWorkshopExportRow(row)),
});

const countWorkshopRowsByProduct = (rowsToCount = []) =>
  rowsToCount.reduce((acc, row) => {
    const key = getWorkshopRowProductLabel(row) || "Unknown";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

const validateWorkshopRowsMatchActiveRows = (activeRows = [], workshopRows = []) => {
  const activeSummary = activeRows.map(summarizeWorkshopExportRow);
  const workshopSummary = workshopRows.map(summarizeWorkshopExportRow);
  const mismatches = [];

  const activeCounts = countWorkshopRowsByProduct(activeRows);
  const workshopCounts = countWorkshopRowsByProduct(workshopRows);
  const productKeys = new Set([
    ...Object.keys(activeCounts),
    ...Object.keys(workshopCounts),
  ]);

  productKeys.forEach((product) => {
    if (Number(activeCounts[product] || 0) !== Number(workshopCounts[product] || 0)) {
      mismatches.push({
        type: "product-count",
        product,
        activeCount: activeCounts[product] || 0,
        workshopCount: workshopCounts[product] || 0,
      });
    }
  });

  activeRows.forEach((activeRow, index) => {
    const workshopRow = workshopRows[index];
    if (!workshopRow) {
      mismatches.push({
        type: "missing-workshop-row",
        active: activeSummary[index],
      });
      return;
    }

    const activeFabric = getCurtainRowFabricLabel(activeRow);
    const workshopFabric = getCurtainRowFabricLabel(workshopRow);
    const rowMismatch =
      !isSameWorkshopRowIdentity(activeRow, workshopRow) ||
      activeFabric !== workshopFabric ||
      !closeWorkshopNumber(activeRow?.fabricWidthIn, workshopRow?.fabricWidthIn) ||
      !closeWorkshopNumber(activeRow?.width, workshopRow?.width) ||
      !closeWorkshopNumber(activeRow?.height, workshopRow?.height);

    if (rowMismatch) {
      mismatches.push({
        type: "row-mismatch",
        active: activeSummary[index],
        workshop: workshopSummary[index],
      });
    }
  });

  workshopRows.slice(activeRows.length).forEach((workshopRow, offset) => {
    mismatches.push({
      type: "extra-workshop-row",
      workshop: workshopSummary[activeRows.length + offset],
      row: workshopRow,
    });
  });

  return {
    ok: mismatches.length === 0,
    activeCounts,
    workshopCounts,
    mismatches,
  };
};

const toMillis = (value) => {
  if (!value) return 0;

  if (typeof value?.toMillis === "function") {
    return value.toMillis();
  }

  if (typeof value?.toDate === "function") {
    const dateValue = value.toDate();
    return dateValue instanceof Date && !Number.isNaN(dateValue.getTime())
      ? dateValue.getTime()
      : 0;
  }

  if (value instanceof Date) {
    return !Number.isNaN(value.getTime()) ? value.getTime() : 0;
  }

  if (typeof value === "string" || typeof value === "number") {
    const dateValue = new Date(value);
    return !Number.isNaN(dateValue.getTime()) ? dateValue.getTime() : 0;
  }

  return 0;
};

const getCreatedMillis = (quote) =>
  toMillis(quote?.createdAt) ||
  toMillis(quote?.date) ||
  toMillis(quote?.quoteDate) ||
  toMillis(quote?.generalQuote?.quoteDate) ||
  toMillis(quote?.generalQuoteData?.quoteDate) ||
  toMillis(quote?.crm?.createdAt) ||
  toMillis(quote?.updatedAt) ||
  0;

const getUpdatedMillis = (quote) =>
  toMillis(quote?.updatedAt) ||
  toMillis(quote?.modifiedAt) ||
  toMillis(quote?.editedAt) ||
  toMillis(quote?.crm?.updatedAt) ||
  getCreatedMillis(quote);

const getQuoteValue = (quote) => {
  const candidates = [
    quote?.grandTotal,
    quote?.total,
    quote?.finalTotal,
    quote?.totalAmount,
    quote?.amount,
    quote?.quoteValue,
    quote?.linkedQuoteValue,
    quote?.finalAmount,
    quote?.approvedValue,
    quote?.subtotal,
    quote?.crm?.finalValue,
    quote?.crm?.expectedValue,
  ];
  return (
    candidates
      .map((value) => Number(value))
      .find((value) => Number.isFinite(value) && value > 0) || 0
  );
};

const resolveQuoteType = (quote) => {
  const explicitType = String(
    quote?.quoteType ||
      quote?.type ||
      quote?.quotationType ||
      quote?.crm?.quoteType ||
      quote?.crm?.pipelineType ||
      quote?.pipelineType ||
      ""
  )
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  if (
    explicitType.includes("general") ||
    explicitType.includes("generalquotation")
  ) {
    return GENERAL_QUOTE_TYPE;
  }
  if (explicitType.includes("curtain")) return CURTAIN_QUOTE_TYPE;

  const embeddedGeneralQuote =
    quote?.generalQuote && typeof quote.generalQuote === "object"
      ? quote.generalQuote
      : quote?.generalQuoteData && typeof quote.generalQuoteData === "object"
      ? quote.generalQuoteData
      : null;

  if (
    embeddedGeneralQuote &&
    ((Array.isArray(embeddedGeneralQuote.lineItems) &&
      embeddedGeneralQuote.lineItems.length > 0) ||
      String(embeddedGeneralQuote.title || "").trim() ||
      embeddedGeneralQuote.quoteFrom ||
      embeddedGeneralQuote.quoteFor)
  ) {
    return GENERAL_QUOTE_TYPE;
  }

  if (
    (Array.isArray(quote?.items) && quote.items.length > 0) ||
    (Array.isArray(quote?.lineItems) && quote.lineItems.length > 0)
  ) {
    return GENERAL_QUOTE_TYPE;
  }

  return CURTAIN_QUOTE_TYPE;
};

const ROUTE_TO_TAB = {
  "/dashboard": { tab: "dashboard" },
  "/todays-tasks": { tab: "todayTasks" },
  "/crm": { tab: "crm" },
  "/customer-ledger": { tab: "customerLedger" },
  "/execution-details": { tab: "executionSpecs" },
  "/execution-templates": { tab: "executionTemplates" },
  "/general-quotes": { tab: "quote", quoteSubTab: GENERAL_QUOTE_TYPE },
  "/curtain-quotes": { tab: "quote", quoteSubTab: CURTAIN_QUOTE_TYPE },
  "/stitching-rates": { tab: "stitching" },
  "/products-calculations": { tab: "products" },
  "/roll-optimizer": { tab: "fco" },
  "/customer-pdf": { tab: "quote" },
  "/workshop-pdf": { tab: "quote" },
  "/team-access": { tab: "teamAccess" },
  "/account-security": { tab: "accountSecurity" },
  "/maintenance": { tab: "maintenance" },
  "/saved-quotes": { tab: "saved" },
  "/settings": { tab: "settings" },
};

const getRouteForTab = (tab, quoteSubTab) => {
  if (tab === "dashboard" || tab === "dashboardList") return "/dashboard";
  if (tab === "todayTasks") return "/todays-tasks";
  if (tab === "crm") return "/crm";
  if (tab === "customerLedger") return "/customer-ledger";
  if (tab === "executionSpecs") return "/execution-details";
  if (tab === "executionTemplates") return "/execution-templates";
  if (tab === "quote") {
    return quoteSubTab === GENERAL_QUOTE_TYPE ? "/general-quotes" : "/curtain-quotes";
  }
  if (tab === "stitching") return "/stitching-rates";
  if (tab === "products") return "/products-calculations";
  if (tab === "fco") return "/roll-optimizer";
  if (tab === "teamAccess") return "/team-access";
  if (tab === "accountSecurity") return "/account-security";
  if (tab === "maintenance") return "/maintenance";
  if (tab === "saved") return "/saved-quotes";
  if (tab === "settings") return "/settings";
  return "/dashboard";
};

const getEmbeddedGeneralQuote = (quote) => {
  if (quote?.generalQuote && typeof quote.generalQuote === "object") {
    return {
      ...quote.generalQuote,
      discountType:
        quote.generalQuote.discountType ?? quote.discountType ?? quote.discountMode,
      discountValue:
        quote.generalQuote.discountValue ??
        quote.discountValue ??
        quote.summaryDiscountValue,
      discountAmount: quote.generalQuote.discountAmount ?? quote.discountAmount,
      discountPercent: quote.generalQuote.discountPercent ?? quote.discountPercent,
      summaryDiscountMode:
        quote.generalQuote.summaryDiscountMode ?? quote.summaryDiscountMode,
      summaryDiscountValue:
        quote.generalQuote.summaryDiscountValue ?? quote.summaryDiscountValue,
    };
  }

  if (quote?.generalQuoteData && typeof quote.generalQuoteData === "object") {
    return {
      ...quote.generalQuoteData,
      discountType:
        quote.generalQuoteData.discountType ?? quote.discountType ?? quote.discountMode,
      discountValue:
        quote.generalQuoteData.discountValue ??
        quote.discountValue ??
        quote.summaryDiscountValue,
      discountAmount: quote.generalQuoteData.discountAmount ?? quote.discountAmount,
      discountPercent: quote.generalQuoteData.discountPercent ?? quote.discountPercent,
      summaryDiscountMode:
        quote.generalQuoteData.summaryDiscountMode ?? quote.summaryDiscountMode,
      summaryDiscountValue:
        quote.generalQuoteData.summaryDiscountValue ?? quote.summaryDiscountValue,
    };
  }

  if (Array.isArray(quote?.lineItems) || Array.isArray(quote?.items)) {
    return {
      ...quote,
      lineItems: Array.isArray(quote?.lineItems)
        ? quote.lineItems
        : quote.items,
    };
  }

  return {};
};

const getConfirmedMillis = (quote) => {
  if (!isConfirmedQuoteRecord(quote)) {
    return 0;
  }

  return (
    toMillis(quote?.confirmedAt) ||
    toMillis(quote?.crm?.confirmedAt) ||
    toMillis(quote?.modifiedAt) ||
    toMillis(quote?.updatedAt) ||
    getCreatedMillis(quote)
  );
};

const getInstallationMillis = (quote) =>
  toMillis(quote?.installationDate) ||
  toMillis(quote?.crm?.installationDate) ||
  toMillis(quote?.scheduledInstallationDate) ||
  toMillis(quote?.siteInstallDate) ||
  toMillis(quote?.installDate) ||
  0;

const getQuoteCustomerLabel = (quote) =>
  String(
    quote?.customerName ||
      quote?.customer?.name ||
      quote?.customer ||
      quote?.crm?.customerName ||
      quote?.crm?.contact?.name ||
      quote?.quoteFor?.name ||
      quote?.quoteFor?.company ||
      quote?.generalQuote?.quoteFor?.company ||
      quote?.generalQuote?.quoteFor?.name ||
      quote?.generalQuoteData?.quoteFor?.company ||
      quote?.generalQuoteData?.quoteFor?.name ||
      "Unnamed Customer"
  ).trim() || "Unnamed Customer";

const getQuoteStageValue = (quote) =>
  String(
    quote?.workStage ||
      quote?.workStatus ||
      quote?.crm?.stageId ||
      quote?.crm?.stageName ||
      quote?.stage ||
      quote?.status ||
      "quote_given"
  )
    .trim()
    .toLowerCase();

const isDeletedQuoteRecord = (quote) =>
  quote?.deleted === true || Boolean(quote?.deletedAt);

const isCancelledQuoteRecord = (quote) =>
  quote?.cancelled === true ||
  ["cancelled", "canceled", "lost"].includes(getQuoteStageValue(quote)) ||
  ["cancelled", "canceled", "lost"].includes(
    String(quote?.crm?.status || quote?.status || "").toLowerCase()
  );

const REVENUE_STAGE_SET = new Set([
  "materials_ordered",
  "in_production",
  "ready",
  "ready_for_installation",
  "installed",
  "completed",
]);

const isRevenueQuote = (quote) => {
  if (!quote) return false;
  if (isDeletedQuoteRecord(quote) || isCancelledQuoteRecord(quote)) return false;
  if (!isConfirmedQuoteRecord(quote)) return false;

  return REVENUE_STAGE_SET.has(getQuoteStageValue(quote));
};

function isConfirmedQuoteRecord(quote) {
  const statusText = [
    quote?.status,
    quote?.crm?.status,
    quote?.workStage,
    quote?.workStatus,
    quote?.crm?.stageId,
    quote?.crm?.stageName,
    quote?.stage,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return (
    quote?.isConfirmed === true ||
    quote?.confirmed === true ||
    Boolean(quote?.confirmedAt || quote?.crm?.confirmedAt) ||
    statusText.includes("confirmed") ||
    statusText.includes("advance") ||
    statusText.includes("materials ordered") ||
    statusText.includes("in production") ||
    statusText.includes("ready for installation") ||
    statusText.includes("installed") ||
    statusText.includes("completed")
  );
}

const isGeneralExecutionEligibleDeal = (deal = {}) => {
  if (!deal || typeof deal !== "object") return false;

  const type = String(
    deal.pipelineType ||
      deal.quoteTypeResolved ||
      deal.quoteType ||
      deal.linkedQuoteType ||
      deal.crm?.quoteType ||
      deal.crm?.pipelineType ||
      ""
  )
    .trim()
    .toLowerCase();
  const compactType = type.replace(/[\s_-]+/g, "");

  return (
    type === String(GENERAL_QUOTE_TYPE).toLowerCase() ||
    compactType === "generalquote" ||
    compactType === "generalquotation" ||
    compactType.startsWith("generalquote") ||
    compactType.startsWith("generalquotation")
  );
};

const sanitizeTaskHistorySegment = (value) =>
  String(value ?? "")
    .trim()
    .replace(/[^a-zA-Z0-9_-]+/g, "_")
    .replace(/^_+|_+$/g, "") || "record";

const buildTaskHistoryId = (taskType, dealId, sourceTaskId) =>
  `${sanitizeTaskHistorySegment(taskType)}_${sanitizeTaskHistorySegment(
    dealId
  )}_${sanitizeTaskHistorySegment(sourceTaskId)}`;

const serializeTaskHistoryValue = (value) => {
  if (value == null) return value ?? null;
  if (value instanceof Date) return value.toISOString();
  if (typeof value?.toDate === "function") {
    return value.toDate().toISOString();
  }
  if (Array.isArray(value)) {
    return value.map((entry) => serializeTaskHistoryValue(entry));
  }
  if (typeof value === "object") {
    return Object.entries(value).reduce((result, [key, entryValue]) => {
      if (typeof entryValue === "undefined" || typeof entryValue === "function") {
        return result;
      }
      result[key] = serializeTaskHistoryValue(entryValue);
      return result;
    }, {});
  }
  return value;
};

const sanitizeCrmFirestoreValue = (value) => {
  if (typeof value === "undefined" || typeof value === "function") {
    return undefined;
  }
  if (value == null) return null;
  if (value instanceof Date) return value.toISOString();
  if (typeof value?.toDate === "function") {
    return value.toDate().toISOString();
  }
  if (Array.isArray(value)) {
    return value
      .map((entry) => sanitizeCrmFirestoreValue(entry))
      .filter((entry) => typeof entry !== "undefined");
  }
  if (typeof value === "object") {
    return Object.entries(value).reduce((result, [key, entryValue]) => {
      const sanitized = sanitizeCrmFirestoreValue(entryValue);
      if (typeof sanitized !== "undefined") {
        result[key] = sanitized;
      }
      return result;
    }, {});
  }
  return value;
};

const buildCrmWriteDebugPreview = (payload = {}) => {
  try {
    return JSON.stringify(sanitizeCrmFirestoreValue(payload), null, 2).slice(0, 6000);
  } catch (error) {
    return `Unable to preview CRM payload: ${error?.message || error}`;
  }
};

const logCrmTaskDoneFailure = (label, error, details = {}) => {
  if (!isDevRuntime) return;

  const { payload, ...rest } = details;
  console.error("CRM TASK MARK DONE FAILED", {
    label,
    code: error?.code || "",
    message: error?.message || String(error || ""),
    stack: error?.stack || "",
    attemptedPayload: payload ? buildCrmWriteDebugPreview(payload) : "",
    ...rest,
  });
};

const getCanonicalChecklistCompleted = (item = {}) =>
  item.completed === true ||
  item.done === true ||
  item.checked === true ||
  item.isComplete === true ||
  item.completed === "true" ||
  item.done === "true" ||
  item.checked === "true" ||
  item.isComplete === "true" ||
  item.completed === 1 ||
  item.done === 1 ||
  item.checked === 1 ||
  item.isComplete === 1;

const withCanonicalChecklistAliases = (items = []) => {
  const normalized = preserveChecklistDraftTree(items || [], {
    templateMode: false,
    idPrefix: "crm-check",
  });

  const normalizeNode = (node = {}, index = 0) => {
    const children = Array.isArray(node.children)
      ? node.children.map((child, childIndex) => normalizeNode(child, childIndex))
      : [];
    const completed = getCanonicalChecklistCompleted(node);
    const label = String(node.label || node.title || "").trim();

    return sanitizeCrmFirestoreValue({
      ...node,
      id: String(node.id || node.templateId || `crm-check-${index + 1}`).trim(),
      label,
      title: String(node.title || label || "").trim(),
      completed,
      done: completed,
      checked: completed,
      assignedTo: node.assignedTo || node.assignedToNameSnapshot || "",
      children,
      subItems: children,
      subChecklist: children,
    });
  };

  return normalized.map((node, index) => normalizeNode(node, index));
};

const SITE_LOCATION_COORDINATE_PATTERNS = [
  /@(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/i,
  /[?&](?:q|ll)=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/i,
];

const extractSiteLocationCoordinates = (value = "") => {
  const text = decodeURIComponent(String(value || "").trim());
  for (const pattern of SITE_LOCATION_COORDINATE_PATTERNS) {
    const match = text.match(pattern);
    if (!match) continue;
    const latitude = Number(match[1]);
    const longitude = Number(match[2]);
    if (
      Number.isFinite(latitude) &&
      Number.isFinite(longitude) &&
      Math.abs(latitude) <= 90 &&
      Math.abs(longitude) <= 180
    ) {
      return { latitude, longitude };
    }
  }
  return { latitude: null, longitude: null };
};

const createSiteLocationDraft = (overrides = {}) => ({
  id: String(overrides.id || "").trim(),
  label: String(overrides.label || overrides.locationLabel || "").trim(),
  addressText: String(
    overrides.addressText || overrides.locationAddress || overrides.address || ""
  ).trim(),
  googleMapsUrl: String(
    overrides.googleMapsUrl || overrides.url || overrides.locationUrl || ""
  ).trim(),
  latitude:
    typeof overrides.latitude === "number" || String(overrides.latitude || "").trim()
      ? overrides.latitude
      : "",
  longitude:
    typeof overrides.longitude === "number" ||
    String(overrides.longitude || "").trim()
      ? overrides.longitude
      : "",
  notes: String(overrides.notes || "").trim(),
  isPrimary: overrides.isPrimary === true,
  createdAt: overrides.createdAt || null,
  createdBy: String(overrides.createdBy || "").trim(),
  updatedAt: overrides.updatedAt || null,
  updatedBy: String(overrides.updatedBy || "").trim(),
});

const normalizeSiteLocation = (location = {}, index = 0) => {
  const draft = createSiteLocationDraft(location);
  const extracted = extractSiteLocationCoordinates(draft.googleMapsUrl);
  const latitude =
    Number.isFinite(Number(draft.latitude)) && String(draft.latitude).trim() !== ""
      ? Number(draft.latitude)
      : extracted.latitude;
  const longitude =
    Number.isFinite(Number(draft.longitude)) && String(draft.longitude).trim() !== ""
      ? Number(draft.longitude)
      : extracted.longitude;

  return {
    id: draft.id || createClientId(`site-location-${index}`),
    label: draft.label || (index === 0 ? "Primary Site" : `Site ${index + 1}`),
    addressText: draft.addressText,
    googleMapsUrl: draft.googleMapsUrl,
    latitude,
    longitude,
    notes: draft.notes,
    isPrimary: draft.isPrimary === true,
    createdAt: draft.createdAt || null,
    createdBy: draft.createdBy || "",
    updatedAt: draft.updatedAt || null,
    updatedBy: draft.updatedBy || "",
  };
};

const normalizeSiteLocations = (locations = []) => {
  const normalized = (Array.isArray(locations) ? locations : [])
    .filter(Boolean)
    .map((location, index) => normalizeSiteLocation(location, index))
    .filter(
      (location) =>
        location.label ||
        location.addressText ||
        location.googleMapsUrl ||
        location.notes
    );

  let hasPrimary = false;
  return normalized.map((location, index) => {
    const isPrimary =
      location.isPrimary === true && !hasPrimary
        ? true
        : !hasPrimary && index === 0 && !normalized.some((entry) => entry.isPrimary);
    if (isPrimary) hasPrimary = true;
    return { ...location, isPrimary };
  });
};

const getPrimarySiteLocation = (record = {}) => {
  const locations = normalizeSiteLocations(
    record?.siteLocations || record?.crm?.siteLocations || []
  );
  return (
    locations.find((location) => location.isPrimary) ||
    locations[0] ||
    null
  );
};

const buildSiteLocationSearchText = (locations = []) =>
  normalizeSiteLocations(locations)
    .flatMap((location) => [
      location.label,
      location.addressText,
      location.googleMapsUrl,
      location.notes,
    ])
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const buildSiteLocationOpenUrl = (location = {}) => {
  const normalized = normalizeSiteLocation(location);
  if (/^https?:\/\//i.test(normalized.googleMapsUrl)) {
    return normalized.googleMapsUrl;
  }
  if (Number.isFinite(normalized.latitude) && Number.isFinite(normalized.longitude)) {
    return `https://www.google.com/maps/search/?api=1&query=${normalized.latitude},${normalized.longitude}`;
  }
  const query = normalized.addressText || normalized.label;
  return query
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
    : "";
};

const normalizeCustomerLedgerName = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ");

const normalizeCustomerLedgerPhone = (value = "") => {
  const digits = String(value || "").replace(/\D+/g, "");
  return digits.length > 10 ? digits.slice(-10) : digits;
};

const normalizeCustomerLedgerEmail = (value = "") =>
  String(value || "").trim().toLowerCase();

const buildCustomerLedgerSearchText = (customer = {}) => {
  const baseIndex = buildSafeCustomerSearchIndex(customer).searchText;
  const extraLocationText = normalizeSearchText(
    safeSearchPiece(customer.leadLocationSearchText, 800)
  );
  const indexedText = normalizeSearchText(
    [
      customer.searchText,
      customer.latestQuoteNo,
      customer.lastQuoteNo,
      customer.quoteNo,
      customer.stageLabel,
      customer.lastDealStage,
      ...(Array.isArray(customer.sourceLabels) ? customer.sourceLabels : []),
    ]
      .filter(Boolean)
      .join(" ")
  );
  return [baseIndex, indexedText, extraLocationText].filter(Boolean).join(" ");
};

const normalizeCustomerLedgerRecord = (record = {}) => {
  const customerName = String(
    record.customerName || record.name || record.contactName || "Unnamed Customer"
  ).trim();
  const phone = String(record.phone || record.customerPhone || "").trim();
  const whatsapp = String(record.whatsapp || record.customerWhatsapp || "").trim();
  const email = String(record.email || record.customerEmail || "").trim();
  const normalizedName =
    record.normalizedName || normalizeCustomerLedgerName(customerName);
  const normalizedPhone =
    record.normalizedPhone || normalizeCustomerLedgerPhone(phone || whatsapp);
  const normalizedWhatsapp =
    record.normalizedWhatsapp || normalizeCustomerLedgerPhone(whatsapp || phone);
  const normalizedEmail =
    record.normalizedEmail || normalizeCustomerLedgerEmail(email);
  const searchIndex = buildSafeCustomerSearchIndex({
    ...record,
    customerName,
    phone,
    whatsapp,
    email,
    normalizedName,
    nameLower: normalizeCustomerSearchText(customerName),
    customerNameLower: normalizeCustomerSearchText(customerName),
    normalizedPhone,
    normalizedWhatsapp,
    normalizedEmail,
  });

  return {
    id: String(record.id || "").trim(),
    customerKey: String(record.customerKey || record.id || record.customerId || "").trim(),
    customerName,
    normalizedName,
    normalizedPhone,
    normalizedWhatsapp,
    normalizedEmail,
    phone,
    whatsapp,
    email,
    address: String(record.address || "").trim(),
    companyName: String(record.companyName || record.companyOrProject || "").trim(),
    source: String(record.source || "").trim(),
    sourceType: String(record.sourceType || "").trim(),
    sourceTypes: Array.isArray(record.sourceTypes)
      ? record.sourceTypes.filter(Boolean).map(String)
      : [record.sourceType].filter(Boolean).map(String),
    sourceLabels: Array.isArray(record.sourceLabels)
      ? record.sourceLabels.filter(Boolean).map(String)
      : getCustomerSearchSourceLabels(record),
    personId: String(record.personId || record.customerSearchIndexId || "").trim(),
    customerSearchIndexId: String(record.customerSearchIndexId || record.personId || "").trim(),
    customerId: String(record.customerId || "").trim(),
    crmLeadId: String(record.crmLeadId || record.leadId || "").trim(),
    leadId: String(record.leadId || record.crmLeadId || "").trim(),
    quoteId: String(record.quoteId || record.latestQuoteId || "").trim(),
    latestQuoteId: String(record.latestQuoteId || record.quoteId || "").trim(),
    latestQuoteNo: String(record.latestQuoteNo || record.quoteNo || "").trim(),
    stageLabel: String(record.stageLabel || record.lastDealStage || "").trim(),
    status: String(record.status || "").trim(),
    tags: Array.isArray(record.tags) ? record.tags.filter(Boolean).map(String) : [],
    notes: String(record.notes || "").trim(),
    siteLocations: normalizeSiteLocations(record.siteLocations || []),
    totals:
      record.totals && typeof record.totals === "object"
        ? {
            ...record.totals,
            totalQuotes: Number(record.totals.totalQuotes ?? record.quoteCount ?? 0),
            totalQuoteValue: Number(
              record.totals.totalQuoteValue ?? record.totalQuotedValue ?? 0
            ),
            confirmedQuotes: Number(
              record.totals.confirmedQuotes ?? record.confirmedCount ?? 0
            ),
            confirmedValue: Number(
              record.totals.confirmedValue ?? record.confirmedValue ?? 0
            ),
          }
        : {
            totalQuotes: Number(record.quoteCount || 0),
            totalQuoteValue: Number(record.totalQuotedValue || 0),
            confirmedQuotes: Number(record.confirmedCount || 0),
            confirmedValue: Number(record.confirmedValue || 0),
            completedJobs: 0,
            pendingAmount: 0,
            receivedAmount: 0,
          },
    quoteCount: Number(record.quoteCount || record.totals?.totalQuotes || 0),
    totalQuotedValue: Number(record.totalQuotedValue || record.totals?.totalQuoteValue || 0),
    confirmedValue: Number(record.confirmedValue || record.totals?.confirmedValue || 0),
    openDealCount: Number(record.openDealCount || 0),
    activeDealValue: Number(record.activeDealValue || 0),
    pendingFollowupCount: Number(record.pendingFollowupCount || 0),
    upcomingInstallationCount: Number(record.upcomingInstallationCount || 0),
    lastQuoteId: String(record.lastQuoteId || "").trim(),
    lastQuoteNo: String(record.lastQuoteNo || "").trim(),
    lastDealId: String(record.lastDealId || "").trim(),
    lastDealStage: String(record.lastDealStage || "").trim(),
    createdAt: record.createdAt || null,
    updatedAt: record.updatedAt || null,
    createdBy: record.createdBy || "",
    lastActivityAt: record.lastActivityAt || record.updatedAt || record.createdAt || null,
    searchText: String(record.searchText || searchIndex.searchText || "").slice(
      0,
      CUSTOMER_SEARCH_TEXT_MAX_CHARS
    ),
    searchKeywords: Array.isArray(record.searchKeywords)
      ? record.searchKeywords.slice(0, CUSTOMER_SEARCH_KEYWORDS_MAX)
      : searchIndex.searchKeywords,
    searchPrefixes: Array.isArray(record.searchPrefixes)
      ? record.searchPrefixes.slice(0, CUSTOMER_SEARCH_PREFIXES_MAX)
      : searchIndex.searchPrefixes,
    phonePrefixes: Array.isArray(record.phonePrefixes)
      ? record.phonePrefixes.slice(0, CUSTOMER_SEARCH_PREFIXES_MAX)
      : searchIndex.phonePrefixes,
  };
};

const CUSTOMER_SEARCH_SOURCE_LABELS = {
  customer: "Customer",
  crm_lead: "Lead",
  quote_customer: "Existing Quote",
};

const getCustomerSearchSourceLabels = (record = {}) => {
  const sourceTypes = Array.isArray(record.sourceTypes)
    ? record.sourceTypes
    : [record.sourceType].filter(Boolean);
  return uniqueValues(
    sourceTypes.map((sourceType) => CUSTOMER_SEARCH_SOURCE_LABELS[sourceType] || sourceType)
  ).filter(Boolean);
};

const normalizeCustomerSearchPersonForQuote = (record = {}) => {
  const customerId = String(record.customerId || record.customerKey || "").trim();
  const personId = String(record.personId || record.id || "").trim();
  const sourceLabels = getCustomerSearchSourceLabels(record);
  const normalized = normalizeCustomerLedgerRecord({
    ...record,
    id: customerId || personId,
    customerKey: record.customerKey || customerId || personId,
    customerName: record.customerName || record.displayName || "",
    phone: record.phone || record.customerPhone || "",
    whatsapp: record.whatsapp || record.customerWhatsapp || "",
    email: record.email || record.customerEmail || "",
    address: record.address || record.customerAddress || "",
    companyName: record.companyName || record.customerCompanyName || record.companyOrProject || "",
    quoteCount: Number(record.quoteCount || record.totals?.totalQuotes || (record.latestQuoteId ? 1 : 0)),
    totalQuotedValue: Number(record.totalQuotedValue || record.totalAmount || 0),
    lastQuoteId: record.lastQuoteId || record.latestQuoteId || record.quoteId || "",
    lastQuoteNo: record.lastQuoteNo || record.latestQuoteNo || record.quoteNo || "",
    lastDealId: record.lastDealId || record.crmLeadId || record.leadId || "",
    lastDealStage: record.lastDealStage || record.stageLabel || record.stageId || "",
    lastActivityAt: record.lastActivityAt || record.updatedAt || record.createdAt || null,
  });

  return {
    ...normalized,
    sourceType: record.sourceType || "",
    sourceTypes: Array.isArray(record.sourceTypes) ? record.sourceTypes : [record.sourceType].filter(Boolean),
    sourceLabels,
    personId,
    customerSearchIndexId: personId,
    customerId,
    crmLeadId: String(record.crmLeadId || record.leadId || "").trim(),
    leadId: String(record.crmLeadId || record.leadId || "").trim(),
    quoteId: String(record.latestQuoteId || record.quoteId || "").trim(),
    latestQuoteId: String(record.latestQuoteId || record.quoteId || "").trim(),
    latestQuoteNo: String(record.latestQuoteNo || record.quoteNo || "").trim(),
    stageLabel: String(record.stageLabel || record.lastDealStage || "").trim(),
    status: record.status || "",
  };
};

const flattenChecklistNodes = (items = []) => {
  const nodes = [];

  const walk = (entries, parents = []) => {
    (Array.isArray(entries) ? entries : []).forEach((entry) => {
      if (!entry) return;

      const title =
        String(entry.title ?? entry.label ?? "").trim() || "Checklist Item";
      const path = [...parents, title];

      nodes.push({
        ...entry,
        title,
        path,
        pathLabel: path.join(" > "),
      });

      if (Array.isArray(entry.children) && entry.children.length > 0) {
        walk(entry.children, path);
      }
    });
  };

  walk(normalizeChecklistTree(items));
  return nodes;
};

const getNewlyCompletedChecklistNodes = (previousItems = [], nextItems = []) => {
  const previousMap = new Map(
    flattenChecklistNodes(previousItems).map((entry) => [entry.id, entry])
  );

  return flattenChecklistNodes(nextItems).filter((entry) => {
    if (entry.done !== true) return false;
    return previousMap.get(entry.id)?.done !== true;
  });
};

/* --------------------------- DEFAULTS --------------------------- */

const DEFAULT_STITCHING = {
  "3 Pleat - M Track": {
    rate: 330,
    fullness: 3,
    trackType: "M Track",
    trackLengthFt: 16,
    trackRateFt: 0, // user can edit in Stitching tab
  },
  "Ripple - Arabian Track": {
    rate: 470,
    fullness: 3,
    trackType: "Arabian Track",
    trackLengthFt: 16,
    trackRateFt: 0,
  },
  "Ripple - Jumbo Track": {
    rate: 550,
    fullness: 3,
    trackType: "Jumbo Track",
    trackLengthFt: 16,
    trackRateFt: 0,
  },
  "Eyelet (Custom)": {
    rate: 0,
    fullness: 3,
    trackType: "",
    trackLengthFt: 0,
    trackRateFt: 0,
  },
};

const STITCHING_SETTING_HELP = {
  rate:
    "Used to calculate stitching/track charge based on curtain width or configured track length.",
  fullness:
    "Used for fabric calculation. Example: 3 means curtain fabric is calculated at 3x fullness.",
  trackLength:
    "Used as fallback track length when a row does not have custom track length.",
};

const BENDED_TRACK_PRICING_METHODS = [
  { value: "none", label: "No extra bend charge" },
  { value: "fixed_per_bend", label: "Fixed charge per bend" },
  { value: "by_angle", label: "Charge by bend angle" },
  { value: "manual", label: "Manual charge in quote" },
];

const BENDED_TRACK_SHAPES = [
  { shape: "straight", label: "Straight Track", defaultBendCount: 0, defaultAngleDeg: null },
  { shape: "bend_90", label: "90 deg L Bend", defaultBendCount: 1, defaultAngleDeg: 90, angleKey: "90" },
  { shape: "bend_120", label: "120 deg Bend", defaultBendCount: 1, defaultAngleDeg: 120, angleKey: "120" },
  { shape: "bend_135", label: "135 deg Bend", defaultBendCount: 1, defaultAngleDeg: 135, angleKey: "135" },
  { shape: "u_shape", label: "U Shape / Two Bends", defaultBendCount: 2, defaultAngleDeg: null, angleKey: "custom" },
  { shape: "custom", label: "Custom Angle", defaultBendCount: 1, defaultAngleDeg: null, angleKey: "custom" },
];

const BENDED_TRACK_SHAPE_MAP = Object.fromEntries(
  BENDED_TRACK_SHAPES.map((entry) => [entry.shape, entry])
);

const DEFAULT_BEND_SETTINGS = {
  enabled: false,
  pricingMethod: "none",
  defaultBendCharge: 0,
  angleCharges: {
    90: 0,
    120: 0,
    135: 0,
    custom: 0,
  },
  allowedAngles: ["bend_90", "bend_120", "bend_135", "u_shape", "custom"],
  notes: "",
};

const DEFAULT_TRACK_CONFIG = {
  shape: "straight",
  isBended: false,
  angleDeg: null,
  bendCount: 0,
  bendChargePerBend: 0,
  bendAmount: 0,
  pricingMethod: "none",
  manualBendCharge: null,
  notes: "",
  segmentUnit: "in",
  segments: [
    { label: "A", length: 0, unit: "in" },
    { label: "B", length: 0, unit: "in" },
    { label: "C", length: 0, unit: "in" },
  ],
  totalSegmentLength: 0,
  totalSegmentLengthUnit: "in",
  totalTrackLengthFt: 0,
  bendLocations: [],
  bendLocationSummary: "",
};

const parseStitchingSettingNumber = (value, fallback = 0) => {
  if (value === "" || value == null) return fallback;
  const numeric = Number(String(value).replace(/,/g, "").trim());
  return Number.isFinite(numeric) ? numeric : Number.NaN;
};

const normalizeBendedTrackShape = (shape = "straight") => {
  const normalizedShape = String(shape || "straight").trim();
  if (BENDED_TRACK_SHAPE_MAP[normalizedShape]) return normalizedShape;
  if (normalizedShape === "90" || normalizedShape === "bend90") return "bend_90";
  if (normalizedShape === "120" || normalizedShape === "bend120") return "bend_120";
  if (normalizedShape === "135" || normalizedShape === "bend135") return "bend_135";
  if (normalizedShape === "u" || normalizedShape === "u-shape") return "u_shape";
  return "straight";
};

const normalizeAllowedBendShapes = (allowedAngles = DEFAULT_BEND_SETTINGS.allowedAngles) => {
  const source = Array.isArray(allowedAngles) ? allowedAngles : DEFAULT_BEND_SETTINGS.allowedAngles;
  const allowed = source
    .map((entry) => {
      if (entry === 90 || entry === "90") return "bend_90";
      if (entry === 120 || entry === "120") return "bend_120";
      if (entry === 135 || entry === "135") return "bend_135";
      if (entry === "u" || entry === "u_shape" || entry === "u-shape") return "u_shape";
      if (entry === "custom") return "custom";
      return normalizeBendedTrackShape(entry);
    })
    .filter((entry) => entry && entry !== "straight");
  return Array.from(new Set(allowed.length ? allowed : DEFAULT_BEND_SETTINGS.allowedAngles));
};

const normalizeBendSettingsForSave = (values = {}) => {
  const bendSource = values?.bendSettings || {};
  const pricingMethod = BENDED_TRACK_PRICING_METHODS.some(
    (entry) => entry.value === bendSource.pricingMethod
  )
    ? bendSource.pricingMethod
    : "none";

  return {
    ...DEFAULT_BEND_SETTINGS,
    ...bendSource,
    enabled: values?.supportsBendedTrack === true || bendSource.enabled === true,
    pricingMethod,
    defaultBendCharge: parseStitchingSettingNumber(bendSource.defaultBendCharge, 0),
    angleCharges: {
      90: parseStitchingSettingNumber(bendSource.angleCharges?.["90"], 0),
      120: parseStitchingSettingNumber(bendSource.angleCharges?.["120"], 0),
      135: parseStitchingSettingNumber(bendSource.angleCharges?.["135"], 0),
      custom: parseStitchingSettingNumber(bendSource.angleCharges?.custom, 0),
    },
    allowedAngles: normalizeAllowedBendShapes(bendSource.allowedAngles),
    notes: String(bendSource.notes || ""),
  };
};

const normalizeStitchingSettingForState = (values = {}) => {
  const supportsBendedTrack = values?.supportsBendedTrack === true;
  const normalizedBendSettings = normalizeBendSettingsForSave({
    ...values,
    supportsBendedTrack,
  });
  return {
    rate: parseStitchingSettingNumber(values?.rate, 0),
    fullness: parseStitchingSettingNumber(values?.fullness, Number.NaN),
    trackType: values?.trackType || "",
    trackLengthFt: parseStitchingSettingNumber(
      values?.trackLengthFt ?? values?.trackLength,
      0
    ),
    trackRateFt: parseStitchingSettingNumber(values?.trackRateFt, 0),
    includesTrack: values?.includesTrack === true,
    supportsBendedTrack,
    bendSettings: {
      ...normalizedBendSettings,
      enabled: supportsBendedTrack && normalizedBendSettings.enabled,
    },
  };
};

const buildStitchingSettingsForSave = (settings = {}) => {
  const normalized = {};
  const errors = [];

  Object.entries(settings || {}).forEach(([rawName, values = {}]) => {
    const name = String(rawName || "").trim();
    const displayName = name || "Unnamed style";
    const setting = normalizeStitchingSettingForState(values);
    const { rate, fullness, trackLengthFt, trackRateFt } = setting;
    const bendSettings = setting.bendSettings || DEFAULT_BEND_SETTINGS;

    if (!name) errors.push("Style name is required.");
    if (!Number.isFinite(rate) || rate < 0) {
      errors.push(`${displayName}: Rate / Running ft must be 0 or greater.`);
    }
    if (!Number.isFinite(fullness) || fullness <= 0) {
      errors.push(`${displayName}: Fullness Multiplier must be greater than 0.`);
    }
    if (!Number.isFinite(trackLengthFt) || trackLengthFt < 0) {
      errors.push(`${displayName}: Default Track Length must be 0 or greater.`);
    }
    if (!Number.isFinite(trackRateFt) || trackRateFt < 0) {
      errors.push(`${displayName}: Track rate must be 0 or greater.`);
    }
    if (setting.supportsBendedTrack) {
      if (!BENDED_TRACK_PRICING_METHODS.some((entry) => entry.value === bendSettings.pricingMethod)) {
        errors.push(`${displayName}: Bend pricing method is invalid.`);
      }
      if (!Number.isFinite(bendSettings.defaultBendCharge) || bendSettings.defaultBendCharge < 0) {
        errors.push(`${displayName}: Default bend charge must be 0 or greater.`);
      }
      Object.entries(bendSettings.angleCharges || {}).forEach(([angle, charge]) => {
        if (!Number.isFinite(charge) || charge < 0) {
          errors.push(`${displayName}: ${angle} bend charge must be 0 or greater.`);
        }
      });
    }

    if (name) {
      normalized[name] = {
        rate,
        fullness,
        trackType: setting.trackType || "",
        trackLengthFt,
        trackRateFt,
        includesTrack: setting.includesTrack === true,
        supportsBendedTrack: setting.supportsBendedTrack === true,
        bendSettings,
      };
    }
  });

  return { normalized, errors };
};

const normalizeQuoteFullnessOverrideEntry = (entry = {}, fallbackKey = "") => {
  const source = entry && typeof entry === "object" ? entry : { quoteFullness: entry };
  const stableStitchingId = sanitizeEditableText(
    source.stitchingStyleId ||
      source.stitchingConfigId ||
      source.stitchingId ||
      source.stitchId ||
      source.styleId ||
      source.id ||
      ""
  );
  const rawKey =
    stableStitchingId ||
    source.stitchingKey ||
    source.stitchingStyleKey ||
    source.key ||
    fallbackKey ||
    source.stitchingLabel ||
    source.label ||
    "";
  const stitchingKey = normalizeStitchingKey(rawKey);
  if (!stitchingKey) return null;
  const stitchingLabel =
    sanitizeEditableText(
      source.stitchingLabel ||
        source.label ||
        source.name ||
        source.displayName ||
        fallbackKey ||
        rawKey
    ) || rawKey;
  const defaultFullness = Number(source.defaultFullness);
  const quoteFullness = Number(source.quoteFullness);
  const safeQuoteFullness =
    Number.isFinite(quoteFullness) && quoteFullness > 0 && quoteFullness <= 10
      ? quoteFullness
      : null;

  return {
    stitchingKey,
    stitchingStyleId: stableStitchingId || stitchingKey,
    stitchingLabel,
    defaultFullness:
      Number.isFinite(defaultFullness) && defaultFullness > 0
        ? Number(defaultFullness.toFixed(2))
        : null,
    quoteFullness: safeQuoteFullness == null ? null : Number(safeQuoteFullness.toFixed(2)),
    enabled: safeQuoteFullness != null,
    source: safeQuoteFullness != null ? "quote_override" : "default",
  };
};

const normalizeQuoteFullnessOverrides = (overrides = {}) => {
  if (!overrides || typeof overrides !== "object" || Array.isArray(overrides)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(overrides)
      .map(([key, value]) => normalizeQuoteFullnessOverrideEntry(value, key))
      .filter(Boolean)
      .map((entry) => [entry.stitchingKey, entry])
  );
};

const buildQuoteFullnessOverrideEntry = ({
  stitchingKey = "",
  stitchingStyleId = "",
  stitchingLabel = "",
  defaultFullness = null,
  quoteFullness = null,
} = {}) =>
  normalizeQuoteFullnessOverrideEntry(
    {
      stitchingKey,
      stitchingStyleId,
      stitchingLabel,
      defaultFullness,
      quoteFullness,
    },
    stitchingKey || stitchingLabel
  );

const DEFAULT_MIN_SQFT = {
  "Roman Blind - Normal": 12,
  "Roman Blind - Clip On": 12,
  "Zebra Blind": 10,
  "Roller Blind": 10,
  "Venetian Blind": 10,
  "Vertical Blind": 10,
};

const normalizeFabricCalcSettingsForState = (settings = {}) => ({
  ...(settings && typeof settings === "object" ? settings : {}),
  curtain_top_hem:
    settings?.curtain_top_hem ?? settings?.curtainTopHem ?? 4,
  curtain_bottom_hem:
    settings?.curtain_bottom_hem ?? settings?.curtainBottomHem ?? 4,
  roman_top_hem: settings?.roman_top_hem ?? settings?.romanTopHem ?? 3,
  roman_bottom_hem:
    settings?.roman_bottom_hem ?? settings?.romanBottomHem ?? 4,
  roman_side_hem: settings?.roman_side_hem ?? settings?.romanSideHem ?? 6,
  usable_width_deduction:
    settings?.usable_width_deduction ?? settings?.usableWidthDeduction ?? 4,
  blindSqftRoundingMode: resolveBlindSqftRoundingMode(settings, "exact"),
});

/* --------------------------- CURRENCIES --------------------------- */

const CURRENCY_LIST = [
  "Rs. Indian Rupee",
  "$ US Dollar",
  "CAD Canadian Dollar",
  "AED UAE Dirham",
  "QAR Qatari Riyal",
  "SAR Saudi Riyal",
  "BHD Bahraini Dinar",
  "OMR Omani Rial",
  "KWD Kuwaiti Dinar",
];

const currencySymbol = (cur) => {
  if (!cur) return "";

  const c = cur.toLowerCase();

  if (c.includes("rupee") || c.includes("indian")) return "Rs.";

  if (c.includes("aed")) return "AED ";
  if (c.includes("usd")) return "$";
  if (c.includes("eur")) return "EUR ";
  if (c.includes("gbp") || c.includes("pound")) return "GBP ";

  return "";
};

const isIndianCurrency = (cur) => {
  const c = String(cur || "").toLowerCase();
  return c.includes("rupee") || c.includes("indian") || c.startsWith("rs");
};

const formatCurrencyINR = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(Number(value)) ? Number(value) : 0);

const formatCurrencyDisplay = (value, cur = "Rs. Indian Rupee") => {
  const amount = Number.isFinite(Number(value)) ? Number(value) : 0;
  if (isIndianCurrency(cur)) return formatCurrencyINR(amount);

  const symbol = currencySymbol(cur);
  return `${symbol ? `${symbol} ` : ""}${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`.trim();
};



// ASCII-safe symbol for PDFs
const symbolFromCurrency = (c) => {
  switch ((c || "").toUpperCase()) {
    case "INR":
      return "Rs.";
    case "USD":
      return "$";
    case "EUR":
      return "EUR";
    case "GBP":
      return "GBP";
    case "AED":
      return "AED";
    case "SAR":
      return "SAR";
    case "OMR":
      return "OMR";
    case "QAR":
      return "QAR";
    case "BHD":
      return "BHD";
    case "KWD":
      return "KWD";
    case "CAD":
      return "CAD";
    default:
      return c || "";
  }
};

const pdfCurrencySymbol = (c) => {
  const raw = String(c || "").trim();
  if (!raw) return "Rs.";

  const lower = raw.toLowerCase();

  if (lower.includes("rupee") || lower.startsWith("rs.")) return "Rs.";
  if (lower.includes("usd") || raw.startsWith("$")) return "$";
  if (lower.includes("eur")) return "EUR";
  if (lower.includes("gbp") || lower.includes("pound")) return "GBP";
  if (lower.includes("aed")) return "AED";
  if (lower.includes("sar")) return "SAR";
  if (lower.includes("omr")) return "OMR";
  if (lower.includes("qar")) return "QAR";
  if (lower.includes("bhd")) return "BHD";
  if (lower.includes("kwd")) return "KWD";
  if (lower.includes("cad")) return "CAD";

  return symbolFromCurrency(raw.toUpperCase());
};

const formatPdfMoney = (value, currencyValue) =>
  `${pdfCurrencySymbol(currencyValue)} ${Number(value || 0).toFixed(2)}`;

/* ====================== UNIT CONVERSION HELPERS ====================== */
/* =========================
   UNIT HELPERS (GLOBAL)
   ========================= */

const INCH_PER_CM = 0.393700787;

/* DISPLAY */

const displayValue = (inches, unit) => {
  if (inches === "" || inches === null || inches === undefined) return "";

  const n = Number(inches);

  if (unit === "cm") {
    return (n / INCH_PER_CM).toFixed(2);
  }

  return n.toFixed(2);
};

const normalizeLengthUnit = (value = "in") => {
  const normalized = String(value || "in").trim().toLowerCase();
  if (["in", "inch", "inches"].includes(normalized)) return "in";
  if (["cm", "centimeter", "centimeters"].includes(normalized)) return "cm";
  if (["mm", "millimeter", "millimeters"].includes(normalized)) return "mm";
  if (["ft", "foot", "feet", "rft", "running ft", "runningft"].includes(normalized)) return "ft";
  if (["m", "meter", "meters", "metre", "metres"].includes(normalized)) return "m";
  return "in";
};

const unitLabel = (unit) => {
  const normalized = normalizeLengthUnit(unit);
  if (normalized === "cm") return "cm";
  if (normalized === "mm") return "mm";
  if (normalized === "ft") return "ft";
  if (normalized === "m") return "m";
  return "in";
};

const hasLegacyTrackSegmentFeet = (trackConfig = {}) =>
  Array.isArray(trackConfig.segments) &&
  trackConfig.segments.some((segment = {}) => {
    const hasRawLength =
      segment.length !== undefined &&
      segment.length !== null &&
      segment.length !== "";
    return (
      !hasRawLength &&
      segment.lengthFt !== undefined &&
      segment.lengthFt !== null &&
      segment.lengthFt !== ""
    );
  });

const getRowMeasurementUnit = (row = {}, quoteUnit = "in") => {
  const trackConfig = row.trackConfig || {};
  return normalizeLengthUnit(
    row.segmentUnit ||
      trackConfig.segmentUnit ||
      trackConfig.totalSegmentLengthUnit ||
      (hasLegacyTrackSegmentFeet(trackConfig) ? "ft" : "") ||
      row.measurementUnit ||
      row.dimensionUnit ||
      row.unit ||
      quoteUnit ||
      "in"
  );
};

const convertLengthToFeet = (value, sourceUnit = "in") => {
  const amount = Math.max(0, num(value));
  const normalizedUnit = normalizeLengthUnit(sourceUnit);
  if (normalizedUnit === "ft") return round2(amount);
  if (normalizedUnit === "cm") return round2(amount / 30.48);
  if (normalizedUnit === "mm") return round2(amount / 304.8);
  if (normalizedUnit === "m") return round2(amount * 3.28084);
  return round2(amount / 12);
};

const formatLengthWithUnit = (value, sourceUnit = "in", decimals = 2) => {
  const amount = Number.isFinite(Number(value)) ? Number(value) : 0;
  return `${amount.toFixed(decimals)} ${unitLabel(sourceUnit)}`;
};

const formatMeasurementWithUnit = (valueInches, unit, label = "") => {
  const suffix = label ? ` (${label})` : "";
  return `${displayValue(Number(valueInches || 0), unit)} ${unitLabel(unit)}${suffix}`;
};

const formatMetersLabel = (value) => `${Number(value || 0).toFixed(2)} m`;
const CRM_SCOPE_ALL = "all";



/* --------------------------- HELPERS --------------------------- */

const num = (v) => {
  if (v == null || v === "") return 0;
  const cleaned = String(v)
    .replace(/,/g, "")
    .replace(/[^\d.-]/g, "")
    .trim();
  const n = Number.parseFloat(cleaned);
  if (Number.isNaN(n)) return 0;
  return Math.round(n * 100) / 100;
};

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

const LEGACY_CALCULATION_IMPACT = {
  NO_IMPACT: "no_impact",
  FINANCIAL_DIFFERENCE: "financial_difference",
  PRODUCTION_DIFFERENCE: "production_difference",
  UNKNOWN_REQUIRES_REVIEW: "unknown_requires_review",
};

const LEGACY_CALCULATION_VERSION_STATUS = {
  CURRENT: "current",
  OLDER_VERSION: "older_version",
  MISSING_VERSION: "missing_version",
  MISSING_SNAPSHOT: "missing_snapshot",
};

const rowIsAffectedByLegacyCurtainFormulaStatic = (row = {}, productConfig = null) => {
  const calculationType = String(
    productConfig?.calculationType || row?.calculationType || ""
  )
    .trim()
    .toLowerCase();
  const category = String(productConfig?.category || row?.category || "")
    .trim()
    .toLowerCase();
  const normalizedProduct = normalizeProductName(
    row.productName ||
      row.productType ||
      row.product ||
      row.calculationSnapshot?.productName ||
      productConfig?.name ||
      ""
  );

  if (isAccessoryCalculationType(calculationType)) return false;

  return Boolean(
    productConfig?.usesFabric === true ||
      row?.usesFabric === true ||
      calculationType === "fabric_meter" ||
      calculationType === "sqft_plus_fabric" ||
      category === "curtain" ||
      category === "roman_blind" ||
      category === "roman blind" ||
      normalizedProduct === "curtains" ||
      normalizedProduct.startsWith("roman")
  );
};

const hasCurrencyDifference = (a, b, tolerance = 0.01) =>
  Math.abs(round2(Number(a || 0) - Number(b || 0))) > tolerance;

const deriveLegacyRecalculationImpact = (preview = null) => {
  if (!preview) {
    return {
      recalculationImpact: LEGACY_CALCULATION_IMPACT.NO_IMPACT,
      actualChangedRows: 0,
      totalDifference: 0,
      hasFinancialDifference: false,
      hasProductionDifference: false,
      hasUnknownDifference: false,
      shouldShowBanner: false,
      reason: "no_preview",
    };
  }

  const changedRows = Array.isArray(preview.changedRows) ? preview.changedRows : [];
  const rowDiffRows = Array.isArray(preview.rowDiffRows) ? preview.rowDiffRows : [];
  const subtotalDifference = round2(Number(preview.difference?.subtotal || 0));
  const gstDifference = round2(Number(preview.difference?.gst || 0));
  const grandTotalDifference = round2(Number(preview.difference?.grandTotal || 0));
  const hasFinancialDifference =
    hasCurrencyDifference(preview.oldTotals?.subtotalBeforeGst, preview.newTotals?.subtotalBeforeGst) ||
    hasCurrencyDifference(
      preview.oldTotals?.totalGstAmount ?? preview.oldTotals?.gstAmount,
      preview.newTotals?.totalGstAmount ?? preview.newTotals?.gstAmount
    ) ||
    hasCurrencyDifference(preview.oldTotals?.grandTotal, preview.newTotals?.grandTotal) ||
    changedRows.some((row) => Math.abs(Number(row?.difference || 0)) > 0.01) ||
    rowDiffRows.some(
      (row) =>
        row?.type === "money" &&
        row?.oldExists === true &&
        Math.abs(Number(row?.difference || 0)) > 0.01
    );
  const hasProductionDifference = rowDiffRows.some((row) => {
    const field = String(row?.field || "").toLowerCase();
    if (field.includes("row total") || field.includes("fabric amount")) return false;
    return Math.abs(Number(row?.difference || 0)) > 0.01;
  });
  const hasUnknownDifference = rowDiffRows.some(
    (row) => row?.oldExists === false && Math.abs(Number(row?.newValue || 0)) > 0.01
  );
  const recalculationImpact = hasFinancialDifference
    ? LEGACY_CALCULATION_IMPACT.FINANCIAL_DIFFERENCE
    : hasProductionDifference
    ? LEGACY_CALCULATION_IMPACT.PRODUCTION_DIFFERENCE
    : hasUnknownDifference
    ? LEGACY_CALCULATION_IMPACT.UNKNOWN_REQUIRES_REVIEW
    : LEGACY_CALCULATION_IMPACT.NO_IMPACT;

  return {
    recalculationImpact,
    actualChangedRows: changedRows.length,
    totalDifference: grandTotalDifference,
    subtotalDifference,
    gstDifference,
    grandTotalDifference,
    hasFinancialDifference,
    hasProductionDifference,
    hasUnknownDifference,
    shouldShowBanner: recalculationImpact !== LEGACY_CALCULATION_IMPACT.NO_IMPACT,
    reason:
      recalculationImpact === LEGACY_CALCULATION_IMPACT.NO_IMPACT
        ? "saved_and_recalculated_values_match"
        : recalculationImpact,
  };
};

const BEND_LOCATION_OPTIONS = [
  {
    value: "after_a",
    label: "After Segment A / between A and B",
    locationType: "between_segments",
    afterSegment: "A",
    betweenSegments: ["A", "B"],
    side: null,
  },
  {
    value: "between_b_c",
    label: "Between Segment B and C",
    locationType: "between_segments",
    afterSegment: "B",
    betweenSegments: ["B", "C"],
    side: null,
  },
  {
    value: "left_side",
    label: "Left side bend",
    locationType: "left_side",
    afterSegment: "",
    betweenSegments: [],
    side: "left",
  },
  {
    value: "right_side",
    label: "Right side bend",
    locationType: "right_side",
    afterSegment: "",
    betweenSegments: [],
    side: "right",
  },
  {
    value: "center",
    label: "Center bend",
    locationType: "center",
    afterSegment: "",
    betweenSegments: [],
    side: null,
  },
  {
    value: "custom",
    label: "Custom note",
    locationType: "custom",
    afterSegment: "",
    betweenSegments: [],
    side: null,
  },
];

const getBendLocationOption = (location = {}) => {
  if (location.locationType === "left_side") return "left_side";
  if (location.locationType === "right_side") return "right_side";
  if (location.locationType === "center") return "center";
  if (location.locationType === "custom") return "custom";
  const between = Array.isArray(location.betweenSegments)
    ? location.betweenSegments.join("-")
    : "";
  if (between === "B-C") return "between_b_c";
  return "after_a";
};

const buildBendLocationFromOption = (
  optionValue = "after_a",
  bendNo = 1,
  angleDeg = null,
  distanceUnit = "in",
  previous = {}
) => {
  const option =
    BEND_LOCATION_OPTIONS.find((entry) => entry.value === optionValue) ||
    BEND_LOCATION_OPTIONS[0];
  return {
    bendNo,
    angleDeg: angleDeg == null ? null : Number(angleDeg),
    locationType: option.locationType,
    afterSegment: option.afterSegment,
    betweenSegments: [...option.betweenSegments],
    side: option.side,
    distanceFromStart: previous.distanceFromStart ?? null,
    distanceUnit: normalizeLengthUnit(previous.distanceUnit || distanceUnit),
    note: previous.note || "",
  };
};

const getDefaultBendLocationOption = (shape = "straight", bendNo = 1) => {
  const normalizedShape = normalizeBendedTrackShape(shape);
  if (normalizedShape === "u_shape" && Number(bendNo) === 2) return "between_b_c";
  return "after_a";
};

const normalizeBendLocations = (
  bendLocations = [],
  { shape = "straight", bendCount = 0, angleDeg = null, unit = "in" } = {}
) => {
  const source = Array.isArray(bendLocations) ? bendLocations : [];
  const count = Math.max(0, Math.round(num(bendCount)));
  return Array.from({ length: count }).map((_, index) => {
    const bendNo = index + 1;
    const previous = source[index] || {};
    const option = previous.locationType
      ? getBendLocationOption(previous)
      : getDefaultBendLocationOption(shape, bendNo);
    return {
      ...buildBendLocationFromOption(option, bendNo, previous.angleDeg ?? angleDeg, unit, previous),
      angleDeg:
        previous.angleDeg == null || previous.angleDeg === ""
          ? angleDeg == null
            ? null
            : Number(angleDeg)
          : Number(previous.angleDeg),
    };
  });
};

const summarizeBendLocation = (location = {}) => {
  if (location.locationType === "left_side") return "Left side bend";
  if (location.locationType === "right_side") return "Right side bend";
  if (location.locationType === "center") return "Center bend";
  if (location.locationType === "custom") return location.note || "Custom bend location";
  const between = Array.isArray(location.betweenSegments)
    ? location.betweenSegments.filter(Boolean)
    : [];
  if (between.length >= 2) {
    return `After Segment ${between[0]} / between ${between[0]} and ${between[1]}`;
  }
  if (location.afterSegment) return `After Segment ${location.afterSegment}`;
  return "";
};

const summarizeBendLocations = (locations = []) =>
  (Array.isArray(locations) ? locations : [])
    .map(summarizeBendLocation)
    .filter(Boolean)
    .join("; ");

const normalizeTrackSegments = (segments = [], defaultUnit = "in") => {
  const source = Array.isArray(segments) ? segments : [];
  const fallbackUnit = normalizeLengthUnit(defaultUnit);
  return ["A", "B", "C"].map((label, index) => {
    const sourceSegment = source[index] || {};
    const hasRawLength =
      sourceSegment.length !== undefined &&
      sourceSegment.length !== null &&
      sourceSegment.length !== "";
    const segmentUnit = hasRawLength
      ? normalizeLengthUnit(sourceSegment.unit || sourceSegment.segmentUnit || fallbackUnit)
      : sourceSegment.lengthFt !== undefined && sourceSegment.lengthFt !== null
      ? "ft"
      : fallbackUnit;
    const length = Math.max(
      0,
      num(hasRawLength ? sourceSegment.length : sourceSegment.lengthFt ?? 0)
    );

    return {
      label: sourceSegment.label || label,
      length,
      unit: segmentUnit,
      lengthFt: convertLengthToFeet(length, segmentUnit),
    };
  });
};

const getTrackSegmentEntries = (trackConfig = {}) =>
  normalizeTrackSegments(
    trackConfig.segments,
    trackConfig.segmentUnit || trackConfig.totalSegmentLengthUnit || "in"
  ).filter((segment) => Number(segment.length || 0) > 0);

const formatTrackSegmentsInline = (trackConfig = {}) =>
  getTrackSegmentEntries(trackConfig)
    .map((segment) =>
      `${segment.label || ""} ${formatLengthWithUnit(
        segment.length || 0,
        segment.unit || trackConfig.segmentUnit || "in"
      )}`.trim()
    )
    .join(" + ");

const buildBendedTrackLayoutPreview = (trackConfig = {}) => {
  const segments = getTrackSegmentEntries(trackConfig);
  if (!segments.length) return "";
  const locations = Array.isArray(trackConfig.bendLocations)
    ? trackConfig.bendLocations
    : [];
  return segments
    .map((segment, index) => {
      const part = `${segment.label}: ${formatLengthWithUnit(
        segment.length,
        segment.unit || trackConfig.segmentUnit
      )}`;
      const bend = locations[index];
      if (!bend) return part;
      const angle = bend.angleDeg || trackConfig.angleDeg;
      return `${part} -> Bend${angle ? ` ${Number(angle).toFixed(0)} deg` : ""} ->`;
    })
    .join(" ")
    .replace(/ ->$/, "");
};

const getBendedTrackShapeMeta = (shape = "straight") =>
  BENDED_TRACK_SHAPE_MAP[normalizeBendedTrackShape(shape)] || BENDED_TRACK_SHAPE_MAP.straight;

const calculateCurtainTrackConfig = (
  row = {},
  selectedStitchingTrack = {},
  { baseTrackLengthFt = 0, measurementUnit = "in" } = {}
) => {
  const packageConfig = normalizeStitchingSettingForState(selectedStitchingTrack || {});
  const trackConfig = row?.trackConfig || {};
  const shape = normalizeBendedTrackShape(trackConfig.shape || "straight");
  const shapeMeta = getBendedTrackShapeMeta(shape);
  const segmentUnit = getRowMeasurementUnit(
    {
      ...row,
      segmentUnit: trackConfig.segmentUnit,
      trackConfig,
    },
    measurementUnit
  );
  const supportsBendedTrack = packageConfig.supportsBendedTrack === true;
  const includesTrack = packageConfig.includesTrack === true || supportsBendedTrack;
  const isBended =
    supportsBendedTrack &&
    shape !== "straight" &&
    (trackConfig.isBended === true || shape !== "straight");
  const bendSettings = packageConfig.bendSettings || DEFAULT_BEND_SETTINGS;
  const pricingMethod = supportsBendedTrack
    ? bendSettings.pricingMethod || "none"
    : "none";
  const bendCount = isBended
    ? Math.max(0, num(trackConfig.bendCount ?? shapeMeta.defaultBendCount ?? 1))
    : 0;
  const angleDeg =
    shape === "custom"
      ? num(trackConfig.angleDeg || 0) || null
      : shapeMeta.defaultAngleDeg ?? (trackConfig.angleDeg == null ? null : num(trackConfig.angleDeg));
  const angleKey = shapeMeta.angleKey || "custom";
  const autoBendCharge =
    pricingMethod === "fixed_per_bend"
      ? num(bendSettings.defaultBendCharge)
      : pricingMethod === "by_angle"
      ? num(bendSettings.angleCharges?.[angleKey] ?? bendSettings.angleCharges?.custom)
      : 0;
  const manualBendCharge =
    trackConfig.manualBendCharge === "" || trackConfig.manualBendCharge == null
      ? null
      : Math.max(0, num(trackConfig.manualBendCharge));
  const bendChargePerBend =
    isBended && pricingMethod === "manual"
      ? Number(manualBendCharge || 0)
      : isBended
      ? Math.max(0, autoBendCharge)
      : 0;
  const bendAmount = round2(bendCount * bendChargePerBend);
  const segments = normalizeTrackSegments(trackConfig.segments, segmentUnit);
  const totalSegmentLength = round2(
    segments.reduce((sum, segment) => sum + Number(segment.length || 0), 0)
  );
  const segmentTotalFt = convertLengthToFeet(totalSegmentLength, segmentUnit);
  const hasSegmentOverride = totalSegmentLength > 0;
  const totalTrackLengthFt = segmentTotalFt > 0 ? segmentTotalFt : round2(num(baseTrackLengthFt));
  const bendLocations = normalizeBendLocations(trackConfig.bendLocations, {
    shape,
    bendCount,
    angleDeg,
    unit: segmentUnit,
  });
  const bendLocationSummary = summarizeBendLocations(bendLocations);

  return {
    ...DEFAULT_TRACK_CONFIG,
    ...trackConfig,
    shape,
    isBended,
    angleDeg,
    bendCount,
    bendChargePerBend,
    bendAmount,
    pricingMethod,
    manualBendCharge,
    notes: String(trackConfig.notes || ""),
    segmentUnit,
    segments,
    totalSegmentLength,
    totalSegmentLengthUnit: segmentUnit,
    hasSegmentOverride,
    totalTrackLengthFt,
    bendLocations,
    bendLocationSummary,
    includesTrack,
    supportsBendedTrack,
    disabledReason:
      trackConfig.isBended === true && !supportsBendedTrack
        ? "Bended track disabled because selected package does not support it."
      : "",
  };
};

const applyBendedTrackToCalcResult = (
  row = {},
  calc = {},
  selectedStitchingTrack = {},
  measurementUnit = "in"
) => {
  const baseTrackLengthFt =
    num(row.trackLengthFt ?? row.trackLength) ||
    num(selectedStitchingTrack?.trackLengthFt ?? selectedStitchingTrack?.trackLength);
  const trackConfig = calculateCurtainTrackConfig(row, selectedStitchingTrack, {
    baseTrackLengthFt,
    measurementUnit: getRowMeasurementUnit(row, measurementUnit),
  });
  const trackRateFt = num(selectedStitchingTrack?.trackRateFt);
  const hasSegmentOverride = trackConfig.totalTrackLengthFt > 0 && trackConfig.totalTrackLengthFt !== baseTrackLengthFt;
  const trackLengthAdjustment =
    hasSegmentOverride && trackRateFt > 0
      ? round2((trackConfig.totalTrackLengthFt - baseTrackLengthFt) * trackRateFt)
      : 0;
  const bendAmount = round2(Number(trackConfig.bendAmount || 0));
  const totalDelta = round2(trackLengthAdjustment + bendAmount);
  const discountScope = calc.discountScope || "fabric_only";
  const discountPercent = Math.min(
    100,
    Math.max(0, num(calc.fabricDiscountPercent ?? row.discount))
  );
  const baseLineAmountBeforeDiscount =
    discountScope === "line_total"
      ? round2(
          Number(
            calc.baseLineAmountBeforeDiscount ??
              Number(calc.lineTotal ?? calc.rowTotal ?? 0) + Number(calc.discountAmount || 0)
          ) + totalDelta
        )
      : round2(Number(calc.baseLineAmountBeforeDiscount ?? calc.lineTotal ?? calc.rowTotal ?? 0) + totalDelta);
  const discountAmount =
    discountScope === "line_total"
      ? round2(baseLineAmountBeforeDiscount * (discountPercent / 100))
      : Number(calc.discountAmount || calc.fabricDiscountAmount || 0);
  const lineTotal =
    discountScope === "line_total"
      ? round2(Math.max(0, baseLineAmountBeforeDiscount - discountAmount))
      : round2(Number(calc.lineTotal ?? calc.rowTotal ?? 0) + totalDelta);
  const gstRate = num(calc.gstRate);
  const gstAmount = round2((lineTotal * gstRate) / 100);

  return {
    ...calc,
    trackConfig,
    bendAmount,
    trackCost: round2(Number(calc.trackCost || 0) + trackLengthAdjustment),
    baseLineAmountBeforeDiscount,
    discountScope,
    discountAmount,
    discountBase:
      discountScope === "line_total"
        ? baseLineAmountBeforeDiscount
        : Number(calc.discountBase || calc.fabricGrossAmount || 0),
    lineTotal,
    rowTotal: lineTotal,
    taxableAmount: lineTotal,
    gstAmount,
    total: round2(lineTotal + gstAmount),
  };
};

const isDevelopmentMode = () =>
  IS_DEV_UI_ENABLED;

const CURTAIN_GST_RATE_OPTIONS = [0, 5, 12, 18];

const normalizeCurtainGstRate = (value = 0) => {
  const numericRate = Number(value);
  return CURTAIN_GST_RATE_OPTIONS.includes(numericRate) ? numericRate : 0;
};

const getSummaryDiscountModeFromType = (discountType) =>
  normalizeGeneralDiscountType(discountType) === "percent" ? "%" : "";

const getDiscountTypeFromSummaryMode = (summaryDiscountMode) =>
  summaryDiscountMode === "%" ? "percent" : "flat";

const normalizeQuoteDiscount = (quoteLike = {}, source = "quote") => {
  const normalized = normalizeGeneralDiscount(quoteLike, source);

  if (isDevelopmentMode()) {
    console.log("QUOTE DISCOUNT SAVE/LOAD", {
      source,
      discountType: normalized.discountType,
      discountValue: normalized.discountValue,
    });
  }

  return normalized;
};

const calculateCanonicalQuoteDiscount = ({
  quoteType = CURTAIN_QUOTE_TYPE,
  subtotal = 0,
  discountType = "flat",
  discountValue = 0,
  source = "quote_totals",
} = {}) => {
  const safeSubtotal = Math.max(0, num(subtotal));
  const normalizedType = normalizeGeneralDiscountType(discountType);
  const rawValue = Math.max(0, num(discountValue));
  const safeValue = normalizedType === "percent" ? Math.min(rawValue, 100) : rawValue;
  const discountAmount =
    normalizedType === "percent"
      ? round2((safeSubtotal * safeValue) / 100)
      : round2(Math.min(safeValue, safeSubtotal));
  const grandTotal = round2(Math.max(0, safeSubtotal - discountAmount));

  if (isDevelopmentMode()) {
    console.log("QUOTE DISCOUNT CALC", {
      source,
      quoteType,
      subtotal: safeSubtotal,
      discountType: normalizedType,
      discountValue: safeValue,
      discountAmount,
      grandTotal,
    });
  }

  return {
    discountType: normalizedType,
    discountValue: safeValue,
    discountAmount,
    grandTotal,
  };
};

const calculateCurtainGstTotals = ({
  subtotal = 0,
  gstRate = 0,
} = {}) => {
  const taxableAmount = round2(Math.max(0, num(subtotal)));
  const normalizedGstRate = normalizeCurtainGstRate(gstRate);
  const gstAmount = round2((taxableAmount * normalizedGstRate) / 100);
  const finalTotal = round2(Math.max(0, taxableAmount + gstAmount));

  return {
    gstRate: normalizedGstRate,
    taxableAmount,
    gstAmount,
    finalTotal,
    grandTotal: finalTotal,
  };
};

const hasExplicitCurtainRowGstRate = (value) =>
  value !== null && value !== undefined && String(value).trim() !== "";

const resolveCurtainAccessoryRowGstRate = (row = {}) =>
  normalizeCurtainGstRate(
    hasExplicitCurtainRowGstRate(row?.gstRate ?? row?.gst)
      ? row?.gstRate ?? row?.gst
      : 0
  );

const resolveCurtainSavedRowGstRate = (row = {}) =>
  isCurtainAccessoryRowStatic(row)
    ? resolveCurtainAccessoryRowGstRate(row)
    : "";

const calculateCurtainQuoteTotals = ({
  rows = [],
  subtotal = 0,
  discountAmount = 0,
  discountType = "flat",
  discountValue = 0,
  quoteGstRate = 0,
  getRowTaxableTotal,
  isAccessoryRow = isCurtainAccessoryRowStatic,
} = {}) => {
  const safeSubtotal = round2(Math.max(0, num(subtotal)));
  const normalizedQuoteGstRate = normalizeCurtainGstRate(quoteGstRate);
  const resolvePostGstDiscount = (baseAmount, source = "curtain_quote_totals") => {
    const hasLegacyDiscountAmount =
      num(discountValue) <= 0 && num(discountAmount) > 0;
    if (hasLegacyDiscountAmount) {
      const safeDiscountAmount = round2(
        Math.min(Math.max(0, num(discountAmount)), Math.max(0, num(baseAmount)))
      );
      return {
        discountType: normalizeGeneralDiscountType(discountType),
        discountValue: safeDiscountAmount,
        discountAmount: safeDiscountAmount,
        grandTotal: round2(Math.max(0, num(baseAmount) - safeDiscountAmount)),
      };
    }

    return calculateCanonicalQuoteDiscount({
      quoteType: CURTAIN_QUOTE_TYPE,
      subtotal: baseAmount,
      discountType,
      discountValue,
      source,
    });
  };
  const rowEntries = rows.map((row, index) => {
    const rowTaxable = round2(Math.max(0, num(getRowTaxableTotal?.(row, index))));
    const rowIsAccessory = Boolean(isAccessoryRow?.(row, index));
    return {
      row,
      taxable: rowTaxable,
      isAccessory: rowIsAccessory,
      gstRate: rowIsAccessory
        ? resolveCurtainAccessoryRowGstRate(row)
        : normalizedQuoteGstRate,
    };
  });
  const rowSubtotal = round2(
    rowEntries.reduce((sum, entry) => sum + entry.taxable, 0)
  );

  if (!rowEntries.length || rowSubtotal <= 0) {
    const emptyTotals = calculateCurtainGstTotals({
      subtotal: safeSubtotal,
      gstRate: normalizedQuoteGstRate,
    });
    const emptyTotalAfterGst = round2(
      Number(emptyTotals.finalTotal ?? emptyTotals.grandTotal ?? 0)
    );
    const emptyDiscountTotals = resolvePostGstDiscount(
      emptyTotalAfterGst,
      "curtain_quote_totals_empty"
    );
    return {
      ...emptyTotals,
      quoteGstRate: normalizedQuoteGstRate,
      measurementSubtotalBeforeDiscount: safeSubtotal,
      accessorySubtotalBeforeDiscount: 0,
      discountType: emptyDiscountTotals.discountType,
      discountValue: emptyDiscountTotals.discountValue,
      discountAmount: emptyDiscountTotals.discountAmount,
      discountApplication: "after_gst",
      postGstDiscountAmount: emptyDiscountTotals.discountAmount,
      measurementDiscountAmount: 0,
      accessoryDiscountAmount: 0,
      taxableMeasurementAmount: emptyTotals.taxableAmount,
      taxableAccessoryAmount: 0,
      measurementGstAmount: emptyTotals.gstAmount,
      accessoryGstAmount: 0,
      subtotalBeforeGst: emptyTotals.taxableAmount,
      totalGstAmount: emptyTotals.gstAmount,
      totalAfterGst: emptyTotalAfterGst,
      finalTotal: emptyDiscountTotals.grandTotal,
      grandTotal: emptyDiscountTotals.grandTotal,
    };
  }

  const measurementEntries = rowEntries.filter((entry) => !entry.isAccessory);
  const accessoryEntries = rowEntries.filter((entry) => entry.isAccessory);
  const measurementSubtotalBeforeDiscount = round2(
    measurementEntries.reduce((sum, entry) => sum + entry.taxable, 0)
  );
  const accessorySubtotalBeforeDiscount = round2(
    accessoryEntries.reduce((sum, entry) => sum + entry.taxable, 0)
  );
  const measurementDiscountAmount = 0;
  const accessoryDiscountAmount = 0;
  const taxableMeasurementAmount = measurementSubtotalBeforeDiscount;
  const taxableAccessoryAmount = accessorySubtotalBeforeDiscount;
  const measurementGstAmount = round2(
    (taxableMeasurementAmount * normalizedQuoteGstRate) / 100
  );
  const accessoryGstAmount = round2(
    accessoryEntries.reduce((sum, entry) => {
      return sum + (entry.taxable * entry.gstRate) / 100;
    }, 0)
  );
  const subtotalBeforeGst = round2(taxableMeasurementAmount + taxableAccessoryAmount);
  const totalGstAmount = round2(measurementGstAmount + accessoryGstAmount);
  const totalAfterGst = round2(Math.max(0, subtotalBeforeGst + totalGstAmount));
  const discountTotals = resolvePostGstDiscount(totalAfterGst);
  const finalTotal = discountTotals.grandTotal;
  const accessoryGstRates = Array.from(
    new Set(accessoryEntries.map((entry) => entry.gstRate).filter((rate) => rate > 0))
  );
  const distinctRates = Array.from(
    new Set([
      ...(normalizedQuoteGstRate > 0 && measurementSubtotalBeforeDiscount > 0
        ? [normalizedQuoteGstRate]
        : []),
      ...accessoryGstRates,
    ])
  );
  const hasMeasurementGst = measurementGstAmount > 0;
  const hasAccessoryGst = accessoryGstAmount > 0;

  return {
    gstRate: normalizedQuoteGstRate,
    quoteGstRate: normalizedQuoteGstRate,
    gstRates: distinctRates,
    accessoryGstRates,
    hasMixedGstRates: hasMeasurementGst && hasAccessoryGst,
    measurementSubtotalBeforeDiscount,
    accessorySubtotalBeforeDiscount,
    discountType: discountTotals.discountType,
    discountValue: discountTotals.discountValue,
    discountAmount: discountTotals.discountAmount,
    discountApplication: "after_gst",
    postGstDiscountAmount: discountTotals.discountAmount,
    measurementDiscountAmount,
    accessoryDiscountAmount,
    taxableMeasurementAmount,
    taxableAccessoryAmount,
    taxableAmount: subtotalBeforeGst,
    subtotalBeforeGst,
    measurementGstAmount,
    accessoryGstAmount,
    gstAmount: totalGstAmount,
    totalGstAmount,
    totalAfterGst,
    finalTotal,
    grandTotal: finalTotal,
  };
};

const safeFabricKey = (fab) =>
  (fab || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_") || "__no_fabric__";



/* -----------------------Firestore Timestamp  input and text-----------------------------------------*/

const GENERAL_IMAGE_TTL_MS =
  GENERAL_QUOTE_IMAGE_TTL_DAYS * 24 * 60 * 60 * 1000;

const safeTimeMillis = (value) => {
  if (!value) return 0;

  if (typeof value?.toMillis === "function") {
    return value.toMillis();
  }

  if (typeof value?.toDate === "function") {
    const dateValue = value.toDate();
    return dateValue instanceof Date && !isNaN(dateValue.getTime())
      ? dateValue.getTime()
      : 0;
  }

  if (value instanceof Date) {
    return !isNaN(value.getTime()) ? value.getTime() : 0;
  }

  const parsed = new Date(value);
  return !isNaN(parsed.getTime()) ? parsed.getTime() : 0;
};

const normalizeFileName = (name = "") =>
  String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9.-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "") || "item-image";

const GENERAL_QUOTE_IMAGE_STORAGE_ROOT = "generalQuotationImages";
const GENERAL_QUOTE_ALLOWED_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
]);
const GENERAL_QUOTE_MAX_IMAGE_BYTES = 10 * 1024 * 1024;

const validateGeneralQuoteImageFile = (file) => {
  const fileType = String(file?.type || "").toLowerCase();
  if (!GENERAL_QUOTE_ALLOWED_IMAGE_TYPES.has(fileType)) {
    return {
      valid: false,
      message: "Please choose a JPG, PNG, or WebP image.",
    };
  }
  if (Number(file?.size || 0) > GENERAL_QUOTE_MAX_IMAGE_BYTES) {
    return {
      valid: false,
      message: "Image is too large. Please choose an image under 10 MB.",
    };
  }
  return { valid: true, message: "" };
};

const buildGeneralQuoteImageStoragePath = ({
  workspaceUid,
  quoteId = "",
  draftId = "",
  itemId,
  imageId,
  fileName,
  timestamp = Date.now(),
} = {}) => {
  const workspaceSegment = sanitizeFirestoreDocIdSegment(workspaceUid);
  const quoteSegment = quoteId
    ? sanitizeFirestoreDocIdSegment(quoteId)
    : `drafts/${sanitizeFirestoreDocIdSegment(draftId || "draft-new")}`;
  return `${GENERAL_QUOTE_IMAGE_STORAGE_ROOT}/${workspaceSegment}/${quoteSegment}/${sanitizeFirestoreDocIdSegment(
    itemId
  )}/${sanitizeFirestoreDocIdSegment(imageId)}-${Number(timestamp) || Date.now()}-${normalizeFileName(
    fileName || "item-image"
  )}`;
};

const buildCrmManagedAttachmentStoragePath = ({
  workspaceUid,
  leadId,
  folder = "",
  attachmentId,
  fileName,
  timestamp = Date.now(),
} = {}) => {
  const workspaceSegment = sanitizeFirestoreDocIdSegment(workspaceUid);
  const leadSegment = sanitizeFirestoreDocIdSegment(leadId || "crm-record");
  const folderSegment = folder ? `/${sanitizeFirestoreDocIdSegment(folder)}` : "";
  return `crmAttachments/${workspaceSegment}/${leadSegment}${folderSegment}/${sanitizeFirestoreDocIdSegment(
    attachmentId || createClientId("crm-attachment")
  )}-${Number(timestamp) || Date.now()}-${normalizeFileName(fileName || "attachment")}`;
};

const isGeneralQuoteDraftImagePath = (path = "", workspaceUid = "") =>
  String(path || "").startsWith(
    `${GENERAL_QUOTE_IMAGE_STORAGE_ROOT}/${sanitizeFirestoreDocIdSegment(workspaceUid)}/drafts/`
  );

const hasGeneralQuoteDraftImagePaths = (quote = {}, workspaceUid = "") =>
  (Array.isArray(quote?.lineItems) ? quote.lineItems : []).some((item) =>
    normalizeGeneralQuoteItemImages(item).some((image) =>
      isGeneralQuoteDraftImagePath(getGeneralQuoteImageStoragePath(image), workspaceUid)
    )
  );

const uploadGeneralQuoteImage = async ({
  workspaceUid,
  quoteId = "",
  draftId = "",
  itemId,
  imageId,
  file,
  metadata = {},
  timestamp = Date.now(),
} = {}) => {
  const validation = validateGeneralQuoteImageFile(file);
  if (!validation.valid) {
    const error = new Error(validation.message);
    error.code = "invalid-general-quote-image";
    throw error;
  }

  const uploadedAt = new Date().toISOString();
  const resolvedImageId = String(imageId || createClientId("general-image"));
  const safeFileName = normalizeFileName(file.name || "item-image");
  const storagePath = buildGeneralQuoteImageStoragePath({
    workspaceUid,
    quoteId,
    draftId,
    itemId,
    imageId: resolvedImageId,
    fileName: safeFileName,
    timestamp,
  });
  const managedImage = await uploadManagedImage({
    workspaceUid,
    file,
    ownerType: "generalQuote",
    ownerId: quoteId || (draftId ? `drafts/${draftId}` : ""),
    quoteId: quoteId || "",
    itemId,
    imageId: resolvedImageId,
    fullPath: storagePath,
    uploadedBy: auth.currentUser?.uid || "",
    metadata: {
      workspaceUid,
      quoteId: quoteId || (draftId ? `drafts/${draftId}` : ""),
      quoteType: GENERAL_QUOTE_TYPE,
      itemId,
      imageId: resolvedImageId,
      uploadedAt,
      ...metadata,
    },
  });

  return createGeneralQuoteImage({
    ...managedImage,
    id: resolvedImageId,
    imageId: resolvedImageId,
    name: managedImage.filename || file.name,
    filename: managedImage.filename || file.name,
    bucket: firebaseApp.options.storageBucket || "",
    storageBucket: firebaseApp.options.storageBucket || "",
    uploadedAt,
  });
};

const getGeneralQuoteImageDisplayUrl = (image = {}) => {
  const url = String(image?.downloadURL || image?.url || image?.previewUrl || "").trim();
  return isProtectedFirebaseStorageRestUrl(url) ? "" : url;
};

const getGeneralQuoteImageStoragePath = (image = {}) =>
  String(image?.fullPath || image?.storagePath || image?.path || "").trim();

const getGeneralQuoteImageCacheKey = (image = {}) => {
  const bucket = String(image?.storageBucket || "").trim();
  const path = getGeneralQuoteImageStoragePath(image);
  return `${bucket || firebaseApp.options.storageBucket || "default"}:${path}`;
};

const getGeneralQuoteImageStorageRef = (image = {}) => {
  const fullPath = getGeneralQuoteImageStoragePath(image);
  const bucket = String(image?.storageBucket || "").trim();
  if (bucket && fullPath) {
    return ref(storage, `gs://${bucket}/${fullPath}`);
  }
  return ref(storage, fullPath);
};

const isJonyLeadsImageDebugContext = (context = {}) => {
  const haystack = [
    context.quoteNo,
    context.quoteId,
    context.customerName,
    context.itemName,
  ]
    .map((value) => String(value || "").toLowerCase())
    .join(" ");
  return (
    haystack.includes("ac-2026-9451") ||
    haystack.includes("jonyleads") ||
    haystack.includes("rolex chair without arms")
  );
};

const shouldLogGeneralQuoteImageDebug = (context = {}) =>
  isDevelopmentMode() || isJonyLeadsImageDebugContext(context);

const EXACT_JONYLEADS_IMAGE_BUCKET = "accentsquotationapp.firebasestorage.app";
const EXACT_JONYLEADS_IMAGE_FULL_PATH =
  "general-quote-items/scg3CjxGaihVxRL2baAF6R3qacm2/general-item-0b4f9d8f-7e9b-4da7-9f11-5349f0b30260-1776335819996-whatsapp-image-2026-04-16-at-4.00.37-pm.jpeg";

const getRawGeneralQuoteImageDebugEntries = (item = {}) => {
  const entries = [];
  const push = (rawImage, source) => {
    if (rawImage == null || rawImage === "") return;
    entries.push({ rawImage, source });
  };
  [
    ["images", item?.images],
    ["itemImages", item?.itemImages],
    ["productImages", item?.productImages],
    ["referenceImages", item?.referenceImages],
    ["attachments", item?.attachments],
  ].forEach(([source, value]) => {
    if (Array.isArray(value)) value.forEach((image) => push(image, source));
  });
  [
    ["image", item?.image],
    ["imageData", item?.imageData],
    ["referenceImage", item?.referenceImage],
    ["productImage", item?.productImage],
  ].forEach(([source, value]) => push(value, source));
  if (
    item?.imageUrl ||
    item?.imageURL ||
    item?.downloadURL ||
    item?.downloadUrl ||
    item?.imageStoragePath ||
    item?.fullPath ||
    item?.storagePath ||
    item?.path ||
    item?.filePath ||
    item?.gsUrl ||
    item?.gsURL ||
    item?.imageName ||
    item?.imagePreviewName ||
    item?.filename
  ) {
    push(
      {
        imageUrl: item?.imageUrl || item?.imageURL || "",
        downloadURL: item?.downloadURL || item?.downloadUrl || "",
        imageStoragePath: item?.imageStoragePath || "",
        fullPath: item?.fullPath || "",
        storagePath: item?.storagePath || "",
        path: item?.path || "",
        filePath: item?.filePath || "",
        gsUrl: item?.gsUrl || item?.gsURL || "",
        imageName: item?.imageName || "",
        imagePreviewName: item?.imagePreviewName || "",
        filename: item?.filename || "",
      },
      "legacy-item-fields"
    );
  }
  return entries;
};

const getGeneralQuoteImageResolveErrorMessage = (error) => {
  const code = String(error?.code || "").trim();
  if (code === "storage/unauthorized") {
    return "You do not have permission to view this image.";
  }
  if (code === "storage/object-not-found") {
    return "Image file was not found in Storage.";
  }
  if (code === "storage/retry-limit-exceeded" || /network/i.test(String(error?.message || ""))) {
    return "Image could not load due to network issue.";
  }
  return "Image unavailable.";
};

// eslint-disable-next-line no-unused-vars
const ellipsizeChartLabel = (value = "", maxLength = 18) => {
  const text = String(value || "").trim();
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return `${text.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
};



/* ================================================================
   MAIN COMPONENT
   ================================================================ */

export default function QuotationApp() {
  const {
    account,
    accountUser,
    isSuperAdmin,
    features,
    hasFeature,
    hasActiveSubscription,
    isTrialAccount,
    loading: accountLoading,
    currentAccount,
    workspaceUid: accountWorkspaceUid,
    currentUser: accountCurrentUser,
    refreshAccount,
    trialQuotationLimit,
    trialQuotationsRemaining,
    trialQuotationsUsed,
  } = useAccount();
  const [user, setUser] = useState(null);
  const [workspaceContext, setWorkspaceContext] = useState(null);
  const [isResolvingWorkspace, setIsResolvingWorkspace] = useState(true);
  const [workspaceBootstrapError, setWorkspaceBootstrapError] = useState(null);
  const [workspaceBootstrapDebug, setWorkspaceBootstrapDebug] = useState(() =>
    buildWorkspaceBootstrapDebug({ loadingStep: "initializing" })
  );
  const workspaceBootstrapRunRef = useRef(0);
  const { isMobile, isTablet, isCompact } = useViewport();
const [isNewQuote, setIsNewQuote] = useState(true);
const [selectedQuote, setSelectedQuote] = useState(null);
const [selectedSolutionIndex, setSelectedSolutionIndex] = useState(0);
const [isGeneralQuoteDebugOpen, setIsGeneralQuoteDebugOpen] = useState(false);
const [generalQuoteDebugUploadFile, setGeneralQuoteDebugUploadFile] =
  useState(null);
const [generalQuoteDebugState, setGeneralQuoteDebugState] = useState({
  uploadState: "idle",
  lastUpload: null,
  lastUploadError: null,
  lastSave: null,
  lastSaveError: null,
  directFirestoreTest: null,
  directStorageTest: null,
});

// ====================== STOCK TAB STATE ======================
const [stockQuoteId, setStockQuoteId] = useState("");
const [stockFabricName, setStockFabricName] = useState("");
const [stockRollMeters, setStockRollMeters] = useState(""); // comma separated
const [stockLooseMeters, setStockLooseMeters] = useState("");
const [stockWastageMeters, setStockWastageMeters] = useState("");






  /* Settings (per user) */
  const [stitchingRates, setStitchingRates] = useState(DEFAULT_STITCHING);
  const [minSqft, setMinSqft] = useState(DEFAULT_MIN_SQFT);
  const [productConfigs, setProductConfigs] = useState(() =>
    mergeProductConfigsWithDefaults([])
  );
  const [productConfigsLoadState, setProductConfigsLoadState] = useState({
    loading: false,
    loadedWorkspace: "",
    error: "",
  });
  const [productConfigDraft, setProductConfigDraft] = useState(() =>
    createProductConfigDraft({ name: "Mosquito Net", calculationType: "sqft_only" })
  );
  const [editingProductConfigId, setEditingProductConfigId] = useState("");
  const [savingProductConfigId, setSavingProductConfigId] = useState("");
  const productConfigsLoadedWorkspaceRef = useRef("");
  const productConfigsLoadingWorkspaceRef = useRef("");
  const [currency, setCurrency] = useState("Rs. Indian Rupee");
  const [businessName, setBusinessName] = useState("");
  const [shopAddress, setShopAddress] = useState("");
  const [businessPhone, setBusinessPhone] = useState("");
  const [businessEmail, setBusinessEmail] = useState("");
  const [teamMembers, setTeamMembers] = useState([]);
  const [logoURL, setLogoURL] = useState("");
  const [isTeamMemberModalOpen, setIsTeamMemberModalOpen] = useState(false);
  const [teamMemberDraft, setTeamMemberDraft] = useState(() =>
    createTeamMemberDraft({
      permissions: DEFAULT_TEAM_PERMISSIONS,
      status: "draft",
      isActive: false,
    })
  );
  const [teamMemberModalError, setTeamMemberModalError] = useState("");
  const [isSendingTeamInvite, setIsSendingTeamInvite] = useState(false);
  const [resendingTeamInviteId, setResendingTeamInviteId] = useState("");
  const [isTestingTeamInviteEmail, setIsTestingTeamInviteEmail] = useState(false);

  const [newStitch, setNewStitch] = useState("");
  const [newRate, setNewRate] = useState("");
  const [newFullness, setNewFullness] = useState("3");
  const [newTrackLengthFt, setNewTrackLengthFt] = useState("16");
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isStitchingSettingsDirty, setIsStitchingSettingsDirty] = useState(false);







  /* Theme + nav */
  const [isDark, setIsDark] = useState(false);
  const [tab, setTab] = useState("quote"); // quote | summary | settings | stitching | saved
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [expandedCurtainRows, setExpandedCurtainRows] = useState({});


  /* Quotation meta */
  const [customerName, setCustomerName] = useState("");
  const [quoteCustomerLink, setQuoteCustomerLink] = useState({
    customerId: "",
    customerKey: "",
    customerName: "",
    phone: "",
    whatsapp: "",
    email: "",
    address: "",
    companyName: "",
  });
  const [customerPickerOpenKey, setCustomerPickerOpenKey] = useState("");
  const [customerCreateState, setCustomerCreateState] = useState(null);
  const [isCreatingCustomerFromQuote, setIsCreatingCustomerFromQuote] = useState(false);
  const [quoteNo, setQuoteNo] = useState("");
  const [date, setDate] = useState(
    () => getTodayLocalDateString()
  );
  const [quoteSubTab, setQuoteSubTab] = useState(CURTAIN_QUOTE_TYPE);
  const [generalEditingQuoteId, setGeneralEditingQuoteId] = useState(null);
  const [generalUploadingItemId, setGeneralUploadingItemId] = useState("");
  const [generalRepairingImageKey, setGeneralRepairingImageKey] = useState("");
  const [generalImageRepairState, setGeneralImageRepairState] = useState({});
  const [generalImageRepairCandidates, setGeneralImageRepairCandidates] = useState({});
  const [generalQuote, setGeneralQuote] = useState(() =>
    createDefaultGeneralQuote({
      currency,
      businessName,
      shopAddress,
      businessPhone,
      businessEmail,
    })
  );
  const generalQuoteRef = useRef(generalQuote);
  const generalUploadPromiseRef = useRef(null);
  const generalQuoteImageUrlCacheRef = useRef(new Map());
  const generalQuoteImageRepairDebugRef = useRef({});
  generalQuoteRef.current = generalQuote;
  const setGeneralQuoteLive = useCallback((updater) => {
    setGeneralQuote((previousQuote) => {
      const nextQuote =
        typeof updater === "function" ? updater(previousQuote) : updater;
      generalQuoteRef.current = nextQuote;
      return nextQuote;
    });
  }, []);

// ---------------- TRACK CUTTING OPTIMIZER (TCO) STATE ----------------
const [, setTrackLeftovers] = useState({});   // key -> remaining ft
const [, setTrackCutPlan] = useState([]);       // optimization results


const [, setIsLoadingQuote] = useState(false);

const toggleCurtainRowExpanded = useCallback((rowKey) => {
  setExpandedCurtainRows((prev) => ({
    ...prev,
    [rowKey]: !prev[rowKey],
  }));
}, []);
/* ---------------- DASHBOARD STATES ---------------- */
const [dashView, setDashView] = useState("home"); 
// "home" | "month" | "confirmed" | "draft" | "final"
const [dashboardMode, setDashboardMode] = useState("business");
const [businessDashboardDrilldownKey, setBusinessDashboardDrilldownKey] = useState("");
const [dashboardDrilldownModal, setDashboardDrilldownModal] = useState(null);
const [dashboardDrilldownSearch, setDashboardDrilldownSearch] = useState("");
const [crmView, setCrmView] = useState("overview");
const [crmScope, setCrmScope] = useState(CRM_SCOPE_ALL);
const [crmPipelineType, setCrmPipelineType] = useState(CURTAIN_QUOTE_TYPE);
const [selectedCrmDealId, setSelectedCrmDealId] = useState("");
const [crmDetailDraft, setCrmDetailDraft] = useState(null);
const [crmSearch, setCrmSearch] = useState("");
const deferredCrmSearch = useDeferredValue(crmSearch);
const [crmLeadSearch, setCrmLeadSearch] = useState("");
const deferredCrmLeadSearch = useDeferredValue(crmLeadSearch);
const [crmDealsVisibleCount, setCrmDealsVisibleCount] = useState(CRM_DEALS_PAGE_SIZE);
const [crmLeadsVisibleCount, setCrmLeadsVisibleCount] = useState(CRM_LEADS_PAGE_SIZE);
const [crmDuplicateDealAudit, setCrmDuplicateDealAudit] = useState({
  scanned: false,
  isScanning: false,
  groups: [],
});
const [repairingDuplicateDealKey, setRepairingDuplicateDealKey] = useState("");
const [isRepairingCrmSearchIndex, setIsRepairingCrmSearchIndex] = useState(false);
const [crmStageFilter, setCrmStageFilter] = useState("all");
const [crmTypeFilter, setCrmTypeFilter] = useState("all");
const [crmSourceFilter, setCrmSourceFilter] = useState("all");
const [crmSortBy, setCrmSortBy] = useState("newest");
const [crmLeadStageFilter, setCrmLeadStageFilter] = useState("all");
const [crmLeadSourceFilter, setCrmLeadSourceFilter] = useState("all");
const [crmLeadAssignedFilter, setCrmLeadAssignedFilter] = useState("all");
const [crmLeadFromDate, setCrmLeadFromDate] = useState("");
const [crmLeadToDate, setCrmLeadToDate] = useState("");
const [quoteLinkModalState, setQuoteLinkModalState] = useState(null);
const [quoteDealChoiceState, setQuoteDealChoiceState] = useState(null);
const [quoteLinkSearch, setQuoteLinkSearch] = useState("");
const [quoteLinkTypeFilter, setQuoteLinkTypeFilter] = useState("all");
const [quoteLinkMinAmount, setQuoteLinkMinAmount] = useState("");
const [quoteLinkMaxAmount, setQuoteLinkMaxAmount] = useState("");
const [isLinkingQuote, setIsLinkingQuote] = useState(false);
const [crmDrilldownKey, setCrmDrilldownKey] = useState("");
const [crmLeadRecords, setCrmLeadRecords] = useState([]);
const [debugRawCrmLeadCount, setDebugRawCrmLeadCount] = useState(0);
const [debugRawQuotationCount, setDebugRawQuotationCount] = useState(0);
const [quoteSearchRepairState, setQuoteSearchRepairState] = useState({
  running: false,
  processed: 0,
  updated: 0,
  error: "",
});
const [analyticsRebuildState, setAnalyticsRebuildState] = useState({
  running: false,
  scanned: 0,
  indexedQuotes: 0,
  monthlyUpdated: 0,
  clientUpdated: 0,
  installationsUpdated: 0,
  staleCleared: 0,
  skipped: 0,
  failed: 0,
  error: "",
});
const [adminDataAuditState, setAdminDataAuditState] = useState({
  running: false,
  checkedAt: "",
  error: "",
  collections: {},
  progress: {
    collection: "",
    scanned: 0,
  },
  report: null,
});
const [dashboardAnalyticsState, setDashboardAnalyticsState] = useState({
  monthKeys: [],
  monthlySummaries: {},
  topClients: [],
  installationRecords: [],
  diagnostics: {
    source: "idle",
    reads: {},
    monthKey: "",
    loadedAt: "",
  },
});
const [customerRecords, setCustomerRecords] = useState([]);
const [customerLedgerSearch, setCustomerLedgerSearch] = useState("");
const deferredCustomerLedgerSearch = useDeferredValue(customerLedgerSearch);
const [globalSearchStatus, setGlobalSearchStatus] = useState({});
const [customerLedgerSourceFilter, setCustomerLedgerSourceFilter] = useState("all");
const [customerLedgerSortBy, setCustomerLedgerSortBy] = useState("latest");
const [selectedCustomerLedgerId, setSelectedCustomerLedgerId] = useState("");
const [customerLedgerDetailTab, setCustomerLedgerDetailTab] = useState("overview");
const [customerLedgerCrmDealsByCustomer, setCustomerLedgerCrmDealsByCustomer] =
  useState({});
const [customerLedgerCrmDealsLoadingId, setCustomerLedgerCrmDealsLoadingId] =
  useState("");
const customerLedgerCrmDealsLoadRef = useRef({});
const [customerLedgerQuotesByCustomer, setCustomerLedgerQuotesByCustomer] =
  useState({});
const [customerLedgerQuotesLoadingId, setCustomerLedgerQuotesLoadingId] =
  useState("");
const customerLedgerQuotesLoadRef = useRef({});
const [quotationCustomerSearchQueries, setQuotationCustomerSearchQueries] =
  useState({});
const [quotationCustomerSearchState, setQuotationCustomerSearchState] =
  useState({});
const quotationCustomerSearchCacheRef = useRef(new Map());
const quotationCustomerSearchRequestRef = useRef({});
const [customerSearchRepairState, setCustomerSearchRepairState] = useState({
  running: false,
  processed: 0,
  updated: 0,
  error: "",
});
const [customerLedgerPaymentDraft, setCustomerLedgerPaymentDraft] = useState({
  amount: "",
  date: todayISO(),
  notes: "",
});
const [customerLocationDraft, setCustomerLocationDraft] = useState(() =>
  createSiteLocationDraft()
);
const [crmSiteLocationDraft, setCrmSiteLocationDraft] = useState(() =>
  createSiteLocationDraft()
);
const [isSavingCustomerPayment, setIsSavingCustomerPayment] = useState(false);
const migratedCrmQuoteLinksRef = useRef(new Set());
const [crmExecutionTemplates, setCrmExecutionTemplates] = useState([]);
const [executionTemplatesLoadState, setExecutionTemplatesLoadState] = useState({
  workspaceUid: "",
  loading: false,
  loaded: false,
  error: "",
});
const [isSavingExecutionTemplates, setIsSavingExecutionTemplates] = useState(false);
const [crmReminderRecords, setCrmReminderRecords] = useState([]);
const [crmTaskHistoryRecords, setCrmTaskHistoryRecords] = useState([]);
const [crmReminderNow, setCrmReminderNow] = useState(() => Date.now());
const globalReminderCheckedWorkspaceRef = useRef("");
const globalReminderLastCheckRef = useRef(0);
const globalReminderShownIdsRef = useRef(new Set());
const [isCrmLeadModalOpen, setIsCrmLeadModalOpen] = useState(false);
const [crmLeadDraft, setCrmLeadDraft] = useState(null);
const [isSavingCrmLead, setIsSavingCrmLead] = useState(false);
const [pendingCrmLeadId, setPendingCrmLeadId] = useState("");
const [crmPipelines, setCrmPipelines] = useState(() =>
  normalizeCrmPipelines(DEFAULT_CRM_PIPELINES)
);
const [crmAttachmentUploadDealId, setCrmAttachmentUploadDealId] = useState("");
const [crmFollowUpAttachmentUploadKey, setCrmFollowUpAttachmentUploadKey] = useState("");
const [isSavingCrmDetails, setIsSavingCrmDetails] = useState(false);
const [markingDoneKey, setMarkingDoneKey] = useState("");
const [crmDetailsSavedAt, setCrmDetailsSavedAt] = useState(null);
const [crmSaveDebugExpanded, setCrmSaveDebugExpanded] = useState(false);
const [crmSaveDebugState, setCrmSaveDebugState] = useState({
  lastPayload: null,
  lastImmediateReadback: null,
  lastDelayedReadback: null,
  lastWritePath: "",
  lastVerification: null,
});
const [crmDetailsDebugExpanded, setCrmDetailsDebugExpanded] = useState(false);
const [crmDetailsOpenDebug, setCrmDetailsOpenDebug] = useState({
  lastSource: "",
  lastRecordId: "",
  lastResolvedLeadId: "",
  lastResolveReason: "",
  lastCustomerName: "",
  lastQuoteNo: "",
  lastError: "",
});
const [crmFollowUpModalState, setCrmFollowUpModalState] = useState(null);
const [crmExecutionAttachmentUploadKey, setCrmExecutionAttachmentUploadKey] = useState("");
const quotationSettingsLoadedWorkspaceRef = useRef("");
const quotationSettingsLoadingWorkspaceRef = useRef("");
const [quotationSettingsReadyWorkspace, setQuotationSettingsReadyWorkspace] = useState("");
const mainSettingsLoadedWorkspaceRef = useRef("");
const mainSettingsLoadingWorkspaceRef = useRef("");
const trackStockLoadedWorkspaceRef = useRef("");
const trackStockLoadingWorkspaceRef = useRef("");
const [executionWorkspaceSearch, setExecutionWorkspaceSearch] = useState("");
const [executionWorkspaceSourceFilter, setExecutionWorkspaceSourceFilter] = useState("all");
const [executionWorkspaceStageFilter, setExecutionWorkspaceStageFilter] = useState("all");
const [executionWorkspaceItemTypeFilter, setExecutionWorkspaceItemTypeFilter] = useState("all");
const [executionWorkspaceAttachmentFilter, setExecutionWorkspaceAttachmentFilter] = useState("all");
const [executionWorkspaceSortBy, setExecutionWorkspaceSortBy] = useState("updated");
const [selectedExecutionItemId, setSelectedExecutionItemId] = useState("");
const [selectedExecutionTemplateId, setSelectedExecutionTemplateId] = useState("");
const [executionWorkspaceOpeningDealId, setExecutionWorkspaceOpeningDealId] = useState("");
const [executionTemplateSearch, setExecutionTemplateSearch] = useState("");
const [executionTemplateSortBy, setExecutionTemplateSortBy] = useState("name");
const [selectedExecutionTemplateManagerId, setSelectedExecutionTemplateManagerId] = useState("");
const [crmTaskHistorySearch, setCrmTaskHistorySearch] = useState("");
const deferredCrmTaskHistorySearch = useDeferredValue(crmTaskHistorySearch);
const [crmTaskHistoryTypeFilter, setCrmTaskHistoryTypeFilter] = useState("all");
const [crmTaskHistoryCompletedByFilter, setCrmTaskHistoryCompletedByFilter] = useState("all");
const [crmTaskHistoryFromDate, setCrmTaskHistoryFromDate] = useState("");
const [crmTaskHistoryToDate, setCrmTaskHistoryToDate] = useState("");
const [selectedCrmTaskHistoryId, setSelectedCrmTaskHistoryId] = useState("");
const [dailyTaskDate, setDailyTaskDate] = useState(() => todayISO());
const [dailyTaskDateMode, setDailyTaskDateMode] = useState("today");
const [dailyTaskRangeStart, setDailyTaskRangeStart] = useState(() => todayISO());
const [dailyTaskRangeEnd, setDailyTaskRangeEnd] = useState(() => todayISO());
const [dailyTaskSearch, setDailyTaskSearch] = useState("");
const deferredDailyTaskSearch = useDeferredValue(dailyTaskSearch);
const [dailyTaskAssignedFilter, setDailyTaskAssignedFilter] = useState("all");
const [dailyTaskTypeFilter, setDailyTaskTypeFilter] = useState("all");
const [dailyTaskPriorityFilter, setDailyTaskPriorityFilter] = useState("all");
const [dailyTaskStatusFilter, setDailyTaskStatusFilter] = useState("all");
const [dailyTaskSourceFilter, setDailyTaskSourceFilter] = useState("all");
const [dailyTaskQuoteTypeFilter, setDailyTaskQuoteTypeFilter] = useState("all");
const [selectedDailyTaskId, setSelectedDailyTaskId] = useState("");
const [dailyTaskRescheduleState, setDailyTaskRescheduleState] = useState(null);
const [appToasts, setAppToasts] = useState([]);
const [confirmDialog, setConfirmDialog] = useState(null);
const [firebaseHealthWarning, setFirebaseHealthWarning] = useState(null);
const [firebaseReadHealthTick, setFirebaseReadHealthTick] = useState(0);
const [securitySessionId, setSecuritySessionId] = useState("");
const [securitySessions, setSecuritySessions] = useState([]);
const [securityActivity, setSecurityActivity] = useState([]);
const [securityCenterState, setSecurityCenterState] = useState({
  loading: false,
  error: "",
  loadedForUid: "",
});
const [securityActionState, setSecurityActionState] = useState({
  password: false,
  currentSession: false,
  otherSessions: false,
  allSessions: false,
  targetSessionId: "",
});
const [passwordForm, setPasswordForm] = useState({
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
  logoutOtherSessions: true,
  showPasswords: false,
  message: "",
  error: "",
});
const securitySessionRegisteredRef = useRef({
  uid: "",
  sessionId: "",
  registering: false,
  lastHeartbeatAt: 0,
});

// ---- INSTALLATION CALENDAR STATE ----
const [calendarMonth, setCalendarMonth] = useState(() => {
  const today = new Date();
  return { year: today.getFullYear(), month: today.getMonth() }; // month: 011
});
const [selectedInstallDate, setSelectedInstallDate] = useState("");
const [isCrmDrawerHovered, setIsCrmDrawerHovered] = useState(false);
const [isCrmDrawerPinned, setIsCrmDrawerPinned] = useState(false);

const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December"
];

const changeCalendarMonth = (delta) => {
  setCalendarMonth((prev) => {
    const raw = prev.month + delta;
    const year = prev.year + Math.floor(raw / 12);
    const month = ((raw % 12) + 12) % 12;
    return { year, month };
  });
};


  /* Quote-level summary discount */
  const [summaryDiscountMode, setSummaryDiscountMode] = useState("%"); // "% | ""
  const [summaryDiscountValue, setSummaryDiscountValue] = useState("0");
  const [curtainGstRate, setCurtainGstRate] = useState(0);
  const [quoteFullnessOverrides, setQuoteFullnessOverrides] = useState({});
  const [quoteFullnessOverrideInputs, setQuoteFullnessOverrideInputs] =
    useState({});
  const [curtainTotalsSettingsEditVersion, setCurtainTotalsSettingsEditVersion] =
    useState(0);
  const summaryDiscountModeRef = useRef(summaryDiscountMode);
  const summaryDiscountValueRef = useRef(summaryDiscountValue);
  const curtainGstRateRef = useRef(curtainGstRate);
  const quoteFullnessOverridesRef = useRef(quoteFullnessOverrides);
  const curtainSummaryTotalsRef = useRef(null);

  /* Rows */
  const [rows, setRows] = useState(() => [createDefaultCurtainQuoteRow("W1")]);
  const rowsRef = useRef(rows);
  const currentQuoteEditVersionRef = useRef(0);
  const lastQuoteFieldMutationRef = useRef(null);
  const [quoteDataIntegrityWatch, setQuoteDataIntegrityWatch] = useState(null);
  const [legacyRecalculationModalOpen, setLegacyRecalculationModalOpen] =
    useState(false);
  const [legacyRecalculationPreview, setLegacyRecalculationPreview] =
    useState(null);
  const [pendingRecalculationAudit, setPendingRecalculationAudit] =
    useState(null);
  const [pendingLegacyCalculationSnapshot, setPendingLegacyCalculationSnapshot] =
    useState(null);
  const [legacyRecalculationRevisionRequest, setLegacyRecalculationRevisionRequest] =
    useState(null);
  const legacyRecalculationRevisionSaveRef = useRef("");
  const legacyCurtainRecalculationImpactRef = useRef({
    shouldShowBanner: false,
    recalculationImpact: LEGACY_CALCULATION_IMPACT.NO_IMPACT,
  });
  const saveQuotationAsNewRef = useRef(null);


// ================= REPEAT SETTINGS (PER QUOTE / PER FABRIC) =================
const [repeatSettings, setRepeatSettings] = useState({});
const repeatSettingsRef = useRef(repeatSettings);
const [fabricSummaryMeta, setFabricSummaryMeta] = useState({});
const fabricSummaryMetaRef = useRef(fabricSummaryMeta);
const [fabricBrands, setFabricBrands] = useState([]);
const fabricBrandsRef = useRef([]);
const [fabricBrandsLoading, setFabricBrandsLoading] = useState(false);
const [fabricBrandDraft, setFabricBrandDraft] = useState({
  name: "",
  supplier: "",
  notes: "",
});
const fabricBrandsLoadedWorkspaceRef = useRef("");
const fabricBrandsLoadingWorkspaceRef = useRef("");
const [expandedFabricBrandId, setExpandedFabricBrandId] = useState("");
const [fabricCatalogueLibraryByBrand, setFabricCatalogueLibraryByBrand] =
  useState({});
const fabricCatalogueLibraryRef = useRef({});
const fabricCatalogueLoadedBrandKeysRef = useRef(new Set());
const fabricCatalogueLoadingBrandKeysRef = useRef(new Set());
const fabricCatalogueAllBrandsLoadedWorkspaceRef = useRef("");
const fabricCatalogueAllBrandsLoadingWorkspaceRef = useRef("");
const [fabricCatalogueDrafts, setFabricCatalogueDrafts] = useState({});
const [fabricSrRuleDrafts, setFabricSrRuleDrafts] = useState({});
const [fabricCatalogueSavingKey, setFabricCatalogueSavingKey] = useState("");
const [activeFabricSuggestionCell, setActiveFabricSuggestionCell] = useState("");
const [fabricSuggestionKeyboardIndex, setFabricSuggestionKeyboardIndex] =
  useState(0);
const [fabricSuggestionPosition, setFabricSuggestionPosition] = useState(null);
const fabricSrInputRefs = useRef({});
const [useHalfPieceReuse, setUseHalfPieceReuse] = useState(true);
const [passHalfPieceSavingsToCustomer, setPassHalfPieceSavingsToCustomer] = useState(false);
const useHalfPieceReuseRef = useRef(useHalfPieceReuse);
const passHalfPieceSavingsToCustomerRef = useRef(passHalfPieceSavingsToCustomer);
const [halfPieceAuditOpen, setHalfPieceAuditOpen] = useState(false);
const orderedFabricAuditSignatureRef = useRef("");

summaryDiscountModeRef.current = summaryDiscountMode;
summaryDiscountValueRef.current = summaryDiscountValue;
curtainGstRateRef.current = curtainGstRate;
quoteFullnessOverridesRef.current = quoteFullnessOverrides;
repeatSettingsRef.current = repeatSettings;
fabricSummaryMetaRef.current = fabricSummaryMeta;
useHalfPieceReuseRef.current = useHalfPieceReuse;
passHalfPieceSavingsToCustomerRef.current = passHalfPieceSavingsToCustomer;

useEffect(() => {
  const closeFabricSuggestions = () => {
    setActiveFabricSuggestionCell("");
    setFabricSuggestionKeyboardIndex(0);
    setFabricSuggestionPosition(null);
  };

  const handlePointerDown = (event) => {
    const target = event.target;
    if (
      target &&
      typeof target.closest === "function" &&
      target.closest('[data-fabric-combobox-root="true"]')
    ) {
      return;
    }
    closeFabricSuggestions();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      closeFabricSuggestions();
    }
  };

  document.addEventListener("mousedown", handlePointerDown);
  document.addEventListener("touchstart", handlePointerDown);
  document.addEventListener("keydown", handleKeyDown);

  return () => {
    document.removeEventListener("mousedown", handlePointerDown);
    document.removeEventListener("touchstart", handlePointerDown);
    document.removeEventListener("keydown", handleKeyDown);
  };
}, []);

  // Effective rows after leftover engine
  const [effectiveRows, setEffectiveRows] = useState([]);
  const [, setLeftoverUsageLog] = useState([]);

  const normalizeRowsForState = useCallback(
    (sourceRows = []) =>
      (Array.isArray(sourceRows) ? sourceRows : []).map((row, index) =>
        withCurtainRowId({ ...(row || {}) }, index)
      ),
    []
  );

  const commitCurtainRows = useCallback(
    (updater, source = QUOTE_RAW_FIELD_MUTATION_SOURCES.UNKNOWN) => {
      setRows((previousRows) => {
        const previousNormalized = normalizeRowsForState(previousRows);
        const proposedRows =
          typeof updater === "function" ? updater(previousNormalized) : updater;
        const nextRows = normalizeRowsForState(proposedRows);
        const mutationEvents = auditQuotationRowMutation(
          previousNormalized,
          nextRows,
          source
        );
        const latestEvent = mutationEvents[mutationEvents.length - 1] || null;

        if (
          mutationEvents.length > 0 &&
          source !== QUOTE_RAW_FIELD_MUTATION_SOURCES.AUTOSAVE_SAVE
        ) {
          currentQuoteEditVersionRef.current += 1;
        }

        const signature = getCurtainRowsSignature(nextRows);
        rowsRef.current = nextRows;

        if (typeof window !== "undefined") {
          window.__FQ_CURRENT_QUOTE_ROW_SIGNATURE__ = signature;
          window.__FQ_CURRENT_QUOTE_EDIT_VERSION__ = currentQuoteEditVersionRef.current;
        }

        if (latestEvent && isQuoteMutationAuditDevMode()) {
          lastQuoteFieldMutationRef.current = {
            ...latestEvent,
            rowSignature: signature,
            editVersion: currentQuoteEditVersionRef.current,
          };
          window.setTimeout(() => {
            setQuoteDataIntegrityWatch(lastQuoteFieldMutationRef.current);
          }, 0);
        }

        return nextRows;
      });
    },
    [normalizeRowsForState]
  );

  const replaceCurtainRows = useCallback(
    (nextRows, source = QUOTE_RAW_FIELD_MUTATION_SOURCES.BULK_REPLACE, context = {}) => {
      if (isQuoteMutationAuditDevMode()) {
        console.warn("QUOTE_ROWS_BULK_REPLACE", {
          source,
          previousCount: rowsRef.current?.length || 0,
          nextCount: Array.isArray(nextRows) ? nextRows.length : 0,
          quoteId: context.quoteId || selectedQuote?.id || "",
          stack: new Error().stack,
        });
      }
      commitCurtainRows(nextRows, source);
    },
    [commitCurtainRows, selectedQuote?.id]
  );

  const updateQuoteRow = useCallback(
    (rowId, patchOrUpdater, source = QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT) => {
      if (!rowId) return;
      commitCurtainRows((previousRows) =>
        previousRows.map((row, index) => {
          const stableRow = withCurtainRowId(row || {}, index);
          if (getWorkshopRowStableId(stableRow) !== rowId) return stableRow;

          const patch =
            typeof patchOrUpdater === "function"
              ? patchOrUpdater(stableRow)
              : patchOrUpdater || {};
          const touchedPatch =
            source === QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT
              ? Object.keys(patch).reduce(
                  (acc, fieldName) => ({
                    ...acc,
                    [fieldName]: true,
                  }),
                  stableRow.touchedFields || {}
                )
              : stableRow.touchedFields || {};

          return withCurtainRowId(
            {
              ...stableRow,
              ...patch,
              touchedFields: touchedPatch,
              rowUpdatedAt: new Date().toISOString(),
            },
            index
          );
        }),
        source
      );
    },
    [commitCurtainRows]
  );

  const removeQuoteRow = useCallback(
    (rowId, source = QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT) => {
      if (!rowId) return;
      commitCurtainRows(
        (previousRows) =>
          previousRows.filter((row, index) => {
            const stableRow = withCurtainRowId(row || {}, index);
            return getWorkshopRowStableId(stableRow) !== rowId;
          }),
        source
      );
    },
    [commitCurtainRows]
  );

  const markCurtainTotalsSettingsEdited = useCallback((fieldName = "", rawValue = "") => {
    currentQuoteEditVersionRef.current += 1;
    setCurtainTotalsSettingsEditVersion((version) => version + 1);

    if (typeof window !== "undefined") {
      window.__FQ_CURRENT_QUOTE_EDIT_VERSION__ = currentQuoteEditVersionRef.current;
    }

    if (typeof window !== "undefined" && isQuoteMutationAuditDevMode()) {
      lastQuoteFieldMutationRef.current = {
        source: QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT,
        fieldName,
        nextValue: rawValue,
        timestamp: new Date().toISOString(),
        editVersion: currentQuoteEditVersionRef.current,
      };
      window.setTimeout(() => {
        setQuoteDataIntegrityWatch(lastQuoteFieldMutationRef.current);
      }, 0);
    }
  }, []);

  useEffect(() => {
    rowsRef.current = normalizeRowsForState(rows);
    const signature = getCurtainRowsSignature(rowsRef.current);
    if (typeof window !== "undefined") {
      window.__FQ_CURRENT_QUOTE_ROW_SIGNATURE__ = signature;
      window.__FQ_CURRENT_QUOTE_EDIT_VERSION__ = currentQuoteEditVersionRef.current;
    }
    if (isQuoteMutationAuditDevMode()) {
      console.log("QUOTE_ROW_SIGNATURE", {
        signature,
        rowCount: rowsRef.current.length,
        editVersion: currentQuoteEditVersionRef.current,
      });
    }
  }, [normalizeRowsForState, rows]);

  useEffect(() => {
    if (!isQuoteMutationAuditDevMode() || typeof window === "undefined") return undefined;
    window.__FQ_UPDATE_QUOTE_ROW__ = updateQuoteRow;
    return () => {
      if (window.__FQ_UPDATE_QUOTE_ROW__ === updateQuoteRow) {
        delete window.__FQ_UPDATE_QUOTE_ROW__;
      }
    };
  }, [updateQuoteRow]);

  /* Saved quotes */
  const [savedQuotes, setSavedQuotes] = useState([]);
  const savedQuotesRef = useRef([]);
  savedQuotesRef.current = savedQuotes || [];
  const [deletedQuotes, setDeletedQuotes] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [savedQuotesAccessError, setSavedQuotesAccessError] = useState(null);
  const [savedQuoteOpenError, setSavedQuoteOpenError] = useState(null);
  const [repairingSavedQuoteLinkId, setRepairingSavedQuoteLinkId] = useState("");
  const [recycleBinOpen, setRecycleBinOpen] = useState(false);
  const [loadingRecycleBinQuotes, setLoadingRecycleBinQuotes] = useState(false);
  const [search, setSearch] = useState("");
  const deferredSavedQuoteSearch = useDeferredValue(search);
  const [savedQuotesVisibleCount, setSavedQuotesVisibleCount] = useState(
    SAVED_QUOTES_PAGE_SIZE
  );
  const [hasMoreSavedQuoteIndex, setHasMoreSavedQuoteIndex] = useState(false);
  const [loadingMoreSavedQuotes, setLoadingMoreSavedQuotes] = useState(false);
  const savedQuoteIndexCursorRef = useRef(null);
  const savedQuotesRecentLoadRef = useRef({
    workspaceUid: "",
    loading: false,
    loaded: false,
    promise: null,
  });
  const savedQuotesRecycleBinLoadRef = useRef({
    workspaceUid: "",
    loading: false,
    loaded: false,
    promise: null,
  });

  /* Logo upload */
  const [selectedLogoFile, setSelectedLogoFile] = useState(null);
  const [, setUploadProgress] = useState(0);
  const [, setUploading] = useState(false);
  const [storageManagerState, setStorageManagerState] = useState({
    loading: false,
    error: "",
    files: [],
    usage: null,
    loadedAt: "",
  });

// NEW REQUIRED STATES (fixes your crash)
const [optimizerFabric, setOptimizerFabric] = useState("");
const [rollOptions, setRollOptions] = useState([]);

const [cutPlan, setCutPlan] = useState([]);
const [optimizerFinalDemands, setOptimizerFinalDemands] = useState([]);

function fabricHasRepeat(fabricKey) {

const key = normalizeFabricKey(fabricKey);

const settings = repeatSettings?.[key];

if (!settings) return false;

return (
settings.verticalEnabled === true ||
settings.horizontalEnabled === true
);
}

const exportCustomerPDF = () => {
  if (!canUseCustomerPdf) {
    alert("You do not have permission to export customer PDFs.");
    return;
  }

  generateCustomerPDF();
};

const exportWorkshopPDF = () => {
  if (!canUseWorkshopPdf) {
    alert("You do not have permission to export workshop PDFs.");
    return;
  }

  generateWorkshopZIP();
};
const tcoWorkerRef = useRef(null);
const crmContactSectionRef = useRef(null);
const crmAttachmentInputRef = useRef(null);
const dismissedDraftKeysRef = useRef(new Set());
const generalQuotePersistedSignatureRef = useRef("");
const generalQuoteStaleTotalsWarningRef = useRef("");
const curtainQuotePersistedSignatureRef = useRef("");
const crmLeadPersistedSignatureRef = useRef("");
const executionDetailsPersistedSignatureRef = useRef("");
const executionTemplatesDirtyRef = useRef(false);
const executionTemplatesLoadedWorkspaceRef = useRef("");
const executionTemplatesLoadingWorkspaceRef = useRef("");
const executionTemplatesRenderCountRef = useRef(0);
const crmDetailDraftDealIdRef = useRef("");
const crmDetailDraftDirtyRef = useRef(false);
const selectedCrmDealCacheRef = useRef(null);
const selectedCrmDealIdRef = useRef("");
const pendingChecklistFocusIdRef = useRef("");
const checklistInputRefs = useRef(new Map());
const executionTemplatePersistedSignatureRef = useRef("");
const toastTimersRef = useRef(new Map());
const quoteDealChoiceResolverRef = useRef(null);
const previousRouteRef = useRef({
  tab: "quote",
  quoteSubTab: CURTAIN_QUOTE_TYPE,
});
const [, setIsOptimizing] = useState(false);

const [optimizerQuoteId, setOptimizerQuoteId] = useState("");
const [optimizerQuote, setOptimizerQuote] = useState(null);
const [optimizerQuoteSearch, setOptimizerQuoteSearch] = useState("");
const [optimizerQuoteSearchState, setOptimizerQuoteSearchState] = useState({
  loading: false,
  error: "",
  results: [],
  searched: false,
});
const optimizerQuoteSearchCacheRef = useRef(new Map());
const optimizerQuoteSearchRequestRef = useRef(0);
const [optimizerQuoteLoadingId, setOptimizerQuoteLoadingId] = useState("");

const [selectedQuoteWorkStage, setSelectedQuoteWorkStage] = useState("quote_given");
const [selectedQuoteStatus, setSelectedQuoteStatus] = useState("draft");
const [selectedInstallationDate, setSelectedInstallationDate] = useState(null);

// ======== Fabric Stock Viewer State ========
const [fabricStock, setFabricStock] = useState([]);
const [loadingFabricStock, setLoadingFabricStock] = useState(false);

const [stockHistory, setStockHistory] = useState([]);
const [loadingStockHistory, setLoadingStockHistory] = useState(false);

const [stockHistorySearch, setStockHistorySearch] = useState("");
const [stockSearch, setStockSearch] = useState("");

const [isSaving, setIsSaving] = useState(false);
const [editingQuoteId, setEditingQuoteId] = useState(null);
const [autoSaveStatus, setAutoSaveStatus] = useState("idle");
// idle | saving | saved | error
const [localDraftStatuses, setLocalDraftStatuses] = useState({
  [LOCAL_DRAFT_MODULES.GENERAL_QUOTE]: { state: "idle", savedAt: null },
  [LOCAL_DRAFT_MODULES.CURTAIN_QUOTE]: { state: "idle", savedAt: null },
  [LOCAL_DRAFT_MODULES.CRM_LEAD]: { state: "idle", savedAt: null },
  [LOCAL_DRAFT_MODULES.EXECUTION_DETAILS]: { state: "idle", savedAt: null },
  [LOCAL_DRAFT_MODULES.EXECUTION_TEMPLATE]: { state: "idle", savedAt: null },
});
const [draftRecoveryPrompt, setDraftRecoveryPrompt] = useState(null);


const [quoteSettings, setQuoteSettings] = useState({
  startNumber: "",
});

const [unit, setUnit] = useState(null);  // no default


// ================= FABRIC CALC SETTINGS =================
const [fabricCalcSettings, setFabricCalcSettings] = useState(() =>
  normalizeFabricCalcSettingsForState()
);


// ================= DASHBOARD PERIOD FILTER =================
const [periodType, setPeriodType] = useState("thisMonth"); 
const [customRange, setCustomRange] = useState({
  start: null,
  end: null,
});
 
// "month" | "lastMonth" | "3months" | "custom"

const [statusFilter, setStatusFilter] = useState("all"); // all | confirmed | draft
const [stageFilter, setStageFilter] = useState("all");   // all | specific stage
const [quoteTypeFilter, setQuoteTypeFilter] = useState("all"); // all | curtains | general
const [sortBy, setSortBy] = useState("newest");          // newest | recently_edited | oldest | highest | lowest

const toDateInputValue = useCallback((value) => {
  const millis = safeTimeMillis(value);
  if (!millis) return "";

  const date = new Date(millis);
  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}, []);

const parseDateInputValue = useCallback((value) => {
  if (!value) return null;

  const [year, month, day] = String(value)
    .split("-")
    .map((part) => Number(part));

  if (!year || !month || !day) return null;

  const parsed = new Date(year, month - 1, day, 12, 0, 0, 0);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}, []);

const getMonthKeyFromValue = useCallback((value) => {
  const millis = safeTimeMillis(value);
  if (!millis) return "";
  const date = new Date(millis);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}, []);

const getMonthKeyFromDateParts = useCallback(
  (year, monthIndex) => `${year}-${String(monthIndex + 1).padStart(2, "0")}`,
  []
);

const getRecentMonthKeys = useCallback((count = DASHBOARD_MONTHLY_CHART_MONTHS) => {
  const now = new Date();
  return Array.from({ length: count }, (_, index) => {
    const offset = count - index - 1;
    const date = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    return getMonthKeyFromDateParts(date.getFullYear(), date.getMonth());
  });
}, [getMonthKeyFromDateParts]);

const getPrimaryDashboardMonthKey = useCallback((type, range) => {
  const now = new Date();
  if (type === "lastMonth") {
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return getMonthKeyFromDateParts(lastMonth.getFullYear(), lastMonth.getMonth());
  }
  if (type === "custom" && range?.start) {
    return getMonthKeyFromValue(parseDateInputValue(range.start)) || getMonthKeyFromValue(now);
  }
  return getMonthKeyFromValue(now);
}, [getMonthKeyFromDateParts, getMonthKeyFromValue, parseDateInputValue]);

const getDashboardAnalyticsMonthKeys = useCallback((type, range) => {
  const now = new Date();
  if (type === "thisYear") {
    return Array.from({ length: 12 }, (_, index) =>
      getMonthKeyFromDateParts(now.getFullYear(), index)
    );
  }
  if (type === "custom" && range?.start && range?.end) {
    const start = parseDateInputValue(range.start);
    const end = parseDateInputValue(range.end);
    if (!start || !end) return [getPrimaryDashboardMonthKey(type, range)];
    const keys = [];
    const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    const endCursor = new Date(end.getFullYear(), end.getMonth(), 1);
    while (cursor <= endCursor && keys.length < 12) {
      keys.push(getMonthKeyFromDateParts(cursor.getFullYear(), cursor.getMonth()));
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return keys.length ? keys : [getPrimaryDashboardMonthKey(type, range)];
  }
  return [getPrimaryDashboardMonthKey(type, range)];
}, [
  getMonthKeyFromDateParts,
  getPrimaryDashboardMonthKey,
  parseDateInputValue,
]);

const parseTimeInputValue = (value) => {
  const normalized = String(value || "").trim();
  return /^\d{2}:\d{2}$/.test(normalized)
    ? normalized
    : DEFAULT_CRM_REMINDER_TIME;
};

const normalizeBooleanInput = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "on"].includes(normalized)) return true;
    if (["false", "0", "no", "off"].includes(normalized)) return false;
  }
  return undefined;
};

const buildCrmFollowUpActor = useCallback((actor = {}) => ({
  uid: String(actor?.uid || actor?.completedByUid || actor?.createdByUid || "").trim(),
  name: String(actor?.name || actor?.completedByName || actor?.createdByName || "").trim(),
  email: String(actor?.email || actor?.completedByEmail || actor?.createdByEmail || "").trim(),
}), []);

const createCrmFollowUpDraft = useCallback((overrides = {}) => {
  const id = String(overrides?.id || "").trim() || createClientId("crm-follow-up");
  const priority = String(overrides?.priority || "").trim().toLowerCase();
  const dueDate = toDateInputValue(overrides?.dueDate) || String(overrides?.dueDate || "");
  const status =
    overrides?.status === CRM_FOLLOW_UP_STATUS_DONE
      ? CRM_FOLLOW_UP_STATUS_DONE
      : CRM_FOLLOW_UP_STATUS_PENDING;
  const explicitReminderEnabled = normalizeBooleanInput(
    overrides?.reminderEnabled ?? overrides?.enabled
  );

  return {
    id,
    order: Number.isFinite(Number(overrides?.order)) ? Number(overrides.order) : 0,
    priority: CRM_FOLLOW_UP_PRIORITY_OPTIONS.some((option) => option.key === priority)
      ? priority
      : CRM_FOLLOW_UP_PRIORITY_MEDIUM,
    reminderId: String(overrides?.reminderId || "").trim() || id,
    title: String(overrides?.title || "").trim(),
    description: String(overrides?.description || "").trim(),
    dueDate,
    dueTime: parseTimeInputValue(overrides?.dueTime),
    repeat: String(overrides?.repeat || "none").trim() || "none",
    reminderEnabled:
      explicitReminderEnabled !== undefined
        ? explicitReminderEnabled
        : status !== CRM_FOLLOW_UP_STATUS_DONE && Boolean(dueDate),
    status,
    createdAt: overrides?.createdAt || null,
    updatedAt: overrides?.updatedAt || null,
    createdBy: buildCrmFollowUpActor(overrides?.createdBy),
    completedAt: overrides?.completedAt || null,
    completedBy: buildCrmFollowUpActor(overrides?.completedBy),
    attachments: normalizeCrmAttachments(overrides?.attachments),
  };
}, [buildCrmFollowUpActor]);

const combineReminderDateTime = useCallback((dateValue, timeValue) => {
  if (!dateValue) return null;

  const [year, month, day] = String(dateValue)
    .split("-")
    .map((part) => Number(part));
  const [hours, minutes] = parseTimeInputValue(timeValue)
    .split(":")
    .map((part) => Number(part));

  if (!year || !month || !day) return null;

  const parsed = new Date(
    year,
    month - 1,
    day,
    Number.isFinite(hours) ? hours : 9,
    Number.isFinite(minutes) ? minutes : 0,
    0,
    0
  );

  return Number.isNaN(parsed.getTime()) ? null : parsed;
}, []);

const coerceReminderBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "on", "enabled"].includes(normalized)) return true;
    if (["false", "0", "no", "off", "disabled"].includes(normalized)) return false;
  }
  return undefined;
};

const buildCanonicalInstallationReminder = useCallback(
  (source = {}, { installationDate = null, reminderConfigs = null } = {}) => {
    const configFromMap =
      reminderConfigs?.[CRM_REMINDER_TYPE_INSTALLATION] ||
      source?.reminderConfigs?.[CRM_REMINDER_TYPE_INSTALLATION] ||
      source?.crm?.reminderConfigs?.[CRM_REMINDER_TYPE_INSTALLATION] ||
      null;
    const raw =
      source?.installationReminder ||
      source?.crm?.installationReminder ||
      source?.reminderConfig ||
      source?.crm?.reminderConfig ||
      configFromMap ||
      {};
    const fallbackDate = toDateInputValue(installationDate);
    const reminderDate = String(
      raw?.reminderDate || raw?.date || configFromMap?.reminderDate || fallbackDate || ""
    );
    const reminderTime = parseTimeInputValue(
      raw?.reminderTime || raw?.time || configFromMap?.reminderTime || DEFAULT_CRM_REMINDER_TIME
    );
    const reminderEnabledValue = coerceReminderBoolean(
      raw?.reminderEnabled ?? configFromMap?.reminderEnabled
    );
    const enabledValue = coerceReminderBoolean(raw?.enabled ?? configFromMap?.enabled);
    const enabled =
      reminderEnabledValue !== undefined
        ? reminderEnabledValue
        : enabledValue !== undefined
        ? enabledValue
        : Boolean(reminderDate && (raw?.status || configFromMap?.status));
    const repeat = String(
      raw?.repeat || raw?.repeatKey || configFromMap?.repeat || configFromMap?.repeatKey || "daily"
    );
    const scheduledAt = combineReminderDateTime(reminderDate, reminderTime);
    const status =
      raw?.status === CRM_REMINDER_STATUS_DONE ||
      configFromMap?.status === CRM_REMINDER_STATUS_DONE
        ? CRM_REMINDER_STATUS_DONE
        : enabled
        ? CRM_REMINDER_STATUS_ACTIVE
        : CRM_REMINDER_STATUS_DISABLED;

    return {
      type: CRM_REMINDER_TYPE_INSTALLATION,
      enabled,
      reminderEnabled: enabled,
      reminderDate,
      reminderTime,
      repeat,
      repeatKey: repeat,
      status,
      completedAt: raw?.completedAt || configFromMap?.completedAt || null,
      completedBy: raw?.completedBy || configFromMap?.completedBy || null,
      nextNotifyAt:
        raw?.nextNotifyAt ||
        configFromMap?.nextNotifyAt ||
        (enabled && scheduledAt ? scheduledAt.toISOString() : null),
      lastNotifiedAt: raw?.lastNotifiedAt || configFromMap?.lastNotifiedAt || null,
      updatedAt: raw?.updatedAt || configFromMap?.updatedAt || new Date().toISOString(),
    };
  },
  [combineReminderDateTime]
);

const buildReminderDocId = useCallback((dealId, reminderType) =>
  `${String(dealId || "").trim()}__${String(reminderType || "").trim()}`, []);

const buildFollowUpReminderDocId = useCallback((dealId, followUp = {}) => {
  const reminderKey = String(
    followUp?.reminderId || followUp?.id || followUp || ""
  ).trim();
  return `${buildReminderDocId(dealId, CRM_REMINDER_TYPE_FOLLOW_UP)}__${reminderKey}`;
}, [buildReminderDocId]);

const sanitizeDueReminderIdPart = (value = "") =>
  String(value || "")
    .trim()
    .replace(DUE_REMINDER_ID_UNSAFE_CHARS, "_")
    .replace(/\s+/g, "_")
    .slice(0, 140);

const mapCrmReminderTypeToDueReminderType = (reminderType) => {
  if (reminderType === CRM_REMINDER_TYPE_FOLLOW_UP) return DUE_REMINDER_TYPE_FOLLOW_UP;
  if (reminderType === CRM_REMINDER_TYPE_INSTALLATION) return DUE_REMINDER_TYPE_INSTALLATION;
  return DUE_REMINDER_TYPE_TASK;
};

const buildDueReminderDocId = useCallback((leadId, reminderType, sourceId = "") => {
  const safeLeadId = sanitizeDueReminderIdPart(leadId);
  const dueType = mapCrmReminderTypeToDueReminderType(reminderType);
  if (!safeLeadId) return "";

  if (dueType === DUE_REMINDER_TYPE_FOLLOW_UP) {
    const safeSourceId = sanitizeDueReminderIdPart(sourceId) || "followup";
    return `${safeLeadId}_${safeSourceId}`;
  }

  if (dueType === DUE_REMINDER_TYPE_INSTALLATION) {
    return `${safeLeadId}_installation`;
  }

  return `${safeLeadId}_task`;
}, []);

const buildDueReminderStatus = (enabled, sourceStatus, hasNextNotifyAt) => {
  if (sourceStatus === CRM_REMINDER_STATUS_DONE || sourceStatus === CRM_FOLLOW_UP_STATUS_DONE) {
    return DUE_REMINDER_STATUS_COMPLETED;
  }
  if (enabled === true && hasNextNotifyAt) return DUE_REMINDER_STATUS_PENDING;
  return DUE_REMINDER_STATUS_CANCELLED;
};

const formatReminderDateTime = (value) => {
  const millis = safeTimeMillis(value);
  if (!millis) return "-";

  return new Date(millis).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatAttachmentSize = (sizeBytes) => {
  const size = Number(sizeBytes || 0);
  if (!size) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const getAttachmentVisual = (attachment) => {
  const type = String(attachment?.contentType || "").toLowerCase();
  const filename = String(
    attachment?.name || attachment?.filename || ""
  ).toLowerCase();

  if (type.startsWith("image/")) {
    return { icon: FileImage, label: "Image", accent: "#2563eb" };
  }

  if (type === "application/pdf" || filename.endsWith(".pdf")) {
    return { icon: FileText, label: "PDF", accent: "#dc2626" };
  }

  if (
    filename.endsWith(".zip") ||
    filename.endsWith(".rar") ||
    filename.endsWith(".7z")
  ) {
    return { icon: FileArchive, label: "Archive", accent: "#7c3aed" };
  }

  return { icon: Paperclip, label: "File", accent: "#0f172a" };
};

const buildExecutionSpecAttributeRows = useCallback(
  (attributes = {}) =>
    Object.entries(attributes || {}).map(([key, value]) => ({
      id: createClientId("exec-field"),
      key: String(key || ""),
      value: value == null ? "" : String(value),
    })),
  []
);

const buildExecutionSpecDraftItem = useCallback(
  (item = {}) => {
    const normalized = createExecutionSpecItem(item);
    return {
      ...normalized,
      attributeRows: buildExecutionSpecAttributeRows(normalized.attributes),
    };
  },
  [buildExecutionSpecAttributeRows]
);

const hydrateExecutionSpecDraftItems = useCallback(
  (items = []) => normalizeExecutionSpecs(items).map((item) => buildExecutionSpecDraftItem(item)),
  [buildExecutionSpecDraftItem]
);

const serializeExecutionSpecDraftItems = useCallback(
  (items = []) =>
    normalizeExecutionSpecs(
      (Array.isArray(items) ? items : []).map((item) => ({
        ...item,
        attributes: (Array.isArray(item?.attributeRows) ? item.attributeRows : []).reduce(
          (accumulator, field) => {
            const nextKey = String(field?.key || "").trim();
            if (!nextKey) return accumulator;
            accumulator[nextKey] = String(field?.value || "");
            return accumulator;
          },
          {}
        ),
        attachments: normalizeExecutionSpecAttachments(item?.attachments),
      }))
    ).map((item) => ({
      ...item,
      createdAt: item.createdAt || new Date(),
      updatedAt: new Date(),
    })),
  []
);

const stampExecutionSpecs = useCallback((items = []) => {
  const stampedAt = new Date();
  return normalizeExecutionSpecs(items).map((item) => ({
    ...item,
    createdAt: item.createdAt || stampedAt,
    updatedAt: stampedAt,
  }));
}, []);

const formatExecutionItemType = useCallback((value) => {
  const trimmed = String(value || "").trim();
  return trimmed ? trimmed : "Custom Item";
}, []);

const mergeExecutionTemplateFieldsIntoItem = useCallback(
  (item, fields = []) => {
    const currentRows = Array.isArray(item?.attributeRows) ? item.attributeRows : [];
    const currentFieldIndexByKey = new Map();

    currentRows.forEach((row, index) => {
      const normalizedKey = String(row?.key || "").trim().toLowerCase();
      if (!normalizedKey) return;
      currentFieldIndexByKey.set(normalizedKey, index);
    });

    const nextRows = [...currentRows];
    (Array.isArray(fields) ? fields : []).forEach((field) => {
      const nextFieldKey = String(field?.key || field || "").trim();
      const nextFieldDefault =
        field?.defaultValue == null ? "" : String(field.defaultValue);
      if (!nextFieldKey) return;

      const normalizedFieldKey = nextFieldKey.toLowerCase();
      const existingIndex = currentFieldIndexByKey.get(normalizedFieldKey);

      if (existingIndex != null) {
        const existingField = nextRows[existingIndex];
        if (!String(existingField?.value || "").trim() && nextFieldDefault) {
          nextRows[existingIndex] = {
            ...existingField,
            key: existingField?.key || nextFieldKey,
            value: nextFieldDefault,
          };
        }
        return;
      }

      nextRows.push({
        id: createClientId("exec-field"),
        key: nextFieldKey,
        value: nextFieldDefault,
      });
    });

    return {
      ...item,
      itemType: formatExecutionItemType(item?.itemType),
      attributeRows: nextRows,
    };
  },
  [formatExecutionItemType]
);

const pickExecutionSpecAttributes = useCallback(
  (entries = {}) =>
    Object.entries(entries).reduce((accumulator, [key, value]) => {
      const nextValue = value == null ? "" : String(value).trim();
      if (!nextValue) return accumulator;
      accumulator[key] = nextValue;
      return accumulator;
    }, {}),
  []
);

const resolveExecutionSpecsForCrmDeal = useCallback((dealLike = {}) => {
  const linkedLeadId = String(
    dealLike?.leadId || dealLike?.crm?.leadId || ""
  ).trim();

  if (dealLike?.crmRecordType === "lead" || linkedLeadId) {
    return normalizeExecutionSpecs(dealLike?.executionSpecs || []);
  }

  return normalizeExecutionSpecs(
    dealLike?.executionSpecs || dealLike?.crm?.executionSpecs || []
  );
}, []);

const getSavedQuoteTotals = (q) => {
  const storedSubtotal = Number(q?.subtotal ?? q?.taxableAmount ?? q?.grandTotal ?? q?.totalAmount ?? 0);
  const storedGrandTotal = Number(
    q?.grandTotal ??
      q?.total ??
      q?.finalTotal ??
      q?.finalAmount ??
      q?.totalAmount ??
      0
  );
  if (resolveQuoteType(q) === GENERAL_QUOTE_TYPE) {
    const embeddedGeneral = getEmbeddedGeneralQuote(q);
    const hasGeneralItems =
      (Array.isArray(embeddedGeneral?.lineItems) && embeddedGeneral.lineItems.length > 0) ||
      (Array.isArray(embeddedGeneral?.items) && embeddedGeneral.items.length > 0);
    if (!hasGeneralItems && storedGrandTotal > 0) {
      return {
        subtotal: Number(storedSubtotal || storedGrandTotal || 0),
        discountAmount: Number(q?.discountAmount || 0),
        grandTotal: storedGrandTotal,
      };
    }
    const normalized = normalizeGeneralQuote(getEmbeddedGeneralQuote(q), {
      shopAddress,
      currency,
      businessName,
      businessPhone,
      businessEmail,
    });
    return calculateGeneralQuoteTotals(normalized);
  }

  const incoming = q.repeatSettings || {};
  if ((!Array.isArray(q.rows) || q.rows.length === 0) && storedGrandTotal > 0) {
    return {
      subtotal: Number(storedSubtotal || storedGrandTotal || 0),
      discountAmount: Number(q?.discountAmount || 0),
      grandTotal: storedGrandTotal,
    };
  }

  const localRepeatSettings = {};
  Object.keys(incoming).forEach((key) => {
    localRepeatSettings[normalizeFabricKey(key)] = incoming[key];
  });

  let subtotal = 0;
  for (const r of q.rows || []) {
    const c = calcRow(r, localRepeatSettings);
    subtotal += Number(c.rowTotal || 0);
  }

  const normalizedDiscount = normalizeQuoteDiscount(q, "saved_quote_totals");
  const quoteTotals = calculateCurtainQuoteTotals({
    rows: q.rows || [],
    subtotal,
    quoteGstRate: q.quoteGstRate ?? q.gstRate ?? 0,
    discountType: normalizedDiscount.discountType,
    discountValue: normalizedDiscount.discountValue,
    getRowTaxableTotal: (row) => calcRow(row, localRepeatSettings).rowTotal,
    isAccessoryRow: isCurtainAccessoryRowStatic,
  });

  return {
    subtotal: Number(subtotal.toFixed(2)),
    discountAmount: Number(quoteTotals.discountAmount || 0),
    totalAfterGst: Number(quoteTotals.totalAfterGst || 0),
    grandTotal: Number(quoteTotals.grandTotal || 0),
  };
};

const getNormalizedQuoteTotal = useCallback(
  (quote = {}) => {
    if (!quote) return 0;

    const quoteType = resolveQuoteType(quote);
    const calculatedGeneralTotal =
      quoteType === GENERAL_QUOTE_TYPE
        ? calculateGeneralQuoteTotals(
            normalizeGeneralQuote(getEmbeddedGeneralQuote(quote), {
              shopAddress,
              currency,
              businessName,
              businessPhone,
              businessEmail,
            })
          )?.grandTotal
        : null;
    const candidates = [
      calculatedGeneralTotal,
      getQuoteValue(quote),
      quote.grandTotal,
      quote.totalAmount,
      quote.finalAmount,
      quote.finalTotal,
      quote.approvedValue,
      quote.quoteValue,
      quote.linkedQuoteValue,
      quote.amount,
      quote.total,
      quote.subtotal,
      quote.crm?.finalValue,
      quote.crm?.expectedValue,
    ];

    const firstValid = candidates
      .map((value) => Number(value))
      .find((value) => Number.isFinite(value) && value > 0);

    return Number(firstValid || 0);
  },
  [businessEmail, businessName, businessPhone, currency, shopAddress]
);

const getQuoteTotalForAnalytics = useCallback(
  (quote = {}) => {
    const candidates = [
      ["grandTotal", quote.grandTotal],
      ["finalTotal", quote.finalTotal],
      ["total", quote.total],
      ["netTotal", quote.netTotal],
      ["quoteTotal", quote.quoteTotal],
      ["amount", quote.amount],
      ["totalAmount", quote.totalAmount],
      ["finalAmount", quote.finalAmount],
      ["approvedValue", quote.approvedValue],
      ["quoteValue", quote.quoteValue],
      ["linkedQuoteValue", quote.linkedQuoteValue],
      ["crm.finalValue", quote.crm?.finalValue],
      ["crm.expectedValue", quote.crm?.expectedValue],
      ["computed", getNormalizedQuoteTotal(quote)],
    ];

    for (const [source, value] of candidates) {
      const numericValue = Number(value);
      if (Number.isFinite(numericValue) && numericValue > 0) {
        return {
          amount: Number(numericValue.toFixed(2)),
          source,
          valid: true,
          issue: "",
        };
      }
    }

    return {
      amount: 0,
      source: "",
      valid: false,
      issue: "invalid_amount",
    };
  },
  [getNormalizedQuoteTotal]
);

const buildAnalyticsContributionForQuote = useCallback(
  (quote = {}) => {
    const quoteId = String(quote.id || quote.quoteId || quote.quoteNo || "").trim();
    if (!quoteId) return null;

    const customerNameForIndex = getQuoteCustomerLabel(quote);
    const normalizedPhone = normalizeSearchCompact(
      quote.customerPhone ||
        quote.customerWhatsapp ||
        quote.crm?.customerPhone ||
        quote.crm?.contact?.phone ||
        quote.generalQuote?.quoteFor?.phone ||
        ""
    );
    const customerKey = String(
      quote.customerId ||
        quote.crm?.customerId ||
        normalizedPhone ||
        normalizeSearchCompact(customerNameForIndex) ||
        "unknown"
    ).slice(0, 80);
    const issues = [];
    const totalResult = getQuoteTotalForAnalytics(quote);
    const totalAmount = Number(totalResult.amount || 0);
    if (!totalResult.valid) {
      issues.push("invalid_amount");
    }
    const quoteTypeResolved = quote.quoteTypeResolved || resolveQuoteType(quote);
    const status = String(quote.status || "draft").trim().toLowerCase();
    const workStage = String(quote.workStage || quote.crm?.stageId || "quote_given").trim();
    const deleted = quote.deleted === true;
    const cancelled = isCancelledQuoteRecord(quote);
    const confirmed = isConfirmedQuoteRecord(quote) && !cancelled && !deleted;
    const installed =
      !deleted &&
      !cancelled &&
      ["installed", "completed", "closed_won"].includes(workStage.toLowerCase());
    const quoteDateKey = resolveQuoteDateForInput(quote);
    const createdAt = quote.createdAt || quote.createdOn || quote.createdDate || null;
    const confirmedAt =
      quote.confirmedAt || quote.crm?.confirmedAt || null;
    if (confirmed && !confirmedAt) {
      issues.push("confirmed_without_confirmedAt");
    }
    const installationDate =
      quote.installationDate ||
      quote.expectedInstallationDate ||
      quote.scheduledInstallationDate ||
      quote.installedAt ||
      quote.deliveryDate ||
      quote.crm?.installationDate ||
      null;
    const quoteMonthKey = quoteDateKey.slice(0, 7) || getMonthKeyFromValue(createdAt || quote.updatedAt);
    const confirmedMonthKey = confirmed ? getMonthKeyFromValue(confirmedAt) : "";
    const installationDateKey = toDateInputValue(installationDate);
    const installationMonthKey = installationDateKey.slice(0, 7);
    if (!quoteMonthKey) {
      issues.push("invalid_created_date");
    }
    if (confirmed && !confirmedMonthKey) {
      issues.push("invalid_confirmed_date");
    }
    if (installationDate && !installationDateKey) {
      issues.push("invalid_installation_date");
    }

    return {
      quoteId,
      quoteNo: quote.quoteNo || quoteId,
      quoteType: quoteTypeResolved,
      customerKey,
      customerId: quote.customerId || quote.crm?.customerId || "",
      customerName: customerNameForIndex,
      normalizedPhone,
      quoteDate: quoteDateKey,
      quoteMonthKey,
      confirmedMonthKey,
      installationMonthKey,
      installationDateKey,
      status,
      workStage,
      totalAmount,
      totalSource: totalResult.source,
      issues,
      needsRepair: issues.length > 0,
      quotationRevenue: deleted ? 0 : totalAmount,
      confirmedRevenue: confirmed && confirmedMonthKey ? totalAmount : 0,
      cancelledRevenue: cancelled ? totalAmount : 0,
      installedRevenue: installationMonthKey && installed ? totalAmount : 0,
      deleted,
      cancelled,
      confirmed,
      installed,
      hasInstallation: Boolean(installationDateKey),
      lastActivityAt: quote.updatedAt || quote.modifiedAt || confirmedAt || createdAt || new Date(),
      installationDate,
      itemSummary:
        quote.itemSummary ||
        quote.title ||
        quote.projectName ||
        quote.projectTitle ||
        getQuoteTypeLabel(quoteTypeResolved),
      location:
        quote.location ||
        quote.site ||
        quote.siteLocation ||
        quote.crm?.locationAddress ||
        quote.crm?.siteLocation ||
        "",
    };
  },
  [getMonthKeyFromValue, getQuoteTotalForAnalytics, toDateInputValue]
);

const applySummaryDelta = useCallback((current = {}, delta = {}) => ({
  ...current,
  confirmedRevenue: Number(current.confirmedRevenue || 0) + Number(delta.confirmedRevenue || 0),
  confirmedCount: Number(current.confirmedCount || 0) + Number(delta.confirmedCount || 0),
  quotationRevenue: Number(current.quotationRevenue || 0) + Number(delta.quotationRevenue || 0),
  quotationCount: Number(current.quotationCount || 0) + Number(delta.quotationCount || 0),
  cancelledRevenue: Number(current.cancelledRevenue || 0) + Number(delta.cancelledRevenue || 0),
  cancelledCount: Number(current.cancelledCount || 0) + Number(delta.cancelledCount || 0),
  installedRevenue: Number(current.installedRevenue || 0) + Number(delta.installedRevenue || 0),
  installedCount: Number(current.installedCount || 0) + Number(delta.installedCount || 0),
}), []);

const getMonthlyDeltaFromContribution = useCallback((contribution, sign = 1) => {
  const monthDeltas = new Map();
  const add = (monthKey, delta) => {
    if (!monthKey) return;
    monthDeltas.set(monthKey, applySummaryDelta(monthDeltas.get(monthKey), delta));
  };
  add(contribution.quoteMonthKey, {
    quotationRevenue: sign * Number(contribution.quotationRevenue || 0),
    quotationCount: sign * (contribution.deleted ? 0 : 1),
    cancelledRevenue: sign * Number(contribution.cancelledRevenue || 0),
    cancelledCount: sign * (contribution.cancelled ? 1 : 0),
  });
  add(contribution.confirmedMonthKey, {
    confirmedRevenue: sign * Number(contribution.confirmedRevenue || 0),
    confirmedCount: sign * (contribution.confirmed ? 1 : 0),
  });
  add(contribution.installationMonthKey, {
    installedRevenue: sign * Number(contribution.installedRevenue || 0),
    installedCount: sign * (contribution.installed ? 1 : 0),
  });
  return monthDeltas;
}, [applySummaryDelta]);

const dashboardPeriodLabel = useMemo(() => {
  switch (periodType) {
    case "today":
      return "Today";
    case "thisWeek":
      return "This Week";
    case "thisMonth":
      return "This Month";
    case "lastMonth":
      return "Last Month";
    case "thisYear":
      return "This Year";
    case "custom":
      return customRange.start && customRange.end
        ? `${customRange.start} to ${customRange.end}`
        : "Custom Range";
    default:
      return "Selected Period";
  }
}, [customRange.end, customRange.start, periodType]);

const dashboardAllQuotes = useMemo(() => {
  return (savedQuotes || [])
    .filter(Boolean)
    .map((quote) => {
      const createdMillis = getCreatedMillis(quote);
      const updatedMillis = getUpdatedMillis(quote);
      const confirmedMillis = getConfirmedMillis(quote);
      const installationMillis = getInstallationMillis(quote);
      const resolvedQuoteType = resolveQuoteType(quote);
      const workStageResolved = getQuoteStageValue(quote);
      const totalValue = getNormalizedQuoteTotal(quote);
      const customerLabel = getQuoteCustomerLabel(quote);
      const isDeleted = isDeletedQuoteRecord(quote);
      const isCancelled = isCancelledQuoteRecord(quote);
      const isConfirmed = isConfirmedQuoteRecord(quote);

      return {
        ...quote,
        customerLabel,
        quoteTypeResolved: resolvedQuoteType,
        workStageResolved,
        createdMillis,
        updatedMillis,
        confirmedMillis,
        installationMillis,
        totalValue,
        isDeleted,
        isCancelled,
        isConfirmed,
        isRevenueEligible: isRevenueQuote(quote),
      };
    })
    .filter((quote) => !quote.isDeleted)
    .sort((a, b) => {
      if (b.updatedMillis !== a.updatedMillis) {
        return b.updatedMillis - a.updatedMillis;
      }
      return b.createdMillis - a.createdMillis;
    });
}, [
  getNormalizedQuoteTotal,
  savedQuotes,
]);

const periodQuotes = useMemo(() => {
  return dashboardAllQuotes.filter((quote) => {
    if (!quote.createdMillis) return false;
    return isDateInPeriod(new Date(quote.createdMillis), periodType, customRange);
  });
}, [customRange, dashboardAllQuotes, periodType]);

const periodConfirmed = useMemo(() => {
  return dashboardAllQuotes.filter((quote) => {
    if (!quote.isConfirmed || quote.isCancelled || !quote.confirmedMillis) return false;
    return isDateInPeriod(new Date(quote.confirmedMillis), periodType, customRange);
  });
}, [customRange, dashboardAllQuotes, periodType]);

const draftQuotes = useMemo(
  () => dashboardAllQuotes.filter((quote) => !quote.isConfirmed && !quote.isCancelled),
  [dashboardAllQuotes]
);

const finalQuotes = useMemo(
  () => dashboardAllQuotes.filter((quote) => quote.isConfirmed && !quote.isCancelled),
  [dashboardAllQuotes]
);

const openPipelineQuotes = useMemo(() => {
  return dashboardAllQuotes.filter((quote) => {
    if (quote.isCancelled) return false;
    return !["installed", "completed"].includes(quote.workStageResolved);
  });
}, [dashboardAllQuotes]);

const todayStartMillis = useMemo(() => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
}, []);

const dashboardInstallationRecords = useMemo(
  () =>
    (dashboardAnalyticsState.installationRecords || [])
      .map((record) => {
        const installationDateKey =
          record.installationDateKey || toDateInputValue(record.installationDate);
        const installationMillis = safeTimeMillis(
          record.installationDate || installationDateKey
        );
        const quoteId = record.quoteId || record.jobId || record.id || "";
        const status = String(record.status || "").toLowerCase();
        const workStage = String(record.workStage || "").toLowerCase();

        return {
          ...record,
          id: quoteId,
          quoteId,
          customerLabel:
            record.customerLabel || record.customerName || "Unnamed Site",
          installationDateKey,
          installationMillis,
          totalValue: Number(record.totalAmount || record.totalValue || 0),
          isCancelled:
            record.deleted === true ||
            ["cancelled", "canceled", "lost"].includes(status) ||
            ["cancelled", "canceled", "lost"].includes(workStage),
        };
      })
      .filter((record) => record.id && record.installationDateKey && record.installationMillis),
  [dashboardAnalyticsState.installationRecords]
);

const upcomingInstallationQuotes = useMemo(() => {
  return dashboardInstallationRecords
    .filter(
      (quote) =>
        !quote.isCancelled &&
        quote.installationMillis &&
        quote.installationMillis >= todayStartMillis
    )
    .sort((a, b) => a.installationMillis - b.installationMillis);
}, [dashboardInstallationRecords, todayStartMillis]);

const upcomingInstallations = useMemo(
  () => upcomingInstallationQuotes.slice(0, 6),
  [upcomingInstallationQuotes]
);

const installationByDate = useMemo(() => {
  const map = {};

  dashboardInstallationRecords.forEach((quote) => {
    if (quote.isCancelled || !quote.installationDateKey) return;
    const key = quote.installationDateKey;

    if (!map[key]) map[key] = [];

    map[key].push(quote);
  });

  return map;
}, [dashboardInstallationRecords]);

const recentQuotes = useMemo(() => dashboardAllQuotes.slice(0, 6), [dashboardAllQuotes]);

const topCustomers = useMemo(() => {
  return (dashboardAnalyticsState.topClients || [])
    .map((customer) => ({
      name: customer.customerName || customer.name || "Unnamed Customer",
      customerId: String(customer.customerId || ""),
      phone: customer.normalizedPhone || customer.phone || "",
      value: Number(customer.confirmedRevenue || customer.quotationRevenue || customer.value || 0),
      count: Number(customer.confirmedCount || customer.quoteCount || customer.count || 0),
      records: [],
      monthKey: customer.monthKey || dashboardAnalyticsState.diagnostics?.monthKey || "",
    }))
    .sort((a, b) => {
      if (b.value !== a.value) return b.value - a.value;
      return b.count - a.count;
    })
    .slice(0, 5)
    .map((entry) => ({
      ...entry,
      value: Number(entry.value.toFixed(2)),
    }));
}, [dashboardAnalyticsState.diagnostics?.monthKey, dashboardAnalyticsState.topClients]);

const stageDistribution = useMemo(() => {
  const stageMap = new Map();

  dashboardAllQuotes.forEach((quote) => {
    const quoteType = quote.quoteTypeResolved || resolveQuoteType(quote);
    const stageMeta = getCrmStage(
      crmPipelines,
      quoteType,
      quote.workStageResolved || quote.workStage
    );
    const key = `${quoteType}::${stageMeta?.id || quote.workStageResolved || "quote_given"}`;
    const current = stageMap.get(key) || {
      key,
      quoteType,
      stageId: stageMeta?.id || quote.workStageResolved || "quote_given",
      stageLabel: stageMeta?.label || "Quote Given",
      fullLabel: `${getQuoteTypeLabel(quoteType)} | ${stageMeta?.label || "Quote Given"}`,
      label: `${getQuoteTypeLabel(quoteType)} | ${stageMeta?.label || "Quote Given"}`,
      value: 0,
      color: stageMeta?.color || "#64748b",
    };
    current.value += 1;
    stageMap.set(key, current);
  });

  return Array.from(stageMap.values()).sort((a, b) => b.value - a.value);
}, [crmPipelines, dashboardAllQuotes]);

const quoteTypeDistribution = useMemo(() => {
  const curtainsCount = dashboardAllQuotes.filter(
    (quote) => quote.quoteTypeResolved === CURTAIN_QUOTE_TYPE
  ).length;
  const generalCount = dashboardAllQuotes.filter(
    (quote) => quote.quoteTypeResolved === GENERAL_QUOTE_TYPE
  ).length;

  return [
    { name: "Curtains", value: curtainsCount, color: "#4f46e5" },
    {
      name: "General Quotation",
      value: generalCount,
      color: "#0ea5e9",
    },
  ].filter((entry) => entry.value > 0);
}, [dashboardAllQuotes]);

const monthlyTimeSeries = useMemo(() => {
  const monthMap = {};
  const now = new Date();
  const summaries = dashboardAnalyticsState.monthlySummaries || {};

  for (let offset = 5; offset >= 0; offset--) {
    const date = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    const summary = summaries[key] || {};
    monthMap[key] = {
      key,
      month: date.toLocaleString("default", {
        month: "short",
        year: "numeric",
      }),
      created: Number(summary.quotationCount || 0),
      confirmed: Number(summary.confirmedCount || 0),
      revenue: Number(summary.confirmedRevenue || 0),
      quotationRevenue: Number(summary.quotationRevenue || 0),
      installedRevenue: Number(summary.installedRevenue || 0),
    };
  }

  return Object.values(monthMap).map((entry) => ({
    ...entry,
    revenue: Number(entry.revenue.toFixed(2)),
  }));
}, [dashboardAnalyticsState.monthlySummaries]);

const selectedDashboardMonthKeys = useMemo(
  () => getDashboardAnalyticsMonthKeys(periodType, customRange),
  [customRange, getDashboardAnalyticsMonthKeys, periodType]
);

const dashboardSummaryAggregate = useMemo(() => {
  const summaries = dashboardAnalyticsState.monthlySummaries || {};
  return selectedDashboardMonthKeys.reduce(
    (accumulator, monthKey) => {
      const summary = summaries[monthKey] || {};
      accumulator.quotationCount += Number(summary.quotationCount || 0);
      accumulator.quotationRevenue += Number(summary.quotationRevenue || 0);
      accumulator.confirmedCount += Number(summary.confirmedCount || 0);
      accumulator.confirmedRevenue += Number(summary.confirmedRevenue || 0);
      accumulator.cancelledCount += Number(summary.cancelledCount || 0);
      accumulator.cancelledRevenue += Number(summary.cancelledRevenue || 0);
      accumulator.installedCount += Number(summary.installedCount || 0);
      accumulator.installedRevenue += Number(summary.installedRevenue || 0);
      return accumulator;
    },
    {
      quotationCount: 0,
      quotationRevenue: 0,
      confirmedCount: 0,
      confirmedRevenue: 0,
      cancelledCount: 0,
      cancelledRevenue: 0,
      installedCount: 0,
      installedRevenue: 0,
    }
  );
}, [dashboardAnalyticsState.monthlySummaries, selectedDashboardMonthKeys]);

const dashboardAnalyticsStatus = dashboardAnalyticsState.diagnostics?.status || "idle";
const dashboardAnalyticsIndexMissing = dashboardAnalyticsStatus === "missing";
const dashboardAnalyticsError = dashboardAnalyticsStatus === "error";
const dashboardAnalyticsNotReady =
  dashboardAnalyticsIndexMissing ||
  dashboardAnalyticsError ||
  dashboardAnalyticsState.diagnostics?.source === "idle";

const conversionRate = dashboardSummaryAggregate.quotationCount
  ? Math.round((dashboardSummaryAggregate.confirmedCount / dashboardSummaryAggregate.quotationCount) * 100)
  : 0;

const avgQuoteValue = dashboardSummaryAggregate.quotationCount
  ? dashboardSummaryAggregate.quotationRevenue / dashboardSummaryAggregate.quotationCount
  : 0;

const avgConfirmedValue = dashboardSummaryAggregate.confirmedCount
  ? dashboardSummaryAggregate.confirmedRevenue / dashboardSummaryAggregate.confirmedCount
  : 0;

const businessOverviewDrilldowns = useMemo(
  () => ({
    quotesThisMonth: {
      key: "quotesThisMonth",
      label: `Quotes ${dashboardPeriodLabel}`,
      records: periodQuotes,
      count: dashboardSummaryAggregate.quotationCount,
      summaryValue: dashboardSummaryAggregate.quotationRevenue,
    },
    month: {
      key: "quotesThisMonth",
      label: `Quotes ${dashboardPeriodLabel}`,
      records: periodQuotes,
      count: dashboardSummaryAggregate.quotationCount,
      summaryValue: dashboardSummaryAggregate.quotationRevenue,
    },
    confirmedThisMonth: {
      key: "confirmedThisMonth",
      label: `Confirmed ${dashboardPeriodLabel}`,
      records: periodConfirmed,
      count: dashboardSummaryAggregate.confirmedCount,
      summaryValue: dashboardSummaryAggregate.confirmedRevenue,
    },
    confirmed: {
      key: "confirmedThisMonth",
      label: `Confirmed ${dashboardPeriodLabel}`,
      records: periodConfirmed,
      count: dashboardSummaryAggregate.confirmedCount,
      summaryValue: dashboardSummaryAggregate.confirmedRevenue,
    },
    revenueThisMonth: {
      key: "revenueThisMonth",
      label: `Revenue ${dashboardPeriodLabel}`,
      records: periodConfirmed.filter((quote) => quote.isRevenueEligible),
      count: dashboardSummaryAggregate.confirmedCount,
      summaryValue: dashboardSummaryAggregate.confirmedRevenue,
    },
    draftQuotes: {
      key: "draftQuotes",
      label: "Draft Quotes",
      records: draftQuotes,
      count: draftQuotes.length,
      summaryValue: null,
    },
    draft: {
      key: "draftQuotes",
      label: "Draft Quotes",
      records: draftQuotes,
      count: draftQuotes.length,
      summaryValue: null,
    },
    openPipeline: {
      key: "openPipeline",
      label: "Open Pipeline",
      records: openPipelineQuotes,
      count: openPipelineQuotes.length,
      summaryValue: null,
    },
    upcomingInstallations: {
      key: "upcomingInstallations",
      label: "Upcoming Installations",
      records: upcomingInstallationQuotes,
      count: upcomingInstallationQuotes.length,
      summaryValue: null,
    },
    activeQuotes: {
      key: "activeQuotes",
      label: "Recent Active Quotes",
      records: dashboardAllQuotes,
      count: dashboardAllQuotes.length,
      summaryValue: null,
    },
    final: {
      key: "finalQuotes",
      label: "Final Quotes",
      records: finalQuotes,
      count: finalQuotes.length,
      summaryValue: null,
    },
  }),
  [
    dashboardAllQuotes,
    dashboardPeriodLabel,
    dashboardSummaryAggregate,
    draftQuotes,
    finalQuotes,
    openPipelineQuotes,
    periodConfirmed,
    periodQuotes,
    upcomingInstallationQuotes,
  ]
);

const activeBusinessDashboardDrilldown = businessDashboardDrilldownKey
  ? businessOverviewDrilldowns[businessDashboardDrilldownKey] || null
  : null;

const activeBusinessDashboardDrilldownIds = useMemo(() => {
  if (!activeBusinessDashboardDrilldown) return null;
  return new Set(
    (activeBusinessDashboardDrilldown.records || [])
      .map((quote) => quote?.id)
      .filter(Boolean)
  );
}, [activeBusinessDashboardDrilldown]);

const dashboardListQuotes = useMemo(() => {
  if (!dashView || dashView === "home") {
    return [];
  }

  return businessOverviewDrilldowns[dashView]?.records || [];
}, [businessOverviewDrilldowns, dashView]);

const activeDashboardListView =
  dashView && dashView !== "home"
    ? businessOverviewDrilldowns[dashView] || null
    : null;

const syncGeneralQuoteDefaults = useCallback(
  (quote) =>
    normalizeGeneralQuote(quote, {
      shopAddress,
      currency,
      businessName,
      businessPhone,
      businessEmail,
    }),
  [shopAddress, currency, businessName, businessPhone, businessEmail]
);

const workspaceUid =
  currentAccount?.dataOwnerUid ||
  currentAccount?.ownerUid ||
  accountWorkspaceUid ||
  workspaceContext?.workspaceId ||
  auth.currentUser?.uid ||
  user?.uid ||
  "";
const createQuotationWithTrialGuardCallable = useMemo(
  () => httpsCallable(functions, "createQuotationWithTrialGuard"),
  []
);
  const persistNewQuotationWithTrialGuard = useCallback(
  async ({ quoteId, quotePayload, quoteIndexSource }) => {
    await createQuotationWithTrialGuardCallable({
      workspaceUid,
      quoteId,
      quotePayload: sanitizeCrmFirestoreValue({
        ...quotePayload,
        createdAt: undefined,
        updatedAt: undefined,
      }),
      quoteIndexSource: sanitizeCrmFirestoreValue({
        ...quoteIndexSource,
        createdAt: undefined,
        updatedAt: undefined,
      }),
    });
    await refreshAccount?.();
  },
  [createQuotationWithTrialGuardCallable, refreshAccount, workspaceUid]
);
const markDueReminderCompleted = useCallback(
  async ({
    leadId,
    reminderType,
    sourceId = "",
    crmReminderId = "",
    status = DUE_REMINDER_STATUS_COMPLETED,
  }) => {
    if (!workspaceUid || !leadId) return;

    const dueReminderId = buildDueReminderDocId(leadId, reminderType, sourceId);
    if (!dueReminderId) return;

    await setDoc(
      doc(db, "users", workspaceUid, "due_reminders", dueReminderId),
      {
        id: dueReminderId,
        workspaceUid,
        crmLeadId: String(leadId || ""),
        crmReminderId: String(crmReminderId || ""),
        status,
        reminderEnabled: false,
        notificationSent: false,
        completedAt: status === DUE_REMINDER_STATUS_COMPLETED ? serverTimestamp() : null,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  },
  [buildDueReminderDocId, workspaceUid]
);
const updateLocalDraftStatus = useCallback((module, state, savedAt = null) => {
  setLocalDraftStatuses((prev) => ({
    ...prev,
    [module]: {
      state,
      savedAt:
        savedAt ?? (state === "autosaved" ? new Date().toISOString() : prev[module]?.savedAt ?? null),
    },
  }));
}, []);
const buildLocalDraftPayload = useCallback(
  (module, entityId, data, serverUpdatedAt = 0, metadata = {}) => ({
    version: LOCAL_DRAFT_VERSION,
    payloadVersion: LOCAL_DRAFT_VERSION,
    appVersion: process.env.REACT_APP_VERSION || process.env.npm_package_version || "",
    module,
    entityId: String(entityId || "new"),
    workspaceUid,
    updatedAt: new Date().toISOString(),
    savedAt: new Date().toISOString(),
    serverUpdatedAt: Number(serverUpdatedAt || 0),
    metadata,
    data,
  }),
  [workspaceUid]
);
const generalQuoteDraftKey = useMemo(
  () =>
    workspaceUid
      ? buildLocalDraftKey(
          workspaceUid,
          LOCAL_DRAFT_MODULES.GENERAL_QUOTE,
          generalEditingQuoteId || "new"
        )
      : "",
  [generalEditingQuoteId, workspaceUid]
);
const curtainDraftEntityId = useMemo(
  () =>
    editingQuoteId ||
    (resolveQuoteType(selectedQuote) === CURTAIN_QUOTE_TYPE ? selectedQuote?.id : "") ||
    "new",
  [editingQuoteId, selectedQuote]
);
const curtainQuoteDraftKey = useMemo(
  () =>
    workspaceUid
      ? buildLocalDraftKey(
          workspaceUid,
          LOCAL_DRAFT_MODULES.CURTAIN_QUOTE,
          curtainDraftEntityId || "new"
        )
      : "",
  [curtainDraftEntityId, workspaceUid]
);
const crmLeadDraftKey = useMemo(
  () =>
    workspaceUid
      ? buildLocalDraftKey(workspaceUid, LOCAL_DRAFT_MODULES.CRM_LEAD, "new")
      : "",
  [workspaceUid]
);
const executionDetailsDraftKey = useMemo(
  () =>
    workspaceUid && selectedCrmDealId
      ? buildLocalDraftKey(
          workspaceUid,
          LOCAL_DRAFT_MODULES.EXECUTION_DETAILS,
          selectedCrmDealId
        )
      : "",
  [selectedCrmDealId, workspaceUid]
);
const executionTemplatesDraftKey = useMemo(
  () =>
    workspaceUid
      ? buildLocalDraftKey(workspaceUid, LOCAL_DRAFT_MODULES.EXECUTION_TEMPLATE, "library")
      : "",
  [workspaceUid]
);
const serializeGeneralQuoteDraftData = useCallback(
  (quoteLike = generalQuote) => JSON.stringify(syncGeneralQuoteDefaults(quoteLike || {})),
  [generalQuote, syncGeneralQuoteDefaults]
);

const attachProductSnapshotsToCurtainRows = useCallback(
  (sourceRows = []) =>
    (Array.isArray(sourceRows) ? sourceRows : []).map((row, index) => {
      const stableRow = withCurtainRowId(row || {}, index);
      const config = resolveProductConfig(
        stableRow,
        productConfigs,
        fabricCalcSettings
      );
      if (!config || shouldUseLegacyProductCalculation(stableRow, config)) {
        return stableRow;
      }

      return {
        ...stableRow,
        lineType:
          stableRow.lineType ||
          (isAccessoryProductConfig(config) ? "accessory" : "measurement"),
        category: stableRow.category || config.category || "",
        productId: stableRow.productId || config.id,
        productName: stableRow.productName || config.name,
        product: stableRow.product || config.name,
        productType: stableRow.productType || config.name,
        calculationType: stableRow.calculationType || config.calculationType,
        productConfigSnapshot:
          stableRow.productConfigSnapshot || buildProductConfigSnapshot(config),
      };
    }),
  [fabricCalcSettings, productConfigs]
);

const normalizeCurtainQuoteDraftData = useCallback(
  (draft = {}) => {
    const safeRows = Array.isArray(draft.rows)
      ? serializeCurtainRowsForDraft(draft.rows)
      : [createDefaultCurtainQuoteRow("W1")];
    const incomingRepeatSettings = draft.repeatSettings || {};
    const cleanedRepeatSettings = {};
    Object.keys(incomingRepeatSettings).forEach((key) => {
      cleanedRepeatSettings[normalizeFabricKey(key)] = incomingRepeatSettings[key];
    });
    const normalizedDiscount = normalizeQuoteDiscount(
      draft,
      "curtain_draft_normalize"
    );
    const normalizedGstRate = normalizeCurtainGstRate(
      draft.quoteGstRate ?? draft.gstRate ?? draft.curtainGstRate ?? 0
    );
    const normalizedQuoteDate = resolveQuoteDateForInput(
      draft,
      todayISO()
    );

    return {
      ...draft,
      customerName: String(draft.customerName || ""),
      quoteCustomerLink: {
        customerId: draft.quoteCustomerLink?.customerId || draft.customerId || "",
        customerKey:
          draft.quoteCustomerLink?.customerKey ||
          draft.customerKey ||
          draft.quoteCustomerLink?.customerId ||
          draft.customerId ||
          "",
        customerName:
          draft.quoteCustomerLink?.customerName || draft.customerName || "",
        phone: draft.quoteCustomerLink?.phone || draft.customerPhone || "",
        whatsapp:
          draft.quoteCustomerLink?.whatsapp || draft.customerWhatsapp || "",
        email: draft.quoteCustomerLink?.email || draft.customerEmail || "",
        address:
          draft.quoteCustomerLink?.address || draft.customerAddress || "",
        companyName:
          draft.quoteCustomerLink?.companyName ||
          draft.customerCompanyName ||
          "",
      },
      quoteNo: String(draft.quoteNo || ""),
      quoteDate: normalizedQuoteDate,
      date: normalizedQuoteDate,
      quotationDate: normalizedQuoteDate,
      rows: safeRows,
      rowCount: safeRows.length,
      rowIds: safeRows.map((row) => getWorkshopRowStableId(row)).filter(Boolean),
      repeatSettings: cleanedRepeatSettings,
      quoteFullnessOverrides: normalizeQuoteFullnessOverrides(
        draft.quoteFullnessOverrides || draft.fullnessOverrides || {}
      ),
      fabricSummaryMeta: normalizeFabricSummaryMetaWithRepeatSettings(
        draft.fabricSummaryMeta,
        cleanedRepeatSettings
      ),
      discountType: normalizedDiscount.discountType,
      discountValue: normalizedDiscount.discountValue,
      quoteGstRate: normalizedGstRate,
      gstRate: normalizedGstRate,
      gstAmount: round2(num(draft.gstAmount)),
      taxableAmount: round2(num(draft.taxableAmount)),
      finalTotal: round2(num(draft.finalTotal ?? draft.grandTotal)),
      calculationVersion:
        draft.calculationVersion || CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
      formulaCheckStatus: draft.formulaCheckStatus || "current",
      summaryDiscountMode: getSummaryDiscountModeFromType(
        normalizedDiscount.discountType
      ),
      summaryDiscountValue: String(normalizedDiscount.discountValue),
      selectedQuoteWorkStage: draft.selectedQuoteWorkStage || "quote_given",
      selectedQuoteStatus: draft.selectedQuoteStatus || "draft",
      selectedInstallationDate: draft.selectedInstallationDate || null,
      useHalfPieceReuse: draft.useHalfPieceReuse !== false,
      passHalfPieceSavingsToCustomer: draft.passHalfPieceSavingsToCustomer === true,
      pendingCrmLeadId: draft.pendingCrmLeadId || "",
      quoteCustomerLinkSelection: draft.quoteCustomerLinkSelection || null,
      expandedCurtainRows: draft.expandedCurtainRows || {},
      settingsSnapshot: draft.settingsSnapshot || {},
      optimizer: draft.optimizer || {},
    };
  },
  []
);
const buildCurtainQuoteDraftData = useCallback(
  () => {
    const latestSummaryTotals = curtainSummaryTotalsRef.current || {};
    return normalizeCurtainQuoteDraftData({
      quoteType: CURTAIN_QUOTE_TYPE,
      quoteId: curtainDraftEntityId || "new",
      editingQuoteId: editingQuoteId || "",
      selectedQuoteId:
        resolveQuoteType(selectedQuote) === CURTAIN_QUOTE_TYPE
          ? selectedQuote?.id || ""
          : "",
      customerName,
      quoteCustomerLink,
      customerId: quoteCustomerLink.customerId || selectedQuote?.customerId || "",
      customerPhone: quoteCustomerLink.phone || selectedQuote?.customerPhone || "",
      customerWhatsapp:
        quoteCustomerLink.whatsapp || selectedQuote?.customerWhatsapp || "",
      customerEmail: quoteCustomerLink.email || selectedQuote?.customerEmail || "",
      customerAddress:
        quoteCustomerLink.address || selectedQuote?.customerAddress || "",
      customerCompanyName:
        quoteCustomerLink.companyName ||
        selectedQuote?.customerCompanyName ||
        "",
      quoteNo,
      quoteDate: resolveQuoteDateForInput({ quoteDate: date, date }, todayISO()),
      date: resolveQuoteDateForInput({ quoteDate: date, date }, todayISO()),
      quotationDate: resolveQuoteDateForInput({ quoteDate: date, date }, todayISO()),
      rows: serializeCurtainRowsForDraft(attachProductSnapshotsToCurtainRows(rows)),
      rowCount: rows.length,
      rowIds: rows.map((row) => getWorkshopRowStableId(row)).filter(Boolean),
      repeatSettings,
      quoteFullnessOverrides: normalizeQuoteFullnessOverrides(quoteFullnessOverrides),
      fabricSummaryMeta,
      summaryDiscountMode,
      summaryDiscountValue,
      discountType: getDiscountTypeFromSummaryMode(summaryDiscountMode),
      discountValue: num(summaryDiscountValue),
      quoteGstRate: curtainGstRate,
      gstRate: curtainGstRate,
      gstAmount: Number(latestSummaryTotals.gstAmount || 0),
      taxableAmount: Number(latestSummaryTotals.taxableAmount || 0),
      finalTotal: Number(latestSummaryTotals.finalTotal || latestSummaryTotals.grandTotal || 0),
      grandTotal: Number(latestSummaryTotals.grandTotal || latestSummaryTotals.finalTotal || 0),
      calculationVersion: CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
      formulaCheckStatus: "current",
      recalculationAudit:
        pendingRecalculationAudit === false
          ? null
          : pendingRecalculationAudit || selectedQuote?.recalculationAudit || null,
      legacyCalculationSnapshot:
        pendingLegacyCalculationSnapshot === false
          ? null
          : pendingLegacyCalculationSnapshot ||
            selectedQuote?.legacyCalculationSnapshot ||
            null,
      selectedQuoteWorkStage,
      selectedQuoteStatus,
      selectedInstallationDate,
      useHalfPieceReuse,
      passHalfPieceSavingsToCustomer,
      calculationSettings: {
        ...(selectedQuote?.calculationSettings || {}),
        blindSqftRoundingMode: resolveBlindSqftRoundingMode(
          fabricCalcSettings,
          "exact"
        ),
      },
      pendingCrmLeadId,
      quoteCustomerLinkSelection: quoteCustomerLink,
      expandedCurtainRows,
      settingsSnapshot: {
        unit,
        currency,
        stitchingRates,
        minSqft,
        fabricCalcSettings,
      },
      optimizer: {
        optimizerQuoteId,
        selectedSolutionIndex,
      },
      selectedQuoteCrm: selectedQuote?.crm || null,
      linkedCrmLeadId: pendingCrmLeadId || selectedQuote?.crm?.leadId || "",
    });
  },
  [
    curtainDraftEntityId,
    curtainGstRate,
    attachProductSnapshotsToCurtainRows,
    currency,
    customerName,
    date,
    editingQuoteId,
    expandedCurtainRows,
    fabricCalcSettings,
    fabricSummaryMeta,
    minSqft,
    normalizeCurtainQuoteDraftData,
    optimizerQuoteId,
    pendingLegacyCalculationSnapshot,
    pendingRecalculationAudit,
    passHalfPieceSavingsToCustomer,
    pendingCrmLeadId,
    quoteCustomerLink,
    quoteNo,
    quoteFullnessOverrides,
    repeatSettings,
    rows,
    selectedInstallationDate,
    selectedQuote,
    selectedQuoteStatus,
    selectedQuoteWorkStage,
    selectedSolutionIndex,
    stitchingRates,
    summaryDiscountMode,
    summaryDiscountValue,
    unit,
    useHalfPieceReuse,
  ]
);
const serializeCurtainQuoteDraftData = useCallback(
  (draft = buildCurtainQuoteDraftData()) =>
    JSON.stringify(normalizeCurtainQuoteDraftData(draft || {})),
  [buildCurtainQuoteDraftData, normalizeCurtainQuoteDraftData]
);
const serializeCrmLeadDraftData = useCallback(
  (draft = crmLeadDraft) => JSON.stringify(draft || {}),
  [crmLeadDraft]
);
const serializeExecutionDetailsDraftData = useCallback(
  (draft = crmDetailDraft) => JSON.stringify(draft || {}),
  [crmDetailDraft]
);
const serializeExecutionTemplatesDraftData = useCallback(
  (templates = []) =>
    JSON.stringify(normalizeExecutionTemplateDrafts(templates || [])),
  []
);
const generalQuoteIsDirty = useMemo(
  () =>
    Boolean(generalQuotePersistedSignatureRef.current) &&
    serializeGeneralQuoteDraftData(generalQuote) !== generalQuotePersistedSignatureRef.current,
  [generalQuote, serializeGeneralQuoteDraftData]
);
const curtainQuoteIsDirty = useMemo(
  () =>
    tab === "quote" &&
    quoteSubTab === CURTAIN_QUOTE_TYPE &&
    Boolean(curtainQuotePersistedSignatureRef.current) &&
    serializeCurtainQuoteDraftData() !== curtainQuotePersistedSignatureRef.current,
  [quoteSubTab, serializeCurtainQuoteDraftData, tab]
);
const crmLeadDraftIsDirty = useMemo(
  () =>
    isCrmLeadModalOpen &&
    Boolean(crmLeadPersistedSignatureRef.current) &&
    serializeCrmLeadDraftData(crmLeadDraft) !== crmLeadPersistedSignatureRef.current,
  [crmLeadDraft, isCrmLeadModalOpen, serializeCrmLeadDraftData]
);
const executionDetailsDraftIsDirty = useMemo(
  () =>
    Boolean(selectedCrmDealId) &&
    Boolean(executionDetailsPersistedSignatureRef.current) &&
    serializeExecutionDetailsDraftData(crmDetailDraft) !==
      executionDetailsPersistedSignatureRef.current,
  [crmDetailDraft, selectedCrmDealId, serializeExecutionDetailsDraftData]
);
const executionTemplatesDraftIsDirty = useMemo(
  () =>
    Boolean(executionTemplatePersistedSignatureRef.current) &&
    serializeExecutionTemplatesDraftData(crmExecutionTemplates) !==
      executionTemplatePersistedSignatureRef.current,
  [crmExecutionTemplates, serializeExecutionTemplatesDraftData]
);
executionTemplatesDirtyRef.current = executionTemplatesDraftIsDirty;
const logWorkspaceQuery = useCallback((moduleName, collectionPath, count = null) => {
  if (!isDevRuntime) return;
  console.log("workspaceUid:", workspaceUid);
  console.log("module:", moduleName);
  console.log("collectionPath:", collectionPath);
  if (count != null) {
    console.log(`${moduleName} count:`, count);
  }
}, [workspaceUid]);
const markGeneralQuoteBaseline = useCallback(
  (quoteLike, nextState = "idle") => {
    generalQuotePersistedSignatureRef.current = serializeGeneralQuoteDraftData(
      quoteLike
    );
    updateLocalDraftStatus(LOCAL_DRAFT_MODULES.GENERAL_QUOTE, nextState, null);
  },
  [serializeGeneralQuoteDraftData, updateLocalDraftStatus]
);
const markCurtainQuoteBaseline = useCallback(
  (draftLike = null, nextState = "idle") => {
    const normalizedDraft = normalizeCurtainQuoteDraftData(
      draftLike || buildCurtainQuoteDraftData()
    );
    curtainQuotePersistedSignatureRef.current =
      serializeCurtainQuoteDraftData(normalizedDraft);
    setCurtainTotalsSettingsEditVersion(0);
    updateLocalDraftStatus(LOCAL_DRAFT_MODULES.CURTAIN_QUOTE, nextState, null);
  },
  [
    buildCurtainQuoteDraftData,
    normalizeCurtainQuoteDraftData,
    serializeCurtainQuoteDraftData,
    updateLocalDraftStatus,
  ]
);
const markCrmLeadBaseline = useCallback(
  (draftLike, nextState = "idle") => {
    crmLeadPersistedSignatureRef.current = serializeCrmLeadDraftData(draftLike);
    updateLocalDraftStatus(LOCAL_DRAFT_MODULES.CRM_LEAD, nextState, null);
  },
  [serializeCrmLeadDraftData, updateLocalDraftStatus]
);
const markExecutionDetailsBaseline = useCallback(
  (draftLike, nextState = "idle") => {
    crmDetailDraftDirtyRef.current = false;
    executionDetailsPersistedSignatureRef.current =
      serializeExecutionDetailsDraftData(draftLike);
    updateLocalDraftStatus(
      LOCAL_DRAFT_MODULES.EXECUTION_DETAILS,
      nextState,
      null
    );
  },
  [serializeExecutionDetailsDraftData, updateLocalDraftStatus]
);
const markCrmDetailDraftDirty = useCallback(() => {
  crmDetailDraftDirtyRef.current = true;
  updateLocalDraftStatus(LOCAL_DRAFT_MODULES.EXECUTION_DETAILS, "dirty", null);
}, [updateLocalDraftStatus]);
const updateCrmDetailDraft = useCallback(
  (updater) => {
    markCrmDetailDraftDirty();
    setCrmDetailDraft((prev) => {
      if (!prev) return prev;
      return typeof updater === "function" ? updater(prev) : { ...prev, ...updater };
    });
  },
  [markCrmDetailDraftDirty]
);
const markExecutionTemplatesBaseline = useCallback(
  (
    templatesLike = crmExecutionTemplates,
    selectedId = selectedExecutionTemplateManagerId || "library",
    nextState = "idle"
  ) => {
    executionTemplatePersistedSignatureRef.current =
      serializeExecutionTemplatesDraftData(templatesLike, selectedId);
    updateLocalDraftStatus(
      LOCAL_DRAFT_MODULES.EXECUTION_TEMPLATE,
      nextState,
      null
    );
  },
  [
    crmExecutionTemplates,
    selectedExecutionTemplateManagerId,
    serializeExecutionTemplatesDraftData,
    updateLocalDraftStatus,
  ]
);
const clearCrmLeadDraft = useCallback(() => {
  if (!crmLeadDraftKey) return;
  clearLocalDraft(crmLeadDraftKey);
}, [crmLeadDraftKey]);
const clearCurtainQuoteDraft = useCallback(
  (key = curtainQuoteDraftKey) => {
    if (!key) return;
    clearLocalDraft(key);
  },
  [curtainQuoteDraftKey]
);
const clearExecutionDetailsDraft = useCallback(() => {
  if (!executionDetailsDraftKey) return;
  clearLocalDraft(executionDetailsDraftKey);
}, [executionDetailsDraftKey]);
const clearExecutionTemplatesDraft = useCallback(() => {
  if (!executionTemplatesDraftKey) return;
  clearLocalDraft(executionTemplatesDraftKey);
}, [executionTemplatesDraftKey]);
const persistGeneralQuoteDraft = useCallback(
  (reason = "autosave") => {
    if (!generalQuoteDraftKey || !workspaceUid || !generalQuoteIsDirty) return false;

    const normalized = syncGeneralQuoteDefaults(generalQuote);
    const payload = buildLocalDraftPayload(
      LOCAL_DRAFT_MODULES.GENERAL_QUOTE,
      generalEditingQuoteId || "new",
      normalized,
      getUpdatedMillis(selectedQuote),
      {
        reason,
        quoteNo: normalized.quoteNo || "",
        customerLabel:
          normalized.quoteFor?.company || normalized.quoteFor?.name || normalized.title || "",
      }
    );

    saveLocalDraft(generalQuoteDraftKey, payload);
    generalQuotePersistedSignatureRef.current = serializeGeneralQuoteDraftData(
      normalized
    );
    updateLocalDraftStatus(
      LOCAL_DRAFT_MODULES.GENERAL_QUOTE,
      "autosaved",
      payload.updatedAt
    );
    return true;
  },
  [
    buildLocalDraftPayload,
    generalEditingQuoteId,
    generalQuote,
    generalQuoteDraftKey,
    generalQuoteIsDirty,
    serializeGeneralQuoteDraftData,
    selectedQuote,
    syncGeneralQuoteDefaults,
    updateLocalDraftStatus,
    workspaceUid,
  ]
);
const persistCurtainQuoteDraft = useCallback(
  (reason = "autosave") => {
    if (!curtainQuoteDraftKey || !workspaceUid || !curtainQuoteIsDirty) {
      return false;
    }

    const hasFrozenLegacyRows =
      quoteSubTab === CURTAIN_QUOTE_TYPE &&
      legacyCurtainRecalculationImpactRef.current?.shouldShowBanner === true &&
      !pendingRecalculationAudit &&
      !legacyRecalculationRevisionRequest;

    if (hasFrozenLegacyRows) {
      updateLocalDraftStatus(LOCAL_DRAFT_MODULES.CURTAIN_QUOTE, "paused", null);
      if (isDevRuntime) {
        console.warn("CURTAIN_LEGACY_AUTOSAVE_PAUSED", {
          reason,
          quoteId: curtainDraftEntityId || "new",
          quoteNo,
          rowCount: rows.length,
        });
      }
      return false;
    }

    const normalized = buildCurtainQuoteDraftData();
    if (isQuoteMutationAuditDevMode()) {
      console.log("AUTOSAVE_TRACE", {
        action: "save",
        reason,
        quoteId: curtainDraftEntityId || "new",
        draftUpdatedAt: new Date().toISOString(),
        currentDirty: curtainQuoteIsDirty,
        rowCount: normalized.rows?.length || 0,
        rowSignature: getCurtainRowsSignature(normalized.rows || []),
        editVersion: currentQuoteEditVersionRef.current,
      });
    }
    const payload = buildLocalDraftPayload(
      LOCAL_DRAFT_MODULES.CURTAIN_QUOTE,
      curtainDraftEntityId || "new",
      normalized,
      getUpdatedMillis(selectedQuote),
      {
        reason,
        quoteId: curtainDraftEntityId || "new",
        quoteNo: normalized.quoteNo || "",
        customerLabel:
          normalized.quoteCustomerLink?.customerName ||
          normalized.customerName ||
          "",
        rowCount: normalized.rows.length,
        rowIds: normalized.rows.map((row) => getWorkshopRowStableId(row)).filter(Boolean),
      }
    );

    saveLocalDraft(curtainQuoteDraftKey, payload);
    if (isDevRuntime) {
      console.log("Curtain autosave draft rows", {
        savedRowCount: normalized.rows.length,
        restoredRowCount: null,
        rowIds: normalized.rows.map((row) => getWorkshopRowStableId(row)).filter(Boolean),
      });
    }
    curtainQuotePersistedSignatureRef.current =
      serializeCurtainQuoteDraftData(normalized);
    updateLocalDraftStatus(
      LOCAL_DRAFT_MODULES.CURTAIN_QUOTE,
      "autosaved",
      payload.updatedAt
    );
    return true;
  },
  [
    buildCurtainQuoteDraftData,
    buildLocalDraftPayload,
    curtainDraftEntityId,
    curtainQuoteDraftKey,
    curtainQuoteIsDirty,
    legacyRecalculationRevisionRequest,
    pendingRecalculationAudit,
    quoteNo,
    quoteSubTab,
    rows,
    selectedQuote,
    serializeCurtainQuoteDraftData,
    updateLocalDraftStatus,
    workspaceUid,
  ]
);
const restoreCurtainQuoteDraft = useCallback(
  (draftRecord) => {
    const restoredDraft = normalizeCurtainQuoteDraftData(draftRecord?.data || {});
    const restoredRowIds = restoredDraft.rows
      .map((row) => getWorkshopRowStableId(row))
      .filter(Boolean);

    setTab("quote");
    setQuoteSubTab(CURTAIN_QUOTE_TYPE);
    setGeneralEditingQuoteId(null);
    setCustomerName(restoredDraft.customerName || "");
    setQuoteCustomerLink(restoredDraft.quoteCustomerLink);
    setQuoteNo(restoredDraft.quoteNo || "");
    setDate(resolveQuoteDateForInput(restoredDraft, todayISO()));
    if (isQuoteMutationAuditDevMode()) {
      console.log("AUTOSAVE_TRACE", {
        action: "restore",
        quoteId: restoredDraft.editingQuoteId || restoredDraft.quoteId || "new",
        draftUpdatedAt: draftRecord?.updatedAt || null,
        currentDirty: curtainQuoteIsDirty,
        rowCount: restoredDraft.rows?.length || 0,
        rowSignature: getCurtainRowsSignature(restoredDraft.rows || []),
        editVersion: currentQuoteEditVersionRef.current,
      });
    }
    replaceCurtainRows(restoredDraft.rows, QUOTE_RAW_FIELD_MUTATION_SOURCES.AUTOSAVE_RESTORE, {
      quoteId: restoredDraft.editingQuoteId || restoredDraft.quoteId || "",
    });
    setEffectiveRows(restoredDraft.rows);
    const restoredRepeatSettings = mergeRepeatSettingsFromFabricSummaryMeta(
      restoredDraft.repeatSettings || {},
      restoredDraft.fabricSummaryMeta || {}
    );
    setRepeatSettings(restoredRepeatSettings);
    setFabricSummaryMeta(
      normalizeFabricSummaryMetaWithRepeatSettings(
        restoredDraft.fabricSummaryMeta,
        restoredRepeatSettings
      )
    );
    setSummaryDiscountMode(restoredDraft.summaryDiscountMode === "%" ? "%" : "");
    setSummaryDiscountValue(restoredDraft.summaryDiscountValue || "0");
    setCurtainGstRate(
      normalizeCurtainGstRate(restoredDraft.quoteGstRate ?? restoredDraft.gstRate)
    );
    const restoredQuoteFullnessOverrides = normalizeQuoteFullnessOverrides(
      restoredDraft.quoteFullnessOverrides || {}
    );
    quoteFullnessOverridesRef.current = restoredQuoteFullnessOverrides;
    setQuoteFullnessOverrides(restoredQuoteFullnessOverrides);
    setQuoteFullnessOverrideInputs({});
    setSelectedQuoteWorkStage(
      restoredDraft.selectedQuoteWorkStage || "quote_given"
    );
    setSelectedQuoteStatus(restoredDraft.selectedQuoteStatus || "draft");
    setSelectedInstallationDate(restoredDraft.selectedInstallationDate || null);
    setUseHalfPieceReuse(restoredDraft.useHalfPieceReuse !== false);
    setPassHalfPieceSavingsToCustomer(
      restoredDraft.passHalfPieceSavingsToCustomer === true
    );
    setPendingCrmLeadId(restoredDraft.pendingCrmLeadId || "");
    setExpandedCurtainRows(restoredDraft.expandedCurtainRows || {});
    setEditingQuoteId(restoredDraft.editingQuoteId || null);

    curtainQuotePersistedSignatureRef.current =
      serializeCurtainQuoteDraftData(restoredDraft);
    updateLocalDraftStatus(
      LOCAL_DRAFT_MODULES.CURTAIN_QUOTE,
      "restored",
      draftRecord?.updatedAt || null
    );
    if (isDevRuntime) {
      console.log("Curtain autosave draft rows", {
        savedRowCount:
          Number(draftRecord?.metadata?.rowCount || draftRecord?.data?.rowCount || 0) ||
          restoredDraft.rows.length,
        restoredRowCount: restoredDraft.rows.length,
        rowIds: restoredRowIds,
      });
    }
  },
  [
    curtainQuoteIsDirty,
    normalizeCurtainQuoteDraftData,
    replaceCurtainRows,
    serializeCurtainQuoteDraftData,
    updateLocalDraftStatus,
  ]
);
const persistCrmLeadDraftLocally = useCallback(
  (reason = "autosave") => {
    if (!crmLeadDraftKey || !workspaceUid || !isCrmLeadModalOpen || !crmLeadDraftIsDirty) {
      return false;
    }

    const payload = buildLocalDraftPayload(
      LOCAL_DRAFT_MODULES.CRM_LEAD,
      "new",
      crmLeadDraft || {},
      0,
      { reason }
    );
    saveLocalDraft(crmLeadDraftKey, payload);
    crmLeadPersistedSignatureRef.current = serializeCrmLeadDraftData(crmLeadDraft);
    updateLocalDraftStatus(
      LOCAL_DRAFT_MODULES.CRM_LEAD,
      "autosaved",
      payload.updatedAt
    );
    return true;
  },
  [
    buildLocalDraftPayload,
    crmLeadDraft,
    crmLeadDraftIsDirty,
    crmLeadDraftKey,
    isCrmLeadModalOpen,
    serializeCrmLeadDraftData,
    updateLocalDraftStatus,
    workspaceUid,
  ]
);
const persistExecutionDetailsDraftLocally = useCallback(
  (reason = "autosave") => {
    if (
      !executionDetailsDraftKey ||
      !workspaceUid ||
      !selectedCrmDealId ||
      !crmDetailDraft ||
      !executionDetailsDraftIsDirty
    ) {
      return false;
    }

    const payload = buildLocalDraftPayload(
      LOCAL_DRAFT_MODULES.EXECUTION_DETAILS,
      selectedCrmDealId,
      crmDetailDraft,
      0,
      {
        reason,
        dealTitle: crmDetailDraft?.title || "",
      }
    );
    saveLocalDraft(executionDetailsDraftKey, payload);
    executionDetailsPersistedSignatureRef.current =
      serializeExecutionDetailsDraftData(crmDetailDraft);
    updateLocalDraftStatus(
      LOCAL_DRAFT_MODULES.EXECUTION_DETAILS,
      "autosaved",
      payload.updatedAt
    );
    return true;
  },
  [
    buildLocalDraftPayload,
    crmDetailDraft,
    executionDetailsDraftIsDirty,
    executionDetailsDraftKey,
    selectedCrmDealId,
    serializeExecutionDetailsDraftData,
    updateLocalDraftStatus,
    workspaceUid,
  ]
);
const persistExecutionTemplatesDraftLocally = useCallback(
  (reason = "autosave") => {
    if (
      !executionTemplatesDraftKey ||
      !workspaceUid ||
      !executionTemplatesDraftIsDirty
    ) {
      return false;
    }

    const payload = buildLocalDraftPayload(
      LOCAL_DRAFT_MODULES.EXECUTION_TEMPLATE,
      selectedExecutionTemplateManagerId || "library",
      {
        selectedId: selectedExecutionTemplateManagerId || "",
        templates: normalizeExecutionTemplateDrafts(crmExecutionTemplates),
      },
      0,
      { reason }
    );
    saveLocalDraft(executionTemplatesDraftKey, payload);
    executionTemplatePersistedSignatureRef.current =
      serializeExecutionTemplatesDraftData(
        crmExecutionTemplates,
        selectedExecutionTemplateManagerId || "library"
      );
    updateLocalDraftStatus(
      LOCAL_DRAFT_MODULES.EXECUTION_TEMPLATE,
      "autosaved",
      payload.updatedAt
    );
    return true;
  },
  [
    buildLocalDraftPayload,
    crmExecutionTemplates,
    executionTemplatesDraftIsDirty,
    executionTemplatesDraftKey,
    selectedExecutionTemplateManagerId,
    serializeExecutionTemplatesDraftData,
    updateLocalDraftStatus,
    workspaceUid,
  ]
);
const promptToRestoreLocalDraft = useCallback(
  ({
    module,
    key,
    title,
    description,
    restoreLabel = "Restore",
    discardLabel = "Discard",
    serverNewerDescription =
      "A newer saved version is available. Restore this copy only if you want to continue from these earlier changes.",
    serverUpdatedAt = 0,
    onRestore,
  }) => {
    if (!key || dismissedDraftKeysRef.current.has(key)) return;

    const draftRecord = loadLocalDraft(key);
    if (!draftRecord) return;
    const draftMeta = getDraftMeta(key);

    const serverIsNewer =
      Number(serverUpdatedAt || 0) > 0 &&
      Number(serverUpdatedAt || 0) >
        Number(draftMeta?.serverUpdatedAt || draftRecord.serverUpdatedAt || 0);

    setDraftRecoveryPrompt({
      key,
      module,
      title,
      description,
      restoreLabel,
      discardLabel,
      serverNewerDescription,
      serverIsNewer,
      savedAt: draftMeta?.savedAt || draftRecord.updatedAt || null,
      onRestore: () => {
        const latestDraft = loadLocalDraft(key);
        dismissedDraftKeysRef.current.add(key);
        setDraftRecoveryPrompt(null);
        if (!latestDraft) return;
        onRestore(latestDraft);
      },
      onDiscard: () => {
        dismissedDraftKeysRef.current.add(key);
        clearLocalDraftRecord(key);
        setDraftRecoveryPrompt(null);
        updateLocalDraftStatus(module, "idle", null);
      },
    });
  },
  [updateLocalDraftStatus]
);
const getLocalDraftStatusText = useCallback(
  (module) => {
    const status = localDraftStatuses[module] || { state: "idle", savedAt: null };
    if (status.state === "dirty") return "Unsaved changes";
    if (status.state === "autosaved") {
      return `Autosaved locally${
        status.savedAt
          ? ` at ${new Date(status.savedAt).toLocaleTimeString("en-IN", {
              hour: "numeric",
              minute: "2-digit",
            })}`
          : ""
      }`;
    }
    if (status.state === "saved") return "Saved";
    if (status.state === "restored") return "Draft restored";
    if (status.state === "paused") {
      return "Autosave paused for legacy quote until you save as a new revision.";
    }
    return "Ready";
  },
  [localDraftStatuses]
);
const isWorkspaceOwner = workspaceContext?.role !== "staff";
const currentWorkspaceMember = useMemo(
  () =>
    findMatchingTeamMember(
      teamMembers,
      user,
      workspaceContext?.memberId || ""
    ),
  [teamMembers, user, workspaceContext?.memberId]
);
const currentWorkspacePermissions = useMemo(() => {
  if (isWorkspaceOwner) return OWNER_TEAM_PERMISSIONS;

  return normalizePermissionSet(
    currentWorkspaceMember?.permissions || workspaceContext?.permissions || {}
  );
}, [
  currentWorkspaceMember?.permissions,
  isWorkspaceOwner,
  workspaceContext?.permissions,
]);
const currentWorkspaceStatus = String(
  currentWorkspaceMember?.status || workspaceContext?.status || "active"
);
const isWorkspaceAccessRevoked =
  !isWorkspaceOwner &&
  (currentWorkspaceStatus === "disabled" ||
    currentWorkspaceMember?.isActive === false);
const canAccessTeamPermission = useCallback(
  (permissionKey) =>
    isWorkspaceOwner || hasFeatureAccess(currentWorkspacePermissions, permissionKey),
  [currentWorkspacePermissions, isWorkspaceOwner]
);
const canAccessFeature = useCallback(
  (featureKey) => hasActiveSubscription && hasFeature(featureKey),
  [hasActiveSubscription, hasFeature]
);
const planAllowsDashboard = canAccessFeature("dashboard");
const planAllowsCrm = canAccessFeature("crm");
const planAllowsGeneralQuotes = canAccessFeature("generalQuotes");
const planAllowsCurtainQuotes = canAccessFeature("curtainQuotes");
const planAllowsAnyQuotes = planAllowsGeneralQuotes || planAllowsCurtainQuotes;
const planAllowsCustomerPdf = canAccessFeature("customerPdf");
const planAllowsWorkshopPdf = canAccessFeature("workshopPdf");
const planAllowsSavedQuotes = canAccessFeature("savedQuotes");
const planAllowsOptimizer = canAccessFeature("rollOptimizer");
const planAllowsStitching = canAccessFeature("stitchingRates");
const planAllowsSettings = canAccessFeature("settings");
const canAccessTeamAccess =
  isSuperAdmin || ["owner", "admin"].includes(accountUser?.role || "");
const canViewQuotes =
  planAllowsAnyQuotes &&
  (isWorkspaceOwner ||
    canAccessTeamPermission("quotationsView") ||
    canAccessTeamPermission("quotationsCreate") ||
    canAccessTeamPermission("quotationsEdit"));
const canCreateQuotes =
  planAllowsAnyQuotes && (isWorkspaceOwner || canAccessTeamPermission("quotationsCreate"));
const canEditQuotes =
  planAllowsAnyQuotes && (isWorkspaceOwner || canAccessTeamPermission("quotationsEdit"));
const canUseCustomerPdf =
  planAllowsCustomerPdf && (isWorkspaceOwner || canAccessTeamPermission("customerPdf"));
const canUseWorkshopPdf =
  planAllowsWorkshopPdf && (isWorkspaceOwner || canAccessTeamPermission("workshopPdf"));
const canAccessSavedQuotes =
  planAllowsSavedQuotes && (isWorkspaceOwner || canAccessTeamPermission("savedQuotes"));
const canAccessDashboard =
  planAllowsDashboard && (isWorkspaceOwner || canAccessTeamPermission("dashboard"));
const canAccessCrm =
  planAllowsCrm && (isWorkspaceOwner || canAccessTeamPermission("crm"));
const canAccessOptimizer =
  planAllowsOptimizer && (isWorkspaceOwner || canAccessTeamPermission("optimizer"));
const canAccessStitching =
  planAllowsStitching && (isWorkspaceOwner || canAccessTeamPermission("stitchingRatesTab"));
const canAccessSettingsTab =
  planAllowsSettings && (isWorkspaceOwner || canAccessTeamPermission("settings"));
const canManageProductConfigs = canAccessSettingsTab || canAccessStitching;
const canAccessCurtainQuotes = planAllowsCurtainQuotes && canViewQuotes;
const canAccessGeneralQuotes = planAllowsGeneralQuotes && canViewQuotes;
const accessibleTabs = useMemo(() => {
  const items = [];

  if (canAccessDashboard) items.push("dashboard");
  if (canViewQuotes) items.push("quote");
  if (canAccessSavedQuotes) items.push("saved");
  if (canAccessCrm) items.push("crm");
  if (canAccessCrm) items.push("customerLedger");
  if (canAccessCrm) items.push("todayTasks");
  if (canAccessCrm) items.push("executionSpecs");
  if (canAccessCrm) items.push("executionTemplates");
  if (canAccessOptimizer) items.push("fco");
  if (canManageProductConfigs) items.push("products");
  if (canAccessStitching) items.push("stitching");
  if (canAccessTeamAccess) items.push("teamAccess");
  if (user?.uid) items.push("accountSecurity");
  if (isSuperAdmin && IS_DEV_UI_ENABLED) items.push("maintenance");
  if (canAccessSettingsTab) items.push("settings");

  return items.length > 0 ? items : [];
}, [
  canAccessCrm,
  canAccessDashboard,
  canAccessOptimizer,
  canAccessSavedQuotes,
  canAccessSettingsTab,
  canAccessStitching,
  canAccessTeamAccess,
  canManageProductConfigs,
  canViewQuotes,
  isSuperAdmin,
  user?.uid,
]);
const quoteEditorReadOnly =
  !isWorkspaceOwner && canViewQuotes && !canEditQuotes;

const fallbackTab = accessibleTabs[0] || "dashboard";

const setRoutedTab = useCallback(
  (nextTab, nextQuoteSubTab = null, options = {}) => {
    const normalizedNextTab = nextTab === "dashboardList" ? "dashboard" : nextTab;
    const allowedTab = accessibleTabs.includes(normalizedNextTab)
      ? nextTab
      : fallbackTab;

    if (allowedTab === "quote") {
      const requestedQuoteType =
        nextQuoteSubTab ||
        (canAccessCurtainQuotes ? CURTAIN_QUOTE_TYPE : GENERAL_QUOTE_TYPE);
      const allowedQuoteType =
        requestedQuoteType === GENERAL_QUOTE_TYPE && canAccessGeneralQuotes
          ? GENERAL_QUOTE_TYPE
          : requestedQuoteType === CURTAIN_QUOTE_TYPE && canAccessCurtainQuotes
          ? CURTAIN_QUOTE_TYPE
          : canAccessCurtainQuotes
          ? CURTAIN_QUOTE_TYPE
          : GENERAL_QUOTE_TYPE;
      setQuoteSubTab(allowedQuoteType);
    }

    setTab(allowedTab);

    if (options.updatePath !== false) {
      const route = getRouteForTab(allowedTab, nextQuoteSubTab || quoteSubTab);
      if (route && window.location.pathname !== route) {
        window.history.replaceState({}, "", route);
      }
    }
  },
  [
    accessibleTabs,
    canAccessCurtainQuotes,
    canAccessGeneralQuotes,
    fallbackTab,
    quoteSubTab,
  ]
);

useEffect(() => {
  if (accountLoading || !hasActiveSubscription) return;

  const route = ROUTE_TO_TAB[window.location.pathname];
  if (route) {
    if (window.location.pathname === "/customer-pdf" && !canUseCustomerPdf) {
      setRoutedTab(accessibleTabs[0] || "dashboard");
      return;
    }

    if (window.location.pathname === "/workshop-pdf" && !canUseWorkshopPdf) {
      setRoutedTab(accessibleTabs[0] || "dashboard");
      return;
    }

    if (route.quoteSubTab === GENERAL_QUOTE_TYPE && !canAccessGeneralQuotes) {
      setRoutedTab(accessibleTabs[0] || "dashboard");
      return;
    }

    if (route.quoteSubTab === CURTAIN_QUOTE_TYPE && !canAccessCurtainQuotes) {
      setRoutedTab(accessibleTabs[0] || "dashboard");
      return;
    }

    const normalizedRouteTab = route.tab === "dashboardList" ? "dashboard" : route.tab;
    if (!accessibleTabs.includes(normalizedRouteTab)) {
      setRoutedTab(accessibleTabs[0] || "dashboard");
      return;
    }

    setRoutedTab(route.tab, route.quoteSubTab || null, { updatePath: false });
    return;
  }

  const normalizedActiveTab =
    tab === "dashboardList" ? "dashboard" : tab;

  if (!accessibleTabs.includes(normalizedActiveTab)) {
    setRoutedTab(accessibleTabs[0] || "dashboard");
  }

  if (tab === "quote") {
    if (quoteSubTab === GENERAL_QUOTE_TYPE && !canAccessGeneralQuotes) {
      setRoutedTab("quote", CURTAIN_QUOTE_TYPE);
    }
    if (quoteSubTab === CURTAIN_QUOTE_TYPE && !canAccessCurtainQuotes) {
      setRoutedTab("quote", GENERAL_QUOTE_TYPE);
    }
  }
}, [
  accessibleTabs,
  accountLoading,
  canAccessCurtainQuotes,
  canAccessGeneralQuotes,
  canUseCustomerPdf,
  canUseWorkshopPdf,
  hasActiveSubscription,
  quoteSubTab,
  setRoutedTab,
  tab,
]);

useEffect(() => {
  if (accountLoading || !hasActiveSubscription) return;

  const params = new URLSearchParams(window.location.search);
  const leadId = params.get("leadId");
  const customerId = params.get("customerId");

  if (window.location.pathname === "/crm" && leadId) {
    setSelectedCrmDealId(leadId);
    setCrmView("list");
    setIsCrmDrawerPinned(true);
    setIsCrmDrawerHovered(false);
    window.history.replaceState({}, "", "/crm");
  }

  if (window.location.pathname === "/customer-ledger" && customerId) {
    setSelectedCustomerLedgerId(customerId);
    setCustomerLedgerDetailTab("overview");
    window.history.replaceState({}, "", "/customer-ledger");
  }
}, [accountLoading, hasActiveSubscription]);

const activeTeamMembers = useMemo(
  () => teamMembers.filter((member) => isActiveTeamMember(member)),
  [teamMembers]
);
const occupiedStaffSlots = useMemo(
  () => countOccupiedStaffSlots(teamMembers),
  [teamMembers]
);
const accountStaffLimit = normalizeStaffLimit(account?.staffLimit, DEFAULT_STAFF_LIMIT);
const remainingStaffSlots = Math.max(0, accountStaffLimit - occupiedStaffSlots);

const teamAccessRows = useMemo(() => {
  const accountMember = accountUser?.member
    ? {
        id: accountUser.member.uid || user?.uid || "current-user",
        uid: accountUser.member.uid || user?.uid || "",
        name: accountUser.displayName || businessName || "Current User",
        email: accountUser.member.email || accountUser.email || user?.email || "",
        role: accountUser.member.role || accountUser.role || "owner",
        plan: accountUser.member.plan || accountUser.plan || account?.plan || "full",
        isActive: accountUser.member.isActive !== false,
        accountName: account?.name || businessName || "FabriQuote Workspace",
        features: accountUser.member.features || features || {},
      }
    : null;

  const legacyRows = (teamMembers || []).map((member) => ({
    id: member.id || member.uid || member.email,
    uid: member.uid || "",
    name: member.name || "Team member",
    email: member.email || "",
    role: member.role || "staff",
    plan: account?.plan || accountUser?.plan || "full",
    isActive: member.isActive !== false && member.status !== "disabled",
    accountName: account?.name || businessName || "FabriQuote Workspace",
    features: features || {},
  }));

  const rows = accountMember ? [accountMember, ...legacyRows] : legacyRows;
  const seen = new Set();
  return rows.filter((row) => {
    const key = row.uid || row.email || row.id;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}, [account, accountUser, businessName, features, teamMembers, user]);

const accessFeatureLabels = [
  ["dashboard", "Dashboard"],
  ["crm", "CRM"],
  ["generalQuotes", "General Quotes"],
  ["curtainQuotes", "Curtain Quotes"],
  ["stitchingRates", "Stitching & Rates"],
  ["rollOptimizer", "Roll Optimizer"],
  ["customerPdf", "Customer PDF"],
  ["workshopPdf", "Workshop PDF"],
  ["savedQuotes", "Saved Quotes"],
  ["settings", "Settings"],
];

const teamCellStyle = {
  padding: "14px 12px",
  background: "rgba(255,255,255,0.72)",
  borderTop: "1px solid rgba(226,232,240,0.9)",
  borderBottom: "1px solid rgba(226,232,240,0.9)",
  fontSize: 13,
  fontWeight: 700,
};

const statusPillStyle = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: 999,
  padding: "5px 9px",
  fontSize: 11,
  fontWeight: 900,
};

const getTeamMemberById = useCallback(
  (memberId) => teamMembers.find((member) => member.id === memberId) || null,
  [teamMembers]
);

const getTeamMemberLabel = useCallback(
  (memberId, snapshot = "") => {
    const member = getTeamMemberById(memberId);
    if (member?.name) return member.name;
    return String(snapshot || "").trim() || "Unassigned";
  },
  [getTeamMemberById]
);

const getAssignableTeamMembers = useCallback(
  (selectedMemberId = "") => {
    const selectedMember = getTeamMemberById(selectedMemberId);
    const options = [...activeTeamMembers];

    if (
      selectedMember &&
      !options.some((member) => member.id === selectedMember.id)
    ) {
      options.push(selectedMember);
    }

    return options;
  },
  [activeTeamMembers, getTeamMemberById]
);

const enqueueToast = useCallback((message, tone = "info", duration = 3800) => {
  const text = String(message || "").trim();
  if (!text) return "";

  const toastId = createClientId("toast");

  setAppToasts((prev) => [
    ...prev,
    {
      id: toastId,
      tone,
      message: text,
    },
  ]);

  const timeoutId = window.setTimeout(() => {
    setAppToasts((prev) => prev.filter((toast) => toast.id !== toastId));
    toastTimersRef.current.delete(toastId);
  }, duration);

  toastTimersRef.current.set(toastId, timeoutId);
  return toastId;
}, []);

const dismissToast = useCallback((toastId) => {
  const timeoutId = toastTimersRef.current.get(toastId);
  if (timeoutId) {
    window.clearTimeout(timeoutId);
    toastTimersRef.current.delete(toastId);
  }

  setAppToasts((prev) => prev.filter((toast) => toast.id !== toastId));
}, []);

const notify = useCallback(
  (message, tone = "info", duration = 3800) =>
    enqueueToast(message, tone, duration),
  [enqueueToast]
);

const raiseFirebaseHealthWarning = useCallback(
  (kind, error, details = {}) => {
    const isFirestoreQuota =
      kind === "firestore" && isFirestoreResourceExhausted(error);
    const isStorageBilling =
      kind === "storage" && isFirebaseStorageBillingError(error);

    if (!isFirestoreQuota && !isStorageBilling) return;

    const message = isStorageBilling
      ? "Firebase Storage billing or quota issue detected. Image uploads may fail."
      : "Firebase usage limit reached. Firestore reads/writes may fail.";

    setFirebaseHealthWarning({
      kind,
      message,
      code: error?.code || "",
      detail: error?.message || String(error || ""),
      details,
      detectedAt: new Date().toISOString(),
    });

    if (isDevRuntime) {
      console.error("Firebase quota/billing issue detected", {
        kind,
        code: error?.code,
        message: error?.message,
        details,
      });
    }
  },
  []
);

useEffect(() => {
  if (!IS_DEV_UI_ENABLED) return undefined;
  console.log("FIREBASE_PROJECT_TRACE", {
    projectId: firebaseApp.options.projectId,
    authDomain: firebaseApp.options.authDomain,
    storageBucket: firebaseApp.options.storageBucket,
  });
  return undefined;
}, []);

useEffect(() => {
  if (!IS_DEV_UI_ENABLED) return undefined;

  const intervalId = window.setInterval(() => {
    setFirebaseReadHealthTick((value) => value + 1);
  }, 5000);

  return () => window.clearInterval(intervalId);
}, []);

useEffect(() => {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return undefined;
  }

  let cancelled = false;
  navigator.serviceWorker
    .getRegistration("/firebase-messaging-sw.js")
    .then((registration) => {
      if (cancelled || !registration) return;
      if (isDevRuntime) {
        console.log("Removing legacy Firebase Messaging service worker", {
          scope: registration.scope,
        });
      }
      registration.unregister().catch((error) => {
        if (isDevRuntime) {
          console.warn("Could not unregister Firebase Messaging service worker", error);
        }
      });
    })
    .catch((error) => {
      if (isDevRuntime) {
        console.warn("Legacy Firebase Messaging service worker cleanup failed", error);
      }
    });

  return () => {
    cancelled = true;
  };
}, []);

useEffect(() => {
  if (tab !== "quote" || quoteSubTab !== CURTAIN_QUOTE_TYPE) {
    return undefined;
  }

  let cancelled = false;
  const runPreload = () => {
    if (!cancelled) {
      preloadWorkshopZipRuntime();
    }
  };

  const canUseIdleCallback =
    typeof window !== "undefined" &&
    typeof window.requestIdleCallback === "function";
  const preloadHandle = canUseIdleCallback
    ? window.requestIdleCallback(runPreload, { timeout: 4000 })
    : window.setTimeout(runPreload, 1500);

  return () => {
    cancelled = true;
    if (typeof window === "undefined") return;
    if (
      canUseIdleCallback &&
      typeof window.cancelIdleCallback === "function"
    ) {
      window.cancelIdleCallback(preloadHandle);
      return;
    }
    window.clearTimeout(preloadHandle);
  };
}, [quoteSubTab, tab]);

const registerCurrentSecuritySession = useCallback(
  async ({ force = false, reason = "boot" } = {}) => {
    const currentUser = auth.currentUser || user;
    if (!currentUser?.uid) return "";

    const sessionId = getOrCreateSecuritySessionId(currentUser.uid);
    if (!sessionId) return "";

    const now = Date.now();
    const registration = securitySessionRegisteredRef.current;
    const recentlyRegistered =
      registration.uid === currentUser.uid &&
      registration.sessionId === sessionId &&
      now - Number(registration.lastHeartbeatAt || 0) < ACCOUNT_SECURITY_HEARTBEAT_MS;

    if (!force && recentlyRegistered) {
      setSecuritySessionId(sessionId);
      return sessionId;
    }

    if (registration.registering) return sessionId;

    securitySessionRegisteredRef.current = {
      ...registration,
      uid: currentUser.uid,
      sessionId,
      registering: true,
    };

    try {
      const registerSession = httpsCallable(functions, "registerSession");
      await registerSession({
        sessionId,
        reason,
        device: await getSafeSessionDeviceInfo(),
      });
      securitySessionRegisteredRef.current = {
        uid: currentUser.uid,
        sessionId,
        registering: false,
        lastHeartbeatAt: Date.now(),
      };
      setSecuritySessionId(sessionId);
      return sessionId;
    } catch (error) {
      securitySessionRegisteredRef.current = {
        uid: currentUser.uid,
        sessionId,
        registering: false,
        lastHeartbeatAt: 0,
      };
      if (isDevRuntime) {
        console.error("ACCOUNT SECURITY SESSION REGISTER FAILED", {
          code: error?.code,
          message: error?.message,
          stack: error?.stack,
          uid: currentUser.uid,
          sessionId,
          reason,
        });
      }
      if (String(error?.code || "").includes("permission-denied")) {
        clearSecuritySessionId(currentUser.uid);
        notify("This session was signed out for security.", "warning", 6000);
        await signOut(auth);
      }
      return "";
    }
  },
  [notify, user]
);

useEffect(() => {
  if (!user?.uid) {
    setSecuritySessionId("");
    securitySessionRegisteredRef.current = {
      uid: "",
      sessionId: "",
      registering: false,
      lastHeartbeatAt: 0,
    };
    return undefined;
  }

  let cancelled = false;
  registerCurrentSecuritySession({ force: true, reason: "auth_boot" }).then((sessionId) => {
    if (!cancelled && sessionId) setSecuritySessionId(sessionId);
  });

  const heartbeat = () => {
    if (document.visibilityState === "hidden") return;
    registerCurrentSecuritySession({ reason: "focus_or_visibility" });
  };

  window.addEventListener("focus", heartbeat);
  document.addEventListener("visibilitychange", heartbeat);

  return () => {
    cancelled = true;
    window.removeEventListener("focus", heartbeat);
    document.removeEventListener("visibilitychange", heartbeat);
  };
}, [registerCurrentSecuritySession, user?.uid]);

useEffect(() => {
  if (!user?.uid || !securitySessionId) return undefined;

  const sessionRef = doc(db, "users", user.uid, "sessions", securitySessionId);
  return trackedOnSnapshot(
    sessionRef,
    {
      label: "accountSecurityCurrentSessionRevocationCheck",
      cacheKey: `users/${user.uid}/sessions/${securitySessionId}`,
    },
    async (snap) => {
      const data = snap.exists() ? snap.data() : null;
      if (data?.revoked === true) {
        if (isDevRuntime) {
          console.warn("ACCOUNT SECURITY CURRENT SESSION REVOKED", {
            uid: user.uid,
            sessionId: securitySessionId,
            revokedReason: data.revokedReason || "",
          });
        }
        clearSecuritySessionId(user.uid);
        notify("This session was signed out for security.", "warning", 7000);
        await signOut(auth);
      }
    },
    (error) => {
      if (isDevRuntime) {
        console.error("ACCOUNT SECURITY SESSION LISTENER FAILED", {
          code: error?.code,
          message: error?.message,
          uid: user.uid,
          sessionId: securitySessionId,
        });
      }
    }
  );
}, [notify, securitySessionId, user?.uid]);

const loadAccountSecurityData = useCallback(async () => {
  const currentUser = auth.currentUser || user;
  if (!currentUser?.uid) return;

  setSecurityCenterState({
    loading: true,
    error: "",
    loadedForUid: currentUser.uid,
  });

  try {
    await registerCurrentSecuritySession({ reason: "security_page_open" });
    const sessionsRef = collection(db, "users", currentUser.uid, "sessions");
    const activityRef = collection(db, "users", currentUser.uid, "security_activity");
    const [sessionsSnap, activitySnap] = await Promise.all([
      getDocs(
        firestoreQuery(
          sessionsRef,
          firestoreWhere("revoked", "==", false),
          firestoreOrderBy("lastSeenAt", "desc"),
          firestoreLimit(ACCOUNT_SECURITY_SESSION_LIMIT)
        ),
        {
          label: "accountSecurityActiveSessions",
          cacheKey: `users/${currentUser.uid}/sessions?revoked=false&orderBy=lastSeenAt_desc&limit=${ACCOUNT_SECURITY_SESSION_LIMIT}`,
        }
      ),
      getDocs(
        firestoreQuery(
          activityRef,
          firestoreOrderBy("createdAt", "desc"),
          firestoreLimit(ACCOUNT_SECURITY_ACTIVITY_LIMIT)
        ),
        {
          label: "accountSecurityActivity",
          cacheKey: `users/${currentUser.uid}/security_activity?orderBy=createdAt_desc&limit=${ACCOUNT_SECURITY_ACTIVITY_LIMIT}`,
        }
      ),
    ]);

    setSecuritySessions(
      sessionsSnap.docs.map((sessionDoc) => ({
        id: sessionDoc.id,
        ...sessionDoc.data(),
      }))
    );
    setSecurityActivity(
      activitySnap.docs.map((activityDoc) => ({
        id: activityDoc.id,
        ...activityDoc.data(),
      }))
    );
    setSecurityCenterState({
      loading: false,
      error: "",
      loadedForUid: currentUser.uid,
    });
  } catch (error) {
    if (isDevRuntime) {
      console.error("ACCOUNT SECURITY LOAD FAILED", {
        code: error?.code,
        message: error?.message,
        stack: error?.stack,
        uid: currentUser.uid,
      });
    }
    setSecurityCenterState({
      loading: false,
      error: getSecurityAuthErrorMessage(error),
      loadedForUid: currentUser.uid,
    });
    notify(getSecurityAuthErrorMessage(error), "error", 6000);
  }
}, [notify, registerCurrentSecuritySession, user]);

useEffect(() => {
  if (tab !== "accountSecurity" || !user?.uid) return;
  loadAccountSecurityData();
}, [loadAccountSecurityData, tab, user?.uid]);

const handleChangePassword = useCallback(async () => {
  const currentUser = auth.currentUser || user;
  const providerSummary = getSecurityProviderSummary(currentUser);
  const currentPassword = passwordForm.currentPassword;
  const newPassword = passwordForm.newPassword;
  const confirmPassword = passwordForm.confirmPassword;

  if (!currentUser?.email || !providerSummary.hasPassword) {
    setPasswordForm((prev) => ({
      ...prev,
      error: "Password changes are managed by your sign-in provider.",
      message: "",
    }));
    return;
  }

  if (!currentPassword || !newPassword || !confirmPassword) {
    setPasswordForm((prev) => ({
      ...prev,
      error: "Enter your current password, new password, and confirmation.",
      message: "",
    }));
    return;
  }

  if (newPassword.length < 8) {
    setPasswordForm((prev) => ({
      ...prev,
      error: "New password must be at least 8 characters.",
      message: "",
    }));
    return;
  }

  if (newPassword === currentPassword) {
    setPasswordForm((prev) => ({
      ...prev,
      error: "New password must be different from your current password.",
      message: "",
    }));
    return;
  }

  if (newPassword !== confirmPassword) {
    setPasswordForm((prev) => ({
      ...prev,
      error: "New password and confirmation do not match.",
      message: "",
    }));
    return;
  }

  setSecurityActionState((prev) => ({ ...prev, password: true }));
  setPasswordForm((prev) => ({ ...prev, error: "", message: "" }));

  try {
    const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
    await reauthenticateWithCredential(currentUser, credential);
    await updatePassword(currentUser, newPassword);
    const sessionId =
      securitySessionId ||
      (await registerCurrentSecuritySession({ force: true, reason: "password_change" }));
    let postUpdateWarning = "";
    if (sessionId) {
      try {
        const recordPasswordChanged = httpsCallable(functions, "recordPasswordChanged");
        await recordPasswordChanged({
          sessionId,
          revokeOtherAppSessions: passwordForm.logoutOtherSessions,
        });
      } catch (activityError) {
        postUpdateWarning =
          "Password updated, but security activity could not be logged.";
        if (isDevRuntime) {
          console.error("ACCOUNT SECURITY PASSWORD ACTIVITY LOG FAILED", {
            code: activityError?.code,
            message: activityError?.message,
            stack: activityError?.stack,
            uid: currentUser?.uid,
            sessionId,
          });
        }
      }
    }
    setPasswordForm({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
      logoutOtherSessions: true,
      showPasswords: false,
      message:
        postUpdateWarning ||
        (passwordForm.logoutOtherSessions
          ? "Password updated. Other app sessions were signed out."
          : "Password updated."),
      error: "",
    });
    notify(postUpdateWarning || "Password updated successfully.", postUpdateWarning ? "warning" : "success");
    await loadAccountSecurityData();
  } catch (error) {
    const message = getSecurityAuthErrorMessage(error);
    if (isDevRuntime) {
      console.error("ACCOUNT SECURITY PASSWORD CHANGE FAILED", {
        code: error?.code,
        message: error?.message,
        stack: error?.stack,
        uid: currentUser?.uid,
      });
    }
    setPasswordForm({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
      logoutOtherSessions: true,
      showPasswords: false,
      message: "",
      error: message,
    });
    notify(message, "error", 6000);
  } finally {
    setSecurityActionState((prev) => ({ ...prev, password: false }));
  }
}, [
  loadAccountSecurityData,
  notify,
  passwordForm.confirmPassword,
  passwordForm.currentPassword,
  passwordForm.logoutOtherSessions,
  passwordForm.newPassword,
  registerCurrentSecuritySession,
  securitySessionId,
  user,
]);

const handleRevokeCurrentSession = useCallback(async () => {
  const currentUser = auth.currentUser || user;
  const sessionId = securitySessionId;
  if (!currentUser?.uid || !sessionId) return;

  setSecurityActionState((prev) => ({ ...prev, currentSession: true }));
  try {
    const revokeCurrentSession = httpsCallable(functions, "revokeCurrentSession");
    await revokeCurrentSession({ sessionId });
    clearSecuritySessionId(currentUser.uid);
    notify("This device has been signed out.", "success");
    await signOut(auth);
  } catch (error) {
    if (isDevRuntime) {
      console.error("ACCOUNT SECURITY LOGOUT CURRENT FAILED", {
        code: error?.code,
        message: error?.message,
        stack: error?.stack,
        uid: currentUser.uid,
        sessionId,
      });
    }
    notify(getSecurityAuthErrorMessage(error), "error", 6000);
  } finally {
    setSecurityActionState((prev) => ({ ...prev, currentSession: false }));
  }
}, [notify, securitySessionId, user]);

const handleRevokeOtherSessions = useCallback(
  async (targetSessionId = "") => {
    const currentUser = auth.currentUser || user;
    const currentSessionId = securitySessionId;
    if (!currentUser?.uid || !currentSessionId) return;

    setSecurityActionState((prev) => ({
      ...prev,
      otherSessions: !targetSessionId,
      targetSessionId,
    }));
    try {
      const revokeOtherSessions = httpsCallable(functions, "revokeOtherSessions");
      const result = await revokeOtherSessions({
        currentSessionId,
        targetSessionId: targetSessionId || undefined,
      });
      const revokedCount = Number(result?.data?.revokedCount || 0);
      notify(
        targetSessionId
          ? "Device signed out."
          : revokedCount > 0
          ? `${revokedCount} other app session${revokedCount === 1 ? "" : "s"} signed out.`
          : "No other app sessions were active.",
        "success"
      );
      await loadAccountSecurityData();
    } catch (error) {
      if (isDevRuntime) {
        console.error("ACCOUNT SECURITY REVOKE OTHER FAILED", {
          code: error?.code,
          message: error?.message,
          stack: error?.stack,
          uid: currentUser.uid,
          currentSessionId,
          targetSessionId,
        });
      }
      notify(getSecurityAuthErrorMessage(error), "error", 6000);
    } finally {
      setSecurityActionState((prev) => ({
        ...prev,
        otherSessions: false,
        targetSessionId: "",
      }));
    }
  },
  [loadAccountSecurityData, notify, securitySessionId, user]
);

const handleRevokeAllSessions = useCallback(async () => {
  const currentUser = auth.currentUser || user;
  const currentSessionId = securitySessionId;
  if (!currentUser?.uid || !currentSessionId) return;

  setSecurityActionState((prev) => ({ ...prev, allSessions: true }));
  try {
    const revokeAllSessions = httpsCallable(functions, "revokeAllSessions");
    await revokeAllSessions({ currentSessionId });
    clearSecuritySessionId(currentUser.uid);
    notify("All sessions were signed out. Please sign in again.", "success", 6000);
    await signOut(auth);
  } catch (error) {
    if (isDevRuntime) {
      console.error("ACCOUNT SECURITY LOGOUT EVERYWHERE FAILED", {
        code: error?.code,
        message: error?.message,
        stack: error?.stack,
        uid: currentUser.uid,
        currentSessionId,
      });
    }
    notify(getSecurityAuthErrorMessage(error), "error", 6000);
  } finally {
    setSecurityActionState((prev) => ({ ...prev, allSessions: false }));
  }
}, [notify, securitySessionId, user]);

const openSiteLocation = useCallback(
  (location) => {
    const url = buildSiteLocationOpenUrl(location);
    if (!url) {
      notify("Add a map link or address before opening navigation.", "warning");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  },
  [notify]
);

const copySiteLocationLink = useCallback(
  async (location) => {
    const url = buildSiteLocationOpenUrl(location);
    if (!url) {
      notify("Add a map link or address before copying.", "warning");
      return;
    }

    try {
      await navigator.clipboard.writeText(url);
      notify("Location link copied", "success");
    } catch (error) {
      console.error("Failed to copy location link:", error);
      notify("Could not copy location link. Please try again.", "error");
    }
  },
  [notify]
);

const openConfirmDialog = useCallback(
  ({
    title,
    description,
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    tone = "warning",
    onConfirm,
  }) => {
    setConfirmDialog({
      title: String(title || "Please confirm"),
      description: String(description || ""),
      confirmLabel,
      cancelLabel,
      tone,
      onConfirm,
    });
  },
  []
);

const closeConfirmDialog = useCallback(() => {
  setConfirmDialog(null);
}, []);

const canManageStorageFiles =
  isSuperAdmin || isWorkspaceOwner || ["owner", "admin"].includes(accountUser?.role || "");

const loadStorageManagerFiles = useCallback(async () => {
  if (!workspaceUid) {
    notify("Workspace is not ready yet.", "warning");
    return;
  }

  setStorageManagerState((prev) => ({ ...prev, loading: true, error: "" }));
  try {
    const usageRef = doc(db, "workspaces", workspaceUid, "usage", "storage");
    const filesQuery = firestoreQuery(
      collection(db, "workspaces", workspaceUid, "storageFiles"),
      firestoreOrderBy("uploadedAt", "desc"),
      firestoreLimit(50)
    );
    const [usageSnap, filesSnap] = await Promise.all([
      trackedGetDoc(usageRef, {
        label: "storageManagerUsage",
        cacheKey: `workspaces/${workspaceUid}/usage/storage`,
      }),
      trackedGetDocs(filesQuery, {
        label: "storageManagerFiles",
        cacheKey: `workspaces/${workspaceUid}/storageFiles?limit=50`,
      }),
    ]);
    const files = filesSnap.docs.map((fileDoc) => ({
      id: fileDoc.id,
      ...(fileDoc.data() || {}),
    }));
    const computedUsedBytes = files.reduce(
      (total, file) =>
        total +
        Number(file.deleted ? 0 : file.sizeBytes || 0) +
        Number(file.deleted ? 0 : file.thumbnailSizeBytes || 0),
      0
    );
    const usage = usageSnap.exists()
      ? usageSnap.data() || {}
      : {
          usedBytes: computedUsedBytes,
          includedBytes: 1024 * 1024 * 1024,
          imageCount: files.length,
          byCategory: {},
        };
    setStorageManagerState({
      loading: false,
      error: "",
      files,
      usage,
      loadedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error("STORAGE_MANAGER_LOAD_FAILED", {
      code: error?.code,
      message: error?.message,
      workspaceUid,
    });
    setStorageManagerState((prev) => ({
      ...prev,
      loading: false,
      error: error?.message || "Could not load storage files.",
    }));
    notify("Could not load storage manager. Please try again.", "error");
  }
}, [notify, workspaceUid]);

const archiveStorageManagerFile = useCallback(
  async (file) => {
    if (!workspaceUid || !file?.fileId) return;
    try {
      await archiveManagedStorageFile({
        workspaceUid,
        fileId: file.fileId,
        removedBy: auth.currentUser?.uid || "",
        removalReason: "manual_storage_manager_archive",
        linkedRecords: Array.isArray(file.linkedRecords) ? file.linkedRecords : [],
      });
      setStorageManagerState((prev) => ({
        ...prev,
        files: (prev.files || []).map((entry) =>
          entry.fileId === file.fileId
            ? {
                ...entry,
                active: false,
                archived: true,
                removedFromRecord: true,
              }
            : entry
        ),
      }));
      notify("File archived. Storage object retained for recovery.", "success");
    } catch (error) {
      console.error("STORAGE_MANAGER_ARCHIVE_FAILED", {
        code: error?.code,
        message: error?.message,
        fileId: file.fileId,
      });
      notify("Could not archive this file.", "error");
    }
  },
  [notify, workspaceUid]
);

const permanentlyDeleteStorageManagerFile = useCallback(
  (file) => {
    if (!workspaceUid || !file?.fileId) return;
    if (!canManageStorageFiles) {
      notify("Only owners/admins can permanently delete stored files.", "warning");
      return;
    }

    const linkedRecords = Array.isArray(file.linkedRecords) ? file.linkedRecords : [];
    openConfirmDialog({
      title: "Permanently delete storage file?",
      description: linkedRecords.length
        ? "This file is linked to a FabriQuote record. Archive/unlink is safer. Permanently deleting removes the Storage object and thumbnail."
        : "This permanently deletes the Storage object and thumbnail. This cannot be undone.",
      confirmLabel: "Delete permanently",
      cancelLabel: "Cancel",
      tone: "danger",
      onConfirm: async () => {
        try {
          const deleteTargets = [file.fullPath, file.thumbnailFullPath]
            .map((path) => String(path || "").trim())
            .filter(Boolean);
          await Promise.all(
            deleteTargets.map((path) =>
              deleteObject(ref(storage, path)).catch((error) => {
                if (error?.code !== "storage/object-not-found") throw error;
              })
            )
          );
          await setDoc(
            doc(db, "workspaces", workspaceUid, "storageFiles", file.fileId),
            {
              active: false,
              archived: true,
              deleted: true,
              deletedAt: serverTimestamp(),
              deletedBy: auth.currentUser?.uid || "",
              deleteMethod: "manual-admin",
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
          const deletedBytes = Number(file.sizeBytes || 0) + Number(file.thumbnailSizeBytes || 0);
          await setDoc(
            doc(db, "workspaces", workspaceUid, "usage", "storage"),
            {
              usedBytes: increment(-deletedBytes),
              imageCount: increment(-1),
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
          setStorageManagerState((prev) => ({
            ...prev,
            files: (prev.files || []).map((entry) =>
              entry.fileId === file.fileId
                ? {
                    ...entry,
                    active: false,
                    archived: true,
                    deleted: true,
                  }
                : entry
            ),
            usage: prev.usage
              ? {
                  ...prev.usage,
                  usedBytes: Math.max(0, Number(prev.usage.usedBytes || 0) - deletedBytes),
                  imageCount: Math.max(0, Number(prev.usage.imageCount || 0) - 1),
                }
              : prev.usage,
          }));
          notify("File permanently deleted.", "success");
        } catch (error) {
          console.error("STORAGE_MANAGER_PERMANENT_DELETE_FAILED", {
            code: error?.code,
            message: error?.message,
            fileId: file.fileId,
          });
          notify("Could not permanently delete this file.", "error");
        }
      },
    });
  },
  [canManageStorageFiles, notify, openConfirmDialog, workspaceUid]
);

const copyStorageManagerFileDebug = useCallback(
  async (file) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(file, null, 2));
      notify("Storage file JSON copied.", "success");
    } catch (error) {
      console.log("STORAGE_FILE_DEBUG_JSON", file);
      notify("Clipboard unavailable. Debug JSON logged to console.", "info");
    }
  },
  [notify]
);

const requestQuoteDealChoice = useCallback((details = {}) => {
  const openDeals = Array.isArray(details.openDeals) ? details.openDeals : [];
  if (!openDeals.length) return Promise.resolve("create");

  return new Promise((resolve) => {
    quoteDealChoiceResolverRef.current = resolve;
    setQuoteDealChoiceState({
      customerName: String(details.customerName || "this customer"),
      quoteNo: String(details.quoteNo || ""),
      quoteType: details.quoteType || "",
      openDeals: openDeals.slice(0, 5),
    });
  });
}, []);

const resolveQuoteDealChoice = useCallback((choice) => {
  const resolver = quoteDealChoiceResolverRef.current;
  quoteDealChoiceResolverRef.current = null;
  setQuoteDealChoiceState(null);
  if (resolver) resolver(choice);
}, []);

useEffect(() => {
  const originalAlert = window.alert;
  const activeToastTimers = toastTimersRef.current;

  window.alert = (message) => {
    const text = String(message || "").trim();
    const lower = text.toLowerCase();
    let tone = "info";

    if (
      lower.includes("saved") ||
      lower.includes("success") ||
      lower.includes("sent") ||
      lower.includes("restored") ||
      lower.includes("added successfully")
    ) {
      tone = "success";
    } else if (
      lower.includes("failed") ||
      lower.includes("error") ||
      lower.includes("could not") ||
      lower.includes("not found") ||
      lower.includes("crashed")
    ) {
      tone = "error";
    } else if (
      lower.includes("permission") ||
      lower.includes("please") ||
      lower.includes("required") ||
      lower.includes("select") ||
      lower.includes("enter") ||
      lower.includes("not enabled")
    ) {
      tone = "warning";
    }

    enqueueToast(text || "Something happened.", tone);
  };

  return () => {
    window.alert = originalAlert;
    activeToastTimers.forEach((timeoutId) => window.clearTimeout(timeoutId));
    activeToastTimers.clear();
  };
}, [enqueueToast]);

const getCurrentTaskHistoryActor = useCallback(() => {
  const name =
    currentWorkspaceMember?.name ||
    accountUser?.displayName ||
    user?.displayName ||
    user?.email ||
    user?.uid ||
    "Workspace User";
  const email =
    currentWorkspaceMember?.email ||
    accountUser?.email ||
    user?.email ||
    "";
  const uid =
    currentWorkspaceMember?.uid ||
    workspaceContext?.memberId ||
    user?.uid ||
    "";

  return {
    uid,
    email,
    name: String(name || email || uid || "Workspace User").trim(),
  };
}, [
  accountUser?.displayName,
  accountUser?.email,
  currentWorkspaceMember?.email,
  currentWorkspaceMember?.name,
  currentWorkspaceMember?.uid,
  user?.displayName,
  user?.email,
  user?.uid,
  workspaceContext?.memberId,
]);

const writeTaskHistoryRecord = useCallback(
  async ({
    taskType,
    sourceTaskId,
    dealLike,
    taskTitle,
    taskDescription = "",
    dueDate = null,
    sourceModule = TASK_HISTORY_SOURCE_CRM,
    originalPayload = {},
  }) => {
    if (!workspaceUid || !sourceTaskId || !dealLike) return;

    const actor = getCurrentTaskHistoryActor();
    const dealId = String(dealLike.id || dealLike.dealId || dealLike.leadId || dealLike.quoteId || "").trim();
    if (!dealId) return;

    const leadId =
      dealLike.crmRecordType === "lead"
        ? dealId
        : String(dealLike.crm?.leadId || dealLike.leadId || "").trim();
    const primaryLinkedQuote = getPrimaryCrmLinkedQuote(
      normalizeCrmLinkedQuotes(dealLike)
    );
    const quoteId =
      dealLike.crmRecordType === "quote"
        ? dealId
        : String(primaryLinkedQuote?.quoteId || "").trim();
    const quoteNo = String(
      dealLike.crmRecordType === "quote"
        ? dealLike.quoteNo || ""
        : primaryLinkedQuote?.quoteNo || ""
    ).trim();
    const customerName = String(
      dealLike.customerLabel ||
        dealLike.crmContact?.name ||
        dealLike.contact?.name ||
        dealLike.clientName ||
        dealLike.dealTitle ||
        "Unnamed Customer"
    ).trim();
    const normalizedPhone = normalizeSearchCompact(
      dealLike.customerPhone ||
        dealLike.crmContact?.phone ||
        dealLike.contact?.phone ||
        dealLike.phone ||
        ""
    );
    const taskSearchIndex = buildSafeSearchResult([
      customerName,
      quoteNo,
      taskTitle,
      taskDescription,
      taskType,
      sourceModule,
      actor.name,
      actor.email,
      normalizedPhone,
    ]);
    const historyId = buildTaskHistoryId(taskType, dealId, sourceTaskId);
    const historyRef = doc(db, "users", workspaceUid, "task_history", historyId);
    const dueMillis = safeTimeMillis(dueDate);
    const dueTimestamp = dueMillis > 0 ? Timestamp.fromDate(new Date(dueMillis)) : null;
    const cleanPayload = serializeTaskHistoryValue(originalPayload);

    const didCreate = await runTransaction(db, async (transaction) => {
      const existing = await transaction.get(historyRef);
      if (existing.exists()) return false;

      transaction.set(historyRef, {
        id: historyId,
        workspaceUid,
        dealId,
        leadId,
        quoteId,
        quoteNo,
        customerName,
        customerNameLower: normalizeSearchText(customerName),
        normalizedPhone,
        taskTitle: String(taskTitle || "Completed Task").trim() || "Completed Task",
        taskTitleLower: normalizeSearchText(taskTitle || "Completed Task"),
        taskDescription: String(taskDescription || "").trim(),
        taskType,
        status: TASK_HISTORY_STATUS_COMPLETED,
        searchText: taskSearchIndex.searchText,
        searchKeywords: taskSearchIndex.searchKeywords,
        dueDate: dueTimestamp,
        completedAt: serverTimestamp(),
        completedByUid: actor.uid || "",
        completedByName: actor.name || actor.email || actor.uid || "Workspace User",
        completedByEmail: actor.email || "",
        sourceModule,
        sourceTaskId: String(sourceTaskId),
        originalPayload: cleanPayload,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return true;
    });

    invalidateWorkspaceCollectionCache(workspaceUid, "task_history");

    if (didCreate) {
      const completedAt = new Date();
      setCrmTaskHistoryRecords((prev) => {
        if (prev.some((record) => record.id === historyId)) return prev;
        return [
          {
            id: historyId,
            workspaceUid,
            dealId,
            leadId,
            quoteId,
            quoteNo,
            customerName,
            customerNameLower: normalizeSearchText(customerName),
            normalizedPhone,
            taskTitle: String(taskTitle || "Completed Task").trim() || "Completed Task",
            taskTitleLower: normalizeSearchText(taskTitle || "Completed Task"),
            taskDescription: String(taskDescription || "").trim(),
            taskType,
            status: TASK_HISTORY_STATUS_COMPLETED,
            searchText: taskSearchIndex.searchText,
            searchKeywords: taskSearchIndex.searchKeywords,
            dueDate: dueMillis > 0 ? new Date(dueMillis) : null,
            completedAt,
            completedByUid: actor.uid || "",
            completedByName: actor.name || actor.email || actor.uid || "Workspace User",
            completedByEmail: actor.email || "",
            sourceModule,
            sourceTaskId: String(sourceTaskId),
            originalPayload: cleanPayload,
            createdAt: completedAt,
            updatedAt: completedAt,
          },
          ...prev,
        ];
      });
    }
  },
  [getCurrentTaskHistoryActor, workspaceUid]
);

const resolveWorkspaceState = useCallback(async (authUser) => {
  if (!authUser?.uid) return null;

  const activeAccountId = accountUser?.activeAccountId || accountUser?.accountId || "";
  if (activeAccountId) {
    const legacyWorkspaceId =
      currentAccount?.dataOwnerUid ||
      currentAccount?.workspaceOwnerUid ||
      accountWorkspaceUid ||
      account?.dataOwnerUid ||
      account?.workspaceOwnerUid ||
      account?.ownerUid ||
      authUser.uid;
    const accountRole = accountUser?.role === "staff" ? "staff" : "owner";
    const accountProfile = {
      workspaceId: legacyWorkspaceId,
      ownerUid: legacyWorkspaceId,
      memberId:
        accountUser?.memberId ||
        accountUser?.member?.uid ||
        authUser.uid,
      role: accountRole,
      status: accountUser?.member?.isActive === false ? "disabled" : "active",
      permissions:
        accountUser?.member?.permissions ||
        accountUser?.permissions ||
        {},
      email: accountUser?.email || authUser.email || "",
      name: accountUser?.displayName || authUser.displayName || "",
    };

    return normalizeWorkspaceProfile(accountProfile, authUser);
  }

  const profileRef = doc(db, "users", authUser.uid, "profile", "main");
  const profileSnap = await trackedGetDoc(profileRef, {
    label: "legacyWorkspaceProfile",
  });

  if (!profileSnap.exists()) {
    const ownerProfile = buildOwnerWorkspaceProfile(authUser);
    await setDoc(
      profileRef,
      {
        ...ownerProfile,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    return normalizeWorkspaceProfile(ownerProfile, authUser);
  }

  const resolvedProfile = normalizeWorkspaceProfile(profileSnap.data(), authUser);

  if (resolvedProfile.role !== "staff") {
    const ownerProfile = buildOwnerWorkspaceProfile(authUser);
    await setDoc(
      profileRef,
      {
        ...ownerProfile,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    return normalizeWorkspaceProfile(ownerProfile, authUser);
  }

  return resolvedProfile;
}, [account, accountUser, accountWorkspaceUid, currentAccount]);

const syncWorkspaceMemberProfiles = useCallback(
  async (members, workspaceOwnerId, previousMembers = []) => {
    if (!workspaceOwnerId) return;

    const normalizedMembers = normalizeTeamMembers(members);
    const normalizedPreviousMembers = normalizeTeamMembers(previousMembers);
    const disabledPermissions = TEAM_PERMISSION_DEFINITIONS.reduce(
      (accumulator, permission) => ({
        ...accumulator,
        [permission.key]: false,
      }),
      {}
    );
    const profileWrites = normalizedMembers
      .filter((member) => member.uid)
      .map((member) =>
        setDoc(
          doc(db, "users", member.uid, "profile", "main"),
          {
            workspaceId: workspaceOwnerId,
            ownerUid: workspaceOwnerId,
            memberId: member.id,
            role: "staff",
            status:
              member.isActive === false || member.status === "disabled"
                ? "disabled"
                : member.status || "active",
            permissions: normalizePermissionSet(member.permissions),
            email: member.email,
            name: member.name,
            inviteId: member.inviteId || member.id,
            updatedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
          },
          { merge: true }
        )
      );

    const revokedProfileWrites = normalizedPreviousMembers
      .filter(
        (member) =>
          member.uid &&
          !normalizedMembers.some((current) => current.uid === member.uid)
      )
      .map((member) =>
        setDoc(
          doc(db, "users", member.uid, "profile", "main"),
          {
            workspaceId: workspaceOwnerId,
            ownerUid: workspaceOwnerId,
            memberId: member.id,
            role: "staff",
            status: "disabled",
            permissions: disabledPermissions,
            email: member.email,
            name: member.name,
            inviteId: member.inviteId || member.id,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        )
      );

    await Promise.all([...profileWrites, ...revokedProfileWrites]);
  },
  []
);

const createInviteActionCodeSettings = useCallback(
  (inviteId, targetEmail, ownerWorkspaceId) => {
    const params = new URLSearchParams({
      inviteId,
      invite: inviteId,
      workspaceId: ownerWorkspaceId,
      workspace: ownerWorkspaceId,
      accountId: ownerWorkspaceId,
      email: targetEmail,
    });
    return {
      url: `${getInviteOrigin()}/accept-invite?${params.toString()}`,
      handleCodeInApp: true,
    };
  },
  []
);

const sendTeamInviteEmail = useCallback(
  async ({ email, inviteId, workspaceId, accountId = workspaceId, invite = null, source = "unknown" }) => {
    const normalizedEmail = normalizeEmailValue(email || "");
    const actionCodeSettings = createInviteActionCodeSettings(
      inviteId,
      normalizedEmail,
      workspaceId
    );
    validateInviteActionCodeSettings({
      email: normalizedEmail,
      accountId,
      workspaceUid: workspaceId,
      inviteId,
      actionCodeSettings,
    });

    if (isDevRuntime) {
      console.log("TEAM INVITE EMAIL START", {
        source,
        email: normalizedEmail,
        accountId,
        workspaceUid: workspaceId,
        actionCodeSettings,
        invite,
      });
      console.log("TEAM INVITE EMAIL FUNCTION CALLED", {
        source,
        email: normalizedEmail,
        inviteId,
        accountId,
        workspaceId,
        workspaceUid: workspaceId,
        actionCodeSettings,
        invite,
      });
    }

    try {
      await sendSignInLinkToEmail(auth, normalizedEmail, actionCodeSettings);
      if (isDevRuntime) {
        console.log("TEAM INVITE EMAIL SUCCESS", {
          source,
          email: normalizedEmail,
          inviteId,
          workspaceId,
        });
      }
    } catch (emailError) {
      if (isDevRuntime) {
        console.error("TEAM INVITE EMAIL FAILED EXACT", {
          source,
          code: emailError?.code,
          message: emailError?.message,
          stack: emailError?.stack,
          invite,
          email: normalizedEmail,
          accountId,
          workspaceUid: workspaceId,
          inviteId,
          workspaceId,
          actionCodeSettings,
        });
      }
      throw emailError;
    }
  },
  [createInviteActionCodeSettings]
);

const resetTeamMemberModalState = useCallback(() => {
  setTeamMemberDraft(
    createTeamMemberDraft({
      permissions: DEFAULT_TEAM_PERMISSIONS,
      status: "draft",
      isActive: true,
    })
  );
  setTeamMemberModalError("");
}, []);

const openTeamMemberInviteModal = useCallback(() => {
  resetTeamMemberModalState();
  setIsTeamMemberModalOpen(true);
}, [resetTeamMemberModalState]);

const closeTeamMemberInviteModal = useCallback(() => {
  setIsTeamMemberModalOpen(false);
  resetTeamMemberModalState();
}, [resetTeamMemberModalState]);

const updateTeamMemberDraftField = useCallback((key, value) => {
  setTeamMemberDraft((prev) => ({
    ...prev,
    [key]:
      key === "permissions"
        ? normalizePermissionSet(value)
        : value,
  }));
}, []);

const updateTeamMemberDraftPermission = useCallback((permissionKey, enabled) => {
  setTeamMemberDraft((prev) => ({
    ...prev,
    permissions: normalizePermissionSet({
      ...prev.permissions,
      [permissionKey]: enabled,
    }),
  }));
}, []);

const updateSavedTeamMemberPermission = useCallback((memberId, permissionKey, enabled) => {
  setTeamMembers((prev) =>
    normalizeTeamMembers(
      prev.map((member) =>
        member.id === memberId
          ? {
              ...member,
              permissions: normalizePermissionSet({
                ...member.permissions,
                [permissionKey]: enabled,
              }),
            }
          : member
      )
    )
  );
}, []);

const sendTeamMemberInvite = useCallback(
  async (draftMember = teamMemberDraft, options = {}) => {
    if (!user?.uid || !workspaceUid || !isWorkspaceOwner) {
      setTeamMemberModalError("Only the workspace owner can invite staff.");
      return;
    }

    const normalizedEmail = normalizeEmailValue(draftMember?.email || "");
    const trimmedName = String(draftMember?.name || "").trim();

    if (!trimmedName) {
      setTeamMemberModalError("Please enter the team member's name.");
      return;
    }

    if (!normalizedEmail) {
      setTeamMemberModalError("Please enter a valid email address.");
      return;
    }

    const memberId = String(draftMember?.id || createClientId("team-member"));
    const isResend =
      options.resend === true || String(draftMember?.status || "") === "invited";
    const inviteSource = isResend ? "resend" : "add";
    const settingsRef = doc(db, "users", workspaceUid, "settings", "main");
    const accountRef = doc(db, "accounts", workspaceUid);
    const inviteRef = doc(db, "users", workspaceUid, "team_invites", memberId);

    if (isDevRuntime) {
      console.log("TEAM INVITE START", {
        source: inviteSource,
        workspaceUid,
        inviteId: memberId,
        email: normalizedEmail,
      });
      if (isResend) {
        console.log("TEAM INVITE RESEND START", {
          workspaceUid,
          inviteId: memberId,
          email: normalizedEmail,
        });
      }
    }

    if (isResend) {
      setResendingTeamInviteId(memberId);
    } else {
      setIsSendingTeamInvite(true);
    }
    setTeamMemberModalError("");

    try {
      const normalizedPermissions = normalizePermissionSet(
        draftMember?.permissions || DEFAULT_TEAM_PERMISSIONS
      );
      const normalizedRole = String(draftMember?.role || "").trim();
      const normalizedPhone = String(draftMember?.phone || "").trim();
      const existingMember = normalizeTeamMembers(teamMembers).find(
        (member) => member.id === memberId
      );

      if (isResend) {
        const inviteSnap = await trackedGetDoc(inviteRef, {
          label: "teamInviteResendValidate",
          cacheKey: `users/${workspaceUid}/team_invites/${memberId}`,
        });
        if (!inviteSnap.exists()) {
          throw new Error("Invite record was not found. Please create the invite again.");
        }

        await sendTeamInviteEmail({
          email: normalizedEmail,
          inviteId: memberId,
          workspaceId: workspaceUid,
          accountId: workspaceUid,
          invite: inviteSnap.data() || {
            memberId,
            workspaceId: workspaceUid,
            email: normalizedEmail,
            status: "invited",
          },
          source: "resend",
        });

        await updateDoc(inviteRef, {
          status: "invited",
          inviteEmailSent: true,
          emailStatus: "sent",
          emailSentAt: serverTimestamp(),
          sentAt: serverTimestamp(),
          resentAt: serverTimestamp(),
          lastResentAt: serverTimestamp(),
          resentBy: user.uid,
          resendCount: increment(1),
          updatedAt: serverTimestamp(),
        });

        const resendTimestamp = new Date().toISOString();
        await runTransaction(db, async (transaction) => {
          const settingsSnap = await transaction.get(settingsRef);
          const existingMembers = normalizeTeamMembers(
            settingsSnap.exists() ? settingsSnap.data()?.teamMembers : []
          );
          const nextMembers = existingMembers.map((member) =>
            member.id === memberId
              ? {
                  ...member,
                  inviteEmailSent: true,
                  emailStatus: "sent",
                  lastInviteSentAt: resendTimestamp,
                  updatedAt: resendTimestamp,
                }
              : member
          );
          transaction.set(
            settingsRef,
            {
              teamMembers: nextMembers,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
        });

        setTeamMembers((prev) =>
          normalizeTeamMembers(
            prev.map((member) =>
              member.id === memberId
                ? {
                    ...member,
                    inviteEmailSent: true,
                    emailStatus: "sent",
                    lastInviteSentAt: resendTimestamp,
                    updatedAt: resendTimestamp,
                  }
                : member
            )
          )
        );

        if (isDevRuntime) {
          console.log("TEAM INVITE RESEND SUCCESS", {
            workspaceUid,
            inviteId: memberId,
            email: normalizedEmail,
          });
        }
        notify("Invite email sent successfully.", "success");
        return;
      }

      await runTransaction(db, async (transaction) => {
        const settingsSnap = await transaction.get(settingsRef);
        const accountSnap = await transaction.get(accountRef);
        const transactionStaffLimit = normalizeStaffLimit(
          accountSnap.exists() ? accountSnap.data()?.staffLimit : accountStaffLimit,
          accountStaffLimit
        );
        const existingMembers = normalizeTeamMembers(
          settingsSnap.exists() ? settingsSnap.data()?.teamMembers : []
        );

        const occupiedSlots = existingMembers.filter((member) => {
          if (member.id === memberId) {
            return false;
          }

          return ["active", "invited"].includes(String(member.status || ""));
        }).length;

        if (occupiedSlots >= transactionStaffLimit) {
          throw new Error("You have reached your staff limit.");
        }

        const nextMember = createTeamMemberDraft({
          ...existingMember,
          ...draftMember,
          id: memberId,
          inviteId: memberId,
          workspaceId: workspaceUid,
          email: normalizedEmail,
          name: trimmedName,
          phone: normalizedPhone,
          role: normalizedRole,
          status: "invited",
          isActive: true,
          invitedBy: user.uid,
          permissions: normalizedPermissions,
          inviteEmailSent: true,
          emailStatus: "sent",
          invitedAt: new Date().toISOString(),
          lastInviteSentAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });

        const nextMembers = normalizeTeamMembers([
          ...existingMembers.filter((member) => member.id !== memberId),
          nextMember,
        ]);

        transaction.set(
          settingsRef,
          {
            teamMembers: nextMembers,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        transaction.set(
          inviteRef,
          {
            memberId,
            workspaceId: workspaceUid,
            name: trimmedName,
            email: normalizedEmail,
            phone: normalizedPhone,
            role: normalizedRole,
            permissions: normalizedPermissions,
            status: "invited",
            invitedByUid: user.uid,
            invitedByEmail: user.email || "",
            sentAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      });

      await sendTeamInviteEmail({
        email: normalizedEmail,
        inviteId: memberId,
        workspaceId: workspaceUid,
        accountId: workspaceUid,
        invite: {
          memberId,
          workspaceId: workspaceUid,
          name: trimmedName,
          email: normalizedEmail,
          phone: normalizedPhone,
          role: normalizedRole,
          status: "invited",
        },
        source: "add",
      });
      await updateDoc(inviteRef, {
        inviteEmailSent: true,
        emailStatus: "sent",
        emailSentAt: serverTimestamp(),
        sentAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const nextMembers = normalizeTeamMembers([
        ...teamMembers.filter((member) => member.id !== memberId),
        {
          ...draftMember,
          id: memberId,
          inviteId: memberId,
          workspaceId: workspaceUid,
          email: normalizedEmail,
          name: trimmedName,
          phone: normalizedPhone,
          role: normalizedRole,
          status: "invited",
          isActive: true,
          invitedBy: user.uid,
          permissions: normalizedPermissions,
          invitedAt: new Date().toISOString(),
          lastInviteSentAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      ]);

      setTeamMembers(nextMembers);

      if (options.closeAfter !== false) {
        closeTeamMemberInviteModal();
      }

      notify("Invite email sent successfully.", "success");
    } catch (error) {
      if (!isResend && memberId && workspaceUid) {
        const failedTimestamp = new Date().toISOString();
        await setDoc(
          inviteRef,
          {
            inviteEmailSent: false,
            emailStatus: "failed",
            emailFailureCode: error?.code || "",
            emailFailureMessage: error?.message || String(error || ""),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        ).catch(() => {});
        await runTransaction(db, async (transaction) => {
          const settingsSnap = await transaction.get(settingsRef);
          const existingMembers = normalizeTeamMembers(
            settingsSnap.exists() ? settingsSnap.data()?.teamMembers : []
          );
          const nextMembers = existingMembers.map((member) =>
            member.id === memberId
              ? {
                  ...member,
                  inviteEmailSent: false,
                  emailStatus: "failed",
                  emailFailureCode: error?.code || "",
                  emailFailureMessage: error?.message || String(error || ""),
                  updatedAt: failedTimestamp,
                }
              : member
          );
          transaction.set(
            settingsRef,
            {
              teamMembers: nextMembers,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
        }).catch(() => {});
      }
      if (isDevRuntime) {
        const failurePayload = {
          source: inviteSource,
          code: error?.code,
          message: error?.message,
          stack: error?.stack,
          workspaceUid,
          inviteId: memberId,
          email: normalizedEmail,
        };
        if (isResend) {
          console.error("TEAM INVITE RESEND FAILED EXACT", failurePayload);
        } else {
          console.error("TEAM INVITE FAILED EXACT", failurePayload);
        }
      }
      const message = error?.code
        ? getInviteEmailErrorMessage(error)
        : error?.message || "Could not send invite email.";
      setTeamMemberModalError(message);
      notify(isResend ? "Could not send invite email." : message, "error", 6000);
    } finally {
      if (isResend) {
        setResendingTeamInviteId("");
      } else {
        setIsSendingTeamInvite(false);
      }
    }
  },
  [
    accountStaffLimit,
    closeTeamMemberInviteModal,
    isWorkspaceOwner,
    notify,
    sendTeamInviteEmail,
    teamMemberDraft,
    teamMembers,
    user,
    workspaceUid,
  ]
);

const sendTeamInviteTestEmail = useCallback(async () => {
  if (!isDevRuntime) return;

  const normalizedEmail = normalizeEmailValue(teamMemberDraft?.email || "");
  if (!normalizedEmail) {
    setTeamMemberModalError("Please enter a valid email address.");
    return;
  }
  if (!workspaceUid) {
    setTeamMemberModalError("Workspace is not ready.");
    return;
  }

  const inviteId = `test_${Date.now().toString(36)}`;
  setIsTestingTeamInviteEmail(true);
  setTeamMemberModalError("");

  try {
    await sendTeamInviteEmail({
      email: normalizedEmail,
      inviteId,
      workspaceId: workspaceUid,
      accountId: workspaceUid,
      source: "team_access_direct_test",
      invite: {
        inviteId,
        workspaceId: workspaceUid,
        email: normalizedEmail,
        status: "test_only",
      },
    });
    notify("Test invite email sent successfully.", "success");
  } catch (testError) {
    if (isDevRuntime) {
      console.error("TEAM INVITE TEST EMAIL FAILED EXACT", {
        code: testError?.code,
        message: testError?.message,
        stack: testError?.stack,
        email: normalizedEmail,
        workspaceUid,
        inviteId,
      });
    }
    const message = testError?.code
      ? getInviteEmailErrorMessage(testError)
      : testError?.message || "Could not send invite email.";
    setTeamMemberModalError(message);
    notify(message, "error", 6000);
  } finally {
    setIsTestingTeamInviteEmail(false);
  }
}, [notify, sendTeamInviteEmail, teamMemberDraft, workspaceUid]);

const getStageMetaForType = useCallback(
  (quoteType, stageId) =>
    getCrmStage(
      crmPipelines,
      quoteType,
      stageId || getDefaultCrmStageId(crmPipelines, quoteType)
    ),
  [crmPipelines]
);

const createCrmLeadDraftState = useCallback(
  (quoteType = CURTAIN_QUOTE_TYPE) => {
    const normalizedQuoteType =
      quoteType === GENERAL_QUOTE_TYPE && canAccessGeneralQuotes
        ? GENERAL_QUOTE_TYPE
        : canAccessCurtainQuotes
        ? CURTAIN_QUOTE_TYPE
        : GENERAL_QUOTE_TYPE;
    const stageId = getDefaultCrmStageId(crmPipelines, normalizedQuoteType);

    return {
      title: "",
      companyOrProject: "",
      contact: {
        name: "",
        phone: "",
        whatsapp: "",
        email: "",
        address: "",
      },
      source: "",
      tags: "",
      notes: "",
      quoteType: normalizedQuoteType,
      pipelineType: normalizedQuoteType,
      stageId,
      expectedValue: "",
      nextAction: "",
      nextActionDate: "",
      assignedToUserId: "",
      assignedToNameSnapshot: "",
      reminderConfigs: normalizeCrmReminderConfigs({}, {}),
      attachments: [],
      executionSpecs: [],
      siteLocations: [],
    };
  },
  [canAccessCurtainQuotes, canAccessGeneralQuotes, crmPipelines]
);

const buildCrmLeadData = useCallback(
  (leadLike = {}, overrides = {}) => {
    const quoteType =
      overrides.quoteType ||
      leadLike.quoteType ||
      leadLike.pipelineType ||
      CURTAIN_QUOTE_TYPE;
    const normalizedQuoteType = isGeneralExecutionEligibleDeal({
      pipelineType: quoteType,
      quoteType,
    })
      ? GENERAL_QUOTE_TYPE
      : CURTAIN_QUOTE_TYPE;
    const stageId =
      overrides.stageId ||
      leadLike.stageId ||
      getDefaultCrmStageId(crmPipelines, normalizedQuoteType);
    const stageMeta = getStageMetaForType(normalizedQuoteType, stageId);
    const linkedQuotes = normalizeCrmLinkedQuotes(leadLike, overrides);
    const primaryLinkedQuote = getPrimaryCrmLinkedQuote(linkedQuotes);
    const hasQuote = Object.prototype.hasOwnProperty.call(overrides, "hasQuote")
      ? overrides.hasQuote === true && linkedQuotes.length > 0
      : linkedQuotes.length > 0;
    const sourceValue = String(
      overrides.source ??
        overrides.contact?.source ??
        leadLike.source ??
        leadLike.contact?.source ??
        ""
    );
    const contact = {
      name: String(
        overrides.contact?.name ??
          leadLike.contact?.name ??
          leadLike.contactName ??
          leadLike.customerName ??
          ""
      ),
      phone: String(
        overrides.contact?.phone ??
          leadLike.contact?.phone ??
          leadLike.phone ??
          ""
      ),
      whatsapp: String(
        overrides.contact?.whatsapp ??
          leadLike.contact?.whatsapp ??
          leadLike.whatsapp ??
          ""
      ),
      email: String(
        overrides.contact?.email ??
          leadLike.contact?.email ??
          leadLike.email ??
          ""
      ),
      address: String(
        overrides.contact?.address ??
          leadLike.contact?.address ??
          leadLike.address ??
          ""
      ),
      source: sourceValue,
      notes: String(
        overrides.contact?.notes ??
          leadLike.contact?.notes ??
          ""
      ),
    };
    const hasChecklistOverride = Object.prototype.hasOwnProperty.call(
      overrides,
      "checklistItems"
    );
    const nextChecklistItems = hasChecklistOverride
      ? preserveChecklistDraftTree(overrides.checklistItems || [], {
          templateMode: false,
          idPrefix: "crm-check",
        })
      : normalizeDealChecklist(
          leadLike.checklistItems ||
            leadLike.checklist ||
            leadLike.stageChecklist ||
            leadLike.crm?.checklistItems ||
            leadLike.crm?.checklist,
          stageMeta
        );
    const expectedValue = Number(
      overrides.expectedValue ?? leadLike.expectedValue ?? 0
    );
    const finalValue = Number(
      overrides.finalValue ?? leadLike.finalValue ?? expectedValue
    );
    const legacyNextActionDate =
      overrides.nextActionDate ?? leadLike.nextActionDate ?? null;
    const hasInstallationDateOverride = Object.prototype.hasOwnProperty.call(
      overrides,
      "installationDate"
    );
    const installationDate = hasInstallationDateOverride
      ? overrides.installationDate
      : leadLike.installationDate ?? null;
    const sourceReminderConfigs = {
      ...(leadLike.reminderConfigs || {}),
      ...(overrides.reminderConfigs || {}),
    };
    const sourceInstallationReminder =
      overrides.installationReminder ??
      overrides.reminderConfig ??
      leadLike.installationReminder ??
      leadLike.reminderConfig ??
      leadLike.crm?.installationReminder ??
      leadLike.crm?.reminderConfig;
    if (sourceInstallationReminder) {
      sourceReminderConfigs[CRM_REMINDER_TYPE_INSTALLATION] =
        sourceInstallationReminder;
    }
    const reminderConfigs = normalizeCrmReminderConfigs(
      sourceReminderConfigs,
      {
        nextActionDate: legacyNextActionDate,
        installationDate,
      }
    );
    const installationReminder = buildCanonicalInstallationReminder(
      {
        ...leadLike,
        ...overrides,
        installationReminder: sourceInstallationReminder,
        reminderConfigs,
      },
      { installationDate, reminderConfigs }
    );
    const followUps = normalizeCrmFollowUps(
      overrides.followUps ?? leadLike.followUps,
      {
        nextAction: overrides.nextAction ?? leadLike.nextAction ?? "",
        nextActionDate: legacyNextActionDate,
        reminderConfig: reminderConfigs[CRM_REMINDER_TYPE_TASK_DUE],
      }
    );
    const primaryFollowUp = getPrimaryPendingCrmFollowUp(followUps);
    const nextActionDate = primaryFollowUp?.dueDate
      ? parseDateInputValue(primaryFollowUp.dueDate)
      : legacyNextActionDate;
    const attachments = normalizeCrmAttachments(
      overrides.attachments ?? leadLike.attachments
    );
    const executionSpecs = normalizeExecutionSpecs(
      overrides.executionSpecs ?? leadLike.executionSpecs
    );
    const siteLocations = normalizeSiteLocations(
      overrides.siteLocations ?? leadLike.siteLocations ?? leadLike.crm?.siteLocations
    );
    const title =
      String(
        overrides.title ??
          leadLike.title ??
          ""
      ).trim() ||
      String(overrides.companyOrProject ?? leadLike.companyOrProject ?? "").trim() ||
      String(contact.name || "").trim() ||
      "New Lead";

    return stripLegacyCrmQuoteLinkFields({
      ...leadLike,
      title,
      contact,
      companyOrProject: String(
        overrides.companyOrProject ?? leadLike.companyOrProject ?? ""
      ),
      source: sourceValue,
      customerId: String(overrides.customerId ?? leadLike.customerId ?? ""),
      customerKey: String(
        overrides.customerKey ??
          leadLike.customerKey ??
          leadLike.customerId ??
          ""
      ),
      customerName: String(
        overrides.customerName ??
          leadLike.customerName ??
          contact.name ??
          title ??
          ""
      ),
      customerPhone: String(
        overrides.customerPhone ??
          leadLike.customerPhone ??
          contact.phone ??
          contact.whatsapp ??
          ""
      ),
      customerWhatsapp: String(
        overrides.customerWhatsapp ??
          leadLike.customerWhatsapp ??
          contact.whatsapp ??
          ""
      ),
      customerEmail: String(
        overrides.customerEmail ??
          leadLike.customerEmail ??
          contact.email ??
          ""
      ),
      customerAddress: String(
        overrides.customerAddress ??
          leadLike.customerAddress ??
          contact.address ??
          ""
      ),
      customerCompanyName: String(
        overrides.customerCompanyName ??
          leadLike.customerCompanyName ??
          leadLike.companyOrProject ??
          ""
      ),
      quoteType: normalizedQuoteType,
      pipelineType: normalizedQuoteType,
      stageId: stageMeta?.id || stageId,
      stageNameSnapshot: stageMeta?.label || leadLike.stageNameSnapshot || stageId,
      stageColor: stageMeta?.color || leadLike.stageColor || "#64748b",
      linkedQuotes,
      hasQuote,
      checklistTemplateId:
        overrides.checklistTemplateId ??
        leadLike.checklistTemplateId ??
        stageMeta?.id ??
        "",
      checklistItems: nextChecklistItems,
      checklist: nextChecklistItems,
      stageChecklist: nextChecklistItems,
      expectedValue,
      finalValue,
      nextAction: String(
        primaryFollowUp?.title ??
          overrides.nextAction ??
          leadLike.nextAction ??
          ""
      ),
      nextActionDate,
      assignedToUserId: String(
        overrides.assignedToUserId ?? leadLike.assignedToUserId ?? ""
      ),
      assignedToNameSnapshot: String(
        overrides.assignedToNameSnapshot ??
          leadLike.assignedToNameSnapshot ??
          ""
      ),
      assignedAt: overrides.assignedAt ?? leadLike.assignedAt ?? null,
      assignedBy: String(overrides.assignedBy ?? leadLike.assignedBy ?? ""),
      tags: Array.isArray(overrides.tags)
        ? overrides.tags
        : Array.isArray(leadLike.tags)
        ? leadLike.tags
        : [],
      notes: String(overrides.notes ?? leadLike.notes ?? ""),
      installationDate,
      installationReminder,
      reminderConfig: installationReminder,
      followUps,
      reminderConfigs,
      attachments,
      executionSpecs,
      siteLocations,
      status: String(
        overrides.status ??
          leadLike.status ??
          (stageMeta?.confirmsQuote ? "confirmed" : "draft")
      ),
      createdAt: leadLike.createdAt || new Date(),
      updatedAt: overrides.updatedAt ?? leadLike.updatedAt ?? new Date(),
      primaryLinkedQuote,
    });
  },
  [buildCanonicalInstallationReminder, crmPipelines, getStageMetaForType]
);

const getStageMetaForQuote = useCallback(
  (quoteLike) => {
    const quoteType = resolveQuoteType(quoteLike);
    return getStageMetaForType(
      quoteType,
      quoteLike?.crm?.stageId || quoteLike?.workStage
    );
  },
  [getStageMetaForType]
);

const getStageLabelForQuote = useCallback(
  (quoteLike) => getStageMetaForQuote(quoteLike)?.label || "Quote Given",
  [getStageMetaForQuote]
);

const getStageColorForQuote = useCallback(
  (quoteLike) => getStageMetaForQuote(quoteLike)?.color || "#64748b",
  [getStageMetaForQuote]
);

const buildQuoteCrmData = useCallback(
  (quoteLike, overrides = {}) => {
    const quoteType = resolveQuoteType(quoteLike);
    const latestQuoteTotal = getNormalizedQuoteTotal(quoteLike);
    const embeddedGeneral =
      quoteType === GENERAL_QUOTE_TYPE
        ? syncGeneralQuoteDefaults(getEmbeddedGeneralQuote(quoteLike))
        : null;
    const stageId =
      overrides.stageId ||
      quoteLike?.crm?.stageId ||
      quoteLike?.workStage ||
      getDefaultCrmStageId(crmPipelines, quoteType);
    const stageMeta = getStageMetaForType(quoteType, stageId);
    const contact = {
      ...buildCrmContactFromQuote(quoteLike, quoteType, embeddedGeneral),
      ...(quoteLike?.crm?.contact || {}),
      ...(overrides.contact || {}),
    };
    const nextActionDate =
      overrides.nextActionDate ?? quoteLike?.crm?.nextActionDate ?? null;
    const hasInstallationDateOverride = Object.prototype.hasOwnProperty.call(
      overrides,
      "installationDate"
    );
    const installationDate = hasInstallationDateOverride
      ? overrides.installationDate
      : quoteLike?.crm?.installationDate ?? quoteLike?.installationDate ?? null;
    const hasChecklistOverride = Object.prototype.hasOwnProperty.call(
      overrides,
      "checklistItems"
    );
    const checklistItems = hasChecklistOverride
      ? preserveChecklistDraftTree(overrides.checklistItems || [], {
          templateMode: false,
          idPrefix: "crm-check",
        })
      : normalizeDealChecklist(
          quoteLike?.crm?.checklistItems ||
            quoteLike?.crm?.checklist ||
            quoteLike?.checklistItems ||
            quoteLike?.checklist,
          stageMeta
        );
    const sourceReminderConfigs = {
      ...(quoteLike?.crm?.reminderConfigs || {}),
      ...(overrides.reminderConfigs || {}),
    };
    const sourceInstallationReminder =
      overrides.installationReminder ??
      overrides.reminderConfig ??
      quoteLike?.crm?.installationReminder ??
      quoteLike?.crm?.reminderConfig;
    if (sourceInstallationReminder) {
      sourceReminderConfigs[CRM_REMINDER_TYPE_INSTALLATION] =
        sourceInstallationReminder;
    }
    const reminderConfigs = normalizeCrmReminderConfigs(
      sourceReminderConfigs,
      {
        nextActionDate,
        installationDate,
      }
    );
    const installationReminder = buildCanonicalInstallationReminder(
      {
        ...(quoteLike?.crm || {}),
        ...overrides,
        installationReminder: sourceInstallationReminder,
        reminderConfigs,
      },
      { installationDate, reminderConfigs }
    );
    const followUps = normalizeCrmFollowUps(
      overrides.followUps ?? quoteLike?.crm?.followUps,
      {
        nextAction: overrides.nextAction ?? quoteLike?.crm?.nextAction ?? "",
        nextActionDate,
        reminderConfig: reminderConfigs[CRM_REMINDER_TYPE_TASK_DUE],
      }
    );
    const primaryFollowUp = getPrimaryPendingCrmFollowUp(followUps);
    const summarizedNextActionDate = primaryFollowUp?.dueDate
      ? parseDateInputValue(primaryFollowUp.dueDate)
      : nextActionDate;
    const attachments = normalizeCrmAttachments(
      overrides.attachments ?? quoteLike?.crm?.attachments
    );
    const siteLocations = normalizeSiteLocations(
      overrides.siteLocations ?? quoteLike?.crm?.siteLocations
    );
    const linkedLeadId = overrides.leadId ?? quoteLike?.crm?.leadId ?? "";
    const executionSpecs = linkedLeadId
      ? []
      : normalizeExecutionSpecs(
          overrides.executionSpecs ?? quoteLike?.crm?.executionSpecs
        );
    const customerLabel =
      embeddedGeneral?.quoteFor?.company ||
      embeddedGeneral?.quoteFor?.name ||
      quoteLike?.customerName ||
      "Unnamed Customer";

    return {
      pipelineType: quoteType,
      stageId: stageMeta?.id || stageId,
      stageName: stageMeta?.label || stageId,
      stageColor: stageMeta?.color || "#64748b",
      leadId: linkedLeadId,
      source:
        overrides.source ??
        quoteLike?.crm?.source ??
        contact?.source ??
        "",
      customerId: overrides.customerId ?? quoteLike?.customerId ?? quoteLike?.crm?.customerId ?? "",
      customerKey:
        overrides.customerKey ??
        quoteLike?.customerKey ??
        quoteLike?.crm?.customerKey ??
        quoteLike?.customerId ??
        quoteLike?.crm?.customerId ??
        "",
      customerName:
        overrides.customerName ??
        quoteLike?.customerName ??
        customerLabel ??
        "",
      customerPhone:
        overrides.customerPhone ??
        quoteLike?.customerPhone ??
        contact?.phone ??
        contact?.whatsapp ??
        "",
      customerWhatsapp:
        overrides.customerWhatsapp ??
        quoteLike?.customerWhatsapp ??
        contact?.whatsapp ??
        "",
      customerEmail:
        overrides.customerEmail ??
        quoteLike?.customerEmail ??
        contact?.email ??
        "",
      customerAddress:
        overrides.customerAddress ??
        quoteLike?.customerAddress ??
        contact?.address ??
        "",
      customerCompanyName:
        overrides.customerCompanyName ??
        quoteLike?.customerCompanyName ??
        embeddedGeneral?.quoteFor?.company ??
        "",
      hasQuote:
        overrides.hasQuote ??
        quoteLike?.crm?.hasQuote ??
        Boolean(quoteLike?.id || quoteLike?.quoteNo),
      title:
        overrides.title ||
        quoteLike?.crm?.title ||
        `${customerLabel}${quoteLike?.quoteNo ? ` - #${quoteLike.quoteNo}` : ""}`,
      contact,
      checklistTemplateId:
        overrides.checklistTemplateId ||
        quoteLike?.crm?.checklistTemplateId ||
        stageMeta?.id ||
        "",
      checklistItems,
      checklist: checklistItems,
      stageChecklist: checklistItems,
      expectedValue: Number(
        overrides.expectedValue ??
          latestQuoteTotal
      ),
      finalValue: Number(
        overrides.finalValue ??
          latestQuoteTotal
      ),
      nextAction:
        primaryFollowUp?.title ??
        overrides.nextAction ??
        quoteLike?.crm?.nextAction ??
        "",
      nextActionDate: summarizedNextActionDate,
      assignedToUserId:
        overrides.assignedToUserId ?? quoteLike?.crm?.assignedToUserId ?? "",
      assignedToNameSnapshot:
        overrides.assignedToNameSnapshot ??
        quoteLike?.crm?.assignedToNameSnapshot ??
        quoteLike?.crm?.assignedTo ??
        "",
      assignedTo:
        overrides.assignedTo ??
        overrides.assignedToNameSnapshot ??
        quoteLike?.crm?.assignedTo ??
        quoteLike?.crm?.assignedToNameSnapshot ??
        "",
      assignedAt:
        overrides.assignedAt ?? quoteLike?.crm?.assignedAt ?? null,
      assignedBy:
        overrides.assignedBy ?? quoteLike?.crm?.assignedBy ?? "",
      installationDate,
      installationReminder,
      reminderConfig: installationReminder,
      status: overrides.status ?? quoteLike?.crm?.status ?? quoteLike?.status ?? "draft",
      tags: Array.isArray(overrides.tags)
        ? overrides.tags
        : Array.isArray(quoteLike?.crm?.tags)
        ? quoteLike.crm.tags
        : [],
      notes: overrides.notes ?? quoteLike?.crm?.notes ?? "",
      followUps,
      reminderConfigs,
      attachments,
      siteLocations,
      ...(linkedLeadId ? {} : { executionSpecs }),
      createdAt: quoteLike?.crm?.createdAt || quoteLike?.createdAt || new Date(),
      updatedAt: new Date(),
    };
  },
  [
    buildCanonicalInstallationReminder,
    crmPipelines,
    getNormalizedQuoteTotal,
    getStageMetaForType,
    syncGeneralQuoteDefaults,
  ]
);

const getQuoteGivenStageIdForType = useCallback(
  (quoteType = CURTAIN_QUOTE_TYPE) => {
    const normalizedQuoteType =
      quoteType === GENERAL_QUOTE_TYPE ? GENERAL_QUOTE_TYPE : CURTAIN_QUOTE_TYPE;
    const pipeline = getCrmPipeline(crmPipelines, normalizedQuoteType);
    const quoteGivenStage = (pipeline?.stages || []).find((stage) => {
      const stageText = `${stage?.id || ""} ${stage?.label || ""} ${stage?.name || ""}`
        .toLowerCase()
        .replace(/[_-]+/g, " ");
      return (
        stageText.includes("quote given") ||
        stageText.includes("quotation sent") ||
        stageText.includes("quote sent")
      );
    });

    return (
      quoteGivenStage?.id ||
      getDefaultCrmStageId(crmPipelines, normalizedQuoteType)
    );
  },
  [crmPipelines]
);

const isOpenCrmDeal = useCallback(
  (lead = {}) => {
    if (!lead || typeof lead !== "object") return false;
    const quoteType =
      lead.pipelineType === GENERAL_QUOTE_TYPE || lead.quoteType === GENERAL_QUOTE_TYPE
        ? GENERAL_QUOTE_TYPE
        : CURTAIN_QUOTE_TYPE;
    const stage = getCrmStage(crmPipelines, quoteType, lead.stageId);
    if (stage?.marksComplete === true || stage?.isCancelled === true) {
      return false;
    }

    const statusText = [
      lead.status,
      lead.dealStatus,
      lead.stageId,
      lead.stageNameSnapshot,
      stage?.id,
      stage?.label,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return !CRM_CLOSED_DEAL_KEYWORDS.some((keyword) =>
      statusText.includes(keyword)
    );
  },
  [crmPipelines]
);

const findOpenCustomerDeals = useCallback(
  (customerFields = {}, quoteType = "") => {
    const customerId = String(customerFields.customerId || "").trim();
    const customerKey = String(customerFields.customerKey || customerId || "").trim();
    const phoneKey = normalizeCustomerLedgerPhone(
      customerFields.customerPhone ||
        customerFields.phone ||
        customerFields.customerWhatsapp ||
        ""
    );
    const emailKey = normalizeCustomerLedgerEmail(
      customerFields.customerEmail || customerFields.email || ""
    );
    const nameKey = normalizeCustomerLedgerName(
      customerFields.customerName || customerFields.name || ""
    );
    const normalizedQuoteType =
      quoteType === GENERAL_QUOTE_TYPE ? GENERAL_QUOTE_TYPE : CURTAIN_QUOTE_TYPE;

    const openDeals = crmLeadRecords
      .filter((lead) => isOpenCrmDeal(lead))
      .filter((lead) => {
        const leadCustomerId = String(lead.customerId || lead.crm?.customerId || "").trim();
        const leadCustomerKey = String(
          lead.customerKey || lead.crm?.customerKey || leadCustomerId || ""
        ).trim();
        const leadContact = lead.contact || lead.crm?.contact || {};
        const leadPhoneKey = normalizeCustomerLedgerPhone(
          lead.customerPhone ||
            leadContact.phone ||
            leadContact.whatsapp ||
            ""
        );
        const leadEmailKey = normalizeCustomerLedgerEmail(
          leadContact.email || lead.email || ""
        );
        const leadNameKey = normalizeCustomerLedgerName(
          lead.customerName ||
            leadContact.name ||
            lead.companyOrProject ||
            lead.title ||
            ""
        );

        return (
          (customerId && leadCustomerId && customerId === leadCustomerId) ||
          (customerKey && leadCustomerKey && customerKey === leadCustomerKey) ||
          (phoneKey && leadPhoneKey && phoneKey === leadPhoneKey) ||
          (emailKey && leadEmailKey && emailKey === leadEmailKey) ||
          (nameKey && leadNameKey && nameKey === leadNameKey)
        );
      });

    const samePipelineDeals = openDeals.filter((lead) => {
      const leadType =
        lead.pipelineType === GENERAL_QUOTE_TYPE || lead.quoteType === GENERAL_QUOTE_TYPE
          ? GENERAL_QUOTE_TYPE
          : CURTAIN_QUOTE_TYPE;
      return leadType === normalizedQuoteType;
    });

    return (samePipelineDeals.length ? samePipelineDeals : openDeals).sort(
      (a, b) =>
        Number(safeTimeMillis(b.updatedAt || b.createdAt) || 0) -
        Number(safeTimeMillis(a.updatedAt || a.createdAt) || 0)
    );
  },
  [crmLeadRecords, isOpenCrmDeal]
);

const buildCrmDetailDraftFromDeal = useCallback(
  (dealLike) => {
    if (!dealLike) return null;
    const draftChecklistItems = cloneChecklistTree(
      dealLike.checklistItems || dealLike.checklist || dealLike.stageChecklist || [],
      {
        preserveIds: true,
        resetState: false,
        idPrefix: "crm-check",
      }
    );
    const draftReminderConfigs = normalizeCrmReminderConfigs(
      {
        ...(dealLike.reminderConfigs || dealLike.crm?.reminderConfigs || {}),
        [CRM_REMINDER_TYPE_INSTALLATION]:
          dealLike.installationReminder ||
          dealLike.crm?.installationReminder ||
          dealLike.reminderConfig ||
          dealLike.crm?.reminderConfig ||
          dealLike.reminderConfigs?.[CRM_REMINDER_TYPE_INSTALLATION] ||
          dealLike.crm?.reminderConfigs?.[CRM_REMINDER_TYPE_INSTALLATION],
      },
      {
        nextActionDate: dealLike.nextActionDate,
        installationDate: dealLike.installationDate,
      }
    );
    const draftInstallationReminder = buildCanonicalInstallationReminder(
      {
        ...dealLike,
        reminderConfigs: draftReminderConfigs,
      },
      {
        installationDate: dealLike.installationDate,
        reminderConfigs: draftReminderConfigs,
      }
    );

    return {
      stageId: dealLike.crmStageId || dealLike.stageId || "",
      stage: dealLike.crmStageId || dealLike.stageId || "",
      status: dealLike.status || dealLike.crmStatus || "draft",
      pipelineType:
        dealLike.pipelineType ||
        dealLike.quoteTypeResolved ||
        dealLike.quoteType ||
        dealLike.crm?.pipelineType ||
        "",
      source: dealLike.source || dealLike.crmContact?.source || "",
      title: dealLike.crmTitle || "",
      contact: {
        name: dealLike.crmContact?.name || "",
        phone: dealLike.crmContact?.phone || "",
        whatsapp: dealLike.crmContact?.whatsapp || "",
        email: dealLike.crmContact?.email || "",
        address: dealLike.crmContact?.address || "",
        source: dealLike.crmContact?.source || "",
        notes: dealLike.crmContact?.notes || "",
      },
      nextAction: dealLike.nextAction || "",
      nextActionDate: toDateInputValue(dealLike.nextActionDate),
      installationDate: toDateInputValue(dealLike.installationDate),
      notes: dealLike.crmNotes || "",
      assignedToUserId:
        dealLike.assignedToUserId ||
        dealLike.crm?.assignedToUserId ||
        "",
      assignedToNameSnapshot:
        dealLike.assignedToNameSnapshot ||
        dealLike.crm?.assignedToNameSnapshot ||
        dealLike.crm?.assignedTo ||
        "",
      assignedAt: dealLike.crm?.assignedAt || null,
      assignedBy: dealLike.crm?.assignedBy || "",
      tags: Array.isArray(dealLike.crm?.tags)
        ? dealLike.crm.tags.join(", ")
        : Array.isArray(dealLike.tags)
        ? dealLike.tags.join(", ")
        : "",
      checklistItems: draftChecklistItems,
      checklist: draftChecklistItems,
      stageChecklist: draftChecklistItems,
      reminderConfigs: draftReminderConfigs,
      installationReminder: draftInstallationReminder,
      reminderConfig: draftInstallationReminder,
      followUps: normalizeCrmFollowUps(
        dealLike.followUps || dealLike.crm?.followUps,
        {
          nextAction: dealLike.nextAction || dealLike.crm?.nextAction || "",
          nextActionDate: dealLike.nextActionDate || dealLike.crm?.nextActionDate || null,
          reminderConfig:
            dealLike.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] ||
            dealLike.crm?.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] ||
            null,
        }
      ).map((followUp) => createCrmFollowUpDraft(followUp)),
      attachments: normalizeCrmAttachments(
        dealLike.attachments || dealLike.crm?.attachments
      ),
      executionSpecs: hydrateExecutionSpecDraftItems(
        resolveExecutionSpecsForCrmDeal(dealLike)
      ),
      siteLocations: normalizeSiteLocations(
        dealLike.siteLocations || dealLike.crm?.siteLocations || []
      ),
    };
  },
  [
    buildCanonicalInstallationReminder,
    createCrmFollowUpDraft,
    hydrateExecutionSpecDraftItems,
    resolveExecutionSpecsForCrmDeal,
  ]
);

const syncQuoteAnalyticsIndexes = useCallback(
  async (quoteId, quoteLike = {}, options = {}) => {
    if (!workspaceUid || !quoteId) return;
    const nextContribution = buildAnalyticsContributionForQuote({
      ...quoteLike,
      id: quoteId,
      quoteId,
    });
    if (!nextContribution) return;

    const contributionRef = doc(db, "users", workspaceUid, "quote_analytics_index", quoteId);
    const installationRef = doc(db, "users", workspaceUid, "installation_index", quoteId);

    await runTransaction(db, async (transaction) => {
      const previousSnap = await transaction.get(contributionRef);
      const previousContribution = previousSnap.exists() ? previousSnap.data() || null : null;
      const monthDeltas = new Map();
      const clientDeltas = new Map();

      const mergeDeltas = (targetMap, sourceMap) => {
        sourceMap.forEach((delta, key) => {
          targetMap.set(key, applySummaryDelta(targetMap.get(key), delta));
        });
      };

      if (previousContribution) {
        mergeDeltas(monthDeltas, getMonthlyDeltaFromContribution(previousContribution, -1));
        const oldClientMonth = previousContribution.confirmedMonthKey || previousContribution.quoteMonthKey;
        if (oldClientMonth) {
          const clientKey = `${oldClientMonth}_${previousContribution.customerKey || "unknown"}`;
          clientDeltas.set(
            clientKey,
            applySummaryDelta(clientDeltas.get(clientKey), {
              confirmedRevenue: -Number(previousContribution.confirmedRevenue || 0),
              confirmedCount:
                previousContribution.confirmed && previousContribution.confirmedMonthKey ? -1 : 0,
              quotationRevenue: -Number(previousContribution.quotationRevenue || 0),
              quotationCount: previousContribution.deleted ? 0 : -1,
            })
          );
        }
      }

      mergeDeltas(monthDeltas, getMonthlyDeltaFromContribution(nextContribution, 1));
      const nextClientMonth = nextContribution.confirmedMonthKey || nextContribution.quoteMonthKey;
      if (nextClientMonth) {
        const clientKey = `${nextClientMonth}_${nextContribution.customerKey || "unknown"}`;
        clientDeltas.set(
          clientKey,
          applySummaryDelta(clientDeltas.get(clientKey), {
            confirmedRevenue: Number(nextContribution.confirmedRevenue || 0),
            confirmedCount:
              nextContribution.confirmed && nextContribution.confirmedMonthKey ? 1 : 0,
            quotationRevenue: Number(nextContribution.quotationRevenue || 0),
            quotationCount: nextContribution.deleted ? 0 : 1,
          })
        );
      }

      monthDeltas.forEach((delta, monthKey) => {
        transaction.set(
          doc(db, "users", workspaceUid, "analytics_monthly", monthKey),
          {
            monthKey,
            confirmedRevenue: increment(Number(delta.confirmedRevenue || 0)),
            confirmedCount: increment(Number(delta.confirmedCount || 0)),
            quotationRevenue: increment(Number(delta.quotationRevenue || 0)),
            quotationCount: increment(Number(delta.quotationCount || 0)),
            cancelledRevenue: increment(Number(delta.cancelledRevenue || 0)),
            cancelledCount: increment(Number(delta.cancelledCount || 0)),
            installedRevenue: increment(Number(delta.installedRevenue || 0)),
            installedCount: increment(Number(delta.installedCount || 0)),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      });

      clientDeltas.forEach((delta, clientDocId) => {
        const monthKey = clientDocId.split("_")[0];
        transaction.set(
          doc(db, "users", workspaceUid, "client_monthly", clientDocId),
          {
            monthKey,
            customerKey: nextContribution.customerKey,
            customerId: nextContribution.customerId,
            customerName: nextContribution.customerName,
            normalizedPhone: nextContribution.normalizedPhone,
            confirmedRevenue: increment(Number(delta.confirmedRevenue || 0)),
            confirmedCount: increment(Number(delta.confirmedCount || 0)),
            quotationRevenue: increment(Number(delta.quotationRevenue || 0)),
            quoteCount: increment(Number(delta.quotationCount || 0)),
            lastQuoteId: quoteId,
            lastActivityAt: nextContribution.lastActivityAt,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      });

      if (nextContribution.hasInstallation && !nextContribution.deleted) {
        transaction.set(
          installationRef,
          {
            quoteId,
            jobId: quoteId,
            quoteNo: nextContribution.quoteNo,
            customerName: nextContribution.customerName,
            normalizedPhone: nextContribution.normalizedPhone,
            installationDate: nextContribution.installationDate,
            installationDateKey: nextContribution.installationDateKey,
            monthKey: nextContribution.installationMonthKey,
            status: nextContribution.status,
            workStage: nextContribution.workStage,
            totalAmount: nextContribution.totalAmount,
            confirmedRevenue: nextContribution.confirmedRevenue,
            itemSummary: nextContribution.itemSummary,
            location: nextContribution.location,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      } else if (previousContribution?.hasInstallation) {
        transaction.delete(installationRef);
      }

      transaction.set(
        contributionRef,
        {
          ...nextContribution,
          syncedAt: serverTimestamp(),
          source: options.source || "quoteWrite",
        },
        { merge: true }
      );
    });

    invalidateWorkspaceCollectionCache(workspaceUid, "analytics_monthly");
    invalidateWorkspaceCollectionCache(workspaceUid, "client_monthly");
    invalidateWorkspaceCollectionCache(workspaceUid, "installation_index");
  },
  [
    applySummaryDelta,
    buildAnalyticsContributionForQuote,
    getMonthlyDeltaFromContribution,
    workspaceUid,
  ]
);

const applyQuoteLocalPatch = useCallback(
  (quoteId, patch) => {
    invalidateWorkspaceCollectionCache(workspaceUid, "quotations");
    invalidateWorkspaceCollectionCache(workspaceUid, "quote_index");
    customerLedgerQuotesLoadRef.current = {};
    setSavedQuotes((prev) =>
      prev.map((item) =>
        item.id === quoteId
          ? {
              ...item,
              ...patch,
            }
          : item
      )
    );
    setCustomerLedgerQuotesByCustomer((prev) =>
      Object.fromEntries(
        Object.entries(prev || {}).map(([customerId, quotes]) => [
          customerId,
          (Array.isArray(quotes) ? quotes : []).map((quote) =>
            String(quote.id || "") === String(quoteId)
              ? {
                  ...quote,
                  ...patch,
                }
              : quote
          ),
        ])
      )
    );

    if (selectedQuote?.id === quoteId) {
      setSelectedQuote((prev) => (prev ? { ...prev, ...patch } : prev));
      if (patch.workStage != null) {
        setSelectedQuoteWorkStage(patch.workStage);
      }
      if (patch.status != null) {
        setSelectedQuoteStatus(patch.status);
      }
      if (Object.prototype.hasOwnProperty.call(patch, "installationDate")) {
        setSelectedInstallationDate(patch.installationDate);
      }
    }

    const indexPatch = buildQuoteIndexPayload(
      { ...patch, id: quoteId, quoteId, sourceQuoteId: quoteId },
      quoteId,
      workspaceUid
    );
    const lightweightPatch = sanitizeCrmFirestoreValue({
      quoteId,
      id: quoteId,
      sourceQuoteId: quoteId,
      sourceCollection: "quotations",
      sourcePath: workspaceUid ? `users/${workspaceUid}/quotations/${quoteId}` : "",
      ...(Object.prototype.hasOwnProperty.call(patch, "status")
        ? { status: indexPatch.status }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(patch, "workStage")
        ? { workStage: indexPatch.workStage }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(patch, "quoteNo")
        ? {
            quoteNo: indexPatch.quoteNo,
            quoteNoString: indexPatch.quoteNoString,
          }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(patch, "customerId") ||
      Object.prototype.hasOwnProperty.call(patch, "customerKey") ||
      Object.prototype.hasOwnProperty.call(patch, "customerName") ||
      Object.prototype.hasOwnProperty.call(patch, "customerPhone") ||
      Object.prototype.hasOwnProperty.call(patch, "customerWhatsapp")
        ? {
            customerId: indexPatch.customerId || "",
            customerKey: indexPatch.customerKey || indexPatch.customerId || "",
            customerName: indexPatch.customerName || "",
            customerNameLower: indexPatch.customerNameLower || "",
            customerPhone: indexPatch.customerPhone || "",
            customerWhatsapp: indexPatch.customerWhatsapp || "",
            normalizedPhone: indexPatch.normalizedPhone || "",
          }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(patch, "crmLeadId") ||
      Object.prototype.hasOwnProperty.call(patch, "leadId") ||
      Object.prototype.hasOwnProperty.call(patch, "crm")
        ? {
            crmLeadId: indexPatch.crmLeadId || "",
            leadId: indexPatch.leadId || indexPatch.crmLeadId || "",
          }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(patch, "deleted")
        ? { deleted: patch.deleted === true }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(patch, "deletedAt")
        ? { deletedAt: patch.deletedAt || null }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(patch, "deleteAfter")
        ? { deleteAfter: patch.deleteAfter || null }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(patch, "confirmedAt")
        ? { confirmedAt: patch.confirmedAt || null }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(patch, "subtotal")
        ? { subtotal: Number(patch.subtotal || 0) }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(patch, "grandTotal")
        ? {
            grandTotal: Number(patch.grandTotal || 0),
            totalAmount: Number(patch.grandTotal || 0),
            total: Number(patch.grandTotal || 0),
            finalTotal: Number(patch.grandTotal || 0),
            finalAmount: Number(patch.grandTotal || 0),
          }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(patch, "updatedAt")
        ? { updatedAt: patch.updatedAt || new Date() }
        : { updatedAt: new Date() }),
    });

    if (workspaceUid && quoteId) {
      setDoc(doc(db, "users", workspaceUid, "quote_index", quoteId), lightweightPatch, {
        merge: true,
      }).then(() => {
        invalidateWorkspaceCollectionCache(workspaceUid, "quote_index");
        const currentQuote =
          selectedQuote?.id === quoteId
            ? selectedQuote
            : savedQuotesRef.current.find((quote) => quote.id === quoteId) || {};
        const customerSearchIndexWrite = buildCustomerSearchIndexWrite(
          {
            ...currentQuote,
            ...patch,
            ...lightweightPatch,
            id: quoteId,
            quoteId,
            latestQuoteId: quoteId,
            latestQuoteNo: lightweightPatch.quoteNo || currentQuote.quoteNo || "",
            sourceType: "quote_customer",
          },
          {
            sourceType: "quote_customer",
            workspaceUid,
          }
        );
        if (customerSearchIndexWrite?.personId) {
          setDoc(
            doc(db, "users", workspaceUid, "customer_search_index", customerSearchIndexWrite.personId),
            customerSearchIndexWrite.payload,
            { merge: true }
          )
            .then(() => {
              invalidateWorkspaceCollectionCache(workspaceUid, "customer_search_index");
              quotationCustomerSearchCacheRef.current.clear();
            })
            .catch((error) => {
              if (isDevRuntime) {
                console.error("QUOTE CUSTOMER SEARCH INDEX PATCH FAILED", {
                  quoteId,
                  personId: customerSearchIndexWrite.personId,
                  code: error?.code,
                  message: error?.message,
                });
              }
            });
        }
        syncQuoteAnalyticsIndexes(
          quoteId,
          {
            ...currentQuote,
            ...patch,
            id: quoteId,
          },
          { source: "quoteLocalPatch" }
        ).catch((error) => {
          if (isDevRuntime) {
            console.error("QUOTE ANALYTICS PATCH SYNC FAILED", {
              quoteId,
              code: error?.code,
              message: error?.message,
            });
          }
        });
      }).catch((error) => {
        if (isDevRuntime) {
          console.error("QUOTE INDEX PATCH FAILED", {
            quoteId,
            code: error?.code,
            message: error?.message,
          });
        }
      });
    }
  },
  [selectedQuote, syncQuoteAnalyticsIndexes, workspaceUid]
);

const upsertQuoteLocalRecord = useCallback(
  async (quoteRecord, { persistIndex = true } = {}) => {
    const quoteId = String(quoteRecord?.id || quoteRecord?.quoteNo || "").trim();
    if (!quoteId) return;

    invalidateWorkspaceCollectionCache(workspaceUid, "quotations");
    invalidateWorkspaceCollectionCache(workspaceUid, "quote_index");
    customerLedgerQuotesLoadRef.current = {};
    const normalizedQuote = {
      ...quoteRecord,
      id: quoteId,
      updatedAt: quoteRecord.updatedAt || new Date(),
    };
    const quoteIndexPayload = buildQuoteIndexSavePayload(
      { ...normalizedQuote, sourceQuoteId: quoteId },
      quoteId,
      workspaceUid
    );
    const indexGrandTotal = Number(quoteIndexPayload.grandTotal || 0);
    const liveGrandTotal = Number(normalizedQuote.grandTotal || 0);

    if (isDevRuntime && Math.abs(indexGrandTotal - liveGrandTotal) > 0.05) {
      console.error("QUOTE_INDEX_TOTAL_MISMATCH", {
        quoteId,
        quoteNo: normalizedQuote.quoteNo || "",
        liveGrandTotal,
        indexGrandTotal,
        type: normalizedQuote.quoteType || normalizedQuote.quoteTypeResolved || "",
      });
    }

    setSavedQuotes((prev) => {
      let found = false;
      const next = prev.map((item) => {
        if (String(item.id || "") !== quoteId) return item;
        found = true;
        return {
          ...item,
          ...normalizedQuote,
          grandTotal: indexGrandTotal,
          totalAmount: indexGrandTotal,
          total: indexGrandTotal,
          finalTotal: indexGrandTotal,
          finalAmount: indexGrandTotal,
        };
      });
      return found
        ? next
        : [
            {
              ...normalizedQuote,
              grandTotal: indexGrandTotal,
              totalAmount: indexGrandTotal,
              total: indexGrandTotal,
              finalTotal: indexGrandTotal,
              finalAmount: indexGrandTotal,
            },
            ...next,
          ];
    });

    if (workspaceUid) {
      try {
        if (persistIndex) {
          await setDoc(doc(db, "users", workspaceUid, "quote_index", quoteId), quoteIndexPayload, {
            merge: true,
          });
          invalidateWorkspaceCollectionCache(workspaceUid, "quote_index");
        }
        const customerSearchIndexWrite = buildCustomerSearchIndexWrite(
          {
            ...normalizedQuote,
            ...quoteIndexPayload,
            id: quoteId,
            quoteId,
            latestQuoteId: quoteId,
            latestQuoteNo: quoteIndexPayload.quoteNo || "",
            sourceType: "quote_customer",
          },
          {
            sourceType: "quote_customer",
            workspaceUid,
          }
        );
        if (customerSearchIndexWrite?.personId) {
          await setDoc(
            doc(db, "users", workspaceUid, "customer_search_index", customerSearchIndexWrite.personId),
            customerSearchIndexWrite.payload,
            { merge: true }
          );
          invalidateWorkspaceCollectionCache(workspaceUid, "customer_search_index");
          quotationCustomerSearchCacheRef.current.clear();
        }
        syncQuoteAnalyticsIndexes(quoteId, normalizedQuote, {
          source: "quoteLocalUpsert",
        }).catch((error) => {
          if (isDevRuntime) {
            console.error("QUOTE ANALYTICS UPSERT SYNC FAILED", {
              quoteId,
              code: error?.code,
              message: error?.message,
            });
          }
        });
      } catch (error) {
        if (isDevRuntime) {
          console.error("QUOTE INDEX UPSERT FAILED", {
            quoteId,
            code: error?.code,
            message: error?.message,
          });
        }
        throw error;
      }
    }
  },
  [syncQuoteAnalyticsIndexes, workspaceUid]
);

const writeQuotationAndQuoteIndex = useCallback(
  async ({
    quoteRef,
    quoteId,
    quotePayload,
    quoteIndexSource,
    create = false,
  }) => {
    if (!workspaceUid || !quoteId || !quoteRef) {
      throw new Error("Cannot save quotation index without workspace, quote id, and document ref.");
    }

    const quoteIndexPayload = buildQuoteIndexSavePayload(
      {
        ...(quoteIndexSource || quotePayload || {}),
        sourceQuoteId: quoteId,
      },
      quoteId,
      workspaceUid
    );
    const customerSearchIndexWrite = buildCustomerSearchIndexWrite(
      {
        ...(quoteIndexSource || quotePayload || {}),
        ...quoteIndexPayload,
        id: quoteId,
        quoteId,
        latestQuoteId: quoteId,
        latestQuoteNo: quoteIndexPayload.quoteNo || "",
        sourceType: "quote_customer",
      },
      {
        sourceType: "quote_customer",
        workspaceUid,
      }
    );
    const liveGrandTotal = Number(
      quoteIndexSource?.grandTotal ??
        quotePayload?.grandTotal ??
        quoteIndexPayload.grandTotal ??
        0
    );
    const indexGrandTotal = Number(quoteIndexPayload.grandTotal || 0);

    if (isDevRuntime && Math.abs(indexGrandTotal - liveGrandTotal) > 0.05) {
      console.error("QUOTE_INDEX_TOTAL_MISMATCH", {
        quoteId,
        quoteNo: quoteIndexSource?.quoteNo || quotePayload?.quoteNo || "",
        liveGrandTotal,
        indexGrandTotal,
        type:
          quoteIndexSource?.quoteType ||
          quoteIndexSource?.quoteTypeResolved ||
          quotePayload?.quoteType ||
          quotePayload?.quoteTypeResolved ||
          "",
      });
    }

    const batch = writeBatch(db);
    if (create) {
      batch.set(quoteRef, quotePayload);
    } else {
      batch.update(quoteRef, quotePayload);
    }
    batch.set(
      doc(db, "users", workspaceUid, "quote_index", quoteId),
      quoteIndexPayload,
      { merge: true }
    );
    if (customerSearchIndexWrite?.personId) {
      batch.set(
        doc(db, "users", workspaceUid, "customer_search_index", customerSearchIndexWrite.personId),
        customerSearchIndexWrite.payload,
        { merge: true }
      );
    }

    await batch.commit();
    invalidateWorkspaceCollectionCache(workspaceUid, "quote_index");
    if (customerSearchIndexWrite?.personId) {
      invalidateWorkspaceCollectionCache(workspaceUid, "customer_search_index");
      quotationCustomerSearchCacheRef.current.clear();
    }
    return quoteIndexPayload;
  },
  [workspaceUid]
);

const applyCrmReminderLocalPatch = useCallback((reminderId, patch) => {
  invalidateWorkspaceCollectionCache(workspaceUid, "crm_reminders");
  setCrmReminderRecords((prev) => {
    const nextPatch = patch
      ? {
          ...patch,
          id: reminderId,
        }
      : null;
    const existingIndex = prev.findIndex((item) => item.id === reminderId);

    if (!nextPatch) {
      return existingIndex >= 0
        ? prev.filter((item) => item.id !== reminderId)
        : prev;
    }

    if (existingIndex < 0) {
      return [nextPatch, ...prev];
    }

    return prev.map((item) =>
      item.id === reminderId
        ? {
            ...item,
            ...nextPatch,
          }
        : item
    );
  });
}, [workspaceUid]);

const syncReminderDocsForDeal = useCallback(
  async (
    dealLike,
    {
      reminderConfigs: reminderOverrides,
      followUps: followUpOverrides,
      nextActionDate,
      installationDate,
    } = {}
  ) => {
    if (!workspaceUid || !dealLike?.id) return;

    const nextReminderConfigs = normalizeCrmReminderConfigs(
      reminderOverrides ?? dealLike.reminderConfigs ?? dealLike.crm?.reminderConfigs,
      {
        nextActionDate:
          nextActionDate ??
          dealLike.nextActionDate ??
          dealLike.crm?.nextActionDate ??
          null,
        installationDate:
          installationDate ??
          dealLike.installationDate ??
          dealLike.crm?.installationDate ??
          null,
      }
    );

    const dealTitle = String(dealLike.crmTitle || dealLike.title || "CRM Deal");
    const clientName = String(
      dealLike.customerLabel ||
        dealLike.crmContact?.name ||
        dealLike.contact?.name ||
        ""
    );
    const primaryLinkedQuote = getPrimaryCrmLinkedQuote(
      normalizeCrmLinkedQuotes(dealLike)
    );
    const quoteNo = String(
      dealLike.crmRecordType === "quote"
        ? dealLike.quoteNo || ""
        : primaryLinkedQuote?.quoteNo || ""
    );
    const pipelineType =
      dealLike.pipelineType ||
      dealLike.quoteTypeResolved ||
      dealLike.quoteType ||
      CURTAIN_QUOTE_TYPE;
    const linkedCustomer = customerRecords.find(
      (customer) =>
        customer.id &&
        String(customer.id) ===
          String(dealLike.customerId || dealLike.crm?.customerId || "")
    );
    const primarySiteLocation =
      getPrimarySiteLocation(dealLike) || getPrimarySiteLocation(linkedCustomer);
    const reminderLocationFields = primarySiteLocation
      ? {
          siteLocation: primarySiteLocation,
          locationLabel: primarySiteLocation.label || "",
          locationAddress: primarySiteLocation.addressText || "",
          locationUrl: buildSiteLocationOpenUrl(primarySiteLocation),
        }
      : {
          siteLocation: null,
          locationLabel: "",
          locationAddress: "",
          locationUrl: "",
        };
    const followUps = normalizeCrmFollowUps(
      followUpOverrides ?? dealLike.followUps ?? dealLike.crm?.followUps,
      {
        nextAction: dealLike.nextAction ?? dealLike.crm?.nextAction ?? "",
        nextActionDate:
          nextActionDate ??
          dealLike.nextActionDate ??
          dealLike.crm?.nextActionDate ??
          null,
        reminderConfig:
          nextReminderConfigs[CRM_REMINDER_TYPE_TASK_DUE] ||
          dealLike.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] ||
          dealLike.crm?.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] ||
          null,
      }
    );
    const reminderTypes = [
      ...(followUps.length === 0 ? [CRM_REMINDER_TYPE_TASK_DUE] : []),
      CRM_REMINDER_TYPE_INSTALLATION,
    ];
    const crmLeadIdForDueQueue = String(
      dealLike.leadId ||
        dealLike.crmLeadId ||
        dealLike.crm?.leadId ||
        dealLike.id ||
        ""
    ).trim();
    const quoteIdForDueQueue =
      String(
        dealLike.quoteId ||
          primaryLinkedQuote?.quoteId ||
          (dealLike.crmRecordType === "quote" ? dealLike.id : "") ||
          ""
      ).trim() || null;
    const customerIdForDueQueue =
      String(
        dealLike.customerId ||
          dealLike.crm?.customerId ||
          linkedCustomer?.id ||
          ""
      ).trim() || null;
    const assignedToForDueQueue =
      String(
        dealLike.assignedToUserId ||
          dealLike.assignedToUid ||
          dealLike.assignedUserId ||
          dealLike.crm?.assignedToUserId ||
          ""
      ).trim() || null;
    const buildDueReminderMessage = (primaryText = "") =>
      [primaryText, clientName, quoteNo ? `#${quoteNo}` : ""]
        .map((part) => String(part || "").trim())
        .filter(Boolean)
        .join(" | ");
    const upsertDueReminderDoc = async ({
      reminderType,
      sourceId = "",
      sourceReminderId = "",
      title = "",
      message = "",
      nextNotifyAt = null,
      sourceStatus = CRM_REMINDER_STATUS_DISABLED,
      reminderEnabled = false,
      repeatKey = "none",
    }) => {
      if (!crmLeadIdForDueQueue) return;

      const dueReminderId = buildDueReminderDocId(
        crmLeadIdForDueQueue,
        reminderType,
        sourceId
      );
      if (!dueReminderId) return;

      const nextNotifyMillis = safeTimeMillis(nextNotifyAt);
      const dueStatus = buildDueReminderStatus(
        reminderEnabled === true,
        sourceStatus,
        nextNotifyMillis > 0
      );
      const dueReminderRef = doc(
        db,
        "users",
        workspaceUid,
        "due_reminders",
        dueReminderId
      );

      await setDoc(
        dueReminderRef,
        {
          id: dueReminderId,
          workspaceUid,
          crmLeadId: crmLeadIdForDueQueue,
          quoteId: quoteIdForDueQueue,
          customerId: customerIdForDueQueue,
          crmReminderId: String(sourceReminderId || ""),
          title: String(title || "Reminder").trim() || "Reminder",
          message:
            String(message || "").trim() ||
            buildDueReminderMessage(dealTitle || "CRM reminder"),
          type: mapCrmReminderTypeToDueReminderType(reminderType),
          nextNotifyAt:
            dueStatus === DUE_REMINDER_STATUS_PENDING && nextNotifyMillis > 0
              ? Timestamp.fromMillis(nextNotifyMillis)
              : null,
          status: dueStatus,
          reminderEnabled: reminderEnabled === true && dueStatus === DUE_REMINDER_STATUS_PENDING,
          notificationSent: false,
          assignedTo: assignedToForDueQueue,
          repeatKey: String(repeatKey || "none"),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    };

    await Promise.all(
      reminderTypes.map(async (reminderType) => {
        const reminderId = buildReminderDocId(dealLike.id, reminderType);
        const reminderRef = doc(
          db,
          "users",
          workspaceUid,
          "crm_reminders",
          reminderId
        );
        const config = nextReminderConfigs[reminderType];
        const scheduledAt = combineReminderDateTime(
          config?.reminderDate,
          config?.reminderTime
        );
        const scheduledMillis = safeTimeMillis(scheduledAt);
        const existingReminder =
          crmReminderRecords.find((record) => record.id === reminderId) || null;
        const existingScheduledMillis = safeTimeMillis(
          existingReminder?.scheduledAt
        );
        const existingNextTriggerMillis = safeTimeMillis(
          existingReminder?.nextTriggerAt
        );
        const sameSchedule =
          existingScheduledMillis === scheduledMillis &&
          String(existingReminder?.repeatKey || "") ===
            String(config?.repeatKey || "");

        let status = config?.status || CRM_REMINDER_STATUS_DISABLED;
        if (config?.reminderEnabled !== true || !scheduledMillis) {
          status =
            status === CRM_REMINDER_STATUS_DONE
              ? CRM_REMINDER_STATUS_DONE
              : CRM_REMINDER_STATUS_DISABLED;
        } else if (status !== CRM_REMINDER_STATUS_DONE) {
          status = CRM_REMINDER_STATUS_ACTIVE;
        }

        let nextTriggerAt = null;
        if (status === CRM_REMINDER_STATUS_ACTIVE && scheduledAt) {
          if (
            sameSchedule &&
            existingReminder?.status === CRM_REMINDER_STATUS_ACTIVE &&
            existingNextTriggerMillis > 0
          ) {
            nextTriggerAt =
              existingReminder.nextTriggerAt instanceof Date
                ? existingReminder.nextTriggerAt
                : new Date(existingNextTriggerMillis);
          } else {
            nextTriggerAt = scheduledAt;
          }
        }

        const localPayload = {
          id: reminderId,
          dealId: dealLike.id,
          dealRecordType: dealLike.crmRecordType || "quote",
          reminderType,
          reminderLabel: getCrmReminderTypeLabel(reminderType),
          dealTitle,
          clientName,
          quoteNo,
          quoteType: pipelineType,
          enabled: config?.reminderEnabled === true,
          reminderEnabled: config?.reminderEnabled === true,
          active: status === CRM_REMINDER_STATUS_ACTIVE,
          repeatKey: config?.repeatKey ?? "daily",
          reminderDate: config?.reminderDate ?? "",
          reminderTime: config?.reminderTime ?? DEFAULT_CRM_REMINDER_TIME,
          scheduledAt,
          nextTriggerAt,
          status,
          completedAt:
            status === CRM_REMINDER_STATUS_DONE
              ? config?.completedAt || existingReminder?.completedAt || new Date()
              : null,
          createdAt: existingReminder?.createdAt || new Date(),
          updatedAt: new Date(),
          ...reminderLocationFields,
        };

        const firestorePayload = {
          dealId: dealLike.id,
          dealRecordType: dealLike.crmRecordType || "quote",
          reminderType,
          reminderLabel: getCrmReminderTypeLabel(reminderType),
          dealTitle,
          clientName,
          quoteNo,
          quoteType: pipelineType,
          enabled: config?.reminderEnabled === true,
          reminderEnabled: config?.reminderEnabled === true,
          active: status === CRM_REMINDER_STATUS_ACTIVE,
          repeatKey: config?.repeatKey ?? "daily",
          reminderDate: config?.reminderDate ?? "",
          reminderTime: config?.reminderTime ?? DEFAULT_CRM_REMINDER_TIME,
          scheduledAt: scheduledAt ? Timestamp.fromDate(scheduledAt) : null,
          nextTriggerAt: nextTriggerAt ? Timestamp.fromDate(nextTriggerAt) : null,
          status,
          completedAt:
            status === CRM_REMINDER_STATUS_DONE
              ? existingReminder?.completedAt || config?.completedAt || serverTimestamp()
              : null,
          updatedAt: serverTimestamp(),
          ...sanitizeCrmFirestoreValue(reminderLocationFields),
        };

        if (!existingReminder?.createdAt) {
          firestorePayload.createdAt = serverTimestamp();
        }

        await setDoc(reminderRef, firestorePayload, { merge: true });
        await upsertDueReminderDoc({
          reminderType,
          sourceReminderId: reminderId,
          title:
            reminderType === CRM_REMINDER_TYPE_INSTALLATION
              ? "Installation reminder"
              : dealLike.nextAction || getCrmReminderTypeLabel(reminderType),
          message: buildDueReminderMessage(
            reminderType === CRM_REMINDER_TYPE_INSTALLATION
              ? "Installation scheduled"
              : dealLike.nextAction || dealTitle
          ),
          nextNotifyAt: nextTriggerAt,
          sourceStatus: status,
          reminderEnabled: config?.reminderEnabled === true,
          repeatKey: config?.repeatKey ?? "daily",
        });
        applyCrmReminderLocalPatch(reminderId, localPayload);
      })
    );

    if (followUps.length > 0) {
      const legacyTaskReminderId = buildReminderDocId(
        dealLike.id,
        CRM_REMINDER_TYPE_TASK_DUE
      );
      await deleteDoc(
        doc(db, "users", workspaceUid, "crm_reminders", legacyTaskReminderId)
      ).catch(() => {});
      await upsertDueReminderDoc({
        reminderType: CRM_REMINDER_TYPE_TASK_DUE,
        sourceReminderId: legacyTaskReminderId,
        title: dealLike.nextAction || "Deal action reminder",
        message: buildDueReminderMessage(dealLike.nextAction || dealTitle),
        sourceStatus: CRM_REMINDER_STATUS_DISABLED,
        reminderEnabled: false,
      });
      applyCrmReminderLocalPatch(legacyTaskReminderId, null);
    }

    const activeFollowUpReminderDocIds = new Set(
      followUps.map((item) => buildFollowUpReminderDocId(dealLike.id, item))
    );
    const existingFollowUpReminders = crmReminderRecords.filter(
      (record) =>
        String(record.dealId || "") === String(dealLike.id) &&
        record.reminderType === CRM_REMINDER_TYPE_FOLLOW_UP
    );

    await Promise.all(
      followUps.map(async (followUp) => {
        const reminderId = buildFollowUpReminderDocId(dealLike.id, followUp);
        const reminderRef = doc(
          db,
          "users",
          workspaceUid,
          "crm_reminders",
          reminderId
        );
        const existingReminder =
          existingFollowUpReminders.find((record) => record.id === reminderId) || null;
        const scheduledAt = combineReminderDateTime(
          followUp.dueDate,
          followUp.dueTime
        );
        const scheduledMillis = safeTimeMillis(scheduledAt);
        const existingScheduledMillis = safeTimeMillis(existingReminder?.scheduledAt);
        const existingNextTriggerMillis = safeTimeMillis(existingReminder?.nextTriggerAt);
        const sameSchedule =
          existingScheduledMillis === scheduledMillis &&
          String(existingReminder?.repeatKey || "") === String(followUp.repeat || "");
        const isDone = followUp.status === CRM_FOLLOW_UP_STATUS_DONE;
        const wantsReminder = followUp.reminderEnabled === true;
        const enabled = wantsReminder && !isDone && Boolean(scheduledMillis);
        const repeatKey = followUp.repeat || "none";

        let nextTriggerAt = null;
        if (enabled && scheduledAt) {
          if (
            sameSchedule &&
            existingReminder?.status === CRM_REMINDER_STATUS_ACTIVE &&
            existingNextTriggerMillis > 0
          ) {
            nextTriggerAt =
              existingReminder.nextTriggerAt instanceof Date
                ? existingReminder.nextTriggerAt
                : new Date(existingNextTriggerMillis);
          } else {
            nextTriggerAt = scheduledAt;
          }
        }

        const localPayload = {
          id: reminderId,
          dealId: dealLike.id,
          dealRecordType: dealLike.crmRecordType || "quote",
          reminderType: CRM_REMINDER_TYPE_FOLLOW_UP,
          reminderLabel: "Follow-up",
          taskTitle: followUp.title || "Follow-up",
          taskDescription: followUp.description || "",
          followUpId: followUp.id,
          sourceTaskId: followUp.id,
          reminderId: followUp.reminderId || followUp.id,
          priority: followUp.priority || CRM_FOLLOW_UP_PRIORITY_MEDIUM,
          order: Number.isFinite(Number(followUp.order)) ? Number(followUp.order) : 0,
          dealTitle,
          clientName,
          quoteNo,
          quoteType: pipelineType,
          enabled,
          reminderEnabled: wantsReminder && !isDone,
          active: enabled && !isDone,
          repeatKey,
          reminderDate: followUp.dueDate || "",
          reminderTime: followUp.dueTime || DEFAULT_CRM_REMINDER_TIME,
          scheduledAt,
          nextTriggerAt,
          status: isDone
            ? CRM_REMINDER_STATUS_DONE
            : enabled
            ? CRM_REMINDER_STATUS_ACTIVE
            : CRM_REMINDER_STATUS_DISABLED,
          completedAt:
            isDone
              ? followUp.completedAt || existingReminder?.completedAt || new Date()
              : null,
          createdAt: existingReminder?.createdAt || followUp.createdAt || new Date(),
          updatedAt: new Date(),
          ...reminderLocationFields,
        };

        const firestorePayload = {
          dealId: dealLike.id,
          dealRecordType: dealLike.crmRecordType || "quote",
          reminderType: CRM_REMINDER_TYPE_FOLLOW_UP,
          reminderLabel: "Follow-up",
          taskTitle: followUp.title || "Follow-up",
          taskDescription: followUp.description || "",
          followUpId: followUp.id,
          sourceTaskId: followUp.id,
          reminderId: followUp.reminderId || followUp.id,
          priority: followUp.priority || CRM_FOLLOW_UP_PRIORITY_MEDIUM,
          order: Number.isFinite(Number(followUp.order)) ? Number(followUp.order) : 0,
          dealTitle,
          clientName,
          quoteNo,
          quoteType: pipelineType,
          enabled,
          reminderEnabled: wantsReminder && !isDone,
          active: enabled && !isDone,
          repeatKey,
          reminderDate: followUp.dueDate || "",
          reminderTime: followUp.dueTime || DEFAULT_CRM_REMINDER_TIME,
          scheduledAt: scheduledAt ? Timestamp.fromDate(scheduledAt) : null,
          nextTriggerAt: nextTriggerAt ? Timestamp.fromDate(nextTriggerAt) : null,
          status: isDone
            ? CRM_REMINDER_STATUS_DONE
            : enabled
            ? CRM_REMINDER_STATUS_ACTIVE
            : CRM_REMINDER_STATUS_DISABLED,
          completedAt: isDone
            ? existingReminder?.completedAt || followUp.completedAt || serverTimestamp()
            : null,
          updatedAt: serverTimestamp(),
          ...sanitizeCrmFirestoreValue(reminderLocationFields),
        };

        if (!existingReminder?.createdAt) {
          firestorePayload.createdAt = serverTimestamp();
        }

        await setDoc(reminderRef, firestorePayload, { merge: true });
        await upsertDueReminderDoc({
          reminderType: CRM_REMINDER_TYPE_FOLLOW_UP,
          sourceId: followUp.id || followUp.reminderId || "",
          sourceReminderId: reminderId,
          title: followUp.title || "Follow-up",
          message: buildDueReminderMessage(followUp.description || followUp.title || "Follow-up"),
          nextNotifyAt: nextTriggerAt,
          sourceStatus: localPayload.status,
          reminderEnabled: enabled,
          repeatKey,
        });
        applyCrmReminderLocalPatch(reminderId, localPayload);
      })
    );

    await Promise.all(
      existingFollowUpReminders
        .filter((record) => !activeFollowUpReminderDocIds.has(record.id))
        .map(async (record) => {
          await deleteDoc(doc(db, "users", workspaceUid, "crm_reminders", record.id)).catch(
            () => {}
          );
          await upsertDueReminderDoc({
            reminderType: CRM_REMINDER_TYPE_FOLLOW_UP,
            sourceId:
              record.followUpId ||
              record.sourceTaskId ||
              record.reminderId ||
              record.id ||
              "",
            sourceReminderId: record.id,
            title: record.taskTitle || "Follow-up",
            message: buildDueReminderMessage(record.taskDescription || record.taskTitle || ""),
            sourceStatus: CRM_REMINDER_STATUS_DISABLED,
            reminderEnabled: false,
          });
          applyCrmReminderLocalPatch(record.id, null);
        })
    );
  },
  [
    applyCrmReminderLocalPatch,
    buildDueReminderDocId,
    buildFollowUpReminderDocId,
    buildReminderDocId,
    combineReminderDateTime,
    crmReminderRecords,
    customerRecords,
    workspaceUid,
  ]
);

const saveCrmFieldsForQuote = useCallback(
  async (quoteId, crmOverrides = {}, options = {}) => {
    if (!workspaceUid || !quoteId) {
      if (options.requireWrite === true) {
        throw new Error("CRM quote save failed: missing workspace or quote id.");
      }
      return null;
    }

    const sourceQuote = savedQuotes.find((item) => item.id === quoteId);
    if (!sourceQuote) {
      if (options.requireWrite === true) {
        throw new Error(`CRM quote save failed: quote ${quoteId} was not found.`);
      }
      return null;
    }

    const nextCrm = buildQuoteCrmData(sourceQuote, crmOverrides);
    const quoteRef = doc(db, "users", workspaceUid, "quotations", quoteId);
    const hasInstallationDateOverride = Object.prototype.hasOwnProperty.call(
      crmOverrides,
      "installationDate"
    );
    const updatePayload = {
      crm: nextCrm,
      updatedAt: serverTimestamp(),
    };

    if (hasInstallationDateOverride) {
      updatePayload.installationDate = crmOverrides.installationDate
        ? Timestamp.fromDate(crmOverrides.installationDate)
        : null;
    }

    await updateDoc(quoteRef, updatePayload);

    const localPatch = {
      crm: nextCrm,
      updatedAt: new Date(),
    };

    if (hasInstallationDateOverride) {
      localPatch.installationDate = crmOverrides.installationDate || null;
    }

    applyQuoteLocalPatch(quoteId, localPatch);

    if (crmOverrides.skipReminderSync !== true && options.skipReminderSync !== true) {
      await syncReminderDocsForDeal({
        ...sourceQuote,
        id: quoteId,
        crmRecordType: "quote",
        quoteTypeResolved: resolveQuoteType(sourceQuote),
        pipelineType: resolveQuoteType(sourceQuote),
        quoteNo: sourceQuote.quoteNo || "",
        customerLabel:
          sourceQuote.customerName ||
          nextCrm.contact?.name ||
          "",
        crmTitle: nextCrm.title,
        crmContact: nextCrm.contact,
        nextActionDate: nextCrm.nextActionDate,
        installationDate:
          Object.prototype.hasOwnProperty.call(localPatch, "installationDate")
            ? localPatch.installationDate
            : nextCrm.installationDate,
        reminderConfigs: nextCrm.reminderConfigs,
        followUps: nextCrm.followUps,
        attachments: nextCrm.attachments,
        siteLocations: nextCrm.siteLocations,
      });
    }

    return {
      quoteId,
      firestorePath: `users/${workspaceUid}/quotations/${quoteId}`,
      updatePayload,
    };
  },
  [
    applyQuoteLocalPatch,
    buildQuoteCrmData,
    savedQuotes,
    syncReminderDocsForDeal,
    workspaceUid,
  ]
);

const upsertCustomerSearchIndexRecord = useCallback(
  async (record = {}, options = {}) => {
    if (!workspaceUid) return null;
    const write = buildCustomerSearchIndexWrite(record, {
      sourceType: options.sourceType || record.sourceType || "customer",
      workspaceUid,
    });
    if (!write?.personId) return null;

    await setDoc(
      doc(db, "users", workspaceUid, "customer_search_index", write.personId),
      write.payload,
      { merge: true }
    );
    invalidateWorkspaceCollectionCache(workspaceUid, "customer_search_index");
    quotationCustomerSearchCacheRef.current.clear();
    return write.localRecord;
  },
  [workspaceUid]
);

const applyCrmLeadLocalPatch = useCallback((leadId, patch) => {
  if (!leadId) return;
  invalidateWorkspaceCollectionCache(workspaceUid, "crm_leads");
  customerLedgerCrmDealsLoadRef.current = {};
  setCrmLeadRecords((prev) => {
    let found = false;
    const nextRecords = prev.map((item) => {
      if (item.id !== leadId) return item;
      found = true;
      return stripLegacyCrmQuoteLinkFields({
        ...item,
        ...patch,
        id: leadId,
      });
    });

    if (found) return nextRecords;

    return [
      stripLegacyCrmQuoteLinkFields({
        id: leadId,
        ...patch,
      }),
      ...nextRecords,
    ];
  });
}, [workspaceUid]);

const syncCrmLeadInstallationIndex = useCallback(
  async (leadId, leadLike = {}, options = {}) => {
    if (!workspaceUid || !leadId) return;
    const linkedQuotes = normalizeCrmLinkedQuotes(leadLike);
    const primaryQuote = getPrimaryCrmLinkedQuote(linkedQuotes);
    const indexId = String(primaryQuote?.quoteId || leadLike.quoteId || leadId).trim();
    if (!indexId) return;

    const installationDate =
      leadLike.installationDate ||
      leadLike.expectedInstallationDate ||
      leadLike.scheduledInstallationDate ||
      leadLike.installedAt ||
      leadLike.deliveryDate ||
      leadLike.crm?.installationDate ||
      null;
    const installationDateKey = toDateInputValue(installationDate);
    const status = String(leadLike.status || "").toLowerCase();
    const workStage = String(
      leadLike.workStage || leadLike.stageId || leadLike.stage || ""
    ).toLowerCase();
    const isInactive =
      leadLike.deleted === true ||
      ["cancelled", "canceled", "lost"].includes(status) ||
      ["cancelled", "canceled", "lost"].includes(workStage);
    const indexRef = doc(db, "users", workspaceUid, "installation_index", indexId);

    if (!installationDateKey || isInactive) {
      await deleteDoc(indexRef).catch((error) => {
        if (isDevRuntime) {
          console.warn("CRM installation index delete skipped", {
            leadId,
            indexId,
            code: error?.code,
            message: error?.message,
          });
        }
      });
      invalidateWorkspaceCollectionCache(workspaceUid, "installation_index");
      return;
    }

    await setDoc(
      indexRef,
      {
        quoteId: primaryQuote?.quoteId || leadLike.quoteId || "",
        jobId: leadId,
        leadId,
        quoteNo: primaryQuote?.quoteNo || leadLike.quoteNo || "",
        customerName:
          leadLike.customerName ||
          leadLike.contact?.name ||
          leadLike.title ||
          leadLike.crmTitle ||
          "Unnamed Customer",
        normalizedPhone: normalizeSearchCompact(
          leadLike.customerPhone ||
            leadLike.contact?.phone ||
            leadLike.contact?.whatsapp ||
            ""
        ),
        installationDate,
        installationDateKey,
        monthKey: installationDateKey.slice(0, 7),
        status: leadLike.status || "",
        workStage: leadLike.workStage || leadLike.stageId || leadLike.stage || "",
        totalAmount: Number(leadLike.finalValue || leadLike.expectedValue || 0),
        itemSummary:
          leadLike.itemSummary ||
          leadLike.title ||
          leadLike.companyOrProject ||
          leadLike.crmTitle ||
          "CRM job",
        location:
          leadLike.location ||
          leadLike.site ||
          leadLike.siteLocation ||
          leadLike.locationAddress ||
          leadLike.contact?.address ||
          "",
        source: options.source || "crmLeadWrite",
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    invalidateWorkspaceCollectionCache(workspaceUid, "installation_index");
  },
  [toDateInputValue, workspaceUid]
);

const findLinkedCrmLeadForQuote = useCallback(
  (quote = {}, explicitLeadId = "") => {
    const quoteId = String(quote?.id || "");
    const quoteNoValue = String(quote?.quoteNo || "");
    const leadId =
      explicitLeadId ||
      quote?.crm?.leadId ||
      quote?.leadId ||
      "";

    const matchedLeads = crmLeadRecords.filter(
      (lead) =>
        (leadId && String(lead.id || "") === String(leadId)) ||
        doesCrmLeadReferenceQuote(lead, quoteId, quoteNoValue)
    );

    return chooseCanonicalQuoteDeal(matchedLeads, leadId) || null;
  },
  [crmLeadRecords]
);

const findLinkedQuoteForLead = useCallback(
  (lead = {}) => {
    const linkedQuotes = normalizeCrmLinkedQuotes(lead);
    const primaryLinkedQuote = getPrimaryCrmLinkedQuote(linkedQuotes);

    return (
      savedQuotes.find(
        (quote) =>
          primaryLinkedQuote?.quoteId &&
          String(quote.id || "") === String(primaryLinkedQuote.quoteId || "")
      ) ||
      savedQuotes.find(
        (quote) =>
          primaryLinkedQuote?.quoteNo &&
          String(quote.quoteNo || "") === String(primaryLinkedQuote.quoteNo || "")
      ) ||
      savedQuotes.find((quote) =>
        linkedQuotes.some(
          (linkedQuote) =>
            linkedQuote.quoteId &&
            String(quote.id || "") === String(linkedQuote.quoteId || "")
        )
      ) ||
      savedQuotes.find((quote) =>
        linkedQuotes.some(
          (linkedQuote) =>
            linkedQuote.quoteNo &&
            String(quote.quoteNo || "") === String(linkedQuote.quoteNo || "")
        )
      ) ||
      null
    );
  },
  [savedQuotes]
);

const ensureCrmLeadForExecutionSpecs = useCallback(
  async (dealLike = {}, executionSpecsOverride = null) => {
    if (!workspaceUid) return null;

    if (dealLike?.crmRecordType === "lead" && dealLike?.id) {
      return crmLeadRecords.find((lead) => lead.id === dealLike.id) || dealLike;
    }

    const primaryLinkedQuote = getPrimaryCrmLinkedQuote(
      normalizeCrmLinkedQuotes(dealLike)
    );
    const directQuoteId =
      dealLike?.crmRecordType === "quote" ? dealLike?.id || "" : "";
    const sourceQuote =
      savedQuotes.find(
        (quote) =>
          directQuoteId && String(quote.id || "") === String(directQuoteId)
      ) ||
      savedQuotes.find((quote) =>
        String(quote.id || "") ===
        String(primaryLinkedQuote?.quoteId || dealLike?.quoteId || "")
      ) ||
      savedQuotes.find(
        (quote) =>
          String(quote.quoteNo || "") &&
          String(quote.quoteNo || "") === String(primaryLinkedQuote?.quoteNo || dealLike?.quoteNo || "")
      ) ||
      null;

    if (!sourceQuote) return null;

    const existingLead = findLinkedCrmLeadForQuote(sourceQuote);
    if (existingLead?.id) {
      setSelectedCrmDealId(existingLead.id);
      return existingLead;
    }

    const quoteCrm = buildQuoteCrmData(sourceQuote);
    const leadId = buildQuoteCreatedLeadId(sourceQuote.id || sourceQuote.quoteNo || "");
    const deterministicLeadSnap = await getDoc(
      doc(db, "users", workspaceUid, "crm_leads", leadId)
    ).catch(() => null);
    if (deterministicLeadSnap?.exists?.()) {
      const deterministicLead = {
        id: leadId,
        ...deterministicLeadSnap.data(),
      };
      setSelectedCrmDealId(leadId);
      return deterministicLead;
    }
    const leadPayload = buildCrmLeadData(
      {
        id: leadId,
        createdAt: new Date(),
      },
      {
        title: quoteCrm.title,
        companyOrProject:
          sourceQuote?.customerName ||
          quoteCrm.contact?.name ||
          "",
        contact: quoteCrm.contact,
        source: quoteCrm.source,
        quoteType: quoteCrm.pipelineType,
        stageId: quoteCrm.stageId,
        linkedQuotes: normalizeCrmLinkedQuotes({}, {
          linkedQuotes: [
            {
              quoteId: sourceQuote.id || "",
              quoteNo: sourceQuote.quoteNo || "",
              type: quoteCrm.pipelineType,
              value: Number(quoteCrm.finalValue || quoteCrm.expectedValue || 0),
              isPrimary: true,
            },
          ],
        }),
        hasQuote: true,
        checklistItems: quoteCrm.checklistItems,
        expectedValue: Number(quoteCrm.expectedValue || 0),
        finalValue: Number(quoteCrm.finalValue || 0),
        nextAction: quoteCrm.nextAction,
        nextActionDate: quoteCrm.nextActionDate,
        installationDate: quoteCrm.installationDate,
        assignedToUserId: quoteCrm.assignedToUserId,
        assignedToNameSnapshot: quoteCrm.assignedToNameSnapshot,
        assignedAt: quoteCrm.assignedAt,
        assignedBy: quoteCrm.assignedBy,
        notes: quoteCrm.notes,
        tags: quoteCrm.tags,
        reminderConfigs: quoteCrm.reminderConfigs,
        followUps: quoteCrm.followUps,
        attachments: quoteCrm.attachments,
        siteLocations: quoteCrm.siteLocations,
        executionSpecs:
          executionSpecsOverride != null
            ? normalizeExecutionSpecs(executionSpecsOverride)
            : normalizeExecutionSpecs(dealLike?.executionSpecs || quoteCrm.executionSpecs || []),
      }
    );

    const leadRef = doc(db, "users", workspaceUid, "crm_leads", leadId);
    const leadCreatePayload = sanitizeCrmFirestoreValue({
      title: leadPayload.title,
      contact: leadPayload.contact,
      companyOrProject: leadPayload.companyOrProject,
      source: leadPayload.source,
      quoteType: leadPayload.quoteType,
      pipelineType: leadPayload.pipelineType,
      stageId: leadPayload.stageId,
      stageNameSnapshot: leadPayload.stageNameSnapshot,
      customerId: leadPayload.customerId || sourceQuote.customerId || "",
      customerName: leadPayload.customerName || sourceQuote.customerName || "",
      customerPhone: leadPayload.customerPhone || sourceQuote.customerPhone || "",
      linkedQuotes: leadPayload.linkedQuotes || [],
      hasQuote: true,
      checklistTemplateId: leadPayload.checklistTemplateId || "",
      checklistItems: normalizeChecklistTree(leadPayload.checklistItems || []),
      expectedValue: Number(leadPayload.expectedValue || 0),
      finalValue: Number(leadPayload.finalValue || 0),
      nextAction: leadPayload.nextAction || "",
      nextActionDate: leadPayload.nextActionDate || null,
      assignedToUserId: leadPayload.assignedToUserId || "",
      assignedToNameSnapshot: leadPayload.assignedToNameSnapshot || "",
      assignedAt: leadPayload.assignedAt || null,
      assignedBy: leadPayload.assignedBy || "",
      tags: leadPayload.tags || [],
      notes: leadPayload.notes || "",
      installationDate: leadPayload.installationDate || null,
      reminderConfigs: leadPayload.reminderConfigs || normalizeCrmReminderConfigs({}, {}),
      followUps: leadPayload.followUps || [],
      attachments: leadPayload.attachments || [],
      executionSpecs: leadPayload.executionSpecs || [],
      siteLocations: leadPayload.siteLocations || [],
      status: leadPayload.status || "draft",
    });

    await setDoc(leadRef, {
      ...leadCreatePayload,
      ...(isDevRuntime
        ? {
            _lastWriteSource: "ensureCrmLeadForExecutionSpecs",
            _lastWriteAt: serverTimestamp(),
          }
        : {}),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    await upsertCustomerSearchIndexRecord(
      {
        ...leadPayload,
        ...leadCreatePayload,
        id: leadId,
        crmLeadId: leadId,
        leadId,
        latestQuoteId: sourceQuote.id || "",
        latestQuoteNo: sourceQuote.quoteNo || "",
        sourceType: "crm_lead",
      },
      { sourceType: "crm_lead" }
    ).catch((error) => {
      if (isDevRuntime) {
        console.error("CUSTOMER SEARCH INDEX EXECUTION CRM UPSERT FAILED", {
          leadId,
          code: error?.code,
          message: error?.message,
        });
      }
    });
    invalidateWorkspaceCollectionCache(workspaceUid, "crm_leads");
    setCrmLeadRecords((prev) => [
      {
        ...leadPayload,
        id: leadId,
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      ...prev,
    ]);

    const nextQuoteCrm = buildQuoteCrmData(sourceQuote, {
      leadId,
      hasQuote: true,
      title: leadPayload.title,
      contact: leadPayload.contact,
      stageId: leadPayload.stageId,
      status: leadPayload.status,
      nextAction: leadPayload.nextAction,
      nextActionDate: leadPayload.nextActionDate,
      installationDate: leadPayload.installationDate,
      checklistItems: leadPayload.checklistItems,
      reminderConfigs: leadPayload.reminderConfigs,
      attachments: leadPayload.attachments,
      assignedToUserId: leadPayload.assignedToUserId,
      assignedToNameSnapshot: leadPayload.assignedToNameSnapshot,
      assignedAt: leadPayload.assignedAt,
      assignedBy: leadPayload.assignedBy,
      notes: leadPayload.notes,
      tags: leadPayload.tags,
      source: leadPayload.source,
      siteLocations: leadPayload.siteLocations,
    });

    await updateDoc(doc(db, "users", workspaceUid, "quotations", sourceQuote.id), {
      crm: nextQuoteCrm,
      updatedAt: serverTimestamp(),
    });
    await setDoc(
      doc(
        db,
        "users",
        workspaceUid,
        QUOTE_DEAL_LINKS_COLLECTION,
        getQuoteDealLinkDocId(sourceQuote.id)
      ),
      {
        quoteId: sourceQuote.id,
        quoteNo: sourceQuote.quoteNo || "",
        leadId,
        customerId: sourceQuote.customerId || "",
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      },
      { merge: true }
    ).catch((error) => {
      console.warn("Execution CRM lead created, but quote deal link mapping failed:", error);
    });

    applyQuoteLocalPatch(sourceQuote.id, {
      crm: nextQuoteCrm,
      updatedAt: new Date(),
    });

    await syncReminderDocsForDeal({
      ...leadPayload,
      id: leadId,
      crmRecordType: "lead",
      customerLabel:
        leadPayload.contact?.name ||
        leadPayload.companyOrProject ||
        leadPayload.title ||
        "Unnamed Lead",
      crmTitle: leadPayload.title,
      crmContact: leadPayload.contact,
      primaryLinkedQuote: getPrimaryCrmLinkedQuote(leadPayload.linkedQuotes || []),
      nextActionDate: leadPayload.nextActionDate,
      installationDate: leadPayload.installationDate,
      reminderConfigs: leadPayload.reminderConfigs,
      followUps: leadPayload.followUps,
      attachments: leadPayload.attachments,
      siteLocations: leadPayload.siteLocations,
      pipelineType: leadPayload.pipelineType,
    }).catch((error) => {
      console.warn("CRM lead created, but reminder sync failed:", error);
    });

    setSelectedCrmDealId(leadId);

    return {
      ...leadPayload,
      id: leadId,
    };
  },
  [
    applyQuoteLocalPatch,
    buildCrmLeadData,
    buildQuoteCrmData,
    crmLeadRecords,
    findLinkedCrmLeadForQuote,
    savedQuotes,
    syncReminderDocsForDeal,
    upsertCustomerSearchIndexRecord,
    workspaceUid,
  ]
);

const saveCrmFieldsForLead = useCallback(
  async (leadId, crmOverrides = {}, options = {}) => {
    if (!workspaceUid || !leadId) {
      if (options.requireWrite === true) {
        throw new Error("CRM lead save failed: missing workspace or lead id.");
      }
      return null;
    }

    let sourceLead =
      options.sourceLead ||
      crmLeadRecords.find((item) => item.id === leadId) ||
      null;

    if (!sourceLead) {
      try {
        const leadSnap = await getDoc(
          doc(db, "users", workspaceUid, "crm_leads", leadId)
        );
        if (leadSnap.exists()) {
          sourceLead = {
            id: leadId,
            ...leadSnap.data(),
          };
        }
      } catch (error) {
        console.error("Failed to load CRM lead before saving:", error);
      }
    }

    if (!sourceLead) {
      if (options.createIfMissing !== true) {
        if (options.requireWrite === true) {
          throw new Error(`CRM lead save failed: lead ${leadId} was not found.`);
        }
        return null;
      }
      sourceLead = {
        id: leadId,
        ...crmOverrides,
        createdAt: null,
        updatedAt: new Date(),
      };
    }

    const nextLead = buildCrmLeadData(sourceLead, crmOverrides);
    const linkedQuoteForValue = findLinkedQuoteForLead(nextLead);
    const primaryLinkedQuote = getPrimaryCrmLinkedQuote(nextLead.linkedQuotes || []);
    const latestLinkedQuoteTotal = linkedQuoteForValue
      ? getNormalizedQuoteTotal(linkedQuoteForValue)
      : 0;
    const syncedExpectedValue =
      latestLinkedQuoteTotal > 0
        ? latestLinkedQuoteTotal
        : Number(nextLead.expectedValue || 0);
    const syncedFinalValue =
      latestLinkedQuoteTotal > 0
        ? latestLinkedQuoteTotal
        : Number(nextLead.finalValue || syncedExpectedValue || 0);
    const syncedLinkedQuotes = (nextLead.linkedQuotes || []).map((entry) =>
      entry.isPrimary
        ? { ...entry, value: syncedFinalValue || Number(entry.value || 0) }
        : entry
    );
    const syncedLinkedQuoteIds = uniqueValues(
      syncedLinkedQuotes.map((entry) => String(entry.quoteId || "").trim())
    );
    const syncedLinkedQuoteNos = uniqueValues(
      syncedLinkedQuotes.map((entry) => String(entry.quoteNo || "").trim())
    );
    const leadRef = doc(db, "users", workspaceUid, "crm_leads", leadId);
    const searchIndexFields = buildCrmSearchIndexFields({
      ...nextLead,
      linkedQuotes: syncedLinkedQuotes,
    });
    const persistedChecklistItems = preserveChecklistDraftTree(
      nextLead.checklistItems || [],
      {
        templateMode: false,
        idPrefix: "crm-check",
      }
    );
    const persistedInstallationReminder =
      nextLead.installationReminder ||
      buildCanonicalInstallationReminder(nextLead, {
        installationDate: nextLead.installationDate,
        reminderConfigs: nextLead.reminderConfigs,
      });
    const hasOverrideKey = (key) =>
      Object.prototype.hasOwnProperty.call(crmOverrides || {}, key);
    const isNewLeadWrite = !sourceLead?.createdAt;
    const persistFullCrmDetails = options.persistFullCrmDetails === true;
    const shouldPersistChecklist =
      persistFullCrmDetails ||
      options.persistChecklist === true ||
      isNewLeadWrite ||
      hasOverrideKey("checklistItems") ||
      hasOverrideKey("checklist") ||
      hasOverrideKey("stageChecklist") ||
      hasOverrideKey("checklistTemplateId");
    const shouldPersistInstallationDate =
      persistFullCrmDetails || isNewLeadWrite || hasOverrideKey("installationDate");
    const shouldPersistInstallationReminder =
      persistFullCrmDetails ||
      options.persistInstallationReminder === true ||
      isNewLeadWrite ||
      hasOverrideKey("installationReminder") ||
      hasOverrideKey("reminderConfig") ||
      hasOverrideKey("reminderConfigs");
    const shouldPersistFollowUps =
      persistFullCrmDetails || isNewLeadWrite || hasOverrideKey("followUps");
    const shouldPersistAttachments =
      persistFullCrmDetails || isNewLeadWrite || hasOverrideKey("attachments");
    const shouldPersistExecutionSpecs =
      persistFullCrmDetails || isNewLeadWrite || hasOverrideKey("executionSpecs");
    const shouldPersistSiteLocations =
      persistFullCrmDetails || isNewLeadWrite || hasOverrideKey("siteLocations");
    const updatePayload = {
      title: nextLead.title,
      contact: nextLead.contact,
      companyOrProject: nextLead.companyOrProject,
      source: nextLead.source,
      customerId: nextLead.customerId || "",
      customerKey: nextLead.customerKey || nextLead.customerId || "",
      customerName: nextLead.customerName || nextLead.contact?.name || "",
      customerPhone:
        nextLead.customerPhone ||
        nextLead.contact?.phone ||
        nextLead.contact?.whatsapp ||
        "",
      customerWhatsapp:
        nextLead.customerWhatsapp ||
        nextLead.contact?.whatsapp ||
        "",
      customerEmail:
        nextLead.customerEmail ||
        nextLead.contact?.email ||
        "",
      customerAddress:
        nextLead.customerAddress ||
        nextLead.contact?.address ||
        "",
      customerCompanyName:
        nextLead.customerCompanyName ||
        nextLead.companyOrProject ||
        "",
      quoteType: nextLead.quoteType,
      pipelineType: nextLead.pipelineType,
      stageId: nextLead.stageId,
      stage: nextLead.stageId,
      stageNameSnapshot: nextLead.stageNameSnapshot,
      linkedQuotes: syncedLinkedQuotes,
      ...createLegacyCrmQuoteLinkDeletePatch(),
      crmLeadId: leadId,
      quoteId: primaryLinkedQuote?.quoteId || syncedLinkedQuoteIds[0] || "",
      quoteNo: primaryLinkedQuote?.quoteNo || syncedLinkedQuoteNos[0] || "",
      linkedQuoteIds: syncedLinkedQuoteIds,
      linkedQuoteNos: syncedLinkedQuoteNos,
      dealValue: syncedFinalValue,
      hasQuote: nextLead.hasQuote === true,
      expectedValue: syncedExpectedValue,
      finalValue: syncedFinalValue,
      nextAction: nextLead.nextAction || "",
      nextActionDate: nextLead.nextActionDate || null,
      assignedToUserId: nextLead.assignedToUserId || "",
      assignedToNameSnapshot: nextLead.assignedToNameSnapshot || "",
      assignedTo: nextLead.assignedToNameSnapshot || "",
      assignedAt: nextLead.assignedAt || null,
      assignedBy: nextLead.assignedBy || "",
      tags: Array.isArray(nextLead.tags) ? nextLead.tags : [],
      notes: nextLead.notes || "",
      ...searchIndexFields,
      status: nextLead.status || "draft",
      updatedAt: serverTimestamp(),
    };

    if (shouldPersistChecklist) {
      updatePayload.checklistTemplateId = nextLead.checklistTemplateId || "";
      updatePayload.checklistItems = persistedChecklistItems;
      updatePayload.checklist = persistedChecklistItems;
      updatePayload.stageChecklist = persistedChecklistItems;
    }

    if (shouldPersistInstallationDate) {
      updatePayload.installationDate = nextLead.installationDate || null;
    }

    if (shouldPersistInstallationReminder) {
      updatePayload.installationReminder = persistedInstallationReminder;
      updatePayload.reminderConfig = persistedInstallationReminder;
      updatePayload.reminderConfigs =
        nextLead.reminderConfigs || normalizeCrmReminderConfigs({}, {});
    }

    if (shouldPersistFollowUps) {
      updatePayload.followUps = sanitizeCrmFirestoreValue(nextLead.followUps || []);
    }

    if (shouldPersistAttachments) {
      updatePayload.attachments = nextLead.attachments || [];
    }

    if (shouldPersistExecutionSpecs) {
      updatePayload.executionSpecs = nextLead.executionSpecs || [];
    }

    if (shouldPersistSiteLocations) {
      updatePayload.siteLocations = sanitizeCrmFirestoreValue(nextLead.siteLocations || []);
    }

    if (isDevRuntime) {
      updatePayload._lastWriteSource = options.writeSource || "saveCrmFieldsForLead";
      updatePayload._lastWriteAt = serverTimestamp();
    }

    if (!sourceLead?.createdAt) {
      updatePayload.createdAt = serverTimestamp();
    }

    if (isDevRuntime && options.writeSource === "saveCrmDetails") {
      console.log("WRITING CRM DETAILS DOC", leadRef.path, updatePayload);
    }

    await setDoc(leadRef, updatePayload, { merge: true });
    await upsertCustomerSearchIndexRecord(
      {
        ...nextLead,
        ...updatePayload,
        id: leadId,
        crmLeadId: leadId,
        leadId,
        latestQuoteId: primaryLinkedQuote?.quoteId || "",
        latestQuoteNo: primaryLinkedQuote?.quoteNo || "",
        sourceType: "crm_lead",
      },
      { sourceType: "crm_lead" }
    ).catch((error) => {
      if (isDevRuntime) {
        console.error("CUSTOMER SEARCH INDEX CRM UPSERT FAILED", {
          leadId,
          code: error?.code,
          message: error?.message,
        });
      }
    });

    const savedLeadSnap = await getDoc(leadRef);
    if (!savedLeadSnap.exists()) {
      throw new Error(`CRM lead save verification failed: lead ${leadId} was not found after write.`);
    }

    const savedLeadRaw = {
      id: leadId,
      ...savedLeadSnap.data(),
    };
    const savedLeadData = buildCrmLeadData(savedLeadRaw);

    const localLeadPatch = {
      ...savedLeadData,
      id: leadId,
      updatedAt: new Date(),
    };

    if (!sourceLead?.createdAt) {
      localLeadPatch.createdAt = new Date();
    }

    applyCrmLeadLocalPatch(leadId, localLeadPatch);
    const reminderSyncDeal = {
      ...savedLeadData,
      ...localLeadPatch,
      id: leadId,
      crmRecordType: "lead",
      customerLabel:
        savedLeadData.contact?.name ||
        savedLeadData.companyOrProject ||
        savedLeadData.title ||
        "Unnamed Lead",
      crmTitle: savedLeadData.title,
      crmContact: savedLeadData.contact,
      primaryLinkedQuote,
      nextActionDate: savedLeadData.nextActionDate,
      installationDate: savedLeadData.installationDate,
      reminderConfigs: savedLeadData.reminderConfigs,
      followUps: savedLeadData.followUps,
      attachments: savedLeadData.attachments,
      siteLocations: savedLeadData.siteLocations,
      pipelineType: savedLeadData.pipelineType,
    };

    if (options.skipReminderSync !== true) {
      await syncReminderDocsForDeal(reminderSyncDeal);
    }

    if (options.syncQuote === false || !primaryLinkedQuote?.quoteId) {
      return {
        leadId,
        firestorePath: `users/${workspaceUid}/crm_leads/${leadId}`,
        updatePayload,
        savedLead: savedLeadData,
        savedLeadRaw,
      };
    }

    const sourceQuote =
      savedQuotes.find((item) => item.id === primaryLinkedQuote.quoteId) || null;
    if (!sourceQuote) {
      return {
        leadId,
        firestorePath: `users/${workspaceUid}/crm_leads/${leadId}`,
        updatePayload,
        savedLead: savedLeadData,
        savedLeadRaw,
      };
    }
    const latestQuoteTotal = getNormalizedQuoteTotal(sourceQuote);

    const nextStageMeta = getStageMetaForType(
      nextLead.pipelineType,
      nextLead.stageId
    );
    const nextStatus =
      nextStageMeta?.confirmsQuote === true ||
      String(sourceQuote?.status || "").toLowerCase() === "confirmed"
        ? "confirmed"
        : "draft";
    const nextCrmPayload = buildQuoteCrmData(
      {
        ...sourceQuote,
        workStage: nextLead.stageId,
        status: nextStatus,
        installationDate: nextLead.installationDate || null,
      },
      {
        stageId: nextLead.stageId,
        status: nextStatus,
        installationDate: nextLead.installationDate || null,
        title: nextLead.title,
        contact: nextLead.contact,
        nextAction: nextLead.nextAction,
        nextActionDate: nextLead.nextActionDate,
        notes: nextLead.notes,
        tags: nextLead.tags,
        checklistItems: nextLead.checklistItems,
        reminderConfigs: nextLead.reminderConfigs,
        followUps: nextLead.followUps,
        attachments: nextLead.attachments,
        assignedToUserId: nextLead.assignedToUserId,
        assignedToNameSnapshot: nextLead.assignedToNameSnapshot,
        assignedTo: nextLead.assignedToNameSnapshot,
        assignedAt: nextLead.assignedAt,
        assignedBy: nextLead.assignedBy,
        leadId: nextLead.id,
        hasQuote: nextLead.hasQuote === true,
        expectedValue: latestQuoteTotal,
        finalValue: latestQuoteTotal,
      }
    );

    const quoteRef = doc(db, "users", workspaceUid, "quotations", primaryLinkedQuote.quoteId);
    await updateDoc(quoteRef, {
      workStage: nextLead.stageId,
      status: nextStatus,
      installationDate: nextLead.installationDate || null,
      crm: nextCrmPayload,
      crmLeadId: leadId,
      leadId,
      customerId: nextLead.customerId || sourceQuote.customerId || "",
      customerKey:
        nextLead.customerKey ||
        sourceQuote.customerKey ||
        nextLead.customerId ||
        sourceQuote.customerId ||
        "",
      customerName: nextLead.customerName || sourceQuote.customerName || "",
      customerPhone: nextLead.customerPhone || sourceQuote.customerPhone || "",
      customerWhatsapp: nextLead.customerWhatsapp || sourceQuote.customerWhatsapp || "",
      customerEmail: nextLead.customerEmail || sourceQuote.customerEmail || "",
      customerAddress: nextLead.customerAddress || sourceQuote.customerAddress || "",
      customerCompanyName:
        nextLead.customerCompanyName || sourceQuote.customerCompanyName || "",
      customerLedgerLinked: Boolean(nextLead.customerId || sourceQuote.customerId),
      updatedAt: serverTimestamp(),
    });

    applyQuoteLocalPatch(primaryLinkedQuote.quoteId, {
      workStage: nextLead.stageId,
      status: nextStatus,
      installationDate: nextLead.installationDate || null,
      crm: nextCrmPayload,
      crmLeadId: leadId,
      leadId,
      customerId: nextLead.customerId || sourceQuote.customerId || "",
      customerKey:
        nextLead.customerKey ||
        sourceQuote.customerKey ||
        nextLead.customerId ||
        sourceQuote.customerId ||
        "",
      customerName: nextLead.customerName || sourceQuote.customerName || "",
      customerPhone: nextLead.customerPhone || sourceQuote.customerPhone || "",
      customerWhatsapp: nextLead.customerWhatsapp || sourceQuote.customerWhatsapp || "",
      customerEmail: nextLead.customerEmail || sourceQuote.customerEmail || "",
      customerAddress: nextLead.customerAddress || sourceQuote.customerAddress || "",
      customerCompanyName:
        nextLead.customerCompanyName || sourceQuote.customerCompanyName || "",
      customerLedgerLinked: Boolean(nextLead.customerId || sourceQuote.customerId),
      updatedAt: new Date(),
    });

    return {
      leadId,
      firestorePath: `users/${workspaceUid}/crm_leads/${leadId}`,
      updatePayload,
      savedLead: savedLeadData,
      savedLeadRaw,
    };
  },
  [
    applyCrmLeadLocalPatch,
    applyQuoteLocalPatch,
    buildCanonicalInstallationReminder,
    buildCrmLeadData,
    buildQuoteCrmData,
    crmLeadRecords,
    findLinkedQuoteForLead,
    getNormalizedQuoteTotal,
    getStageMetaForType,
    savedQuotes,
    syncReminderDocsForDeal,
    upsertCustomerSearchIndexRecord,
    workspaceUid,
  ]
);

const saveCrmFieldsForDeal = useCallback(
  async (dealId, crmOverrides = {}, options = {}) => {
    const sourceLead =
      options.sourceLead ||
      crmLeadRecords.find((deal) => deal.id === dealId) || null;

    if (options.forceLead === true || sourceLead) {
      return await saveCrmFieldsForLead(dealId, crmOverrides, {
        ...options,
        sourceLead,
      });
    }

    return await saveCrmFieldsForQuote(dealId, crmOverrides, options);
  },
  [crmLeadRecords, saveCrmFieldsForLead, saveCrmFieldsForQuote]
);

const upsertQuoteDealLink = useCallback(
  async ({ quoteId, quoteNo = "", leadId = "", customerId = "", customerKey = "" } = {}) => {
    if (!workspaceUid || !quoteId || !leadId) return;

    const linkRef = doc(
      db,
      "users",
      workspaceUid,
      QUOTE_DEAL_LINKS_COLLECTION,
      getQuoteDealLinkDocId(quoteId)
    );
    const linkSnap = await getDoc(linkRef).catch(() => null);
    await setDoc(
      linkRef,
      {
        quoteId,
        quoteNo,
        leadId,
        customerId,
        customerKey: customerKey || customerId || "",
        updatedAt: serverTimestamp(),
        ...(linkSnap?.exists?.() ? {} : { createdAt: serverTimestamp() }),
      },
      { merge: true }
    );
  },
  [workspaceUid]
);

const findExistingDealForQuote = useCallback(
  async ({ quoteId = "", quoteNo = "", preferredLeadId = "" } = {}) => {
    if (!workspaceUid || (!quoteId && !quoteNo)) {
      return {
        canonicalLead: null,
        duplicateLeads: [],
        matchedLeadIds: [],
        mappingLeadId: "",
      };
    }

    const normalizedQuoteId = String(quoteId || "").trim();
    const normalizedQuoteNo = String(quoteNo || "").trim();
    const normalizedPreferredLeadId = String(preferredLeadId || "").trim();

    const fetchLeadById = async (leadId) => {
      if (!leadId) return null;
      const localLead = crmLeadRecords.find(
        (lead) => String(lead.id || "") === String(leadId)
      );
      if (localLead) return localLead;

      try {
        const leadSnap = await getDoc(
          doc(db, "users", workspaceUid, "crm_leads", leadId)
        );
        return leadSnap.exists()
          ? {
              id: leadSnap.id,
              ...leadSnap.data(),
            }
          : null;
      } catch (error) {
        console.warn("Could not fetch linked CRM lead before quote save:", error);
        return null;
      }
    };

    const candidates = [];
    const addCandidate = (lead) => {
      if (!lead?.id) return;
      if (candidates.some((item) => String(item.id) === String(lead.id))) return;
      candidates.push(lead);
    };

    const mappingLeadId = normalizedQuoteId
      ? await getDoc(
          doc(
            db,
            "users",
            workspaceUid,
            QUOTE_DEAL_LINKS_COLLECTION,
            getQuoteDealLinkDocId(normalizedQuoteId)
          )
        )
          .then((snap) => (snap.exists() ? String(snap.data()?.leadId || "") : ""))
          .catch((error) => {
            console.warn("Could not read quote deal link mapping:", error);
            return "";
          })
      : "";

    addCandidate(await fetchLeadById(normalizedPreferredLeadId));
    addCandidate(await fetchLeadById(mappingLeadId));
    addCandidate(await fetchLeadById(buildQuoteCreatedLeadId(normalizedQuoteId)));

    crmLeadRecords
      .filter((lead) =>
        doesCrmLeadReferenceQuote(lead, normalizedQuoteId, normalizedQuoteNo)
      )
      .forEach(addCandidate);

    const canonicalLead =
      chooseCanonicalQuoteDeal(candidates, normalizedPreferredLeadId || mappingLeadId) ||
      null;
    const duplicateLeads = canonicalLead?.id
      ? candidates.filter((lead) => String(lead.id) !== String(canonicalLead.id))
      : [];
    const matchedLeadIds = candidates.map((lead) => String(lead.id || "")).filter(Boolean);

    if (isDevRuntime && matchedLeadIds.length > 1) {
      console.log("Quote deal duplicate audit", {
        quoteId: normalizedQuoteId,
        quoteNo: normalizedQuoteNo,
        matchedLeadIds,
        canonicalLeadId: canonicalLead?.id || "",
      });
    }

    return {
      canonicalLead,
      duplicateLeads,
      matchedLeadIds,
      mappingLeadId,
    };
  },
  [crmLeadRecords, workspaceUid]
);

const ensureSavedQuoteCrmDeal = useCallback(
  async (quoteLike = {}, options = {}) => {
    if (!workspaceUid || !quoteLike?.id || !canAccessCrm) {
      return {
        leadId: quoteLike?.crm?.leadId || "",
        crmPayload: quoteLike?.crm || null,
        stageId: quoteLike?.workStage || "",
        status: quoteLike?.status || "draft",
      };
    }

    const quoteType = resolveQuoteType(quoteLike);
    const quoteId = String(quoteLike.id || "");
    const quoteNoValue = String(options.quoteNo || quoteLike.quoteNo || quoteId);
    const latestQuoteTotal = Number(
      options.totalValue ?? getNormalizedQuoteTotal(quoteLike) ?? 0
    );
    const quoteGivenStageId = getQuoteGivenStageIdForType(quoteType);
    const defaultStatus = String(quoteLike.status || quoteLike.crm?.status || "draft");
    const customerFields = {
      customerId: String(options.customerFields?.customerId || quoteLike.customerId || quoteLike.crm?.customerId || ""),
      customerKey: String(options.customerFields?.customerKey || quoteLike.customerKey || quoteLike.crm?.customerKey || quoteLike.customerId || quoteLike.crm?.customerId || ""),
      customerName: String(options.customerFields?.customerName || quoteLike.customerName || quoteLike.crm?.customerName || ""),
      customerPhone: String(options.customerFields?.customerPhone || quoteLike.customerPhone || quoteLike.crm?.customerPhone || ""),
      customerWhatsapp: String(options.customerFields?.customerWhatsapp || quoteLike.customerWhatsapp || ""),
      customerEmail: String(options.customerFields?.customerEmail || quoteLike.customerEmail || ""),
      customerAddress: String(options.customerFields?.customerAddress || quoteLike.customerAddress || ""),
      customerCompanyName: String(options.customerFields?.customerCompanyName || quoteLike.customerCompanyName || ""),
    };
    const sourceQuote = {
      ...quoteLike,
      quoteType,
      quoteNo: quoteNoValue,
      ...customerFields,
      grandTotal: latestQuoteTotal || quoteLike.grandTotal,
      subtotal: quoteLike.subtotal,
      workStage: quoteLike.workStage || quoteGivenStageId,
      status: defaultStatus,
    };
    const quoteRef = doc(db, "users", workspaceUid, "quotations", quoteId);
    const inheritedCrmQuoteId = String(
      sourceQuote.crm?.quoteId ||
        sourceQuote.crm?.sourceQuoteId ||
        sourceQuote.crm?.linkedQuoteId ||
        ""
    ).trim();
    const shouldIgnoreInheritedLeadId =
      options.saveAsNew === true ||
      (inheritedCrmQuoteId && inheritedCrmQuoteId !== quoteId);
    const requestedLinkedLeadId = shouldIgnoreInheritedLeadId
      ? ""
      : String(options.linkedLeadId || sourceQuote.crm?.leadId || "").trim();
    const existingDealLookup = await findExistingDealForQuote({
      quoteId,
      quoteNo: quoteNoValue,
      preferredLeadId: requestedLinkedLeadId,
    });
    const existingLinkedLead =
      existingDealLookup.canonicalLead ||
      findLinkedCrmLeadForQuote(sourceQuote, requestedLinkedLeadId);
    const openDeals = existingLinkedLead?.id
      ? [existingLinkedLead]
      : findOpenCustomerDeals(customerFields, quoteType);
    const existingOpenDealIds = openDeals
      .map((deal) => String(deal?.id || ""))
      .filter(Boolean);
    const notifyQuoteDealOutcome = (actionSelected) => {
      if (options.showSaveOutcomeToast !== true && options.showToast !== true) return;
      if (actionSelected === "save_quote_only") {
        notify("Quote saved only", "success");
      } else if (actionSelected === "create_new") {
        notify("Quote saved and new CRM deal created", "success");
      } else {
        notify("Quote saved and linked to CRM deal", "success");
      }
    };
    const logQuoteDealOutcome = (actionSelected, finalLinkedLeadId = "") => {
      logQuoteToDealLinkDecision({
        quoteId,
        customerId: customerFields.customerId,
        matchedCustomerId: customerFields.customerId,
        existingOpenDealIds,
        actionSelected,
        finalLinkedLeadId,
      });
    };

    if (
      (sourceQuote.crm?.saveQuoteOnly === true || options.previousSaveQuoteOnly === true) &&
      !existingLinkedLead?.id &&
      options.respectSaveQuoteOnly !== false &&
      options.forceCreate !== true &&
      options.forceLinkExisting !== true
    ) {
      const nextCrmPayload = {
        ...buildQuoteCrmData(sourceQuote, {
          stageId: sourceQuote.workStage,
          status: defaultStatus,
          leadId: "",
          hasQuote: true,
          ...customerFields,
        }),
        saveQuoteOnly: true,
      };
      await updateDoc(quoteRef, {
        crm: nextCrmPayload,
        updatedAt: serverTimestamp(),
      });
      applyQuoteLocalPatch(quoteId, {
        crm: nextCrmPayload,
        updatedAt: new Date(),
      }, { persistIndex: false });
      logQuoteDealOutcome("save_quote_only", "");
      notifyQuoteDealOutcome("save_quote_only");
      return {
        leadId: "",
        crmPayload: nextCrmPayload,
        stageId: sourceQuote.workStage,
        status: defaultStatus,
        action: "saveOnly",
      };
    }

    let choice = existingLinkedLead?.id
      ? "linkExisting"
      : options.forceCreate
      ? "create"
      : options.forceLinkExisting
      ? "linkExisting"
      : "";

    if (!choice) {
      choice = await requestQuoteDealChoice({
        customerName: customerFields.customerName || "this customer",
        quoteNo: quoteNoValue,
        quoteType,
        openDeals,
      });
    }

    const buildFinalQuoteCrm = (overrides = {}) => ({
      ...buildQuoteCrmData(sourceQuote, {
        stageId: overrides.stageId || sourceQuote.workStage || quoteGivenStageId,
        status: overrides.status || defaultStatus,
        leadId: overrides.leadId || "",
        hasQuote: true,
        expectedValue: latestQuoteTotal,
        finalValue: latestQuoteTotal,
        customerId: customerFields.customerId,
        customerKey: customerFields.customerKey,
        customerName: customerFields.customerName,
        customerPhone: customerFields.customerPhone,
        customerWhatsapp: customerFields.customerWhatsapp,
        customerEmail: customerFields.customerEmail,
        customerAddress: customerFields.customerAddress,
        customerCompanyName: customerFields.customerCompanyName,
        contact: {
          name: customerFields.customerName,
          phone: customerFields.customerPhone,
          whatsapp: customerFields.customerWhatsapp,
          email: customerFields.customerEmail,
          address: customerFields.customerAddress,
        },
        installationDate:
          overrides.installationDate ??
          sourceQuote.installationDate ??
          sourceQuote.crm?.installationDate ??
          null,
      }),
      saveQuoteOnly: overrides.saveQuoteOnly === true,
    });

    if (choice === "saveOnly") {
      const nextCrmPayload = buildFinalQuoteCrm({
        stageId: sourceQuote.workStage || quoteGivenStageId,
        status: defaultStatus,
        saveQuoteOnly: true,
      });
      await updateDoc(quoteRef, {
        crm: nextCrmPayload,
        updatedAt: serverTimestamp(),
      });
      applyQuoteLocalPatch(quoteId, {
        crm: nextCrmPayload,
        updatedAt: new Date(),
      }, { persistIndex: false });
      logQuoteDealOutcome("save_quote_only", "");
      notifyQuoteDealOutcome("save_quote_only");
      return {
        leadId: "",
        crmPayload: nextCrmPayload,
        stageId: sourceQuote.workStage || quoteGivenStageId,
        status: defaultStatus,
        action: "saveOnly",
      };
    }

    const targetLead =
      choice === "linkExisting"
        ? existingLinkedLead || openDeals[0] || null
        : null;
    const linkedQuoteEntry = {
      quoteId,
      quoteNo: quoteNoValue,
      type: quoteType,
      value: latestQuoteTotal,
      isPrimary: true,
    };

    if (targetLead?.id) {
      const nextLinkedQuotes = normalizeCrmLinkedQuotes(targetLead, {
        linkedQuotes: [
          ...normalizeCrmLinkedQuotes(targetLead)
            .filter(
              (entry) =>
                String(entry.quoteId || "") !== quoteId &&
                String(entry.quoteNo || "") !== quoteNoValue
            )
            .map((entry) => ({ ...entry, isPrimary: false })),
          linkedQuoteEntry,
        ],
      });
      const nextStageId = targetLead.stageId || quoteGivenStageId;
      const nextStatus = targetLead.status || defaultStatus;

      await saveCrmFieldsForLead(
        targetLead.id,
        {
          quoteType,
          pipelineType: quoteType,
          stageId: nextStageId,
          status: nextStatus,
          linkedQuotes: nextLinkedQuotes,
          hasQuote: true,
          customerId: customerFields.customerId,
          customerKey: customerFields.customerKey,
          customerName: customerFields.customerName,
          customerPhone: customerFields.customerPhone,
          customerWhatsapp: customerFields.customerWhatsapp,
          customerEmail: customerFields.customerEmail,
          customerAddress: customerFields.customerAddress,
          customerCompanyName: customerFields.customerCompanyName,
          contact: {
            ...(targetLead.contact || {}),
            name: targetLead.contact?.name || customerFields.customerName,
            phone: targetLead.contact?.phone || customerFields.customerPhone,
            whatsapp: targetLead.contact?.whatsapp || customerFields.customerWhatsapp,
            email: targetLead.contact?.email || customerFields.customerEmail,
            address: targetLead.contact?.address || customerFields.customerAddress,
          },
          expectedValue: latestQuoteTotal,
          finalValue: latestQuoteTotal,
          installationDate:
            targetLead.installationDate ||
            sourceQuote.installationDate ||
            sourceQuote.crm?.installationDate ||
            null,
        },
        { sourceLead: targetLead, syncQuote: false }
      );
      await upsertQuoteDealLink({
        quoteId,
        quoteNo: quoteNoValue,
        leadId: targetLead.id,
        customerId: customerFields.customerId,
        customerKey: customerFields.customerKey,
      });

      const nextCrmPayload = buildFinalQuoteCrm({
        leadId: targetLead.id,
        stageId: nextStageId,
        status: nextStatus,
        installationDate:
          targetLead.installationDate ||
          sourceQuote.installationDate ||
          sourceQuote.crm?.installationDate ||
          null,
      });
      const quoteCustomerDealPatch = {
        crmLeadId: targetLead.id,
        leadId: targetLead.id,
        customerId: customerFields.customerId,
        customerKey: customerFields.customerKey,
        customerName: customerFields.customerName,
        customerPhone: customerFields.customerPhone,
        customerWhatsapp: customerFields.customerWhatsapp,
        customerEmail: customerFields.customerEmail,
        customerAddress: customerFields.customerAddress,
        customerCompanyName: customerFields.customerCompanyName,
        customerLedgerLinked: Boolean(customerFields.customerId),
      };
      await updateDoc(quoteRef, {
        crm: nextCrmPayload,
        ...quoteCustomerDealPatch,
        workStage: nextStageId,
        status: nextStatus,
        installationDate: nextCrmPayload.installationDate || null,
        updatedAt: serverTimestamp(),
      });
      applyQuoteLocalPatch(quoteId, {
        crm: nextCrmPayload,
        ...quoteCustomerDealPatch,
        workStage: nextStageId,
        status: nextStatus,
        installationDate: nextCrmPayload.installationDate || null,
        updatedAt: new Date(),
      }, { persistIndex: false });

      logQuoteDealOutcome("link_existing", targetLead.id);
      notifyQuoteDealOutcome("link_existing");
      return {
        leadId: targetLead.id,
        crmPayload: nextCrmPayload,
        stageId: nextStageId,
        status: nextStatus,
        action: "linked",
      };
    }

    const leadId = buildQuoteCreatedLeadId(quoteId);
    const quoteCrm = buildFinalQuoteCrm({
      leadId,
      stageId: quoteGivenStageId,
      status: defaultStatus,
    });
    const leadPayload = buildCrmLeadData(
      { id: leadId, createdAt: new Date() },
      {
        title: quoteCrm.title || `${customerFields.customerName || "Customer"} - #${quoteNoValue}`,
        companyOrProject:
          customerFields.customerCompanyName ||
          customerFields.customerName ||
          quoteCrm.title ||
          "",
        contact: quoteCrm.contact,
        source: quoteCrm.source,
        quoteType,
        pipelineType: quoteType,
        stageId: quoteGivenStageId,
        status: defaultStatus,
        linkedQuotes: normalizeCrmLinkedQuotes({}, {
          linkedQuotes: [linkedQuoteEntry],
        }),
        hasQuote: true,
        customerId: customerFields.customerId,
        customerKey: customerFields.customerKey,
        customerName: customerFields.customerName,
        customerPhone: customerFields.customerPhone,
        customerWhatsapp: customerFields.customerWhatsapp,
        customerEmail: customerFields.customerEmail,
        customerAddress: customerFields.customerAddress,
        customerCompanyName: customerFields.customerCompanyName,
        checklistItems: quoteCrm.checklistItems,
        expectedValue: latestQuoteTotal,
        finalValue: latestQuoteTotal,
        nextAction: quoteCrm.nextAction,
        nextActionDate: quoteCrm.nextActionDate,
        installationDate: quoteCrm.installationDate,
        reminderConfigs: quoteCrm.reminderConfigs,
        followUps: quoteCrm.followUps,
        attachments: quoteCrm.attachments,
        notes: quoteCrm.notes,
        tags: quoteCrm.tags,
      }
    );

    await saveCrmFieldsForLead(
      leadId,
      leadPayload,
      {
        sourceLead: { id: leadId, createdAt: null },
        createIfMissing: true,
        forceLead: true,
        syncQuote: false,
      }
    );
    await upsertQuoteDealLink({
      quoteId,
      quoteNo: quoteNoValue,
      leadId,
      customerId: customerFields.customerId,
      customerKey: customerFields.customerKey,
    });

    const nextCrmPayload = {
      ...quoteCrm,
      leadId,
      saveQuoteOnly: false,
    };
    const quoteCustomerDealPatch = {
      crmLeadId: leadId,
      leadId,
      customerId: customerFields.customerId,
      customerKey: customerFields.customerKey,
      customerName: customerFields.customerName,
      customerPhone: customerFields.customerPhone,
      customerWhatsapp: customerFields.customerWhatsapp,
      customerEmail: customerFields.customerEmail,
      customerAddress: customerFields.customerAddress,
      customerCompanyName: customerFields.customerCompanyName,
      customerLedgerLinked: Boolean(customerFields.customerId),
    };
    await updateDoc(quoteRef, {
      crm: nextCrmPayload,
      ...quoteCustomerDealPatch,
      workStage: quoteGivenStageId,
      status: defaultStatus,
      installationDate: nextCrmPayload.installationDate || null,
      updatedAt: serverTimestamp(),
    });
    applyQuoteLocalPatch(quoteId, {
      crm: nextCrmPayload,
      ...quoteCustomerDealPatch,
      workStage: quoteGivenStageId,
      status: defaultStatus,
      installationDate: nextCrmPayload.installationDate || null,
      updatedAt: new Date(),
    }, { persistIndex: false });

    logQuoteDealOutcome("create_new", leadId);
    notifyQuoteDealOutcome("create_new");
    return {
      leadId,
      crmPayload: nextCrmPayload,
      stageId: quoteGivenStageId,
      status: defaultStatus,
      action: "created",
    };
  },
  [
    applyQuoteLocalPatch,
    buildCrmLeadData,
    buildQuoteCrmData,
    canAccessCrm,
    findExistingDealForQuote,
    findLinkedCrmLeadForQuote,
    findOpenCustomerDeals,
    getNormalizedQuoteTotal,
    getQuoteGivenStageIdForType,
    notify,
    requestQuoteDealChoice,
    saveCrmFieldsForLead,
    upsertQuoteDealLink,
    workspaceUid,
  ]
);

const saveCrmPipelinesSettings = useCallback(async () => {
  if (!workspaceUid) {
    notify("Please sign in first.", "warning");
    return;
  }

  const normalized = normalizeCrmPipelines(crmPipelines);
  const settingsRef = doc(db, "users", workspaceUid, "settings", "main");

  await setDoc(
    settingsRef,
    {
      crmPipelines: normalized,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
  invalidateFirestoreReadCache(settingsRef);

  setCrmPipelines(normalized);
  notify("CRM pipeline settings saved", "success");
}, [crmPipelines, notify, workspaceUid]);

const saveCrmExecutionTemplates = useCallback(async () => {
  if (!workspaceUid) {
    notify("Please sign in first.", "warning");
    return;
  }

  if (isSavingExecutionTemplates) return;

  if (
    executionTemplatesLoadingWorkspaceRef.current === workspaceUid ||
    executionTemplatesLoadedWorkspaceRef.current !== workspaceUid
  ) {
    notify("Execution templates are still loading. Please try again in a moment.", "warning");
    if (isDevRuntime) {
      console.warn("EXECUTION TEMPLATE SAVE BLOCKED BEFORE LOAD", {
        workspaceUid,
        loadingWorkspace: executionTemplatesLoadingWorkspaceRef.current,
        loadedWorkspace: executionTemplatesLoadedWorkspaceRef.current,
        localTemplateCount: crmExecutionTemplates.length,
      });
    }
    return;
  }

  setIsSavingExecutionTemplates(true);
  try {
    const templatesRef = collection(db, "users", workspaceUid, "execution_templates");
    const existingSnapshot = await getDocs(templatesRef, {
      label: "saveExecutionTemplatesExistingScan",
      cacheKey: `users/${workspaceUid}/execution_templates?saveExistingScan`,
    });
    const normalizedTemplates = normalizeExecutionTemplates(crmExecutionTemplates).filter(
      (template) => String(template.name || "").trim()
    );
    const nextIds = new Set(normalizedTemplates.map((template) => template.id));
    if (isDevRuntime) {
      console.log("EXECUTION TEMPLATE SAVE START", {
        workspaceUid,
        existingCount: existingSnapshot.size,
        localCount: crmExecutionTemplates.length,
        normalizedCount: normalizedTemplates.length,
        cacheKey: `users/${workspaceUid}/execution_templates`,
      });
    }

    await Promise.all(
      normalizedTemplates.map((template) =>
        setDoc(
          doc(db, "users", workspaceUid, "execution_templates", template.id),
          {
            name: template.name,
            itemType: normalizeExecutionTemplateItemType(
              template.itemType || template.name
            ),
            fields: (Array.isArray(template.fields) ? template.fields : [])
              .map((field) => ({
                id:
                  String(field?.id || "").trim() ||
                  createClientId("exec-template-field"),
                key: String(field?.key || "").trim(),
                defaultValue:
                  field?.defaultValue == null ? "" : String(field.defaultValue),
              }))
              .filter((field) => field.key),
            updatedAt: serverTimestamp(),
            createdAt: template.createdAt || serverTimestamp(),
          },
          { merge: true }
        )
      )
    );

    await Promise.all(
      existingSnapshot.docs
        .filter((docSnap) => !nextIds.has(docSnap.id))
        .map((docSnap) =>
          deleteDoc(doc(db, "users", workspaceUid, "execution_templates", docSnap.id))
        )
    );

    invalidateWorkspaceCollectionCache(workspaceUid, "execution_templates");
    const savedTemplatesForState = normalizeExecutionTemplateDrafts(normalizedTemplates)
      .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
    setCrmExecutionTemplates(savedTemplatesForState);
    executionTemplatesLoadedWorkspaceRef.current = workspaceUid;
    setExecutionTemplatesLoadState({
      workspaceUid,
      loading: false,
      loaded: true,
      error: "",
    });
    markExecutionTemplatesBaseline(
      savedTemplatesForState,
      selectedExecutionTemplateManagerId || "library",
      "saved"
    );
    clearExecutionTemplatesDraft();
    notify("Execution templates saved", "success");
  } catch (error) {
    console.error("Execution templates save failed:", {
      error,
      code: error?.code,
      message: error?.message,
      workspaceUid,
      localTemplateCount: crmExecutionTemplates.length,
    });
    notify("Could not save execution templates. Your changes are still here.", "error");
  } finally {
    setIsSavingExecutionTemplates(false);
  }
}, [
  clearExecutionTemplatesDraft,
  crmExecutionTemplates,
  isSavingExecutionTemplates,
  markExecutionTemplatesBaseline,
  notify,
  selectedExecutionTemplateManagerId,
  workspaceUid,
]);

  const addExecutionTemplate = useCallback(() => {
  if (
    workspaceUid &&
    executionTemplatesLoadedWorkspaceRef.current !== workspaceUid
  ) {
    notify("Execution templates are still loading. Please try again in a moment.", "warning");
    return;
  }
  const nextTemplate = createExecutionTemplateDraft(
    "New Template",
    "new template",
    [createExecutionTemplateFieldDraft("Field 1", "")]
  );
  setSelectedExecutionTemplateManagerId(nextTemplate.id);
  setCrmExecutionTemplates((prev) =>
    normalizeExecutionTemplateDrafts([...prev, nextTemplate])
  );
}, [notify, workspaceUid]);

const updateExecutionTemplate = useCallback((templateId, patch) => {
  setCrmExecutionTemplates((prev) =>
    normalizeExecutionTemplateDrafts(
      prev.map((template) =>
        template.id === templateId
          ? {
              ...template,
              ...patch,
            }
          : template
      )
    )
  );
}, []);

const deleteExecutionTemplate = useCallback((templateId) => {
  setCrmExecutionTemplates((prev) => {
    const nextTemplates = prev.filter((template) => template.id !== templateId);
    if (selectedExecutionTemplateManagerId === templateId) {
      setSelectedExecutionTemplateManagerId(nextTemplates[0]?.id || "");
    }
    return nextTemplates;
  });
}, [selectedExecutionTemplateManagerId]);

const addExecutionTemplateField = useCallback((templateId) => {
  setCrmExecutionTemplates((prev) =>
    normalizeExecutionTemplateDrafts(
      prev.map((template) =>
        template.id === templateId
          ? {
              ...template,
              fields: [
                ...(template.fields || []),
                createExecutionTemplateFieldDraft(
                  `Field ${(template.fields || []).length + 1}`,
                  ""
                ),
              ],
            }
          : template
      )
    )
  );
}, []);

const updateExecutionTemplateField = useCallback((templateId, fieldId, patch) => {
  setCrmExecutionTemplates((prev) =>
    normalizeExecutionTemplateDrafts(
      prev.map((template) =>
        template.id === templateId
          ? {
              ...template,
              fields: (template.fields || []).map((field) =>
                field.id === fieldId
                  ? {
                      ...field,
                      ...patch,
                    }
                  : field
              ),
            }
          : template
      )
    )
  );
}, []);

const removeExecutionTemplateField = useCallback((templateId, fieldId) => {
  setCrmExecutionTemplates((prev) =>
    normalizeExecutionTemplateDrafts(
      prev.map((template) =>
        template.id === templateId
          ? {
              ...template,
              fields: (template.fields || []).filter((field) => field.id !== fieldId),
            }
          : template
      )
    )
  );
}, []);

const updatePipelineStage = useCallback((quoteType, stageId, patch) => {
  setCrmPipelines((prev) => {
    const next = normalizeCrmPipelines(prev);
    const stages = next[quoteType].stages.map((stage) => {
      if (stage.id !== stageId) {
        if (patch.confirmsQuote === true) {
          return { ...stage, confirmsQuote: false };
        }
        if (patch.marksComplete === true) {
          return { ...stage, marksComplete: false };
        }
        if (patch.isCancelled === true) {
          return { ...stage, isCancelled: false };
        }
        return stage;
      }

      return {
        ...stage,
        ...patch,
      };
    });

    next[quoteType] = {
      ...next[quoteType],
      stages,
    };

    return next;
  });
}, []);

const movePipelineStage = useCallback((quoteType, stageId, direction) => {
  setCrmPipelines((prev) => {
    const next = normalizeCrmPipelines(prev);
    const stages = [...next[quoteType].stages];
    const index = stages.findIndex((stage) => stage.id === stageId);
    const targetIndex = index + direction;

    if (index < 0 || targetIndex < 0 || targetIndex >= stages.length) {
      return next;
    }

    const [stage] = stages.splice(index, 1);
    stages.splice(targetIndex, 0, stage);
    next[quoteType] = {
      ...next[quoteType],
      stages,
    };
    return next;
  });
}, []);

const deletePipelineStage = useCallback((quoteType, stageId) => {
  setCrmPipelines((prev) => {
    const next = normalizeCrmPipelines(prev);
    if (next[quoteType].stages.length <= 1) {
      return next;
    }

    next[quoteType] = {
      ...next[quoteType],
      stages: next[quoteType].stages.filter((stage) => stage.id !== stageId),
    };
    return next;
  });
}, []);

const addPipelineStage = useCallback((quoteType) => {
  setCrmPipelines((prev) => {
    const next = normalizeCrmPipelines(prev);
    next[quoteType] = {
      ...next[quoteType],
      stages: [...next[quoteType].stages, createStageDraft("New Stage")],
    };
    return next;
  });
}, []);

const addPipelineChecklistItem = useCallback((quoteType, stageId) => {
  setCrmPipelines((prev) => {
    const next = normalizeCrmPipelines(prev);
    next[quoteType] = {
      ...next[quoteType],
      stages: next[quoteType].stages.map((stage) =>
        stage.id === stageId
          ? {
              ...stage,
              defaultChecklist: [
                ...(stage.defaultChecklist || []),
                createChecklistTemplateDraft("New Checklist Item"),
              ],
            }
          : stage
      ),
    };
    return next;
  });
}, []);

const addPipelineChecklistChild = useCallback(
  (quoteType, stageId, parentId) => {
    setCrmPipelines((prev) => {
      const next = normalizeCrmPipelines(prev);
      next[quoteType] = {
        ...next[quoteType],
        stages: next[quoteType].stages.map((stage) =>
          stage.id === stageId
            ? {
                ...stage,
                defaultChecklist: insertChecklistChildNode(
                  stage.defaultChecklist || [],
                  parentId,
                  createChecklistChildDraft("New Checklist Item", {
                    templateMode: true,
                  })
                ),
              }
            : stage
        ),
      };
      return next;
    });
  },
  []
);

const updatePipelineChecklistItem = useCallback(
  (quoteType, stageId, itemId, patch) => {
    setCrmPipelines((prev) => {
      const next = normalizeCrmPipelines(prev);
      next[quoteType] = {
        ...next[quoteType],
        stages: next[quoteType].stages.map((stage) =>
          stage.id === stageId
            ? {
                ...stage,
                defaultChecklist: updateChecklistNodeById(
                  stage.defaultChecklist || [],
                  itemId,
                  (item) => ({ ...item, ...patch })
                ),
              }
            : stage
        ),
      };
      return next;
    });
  },
  []
);

const movePipelineChecklistItem = useCallback((quoteType, stageId, itemId, direction) => {
  setCrmPipelines((prev) => {
    const next = normalizeCrmPipelines(prev);
    next[quoteType] = {
      ...next[quoteType],
      stages: next[quoteType].stages.map((stage) =>
        stage.id === stageId
          ? {
              ...stage,
              defaultChecklist: moveChecklistNodeById(
                stage.defaultChecklist || [],
                itemId,
                direction
              ),
            }
          : stage
      ),
    };
    return next;
  });
}, []);

const deletePipelineChecklistItem = useCallback((quoteType, stageId, itemId) => {
  setCrmPipelines((prev) => {
    const next = normalizeCrmPipelines(prev);
    next[quoteType] = {
      ...next[quoteType],
      stages: next[quoteType].stages.map((stage) =>
        stage.id === stageId
          ? {
              ...stage,
              defaultChecklist: deleteChecklistNodeById(
                stage.defaultChecklist || [],
                itemId
              ),
            }
          : stage
      ),
    };
    return next;
  });
}, []);

const savedStageOptions = useMemo(
  () => getStageFilterOptions(crmPipelines, quoteTypeFilter),
  [crmPipelines, quoteTypeFilter]
);

const savedQuotesSearchText = useMemo(
  () => deferredSavedQuoteSearch.trim().toLowerCase(),
  [deferredSavedQuoteSearch]
);

const savedQuotesViewModel = useMemo(() => {
  let activeQuotes = savedQuotes.filter(
    (quote) =>
      !savedQuotesSearchText ||
      doesSearchBlobMatch(buildQuoteSearchBlob(quote), savedQuotesSearchText)
  );

  if (statusFilter !== "all") {
    activeQuotes = activeQuotes.filter((quote) => quote.status === statusFilter);
  }

  if (stageFilter !== "all") {
    const [stagePipelineType, stageId] = String(stageFilter).split("::");
    activeQuotes = activeQuotes.filter(
      (quote) =>
        resolveQuoteType(quote) === stagePipelineType &&
        (quote.workStage || quote.crm?.stageId || "quote_given") === stageId
    );
  }

  if (quoteTypeFilter !== "all") {
    activeQuotes = activeQuotes.filter(
      (quote) => resolveQuoteType(quote) === quoteTypeFilter
    );
  }

  const sortedActiveQuotes = [...activeQuotes].sort((a, b) => {
    if (sortBy === "highest") {
      return getQuoteValue(b) - getQuoteValue(a);
    }

    if (sortBy === "lowest") {
      return getQuoteValue(a) - getQuoteValue(b);
    }

    if (sortBy === "oldest") {
      return getCreatedMillis(a) - getCreatedMillis(b);
    }

    if (sortBy === "recently_edited") {
      return getUpdatedMillis(b) - getUpdatedMillis(a);
    }

    return getCreatedMillis(b) - getCreatedMillis(a);
  });

  const filteredRecycledQuotes = deletedQuotes
    .filter(
      (quote) =>
        !savedQuotesSearchText ||
        doesSearchBlobMatch(buildQuoteSearchBlob(quote), savedQuotesSearchText)
    )
    .filter((quote) =>
      quoteTypeFilter === "all"
        ? true
        : resolveQuoteType(quote) === quoteTypeFilter
    );

  return {
    activeQuotes: sortedActiveQuotes,
    filteredRecycledQuotes,
  };
}, [
  deletedQuotes,
  quoteTypeFilter,
  savedQuotes,
  savedQuotesSearchText,
  sortBy,
  stageFilter,
  statusFilter,
]);

useEffect(() => {
  setSavedQuotesVisibleCount(SAVED_QUOTES_PAGE_SIZE);
}, [
  businessDashboardDrilldownKey,
  savedQuotesSearchText,
  stageFilter,
  statusFilter,
  quoteTypeFilter,
  sortBy,
]);

const filteredSavedQuotesForDisplay = useMemo(() => {
  if (!activeBusinessDashboardDrilldownIds) {
    return savedQuotesViewModel.activeQuotes;
  }

  return savedQuotesViewModel.activeQuotes.filter((quote) =>
    activeBusinessDashboardDrilldownIds.has(quote.id)
  );
}, [activeBusinessDashboardDrilldownIds, savedQuotesViewModel.activeQuotes]);

const visibleSavedQuotesDisplay = useMemo(
  () => filteredSavedQuotesForDisplay.slice(0, savedQuotesVisibleCount),
  [filteredSavedQuotesForDisplay, savedQuotesVisibleCount]
);

const hasMoreSavedQuotesDisplay =
  visibleSavedQuotesDisplay.length < filteredSavedQuotesForDisplay.length;
const canLoadMoreSavedQuoteIndex =
  !savedQuotesSearchText && hasMoreSavedQuoteIndex && !activeBusinessDashboardDrilldown;

const recycleBinQuotesForDisplay = activeBusinessDashboardDrilldown
  ? []
  : recycleBinOpen
  ? savedQuotesViewModel.filteredRecycledQuotes
  : [];

const crmLeadDeals = useMemo(() => {
  return crmLeadRecords.filter((lead) => !isArchivedDuplicateCrmLead(lead)).map((lead) => {
    const crmData = buildCrmLeadData(lead);
    const linkedQuote = findLinkedQuoteForLead(crmData);
    const primaryLinkedQuote = getPrimaryCrmLinkedQuote(crmData.linkedQuotes || []);
    const liveQuoteTotal = linkedQuote ? getNormalizedQuoteTotal(linkedQuote) : 0;
    const displayedDealValue =
      liveQuoteTotal > 0 ? liveQuoteTotal : Number(crmData.expectedValue || 0);
    const stageMeta = getStageMetaForType(crmData.pipelineType, crmData.stageId);
    const checklist = normalizeDealChecklist(crmData.checklistItems, stageMeta);
    const checklistSummary = getChecklistSummary({
      ...crmData,
      checklistItems: checklist,
    });
    const followUpSummary = getFollowUpSummary(crmData);
    const nextFollowUp = followUpSummary.nextFollowUp || null;
    const installationSummary = getInstallationSummary(crmData);
    const createdMillis = getCreatedMillis(crmData);
    const updatedMillis = getUpdatedMillis(crmData);
    const installationMillis = getInstallationMillis({
      ...crmData,
      installationDate: installationSummary.installationDate,
    });
    const nextAction = nextFollowUp?.title || crmData.nextAction || "";
    const nextActionDate = nextFollowUp?.dueDate || crmData.nextActionDate || null;
    const nextActionMillis = toMillis(nextActionDate);
    const customerLabel =
      crmData.contact?.name ||
      crmData.companyOrProject ||
      crmData.title ||
      "Unnamed Lead";

    return {
      ...crmData,
      id: lead.id,
      crmRecordType: "lead",
      crm: {
        leadId: lead.id,
        title: crmData.title,
        contact: crmData.contact,
        stageId: crmData.stageId,
        linkedQuotes: crmData.linkedQuotes || [],
        hasQuote: crmData.hasQuote,
        nextAction,
        nextActionDate,
        assignedToUserId: crmData.assignedToUserId,
        assignedToNameSnapshot: crmData.assignedToNameSnapshot,
        assignedTo: crmData.assignedToNameSnapshot,
        assignedAt: crmData.assignedAt,
        assignedBy: crmData.assignedBy,
        notes: crmData.notes,
        tags: crmData.tags,
        checklistItems: checklist,
        source: crmData.source || crmData.contact?.source || "",
        followUps: crmData.followUps || [],
        executionSpecs: crmData.executionSpecs || [],
        siteLocations: crmData.siteLocations || [],
      },
      primaryLinkedQuote,
      quoteNo: primaryLinkedQuote?.quoteNo || "",
      customerLabel,
      quoteTypeResolved: crmData.pipelineType,
      workStage: crmData.stageId,
      workStageResolved: crmData.stageId,
      createdMillis,
      updatedMillis,
      confirmedMillis:
        String(crmData.status || "").toLowerCase() === "confirmed"
          ? updatedMillis || createdMillis
          : 0,
      installationMillis,
      installationDate: installationSummary.installationDate || crmData.installationDate || "",
      installationSummary,
      totalValue: displayedDealValue,
      crmStageId: stageMeta?.id || crmData.stageId,
      crmStageLabel: stageMeta?.label || crmData.stageNameSnapshot || crmData.stageId,
      crmStageColor: stageMeta?.color || crmData.stageColor || "#64748b",
      stageMeta,
      checklistItems: checklist,
      checklistCompleted: checklistSummary.completed,
      checklistTotal: checklistSummary.total,
      checklistSummary,
      nextAction,
      nextActionDate,
      nextActionMillis,
      crmNotes: crmData.notes || "",
      crmTitle: crmData.title || customerLabel,
      crmContact: crmData.contact || {},
      source: crmData.source || crmData.contact?.source || "",
      followUps: crmData.followUps || [],
      followUpSummary,
      reminderConfigs: crmData.reminderConfigs || normalizeCrmReminderConfigs({}, {}),
      attachments: crmData.attachments || [],
      executionSpecs: crmData.executionSpecs || [],
      siteLocations: crmData.siteLocations || [],
      assignedToUserId: crmData.assignedToUserId || "",
      assignedToNameSnapshot: crmData.assignedToNameSnapshot || "",
      pipelineType: crmData.pipelineType,
      isCrmCancelled: stageMeta?.isCancelled === true,
      isCrmClosed: stageMeta?.marksComplete === true,
      isCrmConfirmed: stageMeta?.confirmsQuote === true,
      expectedValue: displayedDealValue,
      finalValue: liveQuoteTotal > 0 ? liveQuoteTotal : Number(crmData.finalValue || 0),
      hasQuote: crmData.hasQuote === true,
      linkedQuotes: crmData.linkedQuotes || [],
    };
  });
}, [
  buildCrmLeadData,
  crmLeadRecords,
  findLinkedQuoteForLead,
    getNormalizedQuoteTotal,
  getStageMetaForType,
]);

const crmDeals = useMemo(() => {
  const leadIdSet = new Set(crmLeadDeals.map((deal) => deal.id));
  const quoteDeals = dashboardAllQuotes
    .filter((quote) => {
      const leadId = quote?.crm?.leadId || "";
      return !leadId || !leadIdSet.has(leadId);
    })
    .map((quote) => {
      const quoteType = quote.quoteTypeResolved || resolveQuoteType(quote);
      const crmData = buildQuoteCrmData(quote);
      const stageMeta = getStageMetaForType(quoteType, crmData.stageId);
      const checklist = normalizeDealChecklist(crmData.checklistItems, stageMeta);
      const checklistSummary = getChecklistSummary({
        ...crmData,
        checklistItems: checklist,
      });
      const followUpSummary = getFollowUpSummary(crmData);
      const nextFollowUp = followUpSummary.nextFollowUp || null;
      const installationSummary = getInstallationSummary(crmData);
      const nextAction = nextFollowUp?.title || crmData.nextAction || "";
      const nextActionDate = nextFollowUp?.dueDate || crmData.nextActionDate || null;
      const nextActionMillis = toMillis(nextActionDate);

      return {
        ...quote,
        crmRecordType: "quote",
        crm: crmData,
        quoteTypeResolved: quoteType,
        crmStageId: stageMeta?.id || crmData.stageId,
        crmStageLabel: stageMeta?.label || crmData.stageName || crmData.stageId,
        crmStageColor: stageMeta?.color || crmData.stageColor || "#64748b",
        stageMeta,
        checklistItems: checklist,
        checklistCompleted: checklistSummary.completed,
        checklistTotal: checklistSummary.total,
        checklistSummary,
        nextAction,
        nextActionDate,
        nextActionMillis,
        crmNotes: crmData.notes || "",
        crmTitle: crmData.title || quote.customerLabel,
        crmContact: crmData.contact || {},
        source: crmData.source || crmData.contact?.source || "",
        followUps: crmData.followUps || [],
        followUpSummary,
        installationDate: installationSummary.installationDate || crmData.installationDate || "",
        installationSummary,
        reminderConfigs: crmData.reminderConfigs || normalizeCrmReminderConfigs({}, {}),
        attachments: crmData.attachments || [],
        executionSpecs: crmData.executionSpecs || [],
        siteLocations: crmData.siteLocations || [],
        assignedToUserId: crmData.assignedToUserId || "",
        assignedToNameSnapshot:
          crmData.assignedToNameSnapshot || crmData.assignedTo || "",
        pipelineType: quoteType,
        isCrmCancelled: stageMeta?.isCancelled === true || quote.isCancelled,
        isCrmClosed: stageMeta?.marksComplete === true,
        isCrmConfirmed:
          quote.isConfirmed || stageMeta?.confirmsQuote === true,
        expectedValue: Number(crmData.expectedValue || quote.totalValue || 0),
        finalValue: Number(crmData.finalValue || quote.totalValue || 0),
        hasQuote: true,
        primaryLinkedQuote: {
          quoteId: quote.id,
          quoteNo: quote.quoteNo || "",
          type: quoteType,
          value: Number(crmData.finalValue || quote.totalValue || 0),
          isPrimary: true,
        },
      };
    });

  return dedupeCrmDealsByQuote([...crmLeadDeals, ...quoteDeals]).sort(
    (a, b) => Number(b.updatedMillis || 0) - Number(a.updatedMillis || 0)
  );
}, [buildQuoteCrmData, crmLeadDeals, dashboardAllQuotes, getStageMetaForType]);

const scanDuplicateQuoteDeals = useCallback(async () => {
  if (!workspaceUid) {
    notify("Please sign in first.", "warning");
    return;
  }

  setCrmDuplicateDealAudit((prev) => ({
    ...prev,
    isScanning: true,
    scanned: true,
  }));

  try {
    let leadRecords = crmLeadRecords;
    if (!leadRecords.length) {
      const leadSnap = await getDocs(
        firestoreQuery(
          collection(db, "users", workspaceUid, "crm_leads"),
          firestoreOrderBy("updatedAt", "desc"),
          firestoreLimit(CRM_LEADS_INITIAL_READ_LIMIT)
        ),
        {
          label: "duplicateDealScanRecentLeads",
          cacheKey: `users/${workspaceUid}/crm_leads?duplicateScanRecent=${CRM_LEADS_INITIAL_READ_LIMIT}`,
        }
      );
      leadRecords = leadSnap.docs.map((leadDoc) => ({
        id: leadDoc.id,
        ...leadDoc.data(),
      }));
    }
    const groups = buildQuoteDealDuplicateGroups(leadRecords);

    if (isDevRuntime) {
      groups.forEach((group) => {
        console.log("Quote deal duplicate audit", {
          quoteId: group.quoteId,
          quoteNo: group.quoteNo,
          matchedLeadIds: group.leads.map((lead) => lead.id),
          canonicalLeadId: group.canonicalLead?.id || "",
        });
      });
    }

    setCrmDuplicateDealAudit({
      scanned: true,
      isScanning: false,
      groups,
    });
    notify(
      groups.length
        ? `${groups.length} duplicate quote deal group${groups.length === 1 ? "" : "s"} found`
        : "No duplicate quote deals found",
      groups.length ? "warning" : "success"
    );
  } catch (error) {
    console.error("Duplicate quote deal scan failed:", error);
    setCrmDuplicateDealAudit((prev) => ({
      ...prev,
      isScanning: false,
    }));
    notify("Could not scan duplicate deals. Please try again.", "error");
  }
}, [crmLeadRecords, notify, workspaceUid]);

const repairDuplicateQuoteDealGroup = useCallback(
  async (group = {}) => {
    if (!workspaceUid || !group?.canonicalLead?.id) return;

    const canonicalLead = group.canonicalLead;
    const duplicateLeads = (group.duplicateLeads || []).filter(
      (lead) => lead?.id && lead.id !== canonicalLead.id
    );
    if (!duplicateLeads.length) return;

    setRepairingDuplicateDealKey(group.key || canonicalLead.id);

    try {
      const mergedByKey = (items = [], getKey) => {
        const seen = new Set();
        return items.filter((item, index) => {
          if (!item) return false;
          const key =
            getKey?.(item) ||
            item.id ||
            item.reminderId ||
            item.downloadURL ||
            item.url ||
            item.title ||
            `item-${index}`;
          const normalizedKey = normalizeSearchText(key);
          if (!normalizedKey || seen.has(normalizedKey)) return false;
          seen.add(normalizedKey);
          return true;
        });
      };

      const allLeads = [canonicalLead, ...duplicateLeads];
      const mergedFollowUps = mergedByKey(
        allLeads.flatMap((lead) => normalizeCrmFollowUps(lead.followUps || [])),
        (item) =>
          item.id ||
          item.reminderId ||
          `${item.title || ""}:${item.dueDate || ""}:${item.dueTime || ""}`
      );
      const mergedAttachments = mergedByKey(
        allLeads.flatMap((lead) => lead.attachments || []),
        (item) => item.id || item.downloadURL || item.url || item.name
      );
      const mergedSiteLocations = mergedByKey(
        allLeads.flatMap((lead) => normalizeSiteLocations(lead.siteLocations || [])),
        (item) => item.id || item.googleMapsUrl || `${item.label}:${item.addressText}`
      );
      const mergedExecutionSpecs = mergedByKey(
        allLeads.flatMap((lead) => normalizeExecutionSpecs(lead.executionSpecs || [])),
        (item) => item.id || item.name || item.itemType
      );
      const mergedTags = uniqueValues(
        allLeads.flatMap((lead) => (Array.isArray(lead.tags) ? lead.tags : []))
      );
      const duplicateNotes = duplicateLeads
        .map((lead) => String(lead.notes || "").trim())
        .filter(Boolean)
        .filter((note) => !String(canonicalLead.notes || "").includes(note));
      const mergedNotes = [canonicalLead.notes || "", ...duplicateNotes]
        .filter(Boolean)
        .join("\n\n");
      const canonicalLinkedQuotes = normalizeCrmLinkedQuotes(canonicalLead);
      const mergedLinkedQuotes = normalizeCrmLinkedQuotes(
        {},
        {
          linkedQuotes: allLeads.flatMap((lead) => normalizeCrmLinkedQuotes(lead)),
        }
      );
      const canonicalPatch = {
        linkedQuotes: mergedLinkedQuotes.length ? mergedLinkedQuotes : canonicalLinkedQuotes,
        ...createLegacyCrmQuoteLinkDeletePatch(),
        hasQuote: true,
        followUps: sanitizeCrmFirestoreValue(mergedFollowUps),
        attachments: sanitizeCrmFirestoreValue(mergedAttachments),
        siteLocations: sanitizeCrmFirestoreValue(mergedSiteLocations),
        executionSpecs: sanitizeCrmFirestoreValue(mergedExecutionSpecs),
        tags: mergedTags,
        notes: mergedNotes,
        duplicateDealRepairAt: serverTimestamp(),
        ...(isDevRuntime
          ? {
              _lastWriteSource: "duplicateDealRepair.canonical",
              _lastWriteAt: serverTimestamp(),
            }
          : {}),
        updatedAt: serverTimestamp(),
      };

      await setDoc(
        doc(db, "users", workspaceUid, "crm_leads", canonicalLead.id),
        canonicalPatch,
        { merge: true }
      );

      const archivePayload = {
        status: ARCHIVED_DUPLICATE_STATUS,
        dealStatus: ARCHIVED_DUPLICATE_STATUS,
        isArchivedDuplicate: true,
        duplicateOfLeadId: canonicalLead.id,
        archivedAt: serverTimestamp(),
        archivedBy: auth.currentUser?.uid || user?.uid || "",
        ...(isDevRuntime
          ? {
              _lastWriteSource: "duplicateDealRepair.archive",
              _lastWriteAt: serverTimestamp(),
            }
          : {}),
        updatedAt: serverTimestamp(),
      };

      await Promise.all(
        duplicateLeads.map((lead) =>
          setDoc(
            doc(db, "users", workspaceUid, "crm_leads", lead.id),
            archivePayload,
            { merge: true }
          )
        )
      );

      const quoteId =
        group.quoteId ||
        getCrmLeadQuoteReferenceSummary(canonicalLead).primaryQuoteId ||
        "";
      const quoteNo =
        group.quoteNo ||
        getCrmLeadQuoteReferenceSummary(canonicalLead).primaryQuoteNo ||
        "";
      const sourceQuote =
        dashboardAllQuotes.find(
          (quote) =>
            (quoteId && String(quote.id || "") === String(quoteId)) ||
            (quoteNo && String(quote.quoteNo || "") === String(quoteNo))
        ) || null;

      if (sourceQuote?.id) {
        const nextCrmPayload = buildQuoteCrmData(sourceQuote, {
          leadId: canonicalLead.id,
          hasQuote: true,
          stageId: canonicalLead.stageId || sourceQuote.workStage,
          status: canonicalLead.status || sourceQuote.status || "draft",
        });
        await updateDoc(doc(db, "users", workspaceUid, "quotations", sourceQuote.id), {
          crm: nextCrmPayload,
          updatedAt: serverTimestamp(),
        });
        applyQuoteLocalPatch(sourceQuote.id, {
          crm: nextCrmPayload,
          updatedAt: new Date(),
        });
        await upsertQuoteDealLink({
          quoteId: sourceQuote.id,
          quoteNo: sourceQuote.quoteNo || quoteNo,
          leadId: canonicalLead.id,
          customerId: canonicalLead.customerId || sourceQuote.customerId || "",
          customerKey:
            canonicalLead.customerKey ||
            sourceQuote.customerKey ||
            canonicalLead.customerId ||
            sourceQuote.customerId ||
            "",
        });
      }

      applyCrmLeadLocalPatch(canonicalLead.id, {
        ...canonicalPatch,
        updatedAt: new Date(),
      });
      duplicateLeads.forEach((lead) =>
        applyCrmLeadLocalPatch(lead.id, {
          ...archivePayload,
          archivedAt: new Date(),
          updatedAt: new Date(),
        })
      );

      setCrmDuplicateDealAudit((prev) => ({
        ...prev,
        groups: prev.groups.filter((item) => item.key !== group.key),
      }));
      notify("Duplicate quote deals repaired", "success");
    } catch (error) {
      console.error("Duplicate quote deal repair failed:", error, group);
      notify("Could not repair duplicate deals. Please try again.", "error");
    } finally {
      setRepairingDuplicateDealKey("");
    }
  },
  [
    applyCrmLeadLocalPatch,
    applyQuoteLocalPatch,
    buildQuoteCrmData,
    dashboardAllQuotes,
    notify,
    upsertQuoteDealLink,
    user?.uid,
    workspaceUid,
  ]
);

const repairCrmSearchIndex = useCallback(async () => {
  if (!workspaceUid) {
    notify("Please sign in first.", "warning");
    return;
  }

  setIsRepairingCrmSearchIndex(true);
  try {
    let cursor = null;
    let repairedCount = 0;
    let checkedCount = 0;
    let hasMore = true;

    while (hasMore) {
      const constraints = [
        firestoreOrderBy(firestoreDocumentId()),
        firestoreLimit(100),
      ];
      if (cursor) constraints.push(firestoreStartAfter(cursor));

      const snap = await getDocs(
        firestoreQuery(
          collection(db, "users", workspaceUid, "crm_leads"),
          ...constraints
        ),
        {
          label: "repairCrmSearchIndexBatch",
          cacheKey: `users/${workspaceUid}/crm_leads?repairSearchAfter=${
            cursor?.id || "start"
          }`,
        }
      );

      if (snap.empty) {
        hasMore = false;
        break;
      }

      const batch = writeBatch(db);
      let pageWrites = 0;

      snap.docs.forEach((leadDoc) => {
        const lead = {
          id: leadDoc.id,
          ...(leadDoc.data() || {}),
        };
        const searchIndexFields = buildCrmSearchIndexFields(lead);
        batch.set(
          doc(db, "users", workspaceUid, "crm_leads", leadDoc.id),
          sanitizeCrmFirestoreValue({
            ...searchIndexFields,
            searchIndexRepairedAt: serverTimestamp(),
            ...(isDevRuntime
              ? {
                  _lastWriteSource: "searchIndexRepair",
                  _lastWriteAt: serverTimestamp(),
                }
              : {}),
          }),
          { merge: true }
        );
        pageWrites += 1;
      });

      if (pageWrites > 0) {
        await batch.commit();
      }

      checkedCount += snap.size;
      repairedCount += pageWrites;
      cursor = snap.docs[snap.docs.length - 1];
      hasMore = snap.size === 100;
    }

    setCrmLeadRecords((prev) =>
      prev.map((lead) => ({
        ...lead,
        ...buildCrmSearchIndexFields(lead),
      }))
    );
    invalidateWorkspaceCollectionCache(workspaceUid, "crm_leads");
    notify(
      `Search index repaired for ${repairedCount} CRM deal${
        repairedCount === 1 ? "" : "s"
      } (${checkedCount} checked).`,
      "success"
    );
  } catch (error) {
    console.error("CRM search index repair failed:", error);
    notify("Could not repair search index. Please try again.", "error");
  } finally {
    setIsRepairingCrmSearchIndex(false);
  }
}, [notify, workspaceUid]);

useEffect(() => {
  if (!workspaceUid || crmLeadRecords.length === 0 || savedQuotes.length === 0) return;

  crmLeadRecords.forEach((lead) => {
    const linkedQuote = findLinkedQuoteForLead(lead);
    if (!linkedQuote?.id) return;

    const latestQuoteTotal = getNormalizedQuoteTotal(linkedQuote);
    if (!latestQuoteTotal) return;

    const storedExpected = Number(lead.expectedValue || 0);
    const storedFinal = Number(lead.finalValue || 0);
    const isStale =
      Math.abs(storedExpected - latestQuoteTotal) > 0.01 ||
      Math.abs(storedFinal - latestQuoteTotal) > 0.01;

    if (!isStale) return;

    const linkedQuotes = normalizeCrmLinkedQuotes(lead, {
      linkedQuotes: normalizeCrmLinkedQuotes(lead).map((entry) =>
        String(entry.quoteId || "") === String(linkedQuote.id || "") ||
        String(entry.quoteNo || "") === String(linkedQuote.quoteNo || "")
          ? {
              ...entry,
              quoteId: linkedQuote.id,
              quoteNo: linkedQuote.quoteNo || entry.quoteNo || "",
              type: resolveQuoteType(linkedQuote),
              value: latestQuoteTotal,
            }
          : entry
      ),
    });
    const leadRef = doc(db, "users", workspaceUid, "crm_leads", lead.id);
    setDoc(
      leadRef,
      {
        expectedValue: latestQuoteTotal,
        finalValue: latestQuoteTotal,
        linkedQuotes,
        ...createLegacyCrmQuoteLinkDeletePatch(),
        hasQuote: true,
        ...(isDevRuntime
          ? {
              _lastWriteSource: "crmValueAutoHeal",
              _lastWriteAt: serverTimestamp(),
            }
          : {}),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    )
      .then(() => {
        applyCrmLeadLocalPatch(lead.id, {
          expectedValue: latestQuoteTotal,
          finalValue: latestQuoteTotal,
          linkedQuotes,
          hasQuote: true,
          updatedAt: new Date(),
        });
      })
      .catch((error) => {
        console.error("CRM value auto-heal failed:", error);
      });
  });
}, [
  applyCrmLeadLocalPatch,
  crmLeadRecords,
  findLinkedQuoteForLead,
  getNormalizedQuoteTotal,
  savedQuotes,
  workspaceUid,
]);

const activeCrmPipelineType =
  crmScope === CRM_SCOPE_ALL ? crmPipelineType : crmScope;

const crmScopeDeals = useMemo(
  () =>
    crmDeals.filter(
      (deal) => crmScope === CRM_SCOPE_ALL || deal.pipelineType === crmScope
    ),
  [crmDeals, crmScope]
);

const crmSourceOptions = useMemo(() => {
  const values = new Set();
  crmScopeDeals.forEach((deal) => {
    const sourceValue = String(
      deal.source || deal.crmContact?.source || ""
    ).trim();
    if (sourceValue) {
      values.add(sourceValue);
    }
  });

  return Array.from(values).sort((a, b) => a.localeCompare(b));
}, [crmScopeDeals]);

const executionTemplateCatalog = useMemo(
  () =>
    normalizeExecutionTemplateDrafts(crmExecutionTemplates).sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""))
    ),
  [crmExecutionTemplates]
);

const normalizeExecutionTemplateMatchValue = useCallback(
  (value = "") =>
    String(value || "")
      .toLowerCase()
      .replace(/\s+/g, ""),
  []
);

const getExecutionTemplateForType = useCallback(
  (itemType = "") => {
    const normalizedType = normalizeExecutionTemplateMatchValue(itemType);
    if (!normalizedType) return null;

    const exactMatch =
      executionTemplateCatalog.find(
        (template) =>
          normalizeExecutionTemplateMatchValue(template.itemType) === normalizedType
      ) ||
      executionTemplateCatalog.find(
        (template) =>
          normalizeExecutionTemplateMatchValue(template.name) === normalizedType
      ) ||
      null;

    if (exactMatch) return exactMatch;

    return (
      executionTemplateCatalog.find((template) => {
        const templateType = normalizeExecutionTemplateMatchValue(template.itemType);
        return (
          Boolean(templateType) &&
          (normalizedType.includes(templateType) || templateType.includes(normalizedType))
        );
      }) ||
      executionTemplateCatalog.find((template) => {
        const templateName = normalizeExecutionTemplateMatchValue(template.name);
        return (
          Boolean(templateName) &&
          (normalizedType.includes(templateName) || templateName.includes(normalizedType))
        );
      }) ||
      null
    );
  },
  [executionTemplateCatalog, normalizeExecutionTemplateMatchValue]
);

const getExecutionTemplateById = useCallback(
  (templateId = "") =>
    executionTemplateCatalog.find((template) => template.id === templateId) || null,
  [executionTemplateCatalog]
);

const executionTemplateItemTypeOptions = useMemo(() => {
  const values = new Set(
    executionTemplateCatalog
      .map((template) => String(template.itemType || "").trim())
      .filter(Boolean)
  );

  crmDeals
    .filter((deal) => isGeneralExecutionEligibleDeal(deal))
    .forEach((deal) => {
    normalizeExecutionSpecs(deal.executionSpecs || []).forEach((item) => {
      const itemType = String(item.itemType || "").trim();
      if (itemType) {
        values.add(itemType);
      }
    });
  });

  return Array.from(values).sort((a, b) => a.localeCompare(b));
}, [crmDeals, executionTemplateCatalog]);

const crmBoardFilterOptions = useMemo(
  () => getStageFilterOptions(crmPipelines, activeCrmPipelineType),
  [activeCrmPipelineType, crmPipelines]
);

const crmBoardDeals = useMemo(() => {
  const queryText = normalizeSearchText(crmSearch);

  return crmDeals
    .filter((deal) => deal.pipelineType === activeCrmPipelineType)
    .filter((deal) => {
      if (!queryText) return true;
      const linkedCustomer = customerRecords.find(
        (customer) =>
          customer.id &&
          String(customer.id) === String(deal.customerId || deal.crm?.customerId || "")
      );
      return doesSearchBlobMatch(buildDealSearchBlob(deal, linkedCustomer), queryText);
    })
    .filter((deal) => {
      if (crmSourceFilter === "all") return true;
      return String(deal.source || deal.crmContact?.source || "").trim() === crmSourceFilter;
    })
    .filter((deal) => {
      if (crmStageFilter === "all") return true;
      const [pipelineType, stageId] = crmStageFilter.split("::");
      return deal.pipelineType === pipelineType && deal.crmStageId === stageId;
    })
    .sort((a, b) => b.updatedMillis - a.updatedMillis);
}, [
  activeCrmPipelineType,
  crmDeals,
  crmSearch,
  crmSourceFilter,
  crmStageFilter,
  customerRecords,
]);

const selectedCrmDealFromRecords = useMemo(
  () =>
    crmLeadDeals.find((deal) => String(deal.id || "") === String(selectedCrmDealId || "")) ||
    crmDeals.find((deal) => String(deal.id || "") === String(selectedCrmDealId || "")) ||
    null,
  [crmDeals, crmLeadDeals, selectedCrmDealId]
);

const cachedSelectedCrmDeal = selectedCrmDealCacheRef.current;
const selectedCrmDeal =
  selectedCrmDealFromRecords ||
  (selectedCrmDealId &&
  cachedSelectedCrmDeal &&
  String(cachedSelectedCrmDeal.id || "") === String(selectedCrmDealId)
    ? cachedSelectedCrmDeal
    : null);

useEffect(() => {
  if (selectedCrmDealFromRecords) {
    selectedCrmDealCacheRef.current = selectedCrmDealFromRecords;
    selectedCrmDealIdRef.current = String(selectedCrmDealFromRecords.id || "");
    return;
  }

  if (!selectedCrmDealId) {
    selectedCrmDealCacheRef.current = null;
    selectedCrmDealIdRef.current = "";
  }
}, [selectedCrmDealFromRecords, selectedCrmDealId]);

useEffect(() => {
  if (!isDevRuntime) return;
  if (!(isCrmDrawerPinned || isCrmDrawerHovered || isCompact)) return;

  console.log("CRM drawer render audit", {
    drawerOpen: isCrmDrawerPinned || isCrmDrawerHovered || isCompact,
    selectedCrmDealId,
    hasSelectedFromRecords: Boolean(selectedCrmDealFromRecords),
    hasCache: Boolean(selectedCrmDealCacheRef.current),
    selectedName:
      selectedCrmDeal?.customerName ||
      selectedCrmDeal?.customerLabel ||
      selectedCrmDeal?.crmTitle ||
      selectedCrmDeal?.title ||
      "",
  });
}, [
  isCompact,
  isCrmDrawerHovered,
  isCrmDrawerPinned,
  selectedCrmDeal,
  selectedCrmDealFromRecords,
  selectedCrmDealId,
]);

const buildCrmLeadDetailsRecord = useCallback(
  (leadLike = {}) => {
    const leadId = String(leadLike?.id || leadLike?.leadId || "").trim();
    if (!leadId) return null;

    const existingDeal =
      crmLeadDeals.find((deal) => String(deal.id || "") === leadId) ||
      crmDeals.find(
        (deal) => deal.crmRecordType === "lead" && String(deal.id || "") === leadId
      );
    if (existingDeal) return existingDeal;

    const crmData = buildCrmLeadData({ ...leadLike, id: leadId });
    const linkedQuote = findLinkedQuoteForLead(crmData);
    const primaryLinkedQuote = getPrimaryCrmLinkedQuote(crmData.linkedQuotes || []);
    const liveQuoteTotal = linkedQuote ? getNormalizedQuoteTotal(linkedQuote) : 0;
    const displayedDealValue =
      liveQuoteTotal > 0 ? liveQuoteTotal : Number(crmData.expectedValue || 0);
    const stageMeta = getStageMetaForType(crmData.pipelineType, crmData.stageId);
    const checklist = normalizeDealChecklist(crmData.checklistItems, stageMeta);
    const checklistSummary = getChecklistSummary({
      ...crmData,
      checklistItems: checklist,
    });
    const followUpSummary = getFollowUpSummary(crmData);
    const nextFollowUp = followUpSummary.nextFollowUp || null;
    const installationSummary = getInstallationSummary(crmData);
    const createdMillis = getCreatedMillis(crmData);
    const updatedMillis = getUpdatedMillis(crmData);
    const installationMillis = getInstallationMillis({
      ...crmData,
      installationDate: installationSummary.installationDate,
    });
    const nextAction = nextFollowUp?.title || crmData.nextAction || "";
    const nextActionDate = nextFollowUp?.dueDate || crmData.nextActionDate || null;
    const nextActionMillis = toMillis(nextActionDate);
    const customerLabel =
      crmData.contact?.name ||
      crmData.companyOrProject ||
      crmData.title ||
      "Unnamed Lead";

    return {
      ...crmData,
      id: leadId,
      crmRecordType: "lead",
      crm: {
        leadId,
        title: crmData.title,
        contact: crmData.contact,
        stageId: crmData.stageId,
        linkedQuotes: crmData.linkedQuotes || [],
        hasQuote: crmData.hasQuote,
        nextAction,
        nextActionDate,
        assignedToUserId: crmData.assignedToUserId,
        assignedToNameSnapshot: crmData.assignedToNameSnapshot,
        assignedTo: crmData.assignedToNameSnapshot,
        assignedAt: crmData.assignedAt,
        assignedBy: crmData.assignedBy,
        notes: crmData.notes,
        tags: crmData.tags,
        checklistItems: checklist,
        source: crmData.source || crmData.contact?.source || "",
        followUps: crmData.followUps || [],
        executionSpecs: crmData.executionSpecs || [],
        siteLocations: crmData.siteLocations || [],
        reminderConfigs: crmData.reminderConfigs || {},
        installationReminder: crmData.installationReminder || null,
      },
      primaryLinkedQuote,
      quoteNo: primaryLinkedQuote?.quoteNo || "",
      customerLabel,
      quoteTypeResolved: crmData.pipelineType,
      workStage: crmData.stageId,
      workStageResolved: crmData.stageId,
      createdMillis,
      updatedMillis,
      confirmedMillis:
        String(crmData.status || "").toLowerCase() === "confirmed"
          ? updatedMillis || createdMillis
          : 0,
      installationMillis,
      installationDate: installationSummary.installationDate || crmData.installationDate || "",
      installationSummary,
      totalValue: displayedDealValue,
      crmStageId: stageMeta?.id || crmData.stageId,
      crmStageLabel: stageMeta?.label || crmData.stageNameSnapshot || crmData.stageId,
      crmStageColor: stageMeta?.color || crmData.stageColor || "#64748b",
      stageMeta,
      checklistItems: checklist,
      checklistCompleted: checklistSummary.completed,
      checklistTotal: checklistSummary.total,
      checklistSummary,
      nextAction,
      nextActionDate,
      nextActionMillis,
      crmNotes: crmData.notes || "",
      crmTitle: crmData.title || customerLabel,
      crmContact: crmData.contact || {},
      source: crmData.source || crmData.contact?.source || "",
      followUps: crmData.followUps || [],
      followUpSummary,
      reminderConfigs: crmData.reminderConfigs || normalizeCrmReminderConfigs({}, {}),
      installationReminder: crmData.installationReminder || null,
      attachments: crmData.attachments || [],
      executionSpecs: crmData.executionSpecs || [],
      siteLocations: crmData.siteLocations || [],
      assignedToUserId: crmData.assignedToUserId || "",
      assignedToNameSnapshot: crmData.assignedToNameSnapshot || "",
      pipelineType: crmData.pipelineType,
      isCrmCancelled: stageMeta?.isCancelled === true,
      isCrmClosed: stageMeta?.marksComplete === true,
      isCrmConfirmed: stageMeta?.confirmsQuote === true,
      expectedValue: displayedDealValue,
      finalValue: liveQuoteTotal > 0 ? liveQuoteTotal : Number(crmData.finalValue || 0),
      hasQuote: crmData.hasQuote === true,
      linkedQuotes: crmData.linkedQuotes || [],
    };
  },
  [
    buildCrmLeadData,
    crmDeals,
    crmLeadDeals,
    findLinkedQuoteForLead,
    getNormalizedQuoteTotal,
    getStageMetaForType,
  ]
);

const fetchCrmLeadForDetails = useCallback(
  async (leadId = "", sourceReason = "direct") => {
    const normalizedLeadId = String(leadId || "").trim();
    if (!workspaceUid || !normalizedLeadId) return null;

    const localLead =
      crmLeadRecords.find((lead) => String(lead.id || "") === normalizedLeadId) ||
      null;
    if (localLead) {
      return {
        leadId: normalizedLeadId,
        lead: buildCrmLeadDetailsRecord(localLead),
        sourceReason: `${sourceReason}:local_crm_leads`,
      };
    }

    try {
      const leadSnap = await getDoc(
        doc(db, "users", workspaceUid, "crm_leads", normalizedLeadId)
      );
      if (!leadSnap.exists()) return null;

      const rawLead = { id: leadSnap.id, ...leadSnap.data() };
      return {
        leadId: leadSnap.id,
        lead: buildCrmLeadDetailsRecord(rawLead),
        sourceReason: `${sourceReason}:firestore_crm_leads`,
      };
    } catch (error) {
      if (isDevRuntime) {
        console.error("CRM details lead fetch failed", error, {
          leadId: normalizedLeadId,
          sourceReason,
          path: `users/${workspaceUid}/crm_leads/${normalizedLeadId}`,
        });
      }
      return null;
    }
  },
  [buildCrmLeadDetailsRecord, crmLeadRecords, workspaceUid]
);

const resolveCrmDealForDetails = useCallback(
  async (record = {}) => {
    if (!record) {
      return { leadId: "", lead: null, sourceReason: "missing_record" };
    }

    const tryLeadId = async (leadId, sourceReason) => {
      const resolved = await fetchCrmLeadForDetails(leadId, sourceReason);
      return resolved?.leadId && resolved?.lead ? resolved : null;
    };

    const directCandidates = [
      { value: record.id, reason: "record.id" },
      { value: record.leadId, reason: "record.leadId" },
      { value: record.crmLeadId, reason: "record.crmLeadId" },
      { value: record.canonicalLeadId, reason: "record.canonicalLeadId" },
      { value: record.crm?.leadId, reason: "record.crm.leadId" },
      { value: record.primaryLeadId, reason: "record.primaryLeadId" },
    ];

    for (const candidate of directCandidates) {
      const resolved = await tryLeadId(candidate.value, candidate.reason);
      if (resolved) return resolved;
    }

    const linkedQuoteSummary = getCrmLeadQuoteReferenceSummary(record);
    const quoteIds = [
      linkedQuoteSummary.primaryQuoteId,
      record.quoteId,
      record.primaryQuoteId,
      record.linkedQuoteId,
      record.primaryLinkedQuote?.quoteId,
      record.crm?.quoteId,
      record.crm?.primaryQuoteId,
      record.crmRecordType === "quote" ? record.id : "",
    ]
      .map((value) => String(value || "").trim())
      .filter((value, index, source) => value && source.indexOf(value) === index);
    const quoteNos = [
      linkedQuoteSummary.primaryQuoteNo,
      record.quoteNo,
      record.primaryQuoteNo,
      record.linkedQuoteNo,
      record.primaryLinkedQuote?.quoteNo,
      record.crm?.quoteNo,
      record.crm?.primaryQuoteNo,
    ]
      .map((value) => String(value || "").trim())
      .filter((value, index, source) => value && source.indexOf(value) === index);

    for (const quoteId of quoteIds) {
      const lookup = await findExistingDealForQuote({
        quoteId,
        quoteNo: quoteNos[0] || "",
        preferredLeadId: record.crm?.leadId || record.leadId || record.crmLeadId || "",
      });
      if (lookup?.canonicalLead?.id) {
        const resolved = await tryLeadId(
          lookup.canonicalLead.id,
          `quote_lookup:${quoteId}`
        );
        if (resolved) {
          return {
            ...resolved,
            sourceReason: `${resolved.sourceReason}:canonical_quote_lookup`,
          };
        }
      }

      const mappingLeadId = lookup?.mappingLeadId || "";
      const mappingResolved = await tryLeadId(
        mappingLeadId,
        `quote_deal_links:${quoteId}`
      );
      if (mappingResolved) return mappingResolved;

      const deterministicResolved = await tryLeadId(
        buildQuoteCreatedLeadId(quoteId),
        `deterministic_quote_lead:${quoteId}`
      );
      if (deterministicResolved) return deterministicResolved;
    }

    for (const quoteNo of quoteNos) {
      const localLead =
        crmLeadRecords.find((lead) => doesCrmLeadReferenceQuote(lead, "", quoteNo)) ||
        null;
      if (localLead?.id) {
        const resolved = await tryLeadId(localLead.id, `local_quote_no:${quoteNo}`);
        if (resolved) return resolved;
      }

      if (!workspaceUid) continue;
      try {
        const normalizedQuoteNo = normalizeSearchText(quoteNo);
        const crmLeadSnap = await getDocs(
          firestoreQuery(
            collection(db, "users", workspaceUid, "crm_leads"),
            firestoreWhere("searchKeywords", "array-contains", normalizedQuoteNo),
            firestoreLimit(10)
          ),
          {
            label: "resolveCrmDealByQuoteNo",
            cacheKey: `users/${workspaceUid}/crm_leads?quoteNo=${normalizedQuoteNo}&limit=10`,
          }
        );
        const matchingLeads = crmLeadSnap.docs
          .map((leadDoc) => ({ id: leadDoc.id, ...leadDoc.data() }))
          .filter((lead) => doesCrmLeadReferenceQuote(lead, "", quoteNo));
        const canonicalLead = chooseCanonicalQuoteDeal(matchingLeads);
        if (canonicalLead?.id) {
          const resolved = await tryLeadId(
            canonicalLead.id,
            `firestore_quote_no_scan:${quoteNo}`
          );
          if (resolved) return resolved;
        }
      } catch (error) {
        if (isDevRuntime) {
          console.error("CRM details quote number scan failed", error, {
            quoteNo,
            path: `users/${workspaceUid}/crm_leads`,
          });
        }
      }
    }

    return {
      leadId: "",
      lead: null,
      sourceReason: quoteIds.length || quoteNos.length ? "quote_match_not_found" : "no_identifier",
    };
  },
  [crmLeadRecords, fetchCrmLeadForDetails, findExistingDealForQuote, workspaceUid]
);

const openCrmDealDetails = useCallback(
  async (record = {}, sourceOrOptions = "unknown", maybeOptions = {}) => {
    const source =
      typeof sourceOrOptions === "string"
        ? sourceOrOptions
        : sourceOrOptions?.source || sourceOrOptions?.crmView || "unknown";
    const options =
      typeof sourceOrOptions === "string" ? maybeOptions || {} : sourceOrOptions || {};
    const clickedRecordId = String(record?.id || record?.leadId || record?.crmLeadId || "");
    const customerName =
      record?.customerName ||
      record?.customerLabel ||
      record?.crmContact?.name ||
      record?.contact?.name ||
      "";
    const quoteNo =
      record?.quoteNo ||
      record?.primaryQuoteNo ||
      record?.primaryLinkedQuote?.quoteNo ||
      "";

    if (isDevRuntime) {
      console.log("OPEN CRM DETAILS START", {
        source,
        clickedRecordId,
        customerName,
        quoteNo,
        record,
      });
    }

    try {
      const resolved = await resolveCrmDealForDetails(record);

      if (isDevRuntime) {
        console.log("OPEN CRM DETAILS RESOLVED", {
          source,
          clickedRecordId,
          resolvedLeadId: resolved?.leadId || "",
          sourceReason: resolved?.sourceReason || "",
          customerName:
            resolved?.lead?.customerName ||
            resolved?.lead?.customerLabel ||
            resolved?.lead?.crmContact?.name ||
            customerName,
          quoteNo:
            resolved?.lead?.quoteNo ||
            resolved?.lead?.primaryLinkedQuote?.quoteNo ||
            quoteNo,
        });
      }

      if (!resolved?.leadId || !resolved?.lead) {
        setCrmDetailsOpenDebug({
          lastSource: source,
          lastRecordId: clickedRecordId,
          lastResolvedLeadId: "",
          lastResolveReason: resolved?.sourceReason || "no_lead",
          lastCustomerName: customerName,
          lastQuoteNo: quoteNo,
          lastError: "No CRM lead id could be resolved for this record.",
        });
        console.error("OPEN CRM DETAILS FAILED: no leadId", {
          source,
          record,
          resolved,
        });
        notify("Could not open deal details. Please refresh and try again.", "error");
        return false;
      }

      const dealForDetails = {
        ...resolved.lead,
        id: resolved.leadId,
        crmRecordType: "lead",
      };
      const nextDraft = buildCrmDetailDraftFromDeal(dealForDetails);
      if (!nextDraft) {
        throw new Error("Could not build CRM details draft for selected lead.");
      }

      selectedCrmDealCacheRef.current = dealForDetails;
      selectedCrmDealIdRef.current = resolved.leadId;
      crmDetailDraftDealIdRef.current = resolved.leadId;
      crmDetailDraftDirtyRef.current = false;
      setSelectedCrmDealId(resolved.leadId);
      setCrmDetailDraft(nextDraft);
      markExecutionDetailsBaseline(nextDraft, "saved");
      setCrmDetailsSavedAt(null);
      setCrmDetailsOpenDebug({
        lastSource: source,
        lastRecordId: clickedRecordId,
        lastResolvedLeadId: resolved.leadId,
        lastResolveReason: resolved.sourceReason || "",
        lastCustomerName:
          dealForDetails.customerName ||
          dealForDetails.customerLabel ||
          dealForDetails.crmContact?.name ||
          customerName,
        lastQuoteNo:
          dealForDetails.quoteNo ||
          dealForDetails.primaryLinkedQuote?.quoteNo ||
          quoteNo,
        lastError: "",
      });
      setCrmView((prev) => options.crmView || prev);
      setIsCrmDrawerPinned(true);
      setIsCrmDrawerHovered(false);

      if (isDevRuntime) {
        console.log("OPEN CRM DETAILS APPLIED", {
          leadId: resolved.leadId,
          drawerOpen: true,
          customerName:
            dealForDetails.customerName ||
            dealForDetails.customerLabel ||
            dealForDetails.crmContact?.name ||
            "",
          sourceReason: resolved.sourceReason,
        });
      }

      return true;
    } catch (error) {
      setCrmDetailsOpenDebug({
        lastSource: source,
        lastRecordId: clickedRecordId,
        lastResolvedLeadId: "",
        lastResolveReason: "open_failed",
        lastCustomerName: customerName,
        lastQuoteNo: quoteNo,
        lastError: error?.message || String(error),
      });
      console.error("OPEN CRM DETAILS FAILED", error, { source, record });
      notify("Could not open deal details. Please refresh and try again.", "error");
      return false;
    }
  },
  [
    buildCrmDetailDraftFromDeal,
    markExecutionDetailsBaseline,
    notify,
    resolveCrmDealForDetails,
  ]
);

useEffect(() => {
  if (!workspaceUid || !selectedCrmDealId || selectedCrmDealFromRecords) return undefined;

  const cachedDeal = selectedCrmDealCacheRef.current;
  if (cachedDeal && String(cachedDeal.id || "") === String(selectedCrmDealId)) {
    return undefined;
  }

  let cancelled = false;
  fetchCrmLeadForDetails(selectedCrmDealId, "selected_id_rehydrate").then((resolved) => {
    if (cancelled || !resolved?.leadId || !resolved?.lead) return;

    const dealForDetails = {
      ...resolved.lead,
      id: resolved.leadId,
      crmRecordType: "lead",
    };
    const nextDraft = buildCrmDetailDraftFromDeal(dealForDetails);
    selectedCrmDealCacheRef.current = dealForDetails;
    selectedCrmDealIdRef.current = resolved.leadId;
    if (!crmDetailDraftDirtyRef.current && nextDraft) {
      crmDetailDraftDealIdRef.current = resolved.leadId;
      setCrmDetailDraft(nextDraft);
      markExecutionDetailsBaseline(nextDraft, "saved");
    }
    setCrmDetailsOpenDebug((prev) => ({
      ...prev,
      lastResolvedLeadId: resolved.leadId,
      lastResolveReason: resolved.sourceReason || "selected_id_rehydrate",
      lastError: "",
    }));
  });

  return () => {
    cancelled = true;
  };
}, [
  buildCrmDetailDraftFromDeal,
  fetchCrmLeadForDetails,
  markExecutionDetailsBaseline,
  selectedCrmDealFromRecords,
  selectedCrmDealId,
  workspaceUid,
]);

const executionWorkspaceTemplateOptions = useMemo(
  () =>
    executionTemplateCatalog.filter(
      (template) => String(template.name || "").trim() && String(template.itemType || "").trim()
    ),
  [executionTemplateCatalog]
);

const filteredExecutionTemplates = useMemo(() => {
  const queryText = executionTemplateSearch.trim().toLowerCase();

  return executionTemplateCatalog
    .filter((template) => {
      if (!queryText) return true;

      return [
        template.name,
        template.itemType,
        ...(Array.isArray(template.fields)
          ? template.fields.flatMap((field) => [field.key, field.defaultValue])
          : []),
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(queryText));
    })
    .sort((a, b) => {
      if (executionTemplateSortBy === "updated") {
        return Number(toMillis(b.updatedAt) || 0) - Number(toMillis(a.updatedAt) || 0);
      }

      if (executionTemplateSortBy === "oldest") {
        return Number(toMillis(a.createdAt) || 0) - Number(toMillis(b.createdAt) || 0);
      }

      return String(a.name || "").localeCompare(String(b.name || ""));
    });
}, [executionTemplateCatalog, executionTemplateSearch, executionTemplateSortBy]);

const executionWorkspaceSourceOptions = useMemo(() => {
  const values = new Set();

  crmDeals
    .filter((deal) => isGeneralExecutionEligibleDeal(deal))
    .forEach((deal) => {
    const sourceValue = String(deal.source || deal.crmContact?.source || "").trim();
    if (sourceValue) {
      values.add(sourceValue);
    }
  });

  return Array.from(values).sort((a, b) => a.localeCompare(b));
}, [crmDeals]);

const executionWorkspaceStageOptions = useMemo(() => {
  const values = new Map();

  crmDeals
    .filter((deal) => isGeneralExecutionEligibleDeal(deal))
    .forEach((deal) => {
    const stageKey = `${deal.pipelineType}::${deal.crmStageId}`;
    if (!deal.crmStageId || values.has(stageKey)) return;

    values.set(stageKey, {
      value: stageKey,
      label: `${getQuoteTypeLabel(deal.pipelineType)} | ${deal.crmStageLabel}`,
    });
  });

  return Array.from(values.values()).sort((a, b) => a.label.localeCompare(b.label));
}, [crmDeals]);

const executionWorkspaceDeals = useMemo(
  () =>
    crmDeals
      .filter((deal) => isGeneralExecutionEligibleDeal(deal))
      .sort(
        (a, b) =>
          Number(b.updatedMillis || b.createdMillis || 0) -
          Number(a.updatedMillis || a.createdMillis || 0)
      ),
  [crmDeals]
);

const executionWorkspaceFilteredDeals = useMemo(() => {
  const queryText = executionWorkspaceSearch.trim().toLowerCase();

  return executionWorkspaceDeals
    .filter((deal) => {
      if (!queryText) return true;

      const itemTypes = normalizeExecutionSpecs(deal.executionSpecs || [])
        .map((item) => String(item.itemType || "").trim())
        .filter(Boolean)
        .join(" ");

      return [
        deal.crmTitle,
        deal.customerLabel,
        deal.quoteNo,
        deal.primaryLinkedQuote?.quoteNo,
        deal.crmContact?.name,
        deal.crmContact?.phone,
        deal.crmContact?.email,
        deal.source,
        deal.crmContact?.source,
        deal.companyOrProject,
        deal.crmStageLabel,
        itemTypes,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(queryText));
    })
    .filter((deal) => {
      if (executionWorkspaceSourceFilter === "all") return true;
      return String(deal.source || deal.crmContact?.source || "").trim() === executionWorkspaceSourceFilter;
    })
    .filter((deal) => {
      if (executionWorkspaceStageFilter === "all") return true;
      return `${deal.pipelineType}::${deal.crmStageId}` === executionWorkspaceStageFilter;
    })
    .filter((deal) => {
      if (executionWorkspaceItemTypeFilter === "all") return true;
      return normalizeExecutionSpecs(deal.executionSpecs || []).some(
        (item) => String(item.itemType || "").trim() === executionWorkspaceItemTypeFilter
      );
    })
    .filter((deal) => {
      if (executionWorkspaceAttachmentFilter === "all") return true;

      const hasAttachments = normalizeExecutionSpecs(deal.executionSpecs || []).some(
        (item) => normalizeExecutionSpecAttachments(item.attachments || []).length > 0
      );

      return executionWorkspaceAttachmentFilter === "with"
        ? hasAttachments
        : !hasAttachments;
    })
    .sort((a, b) => {
      if (executionWorkspaceSortBy === "client") {
        return String(a.customerLabel || a.crmContact?.name || "").localeCompare(
          String(b.customerLabel || b.crmContact?.name || "")
        );
      }

      if (executionWorkspaceSortBy === "oldest") {
        return Number(a.updatedMillis || a.createdMillis || 0) - Number(b.updatedMillis || b.createdMillis || 0);
      }

      return Number(b.updatedMillis || b.createdMillis || 0) - Number(a.updatedMillis || a.createdMillis || 0);
    });
}, [
  executionWorkspaceAttachmentFilter,
  executionWorkspaceDeals,
  executionWorkspaceItemTypeFilter,
  executionWorkspaceSearch,
  executionWorkspaceSortBy,
  executionWorkspaceSourceFilter,
  executionWorkspaceStageFilter,
]);

const openExecutionWorkspaceForDeal = useCallback(
  async (dealLike = null) => {
    const sourceDeal = dealLike || selectedCrmDeal || null;

    if (!sourceDeal) {
      setRoutedTab("executionSpecs");
      return;
    }

    if (!isGeneralExecutionEligibleDeal(sourceDeal)) {
      notify(
        "Execution Details is available for General Quotations only.",
        "warning"
      );
      return;
    }

    setExecutionWorkspaceOpeningDealId(sourceDeal.id);

    try {
      const sourceExecutionSpecs =
        selectedCrmDeal?.id === sourceDeal.id
          ? serializeExecutionSpecDraftItems(
              crmDetailDraft?.executionSpecs || sourceDeal.executionSpecs || []
            )
          : normalizeExecutionSpecs(sourceDeal.executionSpecs || []);

      const canonicalDeal =
        sourceDeal.crmRecordType === "quote"
          ? await ensureCrmLeadForExecutionSpecs(
              sourceDeal,
              sourceExecutionSpecs
            )
          : sourceDeal;

      if (!canonicalDeal?.id) {
        notify("Could not open Execution Details for this deal. Please try again.", "error");
        return;
      }

      setSelectedCrmDealId(canonicalDeal?.id || sourceDeal.id || "");
      setSelectedExecutionItemId("");
      setRoutedTab("executionSpecs");
    } catch (error) {
      console.error("Failed to open execution workspace:", error);
      notify("Could not open Execution Details. Please try again.", "error");
    } finally {
      setExecutionWorkspaceOpeningDealId("");
    }
  },
  [
    crmDetailDraft,
    ensureCrmLeadForExecutionSpecs,
    notify,
    selectedCrmDeal,
    serializeExecutionSpecDraftItems,
    setRoutedTab,
  ]
);

const returnToCrmFromExecutionWorkspace = useCallback(
  (dealId = selectedCrmDealId) => {
    if (dealId) {
      setSelectedCrmDealId(dealId);
    }

    setCrmView("list");
    setIsCrmDrawerPinned(true);
    setIsCrmDrawerHovered(false);
    setRoutedTab("crm");
  },
  [selectedCrmDealId, setRoutedTab]
);

const resolveCrmReminderFollowUp = useCallback(
  (reminder = {}) => {
    if (reminder?.reminderType !== CRM_REMINDER_TYPE_FOLLOW_UP) {
      return { deal: null, followUp: null, followUps: [] };
    }

    const candidateMap = new Map();
    const addCandidate = (deal) => {
      if (!deal?.id || candidateMap.has(deal.id)) return;
      candidateMap.set(deal.id, deal);
    };

    crmDeals.forEach((deal) => {
      const linkedQuotes = normalizeCrmLinkedQuotes(deal);
      const matchesDirectId =
        String(deal.id || "") === String(reminder.dealId || "") ||
        String(deal.id || "") === String(reminder.leadId || "") ||
        String(deal.id || "") === String(reminder.quoteId || "");
      const matchesQuoteLink = linkedQuotes.some(
        (linkedQuote) =>
          (reminder.dealId &&
            String(linkedQuote.quoteId || "") === String(reminder.dealId || "")) ||
          (reminder.quoteId &&
            String(linkedQuote.quoteId || "") === String(reminder.quoteId || "")) ||
          (reminder.quoteNo &&
            String(linkedQuote.quoteNo || "") === String(reminder.quoteNo || ""))
      );

      if (matchesDirectId || matchesQuoteLink) {
        addCandidate(deal);
      }
    });

    crmLeadRecords.forEach((lead) => {
      const matchingDeal = crmDeals.find((deal) => deal.id === lead.id);
      if (matchingDeal) {
        return;
      }

      const linkedQuotes = normalizeCrmLinkedQuotes(lead);
      const matchesDirectId =
        String(lead.id || "") === String(reminder.dealId || "") ||
        String(lead.id || "") === String(reminder.leadId || "");
      const matchesQuoteLink = linkedQuotes.some(
        (linkedQuote) =>
          (reminder.dealId &&
            String(linkedQuote.quoteId || "") === String(reminder.dealId || "")) ||
          (reminder.quoteId &&
            String(linkedQuote.quoteId || "") === String(reminder.quoteId || "")) ||
          (reminder.quoteNo &&
            String(linkedQuote.quoteNo || "") === String(reminder.quoteNo || ""))
      );

      if (matchesDirectId || matchesQuoteLink) {
        addCandidate({
          ...lead,
          crmRecordType: "lead",
          crmTitle: lead.title || lead.companyOrProject || lead.contact?.name || "CRM Lead",
          crmContact: lead.contact || {},
          customerLabel:
            lead.contact?.name || lead.companyOrProject || lead.title || "Unnamed Lead",
        });
      }
    });

    crmDeals.forEach(addCandidate);

    for (const deal of candidateMap.values()) {
      const followUps = normalizeCrmFollowUps(deal.followUps || deal.crm?.followUps);
      const followUp = followUps.find((item) =>
        doesCrmReminderMatchFollowUp(reminder, item)
      );

      if (followUp) {
        return { deal, followUp, followUps };
      }
    }

    return { deal: null, followUp: null, followUps: [] };
  },
  [crmDeals, crmLeadRecords]
);

const dueCrmReminders = useMemo(() => {
  if (!canAccessCrm) return [];

  return crmReminderRecords
    .filter(
      (reminder) =>
        reminder.active === true ||
        reminder.enabled === true ||
        reminder.reminderEnabled === true
    )
    .filter((reminder) => reminder.status === CRM_REMINDER_STATUS_ACTIVE)
    .filter((reminder) => {
      const nextTriggerMillis = safeTimeMillis(reminder.nextTriggerAt);
      return nextTriggerMillis > 0 && nextTriggerMillis <= crmReminderNow;
    })
    .sort((a, b) => {
      const aMillis = safeTimeMillis(a.nextTriggerAt);
      const bMillis = safeTimeMillis(b.nextTriggerAt);
      return aMillis - bMillis;
    });
}, [canAccessCrm, crmReminderNow, crmReminderRecords]);

useEffect(() => {
  dueCrmReminders.forEach((reminder) => {
    if (reminder?.id) {
      globalReminderShownIdsRef.current.add(reminder.id);
    }
  });
}, [dueCrmReminders]);

const dailyTaskDateKey = dailyTaskDate || todayISO();

const dailyTaskSourceItems = useMemo(() => {
  const todayKey = todayISO();
  const toDateKeyFromOffset = (offset) => {
    const date = parseDateInputValue(todayKey) || new Date();
    date.setDate(date.getDate() + offset);
    return toDateInputValue(date);
  };
  const tomorrowKey = toDateKeyFromOffset(1);
  const weekEndKey = toDateKeyFromOffset(6);
  const customStartKey = dailyTaskRangeStart || dailyTaskDateKey;
  const customEndKey = dailyTaskRangeEnd || customStartKey;
  const nowMillis = Date.now();
  const tasks = [];

  const getDealBasics = (deal) => {
    const linkedQuotes = normalizeCrmLinkedQuotes(deal);
    const primaryLinkedQuote = getPrimaryCrmLinkedQuote(linkedQuotes);
    const leadId =
      deal.crmRecordType === "lead"
        ? deal.id
        : String(deal.crm?.leadId || deal.leadId || "").trim();
    const quoteId =
      deal.crmRecordType === "quote"
        ? deal.id
        : String(primaryLinkedQuote?.quoteId || deal.quoteId || "").trim();
    const quoteNo =
      deal.crmRecordType === "quote"
        ? deal.quoteNo || ""
        : primaryLinkedQuote?.quoteNo || deal.quoteNo || "";
    const linkedCustomer = customerRecords.find(
      (customer) =>
        customer.id &&
        String(customer.id) === String(deal.customerId || deal.crm?.customerId || "")
    );
    const primarySiteLocation =
      getPrimarySiteLocation(deal) || getPrimarySiteLocation(linkedCustomer);
    const locationUrl = primarySiteLocation
      ? buildSiteLocationOpenUrl(primarySiteLocation)
      : "";

    return {
      deal,
      dealId: deal.id,
      leadId,
      quoteId,
      quoteNo,
      quoteType: deal.pipelineType || deal.quoteTypeResolved || deal.quoteType || "",
      customerName:
        deal.customerLabel ||
        deal.crmContact?.name ||
        deal.contact?.name ||
        deal.companyOrProject ||
        deal.crmTitle ||
        "Unnamed Customer",
      assignedToUserId: deal.assignedToUserId || "",
      assignedTo:
        deal.assignedToNameSnapshot ||
        deal.assignedTo ||
        deal.crm?.assignedToNameSnapshot ||
        "Unassigned",
      source: deal.source || deal.crmContact?.source || "",
      address:
        primarySiteLocation?.addressText ||
        deal.crmContact?.address ||
        deal.contact?.address ||
        deal.address ||
        "",
      siteLocation: primarySiteLocation,
      locationLabel: primarySiteLocation?.label || "",
      locationAddress: primarySiteLocation?.addressText || "",
      locationUrl,
      stageLabel: deal.crmStageLabel || deal.stageNameSnapshot || "",
    };
  };

  const pushTask = (task) => {
    const dueMillis = safeTimeMillis(task.dueDateTime || task.dueDate);
    const completedDateKey = toDateInputValue(task.completedAt);
    const dueDateKey = toDateInputValue(task.dueDate);
    const status = task.status === CRM_FOLLOW_UP_STATUS_DONE ? "completed" : "pending";
    const isOverdue =
      status !== "completed" && dueMillis > 0 && dueMillis < nowMillis;
    let shouldShow = false;

    if (dailyTaskDateMode === "overdue") {
      shouldShow = isOverdue;
    } else if (dailyTaskDateMode === "tomorrow") {
      shouldShow = dueDateKey === tomorrowKey || completedDateKey === tomorrowKey;
    } else if (dailyTaskDateMode === "week") {
      shouldShow =
        (dueDateKey >= todayKey && dueDateKey <= weekEndKey) ||
        (completedDateKey >= todayKey && completedDateKey <= weekEndKey) ||
        isOverdue;
    } else if (dailyTaskDateMode === "custom") {
      const start = customStartKey <= customEndKey ? customStartKey : customEndKey;
      const end = customStartKey <= customEndKey ? customEndKey : customStartKey;
      shouldShow =
        (dueDateKey >= start && dueDateKey <= end) ||
        (completedDateKey >= start && completedDateKey <= end);
    } else {
      shouldShow =
        dueDateKey === todayKey || completedDateKey === todayKey || isOverdue;
    }

    if (!shouldShow) return;

    tasks.push({
      ...task,
      status,
      dueMillis,
      isOverdue,
      typeLabel: DAILY_TASK_TYPE_LABELS[task.sourceType] || "Task",
    });
  };

  crmDeals.forEach((deal) => {
    const basics = getDealBasics(deal);
    const reminderConfigs = normalizeCrmReminderConfigs(deal.reminderConfigs, {
      nextActionDate: deal.nextActionDate,
      installationDate: deal.installationDate,
    });
    const followUps = normalizeCrmFollowUps(deal.followUps || deal.crm?.followUps);

    followUps.forEach((followUp) => {
      const dueDateTime =
        combineReminderDateTime(followUp.dueDate, followUp.dueTime) ||
        parseDateInputValue(followUp.dueDate);

      pushTask({
        id: `followup:${deal.id}:${followUp.id}`,
        sourceId: followUp.id,
        reminderId: followUp.reminderId || followUp.id,
        sourceType: DAILY_TASK_SOURCE_FOLLOW_UP,
        title: followUp.title || "Follow-up",
        description: followUp.description || "",
        dueDate: parseDateInputValue(followUp.dueDate),
        dueDateKey: followUp.dueDate || "",
        dueTime: followUp.dueTime || DEFAULT_CRM_REMINDER_TIME,
        dueDateTime,
        completedAt: followUp.completedAt || null,
        priority: followUp.priority || CRM_FOLLOW_UP_PRIORITY_MEDIUM,
        original: followUp,
        ...basics,
        status:
          followUp.status === CRM_FOLLOW_UP_STATUS_DONE
            ? CRM_FOLLOW_UP_STATUS_DONE
            : CRM_FOLLOW_UP_STATUS_PENDING,
      });
    });

    if (followUps.length === 0 && (deal.nextAction || deal.nextActionDate)) {
      const taskConfig = reminderConfigs[CRM_REMINDER_TYPE_TASK_DUE] || {};
      const dueDate = deal.nextActionDate || parseDateInputValue(taskConfig.reminderDate);
      const dueTime = taskConfig.reminderTime || DEFAULT_CRM_REMINDER_TIME;
      const dueDateTime =
        combineReminderDateTime(toDateInputValue(dueDate), dueTime) || dueDate;

      pushTask({
        id: `deal-action:${deal.id}`,
        sourceId: `${deal.id}__${CRM_REMINDER_TYPE_TASK_DUE}`,
        sourceType: DAILY_TASK_SOURCE_DEAL_ACTION,
        title: deal.nextAction || "Deal action",
        description: deal.crmNotes || deal.notes || "",
        dueDate,
        dueDateKey: toDateInputValue(dueDate),
        dueTime,
        dueDateTime,
        completedAt: taskConfig.completedAt || null,
        priority: CRM_FOLLOW_UP_PRIORITY_MEDIUM,
        original: taskConfig,
        ...basics,
        status:
          taskConfig.status === CRM_REMINDER_STATUS_DONE
            ? CRM_FOLLOW_UP_STATUS_DONE
            : CRM_FOLLOW_UP_STATUS_PENDING,
      });
    }

    const installationDate =
      deal.installationDate ||
      deal.scheduledInstallationDate ||
      deal.crm?.installationDate ||
      null;
    if (installationDate) {
      const installConfig = reminderConfigs[CRM_REMINDER_TYPE_INSTALLATION] || {};
      const installDateKey = toDateInputValue(installationDate);
      const installTime =
        installConfig.reminderTime ||
        (installationDate instanceof Date
          ? `${String(installationDate.getHours()).padStart(2, "0")}:${String(
              installationDate.getMinutes()
            ).padStart(2, "0")}`
          : DEFAULT_CRM_REMINDER_TIME);
      const dueDateTime =
        combineReminderDateTime(installDateKey, installTime) || installationDate;

      pushTask({
        id: `installation:${deal.id}`,
        sourceId: `${deal.id}__${CRM_REMINDER_TYPE_INSTALLATION}`,
        sourceType: DAILY_TASK_SOURCE_INSTALLATION,
        title: "Installation",
        description: basics.locationAddress || basics.address || deal.crmNotes || "",
        dueDate: installationDate,
        dueDateKey: installDateKey,
        dueTime: installTime,
        dueDateTime,
        completedAt: installConfig.completedAt || null,
        priority: CRM_FOLLOW_UP_PRIORITY_HIGH,
        original: installConfig,
        ...basics,
        status:
          installConfig.status === CRM_REMINDER_STATUS_DONE
            ? CRM_FOLLOW_UP_STATUS_DONE
            : CRM_FOLLOW_UP_STATUS_PENDING,
      });
    }

    flattenChecklistNodes(deal.checklistItems || []).forEach((checklistItem) => {
      const checklistDueDate =
        checklistItem.dueDate ||
        checklistItem.dueAt ||
        checklistItem.nextActionDate ||
        checklistItem.reminderDate ||
        "";
      if (!checklistDueDate) return;

      const checklistDueKey = toDateInputValue(checklistDueDate);
      const checklistDueTime =
        parseTimeInputValue(checklistItem.dueTime || checklistItem.reminderTime || "") ||
        DEFAULT_CRM_REMINDER_TIME;
      const checklistDueDateTime =
        combineReminderDateTime(checklistDueKey, checklistDueTime) ||
        parseDateInputValue(checklistDueKey);

      pushTask({
        id: `checklist:${deal.id}:${checklistItem.id}`,
        sourceId: checklistItem.id,
        sourceType: DAILY_TASK_SOURCE_CHECKLIST,
        title: checklistItem.title || checklistItem.label || "Checklist Item",
        description: checklistItem.pathLabel || checklistItem.notes || "",
        dueDate: parseDateInputValue(checklistDueKey),
        dueDateKey: checklistDueKey,
        dueTime: checklistDueTime,
        dueDateTime: checklistDueDateTime,
        completedAt: checklistItem.completedAt || null,
        priority: checklistItem.priority || CRM_FOLLOW_UP_PRIORITY_MEDIUM,
        original: checklistItem,
        ...basics,
        status:
          checklistItem.done === true
            ? CRM_FOLLOW_UP_STATUS_DONE
            : CRM_FOLLOW_UP_STATUS_PENDING,
      });
    });
  });

  return tasks.sort((a, b) => {
    if (a.status !== b.status) return a.status === "pending" ? -1 : 1;
    if (a.isOverdue !== b.isOverdue) return a.isOverdue ? -1 : 1;
    return Number(a.dueMillis || 0) - Number(b.dueMillis || 0);
  });
}, [
  combineReminderDateTime,
  crmDeals,
  customerRecords,
  dailyTaskDateKey,
  dailyTaskDateMode,
  dailyTaskRangeEnd,
  dailyTaskRangeStart,
]);

const dailyTaskAssignedOptions = useMemo(() => {
  const options = new Map();

  dailyTaskSourceItems.forEach((task) => {
    if (task.assignedToUserId) {
      options.set(task.assignedToUserId, {
        value: task.assignedToUserId,
        label: task.assignedTo || task.assignedToUserId,
      });
    } else if (task.assignedTo && task.assignedTo !== "Unassigned") {
      options.set(task.assignedTo, {
        value: task.assignedTo,
        label: task.assignedTo,
      });
    }
  });

  teamMembers.forEach((member) => {
    const value = member.uid || member.id || member.email || "";
    if (!value) return;
    options.set(value, {
      value,
      label: member.name || member.email || value,
    });
  });

  return Array.from(options.values()).sort((a, b) => a.label.localeCompare(b.label));
}, [dailyTaskSourceItems, teamMembers]);

const dailyTaskSourceOptions = useMemo(() => {
  const values = new Set();
  dailyTaskSourceItems.forEach((task) => {
    const sourceValue = String(task.source || "").trim();
    if (sourceValue) values.add(sourceValue);
  });
  return Array.from(values).sort((a, b) => a.localeCompare(b));
}, [dailyTaskSourceItems]);

const filteredDailyTasks = useMemo(() => {
  const queryText = deferredDailyTaskSearch.trim().toLowerCase();

  return dailyTaskSourceItems
    .filter((task) => {
      if (dailyTaskAssignedFilter === "all") return true;
      return (
        task.assignedToUserId === dailyTaskAssignedFilter ||
        task.assignedTo === dailyTaskAssignedFilter
      );
    })
    .filter((task) =>
      dailyTaskTypeFilter === "all" ? true : task.sourceType === dailyTaskTypeFilter
    )
    .filter((task) =>
      dailyTaskPriorityFilter === "all" ? true : task.priority === dailyTaskPriorityFilter
    )
    .filter((task) =>
      dailyTaskSourceFilter === "all" ? true : task.source === dailyTaskSourceFilter
    )
    .filter((task) =>
      dailyTaskQuoteTypeFilter === "all"
        ? true
        : task.quoteType === dailyTaskQuoteTypeFilter
    )
    .filter((task) => {
      if (dailyTaskStatusFilter === "all") return true;
      if (dailyTaskStatusFilter === "overdue") return task.isOverdue;
      if (dailyTaskStatusFilter === "completed") return task.status === "completed";
      return task.status === "pending" && !task.isOverdue;
    })
    .filter((task) => {
      if (!queryText) return true;
      return [
        task.title,
        task.description,
        task.customerName,
        task.quoteNo,
        task.source,
        task.assignedTo,
        task.stageLabel,
        task.locationLabel,
        task.locationAddress,
        task.siteLocation?.notes,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(queryText));
    });
}, [
  dailyTaskAssignedFilter,
  dailyTaskPriorityFilter,
  dailyTaskQuoteTypeFilter,
  dailyTaskSourceFilter,
  dailyTaskSourceItems,
  dailyTaskStatusFilter,
  dailyTaskTypeFilter,
  deferredDailyTaskSearch,
]);

const dailyTaskGroups = useMemo(
  () => ({
    overdue: filteredDailyTasks.filter(
      (task) => task.status !== "completed" && task.isOverdue
    ),
    today: filteredDailyTasks.filter(
      (task) =>
        task.status !== "completed" &&
        !task.isOverdue &&
        task.sourceType !== DAILY_TASK_SOURCE_INSTALLATION
    ),
    installations: filteredDailyTasks.filter(
      (task) =>
        task.status !== "completed" &&
        !task.isOverdue &&
        task.sourceType === DAILY_TASK_SOURCE_INSTALLATION
    ),
    completed: filteredDailyTasks.filter((task) => task.status === "completed"),
  }),
  [filteredDailyTasks]
);

const dailyTaskSummary = useMemo(
  () => ({
    total: filteredDailyTasks.length,
    pending: filteredDailyTasks.filter((task) => task.status !== "completed").length,
    overdue: filteredDailyTasks.filter((task) => task.isOverdue).length,
    installations: filteredDailyTasks.filter(
      (task) => task.sourceType === DAILY_TASK_SOURCE_INSTALLATION
    ).length,
    completed: filteredDailyTasks.filter((task) => task.status === "completed").length,
  }),
  [filteredDailyTasks]
);

const selectedDailyTask = useMemo(
  () =>
    filteredDailyTasks.find((task) => task.id === selectedDailyTaskId) ||
    filteredDailyTasks[0] ||
    null,
  [filteredDailyTasks, selectedDailyTaskId]
);

const crmSummary = useMemo(() => {
  const openDeals = crmDeals.filter(
    (deal) => !deal.isCrmClosed && !deal.isCrmCancelled
  );
  const newDeals = crmDeals.filter((deal) => {
    if (!deal.createdMillis) return false;
    return isDateInPeriod(new Date(deal.createdMillis), periodType, customRange);
  });
  const overdueFollowUps = openDeals.filter(
    (deal) => deal.nextActionMillis && deal.nextActionMillis < Date.now()
  );
  const stalledDeals = openDeals.filter(
    (deal) =>
      deal.updatedMillis &&
      Date.now() - deal.updatedMillis > 1000 * 60 * 60 * 24 * 10
  );
  const completedDeals = crmDeals.filter((deal) => deal.isCrmClosed);
  const lostDeals = crmDeals.filter((deal) => deal.isCrmCancelled);
  const checklistTotals = openDeals.reduce(
    (sum, deal) => {
      sum.completed += deal.checklistCompleted;
      sum.total += deal.checklistTotal;
      return sum;
    },
    { completed: 0, total: 0 }
  );

  return {
    activeDeals: openDeals.length,
    newDeals: newDeals.length,
    overdueFollowUps: overdueFollowUps.length,
    stalledDeals: stalledDeals.length,
    pipelineValue: openDeals.reduce(
      (sum, deal) => sum + Number(deal.expectedValue || 0),
      0
    ),
    wonDeals: completedDeals.length,
    lostDeals: lostDeals.length,
    checklistCompleted: checklistTotals.completed,
    checklistTotal: checklistTotals.total,
    checklistCompletion: checklistTotals.total
      ? Math.round((checklistTotals.completed / checklistTotals.total) * 100)
      : 0,
  };
}, [crmDeals, customRange, periodType]);

const crmStageOverview = useMemo(() => {
  if (crmScope === CRM_SCOPE_ALL) {
    const stageMap = new Map();

    crmScopeDeals.forEach((deal) => {
      const stageMeta = getCrmStage(
        crmPipelines,
        deal.pipelineType,
        deal.crmStageId
      );
      const key = `${deal.pipelineType}::${stageMeta?.id || deal.crmStageId}`;
      const current = stageMap.get(key) || {
        id: key,
        quoteType: deal.pipelineType,
        stageId: stageMeta?.id || deal.crmStageId,
        stageLabel: stageMeta?.label || deal.crmStageLabel,
        fullLabel: `${getQuoteTypeLabel(deal.pipelineType)} | ${stageMeta?.label || deal.crmStageLabel}`,
        label: `${getQuoteTypeLabel(deal.pipelineType)} | ${stageMeta?.label || deal.crmStageLabel}`,
        color: stageMeta?.color || deal.crmStageColor || "#64748b",
        count: 0,
        value: 0,
      };

      current.count += 1;
      current.value += Number(deal.expectedValue || 0);
      stageMap.set(key, current);
    });

    return Array.from(stageMap.values()).sort((a, b) => b.count - a.count);
  }

  const pipeline = getCrmPipeline(crmPipelines, activeCrmPipelineType);

  return pipeline.stages.map((stage) => {
    const deals = crmScopeDeals.filter((deal) => deal.crmStageId === stage.id);

    return {
      ...stage,
      quoteType: activeCrmPipelineType,
      stageId: stage.id,
      stageLabel: stage.label,
      fullLabel: `${getQuoteTypeLabel(activeCrmPipelineType)} | ${stage.label}`,
      count: deals.length,
      value: deals.reduce((sum, deal) => sum + Number(deal.expectedValue || 0), 0),
    };
  });
}, [activeCrmPipelineType, crmPipelines, crmScope, crmScopeDeals]);

const crmBoardStages = useMemo(() => {
  const pipeline = getCrmPipeline(crmPipelines, activeCrmPipelineType);
  const knownIds = new Set(pipeline.stages.map((stage) => stage.id));
  const legacyStages = crmBoardDeals
    .filter((deal) => !knownIds.has(deal.crmStageId))
    .map((deal) => ({
      id: deal.crmStageId,
      label: deal.crmStageLabel,
      color: deal.crmStageColor,
      confirmsQuote: false,
      ordersMaterial: false,
      marksComplete: false,
      isCancelled: false,
      defaultChecklist: [],
    }));

  const dedupedLegacy = legacyStages.filter(
    (stage, index) =>
      legacyStages.findIndex((item) => item.id === stage.id) === index
  );

  return [...pipeline.stages, ...dedupedLegacy];
}, [activeCrmPipelineType, crmBoardDeals, crmPipelines]);

const crmTopClients = useMemo(() => {
  const clientMap = {};

  crmScopeDeals.forEach((deal) => {
    const key =
      deal.crmContact?.name ||
      deal.customerLabel ||
      "Unnamed Customer";
    if (!clientMap[key]) {
      clientMap[key] = {
        name: key,
        value: 0,
        count: 0,
        deals: [],
      };
    }

    clientMap[key].value += Number(deal.expectedValue || 0);
    clientMap[key].count += 1;
    clientMap[key].deals.push(deal);
  });

  return Object.values(clientMap)
    .sort((a, b) => {
      if (b.value !== a.value) return b.value - a.value;
      return b.count - a.count;
    })
}, [crmScopeDeals]);

const crmChecklistTrend = useMemo(
  () => [
    {
      name: "Complete",
      value: crmDeals.reduce((sum, deal) => sum + deal.checklistCompleted, 0),
      color: "#10b981",
    },
    {
      name: "Remaining",
      value: crmDeals.reduce(
        (sum, deal) =>
          sum + Math.max(0, deal.checklistTotal - deal.checklistCompleted),
        0
      ),
      color: "#cbd5e1",
    },
  ].filter((entry) => entry.value > 0),
  [crmDeals]
);

const crmOpenDeals = useMemo(
  () =>
    crmScopeDeals.filter(
      (deal) => !deal.isCrmClosed && !deal.isCrmCancelled
    ),
  [crmScopeDeals]
);

const crmOverdueDeals = useMemo(
  () =>
    crmOpenDeals
      .filter(
        (deal) => deal.nextActionMillis && deal.nextActionMillis < Date.now()
      )
      .sort((a, b) => a.nextActionMillis - b.nextActionMillis),
  [crmOpenDeals]
);

const crmStalledDeals = useMemo(
  () =>
    crmOpenDeals
      .filter(
        (deal) =>
          deal.updatedMillis &&
          Date.now() - deal.updatedMillis > 1000 * 60 * 60 * 24 * 10
      )
      .sort((a, b) => a.updatedMillis - b.updatedMillis),
  [crmOpenDeals]
);

const crmNewDealsInPeriod = useMemo(
  () =>
    crmScopeDeals
      .filter((deal) => {
        if (!deal.createdMillis) return false;
        return isDateInPeriod(new Date(deal.createdMillis), periodType, customRange);
      })
      .sort((a, b) => b.createdMillis - a.createdMillis),
  [crmScopeDeals, customRange, periodType]
);

const crmUpcomingInstallationDeals = useMemo(
  () =>
    crmScopeDeals
      .filter(
        (deal) =>
          !deal.isCrmCancelled &&
          deal.installationMillis &&
          deal.installationMillis >= todayStartMillis
      )
      .sort((a, b) => a.installationMillis - b.installationMillis),
  [crmScopeDeals, todayStartMillis]
);

const crmUpcomingDeals = useMemo(
  () => crmUpcomingInstallationDeals.slice(0, CRM_OVERVIEW_PREVIEW_LIMIT),
  [crmUpcomingInstallationDeals]
);

const crmStageFilterOptions = useMemo(
  () =>
    getStageFilterOptions(
      crmPipelines,
      crmScope === CRM_SCOPE_ALL ? "all" : crmScope
    ),
  [crmPipelines, crmScope]
);

const crmPipelineChecklistTrend = useMemo(
  () => [
    {
      name: "Complete",
      value: crmScopeDeals.reduce(
        (sum, deal) => sum + Number(deal.checklistCompleted || 0),
        0
      ),
      color: "#10b981",
    },
    {
      name: "Remaining",
      value: crmScopeDeals.reduce(
        (sum, deal) =>
          sum + Math.max(0, Number(deal.checklistTotal || 0) - Number(deal.checklistCompleted || 0)),
        0
      ),
      color: "#cbd5e1",
    },
  ].filter((entry) => entry.value > 0),
  [crmScopeDeals]
);

const crmPipelineSummary = useMemo(() => {
  const checklistTotals = crmOpenDeals.reduce(
    (sum, deal) => {
      sum.completed += deal.checklistCompleted;
      sum.total += deal.checklistTotal;
      return sum;
    },
    { completed: 0, total: 0 }
  );

  return {
    activeDeals: crmOpenDeals.length,
    newDeals: crmNewDealsInPeriod.length,
    overdue: crmOverdueDeals.length,
    stalled: crmStalledDeals.length,
    pipelineValue: crmOpenDeals.reduce(
      (sum, deal) => sum + Number(deal.expectedValue || 0),
      0
    ),
    completed: crmScopeDeals.filter((deal) => deal.isCrmClosed).length,
    cancelled: crmScopeDeals.filter((deal) => deal.isCrmCancelled).length,
    checklistCompleted: checklistTotals.completed,
    checklistTotal: checklistTotals.total,
    checklistCompletion: checklistTotals.total
      ? Math.round((checklistTotals.completed / checklistTotals.total) * 100)
      : 0,
  };
}, [crmNewDealsInPeriod.length, crmOpenDeals, crmOverdueDeals.length, crmScopeDeals, crmStalledDeals.length]);

const crmScopeLabel =
  crmScope === CRM_SCOPE_ALL ? "All Pipelines" : getQuoteTypeLabel(crmScope);

const crmScopeSubtitle =
  crmScope === CRM_SCOPE_ALL
    ? "Curtains and general quotation deals combined in one CRM view."
    : getCrmPipeline(crmPipelines, crmScope).label;

const crmRecentActivityDeals = useMemo(
  () => [...crmScopeDeals].sort((a, b) => b.updatedMillis - a.updatedMillis).slice(0, 5),
  [crmScopeDeals]
);

const crmQuoteTypeMix = useMemo(
  () =>
    [
      {
        key: "quoteType:curtains",
        quoteType: CURTAIN_QUOTE_TYPE,
        name: "Curtains",
        deals: crmScopeDeals.filter((deal) => deal.pipelineType === CURTAIN_QUOTE_TYPE),
        color: "#2563eb",
      },
      {
        key: "quoteType:general",
        quoteType: GENERAL_QUOTE_TYPE,
        name: "General Quotation",
        deals: crmScopeDeals.filter((deal) => deal.pipelineType === GENERAL_QUOTE_TYPE),
        color: "#8b5cf6",
      },
    ]
      .map((entry) => ({
        ...entry,
        count: entry.deals.length,
        value: entry.deals.reduce((sum, deal) => sum + Number(deal.expectedValue || 0), 0),
      }))
      .filter((entry) => entry.count > 0 || entry.value > 0),
  [crmScopeDeals]
);

const crmOverviewDrilldowns = useMemo(() => {
  const quoteTypeDrilldowns = crmQuoteTypeMix.reduce(
    (next, entry) => ({
      ...next,
      [entry.key]: {
        key: entry.key,
        label: `${entry.name} Deals`,
        description: `${entry.count} ${entry.name.toLowerCase()} deal${
          entry.count === 1 ? "" : "s"
        } contributing ${formatCurrencyDisplay(entry.value || 0, currency)}.`,
        deals: entry.deals,
        totalValue: entry.value,
      },
    }),
    {}
  );

  const clientDrilldowns = crmTopClients.reduce(
    (next, client) => ({
      ...next,
      [`client:${client.name}`]: {
        key: `client:${client.name}`,
        label: client.name,
        description: `${client.count} linked deal${client.count === 1 ? "" : "s"} for this client.`,
        deals: client.deals,
        totalValue: client.value,
      },
    }),
    {}
  );

  return {
    active: {
      key: "active",
      label: "Active Deals",
      deals: crmOpenDeals,
    },
    new: {
      key: "new",
      label: `New Deals ${dashboardPeriodLabel}`,
      deals: crmNewDealsInPeriod,
    },
    value: {
      key: "value",
      label: "Pipeline Value",
      deals: crmOpenDeals,
      totalValue: crmOpenDeals.reduce(
        (sum, deal) => sum + Number(deal.expectedValue || 0),
        0
      ),
    },
    overdue: {
      key: "overdue",
      label: "Overdue Follow-ups",
      deals: crmOverdueDeals,
    },
    stalled: {
      key: "stalled",
      label: "Stalled Deals",
      deals: crmStalledDeals,
    },
    installs: {
      key: "installs",
      label: "Upcoming Installations",
      deals: crmUpcomingInstallationDeals,
    },
    quoteTypeMix: {
      key: "quoteTypeMix",
      label: "Quote Type Mix",
      description: "All CRM deals grouped by quotation type.",
      deals: crmScopeDeals,
      totalValue: crmScopeDeals.reduce((sum, deal) => sum + Number(deal.expectedValue || 0), 0),
    },
    topClients: {
      key: "topClients",
      label: "Top Clients",
      description: "All ranked client records contributing to the top-client widget.",
      deals: crmTopClients.flatMap((client) => client.deals),
      totalValue: crmTopClients.reduce((sum, client) => sum + Number(client.value || 0), 0),
    },
    ...quoteTypeDrilldowns,
    ...clientDrilldowns,
  };
}, [
  crmNewDealsInPeriod,
  crmOpenDeals,
  crmOverdueDeals,
  crmQuoteTypeMix,
  crmScopeDeals,
  crmStalledDeals,
  crmTopClients,
  crmUpcomingInstallationDeals,
  currency,
  dashboardPeriodLabel,
]);

const crmActiveDrilldown = crmDrilldownKey
  ? crmOverviewDrilldowns[crmDrilldownKey] || null
  : null;

const crmDealListBaseDeals = useMemo(
  () => (crmActiveDrilldown ? crmActiveDrilldown.deals : crmScopeDeals),
  [crmActiveDrilldown, crmScopeDeals]
);

const crmFilteredDeals = useMemo(() => {
  const queryText = normalizeSearchText(deferredCrmSearch);

  return crmDealListBaseDeals
    .filter((deal) => {
      if (!queryText) return true;
      const linkedCustomer = customerRecords.find(
        (customer) =>
          customer.id &&
          String(customer.id) === String(deal.customerId || deal.crm?.customerId || "")
      );
      return doesSearchBlobMatch(buildDealSearchBlob(deal, linkedCustomer), queryText);
    })
    .filter((deal) => {
      if (crmSourceFilter === "all") return true;
      return String(deal.source || deal.crmContact?.source || "").trim() === crmSourceFilter;
    })
    .filter((deal) => {
      if (crmTypeFilter === "all") return true;
      return deal.pipelineType === crmTypeFilter;
    })
    .filter((deal) => {
      if (crmStageFilter === "all") return true;
      const [pipelineType, stageId] = crmStageFilter.split("::");
      return deal.pipelineType === pipelineType && deal.crmStageId === stageId;
    })
    .sort((a, b) => {
      if (crmSortBy === "highest") {
        return Number(b.expectedValue || 0) - Number(a.expectedValue || 0);
      }
      if (crmSortBy === "client") {
        return String(a.customerLabel || a.crmContact?.name || "").localeCompare(
          String(b.customerLabel || b.crmContact?.name || "")
        );
      }
      if (crmSortBy === "oldest") {
        return Number(a.updatedMillis || a.createdMillis || 0) - Number(b.updatedMillis || b.createdMillis || 0);
      }
      return Number(b.updatedMillis || b.createdMillis || 0) - Number(a.updatedMillis || a.createdMillis || 0);
    });
}, [
  crmDealListBaseDeals,
  crmSortBy,
  crmSourceFilter,
  crmStageFilter,
  crmTypeFilter,
  customerRecords,
  deferredCrmSearch,
]);

const visibleCrmDeals = useMemo(
  () => crmFilteredDeals.slice(0, crmDealsVisibleCount),
  [crmDealsVisibleCount, crmFilteredDeals]
);

const hasMoreCrmDeals = visibleCrmDeals.length < crmFilteredDeals.length;

useEffect(() => {
  if (!isDevRuntime) return;
  const auditRecords = visibleCrmDeals.slice(0, 5).map((deal) => ({
    id: deal.id,
    leadId: deal.leadId,
    crmLeadId: deal.crmLeadId,
    canonicalLeadId: deal.canonicalLeadId,
    crmLeadIdFromCrm: deal.crm?.leadId,
    quoteId: deal.quoteId || deal.primaryLinkedQuote?.quoteId || "",
    quoteNo: deal.quoteNo || deal.primaryLinkedQuote?.quoteNo || "",
    customerName:
      deal.customerName ||
      deal.customerLabel ||
      deal.crmContact?.name ||
      deal.contact?.name ||
      "",
    sourceType: deal.sourceType || deal.crmRecordType || "",
    hasRawLead: Boolean(deal.rawLead),
  }));
  console.log("DEAL LIST RECORD AUDIT", auditRecords);
}, [visibleCrmDeals]);

useEffect(() => {
  if (!isDevRuntime) return;
  const auditDeal = visibleCrmDeals.find(
    (deal) =>
      (selectedCrmDealId && String(deal.id || "") === String(selectedCrmDealId)) ||
      (crmDetailsOpenDebug.lastResolvedLeadId &&
        String(deal.id || "") === String(crmDetailsOpenDebug.lastResolvedLeadId))
  );
  if (!auditDeal) return;

  console.log("DEAL CARD SUMMARY AUDIT", {
    id: auditDeal.id,
    customerName:
      auditDeal.customerName ||
      auditDeal.customerLabel ||
      auditDeal.crmContact?.name ||
      "",
    rawChecklistItems: auditDeal.checklistItems,
    checklistSummary: getChecklistSummary(auditDeal),
    rawFollowUps: auditDeal.followUps,
    followUpSummary: getFollowUpSummary(auditDeal, { nowMillis: Date.now() }),
    installationDate: auditDeal.installationDate,
    installationSummary: getInstallationSummary(auditDeal),
  });
}, [crmDetailsOpenDebug.lastResolvedLeadId, selectedCrmDealId, visibleCrmDeals]);

useEffect(() => {
  setCrmDealsVisibleCount(CRM_DEALS_PAGE_SIZE);
}, [
  crmActiveDrilldown,
  crmDealListBaseDeals.length,
  crmSortBy,
  crmSourceFilter,
  crmStageFilter,
  crmTypeFilter,
  deferredCrmSearch,
]);

const crmQuoteLeadDeals = useMemo(() => {
  const linkedLeadQuoteIds = new Set();
  const linkedLeadQuoteNos = new Set();

  crmLeadDeals.forEach((deal) => {
    normalizeCrmLinkedQuotes(deal).forEach((linkedQuote) => {
      if (linkedQuote.quoteId) {
        linkedLeadQuoteIds.add(String(linkedQuote.quoteId));
      }
      if (linkedQuote.quoteNo) {
        linkedLeadQuoteNos.add(String(linkedQuote.quoteNo));
      }
    });
  });

  return crmDeals.filter((deal) => {
    if (deal.crmRecordType !== "quote") return false;

    const linkedLeadId = String(deal.crm?.leadId || "").trim();
    if (linkedLeadId) return false;

    const quoteId = String(deal.id || "");
    const quoteNo = String(deal.quoteNo || "");

    if (quoteId && linkedLeadQuoteIds.has(quoteId)) return false;
    if (quoteNo && linkedLeadQuoteNos.has(quoteNo)) return false;

    return true;
  });
}, [crmDeals, crmLeadDeals]);

const crmUnifiedLeadDeals = useMemo(() => {
  return [...crmLeadDeals, ...crmQuoteLeadDeals].sort((a, b) => {
    const createdDelta =
      Number(b.createdMillis || b.updatedMillis || 0) -
      Number(a.createdMillis || a.updatedMillis || 0);

    if (createdDelta !== 0) return createdDelta;

    return Number(b.updatedMillis || 0) - Number(a.updatedMillis || 0);
  });
}, [crmLeadDeals, crmQuoteLeadDeals]);

useEffect(() => {
  if (!IS_DEV_UI_ENABLED) return;
  console.log("crm_leads:", crmLeadDeals.length);
  console.log("unlinked quotes:", crmQuoteLeadDeals.length);
  console.log("final leads:", crmUnifiedLeadDeals.length);
}, [crmLeadDeals.length, crmQuoteLeadDeals.length, crmUnifiedLeadDeals.length]);

const crmLeadSourceOptions = useMemo(() => {
  const values = new Set();

  crmUnifiedLeadDeals.forEach((deal) => {
    const sourceValue = String(deal.source || deal.crmContact?.source || "").trim();
    if (sourceValue) values.add(sourceValue);
  });

  return Array.from(values).sort((a, b) => a.localeCompare(b));
}, [crmUnifiedLeadDeals]);

const crmLeadAssignedOptions = useMemo(() => {
  const values = new Map();

  crmUnifiedLeadDeals.forEach((deal) => {
    const memberId = String(deal.assignedToUserId || "").trim();
    if (!memberId || values.has(memberId)) return;

    values.set(memberId, {
      id: memberId,
      label: getTeamMemberLabel(memberId, deal.assignedToNameSnapshot),
    });
  });

  return Array.from(values.values()).sort((a, b) => a.label.localeCompare(b.label));
}, [crmUnifiedLeadDeals, getTeamMemberLabel]);

const crmLeadStageOptions = useMemo(() => {
  const values = new Map();

  crmUnifiedLeadDeals.forEach((deal) => {
    const stageKey = `${deal.pipelineType}::${deal.crmStageId}`;
    if (!deal.crmStageId || values.has(stageKey)) return;

    values.set(stageKey, {
      key: stageKey,
      label: `${getQuoteTypeLabel(deal.pipelineType)} | ${deal.crmStageLabel}`,
    });
  });

  return Array.from(values.values()).sort((a, b) => a.label.localeCompare(b.label));
}, [crmUnifiedLeadDeals]);

const crmFilteredLeadDeals = useMemo(() => {
  const queryText = normalizeSearchText(deferredCrmLeadSearch);
  const fromMillis = crmLeadFromDate
    ? safeTimeMillis(parseDateInputValue(crmLeadFromDate))
    : 0;
  const toMillisValue = crmLeadToDate
    ? safeTimeMillis(parseDateInputValue(crmLeadToDate)) + 24 * 60 * 60 * 1000 - 1
    : 0;

  return crmUnifiedLeadDeals
    .filter((deal) => {
      if (!queryText) return true;
      const linkedCustomer = customerRecords.find(
        (customer) =>
          customer.id &&
          String(customer.id) === String(deal.customerId || deal.crm?.customerId || "")
      );
      return doesSearchBlobMatch(buildDealSearchBlob(deal, linkedCustomer), queryText);
    })
    .filter((deal) => {
      if (crmLeadSourceFilter === "all") return true;
      return String(deal.source || deal.crmContact?.source || "").trim() === crmLeadSourceFilter;
    })
    .filter((deal) => {
      if (crmLeadStageFilter === "all") return true;
      return `${deal.pipelineType}::${deal.crmStageId}` === crmLeadStageFilter;
    })
    .filter((deal) => {
      if (crmLeadAssignedFilter === "all") return true;
      return String(deal.assignedToUserId || "") === crmLeadAssignedFilter;
    })
    .filter((deal) => {
      const referenceMillis = Number(deal.createdMillis || deal.updatedMillis || 0);
      if (fromMillis && referenceMillis < fromMillis) return false;
      if (toMillisValue && referenceMillis > toMillisValue) return false;
      return true;
    })
    .sort(
      (a, b) =>
        Number(b.createdMillis || b.updatedMillis || 0) -
        Number(a.createdMillis || a.updatedMillis || 0)
    );
}, [
  crmLeadAssignedFilter,
  crmLeadFromDate,
  crmLeadSourceFilter,
  crmLeadStageFilter,
  crmLeadToDate,
  crmUnifiedLeadDeals,
  customerRecords,
  deferredCrmLeadSearch,
]);

const getQuoteLinkDisplayData = useCallback(
  (quote = {}) => {
    const quoteType = resolveQuoteType(quote);
    const embeddedGeneral =
      quoteType === GENERAL_QUOTE_TYPE ? getEmbeddedGeneralQuote(quote) : null;
    const contact = buildCrmContactFromQuote(quote, quoteType, embeddedGeneral);
    const quoteDate = resolveQuoteDateForInput(
      {
        ...quote,
        generalQuote: embeddedGeneral || quote.generalQuote,
      },
      null
    ) || quote.createdAt || null;

    return {
      id: quote.id || "",
      quoteNo: quote.quoteNo || "",
      customerName:
        contact?.name ||
        quote.customerName ||
        embeddedGeneral?.quoteFor?.company ||
        embeddedGeneral?.quoteFor?.name ||
        getQuoteCustomerLabel(quote),
      phone: contact?.phone || quote.phone || embeddedGeneral?.quoteFor?.phone || "",
      email: contact?.email || quote.email || embeddedGeneral?.quoteFor?.email || "",
      quoteType,
      quoteTypeLabel: quoteType === GENERAL_QUOTE_TYPE ? "General" : "Curtain",
      quoteDate,
      quoteDateLabel: quoteDate ? toDateInputValue(quoteDate) || "-" : "-",
      totalValue: getNormalizedQuoteTotal(quote),
      status: quote.status || quote.crm?.status || "draft",
      crmLeadId: quote.crmLeadId || quote.leadId || quote.crm?.leadId || "",
    };
  },
  [getNormalizedQuoteTotal]
);

const quoteLinkTargetLead = useMemo(() => {
  if (!quoteLinkModalState?.leadId) return null;
  return crmDeals.find((deal) => deal.id === quoteLinkModalState.leadId) || null;
}, [crmDeals, quoteLinkModalState]);

const quoteLinkResults = useMemo(() => {
  const queryText = quoteLinkSearch.trim().toLowerCase();
  const minAmount = quoteLinkMinAmount === "" ? null : Number(quoteLinkMinAmount);
  const maxAmount = quoteLinkMaxAmount === "" ? null : Number(quoteLinkMaxAmount);

  return savedQuotes
    .filter((quote) => !isDeletedQuoteRecord(quote))
    .map((quote) => ({
      quote,
      meta: getQuoteLinkDisplayData(quote),
    }))
    .filter(({ meta }) => {
      if (quoteLinkTypeFilter !== "all" && meta.quoteType !== quoteLinkTypeFilter) {
        return false;
      }

      if (Number.isFinite(minAmount) && meta.totalValue < minAmount) {
        return false;
      }

      if (Number.isFinite(maxAmount) && meta.totalValue > maxAmount) {
        return false;
      }

      if (!queryText) return true;

      return [
        meta.quoteNo,
        meta.customerName,
        meta.phone,
        meta.email,
        meta.quoteTypeLabel,
        meta.status,
        meta.totalValue,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(queryText));
    })
    .sort((a, b) => {
      const linkedDelta =
        Number(Boolean(b.meta.crmLeadId)) - Number(Boolean(a.meta.crmLeadId));
      if (linkedDelta !== 0) return linkedDelta;
      return Number(getUpdatedMillis(b.quote) || getCreatedMillis(b.quote) || 0) -
        Number(getUpdatedMillis(a.quote) || getCreatedMillis(a.quote) || 0);
    })
    .slice(0, 80);
}, [
  getQuoteLinkDisplayData,
  quoteLinkMaxAmount,
  quoteLinkMinAmount,
  quoteLinkSearch,
  quoteLinkTypeFilter,
  savedQuotes,
]);

const getCustomerIdentityFromQuote = useCallback(
  (quoteLike = {}) => {
    const quoteType = resolveQuoteType(quoteLike);
    const embeddedGeneral =
      quoteType === GENERAL_QUOTE_TYPE ? getEmbeddedGeneralQuote(quoteLike) : null;
    const contact = buildCrmContactFromQuote(quoteLike, quoteType, embeddedGeneral);
    const customerName =
      contact?.name ||
      quoteLike.customerName ||
      embeddedGeneral?.quoteFor?.company ||
      embeddedGeneral?.quoteFor?.name ||
      getQuoteCustomerLabel(quoteLike);

    return {
      customerName,
      phone: contact?.phone || quoteLike.customerPhone || quoteLike.phone || embeddedGeneral?.quoteFor?.phone || "",
      whatsapp: contact?.whatsapp || quoteLike.whatsapp || "",
      email: contact?.email || quoteLike.email || embeddedGeneral?.quoteFor?.email || "",
      address: contact?.address || quoteLike.address || embeddedGeneral?.quoteFor?.address || "",
      companyName: embeddedGeneral?.quoteFor?.company || quoteLike.companyName || "",
      source: quoteLike.source || quoteLike.crm?.source || "",
      tags: Array.isArray(quoteLike.tags) ? quoteLike.tags : [],
    };
  },
  []
);

const getCustomerIdentityFromLeadDraft = useCallback((draft = {}) => {
  const contact = draft.contact || {};
  return {
    customerName:
      contact.name ||
      draft.companyOrProject ||
      draft.title ||
      "Unnamed Customer",
    phone: contact.phone || "",
    whatsapp: contact.whatsapp || "",
    email: contact.email || "",
    address: contact.address || "",
    companyName: draft.companyOrProject || "",
    source: draft.source || contact.source || "",
    tags: String(draft.tags || "")
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean),
  };
}, []);

const findCustomerLedgerMatch = useCallback(
  (identity = {}) => {
    const normalizedPhone = normalizeCustomerLedgerPhone(
      identity.phone || identity.whatsapp
    );
    const normalizedWhatsapp = normalizeCustomerLedgerPhone(
      identity.whatsapp || identity.phone
    );
    const normalizedEmail = normalizeCustomerLedgerEmail(identity.email);
    const normalizedName = normalizeCustomerLedgerName(identity.customerName);

    const exactContactMatch = customerRecords.find((customer) => {
      const customerPhone = normalizeCustomerLedgerPhone(
        customer.phone || customer.normalizedPhone
      );
      const customerWhatsapp = normalizeCustomerLedgerPhone(
        customer.whatsapp || customer.normalizedWhatsapp
      );
      const customerEmail = normalizeCustomerLedgerEmail(
        customer.email || customer.normalizedEmail
      );

      return (
        (normalizedPhone &&
          (customerPhone === normalizedPhone || customerWhatsapp === normalizedPhone)) ||
        (normalizedWhatsapp &&
          (customerPhone === normalizedWhatsapp || customerWhatsapp === normalizedWhatsapp)) ||
        (normalizedEmail && customerEmail === normalizedEmail)
      );
    });

    if (exactContactMatch) {
      return { customer: exactContactMatch, confidence: "exact" };
    }

    const exactNameMatches = customerRecords.filter(
      (customer) =>
        normalizedName &&
        normalizeCustomerLedgerName(customer.normalizedName || customer.customerName) ===
          normalizedName
    );

    if (!normalizedPhone && !normalizedEmail && exactNameMatches.length === 1) {
      return { customer: exactNameMatches[0], confidence: "name" };
    }

    return {
      customer: null,
      confidence: "none",
      possibleMatches: exactNameMatches,
    };
  },
  [customerRecords]
);

const isExactCustomerIdentityMatch = useCallback((customer = {}, identity = {}) => {
  const normalizedPhone = normalizeCustomerLedgerPhone(
    identity.phone || identity.whatsapp
  );
  const normalizedWhatsapp = normalizeCustomerLedgerPhone(
    identity.whatsapp || identity.phone
  );
  const normalizedEmail = normalizeCustomerLedgerEmail(identity.email);
  const normalizedName = normalizeCustomerLedgerName(identity.customerName);
  const customerPhone = normalizeCustomerLedgerPhone(
    customer.phone || customer.normalizedPhone
  );
  const customerWhatsapp = normalizeCustomerLedgerPhone(
    customer.whatsapp || customer.normalizedWhatsapp
  );
  const customerEmail = normalizeCustomerLedgerEmail(
    customer.email || customer.normalizedEmail
  );
  const customerName = normalizeCustomerLedgerName(
    customer.normalizedName || customer.customerName
  );

  return Boolean(
    (normalizedPhone &&
      (customerPhone === normalizedPhone || customerWhatsapp === normalizedPhone)) ||
      (normalizedWhatsapp &&
        (customerPhone === normalizedWhatsapp ||
          customerWhatsapp === normalizedWhatsapp)) ||
      (normalizedEmail && customerEmail === normalizedEmail) ||
      (normalizedName && customerName === normalizedName)
  );
}, []);

const mergeCustomersById = useCallback((entries = []) => {
  const seen = new Set();
  return (Array.isArray(entries) ? entries : [])
    .filter(Boolean)
    .map((entry) => normalizeCustomerLedgerRecord(entry))
    .filter((entry) => {
      if (!entry.id || seen.has(entry.id)) return false;
      seen.add(entry.id);
      return true;
    });
}, []);

const searchCustomersForQuotation = useCallback(
  async (queryText = "", options = {}) => {
    const normalizedQuery = normalizeCustomerSearchText(queryText);
    const normalizedPhoneQuery = normalizeCustomerSearchPhone(queryText);

    if (!workspaceUid || normalizedQuery.length < 2) {
      return [];
    }

    const cacheKey = `${workspaceUid}:${normalizedQuery}:${
      options.includeLegacyScan ? "legacy" : "indexed"
    }`;
    const cached = quotationCustomerSearchCacheRef.current.get(cacheKey);
    if (cached && Date.now() - cached.fetchedAt < CUSTOMER_SEARCH_TTL_MS) {
      if (isDevRuntime) {
        console.log("CUSTOMER SEARCH QUERY", {
          query: queryText,
          normalizedQuery,
          cacheHit: true,
          resultCount: cached.results.length,
          source: "memory-cache",
        });
      }
      return cached.results;
    }

    const resultMap = new Map();
    const personResults = await globalSearchCustomerPeople(workspaceUid, queryText, {
      limit: CUSTOMER_SEARCH_RESULT_LIMIT,
      ttlMs: CUSTOMER_SEARCH_TTL_MS,
      labelPrefix: "quotationCustomerPersonSearch",
    });
    personResults.forEach((entry) => {
      const customer = normalizeCustomerSearchPersonForQuote(entry);
      if (customer.id) {
        resultMap.set(customer.id, customer);
      }
    });

    if (resultMap.size < CUSTOMER_SEARCH_RESULT_LIMIT) {
      const indexedResults = await globalSearchCustomers(workspaceUid, queryText, {
        limit: CUSTOMER_SEARCH_RESULT_LIMIT - resultMap.size,
        ttlMs: CUSTOMER_SEARCH_TTL_MS,
        labelPrefix: "quotationCustomerLedgerSearch",
      });
      indexedResults.forEach((entry) => {
        const customer = normalizeCustomerLedgerRecord(entry);
        if (customer.id && !resultMap.has(customer.id)) {
          resultMap.set(customer.id, customer);
        }
      });
    }

    if (resultMap.size < CUSTOMER_SEARCH_RESULT_LIMIT) {
      const crmResults = await globalSearchCrmLeads(workspaceUid, queryText, {
        limit: CUSTOMER_SEARCH_RESULT_LIMIT - resultMap.size,
        ttlMs: CUSTOMER_SEARCH_TTL_MS,
        labelPrefix: "quotationCustomerCrmSearch",
      });
      crmResults.forEach((entry) => {
        const primaryQuote = getPrimaryCrmLinkedQuote(normalizeCrmLinkedQuotes(entry));
        const customer = normalizeCustomerSearchPersonForQuote({
          ...entry,
          id: entry.id,
          crmLeadId: entry.id,
          leadId: entry.id,
          displayName:
            entry.customerName ||
            entry.contact?.name ||
            entry.companyOrProject ||
            entry.title ||
            "",
          phone: entry.customerPhone || entry.contact?.phone || "",
          whatsapp: entry.customerWhatsapp || entry.contact?.whatsapp || "",
          email: entry.customerEmail || entry.contact?.email || "",
          address: entry.customerAddress || entry.contact?.address || "",
          companyName: entry.customerCompanyName || entry.companyOrProject || "",
          latestQuoteId: primaryQuote?.quoteId || entry.quoteId || "",
          latestQuoteNo: primaryQuote?.quoteNo || entry.quoteNo || "",
          stageLabel: entry.stageNameSnapshot || entry.stageId || "",
          sourceType: "crm_lead",
        });
        if (customer.id && !resultMap.has(customer.id)) {
          resultMap.set(customer.id, customer);
        }
      });
    }

    if (resultMap.size < CUSTOMER_SEARCH_RESULT_LIMIT) {
      const quoteResults = await globalSearchQuotations(workspaceUid, queryText, {
        limit: CUSTOMER_SEARCH_RESULT_LIMIT - resultMap.size,
        ttlMs: CUSTOMER_SEARCH_TTL_MS,
        labelPrefix: "quotationCustomerQuoteIndexSearch",
      });
      quoteResults.forEach((entry) => {
        const customer = normalizeCustomerSearchPersonForQuote({
          ...entry,
          id: entry.quoteId || entry.id,
          quoteId: entry.quoteId || entry.id,
          displayName: entry.customerName || "",
          phone: entry.customerPhone || "",
          whatsapp: entry.customerWhatsapp || "",
          email: entry.customerEmail || "",
          latestQuoteId: entry.quoteId || entry.id || "",
          latestQuoteNo: entry.quoteNo || "",
          totalAmount: entry.totalAmount || entry.grandTotal || 0,
          sourceType: "quote_customer",
        });
        if (customer.id && !resultMap.has(customer.id)) {
          resultMap.set(customer.id, customer);
        }
      });
    }

    if (options.includeLegacyScan && resultMap.size < CUSTOMER_SEARCH_RESULT_LIMIT) {
      const customersRef = collection(db, "users", workspaceUid, "customers");
      const legacyQuery = firestoreQuery(
        customersRef,
        firestoreOrderBy(firestoreDocumentId()),
        firestoreLimit(CUSTOMER_SEARCH_RESULT_LIMIT)
      );
      const legacySnap = await getDocsCached(legacyQuery, {
        label: "quotation customer explicit legacy search",
        cacheKey: `users/${workspaceUid}/customers?legacySearch=${normalizedQuery}&limit=${CUSTOMER_SEARCH_RESULT_LIMIT}`,
        ttlMs: CUSTOMER_SEARCH_TTL_MS,
      });
      legacySnap.docs.forEach((docSnap) => {
        const customer = normalizeCustomerLedgerRecord({
          id: docSnap.id,
          ...docSnap.data(),
        });
        if (
          buildCustomerLedgerSearchText(customer).includes(normalizedQuery) ||
          (normalizedPhoneQuery &&
            [
              customer.normalizedPhone,
              customer.normalizedWhatsapp,
              customer.phone,
              customer.whatsapp,
            ]
              .map(normalizeCustomerSearchPhone)
              .some((value) => value.startsWith(normalizedPhoneQuery)))
        ) {
          resultMap.set(customer.id, customer);
        }
      });
    }

    const results = Array.from(resultMap.values())
      .filter((customer) => {
        const searchText = buildCustomerLedgerSearchText(customer);
        const phoneMatches =
          normalizedPhoneQuery &&
          [
            customer.normalizedPhone,
            customer.normalizedWhatsapp,
            customer.phone,
            customer.whatsapp,
          ]
            .map(normalizeCustomerSearchPhone)
            .some((value) => value.startsWith(normalizedPhoneQuery));
        return searchText.includes(normalizedQuery) || phoneMatches;
      })
      .sort((a, b) => {
        const aActivity = safeTimeMillis(a.lastActivityAt || a.updatedAt);
        const bActivity = safeTimeMillis(b.lastActivityAt || b.updatedAt);
        if (aActivity !== bActivity) return bActivity - aActivity;
        return String(a.customerName || "").localeCompare(
          String(b.customerName || "")
        );
      })
      .slice(0, CUSTOMER_SEARCH_RESULT_LIMIT);

    quotationCustomerSearchCacheRef.current.set(cacheKey, {
      fetchedAt: Date.now(),
      results,
    });

    if (isDevRuntime) {
      console.log("CUSTOMER SEARCH QUERY", {
        query: queryText,
        normalizedQuery,
        cacheHit: false,
        resultCount: results.length,
        source: options.includeLegacyScan
          ? "person-index+ledger+crm+quote-index+legacy"
          : "person-index+ledger+crm+quote-index",
      });
    }

    return results;
  },
  [workspaceUid]
);

const runQuotationCustomerSearch = useCallback(
  async (context, queryText, options = {}) => {
    const requestId =
      Number(quotationCustomerSearchRequestRef.current[context] || 0) + 1;
    quotationCustomerSearchRequestRef.current[context] = requestId;
    const normalizedQuery = normalizeCustomerSearchText(queryText);

    if (!workspaceUid || normalizedQuery.length < 2) {
      setQuotationCustomerSearchState((prev) => ({
        ...prev,
        [context]: {
          query: queryText,
          loading: false,
          results: [],
          error: "",
          searched: false,
        },
      }));
      return;
    }

    setQuotationCustomerSearchState((prev) => ({
      ...prev,
      [context]: {
        ...(prev[context] || {}),
        query: queryText,
        loading: true,
        error: "",
        searched: true,
      },
    }));

    try {
      const results = await searchCustomersForQuotation(queryText, options);
      if (requestId !== quotationCustomerSearchRequestRef.current[context]) return;

      setQuotationCustomerSearchState((prev) => ({
        ...prev,
        [context]: {
          query: queryText,
          loading: false,
          results,
          error: "",
          searched: true,
        },
      }));
      setCustomerRecords((prev) =>
        mergeCustomersById([...results, ...prev])
      );
    } catch (error) {
      if (requestId !== quotationCustomerSearchRequestRef.current[context]) return;
      console.error("Quotation customer search failed", error);
      setQuotationCustomerSearchState((prev) => ({
        ...prev,
        [context]: {
          ...(prev[context] || {}),
          query: queryText,
          loading: false,
          error: "Could not search customers.",
          searched: true,
        },
      }));
      if (isFirestoreResourceExhausted(error)) {
        notify("Firebase read limit reached. Please wait and try again.", "warning");
      }
    }
  },
  [mergeCustomersById, notify, searchCustomersForQuotation, workspaceUid]
);

useEffect(() => {
  const timers = Object.entries(quotationCustomerSearchQueries).map(
    ([context, queryText]) =>
      window.setTimeout(() => {
        runQuotationCustomerSearch(context, queryText);
      }, CUSTOMER_SEARCH_DEBOUNCE_MS)
  );

  return () => {
    timers.forEach((timerId) => window.clearTimeout(timerId));
  };
}, [quotationCustomerSearchQueries, runQuotationCustomerSearch]);

const buildCustomerLedgerTotals = useCallback(
  (customerId, extraQuote = null, extraLead = null) => {
    const existingCustomer = customerRecords.find((customer) => customer.id === customerId);
    const customerPhoneKey = normalizeCustomerLedgerPhone(
      existingCustomer?.phone || existingCustomer?.whatsapp || ""
    );
    const customerEmailKey = normalizeCustomerLedgerEmail(existingCustomer?.email || "");
    const customerNameKey = normalizeCustomerLedgerName(existingCustomer?.customerName || "");
    const quoteMatchesCustomer = (quote) => {
      const quoteCustomerId = String(quote.customerId || quote.crm?.customerId || "");
      if (quoteCustomerId && quoteCustomerId === String(customerId)) return true;
      const identity = getCustomerIdentityFromQuote(quote);
      const quotePhoneKey = normalizeCustomerLedgerPhone(identity.phone || identity.whatsapp);
      const quoteEmailKey = normalizeCustomerLedgerEmail(identity.email);
      const quoteNameKey = normalizeCustomerLedgerName(identity.customerName);
      return (
        (customerPhoneKey && quotePhoneKey && customerPhoneKey === quotePhoneKey) ||
        (customerEmailKey && quoteEmailKey && customerEmailKey === quoteEmailKey) ||
        (customerNameKey && quoteNameKey && customerNameKey === quoteNameKey)
      );
    };
    const leadMatchesCustomer = (lead) => {
      const leadCustomerId = String(lead.customerId || lead.crm?.customerId || "");
      if (leadCustomerId && leadCustomerId === String(customerId)) return true;
      const leadContact = lead.contact || lead.crm?.contact || {};
      const leadPhoneKey = normalizeCustomerLedgerPhone(
        lead.customerPhone || leadContact.phone || leadContact.whatsapp || ""
      );
      const leadEmailKey = normalizeCustomerLedgerEmail(leadContact.email || lead.email || "");
      const leadNameKey = normalizeCustomerLedgerName(
        lead.customerName || leadContact.name || lead.companyOrProject || lead.title || ""
      );
      return (
        (customerPhoneKey && leadPhoneKey && customerPhoneKey === leadPhoneKey) ||
        (customerEmailKey && leadEmailKey && customerEmailKey === leadEmailKey) ||
        (customerNameKey && leadNameKey && customerNameKey === leadNameKey)
      );
    };
    const allQuotes = [
      ...savedQuotes,
      ...(extraQuote ? [extraQuote] : []),
    ].filter(
      (quote, index, list) =>
        quote &&
        quoteMatchesCustomer(quote) &&
        !isDeletedQuoteRecord(quote) &&
        list.findIndex((entry) => String(entry.id || "") === String(quote.id || "")) === index
    );
    const allLeads = [
      ...crmLeadRecords,
      ...(extraLead ? [extraLead] : []),
    ].filter(
      (lead, index, list) =>
        lead &&
        leadMatchesCustomer(lead) &&
        list.findIndex((entry) => String(entry.id || "") === String(lead.id || "")) === index
    );
    const totalQuoteValue = allQuotes.reduce(
      (sum, quote) => sum + Number(getNormalizedQuoteTotal(quote) || 0),
      0
    );
    const confirmedQuoteRecords = allQuotes.filter(
      (quote) => String(quote.status || quote.crm?.status || "").toLowerCase() === "confirmed"
    );
    const confirmedQuotes = confirmedQuoteRecords.length;
    const confirmedValue = confirmedQuoteRecords.reduce(
      (sum, quote) => sum + Number(getNormalizedQuoteTotal(quote) || 0),
      0
    );
    const openDealRecords = allLeads.filter((lead) => isOpenCrmDeal(lead));
    const openDealCount = openDealRecords.length;
    const activeDealValue = openDealRecords.reduce(
      (sum, lead) =>
        sum +
        Number(
          lead.expectedValue ||
            lead.finalValue ||
            lead.totalValue ||
            lead.crm?.expectedValue ||
            lead.crm?.finalValue ||
            0
        ),
      0
    );
    const pendingFollowupCount = allLeads.reduce(
      (sum, lead) =>
        sum +
        normalizeCrmFollowUps(lead.followUps || lead.crm?.followUps || []).filter(
          (followUp) => followUp.status !== CRM_FOLLOW_UP_STATUS_DONE
        ).length,
      0
    );
    const upcomingInstallationCount = allLeads.filter(
      (lead) =>
        lead.installationDate ||
        lead.crm?.installationDate ||
        lead.expectedInstallationDate ||
        lead.scheduledInstallationDate
    ).length;
    const completedJobs = allLeads.filter((lead) => {
      const stage = getCrmStage(crmPipelines, lead.pipelineType || lead.quoteType, lead.stageId);
      return stage?.marksComplete === true || String(lead.status || "").toLowerCase() === "completed";
    }).length;
    const receivedAmount = Number(existingCustomer?.totals?.receivedAmount || 0);

    return {
      totalQuotes: allQuotes.length,
      totalQuoteValue,
      confirmedQuotes,
      confirmedValue,
      openDealCount,
      activeDealValue,
      pendingFollowupCount,
      upcomingInstallationCount,
      completedJobs,
      pendingAmount: Math.max(0, totalQuoteValue - receivedAmount),
      receivedAmount,
    };
  },
  [
    crmLeadRecords,
    crmPipelines,
    customerRecords,
    getCustomerIdentityFromQuote,
    getNormalizedQuoteTotal,
    isOpenCrmDeal,
    savedQuotes,
  ]
);

const ensureCustomerLedgerRecord = useCallback(
  async (identity = {}, options = {}) => {
    if (!workspaceUid) return null;

    const sanitizedIdentity = normalizeCustomerLedgerRecord({
      customerName: identity.customerName,
      phone: identity.phone,
      whatsapp: identity.whatsapp,
      email: identity.email,
      address: identity.address,
      companyName: identity.companyName,
      source: identity.source,
      tags: identity.tags,
      notes: identity.notes,
    });
    const match = findCustomerLedgerMatch(sanitizedIdentity);
    let existingCustomer = options.customerId
      ? customerRecords.find((customer) => customer.id === options.customerId) || null
      : match.customer;

    if (!existingCustomer && !options.customerId) {
      const lookupText =
        sanitizedIdentity.phone ||
        sanitizedIdentity.whatsapp ||
        sanitizedIdentity.email ||
        sanitizedIdentity.customerName;
      const candidates = await searchCustomersForQuotation(lookupText);
      existingCustomer =
        candidates.find((customer) =>
          isExactCustomerIdentityMatch(customer, sanitizedIdentity)
        ) || null;
    }

    const customerId =
      existingCustomer?.id || options.customerId || createClientId("customer");
    const customerKey =
      existingCustomer?.customerKey ||
      options.customerKey ||
      customerId;
    const nextCustomer = normalizeCustomerLedgerRecord({
      ...existingCustomer,
      ...sanitizedIdentity,
      id: customerId,
      customerKey,
      customerName:
        sanitizedIdentity.customerName ||
        existingCustomer?.customerName ||
        "Unnamed Customer",
      phone: sanitizedIdentity.phone || existingCustomer?.phone || "",
      whatsapp: sanitizedIdentity.whatsapp || existingCustomer?.whatsapp || "",
      email: sanitizedIdentity.email || existingCustomer?.email || "",
      address: sanitizedIdentity.address || existingCustomer?.address || "",
      companyName: sanitizedIdentity.companyName || existingCustomer?.companyName || "",
      source: sanitizedIdentity.source || existingCustomer?.source || "",
      tags:
        sanitizedIdentity.tags.length > 0
          ? sanitizedIdentity.tags
          : existingCustomer?.tags || [],
    });
    const totals = buildCustomerLedgerTotals(
      customerId,
      options.extraQuote
        ? { ...options.extraQuote, customerId }
        : null,
      options.extraLead
        ? { ...options.extraLead, customerId }
        : null
    );
    const existingTotals = existingCustomer?.totals || {};
    const mergedTotals = {
      ...totals,
      totalQuotes: Math.max(
        Number(existingTotals.totalQuotes || 0),
        Number(existingCustomer?.quoteCount || 0),
        Number(totals.totalQuotes || 0)
      ),
      totalQuoteValue: Math.max(
        Number(existingTotals.totalQuoteValue || 0),
        Number(existingCustomer?.totalQuotedValue || 0),
        Number(totals.totalQuoteValue || 0)
      ),
      confirmedQuotes: Math.max(
        Number(existingTotals.confirmedQuotes || 0),
        Number(existingCustomer?.confirmedCount || 0),
        Number(totals.confirmedQuotes || 0)
      ),
      confirmedValue: Math.max(
        Number(existingTotals.confirmedValue || 0),
        Number(existingCustomer?.confirmedValue || 0),
        Number(totals.confirmedValue || 0)
      ),
      completedJobs: Math.max(
        Number(existingTotals.completedJobs || 0),
        Number(existingCustomer?.completedJobsCount || 0),
        Number(totals.completedJobs || 0)
      ),
      openDealCount: Math.max(
        Number(existingTotals.openDealCount || 0),
        Number(existingCustomer?.openDealCount || 0),
        Number(totals.openDealCount || 0)
      ),
      activeDealValue: Math.max(
        Number(existingTotals.activeDealValue || 0),
        Number(existingCustomer?.activeDealValue || 0),
        Number(totals.activeDealValue || 0)
      ),
      pendingFollowupCount: Math.max(
        Number(existingTotals.pendingFollowupCount || 0),
        Number(existingCustomer?.pendingFollowupCount || 0),
        Number(totals.pendingFollowupCount || 0)
      ),
      upcomingInstallationCount: Math.max(
        Number(existingTotals.upcomingInstallationCount || 0),
        Number(existingCustomer?.upcomingInstallationCount || 0),
        Number(totals.upcomingInstallationCount || 0)
      ),
      receivedAmount: Math.max(
        Number(existingTotals.receivedAmount || 0),
        Number(totals.receivedAmount || 0)
      ),
    };
    mergedTotals.pendingAmount = Math.max(
      0,
      Number(mergedTotals.totalQuoteValue || 0) - Number(mergedTotals.receivedAmount || 0)
    );
    const customerSummaryFields = {
      quoteCount: Number(mergedTotals.totalQuotes || 0),
      totalQuotedValue: Number(mergedTotals.totalQuoteValue || 0),
      confirmedCount: Number(mergedTotals.confirmedQuotes || 0),
      confirmedValue: Number(mergedTotals.confirmedValue || 0),
      openDealCount: Number(mergedTotals.openDealCount || 0),
      activeDealValue: Number(mergedTotals.activeDealValue || 0),
      pendingFollowupCount: Number(mergedTotals.pendingFollowupCount || 0),
      upcomingInstallationCount: Number(mergedTotals.upcomingInstallationCount || 0),
      lastQuoteId: options.extraQuote?.id || existingCustomer?.lastQuoteId || "",
      lastQuoteNo: options.extraQuote?.quoteNo || existingCustomer?.lastQuoteNo || "",
      lastDealId: options.extraLead?.id || existingCustomer?.lastDealId || "",
      lastDealStage:
        options.extraLead?.stageNameSnapshot ||
        options.extraLead?.stageId ||
        existingCustomer?.lastDealStage ||
        "",
    };
    const customerRef = doc(db, "users", workspaceUid, "customers", customerId);
    const searchIndexFields = buildSafeCustomerSearchIndex(nextCustomer);
    const customerPayload = sanitizeCrmFirestoreValue({
      id: customerId,
      customerKey,
      customerName: nextCustomer.customerName,
      normalizedName: nextCustomer.normalizedName,
      nameLower: nextCustomer.nameLower,
      customerNameLower: nextCustomer.customerNameLower,
      phone: nextCustomer.phone,
      whatsapp: nextCustomer.whatsapp,
      email: nextCustomer.email,
      normalizedPhone: nextCustomer.normalizedPhone,
      normalizedWhatsapp: nextCustomer.normalizedWhatsapp,
      normalizedEmail: nextCustomer.normalizedEmail,
      address: nextCustomer.address,
      companyName: nextCustomer.companyName,
      source: nextCustomer.source,
      tags: nextCustomer.tags,
      notes: nextCustomer.notes,
      ...searchIndexFields,
      searchPrefixes: searchIndexFields.searchPrefixes,
      phonePrefixes: searchIndexFields.phonePrefixes,
      totals: mergedTotals,
      ...customerSummaryFields,
      createdBy:
        existingCustomer?.createdBy ||
        user?.uid ||
        user?.email ||
        "",
    });

    await setDoc(
      customerRef,
      {
        ...customerPayload,
        ...(existingCustomer?.id ? {} : { createdAt: serverTimestamp() }),
        updatedAt: serverTimestamp(),
        lastActivityAt: serverTimestamp(),
      },
      { merge: true }
    );

    const localCustomer = {
      ...nextCustomer,
      ...searchIndexFields,
      totals: mergedTotals,
      ...customerSummaryFields,
      createdAt: existingCustomer?.createdAt || new Date(),
      updatedAt: new Date(),
      lastActivityAt: new Date(),
    };
    await upsertCustomerSearchIndexRecord(
      {
        ...localCustomer,
        customerId,
        sourceType: "customer",
      },
      { sourceType: "customer" }
    ).catch((error) => {
      if (isDevRuntime) {
        console.error("CUSTOMER SEARCH INDEX CUSTOMER UPSERT FAILED", {
          customerId,
          code: error?.code,
          message: error?.message,
        });
      }
    });
    invalidateWorkspaceCollectionCache(workspaceUid, "customers");
    quotationCustomerSearchCacheRef.current.clear();
    setCustomerRecords((prev) => {
      const without = prev.filter((customer) => customer.id !== customerId);
      return [localCustomer, ...without];
    });

    return localCustomer;
  },
  [
    buildCustomerLedgerTotals,
    customerRecords,
    findCustomerLedgerMatch,
    isExactCustomerIdentityMatch,
    searchCustomersForQuotation,
    upsertCustomerSearchIndexRecord,
    user,
    workspaceUid,
  ]
);

const findCustomerDuplicateCandidates = useCallback(
  (identity = {}) => {
    const normalizedPhone = normalizeCustomerLedgerPhone(
      identity.phone || identity.whatsapp
    );
    const normalizedWhatsapp = normalizeCustomerLedgerPhone(
      identity.whatsapp || identity.phone
    );
    const normalizedEmail = normalizeCustomerLedgerEmail(identity.email);
    const normalizedName = normalizeCustomerLedgerName(identity.customerName);
    const normalizedAddress = normalizeCustomerLedgerName(identity.address);

    return customerRecords
      .map((customer) => {
        const customerPhone = normalizeCustomerLedgerPhone(
          customer.phone || customer.normalizedPhone
        );
        const customerWhatsapp = normalizeCustomerLedgerPhone(
          customer.whatsapp || customer.normalizedWhatsapp
        );
        const customerEmail = normalizeCustomerLedgerEmail(
          customer.email || customer.normalizedEmail
        );
        const customerName = normalizeCustomerLedgerName(
          customer.normalizedName || customer.customerName
        );
        const customerAddress = normalizeCustomerLedgerName(customer.address);

        const exactContact =
          (normalizedPhone &&
            (customerPhone === normalizedPhone ||
              customerWhatsapp === normalizedPhone)) ||
          (normalizedWhatsapp &&
            (customerPhone === normalizedWhatsapp ||
              customerWhatsapp === normalizedWhatsapp)) ||
          (normalizedEmail && customerEmail === normalizedEmail);
        const exactName = normalizedName && customerName === normalizedName;
        const addressHint =
          exactName &&
          normalizedAddress &&
          customerAddress &&
          (customerAddress.includes(normalizedAddress) ||
            normalizedAddress.includes(customerAddress));

        if (!exactContact && !exactName && !addressHint) return null;

        return {
          customer,
          reason: exactContact
            ? "Same contact"
            : addressHint
            ? "Similar name and address"
            : "Same name",
        };
      })
      .filter(Boolean)
      .slice(0, 5);
  },
  [customerRecords]
);

const applyCustomerToQuoteContext = useCallback(
  (context = CURTAIN_QUOTE_TYPE, customer = {}) => {
    if (!customer?.id) return;

    const searchIndexIdPattern = /^(phone|email|lead|quote|name)_/i;
    const customerSearchIndexId = String(
      customer.customerSearchIndexId || customer.personId || ""
    ).trim();
    const resolvedCustomerId =
      customer.customerId ||
      (customerSearchIndexId || searchIndexIdPattern.test(String(customer.id || ""))
        ? ""
        : customer.id);
    const resolvedCustomerKey =
      customer.customerKey ||
      resolvedCustomerId ||
      customerSearchIndexId ||
      customer.id ||
      "";
    const resolvedCrmLeadId = customer.crmLeadId || customer.leadId || "";

    const nextLink = {
      customerId: resolvedCustomerId,
      customerKey: resolvedCustomerKey,
      customerSearchIndexId: customerSearchIndexId || customer.id || "",
      crmLeadId: resolvedCrmLeadId,
      leadId: resolvedCrmLeadId,
      customerName: customer.customerName || "",
      phone: customer.phone || "",
      whatsapp: customer.whatsapp || "",
      email: customer.email || "",
      address: customer.address || "",
      companyName: customer.companyName || "",
    };
    const normalizedCustomer = normalizeCustomerLedgerRecord(customer);

    setCustomerRecords((prev) =>
      mergeCustomersById([normalizedCustomer, ...prev])
    );
    if (resolvedCrmLeadId) {
      setPendingCrmLeadId(resolvedCrmLeadId);
    }

    if (context === GENERAL_QUOTE_TYPE) {
      setGeneralQuote((prev) => ({
        ...prev,
        customerId: resolvedCustomerId,
        customerKey: resolvedCustomerKey,
        customerSearchIndexId: customerSearchIndexId || customer.id || "",
        crmLeadId: resolvedCrmLeadId,
        leadId: resolvedCrmLeadId,
        customerName: customer.customerName || "",
        customerPhone: customer.phone || "",
        customerWhatsapp: customer.whatsapp || "",
        customerEmail: customer.email || "",
        customerAddress: customer.address || "",
        customerCompanyName: customer.companyName || "",
        customerLedgerLinked: true,
        quoteFor: {
          ...(prev.quoteFor || {}),
          name: customer.customerName || prev.quoteFor?.name || "",
          company: customer.companyName || prev.quoteFor?.company || "",
          phone: customer.phone || customer.whatsapp || prev.quoteFor?.phone || "",
          email: customer.email || prev.quoteFor?.email || "",
          address: customer.address || prev.quoteFor?.address || "",
        },
      }));
      setCustomerName(customer.companyName || customer.customerName || "");
    } else {
      setCustomerName(customer.customerName || "");
      setQuoteCustomerLink(nextLink);
    }

    setCustomerPickerOpenKey("");
  },
  [mergeCustomersById, setGeneralQuote]
);

const openCreateCustomerFromQuote = useCallback(
  (context = CURTAIN_QUOTE_TYPE, seed = {}) => {
    setCustomerCreateState({
      context,
      draft: {
        customerName: seed.customerName || seed.name || "",
        phone: seed.phone || "",
        whatsapp: seed.whatsapp || "",
        email: seed.email || "",
        address: seed.address || "",
        companyName: seed.companyName || "",
        source: seed.source || "",
        tags: Array.isArray(seed.tags) ? seed.tags.join(", ") : seed.tags || "",
      },
      duplicateMatches: [],
      duplicateWarning: false,
    });
    setCustomerPickerOpenKey("");
  },
  []
);

const openCreateLeadFromQuoteCustomer = useCallback(
  (context = CURTAIN_QUOTE_TYPE, seed = {}) => {
    const quoteType = context === GENERAL_QUOTE_TYPE ? GENERAL_QUOTE_TYPE : CURTAIN_QUOTE_TYPE;
    const nextDraft = createCrmLeadDraftState(quoteType);
    const customerLabel = String(seed.customerName || seed.name || "").trim();
    const seededDraft = {
      ...nextDraft,
      title: customerLabel || nextDraft.title,
      companyOrProject: seed.companyName || nextDraft.companyOrProject || "",
      contact: {
        ...(nextDraft.contact || {}),
        name: customerLabel || nextDraft.contact?.name || "",
        phone: seed.phone || nextDraft.contact?.phone || "",
        whatsapp: seed.whatsapp || nextDraft.contact?.whatsapp || "",
        email: seed.email || nextDraft.contact?.email || "",
        address: seed.address || nextDraft.contact?.address || "",
      },
    };
    setCrmLeadDraft(seededDraft);
    markCrmLeadBaseline(seededDraft, "idle");
    setIsCrmLeadModalOpen(true);
    setCustomerPickerOpenKey("");
  },
  [createCrmLeadDraftState, markCrmLeadBaseline]
);

const createCustomerFromQuote = useCallback(
  async ({ force = false } = {}) => {
    if (!workspaceUid || !customerCreateState?.draft) {
      notify("Could not create customer. Please try again.", "error");
      return;
    }

    const draft = customerCreateState.draft;
    const tags = String(draft.tags || "")
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);
    const identity = {
      ...draft,
      tags,
      customerName: String(draft.customerName || "").trim(),
    };

    if (!identity.customerName) {
      notify("Please enter a customer name.", "warning");
      return;
    }

    const duplicateMatches = findCustomerDuplicateCandidates(identity);
    if (!force && duplicateMatches.length > 0) {
      setCustomerCreateState((prev) => ({
        ...prev,
        duplicateMatches,
        duplicateWarning: true,
      }));
      return;
    }

    setIsCreatingCustomerFromQuote(true);
    try {
      const customerId = createClientId("customer");
      const customerKey = customerId;
      const nextCustomer = normalizeCustomerLedgerRecord({
        ...identity,
        id: customerId,
        customerKey,
      });
      const customerRef = doc(db, "users", workspaceUid, "customers", customerId);
      const searchIndexFields = buildSafeCustomerSearchIndex(nextCustomer);
      const totals = {
        totalQuotes: 0,
        totalQuoteValue: 0,
        confirmedQuotes: 0,
        completedJobs: 0,
        pendingAmount: 0,
        receivedAmount: 0,
      };
      const payload = sanitizeCrmFirestoreValue({
        id: customerId,
        customerKey,
        customerName: nextCustomer.customerName,
        normalizedName: nextCustomer.normalizedName,
        nameLower: nextCustomer.nameLower,
        customerNameLower: nextCustomer.customerNameLower,
        phone: nextCustomer.phone,
        whatsapp: nextCustomer.whatsapp,
        email: nextCustomer.email,
        normalizedPhone: nextCustomer.normalizedPhone,
        normalizedWhatsapp: nextCustomer.normalizedWhatsapp,
        normalizedEmail: nextCustomer.normalizedEmail,
        address: nextCustomer.address,
        companyName: nextCustomer.companyName,
        source: nextCustomer.source,
        tags: nextCustomer.tags,
        notes: nextCustomer.notes,
        searchText: searchIndexFields.searchText,
        searchKeywords: searchIndexFields.searchKeywords,
        searchPrefixes: searchIndexFields.searchPrefixes,
        phonePrefixes: searchIndexFields.phonePrefixes,
        totals,
        createdBy: user?.uid || user?.email || "",
      });

      await setDoc(customerRef, {
        ...payload,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastActivityAt: serverTimestamp(),
      });

      const localCustomer = {
        ...nextCustomer,
        ...searchIndexFields,
        totals,
        createdAt: new Date(),
        updatedAt: new Date(),
        lastActivityAt: new Date(),
      };

      await upsertCustomerSearchIndexRecord(
        {
          ...localCustomer,
          customerId,
          sourceType: "customer",
        },
        { sourceType: "customer" }
      ).catch((error) => {
        if (isDevRuntime) {
          console.error("CUSTOMER SEARCH INDEX CREATE UPSERT FAILED", {
            customerId,
            code: error?.code,
            message: error?.message,
          });
        }
      });
      quotationCustomerSearchCacheRef.current.clear();
      invalidateWorkspaceCollectionCache(workspaceUid, "customers");
      setCustomerRecords((prev) => mergeCustomersById([localCustomer, ...prev]));
      applyCustomerToQuoteContext(customerCreateState.context, localCustomer);
      setCustomerCreateState(null);
      notify("Customer created", "success");
    } catch (error) {
      console.error("Customer create failed", error);
      notify("Could not create customer. Please try again.", "error");
    } finally {
      setIsCreatingCustomerFromQuote(false);
    }
  },
  [
    applyCustomerToQuoteContext,
    customerCreateState,
    findCustomerDuplicateCandidates,
    mergeCustomersById,
    notify,
    upsertCustomerSearchIndexRecord,
    user,
    workspaceUid,
  ]
);

const repairCustomerSearchIndex = useCallback(async () => {
  if (!workspaceUid || customerSearchRepairState.running) return;

  setCustomerSearchRepairState({
    running: true,
    processed: 0,
    updated: 0,
    error: "",
  });

  try {
    let cursor = null;
    let processed = 0;
    let updated = 0;
    let hasMore = true;

    while (hasMore) {
      const constraints = [
        firestoreOrderBy(firestoreDocumentId()),
        firestoreLimit(100),
      ];
      if (cursor) constraints.push(firestoreStartAfter(cursor));

      const pageQuery = firestoreQuery(
        collection(db, "users", workspaceUid, "customers"),
        ...constraints
      );
      const snap = await getDocsCached(pageQuery, {
        label: "repairCustomerSearchIndex",
        cacheKey: `users/${workspaceUid}/customers?repairSearchIndexAfter=${
          cursor?.id || "start"
        }`,
        ttlMs: 0,
      });

      if (snap.empty) {
        hasMore = false;
        break;
      }

      const batch = writeBatch(db);
      let hasWrites = false;
      let pageUpdated = 0;

      snap.docs.forEach((docSnap) => {
        const raw = docSnap.data() || {};
        const normalized = normalizeCustomerLedgerRecord({
          id: docSnap.id,
          ...raw,
        });
        const searchIndexFields = buildSafeCustomerSearchIndex(normalized);
        const needsUpdate =
          raw.normalizedName !== normalized.normalizedName ||
          raw.nameLower !== normalized.nameLower ||
          raw.customerNameLower !== normalized.customerNameLower ||
          raw.normalizedPhone !== normalized.normalizedPhone ||
          raw.normalizedWhatsapp !== normalized.normalizedWhatsapp ||
          raw.normalizedEmail !== normalized.normalizedEmail ||
          raw.searchText !== searchIndexFields.searchText ||
          JSON.stringify(raw.searchKeywords || []) !==
            JSON.stringify(searchIndexFields.searchKeywords) ||
          JSON.stringify(raw.searchPrefixes || []) !==
            JSON.stringify(searchIndexFields.searchPrefixes) ||
          JSON.stringify(raw.phonePrefixes || []) !==
            JSON.stringify(searchIndexFields.phonePrefixes);

        if (!needsUpdate) return;

        hasWrites = true;
        pageUpdated += 1;
        batch.set(
          doc(db, "users", workspaceUid, "customers", docSnap.id),
          sanitizeCrmFirestoreValue({
            normalizedName: normalized.normalizedName,
            nameLower: normalized.nameLower,
            customerNameLower: normalized.customerNameLower,
            normalizedPhone: normalized.normalizedPhone,
            normalizedWhatsapp: normalized.normalizedWhatsapp,
            normalizedEmail: normalized.normalizedEmail,
            searchText: searchIndexFields.searchText,
            searchKeywords: searchIndexFields.searchKeywords,
            searchPrefixes: searchIndexFields.searchPrefixes,
            phonePrefixes: searchIndexFields.phonePrefixes,
            searchIndexRepairedAt: serverTimestamp(),
          }),
          { merge: true }
        );
      });

      if (hasWrites) {
        await batch.commit();
      }

      processed += snap.size;
      updated += pageUpdated;
      cursor = snap.docs[snap.docs.length - 1];
      hasMore = snap.size === 100;
      setCustomerSearchRepairState({
        running: true,
        processed,
        updated,
        error: "",
      });
    }

    quotationCustomerSearchCacheRef.current.clear();
    invalidateWorkspaceCollectionCache(workspaceUid, "customers");
    setCustomerSearchRepairState({
      running: false,
      processed,
      updated,
      error: "",
    });
    notify(`Customer search index repaired (${updated} updated).`, "success");
  } catch (error) {
    console.error("Customer search index repair failed", error);
    setCustomerSearchRepairState((prev) => ({
      ...prev,
      running: false,
      error: error?.message || "Repair failed",
    }));
    notify("Could not repair customer search index.", "error");
  }
}, [
  customerSearchRepairState.running,
  notify,
  workspaceUid,
]);

const customerLedgerSourceOptions = useMemo(() => {
  const values = new Set();
  customerRecords.forEach((customer) => {
    if (customer.source) values.add(customer.source);
  });
  return Array.from(values).sort((a, b) => a.localeCompare(b));
}, [customerRecords]);

const customerLedgerSummaries = useMemo(() => {
  const customerMap = new Map();
  customerRecords.forEach((customer) => {
    customerMap.set(customer.id, {
      ...customer,
      quotes: [],
      leads: [],
      openDealsCount: Number(customer.openDealCount || 0),
      completedJobsCount: Number(customer.completedJobsCount || customer.totals?.completedJobs || 0),
      pendingFollowUpsCount: Number(customer.pendingFollowupCount || 0),
      installationCount: Number(customer.upcomingInstallationCount || 0),
      activeDealValue: Number(customer.activeDealValue || 0),
      duplicateWarning: "",
      leadLocationSearchText: "",
    });
  });

  const getCustomerMatches = (record = {}, type = "quote") => {
    const recordCustomerId = String(record.customerId || record.crm?.customerId || "");
    if (recordCustomerId && customerMap.has(recordCustomerId)) {
      return [customerMap.get(recordCustomerId)];
    }

    const identity =
      type === "quote"
        ? getCustomerIdentityFromQuote(record)
        : {
            customerName:
              record.customerName ||
              record.contact?.name ||
              record.crm?.contact?.name ||
              record.companyOrProject ||
              record.title ||
              "",
            phone:
              record.customerPhone ||
              record.contact?.phone ||
              record.contact?.whatsapp ||
              record.crm?.contact?.phone ||
              "",
            whatsapp: record.contact?.whatsapp || record.crm?.contact?.whatsapp || "",
            email: record.contact?.email || record.crm?.contact?.email || record.email || "",
          };
    const phoneKey = normalizeCustomerLedgerPhone(identity.phone || identity.whatsapp);
    const emailKey = normalizeCustomerLedgerEmail(identity.email);
    const nameKey = normalizeCustomerLedgerName(identity.customerName);

    return Array.from(customerMap.values()).filter((customer) => {
      const customerPhoneKey = normalizeCustomerLedgerPhone(customer.phone || customer.whatsapp);
      const customerEmailKey = normalizeCustomerLedgerEmail(customer.email);
      const customerNameKey = normalizeCustomerLedgerName(customer.customerName);
      return (
        (phoneKey && customerPhoneKey && phoneKey === customerPhoneKey) ||
        (emailKey && customerEmailKey && emailKey === customerEmailKey) ||
        (nameKey && customerNameKey && nameKey === customerNameKey)
      );
    });
  };

  savedQuotes.forEach((quote) => {
    if (isDeletedQuoteRecord(quote)) return;
    getCustomerMatches(quote, "quote").forEach((summary) => {
      if (
        !summary.quotes.some(
          (existingQuote) => String(existingQuote.id || "") === String(quote.id || "")
        )
      ) {
        summary.quotes.push(quote);
      }
    });
  });

  Object.entries(customerLedgerQuotesByCustomer || {}).forEach(([customerId, quotes]) => {
    const summary = customerMap.get(customerId);
    if (!summary) return;
    (Array.isArray(quotes) ? quotes : []).forEach((quote) => {
      if (isDeletedQuoteRecord(quote)) return;
      if (
        summary.quotes.some(
          (existingQuote) => String(existingQuote.id || "") === String(quote.id || "")
        )
      ) {
        return;
      }
      summary.quotes.push(quote);
    });
  });

  Object.entries(customerLedgerCrmDealsByCustomer || {}).forEach(([customerId, leads]) => {
    const summary = customerMap.get(customerId);
    if (!summary) return;
    (Array.isArray(leads) ? leads : []).forEach((lead) => {
      if (
        summary.leads.some(
          (existingLead) => String(existingLead.id || "") === String(lead.id || "")
        )
      ) {
        return;
      }
      summary.leads.push(lead);
      const leadLocations = normalizeSiteLocations(lead.siteLocations || []);
      if (leadLocations.length) {
        summary.leadLocationSearchText = [
          summary.leadLocationSearchText,
          buildSiteLocationSearchText(leadLocations),
        ]
          .filter(Boolean)
          .join(" ");
      }
      const stage = getCrmStage(
        crmPipelines,
        lead.pipelineType || lead.quoteType,
        lead.stageId
      );
      if (stage?.marksComplete || String(lead.status || "").toLowerCase() === "completed") {
        summary.completedJobsCount += 1;
      } else if (isOpenCrmDeal(lead)) {
        summary.openDealsCount += 1;
      }
      summary.pendingFollowUpsCount += normalizeCrmFollowUps(lead.followUps).filter(
        (followUp) => followUp.status !== CRM_FOLLOW_UP_STATUS_DONE
      ).length;
      if (lead.installationDate) summary.installationCount += 1;
    });
  });

  const phoneCounts = new Map();
  const emailCounts = new Map();
  const nameCounts = new Map();
  customerRecords.forEach((customer) => {
    const phoneKey = normalizeCustomerLedgerPhone(customer.phone || customer.whatsapp);
    const emailKey = normalizeCustomerLedgerEmail(customer.email);
    const nameKey = normalizeCustomerLedgerName(customer.customerName);
    if (phoneKey) phoneCounts.set(phoneKey, (phoneCounts.get(phoneKey) || 0) + 1);
    if (emailKey) emailCounts.set(emailKey, (emailCounts.get(emailKey) || 0) + 1);
    if (nameKey) nameCounts.set(nameKey, (nameCounts.get(nameKey) || 0) + 1);
  });

  return Array.from(customerMap.values()).map((summary) => {
    const totalQuoteValue = Math.max(
      Number(summary.totalQuotedValue || summary.totals?.totalQuoteValue || 0),
      summary.quotes.reduce(
        (sum, quote) => sum + Number(getNormalizedQuoteTotal(quote) || 0),
        0
      )
    );
    const confirmedQuotes = summary.quotes.filter(
      (quote) => String(quote.status || quote.crm?.status || "").toLowerCase() === "confirmed"
    ).length || Number(summary.totals?.confirmedQuotes || 0);
    const phoneKey = normalizeCustomerLedgerPhone(summary.phone || summary.whatsapp);
    const emailKey = normalizeCustomerLedgerEmail(summary.email);
    const nameKey = normalizeCustomerLedgerName(summary.customerName);
    const duplicateReasons = [
      phoneKey && phoneCounts.get(phoneKey) > 1 ? "same phone" : "",
      emailKey && emailCounts.get(emailKey) > 1 ? "same email" : "",
      nameKey && nameCounts.get(nameKey) > 1 ? "same name" : "",
    ].filter(Boolean);

    return {
      ...summary,
      totals: {
        ...summary.totals,
        totalQuotes: Math.max(
          summary.quotes.length,
          Number(summary.quoteCount || summary.totals?.totalQuotes || 0)
        ),
        totalQuoteValue,
        confirmedQuotes,
        completedJobs: summary.completedJobsCount,
        activeDealValue: summary.activeDealValue,
        confirmedValue: Number(summary.confirmedValue || summary.totals?.confirmedValue || 0),
        pendingAmount: Math.max(
          0,
          totalQuoteValue - Number(summary.totals?.receivedAmount || 0)
        ),
      },
      lastActivityMillis: Math.max(
        safeTimeMillis(summary.lastActivityAt),
        ...summary.quotes.map((quote) => getUpdatedMillis(quote) || getCreatedMillis(quote) || 0),
        ...summary.leads.map((lead) => safeTimeMillis(lead.updatedAt || lead.createdAt))
      ),
      duplicateWarning: duplicateReasons.join(", "),
    };
  });
}, [
  crmPipelines,
  customerLedgerCrmDealsByCustomer,
  customerLedgerQuotesByCustomer,
  customerRecords,
  getCustomerIdentityFromQuote,
  getNormalizedQuoteTotal,
  isOpenCrmDeal,
  savedQuotes,
]);

const filteredCustomerLedgerSummaries = useMemo(() => {
  const queryText = deferredCustomerLedgerSearch.trim().toLowerCase();
  const filtered = customerLedgerSummaries.filter((customer) => {
    if (customerLedgerSourceFilter !== "all" && customer.source !== customerLedgerSourceFilter) {
      return false;
    }
    if (!queryText) return true;
    return buildCustomerLedgerSearchText(customer).includes(queryText);
  });

  return filtered.sort((a, b) => {
    if (customerLedgerSortBy === "value") {
      return Number(b.totals?.totalQuoteValue || 0) - Number(a.totals?.totalQuoteValue || 0);
    }
    if (customerLedgerSortBy === "quotes") {
      return Number(b.totals?.totalQuotes || 0) - Number(a.totals?.totalQuotes || 0);
    }
    if (customerLedgerSortBy === "name") {
      return String(a.customerName || "").localeCompare(String(b.customerName || ""));
    }
    return Number(b.lastActivityMillis || 0) - Number(a.lastActivityMillis || 0);
  });
}, [
  customerLedgerSourceFilter,
  customerLedgerSortBy,
  customerLedgerSummaries,
  deferredCustomerLedgerSearch,
]);

const selectedCustomerLedger =
  customerLedgerSummaries.find((customer) => customer.id === selectedCustomerLedgerId) ||
  filteredCustomerLedgerSummaries[0] ||
  null;

useEffect(() => {
  if (selectedCustomerLedgerId && customerLedgerSummaries.some((customer) => customer.id === selectedCustomerLedgerId)) {
    return;
  }
  setSelectedCustomerLedgerId(filteredCustomerLedgerSummaries[0]?.id || "");
}, [customerLedgerSummaries, filteredCustomerLedgerSummaries, selectedCustomerLedgerId]);

const openCustomerLedger = useCallback(
  (customerId) => {
    if (!customerId) {
      notify("This record is not linked to a customer ledger yet.", "info");
      return;
    }
    setSelectedCustomerLedgerId(customerId);
    setRoutedTab("customerLedger");
  },
  [notify, setRoutedTab]
);

const saveCustomerSiteLocations = useCallback(
  async (customerId, nextLocations = []) => {
    if (!workspaceUid || !customerId) {
      notify("Select a customer before saving a location.", "warning");
      return false;
    }

    const normalizedLocations = normalizeSiteLocations(nextLocations).map(
      (location) => ({
        ...location,
        updatedAt: location.updatedAt || new Date().toISOString(),
        updatedBy: location.updatedBy || user?.uid || user?.email || "",
      })
    );

    try {
      await setDoc(
        doc(db, "users", workspaceUid, "customers", customerId),
        {
          siteLocations: sanitizeCrmFirestoreValue(normalizedLocations),
          updatedAt: serverTimestamp(),
          lastActivityAt: serverTimestamp(),
          updatedBy: user?.uid || user?.email || "",
        },
        { merge: true }
      );

      setCustomerRecords((prev) =>
        prev.map((customer) =>
          customer.id === customerId
            ? {
                ...customer,
                siteLocations: normalizedLocations,
                updatedAt: new Date(),
                lastActivityAt: new Date(),
              }
            : customer
        )
      );
      invalidateWorkspaceCollectionCache(workspaceUid, "customers");
      return true;
    } catch (error) {
      console.error("Failed to save customer site location:", error, {
        customerId,
      });
      notify("Could not save site location. Please try again.", "error");
      return false;
    }
  },
  [notify, user, workspaceUid]
);

const backfillCustomerLedgers = useCallback(async () => {
  if (!workspaceUid) return;

  const quotesToLink = savedQuotes.filter((quote) => !quote.customerId && !isDeletedQuoteRecord(quote));
  const leadsToLink = crmLeadRecords.filter((lead) => !lead.customerId);
  const totalRecords = quotesToLink.length + leadsToLink.length;

  if (totalRecords === 0) {
    notify("All visible records are already linked to customer ledgers.", "success");
    return;
  }

  openConfirmDialog({
    title: "Repair quote customer links?",
    description: `${totalRecords} visible quote or lead record${totalRecords === 1 ? "" : "s"} will be linked to non-destructive customer ledger records. Existing quotes and leads will not be deleted.`,
    confirmLabel: "Repair Links",
    cancelLabel: "Cancel",
    tone: "info",
    onConfirm: async () => {
      try {
        for (const quote of quotesToLink) {
          const customer = await ensureCustomerLedgerRecord(getCustomerIdentityFromQuote(quote), {
            extraQuote: quote,
          });
          if (!customer?.id) continue;
          await updateDoc(doc(db, "users", workspaceUid, "quotations", quote.id), {
            customerId: customer.id,
            customerKey: customer.customerKey || customer.id,
            customerName: customer.customerName,
            customerPhone: customer.phone || customer.whatsapp || "",
            customerLedgerLinked: true,
            "crm.customerId": customer.id,
            "crm.customerKey": customer.customerKey || customer.id,
            "crm.customerName": customer.customerName,
            "crm.customerPhone": customer.phone || customer.whatsapp || "",
            updatedAt: serverTimestamp(),
          });
          applyQuoteLocalPatch(quote.id, {
            customerId: customer.id,
            customerKey: customer.customerKey || customer.id,
            customerName: customer.customerName,
            customerPhone: customer.phone || customer.whatsapp || "",
            customerLedgerLinked: true,
            crm: {
              ...(quote.crm || {}),
              customerId: customer.id,
              customerKey: customer.customerKey || customer.id,
              customerName: customer.customerName,
              customerPhone: customer.phone || customer.whatsapp || "",
            },
            updatedAt: new Date(),
          });
        }

        for (const lead of leadsToLink) {
          const customer = await ensureCustomerLedgerRecord(
            getCustomerIdentityFromLeadDraft({
              title: lead.title,
              companyOrProject: lead.companyOrProject,
              contact: lead.contact || lead.crmContact || {},
              source: lead.source,
              tags: Array.isArray(lead.tags) ? lead.tags.join(", ") : "",
            }),
            { extraLead: lead }
          );
          if (!customer?.id) continue;
          await updateDoc(doc(db, "users", workspaceUid, "crm_leads", lead.id), {
            customerId: customer.id,
            customerKey: customer.customerKey || customer.id,
            customerName: customer.customerName,
            customerPhone: customer.phone || customer.whatsapp || "",
            ...(isDevRuntime
              ? {
                  _lastWriteSource: "customerLedgerBackfill",
                  _lastWriteAt: serverTimestamp(),
                }
              : {}),
            updatedAt: serverTimestamp(),
          });
          applyCrmLeadLocalPatch(lead.id, {
            customerId: customer.id,
            customerKey: customer.customerKey || customer.id,
            customerName: customer.customerName,
            customerPhone: customer.phone || customer.whatsapp || "",
            updatedAt: new Date(),
          });
        }

        notify("Quote customer links repaired", "success");
      } catch (error) {
        console.error("Quote customer link repair failed:", error);
        notify("Could not repair quote customer links. Please try again.", "error");
      }
    },
  });
}, [
  applyCrmLeadLocalPatch,
  applyQuoteLocalPatch,
  crmLeadRecords,
  ensureCustomerLedgerRecord,
  getCustomerIdentityFromLeadDraft,
  getCustomerIdentityFromQuote,
  notify,
  openConfirmDialog,
  savedQuotes,
  workspaceUid,
]);

const addCustomerLedgerPayment = useCallback(async () => {
  if (!workspaceUid || !selectedCustomerLedger?.id) return;
  const amount = Number(customerLedgerPaymentDraft.amount || 0);
  if (!Number.isFinite(amount) || amount <= 0) {
    notify("Please enter a valid payment amount.", "warning");
    return;
  }

  setIsSavingCustomerPayment(true);
  try {
    const entryId = createClientId("ledger-entry");
    const entryRef = doc(
      db,
      "users",
      workspaceUid,
      "customers",
      selectedCustomerLedger.id,
      "ledger_entries",
      entryId
    );
    const entryPayload = sanitizeCrmFirestoreValue({
      id: entryId,
      type: "payment",
      title: "Payment received",
      amount,
      date: customerLedgerPaymentDraft.date || todayISO(),
      notes: customerLedgerPaymentDraft.notes || "",
      createdBy: user?.uid || user?.email || "",
    });
    const receivedAmount =
      Number(selectedCustomerLedger.totals?.receivedAmount || 0) + amount;
    const pendingAmount = Math.max(
      0,
      Number(selectedCustomerLedger.totals?.totalQuoteValue || 0) - receivedAmount
    );

    await setDoc(entryRef, {
      ...entryPayload,
      createdAt: serverTimestamp(),
    });
    await setDoc(
      doc(db, "users", workspaceUid, "customers", selectedCustomerLedger.id),
      {
        totals: {
          ...(selectedCustomerLedger.totals || {}),
          receivedAmount,
          pendingAmount,
        },
        lastActivityAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    setCustomerRecords((prev) =>
      prev.map((customer) =>
        customer.id === selectedCustomerLedger.id
          ? {
              ...customer,
              totals: {
                ...(customer.totals || {}),
                receivedAmount,
                pendingAmount,
              },
              lastActivityAt: new Date(),
              updatedAt: new Date(),
            }
          : customer
      )
    );
    invalidateWorkspaceCollectionCache(workspaceUid, "customers");
    setCustomerLedgerPaymentDraft({ amount: "", date: todayISO(), notes: "" });
    notify("Payment added", "success");
  } catch (error) {
    console.error("Customer payment save failed:", error);
    notify("Could not add payment. Please try again.", "error");
  } finally {
    setIsSavingCustomerPayment(false);
  }
}, [
  customerLedgerPaymentDraft,
  notify,
  selectedCustomerLedger,
  user,
  workspaceUid,
]);

const visibleCrmLeadDeals = useMemo(
  () => crmFilteredLeadDeals.slice(0, crmLeadsVisibleCount),
  [crmFilteredLeadDeals, crmLeadsVisibleCount]
);

const hasMoreCrmLeadDeals =
  visibleCrmLeadDeals.length < crmFilteredLeadDeals.length;

useEffect(() => {
  setCrmLeadsVisibleCount(CRM_LEADS_PAGE_SIZE);
}, [
  crmLeadAssignedFilter,
  crmLeadFromDate,
  crmLeadSourceFilter,
  crmLeadStageFilter,
  crmLeadToDate,
  crmUnifiedLeadDeals.length,
  deferredCrmLeadSearch,
]);

const crmTaskHistoryCompletedByOptions = useMemo(() => {
  const options = new Map();

  crmTaskHistoryRecords.forEach((record) => {
    const key = String(
      record.completedByUid || record.completedByEmail || record.completedByName || ""
    ).trim();
    if (!key || options.has(key)) return;

    options.set(key, {
      key,
      label:
        record.completedByName ||
        record.completedByEmail ||
        record.completedByUid ||
        "Workspace User",
    });
  });

  return [...options.values()].sort((a, b) => a.label.localeCompare(b.label));
}, [crmTaskHistoryRecords]);

const crmFilteredTaskHistoryRecords = useMemo(() => {
  const queryText = deferredCrmTaskHistorySearch.trim().toLowerCase();
  const fromMillis = crmTaskHistoryFromDate
    ? safeTimeMillis(parseDateInputValue(crmTaskHistoryFromDate))
    : 0;
  const toMillisValue = crmTaskHistoryToDate
    ? safeTimeMillis(parseDateInputValue(crmTaskHistoryToDate)) + 24 * 60 * 60 * 1000 - 1
    : 0;

  return crmTaskHistoryRecords
    .filter((record) => {
      if (!queryText) return true;

      return [
        record.taskTitle,
        record.taskDescription,
        record.customerName,
        record.quoteNo,
        record.completedByName,
        record.completedByEmail,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(queryText));
    })
    .filter((record) => {
      if (crmTaskHistoryTypeFilter === "all") return true;
      return String(record.taskType || "") === crmTaskHistoryTypeFilter;
    })
    .filter((record) => {
      if (crmTaskHistoryCompletedByFilter === "all") return true;
      const actorKey = String(
        record.completedByUid || record.completedByEmail || record.completedByName || ""
      ).trim();
      return actorKey === crmTaskHistoryCompletedByFilter;
    })
    .filter((record) => {
      const completedMillis = safeTimeMillis(record.completedAt);
      if (fromMillis && completedMillis < fromMillis) return false;
      if (toMillisValue && completedMillis > toMillisValue) return false;
      return true;
    });
}, [
  crmTaskHistoryCompletedByFilter,
  crmTaskHistoryFromDate,
  crmTaskHistoryRecords,
  crmTaskHistoryToDate,
  crmTaskHistoryTypeFilter,
  deferredCrmTaskHistorySearch,
]);

const selectedCrmTaskHistoryRecord = useMemo(
  () =>
    crmFilteredTaskHistoryRecords.find((record) => record.id === selectedCrmTaskHistoryId) ||
    null,
  [crmFilteredTaskHistoryRecords, selectedCrmTaskHistoryId]
);

useEffect(() => {
  if (crmFilteredTaskHistoryRecords.length === 0) {
    if (selectedCrmTaskHistoryId) {
      setSelectedCrmTaskHistoryId("");
    }
    return;
  }

  if (
    !selectedCrmTaskHistoryId ||
    !crmFilteredTaskHistoryRecords.some((record) => record.id === selectedCrmTaskHistoryId)
  ) {
    setSelectedCrmTaskHistoryId(crmFilteredTaskHistoryRecords[0].id);
  }
}, [crmFilteredTaskHistoryRecords, selectedCrmTaskHistoryId]);

const crmVisibleDeals = useMemo(() => {
  if (crmView === "board") {
    return crmBoardDeals;
  }

  if (crmView === "leads") {
    return crmFilteredLeadDeals;
  }

  if (crmView === "list") {
    return crmFilteredDeals;
  }

  return crmScopeDeals;
}, [crmBoardDeals, crmFilteredDeals, crmFilteredLeadDeals, crmScopeDeals, crmView]);

const openNewCrmLeadModal = useCallback(
  (quoteType = crmScope === CRM_SCOPE_ALL ? crmPipelineType : crmScope) => {
    const nextDraft = createCrmLeadDraftState(quoteType);
    setCrmLeadDraft(nextDraft);
    markCrmLeadBaseline(nextDraft, "idle");
    setIsCrmLeadModalOpen(true);
  },
  [createCrmLeadDraftState, crmPipelineType, crmScope, markCrmLeadBaseline]
);

const closeNewCrmLeadModal = useCallback(() => {
  persistCrmLeadDraftLocally("route-change");
  setIsCrmLeadModalOpen(false);
  setCrmLeadDraft(null);
}, [persistCrmLeadDraftLocally]);

const saveNewCrmLead = useCallback(async () => {
  if (!crmLeadDraft) {
    return;
  }

  if (!workspaceUid) {
    notify("Please sign in before creating a lead.", "warning");
    return;
  }

  const contactName = String(crmLeadDraft.contact?.name || "").trim();
  const leadTitle = String(crmLeadDraft.title || "").trim();

  if (!contactName && !leadTitle) {
    notify("Please fill required fields.", "warning");
    return;
  }

  const normalizedQuoteType = isGeneralExecutionEligibleDeal({
    pipelineType: crmLeadDraft.pipelineType || crmLeadDraft.quoteType,
    quoteType: crmLeadDraft.quoteType,
  })
    ? GENERAL_QUOTE_TYPE
    : CURTAIN_QUOTE_TYPE;
  const pipeline = getCrmPipeline(crmPipelines, normalizedQuoteType);
  const requestedStageId = String(crmLeadDraft.stageId || "").trim();
  const stageMeta =
    pipeline.stages.find((stage) => stage.id === requestedStageId) ||
    pipeline.stages[0] ||
    null;

  if (!normalizedQuoteType || !stageMeta?.id) {
    notify("Please fill required fields.", "warning");
    return;
  }

  const parsedNextActionDate = crmLeadDraft.nextActionDate
    ? parseDateInputValue(crmLeadDraft.nextActionDate)
    : null;
  if (crmLeadDraft.nextActionDate && !parsedNextActionDate) {
    notify("Please select a valid next action date.", "warning");
    return;
  }

  setIsSavingCrmLead(true);

  let leadPayloadForLog = null;
  let leadCreatePayloadForLog = null;

  try {
    const assignedMember = getTeamMemberById(crmLeadDraft.assignedToUserId);
    const tags = String(crmLeadDraft.tags || "")
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);
    const leadId = createClientId("crm-lead");
    const expectedValue = Number(crmLeadDraft.expectedValue || 0);
    const customerLedger = await ensureCustomerLedgerRecord(
      getCustomerIdentityFromLeadDraft({
        ...crmLeadDraft,
        tags: tags.join(", "),
      })
    );
    const leadPayload = buildCrmLeadData(
      {
        id: leadId,
        createdAt: new Date(),
      },
      {
        title: crmLeadDraft.title,
        companyOrProject: crmLeadDraft.companyOrProject,
        contact: crmLeadDraft.contact,
        source: crmLeadDraft.source,
        quoteType: normalizedQuoteType,
        pipelineType: normalizedQuoteType,
        stageId: stageMeta.id,
        customerId: customerLedger?.id || "",
        customerName:
          customerLedger?.customerName ||
          contactName ||
          leadTitle ||
          "",
        customerPhone:
          customerLedger?.phone ||
          customerLedger?.whatsapp ||
          crmLeadDraft.contact?.phone ||
          crmLeadDraft.contact?.whatsapp ||
          "",
        expectedValue: Number.isFinite(expectedValue) ? expectedValue : 0,
        nextAction: crmLeadDraft.nextAction,
        nextActionDate: parsedNextActionDate,
        assignedToUserId: crmLeadDraft.assignedToUserId || "",
        assignedToNameSnapshot: assignedMember?.name || "",
        assignedAt: crmLeadDraft.assignedToUserId ? new Date() : null,
        assignedBy: crmLeadDraft.assignedToUserId
          ? user?.email || user?.uid || ""
          : "",
        notes: crmLeadDraft.notes,
        tags,
        linkedQuotes: [],
        hasQuote: false,
        reminderConfigs: crmLeadDraft.reminderConfigs,
        followUps: [],
        attachments: crmLeadDraft.attachments,
        executionSpecs: crmLeadDraft.executionSpecs,
        siteLocations: crmLeadDraft.siteLocations,
      }
    );
    leadPayloadForLog = leadPayload;
    const leadRef = doc(db, "users", workspaceUid, "crm_leads", leadId);
    const searchIndexFields = buildCrmSearchIndexFields(leadPayload);
    const leadCreatePayload = sanitizeCrmFirestoreValue({
      title: leadPayload.title,
      contact: leadPayload.contact,
      companyOrProject: leadPayload.companyOrProject,
      source: leadPayload.source,
      quoteType: leadPayload.quoteType,
      pipelineType: leadPayload.pipelineType,
      stageId: leadPayload.stageId,
      stageNameSnapshot: leadPayload.stageNameSnapshot,
      customerId: leadPayload.customerId || "",
      customerName: leadPayload.customerName || leadPayload.contact?.name || "",
      customerPhone:
        leadPayload.customerPhone ||
        leadPayload.contact?.phone ||
        leadPayload.contact?.whatsapp ||
        "",
      linkedQuotes: [],
      hasQuote: false,
      checklistTemplateId: leadPayload.checklistTemplateId || "",
      checklistItems: normalizeChecklistTree(leadPayload.checklistItems || []),
      expectedValue: Number(leadPayload.expectedValue || 0),
      finalValue: Number(leadPayload.finalValue || 0),
      nextAction: leadPayload.nextAction || "",
      nextActionDate: leadPayload.nextActionDate || null,
      assignedToUserId: leadPayload.assignedToUserId || "",
      assignedToNameSnapshot: leadPayload.assignedToNameSnapshot || "",
      assignedAt: leadPayload.assignedAt || null,
      assignedBy: leadPayload.assignedBy || "",
      tags: leadPayload.tags || [],
      notes: leadPayload.notes || "",
      installationDate: null,
      reminderConfigs: leadPayload.reminderConfigs || normalizeCrmReminderConfigs({}, {}),
      followUps: leadPayload.followUps || [],
      attachments: leadPayload.attachments || [],
      executionSpecs: leadPayload.executionSpecs || [],
      siteLocations: leadPayload.siteLocations || [],
      ...searchIndexFields,
      status: leadPayload.status || "draft",
    });
    leadCreatePayloadForLog = leadCreatePayload;

    await setDoc(leadRef, {
      ...leadCreatePayload,
      ...(isDevRuntime
        ? {
            _lastWriteSource: "createCrmLead",
            _lastWriteAt: serverTimestamp(),
          }
        : {}),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    await upsertCustomerSearchIndexRecord(
      {
        ...leadPayload,
        ...leadCreatePayload,
        id: leadId,
        crmLeadId: leadId,
        leadId,
        sourceType: "crm_lead",
      },
      { sourceType: "crm_lead" }
    ).catch((error) => {
      if (isDevRuntime) {
        console.error("CUSTOMER SEARCH INDEX NEW LEAD UPSERT FAILED", {
          leadId,
          code: error?.code,
          message: error?.message,
        });
      }
    });
    invalidateWorkspaceCollectionCache(workspaceUid, "crm_leads");
    setCrmLeadRecords((prev) => [
      {
        ...leadPayload,
        ...searchIndexFields,
        id: leadId,
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      ...prev,
    ]);

    let reminderSyncFailed = false;
    await syncReminderDocsForDeal({
      ...leadPayload,
      id: leadId,
      crmRecordType: "lead",
      customerLabel:
        leadPayload.contact?.name ||
        leadPayload.companyOrProject ||
        leadPayload.title ||
        "Unnamed Lead",
      crmTitle: leadPayload.title,
      crmContact: leadPayload.contact,
      nextActionDate: leadPayload.nextActionDate,
      installationDate: null,
      reminderConfigs: leadPayload.reminderConfigs,
      attachments: leadPayload.attachments,
      siteLocations: leadPayload.siteLocations,
      pipelineType: leadPayload.pipelineType,
    }).catch((error) => {
      reminderSyncFailed = true;
      console.warn("CRM lead created, but reminder sync failed:", error);
    });

    setSelectedCrmDealId(leadId);
    if (crmView === "overview") {
      setCrmView("list");
    }
    setIsCrmDrawerPinned(true);
    setIsCrmDrawerHovered(false);
    closeNewCrmLeadModal();
    clearCrmLeadDraft();
    notify(
      reminderSyncFailed
        ? "Lead created, but reminders could not be scheduled."
        : "Lead created",
      reminderSyncFailed ? "warning" : "success"
    );
  } catch (error) {
    console.error("Create lead failed", error, {
      workspaceUid,
      leadPayload: leadCreatePayloadForLog || leadPayloadForLog,
    });
    notify("Could not create lead. Please try again.", "error");
  } finally {
    setIsSavingCrmLead(false);
  }
}, [
  buildCrmLeadData,
  clearCrmLeadDraft,
  closeNewCrmLeadModal,
  crmLeadDraft,
  crmPipelines,
  crmView,
  ensureCustomerLedgerRecord,
  getCustomerIdentityFromLeadDraft,
  getTeamMemberById,
  notify,
  syncReminderDocsForDeal,
  upsertCustomerSearchIndexRecord,
  user,
  workspaceUid,
]);






useEffect(() => {
  // Quote numbers are now generated ONLY when saving
  // This prevents duplicates and race conditions

  if (editingQuoteId) return;

  if (!customerName) return;

  // Do nothing  number will be generated in saveQuotation()
}, [editingQuoteId, customerName]);


useEffect(() => {
  console.log("editingQuoteId changed:", editingQuoteId);
}, [editingQuoteId]);

useEffect(() => {
  console.log("isNewQuote changed:", isNewQuote);
}, [isNewQuote]);

useEffect(() => {
  setGeneralQuote((prev) => {
    const normalized = syncGeneralQuoteDefaults(prev);
    return JSON.stringify(prev) === JSON.stringify(normalized) ? prev : normalized;
  });
}, [syncGeneralQuoteDefaults]);

useEffect(() => {
  if (!isMobile) {
    setIsNavOpen(false);
  }
}, [isMobile]);

useEffect(() => {
  if (crmView !== "board" && crmView !== "list" && crmView !== "leads") {
    setIsCrmDrawerHovered(false);
    setIsCrmDrawerPinned(false);
  }
}, [crmView]);

useEffect(() => {
  if (tab === "executionSpecs") {
    return;
  }

  if (
    isDevRuntime &&
    selectedCrmDealId &&
    !crmVisibleDeals.some((deal) => String(deal.id || "") === String(selectedCrmDealId))
  ) {
    console.warn("CRM drawer selected deal is outside the current visible list", {
      selectedCrmDealId,
      drawerOpen: isCrmDrawerPinned || isCrmDrawerHovered || isCompact,
      lastKnownDealName:
        selectedCrmDealCacheRef.current?.crmTitle ||
        selectedCrmDealCacheRef.current?.customerLabel ||
        selectedCrmDealCacheRef.current?.title ||
        "",
      activeFilters: {
        tab,
        crmView,
        crmScope,
        crmPipelineType,
        crmStageFilter,
        crmTypeFilter,
        crmSourceFilter,
        crmSearch,
        crmLeadSearch,
      },
      visibleCount: crmVisibleDeals.length,
    });
  }
}, [
  crmLeadSearch,
  crmPipelineType,
  crmScope,
  crmSearch,
  crmSourceFilter,
  crmStageFilter,
  crmTypeFilter,
  crmView,
  crmVisibleDeals,
  isCompact,
  isCrmDrawerHovered,
  isCrmDrawerPinned,
  selectedCrmDealId,
  tab,
]);

useEffect(() => {
  if (crmScope === CRM_SCOPE_ALL) return;
  if (crmPipelineType === crmScope) return;
  setCrmPipelineType(crmScope);
}, [crmPipelineType, crmScope]);

useEffect(() => {
  if (crmScope !== CRM_SCOPE_ALL) return;
  if (crmView !== "board" && crmView !== "settings") return;

  if (crmStageFilter !== "all") {
    const [pipelineType] = String(crmStageFilter).split("::");
    if (pipelineType !== activeCrmPipelineType) {
      setCrmStageFilter("all");
    }
  }
}, [activeCrmPipelineType, crmScope, crmStageFilter, crmView]);

useEffect(() => {
  if (!selectedCrmDeal) {
    if (selectedCrmDealId) {
      if (isDevRuntime) {
        console.warn("CRM drawer lost selected deal", {
          selectedCrmDealId,
          drawerOpen: isCrmDrawerPinned || isCrmDrawerHovered || isCompact,
          lastKnownDealName:
            selectedCrmDealCacheRef.current?.crmTitle ||
            selectedCrmDealCacheRef.current?.customerLabel ||
            selectedCrmDealCacheRef.current?.title ||
            "",
          activeFilters: {
            tab,
            crmView,
            crmScope,
            crmPipelineType,
            crmStageFilter,
            crmTypeFilter,
            crmSourceFilter,
            crmSearch,
            crmLeadSearch,
          },
          crmLeadRecordsCount: crmLeadRecords.length,
        });
      }
      return;
    }

    if (isDevRuntime && crmDetailDraft) {
      console.warn("CRM DETAILS SELECTION CLEARED", {
        reason: "no_selected_crm_deal_id",
        previousSelectedCrmDealId: selectedCrmDealIdRef.current,
        drawerOpen: isCrmDrawerPinned || isCrmDrawerHovered || isCompact,
      });
    }
    setCrmDetailDraft(null);
    crmDetailDraftDealIdRef.current = "";
    crmDetailDraftDirtyRef.current = false;
    executionDetailsPersistedSignatureRef.current = "";
    updateLocalDraftStatus(LOCAL_DRAFT_MODULES.EXECUTION_DETAILS, "idle", null);
    return;
  }

  const nextDealId = String(selectedCrmDeal.id || "");
  const isSameSelectedDeal = crmDetailDraftDealIdRef.current === nextDealId;
  if (
    isSameSelectedDeal &&
    (isSavingCrmDetails || crmDetailDraftDirtyRef.current || executionDetailsDraftIsDirty)
  ) {
    return;
  }

  const nextDraft = buildCrmDetailDraftFromDeal(selectedCrmDeal);
  crmDetailDraftDealIdRef.current = nextDealId;
  setCrmDetailDraft(nextDraft);
  markExecutionDetailsBaseline(nextDraft, "saved");
}, [
  buildCrmDetailDraftFromDeal,
  crmDetailDraft,
  crmLeadRecords.length,
  crmLeadSearch,
  crmPipelineType,
  crmScope,
  crmSearch,
  crmSourceFilter,
  crmStageFilter,
  crmTypeFilter,
  crmView,
  executionDetailsDraftIsDirty,
  isCompact,
  isCrmDrawerHovered,
  isCrmDrawerPinned,
  isSavingCrmDetails,
  markExecutionDetailsBaseline,
  selectedCrmDeal,
  selectedCrmDealId,
  tab,
  updateLocalDraftStatus,
]);

useEffect(() => {
  if (tab !== "executionSpecs") return;

  const executionItems = Array.isArray(crmDetailDraft?.executionSpecs)
    ? crmDetailDraft.executionSpecs
    : [];

  if (executionItems.length === 0) {
    if (selectedExecutionItemId) {
      setSelectedExecutionItemId("");
    }
    return;
  }

  if (!selectedExecutionItemId || !executionItems.some((item) => item.id === selectedExecutionItemId)) {
    setSelectedExecutionItemId(executionItems[0].id);
  }
}, [crmDetailDraft, selectedExecutionItemId, tab]);

useEffect(() => {
  const checklistItemId = pendingChecklistFocusIdRef.current;
  if (!checklistItemId) return undefined;

  const frameId = window.requestAnimationFrame(() => {
    const element = checklistInputRefs.current.get(checklistItemId);
    if (element) {
      element.focus();
      element.select?.();
      pendingChecklistFocusIdRef.current = "";
    }
  });

  return () => window.cancelAnimationFrame(frameId);
}, [crmDetailDraft?.checklistItems]);

useEffect(() => {
  const selectedExecutionType = selectedExecutionItemId
    ? crmDetailDraft?.executionSpecs?.find((item) => item.id === selectedExecutionItemId)
        ?.itemType || ""
    : "";
  if (!normalizeExecutionTemplateItemType(selectedExecutionType)) {
    setSelectedExecutionTemplateId("");
    return;
  }

  setSelectedExecutionTemplateId(
    getExecutionTemplateForType(selectedExecutionType)?.id || ""
  );
}, [crmDetailDraft, getExecutionTemplateForType, selectedExecutionItemId]);

useEffect(() => {
  if (executionTemplateCatalog.length === 0) {
    if (selectedExecutionTemplateManagerId) {
      setSelectedExecutionTemplateManagerId("");
    }
    return;
  }

  if (
    !selectedExecutionTemplateManagerId ||
    !executionTemplateCatalog.some((template) => template.id === selectedExecutionTemplateManagerId)
  ) {
    setSelectedExecutionTemplateManagerId(executionTemplateCatalog[0].id);
  }
}, [executionTemplateCatalog, selectedExecutionTemplateManagerId]);

useEffect(() => {
  if (!generalQuotePersistedSignatureRef.current) {
    generalQuotePersistedSignatureRef.current = serializeGeneralQuoteDraftData(generalQuote);
  }
}, [generalQuote, serializeGeneralQuoteDraftData]);

useEffect(() => {
  if (!curtainQuotePersistedSignatureRef.current) {
    curtainQuotePersistedSignatureRef.current = serializeCurtainQuoteDraftData();
  }
}, [serializeCurtainQuoteDraftData]);

useEffect(() => {
  if (
    tab === "quote" &&
    quoteSubTab === GENERAL_QUOTE_TYPE &&
    generalQuoteIsDirty
  ) {
    if (autoSaveStatus === "saved") {
      setAutoSaveStatus("idle");
    }
    updateLocalDraftStatus(LOCAL_DRAFT_MODULES.GENERAL_QUOTE, "dirty", null);
  }
}, [autoSaveStatus, generalQuoteIsDirty, quoteSubTab, tab, updateLocalDraftStatus]);

useEffect(() => {
  if (
    tab === "quote" &&
    quoteSubTab === CURTAIN_QUOTE_TYPE &&
    curtainQuoteIsDirty
  ) {
    if (autoSaveStatus === "saved") {
      setAutoSaveStatus("idle");
    }
    updateLocalDraftStatus(LOCAL_DRAFT_MODULES.CURTAIN_QUOTE, "dirty", null);
  }
}, [autoSaveStatus, curtainQuoteIsDirty, quoteSubTab, tab, updateLocalDraftStatus]);

useEffect(() => {
  if (isCrmLeadModalOpen && crmLeadDraftIsDirty) {
    updateLocalDraftStatus(LOCAL_DRAFT_MODULES.CRM_LEAD, "dirty", null);
  }
}, [crmLeadDraftIsDirty, isCrmLeadModalOpen, updateLocalDraftStatus]);

useEffect(() => {
  if ((tab === "executionSpecs" || tab === "crm") && executionDetailsDraftIsDirty) {
    updateLocalDraftStatus(LOCAL_DRAFT_MODULES.EXECUTION_DETAILS, "dirty", null);
  }
}, [executionDetailsDraftIsDirty, tab, updateLocalDraftStatus]);

useEffect(() => {
  if (tab === "executionTemplates" && executionTemplatesDraftIsDirty) {
    updateLocalDraftStatus(LOCAL_DRAFT_MODULES.EXECUTION_TEMPLATE, "dirty", null);
  }
}, [executionTemplatesDraftIsDirty, tab, updateLocalDraftStatus]);

useEffect(() => {
  if (tab !== "quote" || quoteSubTab !== GENERAL_QUOTE_TYPE || !generalQuoteDraftKey) {
    return;
  }

  promptToRestoreLocalDraft({
    module: LOCAL_DRAFT_MODULES.GENERAL_QUOTE,
    key: generalQuoteDraftKey,
    title: "Unsaved work found",
    description:
      "We found changes that were not saved. Would you like to continue where you left off?",
    serverUpdatedAt: getUpdatedMillis(selectedQuote),
    onRestore: (draftRecord) => {
      const restoredQuote = syncGeneralQuoteDefaults({
        ...(draftRecord?.data || {}),
        quoteDate: resolveQuoteDateForInput(draftRecord?.data || {}, todayISO()),
      });
      setGeneralQuote(restoredQuote);
      setCustomerName(
        restoredQuote.quoteFor?.company ||
          restoredQuote.quoteFor?.name ||
          ""
      );
      setQuoteNo(restoredQuote.quoteNo || "");
      setDate(resolveQuoteDateForInput(restoredQuote, todayISO()));
      generalQuotePersistedSignatureRef.current =
        serializeGeneralQuoteDraftData(restoredQuote);
      updateLocalDraftStatus(
        LOCAL_DRAFT_MODULES.GENERAL_QUOTE,
        "autosaved",
        draftRecord?.updatedAt || null
      );
    },
  });
}, [
  generalQuoteDraftKey,
  promptToRestoreLocalDraft,
  quoteSubTab,
  selectedQuote,
  serializeGeneralQuoteDraftData,
  syncGeneralQuoteDefaults,
  tab,
  updateLocalDraftStatus,
]);

useEffect(() => {
  if (tab !== "quote" || quoteSubTab !== CURTAIN_QUOTE_TYPE || !curtainQuoteDraftKey) {
    return;
  }

  promptToRestoreLocalDraft({
    module: LOCAL_DRAFT_MODULES.CURTAIN_QUOTE,
    key: curtainQuoteDraftKey,
    title: "Unsaved curtain quotation found",
    description:
      "We found changes that were not saved. Would you like to continue where you left off?",
    restoreLabel: "Restore Draft",
    discardLabel: "Discard",
    serverNewerDescription:
      "This quote was saved more recently than the recovered draft. Restore anyway?",
    serverUpdatedAt: getUpdatedMillis(selectedQuote),
    onRestore: restoreCurtainQuoteDraft,
  });
}, [
  curtainQuoteDraftKey,
  promptToRestoreLocalDraft,
  quoteSubTab,
  restoreCurtainQuoteDraft,
  selectedQuote,
  tab,
]);

useEffect(() => {
  if (!isCrmLeadModalOpen || !crmLeadDraftKey) return;

  promptToRestoreLocalDraft({
    module: LOCAL_DRAFT_MODULES.CRM_LEAD,
    key: crmLeadDraftKey,
    title: "Unsaved work found",
    description:
      "We found changes that were not saved. Would you like to continue where you left off?",
    onRestore: (draftRecord) => {
      const restoredDraft = draftRecord?.data || createCrmLeadDraftState();
      setCrmLeadDraft(restoredDraft);
      crmLeadPersistedSignatureRef.current =
        serializeCrmLeadDraftData(restoredDraft);
      updateLocalDraftStatus(
        LOCAL_DRAFT_MODULES.CRM_LEAD,
        "autosaved",
        draftRecord?.updatedAt || null
      );
    },
  });
}, [
  crmLeadDraftKey,
  createCrmLeadDraftState,
  isCrmLeadModalOpen,
  promptToRestoreLocalDraft,
  serializeCrmLeadDraftData,
  updateLocalDraftStatus,
]);

useEffect(() => {
  if (tab !== "executionSpecs" || !executionDetailsDraftKey || !selectedCrmDealId) {
    return;
  }

  promptToRestoreLocalDraft({
    module: LOCAL_DRAFT_MODULES.EXECUTION_DETAILS,
    key: executionDetailsDraftKey,
    title: "Unsaved work found",
    description:
      "We found changes that were not saved. Would you like to continue where you left off?",
    serverUpdatedAt: getUpdatedMillis(selectedCrmDeal),
    onRestore: (draftRecord) => {
      const restoredDraft = draftRecord?.data || null;
      if (!restoredDraft) return;
      setCrmDetailDraft(restoredDraft);
      executionDetailsPersistedSignatureRef.current =
        serializeExecutionDetailsDraftData(restoredDraft);
      updateLocalDraftStatus(
        LOCAL_DRAFT_MODULES.EXECUTION_DETAILS,
        "autosaved",
        draftRecord?.updatedAt || null
      );
    },
  });
}, [
  executionDetailsDraftKey,
  promptToRestoreLocalDraft,
  selectedCrmDeal,
  selectedCrmDealId,
  serializeExecutionDetailsDraftData,
  tab,
  updateLocalDraftStatus,
]);

useEffect(() => {
  if (tab !== "executionTemplates" || !executionTemplatesDraftKey) return;
  if (
    !executionTemplatesLoadState.loaded ||
    executionTemplatesLoadState.workspaceUid !== workspaceUid
  ) {
    return;
  }

  const existingDraft = loadLocalDraft(executionTemplatesDraftKey);
  const draftTemplates = normalizeExecutionTemplateDrafts(
    existingDraft?.data?.templates || []
  );
  if (existingDraft && draftTemplates.length === 0 && crmExecutionTemplates.length > 0) {
    if (isDevRuntime) {
      console.warn("IGNORING EMPTY EXECUTION TEMPLATE LOCAL DRAFT", {
        workspaceUid,
        serverTemplateCount: crmExecutionTemplates.length,
        draftKey: executionTemplatesDraftKey,
      });
    }
    clearLocalDraftRecord(executionTemplatesDraftKey);
    return;
  }

  promptToRestoreLocalDraft({
    module: LOCAL_DRAFT_MODULES.EXECUTION_TEMPLATE,
    key: executionTemplatesDraftKey,
    title: "Unsaved work found",
    description:
      "We found changes that were not saved. Would you like to continue where you left off?",
    onRestore: (draftRecord) => {
      const restoredTemplates = normalizeExecutionTemplateDrafts(
        draftRecord?.data?.templates || []
      );
      const restoredSelectedId =
        draftRecord?.data?.selectedId || restoredTemplates[0]?.id || "";
      setCrmExecutionTemplates(restoredTemplates);
      setSelectedExecutionTemplateManagerId(restoredSelectedId);
      executionTemplatePersistedSignatureRef.current =
        serializeExecutionTemplatesDraftData(restoredTemplates, restoredSelectedId);
      updateLocalDraftStatus(
        LOCAL_DRAFT_MODULES.EXECUTION_TEMPLATE,
        "autosaved",
        draftRecord?.updatedAt || null
      );
    },
  });
}, [
  crmExecutionTemplates.length,
  executionTemplatesLoadState.loaded,
  executionTemplatesLoadState.workspaceUid,
  executionTemplatesDraftKey,
  promptToRestoreLocalDraft,
  serializeExecutionTemplatesDraftData,
  tab,
  updateLocalDraftStatus,
  workspaceUid,
]);

useEffect(() => {
  if (!(tab === "quote" && quoteSubTab === GENERAL_QUOTE_TYPE && generalQuoteIsDirty)) {
    return undefined;
  }

  const intervalId = window.setInterval(() => {
    persistGeneralQuoteDraft("interval");
  }, LOCAL_DRAFT_INTERVAL_MS);

  return () => window.clearInterval(intervalId);
}, [generalQuoteIsDirty, persistGeneralQuoteDraft, quoteSubTab, tab]);

useEffect(() => {
  if (!(tab === "quote" && quoteSubTab === CURTAIN_QUOTE_TYPE && curtainQuoteIsDirty)) {
    return undefined;
  }

  const intervalId = window.setInterval(() => {
    persistCurtainQuoteDraft("interval");
  }, LOCAL_DRAFT_INTERVAL_MS);

  return () => window.clearInterval(intervalId);
}, [curtainQuoteIsDirty, persistCurtainQuoteDraft, quoteSubTab, tab]);

useEffect(() => {
  if (!(isCrmLeadModalOpen && crmLeadDraftIsDirty)) {
    return undefined;
  }

  const intervalId = window.setInterval(() => {
    persistCrmLeadDraftLocally("interval");
  }, LOCAL_DRAFT_INTERVAL_MS);

  return () => window.clearInterval(intervalId);
}, [crmLeadDraftIsDirty, isCrmLeadModalOpen, persistCrmLeadDraftLocally]);

useEffect(() => {
  if (!(tab === "executionSpecs" && executionDetailsDraftIsDirty)) {
    return undefined;
  }

  const intervalId = window.setInterval(() => {
    persistExecutionDetailsDraftLocally("interval");
  }, LOCAL_DRAFT_INTERVAL_MS);

  return () => window.clearInterval(intervalId);
}, [executionDetailsDraftIsDirty, persistExecutionDetailsDraftLocally, tab]);

useEffect(() => {
  if (!(tab === "executionTemplates" && executionTemplatesDraftIsDirty)) {
    return undefined;
  }

  const intervalId = window.setInterval(() => {
    persistExecutionTemplatesDraftLocally("interval");
  }, LOCAL_DRAFT_INTERVAL_MS);

  return () => window.clearInterval(intervalId);
}, [executionTemplatesDraftIsDirty, persistExecutionTemplatesDraftLocally, tab]);

useEffect(() => {
  const previousRoute = previousRouteRef.current;

  if (
    previousRoute.tab === "quote" &&
    previousRoute.quoteSubTab === GENERAL_QUOTE_TYPE &&
    (tab !== "quote" || quoteSubTab !== GENERAL_QUOTE_TYPE)
  ) {
    persistGeneralQuoteDraft("route-change");
  }

  if (
    previousRoute.tab === "quote" &&
    previousRoute.quoteSubTab === CURTAIN_QUOTE_TYPE &&
    (tab !== "quote" || quoteSubTab !== CURTAIN_QUOTE_TYPE)
  ) {
    persistCurtainQuoteDraft("route-change");
  }

  if (previousRoute.tab === "executionSpecs" && tab !== "executionSpecs") {
    persistExecutionDetailsDraftLocally("route-change");
  }

  if (previousRoute.tab === "executionTemplates" && tab !== "executionTemplates") {
    persistExecutionTemplatesDraftLocally("route-change");
  }

  previousRouteRef.current = { tab, quoteSubTab };
}, [
  persistExecutionDetailsDraftLocally,
  persistExecutionTemplatesDraftLocally,
  persistCurtainQuoteDraft,
  persistGeneralQuoteDraft,
  quoteSubTab,
  tab,
]);

useEffect(() => {
  const handleBeforeUnload = () => {
    persistGeneralQuoteDraft("route-change");
    persistCurtainQuoteDraft("route-change");
    persistCrmLeadDraftLocally("route-change");
    persistExecutionDetailsDraftLocally("route-change");
    persistExecutionTemplatesDraftLocally("route-change");
  };

  window.addEventListener("beforeunload", handleBeforeUnload);
  return () => window.removeEventListener("beforeunload", handleBeforeUnload);
}, [
  persistCrmLeadDraftLocally,
  persistCurtainQuoteDraft,
  persistExecutionDetailsDraftLocally,
  persistExecutionTemplatesDraftLocally,
  persistGeneralQuoteDraft,
]);

useEffect(() => {
  if (!isStitchingSettingsDirty) return undefined;

  const handleBeforeUnload = (event) => {
    event.preventDefault();
    event.returnValue = "";
    return "";
  };

  window.addEventListener("beforeunload", handleBeforeUnload);
  return () => window.removeEventListener("beforeunload", handleBeforeUnload);
}, [isStitchingSettingsDirty]);



useEffect(() => {
  // Only auto-generate for NEW quotes
  if (editingQuoteId) return;
  if (!quoteSettings?.startingQuoteNumber) return;
  if (quoteNo) return; // do not override manual input

  const start = String(quoteSettings.startingQuoteNumber);

  // Find highest existing numeric suffix
  let max = null;

  savedQuotes.forEach((q) => {
    const match = String(q.quoteNo || "").match(/(\d+)$/);
    if (match) {
      const num = parseInt(match[1], 10);
      if (!Number.isNaN(num)) {
        max = max === null ? num : Math.max(max, num);
      }
    }
  });

  if (max === null) {
    setQuoteNo(start);
  } else {
    const prefix = start.replace(/\d+$/, "");
    setQuoteNo(prefix + (max + 1));
  }
}, [quoteSettings, savedQuotes, editingQuoteId, quoteNo]);





useEffect(() => {
  if (!workspaceUid) return;
  if (tab !== "quote" && tab !== "settings" && tab !== "products") return;
  if (quotationSettingsLoadedWorkspaceRef.current === workspaceUid) {
    if (quotationSettingsReadyWorkspace !== workspaceUid) {
      setQuotationSettingsReadyWorkspace(workspaceUid);
    }
    if (isDevRuntime) {
      console.log("Quotation settings load skipped; workspace already loaded", {
        workspaceUid,
      });
    }
    return;
  }
  if (quotationSettingsLoadingWorkspaceRef.current === workspaceUid) {
    if (isDevRuntime) {
      console.log("Quotation settings load skipped; workspace load already in flight", {
        workspaceUid,
      });
    }
    return;
  }
  quotationSettingsLoadingWorkspaceRef.current = workspaceUid;

  const loadSettings = async () => {
    try {
      logWorkspaceQuery("Quotation Settings", `users/${workspaceUid}/settings/quotation`);
      const ref = doc(db, "users", workspaceUid, "settings", "quotation");
      const snap = await getDocCached(ref, {
        label: "loadQuotationSettings",
        cacheKey: `users/${workspaceUid}/settings/quotation`,
        ttlMs: 5 * 60 * 1000,
      });
      if (isDevRuntime) {
        console.log("Quotation Settings exists:", snap.exists());
      }

      if (snap.exists()) {
        const data = snap.data();

        // Quote numbering
        setQuoteSettings({
          startNumber: data.startNumber || "",
          lastQuoteNumber: data.lastQuoteNumber || "",
        });

        // Fabric calculation settings
        if (data.fabricCalcSettings) {
          setFabricCalcSettings(
            normalizeFabricCalcSettingsForState(data.fabricCalcSettings)
          );
        }
      }
      quotationSettingsLoadedWorkspaceRef.current = workspaceUid;
      setQuotationSettingsReadyWorkspace(workspaceUid);
    } finally {
      if (quotationSettingsLoadingWorkspaceRef.current === workspaceUid) {
        quotationSettingsLoadingWorkspaceRef.current = "";
      }
    }
  };

  loadSettings().catch((error) => {
    console.error("Failed to load quotation settings:", error);
    if (isFirestoreResourceExhausted(error)) {
      raiseFirebaseHealthWarning("firestore", error, {
        path: `users/${workspaceUid}/settings/quotation`,
        source: "loadQuotationSettings",
      });
      notify("Firebase read limit reached. Please wait and try again.", "warning");
    }
  });
}, [
  logWorkspaceQuery,
  notify,
  quotationSettingsReadyWorkspace,
  raiseFirebaseHealthWarning,
  tab,
  workspaceUid,
]);

const loadFabricBrands = useCallback(
  async ({ force = false } = {}) => {
    if (!workspaceUid) return [];
    if (!force && fabricBrandsLoadedWorkspaceRef.current === workspaceUid) {
      return fabricBrandsRef.current;
    }
    if (!force && fabricBrandsLoadingWorkspaceRef.current === workspaceUid) {
      return fabricBrandsRef.current;
    }

    fabricBrandsLoadingWorkspaceRef.current = workspaceUid;
    setFabricBrandsLoading(true);
    try {
      const brandQuery = firestoreQuery(
        collection(db, "users", workspaceUid, "fabricBrands"),
        firestoreLimit(200)
      );
      logWorkspaceQuery("Fabric Brands", `users/${workspaceUid}/fabricBrands`);
      const snap = await getDocsCached(brandQuery, {
        label: "loadFabricBrands",
        cacheKey: `users/${workspaceUid}/fabricBrands:list:v1`,
        ttlMs: 5 * 60 * 1000,
      });
      const nextBrands = normalizeFabricBrands(
        snap.docs.map((brandDoc) => ({
          id: brandDoc.id,
          ...brandDoc.data(),
        }))
      );
      fabricBrandsRef.current = nextBrands;
      setFabricBrands(nextBrands);
      fabricBrandsLoadedWorkspaceRef.current = workspaceUid;
      return nextBrands;
    } catch (error) {
      console.error("FABRIC_BRANDS_LOAD_FAILED", {
        code: error?.code,
        message: error?.message,
        workspaceUid,
      });
      if (isFirestoreResourceExhausted(error)) {
        notify("Firebase read limit reached. Fabric brands will load later.", "warning");
      }
      return [];
    } finally {
      if (fabricBrandsLoadingWorkspaceRef.current === workspaceUid) {
        fabricBrandsLoadingWorkspaceRef.current = "";
      }
      setFabricBrandsLoading(false);
    }
  },
  [logWorkspaceQuery, notify, workspaceUid]
);

useEffect(() => {
  if (!workspaceUid) return;
  if (tab !== "quote" && tab !== "settings") return;
  loadFabricBrands();
}, [loadFabricBrands, tab, workspaceUid]);

const saveFabricBrandDraft = useCallback(async () => {
  if (!workspaceUid) {
    notify("Workspace is not ready. Please refresh and try again.", "warning");
    return;
  }
  const name = sanitizeEditableText(fabricBrandDraft.name || "");
  if (!name) {
    notify("Fabric brand name is required.", "warning");
    return;
  }

  try {
    const brandRef = doc(collection(db, "users", workspaceUid, "fabricBrands"));
    await setDoc(brandRef, {
      id: brandRef.id,
      name,
      nameLower: name.toLowerCase(),
      supplier: sanitizeEditableText(fabricBrandDraft.supplier || ""),
      notes: sanitizeEditableText(fabricBrandDraft.notes || ""),
      archived: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: user?.uid || "",
    });
    invalidateFirestoreReadCacheByPrefix(`users/${workspaceUid}/fabricBrands`);
    invalidateFirestoreReadCacheByPrefix(`users/${workspaceUid}/fabricBrands:list`);
    fabricBrandsLoadedWorkspaceRef.current = "";
    fabricCatalogueAllBrandsLoadedWorkspaceRef.current = "";
    setFabricBrandDraft({ name: "", supplier: "", notes: "" });
    await loadFabricBrands({ force: true });
    notify("Fabric brand added.", "success");
  } catch (error) {
    console.error("FABRIC_BRAND_SAVE_FAILED", {
      code: error?.code,
      message: error?.message,
      workspaceUid,
      name,
    });
    notify("Could not save fabric brand.", "error");
  }
}, [fabricBrandDraft, loadFabricBrands, notify, user?.uid, workspaceUid]);

const archiveFabricBrand = useCallback(
  async (brandId) => {
    if (!workspaceUid || !brandId) return;
    try {
      const brandRef = doc(db, "users", workspaceUid, "fabricBrands", brandId);
      await updateDoc(brandRef, {
        archived: true,
        updatedAt: serverTimestamp(),
      });
      invalidateFirestoreReadCacheByPrefix(`users/${workspaceUid}/fabricBrands`);
      invalidateFirestoreReadCacheByPrefix(`users/${workspaceUid}/fabricBrands:list`);
      fabricBrandsLoadedWorkspaceRef.current = "";
      fabricCatalogueAllBrandsLoadedWorkspaceRef.current = "";
      setFabricBrands((prev) => {
        const nextBrands = prev.filter((brand) => brand.id !== brandId);
        fabricBrandsRef.current = nextBrands;
        return nextBrands;
      });
      notify("Fabric brand archived.", "success");
    } catch (error) {
      console.error("FABRIC_BRAND_ARCHIVE_FAILED", {
        code: error?.code,
        message: error?.message,
        workspaceUid,
        brandId,
      });
      notify("Could not archive fabric brand.", "error");
    }
  },
  [notify, workspaceUid]
);

const getEmptyFabricCatalogueLibrary = useCallback(
  (brand = {}) => ({
    brandId: brand.id || "",
    brandName: brand.name || "",
    catalogues: [],
    rulesByCatalogueId: {},
    loadedAt: "",
  }),
  []
);

const getFabricBrandById = useCallback((brandId = "") => {
  const normalizedId = sanitizeEditableText(brandId);
  return (
    fabricBrandsRef.current.find((brand) => brand.id === normalizedId) ||
    fabricBrands.find((brand) => brand.id === normalizedId) ||
    null
  );
}, [fabricBrands]);

const loadFabricCatalogueLibrary = useCallback(
  async (brandId, { force = false } = {}) => {
    if (!workspaceUid || !brandId) return getEmptyFabricCatalogueLibrary();
    const brand = getFabricBrandById(brandId) || { id: brandId, name: "" };
    const loadedKey = `${workspaceUid}:${brandId}`;
    const cachedLibrary =
      fabricCatalogueLibraryRef.current[brandId] ||
      getEmptyFabricCatalogueLibrary(brand);

    if (
      !force &&
      fabricCatalogueLoadedBrandKeysRef.current.has(loadedKey)
    ) {
      return cachedLibrary;
    }

    if (
      !force &&
      fabricCatalogueLoadingBrandKeysRef.current.has(loadedKey)
    ) {
      return cachedLibrary;
    }

    if (force) {
      invalidateFirestoreReadCacheByPrefix(
        `users/${workspaceUid}/fabricBrands/${brandId}/catalogues`
      );
      fabricCatalogueLoadedBrandKeysRef.current.delete(loadedKey);
    }

    fabricCatalogueLoadingBrandKeysRef.current.add(loadedKey);
    try {
      const cataloguesPath = `users/${workspaceUid}/fabricBrands/${brandId}/catalogues`;
      const cataloguesQuery = firestoreQuery(
        collection(db, "users", workspaceUid, "fabricBrands", brandId, "catalogues"),
        firestoreLimit(200)
      );
      logWorkspaceQuery("Fabric Catalogues", cataloguesPath);
      const catalogueSnap = await getDocsCached(cataloguesQuery, {
        label: "loadFabricCataloguesForBrand",
        cacheKey: `${cataloguesPath}:list:v1`,
        ttlMs: 5 * 60 * 1000,
      });
      const catalogues = catalogueSnap.docs
        .map((catalogueDoc) =>
          normalizeFabricCatalogue(
            {
              id: catalogueDoc.id,
              brandId,
              brandName: brand.name || "",
              ...catalogueDoc.data(),
            },
            catalogueDoc.id
          )
        )
        .filter((catalogue) => catalogue.id && catalogue.catalogueName && catalogue.active)
        .sort((a, b) => a.catalogueName.localeCompare(b.catalogueName));

      const rulesEntries = await Promise.all(
        catalogues.map(async (catalogue) => {
          const rulesPath = `${cataloguesPath}/${catalogue.id}/srPriceRules`;
          const rulesQuery = firestoreQuery(
            collection(
              db,
              "users",
              workspaceUid,
              "fabricBrands",
              brandId,
              "catalogues",
              catalogue.id,
              "srPriceRules"
            ),
            firestoreLimit(500)
          );
          const rulesSnap = await getDocsCached(rulesQuery, {
            label: "loadFabricSrPriceRulesForCatalogue",
            cacheKey: `${rulesPath}:list:v1`,
            ttlMs: 5 * 60 * 1000,
          });
          const rules = rulesSnap.docs
            .map((ruleDoc) =>
              normalizeSrPriceRule(
                {
                  id: ruleDoc.id,
                  brandId,
                  brandName: brand.name || "",
                  catalogueId: catalogue.id,
                  catalogueName: catalogue.catalogueName,
                  catalogueKey: catalogue.catalogueKey,
                  ...ruleDoc.data(),
                },
                ruleDoc.id
              )
            )
            .filter((rule) => rule.id && rule.active)
            .sort((a, b) => getSrPriceRuleLabel(a).localeCompare(getSrPriceRuleLabel(b)));
          return [catalogue.id, rules];
        })
      );

      const library = {
        brandId,
        brandName: brand.name || "",
        catalogues,
        rulesByCatalogueId: Object.fromEntries(rulesEntries),
        loadedAt: new Date().toISOString(),
      };
      fabricCatalogueLibraryRef.current = {
        ...fabricCatalogueLibraryRef.current,
        [brandId]: library,
      };
      setFabricCatalogueLibraryByBrand((prev) => ({
        ...prev,
        [brandId]: library,
      }));
      fabricCatalogueLoadedBrandKeysRef.current.add(loadedKey);
      return library;
    } catch (error) {
      console.error("FABRIC_CATALOGUE_LIBRARY_LOAD_FAILED", {
        code: error?.code,
        message: error?.message,
        workspaceUid,
        brandId,
      });
      notify("Could not load fabric catalogues.", "error");
      return cachedLibrary;
    } finally {
      fabricCatalogueLoadingBrandKeysRef.current.delete(loadedKey);
    }
  },
  [
    getEmptyFabricCatalogueLibrary,
    getFabricBrandById,
    logWorkspaceQuery,
    notify,
    workspaceUid,
  ]
);

const loadAllFabricCatalogueLibraries = useCallback(
  async ({ force = false } = {}) => {
    if (!workspaceUid) return [];
    if (
      !force &&
      fabricCatalogueAllBrandsLoadedWorkspaceRef.current === workspaceUid
    ) {
      return Object.values(fabricCatalogueLibraryRef.current || {});
    }
    if (
      !force &&
      fabricCatalogueAllBrandsLoadingWorkspaceRef.current === workspaceUid
    ) {
      return Object.values(fabricCatalogueLibraryRef.current || {});
    }

    fabricCatalogueAllBrandsLoadingWorkspaceRef.current = workspaceUid;
    try {
      const brands = await loadFabricBrands({ force: false });
      const activeBrands = Array.isArray(brands) && brands.length ? brands : fabricBrandsRef.current;
      const libraries = await Promise.all(
        (activeBrands || [])
          .filter((brand) => brand?.id)
          .map((brand) => loadFabricCatalogueLibrary(brand.id, { force }))
      );
      fabricCatalogueAllBrandsLoadedWorkspaceRef.current = workspaceUid;
      return libraries.filter(Boolean);
    } finally {
      if (fabricCatalogueAllBrandsLoadingWorkspaceRef.current === workspaceUid) {
        fabricCatalogueAllBrandsLoadingWorkspaceRef.current = "";
      }
    }
  },
  [loadFabricBrands, loadFabricCatalogueLibrary, workspaceUid]
);

const saveFabricCatalogueDraft = useCallback(
  async (brand) => {
    if (!workspaceUid || !brand?.id) return;
    const draft = fabricCatalogueDrafts[brand.id] || {};
    const catalogueName = sanitizeEditableText(draft.catalogueName || "");
    const catalogueKey = normalizeCatalogueKey(catalogueName);
    if (!catalogueName || !catalogueKey) {
      notify("Catalogue name is required.", "warning");
      return;
    }

    const library =
      fabricCatalogueLibraryRef.current[brand.id] ||
      (await loadFabricCatalogueLibrary(brand.id));
    const duplicate = (library.catalogues || []).find(
      (catalogue) => catalogue.catalogueKey === catalogueKey
    );
    if (duplicate) {
      notify(`${catalogueName} already exists under ${brand.name}.`, "warning");
      return;
    }

    const savingKey = `${brand.id}:catalogue:${catalogueKey}`;
    setFabricCatalogueSavingKey(savingKey);
    try {
      const catalogueRef = doc(
        db,
        "users",
        workspaceUid,
        "fabricBrands",
        brand.id,
        "catalogues",
        catalogueKey
      );
      await setDoc(
        catalogueRef,
        {
          id: catalogueKey,
          brandId: brand.id,
          brandName: brand.name || "",
          catalogueName,
          catalogueKey,
          defaultRrpPerMeter:
            Number(draft.defaultRrpPerMeter) > 0
              ? Number(draft.defaultRrpPerMeter)
              : null,
          currency: "INR",
          active: true,
          archived: false,
          notes: sanitizeEditableText(draft.notes || ""),
          createdAt: serverTimestamp(),
          createdBy: user?.uid || "",
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid || "",
        },
        { merge: true }
      );
      setFabricCatalogueDrafts((prev) => ({
        ...prev,
        [brand.id]: { catalogueName: "", defaultRrpPerMeter: "", notes: "" },
      }));
      await loadFabricCatalogueLibrary(brand.id, { force: true });
      notify(`${catalogueName} added under ${brand.name}.`, "success");
    } catch (error) {
      console.error("FABRIC_CATALOGUE_SAVE_FAILED", {
        code: error?.code,
        message: error?.message,
        workspaceUid,
        brandId: brand.id,
        catalogueName,
      });
      notify("Could not save catalogue.", "error");
    } finally {
      setFabricCatalogueSavingKey("");
    }
  },
  [
    fabricCatalogueDrafts,
    loadFabricCatalogueLibrary,
    notify,
    user?.uid,
    workspaceUid,
  ]
);

const createFabricSrRuleDraftRow = useCallback(
  (overrides = {}) => ({
    clientId: createClientId("fabric-sr-rule"),
    existingRuleId: "",
    ruleType: "range",
    srNumber: "",
    srFrom: "",
    srTo: "",
    srNumbers: "",
    rrpPerMeter: "",
    notes: "",
    ...overrides,
  }),
  []
);

const getFabricSrRuleDraftRows = useCallback(
  (draftKey = "") => {
    const value = fabricSrRuleDrafts[draftKey];
    if (Array.isArray(value)) return value;
    if (value && typeof value === "object") {
      return [
        createFabricSrRuleDraftRow({
          ...value,
          clientId: value.clientId || createClientId("fabric-sr-rule"),
        }),
      ];
    }
    return [];
  },
  [createFabricSrRuleDraftRow, fabricSrRuleDrafts]
);

const setFabricSrRuleDraftRows = useCallback((draftKey = "", updater) => {
  setFabricSrRuleDrafts((prev) => {
    const current = Array.isArray(prev[draftKey])
      ? prev[draftKey]
      : prev[draftKey] && typeof prev[draftKey] === "object"
      ? [prev[draftKey]]
      : [];
    const nextRows =
      typeof updater === "function" ? updater(current) : Array.isArray(updater) ? updater : current;
    return {
      ...prev,
      [draftKey]: nextRows,
    };
  });
}, []);

const addFabricSrRuleDraftRow = useCallback(
  (brand, catalogue, overrides = {}) => {
    if (!brand?.id || !catalogue?.id) return;
    const draftKey = `${brand.id}:${catalogue.id}`;
    setFabricSrRuleDraftRows(draftKey, (current) => [
      ...current,
      createFabricSrRuleDraftRow(overrides),
    ]);
  },
  [createFabricSrRuleDraftRow, setFabricSrRuleDraftRows]
);

const editFabricSrPriceRule = useCallback(
  (brand, catalogue, rule) => {
    if (!brand?.id || !catalogue?.id || !rule?.id) return;
    addFabricSrRuleDraftRow(brand, catalogue, {
      clientId: `edit-${rule.id}-${Date.now()}`,
      existingRuleId: rule.id,
      ruleType: rule.ruleType || "single",
      srNumber: rule.srNumber || "",
      srFrom: rule.srFrom || "",
      srTo: rule.srTo || "",
      srNumbers: (rule.normalizedSrNumbers || rule.srNumbers || []).join(", "),
      rrpPerMeter: rule.rrpPerMeter != null ? String(rule.rrpPerMeter) : "",
      notes: rule.notes || "",
    });
  },
  [addFabricSrRuleDraftRow]
);

const updateFabricSrRuleDraftRow = useCallback(
  (draftKey = "", clientId = "", field = "", value = "") => {
    setFabricSrRuleDraftRows(draftKey, (current) =>
      current.map((draft) =>
        draft.clientId === clientId ? { ...draft, [field]: value } : draft
      )
    );
  },
  [setFabricSrRuleDraftRows]
);

const removeFabricSrRuleDraftRow = useCallback(
  (draftKey = "", clientId = "") => {
    setFabricSrRuleDraftRows(draftKey, (current) =>
      current.filter((draft) => draft.clientId !== clientId)
    );
  },
  [setFabricSrRuleDraftRows]
);

const getSrNumericValue = useCallback((value = "") => {
  const match = String(normalizeSrNumber(value) || "").match(/\d+/);
  return match ? Number(match[0]) : null;
}, []);

const validateFabricSrRuleDraft = useCallback(
  ({ draft = {}, brand = {}, catalogue = {}, existingRules = [] } = {}) => {
    const payload = buildSrPriceRulePayload({ draft, brand, catalogue });
    const ruleId = buildSrPriceRuleDocId(draft);
    const errors = [];
    const warnings = [];
    const activeRules = (existingRules || []).filter(
      (rule) => rule.active !== false && rule.id !== draft.existingRuleId
    );
    const rrp = Number(payload.rrpPerMeter || 0);

    if (!(rrp > 0)) errors.push("RRP per meter must be greater than 0.");

    if (payload.ruleType === "single" && !payload.srNumber) {
      errors.push("SR number is required.");
    }
    if (payload.ruleType === "range") {
      if (!payload.srFrom || !payload.srTo) {
        errors.push("SR range from/to is required.");
      } else {
        const fromNumeric = getSrNumericValue(payload.srFrom);
        const toNumeric = getSrNumericValue(payload.srTo);
        if (fromNumeric == null || toNumeric == null) {
          errors.push("SR range values must include numbers.");
        } else if (fromNumeric > toNumeric) {
          errors.push("SR range From must be less than or equal to To.");
        }
      }
    }
    if (payload.ruleType === "list" && !payload.normalizedSrNumbers.length) {
      errors.push("Enter at least one SR number in the list.");
    }

    const duplicateExact = activeRules.find(
      (rule) =>
        rule.ruleType === "single" &&
        payload.ruleType === "single" &&
        normalizeSrNumber(rule.srNumber) === normalizeSrNumber(payload.srNumber) &&
        rule.id !== ruleId
    );
    if (duplicateExact) {
      errors.push(`${getSrPriceRuleLabel(duplicateExact)} already exists. Use Edit to update it.`);
    }

    if (payload.ruleType === "range" && !errors.length) {
      const candidateFrom = getSrNumericValue(payload.srFrom);
      const candidateTo = getSrNumericValue(payload.srTo);
      activeRules
        .filter((rule) => rule.ruleType === "range")
        .forEach((rule) => {
          const fromNumeric = getSrNumericValue(rule.srFrom);
          const toNumeric = getSrNumericValue(rule.srTo);
          if (
            candidateFrom == null ||
            candidateTo == null ||
            fromNumeric == null ||
            toNumeric == null
          ) {
            return;
          }
          const overlaps =
            Math.max(candidateFrom, Math.min(fromNumeric, toNumeric)) <=
            Math.min(candidateTo, Math.max(fromNumeric, toNumeric));
          if (overlaps) {
            warnings.push(
              `${getSrPriceRuleLabel(payload)} overlaps with ${getSrPriceRuleLabel(rule)}. Prefer non-overlapping ranges like 1-10 and 11-20.`
            );
          }
        });
    }

    const candidateSrNumbers =
      payload.ruleType === "single"
        ? [payload.srNumber]
        : payload.ruleType === "list"
        ? payload.normalizedSrNumbers
        : [];
    if (candidateSrNumbers.length) {
      const duplicateSrNumbers = [];
      activeRules.forEach((rule) => {
        const existingSrNumbers =
          rule.ruleType === "single"
            ? [rule.srNumber]
            : rule.ruleType === "list"
            ? rule.normalizedSrNumbers || rule.srNumbers || []
            : [];
        existingSrNumbers.forEach((sr) => {
          if (
            candidateSrNumbers
              .map((entry) => normalizeSrNumber(entry))
              .includes(normalizeSrNumber(sr))
          ) {
            duplicateSrNumbers.push(normalizeSrNumber(sr));
          }
        });
      });
      if (duplicateSrNumbers.length) {
        warnings.push(
          `SR ${[...new Set(duplicateSrNumbers)].join(", ")} also appears in another exact/list rule. Exact/list priority will apply.`
        );
      }
    }

    return { payload, ruleId, errors, warnings };
  },
  [getSrNumericValue]
);

const saveFabricSrRuleDraftRow = useCallback(
  async (
    brand,
    catalogue,
    draft,
    { skipWarnings = false, removeAfterSave = true, silent = false } = {}
  ) => {
    if (!workspaceUid || !brand?.id || !catalogue?.id || !draft) return false;
    const draftKey = `${brand.id}:${catalogue.id}`;
    const library =
      fabricCatalogueLibraryRef.current[brand.id] ||
      fabricCatalogueLibraryByBrand[brand.id] ||
      getEmptyFabricCatalogueLibrary(brand);
    const existingRules = library.rulesByCatalogueId?.[catalogue.id] || [];
    const validation = validateFabricSrRuleDraft({
      draft,
      brand,
      catalogue,
      existingRules,
    });

    if (validation.errors.length) {
      notify(validation.errors[0], "warning");
      return false;
    }

    if (!skipWarnings && validation.warnings.length) {
      openConfirmDialog({
        title: "SR pricing overlap warning",
        description: validation.warnings.join(" "),
        confirmLabel: "Save Anyway",
        cancelLabel: "Review",
        tone: "warning",
        onConfirm: () =>
          saveFabricSrRuleDraftRow(brand, catalogue, draft, {
            skipWarnings: true,
            removeAfterSave,
            silent,
          }),
      });
      return false;
    }

    const { payload, ruleId } = validation;
    const savingKey = `${draftKey}:rule:${ruleId}`;
    setFabricCatalogueSavingKey(savingKey);
    try {
      const ruleRef = doc(
        db,
        "users",
        workspaceUid,
        "fabricBrands",
        brand.id,
        "catalogues",
        catalogue.id,
        "srPriceRules",
        ruleId
      );
      await setDoc(
        ruleRef,
        {
          id: ruleId,
          ...payload,
          ...(draft.existingRuleId ? {} : { createdAt: serverTimestamp(), createdBy: user?.uid || "" }),
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid || "",
        },
        { merge: true }
      );

      if (draft.existingRuleId && draft.existingRuleId !== ruleId) {
        await updateDoc(
          doc(
            db,
            "users",
            workspaceUid,
            "fabricBrands",
            brand.id,
            "catalogues",
            catalogue.id,
            "srPriceRules",
            draft.existingRuleId
          ),
          {
            active: false,
            archived: true,
            replacedByRuleId: ruleId,
            updatedAt: serverTimestamp(),
            updatedBy: user?.uid || "",
          }
        );
      }

      if (removeAfterSave) {
        removeFabricSrRuleDraftRow(draftKey, draft.clientId);
      }
      await loadFabricCatalogueLibrary(brand.id, { force: true });
      if (!silent) {
        notify("SR pricing rule saved.", "success");
      }
      return true;
    } catch (error) {
      console.error("FABRIC_SR_PRICE_RULE_SAVE_FAILED", {
        code: error?.code,
        message: error?.message,
        workspaceUid,
        brandId: brand.id,
        catalogueId: catalogue.id,
        ruleId,
      });
      notify("Could not save SR price rule.", "error");
      return false;
    } finally {
      setFabricCatalogueSavingKey("");
    }
  },
  [
    fabricCatalogueLibraryByBrand,
    getEmptyFabricCatalogueLibrary,
    loadFabricCatalogueLibrary,
    notify,
    openConfirmDialog,
    removeFabricSrRuleDraftRow,
    user?.uid,
    validateFabricSrRuleDraft,
    workspaceUid,
  ]
);

const saveAllFabricSrRuleDrafts = useCallback(
  async (brand, catalogue, { skipWarnings = false } = {}) => {
    if (!workspaceUid || !brand?.id || !catalogue?.id) return;
    const draftKey = `${brand.id}:${catalogue.id}`;
    const draftRows = getFabricSrRuleDraftRows(draftKey);
    if (!draftRows.length) {
      notify("Add at least one SR pricing rule first.", "info");
      return;
    }

    const library =
      fabricCatalogueLibraryRef.current[brand.id] ||
      fabricCatalogueLibraryByBrand[brand.id] ||
      getEmptyFabricCatalogueLibrary(brand);
    const existingRules = library.rulesByCatalogueId?.[catalogue.id] || [];
    const validations = draftRows.map((draft) =>
      validateFabricSrRuleDraft({ draft, brand, catalogue, existingRules })
    );
    const firstError = validations.flatMap((entry) => entry.errors)[0];
    if (firstError) {
      notify(firstError, "warning");
      return;
    }
    const warnings = validations.flatMap((entry) => entry.warnings);
    if (warnings.length && !skipWarnings) {
      openConfirmDialog({
        title: "SR pricing overlap warning",
        description: [...new Set(warnings)].join(" "),
        confirmLabel: "Save All Anyway",
        cancelLabel: "Review",
        tone: "warning",
        onConfirm: () =>
          saveAllFabricSrRuleDrafts(brand, catalogue, { skipWarnings: true }),
      });
      return;
    }

    let savedCount = 0;
    for (const draft of draftRows) {
      const saved = await saveFabricSrRuleDraftRow(brand, catalogue, draft, {
        skipWarnings: true,
        removeAfterSave: false,
        silent: true,
      });
      if (saved) savedCount += 1;
      else break;
    }

    if (savedCount > 0) {
      setFabricSrRuleDrafts((prev) => ({
        ...prev,
        [draftKey]: draftRows.slice(savedCount),
      }));
      await loadFabricCatalogueLibrary(brand.id, { force: true });
      notify(`${savedCount} SR pricing rule${savedCount === 1 ? "" : "s"} saved.`, "success");
    }
  },
  [
    fabricCatalogueLibraryByBrand,
    getFabricSrRuleDraftRows,
    getEmptyFabricCatalogueLibrary,
    loadFabricCatalogueLibrary,
    notify,
    openConfirmDialog,
    saveFabricSrRuleDraftRow,
    validateFabricSrRuleDraft,
    workspaceUid,
  ]
);

const archiveFabricCatalogue = useCallback(
  async (brand, catalogue) => {
    if (!workspaceUid || !brand?.id || !catalogue?.id) return;
    try {
      await updateDoc(
        doc(
          db,
          "users",
          workspaceUid,
          "fabricBrands",
          brand.id,
          "catalogues",
          catalogue.id
        ),
        {
          active: false,
          archived: true,
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid || "",
        }
      );
      await loadFabricCatalogueLibrary(brand.id, { force: true });
      notify("Catalogue archived.", "success");
    } catch (error) {
      console.error("FABRIC_CATALOGUE_ARCHIVE_FAILED", {
        code: error?.code,
        message: error?.message,
        workspaceUid,
        brandId: brand.id,
        catalogueId: catalogue.id,
      });
      notify("Could not archive catalogue.", "error");
    }
  },
  [loadFabricCatalogueLibrary, notify, user?.uid, workspaceUid]
);

const archiveFabricSrPriceRule = useCallback(
  async (brand, catalogue, rule) => {
    if (!workspaceUid || !brand?.id || !catalogue?.id || !rule?.id) return;
    try {
      await updateDoc(
        doc(
          db,
          "users",
          workspaceUid,
          "fabricBrands",
          brand.id,
          "catalogues",
          catalogue.id,
          "srPriceRules",
          rule.id
        ),
        {
          active: false,
          archived: true,
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid || "",
        }
      );
      await loadFabricCatalogueLibrary(brand.id, { force: true });
      notify("SR price rule archived.", "success");
    } catch (error) {
      console.error("FABRIC_SR_PRICE_RULE_ARCHIVE_FAILED", {
        code: error?.code,
        message: error?.message,
        workspaceUid,
        brandId: brand.id,
        catalogueId: catalogue.id,
        ruleId: rule.id,
      });
      notify("Could not archive SR price rule.", "error");
    }
  },
  [loadFabricCatalogueLibrary, notify, user?.uid, workspaceUid]
);

const deleteFabricSrPriceRule = useCallback(
  async (brand, catalogue, rule) => {
    if (!workspaceUid || !brand?.id || !catalogue?.id || !rule?.id) return;
    openConfirmDialog({
      title: "Delete SR pricing rule?",
      description: `${getSrPriceRuleLabel(rule)} will be permanently removed from ${catalogue.catalogueName}. Archive is safer if you only want to stop using it.`,
      confirmLabel: "Delete Rule",
      cancelLabel: "Cancel",
      tone: "danger",
      onConfirm: async () => {
        try {
          await deleteDoc(
            doc(
              db,
              "users",
              workspaceUid,
              "fabricBrands",
              brand.id,
              "catalogues",
              catalogue.id,
              "srPriceRules",
              rule.id
            )
          );
          await loadFabricCatalogueLibrary(brand.id, { force: true });
          notify("SR pricing rule deleted.", "success");
        } catch (error) {
          console.error("FABRIC_SR_PRICE_RULE_DELETE_FAILED", {
            code: error?.code,
            message: error?.message,
            workspaceUid,
            brandId: brand.id,
            catalogueId: catalogue.id,
            ruleId: rule.id,
          });
          notify("Could not delete SR price rule.", "error");
        }
      },
    });
  },
  [loadFabricCatalogueLibrary, notify, openConfirmDialog, workspaceUid]
);

const renderFabricBrandCatalogueManager = (brand) => {
  const library = getFabricCatalogueLibraryForBrand(brand.id);
  const catalogueDraft = fabricCatalogueDrafts[brand.id] || {
    catalogueName: "",
    defaultRrpPerMeter: "",
    notes: "",
  };
  const updateCatalogueDraft = (field, value) =>
    setFabricCatalogueDrafts((prev) => ({
      ...prev,
      [brand.id]: {
        ...(prev[brand.id] || {}),
        [field]: value,
      },
    }));
  const updateRuleDraft = (catalogueId, clientId, field, value) => {
    const draftKey = `${brand.id}:${catalogueId}`;
    updateFabricSrRuleDraftRow(draftKey, clientId, field, value);
  };
  const ruleCardStyle = {
    borderRadius: 14,
    padding: 12,
    border: isDark ? "1px solid rgba(148,163,184,0.14)" : "1px solid #e2e8f0",
    background: isDark ? "rgba(15,23,42,0.7)" : "#ffffff",
  };
  const miniLabelStyle = {
    fontSize: 11,
    fontWeight: 900,
    color: dashboardMuted,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  };

  return (
    <div
      style={{
        marginTop: 12,
        padding: 14,
        borderRadius: 14,
        background: isDark ? "rgba(2,6,23,0.38)" : "#f8fafc",
        border: isDark
          ? "1px solid rgba(96,165,250,0.16)"
          : "1px solid rgba(191,219,254,0.85)",
        display: "grid",
        gap: 14,
      }}
    >
      <div style={{ display: "grid", gap: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 900 }}>
          Catalogues / books for {brand.name}
        </div>
        <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.55 }}>
          Add catalogue names once, then add exact, range, list, or default SR RRP
          rules. Quote rows resolve these locally after this brand library is loaded.
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile
            ? "1fr"
            : "minmax(180px, 1.2fr) minmax(130px, 0.5fr) minmax(180px, 1fr) auto",
          gap: 10,
          alignItems: "end",
        }}
      >
        <label style={{ display: "grid", gap: 5 }}>
          <span style={miniLabelStyle}>Catalogue / Book</span>
          <input
            style={input}
            value={catalogueDraft.catalogueName || ""}
            placeholder="Soft Drape, Wilbert, Vista"
            onChange={(event) =>
              updateCatalogueDraft("catalogueName", event.target.value)
            }
          />
        </label>
        <label style={{ display: "grid", gap: 5 }}>
          <span style={miniLabelStyle}>Default RRP/m</span>
          <input
            type="text"
            inputMode="decimal"
            style={input}
            value={catalogueDraft.defaultRrpPerMeter || ""}
            placeholder="Optional"
            onChange={(event) =>
              updateCatalogueDraft("defaultRrpPerMeter", event.target.value)
            }
          />
        </label>
        <label style={{ display: "grid", gap: 5 }}>
          <span style={miniLabelStyle}>Notes</span>
          <input
            style={input}
            value={catalogueDraft.notes || ""}
            placeholder="Optional"
            onChange={(event) => updateCatalogueDraft("notes", event.target.value)}
          />
        </label>
        <Button
          icon={Plus}
          variant="success"
          disabled={Boolean(fabricCatalogueSavingKey)}
          onClick={() => saveFabricCatalogueDraft(brand)}
        >
          Add Catalogue
        </Button>
      </div>

      {(library.catalogues || []).length ? (
        <div style={{ display: "grid", gap: 12 }}>
          {library.catalogues.map((catalogue) => {
            const draftKey = `${brand.id}:${catalogue.id}`;
            const draftRows = getFabricSrRuleDraftRows(draftKey);
            const rules = library.rulesByCatalogueId?.[catalogue.id] || [];
            const tableHeaderStyle = {
              padding: "8px 10px",
              fontSize: 10.5,
              fontWeight: 900,
              color: dashboardMuted,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              borderBottom: isDark
                ? "1px solid rgba(148,163,184,0.14)"
                : "1px solid #e2e8f0",
            };
            const tableCellStyle = {
              padding: "9px 10px",
              fontSize: 12,
              borderBottom: isDark
                ? "1px solid rgba(148,163,184,0.1)"
                : "1px solid #edf2f7",
              verticalAlign: "middle",
            };
            return (
              <div key={catalogue.id} style={ruleCardStyle}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 10,
                    flexWrap: "wrap",
                    alignItems: "flex-start",
                  }}
                >
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 900 }}>
                      {catalogue.catalogueName}
                    </div>
                    <div style={{ marginTop: 3, fontSize: 11.5, color: dashboardMuted }}>
                      Default RRP:{" "}
                      {catalogue.defaultRrpPerMeter != null
                        ? `${currencySymbol(currency)} ${Number(
                            catalogue.defaultRrpPerMeter
                          ).toFixed(2)}/m`
                        : "Not set"}
                      {catalogue.notes ? ` | ${catalogue.notes}` : ""}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => archiveFabricCatalogue(brand, catalogue)}
                    style={{
                      border: 0,
                      background: "transparent",
                      color: "#dc2626",
                      fontSize: 12,
                      fontWeight: 900,
                      cursor: "pointer",
                      padding: 0,
                    }}
                  >
                    Archive catalogue
                  </button>
                </div>

                <div style={{ marginTop: 14, display: "grid", gap: 12 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 10,
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 900 }}>
                        SR Pricing Rules
                      </div>
                      <div style={{ marginTop: 3, fontSize: 11.5, color: dashboardMuted }}>
                        One catalogue can contain many single, range, and list rules.
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <Button
                        icon={Plus}
                        variant="secondary"
                        disabled={Boolean(fabricCatalogueSavingKey)}
                        onClick={() => addFabricSrRuleDraftRow(brand, catalogue)}
                      >
                        Add SR Rule
                      </Button>
                      <Button
                        icon={Save}
                        variant="primary"
                        disabled={Boolean(fabricCatalogueSavingKey) || !draftRows.length}
                        onClick={() => saveAllFabricSrRuleDrafts(brand, catalogue)}
                      >
                        Save All Rules
                      </Button>
                    </div>
                  </div>

                  <div
                    style={{
                      overflowX: "auto",
                      borderRadius: 12,
                      border: isDark
                        ? "1px solid rgba(148,163,184,0.14)"
                        : "1px solid #e2e8f0",
                      background: isDark ? "rgba(2,6,23,0.28)" : "#ffffff",
                    }}
                  >
                    <table
                      style={{
                        width: "100%",
                        minWidth: 760,
                        borderCollapse: "collapse",
                      }}
                    >
                      <thead>
                        <tr>
                          <th style={tableHeaderStyle}>Rule Type</th>
                          <th style={tableHeaderStyle}>From</th>
                          <th style={tableHeaderStyle}>To</th>
                          <th style={tableHeaderStyle}>SR List</th>
                          <th style={tableHeaderStyle}>RRP/m</th>
                          <th style={tableHeaderStyle}>Notes</th>
                          <th style={tableHeaderStyle}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rules.length ? (
                          rules.map((rule) => (
                            <tr key={rule.id}>
                              <td style={tableCellStyle}>{getSrPriceRuleLabel(rule)}</td>
                              <td style={tableCellStyle}>{rule.ruleType === "range" ? rule.srFrom : "-"}</td>
                              <td style={tableCellStyle}>{rule.ruleType === "range" ? rule.srTo : "-"}</td>
                              <td style={tableCellStyle}>
                                {rule.ruleType === "list"
                                  ? (rule.normalizedSrNumbers || rule.srNumbers || []).join(", ")
                                  : rule.ruleType === "single"
                                  ? rule.srNumber
                                  : "-"}
                              </td>
                              <td style={tableCellStyle}>
                                {currencySymbol(currency)}{" "}
                                {Number(rule.rrpPerMeter || 0).toFixed(2)}
                              </td>
                              <td style={tableCellStyle}>{rule.notes || "-"}</td>
                              <td style={tableCellStyle}>
                                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                  <button
                                    type="button"
                                    onClick={() => editFabricSrPriceRule(brand, catalogue, rule)}
                                    style={{
                                      border: 0,
                                      background: "transparent",
                                      color: "#2563eb",
                                      fontSize: 12,
                                      fontWeight: 900,
                                      cursor: "pointer",
                                      padding: 0,
                                    }}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => archiveFabricSrPriceRule(brand, catalogue, rule)}
                                    style={{
                                      border: 0,
                                      background: "transparent",
                                      color: "#b45309",
                                      fontSize: 12,
                                      fontWeight: 900,
                                      cursor: "pointer",
                                      padding: 0,
                                    }}
                                  >
                                    Archive
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => deleteFabricSrPriceRule(brand, catalogue, rule)}
                                    style={{
                                      border: 0,
                                      background: "transparent",
                                      color: "#dc2626",
                                      fontSize: 12,
                                      fontWeight: 900,
                                      cursor: "pointer",
                                      padding: 0,
                                    }}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={7}
                              style={{
                                ...tableCellStyle,
                                color: dashboardMuted,
                                textAlign: "center",
                              }}
                            >
                              No SR rules yet. Catalogue default will apply if it is set.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {draftRows.length ? (
                    <div style={{ display: "grid", gap: 10 }}>
                      {draftRows.map((ruleDraft) => {
                        const draftValidation = validateFabricSrRuleDraft({
                          draft: ruleDraft,
                          brand,
                          catalogue,
                          existingRules: rules,
                        });
                        return (
                          <div
                            key={ruleDraft.clientId}
                            style={{
                              borderRadius: 12,
                              padding: 12,
                              border: isDark
                                ? "1px solid rgba(96,165,250,0.22)"
                                : "1px solid rgba(191,219,254,0.95)",
                              background: isDark ? "rgba(30,41,59,0.55)" : "#eff6ff",
                              display: "grid",
                              gap: 10,
                            }}
                          >
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: isMobile
                                  ? "1fr"
                                  : "120px repeat(2, minmax(100px, 1fr)) minmax(150px, 1.2fr) 110px minmax(140px, 1fr) auto",
                                gap: 8,
                                alignItems: "end",
                              }}
                            >
                              <label style={{ display: "grid", gap: 4 }}>
                                <span style={miniLabelStyle}>Rule</span>
                                <select
                                  style={selectInput}
                                  value={ruleDraft.ruleType || "single"}
                                  onChange={(event) =>
                                    updateRuleDraft(
                                      catalogue.id,
                                      ruleDraft.clientId,
                                      "ruleType",
                                      event.target.value
                                    )
                                  }
                                >
                                  <option value="single">Single SR</option>
                                  <option value="range">SR Range</option>
                                  <option value="list">SR List</option>
                                </select>
                              </label>

                              {ruleDraft.ruleType === "range" ? (
                                <>
                                  <label style={{ display: "grid", gap: 4 }}>
                                    <span style={miniLabelStyle}>From</span>
                                    <input
                                      style={input}
                                      value={ruleDraft.srFrom || ""}
                                      placeholder="1"
                                      onChange={(event) =>
                                        updateRuleDraft(
                                          catalogue.id,
                                          ruleDraft.clientId,
                                          "srFrom",
                                          event.target.value
                                        )
                                      }
                                    />
                                  </label>
                                  <label style={{ display: "grid", gap: 4 }}>
                                    <span style={miniLabelStyle}>To</span>
                                    <input
                                      style={input}
                                      value={ruleDraft.srTo || ""}
                                      placeholder="10"
                                      onChange={(event) =>
                                        updateRuleDraft(
                                          catalogue.id,
                                          ruleDraft.clientId,
                                          "srTo",
                                          event.target.value
                                        )
                                      }
                                    />
                                  </label>
                                  <div />
                                </>
                              ) : ruleDraft.ruleType === "list" ? (
                                <label style={{ display: "grid", gap: 4, gridColumn: isMobile ? "auto" : "span 3" }}>
                                  <span style={miniLabelStyle}>SR List</span>
                                  <textarea
                                    style={{ ...input, minHeight: 38, resize: "vertical" }}
                                    value={ruleDraft.srNumbers || ""}
                                    placeholder="12, 14, 18, 22"
                                    onChange={(event) =>
                                      updateRuleDraft(
                                        catalogue.id,
                                        ruleDraft.clientId,
                                        "srNumbers",
                                        event.target.value
                                      )
                                    }
                                  />
                                </label>
                              ) : (
                                <label style={{ display: "grid", gap: 4, gridColumn: isMobile ? "auto" : "span 3" }}>
                                  <span style={miniLabelStyle}>SR Number</span>
                                  <input
                                    style={input}
                                    value={ruleDraft.srNumber || ""}
                                    placeholder="12"
                                    onChange={(event) =>
                                      updateRuleDraft(
                                        catalogue.id,
                                        ruleDraft.clientId,
                                        "srNumber",
                                        event.target.value
                                      )
                                    }
                                  />
                                </label>
                              )}

                              <label style={{ display: "grid", gap: 4 }}>
                                <span style={miniLabelStyle}>RRP/m</span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  style={input}
                                  value={ruleDraft.rrpPerMeter || ""}
                                  placeholder="0"
                                  onChange={(event) =>
                                    updateRuleDraft(
                                      catalogue.id,
                                      ruleDraft.clientId,
                                      "rrpPerMeter",
                                      event.target.value
                                    )
                                  }
                                />
                              </label>
                              <label style={{ display: "grid", gap: 4 }}>
                                <span style={miniLabelStyle}>Notes</span>
                                <input
                                  style={input}
                                  value={ruleDraft.notes || ""}
                                  placeholder="Optional"
                                  onChange={(event) =>
                                    updateRuleDraft(
                                      catalogue.id,
                                      ruleDraft.clientId,
                                      "notes",
                                      event.target.value
                                    )
                                  }
                                />
                              </label>
                              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                <Button
                                  icon={Save}
                                  variant="primary"
                                  disabled={Boolean(fabricCatalogueSavingKey)}
                                  onClick={() =>
                                    saveFabricSrRuleDraftRow(brand, catalogue, ruleDraft)
                                  }
                                >
                                  Save
                                </Button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    removeFabricSrRuleDraftRow(draftKey, ruleDraft.clientId)
                                  }
                                  style={{
                                    border: 0,
                                    background: "transparent",
                                    color: "#dc2626",
                                    fontSize: 12,
                                    fontWeight: 900,
                                    cursor: "pointer",
                                    padding: 0,
                                  }}
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                            {draftValidation.warnings.length ? (
                              <div
                                style={{
                                  padding: "8px 10px",
                                  borderRadius: 10,
                                  background: isDark
                                    ? "rgba(245,158,11,0.18)"
                                    : "#fffbeb",
                                  color: isDark ? "#fde68a" : "#92400e",
                                  fontSize: 11.5,
                                  fontWeight: 800,
                                }}
                              >
                                {draftValidation.warnings[0]}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: dashboardMuted }}>
                      Click Add SR Rule to add one or more pricing rows under this catalogue.
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ fontSize: 12, color: dashboardMuted }}>
          No catalogues added for this brand yet.
        </div>
      )}
    </div>
  );
};

useEffect(() => {
  const shouldLoadProducts =
    Boolean(workspaceUid) && (tab === "quote" || tab === "products");
  if (!shouldLoadProducts) return;
  if (
    quotationSettingsLoadedWorkspaceRef.current !== workspaceUid &&
    quotationSettingsLoadingWorkspaceRef.current === workspaceUid
  ) {
    return;
  }
  if (productConfigsLoadedWorkspaceRef.current === workspaceUid) return;
  if (productConfigsLoadingWorkspaceRef.current === workspaceUid) return;

  productConfigsLoadingWorkspaceRef.current = workspaceUid;
  setProductConfigsLoadState({
    loading: true,
    loadedWorkspace: productConfigsLoadedWorkspaceRef.current,
    error: "",
  });

  const loadProductConfigs = async () => {
    try {
      const productRef = collection(
        db,
        "users",
        workspaceUid,
        PRODUCT_CONFIG_COLLECTION
      );
      const snap = await getDocsCached(productRef, {
        label: "loadProductConfigs",
        cacheKey: `productConfigs:${workspaceUid}`,
        ttlMs: 5 * 60 * 1000,
      });
      const fetched = snap.docs.map((docSnap) =>
        normalizeProductConfig({ id: docSnap.id, ...docSnap.data() })
      );
      const fetchedIds = new Set(fetched.map((config) => String(config.id || "")));
      const defaultProductConfigs = buildDefaultProductConfigs(fabricCalcSettings);
      const missingDefaults = defaultProductConfigs.filter(
        (config) => !fetchedIds.has(config.id)
      );

      if (missingDefaults.length > 0) {
        await Promise.all(
          missingDefaults.map((config) =>
            setDoc(
              doc(db, "users", workspaceUid, PRODUCT_CONFIG_COLLECTION, config.id),
              {
                ...normalizeProductConfig(config),
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              },
              { merge: true }
            )
          )
        );
      }

      setProductConfigs(mergeProductConfigsWithDefaults(fetched, fabricCalcSettings));
      productConfigsLoadedWorkspaceRef.current = workspaceUid;
      setProductConfigsLoadState({
        loading: false,
        loadedWorkspace: workspaceUid,
        error: "",
      });
    } catch (error) {
      console.error("Failed to load product configs:", error);
      setProductConfigs(mergeProductConfigsWithDefaults([], fabricCalcSettings));
      setProductConfigsLoadState({
        loading: false,
        loadedWorkspace: productConfigsLoadedWorkspaceRef.current,
        error: error?.message || "Could not load product configs.",
      });
    } finally {
      if (productConfigsLoadingWorkspaceRef.current === workspaceUid) {
        productConfigsLoadingWorkspaceRef.current = "";
      }
    }
  };

  loadProductConfigs();
}, [fabricCalcSettings, quotationSettingsReadyWorkspace, tab, workspaceUid]);


useEffect(() => {
  if (editingQuoteId) {
    console.log("EDIT MODE LOCKED:", editingQuoteId);
  }
}, [editingQuoteId]);

function getVerticalRepeatMeters(fabricKey) {

const key = normalizeFabricKey(fabricKey);
const settings = repeatSettings?.[key];

if (!settings || !settings.verticalEnabled) return 0;

return (Number(settings.verticalRepeatInch) || 0) / 39.37;
}


const filteredStockHistory = useMemo(() => {
  const queryText = stockHistorySearch.toLowerCase().trim();
  if (!queryText) return stockHistory;

  return stockHistory.filter((h) => {
    const customer = (h.customerName || "").toLowerCase();
    const quoteId = (h.quoteId || "").toLowerCase();
    const quoteLabel = (h.quoteLabel || "").toLowerCase();

    const fabricsText = Array.isArray(h.fabrics)
      ? h.fabrics
          .map((f) => f.fabricKey || "")
          .join(" ")
          .toLowerCase()
      : (h.fabricKey || "").toLowerCase();

    return (
      customer.includes(queryText) ||
      quoteId.includes(queryText) ||
      quoteLabel.includes(queryText) ||
      fabricsText.includes(queryText)
    );
  });
}, [stockHistory, stockHistorySearch]);






useEffect(() => {
  tcoWorkerRef.current = new Worker("/tcoWorker.js");

  tcoWorkerRef.current.onmessage = async (e) => {
    const { ok, result, error } = e.data;
    setIsOptimizing(false);

    if (!ok) {
      alert(error || "Track optimizer failed");
      return;
    }

    const rawLog = Array.isArray(result.log) ? result.log : [];
    const finalStock = result.newStock || {};

    const mappedLog = rawLog.map((entry, idx) => {
      const usedIn = Number(
        entry.usedIn ?? entry.usedFrom ?? entry.usedSum ?? 0
      );
      const leftoverIn = Number(entry.leftoverGenerated ?? 0);

      return {
        ...entry,
        usedFt: Math.round((usedIn / 12) * 100) / 100,
        leftoverFt: Math.round((leftoverIn / 12) * 100) / 100,
        trackId: entry.trackId || `T${idx + 1}`,
      };
    });

    setTrackCutPlan(mappedLog);
    setTrackLeftovers(finalStock);
  };

  tcoWorkerRef.current.onerror = (err) => {
    console.error("TCO Worker crashed:", err);
    setIsOptimizing(false);
    alert("Track optimizer worker crashed.");
  };

  return () => tcoWorkerRef.current?.terminate();
}, []);


/* ============================================================
   SAVE SETTINGS  FINAL W1 VERSION
   ============================================================ */
const saveSettings = async (options = {}) => {
  const saveOptions =
    options && typeof options === "object" && !options.nativeEvent ? options : {};
  if (isSavingSettings) return;

  const stitchingSettingsResult = buildStitchingSettingsForSave(stitchingRates);
  if (stitchingSettingsResult.errors.length) {
    notify(stitchingSettingsResult.errors[0], "error", 5200);
    return;
  }

  setIsSavingSettings(true);
  try {
    if (!user || !workspaceUid) {
      notify("Please sign in first.", "warning");
      return;
    }
    const normalizedTeamMembers = normalizeTeamMembers(teamMembers);

    let finalLogoURL = logoURL; // Keep existing logo if no new upload

    /* --------------------- LOGO UPLOAD --------------------- */
    if (selectedLogoFile) {
      setUploading(true);

      const logoImageId = createClientId("logo");
      const logoPath = `logos/${sanitizeFirestoreDocIdSegment(workspaceUid)}/${logoImageId}-${Date.now()}-${normalizeFileName(
        selectedLogoFile.name || "branding-logo"
      )}`;
      const managedLogo = await uploadManagedImage({
        workspaceUid,
        file: selectedLogoFile,
        ownerType: "logo",
        ownerId: "branding",
        imageId: logoImageId,
        fullPath: logoPath,
        uploadedBy: user?.uid || "",
        preserveTransparency: true,
        metadata: {
          workspaceUid,
          purpose: "branding_logo",
        },
      });

      finalLogoURL = managedLogo.downloadURL;
      setLogoURL(finalLogoURL);

      setUploading(false);
      setUploadProgress(100);
    }

    /* --------------------- FIRESTORE SAVE --------------------- */
    const settingsRef = doc(db, "users", workspaceUid, "settings", "main");
    const previousSettingsSnap = await getDoc(settingsRef);
    const previousTeamMembers = normalizeTeamMembers(
      previousSettingsSnap.exists() ? previousSettingsSnap.data()?.teamMembers : []
    );
    const fabricCalcSettingsForSave =
      normalizeFabricCalcSettingsForState(fabricCalcSettings);

await setDoc(
  settingsRef,
  {
    fabricCalcSettings: fabricCalcSettingsForSave,
    stitching: stitchingSettingsResult.normalized,
    businessName,
    businessPhone,
    businessEmail,
    currency,
    crmPipelines,
    teamMembers: normalizedTeamMembers,
    shopAddress,
    minSqft,
    unit,
    logoURL: finalLogoURL,
    updatedAt: serverTimestamp(),
  },
  { merge: true }
);

    invalidateFirestoreReadCache(settingsRef);
    mainSettingsLoadedWorkspaceRef.current = "";
    setFabricCalcSettings(fabricCalcSettingsForSave);
    commitCurtainRows(
      (prevRows) => prevRows.map((row) => ({ ...row })),
      QUOTE_RAW_FIELD_MUTATION_SOURCES.PRODUCT_DEFAULT_APPLY
    );
    setEffectiveRows((prevRows) => prevRows.map((row) => ({ ...row })));


    await syncWorkspaceMemberProfiles(
      normalizedTeamMembers,
      workspaceUid,
      previousTeamMembers
    );
    setSelectedLogoFile(null);
    setTeamMembers(normalizedTeamMembers);
    setStitchingRates({ ...stitchingSettingsResult.normalized });
    setIsStitchingSettingsDirty(false);
    notify(saveOptions.successMessage || "Settings saved successfully.", "success");
  } catch (error) {
    console.error("Error saving settings:", error);
    notify(
      error?.code === "storage-limit-reached" || error?.code === "invalid-image-file"
        ? error.message
        : "Failed to save settings. Check console for details.",
      "error",
      5200
    );
  } finally {
    setIsSavingSettings(false);
    setUploading(false);
  }
};



/* ============================================================
   LOAD USER SETTINGS  FINAL W1 VERSION
   ============================================================ */
const loadUserSettings = useCallback(async (uid) => {
  try {
    if (!uid) return;
    if (mainSettingsLoadedWorkspaceRef.current === uid) {
      if (isDevRuntime) {
        console.log("Settings load skipped; workspace already loaded", { uid });
      }
      return;
    }
    if (mainSettingsLoadingWorkspaceRef.current === uid) {
      if (isDevRuntime) {
        console.log("Settings load skipped; workspace load already in flight", { uid });
      }
      return;
    }
    mainSettingsLoadingWorkspaceRef.current = uid;

    // Correct W1 path
    console.log("workspaceUid:", uid);
    console.log("module:", "Settings");
    console.log("collectionPath:", `users/${uid}/settings/main`);
    const settingsRef = doc(db, "users", uid, "settings", "main");
    const snap = await getDocCached(settingsRef, {
      label: "loadUserSettings",
      cacheKey: `users/${uid}/settings/main`,
      ttlMs: 5 * 60 * 1000,
    });
    if (isDevRuntime) {
      console.log("Settings exists:", snap.exists());
    }
    const data = snap.exists() ? snap.data() : {};

setFabricCalcSettings(
  normalizeFabricCalcSettingsForState(data.fabricCalcSettings)
);

    /* ---------------------------
       NORMALISE STITCHING
       --------------------------- */

   const raw =
  data.stitching && typeof data.stitching === "object"
    ? data.stitching
    : {};
const normalised = {};

// 1) Load EXACT USER-SAVED FIRST  never lose new entries
for (const [name, vals] of Object.entries(raw)) {
  normalised[name] = normalizeStitchingSettingForState(vals);
}

// 2) Add defaults ONLY IF missing AND ensure no overwrite
for (const [name, def] of Object.entries(DEFAULT_STITCHING)) {
  if (!Object.hasOwn(normalised, name)) {
    normalised[name] = normalizeStitchingSettingForState(def);
  }
}

// ALWAYS preserve user-added order
setStitchingRates({ ...normalised });
setIsStitchingSettingsDirty(false);


    
    /* ---------------------------
       BASIC SETTINGS
       --------------------------- */

    setCurrency(data.currency || "Rs. Indian Rupee");
    setBusinessName(data.businessName || "");
    setBusinessPhone(data.businessPhone || "");
    setBusinessEmail(data.businessEmail || "");
    setShopAddress(data.shopAddress || "");
    setTeamMembers(normalizeTeamMembers(data.teamMembers));
    setCrmPipelines(normalizeCrmPipelines(data.crmPipelines));
    setMinSqft(data.minSqft || DEFAULT_MIN_SQFT);
    setUnit(data.unit || "in");   // <-- ADD THIS

    /* ---------------------------
       LOAD LOGO
       --------------------------- */
    try {
      const logoRef = ref(storage, `logos/${uid}/branding-logo.png`);
      const url = await getDownloadURL(logoRef);
      setLogoURL(url);
    } catch (e) {
      setLogoURL("");
    }
    mainSettingsLoadedWorkspaceRef.current = uid;

    /* ---------------------------
       LOAD QUOTES (W1 path)
       --------------------------- */
  

  } catch (error) {
    console.error("Error loading user settings:", error);
    if (isFirestoreResourceExhausted(error)) {
      raiseFirebaseHealthWarning("firestore", error, {
        path: `users/${uid}/settings/main`,
        source: "loadUserSettings",
      });
      notify("Firebase read limit reached. Please wait and try again.", "warning");
    }
  } finally {
    if (mainSettingsLoadingWorkspaceRef.current === uid) {
      mainSettingsLoadingWorkspaceRef.current = "";
    }
  }
}, [notify, raiseFirebaseHealthWarning]);


/* --------------------------- LOAD TRACK STOCK (W1) --------------------------- */
const loadTrackStock = useCallback(async (uid) => {
  try {
    if (!uid) return;
    if (trackStockLoadedWorkspaceRef.current === uid) {
      if (isDevRuntime) {
        console.log("Track stock load skipped; workspace already loaded", { uid });
      }
      return;
    }
    if (trackStockLoadingWorkspaceRef.current === uid) {
      if (isDevRuntime) {
        console.log("Track stock load skipped; workspace load already in flight", { uid });
      }
      return;
    }
    trackStockLoadingWorkspaceRef.current = uid;

    //  Correct new W1 path
    console.log("workspaceUid:", uid);
    console.log("module:", "Track Stock");
    console.log("collectionPath:", `users/${uid}/trackStock/main`);
    const refDoc = doc(db, "users", uid, "trackStock", "main");
    const snap = await getDocCached(refDoc, {
      label: "loadTrackStock",
      cacheKey: `users/${uid}/trackStock/main`,
      ttlMs: 5 * 60 * 1000,
    });
    if (isDevRuntime) {
      console.log("Track Stock exists:", snap.exists());
    }

    if (snap.exists()) {
      setTrackLeftovers(snap.data().stock || {});
    } else {
      setTrackLeftovers({});
    }
    trackStockLoadedWorkspaceRef.current = uid;
  } catch (err) {
    console.error("Error loading track stock:", err);
    if (isFirestoreResourceExhausted(err)) {
      raiseFirebaseHealthWarning("firestore", err, {
        path: `users/${uid}/trackStock/main`,
        source: "loadTrackStock",
      });
      notify("Firebase read limit reached. Please wait and try again.", "warning");
    }
  } finally {
    if (trackStockLoadingWorkspaceRef.current === uid) {
      trackStockLoadingWorkspaceRef.current = "";
    }
  }
}, [notify, raiseFirebaseHealthWarning]);

/* ========================= FETCH FABRIC STOCK (NORMALIZED + SAFE) ========================= */

const fetchFabricStock = useCallback(async () => {
  try {
    if (!workspaceUid) return;

    setLoadingFabricStock(true);

    const collectionPath = `users/${workspaceUid}/fabricStock`;
    logWorkspaceQuery("Fabric Stock", collectionPath);
    const snap = await getDocsCached(
      firestoreQuery(
        collection(db, "users", workspaceUid, "fabricStock"),
        firestoreOrderBy(firestoreDocumentId()),
        firestoreLimit(FABRIC_STOCK_READ_LIMIT)
      ),
      {
        label: "fetchFabricStock",
        cacheKey: `${collectionPath}?limit=${FABRIC_STOCK_READ_LIMIT}`,
        ttlMs: 60 * 1000,
      }
    );
    logWorkspaceQuery("Fabric Stock", collectionPath, snap.size);

    const list = snap.docs.map((doc) => {
      const d = doc.data();

      let buckets = [];

      /* ---------- CASE 1: NEW FORMAT ---------- */
      if (Array.isArray(d.buckets)) {
        buckets = d.buckets.map((b) => {
          let length = Number(b.length) || 0;
          let type = b.type;

          //  Normalize legacy / invalid bucket types
          if (type === "NEGATIVE") {
            type = "LEFTOVER";
          }

          //  Roll should NEVER be negative
          if (type === "ROLL" && length < 0) {
            type = "LEFTOVER";
          }

          return {
            ...b,
            type,
            length,
          };
        });
      }

      /* ---------- CASE 2: OLD FORMAT (rolls / loose) ---------- */
      else {
        if (Array.isArray(d.rolls)) {
          d.rolls.forEach((r) => {
            const len = Number(r);
            if (len !== 0) {
              buckets.push({
                type: "ROLL",
                length: len,
              });
            }
          });
        }

        if (Number(d.loose)) {
          buckets.push({
            type: "LEFTOVER",
            length: Number(d.loose),
          });
        }
      }

      /* ---------- FINAL CLEANUP ---------- */

      // Remove zero-length junk
      buckets = buckets.filter(
        (b) => Number(b.length) !== 0
      );

      return {
        fabricKey: doc.id,
        buckets,
      };
    });

    setFabricStock(list);
  } catch (err) {
    console.error("Failed to load fabric stock", err);
    if (isFirestoreResourceExhausted(err)) {
      raiseFirebaseHealthWarning("firestore", err, {
        path: `users/${workspaceUid}/fabricStock`,
        source: "fetchFabricStock",
      });
      notify("Firebase read limit reached. Please wait and try again.", "warning");
    } else {
      alert("Failed to load fabric stock");
    }
  } finally {
    setLoadingFabricStock(false);
  }
}, [logWorkspaceQuery, notify, raiseFirebaseHealthWarning, workspaceUid]);

/* ========================= FETCH STOCK HISTORY (W1) ========================= */
const fetchStockHistory = useCallback(async () => {
  try {
    if (!workspaceUid) return;

    setLoadingStockHistory(true);

    const collectionPath = `users/${workspaceUid}/stockHistory`;
    logWorkspaceQuery("Stock History", collectionPath);
    let snap = await getDocsCached(
      firestoreQuery(
        collection(db, "users", workspaceUid, "stockHistory"),
        firestoreOrderBy("triggeredAt", "desc"),
        firestoreLimit(STOCK_HISTORY_READ_LIMIT)
      ),
      {
        label: "fetchStockHistory",
        cacheKey: `${collectionPath}?orderBy=triggeredAt_desc&limit=${STOCK_HISTORY_READ_LIMIT}`,
        ttlMs: 60 * 1000,
      }
    );
    if (snap.size === 0) {
      snap = await getDocsCached(
        firestoreQuery(
          collection(db, "users", workspaceUid, "stockHistory"),
          firestoreOrderBy(firestoreDocumentId()),
          firestoreLimit(STOCK_HISTORY_READ_LIMIT)
        ),
        {
          label: "fetchStockHistoryLegacyPage",
          cacheKey: `${collectionPath}?legacyDocIdLimit=${STOCK_HISTORY_READ_LIMIT}`,
          ttlMs: 60 * 1000,
        }
      );
    }
    logWorkspaceQuery("Stock History", collectionPath, snap.size);

    const history = snap.docs
      .map((d) => ({
        id: d.id,
        ...d.data(),
      }))
      .sort((a, b) => {
        const ta = a.triggeredAt?.toMillis?.() || 0; //  FIXED
        const tb = b.triggeredAt?.toMillis?.() || 0;
        return tb - ta;
      });

    setStockHistory(history);
  } catch (err) {
    console.error("Failed to load stock history", err);
    if (isFirestoreResourceExhausted(err)) {
      raiseFirebaseHealthWarning("firestore", err, {
        path: `users/${workspaceUid}/stockHistory`,
        source: "fetchStockHistory",
      });
      notify("Firebase read limit reached. Please wait and try again.", "warning");
    }
  } finally {
    setLoadingStockHistory(false);
  }
}, [logWorkspaceQuery, notify, raiseFirebaseHealthWarning, workspaceUid]);

const shouldSubscribeSavedQuotes = useMemo(
  () => tab === "saved",
  [tab]
);

const shouldSubscribeCrmLeads = useMemo(
  () => tab === "crm" || tab === "todayTasks" || tab === "executionSpecs",
  [tab]
);

const shouldSubscribeCustomerLedger = useMemo(
  () => tab === "customerLedger",
  [tab]
);

const shouldSubscribeCrmReminders = useMemo(
  () => true,
  []
);

const shouldSubscribeCrmExecutionTemplates = useMemo(
  () =>
    tab === "crm" ||
    tab === "executionSpecs" ||
    tab === "executionTemplates" ||
    (tab === "quote" && quoteSubTab === GENERAL_QUOTE_TYPE),
  [quoteSubTab, tab]
);

/* --------------------------- AUTH BOOTSTRAP --------------------------- */
useEffect(() => {
  const bootstrapUser = accountCurrentUser || auth.currentUser || null;
  const runId = workspaceBootstrapRunRef.current + 1;
  workspaceBootstrapRunRef.current = runId;
  let cancelled = false;
  let timeoutId = null;
  let lastCompletedStep = "";
  let loadingStep = accountLoading ? "account-loading" : "auth-state-start";

  const setDebug = (patch = {}) => {
    if (cancelled || workspaceBootstrapRunRef.current !== runId) return;
    setWorkspaceBootstrapDebug(
      buildWorkspaceBootstrapDebug({
        authUid: bootstrapUser?.uid || "",
        email: bootstrapUser?.email || "",
        workspaceUid: accountWorkspaceUid || "",
        loadingStep,
        lastCompletedStep,
        ...patch,
      })
    );
  };

  logAppStartupTrace({ step: "auth-state-start" });
  setUser(bootstrapUser);

  if (accountLoading) {
    setIsResolvingWorkspace(true);
    setWorkspaceBootstrapError(null);
    setDebug({ loadingStep: "account-loading" });
    logAppStartupTrace({ step: "auth-state-waiting-for-account" });
    return () => {
      cancelled = true;
    };
  }

  logAppStartupTrace({
    step: "auth-state-resolved",
    uid: bootstrapUser?.uid || "",
    email: bootstrapUser?.email || "",
  });

  if (!bootstrapUser?.uid) {
    setWorkspaceContext(null);
    setTeamMembers([]);
    setSavedQuotes([]);
    setCrmLeadRecords([]);
    setCustomerRecords([]);
    setWorkspaceBootstrapError(null);
    loadingStep = "signed-out";
    lastCompletedStep = "signed-out-cleanup";
    setDebug({
      authUid: "",
      email: "",
      workspaceUid: "",
      loadingStep,
      lastCompletedStep,
    });
    setIsResolvingWorkspace(false);
    return () => {
      cancelled = true;
    };
  }

  const finishWithError = (error, fallbackCode = "workspace_bootstrap_failed") => {
    if (cancelled || workspaceBootstrapRunRef.current !== runId) return;
    const errorCode = error?.code || error?.debug?.code || fallbackCode;
    const errorMessage =
      error?.message ||
      "We could not load your workspace. Please refresh or contact support.";
    const debug = buildWorkspaceBootstrapDebug({
      authUid: bootstrapUser.uid,
      email: bootstrapUser.email || "",
      workspaceUid: accountWorkspaceUid || "",
      loadingStep,
      lastCompletedStep,
      errorCode,
      errorMessage,
    });
    console.error("FABRIQUOTE_WORKSPACE_LOAD_FAILED", {
      step: "workspace-load-failed",
      ...debug,
      stack: error?.stack || "",
    });
    setWorkspaceBootstrapDebug(debug);
    setWorkspaceBootstrapError({
      code: errorCode,
      message:
        String(errorCode).toLowerCase().includes("permission") ||
        String(error?.message || "").toLowerCase().includes("permission")
          ? "We could not load your account. Missing or insufficient permissions."
          : errorMessage,
    });
    setWorkspaceContext(null);
    setIsResolvingWorkspace(false);
  };

  const bootstrapWorkspace = async () => {
    setIsResolvingWorkspace(true);
    setWorkspaceBootstrapError(null);
    loadingStep = "workspace-load-start";
    setDebug({ loadingStep });
    logAppStartupTrace({ step: "workspace-load-start" });

    timeoutId = window.setTimeout(() => {
      const timeoutError = new Error(
        "We could not load your workspace. Please refresh or contact support."
      );
      timeoutError.code = "workspace_timeout";
      finishWithError(timeoutError, "workspace_timeout");
    }, WORKSPACE_BOOTSTRAP_TIMEOUT_MS);

    try {
      const resolvedWorkspace = await resolveWorkspaceState(bootstrapUser);
      if (cancelled || workspaceBootstrapRunRef.current !== runId) return;

      setWorkspaceContext(resolvedWorkspace);
      const resolvedWorkspaceUid = resolvedWorkspace?.workspaceId || bootstrapUser.uid;
      lastCompletedStep = "workspace-load-success";
      loadingStep = "settings-load-start";
      setDebug({
        workspaceUid: resolvedWorkspaceUid,
        loadingStep,
        lastCompletedStep,
      });
      logAppStartupTrace({
        step: "workspace-load-success",
        workspaceUid: resolvedWorkspaceUid,
      });

      await loadUserSettings(resolvedWorkspaceUid);
      if (cancelled || workspaceBootstrapRunRef.current !== runId) return;

      lastCompletedStep = "settings-load-success";
      loadingStep = "app-render-ready";
      if (timeoutId) window.clearTimeout(timeoutId);
      timeoutId = null;
      setWorkspaceBootstrapError(null);
      setDebug({
        workspaceUid: resolvedWorkspaceUid,
        loadingStep,
        lastCompletedStep,
      });
      logAppStartupTrace({
        step: "app-render-ready",
        workspaceUid: resolvedWorkspaceUid,
      });
      setIsResolvingWorkspace(false);
    } catch (error) {
      if (timeoutId) window.clearTimeout(timeoutId);
      timeoutId = null;
      if (isFirestoreResourceExhausted(error)) {
        raiseFirebaseHealthWarning("firestore", error, {
          path: `users/${bootstrapUser.uid}/settings/main`,
          source: "workspaceBootstrap",
        });
        notify("Firebase read limit reached. Please wait and try again.", "warning");
      }
      finishWithError(error);
    }
  };

  bootstrapWorkspace();

  return () => {
    cancelled = true;
    if (timeoutId) window.clearTimeout(timeoutId);
  };
}, [
  accountCurrentUser,
  accountLoading,
  accountWorkspaceUid,
  loadUserSettings,
  notify,
  raiseFirebaseHealthWarning,
  resolveWorkspaceState,
]);


/* ================= LOAD STOCK + HISTORY WHEN STOCK TAB OPENS ================= */
useEffect(() => {
  if (tab === "stock" && workspaceUid) {
    fetchFabricStock();
    fetchStockHistory();
  }
}, [fetchFabricStock, fetchStockHistory, tab, workspaceUid]);

useEffect(() => {
  if (!workspaceUid) return;
  if (tab !== "stock" && tab !== "fco") return;

  loadTrackStock(workspaceUid);
}, [loadTrackStock, tab, workspaceUid]);


//  Auto-load quotation when KPI list selects a quote
/* --------------------- RESET Selected Quote When Leaving Quote Tab --------------------- */
useEffect(() => {
  if (tab !== "quote" && tab !== "fco") {
    setSelectedQuote(null); 
  }
}, [tab]);

// INSERT THIS RIGHT ABOVE "return ("
// SAFE VERSION  fresh object every time
const insertRowAfter = (index) => {
  commitCurtainRows((prevRows) => {
    const newRows = [...prevRows];

    newRows.splice(index + 1, 0, createDefaultCurtainQuoteRow(`W${index + 2}`));

    return newRows;
  }, QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT);
};

const addCurtainMeasurementRow = () => {
  commitCurtainRows((prevRows) => {
    const measurementCount = prevRows.filter((row) => !isCurtainAccessoryRow(row)).length;
    return [...prevRows, createDefaultCurtainQuoteRow(`W${measurementCount + 1}`)];
  }, QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT);
};

const addCurtainAccessoryRow = () => {
  commitCurtainRows((prevRows) => {
    const accessoryCount = prevRows.filter((row) => isCurtainAccessoryRow(row)).length;
    return [
      ...prevRows,
      createDefaultCurtainAccessoryRow(`Accessory ${accessoryCount + 1}`),
    ];
  }, QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT);
};

const filteredFabricStock = useMemo(() => {
  const queryText = stockSearch.trim().toLowerCase();
  if (!queryText) return fabricStock;

  return fabricStock.filter((fab) => {
    const name = (fab.displayName || fab.fabricKey || "").toLowerCase();
    return name.includes(queryText);
  });
}, [fabricStock, stockSearch]);

/* ---------------- Quote number ---------------- */


/* ---------------- LOAD SELECTED QUOTE INTO EDITOR ---------------- */


/* ---------------- LOAD SELECTED QUOTE INTO EDITOR ---------------- */
useEffect(() => {
  //  IMPORTANT:
  // Quotation loading is handled explicitly via loadQuotation()
  // from Dashboard / Saved tab / other click handlers.
  // This effect previously caused duplicate fetches and false
  // "Failed to load quotation" errors.
  
  // Intentionally left blank.
}, [selectedQuote, user]);



/* ---------------- LOAD OPTIMIZER QUOTE WHEN ID CHANGES ---------------- */
const getOptimizerQuoteSearchText = (quote = {}) =>
  normalizeSearchText(
    [
      quote.quoteNo,
      quote.quoteNoString,
      quote.customerName,
      quote.customerNameLower,
      quote.customerPhone,
      quote.customerWhatsapp,
      quote.normalizedPhone,
      quote.projectName,
      quote.projectNameLower,
      quote.title,
      quote.searchText,
      Array.isArray(quote.searchKeywords) ? quote.searchKeywords.join(" ") : "",
    ]
      .filter(Boolean)
      .join(" ")
  );

const normalizeOptimizerQuoteResult = useCallback((id, raw = {}) => ({
  id,
  quoteNo: raw.quoteNo || id,
  customerName:
    raw.customerName ||
    raw.quoteFor?.name ||
    raw.quoteFor?.company ||
    raw.crm?.customerName ||
    "Untitled customer",
  customerPhone:
    raw.customerPhone ||
    raw.customerWhatsapp ||
    raw.crm?.customerPhone ||
    raw.crm?.contact?.phone ||
    "",
  projectName:
    raw.projectName ||
    raw.projectTitle ||
    raw.title ||
    raw.crm?.projectTitle ||
    "",
  quoteDate: resolveQuoteDateForInput(raw),
  date: resolveQuoteDateForInput(raw),
  quoteType: raw.quoteType || resolveQuoteType(raw),
  status: raw.status || "draft",
  grandTotal: Number(
    raw.grandTotal ??
      raw.finalTotal ??
      raw.totalAmount ??
      raw.finalAmount ??
      raw.total ??
      0
  ),
  updatedAt: raw.updatedAt || raw.createdAt || null,
  searchText: getOptimizerQuoteSearchText(raw),
}), []);

const cloneOptimizerQuoteForState = (quote = {}) => ({
  ...quote,
  rows: Array.isArray(quote.rows)
    ? quote.rows.map((row, index) =>
        sanitizeCurtainRowEditableTextFields(withCurtainRowId({ ...row }, index))
      )
    : [],
  repeatSettings: quote.repeatSettings ? { ...quote.repeatSettings } : {},
});

const searchOptimizerQuotes = useCallback(
  async (queryText) => {
    const normalizedQuery = normalizeSearchText(queryText);
    const compactQuery = normalizeSearchCompact(queryText);
    if (!workspaceUid || normalizedQuery.length < 2) {
      setOptimizerQuoteSearchState({
        loading: false,
        error: "",
        results: [],
        searched: false,
      });
      return;
    }

    const cacheKey = `${workspaceUid}:${normalizedQuery}`;
    const cached = optimizerQuoteSearchCacheRef.current.get(cacheKey);
    if (
      cached &&
      Date.now() - Number(cached.cachedAt || 0) < OPTIMIZER_QUOTE_SEARCH_CACHE_TTL_MS
    ) {
      setOptimizerQuoteSearchState({
        loading: false,
        error: "",
        results: cached.results,
        searched: true,
      });
      if (isDevRuntime) {
        console.log("OPTIMIZER QUOTE SEARCH CACHE HIT", {
          query: queryText,
          normalizedQuery,
          resultCount: cached.results.length,
        });
      }
      return;
    }

    const requestId = optimizerQuoteSearchRequestRef.current + 1;
    optimizerQuoteSearchRequestRef.current = requestId;
    setOptimizerQuoteSearchState((prev) => ({
      ...prev,
      loading: true,
      error: "",
      searched: true,
    }));

    try {
      const quoteRef = collection(db, "users", workspaceUid, "quote_index");
      const resultMap = new Map();
      const addSnapResults = (snap, source) => {
        snap.docs.forEach((docSnap) => {
          if (resultMap.size >= OPTIMIZER_QUOTE_SEARCH_LIMIT) return;
          const raw = docSnap.data() || {};
          if (raw.deleted === true) return;
          if (resolveQuoteType(raw) === GENERAL_QUOTE_TYPE) return;
          const result = normalizeOptimizerQuoteResult(docSnap.id, raw);
          const haystack = result.searchText;
          const compactHaystack = normalizeSearchCompact(haystack);
          if (
            haystack.includes(normalizedQuery) ||
            (compactQuery && compactHaystack.includes(compactQuery))
          ) {
            resultMap.set(docSnap.id, { ...result, source });
          }
        });
      };

      const globalResults = await globalSearchQuotations(workspaceUid, queryText, {
        limit: OPTIMIZER_QUOTE_SEARCH_LIMIT,
        ttlMs: OPTIMIZER_QUOTE_SEARCH_CACHE_TTL_MS,
        labelPrefix: "optimizerQuoteIndexSearch",
      });
      globalResults.forEach((raw) => {
        if (resultMap.size >= OPTIMIZER_QUOTE_SEARCH_LIMIT) return;
        if (raw.deleted === true) return;
        if (resolveQuoteType(raw) === GENERAL_QUOTE_TYPE) return;
        const result = normalizeOptimizerQuoteResult(raw.id, raw);
        const haystack = result.searchText;
        const compactHaystack = normalizeSearchCompact(haystack);
        if (
          haystack.includes(normalizedQuery) ||
          (compactQuery && compactHaystack.includes(compactQuery))
        ) {
          resultMap.set(raw.id, { ...result, source: raw._searchSource || "globalSearch" });
        }
      });

      if (resultMap.size < OPTIMIZER_QUOTE_SEARCH_LIMIT) {
        const recentSnap = await getDocsCached(
          firestoreQuery(
            quoteRef,
            firestoreOrderBy("updatedAt", "desc"),
            firestoreLimit(OPTIMIZER_QUOTE_SEARCH_FALLBACK_LIMIT)
          ),
          {
            label: "optimizerQuoteIndexRecentFallback",
            cacheKey: `users/${workspaceUid}/quote_index?optimizerRecentFallback=${OPTIMIZER_QUOTE_SEARCH_FALLBACK_LIMIT}`,
            ttlMs: 60 * 1000,
          }
        );
        addSnapResults(recentSnap, "recentFallback");
      }

      const results = Array.from(resultMap.values()).slice(
        0,
        OPTIMIZER_QUOTE_SEARCH_LIMIT
      );
      optimizerQuoteSearchCacheRef.current.set(cacheKey, {
        cachedAt: Date.now(),
        results,
      });

      if (optimizerQuoteSearchRequestRef.current !== requestId) return;
      setOptimizerQuoteSearchState({
        loading: false,
        error: "",
        results,
        searched: true,
      });
      if (isDevRuntime) {
        console.log("OPTIMIZER QUOTE SEARCH QUERY", {
          query: queryText,
          normalizedQuery,
          resultCount: results.length,
          sources: Array.from(new Set(results.map((result) => result.source))),
        });
      }
    } catch (error) {
      if (optimizerQuoteSearchRequestRef.current !== requestId) return;
      console.error("Optimizer quote search failed:", {
        code: error?.code,
        message: error?.message,
        workspaceUid,
        queryText,
      });
      setOptimizerQuoteSearchState({
        loading: false,
        error: "Could not search saved quotations.",
        results: [],
        searched: true,
      });
      if (isFirestoreResourceExhausted(error)) {
        notify("Firebase read limit reached. Please wait and try again.", "warning");
      }
    }
  },
  [normalizeOptimizerQuoteResult, notify, workspaceUid]
);

const loadOptimizerQuoteById = useCallback(
  async (quoteId) => {
    if (!quoteId || !workspaceUid) return null;
    setOptimizerQuoteLoadingId(quoteId);
    try {
      const ref = doc(db, "users", workspaceUid, "quotations", quoteId);
      const snap = await getDocCached(ref, {
        label: "fullQuoteLoadById.optimizer",
        cacheKey: `users/${workspaceUid}/quotations/${quoteId}`,
        ttlMs: 60 * 1000,
      });

      if (!snap.exists()) {
        notify("Quote not found.", "warning");
        return null;
      }

      const fullQuote = cloneOptimizerQuoteForState({
        id: snap.id,
        ...snap.data(),
      });
      if (resolveQuoteType(fullQuote) === GENERAL_QUOTE_TYPE) {
        notify("Roll Optimizer currently supports curtain quotations.", "warning");
        return null;
      }

      setOptimizerQuote(fullQuote);
      setOptimizerQuoteId(snap.id);
      setOptimizerFabric("");
      setRollOptions([]);
      setCutPlan([]);
      setOptimizerFinalDemands([]);
      setSelectedSolutionIndex(0);
      return fullQuote;
    } catch (error) {
      console.error("Failed to load optimizer quote:", {
        code: error?.code,
        message: error?.message,
        workspaceUid,
        quoteId,
      });
      notify(
        isFirestoreResourceExhausted(error)
          ? "Firebase read limit reached. Please wait and try again."
          : "Could not load quotation for optimizer.",
        "warning"
      );
      return null;
    } finally {
      setOptimizerQuoteLoadingId("");
    }
  },
  [notify, workspaceUid]
);

useEffect(() => {
  if (tab !== "fco") return undefined;
  const queryText = optimizerQuoteSearch.trim();
  if (queryText.length < 2) {
    setOptimizerQuoteSearchState({
      loading: false,
      error: "",
      results: [],
      searched: false,
    });
    return undefined;
  }

  const timerId = window.setTimeout(() => {
    searchOptimizerQuotes(queryText);
  }, OPTIMIZER_QUOTE_SEARCH_DEBOUNCE_MS);

  return () => window.clearTimeout(timerId);
}, [optimizerQuoteSearch, searchOptimizerQuotes, tab]);

useEffect(() => {
  if (!optimizerQuoteId || !workspaceUid) return;
  if (optimizerQuote?.id === optimizerQuoteId && optimizerQuote?.rows) return;
  loadOptimizerQuoteById(optimizerQuoteId);
}, [loadOptimizerQuoteById, optimizerQuote, optimizerQuoteId, workspaceUid]);

const currentEditorOptimizerQuote = useMemo(() => {
  if (!selectedQuote?.id || resolveQuoteType(selectedQuote) === GENERAL_QUOTE_TYPE) {
    return null;
  }

  const currentRows = Array.isArray(selectedQuote.rows) && selectedQuote.rows.length
    ? selectedQuote.rows
    : rows;

  return cloneOptimizerQuoteForState({
    ...selectedQuote,
    rows: currentRows,
    repeatSettings: selectedQuote.repeatSettings || repeatSettings,
  });
}, [repeatSettings, rows, selectedQuote]);

/* --------------------------- QUOTE LOADER --------------------------- */
/* --------------------------- QUOTE LIST LOADER (FAST) --------------------------- */
/* --------------------------- FETCH SAVED QUOTES (normalized) --------------------------- */
/* --------------------------- FETCH SAVED QUOTES --------------------------- */
const subscribeSavedQuotes = useCallback((uid) => {
  if (!uid) return () => {};

  setLoadingSaved(true);

  const normalizeTs = (v) => {
    if (!v) return null;
    if (v?.toDate) return v.toDate();
    if (typeof v === "string" || v instanceof Date) return new Date(v);
    return null;
  };

  const allowedQuoteTypes = [
    canAccessCurtainQuotes ? CURTAIN_QUOTE_TYPE : null,
    canAccessGeneralQuotes ? GENERAL_QUOTE_TYPE : null,
  ].filter(Boolean);

  if (allowedQuoteTypes.length === 0) {
    setSavedQuotes([]);
    setDeletedQuotes([]);
    setDebugRawQuotationCount(0);
    savedQuoteIndexCursorRef.current = null;
    setHasMoreSavedQuoteIndex(false);
    setLoadingSaved(false);
    return () => {};
  }

  const collectionPath = `users/${uid}/quote_index`;
  console.log("workspaceUid:", uid);
  console.log("module:", "Saved Quotations Index");
  console.log("collectionPath:", collectionPath);
  if (isDevRuntime) {
    console.log("SAVED_QUOTES_ACCESS_TRACE", {
      authUid: auth.currentUser?.uid || user?.uid || "",
      workspaceUid: uid,
      route: typeof window !== "undefined" ? window.location.pathname : "",
      quoteIndexPath: collectionPath,
      userRole: accountUser?.role || workspaceContext?.role || "",
      isOwner: isWorkspaceOwner,
      isStaff: workspaceContext?.role === "staff",
      allowedQuoteTypes,
    });
  }
  setSavedQuotesAccessError(null);

  const qRef = collection(db, "users", uid, "quote_index");
  const recentQuotesQuery = firestoreQuery(
    qRef,
    firestoreOrderBy("updatedAt", "desc"),
    firestoreLimit(SAVED_QUOTES_INITIAL_READ_LIMIT)
  );

  let cancelled = false;
  const recentCacheKey = `savedQuotes:recent:${uid}:firstPage:limit${SAVED_QUOTES_INITIAL_READ_LIMIT}`;
  const currentRecentLoad = savedQuotesRecentLoadRef.current || {};

  if (currentRecentLoad.workspaceUid !== uid) {
    savedQuotesRecentLoadRef.current = {
      workspaceUid: uid,
      loading: false,
      loaded: false,
      promise: null,
      result: null,
    };
  }

  const applyRecentResult = (result) => {
    if (cancelled || !result) return;
    savedQuoteIndexCursorRef.current = result.cursor || null;
    setHasMoreSavedQuoteIndex(Boolean(result.hasMore));
    setDebugRawQuotationCount(Number(result.debugCount || 0));
    setSavedQuotes(result.activeQuotes || []);
    setLoadingSaved(false);
  };

  const loadSavedQuotesRecentPage = async () => {
    const loadState = savedQuotesRecentLoadRef.current || {};
    if (loadState.workspaceUid === uid && loadState.loaded && loadState.result) {
      return loadState.result;
    }

    if (loadState.workspaceUid === uid && loadState.loading && loadState.promise) {
      return loadState.promise;
    }

    const actualLoadPromise = (async () => {
      let recentSnap = await getDocsCached(recentQuotesQuery, {
        label: "loadSavedQuotesRecentPage",
        cacheKey: recentCacheKey,
        ttlMs: 5 * 60 * 1000,
      });

      if (!recentSnap || recentSnap.size === 0) {
        recentSnap = await getDocsCached(
          firestoreQuery(
            qRef,
            firestoreOrderBy(firestoreDocumentId()),
            firestoreLimit(SAVED_QUOTES_INITIAL_READ_LIMIT)
          ),
          {
            label: "loadSavedQuoteIndexLegacyPage",
            cacheKey: `savedQuotes:recent:${uid}:legacyDocId:firstPage:limit${SAVED_QUOTES_INITIAL_READ_LIMIT}`,
            ttlMs: 5 * 60 * 1000,
          }
        );
      }

      const allQuotes = [];
      recentSnap.forEach((d) => {
        const raw = d.data() || {};
        allQuotes.push(normalizeQuoteIndexRecordForList(d.id, raw, normalizeTs));
      });

      const planVisibleQuotes = allQuotes.filter((q) =>
        allowedQuoteTypes.includes(q.quoteTypeResolved || resolveQuoteType(q))
      );
      const activeQuotes = planVisibleQuotes.filter((q) => !q.deleted);
      const result = {
        activeQuotes,
        cursor: recentSnap.docs[recentSnap.docs.length - 1] || null,
        hasMore: recentSnap.size === SAVED_QUOTES_INITIAL_READ_LIMIT,
        debugCount: Number(recentSnap.size || 0),
      };

      savedQuotesRecentLoadRef.current = {
        workspaceUid: uid,
        loading: false,
        loaded: true,
        promise: null,
        result,
      };

      return result;
    })().catch((error) => {
      savedQuotesRecentLoadRef.current = {
        workspaceUid: uid,
        loading: false,
        loaded: false,
        promise: null,
        result: null,
      };
      throw error;
    });

    savedQuotesRecentLoadRef.current = {
      workspaceUid: uid,
      loading: true,
      loaded: false,
      promise: actualLoadPromise,
      result: null,
    };

    return actualLoadPromise;
  };

  loadSavedQuotesRecentPage()
    .then((result) => {
      if (cancelled) return;
      console.log("Saved Quotations count:", result?.debugCount || 0);
      applyRecentResult(result);
    })
    .catch((err) => {
      if (cancelled) return;
      console.error("Saved quotes load failed:", {
        code: err?.code,
        message: err?.message,
        path: collectionPath,
        authUid: auth.currentUser?.uid || user?.uid || "",
        workspaceUid: uid,
        userRole: accountUser?.role || workspaceContext?.role || "",
      });
      if (isFirestoreResourceExhausted(err)) {
        notify("Firebase read limit reached. Please wait and try again.", "warning");
      } else if (String(err?.code || "").includes("permission-denied")) {
        setSavedQuotesAccessError({
          code: err?.code || "permission-denied",
          message:
            "Saved quotes could not be loaded because your account does not have permission for this workspace.",
          path: collectionPath,
        });
        notify(
          "Saved quotes cannot be loaded due to workspace permissions.",
          "error",
          7000
        );
      } else {
        setSavedQuotesAccessError({
          code: err?.code || "",
          message: "Saved quotes could not be loaded. Please try again.",
          path: collectionPath,
        });
      }
      setDebugRawQuotationCount(0);
      savedQuoteIndexCursorRef.current = null;
      setHasMoreSavedQuoteIndex(false);
      setSavedQuotes([]);
      setDeletedQuotes([]);
      setLoadingSaved(false);
    });

  return () => {
    cancelled = true;
    if (isDevRuntime) {
      console.log("ONE-TIME FIRESTORE LOAD CLEANUP", {
        label: "loadSavedQuotesOnce",
        path: collectionPath,
        at: new Date().toISOString(),
      });
    }
  };
}, [
  accountUser?.role,
  canAccessCurtainQuotes,
  canAccessGeneralQuotes,
  isWorkspaceOwner,
  notify,
  user?.uid,
  workspaceContext?.role,
]);

const openRecycleBin = useCallback(async () => {
  if (!workspaceUid) return;
  setRecycleBinOpen(true);

  const allowedQuoteTypes = [
    canAccessCurtainQuotes ? CURTAIN_QUOTE_TYPE : null,
    canAccessGeneralQuotes ? GENERAL_QUOTE_TYPE : null,
  ].filter(Boolean);
  if (allowedQuoteTypes.length === 0) {
    setDeletedQuotes([]);
    return;
  }

  const normalizeTs = (v) => {
    if (!v) return null;
    if (v?.toDate) return v.toDate();
    if (typeof v === "string" || v instanceof Date) return new Date(v);
    return null;
  };

  const applyRecycleResult = (result) => {
    setDeletedQuotes(result?.deletedQuotes || []);
  };

  const loadState = savedQuotesRecycleBinLoadRef.current || {};
  if (loadState.workspaceUid !== workspaceUid) {
    savedQuotesRecycleBinLoadRef.current = {
      workspaceUid,
      loading: false,
      loaded: false,
      promise: null,
      result: null,
    };
  }

  const currentLoadState = savedQuotesRecycleBinLoadRef.current || {};
  if (currentLoadState.loaded && currentLoadState.result) {
    applyRecycleResult(currentLoadState.result);
    return;
  }

  if (currentLoadState.loading && currentLoadState.promise) {
    setLoadingRecycleBinQuotes(true);
    try {
      applyRecycleResult(await currentLoadState.promise);
    } finally {
      setLoadingRecycleBinQuotes(false);
    }
    return;
  }

  setLoadingRecycleBinQuotes(true);
  const collectionPath = `users/${workspaceUid}/quote_index`;
  const recycleCacheKey = `savedQuotes:recycleBin:${workspaceUid}:firstPage:limit${SAVED_QUOTES_DELETED_READ_LIMIT}`;
  const recyclePromise = (async () => {
    const snap = await getDocsCached(
      firestoreQuery(
        collection(db, "users", workspaceUid, "quote_index"),
        firestoreWhere("deleted", "==", true),
        firestoreOrderBy("deletedAt", "desc"),
        firestoreLimit(SAVED_QUOTES_DELETED_READ_LIMIT)
      ),
      {
        label: "loadSavedQuotesRecycleBinPage",
        cacheKey: recycleCacheKey,
        ttlMs: 5 * 60 * 1000,
      }
    );

    const deletedQuotes = snap.docs
      .map((docSnap) =>
        normalizeQuoteIndexRecordForList(docSnap.id, docSnap.data() || {}, normalizeTs)
      )
      .filter((quote) =>
        allowedQuoteTypes.includes(quote.quoteTypeResolved || resolveQuoteType(quote))
      )
      .filter((quote) => quote.deleted);
    const result = { deletedQuotes };
    savedQuotesRecycleBinLoadRef.current = {
      workspaceUid,
      loading: false,
      loaded: true,
      promise: null,
      result,
    };
    return result;
  })().catch((error) => {
    savedQuotesRecycleBinLoadRef.current = {
      workspaceUid,
      loading: false,
      loaded: false,
      promise: null,
      result: null,
    };
    throw error;
  });

  savedQuotesRecycleBinLoadRef.current = {
    workspaceUid,
    loading: true,
    loaded: false,
    promise: recyclePromise,
    result: null,
  };

  try {
    applyRecycleResult(await recyclePromise);
  } catch (error) {
    console.error("Saved quotes recycle bin load failed:", {
      code: error?.code,
      message: error?.message,
      path: collectionPath,
    });
    if (isFirestoreResourceExhausted(error)) {
      notify("Firebase read limit reached. Please wait and try again.", "warning");
    } else {
      notify("Could not load Recycle Bin.", "warning");
    }
  } finally {
    setLoadingRecycleBinQuotes(false);
  }
}, [canAccessCurtainQuotes, canAccessGeneralQuotes, notify, workspaceUid]);

const loadMoreSavedQuoteIndex = useCallback(async () => {
  if (!workspaceUid || loadingMoreSavedQuotes || !hasMoreSavedQuoteIndex) return;
  const cursor = savedQuoteIndexCursorRef.current;
  if (!cursor) return;

  const allowedQuoteTypes = [
    canAccessCurtainQuotes ? CURTAIN_QUOTE_TYPE : null,
    canAccessGeneralQuotes ? GENERAL_QUOTE_TYPE : null,
  ].filter(Boolean);
  if (allowedQuoteTypes.length === 0) return;

  const normalizeTs = (v) => {
    if (!v) return null;
    if (v?.toDate) return v.toDate();
    if (typeof v === "string" || v instanceof Date) return new Date(v);
    return null;
  };

  setLoadingMoreSavedQuotes(true);
  try {
    const collectionPath = `users/${workspaceUid}/quote_index`;
    const nextPageQuery = firestoreQuery(
      collection(db, "users", workspaceUid, "quote_index"),
      firestoreOrderBy("updatedAt", "desc"),
      firestoreStartAfter(cursor),
      firestoreLimit(SAVED_QUOTES_PAGE_SIZE)
    );
    const snap = await getDocsCached(nextPageQuery, {
      label: "loadSavedQuoteIndexNextPage",
      cacheKey: `${collectionPath}?orderBy=updatedAt_desc&startAfter=${cursor.id}&limit=${SAVED_QUOTES_PAGE_SIZE}`,
      ttlMs: 5 * 60 * 1000,
    });

    const nextQuotes = snap.docs
      .map((docSnap) => normalizeQuoteIndexRecordForList(docSnap.id, docSnap.data() || {}, normalizeTs))
      .filter((quote) =>
        allowedQuoteTypes.includes(quote.quoteTypeResolved || resolveQuoteType(quote))
      );

    setSavedQuotes((prev) => {
      const merged = new Map((prev || []).map((quote) => [quote.id, quote]));
      nextQuotes.forEach((quote) => {
        if (!quote.deleted) merged.set(quote.id, quote);
      });
      return Array.from(merged.values());
    });
    setDeletedQuotes((prev) => {
      const merged = new Map((prev || []).map((quote) => [quote.id, quote]));
      nextQuotes.forEach((quote) => {
        if (quote.deleted) merged.set(quote.id, quote);
      });
      return Array.from(merged.values());
    });
    savedQuoteIndexCursorRef.current = snap.docs[snap.docs.length - 1] || null;
    setHasMoreSavedQuoteIndex(snap.size === SAVED_QUOTES_PAGE_SIZE);
    setSavedQuotesVisibleCount((prev) => prev + SAVED_QUOTES_PAGE_SIZE);
  } catch (error) {
    console.error("Saved quote index pagination failed:", {
      code: error?.code,
      message: error?.message,
      workspaceUid,
    });
    if (isFirestoreResourceExhausted(error)) {
      notify("Firebase read limit reached. Please wait and try again.", "warning");
    } else if (String(error?.code || "").includes("permission-denied")) {
      setSavedQuotesAccessError({
        code: error?.code || "permission-denied",
        message:
          "Saved quotes could not be loaded because your account does not have permission for this workspace.",
        path: `users/${workspaceUid}/quote_index`,
      });
      notify("Saved quotes cannot be loaded due to workspace permissions.", "error", 7000);
    } else {
      notify("Could not load more saved quotations.", "warning");
    }
  } finally {
    setLoadingMoreSavedQuotes(false);
  }
}, [
  canAccessCurtainQuotes,
  canAccessGeneralQuotes,
  hasMoreSavedQuoteIndex,
  loadingMoreSavedQuotes,
  notify,
  workspaceUid,
]);

const searchSavedQuotesRemote = useCallback(
  async (uid, queryText) => {
    const normalizedQuery = normalizeSearchText(queryText);
    if (!uid || normalizedQuery.length < 2) return;

    const allowedQuoteTypes = [
      canAccessCurtainQuotes ? CURTAIN_QUOTE_TYPE : null,
      canAccessGeneralQuotes ? GENERAL_QUOTE_TYPE : null,
    ].filter(Boolean);
    if (allowedQuoteTypes.length === 0) return;

    const normalizeTs = (v) => {
      if (!v) return null;
      if (v?.toDate) return v.toDate();
      if (typeof v === "string" || v instanceof Date) return new Date(v);
      return null;
    };
    const normalizeQuoteRecord = (id, rawInput = {}) => {
      const raw = rawInput || {};
      return normalizeQuoteIndexRecordForList(id, raw, normalizeTs);
    };

    const resultMap = new Map();
    const results = await globalSearchQuotations(uid, queryText, {
      limit: SAVED_QUOTES_PAGE_SIZE,
      labelPrefix: "savedQuotesQuoteIndexSearch",
    });
    setSavedQuotesAccessError(null);
    results.forEach((entry) => {
        const quote = normalizeQuoteRecord(entry.id, entry);
        if (!allowedQuoteTypes.includes(quote.quoteTypeResolved || resolveQuoteType(quote))) {
          return;
        }
        if (doesSearchBlobMatch(buildQuoteSearchBlob(quote), normalizedQuery)) {
          resultMap.set(quote.id, quote);
        }
    });
    if (resultMap.size === 0) return;

    setSavedQuotes((prev) => {
      const merged = new Map((prev || []).map((quote) => [quote.id, quote]));
      resultMap.forEach((quote, id) => {
        if (!quote.deleted) merged.set(id, quote);
      });
      return Array.from(merged.values());
    });
    setDeletedQuotes((prev) => {
      const merged = new Map((prev || []).map((quote) => [quote.id, quote]));
      resultMap.forEach((quote, id) => {
        if (quote.deleted) merged.set(id, quote);
      });
      return Array.from(merged.values());
    });
  },
  [canAccessCurtainQuotes, canAccessGeneralQuotes]
);

const repairQuotationSearchIndex = useCallback(async () => {
  if (!workspaceUid || quoteSearchRepairState.running) return;

  setQuoteSearchRepairState({
    running: true,
    processed: 0,
    updated: 0,
    error: "",
  });

  try {
    const repairQuoteSearchIndexCallable = httpsCallable(functions, "repairQuoteSearchIndex");
    const result = await repairQuoteSearchIndexCallable({ workspaceUid });
    const processed = Number(result?.data?.scanned || 0);
    const updated = Number(result?.data?.repaired || 0);
    invalidateWorkspaceCollectionCache(workspaceUid, "quote_index");
    savedQuotesRecentLoadRef.current = {
      workspaceUid: "",
      loading: false,
      loaded: false,
      promise: null,
      result: null,
    };
    setSavedQuotesAccessError(null);
    setQuoteSearchRepairState({
      running: false,
      processed,
      updated,
      error: result?.data?.errors ? `${result.data.errors} record(s) skipped` : "",
    });
    notify(`Quotation search index repaired (${updated} updated).`, "success");
    if (tab === "saved") {
      subscribeSavedQuotes(workspaceUid);
    }
  } catch (error) {
    console.error("Quotation search index repair failed", {
      code: error?.code,
      message: error?.message,
      workspaceUid,
    });
    setQuoteSearchRepairState((prev) => ({
      ...prev,
      running: false,
      error: error?.message || "Repair failed",
    }));
    notify("Could not repair quotation search index.", "error");
  }
}, [notify, quoteSearchRepairState.running, subscribeSavedQuotes, tab, workspaceUid]);

const rebuildAnalyticsAndCalendarIndexes = useCallback(async () => {
  if (!workspaceUid || analyticsRebuildState.running) return;

  setAnalyticsRebuildState({
    running: true,
    scanned: 0,
    indexedQuotes: 0,
    monthlyUpdated: 0,
    clientUpdated: 0,
    installationsUpdated: 0,
    staleCleared: 0,
    skipped: 0,
    failed: 0,
    error: "",
  });

  try {
    let cursor = null;
    let scanned = 0;
    let indexedQuotes = 0;
    let monthlyUpdated = 0;
    let clientUpdated = 0;
    let installationsUpdated = 0;
    let staleCleared = 0;
    let skipped = 0;
    let failed = 0;
    let hasMore = true;
    const monthlyTotals = new Map();
    const clientTotals = new Map();
    const clientMetadata = new Map();
    const expectedQuoteIds = new Set();
    const expectedInstallationIds = new Set();
    const mergeSummaryDelta = (targetMap, sourceMap) => {
      sourceMap.forEach((delta, key) => {
        targetMap.set(key, applySummaryDelta(targetMap.get(key), delta));
      });
    };

    while (hasMore) {
      const constraints = [
        firestoreOrderBy(firestoreDocumentId()),
        firestoreLimit(100),
      ];
      if (cursor) constraints.push(firestoreStartAfter(cursor));

      const snap = await getDocs(
        firestoreQuery(
          collection(db, "users", workspaceUid, "quotations"),
          ...constraints
        ),
        {
          label: "rebuildAnalyticsIndexes",
          cacheKey: `users/${workspaceUid}/quotations?rebuildAnalyticsAfter=${
            cursor?.id || "start"
          }`,
        }
      );

      if (snap.empty) {
        hasMore = false;
        break;
      }

      const batch = writeBatch(db);
      const pageSources = [];

      snap.docs.forEach((docSnap) => {
        const raw = docSnap.data() || {};
        const source = {
          id: docSnap.id,
          ...raw,
        };
        pageSources.push(source);
        batch.set(
          doc(db, "users", workspaceUid, "quote_index", docSnap.id),
          {
            ...sanitizeCrmFirestoreValue(
              buildQuoteIndexPayload(source, docSnap.id, workspaceUid)
            ),
            searchIndexRepairedAt: serverTimestamp(),
            analyticsIndexRebuiltAt: serverTimestamp(),
          },
          { merge: true }
        );
      });

      for (const source of pageSources) {
        try {
          scanned += 1;
          const contribution = buildAnalyticsContributionForQuote(source);
          if (!contribution) {
            skipped += 1;
            continue;
          }
          expectedQuoteIds.add(source.id);

          mergeSummaryDelta(monthlyTotals, getMonthlyDeltaFromContribution(contribution, 1));
          const clientMonth = contribution.confirmedMonthKey || contribution.quoteMonthKey;
          if (clientMonth) {
            const clientDocId = `${clientMonth}_${contribution.customerKey || "unknown"}`;
            clientTotals.set(
              clientDocId,
            applySummaryDelta(clientTotals.get(clientDocId), {
              confirmedRevenue: Number(contribution.confirmedRevenue || 0),
              confirmedCount:
                contribution.confirmed && contribution.confirmedMonthKey ? 1 : 0,
              quotationRevenue: Number(contribution.quotationRevenue || 0),
              quotationCount: contribution.deleted ? 0 : 1,
            })
            );
            clientMetadata.set(clientDocId, {
              monthKey: clientMonth,
              customerKey: contribution.customerKey || "unknown",
              customerId: contribution.customerId || "",
              customerName: contribution.customerName || "Unnamed Customer",
              normalizedPhone: contribution.normalizedPhone || "",
              lastQuoteId: source.id,
              lastActivityAt: contribution.lastActivityAt || new Date(),
            });
          }

          batch.set(
            doc(db, "users", workspaceUid, "quote_analytics_index", source.id),
            {
              ...sanitizeCrmFirestoreValue(contribution),
              syncedAt: serverTimestamp(),
              source: "rebuildAnalyticsIndexes",
            },
            { merge: true }
          );

          if (contribution.hasInstallation && !contribution.deleted) {
            expectedInstallationIds.add(source.id);
            batch.set(
              doc(db, "users", workspaceUid, "installation_index", source.id),
              {
                quoteId: source.id,
                jobId: source.id,
                quoteNo: contribution.quoteNo,
                customerName: contribution.customerName,
                normalizedPhone: contribution.normalizedPhone,
                installationDate: contribution.installationDate,
                installationDateKey: contribution.installationDateKey,
                monthKey: contribution.installationMonthKey,
                status: contribution.status,
                workStage: contribution.workStage,
                totalAmount: contribution.totalAmount,
                confirmedRevenue: contribution.confirmedRevenue,
                itemSummary: contribution.itemSummary,
                location: contribution.location,
                source: "rebuildAnalyticsIndexes",
                updatedAt: serverTimestamp(),
              },
              { merge: true }
            );
          }

          const uniqueMonths = new Set(
            [
              contribution.quoteMonthKey,
              contribution.confirmedMonthKey,
              contribution.installationMonthKey,
            ].filter(Boolean)
          );
          indexedQuotes += 1;
          monthlyUpdated = monthlyTotals.size || uniqueMonths.size;
          clientUpdated = clientTotals.size;
          installationsUpdated += contribution.hasInstallation && !contribution.deleted ? 1 : 0;
        } catch (error) {
          failed += 1;
          if (isDevRuntime) {
            console.error("Analytics rebuild failed for quote", {
              quoteId: source.id,
              code: error?.code,
              message: error?.message,
            });
          }
        }
      }

      await batch.commit();

      cursor = snap.docs[snap.docs.length - 1];
      hasMore = snap.size === 100;
      setAnalyticsRebuildState({
        running: true,
        scanned,
        indexedQuotes,
        monthlyUpdated,
        clientUpdated,
        installationsUpdated,
        staleCleared,
        skipped,
        failed,
        error: "",
      });
    }

    const commitSummaryEntries = async (entries, writer) => {
      for (let index = 0; index < entries.length; index += 450) {
        const page = entries.slice(index, index + 450);
        const batch = writeBatch(db);
        page.forEach(([key, value]) => writer(batch, key, value));
        await batch.commit();
      }
    };

    await commitSummaryEntries(Array.from(monthlyTotals.entries()), (batch, monthKey, totals) => {
      batch.set(
        doc(db, "users", workspaceUid, "analytics_monthly", monthKey),
        {
          monthKey,
          confirmedRevenue: Number(Number(totals.confirmedRevenue || 0).toFixed(2)),
          confirmedCount: Number(totals.confirmedCount || 0),
          quotationRevenue: Number(Number(totals.quotationRevenue || 0).toFixed(2)),
          quotationCount: Number(totals.quotationCount || 0),
          cancelledRevenue: Number(Number(totals.cancelledRevenue || 0).toFixed(2)),
          cancelledCount: Number(totals.cancelledCount || 0),
          installedRevenue: Number(Number(totals.installedRevenue || 0).toFixed(2)),
          installedCount: Number(totals.installedCount || 0),
          rebuiltAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: false }
      );
    });

    await commitSummaryEntries(Array.from(clientTotals.entries()), (batch, clientDocId, totals) => {
      const meta = clientMetadata.get(clientDocId) || {};
      batch.set(
        doc(db, "users", workspaceUid, "client_monthly", clientDocId),
        {
          monthKey: meta.monthKey || clientDocId.split("_")[0],
          customerKey: meta.customerKey || "unknown",
          customerId: meta.customerId || "",
          customerName: meta.customerName || "Unnamed Customer",
          normalizedPhone: meta.normalizedPhone || "",
          confirmedRevenue: Number(Number(totals.confirmedRevenue || 0).toFixed(2)),
          confirmedCount: Number(totals.confirmedCount || 0),
          quotationRevenue: Number(Number(totals.quotationRevenue || 0).toFixed(2)),
          quoteCount: Number(totals.quotationCount || 0),
          lastQuoteId: meta.lastQuoteId || "",
          lastActivityAt: meta.lastActivityAt || new Date(),
          rebuiltAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: false }
      );
    });

    monthlyUpdated = monthlyTotals.size;
    clientUpdated = clientTotals.size;

    let leadCursor = null;
    let hasMoreLeads = true;
    while (hasMoreLeads) {
      const constraints = [
        firestoreOrderBy(firestoreDocumentId()),
        firestoreLimit(100),
      ];
      if (leadCursor) constraints.push(firestoreStartAfter(leadCursor));

      const leadSnap = await getDocs(
        firestoreQuery(
          collection(db, "users", workspaceUid, "crm_leads"),
          ...constraints
        ),
        {
          label: "rebuildAnalyticsIndexes.crmLeads",
          cacheKey: `users/${workspaceUid}/crm_leads?rebuildInstallationAfter=${
            leadCursor?.id || "start"
          }`,
        }
      );

      if (leadSnap.empty) {
        hasMoreLeads = false;
        break;
      }

      for (const leadDoc of leadSnap.docs) {
        scanned += 1;
        const lead = { id: leadDoc.id, ...(leadDoc.data() || {}) };
        const hasInstallationDate = Boolean(
          lead.installationDate ||
            lead.expectedInstallationDate ||
            lead.scheduledInstallationDate ||
            lead.installedAt ||
            lead.deliveryDate ||
            lead.crm?.installationDate
        );
        if (!hasInstallationDate) {
          skipped += 1;
          continue;
        }
        try {
          const linkedQuotes = normalizeCrmLinkedQuotes(lead);
          const primaryQuote = getPrimaryCrmLinkedQuote(linkedQuotes);
          const expectedInstallationId = String(
            primaryQuote?.quoteId || lead.quoteId || leadDoc.id
          ).trim();
          const leadStatus = String(lead.status || "").toLowerCase();
          const leadWorkStage = String(
            lead.workStage || lead.stageId || lead.stage || ""
          ).toLowerCase();
          const leadInactive =
            lead.deleted === true ||
            ["cancelled", "canceled", "lost"].includes(leadStatus) ||
            ["cancelled", "canceled", "lost"].includes(leadWorkStage);
          if (expectedInstallationId && !leadInactive) {
            expectedInstallationIds.add(expectedInstallationId);
          }
          await syncCrmLeadInstallationIndex(leadDoc.id, lead, {
            source: "rebuildAnalyticsIndexes.crmLeads",
          });
          installationsUpdated += 1;
        } catch (error) {
          failed += 1;
          if (isDevRuntime) {
            console.error("CRM installation index rebuild failed for lead", {
              leadId: leadDoc.id,
              code: error?.code,
              message: error?.message,
            });
          }
        }
      }

      leadCursor = leadSnap.docs[leadSnap.docs.length - 1];
      hasMoreLeads = leadSnap.size === 100;
      setAnalyticsRebuildState({
        running: true,
        scanned,
        indexedQuotes,
        monthlyUpdated,
        clientUpdated,
        installationsUpdated,
        staleCleared,
        skipped,
        failed,
        error: "",
      });
    }

    const markStaleDerivedDocs = async (collectionName, expectedIds, buildPatch) => {
      let staleCursor = null;
      let hasMoreStaleDocs = true;
      while (hasMoreStaleDocs) {
        const constraints = [
          firestoreOrderBy(firestoreDocumentId()),
          firestoreLimit(100),
        ];
        if (staleCursor) constraints.push(firestoreStartAfter(staleCursor));

        const staleSnap = await getDocs(
          firestoreQuery(
            collection(db, "users", workspaceUid, collectionName),
            ...constraints
          ),
          {
            label: `rebuildAnalyticsIndexes.clearStale.${collectionName}`,
            cacheKey: `users/${workspaceUid}/${collectionName}?clearStaleAfter=${
              staleCursor?.id || "start"
            }`,
          }
        );

        if (staleSnap.empty) {
          hasMoreStaleDocs = false;
          break;
        }

        const staleBatch = writeBatch(db);
        let pageWrites = 0;
        staleSnap.docs.forEach((docSnap) => {
          if (expectedIds.has(docSnap.id)) return;
          const patch = sanitizeCrmFirestoreValue(buildPatch(docSnap.data() || {}, docSnap.id));
          if (!patch || Object.keys(patch).length === 0) return;
          staleBatch.set(
            doc(db, "users", workspaceUid, collectionName, docSnap.id),
            patch,
            { merge: true }
          );
          pageWrites += 1;
        });

        if (pageWrites > 0) {
          await staleBatch.commit();
          staleCleared += pageWrites;
          setAnalyticsRebuildState({
            running: true,
            scanned,
            indexedQuotes,
            monthlyUpdated,
            clientUpdated,
            installationsUpdated,
            staleCleared,
            skipped,
            failed,
            error: "",
          });
        }

        staleCursor = staleSnap.docs[staleSnap.docs.length - 1];
        hasMoreStaleDocs = staleSnap.size === 100;
      }
    };

    await markStaleDerivedDocs("analytics_monthly", new Set(monthlyTotals.keys()), () => ({
      confirmedRevenue: 0,
      confirmedCount: 0,
      quotationRevenue: 0,
      quotationCount: 0,
      cancelledRevenue: 0,
      cancelledCount: 0,
      installedRevenue: 0,
      installedCount: 0,
      stale: true,
      staleReason: "not_present_in_latest_source_rebuild",
      rebuiltAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }));
    await markStaleDerivedDocs("client_monthly", new Set(clientTotals.keys()), () => ({
      confirmedRevenue: 0,
      confirmedCount: 0,
      quotationRevenue: 0,
      quoteCount: 0,
      stale: true,
      staleReason: "not_present_in_latest_source_rebuild",
      rebuiltAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }));
    await markStaleDerivedDocs("installation_index", expectedInstallationIds, () => ({
      active: false,
      stale: true,
      staleReason: "no_current_source_installation_date",
      updatedAt: serverTimestamp(),
    }));
    await markStaleDerivedDocs("quote_analytics_index", expectedQuoteIds, () => ({
      quotationRevenue: 0,
      confirmedRevenue: 0,
      cancelledRevenue: 0,
      installedRevenue: 0,
      totalAmount: 0,
      sourceMissing: true,
      stale: true,
      staleReason: "source_quotation_missing",
      syncedAt: serverTimestamp(),
    }));
    await markStaleDerivedDocs("quote_index", expectedQuoteIds, () => ({
      deleted: true,
      sourceMissing: true,
      stale: true,
      staleReason: "source_quotation_missing",
      updatedAt: serverTimestamp(),
    }));

    invalidateWorkspaceCollectionCache(workspaceUid, "quote_index");
    invalidateWorkspaceCollectionCache(workspaceUid, "quote_analytics_index");
    invalidateWorkspaceCollectionCache(workspaceUid, "analytics_monthly");
    invalidateWorkspaceCollectionCache(workspaceUid, "client_monthly");
    invalidateWorkspaceCollectionCache(workspaceUid, "installation_index");
    setAnalyticsRebuildState({
      running: false,
      scanned,
      indexedQuotes,
      monthlyUpdated,
      clientUpdated,
      installationsUpdated,
      staleCleared,
      skipped,
      failed,
      error: "",
    });
    notify(`Analytics and calendar indexes rebuilt (${indexedQuotes} quotations indexed).`, "success");
  } catch (error) {
    console.error("Analytics and calendar index rebuild failed", error);
    setAnalyticsRebuildState((prev) => ({
      ...prev,
      running: false,
      error: error?.message || "Rebuild failed",
    }));
    notify("Could not rebuild analytics and calendar indexes.", "error");
  }
}, [
  analyticsRebuildState.running,
  applySummaryDelta,
  buildAnalyticsContributionForQuote,
  getMonthlyDeltaFromContribution,
  notify,
  syncCrmLeadInstallationIndex,
  workspaceUid,
]);

const runTemporaryFullDataAudit = useCallback(async () => {
  if (!workspaceUid || adminDataAuditState.running) return;

  setAdminDataAuditState({
    running: true,
    checkedAt: "",
    error: "",
    collections: {},
    progress: {
      collection: "",
      scanned: 0,
    },
    report: null,
  });

  if (typeof httpsCallable === "function") {
    try {
      const auditWorkspaceDataCallable = httpsCallable(functions, "auditWorkspaceData");
      const result = await auditWorkspaceDataCallable({ workspaceUid });
      const data = result?.data || {};
      const collections = data.collections || {};
      const report = data.report || {
        generatedAt: data.checkedAt || new Date().toISOString(),
        workspaceUid,
        sourceDocumentCounts: {},
        derivedDocumentCounts: {},
        issueCounts: {},
        mismatchSummary: {
          totalMismatchCount: 0,
          rootCauseHints: [],
        },
      };
      const totalScanned = Object.values(collections).reduce(
        (total, collectionResult) =>
          total + Number(collectionResult?.totalCount || 0),
        0
      );

      setAdminDataAuditState({
        running: false,
        checkedAt: data.checkedAt || report.generatedAt || new Date().toISOString(),
        error: "",
        collections,
        progress: {
          collection: "",
          scanned: totalScanned,
        },
        report,
      });
      notify("Firebase data audit completed.", "success");
      return;
    } catch (error) {
      console.error("Audit Firebase Data callable failed", {
        code: error?.code,
        message: error?.message,
        workspaceUid,
      });
      setAdminDataAuditState((prev) => ({
        ...prev,
        running: false,
        error: error?.message || "Audit failed",
      }));
      notify("Could not complete Firebase data audit.", "error");
      return;
    }
  }

  const collectionNames = [
    "quotations",
    "crm_leads",
    "customers",
    "quote_index",
    "analytics_monthly",
    "client_monthly",
    "installation_index",
    "quote_analytics_index",
    "crm_reminders",
  ];

  const nextCollections = {};
  const collectionDocs = {};
  const startedAt = new Date().toISOString();

  const roundAuditNumber = (value) => Number(Number(value || 0).toFixed(2));
  const numberCloseEnough = (actual, expected) =>
    Math.abs(Number(actual || 0) - Number(expected || 0)) < 0.01;
  const nonZeroSummaryValue = (docData = {}) =>
    [
      "confirmedRevenue",
      "confirmedCount",
      "quotationRevenue",
      "quotationCount",
      "cancelledRevenue",
      "cancelledCount",
      "installedRevenue",
      "installedCount",
      "quoteCount",
    ].some((field) => Math.abs(Number(docData?.[field] || 0)) > 0.01);
  const pushSample = (target, value, max = 20) => {
    if (!Array.isArray(target) || target.length >= max) return;
    target.push(sanitizeCrmFirestoreValue(value));
  };
  const mergeAuditSummaryDelta = (targetMap, sourceMap) => {
    sourceMap.forEach((delta, key) => {
      targetMap.set(key, applySummaryDelta(targetMap.get(key), delta));
    });
  };
  const summarizeAuditDoc = (id, data = {}) => ({
    id,
    keys: Object.keys(data).slice(0, 16),
    quoteNo: data.quoteNo || "",
    customerName: data.customerName || data.contact?.name || "",
    monthKey: data.monthKey || "",
    installationDateKey: data.installationDateKey || "",
    quoteType: data.quoteType || data.quoteTypeResolved || "",
  });

  const scanCollectionForAudit = async (collectionName) => {
    const collectionPath = `users/${workspaceUid}/${collectionName}`;
    const docsMap = new Map();
    const samples = [];
    let cursor = null;
    let hasMore = true;
    let batches = 0;

    try {
      while (hasMore) {
        const constraints = [
          firestoreOrderBy(firestoreDocumentId()),
          firestoreLimit(100),
        ];
        if (cursor) constraints.push(firestoreStartAfter(cursor));

        const snap = await getDocs(
          firestoreQuery(
            collection(db, "users", workspaceUid, collectionName),
            ...constraints
          ),
          {
            label: `auditFirebaseData.scan.${collectionName}`,
            cacheKey: `${collectionPath}?auditAfter=${cursor?.id || "start"}`,
          }
        );

        batches += 1;
        snap.docs.forEach((docSnap) => {
          const data = docSnap.data() || {};
          docsMap.set(docSnap.id, {
            id: docSnap.id,
            ...data,
          });
          if (samples.length < 5) {
            samples.push(summarizeAuditDoc(docSnap.id, data));
          }
        });

        nextCollections[collectionName] = {
          ok: true,
          path: collectionPath,
          totalCount: docsMap.size,
          sampleCount: samples.length,
          batches,
          samples,
        };

        setAdminDataAuditState((prev) => ({
          ...prev,
          running: true,
          checkedAt: "",
          error: "",
          collections: { ...nextCollections },
          progress: {
            collection: collectionName,
            scanned: docsMap.size,
          },
          report: null,
        }));

        cursor = snap.docs[snap.docs.length - 1] || null;
        hasMore = snap.size === 100;
      }
    } catch (error) {
      nextCollections[collectionName] = {
        ok: false,
        path: collectionPath,
        totalCount: docsMap.size,
        sampleCount: samples.length,
        batches,
        samples,
        errorCode: error?.code || "",
        errorMessage: error?.message || String(error),
      };
      if (isDevRuntime) {
        console.error("Audit Firebase Data collection failed", {
          collectionName,
          path: collectionPath,
          code: error?.code,
          message: error?.message,
        });
      }
    }

    return docsMap;
  };

  try {
    for (const collectionName of collectionNames) {
      collectionDocs[collectionName] = await scanCollectionForAudit(collectionName);
    }

    const quoteDocs = collectionDocs.quotations || new Map();
    const crmLeadDocs = collectionDocs.crm_leads || new Map();
    const quoteIndexDocs = collectionDocs.quote_index || new Map();
    const analyticsMonthlyDocs = collectionDocs.analytics_monthly || new Map();
    const clientMonthlyDocs = collectionDocs.client_monthly || new Map();
    const installationIndexDocs = collectionDocs.installation_index || new Map();
    const quoteAnalyticsDocs = collectionDocs.quote_analytics_index || new Map();

    const expectedMonthly = new Map();
    const expectedClientMonthly = new Map();
    const expectedClientMetadata = new Map();
    const expectedInstallation = new Map();
    const expectedQuoteAnalytics = new Map();
    const sourceQuoteIds = new Set();
    const auditSamples = {
      invalidAmounts: [],
      invalidDates: [],
      confirmedWithoutConfirmedAt: [],
      missingQuoteIndex: [],
      missingQuoteAnalyticsIndex: [],
      missingAnalyticsMonthly: [],
      analyticsMismatches: [],
      staleAnalyticsMonthly: [],
      missingClientMonthly: [],
      clientMonthlyMismatches: [],
      duplicateClientMonthly: [],
      staleClientMonthly: [],
      missingInstallationIndex: [],
      installationMismatches: [],
      staleInstallationIndex: [],
      quoteAnalyticsMismatches: [],
      quoteAnalyticsSourceMissing: [],
      quoteIndexSourceMissing: [],
      deletedIncludedInAnalytics: [],
      cancelledIncludedInConfirmedRevenue: [],
      skippedQuotes: [],
    };
    const issueCounts = {
      invalidAmountCount: 0,
      invalidDateCount: 0,
      confirmedWithoutConfirmedAtCount: 0,
      skippedQuoteCount: 0,
      missingQuoteIndexCount: 0,
      missingQuoteAnalyticsIndexCount: 0,
      missingAnalyticsMonthlyCount: 0,
      analyticsMismatchCount: 0,
      staleAnalyticsMonthlyCount: 0,
      missingClientMonthlyCount: 0,
      clientMonthlyMismatchCount: 0,
      duplicatedClientMonthlyCount: 0,
      staleClientMonthlyCount: 0,
      missingInstallationIndexCount: 0,
      installationMismatchCount: 0,
      staleInstallationIndexCount: 0,
      quoteAnalyticsMismatchCount: 0,
      quoteAnalyticsSourceMissingCount: 0,
      quoteIndexSourceMissingCount: 0,
      deletedIncludedInAnalyticsCount: 0,
      cancelledIncludedInConfirmedRevenueCount: 0,
    };

    const compareMonthlyFields = (actual = {}, expected = {}, fields, onMismatch) => {
      fields.forEach((field) => {
        if (!numberCloseEnough(actual[field], expected[field])) {
          onMismatch({
            field,
            actual: roundAuditNumber(actual[field]),
            expected: roundAuditNumber(expected[field]),
          });
        }
      });
    };

    quoteDocs.forEach((quote, quoteId) => {
      sourceQuoteIds.add(quoteId);
      const contribution = buildAnalyticsContributionForQuote({
        ...quote,
        id: quoteId,
        quoteId,
      });

      if (!contribution) {
        issueCounts.skippedQuoteCount += 1;
        pushSample(auditSamples.skippedQuotes, { quoteId, reason: "No analytics contribution" });
        return;
      }

      expectedQuoteAnalytics.set(quoteId, contribution);
      mergeAuditSummaryDelta(expectedMonthly, getMonthlyDeltaFromContribution(contribution, 1));

      const clientMonth = contribution.confirmedMonthKey || contribution.quoteMonthKey;
      if (clientMonth) {
        const clientDocId = `${clientMonth}_${contribution.customerKey || "unknown"}`;
        expectedClientMonthly.set(
          clientDocId,
          applySummaryDelta(expectedClientMonthly.get(clientDocId), {
            confirmedRevenue: Number(contribution.confirmedRevenue || 0),
            confirmedCount:
              contribution.confirmed && contribution.confirmedMonthKey ? 1 : 0,
            quotationRevenue: Number(contribution.quotationRevenue || 0),
            quotationCount: contribution.deleted ? 0 : 1,
          })
        );
        expectedClientMetadata.set(clientDocId, {
          monthKey: clientMonth,
          customerKey: contribution.customerKey || "unknown",
          customerName: contribution.customerName || "Unnamed Customer",
        });
      }

      if (contribution.hasInstallation && !contribution.deleted) {
        expectedInstallation.set(quoteId, {
          source: "quotations",
          quoteId,
          installationDateKey: contribution.installationDateKey,
          monthKey: contribution.installationMonthKey,
          customerName: contribution.customerName,
          quoteNo: contribution.quoteNo,
        });
      }

      if (contribution.issues?.includes("invalid_amount")) {
        issueCounts.invalidAmountCount += 1;
        pushSample(auditSamples.invalidAmounts, {
          quoteId,
          quoteNo: contribution.quoteNo,
          customerName: contribution.customerName,
          totalSource: contribution.totalSource,
        });
      }
      const dateIssues = (contribution.issues || []).filter((issue) =>
        issue.startsWith("invalid_") && issue.includes("date")
      );
      if (dateIssues.length) {
        issueCounts.invalidDateCount += 1;
        pushSample(auditSamples.invalidDates, {
          quoteId,
          quoteNo: contribution.quoteNo,
          issues: dateIssues,
        });
      }
      if (contribution.issues?.includes("confirmed_without_confirmedAt")) {
        issueCounts.confirmedWithoutConfirmedAtCount += 1;
        pushSample(auditSamples.confirmedWithoutConfirmedAt, {
          quoteId,
          quoteNo: contribution.quoteNo,
          customerName: contribution.customerName,
          status: contribution.status,
          workStage: contribution.workStage,
        });
      }

      if (!quoteIndexDocs.has(quoteId)) {
        issueCounts.missingQuoteIndexCount += 1;
        pushSample(auditSamples.missingQuoteIndex, {
          quoteId,
          quoteNo: contribution.quoteNo,
          customerName: contribution.customerName,
        });
      }

      const quoteAnalyticsDoc = quoteAnalyticsDocs.get(quoteId);
      if (!quoteAnalyticsDoc) {
        issueCounts.missingQuoteAnalyticsIndexCount += 1;
        pushSample(auditSamples.missingQuoteAnalyticsIndex, {
          quoteId,
          quoteNo: contribution.quoteNo,
        });
      } else {
        const mismatches = [];
        compareMonthlyFields(
          quoteAnalyticsDoc,
          contribution,
          [
            "quotationRevenue",
            "confirmedRevenue",
            "cancelledRevenue",
            "installedRevenue",
            "totalAmount",
          ],
          (mismatch) => mismatches.push(mismatch)
        );
        ["quoteMonthKey", "confirmedMonthKey", "installationDateKey", "installationMonthKey"].forEach(
          (field) => {
            if (String(quoteAnalyticsDoc[field] || "") !== String(contribution[field] || "")) {
              mismatches.push({
                field,
                actual: quoteAnalyticsDoc[field] || "",
                expected: contribution[field] || "",
              });
            }
          }
        );
        if (mismatches.length) {
          issueCounts.quoteAnalyticsMismatchCount += 1;
          pushSample(auditSamples.quoteAnalyticsMismatches, {
            quoteId,
            quoteNo: contribution.quoteNo,
            mismatches,
          });
        }
        if (contribution.deleted && nonZeroSummaryValue(quoteAnalyticsDoc)) {
          issueCounts.deletedIncludedInAnalyticsCount += 1;
          pushSample(auditSamples.deletedIncludedInAnalytics, {
            quoteId,
            quoteNo: contribution.quoteNo,
            actual: {
              quotationRevenue: quoteAnalyticsDoc.quotationRevenue || 0,
              confirmedRevenue: quoteAnalyticsDoc.confirmedRevenue || 0,
            },
          });
        }
        if (contribution.cancelled && Number(quoteAnalyticsDoc.confirmedRevenue || 0) > 0) {
          issueCounts.cancelledIncludedInConfirmedRevenueCount += 1;
          pushSample(auditSamples.cancelledIncludedInConfirmedRevenue, {
            quoteId,
            quoteNo: contribution.quoteNo,
            confirmedRevenue: quoteAnalyticsDoc.confirmedRevenue || 0,
          });
        }
      }
    });

    crmLeadDocs.forEach((lead, leadId) => {
      const linkedQuotes = normalizeCrmLinkedQuotes(lead);
      const primaryQuote = getPrimaryCrmLinkedQuote(linkedQuotes);
      const indexId = String(primaryQuote?.quoteId || lead.quoteId || leadId).trim();
      if (!indexId) return;

      const installationDate =
        lead.installationDate ||
        lead.expectedInstallationDate ||
        lead.scheduledInstallationDate ||
        lead.installedAt ||
        lead.deliveryDate ||
        lead.crm?.installationDate ||
        null;
      const installationDateKey = toDateInputValue(installationDate);
      const status = String(lead.status || "").toLowerCase();
      const workStage = String(lead.workStage || lead.stageId || lead.stage || "").toLowerCase();
      const inactive =
        lead.deleted === true ||
        ["cancelled", "canceled", "lost"].includes(status) ||
        ["cancelled", "canceled", "lost"].includes(workStage);

      if (installationDateKey && !inactive && !expectedInstallation.has(indexId)) {
        expectedInstallation.set(indexId, {
          source: "crm_leads",
          quoteId: primaryQuote?.quoteId || lead.quoteId || "",
          leadId,
          installationDateKey,
          monthKey: installationDateKey.slice(0, 7),
          customerName:
            lead.customerName || lead.contact?.name || lead.title || "Unnamed Customer",
          quoteNo: primaryQuote?.quoteNo || lead.quoteNo || "",
        });
      }
    });

    expectedMonthly.forEach((totals, monthKey) => {
      const actual = analyticsMonthlyDocs.get(monthKey);
      if (!actual) {
        issueCounts.missingAnalyticsMonthlyCount += 1;
        pushSample(auditSamples.missingAnalyticsMonthly, { monthKey, expected: totals });
        return;
      }
      const mismatches = [];
      compareMonthlyFields(
        actual,
        totals,
        [
          "confirmedRevenue",
          "confirmedCount",
          "quotationRevenue",
          "quotationCount",
          "cancelledRevenue",
          "cancelledCount",
          "installedRevenue",
          "installedCount",
        ],
        (mismatch) => mismatches.push(mismatch)
      );
      if (mismatches.length) {
        issueCounts.analyticsMismatchCount += 1;
        pushSample(auditSamples.analyticsMismatches, {
          monthKey,
          mismatches,
        });
      }
    });

    analyticsMonthlyDocs.forEach((actual, monthKey) => {
      if (!expectedMonthly.has(monthKey) && nonZeroSummaryValue(actual)) {
        issueCounts.staleAnalyticsMonthlyCount += 1;
        pushSample(auditSamples.staleAnalyticsMonthly, {
          monthKey,
          actual: {
            confirmedRevenue: actual.confirmedRevenue || 0,
            quotationRevenue: actual.quotationRevenue || 0,
            installedRevenue: actual.installedRevenue || 0,
          },
        });
      }
    });

    expectedClientMonthly.forEach((totals, clientDocId) => {
      const actual = clientMonthlyDocs.get(clientDocId);
      if (!actual) {
        issueCounts.missingClientMonthlyCount += 1;
        pushSample(auditSamples.missingClientMonthly, {
          clientDocId,
          metadata: expectedClientMetadata.get(clientDocId),
          expected: totals,
        });
        return;
      }
      const mismatches = [];
      compareMonthlyFields(
        actual,
        {
          ...totals,
          quoteCount: totals.quotationCount,
        },
        ["confirmedRevenue", "confirmedCount", "quotationRevenue", "quoteCount"],
        (mismatch) => mismatches.push(mismatch)
      );
      if (mismatches.length) {
        issueCounts.clientMonthlyMismatchCount += 1;
        pushSample(auditSamples.clientMonthlyMismatches, {
          clientDocId,
          metadata: expectedClientMetadata.get(clientDocId),
          mismatches,
        });
      }
    });

    const clientSignatureGroups = new Map();
    clientMonthlyDocs.forEach((actual, clientDocId) => {
      const signature = `${actual.monthKey || clientDocId.split("_")[0] || ""}_${
        actual.customerKey || "unknown"
      }`;
      clientSignatureGroups.set(signature, [
        ...(clientSignatureGroups.get(signature) || []),
        clientDocId,
      ]);
      if (!expectedClientMonthly.has(clientDocId) && nonZeroSummaryValue(actual)) {
        issueCounts.staleClientMonthlyCount += 1;
        pushSample(auditSamples.staleClientMonthly, {
          clientDocId,
          monthKey: actual.monthKey || "",
          customerName: actual.customerName || "",
          confirmedRevenue: actual.confirmedRevenue || 0,
          quotationRevenue: actual.quotationRevenue || 0,
        });
      }
    });
    clientSignatureGroups.forEach((ids, signature) => {
      if (ids.length > 1) {
        issueCounts.duplicatedClientMonthlyCount += ids.length - 1;
        pushSample(auditSamples.duplicateClientMonthly, { signature, ids });
      }
    });

    expectedInstallation.forEach((expected, installationId) => {
      const actual = installationIndexDocs.get(installationId);
      if (!actual) {
        issueCounts.missingInstallationIndexCount += 1;
        pushSample(auditSamples.missingInstallationIndex, {
          installationId,
          ...expected,
        });
        return;
      }
      const mismatches = [];
      ["installationDateKey", "monthKey"].forEach((field) => {
        if (String(actual[field] || "") !== String(expected[field] || "")) {
          mismatches.push({
            field,
            actual: actual[field] || "",
            expected: expected[field] || "",
          });
        }
      });
      if (mismatches.length) {
        issueCounts.installationMismatchCount += 1;
        pushSample(auditSamples.installationMismatches, {
          installationId,
          source: expected.source,
          mismatches,
        });
      }
    });

    installationIndexDocs.forEach((actual, installationId) => {
      if (!expectedInstallation.has(installationId) && actual.active !== false && actual.stale !== true) {
        issueCounts.staleInstallationIndexCount += 1;
        pushSample(auditSamples.staleInstallationIndex, {
          installationId,
          quoteId: actual.quoteId || "",
          leadId: actual.leadId || "",
          customerName: actual.customerName || "",
          installationDateKey: actual.installationDateKey || "",
        });
      }
    });

    quoteAnalyticsDocs.forEach((actual, quoteId) => {
      if (!sourceQuoteIds.has(quoteId) && actual.sourceMissing !== true && actual.stale !== true) {
        issueCounts.quoteAnalyticsSourceMissingCount += 1;
        pushSample(auditSamples.quoteAnalyticsSourceMissing, {
          quoteId,
          quoteNo: actual.quoteNo || "",
          customerName: actual.customerName || "",
        });
      }
    });

    quoteIndexDocs.forEach((actual, quoteId) => {
      if (!sourceQuoteIds.has(quoteId) && actual.sourceMissing !== true && actual.stale !== true) {
        issueCounts.quoteIndexSourceMissingCount += 1;
        pushSample(auditSamples.quoteIndexSourceMissing, {
          quoteId,
          quoteNo: actual.quoteNo || "",
          customerName: actual.customerName || "",
        });
      }
    });

    const totalMismatchCount = Object.entries(issueCounts)
      .filter(([key]) => !["invalidAmountCount", "invalidDateCount"].includes(key))
      .reduce((total, [, value]) => total + Number(value || 0), 0);
    const rootCauseHints = [];
    if (
      issueCounts.missingAnalyticsMonthlyCount ||
      issueCounts.missingClientMonthlyCount ||
      issueCounts.missingQuoteIndexCount ||
      issueCounts.missingQuoteAnalyticsIndexCount ||
      issueCounts.missingInstallationIndexCount
    ) {
      rootCauseHints.push("Derived index/summary documents are missing or rebuild did not complete.");
    }
    if (
      issueCounts.analyticsMismatchCount ||
      issueCounts.clientMonthlyMismatchCount ||
      issueCounts.quoteAnalyticsMismatchCount ||
      issueCounts.installationMismatchCount
    ) {
      rootCauseHints.push("Derived summaries are out of sync with source quotations/CRM leads.");
    }
    if (
      issueCounts.staleAnalyticsMonthlyCount ||
      issueCounts.staleClientMonthlyCount ||
      issueCounts.staleInstallationIndexCount ||
      issueCounts.quoteAnalyticsSourceMissingCount ||
      issueCounts.quoteIndexSourceMissingCount
    ) {
      rootCauseHints.push("Stale derived documents from earlier rebuild/diff logic are still visible.");
    }
    if (issueCounts.confirmedWithoutConfirmedAtCount) {
      rootCauseHints.push("Some confirmed quotations are missing confirmedAt, so monthly confirmed revenue needs repair.");
    }
    if (issueCounts.invalidAmountCount) {
      rootCauseHints.push("Some quotations do not expose a recognizable total amount field.");
    }
    if (!rootCauseHints.length) {
      rootCauseHints.push("No source-vs-index mismatches found by this audit.");
    }

    const report = {
      generatedAt: new Date().toISOString(),
      startedAt,
      workspaceUid,
      sourceDocumentCounts: {
        quotations: quoteDocs.size,
        crm_leads: crmLeadDocs.size,
        customers: (collectionDocs.customers || new Map()).size,
        crm_reminders: (collectionDocs.crm_reminders || new Map()).size,
      },
      derivedDocumentCounts: {
        quote_index: quoteIndexDocs.size,
        analytics_monthly: analyticsMonthlyDocs.size,
        client_monthly: clientMonthlyDocs.size,
        installation_index: installationIndexDocs.size,
        quote_analytics_index: quoteAnalyticsDocs.size,
      },
      expectedDerivedCounts: {
        quote_index: sourceQuoteIds.size,
        analytics_monthly: expectedMonthly.size,
        client_monthly: expectedClientMonthly.size,
        installation_index: expectedInstallation.size,
        quote_analytics_index: expectedQuoteAnalytics.size,
      },
      issueCounts,
      mismatchSummary: {
        totalMismatchCount,
        rootCauseHints,
      },
      expectedMonthly: Object.fromEntries(
        Array.from(expectedMonthly.entries()).map(([monthKey, totals]) => [
          monthKey,
          {
            confirmedRevenue: roundAuditNumber(totals.confirmedRevenue),
            confirmedCount: Number(totals.confirmedCount || 0),
            quotationRevenue: roundAuditNumber(totals.quotationRevenue),
            quotationCount: Number(totals.quotationCount || 0),
            cancelledRevenue: roundAuditNumber(totals.cancelledRevenue),
            cancelledCount: Number(totals.cancelledCount || 0),
            installedRevenue: roundAuditNumber(totals.installedRevenue),
            installedCount: Number(totals.installedCount || 0),
          },
        ])
      ),
      samples: auditSamples,
      collectionResults: nextCollections,
      readLabels: collectionNames.map((name) => `auditFirebaseData.scan.${name}`),
    };

    setAdminDataAuditState({
      running: false,
      checkedAt: report.generatedAt,
      error: "",
      collections: nextCollections,
      progress: {
        collection: "",
        scanned:
          quoteDocs.size +
          crmLeadDocs.size +
          quoteIndexDocs.size +
          analyticsMonthlyDocs.size +
          clientMonthlyDocs.size +
          installationIndexDocs.size +
          quoteAnalyticsDocs.size,
      },
      report,
    });
  } catch (error) {
    console.error("Audit Firebase Data failed", error);
    setAdminDataAuditState((prev) => ({
      ...prev,
      running: false,
      error: error?.message || "Audit failed",
    }));
    notify("Could not complete Firebase data audit.", "error");
  }
}, [
  adminDataAuditState.running,
  applySummaryDelta,
  buildAnalyticsContributionForQuote,
  getMonthlyDeltaFromContribution,
  notify,
  toDateInputValue,
  workspaceUid,
]);

const downloadAdminDataAuditReport = useCallback(() => {
  if (!adminDataAuditState.report || typeof document === "undefined") return;
  const fileNameWorkspace = String(workspaceUid || "workspace").replace(/[^a-z0-9_-]/gi, "");
  const blob = new Blob(
    [JSON.stringify(adminDataAuditState.report, null, 2)],
    { type: "application/json" }
  );
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `fabriquote-firebase-audit-${fileNameWorkspace}-${Date.now()}.json`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}, [adminDataAuditState.report, workspaceUid]);

const setSearchStatus = useCallback((key, patch) => {
  setGlobalSearchStatus((prev) => ({
    ...prev,
    [key]: {
      ...(prev[key] || {}),
      ...patch,
    },
  }));
}, []);

const normalizeCrmLeadSearchRecord = useCallback(
  (entry = {}) => {
    const lead = buildCrmLeadData({
      id: entry.id,
      ...entry,
    });
    return {
      ...lead,
      ...buildCrmSearchIndexFields(lead),
    };
  },
  [buildCrmLeadData]
);

const mergeCrmLeadSearchRecords = useCallback(
  (entries = []) => {
    const normalizedEntries = (Array.isArray(entries) ? entries : [])
      .map(normalizeCrmLeadSearchRecord)
      .filter((lead) => lead?.id);
    if (normalizedEntries.length === 0) return;

    setCrmLeadRecords((prev) => {
      const merged = new Map((prev || []).map((lead) => [String(lead.id || ""), lead]));
      normalizedEntries.forEach((lead) => {
        const key = String(lead.id || "");
        merged.set(key, {
          ...(merged.get(key) || {}),
          ...lead,
        });
      });
      return Array.from(merged.values()).sort(
        (a, b) =>
          safeTimeMillis(b.updatedAt || b.createdAt) -
          safeTimeMillis(a.updatedAt || a.createdAt)
      );
    });
  },
  [normalizeCrmLeadSearchRecord]
);

const mergeCustomerSearchRecords = useCallback(
  (entries = []) => {
    const normalizedEntries = mergeCustomersById(entries);
    if (normalizedEntries.length === 0) return;

    setCustomerRecords((prev) => {
      const merged = new Map((prev || []).map((customer) => [String(customer.id || ""), customer]));
      normalizedEntries.forEach((customer) => {
        const key = String(customer.id || "");
        merged.set(key, {
          ...(merged.get(key) || {}),
          ...customer,
        });
      });
      return Array.from(merged.values()).sort(
        (a, b) =>
          safeTimeMillis(b.lastActivityAt || b.updatedAt || b.createdAt) -
          safeTimeMillis(a.lastActivityAt || a.updatedAt || a.createdAt)
      );
    });
  },
  [mergeCustomersById]
);

const runCrmGlobalSearch = useCallback(
  async (queryText, statusKey = "crm") => {
    const normalizedQuery = normalizeSearchText(queryText);
    if (!workspaceUid || !canAccessCrm || normalizedQuery.length < 2) return;

    setSearchStatus(statusKey, { loading: true, error: "", query: queryText });
    try {
      const results = await globalSearchCrmLeads(workspaceUid, queryText, {
        limit: GLOBAL_SEARCH_RESULT_LIMIT,
        labelPrefix: `${statusKey}.globalCrmLeadSearch`,
      });
      mergeCrmLeadSearchRecords(results);
      setSearchStatus(statusKey, {
        loading: false,
        error: "",
        resultCount: results.length,
        searched: true,
      });
      if (isDevRuntime) {
        console.log("GLOBAL CRM SEARCH HYDRATED", {
          statusKey,
          query: queryText,
          resultCount: results.length,
        });
      }
    } catch (error) {
      console.error("Global CRM search failed:", {
        code: error?.code,
        message: error?.message,
        statusKey,
        queryText,
      });
      setSearchStatus(statusKey, {
        loading: false,
        error:
          error?.code === "failed-precondition"
            ? "Search index required. Please ask admin to create the Firestore index."
            : "Could not search older CRM records.",
        searched: true,
      });
      if (isFirestoreResourceExhausted(error)) {
        notify("Firebase read limit reached. Please wait and try again.", "warning");
      }
    }
  },
  [
    canAccessCrm,
    mergeCrmLeadSearchRecords,
    notify,
    setSearchStatus,
    workspaceUid,
  ]
);

const runCustomerGlobalSearch = useCallback(
  async (queryText, statusKey = "customers") => {
    const normalizedQuery = normalizeCustomerSearchText(queryText);
    if (!workspaceUid || normalizedQuery.length < 2) return;

    setSearchStatus(statusKey, { loading: true, error: "", query: queryText });
    try {
      const results = await globalSearchCustomers(workspaceUid, queryText, {
        limit: CUSTOMER_SEARCH_RESULT_LIMIT,
        ttlMs: CUSTOMER_SEARCH_TTL_MS,
        labelPrefix: `${statusKey}.globalCustomerSearch`,
      });
      mergeCustomerSearchRecords(results);
      setSearchStatus(statusKey, {
        loading: false,
        error: "",
        resultCount: results.length,
        searched: true,
      });
      if (isDevRuntime) {
        console.log("GLOBAL CUSTOMER SEARCH HYDRATED", {
          statusKey,
          query: queryText,
          resultCount: results.length,
        });
      }
    } catch (error) {
      console.error("Global customer search failed:", {
        code: error?.code,
        message: error?.message,
        statusKey,
        queryText,
      });
      setSearchStatus(statusKey, {
        loading: false,
        error:
          error?.code === "failed-precondition"
            ? "Search index required. Please ask admin to create the Firestore index."
            : "Could not search older customer records.",
        searched: true,
      });
      if (isFirestoreResourceExhausted(error)) {
        notify("Firebase read limit reached. Please wait and try again.", "warning");
      }
    }
  },
  [
    mergeCustomerSearchRecords,
    notify,
    setSearchStatus,
    workspaceUid,
  ]
);

const runTaskHistoryGlobalSearch = useCallback(
  async (queryText, statusKey = "taskHistorySearch") => {
    const normalizedQuery = normalizeSearchText(queryText);
    if (!workspaceUid || !canAccessCrm || normalizedQuery.length < 2) return;

    setSearchStatus(statusKey, { loading: true, error: "", query: queryText });
    try {
      const results = await globalSearchTaskHistory(workspaceUid, queryText, {
        limit: GLOBAL_SEARCH_RESULT_LIMIT,
        labelPrefix: `${statusKey}.globalTaskHistorySearch`,
      });
      setCrmTaskHistoryRecords((prev) => {
        const merged = new Map((prev || []).map((record) => [String(record.id || ""), record]));
        results.forEach((record) => {
          const key = String(record.id || "");
          if (!key) return;
          merged.set(key, {
            ...(merged.get(key) || {}),
            ...record,
          });
        });
        return Array.from(merged.values()).sort(
          (a, b) =>
            safeTimeMillis(b.completedAt || b.updatedAt || b.createdAt) -
            safeTimeMillis(a.completedAt || a.updatedAt || a.createdAt)
        );
      });
      setSearchStatus(statusKey, {
        loading: false,
        error: "",
        resultCount: results.length,
        searched: true,
      });
    } catch (error) {
      console.error("Global task history search failed:", {
        code: error?.code,
        message: error?.message,
        statusKey,
        queryText,
      });
      setSearchStatus(statusKey, {
        loading: false,
        error:
          error?.code === "failed-precondition"
            ? "Search index required. Please ask admin to create the Firestore index."
            : "Could not search older task records.",
        searched: true,
      });
      if (isFirestoreResourceExhausted(error)) {
        notify("Firebase read limit reached. Please wait and try again.", "warning");
      }
    }
  },
  [canAccessCrm, notify, setSearchStatus, workspaceUid]
);

const subscribeCrmLeads = useCallback((uid) => {
  if (!uid) return () => {};

  const normalizeTs = (v) => {
    if (!v) return null;
    if (v?.toDate) return v.toDate();
    if (typeof v === "string" || v instanceof Date) return new Date(v);
    return null;
  };

  const allowedLeadTypes = [
    canAccessCurtainQuotes ? CURTAIN_QUOTE_TYPE : null,
    canAccessGeneralQuotes ? GENERAL_QUOTE_TYPE : null,
  ].filter(Boolean);

  if (allowedLeadTypes.length === 0) {
    setCrmLeadRecords([]);
    setDebugRawCrmLeadCount(0);
    return () => {};
  }

  const collectionPath = `users/${uid}/crm_leads`;
  if (IS_DEV_UI_ENABLED) {
    console.log("workspaceUid:", uid);
    console.log("module:", "CRM");
    console.log("collectionPath:", collectionPath);
  }

  const leadsRef = collection(db, "users", uid, "crm_leads");
  const recentLeadsQuery = firestoreQuery(
    leadsRef,
    firestoreOrderBy("updatedAt", "desc"),
    firestoreLimit(CRM_LEADS_INITIAL_READ_LIMIT)
  );

  let cancelled = false;
  getDocsCached(recentLeadsQuery, {
    label: "loadCrmLeadsRecentPage",
    cacheKey: `crmDeals:recent:${uid}:limit${CRM_LEADS_INITIAL_READ_LIMIT}`,
    ttlMs: 5 * 60 * 1000,
  })
    .then(async (snap) => {
      if (cancelled) return;
      let sourceSnap = snap;
      if (sourceSnap.size === 0) {
        sourceSnap = await getDocsCached(
          firestoreQuery(
            leadsRef,
            firestoreOrderBy(firestoreDocumentId()),
            firestoreLimit(CRM_LEADS_INITIAL_READ_LIMIT)
          ),
          {
            label: "loadCrmLeadsLegacyPage",
            cacheKey: `crmDeals:recent:${uid}:legacyDocId:limit${CRM_LEADS_INITIAL_READ_LIMIT}`,
            ttlMs: 5 * 60 * 1000,
          }
        );
      }
      if (IS_DEV_UI_ENABLED) {
        console.log("CRM count:", sourceSnap.size);
      }
      setDebugRawCrmLeadCount(sourceSnap.size);
      const nextLeads = [];

      sourceSnap.forEach((docSnap) => {
        const raw = docSnap.data() || {};
        const pipelineType = isGeneralExecutionEligibleDeal(raw)
          ? GENERAL_QUOTE_TYPE
          : CURTAIN_QUOTE_TYPE;

        if (!allowedLeadTypes.includes(pipelineType)) {
          return;
        }

        const needsQuoteLinkMigration = CRM_LEGACY_QUOTE_LINK_FIELDS.some((field) =>
          Object.prototype.hasOwnProperty.call(raw, field)
        );
        const linkedQuotes = normalizeCrmLinkedQuotes({
          ...raw,
          linkedQuoteDate: normalizeTs(raw.linkedQuoteDate) || raw.linkedQuoteDate || null,
        });
        const rawChecklistItems = Array.isArray(raw.checklistItems)
          ? raw.checklistItems
          : Array.isArray(raw.checklist)
          ? raw.checklist
          : Array.isArray(raw.stageChecklist)
          ? raw.stageChecklist
          : [];

        nextLeads.push(stripLegacyCrmQuoteLinkFields({
          id: docSnap.id,
          ...raw,
          quoteType: raw.quoteType || pipelineType,
          pipelineType,
          contact: raw.contact || {},
          customerId: raw.customerId || "",
          customerName:
            raw.customerName ||
            raw.contact?.name ||
            raw.title ||
            "",
          customerPhone:
            raw.customerPhone ||
            raw.contact?.phone ||
            raw.contact?.whatsapp ||
            "",
          linkedQuotes,
          tags: Array.isArray(raw.tags) ? raw.tags : [],
          checklistItems: rawChecklistItems,
          hasQuote: raw.hasQuote === true || linkedQuotes.length > 0,
          nextActionDate: normalizeTs(raw.nextActionDate),
          installationDate: normalizeTs(raw.installationDate),
          reminderConfigs: normalizeCrmReminderConfigs(raw.reminderConfigs, {
            nextActionDate: normalizeTs(raw.nextActionDate),
            installationDate: normalizeTs(raw.installationDate),
          }),
          followUps: normalizeCrmFollowUps(raw.followUps, {
            nextAction: raw.nextAction,
            nextActionDate: normalizeTs(raw.nextActionDate),
            reminderConfig: raw?.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] || null,
          }),
          attachments: normalizeCrmAttachments(raw.attachments),
          executionSpecs: normalizeExecutionSpecs(raw.executionSpecs),
          assignedAt: normalizeTs(raw.assignedAt),
          createdAt: normalizeTs(raw.createdAt),
          updatedAt: normalizeTs(raw.updatedAt),
          needsQuoteLinkMigration,
        }));
      });

      setCrmLeadRecords(nextLeads);
    })
    .catch((error) => {
      if (cancelled) return;
      console.error("CRM leads load failed:", error);
      if (isFirestoreResourceExhausted(error)) {
        notify("Firebase read limit reached. Please wait and try again.", "warning");
      }
      setDebugRawCrmLeadCount(0);
      setCrmLeadRecords([]);
    });

  return () => {
    cancelled = true;
    if (isDevRuntime) {
      console.log("ONE-TIME FIRESTORE LOAD CLEANUP", {
        label: "loadCrmLeadsOnce",
        path: collectionPath,
        at: new Date().toISOString(),
      });
    }
  };
}, [canAccessCurtainQuotes, canAccessGeneralQuotes, notify]);

const loadDashboardPreviewData = useCallback(async (uid) => {
  if (!uid) return;
  if (isLocalStorageKillSwitchEnabled("FQ_DISABLE_DASHBOARD_PRELOADS")) {
    if (isDevRuntime) {
      console.warn("Dashboard preview reads disabled by FQ_DISABLE_DASHBOARD_PRELOADS");
    }
    return;
  }

  const normalizeTs = (v) => {
    if (!v) return null;
    if (v?.toDate) return v.toDate();
    if (typeof v === "string" || v instanceof Date) return new Date(v);
    return null;
  };

  const allowedQuoteTypes = [
    canAccessCurtainQuotes ? CURTAIN_QUOTE_TYPE : null,
    canAccessGeneralQuotes ? GENERAL_QUOTE_TYPE : null,
  ].filter(Boolean);

  const allowedLeadTypes = allowedQuoteTypes;

  try {
    const selectedAnalyticsMonthKeys = Array.from(
      new Set([
        ...getRecentMonthKeys(DASHBOARD_MONTHLY_CHART_MONTHS),
        ...getDashboardAnalyticsMonthKeys(periodType, customRange),
      ])
    );
    const primaryMonthKey = getPrimaryDashboardMonthKey(periodType, customRange);
    const calendarMonthKey = getMonthKeyFromDateParts(calendarMonth.year, calendarMonth.month);

    const monthlySnaps = await Promise.all(
      selectedAnalyticsMonthKeys.map((monthKey) =>
        getDocCached(doc(db, "users", uid, "analytics_monthly", monthKey), {
          label: "dashboardAnalyticsMonthlyById",
          cacheKey: `users/${uid}/analytics_monthly/${monthKey}`,
          ttlMs: 5 * 60 * 1000,
        }).then((snap) => ({ monthKey, snap }))
      )
    );

    const topClientsQuery = firestoreQuery(
      collection(db, "users", uid, "client_monthly"),
      firestoreWhere("monthKey", "==", primaryMonthKey),
      firestoreOrderBy("confirmedRevenue", "desc"),
      firestoreLimit(DASHBOARD_TOP_CLIENT_LIMIT)
    );
    const topClientSnap = await getDocsCached(topClientsQuery, {
      label: "dashboardClientMonthlyTop",
      cacheKey: `users/${uid}/client_monthly?monthKey=${primaryMonthKey}&orderBy=confirmedRevenue_desc&limit=${DASHBOARD_TOP_CLIENT_LIMIT}`,
      ttlMs: 5 * 60 * 1000,
    });

    const installationQuery = firestoreQuery(
      collection(db, "users", uid, "installation_index"),
      firestoreWhere("monthKey", "==", calendarMonthKey),
      firestoreOrderBy("installationDateKey", "asc"),
      firestoreLimit(DASHBOARD_INSTALLATION_MONTH_LIMIT)
    );
    const installationSnap = await getDocsCached(installationQuery, {
      label: "dashboardInstallationIndexMonth",
      cacheKey: `users/${uid}/installation_index?monthKey=${calendarMonthKey}&orderBy=installationDateKey_asc&limit=${DASHBOARD_INSTALLATION_MONTH_LIMIT}`,
      ttlMs: 5 * 60 * 1000,
    });

    const existingMonthlyCount = monthlySnaps.filter(({ snap }) => snap.exists()).length;
    const monthlySummaries = monthlySnaps.reduce((accumulator, { monthKey, snap }) => {
      accumulator[monthKey] = snap.exists()
        ? {
            monthKey,
            ...(snap.data() || {}),
            updatedAt: normalizeTs(snap.data()?.updatedAt),
          }
        : {
            monthKey,
            confirmedRevenue: 0,
            confirmedCount: 0,
            quotationRevenue: 0,
            quotationCount: 0,
            cancelledRevenue: 0,
            cancelledCount: 0,
            installedRevenue: 0,
            installedCount: 0,
            updatedAt: null,
          };
      return accumulator;
    }, {});
    const analyticsIndexMissing =
      existingMonthlyCount === 0 && topClientSnap.size === 0 && installationSnap.size === 0;

    const topClients = topClientSnap.docs.map((docSnap) => {
      const raw = docSnap.data() || {};
      return {
        id: docSnap.id,
        ...raw,
        name: raw.customerName || "Unnamed Customer",
        value: Number(raw.confirmedRevenue || raw.quotationRevenue || 0),
        count: Number(raw.confirmedCount || raw.quoteCount || 0),
        lastActivityAt: normalizeTs(raw.lastActivityAt),
        updatedAt: normalizeTs(raw.updatedAt),
      };
    });

    const installationRecords = installationSnap.docs.map((docSnap) => {
      const raw = docSnap.data() || {};
      const installDateKey =
        raw.installationDateKey || toDateInputValue(raw.installationDate);
      return {
        id: docSnap.id,
        ...raw,
        quoteId: raw.quoteId || docSnap.id,
        jobId: raw.jobId || raw.quoteId || docSnap.id,
        customerLabel: raw.customerName || "Unnamed Customer",
        customerName: raw.customerName || "",
        quoteNo: raw.quoteNo || "",
        installationDateKey: installDateKey,
        installationMillis: safeTimeMillis(raw.installationDate || installDateKey),
        totalValue: Number(raw.totalAmount || raw.confirmedRevenue || 0),
        updatedAt: normalizeTs(raw.updatedAt),
      };
    });

    setDashboardAnalyticsState({
      monthKeys: selectedAnalyticsMonthKeys,
      monthlySummaries,
      topClients,
      installationRecords,
      diagnostics: {
        source: "analytics_indexes",
        monthKey: primaryMonthKey,
        calendarMonthKey,
        reads: {
          analyticsMonthly: selectedAnalyticsMonthKeys.length,
          clientMonthly: topClientSnap.size,
          installationIndex: installationSnap.size,
        },
        summaryDocsFound: existingMonthlyCount,
        topClientDocsFound: topClientSnap.size,
        installationDocsFound: installationSnap.size,
        indexMissing: analyticsIndexMissing,
        status: analyticsIndexMissing ? "missing" : "ready",
        lastUpdated:
          monthlySummaries[primaryMonthKey]?.updatedAt ||
          topClients[0]?.updatedAt ||
          installationRecords[0]?.updatedAt ||
          null,
        loadedAt: new Date().toISOString(),
      },
    });

    const quoteQuery = firestoreQuery(
      collection(db, "users", uid, "quote_index"),
      firestoreOrderBy("updatedAt", "desc"),
      firestoreLimit(DASHBOARD_PREVIEW_READ_LIMIT)
    );
    const quoteSnap = await getDocsCached(quoteQuery, {
      label: "dashboardQuoteIndexLimited",
      cacheKey: `users/${uid}/quote_index?dashboardOrderBy=updatedAt_desc&limit=${DASHBOARD_PREVIEW_READ_LIMIT}`,
      ttlMs: 5 * 60 * 1000,
    });

    const dashboardQuotes = quoteSnap.docs
      .map((docSnap) => {
        const raw = docSnap.data() || {};
        return normalizeQuoteIndexRecordForList(docSnap.id, raw, normalizeTs);
      })
      .filter(
        (quote) =>
          !quote.deleted &&
          allowedQuoteTypes.includes(
            quote.quoteTypeResolved || resolveQuoteType(quote)
          )
      );

    setSavedQuotes(dashboardQuotes);
    setDeletedQuotes([]);
    setDebugRawQuotationCount(quoteSnap.size);

    if (!canAccessCrm || allowedLeadTypes.length === 0) {
      setCrmLeadRecords([]);
      return;
    }

    const leadQuery = firestoreQuery(
      collection(db, "users", uid, "crm_leads"),
      firestoreOrderBy("updatedAt", "desc"),
      firestoreLimit(DASHBOARD_PREVIEW_READ_LIMIT)
    );
    const leadSnap = await getDocsCached(leadQuery, {
      label: "dashboardCrmLeadsLimited",
      cacheKey: `users/${uid}/crm_leads?dashboardOrderBy=updatedAt_desc&limit=${DASHBOARD_PREVIEW_READ_LIMIT}`,
      ttlMs: 5 * 60 * 1000,
    });

    const dashboardLeads = [];
    leadSnap.forEach((docSnap) => {
      const raw = docSnap.data() || {};
      const pipelineType = isGeneralExecutionEligibleDeal(raw)
        ? GENERAL_QUOTE_TYPE
        : CURTAIN_QUOTE_TYPE;

      if (!allowedLeadTypes.includes(pipelineType)) {
        return;
      }

      const linkedQuotes = normalizeCrmLinkedQuotes({
        ...raw,
        linkedQuoteDate: normalizeTs(raw.linkedQuoteDate) || raw.linkedQuoteDate || null,
      });
      const rawChecklistItems = Array.isArray(raw.checklistItems)
        ? raw.checklistItems
        : Array.isArray(raw.checklist)
        ? raw.checklist
        : Array.isArray(raw.stageChecklist)
        ? raw.stageChecklist
        : [];

      dashboardLeads.push(stripLegacyCrmQuoteLinkFields({
        id: docSnap.id,
        ...raw,
        quoteType: raw.quoteType || pipelineType,
        pipelineType,
        contact: raw.contact || {},
        customerId: raw.customerId || "",
        customerName:
          raw.customerName ||
          raw.contact?.name ||
          raw.title ||
          "",
        customerPhone:
          raw.customerPhone ||
          raw.contact?.phone ||
          raw.contact?.whatsapp ||
          "",
        linkedQuotes,
        tags: Array.isArray(raw.tags) ? raw.tags : [],
        checklistItems: rawChecklistItems,
        hasQuote: raw.hasQuote === true || linkedQuotes.length > 0,
        nextActionDate: normalizeTs(raw.nextActionDate),
        installationDate: normalizeTs(raw.installationDate),
        reminderConfigs: normalizeCrmReminderConfigs(raw.reminderConfigs, {
          nextActionDate: normalizeTs(raw.nextActionDate),
          installationDate: normalizeTs(raw.installationDate),
        }),
        followUps: normalizeCrmFollowUps(raw.followUps, {
          nextAction: raw.nextAction,
          nextActionDate: normalizeTs(raw.nextActionDate),
          reminderConfig: raw?.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] || null,
        }),
        attachments: normalizeCrmAttachments(raw.attachments),
        executionSpecs: normalizeExecutionSpecs(raw.executionSpecs),
        assignedAt: normalizeTs(raw.assignedAt),
        createdAt: normalizeTs(raw.createdAt),
        updatedAt: normalizeTs(raw.updatedAt),
        needsQuoteLinkMigration: false,
      }));
    });

    setDebugRawCrmLeadCount(leadSnap.size);
    setCrmLeadRecords(dashboardLeads);
  } catch (error) {
    console.error("Dashboard preview load failed:", error);
    setDashboardAnalyticsState((prev) => ({
      ...prev,
      diagnostics: {
        ...(prev.diagnostics || {}),
        source: "analytics_indexes",
        status: "error",
        errorCode: error?.code || "",
        errorMessage: error?.message || String(error),
        loadedAt: new Date().toISOString(),
      },
    }));
    const message = String(error?.message || "");
    if (error?.code === "failed-precondition" && message.includes("index")) {
      console.error("Missing Firestore index for dashboard summary query", {
        expectedIndexes: [
          "client_monthly: monthKey ASC, confirmedRevenue DESC",
          "installation_index: monthKey ASC, installationDateKey ASC",
        ],
      });
      if (isDevRuntime) {
        notify("Dashboard summary index is missing. Deploy firestore.indexes.json.", "warning");
      }
    }
    if (isFirestoreResourceExhausted(error)) {
      raiseFirebaseHealthWarning("firestore", error, {
        path: `users/${uid}/dashboard-limited-data`,
        source: "loadDashboardPreviewData",
      });
      notify("Firebase read limit reached. Please wait and try again.", "warning");
    }
  }
}, [
  canAccessCrm,
  canAccessCurtainQuotes,
  canAccessGeneralQuotes,
  calendarMonth,
  customRange,
  getDashboardAnalyticsMonthKeys,
  getMonthKeyFromDateParts,
  getPrimaryDashboardMonthKey,
  getRecentMonthKeys,
  notify,
  periodType,
  raiseFirebaseHealthWarning,
]);

const subscribeCustomerLedger = useCallback((uid) => {
  if (!uid) return () => {};

  const normalizeTs = (value) => {
    if (!value) return null;
    if (value?.toDate) return value.toDate();
    if (value instanceof Date) return value;
    if (typeof value === "string" || typeof value === "number") {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  };

  const customersRef = collection(db, "users", uid, "customers");
  const customersQuery = firestoreQuery(
    customersRef,
    firestoreOrderBy("lastActivityAt", "desc"),
    firestoreLimit(CUSTOMER_LEDGER_INITIAL_READ_LIMIT)
  );

  const collectionPath = `users/${uid}/customers`;
  let cancelled = false;
  getDocsCached(customersQuery, {
    label: "loadCustomerLedgerRecentPage",
    cacheKey: `customerLedger:recent:${uid}:limit${CUSTOMER_LEDGER_INITIAL_READ_LIMIT}`,
    ttlMs: 5 * 60 * 1000,
  })
    .then(async (snap) => {
      if (cancelled) return;
      let sourceSnap = snap;
      if (sourceSnap.size === 0) {
        sourceSnap = await getDocsCached(
          firestoreQuery(
            customersRef,
            firestoreOrderBy(firestoreDocumentId()),
            firestoreLimit(CUSTOMER_LEDGER_INITIAL_READ_LIMIT)
          ),
          {
            label: "loadCustomerLedgerLegacyPage",
            cacheKey: `customerLedger:recent:${uid}:legacyDocId:limit${CUSTOMER_LEDGER_INITIAL_READ_LIMIT}`,
            ttlMs: 5 * 60 * 1000,
          }
        );
      }

      const nextCustomers = sourceSnap.docs
        .map((docSnap) =>
          normalizeCustomerLedgerRecord({
            id: docSnap.id,
            ...docSnap.data(),
          })
        )
        .map((customer) => ({
          ...customer,
          createdAt: normalizeTs(customer.createdAt),
          updatedAt: normalizeTs(customer.updatedAt),
          lastActivityAt: normalizeTs(customer.lastActivityAt),
        }))
        .sort(
          (a, b) =>
            safeTimeMillis(b.lastActivityAt || b.updatedAt || b.createdAt) -
            safeTimeMillis(a.lastActivityAt || a.updatedAt || a.createdAt)
        );

      setCustomerRecords(nextCustomers);
    })
    .catch((error) => {
      if (cancelled) return;
      console.error("Customer ledger load failed:", error);
      setCustomerRecords([]);
      notify(
        isFirestoreResourceExhausted(error)
          ? "Firebase read limit reached. Please wait and try again."
          : "Could not load customer list. You can still enter customer details manually.",
        "warning"
      );
    });

  return () => {
    cancelled = true;
    if (isDevRuntime) {
      console.log("ONE-TIME FIRESTORE LOAD CLEANUP", {
        label: "loadCustomerLedgerOnce",
        path: collectionPath,
        at: new Date().toISOString(),
      });
    }
  };
}, [notify]);

const loadCustomerLedgerQuotes = useCallback(
  async (customerId) => {
    const normalizedCustomerId = String(customerId || "").trim();
    if (!workspaceUid || !normalizedCustomerId) return [];

    const selectedCustomer =
      customerRecords.find((customer) => String(customer.id || "") === normalizedCustomerId) ||
      null;
    const cacheKey = `customerLedger:quotes:${workspaceUid}:${normalizedCustomerId}:limit${CUSTOMER_LEDGER_SELECTED_QUOTE_LIMIT}`;
    const loadState = customerLedgerQuotesLoadRef.current?.[cacheKey];

    if (loadState?.loaded && Array.isArray(loadState.records)) {
      setCustomerLedgerQuotesByCustomer((prev) => ({
        ...prev,
        [normalizedCustomerId]: loadState.records,
      }));
      return loadState.records;
    }

    if (loadState?.loading && loadState.promise) {
      setCustomerLedgerQuotesLoadingId(normalizedCustomerId);
      try {
        const records = await loadState.promise;
        setCustomerLedgerQuotesByCustomer((prev) => ({
          ...prev,
          [normalizedCustomerId]: records,
        }));
        return records;
      } finally {
        setCustomerLedgerQuotesLoadingId("");
      }
    }

    const normalizeTs = (v) => {
      if (!v) return null;
      if (v?.toDate) return v.toDate();
      if (v instanceof Date) return v;
      if (typeof v === "string" || typeof v === "number") {
        const parsed = new Date(v);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      }
      return null;
    };

    const allowedQuoteTypes = [
      canAccessCurtainQuotes ? CURTAIN_QUOTE_TYPE : null,
      canAccessGeneralQuotes ? GENERAL_QUOTE_TYPE : null,
    ].filter(Boolean);

    const runCustomerQuoteQuery = async () => {
      const quoteIndexRef = collection(db, "users", workspaceUid, "quote_index");
      const preferredQuery = firestoreQuery(
        quoteIndexRef,
        firestoreWhere("customerId", "==", normalizedCustomerId),
        firestoreOrderBy("updatedAt", "desc"),
        firestoreLimit(CUSTOMER_LEDGER_SELECTED_QUOTE_LIMIT)
      );

      let snap;
      try {
        snap = await getDocsCached(preferredQuery, {
          label: "customerLedgerSelectedCustomerQuotes",
          cacheKey,
          ttlMs: 5 * 60 * 1000,
        });
      } catch (error) {
        if (String(error?.code || "") !== "failed-precondition") {
          throw error;
        }
        snap = await getDocsCached(
          firestoreQuery(
            quoteIndexRef,
            firestoreWhere("customerId", "==", normalizedCustomerId),
            firestoreLimit(CUSTOMER_LEDGER_SELECTED_QUOTE_LIMIT)
          ),
          {
            label: "customerLedgerSelectedCustomerQuotesNoOrderFallback",
            cacheKey: `${cacheKey}:noOrderFallback`,
            ttlMs: 5 * 60 * 1000,
          }
        );
      }

      let records = snap.docs
        .map((docSnap) =>
          normalizeQuoteIndexRecordForList(docSnap.id, docSnap.data() || {}, normalizeTs)
        )
        .filter((quote) =>
          allowedQuoteTypes.includes(quote.quoteTypeResolved || resolveQuoteType(quote))
        );

      if (records.length === 0 && selectedCustomer?.customerKey) {
        const keySnap = await getDocsCached(
          firestoreQuery(
            quoteIndexRef,
            firestoreWhere("customerKey", "==", selectedCustomer.customerKey),
            firestoreLimit(CUSTOMER_LEDGER_SELECTED_QUOTE_LIMIT)
          ),
          {
            label: "customerLedgerSelectedCustomerQuotesByCustomerKey",
            cacheKey: `${cacheKey}:customerKey`,
            ttlMs: 5 * 60 * 1000,
          }
        );
        records = keySnap.docs
          .map((docSnap) =>
            normalizeQuoteIndexRecordForList(docSnap.id, docSnap.data() || {}, normalizeTs)
          )
          .filter((quote) =>
            allowedQuoteTypes.includes(quote.quoteTypeResolved || resolveQuoteType(quote))
          );
      }

      records.sort(
        (a, b) =>
          safeTimeMillis(b.updatedAt || b.createdAt) -
          safeTimeMillis(a.updatedAt || a.createdAt)
      );
      return records;
    };

    const promise = runCustomerQuoteQuery()
      .then((records) => {
        customerLedgerQuotesLoadRef.current[cacheKey] = {
          loading: false,
          loaded: true,
          promise: null,
          records,
        };
        return records;
      })
      .catch((error) => {
        delete customerLedgerQuotesLoadRef.current[cacheKey];
        throw error;
      });

    customerLedgerQuotesLoadRef.current[cacheKey] = {
      loading: true,
      loaded: false,
      promise,
      records: [],
    };

    setCustomerLedgerQuotesLoadingId(normalizedCustomerId);
    try {
      const records = await promise;
      setCustomerLedgerQuotesByCustomer((prev) => ({
        ...prev,
        [normalizedCustomerId]: records,
      }));
      return records;
    } catch (error) {
      console.error("Customer ledger selected quotes load failed:", {
        code: error?.code,
        message: error?.message,
        workspaceUid,
        customerId: normalizedCustomerId,
      });
      if (isFirestoreResourceExhausted(error)) {
        notify("Firebase read limit reached. Please wait and try again.", "warning");
      } else {
        notify("Could not load quotes for this customer.", "warning");
      }
      return [];
    } finally {
      setCustomerLedgerQuotesLoadingId("");
    }
  },
  [
    canAccessCurtainQuotes,
    canAccessGeneralQuotes,
    customerRecords,
    notify,
    workspaceUid,
  ]
);

const loadCustomerLedgerCrmDeals = useCallback(
  async (customerId) => {
    const normalizedCustomerId = String(customerId || "").trim();
    if (!workspaceUid || !normalizedCustomerId || !canAccessCrm) return [];

    const selectedCustomer =
      customerRecords.find((customer) => String(customer.id || "") === normalizedCustomerId) ||
      null;
    const cacheKey = `customerLedger:crmDeals:${workspaceUid}:${normalizedCustomerId}:limit${CUSTOMER_LEDGER_SELECTED_CRM_LIMIT}`;
    const loadState = customerLedgerCrmDealsLoadRef.current?.[cacheKey];

    if (loadState?.loaded && Array.isArray(loadState.records)) {
      setCustomerLedgerCrmDealsByCustomer((prev) => ({
        ...prev,
        [normalizedCustomerId]: loadState.records,
      }));
      return loadState.records;
    }

    if (loadState?.loading && loadState.promise) {
      setCustomerLedgerCrmDealsLoadingId(normalizedCustomerId);
      try {
        const records = await loadState.promise;
        setCustomerLedgerCrmDealsByCustomer((prev) => ({
          ...prev,
          [normalizedCustomerId]: records,
        }));
        return records;
      } finally {
        setCustomerLedgerCrmDealsLoadingId("");
      }
    }

    const normalizeTs = (v) => {
      if (!v) return null;
      if (v?.toDate) return v.toDate();
      if (v instanceof Date) return v;
      if (typeof v === "string" || typeof v === "number") {
        const parsed = new Date(v);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      }
      return null;
    };

    const allowedLeadTypes = [
      canAccessCurtainQuotes ? CURTAIN_QUOTE_TYPE : null,
      canAccessGeneralQuotes ? GENERAL_QUOTE_TYPE : null,
    ].filter(Boolean);

    const normalizeLeadDoc = (docSnap) => {
      const raw = docSnap.data() || {};
      const pipelineType = isGeneralExecutionEligibleDeal(raw)
        ? GENERAL_QUOTE_TYPE
        : CURTAIN_QUOTE_TYPE;
      const linkedQuotes = normalizeCrmLinkedQuotes({
        ...raw,
        linkedQuoteDate: normalizeTs(raw.linkedQuoteDate) || raw.linkedQuoteDate || null,
      });
      return normalizeCrmLeadSearchRecord({
        id: docSnap.id,
        ...raw,
        quoteType: raw.quoteType || pipelineType,
        pipelineType,
        contact: raw.contact || {},
        customerId: raw.customerId || "",
        customerName: raw.customerName || raw.contact?.name || raw.title || "",
        customerPhone:
          raw.customerPhone ||
          raw.contact?.phone ||
          raw.contact?.whatsapp ||
          "",
        linkedQuotes,
        tags: Array.isArray(raw.tags) ? raw.tags : [],
        hasQuote: raw.hasQuote === true || linkedQuotes.length > 0,
        nextActionDate: normalizeTs(raw.nextActionDate),
        installationDate: normalizeTs(raw.installationDate),
        reminderConfigs: normalizeCrmReminderConfigs(raw.reminderConfigs, {
          nextActionDate: normalizeTs(raw.nextActionDate),
          installationDate: normalizeTs(raw.installationDate),
        }),
        followUps: normalizeCrmFollowUps(raw.followUps, {
          nextAction: raw.nextAction,
          nextActionDate: normalizeTs(raw.nextActionDate),
          reminderConfig: raw?.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] || null,
        }),
        attachments: normalizeCrmAttachments(raw.attachments),
        executionSpecs: normalizeExecutionSpecs(raw.executionSpecs),
      });
    };

    const runCustomerLeadQuery = async () => {
      const leadsRef = collection(db, "users", workspaceUid, "crm_leads");
      const preferredQuery = firestoreQuery(
        leadsRef,
        firestoreWhere("customerId", "==", normalizedCustomerId),
        firestoreOrderBy("updatedAt", "desc"),
        firestoreLimit(CUSTOMER_LEDGER_SELECTED_CRM_LIMIT)
      );

      let snap;
      try {
        snap = await getDocsCached(preferredQuery, {
          label: "customerLedgerSelectedCustomerCrmDeals",
          cacheKey,
          ttlMs: 5 * 60 * 1000,
        });
      } catch (error) {
        if (String(error?.code || "") !== "failed-precondition") {
          throw error;
        }
        snap = await getDocsCached(
          firestoreQuery(
            leadsRef,
            firestoreWhere("customerId", "==", normalizedCustomerId),
            firestoreLimit(CUSTOMER_LEDGER_SELECTED_CRM_LIMIT)
          ),
          {
            label: "customerLedgerSelectedCustomerCrmDealsNoOrderFallback",
            cacheKey: `${cacheKey}:noOrderFallback`,
            ttlMs: 5 * 60 * 1000,
          }
        );
      }

      let records = snap.docs.map(normalizeLeadDoc).filter((lead) =>
        allowedLeadTypes.includes(lead.pipelineType || lead.quoteType)
      );

      if (records.length === 0 && selectedCustomer?.customerKey) {
        const keySnap = await getDocsCached(
          firestoreQuery(
            leadsRef,
            firestoreWhere("customerKey", "==", selectedCustomer.customerKey),
            firestoreLimit(CUSTOMER_LEDGER_SELECTED_CRM_LIMIT)
          ),
          {
            label: "customerLedgerSelectedCustomerCrmDealsByCustomerKey",
            cacheKey: `${cacheKey}:customerKey`,
            ttlMs: 5 * 60 * 1000,
          }
        );
        records = keySnap.docs.map(normalizeLeadDoc).filter((lead) =>
          allowedLeadTypes.includes(lead.pipelineType || lead.quoteType)
        );
      }

      records.sort(
        (a, b) =>
          safeTimeMillis(b.updatedAt || b.createdAt) -
          safeTimeMillis(a.updatedAt || a.createdAt)
      );
      return records;
    };

    const promise = runCustomerLeadQuery()
      .then((records) => {
        customerLedgerCrmDealsLoadRef.current[cacheKey] = {
          loading: false,
          loaded: true,
          promise: null,
          records,
        };
        return records;
      })
      .catch((error) => {
        delete customerLedgerCrmDealsLoadRef.current[cacheKey];
        throw error;
      });

    customerLedgerCrmDealsLoadRef.current[cacheKey] = {
      loading: true,
      loaded: false,
      promise,
      records: [],
    };

    setCustomerLedgerCrmDealsLoadingId(normalizedCustomerId);
    try {
      const records = await promise;
      setCustomerLedgerCrmDealsByCustomer((prev) => ({
        ...prev,
        [normalizedCustomerId]: records,
      }));
      mergeCrmLeadSearchRecords(records);
      return records;
    } catch (error) {
      console.error("Customer ledger selected CRM deals load failed:", {
        code: error?.code,
        message: error?.message,
        workspaceUid,
        customerId: normalizedCustomerId,
      });
      if (isFirestoreResourceExhausted(error)) {
        notify("Firebase read limit reached. Please wait and try again.", "warning");
      } else {
        notify("Could not load CRM deals for this customer.", "warning");
      }
      return [];
    } finally {
      setCustomerLedgerCrmDealsLoadingId("");
    }
  },
  [
    canAccessCrm,
    canAccessCurtainQuotes,
    canAccessGeneralQuotes,
    customerRecords,
    mergeCrmLeadSearchRecords,
    notify,
    normalizeCrmLeadSearchRecord,
    workspaceUid,
  ]
);

const subscribeCrmReminders = useCallback((uid, reason = "global") => {
  if (!uid) return () => {};
  if (isLocalStorageKillSwitchEnabled("FQ_DISABLE_REMINDER_CHECKS")) {
    if (isDevRuntime) {
      console.warn("Global reminder reads disabled by FQ_DISABLE_REMINDER_CHECKS", {
        workspaceUid: uid,
        reason,
      });
    }
    return () => {};
  }

  const normalizeTs = (v) => {
    if (!v) return null;
    if (v?.toDate) return v.toDate();
    if (typeof v === "string" || v instanceof Date) return new Date(v);
    return null;
  };

  const collectionPath = `users/${uid}/crm_reminders`;
  const now = Date.now();
  if (globalReminderCheckedWorkspaceRef.current !== uid) {
    globalReminderCheckedWorkspaceRef.current = uid;
    globalReminderLastCheckRef.current = 0;
    globalReminderShownIdsRef.current.clear();
  }
  if (
    reason !== "force" &&
    globalReminderLastCheckRef.current &&
    now - globalReminderLastCheckRef.current < GLOBAL_REMINDER_CHECK_TTL_MS
  ) {
    if (isDevRuntime) {
      console.log("GLOBAL REMINDER DUE CHECK SKIPPED", {
        reason,
        ageMs: now - globalReminderLastCheckRef.current,
      });
    }
    return () => {};
  }
  globalReminderLastCheckRef.current = now;

  const remindersRef = collection(db, "users", uid, "crm_reminders");
  const lookaheadAt = new Date(now + GLOBAL_REMINDER_LOOKAHEAD_MS);
  const activeRemindersQuery = firestoreQuery(
    remindersRef,
    firestoreWhere("active", "==", true),
    firestoreWhere("nextTriggerAt", "<=", Timestamp.fromDate(lookaheadAt)),
    firestoreOrderBy("nextTriggerAt", "asc"),
    firestoreLimit(GLOBAL_REMINDER_DUE_READ_LIMIT)
  );

  let cancelled = false;
  getDocsCached(activeRemindersQuery, {
    label: "globalReminderDueCheck.active",
    cacheKey: `${collectionPath}?active=true&nextTriggerAt_lte=${lookaheadAt.toISOString()}&limit=${GLOBAL_REMINDER_DUE_READ_LIMIT}`,
    ttlMs: 60 * 1000,
  })
    .then(async (activeSnap) => {
      if (cancelled) return;
      const reminderDocs = new Map();
      activeSnap.docs.forEach((docSnap) => reminderDocs.set(docSnap.id, docSnap));
      const nextReminders = [];

      reminderDocs.forEach((docSnap) => {
        const raw = docSnap.data() || {};
        nextReminders.push({
          id: docSnap.id,
          ...raw,
          active:
            raw.active === true ||
            (raw.status === CRM_REMINDER_STATUS_ACTIVE &&
              (raw.enabled === true || raw.reminderEnabled === true)),
          scheduledAt: normalizeTs(raw.scheduledAt),
          nextTriggerAt: normalizeTs(raw.nextTriggerAt),
          completedAt: normalizeTs(raw.completedAt),
          createdAt: normalizeTs(raw.createdAt),
          updatedAt: normalizeTs(raw.updatedAt),
        });
      });

      setCrmReminderRecords((prev) => {
        const dueIds = new Set(nextReminders.map((reminder) => reminder.id));
        const preserved = (prev || []).filter((reminder) => !dueIds.has(reminder.id));
        return [...nextReminders, ...preserved].filter((reminder) => {
          if (reminder.status === CRM_REMINDER_STATUS_DONE) return false;
          const nextMillis = safeTimeMillis(reminder.nextTriggerAt);
          return !nextMillis || nextMillis <= safeTimeMillis(lookaheadAt);
        });
      });
      setCrmReminderNow(Date.now());
    })
    .catch((error) => {
      if (cancelled) return;
      console.error("Global CRM reminders due check failed:", error);
      const message = String(error?.message || "");
      if (error?.code === "failed-precondition" && message.includes("index")) {
        console.error("Missing Firestore index for global reminders due check", {
          collection: "crm_reminders",
          fields: ["active ASC", "nextTriggerAt ASC"],
          path: collectionPath,
        });
        if (isDevRuntime) {
          notify("CRM reminder index is missing. Deploy firestore.indexes.json.", "warning");
        }
      }
      if (isFirestoreResourceExhausted(error)) {
        notify("Firebase read limit reached. Please wait and try again.", "warning");
      }
    });

  return () => {
    cancelled = true;
    if (isDevRuntime) {
      console.log("ONE-TIME FIRESTORE LOAD CLEANUP", {
        label: "loadCrmRemindersOnce",
        path: collectionPath,
        reason,
        at: new Date().toISOString(),
      });
    }
  };
}, [notify]);

const subscribeCrmExecutionTemplates = useCallback((uid) => {
  if (!uid) return () => {};

  const normalizeTs = (value) => {
    if (!value) return null;
    if (value?.toDate) return value.toDate();
    if (value instanceof Date) return value;
    if (typeof value === "string" || typeof value === "number") {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  };

  const templatesRef = collection(db, "users", uid, "execution_templates");

  const collectionPath = `users/${uid}/execution_templates`;
  let cancelled = false;
  executionTemplatesLoadingWorkspaceRef.current = uid;
  setExecutionTemplatesLoadState({
    workspaceUid: uid,
    loading: true,
    loaded: executionTemplatesLoadedWorkspaceRef.current === uid,
    error: "",
  });
  if (isDevRuntime) {
    console.log("EXECUTION TEMPLATE LOAD START", {
      workspaceUid: uid,
      cacheKey: collectionPath,
    });
  }
  getDocsCached(templatesRef, {
    label: "loadCrmExecutionTemplatesOnce",
    cacheKey: collectionPath,
    ttlMs: 5 * 60 * 1000,
  })
    .then((snap) => {
      if (cancelled) return;
      const nextTemplates = normalizeExecutionTemplateDrafts(
        snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
          createdAt: normalizeTs(docSnap.data()?.createdAt),
          updatedAt: normalizeTs(docSnap.data()?.updatedAt),
        }))
      ).sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));

      executionTemplatesLoadedWorkspaceRef.current = uid;
      executionTemplatesLoadingWorkspaceRef.current = "";
      setExecutionTemplatesLoadState({
        workspaceUid: uid,
        loading: false,
        loaded: true,
        error: "",
      });

      if (isDevRuntime) {
        console.log("EXECUTION TEMPLATE LOAD RESULT", {
          workspaceUid: uid,
          cacheKey: collectionPath,
          returnedCount: snap.size,
          normalizedCount: nextTemplates.length,
          localDirty: executionTemplatesDirtyRef.current,
        });
      }

      if (executionTemplatesDirtyRef.current) {
        if (isDevRuntime) {
          console.warn("EXECUTION TEMPLATE LOAD SKIPPED LOCAL OVERWRITE", {
            workspaceUid: uid,
            reason: "local template draft is dirty",
            returnedCount: nextTemplates.length,
          });
        }
        return;
      }

      setCrmExecutionTemplates(nextTemplates);
      executionTemplatePersistedSignatureRef.current =
        serializeExecutionTemplatesDraftData(nextTemplates);
    })
    .catch((error) => {
      if (cancelled) return;
      executionTemplatesLoadingWorkspaceRef.current = "";
      setExecutionTemplatesLoadState({
        workspaceUid: uid,
        loading: false,
        loaded: executionTemplatesLoadedWorkspaceRef.current === uid,
        error: error?.message || String(error),
      });
      console.error("CRM execution templates load failed:", error);
      if (isFirestoreResourceExhausted(error)) {
        notify("Firebase read limit reached. Please wait and try again.", "warning");
      }
    });

  return () => {
    cancelled = true;
    if (executionTemplatesLoadingWorkspaceRef.current === uid) {
      executionTemplatesLoadingWorkspaceRef.current = "";
    }
    if (isDevRuntime) {
      console.log("ONE-TIME FIRESTORE LOAD CLEANUP", {
        label: "loadCrmExecutionTemplatesOnce",
        path: collectionPath,
        at: new Date().toISOString(),
      });
    }
  };
}, [notify, serializeExecutionTemplatesDraftData]);

const subscribeCrmTaskHistory = useCallback((uid) => {
  if (!uid) return () => {};

  const normalizeTs = (value) => {
    if (!value) return null;
    if (value?.toDate) return value.toDate();
    if (value instanceof Date) return value;
    if (typeof value === "string" || typeof value === "number") {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  };

  const historyRef = collection(db, "users", uid, "task_history");
  const historyQuery = firestoreQuery(
    historyRef,
    firestoreOrderBy("completedAt", "desc"),
    firestoreLimit(CRM_TASK_HISTORY_INITIAL_READ_LIMIT)
  );

  const collectionPath = `users/${uid}/task_history`;
  let cancelled = false;
  getDocsCached(historyQuery, {
    label: "loadCrmTaskHistoryRecentPage",
    cacheKey: `${collectionPath}?orderBy=completedAt_desc&limit=${CRM_TASK_HISTORY_INITIAL_READ_LIMIT}`,
    ttlMs: 60 * 1000,
  })
    .then(async (snap) => {
      if (cancelled) return;
      let sourceSnap = snap;
      if (sourceSnap.size === 0) {
        sourceSnap = await getDocsCached(
          firestoreQuery(
            historyRef,
            firestoreOrderBy(firestoreDocumentId()),
            firestoreLimit(CRM_TASK_HISTORY_INITIAL_READ_LIMIT)
          ),
          {
            label: "loadCrmTaskHistoryLegacyPage",
            cacheKey: `${collectionPath}?legacyDocIdLimit=${CRM_TASK_HISTORY_INITIAL_READ_LIMIT}`,
            ttlMs: 60 * 1000,
          }
        );
      }
      const nextRecords = sourceSnap.docs
        .map((docSnap) => {
          const raw = docSnap.data() || {};
          return {
            id: docSnap.id,
            ...raw,
            dueDate: normalizeTs(raw.dueDate),
            completedAt: normalizeTs(raw.completedAt),
            createdAt: normalizeTs(raw.createdAt),
            updatedAt: normalizeTs(raw.updatedAt),
          };
        })
        .sort(
          (a, b) =>
            safeTimeMillis(b.completedAt || b.updatedAt || b.createdAt) -
            safeTimeMillis(a.completedAt || a.updatedAt || a.createdAt)
        );

      setCrmTaskHistoryRecords(nextRecords);
    })
    .catch((error) => {
      if (cancelled) return;
      console.error("CRM task history load failed:", error);
      if (isFirestoreResourceExhausted(error)) {
        notify("Firebase read limit reached. Please wait and try again.", "warning");
      }
      setCrmTaskHistoryRecords([]);
    });

  return () => {
    cancelled = true;
    if (isDevRuntime) {
      console.log("ONE-TIME FIRESTORE LOAD CLEANUP", {
        label: "loadCrmTaskHistoryOnce",
        path: collectionPath,
        at: new Date().toISOString(),
      });
    }
  };
}, [notify]);

useEffect(() => {
  if (!workspaceUid || tab !== "dashboard") return;
  if (isLocalStorageKillSwitchEnabled("FQ_DISABLE_DASHBOARD_PRELOADS")) {
    if (isDevRuntime) {
      console.warn("Dashboard preview effect skipped by FQ_DISABLE_DASHBOARD_PRELOADS");
    }
    return;
  }

  loadDashboardPreviewData(workspaceUid);
}, [loadDashboardPreviewData, tab, workspaceUid]);

useEffect(() => {
  if (!workspaceUid || !shouldSubscribeSavedQuotes) return undefined;

  const unsubscribe = subscribeSavedQuotes(workspaceUid);

  return () => unsubscribe();
}, [
  canAccessCurtainQuotes,
  canAccessGeneralQuotes,
  shouldSubscribeSavedQuotes,
  subscribeSavedQuotes,
  workspaceUid,
]);

useEffect(() => {
  if (!workspaceUid || tab !== "saved") return undefined;
  const queryText = savedQuotesSearchText.trim();
  if (queryText.length < 2) return undefined;

  const timerId = window.setTimeout(() => {
    searchSavedQuotesRemote(workspaceUid, queryText).catch((error) => {
      console.error("Saved quotes remote search failed:", {
        code: error?.code,
        message: error?.message,
        queryText,
        path: `users/${workspaceUid}/quote_index`,
      });
      if (isFirestoreResourceExhausted(error)) {
        notify("Firebase read limit reached. Please wait and try again.", "warning");
      } else if (String(error?.code || "").includes("permission-denied")) {
        setSavedQuotesAccessError({
          code: error?.code || "permission-denied",
          message:
            "Saved quotes search could not run because your account does not have permission for this workspace.",
          path: `users/${workspaceUid}/quote_index`,
        });
        notify("Saved quotes search is blocked by workspace permissions.", "error", 7000);
      }
    });
  }, 450);

  return () => window.clearTimeout(timerId);
}, [notify, savedQuotesSearchText, searchSavedQuotesRemote, tab, workspaceUid]);

useEffect(() => {
  if (!workspaceUid || !quoteLinkModalState) return undefined;
  const queryText = quoteLinkSearch.trim();
  if (queryText.length < 2) return undefined;

  const timerId = window.setTimeout(() => {
    searchSavedQuotesRemote(workspaceUid, queryText).catch((error) => {
      console.error("Quote link global search failed:", {
        code: error?.code,
        message: error?.message,
        queryText,
      });
    });
  }, GLOBAL_SEARCH_DEBOUNCE_MS);

  return () => window.clearTimeout(timerId);
}, [quoteLinkModalState, quoteLinkSearch, searchSavedQuotesRemote, workspaceUid]);

useEffect(() => {
  if (tab !== "crm") return undefined;
  if (!["overview", "list", "board"].includes(crmView)) return undefined;
  const queryText = deferredCrmSearch.trim();
  if (queryText.length < 2) return undefined;

  const timerId = window.setTimeout(() => {
    runCrmGlobalSearch(queryText, "crmDealSearch");
    searchSavedQuotesRemote(workspaceUid, queryText).catch((error) => {
      console.error("CRM quote-derived deal search failed:", {
        code: error?.code,
        message: error?.message,
        queryText,
      });
    });
  }, GLOBAL_SEARCH_DEBOUNCE_MS);

  return () => window.clearTimeout(timerId);
}, [crmView, deferredCrmSearch, runCrmGlobalSearch, searchSavedQuotesRemote, tab, workspaceUid]);

useEffect(() => {
  if (tab !== "crm" || crmView !== "leads") return undefined;
  const queryText = deferredCrmLeadSearch.trim();
  if (queryText.length < 2) return undefined;

  const timerId = window.setTimeout(() => {
    runCrmGlobalSearch(queryText, "crmLeadSearch");
  }, GLOBAL_SEARCH_DEBOUNCE_MS);

  return () => window.clearTimeout(timerId);
}, [crmView, deferredCrmLeadSearch, runCrmGlobalSearch, tab]);

useEffect(() => {
  if (tab !== "executionSpecs") return undefined;
  const queryText = executionWorkspaceSearch.trim();
  if (queryText.length < 2) return undefined;

  const timerId = window.setTimeout(() => {
    runCrmGlobalSearch(queryText, "executionWorkspaceSearch");
    searchSavedQuotesRemote(workspaceUid, queryText).catch((error) => {
      console.error("Execution workspace quote search failed:", {
        code: error?.code,
        message: error?.message,
        queryText,
      });
    });
  }, GLOBAL_SEARCH_DEBOUNCE_MS);

  return () => window.clearTimeout(timerId);
}, [executionWorkspaceSearch, runCrmGlobalSearch, searchSavedQuotesRemote, tab, workspaceUid]);

useEffect(() => {
  if (tab !== "todayTasks") return undefined;
  const queryText = deferredDailyTaskSearch.trim();
  if (queryText.length < 2) return undefined;

  const timerId = window.setTimeout(() => {
    runCrmGlobalSearch(queryText, "todayTaskSearch");
    searchSavedQuotesRemote(workspaceUid, queryText).catch((error) => {
      console.error("Today task quote search failed:", {
        code: error?.code,
        message: error?.message,
        queryText,
      });
    });
  }, GLOBAL_SEARCH_DEBOUNCE_MS);

  return () => window.clearTimeout(timerId);
}, [deferredDailyTaskSearch, runCrmGlobalSearch, searchSavedQuotesRemote, tab, workspaceUid]);

useEffect(() => {
  if (tab !== "customerLedger") return undefined;
  const queryText = deferredCustomerLedgerSearch.trim();
  if (queryText.length < 2) return undefined;

  const timerId = window.setTimeout(() => {
    runCustomerGlobalSearch(queryText, "customerLedgerSearch");
  }, GLOBAL_SEARCH_DEBOUNCE_MS);

  return () => window.clearTimeout(timerId);
}, [deferredCustomerLedgerSearch, runCustomerGlobalSearch, tab]);

useEffect(() => {
  if (tab !== "crm" || crmView !== "history") return undefined;
  const queryText = deferredCrmTaskHistorySearch.trim();
  if (queryText.length < 2) return undefined;

  const timerId = window.setTimeout(() => {
    runTaskHistoryGlobalSearch(queryText, "crmTaskHistorySearch");
  }, GLOBAL_SEARCH_DEBOUNCE_MS);

  return () => window.clearTimeout(timerId);
}, [crmView, deferredCrmTaskHistorySearch, runTaskHistoryGlobalSearch, tab]);

useEffect(() => {
  if (!workspaceUid) {
    setCrmLeadRecords([]);
    return undefined;
  }

  if (!shouldSubscribeCrmLeads) {
    return undefined;
  }

  const unsubscribe = subscribeCrmLeads(workspaceUid);

  return () => unsubscribe();
}, [
  canAccessCurtainQuotes,
  canAccessGeneralQuotes,
  shouldSubscribeCrmLeads,
  subscribeCrmLeads,
  workspaceUid,
]);

useEffect(() => {
  if (!workspaceUid || !canAccessCrm || !shouldSubscribeCustomerLedger) {
    setCustomerRecords([]);
    return undefined;
  }

  const unsubscribe = subscribeCustomerLedger(workspaceUid);
  return () => unsubscribe();
}, [
  canAccessCrm,
  shouldSubscribeCustomerLedger,
  subscribeCustomerLedger,
  workspaceUid,
]);

useEffect(() => {
  if (!workspaceUid || !canAccessCrm || crmLeadRecords.length === 0) return;

  crmLeadRecords.forEach((lead) => {
    if (!lead?.id || !lead.needsQuoteLinkMigration) return;

    const migrationKey = `${workspaceUid}:${lead.id}`;
    if (migratedCrmQuoteLinksRef.current.has(migrationKey)) return;
    migratedCrmQuoteLinksRef.current.add(migrationKey);

    const linkedQuotes = normalizeCrmLinkedQuotes(lead);
    updateDoc(doc(db, "users", workspaceUid, "crm_leads", lead.id), {
      linkedQuotes,
      hasQuote: linkedQuotes.length > 0,
      ...createLegacyCrmQuoteLinkDeletePatch(),
      ...(isDevRuntime
        ? {
            _lastWriteSource: "crmQuoteLinkMigration",
            _lastWriteAt: serverTimestamp(),
          }
        : {}),
      updatedAt: serverTimestamp(),
    })
      .then(() => {
        applyCrmLeadLocalPatch(lead.id, {
          linkedQuotes,
          hasQuote: linkedQuotes.length > 0,
          needsQuoteLinkMigration: false,
          updatedAt: new Date(),
        });
      })
      .catch((error) => {
        migratedCrmQuoteLinksRef.current.delete(migrationKey);
        console.error("Failed to migrate CRM quote links:", error);
      });
  });
}, [applyCrmLeadLocalPatch, canAccessCrm, crmLeadRecords, workspaceUid]);

useEffect(() => {
  if (!canAccessCrm || !workspaceUid || !shouldSubscribeCrmReminders) {
    setCrmReminderRecords([]);
    return undefined;
  }

  const unsubscribe = subscribeCrmReminders(workspaceUid);
  return () => unsubscribe();
}, [
  canAccessCrm,
  shouldSubscribeCrmReminders,
  subscribeCrmReminders,
  workspaceUid,
]);

useEffect(() => {
  if (!canAccessCrm || !workspaceUid) return undefined;

  const runReminderRefresh = () => {
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      return;
    }
    subscribeCrmReminders(workspaceUid, "focus");
  };

  window.addEventListener("focus", runReminderRefresh);
  document.addEventListener("visibilitychange", runReminderRefresh);

  return () => {
    window.removeEventListener("focus", runReminderRefresh);
    document.removeEventListener("visibilitychange", runReminderRefresh);
  };
}, [canAccessCrm, subscribeCrmReminders, workspaceUid]);

useEffect(() => {
  if (!canAccessCrm || !workspaceUid) {
    executionTemplatesLoadedWorkspaceRef.current = "";
    executionTemplatesLoadingWorkspaceRef.current = "";
    setExecutionTemplatesLoadState({
      workspaceUid: workspaceUid || "",
      loading: false,
      loaded: false,
      error: "",
    });
    setCrmExecutionTemplates([]);
    return undefined;
  }

  if (!shouldSubscribeCrmExecutionTemplates) {
    return undefined;
  }

  const unsubscribe = subscribeCrmExecutionTemplates(workspaceUid);
  return () => unsubscribe();
}, [
  canAccessCrm,
  shouldSubscribeCrmExecutionTemplates,
  subscribeCrmExecutionTemplates,
  workspaceUid,
]);

useEffect(() => {
  if (!canAccessCrm || !workspaceUid) {
    setCrmTaskHistoryRecords([]);
    return undefined;
  }

  if (tab !== "crm" || crmView !== "history") {
    return undefined;
  }

  const unsubscribe = subscribeCrmTaskHistory(workspaceUid);
  return () => unsubscribe();
}, [canAccessCrm, crmView, subscribeCrmTaskHistory, tab, workspaceUid]);

useEffect(() => {
  const intervalId = window.setInterval(() => {
    setCrmReminderNow(Date.now());
  }, 30000);

  return () => window.clearInterval(intervalId);
}, []);

/* --------------------------- LOAD FULL QUOTE --------------------------- */
const loadFullQuote = async (quoteInput) => {
  const {
    quoteIndex,
    indexId,
    sourceQuoteId,
    quoteId,
    displayId,
    candidatePaths,
  } = getSavedQuoteOpenCandidates(quoteInput, workspaceUid);
  const quoteNo = quoteIndex.quoteNo || quoteIndex.quoteNoString || "";
  const quoteType = quoteIndex.quoteType || quoteIndex.quoteTypeResolved || "";
  const attemptedPaths = [];
  let fullQuoteReads = 0;
  let fallbackAttempts = 0;
  let lastError = null;
  let schemaOpenError = null;
  let foundAnySourceDoc = false;

  const logOpenTrace = (result, extra = {}) => {
    if (!isDevRuntime) return;
    console.log("SAVED_QUOTE_OPEN_TRACE", {
      authUid: auth.currentUser?.uid || user?.uid || "",
      workspaceUid,
      indexId,
      quoteNo,
      quoteType,
      sourcePath: quoteIndex.sourcePath || "",
      sourceQuoteId,
      quoteId,
      attemptedPaths,
      result,
      ...extra,
    });
    console.log("SAVED_QUOTES_READ_BUDGET_TRACE", {
      action: "openSavedQuote",
      quoteIndexReads: 0,
      fullQuoteReads,
      fallbackFunctionCalled: false,
      attemptedPaths,
    });
    console.log("SAVED_QUOTE_LOW_READ_TRACE", {
      action: "openSavedQuote",
      quoteIndexReads: 0,
      fullQuoteReads,
      fallbackAttempts,
      callableUsed: false,
      broadScanUsed: false,
      attemptedPaths,
    });
  };
  const logRealOpenError = (error, failedPath = "", extra = {}) => {
    if (!isDevRuntime) return;
    console.log("SAVED_QUOTE_OPEN_REAL_ERROR", {
      quoteNo,
      quoteType,
      indexId,
      workspaceUid,
      sourcePath: quoteIndex.sourcePath || "",
      sourceQuoteId,
      attemptedPaths,
      errorCode: error?.code,
      errorName: error?.name,
      errorMessage: error?.message,
      errorStack: error?.stack,
      failedPath,
      ...extra,
    });
  };
  const logIndexRecordTrace = () => {
    if (!isDevRuntime) return;
    const indexRecord = quoteIndex.indexRecordRaw || quoteIndex || {};
    console.log("SAVED_QUOTE_INDEX_RECORD_TRACE", {
      indexId,
      quoteNo: indexRecord.quoteNo || quoteNo,
      quoteType: indexRecord.quoteType || quoteType,
      customerName: indexRecord.customerName || quoteIndex.customerName || "",
      sourcePath: indexRecord.sourcePath || quoteIndex.sourcePath || "",
      sourceQuoteId: indexRecord.sourceQuoteId || sourceQuoteId,
      quoteId: indexRecord.quoteId || quoteId,
      quotationId: indexRecord.quotationId || quoteIndex.quotationId || "",
      id: indexRecord.id || quoteIndex.id || "",
      collection: indexRecord.sourceCollection || quoteIndex.sourceCollection || "quotations",
      createdAt: indexRecord.createdAt || quoteIndex.createdAt || null,
      updatedAt: indexRecord.updatedAt || quoteIndex.updatedAt || null,
      rawKeys: Object.keys(indexRecord || {}),
    });
  };

  if (!workspaceUid || candidatePaths.length === 0) {
    const errorState = {
      quoteIndex,
      indexId,
      quoteNo,
      quoteType,
      sourcePath: quoteIndex.sourcePath || "",
      sourceQuoteId,
      quoteId,
      attemptedPaths,
      result: "not-found",
      message:
        "This quote index exists, but it does not contain a usable full quote reference.",
    };
    setSavedQuoteOpenError(errorState);
    logOpenTrace("not-found", { reason: "no-candidate-paths" });
    notify("This saved quote cannot be opened because its source link is missing.", "warning", 7000);
    return null;
  }

  setIsLoadingQuote(true);
  setSavedQuoteOpenError(null);
  logIndexRecordTrace();

  try {
    for (const [candidateIndex, candidatePath] of candidatePaths.entries()) {
      attemptedPaths.push(candidatePath);
      fallbackAttempts = Math.max(0, candidateIndex);
      const ref = doc(db, ...candidatePath.split("/"));
      let foundExistingDoc = false;
      try {
        if (isDevRuntime) {
          console.log("SAVED_QUOTE_OPEN_ATTEMPT", {
            quoteNo,
            quoteType,
            attemptedPath: candidatePath,
            attemptNumber: candidateIndex + 1,
          });
        }
        fullQuoteReads += 1;
        const snap = await getDoc(ref, {
          label: "fullQuoteLoadBySourcePointer",
          cacheKey: candidatePath,
          reason: "user_open_saved_quote",
        });
        if (isDevRuntime) {
          console.log("SAVED_QUOTE_OPEN_ATTEMPT_RESULT", {
            quoteNo,
            attemptedPath: candidatePath,
            exists: snap.exists(),
            docId: snap.id,
            dataKeys: snap.exists() ? Object.keys(snap.data() || {}) : [],
          });
        }

        if (!snap.exists()) {
          logRealOpenError(
            {
              code: "not-found",
              name: "DocumentMissing",
              message: "Document does not exist.",
            },
            candidatePath
          );
          continue;
        }
        foundExistingDoc = true;
        foundAnySourceDoc = true;

        let fullQuote = normalizeSavedQuoteForEditor(
          {
            id: snap.id,
            ...snap.data(),
          },
          quoteIndex
        );

        const resolvedEditor = resolveQuoteType(fullQuote) === GENERAL_QUOTE_TYPE ? "general" : "curtain";
        if (isDevRuntime) {
          console.log("SAVED_QUOTE_ROUTE_TRACE", {
            quoteNo,
            quoteType,
            resolvedEditor,
            reason:
              resolvedEditor === "general"
                ? "quoteType/general schema resolved"
                : "curtain schema resolved",
          });
        }

        if (resolvedEditor === "general") {
          try {
            const embeddedGeneralQuote = getEmbeddedGeneralQuote(fullQuote);
            const { cleanedQuote, changed } = await cleanupExpiredGeneralQuoteImages(
              embeddedGeneralQuote
            );

            fullQuote = {
              ...fullQuote,
              generalQuote: cleanedQuote,
            };

            if (changed) {
              try {
                await updateDoc(ref, {
                  generalQuote: cleanedQuote,
                  updatedAt: serverTimestamp(),
                });
                applyQuoteLocalPatch(snap.id, {
                  generalQuote: cleanedQuote,
                  updatedAt: new Date(),
                });
              } catch (cleanupWriteError) {
                if (isDevRuntime) {
                  console.warn("GENERAL_QUOTE_OPEN_CLEANUP_WRITE_FAILED", {
                    quoteNo,
                    path: candidatePath,
                    code: cleanupWriteError?.code,
                    message: cleanupWriteError?.message,
                  });
                }
              }
            }
          } catch (cleanupError) {
            if (isDevRuntime) {
              console.warn("GENERAL_QUOTE_OPEN_CLEANUP_SKIPPED", {
                quoteNo,
                path: candidatePath,
                code: cleanupError?.code,
                message: cleanupError?.message,
                stack: cleanupError?.stack,
              });
            }
          }
        }

        const quoteIndexDocId = indexId || displayId || quoteId || snap.id;
        if (
          workspaceUid &&
          quoteIndexDocId &&
          (quoteIndex.sourcePath !== candidatePath || quoteIndex.sourceQuoteId !== snap.id)
        ) {
          try {
            await setDoc(
              doc(db, "users", workspaceUid, "quote_index", quoteIndexDocId),
              sanitizeCrmFirestoreValue({
                ...buildQuoteIndexPayload(fullQuote, snap.id, workspaceUid),
                quoteIndexId: quoteIndexDocId,
                indexId: quoteIndexDocId,
                sourceResolvedAt: serverTimestamp(),
                staleSource: false,
                sourceMissingAt: null,
              }),
              { merge: true }
            );
            invalidateWorkspaceCollectionCache(workspaceUid, "quote_index");
          } catch (indexRepairError) {
            if (isDevRuntime) {
              console.warn("SAVED_QUOTE_INDEX_SELF_HEAL_FAILED", {
                code: indexRepairError?.code,
                message: indexRepairError?.message,
                path: `users/${workspaceUid}/quote_index/${quoteIndexDocId}`,
                loadedPath: candidatePath,
                quoteNo,
              });
            }
          }
        }

        try {
          loadQuoteForEdit(fullQuote);
        } catch (editorError) {
          schemaOpenError = editorError;
          lastError = editorError;
          logRealOpenError(editorError, candidatePath, {
            classificationHint: "schema-error",
          });
          break;
        }
        logOpenTrace("loaded", {
          loadedPath: candidatePath,
          loadedQuoteId: snap.id,
        });
        return fullQuote;
      } catch (error) {
        lastError = error;
        if (foundExistingDoc && !error?.code) {
          schemaOpenError = error;
        }
        logRealOpenError(error, candidatePath, {
          classificationHint: foundExistingDoc ? "schema-error" : "read-error",
        });
        if (isDevRuntime) {
          console.error("SAVED_QUOTE_OPEN_ATTEMPT_ERROR", {
            quoteNo,
            attemptedPath: candidatePath,
            errorCode: error?.code,
            errorName: error?.name,
            errorMessage: error?.message,
            errorStack: error?.stack,
          });
          console.error("SAVED_QUOTE_OPEN_CANDIDATE_FAILED", {
            code: error?.code,
            message: error?.message,
            path: candidatePath,
            workspaceUid,
            indexId,
            quoteNo,
          });
        }
      }
    }

    const classification = classifySavedQuoteOpenFailure({
      readError: lastError,
      exists: foundAnySourceDoc,
      normalizeError: schemaOpenError,
      attemptedPaths,
    });
    const errorState = {
      quoteIndex,
      indexId,
      quoteNo,
      quoteType,
      sourcePath: quoteIndex.sourcePath || "",
      sourceQuoteId,
      quoteId,
      attemptedPaths,
      result: classification.result,
      code: lastError?.code || "",
      message: classification.message,
    };
    setSavedQuoteOpenError(errorState);
    logOpenTrace(errorState.result, {
      code: lastError?.code || "",
      message: lastError?.message || "",
    });
    notify(
      classification.result === "resource-exhausted"
        ? "Firebase read limit reached. Please wait and try again."
        : errorState.message,
      classification.tone,
      8000
    );
    return null;
  } catch (err) {
    lastError = err;
    const classification = classifySavedQuoteOpenFailure({
      readError: err,
      exists: foundAnySourceDoc,
      normalizeError: !err?.code ? err : null,
      attemptedPaths,
    });
    logRealOpenError(err, attemptedPaths[attemptedPaths.length - 1] || "", {
      classificationHint: classification.result,
    });
    const errorState = {
      quoteIndex,
      indexId,
      quoteNo,
      quoteType,
      sourcePath: quoteIndex.sourcePath || "",
      sourceQuoteId,
      quoteId,
      attemptedPaths,
      result: classification.result,
      code: err?.code || "",
      message: classification.message || err?.message || "Could not open this saved quotation.",
    };
    setSavedQuoteOpenError(errorState);
    logOpenTrace(classification.result, {
      code: err?.code || "",
      message: err?.message || "",
    });
    console.error("Failed to load full quote", {
      code: err?.code,
      message: err?.message,
      stack: err?.stack,
      workspaceUid,
      indexId,
      quoteNo,
      attemptedPaths,
    });
    notify(errorState.message, classification.tone || "error");
    return null;
  } finally {
    setIsLoadingQuote(false);
  }
};

const repairSavedQuoteLink = async (quoteInput = savedQuoteOpenError?.quoteIndex) => {
  if (!workspaceUid || !quoteInput || repairingSavedQuoteLinkId) return;

  const {
    quoteIndex,
    indexId,
    sourceQuoteId,
    quoteId,
    displayId,
    candidatePaths,
  } = getSavedQuoteOpenCandidates(quoteInput, workspaceUid);
  const quoteNo = quoteIndex.quoteNo || quoteIndex.quoteNoString || "";
  const quoteType = quoteIndex.quoteType || quoteIndex.quoteTypeResolved || "";
  const repairKey = indexId || displayId || quoteId || sourceQuoteId || quoteNo;
  if (!repairKey) return;

  setRepairingSavedQuoteLinkId(repairKey);

  try {
    if (isDevRuntime) {
      console.log("SAVED_QUOTE_OPEN_TRACE", {
        authUid: auth.currentUser?.uid || user?.uid || "",
        workspaceUid,
        indexId,
        quoteNo,
        quoteType,
        sourcePath: quoteIndex.sourcePath || "",
        sourceQuoteId,
        quoteId,
        attemptedPaths: candidatePaths,
        result: "repair-start",
      });
      console.log("SAVED_QUOTES_READ_BUDGET_TRACE", {
        action: "repairSavedQuoteLink",
        quoteIndexReads: 0,
        fullQuoteReads: 0,
        fallbackFunctionCalled: true,
        attemptedPaths: candidatePaths,
      });
      console.log("SAVED_QUOTE_LOW_READ_TRACE", {
        action: "repairSavedQuoteLink",
        quoteIndexReads: 0,
        fullQuoteReads: 0,
        fallbackAttempts: candidatePaths.length,
        callableUsed: true,
        broadScanUsed: false,
        attemptedPaths: candidatePaths,
      });
    }

    const resolveSavedQuoteCallable = httpsCallable(functions, "resolveSavedQuoteLink");
    const result = await resolveSavedQuoteCallable({
      workspaceUid,
      indexId: indexId || displayId || quoteId,
      quoteNo,
      sourceQuoteId,
      sourcePath: quoteIndex.sourcePath || "",
      quoteType,
    });
    const data = result?.data || {};

    if (isDevRuntime) {
      console.log("SAVED_QUOTE_OPEN_TRACE", {
        authUid: auth.currentUser?.uid || user?.uid || "",
        workspaceUid,
        indexId,
        quoteNo,
        quoteType,
        sourcePath: quoteIndex.sourcePath || "",
        sourceQuoteId,
        quoteId,
        attemptedPaths: candidatePaths,
        result: data.status || (data.ok ? "loaded" : "not-found"),
        resolvedPath: data.sourcePath || "",
        candidateCount: Array.isArray(data.candidates) ? data.candidates.length : 0,
      });
    }

    if (data.ok && data.quote) {
      const fullQuote = normalizeSavedQuoteForEditor(data.quote, {
        ...quoteIndex,
        sourceQuoteId: data.sourceQuoteId || data.quote.id,
        sourcePath: data.sourcePath || "",
      });
      const quoteIndexDocId = data.indexId || indexId || fullQuote.id;
      const localIndexRecord = {
        ...buildQuoteIndexPayload(fullQuote, fullQuote.id, workspaceUid),
        quoteIndexId: quoteIndexDocId,
        indexId: quoteIndexDocId,
        sourceResolvedAt: new Date(),
        staleSource: false,
        sourceMissingAt: null,
      };

      invalidateWorkspaceCollectionCache(workspaceUid, "quote_index");
      setSavedQuotes((prev) => {
        const replaceKeys = new Set(
          [quoteIndexDocId, indexId, displayId, quoteId, sourceQuoteId, fullQuote.id].filter(Boolean)
        );
        let replaced = false;
        const next = (prev || []).map((item) => {
          const itemKeys = [
            item.id,
            item.quoteId,
            item.sourceQuoteId,
            item.indexId,
            item.quoteIndexId,
          ].filter(Boolean);
          if (!itemKeys.some((key) => replaceKeys.has(String(key)))) return item;
          replaced = true;
          return {
            ...item,
            ...localIndexRecord,
          };
        });
        return replaced ? next : [localIndexRecord, ...next];
      });
      setDeletedQuotes((prev) =>
        (prev || []).map((item) => {
          const itemKeys = [
            item.id,
            item.quoteId,
            item.sourceQuoteId,
            item.indexId,
            item.quoteIndexId,
          ].filter(Boolean);
          return itemKeys.some((key) =>
            [quoteIndexDocId, indexId, displayId, quoteId, sourceQuoteId, fullQuote.id]
              .filter(Boolean)
              .includes(String(key))
          )
            ? {
                ...item,
                ...localIndexRecord,
              }
            : item;
        })
      );
      setSavedQuoteOpenError(null);
      notify("Quote link repaired and loaded.", "success");
      loadQuoteForEdit(fullQuote);
      return;
    }

    const message =
      data.status === "multiple-matches"
        ? "Multiple quotations match this old index. Open the exact quote from a repaired index result."
        : "This saved quote search result is stale. The source quotation document was not found.";
    setSavedQuoteOpenError({
      quoteIndex,
      indexId,
      quoteNo,
      quoteType,
      sourcePath: quoteIndex.sourcePath || "",
      sourceQuoteId,
      quoteId,
      attemptedPaths: candidatePaths,
      result: data.status || "not-found",
      message,
      candidates: Array.isArray(data.candidates) ? data.candidates : [],
    });
    notify(message, "warning", 8000);
  } catch (error) {
    console.error("Saved quote link repair failed", {
      code: error?.code,
      message: error?.message,
      stack: error?.stack,
      workspaceUid,
      indexId,
      quoteNo,
    });
    setSavedQuoteOpenError((prev) => ({
      ...(prev || {}),
      quoteIndex,
      indexId,
      quoteNo,
      quoteType,
      sourcePath: quoteIndex.sourcePath || "",
      sourceQuoteId,
      quoteId,
      attemptedPaths: candidatePaths,
      result: String(error?.code || "").includes("permission-denied")
        ? "permission-denied"
        : "resolve-error",
      code: error?.code || "",
      message: error?.message || "Could not repair this saved quote link.",
    }));
    notify("Could not repair this saved quote link.", "error", 7000);
  } finally {
    setRepairingSavedQuoteLinkId("");
  }
};

  /* --------------------------- STYLES --------------------------- */

  const pageBg = isDark ? "#020617" : "#e5e7eb";
  const textColor = isDark ? "#e5e7eb" : "#0f172a";

  const btn = (bg, fg = "#fff") => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "flex-start",
    justifySelf: "start",
    flex: "0 0 auto",
    width: "auto",
    maxWidth: "100%",
    padding: "10px 16px",
    borderRadius: 8,
    border: "none",
    cursor: "pointer",
    fontWeight: 700,
    marginRight: 8,
    color: fg,
    background: bg,
    fontSize: 14,
    whiteSpace: "nowrap",
  });

  const input = {
    padding: 6,
    borderRadius: 6,
    border: "1px solid #cbd5e1",
    background: isDark ? "#020617" : "#ffffff",
    color: textColor,
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    fontSize: 13,
  };

  const selectInput = {
    ...input,
    appearance: "auto",
    WebkitAppearance: "menulist",
    MozAppearance: "menulist",
    WebkitTextFillColor: textColor,
    color: textColor,
    colorScheme: isDark ? "dark" : "light",
  };

  const renderCustomerLedgerCombobox = ({
    context = CURTAIN_QUOTE_TYPE,
    label = "Customer Name",
    value = "",
    selectedCustomerId = "",
    onTextChange,
    seed = {},
  } = {}) => {
    const query = normalizeCustomerSearchText(value);
    const searchState = quotationCustomerSearchState[context] || {};
    const searchResults = Array.isArray(searchState.results)
      ? searchState.results
      : [];
    const availableCustomers = mergeCustomersById([
      ...searchResults,
      ...customerRecords,
    ]);
    const selectedCustomer =
      availableCustomers.find((customer) => customer.id === selectedCustomerId) ||
      null;
    const shouldSearch = query.length >= 2;
    const matches = availableCustomers
      .filter((customer) => {
        if (!query) return selectedCustomerId && customer.id === selectedCustomerId;
        return buildCustomerLedgerSearchText(customer).includes(query);
      })
      .sort((a, b) => {
        if (selectedCustomerId && a.id === selectedCustomerId) return -1;
        if (selectedCustomerId && b.id === selectedCustomerId) return 1;
        return String(a.customerName || "").localeCompare(
          String(b.customerName || "")
        );
      })
      .slice(0, CUSTOMER_SEARCH_RESULT_LIMIT);
    const exactNameExists = availableCustomers.some(
      (customer) =>
        normalizeCustomerLedgerName(customer.customerName) ===
        normalizeCustomerLedgerName(value)
    );
    const isOpen = customerPickerOpenKey === context;
    const createSeed = {
      ...seed,
      customerName: value || seed.customerName || "",
    };

    return (
      <div style={{ position: "relative", minWidth: 0 }}>
        <label style={{ fontSize: 13, opacity: 0.72, fontWeight: 700 }}>
          {label}
        </label>
        <div
          style={{
            position: "relative",
            marginTop: 6,
          }}
        >
          <input
            style={{
              ...input,
              paddingRight: selectedCustomer ? 112 : 14,
              borderRadius: 14,
              paddingTop: 11,
              paddingBottom: 11,
              border: selectedCustomer
                ? "1px solid rgba(37,99,235,0.45)"
                : input.border,
              boxShadow: selectedCustomer
                ? "0 0 0 3px rgba(37,99,235,0.08)"
                : "none",
            }}
            value={value ?? ""}
            onFocus={() => {
              setCustomerPickerOpenKey(context);
              if (normalizeCustomerSearchText(value).length >= 2) {
                setQuotationCustomerSearchQueries((prev) => ({
                  ...prev,
                  [context]: value,
                }));
              }
            }}
            onChange={(event) => {
              const nextValue = event.target.value;
              onTextChange?.(nextValue);
              setQuotationCustomerSearchQueries((prev) => ({
                ...prev,
                [context]: nextValue,
              }));
              setCustomerPickerOpenKey(context);
            }}
            onBlur={() => {
              window.setTimeout(() => {
                setCustomerPickerOpenKey((current) =>
                  current === context ? "" : current
                );
              }, 140);
            }}
            placeholder="Search or type customer name..."
            autoComplete="off"
          />
          {selectedCustomer ? (
            <button
              type="button"
              onClick={() => openCustomerLedger(selectedCustomer.id)}
              style={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                border: "none",
                borderRadius: 999,
                padding: "5px 9px",
                background: isDark ? "rgba(37,99,235,0.22)" : "#dbeafe",
                color: "#2563eb",
                fontSize: 11,
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              Linked
            </button>
          ) : null}
        </div>
        {isOpen ? (
          <div
            style={{
              position: "absolute",
              zIndex: 40,
              left: 0,
              right: 0,
              top: "calc(100% + 8px)",
              padding: 8,
              borderRadius: 18,
              background: isDark ? "#0f172a" : "#ffffff",
              border: isDark
                ? "1px solid rgba(148,163,184,0.22)"
                : "1px solid rgba(226,232,240,0.95)",
              boxShadow: isDark
                ? "0 24px 54px rgba(2,6,23,0.48)"
                : "0 24px 54px rgba(15,23,42,0.16)",
              display: "grid",
              gap: 8,
              maxHeight: 360,
              overflowY: "auto",
            }}
          >
            {searchState.loading ? (
              <div
                style={{
                  padding: 12,
                  borderRadius: 14,
                  background: isDark ? "rgba(255,255,255,0.04)" : "#f8fafc",
                  color: dashboardMuted,
                  fontSize: 13,
                  fontWeight: 800,
                }}
              >
                Searching customers, leads, and saved quotes...
              </div>
            ) : null}
            {searchState.error ? (
              <div
                style={{
                  padding: 12,
                  borderRadius: 14,
                  background: isDark ? "rgba(239,68,68,0.14)" : "#fef2f2",
                  color: "#dc2626",
                  fontSize: 13,
                  fontWeight: 800,
                }}
              >
                {searchState.error}
              </div>
            ) : null}
            {!searchState.loading && matches.length > 0 ? (
              matches.map((customer) => (
                <button
                  key={customer.id}
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => applyCustomerToQuoteContext(context, customer)}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    border: "none",
                    borderRadius: 14,
                    padding: 12,
                    background:
                      customer.id === selectedCustomerId
                        ? isDark
                          ? "rgba(37,99,235,0.22)"
                          : "#eff6ff"
                        : isDark
                        ? "rgba(255,255,255,0.04)"
                        : "#f8fafc",
                    color: textColor,
                    cursor: "pointer",
                    display: "grid",
                    gap: 5,
                  }}
                >
                  {(() => {
                    const sourceLabels = Array.isArray(customer.sourceLabels)
                      ? customer.sourceLabels
                      : getCustomerSearchSourceLabels(customer);
                    const sourceText =
                      sourceLabels.length > 0 ? sourceLabels.join(" + ") : "Customer";
                    const latestQuoteText =
                      customer.latestQuoteNo || customer.lastQuoteNo
                        ? `Last quote #${customer.latestQuoteNo || customer.lastQuoteNo}`
                        : "";
                    const stageText = customer.stageLabel || customer.lastDealStage || "";
                    return (
                      <>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 10,
                      alignItems: "flex-start",
                      minWidth: 0,
                    }}
                  >
                    <strong style={{ fontSize: 14, overflowWrap: "anywhere" }}>
                      {customer.customerName || "Unnamed Customer"}
                    </strong>
                    <span
                      style={{
                        flex: "0 0 auto",
                        borderRadius: 999,
                        padding: "3px 8px",
                        background: isDark ? "rgba(148,163,184,0.12)" : "#e2e8f0",
                        color: isDark ? "#cbd5e1" : "#475569",
                        fontSize: 11,
                        fontWeight: 800,
                      }}
                    >
                      {sourceText}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.5 }}>
                    {[customer.phone || customer.whatsapp, customer.companyName, customer.address]
                      .filter(Boolean)
                      .join(" | ") || "No contact details yet"}
                  </div>
                  {latestQuoteText || stageText ? (
                    <div style={{ fontSize: 11, color: dashboardMuted }}>
                      {[latestQuoteText, stageText].filter(Boolean).join(" | ")}
                    </div>
                  ) : null}
                  {customer.lastActivityAt ? (
                    <div style={{ fontSize: 11, color: dashboardMuted }}>
                      Last activity:{" "}
                      {new Date(customer.lastActivityAt).toLocaleDateString("en-IN")}
                    </div>
                  ) : null}
                      </>
                    );
                  })()}
                </button>
              ))
            ) : !searchState.loading ? (
              <div
                style={{
                  padding: 12,
                  borderRadius: 14,
                  background: isDark ? "rgba(255,255,255,0.04)" : "#f8fafc",
                  color: dashboardMuted,
                  fontSize: 13,
                }}
              >
                {shouldSearch
                  ? "No existing customer found"
                  : "Type at least 2 characters to search customers, leads, and saved quote customers"}
              </div>
            ) : null}
            {shouldSearch && !searchState.loading && matches.length === 0 ? (
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() =>
                  runQuotationCustomerSearch(context, value, {
                    includeLegacyScan: true,
                  })
                }
                style={{
                  border: "none",
                  borderRadius: 14,
                  padding: 12,
                  background: isDark ? "rgba(148,163,184,0.12)" : "#f1f5f9",
                  color: isDark ? "#cbd5e1" : "#475569",
                  fontWeight: 900,
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                Search older customers
              </button>
            ) : null}
            {String(value || "").trim() && !exactNameExists ? (
              <div style={{ display: "grid", gap: 8 }}>
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => openCreateCustomerFromQuote(context, createSeed)}
                  style={{
                    border: "none",
                    borderRadius: 14,
                    padding: 12,
                    background: isDark ? "rgba(37,99,235,0.18)" : "#dbeafe",
                    color: "#2563eb",
                    fontWeight: 900,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  + Create new customer "{String(value || "").trim()}"
                </button>
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => openCreateLeadFromQuoteCustomer(context, createSeed)}
                  style={{
                    border: "none",
                    borderRadius: 14,
                    padding: 12,
                    background: isDark ? "rgba(20,184,166,0.16)" : "#ccfbf1",
                    color: isDark ? "#5eead4" : "#0f766e",
                    fontWeight: 900,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  + Create lead from "{String(value || "").trim()}"
                </button>
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    onTextChange?.(String(value || "").trim());
                    setCustomerPickerOpenKey("");
                  }}
                  style={{
                    border: "none",
                    borderRadius: 14,
                    padding: 12,
                    background: isDark ? "rgba(148,163,184,0.1)" : "#f8fafc",
                    color: dashboardMuted,
                    fontWeight: 900,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  Use typed name without linking
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
        {selectedCustomer ? (
          <div style={{ marginTop: 7, fontSize: 12, color: dashboardMuted }}>
            Linked to Customer Ledger. This quote will save against the selected customer.
          </div>
        ) : (
          <div style={{ marginTop: 7, fontSize: 12, color: dashboardMuted }}>
            Type freely or pick from customers, leads, and saved quote customers.
          </div>
        )}
      </div>
    );
  };

  const getCurtainQuoteCustomerIdentity = (stateOverride = null) => {
    const sourceCustomerName = stateOverride?.customerName ?? customerName;
    const sourceQuoteCustomerLink =
      stateOverride?.quoteCustomerLink || quoteCustomerLink || {};
    const sourceSelectedQuote = stateOverride?.selectedQuote || selectedQuote || {};
    return {
    customerName: String(sourceCustomerName || "").trim(),
    phone:
      sourceQuoteCustomerLink.phone ||
      sourceSelectedQuote?.customerPhone ||
      sourceSelectedQuote?.crm?.customerPhone ||
      "",
    whatsapp:
      sourceQuoteCustomerLink.whatsapp ||
      sourceSelectedQuote?.customerWhatsapp ||
      sourceSelectedQuote?.crm?.customerWhatsapp ||
      "",
    email:
      sourceQuoteCustomerLink.email ||
      sourceSelectedQuote?.customerEmail ||
      sourceSelectedQuote?.email ||
      sourceSelectedQuote?.crm?.contact?.email ||
      "",
    address:
      sourceQuoteCustomerLink.address ||
      sourceSelectedQuote?.customerAddress ||
      sourceSelectedQuote?.address ||
      sourceSelectedQuote?.crm?.contact?.address ||
      "",
    companyName:
      sourceQuoteCustomerLink.companyName ||
      sourceSelectedQuote?.customerCompanyName ||
      sourceSelectedQuote?.companyName ||
      "",
    };
  };

  const getCurtainQuoteSelectedCustomerId = (stateOverride = null) => {
    const sourceQuoteCustomerLink =
      stateOverride?.quoteCustomerLink || quoteCustomerLink || {};
    const sourceSelectedQuote = stateOverride?.selectedQuote || selectedQuote || {};
    return (
      sourceQuoteCustomerLink.customerId ||
      sourceSelectedQuote?.customerId ||
      sourceSelectedQuote?.crm?.customerId ||
      ""
    );
  };

  const buildQuoteCustomerLinkFields = (customerLedger, fallback = {}) => {
    const customerId = customerLedger?.id || fallback.customerId || "";
    const customerKey =
      customerLedger?.customerKey || fallback.customerKey || customerId || "";
    const fallbackCustomerName =
      typeof fallback.customerName === "string"
        ? fallback.customerName
        : customerName?.trim?.() || "";
    const resolvedCustomerName =
      customerLedger?.customerName || fallback.customerName || customerName?.trim?.() || "";
    const customerPhone =
      customerLedger?.phone ||
      fallback.customerPhone ||
      fallback.phone ||
      customerLedger?.whatsapp ||
      "";

    return {
      customerId,
      customerKey,
      customerName: resolvedCustomerName || fallbackCustomerName,
      customerPhone,
      customerWhatsapp:
        customerLedger?.whatsapp || fallback.customerWhatsapp || fallback.whatsapp || "",
      customerEmail: customerLedger?.email || fallback.customerEmail || fallback.email || "",
      customerAddress:
        customerLedger?.address || fallback.customerAddress || fallback.address || "",
      customerCompanyName:
        customerLedger?.companyName ||
        fallback.customerCompanyName ||
        fallback.companyName ||
        "",
      customerLedgerLinked: Boolean(customerId),
    };
  };

  const hasCorrectCurtainCalculationSnapshot = (snapshot = null) =>
    Boolean(
      snapshot &&
        snapshot.calculationVersion === CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION &&
        snapshot.roundingMode === "ceil" &&
        Number(snapshot.usableFabricWidthIn || 0) > 0 &&
        Number.isFinite(Number(snapshot.pieces))
    );

  const hasSavedCurtainCalculationFields = (row = {}) =>
    [
      row.calculationSnapshot?.pieces,
      row.finalPieces,
      row.totalPieces,
      row.pcsFull,
      row.pcs,
      row.orderedFabricMeters,
      row.customerBillableFabricMeters,
      row.fabricMeters,
      row.lineTotal,
      row.rowTotal,
      row.total,
    ].some((value) => Number.isFinite(Number(value)) && Number(value) > 0);

  const hasOwnStoredField = (source = {}, fieldName = "") =>
    Boolean(
      source &&
        Object.prototype.hasOwnProperty.call(source, fieldName) &&
        source[fieldName] !== undefined
    );

  const getFirstStoredNumberField = (
    row = {},
    rowFieldNames = [],
    snapshotFieldNames = []
  ) => {
    const snapshot = row.calculationSnapshot || {};
    for (const fieldName of snapshotFieldNames) {
      if (hasOwnStoredField(snapshot, fieldName)) {
        const value = Number(snapshot[fieldName]);
        return {
          exists: true,
          value: Number.isFinite(value) ? value : null,
          source: `calculationSnapshot.${fieldName}`,
        };
      }
    }
    for (const fieldName of rowFieldNames) {
      if (hasOwnStoredField(row, fieldName)) {
        const value = Number(row[fieldName]);
        return {
          exists: true,
          value: Number.isFinite(value) ? value : null,
          source: fieldName,
        };
      }
    }
    return { exists: false, value: null, source: "" };
  };

  const createLegacyCurtainCalculationSnapshot = (row = {}) => {
    const existingSnapshot = row.calculationSnapshot || {};
    const storedPieces = getFirstStoredNumberField(
      row,
      ["finalPieces", "totalPieces", "pcsFull", "pcs"],
      ["pieces"]
    );
    const storedOrderedMeters = getFirstStoredNumberField(
      row,
      [
        "orderedFabricMeters",
        "customerBillableFabricMeters",
        "fabricMeters",
        "fabricMetersFull",
      ],
      ["orderedFabricMeters", "customerBillableFabricMeters", "fabricMeters"]
    );
    const storedFabricAmount = getFirstStoredNumberField(
      row,
      ["fabricAmount", "fabricCost"],
      ["fabricAmount", "fabricCost"]
    );
    const storedStitchTrackAmount = getFirstStoredNumberField(
      row,
      ["stitchingTrackAmount", "stitchTrackAmount", "stitchingAndTrackAmount"],
      ["stitchingTrackAmount", "stitchTrackAmount"]
    );
    const storedLineTotal = getFirstStoredNumberField(
      row,
      ["lineTotal", "rowTotal", "total", "amount", "finalAmount"],
      ["lineTotal", "rowTotal", "total"]
    );
    const savedPieces = Number(storedPieces.value ?? 0);
    const savedOrderedMeters = Number(storedOrderedMeters.value ?? 0);
    const savedLineTotal = Number(storedLineTotal.value ?? 0);

    if (savedPieces <= 0 && savedOrderedMeters <= 0 && savedLineTotal <= 0) {
      return null;
    }

    return {
      calculationVersion:
        existingSnapshot.calculationVersion || "legacy-saved-row-values",
      productId: row.productId || row.productConfigSnapshot?.id || "",
      productName:
        row.productName || row.productType || row.productConfigSnapshot?.name || "",
      fabricWidthIn: Number(
        existingSnapshot.fabricWidthIn ?? row.fabricWidthIn ?? 54
      ),
      usableWidthDeductionIn: Number(
        existingSnapshot.usableWidthDeductionIn ?? 0
      ),
      usableFabricWidthIn: Number(
        existingSnapshot.usableFabricWidthIn ??
          row.usableFabricWidthIn ??
          row.fabricWidthIn ??
          54
      ),
      fullness: Number(existingSnapshot.fullness ?? row.fullness ?? 0),
      widthIn: Number(existingSnapshot.widthIn ?? row.width ?? 0),
      heightIn: Number(existingSnapshot.heightIn ?? row.height ?? 0),
      rawPieces: Number(existingSnapshot.rawPieces ?? savedPieces),
      pieces: savedPieces,
      roundingMode: existingSnapshot.roundingMode || "legacy",
      cutHeightIn: Number(
        existingSnapshot.cutHeightIn ??
          row.cutHeightIn ??
          row.cutCurtain ??
          row.height ??
          0
      ),
      orderedFabricMeters: savedOrderedMeters,
      customerBillableFabricMeters: Number(
        existingSnapshot.customerBillableFabricMeters ??
          row.customerBillableFabricMeters ??
          savedOrderedMeters
      ),
      fabricAmount: Number(storedFabricAmount.value ?? 0),
      fabricCost: Number(storedFabricAmount.value ?? 0),
      stitchingTrackAmount: Number(storedStitchTrackAmount.value ?? 0),
      lineTotal: savedLineTotal,
      rowTotal: savedLineTotal,
      calculatedAt: existingSnapshot.calculatedAt || row.updatedAt || "",
      legacyFormula: true,
      requiresRecalculation: true,
      syntheticLegacySnapshot: true,
      storedFieldMap: {
        pieces: storedPieces.exists,
        orderedFabric: storedOrderedMeters.exists,
        fabricAmount: storedFabricAmount.exists,
        stitchingTrackAmount: storedStitchTrackAmount.exists,
        rowTotal: storedLineTotal.exists,
      },
      storedFieldSources: {
        pieces: storedPieces.source,
        orderedFabric: storedOrderedMeters.source,
        fabricAmount: storedFabricAmount.source,
        stitchingTrackAmount: storedStitchTrackAmount.source,
        rowTotal: storedLineTotal.source,
      },
    };
  };

  const hydrateCurtainRowCalculationSnapshot = (row = {}) => {
    if (hasCorrectCurtainCalculationSnapshot(row.calculationSnapshot)) {
      return row;
    }
    if (!hasSavedCurtainCalculationFields(row)) return row;
    const productConfig = resolveProductConfig(row, productConfigs, fabricCalcSettings);
    if (!rowIsAffectedByLegacyCurtainFormulaStatic(row, productConfig)) {
      return {
        ...row,
        calculationSnapshot: row.calculationSnapshot
          ? {
              ...row.calculationSnapshot,
              requiresRecalculation: false,
              legacyFormula: false,
              legacyReviewNotApplicable: true,
            }
          : null,
        calculationSnapshotStatus: row.calculationSnapshotStatus || "not_applicable",
      };
    }
    const legacySnapshot = createLegacyCurtainCalculationSnapshot(row);
    if (!legacySnapshot) return row;
    return {
      ...row,
      calculationSnapshot: legacySnapshot,
      calculationSnapshotStatus: "legacy",
    };
  };

  const shouldUseCurtainCalculationSnapshot = (row = {}) =>
    Boolean(
      row.calculationSnapshot &&
        row.__forceCurrentCalculation !== true &&
        row.__recalculated !== true
    );

  const buildCurrentCurtainCalculationSnapshot = (row = {}, calc = {}) => {
    const productConfig = resolveProductConfig(row, productConfigs, fabricCalcSettings);
    const fabricRules =
      calc.calculationBreakdown?.fabricRules ||
      resolveFabricRulesForRow(
        row,
        productConfig && !shouldUseLegacyProductCalculation(row, productConfig)
          ? productConfig
          : null,
        fabricCalcSettings
      );
    const fullnessResult =
      calc.calculationBreakdown?.fullness ||
      resolveCurtainFullness(row, productConfig, stitchingRates, {
        fabricCalcSettings,
        fabricRules,
        quoteFullnessOverrides,
      });
    const pieceCalc =
      calc.calculationBreakdown?.curtainPieceCalc ||
      calculateCurtainPieces({
        widthIn: row.width,
        fabricWidthIn: fabricRules.fabricWidthIn,
        usableWidthDeductionIn: fabricRules.usableWidthDeductionIn,
        fullness: fullnessResult.fullness,
      });

    return {
      calculationVersion: CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
      productId: row.productId || productConfig?.id || "",
      productName:
        row.productName || row.productType || productConfig?.name || "",
      fabricWidthIn: pieceCalc.fabricWidthIn,
      usableWidthDeductionIn: pieceCalc.usableWidthDeductionIn,
      usableFabricWidthIn: pieceCalc.usableFabricWidthIn,
      fullness: pieceCalc.fullness,
      effectiveFullness: Number(fullnessResult.fullness || pieceCalc.fullness || 0),
      fullnessSource: fullnessResult.source || "",
      stitchingKey: fullnessResult.stitchingStyleKey || "",
      stitchingLabel: fullnessResult.stitchingLabel || "",
      defaultFullness: Number(
        fullnessResult.stitchingFullness ||
          fullnessResult.productDefaultFullness ||
          fullnessResult.productFullness ||
          0
      ),
      quoteFullness: Number(fullnessResult.quoteFullness || 0) || null,
      widthIn: Number(row.width || 0),
      heightIn: Number(row.height || 0),
      rawSqft: Number(
        calc.sqftCalculation?.rawSqft ?? calc.rawSqft ?? 0
      ),
      billableSqft: Number(
        calc.sqftCalculation?.billableSqft ??
          calc.billableSqft ??
          calc.adjSqft ??
          0
      ),
      sqftCalculation: calc.sqftCalculation || row.sqftCalculation || null,
      rawPieces: pieceCalc.rawPieces,
      pieces: pieceCalc.pieces,
      roundingMode: "ceil",
      cutHeightIn: Number(calc.cutCurtain || calc.cutRoman || 0),
      orderedFabricMeters: Number(
        calc.orderedFabricMeters ?? calc.fabricMeters ?? 0
      ),
      fullOrderedFabricMeters: Number(
        calc.fullOrderedFabricMeters ??
          calc.noReuseOrderedFabricMeters ??
          calc.orderedFabricMeters ??
          calc.fabricMeters ??
          0
      ),
      customerBillableFabricMeters: Number(
        calc.customerBillableFabricMeters ??
          calc.orderedFabricMeters ??
          calc.fabricMeters ??
          0
      ),
      workshopFabricRequiredMeters: Number(
        calc.workshopFabricRequiredMeters ??
          calc.workshopNewFabricMeters ??
          calc.newFabricNeededMeters ??
          calc.orderedFabricMeters ??
          calc.fabricMeters ??
          0
      ),
      workshopNewFabricMeters: Number(
        calc.workshopNewFabricMeters ??
          calc.newFabricNeededMeters ??
          calc.orderedFabricMeters ??
          calc.fabricMeters ??
          0
      ),
      cutFabricMeters: Number(calc.cutFabricMeters ?? calc.fabricMetersHalf ?? calc.fabricMeters ?? 0),
      fabricAmount: Number(calc.fabricCost || 0),
      fabricCost: Number(calc.fabricCost || 0),
      fabricGrossAmount: Number(calc.fabricGrossAmount || 0),
      fabricDiscountPercent: Number(calc.fabricDiscountPercent ?? row.discount ?? 0),
      fabricDiscountAmount: Number(calc.fabricDiscountAmount || 0),
      fabricNetAmount: Number(calc.fabricNetAmount ?? calc.fabricCost ?? 0),
      discountScope: calc.discountScope || getRowDiscountScope(row, productConfig, {
        hasFabricMeterComponent: Boolean(calc.isCurtain || calc.isRoman),
        fabricGrossAmount: calc.fabricGrossAmount || 0,
      }),
      discountBase: Number(calc.discountBase || 0),
      discountAmount: Number(calc.discountAmount || calc.fabricDiscountAmount || 0),
      baseLineAmountBeforeDiscount: Number(
        calc.baseLineAmountBeforeDiscount ?? calc.rowTotal ?? calc.lineTotal ?? 0
      ),
      sqftAmount: Number(calc.sqftAmount || calc.productCharge || 0),
      stitchingTrackAmount: round2(
        Number(calc.stitchingCost || 0) +
          Number(calc.trackCost || 0) +
          Number(calc.bendAmount || 0)
      ),
      rowSubtotal: Number(calc.rowTotal ?? calc.lineTotal ?? 0),
      rowDiscount: 0,
      rowGst: Number(calc.gstAmount || 0),
      rowTotal: Number(calc.rowTotal ?? calc.lineTotal ?? 0),
      lineTotal: Number(calc.lineTotal ?? calc.rowTotal ?? 0),
      liningAmount: Number(calc.liningAmount ?? calc.lining?.amount ?? 0),
      bendAmount: Number(calc.bendAmount ?? calc.trackConfig?.bendAmount ?? 0),
      trackConfig: calc.trackConfig || row.trackConfig || null,
      repeatSettings: normalizeCurtainRepeatEntry(
        calc.repeatSettings ||
          repeatSettings?.[
            normalizeFabricKey(getCurtainRowFabricKey(row)) || safeFabricKey(row.fabric)
          ] ||
          row.repeatSettings ||
          {}
      ),
      repeatSignature: getCurtainRepeatSignature(
        calc.repeatSettings ||
          repeatSettings?.[
            normalizeFabricKey(getCurtainRowFabricKey(row)) || safeFabricKey(row.fabric)
          ] ||
          row.repeatSettings ||
          {}
      ),
      calculationInputs: {
        fabricWidth: pieceCalc.fabricWidthIn,
        usableWidthDeduction: pieceCalc.usableWidthDeductionIn,
        usableFabricWidth: pieceCalc.usableFabricWidthIn,
        fullness: pieceCalc.fullness,
        effectiveFullness: Number(fullnessResult.fullness || pieceCalc.fullness || 0),
        fullnessSource: fullnessResult.source || "",
        stitchingKey: fullnessResult.stitchingStyleKey || "",
        stitchingLabel: fullnessResult.stitchingLabel || "",
        defaultFullness: Number(
          fullnessResult.stitchingFullness ||
            fullnessResult.productDefaultFullness ||
            fullnessResult.productFullness ||
            0
        ),
        quoteFullness: Number(fullnessResult.quoteFullness || 0) || null,
        repeatSettings: normalizeCurtainRepeatEntry(
          calc.repeatSettings ||
            repeatSettings?.[
              normalizeFabricKey(getCurtainRowFabricKey(row)) || safeFabricKey(row.fabric)
            ] ||
            row.repeatSettings ||
            {}
        ),
        rate: Number(row.mrp || row.mrpPerMeter || row.rate || 0),
        stitchingRate: Number(row.stitchingRate || row.stitchingCost || 0),
        trackLength: Number(row.trackLengthFt || row.trackLength || 0),
        gstRate: resolveCurtainSavedRowGstRate(row),
      },
      calculatedAt: new Date().toISOString(),
    };
  };

  const buildCurtainQuoteCalculationSnapshot = (options = {}) => {
    const snapshotRows = Array.isArray(options.rows) ? options.rows : rows;
    const snapshotTotals = options.summaryTotals || summaryTotals;
    const snapshotGetRowCalc =
      typeof options.getRowCalc === "function"
        ? options.getRowCalc
        : getCurtainProductionAdjustedRowCalc;
    const calculatedAt = new Date().toISOString();
    const currentUser = auth.currentUser;
    const totalsSnapshot = {
      subtotal: Number(snapshotTotals?.subTotal || 0),
      discount: Number(snapshotTotals?.discountAmount || 0),
      discountApplication: snapshotTotals?.discountApplication || "after_gst",
      taxableAmount: Number(snapshotTotals?.taxableAmount || 0),
      gstAmount: Number(snapshotTotals?.totalGstAmount ?? snapshotTotals?.gstAmount ?? 0),
      totalAfterGst: Number(snapshotTotals?.totalAfterGst || 0),
      grandTotal: Number(snapshotTotals?.grandTotal || 0),
      totalInWords: amountToWords(snapshotTotals?.grandTotal || 0, currency),
    };
    const rowSnapshots = snapshotRows.map((row, index) => {
      const calc = snapshotGetRowCalc(row, index);
      const rowSnapshot =
        calc.usesCalculationSnapshot && row.calculationSnapshot
          ? row.calculationSnapshot
          : buildCurrentCurtainCalculationSnapshot(row, calc);

      return {
        rowId: getWorkshopRowStableId(row) || `row-${index}`,
        itemName: row.item || row.name || row.room || `Row ${index + 1}`,
        product: row.productName || row.productType || row.product || "",
        fabricCode: getCurtainRowFabricLabel(row),
        width: row.width ?? "",
        height: row.height ?? "",
        unit: getRowMeasurementUnit(row, unit),
        pieces: Number(rowSnapshot?.pieces ?? calc.totalPieces ?? calc.pcsFull ?? 0),
        orderedFabric: Number(
          rowSnapshot?.orderedFabricMeters ??
            calc.orderedFabricMeters ??
            calc.fabricMeters ??
            0
        ),
        fabricAmount: Number(rowSnapshot?.fabricAmount ?? calc.fabricCost ?? 0),
        stitchingTrackAmount: Number(
          rowSnapshot?.stitchingTrackAmount ??
            round2(
              Number(calc.stitchingCost || 0) +
                Number(calc.trackCost || 0) +
                Number(calc.bendAmount || 0)
            )
        ),
        liningAmount: Number(rowSnapshot?.liningAmount ?? calc.liningAmount ?? 0),
        accessoryAmount: isCurtainAccessoryRowStatic(row)
          ? Number(calc.rowTotal || 0)
          : 0,
        rowSubtotal: Number(rowSnapshot?.rowSubtotal ?? calc.rowTotal ?? 0),
        rowDiscount: Number(rowSnapshot?.rowDiscount || 0),
        rowGst: Number(rowSnapshot?.rowGst ?? calc.gstAmount ?? 0),
        rowTotal: Number(rowSnapshot?.rowTotal ?? calc.rowTotal ?? calc.lineTotal ?? 0),
        calculationInputs: rowSnapshot?.calculationInputs || {
          fabricWidth: Number(rowSnapshot?.fabricWidthIn ?? row.fabricWidthIn ?? 0),
          usableWidthDeduction: Number(rowSnapshot?.usableWidthDeductionIn ?? 0),
          fullness: Number(rowSnapshot?.fullness ?? row.fullness ?? 0),
          rate: Number(row.mrp || row.mrpPerMeter || row.rate || 0),
          stitchingRate: Number(row.stitchingRate || row.stitchingCost || 0),
          trackLength: Number(row.trackLengthFt || row.trackLength || 0),
          gstRate: resolveCurtainSavedRowGstRate(row),
        },
      };
    });

    return {
      calculationVersion: CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
      createdAt: calculatedAt,
      createdBy: currentUser?.uid || "",
      createdByEmail: currentUser?.email || "",
      quoteGstRate: normalizeCurtainGstRate(curtainGstRate),
      discountMode: summaryDiscountMode,
      discountType: getDiscountTypeFromSummaryMode(summaryDiscountMode),
      discountValue: Number(summaryDiscountValue || 0),
      quoteFullnessOverrides: normalizeQuoteFullnessOverrides(quoteFullnessOverrides),
      useHalfPieceReuse,
      passHalfPieceSavingsToCustomer,
      totals: totalsSnapshot,
      rows: rowSnapshots,
      defaultCurtainTopHem:
        fabricCalcSettings?.curtain_top_hem ??
        fabricCalcSettings?.curtainTopHem ??
        null,
      defaultCurtainBottomHem:
        fabricCalcSettings?.curtain_bottom_hem ??
        fabricCalcSettings?.curtainBottomHem ??
        null,
      defaultUsableWidthDeduction:
        fabricCalcSettings?.usable_width_deduction ??
        fabricCalcSettings?.usableWidthDeduction ??
        null,
      defaultPanelRoundingMode: "ceil",
      createdWithAppVersion: "fabriquote-web",
      calculatedAt,
      lastCalculatedAt: calculatedAt,
      lastCalculatedBy: currentUser?.uid || "",
    };
  };

  const getCurtainSnapshotCalc = (row = {}, currentRepeatSettings = {}) => {
    if (!shouldUseCurtainCalculationSnapshot(row)) return null;
    const snapshot = row.calculationSnapshot || {};
    const snapshotRepeatSettings =
      snapshot.repeatSettings ||
      snapshot.calculationInputs?.repeatSettings ||
      row.repeatSettings ||
      {};
    if (
      getCurtainRepeatSignature(snapshotRepeatSettings) !==
      getCurtainRepeatSignature(currentRepeatSettings)
    ) {
      return null;
    }
    const normalizedProduct = normalizeProductName(
      row.productName || row.productType || row.product || snapshot.productName || ""
    );
    const isCurtainRow = normalizedProduct === "curtains";
    const isRomanRow = normalizedProduct.startsWith("roman");
    if (!isCurtainRow && !isRomanRow) return null;

    const pieces = Math.max(0, Number(snapshot.pieces || 0));
    const orderedFabricMeters = round2(
      Number(
        snapshot.orderedFabricMeters ??
          snapshot.customerBillableFabricMeters ??
          row.orderedFabricMeters ??
          row.fabricMeters ??
          0
      )
    );
    const customerBillableFabricMeters = round2(
      Number(snapshot.customerBillableFabricMeters ?? orderedFabricMeters)
    );
    const cutHeightIn = Number(
      snapshot.cutHeightIn || row.cutHeightIn || row.cutCurtain || row.height || 0
    );
    const lineTotal = round2(
      Number(snapshot.lineTotal ?? row.lineTotal ?? row.rowTotal ?? row.total ?? 0)
    );

    const selectedStitchingTrack = findCurtainRowStitchConfig(row, stitchingRates)?.config || {};
    const trackConfig = calculateCurtainTrackConfig(row, selectedStitchingTrack, {
      baseTrackLengthFt: Number(row.trackLengthFt || row.trackLength || selectedStitchingTrack.trackLengthFt || 0),
      measurementUnit: getRowMeasurementUnit(row, unit),
    });

    return {
      pcs: pieces,
      pcsFull: pieces,
      pcsHalf: pieces,
      fabricMeters: customerBillableFabricMeters || orderedFabricMeters,
      fabricMetersFull: orderedFabricMeters,
      fabricMetersHalf: customerBillableFabricMeters || orderedFabricMeters,
      totalPieces: pieces,
      cutCurtain: isCurtainRow ? cutHeightIn : 0,
      cutRoman: isRomanRow ? cutHeightIn : 0,
      rawSqft: Number(
        snapshot.sqftCalculation?.rawSqft ??
          snapshot.rawSqft ??
          (Number(row.width || 0) * Number(row.height || 0)) / 144
      ),
      adjSqft: round2(
        snapshot.sqftCalculation?.billableSqft ??
          snapshot.billableSqft ??
          (Number(row.width || 0) * Number(row.height || 0)) / 144
      ),
      billableSqft: round2(
        snapshot.sqftCalculation?.billableSqft ??
          snapshot.billableSqft ??
          (Number(row.width || 0) * Number(row.height || 0)) / 144
      ),
      sqftCalculation: snapshot.sqftCalculation || row.sqftCalculation || null,
      rowTotal: lineTotal,
      lineTotal,
      taxableAmount: lineTotal,
      trackCost: Number(row.trackCost || 0),
      trackConfig,
      bendAmount: Number(row.bendAmount || trackConfig.bendAmount || 0),
      stitchingCost: Number(row.stitchingCost || 0),
      fabricCost: Number(row.fabricCost || 0),
      disableStitching: !isCurtainRow,
      isCurtain: isCurtainRow,
      isRoman: isRomanRow,
      isBlind: false,
      repeatSettings: normalizeCurtainRepeatEntry(snapshotRepeatSettings),
      calculationSnapshot: snapshot,
      usesCalculationSnapshot: true,
      requiresRecalculation: snapshot.requiresRecalculation === true,
      legacyFormula: snapshot.legacyFormula === true,
    };
  };

/* --------------------------- CALC ROW (FINAL FIXED VERSION) --------------------------- */

/* --------------------------- CALC ROW (FINAL HR + VR SAFE) --------------------------- */
const calcRow = useCallback((r, repeatSettings, options = {}) => {
  const activeQuoteFullnessOverrides = Object.prototype.hasOwnProperty.call(
    options || {},
    "quoteFullnessOverrides"
  )
    ? normalizeQuoteFullnessOverrides(options.quoteFullnessOverrides || {})
    : quoteFullnessOverrides;
  const fabricKey =
    normalizeFabricKey(getCurtainRowFabricKey(r)) ||
    safeFabricKey(r.fabric);
  const fabricRepeat = repeatSettings?.[fabricKey] || null;
  const rowRepeat = r.repeatSettings || null;
  const repeat = hasMeaningfulCurtainRepeatEntry(fabricRepeat)
    ? normalizeCurtainRepeatEntry(fabricRepeat)
    : hasMeaningfulCurtainRepeatEntry(rowRepeat)
    ? normalizeCurtainRepeatEntry(rowRepeat)
    : normalizeCurtainRepeatEntry(fabricRepeat || rowRepeat || {});
  const hrEnabled =
    repeat.horizontalEnabled === true ||
    repeat.horizontalEnabled === "true";
  const hrValue = Number(repeat.horizontalRepeatInch) || 0;
  const vrEnabled =
    repeat.verticalEnabled === true ||
    repeat.verticalEnabled === "true";
  const vrValue = Number(repeat.verticalRepeatInch) || 0;

  const snapshotCalc = getCurtainSnapshotCalc(r, repeat);
  if (snapshotCalc) return snapshotCalc;

  const resolvedProductConfig = resolveProductConfig(
    r,
    productConfigs,
    fabricCalcSettings
  );
  if (
    resolvedProductConfig &&
    !shouldUseLegacyProductCalculation(r, resolvedProductConfig)
  ) {
    const productCalc = calculateProductRow(r, resolvedProductConfig, {
      stitchingRates,
      fabricCalcSettings,
      calculationSettings: selectedQuote?.calculationSettings || null,
      preserveLegacyBlindSqftRounding: Boolean(editingQuoteId || selectedQuote?.id),
      repeatSettings,
      quoteFullnessOverrides: activeQuoteFullnessOverrides,
      gstRate: curtainGstRate,
    });
    const selectedStitchingStyle = resolveSelectedStitchingStyle(r, stitchingRates);
    return applyBendedTrackToCalcResult(r, productCalc, selectedStitchingStyle?.config || {}, unit);
  }

  if (isCurtainAccessoryRowStatic(r)) {
    return calculateProductRow(
      r,
      normalizeProductConfig({
        id: r.productId || r.productName || r.productType || r.product || "accessory-row",
        name: r.productName || r.productType || r.product || "Accessory",
        category: r.category || "accessory",
        calculationType: r.calculationType || "quantity_rate",
        quantityRules: r.quantityRules || {},
        runningFeetRules: r.runningFeetRules || {},
        workshopRules: {
          includeInWorkshopPdf: r.includeInWorkshopPdf !== false,
          includeFabricSummary: false,
          includeRollOptimizer: false,
          ...(r.workshopRules || {}),
        },
        displayFields: r.displayFields || { showFabric: false, showRate: true },
        pricingFields: r.pricingFields || { rateAffectsPrice: true },
      }),
      {
        stitchingRates,
        fabricCalcSettings,
        repeatSettings,
        quoteFullnessOverrides: activeQuoteFullnessOverrides,
        gstRate: curtainGstRate,
      }
    );
  }

  const w = r.width ? Number(r.width) : 0;
  const h = r.height ? Number(r.height) : 0;
  const fabricW = r.fabricWidthIn ? Number(r.fabricWidthIn) : 54;

  const mrp = num(r.mrp);
  const disc = num(r.discount);
  const rateSqft = num(r.sqftRate);

  /* --------------------------- PRODUCT TYPES --------------------------- */

  const isCurtain = r.productType === "Curtains";
  const isRoman = r.productType?.startsWith("Roman");
  const isBlind =
    !isCurtain &&
    !isRoman &&
    r.productType?.toLowerCase().includes("blind");

  const disableStitching = isBlind;
  const resolvedFabricRules = resolveFabricRulesForRow(
    r,
    null,
    fabricCalcSettings
  );
  const fabricRulesForCalculation = applyHorizontalRepeatToFabricRules(
    resolvedFabricRules,
    repeat
  );

  /* --------------------------- SQFT CALC --------------------------- */

  const rawSqft = (w * h) / 144;
  const min = Number(minSqft[r.productType] || 0);
  const storedBlindSqftMode =
    r.sqftCalculation?.roundingMode ||
    r.calculationSnapshot?.sqftCalculation?.roundingMode ||
    selectedQuote?.calculationSettings?.blindSqftRoundingMode ||
    selectedQuote?.settingsSnapshot?.fabricCalcSettings?.blindSqftRoundingMode ||
    selectedQuote?.fabricCalcSettings?.blindSqftRoundingMode;
  const hasStoredBlindSqftValues =
    Number(r.billableSqft ?? r.adjSqft ?? 0) > 0 ||
    Number(r.sqftAmount ?? 0) > 0 ||
    Number(r.rowTotal ?? r.total ?? 0) > 0;
  const useLegacyBlindSqftRounding =
    (isRoman || isBlind) &&
    !storedBlindSqftMode &&
    Boolean(editingQuoteId || selectedQuote?.id) &&
    hasStoredBlindSqftValues;
  const blindSqftRoundingMode = normalizeBlindSqftRoundingMode(
    storedBlindSqftMode ||
      (useLegacyBlindSqftRounding
        ? "ceil"
        : fabricCalcSettings?.blindSqftRoundingMode),
    "exact"
  );
  const blindSqftCalculation =
    isRoman || isBlind
      ? calculateBlindSqft({
          widthIn: w,
          heightIn: h,
          roundingMode: blindSqftRoundingMode,
        })
      : null;
  const adjSqft =
    isRoman || isBlind
      ? Math.max(Number(blindSqftCalculation?.billableSqft || 0), min)
      : round2(rawSqft);
  const sqftCalculation =
    isRoman || isBlind
      ? {
          rawSqft: Number(blindSqftCalculation?.rawSqft || 0),
          billableSqft: round2(adjSqft),
          roundingMode: blindSqftRoundingMode,
          minimumSqft: min,
          minimumApplied:
            min > 0 && Number(blindSqftCalculation?.billableSqft || 0) < min,
          legacyRoundingFallback: useLegacyBlindSqftRounding,
        }
      : null;

  /* --------------------------- PCS LOGIC --------------------------- */

  let pcsFull = 0;
  let pcsHalf = 0;
  let curtainPieceCalc = null;
  const selectedStitchingStyle = resolveSelectedStitchingStyle(r, stitchingRates);
  const stitchCfg = selectedStitchingStyle?.config || {};
  const fullnessResult = resolveCurtainFullness(r, null, stitchingRates, {
    fabricCalcSettings,
    fabricRules: fabricRulesForCalculation,
    quoteFullnessOverrides: activeQuoteFullnessOverrides,
  });

  if (isCurtain) {

    const fullness = Number(fullnessResult.fullness || 1);
    const pieceCalc = calculateCurtainPieces({
      widthIn: w,
      fabricWidthIn: fabricW,
      usableWidthDeductionIn: Number(fabricRulesForCalculation.usableWidthDeductionIn || 0),
      fullness,
    });
    curtainPieceCalc = pieceCalc;

  /* ---------------- PATTERN FABRICS ---------------- */

if (hrEnabled || vrEnabled) {

if (IS_DEV_UI_ENABLED) {
  console.log("repeat-aware curtain panels use canonical usable-width ceil pieces", {
    item: r.item || r.name || "",
    hrEnabled,
    hrValue,
    vrEnabled,
    vrValue,
  });
}

  pcsHalf = pieceCalc.pieces;
  pcsFull = pieceCalc.pieces;
}

    /* ---------------- PLAIN FABRICS ---------------- */

    else {

      pcsHalf = pieceCalc.pieces;
      pcsFull = pieceCalc.pieces;

    }

    if (IS_DEV_UI_ENABLED) {
      console.log("CURTAIN_PIECE_CALC_TRACE", {
        item: r.item || r.name || "",
        product: r.productType || r.product || "",
        widthIn: w,
        fullness,
        fabricWidthIn: pieceCalc.fabricWidthIn,
        usableWidthDeductionIn: pieceCalc.usableWidthDeductionIn,
        usableFabricWidthIn: pieceCalc.usableFabricWidthIn,
        rawPieces: pieceCalc.rawPieces,
        pieces: pieceCalc.pieces,
        roundingMode: "ceil",
        calculationVersion: CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
      });
    }

  }

  /* ---------------- ROMAN BLINDS ---------------- */

  else if (isRoman) {

    const pieceCalc = calculateCurtainPieces({
      widthIn: w,
      fabricWidthIn: fabricW,
      usableWidthDeductionIn: Number(fabricRulesForCalculation.usableWidthDeductionIn || 0),
      fullness: 1,
    });
    curtainPieceCalc = pieceCalc;

    pcsFull = pieceCalc.pieces;
    pcsHalf = pieceCalc.pieces;

  }

  /* ---------------- OTHER BLINDS ---------------- */

  else if (isBlind) {

    pcsFull = 1;
    pcsHalf = 1;

  }

  /* --------------------------- CUT HEIGHT --------------------------- */

  let cutCurtain =
    h +
    Number(fabricRulesForCalculation.dropAllowanceIn || 0);

  let cutRoman =
    h +
    Number(fabricRulesForCalculation.dropAllowanceIn || 0);

  /* ---------------- VERTICAL REPEAT ALIGNMENT ---------------- */

  if (vrEnabled && vrValue > 0) {

    if (isCurtain) {

      const hemTotal = Number(fabricRulesForCalculation.dropAllowanceIn || 0);

      const alignedDrop =
        Math.ceil((h + hemTotal) / vrValue) * vrValue;

      cutCurtain = alignedDrop;

    }

    if (isRoman) {

      const hemTotal = Number(fabricRulesForCalculation.dropAllowanceIn || 0);

      const alignedDrop =
        Math.ceil((h + hemTotal) / vrValue) * vrValue;

      cutRoman = alignedDrop;

    }

  }



  /* ---------------- FABRIC METERS ---------------- */

  let fabricMetersFull = 0;
  let fabricMetersHalf = 0;

  if (isCurtain) {

    fabricMetersFull =
      round2((pcsFull * cutCurtain) / 39.37);

    fabricMetersHalf =
      round2((pcsHalf * cutCurtain) / 39.37);

  }

  else if (isRoman) {

    fabricMetersFull =
      round2((pcsFull * cutRoman) / 39.37);

    fabricMetersHalf = fabricMetersFull;

  }

  /* ---------------- COST CALCULATIONS ---------------- */

  const fabricGrossAmount =
    (isCurtain || isRoman) && mrp > 0
      ? round2(fabricMetersFull * mrp)
      : 0;
  const fabricDiscountPercent = Math.min(100, Math.max(0, disc));
  let fabricDiscountAmount = 0;
  let fabricCost = fabricGrossAmount;

  const baseStitchRate =
    num(stitchCfg.rate) || 0;

  const customStitchRate =
    num(r.customStitchRate) || 0;

  const stitchRate =
    isCurtain && r.stitchingStyle === "Eyelet (Custom)"
      ? customStitchRate
      : baseStitchRate;

  const defaultTrackLengthFt =
    num(stitchCfg.trackLengthFt) || 0;

  const rowTrackLengthFt =
    num(r.trackLengthFt ?? r.trackLength) || 0;

  const resolvedTrackLengthFt =
    rowTrackLengthFt > 0 ? rowTrackLengthFt : defaultTrackLengthFt;
  const trackConfig = calculateCurtainTrackConfig(r, stitchCfg, {
    baseTrackLengthFt: resolvedTrackLengthFt,
    measurementUnit: getRowMeasurementUnit(r, unit),
  });
  const effectiveTrackLengthFt =
    Number(trackConfig.totalTrackLengthFt || 0) > 0
      ? Number(trackConfig.totalTrackLengthFt || 0)
      : resolvedTrackLengthFt;
  const bendAmount = round2(Number(trackConfig.bendAmount || 0));

  const stitchingCost =
    isCurtain && !disableStitching
      ? (w / 12) * stitchRate
      : 0;

  /* ---------------- TRACK COST ---------------- */

  let trackCost = 0;

  if (isCurtain) {

    const trackRateFt =
      num(stitchCfg.trackRateFt) || 0;

    trackCost =
      trackRateFt * effectiveTrackLengthFt;

  }

  if (IS_DEV_UI_ENABLED && isCurtain) {
    console.log("STITCHING_RATE_TRACE", {
      styleName:
        selectedStitchingStyle?.key ||
        r.stitchingStyle ||
        r.stitchingStyleId ||
        "",
      ratePerRunningFt: stitchRate,
      fullness: Number(fullnessResult.fullness || 0),
      defaultTrackLengthFt,
      rowWidth: w,
      resolvedTrackLengthFt,
      stitchingCharge: round2(stitchingCost),
    });
  }

  if (IS_DEV_UI_ENABLED && isCurtain) {
    console.log("FULLNESS_TRACE", {
      item: r.item || r.name || "",
      product: r.productType || r.product || "",
      selectedStitch:
        r.stitchingStyle ||
        r.stitchingStyleId ||
        r.stitchLabel ||
        r.stitch ||
        "",
      productFullness: fullnessResult.productFullness,
      stitchingFullness: fullnessResult.stitchingFullness,
      resolvedFullness: fullnessResult.fullness,
      source: fullnessResult.source,
      stitchingStyleKey: fullnessResult.stitchingStyleKey,
      panels: pcsFull,
      totalPieces: pcsHalf,
      fabricMeters: fabricMetersFull,
    });
  }

  /* ---------------- BLIND COST ---------------- */

  let blindCost = 0;

  if (isBlind && rateSqft > 0) {

    blindCost =
      adjSqft * rateSqft;

  }

  if (isRoman && rateSqft > 0) {

    blindCost =
      adjSqft * rateSqft;

  }

  const baseLineAmountBeforeDiscount = round2(
    fabricGrossAmount +
      stitchingCost +
      trackCost +
      bendAmount +
      blindCost
  );
  const discountScope = getRowDiscountScope(
    r,
    resolvedProductConfig || {
      category: isCurtain ? "curtain" : isRoman ? "roman_blind" : "blind",
      calculationType: isCurtain || isRoman ? "fabric_meter" : "sqft_only",
      usesFabric: isCurtain || isRoman,
    },
    {
      hasFabricMeterComponent: isCurtain || isRoman,
      fabricGrossAmount,
    }
  );
  const discountBase =
    discountScope === "fabric_only"
      ? fabricGrossAmount
      : discountScope === "line_total"
      ? baseLineAmountBeforeDiscount
      : 0;
  const discountAmount = round2(discountBase * (fabricDiscountPercent / 100));
  fabricDiscountAmount = discountScope === "fabric_only" ? discountAmount : 0;
  fabricCost =
    discountScope === "fabric_only"
      ? round2(Math.max(0, fabricGrossAmount - fabricDiscountAmount))
      : fabricGrossAmount;

  const rowTotal =
    discountScope === "line_total"
      ? round2(Math.max(0, baseLineAmountBeforeDiscount - discountAmount))
      : round2(
          fabricCost +
            stitchingCost +
            trackCost +
            bendAmount +
            blindCost
        );

if (isCurtain && !cutCurtain) {
  console.error("cutCurtain missing", r);
}

if (isRoman && !cutRoman) {
  console.error("cutRoman missing", r);
}

  return {

    pcs: pcsFull,
    pcsFull,
    pcsHalf,

    fabricMeters: fabricMetersFull,
    fabricMetersFull,
    fabricMetersHalf,
    totalPieces: pcsHalf,
// totalPieces = final panels (including half)
    cutCurtain,
    cutRoman,

    rawSqft: round2(rawSqft),
    adjSqft: round2(adjSqft),
    sqftCalculation,

    rowTotal,
    trackCost,
    trackConfig,
    bendAmount,
    stitchingCost,
    fabricCost,
    fabricGrossAmount,
    fabricDiscountPercent,
    fabricDiscountAmount,
    fabricNetAmount: fabricCost,
    baseLineAmountBeforeDiscount,
    discountScope,
    discountBase,
    discountAmount,
    sqft: round2(adjSqft),
    billableSqft: round2(adjSqft),
    ratePerSqft: rateSqft,
    sqftAmount: round2(blindCost),
    stitchingTrackAmount: round2(stitchingCost + trackCost + bendAmount),
    rowSubtotalBeforeGst: rowTotal,
    curtainPieceCalc,
    repeatSettings: repeat,
    calculationBreakdown: {
      curtainPieceCalc,
      fabricRules: fabricRulesForCalculation,
      baseFabricRules: resolvedFabricRules,
      fullness: fullnessResult,
      repeatSettings: repeat,
    },

    disableStitching,
    isCurtain,
    isRoman,
    isBlind

  };

}, [
  curtainGstRate,
  editingQuoteId,
  fabricCalcSettings,
  minSqft,
  productConfigs,
  quoteFullnessOverrides,
  selectedQuote,
  stitchingRates,
]);

async function applyStockTransaction({
  uid,
  fabricKey,
  meters,
  type,
  quoteId = null,
  quoteNo = null,
  customerName = null,
}) {
  if (!uid) throw new Error("No user");

  const inventoryRef = doc(db, "fabric_inventory", fabricKey);
  const ledgerRef = doc(collection(db, "fabric_ledger"));

  await runTransaction(db, async (transaction) => {
    const invSnap = await transaction.get(inventoryRef);

    const current = invSnap.exists()
      ? Number(invSnap.data().totalMeters || 0)
      : 0;

    const next = current + meters;

    if (next < 0) {
      throw new Error("Insufficient stock");
    }

    transaction.set(
      inventoryRef,
      {
        fabricKey,
        totalMeters: next,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    transaction.set(ledgerRef, {
      fabricKey,
      meters,
      type,
      quoteId,
      quoteNo,
      customerName,
      createdAt: serverTimestamp(),
    });
  });
}
const applyGeneralQuoteState = (quote) => {
  const normalized = syncGeneralQuoteDefaults(getEmbeddedGeneralQuote(quote));
  const loadedQuoteDate = resolveQuoteDateForInput(
    {
      ...quote,
      generalQuote: normalized,
    },
    todayISO()
  );
  const generalCustomerId =
    normalized.customerId || quote.customerId || quote.crm?.customerId || "";
  const generalCustomerName =
    normalized.customerName ||
    quote.customerName ||
    normalized.quoteFor?.name ||
    normalized.quoteFor?.company ||
    "";
  const generalCustomerPhone =
    normalized.customerPhone ||
    quote.customerPhone ||
    quote.crm?.customerPhone ||
    normalized.quoteFor?.phone ||
    "";
  const generalCustomerEmail =
    normalized.customerEmail ||
    quote.customerEmail ||
    normalized.quoteFor?.email ||
    "";
  const generalCustomerAddress =
    normalized.customerAddress ||
    quote.customerAddress ||
    normalized.quoteFor?.address ||
    "";
  const generalCustomerCompanyName =
    normalized.customerCompanyName ||
    quote.customerCompanyName ||
    normalized.quoteFor?.company ||
    "";
  const nextGeneralQuote = {
    ...normalized,
    quoteDate: loadedQuoteDate,
    date: loadedQuoteDate,
    quotationDate: loadedQuoteDate,
    customerId: generalCustomerId,
    customerName: generalCustomerName,
    customerPhone: generalCustomerPhone,
    customerWhatsapp: normalized.customerWhatsapp || quote.customerWhatsapp || "",
    customerEmail: generalCustomerEmail,
    customerAddress: generalCustomerAddress,
    customerCompanyName: generalCustomerCompanyName,
    customerLedgerLinked:
      normalized.customerLedgerLinked === true ||
      quote.customerLedgerLinked === true ||
      Boolean(generalCustomerId),
    quoteNo: quote.quoteNo || normalized.quoteNo || "",
    currency: normalized.currency || currency,
    quoteFor: {
      ...(normalized.quoteFor || {}),
      name: normalized.quoteFor?.name || generalCustomerName,
      company: normalized.quoteFor?.company || generalCustomerCompanyName,
      phone: normalized.quoteFor?.phone || generalCustomerPhone,
      email: normalized.quoteFor?.email || generalCustomerEmail,
      address: normalized.quoteFor?.address || generalCustomerAddress,
    },
  };
  const generalCustomer =
    nextGeneralQuote.quoteFor?.company ||
    nextGeneralQuote.quoteFor?.name ||
    nextGeneralQuote.customerName ||
    "";

  setRoutedTab("quote", GENERAL_QUOTE_TYPE);
  setEditingQuoteId(null);
  setGeneralEditingQuoteId(quote.id || null);
  generalQuoteRef.current = nextGeneralQuote;
  setGeneralQuote(nextGeneralQuote);
  markGeneralQuoteBaseline(nextGeneralQuote, "saved");
  setSelectedQuote({
    id: quote.id,
    ...quote,
    quoteType: GENERAL_QUOTE_TYPE,
  });
  setSelectedQuoteWorkStage(quote.workStage || "quote_given");
  setSelectedQuoteStatus(quote.status || "draft");
  setSelectedInstallationDate(quote.installationDate || null);
  setTab("quote");
  setIsNewQuote(false);
  setAutoSaveStatus("saved");
  setCustomerName(generalCustomer);
};

const getGeneralQuoteImageCleanupEligibleAt = (removedAtIso) => {
  const removedMillis = safeTimeMillis(removedAtIso) || Date.now();
  return new Date(
    removedMillis + GENERAL_QUOTE_IMAGE_TTL_DAYS * 24 * 60 * 60 * 1000
  ).toISOString();
};

const archiveGeneralQuoteImageMetadata = (
  image = {},
  {
    itemId = "",
    itemName = "",
    quoteId = "",
    quoteNo = "",
    reason = "removed_from_quote",
    removedBy = "",
  } = {}
) => {
  const removedAt = new Date().toISOString();
  const normalized = createGeneralQuoteImage(image);
  return createGeneralQuoteImage({
    ...normalized,
    removedFromQuote: true,
    removedAt,
    removedBy,
    removalReason: reason,
    cleanupEligibleAt: getGeneralQuoteImageCleanupEligibleAt(removedAt),
    cleanupRetentionDays: GENERAL_QUOTE_IMAGE_TTL_DAYS,
    cleanupStatus: "pending_retention_cleanup",
    removedFromQuoteId: quoteId,
    removedFromQuoteNo: quoteNo,
    removedFromItemId: itemId,
    removedFromItemName: itemName,
  });
};

const resolveGeneralQuoteImageDisplayUrl = async (image = {}, context = {}) => {
  const normalized = createGeneralQuoteImage(image);
  const fullPath = getGeneralQuoteImageStoragePath(normalized);
  const displayUrl = getGeneralQuoteImageDisplayUrl(normalized);
  const originalUrl = String(
    image?.downloadURL ||
      image?.downloadUrl ||
      image?.imageUrl ||
      image?.imageURL ||
      image?.url ||
      image?.previewUrl ||
      ""
  ).trim();
  const extractedFullPath = extractFirebaseStorageFullPathFromUrl(originalUrl);
  const originalIsFirebaseStorageRestUrl =
    isFirebaseStorageRestUrl(originalUrl) || isFirebaseStorageRestUrl(displayUrl);
  const storageRef = fullPath
    ? getGeneralQuoteImageStorageRef(normalized)
    : null;
  const shouldLogImageDebug = shouldLogGeneralQuoteImageDebug(context);
  const shouldResolveFromStorage =
    fullPath &&
    (!displayUrl ||
      originalIsFirebaseStorageRestUrl ||
      isProtectedFirebaseStorageRestUrl(displayUrl));
  const isExactJonyLeadsPath =
    fullPath === EXACT_JONYLEADS_IMAGE_FULL_PATH &&
    (normalized.storageBucket || firebaseApp.options.storageBucket || "") ===
      EXACT_JONYLEADS_IMAGE_BUCKET;

  if (shouldLogImageDebug) {
    console.log("JONYLEADS_IMAGE_RESOLVE_STEP", {
      quoteNo: context.quoteNo || "",
      quoteId: context.quoteId || "",
      itemName: context.itemName || "",
      workspaceUid,
      authUid: auth.currentUser?.uid || "",
      originalUrl,
      legacyUrl: normalized.legacyUrl || "",
      downloadURLBefore: normalized.downloadURL || "",
      extractedFullPath,
      decodedFullPath: fullPath,
      storagePathUsedForRef: storageRef?.fullPath || fullPath,
      storageBucketFromImage: normalized.storageBucket || "",
      configuredStorageBucket: firebaseApp.options.storageBucket || "",
      isFirebaseRestUrl: originalIsFirebaseStorageRestUrl,
      hasTokenInOriginalUrl: hasFirebaseStorageDownloadToken(originalUrl),
      willCallGetMetadata: Boolean(fullPath),
      willCallGetDownloadURL: Boolean(shouldResolveFromStorage),
    });
  }

  if (!shouldResolveFromStorage && displayUrl) {
    return {
      ...normalized,
      downloadURL: displayUrl,
      url: displayUrl,
      previewUrl: displayUrl,
      resolveStatus: "ready",
      resolveErrorCode: "",
      resolveErrorMessage: "",
    };
  }

  if (!fullPath) {
    const unavailable = {
      ...normalized,
      url: "",
      previewUrl: "",
      downloadURL: "",
      resolveStatus: "unavailable",
      resolveErrorCode: "missing-storage-path",
      resolveErrorMessage: normalized.filename
        ? "Image file path is missing."
        : "Image unavailable.",
    };
    if (isDevelopmentMode()) {
      console.log("GENERAL_QUOTE_IMAGE_RESOLVE_TRACE", {
        quoteNo: context.quoteNo || "",
        itemName: context.itemName || "",
        imageName: unavailable.name || unavailable.filename || "",
        imageKeys: Object.keys(image || {}),
        fullPath: "",
        hasDownloadURL: false,
        legacyUrl: normalized.legacyUrl || "",
        resolved: false,
        reason: "missing-storage-path",
      });
    }
    return unavailable;
  }

  const cacheKey = getGeneralQuoteImageCacheKey(normalized);
  const cachedUrl = generalQuoteImageUrlCacheRef.current.get(cacheKey);
  if (cachedUrl) {
    return {
      ...normalized,
      fullPath,
      storagePath: fullPath,
      path: fullPath,
      downloadURL: cachedUrl,
      url: cachedUrl,
      previewUrl: cachedUrl,
      resolveStatus: "resolved",
      resolveErrorCode: "",
      resolveErrorMessage: "",
    };
  }

  if (shouldLogImageDebug) {
    console.log("GENERAL_QUOTE_IMAGE_METADATA_TRACE", {
      quoteNo: context.quoteNo || "",
      quoteId: context.quoteId || "",
      itemName: context.itemName || "",
      workspaceUid,
      authUid: auth.currentUser?.uid || "",
      imageKeys: Object.keys(image || {}),
      image: {
        ...normalized,
        downloadURL: normalized.downloadURL ? "[download-url-present]" : "",
        url: normalized.url ? "[url-present]" : "",
        previewUrl: normalized.previewUrl ? "[preview-url-present]" : "",
      },
    });
  }

  try {
    let metadataSuccess = false;
    let metadataErrorCode = "";
    let metadataErrorMessage = "";
    try {
      const metadata = await getMetadata(storageRef);
      metadataSuccess = true;
      if (shouldLogImageDebug) {
        console.log("JONYLEADS_IMAGE_METADATA_RESULT", {
          quoteNo: context.quoteNo || "",
          itemName: context.itemName || "",
          fullPath,
          storageBucket: normalized.storageBucket || firebaseApp.options.storageBucket || "",
          exists: true,
          contentType: metadata?.contentType || "",
          size: metadata?.size || 0,
          customMetadata: metadata?.customMetadata || {},
        });
      }
    } catch (metadataError) {
      metadataErrorCode = metadataError?.code || "";
      metadataErrorMessage = metadataError?.message || "";
      if (shouldLogImageDebug) {
        console.error("JONYLEADS_IMAGE_METADATA_FAILED", {
          quoteNo: context.quoteNo || "",
          itemName: context.itemName || "",
          fullPath,
          storageBucket: normalized.storageBucket || firebaseApp.options.storageBucket || "",
          errorCode: metadataError?.code,
          errorMessage: metadataError?.message,
        });
      }
    }

    const resolvedUrl = await getDownloadURL(storageRef);
    if (isExactJonyLeadsPath || shouldLogImageDebug) {
      console.log("EXACT_JONYLEADS_IMAGE_STORAGE_TEST", {
        bucket: EXACT_JONYLEADS_IMAGE_BUCKET,
        fullPath: EXACT_JONYLEADS_IMAGE_FULL_PATH,
        metadataSuccess,
        downloadUrlSuccess: true,
        errorCode: metadataErrorCode,
        errorMessage: metadataErrorMessage,
      });
    }
    generalQuoteImageUrlCacheRef.current.set(cacheKey, resolvedUrl);
    if (shouldLogImageDebug) {
      console.log("GENERAL_QUOTE_IMAGE_RESOLVE_TRACE", {
        quoteNo: context.quoteNo || "",
        quoteId: context.quoteId || "",
        itemName: context.itemName || "",
        imageName: normalized.name || normalized.filename || "",
        fullPath,
        storageBucket: normalized.storageBucket || firebaseApp.options.storageBucket || "",
        hasDownloadURL: Boolean(normalized.downloadURL),
        legacyUrl: normalized.legacyUrl || "",
        resolved: true,
      });
      console.log("JONYLEADS_IMAGE_RESOLVE_RESULT", {
        quoteNo: context.quoteNo || "",
        itemName: context.itemName || "",
        fullPath,
        resolvedDownloadURL: resolvedUrl,
        resolvedHasToken: resolvedUrl?.includes("token="),
      });
    }
    return {
      ...normalized,
      fullPath,
      storagePath: fullPath,
      path: fullPath,
      downloadURL: resolvedUrl,
      url: resolvedUrl,
      previewUrl: resolvedUrl,
      resolveStatus: "resolved",
      resolveErrorCode: "",
      resolveErrorMessage: "",
    };
  } catch (error) {
    const message = getGeneralQuoteImageResolveErrorMessage(error);
    console.error("GENERAL_QUOTE_IMAGE_RESOLVE_FAILED", {
      quoteNo: context.quoteNo || "",
      quoteId: context.quoteId || "",
      itemName: context.itemName || "",
      fullPath,
      storageBucket: normalized.storageBucket || firebaseApp.options.storageBucket || "",
      errorCode: error?.code,
      errorMessage: error?.message,
    });
    console.error("JONYLEADS_IMAGE_GET_DOWNLOAD_URL_FAILED", {
      quoteNo: context.quoteNo || "",
      itemName: context.itemName || "",
      fullPath,
      errorCode: error?.code,
      errorName: error?.name,
      errorMessage: error?.message,
      storageBucket:
        normalized.storageBucket ||
        storage.app.options.storageBucket ||
        firebaseApp.options.storageBucket ||
        "",
    });
    if (isExactJonyLeadsPath || shouldLogImageDebug) {
      console.log("EXACT_JONYLEADS_IMAGE_STORAGE_TEST", {
        bucket: EXACT_JONYLEADS_IMAGE_BUCKET,
        fullPath: EXACT_JONYLEADS_IMAGE_FULL_PATH,
        metadataSuccess: false,
        downloadUrlSuccess: false,
        errorCode: error?.code || "",
        errorMessage: error?.message || "",
      });
    }
    return {
      ...normalized,
      fullPath,
      storagePath: fullPath,
      path: fullPath,
      downloadURL: "",
      url: "",
      previewUrl: "",
      resolveStatus: "failed",
      resolveErrorCode: error?.code || "",
      resolveErrorMessage: message,
    };
  }
};

const cleanupExpiredGeneralQuoteImages = async (quoteLike) => {
  const normalized = syncGeneralQuoteDefaults(quoteLike || {});
  let changed = false;
  let metadataChanged = false;

  const lineItems = await Promise.all(
    (normalized.lineItems || []).map(async (item) => {
      const imageContext = {
        quoteNo: normalized.quoteNo,
        quoteId: normalized.id || quoteLike?.id || selectedQuote?.id || "",
        customerName:
          normalized.customerName ||
          normalized.quoteFor?.name ||
          normalized.quoteFor?.company ||
          "",
        itemName: item.itemName || item.name || "",
      };
      if (shouldLogGeneralQuoteImageDebug(imageContext)) {
        getRawGeneralQuoteImageDebugEntries(item).forEach((entry, imageIndex) => {
          const rawImage = entry.rawImage;
          console.log("JONYLEADS_IMAGE_DEBUG_RAW", {
            quoteNo: imageContext.quoteNo,
            quoteId: imageContext.quoteId,
            itemName: imageContext.itemName,
            workspaceUid,
            authUid: auth.currentUser?.uid || "",
            imageIndex,
            source: entry.source,
            rawImage,
            rawImageKeys:
              rawImage && typeof rawImage === "object" ? Object.keys(rawImage || {}) : [],
            rawStringValue: typeof rawImage === "string" ? rawImage : null,
          });
        });
      }
      const itemImages = normalizeGeneralQuoteItemImages(item);
      if (!itemImages.length) return syncGeneralQuoteItemPrimaryImageFields(item);

      const activeImages = [];

      for (const image of itemImages) {
        const imageStoragePath = getGeneralQuoteImageStoragePath(image);
        if (!imageStoragePath) {
          activeImages.push(
            await resolveGeneralQuoteImageDisplayUrl(image, {
              ...imageContext,
            })
          );
          continue;
        }

        let expiresAtMillis = safeTimeMillis(image.expiresAt);

        try {
          const metadata = await getMetadata(ref(storage, imageStoragePath));
          expiresAtMillis =
            safeTimeMillis(metadata?.customMetadata?.expiresAt) ||
            expiresAtMillis;
        } catch (error) {
          if (error?.code !== "storage/object-not-found") {
            console.warn("Image metadata lookup failed:", error);
          }
        }

        const expired =
          expiresAtMillis > 0 && expiresAtMillis <= Date.now();

        if (!expired) {
          const resolvedImage = await resolveGeneralQuoteImageDisplayUrl(
            {
              ...image,
              fullPath: imageStoragePath,
              storagePath: imageStoragePath,
              path: imageStoragePath,
            },
            {
              ...imageContext,
            }
          );
          if (
            getGeneralQuoteImageDisplayUrl(resolvedImage) !==
              getGeneralQuoteImageDisplayUrl(image) ||
            getGeneralQuoteImageStoragePath(resolvedImage) !== imageStoragePath
          ) {
            metadataChanged = true;
          }
          activeImages.push(resolvedImage);
          continue;
        }

        const retainedExpiredImage = await resolveGeneralQuoteImageDisplayUrl(
          {
            ...image,
            fullPath: imageStoragePath,
            storagePath: imageStoragePath,
            path: imageStoragePath,
            cleanupEligibleAt: image.cleanupEligibleAt || image.expiresAt || "",
            cleanupStatus: image.cleanupStatus || "retention_expired_review",
          },
          {
            ...imageContext,
          }
        );
        activeImages.push(retainedExpiredImage);
        metadataChanged = true;
        if (isDevelopmentMode()) {
          console.info("GENERAL_QUOTE_IMAGE_EXPIRY_DELETE_SKIPPED", {
            quoteNo: imageContext.quoteNo || "",
            itemName: imageContext.itemName || "",
            storagePath: imageStoragePath,
            reason:
              "Expired General Quote images are retained in Storage and reviewed later.",
          });
        }
      }

      return syncGeneralQuoteItemPrimaryImageFields({
        ...item,
        images: activeImages,
      });
    })
  );

  return {
    cleanedQuote: {
      ...normalized,
      lineItems,
    },
    changed,
    metadataChanged,
  };
};

const uploadGeneralQuoteItemImage = async (itemId, filesInput) => {
  const files = Array.isArray(filesInput)
    ? filesInput.filter(Boolean)
    : filesInput
    ? [filesInput]
    : [];

  if (!workspaceUid || !itemId || files.length === 0) {
    console.error("REAL GENERAL IMAGE UPLOAD BLOCKED: workspace/auth/item missing", {
      workspaceUid,
      uid: auth.currentUser?.uid || "",
      itemId,
      fileCount: files.length,
    });
    notify("Could not save because workspace is not ready. Please refresh and try again.", "error");
    return;
  }

  if (!auth.currentUser?.uid) {
    console.error("REAL GENERAL IMAGE UPLOAD BLOCKED: auth missing", {
      workspaceUid,
      itemId,
    });
    notify("Could not save because workspace is not ready. Please refresh and try again.", "error");
    return;
  }

  const runUpload = async () => {
    setGeneralUploadingItemId(itemId);
    let uploadDebugContext = {
      workspaceUid,
      quoteId:
        generalEditingQuoteId ||
        selectedQuote?.id ||
        generalQuoteRef.current?.quoteNo ||
        generalQuoteDraftKey ||
        "draft-new",
      itemId,
      storagePath: "",
    };

    try {
      const uploadedImages = [];
      const draftId = sanitizeFirestoreDocIdSegment(
        generalQuoteDraftKey || createClientId("general-draft")
      );
      const rawQuoteIdForUpload =
        generalEditingQuoteId ||
          selectedQuote?.id ||
          generalQuoteRef.current?.quoteNo ||
          "";
    const quoteIdOrDraftId = rawQuoteIdForUpload
      ? sanitizeFirestoreDocIdSegment(rawQuoteIdForUpload)
      : `drafts/${draftId}`;
    uploadDebugContext = {
      workspaceUid,
      quoteId: quoteIdOrDraftId,
      itemId,
      storagePath: "",
    };

    for (const file of files) {
      const validation = validateGeneralQuoteImageFile(file);
      if (!validation.valid) {
        notify(validation.message, "warning");
        continue;
      }

      const uploadedAt = new Date().toISOString();
      const expiresAt = new Date(Date.now() + GENERAL_IMAGE_TTL_MS).toISOString();
      const imageId = createClientId("general-image");
      const safeFileName = normalizeFileName(file.name || "item-image");
      const uploadTimestamp = Date.now();
      const storagePath = buildGeneralQuoteImageStoragePath({
        workspaceUid,
        quoteId: rawQuoteIdForUpload,
        draftId,
        itemId,
        imageId,
        fileName: safeFileName,
        timestamp: uploadTimestamp,
      });
      uploadDebugContext = {
        workspaceUid,
        quoteId: quoteIdOrDraftId,
        itemId,
        fileName: file?.name,
        fileType: file?.type,
        fileSize: file?.size,
        storagePath,
      };

      if (isDevelopmentMode()) {
        console.log("GENERAL QUOTE IMAGE UPLOAD START", uploadDebugContext);
      }
      const currentUploadDebugContext = { ...uploadDebugContext };
      console.log("REAL GENERAL IMAGE UPLOAD START", {
        ...currentUploadDebugContext,
        uid: auth.currentUser?.uid,
        draftId,
      });
      setGeneralQuoteDebugState((prev) => ({
        ...prev,
        uploadState: "uploading",
        lastUploadError: null,
        lastUpload: {
          ...currentUploadDebugContext,
          state: "uploading",
          at: new Date().toISOString(),
        },
      }));

      console.log("REAL GENERAL IMAGE UPLOAD BEFORE uploadBytes", uploadDebugContext);
      const uploadedImage = await uploadGeneralQuoteImage({
        workspaceUid,
        quoteId: rawQuoteIdForUpload,
        draftId,
        itemId,
        imageId,
        file,
        timestamp: uploadTimestamp,
        metadata: {
          workspaceUid,
          quoteId: quoteIdOrDraftId,
          expiresAt,
        },
      });

      if (isDevelopmentMode()) {
        console.log("GENERAL QUOTE IMAGE UPLOAD BYTES OK", uploadDebugContext);
      }
      console.log("REAL GENERAL IMAGE UPLOAD uploadBytes SUCCESS", uploadDebugContext);

      if (isDevelopmentMode()) {
        console.log("GENERAL QUOTE IMAGE DOWNLOAD URL OK", {
          ...uploadDebugContext,
          hasDownloadUrl: Boolean(uploadedImage.downloadURL),
        });
      }
      console.log("REAL GENERAL IMAGE UPLOAD getDownloadURL SUCCESS", {
        ...uploadDebugContext,
        hasDownloadUrl: Boolean(uploadedImage.downloadURL),
      });

      uploadedImages.push(
        createGeneralQuoteImage({
          ...uploadedImage,
          expiresAt,
          uploadedAt,
        })
      );
      console.log(
        "GENERAL_QUOTE_NEW_UPLOAD_METADATA_SAVED",
        uploadedImages[uploadedImages.length - 1]
      );
    }

    if (!uploadedImages.length) {
      return;
    }

    console.log("REAL GENERAL IMAGE UPLOAD BEFORE LOCAL STATE UPDATE", {
      workspaceUid,
      quoteId: quoteIdOrDraftId,
      itemId,
      uploadedCount: uploadedImages.length,
    });

    const currentQuote = generalQuoteRef.current || generalQuote;
    const nextQuote = {
      ...currentQuote,
      lineItems: (currentQuote.lineItems || []).map((item) =>
        item.id === itemId
          ? syncGeneralQuoteItemPrimaryImageFields({
              ...item,
              images: [
                ...normalizeGeneralQuoteItemImages(item),
                ...uploadedImages,
              ],
            })
          : item
      ),
    };
    generalQuoteRef.current = nextQuote;
    setGeneralQuote(nextQuote);

    console.log("REAL GENERAL IMAGE UPLOAD LOCAL STATE UPDATE SUCCESS", {
      workspaceUid,
      quoteId: quoteIdOrDraftId,
      itemId,
      uploadedCount: uploadedImages.length,
    });
    setGeneralQuoteDebugState((prev) => ({
      ...prev,
      uploadState: "uploaded",
      lastUploadError: null,
      lastUpload: {
        ...uploadDebugContext,
        state: "uploaded",
        uploadedCount: uploadedImages.length,
        at: new Date().toISOString(),
      },
    }));

    if (isDevelopmentMode()) {
      console.log("GENERAL QUOTE IMAGE LOCAL STATE UPDATED", {
        workspaceUid,
        quoteId: quoteIdOrDraftId,
        itemId,
        uploadedCount: uploadedImages.length,
      });
    }
      return uploadedImages;
    } catch (error) {
    console.error("REAL GENERAL IMAGE UPLOAD FAILED EXACT", {
      code: error?.code,
      message: error?.message,
      stack: error?.stack,
      storagePath: uploadDebugContext.storagePath,
      workspaceUid,
      quoteId: uploadDebugContext.quoteId,
      itemId,
      fileName: uploadDebugContext.fileName || files[0]?.name,
      fileType: uploadDebugContext.fileType || files[0]?.type,
      fileSize: uploadDebugContext.fileSize || files[0]?.size,
    });
    console.error("GENERAL QUOTE IMAGE UPLOAD FAILED", {
      error,
      code: error?.code,
      message: error?.message,
      stack: error?.stack,
      workspaceUid,
      quoteId: uploadDebugContext.quoteId,
      itemId,
      fileName: uploadDebugContext.fileName || files[0]?.name,
      fileType: uploadDebugContext.fileType || files[0]?.type,
      fileSize: uploadDebugContext.fileSize || files[0]?.size,
      storagePath: uploadDebugContext.storagePath,
    });
    setGeneralQuoteDebugState((prev) => ({
      ...prev,
      uploadState: "failed",
      lastUploadError: {
        code: error?.code || "",
        message: error?.message || String(error),
        stack: error?.stack || "",
        storagePath: uploadDebugContext.storagePath,
        at: new Date().toISOString(),
      },
      lastUpload: {
        ...(prev.lastUpload || {}),
        ...uploadDebugContext,
        state: "failed",
      },
    }));
    raiseFirebaseHealthWarning("storage", error, {
      storagePath: uploadDebugContext.storagePath,
      source: "generalQuoteImageUpload",
    });
    notify(
      isFirebaseStorageBillingError(error)
        ? "Firebase Storage billing or bucket access issue detected. Check Firebase Storage billing/bucket configuration."
        : error?.code === "storage-limit-reached" || error?.code === "invalid-image-file"
        ? error.message
        : "Image upload failed. Please try again.",
      "error",
      isFirebaseStorageBillingError(error) ? 7000 : 3800
    );
      return [];
    } finally {
      setGeneralUploadingItemId("");
    }
  };

  const uploadPromise = runUpload();
  generalUploadPromiseRef.current = uploadPromise;
  try {
    return await uploadPromise;
  } finally {
    if (generalUploadPromiseRef.current === uploadPromise) {
      generalUploadPromiseRef.current = null;
    }
  }
};

const removeGeneralQuoteItemImage = async (itemId, imageId) => {
  const currentQuote = generalQuoteRef.current || generalQuote;
  const targetItem = (currentQuote.lineItems || []).find((item) => item.id === itemId);
  if (!targetItem) return;

  const targetImages = normalizeGeneralQuoteItemImages(targetItem);
  const targetImage = targetImages.find((image) => image.id === imageId);
  if (!targetImage) return;

  const archivedImage = archiveGeneralQuoteImageMetadata(targetImage, {
    itemId,
    itemName: targetItem.itemName || targetItem.name || "",
    quoteId: generalEditingQuoteId || selectedQuote?.id || currentQuote.id || "",
    quoteNo: currentQuote.quoteNo || selectedQuote?.quoteNo || "",
    reason: "user_removed_from_quote",
    removedBy: auth.currentUser?.uid || "",
  });
  const nextQuote = {
    ...currentQuote,
    lineItems: (currentQuote.lineItems || []).map((item) =>
      item.id === itemId
        ? syncGeneralQuoteItemPrimaryImageFields({
            ...item,
            images: normalizeGeneralQuoteItemImages(item).filter(
              (image) => image.id !== imageId
            ),
            removedImages: [
              ...(Array.isArray(item.removedImages) ? item.removedImages : []),
              archivedImage,
            ],
          })
        : item
    ),
  };
  generalQuoteRef.current = nextQuote;
  setGeneralQuote(nextQuote);
  if (archivedImage.fileId) {
    archiveManagedStorageFile({
      workspaceUid,
      fileId: archivedImage.fileId,
      removedBy: auth.currentUser?.uid || "",
      removalReason: "user_removed_from_quote",
      linkedRecords: [],
    }).catch((error) => {
      if (isDevelopmentMode()) {
        console.warn("GENERAL_QUOTE_STORAGE_MANIFEST_ARCHIVE_FAILED", {
          fileId: archivedImage.fileId,
          code: error?.code,
          message: error?.message,
        });
      }
    });
  }
  if (isDevelopmentMode()) {
    console.info("GENERAL_QUOTE_IMAGE_DETACHED_FROM_QUOTE", {
      quoteId: generalEditingQuoteId || selectedQuote?.id || currentQuote.id || "",
      quoteNo: currentQuote.quoteNo || selectedQuote?.quoteNo || "",
      itemId,
      imageId,
      storagePath: getGeneralQuoteImageStoragePath(targetImage),
      cleanupEligibleAt: archivedImage.cleanupEligibleAt || "",
    });
  }
};

const repairGeneralQuoteItemImage = async (
  itemId,
  imageId,
  imageIndex = -1,
  selectedCandidate = null
) => {
  const quoteState = generalQuoteRef.current || generalQuote || {};
  const quoteId = generalEditingQuoteId || selectedQuote?.id || quoteState.id || "";
  const targetItem = (quoteState.lineItems || []).find((item) => item.id === itemId);
  const targetImages = normalizeGeneralQuoteItemImages(targetItem || {});
  const numericImageIndex = Number(imageIndex);
  const targetImage =
    Number.isFinite(numericImageIndex) && numericImageIndex >= 0
      ? targetImages[numericImageIndex]
      : targetImages.find((image) => image.id === imageId);
  const repairKey = `${itemId}:${imageId || imageIndex}`;

  if (!workspaceUid || !quoteId || !targetItem || !targetImage) {
    console.error("GENERAL_QUOTE_IMAGE_REPAIR_BLOCKED", {
      workspaceUid,
      quoteId,
      itemId,
      imageId,
      imageIndex,
      hasTargetItem: Boolean(targetItem),
      hasTargetImage: Boolean(targetImage),
    });
    notify("Could not repair this image because the quotation context is missing.", "error");
    return;
  }

  const originalImageUrl =
    targetImage.downloadURL ||
    targetImage.url ||
    targetImage.previewUrl ||
    targetImage.legacyUrl ||
    "";
  const initialDebug = {
    quoteNo: quoteState.quoteNo || selectedQuote?.quoteNo || "",
    quoteId,
    itemName: targetItem.itemName || targetItem.name || "",
    workspaceUid,
    itemId,
    imageId,
    imageIndex,
    originalUrl: originalImageUrl,
    extractedBucket: targetImage.storageBucket || "",
    extractedPath: getGeneralQuoteImageStoragePath(targetImage),
    extractedFullPath: getGeneralQuoteImageStoragePath(targetImage),
    exactPathExists: "checking",
    exactCheckResult: {
      status: "pending",
      fullPath: getGeneralQuoteImageStoragePath(targetImage),
    },
    prefixesSearched: ["pending: exact path check started"],
    candidateSearchResult: null,
    candidatesFound: [],
    selectedCandidate: selectedCandidate || null,
    repairResult: "checking",
    canonicalPath: "",
    finalStatus: "checking",
    debugUpdatedAt: new Date().toISOString(),
  };
  generalQuoteImageRepairDebugRef.current[repairKey] = initialDebug;
  setGeneralImageRepairState((prev) => ({
    ...prev,
    [repairKey]: {
      status: "checking",
      debug: initialDebug,
      error: null,
      updatedAt: initialDebug.debugUpdatedAt,
    },
  }));
  setGeneralRepairingImageKey(repairKey);
  try {
    const repairCallable = httpsCallable(functions, "repairGeneralQuoteImageLinks");
    const result = await repairCallable({
      workspaceUid,
      quoteId,
      quoteNo: quoteState.quoteNo || selectedQuote?.quoteNo || "",
      itemId,
      imageId,
      imageIndex: Number.isFinite(numericImageIndex) ? numericImageIndex : -1,
      storageBucket: firebaseApp.options.storageBucket || "",
      selectedCandidate,
    });
    const responseData = result?.data || {};
    const candidatesFound =
      responseData.candidatesFound ||
      responseData.candidateSearch?.candidates ||
      [];
    const nextDebug = {
      quoteNo: quoteState.quoteNo || selectedQuote?.quoteNo || "",
      quoteId,
      itemName: targetItem.itemName || targetItem.name || "",
      workspaceUid,
      itemId,
      imageId,
      imageIndex,
      originalUrl:
        responseData.originalUrl ||
        originalImageUrl,
      extractedBucket: targetImage.storageBucket || "",
      extractedPath: responseData.extractedPath || getGeneralQuoteImageStoragePath(targetImage),
      extractedFullPath: responseData.extractedPath || getGeneralQuoteImageStoragePath(targetImage),
      exactPathExists:
        typeof responseData.exactPathExists === "boolean"
          ? responseData.exactPathExists
          : (responseData.exactChecks || []).some((check) => check.fileExists),
      exactCheckResult: responseData.exactChecks || null,
      prefixesSearched:
        responseData.prefixesSearched ||
        responseData.candidateSearch?.prefixesSearched ||
        [],
      candidateSearchResult: responseData.candidateSearch || null,
      candidatesFound,
      selectedCandidate: responseData.selectedCandidate || selectedCandidate || null,
      repairResult: responseData.repairResult || responseData.status || null,
      repairResponse: responseData || null,
      canonicalPath: responseData.canonicalPath || responseData.canonicalFullPath || "",
      finalStatus: responseData.finalStatus || responseData.status || "success",
      at: new Date().toISOString(),
      debugUpdatedAt: new Date().toISOString(),
    };
    generalQuoteImageRepairDebugRef.current[repairKey] = nextDebug;
    if (responseData.status === "multiple-candidates") {
      setGeneralImageRepairCandidates((prev) => ({
        ...prev,
        [repairKey]: {
          candidates: candidatesFound,
          quoteNo: quoteState.quoteNo || selectedQuote?.quoteNo || "",
          quoteId,
          itemName: targetItem.itemName || targetItem.name || "",
          workspaceUid,
          originalUrl:
            targetImage.downloadURL ||
            targetImage.url ||
            targetImage.previewUrl ||
            targetImage.legacyUrl ||
            "",
          extractedPath: responseData.extractedPath || getGeneralQuoteImageStoragePath(targetImage),
          prefixesSearched:
            responseData.prefixesSearched ||
            responseData.candidateSearch?.prefixesSearched ||
            [],
          at: new Date().toISOString(),
        },
      }));
      setGeneralImageRepairState((prev) => ({
        ...prev,
        [repairKey]: {
          status: "searching",
          debug: nextDebug,
          error: null,
          updatedAt: nextDebug.debugUpdatedAt,
        },
      }));
      notify(
        "Multiple possible image files found. Choose the correct file below the image.",
        "warning",
        8000
      );
      return;
    }
    if (responseData.ok === false) {
      const isMissing =
        responseData.finalStatus === "needs-user-upload" ||
        responseData.repairResult === "object-not-found" ||
        responseData.status === "not-found";
      setGeneralImageRepairCandidates((prev) => {
        const next = { ...prev };
        delete next[repairKey];
        return next;
      });
      setGeneralImageRepairState((prev) => ({
        ...prev,
        [repairKey]: {
          status: isMissing ? "missing" : "failed",
          debug: {
            ...nextDebug,
            finalStatus: responseData.finalStatus || (isMissing ? "needs-user-upload" : "failed"),
          },
          error:
            responseData.message ||
            (isMissing
              ? "Image file was not found in Firebase Storage."
              : "Could not repair this image."),
          updatedAt: nextDebug.debugUpdatedAt,
        },
      }));
      notify(
        isMissing
          ? "Image file was not found in Firebase Storage. Upload a replacement image if you have it."
          : responseData.message || "Could not repair this image.",
        isMissing ? "warning" : "error",
        8000
      );
      return;
    }
    const repairedImage = createGeneralQuoteImage(responseData.image || {});
    if (!getGeneralQuoteImageDisplayUrl(repairedImage)) {
      throw new Error("Repair completed without a usable download URL.");
    }
    const nextQuote = {
      ...quoteState,
      lineItems: (quoteState.lineItems || []).map((item) => {
        if (item.id !== itemId) return item;
        const images = normalizeGeneralQuoteItemImages(item);
        const nextImages = [...images];
        const targetIndex =
          Number.isFinite(numericImageIndex) && numericImageIndex >= 0
            ? numericImageIndex
            : images.findIndex((image) => image.id === imageId);
        nextImages[targetIndex >= 0 ? targetIndex : nextImages.length] = repairedImage;
        return syncGeneralQuoteItemPrimaryImageFields({
          ...item,
          images: nextImages,
        });
      }),
    };
    generalQuoteRef.current = nextQuote;
    setGeneralQuote(nextQuote);
    generalQuoteImageUrlCacheRef.current.set(
      getGeneralQuoteImageCacheKey(repairedImage),
      getGeneralQuoteImageDisplayUrl(repairedImage)
    );
    setGeneralImageRepairCandidates((prev) => {
      const next = { ...prev };
      delete next[repairKey];
      return next;
    });
    setGeneralImageRepairState((prev) => ({
      ...prev,
      [repairKey]: {
        status: "repaired",
        debug: {
          ...nextDebug,
          repairResult: responseData.repairResult || "repaired",
          finalStatus: responseData.finalStatus || "repaired",
          canonicalPath: responseData.canonicalPath || responseData.canonicalFullPath || repairedImage.fullPath,
        },
        error: null,
        updatedAt: nextDebug.debugUpdatedAt,
      },
    }));
    console.log("GENERAL_QUOTE_IMAGE_REPAIR_SUCCESS", {
      workspaceUid,
      quoteId,
      itemId,
      imageId,
      imageIndex,
      selectedCandidate: responseData.selectedCandidate || selectedCandidate || null,
      canonicalFullPath: responseData.canonicalFullPath || repairedImage.fullPath,
      canonicalBucketName: responseData.canonicalBucketName || repairedImage.storageBucket,
      hasToken: getGeneralQuoteImageDisplayUrl(repairedImage).includes("token="),
    });
    notify("Image link repaired.", "success");
  } catch (error) {
    const errorDebug = {
      quoteNo: quoteState.quoteNo || selectedQuote?.quoteNo || "",
      quoteId,
      itemName: targetItem.itemName || targetItem.name || "",
      workspaceUid,
      itemId,
      imageId,
      imageIndex,
      originalUrl:
        targetImage.downloadURL ||
        targetImage.url ||
        targetImage.previewUrl ||
        targetImage.legacyUrl ||
        "",
      extractedBucket: targetImage.storageBucket || "",
      extractedPath: error?.details?.extractedPath || getGeneralQuoteImageStoragePath(targetImage),
      extractedFullPath: error?.details?.extractedPath || getGeneralQuoteImageStoragePath(targetImage),
      exactPathExists:
        typeof error?.details?.exactPathExists === "boolean"
          ? error.details.exactPathExists
          : (error?.details?.exactChecks || []).some((check) => check.fileExists),
      exactCheckResult: error?.details?.exactChecks || null,
      prefixesSearched:
        error?.details?.prefixesSearched ||
        error?.details?.candidateSearch?.prefixesSearched ||
        [],
      candidateSearchResult: error?.details?.candidateSearch || null,
      candidatesFound:
        error?.details?.candidatesFound ||
        error?.details?.candidateSearch?.candidates ||
        [],
      selectedCandidate: error?.details?.selectedCandidate || selectedCandidate || null,
      repairResult: error?.details?.repairResult || error?.details?.reason || error?.code || "failed",
      repairResponse: error?.details || null,
      canonicalPath: error?.details?.canonicalPath || error?.details?.canonicalFullPath || "",
      finalStatus: error?.details?.finalStatus || error?.details?.reason || error?.code || "failed",
      at: new Date().toISOString(),
      debugUpdatedAt: new Date().toISOString(),
    };
    generalQuoteImageRepairDebugRef.current[repairKey] = errorDebug;
    setGeneralImageRepairState((prev) => ({
      ...prev,
      [repairKey]: {
        status: "failed",
        debug: errorDebug,
        error: error?.message || "Could not repair this image.",
        updatedAt: errorDebug.debugUpdatedAt,
      },
    }));
    console.error("GENERAL_QUOTE_IMAGE_REPAIR_FAILED", {
      code: error?.code,
      message: error?.message,
      details: error?.details,
      workspaceUid,
      quoteId,
      itemId,
      imageId,
      imageIndex,
    });
    notify(
      error?.details?.reason === "object-not-found"
        ? "Image file not found in Firebase Storage. Upload a replacement image if you have it."
        : error?.message || "Could not repair this image.",
      "error",
      8000
    );
  } finally {
    setGeneralRepairingImageKey("");
  }
};

const replaceGeneralQuoteItemImage = async (
  itemId,
  imageId,
  imageIndex = -1,
  file,
  previousImage = {}
) => {
  const quoteState = generalQuoteRef.current || generalQuote || {};
  const quoteId = generalEditingQuoteId || selectedQuote?.id || quoteState.id || "";
  const targetItem = (quoteState.lineItems || []).find((item) => item.id === itemId);
  const numericImageIndex = Number(imageIndex);
  const repairKey = `${itemId}:${imageId || imageIndex}`;

  if (!workspaceUid || !auth.currentUser?.uid || !quoteId || !targetItem || !file) {
    console.error("GENERAL_QUOTE_IMAGE_REPLACEMENT_BLOCKED", {
      workspaceUid,
      uid: auth.currentUser?.uid || "",
      quoteId,
      itemId,
      imageId,
      imageIndex,
      hasTargetItem: Boolean(targetItem),
      hasFile: Boolean(file),
    });
    notify("Could not upload replacement because quotation context is missing.", "error");
    return;
  }

  const validation = validateGeneralQuoteImageFile(file);
  if (!validation.valid) {
    notify(validation.message, "warning");
    return;
  }

  setGeneralRepairingImageKey(repairKey);
  try {
    const imageIdForUpload = String(imageId || "").trim() || createClientId("general-image");
    const safeFileName = normalizeFileName(file.name || "replacement-image");
    const uploadTimestamp = Date.now();
    const storagePath = buildGeneralQuoteImageStoragePath({
      workspaceUid,
      quoteId,
      itemId,
      imageId: imageIdForUpload,
      fileName: safeFileName,
      timestamp: uploadTimestamp,
    });
    const uploadedAt = new Date().toISOString();
    const uploadedImage = await uploadGeneralQuoteImage({
      workspaceUid,
      quoteId,
      itemId,
      imageId: imageIdForUpload,
      file,
      timestamp: uploadTimestamp,
      metadata: {
        workspaceUid,
        quoteId,
        replacementFor: imageId || "",
        uploadedAt,
      },
    });
    const replacementImage = createGeneralQuoteImage({
      ...uploadedImage,
      id: imageIdForUpload,
      uploadedAt,
      replacedMissingImage: true,
      repairedFromMissing: true,
      legacyUrl:
        previousImage?.downloadURL ||
        previousImage?.url ||
        previousImage?.previewUrl ||
        previousImage?.legacyUrl ||
        "",
      legacyFullPath: getGeneralQuoteImageStoragePath(previousImage),
      legacyPath: getGeneralQuoteImageStoragePath(previousImage),
      legacyMissingImage: {
        oldUrl:
          previousImage?.downloadURL ||
          previousImage?.url ||
          previousImage?.previewUrl ||
          previousImage?.legacyUrl ||
          "",
        oldFullPath: getGeneralQuoteImageStoragePath(previousImage),
        oldBucket: previousImage?.storageBucket || "",
        repairAttemptedAt: new Date().toISOString(),
        reason: "object-not-found",
      },
      repairMethod: "replacement-upload",
      replacedAt: uploadedAt,
      repairedAt: uploadedAt,
      repairedBy: auth.currentUser.uid,
    });

    const nextQuote = {
      ...quoteState,
      lineItems: (quoteState.lineItems || []).map((item) => {
        if (item.id !== itemId) return item;
        const images = normalizeGeneralQuoteItemImages(item);
        const nextImages = [...images];
        const targetIndex =
          Number.isFinite(numericImageIndex) && numericImageIndex >= 0
            ? numericImageIndex
            : images.findIndex((image) => image.id === imageId);
        nextImages[targetIndex >= 0 ? targetIndex : nextImages.length] = replacementImage;
        return syncGeneralQuoteItemPrimaryImageFields({
          ...item,
          images: nextImages,
        });
      }),
    };

    generalQuoteRef.current = nextQuote;
    setGeneralQuote(nextQuote);
    setSelectedQuote((prev) =>
      prev?.id === quoteId
        ? {
            ...prev,
            generalQuote: nextQuote,
            updatedAt: new Date(),
          }
        : prev
    );
    generalQuoteImageUrlCacheRef.current.set(
      getGeneralQuoteImageCacheKey(replacementImage),
      getGeneralQuoteImageDisplayUrl(replacementImage)
    );
    await updateDoc(doc(db, "users", workspaceUid, "quotations", quoteId), {
      generalQuote: sanitizeGeneralQuoteForFirestore(nextQuote),
      updatedAt: serverTimestamp(),
    });
    generalQuoteImageRepairDebugRef.current[repairKey] = {
      quoteNo: quoteState.quoteNo || selectedQuote?.quoteNo || "",
      quoteId,
      itemName: targetItem.itemName || targetItem.name || "",
      itemId,
      imageId,
      imageIndex,
      originalUrl:
        previousImage?.downloadURL ||
        previousImage?.url ||
        previousImage?.previewUrl ||
        previousImage?.legacyUrl ||
        "",
      oldUrl:
        previousImage?.downloadURL ||
        previousImage?.url ||
        previousImage?.previewUrl ||
        previousImage?.legacyUrl ||
        "",
      extractedBucket: previousImage?.storageBucket || "",
      oldFullPath: getGeneralQuoteImageStoragePath(previousImage),
      extractedFullPath: getGeneralQuoteImageStoragePath(previousImage),
      exactCheckResult:
        generalQuoteImageRepairDebugRef.current[repairKey]?.exactCheckResult || null,
      candidateSearchResult:
        generalQuoteImageRepairDebugRef.current[repairKey]?.candidateSearchResult || null,
      repairResult: {
        replacementFullPath: storagePath,
        replacementBucket: firebaseApp.options.storageBucket || "",
        replacementDownloadURL: getGeneralQuoteImageDisplayUrl(replacementImage),
      },
      selectedCandidate: null,
      replacementUploaded: true,
      replacementFullPath: storagePath,
      replacementDownloadURL: getGeneralQuoteImageDisplayUrl(replacementImage),
      canonicalPath: storagePath,
      finalStatus: "replacement-uploaded",
      at: new Date().toISOString(),
      debugUpdatedAt: new Date().toISOString(),
    };
    setGeneralImageRepairCandidates((prev) => {
      const next = { ...prev };
      delete next[repairKey];
      return next;
    });
    setGeneralImageRepairState((prev) => ({
      ...prev,
      [repairKey]: {
        status: "repaired",
        debug: generalQuoteImageRepairDebugRef.current[repairKey],
        error: null,
        updatedAt: generalQuoteImageRepairDebugRef.current[repairKey]?.debugUpdatedAt,
      },
    }));
    notify("Replacement image uploaded.", "success");
  } catch (error) {
    console.error("GENERAL_QUOTE_IMAGE_REPLACEMENT_FAILED", {
      code: error?.code,
      message: error?.message,
      workspaceUid,
      quoteId,
      itemId,
      imageId,
      imageIndex,
    });
    notify(
      error?.code === "storage-limit-reached" || error?.code === "invalid-image-file"
        ? error.message
        : "Could not upload replacement image.",
      "error",
      7000
    );
  } finally {
    setGeneralRepairingImageKey("");
  }
};

const selectGeneralQuoteImageRepairCandidate = (
  itemId,
  imageId,
  imageIndex = -1,
  candidate
) => repairGeneralQuoteItemImage(itemId, imageId, imageIndex, candidate);

const copyGeneralQuoteImageDebugJson = async (
  itemId,
  imageId,
  imageIndex = -1,
  image = {}
) => {
  const quoteState = generalQuoteRef.current || generalQuote || {};
  const targetItem = (quoteState.lineItems || []).find((item) => item.id === itemId) || {};
  const repairKey = `${itemId}:${imageId || imageIndex}`;
  const stateEntry = generalImageRepairState[repairKey] || {};
  const debugEntry = stateEntry.debug || generalQuoteImageRepairDebugRef.current[repairKey] || {};
  const candidateEntry = generalImageRepairCandidates[repairKey] || {};
  const payload = {
    quoteNo: quoteState.quoteNo || selectedQuote?.quoteNo || "",
    quoteId: generalEditingQuoteId || selectedQuote?.id || quoteState.id || "",
    itemName: targetItem.itemName || targetItem.name || "",
    workspaceUid,
    itemId,
    imageId,
    imageIndex,
    originalUrl:
      image?.downloadURL || image?.url || image?.previewUrl || image?.legacyUrl || "",
    oldUrl:
      debugEntry.oldUrl ||
      image?.legacyUrl ||
      image?.legacyMissingImage?.oldUrl ||
      image?.downloadURL ||
      image?.url ||
      image?.previewUrl ||
      "",
    extractedBucket: image?.storageBucket || "",
    oldFullPath:
      debugEntry.oldFullPath ||
      image?.legacyFullPath ||
      image?.legacyPath ||
      image?.legacyMissingImage?.oldFullPath ||
      getGeneralQuoteImageStoragePath(image),
    extractedPath: debugEntry.extractedPath || getGeneralQuoteImageStoragePath(image),
    extractedFullPath: debugEntry.extractedFullPath || getGeneralQuoteImageStoragePath(image),
    exactPathExists: debugEntry.exactPathExists ?? null,
    exactCheckResult: debugEntry.exactCheckResult || null,
    prefixesSearched: debugEntry.prefixesSearched || candidateEntry.prefixesSearched || [],
    candidateSearchResult: debugEntry.candidateSearchResult || null,
    candidatesFound: debugEntry.candidatesFound || candidateEntry.candidates || [],
    selectedCandidate: debugEntry.selectedCandidate || null,
    repairResult: debugEntry.repairResult || null,
    repairResponse: debugEntry.repairResponse || null,
    replacementUploaded:
      debugEntry.replacementUploaded === true || image?.replacedMissingImage === true,
    replacementFullPath:
      debugEntry.replacementFullPath ||
      (image?.replacedMissingImage ? getGeneralQuoteImageStoragePath(image) : ""),
    replacementDownloadURL:
      debugEntry.replacementDownloadURL ||
      (image?.replacedMissingImage ? getGeneralQuoteImageDisplayUrl(image) : ""),
    canonicalPath:
      debugEntry.canonicalPath ||
      (image?.replacedMissingImage ? getGeneralQuoteImageStoragePath(image) : ""),
    uiStatus: stateEntry.status || "",
    error: stateEntry.error || "",
    finalStatus: debugEntry.finalStatus || image?.resolveStatus || "",
    debugUpdatedAt: debugEntry.debugUpdatedAt || stateEntry.updatedAt || "",
  };

  try {
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    notify("Image debug JSON copied.", "success");
  } catch (error) {
    console.log("IMAGE_DEBUG_JSON", payload);
    notify("Clipboard unavailable. Debug JSON was logged to console.", "info", 6000);
  }
};

const removeGeneralQuoteItem = async (itemId) => {
  const currentQuote = generalQuoteRef.current || generalQuote;
  const target = (currentQuote.lineItems || []).find((item) => item.id === itemId);
  if (!target) return;

  if ((currentQuote.lineItems || []).length <= 1) {
    return;
  }

  const targetImages = normalizeGeneralQuoteItemImages(target);
  const archivedImages = targetImages.map((image) =>
    archiveGeneralQuoteImageMetadata(image, {
      itemId,
      itemName: target.itemName || target.name || "",
      quoteId: generalEditingQuoteId || selectedQuote?.id || currentQuote.id || "",
      quoteNo: currentQuote.quoteNo || selectedQuote?.quoteNo || "",
      reason: "item_removed_from_quote",
      removedBy: auth.currentUser?.uid || "",
    })
  );
  const existingRemovedImages = Array.isArray(target.removedImages)
    ? target.removedImages.map((image) =>
        createGeneralQuoteImage({
          ...image,
          removedFromQuote: true,
          removedFromItemId: image.removedFromItemId || itemId,
          removedFromItemName:
            image.removedFromItemName || target.itemName || target.name || "",
        })
      )
    : [];

  const nextQuote = {
    ...currentQuote,
    removedImages: [
      ...(Array.isArray(currentQuote.removedImages) ? currentQuote.removedImages : []),
      ...existingRemovedImages,
      ...archivedImages,
    ],
    lineItems: (currentQuote.lineItems || []).filter((item) => item.id !== itemId),
  };
  generalQuoteRef.current = nextQuote;
  setGeneralQuote(nextQuote);
  archivedImages
    .filter((image) => image.fileId)
    .forEach((image) => {
      archiveManagedStorageFile({
        workspaceUid,
        fileId: image.fileId,
        removedBy: auth.currentUser?.uid || "",
        removalReason: "item_removed_from_quote",
        linkedRecords: [],
      }).catch((error) => {
        if (isDevelopmentMode()) {
          console.warn("GENERAL_QUOTE_STORAGE_MANIFEST_ARCHIVE_FAILED", {
            fileId: image.fileId,
            code: error?.code,
            message: error?.message,
          });
        }
      });
    });
  if (isDevelopmentMode() && archivedImages.length) {
    console.info("GENERAL_QUOTE_ITEM_IMAGES_DETACHED_FROM_QUOTE", {
      quoteId: generalEditingQuoteId || selectedQuote?.id || currentQuote.id || "",
      quoteNo: currentQuote.quoteNo || selectedQuote?.quoteNo || "",
      itemId,
      imageCount: archivedImages.length,
      retentionDays: GENERAL_QUOTE_IMAGE_TTL_DAYS,
    });
  }
};

const testGeneralQuoteFirestoreWrite = async () => {
  if (!workspaceUid || !auth.currentUser) {
    const errorMessage = "Missing workspace or signed-in user";
    setGeneralQuoteDebugState((prev) => ({
      ...prev,
      directFirestoreTest: {
        state: "failed",
        message: errorMessage,
        at: new Date().toISOString(),
      },
    }));
    notify("Debug write failed: please sign in again.", "error");
    return;
  }

  const debugRef = doc(
    db,
    "users",
    workspaceUid,
    "quotations",
    "debug_general_quote_test"
  );
  const payload = {
    quoteType: GENERAL_QUOTE_TYPE,
    customerName: "Debug General Customer",
    generalQuote: {
      items: [
        {
          id: "debug-item-1",
          name: "Debug Sofa",
          qty: 1,
          rate: 1000,
          total: 1000,
          images: [],
        },
      ],
    },
    discountType: "flat",
    discountValue: 100,
    subtotal: 1000,
    grandTotal: 900,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  setGeneralQuoteDebugState((prev) => ({
    ...prev,
    directFirestoreTest: {
      state: "writing",
      path: debugRef.path,
      at: new Date().toISOString(),
    },
  }));

  try {
    console.log("DIRECT GENERAL FIRESTORE TEST START", {
      workspaceUid,
      uid: auth.currentUser?.uid,
      path: debugRef.path,
    });
    await setDoc(debugRef, payload, { merge: true });
    const readback = await getDoc(debugRef);
    const readBackData = readback.exists() ? readback.data() : null;
    console.log("DIRECT GENERAL FIRESTORE TEST SUCCESS", readBackData);
    setGeneralQuoteDebugState((prev) => ({
      ...prev,
      directFirestoreTest: {
        state: "passed",
        path: debugRef.path,
        exists: readback.exists(),
        keys: readback.exists() ? Object.keys(readback.data() || {}) : [],
        at: new Date().toISOString(),
      },
    }));
    notify("Debug Firestore write passed", "success");
  } catch (error) {
    console.error("DIRECT GENERAL FIRESTORE TEST FAILED", {
      code: error?.code,
      message: error?.message,
      stack: error?.stack,
      workspaceUid,
      uid: auth.currentUser?.uid,
      path: debugRef.path,
    });
    setGeneralQuoteDebugState((prev) => ({
      ...prev,
      directFirestoreTest: {
        state: "failed",
        path: debugRef.path,
        code: error?.code || "",
        message: error?.message || String(error),
        at: new Date().toISOString(),
      },
    }));
    notify("Debug Firestore write failed", "error");
  }
};

const testGeneralQuoteImageUpload = async () => {
  const file = generalQuoteDebugUploadFile;
  if (!workspaceUid || !auth.currentUser || !file) {
    notify("Choose a test image and make sure you are signed in.", "warning");
    return;
  }

  const validation = validateGeneralQuoteImageFile(file);
  if (!validation.valid) {
    notify(validation.message, "warning");
    return;
  }

  const debugQuoteId = "debug_general_quote_test";
  const debugItemId = "debug-item-1";
  const debugImageId = createClientId("general-image-debug");
  const uploadTimestamp = Date.now();
  const storagePath = buildGeneralQuoteImageStoragePath({
    workspaceUid,
    quoteId: debugQuoteId,
    itemId: debugItemId,
    imageId: debugImageId,
    fileName: file.name || "debug-image",
    timestamp: uploadTimestamp,
  });

  setGeneralQuoteDebugState((prev) => ({
    ...prev,
    directStorageTest: {
      state: "uploading",
      storagePath,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      at: new Date().toISOString(),
    },
  }));

  try {
    console.log("DIRECT GENERAL IMAGE UPLOAD START", {
      workspaceUid,
      uid: auth.currentUser?.uid,
      storagePath,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
    });
    const uploadedImage = await uploadGeneralQuoteImage({
      workspaceUid,
      quoteId: debugQuoteId,
      itemId: debugItemId,
      imageId: debugImageId,
      file,
      timestamp: uploadTimestamp,
      metadata: {
        workspaceUid,
        purpose: "general_quote_debug_upload",
      },
    });
    const downloadUrl = getGeneralQuoteImageDisplayUrl(uploadedImage);
    console.log("DIRECT GENERAL IMAGE UPLOAD SUCCESS", { downloadURL: downloadUrl });
    setGeneralQuoteDebugState((prev) => ({
      ...prev,
      directStorageTest: {
        state: "passed",
        storagePath,
        hasDownloadUrl: Boolean(downloadUrl),
        downloadUrl,
        at: new Date().toISOString(),
      },
    }));
    notify("Debug image upload passed", "success");
  } catch (error) {
    console.error("DIRECT GENERAL IMAGE UPLOAD FAILED", {
      code: error?.code,
      message: error?.message,
      stack: error?.stack,
      storagePath,
      workspaceUid,
      uid: auth.currentUser?.uid,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
    });
    setGeneralQuoteDebugState((prev) => ({
      ...prev,
      directStorageTest: {
        state: "failed",
        storagePath,
        code: error?.code || "",
        message: error?.message || String(error),
        at: new Date().toISOString(),
      },
    }));
    raiseFirebaseHealthWarning("storage", error, {
      storagePath,
      source: "directGeneralQuoteImageUpload",
    });
    notify(
      isFirebaseStorageBillingError(error)
        ? "Firebase Storage billing or bucket access issue detected. Check Firebase Storage billing/bucket configuration."
        : "Debug image upload failed",
      "error",
      isFirebaseStorageBillingError(error) ? 7000 : 3800
    );
  }
};
/* ---------------- LOAD QUOTE SAFELY (EDIT-LOCKED) ---------------- */
/* ---------------- LOAD QUOTE (EDIT-LOCKED) ---------------- */
const loadQuoteForEdit = (quote) => {
  if (!quote?.id) return;

  if (tab === "quote" && quoteSubTab === GENERAL_QUOTE_TYPE && generalQuoteIsDirty) {
    persistGeneralQuoteDraft("route-change");
  }
  if (tab === "quote" && quoteSubTab === CURTAIN_QUOTE_TYPE && curtainQuoteIsDirty) {
    persistCurtainQuoteDraft("route-change");
  }

  if (tab === "quote" && quoteSubTab === CURTAIN_QUOTE_TYPE && curtainQuoteIsDirty) {
    persistCurtainQuoteDraft("route-change");
  }

  console.log("LOAD FOR EDIT CALLED");
  setPendingCrmLeadId(quote?.crm?.leadId || "");
  setPendingRecalculationAudit(null);
  setPendingLegacyCalculationSnapshot(null);
  setLegacyRecalculationPreview(null);
  setLegacyRecalculationModalOpen(false);
  setLegacyRecalculationRevisionRequest(null);
  legacyRecalculationRevisionSaveRef.current = "";

  if (resolveQuoteType(quote) === GENERAL_QUOTE_TYPE) {
    if (!canAccessGeneralQuotes) {
      alert("General quotations are not enabled for this account plan.");
      return;
    }
    applyGeneralQuoteState(quote);
    return;
  }

  if (!canAccessCurtainQuotes) {
    alert("Curtain quotations are not enabled for this account plan.");
    return;
  }

  setRoutedTab("quote", CURTAIN_QUOTE_TYPE);
  setEditingQuoteId(quote.id);
  setGeneralEditingQuoteId(null);
  setSelectedQuote({
    id: quote.id,
    ...quote,
    quoteType: CURTAIN_QUOTE_TYPE,
  });

  /* ================= BASIC FIELDS ================= */
  setCustomerName(quote.customerName || "");
  setQuoteCustomerLink({
    customerId: quote.customerId || quote.crm?.customerId || "",
    customerKey:
      quote.customerKey ||
      quote.crm?.customerKey ||
      quote.customerId ||
      quote.crm?.customerId ||
      "",
    customerName: quote.customerName || "",
    phone: quote.customerPhone || quote.crm?.customerPhone || "",
    whatsapp: quote.customerWhatsapp || quote.crm?.customerWhatsapp || "",
    email: quote.customerEmail || quote.email || quote.crm?.contact?.email || "",
    address:
      quote.customerAddress || quote.address || quote.crm?.contact?.address || "",
    companyName: quote.customerCompanyName || quote.companyName || "",
  });
  setQuoteNo(quote.quoteNo || "");
  const loadedQuoteDate = resolveQuoteDateForInput(quote, todayISO());
  setDate(loadedQuoteDate);

  /* =================  RESTORE REPEAT FIRST ================= */
  const cleaned = mergeRepeatSettingsFromFabricSummaryMeta(
    quote.repeatSettings || {},
    quote.fabricSummaryMeta || {}
  );

  setRepeatSettings(cleaned);
  setFabricSummaryMeta(
    normalizeFabricSummaryMetaWithRepeatSettings(quote.fabricSummaryMeta, cleaned)
  );

  /* ================= ROWS ================= */
const safeRows = Array.isArray(quote.rows)
  ? quote.rows.map((r, index) =>
      hydrateCurtainRowCalculationSnapshot(
        sanitizeCurtainRowEditableTextFields(withCurtainRowId({ ...r }, index))
      )
    )
  : [];

if (IS_DEV_UI_ENABLED) {
  const hasLegacyCalculationRows = safeRows.some(
    (row) => row.calculationSnapshot?.requiresRecalculation === true
  );
  console.log(
    "STITCH_TRACE_SAVED_QUOTE_ROWS",
    safeRows.map((row, index) =>
      buildWorkshopStitchTraceRow(row, stitchingRates, getRowProductConfig(row), {
        stage: "saved_quote_rows",
        index,
      })
    )
  );
  console.log("QUOTE_REOPEN_CALC_TRACE", {
    quoteNo: quote.quoteNo || "",
    mode: hasLegacyCalculationRows ? "legacy-snapshot-awaiting-recalculate" : "hydrate-snapshot",
    rows: safeRows.map((row) => ({
      item: row.item,
      width: row.width,
      height: row.height,
      fabricWidth: row.fabricWidthIn,
      usableFabricWidth: row.calculationSnapshot?.usableFabricWidthIn,
      fullness: row.calculationSnapshot?.fullness,
      rawPanelCount: row.calculationSnapshot?.rawPieces,
      finalPieces: row.calculationSnapshot?.pieces,
      orderedFabricMeters: row.calculationSnapshot?.orderedFabricMeters,
      lineTotal: row.calculationSnapshot?.lineTotal,
      recalculated: row.__recalculated === true,
      calculationVersion: row.calculationSnapshot?.calculationVersion || "",
      requiresRecalculation:
        row.calculationSnapshot?.requiresRecalculation === true,
    })),
  });
}

replaceCurtainRows(safeRows, QUOTE_RAW_FIELD_MUTATION_SOURCES.SERVER_LOAD, {
  quoteId: quote.id || "",
});
  // Workshop export must follow the current visible rows, not a stale saved
  // effectiveRows snapshot from an earlier edit/export cycle.
  setEffectiveRows(safeRows);
  setLeftoverUsageLog(quote.leftoverUsageLog || []);

  /* ================= DISCOUNT ================= */
  const loadedDiscount = normalizeQuoteDiscount(
    quote,
    "curtain_quote_open"
  );
  const loadedGstRate = normalizeCurtainGstRate(
    quote.quoteGstRate ?? quote.gstRate ?? 0
  );
  const loadedQuoteFullnessOverrides = normalizeQuoteFullnessOverrides(
    quote.quoteFullnessOverrides || quote.fullnessOverrides || {}
  );
  setSummaryDiscountMode(getSummaryDiscountModeFromType(loadedDiscount.discountType));
  setSummaryDiscountValue(String(loadedDiscount.discountValue));
  setCurtainGstRate(loadedGstRate);
  quoteFullnessOverridesRef.current = loadedQuoteFullnessOverrides;
  setQuoteFullnessOverrides(loadedQuoteFullnessOverrides);
  setQuoteFullnessOverrideInputs({});

  /* ================= STATUS ================= */
  setSelectedQuoteWorkStage(quote.workStage || "quote_given");
  setSelectedQuoteStatus(quote.status || "draft");
  setSelectedInstallationDate(quote.installationDate || null);
  setUseHalfPieceReuse(quote.useHalfPieceReuse !== false);
  setPassHalfPieceSavingsToCustomer(quote.passHalfPieceSavingsToCustomer === true);
  markCurtainQuoteBaseline(
    {
      quoteType: CURTAIN_QUOTE_TYPE,
      quoteId: quote.id || "new",
      editingQuoteId: quote.id || "",
      selectedQuoteId: quote.id || "",
      customerName: quote.customerName || "",
      quoteCustomerLink: {
        customerId: quote.customerId || quote.crm?.customerId || "",
        customerName: quote.customerName || "",
        phone: quote.customerPhone || quote.crm?.customerPhone || "",
        whatsapp: quote.customerWhatsapp || quote.crm?.customerWhatsapp || "",
        email: quote.customerEmail || quote.email || quote.crm?.contact?.email || "",
        address:
          quote.customerAddress || quote.address || quote.crm?.contact?.address || "",
        companyName: quote.customerCompanyName || quote.companyName || "",
      },
      quoteNo: quote.quoteNo || "",
      quoteDate: loadedQuoteDate,
      date: loadedQuoteDate,
      quotationDate: loadedQuoteDate,
      rows: safeRows,
      repeatSettings: cleaned,
      quoteFullnessOverrides: loadedQuoteFullnessOverrides,
      fabricSummaryMeta: normalizeFabricSummaryMetaWithRepeatSettings(
        quote.fabricSummaryMeta,
        cleaned
      ),
      discountType: loadedDiscount.discountType,
      discountValue: loadedDiscount.discountValue,
      quoteGstRate: loadedGstRate,
      gstRate: loadedGstRate,
      gstAmount: Number(quote.gstAmount || 0),
      taxableAmount: Number(quote.taxableAmount || 0),
      finalTotal: Number(quote.finalTotal || quote.grandTotal || 0),
      grandTotal: Number(quote.grandTotal || quote.finalTotal || 0),
      summaryDiscountMode: getSummaryDiscountModeFromType(loadedDiscount.discountType),
      summaryDiscountValue: String(loadedDiscount.discountValue),
      selectedQuoteWorkStage: quote.workStage || "quote_given",
      selectedQuoteStatus: quote.status || "draft",
      selectedInstallationDate: quote.installationDate || null,
      useHalfPieceReuse: quote.useHalfPieceReuse !== false,
      passHalfPieceSavingsToCustomer: quote.passHalfPieceSavingsToCustomer === true,
      pendingCrmLeadId: quote?.crm?.leadId || "",
    },
    "saved"
  );

  /* ================= NAVIGATION ================= */
  setTab("quote");
};

const openCrmQuoteForDeal = (deal) => {
  if (!canViewQuotes) {
    alert("You do not have permission to view quotations in this workspace.");
    return;
  }

  const primaryLinkedQuote = getPrimaryCrmLinkedQuote(deal?.linkedQuotes || []);
  const quoteId =
    deal?.crmRecordType === "lead"
      ? primaryLinkedQuote?.quoteId || ""
      : deal?.id || "";

  const resolvedQuoteId =
    quoteId ||
    savedQuotes.find(
      (quote) =>
        primaryLinkedQuote?.quoteNo &&
        String(quote.quoteNo || "") === String(primaryLinkedQuote.quoteNo || "")
    )?.id ||
    "";

  if (!resolvedQuoteId) return;
  loadFullQuote(resolvedQuoteId);
};

const startQuoteFromCrmLead = (deal) => {
  if (!canCreateQuotes) {
    alert("You do not have permission to create quotations in this workspace.");
    return;
  }

  if (!deal?.id) return;
  const requestedQuoteType = deal.pipelineType || deal.quoteTypeResolved || CURTAIN_QUOTE_TYPE;

  if (requestedQuoteType === GENERAL_QUOTE_TYPE && !canAccessGeneralQuotes) {
    alert("General quotations are not enabled for this account plan.");
    return;
  }

  if (requestedQuoteType !== GENERAL_QUOTE_TYPE && !canAccessCurtainQuotes) {
    alert("Curtain quotations are not enabled for this account plan.");
    return;
  }

  const leadContact = deal.crmContact || deal.contact || {};
  const leadCustomerName =
    leadContact.name ||
    deal.customerLabel ||
    deal.companyOrProject ||
    deal.crmTitle ||
    "";
  const leadCustomerLink = {
    customerId: deal.customerId || deal.crm?.customerId || "",
    customerName: leadCustomerName,
    phone: leadContact.phone || deal.customerPhone || deal.crm?.customerPhone || "",
    whatsapp: leadContact.whatsapp || deal.customerWhatsapp || "",
    email: leadContact.email || deal.customerEmail || "",
    address: leadContact.address || deal.customerAddress || "",
    companyName: deal.companyOrProject || deal.customerCompanyName || "",
  };

  setPendingCrmLeadId(deal.id);
  if (tab === "quote" && quoteSubTab === GENERAL_QUOTE_TYPE && generalQuoteIsDirty) {
    persistGeneralQuoteDraft("route-change");
  }
  setTab("quote");
  setSelectedQuote(null);
  setSelectedQuoteStatus(deal.status || "draft");
  setSelectedQuoteWorkStage(
    deal.crmStageId ||
      getDefaultCrmStageId(crmPipelines, deal.pipelineType || deal.quoteTypeResolved)
  );
  setSelectedInstallationDate(deal.installationDate || null);
  setIsNewQuote(true);

  if (requestedQuoteType === GENERAL_QUOTE_TYPE) {
    const nextGeneralQuote = normalizeGeneralQuote(
      {
        ...createDefaultGeneralQuote({
          currency,
          businessName,
          shopAddress,
          businessPhone,
          businessEmail,
        }),
        title: deal.crmTitle || "Quotation",
        customerId: leadCustomerLink.customerId,
        customerName: leadCustomerLink.customerName,
        customerPhone: leadCustomerLink.phone,
        customerWhatsapp: leadCustomerLink.whatsapp,
        customerEmail: leadCustomerLink.email,
        customerAddress: leadCustomerLink.address,
        customerCompanyName: leadCustomerLink.companyName,
        customerLedgerLinked: Boolean(leadCustomerLink.customerId),
        quoteFor: {
          name: leadContact.name || "",
          company: deal.companyOrProject || "",
          address: leadContact.address || "",
          phone: leadContact.phone || leadContact.whatsapp || "",
          email: leadContact.email || "",
        },
        notes: deal.crmNotes || "",
      },
      {
        currency,
        businessName,
        shopAddress,
        businessPhone,
        businessEmail,
      }
    );

    setRoutedTab("quote", GENERAL_QUOTE_TYPE);
    setEditingQuoteId(null);
    setGeneralEditingQuoteId(null);
    setGeneralQuote(nextGeneralQuote);
    markGeneralQuoteBaseline(nextGeneralQuote, "idle");
    setCustomerName(leadCustomerName);
    setQuoteCustomerLink(leadCustomerLink);
    setQuoteNo("");
    setDate(resolveQuoteDateForInput(nextGeneralQuote, todayISO()));
    setAutoSaveStatus("idle");
    return;
  }

  setRoutedTab("quote", CURTAIN_QUOTE_TYPE);
  setEditingQuoteId(null);
  setGeneralEditingQuoteId(null);
  setCustomerName(leadCustomerName);
  setQuoteCustomerLink(leadCustomerLink);
  setQuoteNo("");
  setDate(todayISO());
  const defaultCurtainRows = [createDefaultCurtainQuoteRow("W1")];
  replaceCurtainRows(
    defaultCurtainRows,
    QUOTE_RAW_FIELD_MUTATION_SOURCES.BULK_REPLACE,
    { quoteId: "new" }
  );
  setRepeatSettings({});
  setFabricSummaryMeta({});
  setEffectiveRows([]);
  setLeftoverUsageLog([]);
  setSummaryDiscountMode("%");
  setSummaryDiscountValue("0");
  setCurtainGstRate(0);
  quoteFullnessOverridesRef.current = {};
  setQuoteFullnessOverrides({});
  setQuoteFullnessOverrideInputs({});
  setUseHalfPieceReuse(true);
  setPassHalfPieceSavingsToCustomer(false);
  const newCurtainQuoteDate = todayISO();
  markCurtainQuoteBaseline(
    {
      quoteType: CURTAIN_QUOTE_TYPE,
      quoteId: "new",
      editingQuoteId: "",
      selectedQuoteId: "",
      customerName: leadCustomerName,
      quoteCustomerLink: leadCustomerLink,
      quoteNo: "",
      quoteDate: newCurtainQuoteDate,
      date: newCurtainQuoteDate,
      quotationDate: newCurtainQuoteDate,
      rows: defaultCurtainRows,
      repeatSettings: {},
      quoteFullnessOverrides: {},
      fabricSummaryMeta: {},
      discountType: "percent",
      discountValue: 0,
      gstRate: 0,
      gstAmount: 0,
      taxableAmount: 0,
      finalTotal: 0,
      grandTotal: 0,
      summaryDiscountMode: "%",
      summaryDiscountValue: "0",
      selectedQuoteWorkStage:
        deal.crmStageId ||
        getDefaultCrmStageId(crmPipelines, deal.pipelineType || deal.quoteTypeResolved),
      selectedQuoteStatus: deal.status || "draft",
      selectedInstallationDate: deal.installationDate || null,
      useHalfPieceReuse: true,
      passHalfPieceSavingsToCustomer: false,
      pendingCrmLeadId: deal.id,
    },
    "idle"
  );
};

const openQuoteLinkModal = useCallback(
  (deal) => {
    if (!deal?.id) return;

    if (deal.crmRecordType !== "lead") {
      notify("Open the CRM lead record before linking a quote.", "info");
      return;
    }

    const primaryLinkedQuote = getPrimaryCrmLinkedQuote(
      normalizeCrmLinkedQuotes(deal)
    );
    setQuoteLinkModalState({ leadId: deal.id });
    setQuoteLinkSearch(
      primaryLinkedQuote?.quoteNo ||
        deal.quoteNo ||
        deal.crmContact?.name ||
        deal.customerLabel ||
        ""
    );
    setQuoteLinkTypeFilter("all");
    setQuoteLinkMinAmount("");
    setQuoteLinkMaxAmount("");
  },
  [notify]
);

const closeQuoteLinkModal = useCallback(() => {
  setQuoteLinkModalState(null);
  setQuoteLinkSearch("");
  setQuoteLinkTypeFilter("all");
  setQuoteLinkMinAmount("");
  setQuoteLinkMaxAmount("");
  setIsLinkingQuote(false);
}, []);

const openQuoteFromLinkModal = (quote) => {
  if (!quote?.id) return;
  closeQuoteLinkModal();
  loadFullQuote(quote);
};

const performLinkExistingQuote = useCallback(
  async (leadLike, quote) => {
    if (!workspaceUid || !leadLike?.id || !quote?.id) return;

    setIsLinkingQuote(true);
    try {
      const quoteMeta = getQuoteLinkDisplayData(quote);
      const linkedQuoteValue = Number(quoteMeta.totalValue || 0);
      const existingLinkedQuotes = normalizeCrmLinkedQuotes(leadLike);
      const isAlreadyLinkedToLead = existingLinkedQuotes.some(
        (linkedQuote) =>
          String(linkedQuote.quoteId || "") === String(quote.id || "") ||
          (quoteMeta.quoteNo &&
            String(linkedQuote.quoteNo || "") === String(quoteMeta.quoteNo || ""))
      );
      const linkedQuotes = normalizeCrmLinkedQuotes(leadLike, {
        linkedQuotes: [
          ...existingLinkedQuotes
            .filter(
              (linkedQuote) =>
                String(linkedQuote.quoteId || "") !== String(quote.id || "")
            )
            .map((linkedQuote) => ({ ...linkedQuote, isPrimary: false })),
          {
            quoteId: quote.id,
            quoteNo: quoteMeta.quoteNo || "",
            type: quoteMeta.quoteType,
            value: linkedQuoteValue,
            isPrimary: true,
          },
        ],
      });
      const leadRef = doc(db, "users", workspaceUid, "crm_leads", leadLike.id);
      const leadPatch = {
        linkedQuotes,
        ...createLegacyCrmQuoteLinkDeletePatch(),
        hasQuote: true,
        expectedValue: linkedQuoteValue || Number(leadLike.expectedValue || 0),
        finalValue: linkedQuoteValue || Number(leadLike.finalValue || 0),
        ...(isDevRuntime
          ? {
              _lastWriteSource: "linkQuoteToDeal.targetLead",
              _lastWriteAt: serverTimestamp(),
            }
          : {}),
        updatedAt: serverTimestamp(),
      };

      const previousLead = findLinkedCrmLeadForQuote(quote);
      if (previousLead?.id && previousLead.id !== leadLike.id) {
        const previousLinkedQuotes = normalizeCrmLinkedQuotes(previousLead).filter(
          (linkedQuote) =>
            String(linkedQuote.quoteId || "") !== String(quote.id || "") &&
            String(linkedQuote.quoteNo || "") !== String(quoteMeta.quoteNo || "")
        );
        const previousLeadRef = doc(
          db,
          "users",
          workspaceUid,
          "crm_leads",
          previousLead.id
        );
        await setDoc(
          previousLeadRef,
          {
            linkedQuotes: normalizeCrmLinkedQuotes(previousLead, {
              linkedQuotes: previousLinkedQuotes,
            }),
            hasQuote: previousLinkedQuotes.length > 0,
            ...createLegacyCrmQuoteLinkDeletePatch(),
            ...(isDevRuntime
              ? {
                  _lastWriteSource: "linkQuoteToDeal.previousLead",
                  _lastWriteAt: serverTimestamp(),
                }
              : {}),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
        applyCrmLeadLocalPatch(previousLead.id, {
          linkedQuotes: normalizeCrmLinkedQuotes(previousLead, {
            linkedQuotes: previousLinkedQuotes,
          }),
          hasQuote: previousLinkedQuotes.length > 0,
          updatedAt: new Date(),
        });
      }

      await setDoc(leadRef, leadPatch, { merge: true });

      const linkedBy = user?.uid || "";
      const linkedByEmail = user?.email || "";
      const quoteUpdate = {
        "crm.leadId": leadLike.id,
        "crm.linkedAt": serverTimestamp(),
        "crm.linkedBy": linkedBy,
        "crm.linkedByEmail": linkedByEmail,
        ...createLegacyQuoteCrmLinkDeletePatch(),
        updatedAt: serverTimestamp(),
      };

      if (previousLead?.id && previousLead.id !== leadLike.id) {
        quoteUpdate["crm.previousLinkedLeadId"] = previousLead.id;
        quoteUpdate["crm.previousLinkedLeadTitle"] =
          previousLead.title || previousLead.contact?.name || "";
      }

      await updateDoc(doc(db, "users", workspaceUid, "quotations", quote.id), quoteUpdate);

      const localLinkedAt = new Date();
      applyCrmLeadLocalPatch(leadLike.id, {
        ...leadPatch,
        updatedAt: localLinkedAt,
      });
      applyQuoteLocalPatch(quote.id, {
        crm: stripLegacyCrmQuoteLinkFields({
          ...(quote.crm || {}),
          leadId: leadLike.id,
          linkedAt: localLinkedAt,
          linkedBy,
          linkedByEmail,
          ...(previousLead?.id && previousLead.id !== leadLike.id
            ? {
                previousLinkedLeadId: previousLead.id,
                previousLinkedLeadTitle:
                  previousLead.title || previousLead.contact?.name || "",
              }
            : {}),
        }),
        updatedAt: localLinkedAt,
      });

      setSelectedCrmDealId(leadLike.id);
      setCrmDetailsSavedAt(localLinkedAt);
      closeQuoteLinkModal();
      notify(isAlreadyLinkedToLead ? "Quote already linked" : "Quote linked successfully", "success");
    } catch (error) {
      console.error("Failed to link existing quote:", error);
      notify("Could not link quote. Please try again.", "error");
    } finally {
      setIsLinkingQuote(false);
    }
  },
  [
    applyCrmLeadLocalPatch,
    applyQuoteLocalPatch,
    closeQuoteLinkModal,
    findLinkedCrmLeadForQuote,
    getQuoteLinkDisplayData,
    notify,
    user?.email,
    user?.uid,
    workspaceUid,
  ]
);

const requestLinkExistingQuote = useCallback(
  (leadLike, quote) => {
    if (!leadLike?.id || !quote?.id) return;

    const linkedQuotes = normalizeCrmLinkedQuotes(leadLike);
    const alreadyLinkedToThisLead = linkedQuotes.some(
      (linkedQuote) =>
        String(linkedQuote.quoteId || "") === String(quote.id || "") ||
        (quote.quoteNo &&
          String(linkedQuote.quoteNo || "") === String(quote.quoteNo || ""))
    );
    if (alreadyLinkedToThisLead) {
      notify("This quote is already linked to this lead.", "info");
      return;
    }
    const primaryLinkedQuote = getPrimaryCrmLinkedQuote(linkedQuotes);
    const replacingCurrent =
      Boolean(primaryLinkedQuote?.quoteId || primaryLinkedQuote?.quoteNo) &&
      primaryLinkedQuote?.quoteId !== String(quote.id || "") &&
      primaryLinkedQuote?.quoteNo !== String(quote.quoteNo || "");
    const linkedLead = findLinkedCrmLeadForQuote(quote);
    const linkedElsewhere = linkedLead?.id && linkedLead.id !== leadLike.id;

    if (replacingCurrent || linkedElsewhere) {
      const description = linkedElsewhere
        ? "This quote is already linked to another lead."
        : "This lead already has a linked quote. Replace it?";

      openConfirmDialog({
        title: linkedElsewhere ? "Quote already linked" : "Replace linked quote?",
        description,
        confirmLabel: linkedElsewhere ? "Link Anyway" : "Replace Quote",
        cancelLabel: "Cancel",
        tone: linkedElsewhere ? "warning" : "danger",
        onConfirm: () => performLinkExistingQuote(leadLike, quote),
      });
      return;
    }

    performLinkExistingQuote(leadLike, quote);
  },
  [findLinkedCrmLeadForQuote, notify, openConfirmDialog, performLinkExistingQuote]
);

const performUnlinkQuoteFromLead = useCallback(
  async (leadLike) => {
    if (!workspaceUid || !leadLike?.id) return;

    const linkedQuote = findLinkedQuoteForLead(leadLike);
    if (!linkedQuote?.id) {
      notify("This lead does not have a linked quote.", "info");
      return;
    }

    setIsLinkingQuote(true);
    try {
      const currentLinkedQuotes = normalizeCrmLinkedQuotes(leadLike);
      const primaryLinkedQuote = getPrimaryCrmLinkedQuote(currentLinkedQuotes);
      const remainingLinkedQuotes = normalizeCrmLinkedQuotes(leadLike, {
        linkedQuotes: currentLinkedQuotes.filter(
          (linkedQuote) =>
            String(linkedQuote.quoteId || "") !== String(primaryLinkedQuote?.quoteId || "") &&
            String(linkedQuote.quoteNo || "") !== String(primaryLinkedQuote?.quoteNo || "")
        ),
      });
      const leadRef = doc(db, "users", workspaceUid, "crm_leads", leadLike.id);
      await updateDoc(leadRef, {
        linkedQuotes: remainingLinkedQuotes,
        ...createLegacyCrmQuoteLinkDeletePatch(),
        hasQuote: remainingLinkedQuotes.length > 0,
        ...(isDevRuntime
          ? {
              _lastWriteSource: "unlinkQuoteFromDeal",
              _lastWriteAt: serverTimestamp(),
            }
          : {}),
        updatedAt: serverTimestamp(),
      });

      const quoteLeadId = String(linkedQuote.crm?.leadId || "");
      if (quoteLeadId === leadLike.id) {
        await updateDoc(doc(db, "users", workspaceUid, "quotations", linkedQuote.id), {
          "crm.leadId": deleteField(),
          "crm.linkedAt": deleteField(),
          "crm.linkedBy": deleteField(),
          "crm.linkedByEmail": deleteField(),
          ...createLegacyQuoteCrmLinkDeletePatch(),
          updatedAt: serverTimestamp(),
        });

        const nextCrm = stripLegacyCrmQuoteLinkFields({ ...(linkedQuote.crm || {}) });
        delete nextCrm.leadId;
        delete nextCrm.linkedAt;
        delete nextCrm.linkedBy;
        delete nextCrm.linkedByEmail;
        applyQuoteLocalPatch(linkedQuote.id, {
          crm: nextCrm,
          updatedAt: new Date(),
        });
      }

      const localUpdatedAt = new Date();
      applyCrmLeadLocalPatch(leadLike.id, {
        linkedQuotes: remainingLinkedQuotes,
        hasQuote: remainingLinkedQuotes.length > 0,
        updatedAt: localUpdatedAt,
      });

      setCrmDetailsSavedAt(localUpdatedAt);
      notify("Quote unlinked", "success");
    } catch (error) {
      console.error("Failed to unlink quote:", error);
      notify("Could not unlink quote. Please try again.", "error");
    } finally {
      setIsLinkingQuote(false);
    }
  },
  [
    applyCrmLeadLocalPatch,
    applyQuoteLocalPatch,
    findLinkedQuoteForLead,
    notify,
    workspaceUid,
  ]
);

const requestUnlinkQuoteFromLead = useCallback(
  (leadLike) => {
    if (!leadLike?.id) return;

    openConfirmDialog({
      title: "Unlink quote?",
      description: "This removes the quote link from the lead only. The quotation itself stays unchanged.",
      confirmLabel: "Unlink Quote",
      cancelLabel: "Cancel",
      tone: "warning",
      onConfirm: () => performUnlinkQuoteFromLead(leadLike),
    });
  },
  [openConfirmDialog, performUnlinkQuoteFromLead]
);

const generateQuoteNumber = async (uid) => {

  const settingsRef = doc(db, "users", uid, "settings", "quotation");
  const settingsPath = `users/${uid}/settings/quotation`;

  return await runTransaction(db, async (transaction) => {

    const snap = await transaction.get(settingsRef);
    recordFirestoreRead({
      path: settingsPath,
      label: "generateQuoteNumberTransaction",
      kind: "transaction.get",
    });

    const data = snap.exists()
      ? snap.data()
      : {
          startNumber: 1,
          nextNumber: 1,
          prefix: "AC",
          padLength: 4,
        };

    const currentYear = new Date().getFullYear();

    let nextNumber = data.nextNumber || 1;
    let year = data.year || currentYear;

    // reset counter if year changed
    if (year !== currentYear) {
      nextNumber = 1;
      year = currentYear;
    }

    const prefix = data.prefix || "AC";
    const padLength = data.padLength || 4;

    const padded = String(nextNumber).padStart(padLength, "0");

    const quoteNumber = `${prefix}-${year}-${padded}`;

    if (snap.exists()) {
      transaction.update(settingsRef, {
        nextNumber: nextNumber + 1,
        year,
        updatedAt: serverTimestamp(),
      });
    } else {
      transaction.set(
        settingsRef,
        {
          startNumber: 1,
          nextNumber: nextNumber + 1,
          prefix,
          padLength,
          year,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    }
    invalidateFirestoreReadCache(settingsRef);

    return quoteNumber;

  });

};
const saveGeneralQuotation = async ({ asNew = false } = {}) => {
  const isEditingExisting = !asNew && Boolean(generalEditingQuoteId);
  const previousDraftKey = generalQuoteDraftKey;
  let debugQuoteId = generalEditingQuoteId || "";
  let debugPayload = null;

  if (!canAccessGeneralQuotes) {
    alert("General quotations are not enabled for this account plan.");
    return;
  }

  if (isEditingExisting ? !canEditQuotes : !canCreateQuotes) {
    alert(
      isEditingExisting
        ? "You do not have permission to edit quotations in this workspace."
        : "You do not have permission to create quotations in this workspace."
    );
    return;
  }

  if (isSaving) return;

  const isProtectedLegacyUpdate =
    isEditingExisting &&
    quoteSubTab === CURTAIN_QUOTE_TYPE &&
    legacyCurtainRecalculationImpactRef.current?.shouldShowBanner === true &&
    !pendingRecalculationAudit &&
    !legacyRecalculationRevisionRequest;

  if (isProtectedLegacyUpdate) {
    notify(
      "This older quote is protected and still shows saved values. Create a new revision to save the latest formula.",
      "warning"
    );
    setAutoSaveStatus("idle");
    return false;
  }

  setIsSaving(true);
  setAutoSaveStatus("saving");

  try {
    const currentUser = auth.currentUser;
    if (!currentUser || !workspaceUid) {
      console.error("REAL GENERAL QUOTE SAVE BLOCKED: workspace/auth missing", {
        workspaceUid,
        uid: currentUser?.uid || "",
        email: currentUser?.email || "",
      });
      alert("Could not save because workspace is not ready. Please refresh and try again.");
      setAutoSaveStatus("error");
      return;
    }
    if (generalUploadPromiseRef.current) {
      if (isDevelopmentMode()) {
        console.log("GENERAL QUOTE SAVE WAITING FOR IMAGE UPLOAD", {
          workspaceUid,
          uid: currentUser.uid,
          uploadingItemId: generalUploadingItemId,
        });
      }
      await generalUploadPromiseRef.current;
    }

    const liveGeneralActionPayload = buildLiveGeneralQuotePayload({
      action: asNew ? "saveAsNew" : "save",
    });
    traceLiveActionPayload(liveGeneralActionPayload);
    guardStaleActionPayload(
      liveGeneralActionPayload,
      liveGeneralActionPayload.summaryTotals
    );

    const quoteStateSnapshot = liveGeneralActionPayload.quote;
    const { cleanedQuote, changed, metadataChanged } = await cleanupExpiredGeneralQuoteImages(
      quoteStateSnapshot
    );
    const normalized = normalizeGeneralQuote(cleanedQuote, {
      shopAddress,
      currency,
      businessName,
      businessPhone,
      businessEmail,
    });

    const normalizedGeneralQuoteDate = resolveQuoteDateForInput(
      {
        ...selectedQuote,
        generalQuote: normalized,
      },
      todayISO()
    );
    const normalizedWithDate = {
      ...normalized,
      quoteDate: normalizedGeneralQuoteDate,
      date: normalizedGeneralQuoteDate,
      quotationDate: normalizedGeneralQuoteDate,
    };
    const totals = calculateGeneralQuoteTotals(normalizedWithDate);
    const existingId = generalEditingQuoteId;
    const nextQuoteNo =
      !asNew && existingId
        ? normalizedWithDate.quoteNo || selectedQuote?.quoteNo || ""
        : await generateQuoteNumber(workspaceUid);

    const nextQuote = {
      ...normalizedWithDate,
      quoteNo: nextQuoteNo,
      currency: normalizedWithDate.currency || currency,
    };

    if (changed || metadataChanged || nextQuote.quoteNo !== quoteStateSnapshot.quoteNo) {
      generalQuoteRef.current = nextQuote;
      setGeneralQuote(nextQuote);
    }

    const quoteDocId = !asNew && existingId ? existingId : nextQuoteNo;
    const customerLabel =
      nextQuote.quoteFor?.company ||
      nextQuote.quoteFor?.name ||
      nextQuote.title ||
      "General Quotation";
    const selectedGeneralCustomerId =
      nextQuote.customerId ||
      (!asNew
        ? selectedQuote?.customerId || selectedQuote?.crm?.customerId || ""
        : "");
    const customerLedger = await ensureCustomerLedgerRecord(
      getCustomerIdentityFromQuote({
        ...selectedQuote,
        id: quoteDocId,
        quoteType: GENERAL_QUOTE_TYPE,
        customerId: selectedGeneralCustomerId,
        customerName: customerLabel,
        customerPhone: nextQuote.customerPhone || nextQuote.quoteFor?.phone || "",
        customerWhatsapp: nextQuote.customerWhatsapp || "",
        customerEmail: nextQuote.customerEmail || nextQuote.quoteFor?.email || "",
        customerAddress: nextQuote.customerAddress || nextQuote.quoteFor?.address || "",
        customerCompanyName:
          nextQuote.customerCompanyName || nextQuote.quoteFor?.company || "",
        generalQuote: nextQuote,
      }),
      {
        customerId: selectedGeneralCustomerId,
        extraQuote: {
          id: quoteDocId,
          quoteNo: nextQuoteNo,
          quoteType: GENERAL_QUOTE_TYPE,
          quoteDate: normalizedGeneralQuoteDate,
          customerId: selectedGeneralCustomerId,
          customerName: customerLabel,
          grandTotal: Number(totals.grandTotal || 0),
          status:
            !asNew && existingId
              ? selectedQuoteStatus || "draft"
              : "draft",
        },
      }
    );
    const customerFields = buildQuoteCustomerLinkFields(customerLedger, {
      customerId: selectedGeneralCustomerId,
      customerName: customerLabel,
      customerPhone: nextQuote.customerPhone || nextQuote.quoteFor?.phone || "",
      customerWhatsapp: nextQuote.customerWhatsapp || "",
      customerEmail: nextQuote.customerEmail || nextQuote.quoteFor?.email || "",
      customerAddress: nextQuote.customerAddress || nextQuote.quoteFor?.address || "",
      customerCompanyName:
        nextQuote.customerCompanyName || nextQuote.quoteFor?.company || "",
    });
    const linkedGeneralQuote = {
      ...nextQuote,
      ...customerFields,
      subtotal: Number(totals.subtotal || 0),
      subtotalExcludingGst: Number(totals.subtotalExcludingGst || 0),
      subtotalBeforeGst: Number(totals.subtotalBeforeGst || totals.subtotalExcludingGst || 0),
      taxableAmount: Number(totals.taxableAmount || 0),
      totalGst: Number(totals.totalGstAmount ?? totals.gstTotal ?? 0),
      totalGstAmount: Number(totals.totalGstAmount ?? totals.gstTotal ?? 0),
      gstAmount: Number(totals.totalGstAmount ?? totals.gstTotal ?? 0),
      totalAfterGst: Number(totals.totalAfterGst || 0),
      discountApplication: "after_gst",
      discountType: totals.discountType || nextQuote.discountType || "flat",
      discountValue: String(totals.discountValue ?? nextQuote.discountValue ?? 0),
      discountAmount: Number(totals.discountAmount || 0),
      grandTotal: Number(totals.grandTotal || 0),
      finalTotal: Number(totals.grandTotal || 0),
      totalInWords: totals.totalInWords || "",
      quoteFor: {
        ...(nextQuote.quoteFor || {}),
        name: nextQuote.quoteFor?.name || customerFields.customerName,
        company:
          nextQuote.quoteFor?.company || customerFields.customerCompanyName || "",
        phone: nextQuote.quoteFor?.phone || customerFields.customerPhone || "",
        email: nextQuote.quoteFor?.email || customerFields.customerEmail || "",
        address:
          nextQuote.quoteFor?.address || customerFields.customerAddress || "",
      },
    };
    let savedGeneralQuoteForState = linkedGeneralQuote;
    const linkedLeadId =
      pendingCrmLeadId || (!asNew ? selectedQuote?.crm?.leadId || "" : "");
    const linkedLead =
      (linkedLeadId &&
        crmLeadRecords.find((lead) => lead.id === linkedLeadId)) ||
      null;
    const initialGeneralStage =
      !asNew && existingId
        ? selectedQuoteWorkStage || "quote_given"
        : linkedLead?.stageId || getQuoteGivenStageIdForType(GENERAL_QUOTE_TYPE);
    const initialGeneralStatus =
      !asNew && existingId
        ? selectedQuoteStatus || "draft"
        : linkedLead?.status || "draft";

    const rawPayload = {
      quoteType: GENERAL_QUOTE_TYPE,
      ...customerFields,
      quoteNo: nextQuoteNo,
      quoteDate: normalizedGeneralQuoteDate,
      date: normalizedGeneralQuoteDate,
      quotationDate: normalizedGeneralQuoteDate,
      generalQuote: linkedGeneralQuote,
      subtotal: Number(totals.subtotal || 0),
      subtotalExcludingGst: Number(totals.subtotalExcludingGst || 0),
      subtotalBeforeGst: Number(totals.subtotalBeforeGst || totals.subtotalExcludingGst || 0),
      taxableAmount: Number(totals.taxableAmount || 0),
      totalGst: Number(totals.totalGstAmount ?? totals.gstTotal ?? 0),
      totalGstAmount: Number(totals.totalGstAmount ?? totals.gstTotal ?? 0),
      gstAmount: Number(totals.totalGstAmount ?? totals.gstTotal ?? 0),
      totalAfterGst: Number(totals.totalAfterGst || 0),
      grandTotal: Number(totals.grandTotal || 0),
      finalTotal: Number(totals.grandTotal || 0),
      totalInWords: totals.totalInWords || "",
      deleted: false,
      discountType: totals.discountType || nextQuote.discountType || "flat",
      discountValue: Number(totals.discountValue ?? nextQuote.discountValue ?? 0),
      discountAmount: Number(totals.discountAmount || 0),
      discountApplication: "after_gst",
      summaryDiscountMode: getSummaryDiscountModeFromType(
        totals.discountType || nextQuote.discountType
      ),
      summaryDiscountValue: Number(totals.discountValue ?? nextQuote.discountValue ?? 0),
      status: initialGeneralStatus,
      workStage: initialGeneralStage,
      installationDate:
        !asNew && existingId
          ? selectedInstallationDate || null
          : linkedLead?.installationDate || null,
    };
    const crmPayload = buildQuoteCrmData(
      {
        id: quoteDocId,
        ...selectedQuote,
        ...rawPayload,
      },
      {
        stageId: rawPayload.workStage,
        status: rawPayload.status,
        installationDate: rawPayload.installationDate,
        expectedValue: Number(totals.grandTotal || 0),
        finalValue: Number(totals.grandTotal || 0),
        leadId: linkedLeadId,
        hasQuote: true,
        customerId: customerFields.customerId,
        customerName: customerFields.customerName,
        customerPhone: customerFields.customerPhone,
        contact: {
          name: linkedGeneralQuote.quoteFor?.name || customerFields.customerName,
          phone: customerFields.customerPhone,
          whatsapp: customerFields.customerWhatsapp,
          email: customerFields.customerEmail,
          address: customerFields.customerAddress,
        },
      }
    );
    const quoteSearchIndex = buildSafeQuoteSearchIndex({
      id: quoteDocId,
      ...rawPayload,
      crm: crmPayload,
    });
    const payload = sanitizeGeneralQuotePayload({
      ...rawPayload,
      crm: crmPayload,
      searchText: quoteSearchIndex.searchText,
      searchKeywords: quoteSearchIndex.searchKeywords,
      searchTokens: quoteSearchIndex.searchKeywords,
      ...buildQuoteLookupFields({
        id: quoteDocId,
        ...rawPayload,
        crm: crmPayload,
      }),
    });
    payload.updatedAt = serverTimestamp();
    debugQuoteId = quoteDocId;
    debugPayload = payload;
    const payloadValidation = validateGeneralQuotePayloadForFirestore(payload);
    if (!payloadValidation.valid) {
      console.error("GENERAL QUOTE PAYLOAD INVALID", {
        invalidPaths: payloadValidation.invalidPaths,
        diagnostics: payloadValidation.diagnostics,
        workspaceUid,
        uid: auth.currentUser?.uid,
        quoteId: quoteDocId,
        quoteNo: nextQuoteNo,
        payloadPreview: safeDebugPreview(payload),
      });
      setGeneralQuoteDebugState((prev) => ({
        ...prev,
        lastSaveError: {
          code: "invalid-payload",
          message: "General quote payload contains invalid Firestore values.",
          invalidPaths: payloadValidation.invalidPaths,
          path: `users/${workspaceUid}/quotations/${quoteDocId}`,
          at: new Date().toISOString(),
        },
        lastSave: {
          ...(prev.lastSave || {}),
          state: "invalid-payload",
        },
      }));
      throw new Error("General quote payload contains invalid Firestore values.");
    }

    if (isDevelopmentMode()) {
      console.log("QUOTE DISCOUNT SAVE/LOAD", {
        source: "general_quote_save",
        discountType: payload.discountType,
        discountValue: payload.discountValue,
      });
      console.log("GENERAL QUOTE SAVE PAYLOAD READY", {
        workspaceUid,
        quoteId: quoteDocId,
        quoteNo: nextQuoteNo,
        payloadKeys: Object.keys(payload || {}),
        searchTextLength: String(payload.searchText || "").length,
        searchKeywordsCount: Array.isArray(payload.searchKeywords)
          ? payload.searchKeywords.length
          : 0,
        payloadPreview: safeDebugPreview(payload),
      });
    }
    setGeneralQuoteDebugState((prev) => ({
      ...prev,
      lastSaveError: null,
      lastSave: {
        state: "ready",
        workspaceUid,
        quoteId: quoteDocId,
        quoteNo: nextQuoteNo,
        docPath: `users/${workspaceUid}/quotations/${quoteDocId}`,
        payloadKeys: Object.keys(payload || {}),
        payloadSizeBytes: estimateJsonSizeBytes(payload),
        diagnostics: inspectGeneralQuotePayload(payload),
        searchTextLength: String(payload.searchText || "").length,
        searchKeywordsCount: Array.isArray(payload.searchKeywords)
          ? payload.searchKeywords.length
          : 0,
        discountType: payload.discountType,
        discountValue: payload.discountValue,
        itemCount: payload?.generalQuote?.lineItems?.length || 0,
        imageMetadataCount: (payload?.generalQuote?.lineItems || []).reduce(
          (count, item) => count + normalizeGeneralQuoteItemImages(item).length,
          0
        ),
      },
    }));

    const quoteRef = doc(
      db,
      "users",
      workspaceUid,
      "quotations",
      quoteDocId
    );

    const payloadDiagnostics = inspectGeneralQuotePayload(payload);
    console.log("REAL GENERAL QUOTE SAVE START", {
      workspaceUid,
      uid: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      quoteId: quoteDocId,
      quoteNo: nextQuoteNo,
      isExistingQuote: !asNew && Boolean(existingId),
      docPath: quoteRef.path,
      payloadKeys: Object.keys(payload || {}),
      payloadSizeEstimate: JSON.stringify(safeDebugPreview(payload || {})).length,
      itemCount:
        payload?.generalQuote?.items?.length ||
        payload?.generalQuote?.lineItems?.length ||
        payload?.items?.length ||
        0,
      imageCount: countGeneralQuoteImages(payload),
      payloadSizeBytes: estimateJsonSizeBytes(payload),
      hasInvalidUndefinedFields: payloadDiagnostics.hasUndefinedFields,
      hasFileBlobObjects: payloadDiagnostics.hasFileBlobObjects,
      hasInvalidNumbers: payloadDiagnostics.hasInvalidNumbers,
      searchTextLength: String(payload.searchText || "").length,
      searchKeywordsCount: Array.isArray(payload.searchKeywords)
        ? payload.searchKeywords.length
        : 0,
      discountType: payload.discountType || payload?.generalQuote?.discountType,
      discountValue:
        payload.discountValue || payload?.generalQuote?.discountValue,
    });

    try {
      if (!asNew && existingId) {
        await writeQuotationAndQuoteIndex({
          quoteRef,
          quoteId: quoteDocId,
          quotePayload: payload,
          quoteIndexSource: {
            id: quoteDocId,
            ...payload,
            sourceQuoteId: quoteDocId,
          },
          create: false,
        });
      } else {
        await persistNewQuotationWithTrialGuard({
          quoteId: quoteDocId,
          quotePayload: payload,
          quoteIndexSource: {
            id: quoteDocId,
            ...payload,
            sourceQuoteId: quoteDocId,
          },
        });
      }

      console.log("GENERAL QUOTE FIRESTORE WRITE SUCCESS", {
        path: quoteRef.path,
      });

      if (hasGeneralQuoteDraftImagePaths(payload.generalQuote, workspaceUid)) {
        try {
          console.log("GENERAL_QUOTE_DRAFT_IMAGE_CANONICALIZE_START", {
            workspaceUid,
            quoteId: quoteDocId,
            quoteNo: nextQuoteNo,
          });
          const canonicalizeCallable = httpsCallable(
            functions,
            "canonicalizeGeneralQuoteImagePaths"
          );
          const canonicalizeResult = await canonicalizeCallable({
            workspaceUid,
            quoteId: quoteDocId,
            quoteNo: nextQuoteNo,
            storageBucket: firebaseApp.options.storageBucket || "",
          });
          const canonicalizeData = canonicalizeResult?.data || {};
          console.log("GENERAL_QUOTE_DRAFT_IMAGE_CANONICALIZE_RESULT", {
            workspaceUid,
            quoteId: quoteDocId,
            quoteNo: nextQuoteNo,
            changed: canonicalizeData.changed,
            copiedImages: canonicalizeData.copiedImages || [],
            failedImages: canonicalizeData.failedImages || [],
          });
          if (canonicalizeData.changed && canonicalizeData.generalQuote) {
            savedGeneralQuoteForState = normalizeGeneralQuote(
              canonicalizeData.generalQuote,
              {
                shopAddress,
                currency,
                businessName,
                businessPhone,
                businessEmail,
              }
            );
            payload.generalQuote = sanitizeGeneralQuoteForFirestore(
              savedGeneralQuoteForState
            );
          }
          if (Array.isArray(canonicalizeData.failedImages) && canonicalizeData.failedImages.length) {
            notify(
              "Quotation saved, but one draft image could not be moved to the permanent folder.",
              "warning",
              7000
            );
          }
        } catch (canonicalizeError) {
          console.error("GENERAL_QUOTE_DRAFT_IMAGE_CANONICALIZE_FAILED", {
            code: canonicalizeError?.code,
            message: canonicalizeError?.message,
            stack: canonicalizeError?.stack,
            workspaceUid,
            quoteId: quoteDocId,
            quoteNo: nextQuoteNo,
          });
          notify(
            "Quotation saved, but image path cleanup could not finish. You can still repair images from the quote.",
            "warning",
            7000
          );
        }
      }

      if (isDevelopmentMode()) {
        const savedSnap = await getDoc(quoteRef, { label: "generalQuoteSaveReadback" });
        const savedData = savedSnap.exists() ? savedSnap.data() : null;
        const savedItems =
          savedData?.generalQuote?.lineItems ||
          savedData?.generalQuote?.items ||
          savedData?.items ||
          [];

        console.log("GENERAL QUOTE READBACK", {
          exists: savedSnap.exists(),
          dataKeys: savedData ? Object.keys(savedData) : [],
          itemCount: savedItems.length,
          imageMetadataCount: savedItems.reduce(
            (count, item) => count + normalizeGeneralQuoteItemImages(item).length,
            0
          ),
        });

        setGeneralQuoteDebugState((prev) => ({
          ...prev,
          lastSaveError: null,
          lastSave: {
            ...(prev.lastSave || {}),
            state: "saved",
            readbackExists: savedSnap.exists(),
            readbackKeys: savedData ? Object.keys(savedData) : [],
            readbackItemCount: savedItems.length,
            savedAt: new Date().toISOString(),
          },
        }));
      } else {
        setGeneralQuoteDebugState((prev) => ({
          ...prev,
          lastSaveError: null,
          lastSave: {
            ...(prev.lastSave || {}),
            state: "saved",
            savedAt: new Date().toISOString(),
          },
        }));
      }
    } catch (error) {
      console.error("REAL GENERAL QUOTE SAVE FAILED EXACT", {
        code: error?.code,
        message: error?.message,
        stack: error?.stack,
        workspaceUid,
        uid: auth.currentUser?.uid,
        quoteId: quoteDocId,
        quoteNo: nextQuoteNo,
        docPath: quoteRef.path,
        payloadPreview: safeDebugPreview(payload),
      });
      setGeneralQuoteDebugState((prev) => ({
        ...prev,
        lastSaveError: {
          code: error?.code || "",
          message: error?.message || String(error),
          stack: error?.stack || "",
          path: quoteRef.path,
          at: new Date().toISOString(),
        },
        lastSave: {
          ...(prev.lastSave || {}),
          state: "failed",
        },
      }));
      throw error;
    }

    await upsertQuoteLocalRecord({
      id: quoteDocId,
      ...payload,
      createdAt:
        !asNew && existingId
          ? selectedQuote?.createdAt || new Date()
          : new Date(),
      updatedAt: new Date(),
    }, { persistIndex: false });

    let crmDealLinkResult = null;
    let crmSyncFailed = false;
    try {
      if (linkedLeadId) {
        await saveCrmFieldsForLead(
          linkedLeadId,
          {
            quoteType: GENERAL_QUOTE_TYPE,
            stageId: payload.workStage,
            status: payload.status,
            linkedQuotes: [
              {
                quoteId: quoteDocId,
                quoteNo: nextQuoteNo,
                type: GENERAL_QUOTE_TYPE,
                value: Number(totals.grandTotal || 0),
                linkedQuoteDate: normalizedGeneralQuoteDate,
                isPrimary: true,
              },
            ],
            hasQuote: true,
            customerId: customerFields.customerId,
            customerName: customerFields.customerName,
            customerPhone: customerFields.customerPhone,
            expectedValue: Number(totals.grandTotal || 0),
            finalValue: Number(totals.grandTotal || 0),
            installationDate: payload.installationDate || null,
          },
          { syncQuote: false }
        );
        setPendingCrmLeadId("");
      }

      crmDealLinkResult = await ensureSavedQuoteCrmDeal(
        {
          id: quoteDocId,
          ...payload,
          crm: crmPayload,
          ...customerFields,
        },
        {
          quoteNo: nextQuoteNo,
          quoteType: GENERAL_QUOTE_TYPE,
          totalValue: Number(totals.grandTotal || 0),
          customerFields,
          linkedLeadId,
          saveAsNew: asNew,
          previousSaveQuoteOnly: selectedQuote?.crm?.saveQuoteOnly === true,
          showSaveOutcomeToast: true,
        }
      );
    } catch (crmError) {
      crmSyncFailed = true;
      console.error("GENERAL QUOTE CRM SYNC FAILED AFTER QUOTE SAVE", {
        code: crmError?.code,
        message: crmError?.message,
        stack: crmError?.stack,
        workspaceUid,
        quoteId: quoteDocId,
        quoteNo: nextQuoteNo,
      });
      notify(
        "Quotation saved, but CRM linking could not be updated. You can retry from CRM.",
        "warning",
        5200
      );
    }
    if (crmDealLinkResult?.crmPayload) {
      payload.crm = crmDealLinkResult.crmPayload;
      payload.workStage = crmDealLinkResult.stageId || payload.workStage;
      payload.status = crmDealLinkResult.status || payload.status;
      payload.installationDate =
        crmDealLinkResult.crmPayload.installationDate || payload.installationDate || null;
    }

    await upsertQuoteLocalRecord({
      id: quoteDocId,
      ...payload,
      createdAt:
        !asNew && existingId
          ? selectedQuote?.createdAt || new Date()
          : new Date(),
      updatedAt: new Date(),
    }, { persistIndex: false });
    setGeneralEditingQuoteId(quoteDocId);
    setSelectedQuote({
      id: quoteDocId,
      ...payload,
    });
    setSelectedQuoteStatus(payload.status);
    setSelectedQuoteWorkStage(payload.workStage);
    setSelectedInstallationDate(payload.installationDate);
    setQuoteSubTab(GENERAL_QUOTE_TYPE);
    setIsNewQuote(false);
    setTab("quote");
    generalQuoteRef.current = savedGeneralQuoteForState;
    setGeneralQuote(savedGeneralQuoteForState);
    markGeneralQuoteBaseline(savedGeneralQuoteForState, "saved");
    if (previousDraftKey) {
      clearLocalDraftRecord(previousDraftKey);
    }
    clearLocalDraftRecord(
      buildLocalDraftKey(
        workspaceUid,
        LOCAL_DRAFT_MODULES.GENERAL_QUOTE,
        quoteDocId || "new"
      )
    );
    setAutoSaveStatus("saved");
    if (crmSyncFailed) {
      setGeneralQuoteDebugState((prev) => ({
        ...prev,
        lastSave: {
          ...(prev.lastSave || {}),
          crmSync: "failed-after-quote-save",
        },
      }));
    }
  } catch (error) {
    console.error("GENERAL QUOTE SAVE FAILED", {
      error,
      code: error?.code,
      message: error?.message,
      stack: error?.stack,
      workspaceUid,
      quoteId: debugQuoteId,
      quoteNo: debugPayload?.quoteNo,
      payloadKeys: Object.keys(debugPayload || {}),
      payloadPreview: safeDebugPreview(debugPayload || {}),
    });
    setAutoSaveStatus("error");
    alert(
      isFirestoreResourceExhausted(error)
        ? "Firebase read limit reached. Please wait and try again."
        : "Failed to save quotation"
    );
  } finally {
    setIsSaving(false);
  }
};

/* --------------------------- SAVE QUOTATION (HARD UNIQUE  FIXED) --------------------------- */

/* --------------------------- SAVE QUOTATION (FINAL, HARD SAFE) --------------------------- */
/* --------------------------- SAVE QUOTATION (SINGLE SOURCE OF TRUTH) --------------------------- */
const saveQuotation = async () => {
  const isEditingExisting = Boolean(editingQuoteId);
  const previousCurtainDraftKey = curtainQuoteDraftKey;

  if (quoteSubTab === CURTAIN_QUOTE_TYPE && !canAccessCurtainQuotes) {
    alert("Curtain quotations are not enabled for this account plan.");
    return;
  }

  if (isEditingExisting ? !canEditQuotes : !canCreateQuotes) {
    alert(
      isEditingExisting
        ? "You do not have permission to edit quotations in this workspace."
        : "You do not have permission to create quotations in this workspace."
    );
    return;
  }

  if (quoteSubTab === GENERAL_QUOTE_TYPE) {
    await saveGeneralQuotation({ asNew: false });
    return;
  }

  if (isSaving) return;

  setIsSaving(true);
  setAutoSaveStatus("saving");
  const saveStartedEditVersion = currentQuoteEditVersionRef.current;

  try {
    const liveActionPayload = buildLiveCurtainQuotePayload({
      action: isEditingExisting ? "save" : "save",
    });
    traceLiveActionPayload(liveActionPayload);
    guardStaleActionPayload(liveActionPayload);
    const {
      rows,
      repeatSettings,
      fabricSummaryMeta,
      summaryTotals,
      currentCurtainDiscountFields,
      currentCurtainGstFields,
      customerName,
      quoteNo,
      quoteDate: date,
      selectedQuote,
      selectedQuoteStatus,
      selectedQuoteWorkStage,
      selectedInstallationDate,
      pendingCrmLeadId,
      useHalfPieceReuse,
      passHalfPieceSavingsToCustomer,
      quoteFullnessOverrides: liveQuoteFullnessOverrides,
      getRowCalc: getCurtainProductionAdjustedRowCalc,
    } = liveActionPayload;
    const currentQuoteFullnessOverrides = normalizeQuoteFullnessOverrides(
      liveQuoteFullnessOverrides || {}
    );
    const currentQuoteFullnessFields = {
      quoteFullnessOverrides: currentQuoteFullnessOverrides,
    };

    if (!customerName?.trim()) {
      setAutoSaveStatus("idle");
      return;
    }

    const currentUser = auth.currentUser;

    if (!currentUser || !workspaceUid) {
      alert("Please sign in first.");
      setAutoSaveStatus("error");
      return;
    }

    await syncFabricCatalogueEntriesForQuoteRows(rows);

    const productionRowsForSave = rows.map((row, index) => {
      const productionCalc = getCurtainProductionAdjustedRowCalc(row, index);
      const rowForSaveBase = {
        ...row,
        ...getCurtainRowFabricCatalogueSavePatch(row),
      };
      delete rowForSaveBase.__forceCurrentCalculation;
      delete rowForSaveBase.__recalculated;
      const rowCalculationSnapshot =
        productionCalc.isCurtain || productionCalc.isRoman
          ? productionCalc.usesCalculationSnapshot
            ? row.calculationSnapshot || null
            : buildCurrentCurtainCalculationSnapshot(row, productionCalc)
          : null;
      return {
        ...rowForSaveBase,
        repeatSettings:
          repeatSettings?.[normalizeFabricKey(getCurtainRowFabricKey(row))] || {},
        lineType: productionCalc.lineType || row.lineType || (productionCalc.isAccessory ? "accessory" : "measurement"),
        calculationType: productionCalc.calculationType || row.calculationType || "",
        category: row.category || row.productConfigSnapshot?.category || "",
        calculationSnapshot: rowCalculationSnapshot,
        total: Number(productionCalc.rowTotal ?? productionCalc.total ?? 0),
        rowTotal: Number(productionCalc.rowTotal || 0),
        lineTotal: Number(productionCalc.lineTotal ?? productionCalc.rowTotal ?? 0),
        taxableAmount: Number(productionCalc.taxableAmount ?? productionCalc.rowTotal ?? 0),
        adjSqft: Number(productionCalc.adjSqft ?? productionCalc.billableSqft ?? 0),
        billableSqft: Number(productionCalc.billableSqft ?? productionCalc.adjSqft ?? 0),
        ...(productionCalc.sqftCalculation
          ? {
              rawSqft: Number(productionCalc.sqftCalculation.rawSqft || 0),
              sqftCalculation: {
                ...productionCalc.sqftCalculation,
                rawSqft: Number(productionCalc.sqftCalculation.rawSqft || 0),
                billableSqft: Number(
                  productionCalc.sqftCalculation.billableSqft || 0
                ),
              },
            }
          : {}),
        lining:
          productionCalc.lining ||
          calculateCurtainRowLining(row, 0, false, {
            sqft: productionCalc.adjSqft ?? productionCalc.billableSqft ?? 0,
          }),
        liningAmount: Number(productionCalc.liningAmount || productionCalc.lining?.amount || 0),
        trackConfig: productionCalc.trackConfig || row.trackConfig || { ...DEFAULT_TRACK_CONFIG },
        bendAmount: Number(productionCalc.bendAmount || productionCalc.trackConfig?.bendAmount || 0),
        trackCost: Number(productionCalc.trackCost || 0),
        stitchingCost: Number(productionCalc.stitchingCost || 0),
        fabricGrossAmount: Number(productionCalc.fabricGrossAmount || 0),
        fabricDiscountPercent: Number(productionCalc.fabricDiscountPercent ?? row.discount ?? 0),
        fabricDiscountAmount: Number(productionCalc.fabricDiscountAmount || 0),
        fabricNetAmount: Number(productionCalc.fabricNetAmount ?? productionCalc.fabricCost ?? 0),
        discountScope: productionCalc.discountScope || getRowDiscountScope(row, row.productConfigSnapshot, {
          hasFabricMeterComponent: Boolean(productionCalc.isCurtain || productionCalc.isRoman),
          fabricGrossAmount: productionCalc.fabricGrossAmount || 0,
        }),
        discountBase: Number(productionCalc.discountBase || 0),
        discountAmount: Number(
          productionCalc.discountAmount || productionCalc.fabricDiscountAmount || 0
        ),
        baseLineAmountBeforeDiscount: Number(
          productionCalc.baseLineAmountBeforeDiscount ??
            productionCalc.rowTotal ??
            productionCalc.lineTotal ??
            0
        ),
        fabricCost: Number(productionCalc.fabricCost || 0),
        sqftAmount: Number(productionCalc.sqftAmount || productionCalc.productCharge || 0),
        rowSubtotalBeforeGst: Number(
          productionCalc.rowSubtotalBeforeGst ?? productionCalc.rowTotal ?? 0
        ),
        gstRate: resolveCurtainSavedRowGstRate(row),
        fabricMeters: productionCalc.customerBillableFabricMeters ?? productionCalc.orderedFabricMeters ?? productionCalc.fabricMeters,
        customerBillableFabricMeters:
          productionCalc.customerBillableFabricMeters ?? productionCalc.orderedFabricMeters ?? productionCalc.fabricMeters,
        fullOrderedFabricMeters:
          productionCalc.fullOrderedFabricMeters ??
          productionCalc.noReuseOrderedFabricMeters ??
          productionCalc.orderedFabricMeters ??
          productionCalc.fabricMeters,
        noReuseOrderedFabricMeters:
          productionCalc.noReuseOrderedFabricMeters ??
          productionCalc.customerBillableFabricMeters ??
          productionCalc.orderedFabricMeters ??
          productionCalc.fabricMeters,
        cutFabricMeters: productionCalc.cutFabricMeters ?? productionCalc.fabricMeters,
        workshopFabricRequiredMeters:
          productionCalc.workshopFabricRequiredMeters ??
          productionCalc.workshopNewFabricMeters ??
          productionCalc.newFabricNeededMeters ??
          productionCalc.orderedFabricMeters ??
          productionCalc.fabricMeters,
        workshopNewFabricMeters:
          productionCalc.workshopNewFabricMeters ??
          productionCalc.newFabricNeededMeters ??
          productionCalc.orderedFabricMeters ??
          productionCalc.fabricMeters,
        internalNewFabricNeededMeters:
          productionCalc.internalNewFabricNeededMeters ??
          productionCalc.workshopNewFabricMeters ??
          productionCalc.newFabricNeededMeters ??
          productionCalc.orderedFabricMeters ??
          productionCalc.fabricMeters,
        orderedFabricMeters:
          productionCalc.customerBillableFabricMeters ?? productionCalc.orderedFabricMeters ?? productionCalc.fabricMeters,
        newFabricNeededMeters:
          productionCalc.workshopNewFabricMeters ??
          productionCalc.newFabricNeededMeters ??
          productionCalc.orderedFabricMeters ??
          productionCalc.fabricMeters,
        internalHalfPieceSavingMeters: productionCalc.internalHalfPieceSavingMeters || 0,
        internalHalfPieceSavingValue: productionCalc.internalHalfPieceSavingValue || 0,
        halfReusedMeters: productionCalc.halfReusedMeters || 0,
        internalHalfReusedMeters:
          productionCalc.internalHalfReusedMeters ?? productionCalc.halfReusedMeters ?? 0,
        halfPiecesSaved: productionCalc.halfPiecesSaved || 0,
        halfPiecesReused: productionCalc.halfPiecesReused || 0,
      };
    });
    const rowsForSave = sanitizeCurtainRowsEditableTextFields(
      attachProductSnapshotsToCurtainRows(productionRowsForSave)
    );
    const normalizedFabricSummaryMeta = normalizeFabricSummaryMetaWithRepeatSettings(
      fabricSummaryMeta,
      repeatSettings
    );
    const quoteCalculationSnapshot = buildCurtainQuoteCalculationSnapshot({
      rows,
      summaryTotals,
      getRowCalc: getCurtainProductionAdjustedRowCalc,
    });
    const quoteBlindSqftRoundingMode = normalizeBlindSqftRoundingMode(
      rowsForSave.find((row) => row?.sqftCalculation?.roundingMode)
        ?.sqftCalculation?.roundingMode ||
        selectedQuote?.calculationSettings?.blindSqftRoundingMode ||
        fabricCalcSettings?.blindSqftRoundingMode,
      "exact"
    );
    const currentCurtainCalculationSettings = {
      ...(selectedQuote?.calculationSettings || {}),
      blindSqftRoundingMode: quoteBlindSqftRoundingMode,
      fullnessMode: "quote_override_supported",
      quoteFullnessOverrides: currentQuoteFullnessOverrides,
    };
    const currentCalculationMetadata = {
      calculationVersion: CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
      formulaCheckStatus: "current",
    };
    const recalculationFieldsForSave = resolveCurtainRecalculationFieldsForSave();
    const normalizedQuoteDate = resolveQuoteDateForInput(
      {
        ...selectedQuote,
        quoteDate: date || selectedQuote?.quoteDate,
        date: date || selectedQuote?.date,
      },
      todayISO()
    );
    if (normalizedQuoteDate && normalizedQuoteDate !== date) {
      setDate(normalizedQuoteDate);
    }

    if (isDevelopmentMode()) {
      console.log("QUOTE DISCOUNT SAVE/LOAD", {
        source: isEditingExisting ? "curtain_quote_update" : "curtain_quote_create",
        discountType: currentCurtainDiscountFields.discountType,
        discountValue: currentCurtainDiscountFields.discountValue,
      });
      console.log("SAVING repeatSettings:", repeatSettings);
    }

    /* =================================================
        EDIT MODE  UPDATE ONLY
       ================================================= */

    if (editingQuoteId) {

      const ref = doc(
        db,
        "users",
        workspaceUid,
        "quotations",
        editingQuoteId
      );
      const curtainIdentity = getCurtainQuoteCustomerIdentity(liveActionPayload);
      const selectedCurtainCustomerId = getCurtainQuoteSelectedCustomerId(
        liveActionPayload
      );
      const customerLedger = await ensureCustomerLedgerRecord(
        curtainIdentity,
        {
          customerId: selectedCurtainCustomerId,
          extraQuote: {
            id: editingQuoteId,
            quoteNo,
            quoteType: CURTAIN_QUOTE_TYPE,
            quoteDate: normalizedQuoteDate,
            customerId: selectedCurtainCustomerId,
            customerName: curtainIdentity.customerName,
            grandTotal: Number(summaryTotals.grandTotal || 0),
            status: selectedQuoteStatus || "draft",
          },
        }
      );
      const customerFields = buildQuoteCustomerLinkFields(customerLedger, {
        ...curtainIdentity,
        customerId: selectedCurtainCustomerId,
      });
      const linkedLeadId = pendingCrmLeadId || selectedQuote?.crm?.leadId || "";
      const crmPayload = buildQuoteCrmData(
        {
          id: editingQuoteId,
          ...selectedQuote,
          ...customerFields,
          quoteNo,
          quoteDate: normalizedQuoteDate,
          date: normalizedQuoteDate,
          quotationDate: normalizedQuoteDate,
          rows: rowsForSave,
          repeatSettings,
          fabricSummaryMeta: normalizedFabricSummaryMeta,
          useHalfPieceReuse,
          passHalfPieceSavingsToCustomer,
          ...currentQuoteFullnessFields,
          subtotal: Number(summaryTotals.subTotal || 0),
          grandTotal: Number(summaryTotals.grandTotal || 0),
          ...currentCurtainDiscountFields,
          ...currentCurtainGstFields,
          status: selectedQuoteStatus || "draft",
          workStage: selectedQuoteWorkStage || "quote_given",
          installationDate: selectedInstallationDate || null,
        },
        {
          stageId: selectedQuoteWorkStage || "quote_given",
          status: selectedQuoteStatus || "draft",
          installationDate: selectedInstallationDate || null,
          expectedValue: Number(summaryTotals.grandTotal || 0),
          finalValue: Number(summaryTotals.grandTotal || 0),
          leadId: linkedLeadId,
          hasQuote: true,
          customerId: customerFields.customerId,
          customerName: customerFields.customerName,
          customerPhone: customerFields.customerPhone,
          contact: {
            name: customerFields.customerName,
            phone: customerFields.customerPhone,
            whatsapp: customerFields.customerWhatsapp,
            email: customerFields.customerEmail,
            address: customerFields.customerAddress,
          },
        }
      );
      const quoteSearchSource = {
        id: editingQuoteId,
        quoteType: CURTAIN_QUOTE_TYPE,
        ...customerFields,
        quoteNo,
        quoteDate: normalizedQuoteDate,
        date: normalizedQuoteDate,
        quotationDate: normalizedQuoteDate,
        rows: rowsForSave,
        repeatSettings,
        fabricSummaryMeta: normalizedFabricSummaryMeta,
        useHalfPieceReuse,
        passHalfPieceSavingsToCustomer,
        ...currentQuoteFullnessFields,
        calculationSnapshot: quoteCalculationSnapshot,
        calculationSettings: currentCurtainCalculationSettings,
        ...currentCalculationMetadata,
        ...recalculationFieldsForSave,
        subtotal: Number(summaryTotals.subTotal || 0),
        grandTotal: Number(summaryTotals.grandTotal || 0),
        ...currentCurtainDiscountFields,
        ...currentCurtainGstFields,
        status: selectedQuoteStatus || "draft",
        workStage: selectedQuoteWorkStage || "quote_given",
        installationDate: selectedInstallationDate || null,
        crm: crmPayload,
      };
      const quoteSearchIndex = buildSafeQuoteSearchIndex(quoteSearchSource);

      const curtainUpdatePayload = {
        quoteType: CURTAIN_QUOTE_TYPE,

        ...customerFields,
        quoteNo,
        quoteDate: normalizedQuoteDate,
        date: normalizedQuoteDate,
        quotationDate: normalizedQuoteDate,

        rows: rowsForSave,

        repeatSettings: Object.fromEntries(
          Object.entries(repeatSettings).map(([k, v]) => [
            normalizeFabricKey(k),
            v
          ])
        ),
        fabricSummaryMeta: normalizedFabricSummaryMeta,
        useHalfPieceReuse,
        passHalfPieceSavingsToCustomer,
        ...currentQuoteFullnessFields,
        calculationSnapshot: quoteCalculationSnapshot,
        calculationSettings: currentCurtainCalculationSettings,
        ...currentCalculationMetadata,
        ...recalculationFieldsForSave,

        subtotal: Number(summaryTotals.subTotal || 0),
        grandTotal: Number(summaryTotals.grandTotal || 0),

        ...currentCurtainDiscountFields,
        ...currentCurtainGstFields,
        crm: crmPayload,
        searchText: quoteSearchIndex.searchText,
        searchKeywords: quoteSearchIndex.searchKeywords,
        searchTokens: quoteSearchIndex.searchKeywords,
        ...buildQuoteLookupFields(quoteSearchSource),

        updatedAt: serverTimestamp()

      };

      await writeQuotationAndQuoteIndex({
        quoteRef: ref,
        quoteId: editingQuoteId,
        quotePayload: curtainUpdatePayload,
        quoteIndexSource: {
          ...quoteSearchSource,
          sourceQuoteId: editingQuoteId,
        },
        create: false,
      });

      if (linkedLeadId) {
        await saveCrmFieldsForLead(
          linkedLeadId,
          {
            quoteType: CURTAIN_QUOTE_TYPE,
            stageId: selectedQuoteWorkStage || "quote_given",
            status: selectedQuoteStatus || "draft",
            linkedQuotes: [
              {
                quoteId: editingQuoteId,
                quoteNo,
                type: CURTAIN_QUOTE_TYPE,
                value: Number(summaryTotals.grandTotal || 0),
                linkedQuoteDate: normalizedQuoteDate,
                isPrimary: true,
              },
            ],
            hasQuote: true,
            customerId: customerFields.customerId,
            customerName: customerFields.customerName,
            customerPhone: customerFields.customerPhone,
            expectedValue: Number(summaryTotals.grandTotal || 0),
            finalValue: Number(summaryTotals.grandTotal || 0),
            installationDate: selectedInstallationDate || null,
          },
          { syncQuote: false }
        );
        setPendingCrmLeadId("");
      }

      const crmDealLinkResult = await ensureSavedQuoteCrmDeal(
        {
          id: editingQuoteId,
          quoteType: CURTAIN_QUOTE_TYPE,
          ...customerFields,
          quoteNo,
          quoteDate: normalizedQuoteDate,
          date: normalizedQuoteDate,
          quotationDate: normalizedQuoteDate,
          rows: rowsForSave,
          repeatSettings,
          fabricSummaryMeta: normalizedFabricSummaryMeta,
          useHalfPieceReuse,
          passHalfPieceSavingsToCustomer,
          ...currentQuoteFullnessFields,
          calculationSnapshot: quoteCalculationSnapshot,
          calculationSettings: currentCurtainCalculationSettings,
          ...currentCalculationMetadata,
          ...recalculationFieldsForSave,
          subtotal: Number(summaryTotals.subTotal || 0),
          grandTotal: Number(summaryTotals.grandTotal || 0),
          ...currentCurtainDiscountFields,
          ...currentCurtainGstFields,
          status: selectedQuoteStatus || "draft",
          workStage: selectedQuoteWorkStage || "quote_given",
          installationDate: selectedInstallationDate || null,
          crm: crmPayload,
        },
        {
          quoteNo,
          quoteType: CURTAIN_QUOTE_TYPE,
          totalValue: Number(summaryTotals.grandTotal || 0),
          customerFields,
          linkedLeadId,
          previousSaveQuoteOnly: selectedQuote?.crm?.saveQuoteOnly === true,
          showSaveOutcomeToast: true,
        }
      );
      const finalCrmPayload = crmDealLinkResult?.crmPayload || crmPayload;
      const finalWorkStage = crmDealLinkResult?.stageId || selectedQuoteWorkStage || "quote_given";
      const finalQuoteStatus = crmDealLinkResult?.status || selectedQuoteStatus || "draft";
      const finalInstallationDate =
        finalCrmPayload.installationDate || selectedInstallationDate || null;

      await upsertQuoteLocalRecord({
        id: editingQuoteId,
        quoteType: CURTAIN_QUOTE_TYPE,
        ...selectedQuote,
        ...customerFields,
        quoteNo,
        quoteDate: normalizedQuoteDate,
        date: normalizedQuoteDate,
        quotationDate: normalizedQuoteDate,
        rows: rowsForSave,
        repeatSettings,
        fabricSummaryMeta: normalizedFabricSummaryMeta,
        useHalfPieceReuse,
        passHalfPieceSavingsToCustomer,
        ...currentQuoteFullnessFields,
        calculationSnapshot: quoteCalculationSnapshot,
        calculationSettings: currentCurtainCalculationSettings,
        ...currentCalculationMetadata,
        ...recalculationFieldsForSave,
        subtotal: Number(summaryTotals.subTotal || 0),
        grandTotal: Number(summaryTotals.grandTotal || 0),
        ...currentCurtainDiscountFields,
        ...currentCurtainGstFields,
        status: finalQuoteStatus,
        workStage: finalWorkStage,
        installationDate: finalInstallationDate,
        crm: finalCrmPayload,
        updatedAt: new Date(),
      });
      setQuoteCustomerLink({
        customerId: customerFields.customerId,
        customerKey: customerFields.customerKey,
        customerName: customerFields.customerName,
        phone: customerFields.customerPhone,
        whatsapp: customerFields.customerWhatsapp,
        email: customerFields.customerEmail,
        address: customerFields.customerAddress,
        companyName: customerFields.customerCompanyName,
      });
      if (currentQuoteEditVersionRef.current === saveStartedEditVersion) {
        setSelectedQuote({
          id: editingQuoteId,
          quoteType: CURTAIN_QUOTE_TYPE,
          ...selectedQuote,
          ...customerFields,
          quoteNo,
          quoteDate: normalizedQuoteDate,
          date: normalizedQuoteDate,
          quotationDate: normalizedQuoteDate,
          rows: rowsForSave,
          repeatSettings,
          fabricSummaryMeta: normalizedFabricSummaryMeta,
          useHalfPieceReuse,
          passHalfPieceSavingsToCustomer,
          ...currentQuoteFullnessFields,
          calculationSnapshot: quoteCalculationSnapshot,
          calculationSettings: currentCurtainCalculationSettings,
          ...currentCalculationMetadata,
          ...recalculationFieldsForSave,
          subtotal: Number(summaryTotals.subTotal || 0),
          grandTotal: Number(summaryTotals.grandTotal || 0),
          ...currentCurtainDiscountFields,
          ...currentCurtainGstFields,
          status: finalQuoteStatus,
          workStage: finalWorkStage,
          installationDate: finalInstallationDate,
          crm: finalCrmPayload,
        });
        markCurtainQuoteBaseline(
          {
            ...buildCurtainQuoteDraftData(),
            quoteId: editingQuoteId,
            editingQuoteId,
            quoteNo,
            quoteDate: normalizedQuoteDate,
            date: normalizedQuoteDate,
            quotationDate: normalizedQuoteDate,
            selectedQuoteStatus: finalQuoteStatus,
            selectedQuoteWorkStage: finalWorkStage,
            selectedInstallationDate: finalInstallationDate,
          },
          "saved"
        );
        clearCurtainQuoteDraft(previousCurtainDraftKey);
      } else if (isQuoteMutationAuditDevMode()) {
        console.warn("SAVE_READBACK_SKIPPED_NEWER_LOCAL_EDITS", {
          source: QUOTE_RAW_FIELD_MUTATION_SOURCES.SAVE_READBACK,
          quoteId: editingQuoteId,
          saveStartedEditVersion,
          currentEditVersion: currentQuoteEditVersionRef.current,
        });
      }
      setAutoSaveStatus("saved");
      return;
    }

    /* =================================================
        CREATE MODE
       ================================================= */

    const generatedQuoteNo = await generateQuoteNumber(
      workspaceUid
    );

    setQuoteNo(generatedQuoteNo);

    const quoteDocId = generatedQuoteNo;

    const ref = doc(
      db,
      "users",
      workspaceUid,
      "quotations",
      quoteDocId
    );
    const curtainIdentity = getCurtainQuoteCustomerIdentity(liveActionPayload);
    const selectedCurtainCustomerId = getCurtainQuoteSelectedCustomerId(
      liveActionPayload
    );
    const customerLedger = await ensureCustomerLedgerRecord(
      curtainIdentity,
      {
        customerId: selectedCurtainCustomerId,
        extraQuote: {
          id: quoteDocId,
          quoteNo: generatedQuoteNo,
          quoteType: CURTAIN_QUOTE_TYPE,
          quoteDate: normalizedQuoteDate,
          customerId: selectedCurtainCustomerId,
          customerName: curtainIdentity.customerName,
          grandTotal: Number(summaryTotals.grandTotal || 0),
          status: "draft",
        },
      }
    );
    const customerFields = buildQuoteCustomerLinkFields(customerLedger, {
      ...curtainIdentity,
      customerId: selectedCurtainCustomerId,
    });
    const linkedLeadId = pendingCrmLeadId || "";
    const linkedLead =
      (linkedLeadId &&
        crmLeadRecords.find((lead) => lead.id === linkedLeadId)) ||
      null;
    const createStageId =
      linkedLead?.stageId || getQuoteGivenStageIdForType(CURTAIN_QUOTE_TYPE);
    const createStatus = linkedLead?.status || "draft";
    const crmPayload = buildQuoteCrmData(
      {
        id: quoteDocId,
        ...customerFields,
        quoteNo: generatedQuoteNo,
        quoteDate: normalizedQuoteDate,
        date: normalizedQuoteDate,
        quotationDate: normalizedQuoteDate,
        rows: rowsForSave,
        repeatSettings,
        fabricSummaryMeta: normalizedFabricSummaryMeta,
        useHalfPieceReuse,
        passHalfPieceSavingsToCustomer,
        ...currentQuoteFullnessFields,
        calculationSnapshot: quoteCalculationSnapshot,
        calculationSettings: currentCurtainCalculationSettings,
        ...currentCalculationMetadata,
        ...recalculationFieldsForSave,
        subtotal: Number(summaryTotals.subTotal || 0),
        grandTotal: Number(summaryTotals.grandTotal || 0),
        ...currentCurtainDiscountFields,
        ...currentCurtainGstFields,
        status: createStatus,
        workStage: createStageId,
        installationDate: linkedLead?.installationDate || null,
        quoteType: CURTAIN_QUOTE_TYPE,
      },
      {
        stageId: createStageId,
        status: createStatus,
        installationDate: linkedLead?.installationDate || null,
        expectedValue: Number(summaryTotals.grandTotal || 0),
        finalValue: Number(summaryTotals.grandTotal || 0),
        leadId: linkedLeadId,
        hasQuote: true,
        customerId: customerFields.customerId,
        customerName: customerFields.customerName,
        customerPhone: customerFields.customerPhone,
        contact: {
          name: customerFields.customerName,
          phone: customerFields.customerPhone,
          whatsapp: customerFields.customerWhatsapp,
          email: customerFields.customerEmail,
          address: customerFields.customerAddress,
        },
      }
    );
    const quoteSearchSource = {
      id: quoteDocId,
      quoteType: CURTAIN_QUOTE_TYPE,
      ...customerFields,
      quoteNo: generatedQuoteNo,
      quoteDate: normalizedQuoteDate,
      date: normalizedQuoteDate,
      quotationDate: normalizedQuoteDate,
      rows: rowsForSave,
      repeatSettings,
      fabricSummaryMeta: normalizedFabricSummaryMeta,
      useHalfPieceReuse,
      passHalfPieceSavingsToCustomer,
      ...currentQuoteFullnessFields,
      calculationSnapshot: quoteCalculationSnapshot,
      calculationSettings: currentCurtainCalculationSettings,
      ...currentCalculationMetadata,
      ...recalculationFieldsForSave,
      subtotal: Number(summaryTotals.subTotal || 0),
      grandTotal: Number(summaryTotals.grandTotal || 0),
      ...currentCurtainDiscountFields,
      ...currentCurtainGstFields,
      status: createStatus,
      workStage: createStageId,
      installationDate: linkedLead?.installationDate || null,
      crm: crmPayload,
    };
    const quoteSearchIndex = buildSafeQuoteSearchIndex(quoteSearchSource);

    const curtainCreatePayload = {
      quoteType: CURTAIN_QUOTE_TYPE,

      ...customerFields,

      quoteNo: generatedQuoteNo,
      quoteDate: normalizedQuoteDate,
      date: normalizedQuoteDate,
      quotationDate: normalizedQuoteDate,

      rows: rowsForSave,

      repeatSettings: Object.fromEntries(
        Object.entries(repeatSettings).map(([k, v]) => [
          normalizeFabricKey(k),
          v
        ])
      ),
        fabricSummaryMeta: normalizedFabricSummaryMeta,
        useHalfPieceReuse,
        passHalfPieceSavingsToCustomer,
        ...currentQuoteFullnessFields,
        calculationSnapshot: quoteCalculationSnapshot,
        calculationSettings: currentCurtainCalculationSettings,
        ...currentCalculationMetadata,
        ...recalculationFieldsForSave,

        subtotal: Number(summaryTotals.subTotal || 0),
      grandTotal: Number(summaryTotals.grandTotal || 0),

      ...currentCurtainDiscountFields,
      ...currentCurtainGstFields,

      status: createStatus,
      workStage: createStageId,
      crm: crmPayload,
      searchText: quoteSearchIndex.searchText,
      searchKeywords: quoteSearchIndex.searchKeywords,
      searchTokens: quoteSearchIndex.searchKeywords,
      ...buildQuoteLookupFields(quoteSearchSource),

      installationDate: linkedLead?.installationDate || null,

      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()

    };

    await persistNewQuotationWithTrialGuard({
      quoteId: quoteDocId,
      quotePayload: curtainCreatePayload,
      quoteIndexSource: {
        ...quoteSearchSource,
        sourceQuoteId: quoteDocId,
      },
    });

    setEditingQuoteId(quoteDocId);

    if (linkedLeadId) {
      await saveCrmFieldsForLead(
        linkedLeadId,
        {
          quoteType: CURTAIN_QUOTE_TYPE,
          stageId: createStageId,
          status: createStatus,
          linkedQuotes: [
            {
              quoteId: quoteDocId,
              quoteNo: generatedQuoteNo,
              type: CURTAIN_QUOTE_TYPE,
              value: Number(summaryTotals.grandTotal || 0),
              linkedQuoteDate: normalizedQuoteDate,
              isPrimary: true,
            },
          ],
          hasQuote: true,
          customerId: customerFields.customerId,
          customerName: customerFields.customerName,
          customerPhone: customerFields.customerPhone,
          expectedValue: Number(summaryTotals.grandTotal || 0),
          finalValue: Number(summaryTotals.grandTotal || 0),
          installationDate: linkedLead?.installationDate || null,
        },
        { syncQuote: false }
      );
      setPendingCrmLeadId("");
    }

    const crmDealLinkResult = await ensureSavedQuoteCrmDeal(
      {
        id: quoteDocId,
        quoteType: CURTAIN_QUOTE_TYPE,
        ...customerFields,
        quoteNo: generatedQuoteNo,
        quoteDate: normalizedQuoteDate,
        date: normalizedQuoteDate,
        quotationDate: normalizedQuoteDate,
        rows: rowsForSave,
        repeatSettings,
        fabricSummaryMeta: normalizedFabricSummaryMeta,
          useHalfPieceReuse,
          passHalfPieceSavingsToCustomer,
          ...currentQuoteFullnessFields,
          calculationSnapshot: quoteCalculationSnapshot,
          calculationSettings: currentCurtainCalculationSettings,
          ...currentCalculationMetadata,
          ...recalculationFieldsForSave,
          subtotal: Number(summaryTotals.subTotal || 0),
        grandTotal: Number(summaryTotals.grandTotal || 0),
        ...currentCurtainDiscountFields,
        ...currentCurtainGstFields,
        status: createStatus,
        workStage: createStageId,
        installationDate: linkedLead?.installationDate || null,
        crm: crmPayload,
      },
      {
        quoteNo: generatedQuoteNo,
        quoteType: CURTAIN_QUOTE_TYPE,
        totalValue: Number(summaryTotals.grandTotal || 0),
        customerFields,
        linkedLeadId,
        saveAsNew: true,
        showSaveOutcomeToast: true,
      }
    );
    const finalCrmPayload = crmDealLinkResult?.crmPayload || crmPayload;
    const finalWorkStage = crmDealLinkResult?.stageId || createStageId;
    const finalQuoteStatus = crmDealLinkResult?.status || createStatus;
    const finalInstallationDate =
      finalCrmPayload.installationDate || linkedLead?.installationDate || null;

    await upsertQuoteLocalRecord({
      id: quoteDocId,
      quoteType: CURTAIN_QUOTE_TYPE,
      ...customerFields,
      quoteNo: generatedQuoteNo,
      quoteDate: normalizedQuoteDate,
      date: normalizedQuoteDate,
      quotationDate: normalizedQuoteDate,
      rows: rowsForSave,
      repeatSettings,
      fabricSummaryMeta: normalizedFabricSummaryMeta,
      useHalfPieceReuse,
        passHalfPieceSavingsToCustomer,
        ...currentQuoteFullnessFields,
        calculationSnapshot: quoteCalculationSnapshot,
        calculationSettings: currentCurtainCalculationSettings,
        ...currentCalculationMetadata,
        subtotal: Number(summaryTotals.subTotal || 0),
        grandTotal: Number(summaryTotals.grandTotal || 0),
        ...recalculationFieldsForSave,
        ...currentCurtainDiscountFields,
        ...currentCurtainGstFields,
      status: finalQuoteStatus,
      workStage: finalWorkStage,
      installationDate: finalInstallationDate,
      crm: finalCrmPayload,
      createdAt: new Date(),
      updatedAt: new Date(),
    }, { persistIndex: false });
    setQuoteCustomerLink({
      customerId: customerFields.customerId,
      customerKey: customerFields.customerKey,
      customerName: customerFields.customerName,
      phone: customerFields.customerPhone,
      whatsapp: customerFields.customerWhatsapp,
      email: customerFields.customerEmail,
      address: customerFields.customerAddress,
      companyName: customerFields.customerCompanyName,
    });
    if (currentQuoteEditVersionRef.current === saveStartedEditVersion) {
      setSelectedQuote({
        id: quoteDocId,
        quoteType: CURTAIN_QUOTE_TYPE,
        ...customerFields,
        quoteNo: generatedQuoteNo,
        quoteDate: normalizedQuoteDate,
        date: normalizedQuoteDate,
        quotationDate: normalizedQuoteDate,
        rows: rowsForSave,
        repeatSettings,
        fabricSummaryMeta: normalizedFabricSummaryMeta,
        useHalfPieceReuse,
        passHalfPieceSavingsToCustomer,
        ...currentQuoteFullnessFields,
        calculationSnapshot: quoteCalculationSnapshot,
        calculationSettings: currentCurtainCalculationSettings,
        ...currentCalculationMetadata,
        ...recalculationFieldsForSave,
        subtotal: Number(summaryTotals.subTotal || 0),
        grandTotal: Number(summaryTotals.grandTotal || 0),
        status: finalQuoteStatus,
        workStage: finalWorkStage,
        installationDate: finalInstallationDate,
        crm: finalCrmPayload,
      });
      markCurtainQuoteBaseline(
        {
          ...buildCurtainQuoteDraftData(),
          quoteId: quoteDocId,
          editingQuoteId: quoteDocId,
          quoteNo: generatedQuoteNo,
          quoteDate: normalizedQuoteDate,
          date: normalizedQuoteDate,
          quotationDate: normalizedQuoteDate,
          selectedQuoteStatus: finalQuoteStatus,
          selectedQuoteWorkStage: finalWorkStage,
          selectedInstallationDate: finalInstallationDate,
        },
        "saved"
      );
      clearCurtainQuoteDraft(previousCurtainDraftKey);
      clearLocalDraft(
        buildLocalDraftKey(
          workspaceUid,
          LOCAL_DRAFT_MODULES.CURTAIN_QUOTE,
          quoteDocId || "new"
        )
      );
    } else if (isQuoteMutationAuditDevMode()) {
      console.warn("SAVE_READBACK_SKIPPED_NEWER_LOCAL_EDITS", {
        source: QUOTE_RAW_FIELD_MUTATION_SOURCES.SAVE_READBACK,
        quoteId: quoteDocId,
        saveStartedEditVersion,
        currentEditVersion: currentQuoteEditVersionRef.current,
      });
    }
    setAutoSaveStatus("saved");

  } catch (err) {

    console.error("Save failed:", err);

    setAutoSaveStatus("error");

    alert("Failed to save quotation");

  } finally {

    setIsSaving(false);

  }

};
/* --------------------------- DELETE QUOTATION --------------------------- */
const deleteQuotation = async (quoteId) => {
  if (!canEditQuotes) {
    alert("You do not have permission to delete quotations in this workspace.");
    return;
  }

  if (!workspaceUid || !quoteId) return;

  openConfirmDialog({
    title: "Move quotation to Recycle Bin?",
    description:
      "This quotation will stay in Recycle Bin for 30 days, and you can restore it any time before then.",
    confirmLabel: "Move to Bin",
    cancelLabel: "Cancel",
    tone: "warning",
    onConfirm: async () => {
      const ref = doc(db, "users", workspaceUid, "quotations", quoteId);

      await updateDoc(ref, {
        deleted: true,
        deletedAt: serverTimestamp(),
        deleteAfter: Timestamp.fromDate(
          new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
        ),
      });

      applyQuoteLocalPatch(quoteId, {
        deleted: true,
        deletedAt: new Date(),
        deleteAfter: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(),
      });
      notify("Quotation moved to Recycle Bin.", "success");
    },
  });
};


/* --------------------------- RESTORE QUOTATION --------------------------- */
const restoreQuotation = async (quoteId) => {
  if (!canEditQuotes) {
    alert("You do not have permission to restore quotations in this workspace.");
    return;
  }

  if (!workspaceUid || !quoteId) return;

  const ref = doc(
    db,
    "users",
    workspaceUid,
    "quotations",
    quoteId
  );

  await updateDoc(ref, {
    deleted: false,
    deletedAt: deleteField(),
    deleteAfter: deleteField(),
    updatedAt: serverTimestamp(),
  });

  applyQuoteLocalPatch(quoteId, {
    deleted: false,
    deletedAt: null,
    deleteAfter: null,
    updatedAt: new Date(),
  });
  notify("Quotation restored successfully.", "success");
};


/* --------------------------- LOAD QUOTATION --------------------------- */

/* --------------------------- SAVE AS NEW --------------------------- */
const saveQuotationAsNew = async () => {
  const previousCurtainDraftKey = curtainQuoteDraftKey;

  if (quoteSubTab === CURTAIN_QUOTE_TYPE && !canAccessCurtainQuotes) {
    alert("Curtain quotations are not enabled for this account plan.");
    return;
  }

  if (!canCreateQuotes) {
    alert("You do not have permission to create quotations in this workspace.");
    return;
  }

  if (quoteSubTab === GENERAL_QUOTE_TYPE) {
    await saveGeneralQuotation({ asNew: true });
    return;
  }

  if (isSaving) return;

  setIsSaving(true);
  setAutoSaveStatus("saving");
  const saveStartedEditVersion = currentQuoteEditVersionRef.current;

  try {
    const liveActionPayload = buildLiveCurtainQuotePayload({
      action: "saveAsNew",
    });
    traceLiveActionPayload(liveActionPayload);
    guardStaleActionPayload(liveActionPayload);
    const {
      rows,
      repeatSettings,
      fabricSummaryMeta,
      summaryTotals,
      currentCurtainDiscountFields,
      currentCurtainGstFields,
      customerName,
      quoteNo,
      quoteDate: date,
      selectedQuote,
      pendingCrmLeadId,
      useHalfPieceReuse,
      passHalfPieceSavingsToCustomer,
      quoteFullnessOverrides: liveQuoteFullnessOverrides,
      getRowCalc: getCurtainProductionAdjustedRowCalc,
    } = liveActionPayload;
    const currentQuoteFullnessOverrides = normalizeQuoteFullnessOverrides(
      liveQuoteFullnessOverrides || {}
    );
    const currentQuoteFullnessFields = {
      quoteFullnessOverrides: currentQuoteFullnessOverrides,
    };

    if (!customerName?.trim()) {
      alert("Customer name required");
      return;
    }

    const currentUser = auth.currentUser;

    if (!currentUser || !workspaceUid) {
      alert("Please sign in first.");
      return;
    }

    await syncFabricCatalogueEntriesForQuoteRows(rows);

    const productionRowsForSave = rows.map((row, index) => {
      const productionCalc = getCurtainProductionAdjustedRowCalc(row, index);
      const rowForSaveBase = {
        ...row,
        ...getCurtainRowFabricCatalogueSavePatch(row),
      };
      delete rowForSaveBase.__forceCurrentCalculation;
      delete rowForSaveBase.__recalculated;
      const rowCalculationSnapshot =
        productionCalc.isCurtain || productionCalc.isRoman
          ? productionCalc.usesCalculationSnapshot
            ? row.calculationSnapshot || null
            : buildCurrentCurtainCalculationSnapshot(row, productionCalc)
          : null;
      return {
        ...rowForSaveBase,
        repeatSettings:
          repeatSettings?.[normalizeFabricKey(getCurtainRowFabricKey(row))] || {},
        lineType: productionCalc.lineType || row.lineType || (productionCalc.isAccessory ? "accessory" : "measurement"),
        calculationType: productionCalc.calculationType || row.calculationType || "",
        category: row.category || row.productConfigSnapshot?.category || "",
        calculationSnapshot: rowCalculationSnapshot,
        total: Number(productionCalc.rowTotal ?? productionCalc.total ?? 0),
        rowTotal: Number(productionCalc.rowTotal || 0),
        lineTotal: Number(productionCalc.lineTotal ?? productionCalc.rowTotal ?? 0),
        taxableAmount: Number(productionCalc.taxableAmount ?? productionCalc.rowTotal ?? 0),
        adjSqft: Number(productionCalc.adjSqft ?? productionCalc.billableSqft ?? 0),
        billableSqft: Number(productionCalc.billableSqft ?? productionCalc.adjSqft ?? 0),
        ...(productionCalc.sqftCalculation
          ? {
              rawSqft: Number(productionCalc.sqftCalculation.rawSqft || 0),
              sqftCalculation: {
                ...productionCalc.sqftCalculation,
                rawSqft: Number(productionCalc.sqftCalculation.rawSqft || 0),
                billableSqft: Number(
                  productionCalc.sqftCalculation.billableSqft || 0
                ),
              },
            }
          : {}),
        lining:
          productionCalc.lining ||
          calculateCurtainRowLining(row, 0, false, {
            sqft: productionCalc.adjSqft ?? productionCalc.billableSqft ?? 0,
          }),
        liningAmount: Number(productionCalc.liningAmount || productionCalc.lining?.amount || 0),
        trackConfig: productionCalc.trackConfig || row.trackConfig || { ...DEFAULT_TRACK_CONFIG },
        bendAmount: Number(productionCalc.bendAmount || productionCalc.trackConfig?.bendAmount || 0),
        trackCost: Number(productionCalc.trackCost || 0),
        stitchingCost: Number(productionCalc.stitchingCost || 0),
        fabricGrossAmount: Number(productionCalc.fabricGrossAmount || 0),
        fabricDiscountPercent: Number(productionCalc.fabricDiscountPercent ?? row.discount ?? 0),
        fabricDiscountAmount: Number(productionCalc.fabricDiscountAmount || 0),
        fabricNetAmount: Number(productionCalc.fabricNetAmount ?? productionCalc.fabricCost ?? 0),
        discountScope: productionCalc.discountScope || getRowDiscountScope(row, row.productConfigSnapshot, {
          hasFabricMeterComponent: Boolean(productionCalc.isCurtain || productionCalc.isRoman),
          fabricGrossAmount: productionCalc.fabricGrossAmount || 0,
        }),
        discountBase: Number(productionCalc.discountBase || 0),
        discountAmount: Number(
          productionCalc.discountAmount || productionCalc.fabricDiscountAmount || 0
        ),
        baseLineAmountBeforeDiscount: Number(
          productionCalc.baseLineAmountBeforeDiscount ??
            productionCalc.rowTotal ??
            productionCalc.lineTotal ??
            0
        ),
        fabricCost: Number(productionCalc.fabricCost || 0),
        sqftAmount: Number(productionCalc.sqftAmount || productionCalc.productCharge || 0),
        rowSubtotalBeforeGst: Number(
          productionCalc.rowSubtotalBeforeGst ?? productionCalc.rowTotal ?? 0
        ),
        gstRate: resolveCurtainSavedRowGstRate(row),
        fabricMeters: productionCalc.customerBillableFabricMeters ?? productionCalc.orderedFabricMeters ?? productionCalc.fabricMeters,
        customerBillableFabricMeters:
          productionCalc.customerBillableFabricMeters ?? productionCalc.orderedFabricMeters ?? productionCalc.fabricMeters,
        fullOrderedFabricMeters:
          productionCalc.fullOrderedFabricMeters ??
          productionCalc.noReuseOrderedFabricMeters ??
          productionCalc.orderedFabricMeters ??
          productionCalc.fabricMeters,
        noReuseOrderedFabricMeters:
          productionCalc.noReuseOrderedFabricMeters ??
          productionCalc.customerBillableFabricMeters ??
          productionCalc.orderedFabricMeters ??
          productionCalc.fabricMeters,
        cutFabricMeters: productionCalc.cutFabricMeters ?? productionCalc.fabricMeters,
        workshopFabricRequiredMeters:
          productionCalc.workshopFabricRequiredMeters ??
          productionCalc.workshopNewFabricMeters ??
          productionCalc.newFabricNeededMeters ??
          productionCalc.orderedFabricMeters ??
          productionCalc.fabricMeters,
        workshopNewFabricMeters:
          productionCalc.workshopNewFabricMeters ??
          productionCalc.newFabricNeededMeters ??
          productionCalc.orderedFabricMeters ??
          productionCalc.fabricMeters,
        internalNewFabricNeededMeters:
          productionCalc.internalNewFabricNeededMeters ??
          productionCalc.workshopNewFabricMeters ??
          productionCalc.newFabricNeededMeters ??
          productionCalc.orderedFabricMeters ??
          productionCalc.fabricMeters,
        orderedFabricMeters:
          productionCalc.customerBillableFabricMeters ?? productionCalc.orderedFabricMeters ?? productionCalc.fabricMeters,
        newFabricNeededMeters:
          productionCalc.workshopNewFabricMeters ??
          productionCalc.newFabricNeededMeters ??
          productionCalc.orderedFabricMeters ??
          productionCalc.fabricMeters,
        internalHalfPieceSavingMeters: productionCalc.internalHalfPieceSavingMeters || 0,
        internalHalfPieceSavingValue: productionCalc.internalHalfPieceSavingValue || 0,
        halfReusedMeters: productionCalc.halfReusedMeters || 0,
        internalHalfReusedMeters:
          productionCalc.internalHalfReusedMeters ?? productionCalc.halfReusedMeters ?? 0,
        halfPiecesSaved: productionCalc.halfPiecesSaved || 0,
        halfPiecesReused: productionCalc.halfPiecesReused || 0,
      };
    });
    const rowsForSave = sanitizeCurtainRowsEditableTextFields(
      attachProductSnapshotsToCurtainRows(productionRowsForSave)
    );
    const normalizedFabricSummaryMeta = normalizeFabricSummaryMetaWithRepeatSettings(
      fabricSummaryMeta,
      repeatSettings
    );
    const quoteCalculationSnapshot = buildCurtainQuoteCalculationSnapshot({
      rows,
      summaryTotals,
      getRowCalc: getCurtainProductionAdjustedRowCalc,
    });
    const quoteBlindSqftRoundingMode = normalizeBlindSqftRoundingMode(
      rowsForSave.find((row) => row?.sqftCalculation?.roundingMode)
        ?.sqftCalculation?.roundingMode ||
        selectedQuote?.calculationSettings?.blindSqftRoundingMode ||
        fabricCalcSettings?.blindSqftRoundingMode,
      "exact"
    );
    const currentCurtainCalculationSettings = {
      ...(selectedQuote?.calculationSettings || {}),
      blindSqftRoundingMode: quoteBlindSqftRoundingMode,
      fullnessMode: "quote_override_supported",
      quoteFullnessOverrides: currentQuoteFullnessOverrides,
    };
    const currentCalculationMetadata = {
      calculationVersion: CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
      formulaCheckStatus: "current",
    };
    const recalculationFieldsForSave = resolveCurtainRecalculationFieldsForSave();
    const legacyRevisionAuditForSave =
      pendingRecalculationAudit?.action === "save_new_revision"
        ? pendingRecalculationAudit
        : null;
    const normalizedQuoteDate = resolveQuoteDateForInput(
      {
        ...selectedQuote,
        quoteDate: date || selectedQuote?.quoteDate,
        date: date || selectedQuote?.date,
      },
      todayISO()
    );
    if (normalizedQuoteDate && normalizedQuoteDate !== date) {
      setDate(normalizedQuoteDate);
    }

    /* =================================================
       GENERATE NEW QUOTE NUMBER (TRANSACTION SAFE)
       ================================================= */

    const parentQuoteNoForRevision = String(
      legacyRevisionAuditForSave?.revisionOfQuoteNo ||
        selectedQuote?.quoteNo ||
        quoteNo ||
        ""
    ).trim();
    const revisionBaseQuoteNo =
      parentQuoteNoForRevision.replace(/-R\d+$/i, "") ||
      parentQuoteNoForRevision ||
      quoteNo ||
      "";
    const existingRevisionMatch = parentQuoteNoForRevision.match(/-R(\d+)$/i);
    let nextRevisionNo = legacyRevisionAuditForSave
      ? Number(existingRevisionMatch?.[1] || selectedQuote?.revisionNo || 0) + 1
      : 0;
    let newQuoteNo = legacyRevisionAuditForSave
      ? `${revisionBaseQuoteNo}-R${nextRevisionNo}`
      : await generateQuoteNumber(workspaceUid);
    if (legacyRevisionAuditForSave) {
      let attempts = 0;
      while (attempts < 10) {
        const candidateRef = doc(db, "users", workspaceUid, "quotations", newQuoteNo);
        const candidateSnap = await getDoc(candidateRef);
        if (!candidateSnap.exists()) break;
        attempts += 1;
        nextRevisionNo += 1;
        newQuoteNo = `${revisionBaseQuoteNo}-R${nextRevisionNo}`;
      }
      if (attempts >= 10) {
        newQuoteNo = `${revisionBaseQuoteNo}-R${nextRevisionNo}-${Date.now()}`;
      }
    }
    const legacyRevisionFieldsForSave = legacyRevisionAuditForSave
      ? {
          parentQuoteNo: revisionBaseQuoteNo,
          parentQuoteId:
            legacyRevisionAuditForSave.revisionOfQuoteId ||
            selectedQuote?.id ||
            editingQuoteId ||
            "",
          revisionNo: nextRevisionNo,
          revisionReason: "Recalculated with current formula",
          createdFromLegacyCalculation: true,
        }
      : {};

    setQuoteNo(newQuoteNo);

    /* =================================================
       CREATE NEW QUOTATION DOCUMENT
       ================================================= */

    const quoteDocId = newQuoteNo;

    const ref = doc(
      db,
      "users",
      workspaceUid,
      "quotations",
      quoteDocId
    );
    const curtainIdentity = getCurtainQuoteCustomerIdentity(liveActionPayload);
    const selectedCurtainCustomerId = getCurtainQuoteSelectedCustomerId(
      liveActionPayload
    );
    const customerLedger = await ensureCustomerLedgerRecord(
      curtainIdentity,
      {
        customerId: selectedCurtainCustomerId,
        extraQuote: {
          id: quoteDocId,
          quoteNo: newQuoteNo,
          quoteType: CURTAIN_QUOTE_TYPE,
          quoteDate: normalizedQuoteDate,
          customerId: selectedCurtainCustomerId,
          customerName: curtainIdentity.customerName,
          grandTotal: Number(summaryTotals.grandTotal || 0),
          status: "draft",
        },
      }
    );
    const customerFields = buildQuoteCustomerLinkFields(customerLedger, {
      ...curtainIdentity,
      customerId: selectedCurtainCustomerId,
    });
    const linkedLeadId = pendingCrmLeadId || selectedQuote?.crm?.leadId || "";
    const linkedLead =
      (linkedLeadId &&
        crmLeadRecords.find((lead) => lead.id === linkedLeadId)) ||
      null;
    const newStageId =
      linkedLead?.stageId || getQuoteGivenStageIdForType(CURTAIN_QUOTE_TYPE);
    const newStatus = linkedLead?.status || "draft";
    const crmPayload = buildQuoteCrmData(
      {
        id: quoteDocId,
        ...customerFields,
        quoteNo: newQuoteNo,
        quoteDate: normalizedQuoteDate,
        date: normalizedQuoteDate,
        quotationDate: normalizedQuoteDate,
        rows: rowsForSave,
        repeatSettings,
        fabricSummaryMeta: normalizedFabricSummaryMeta,
          useHalfPieceReuse,
          passHalfPieceSavingsToCustomer,
          ...currentQuoteFullnessFields,
          calculationSnapshot: quoteCalculationSnapshot,
          calculationSettings: currentCurtainCalculationSettings,
          ...currentCalculationMetadata,
          ...legacyRevisionFieldsForSave,
          subtotal: Number(summaryTotals.subTotal || 0),
        grandTotal: Number(summaryTotals.grandTotal || 0),
        ...currentCurtainDiscountFields,
        ...currentCurtainGstFields,
        status: newStatus,
        workStage: newStageId,
        installationDate: linkedLead?.installationDate || null,
        quoteType: CURTAIN_QUOTE_TYPE,
      },
      {
        stageId: newStageId,
        status: newStatus,
        installationDate: linkedLead?.installationDate || null,
        expectedValue: Number(summaryTotals.grandTotal || 0),
        finalValue: Number(summaryTotals.grandTotal || 0),
        leadId: linkedLeadId,
        hasQuote: true,
        customerId: customerFields.customerId,
        customerName: customerFields.customerName,
        customerPhone: customerFields.customerPhone,
        contact: {
          name: customerFields.customerName,
          phone: customerFields.customerPhone,
          whatsapp: customerFields.customerWhatsapp,
          email: customerFields.customerEmail,
          address: customerFields.customerAddress,
        },
      }
    );
    const quoteSearchSource = {
      id: quoteDocId,
      quoteType: CURTAIN_QUOTE_TYPE,
      ...customerFields,
      quoteNo: newQuoteNo,
      quoteDate: normalizedQuoteDate,
      date: normalizedQuoteDate,
      quotationDate: normalizedQuoteDate,
      rows: rowsForSave,
      repeatSettings,
      fabricSummaryMeta: normalizedFabricSummaryMeta,
      useHalfPieceReuse,
      passHalfPieceSavingsToCustomer,
      ...currentQuoteFullnessFields,
      calculationSnapshot: quoteCalculationSnapshot,
      calculationSettings: currentCurtainCalculationSettings,
      ...currentCalculationMetadata,
      ...legacyRevisionFieldsForSave,
      ...recalculationFieldsForSave,
      subtotal: Number(summaryTotals.subTotal || 0),
      grandTotal: Number(summaryTotals.grandTotal || 0),
      ...currentCurtainDiscountFields,
      ...currentCurtainGstFields,
      status: newStatus,
      workStage: newStageId,
      installationDate: linkedLead?.installationDate || null,
      crm: crmPayload,
    };
    const quoteSearchIndex = buildSafeQuoteSearchIndex(quoteSearchSource);

    const curtainRevisionPayload = {
      quoteType: CURTAIN_QUOTE_TYPE,

      ...customerFields,

      quoteNo: newQuoteNo,
      quoteDate: normalizedQuoteDate,
      date: normalizedQuoteDate,
      quotationDate: normalizedQuoteDate,

      rows: rowsForSave,

      repeatSettings: Object.fromEntries(
        Object.entries(repeatSettings).map(([k,v]) => [
          normalizeFabricKey(k),
          v
        ])
      ),
          fabricSummaryMeta: normalizedFabricSummaryMeta,
          useHalfPieceReuse,
          passHalfPieceSavingsToCustomer,
          ...currentQuoteFullnessFields,
          calculationSnapshot: quoteCalculationSnapshot,
      calculationSettings: currentCurtainCalculationSettings,
      ...currentCalculationMetadata,
      ...legacyRevisionFieldsForSave,
      ...recalculationFieldsForSave,

      subtotal: Number(summaryTotals.subTotal || 0),
      grandTotal: Number(summaryTotals.grandTotal || 0),

      ...currentCurtainDiscountFields,
      ...currentCurtainGstFields,

      status: newStatus,
      workStage: newStageId,
      crm: crmPayload,
      searchText: quoteSearchIndex.searchText,
      searchKeywords: quoteSearchIndex.searchKeywords,
      searchTokens: quoteSearchIndex.searchKeywords,
      ...buildQuoteLookupFields(quoteSearchSource),

      installationDate: linkedLead?.installationDate || null,

      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),

    };

    await persistNewQuotationWithTrialGuard({
      quoteId: quoteDocId,
      quotePayload: curtainRevisionPayload,
      quoteIndexSource: {
        ...quoteSearchSource,
        sourceQuoteId: quoteDocId,
      },
    });

    setEditingQuoteId(quoteDocId);

    if (linkedLeadId) {
      await saveCrmFieldsForLead(
        linkedLeadId,
        {
          quoteType: CURTAIN_QUOTE_TYPE,
          stageId: newStageId,
          status: newStatus,
          linkedQuotes: [
            {
              quoteId: quoteDocId,
              quoteNo: newQuoteNo,
              type: CURTAIN_QUOTE_TYPE,
              value: Number(summaryTotals.grandTotal || 0),
              linkedQuoteDate: normalizedQuoteDate,
              isPrimary: true,
            },
          ],
          hasQuote: true,
          customerId: customerFields.customerId,
          customerName: customerFields.customerName,
          customerPhone: customerFields.customerPhone,
          expectedValue: Number(summaryTotals.grandTotal || 0),
          finalValue: Number(summaryTotals.grandTotal || 0),
          installationDate: linkedLead?.installationDate || null,
        },
        { syncQuote: false }
      );
      setPendingCrmLeadId("");
    }

    const crmDealLinkResult = await ensureSavedQuoteCrmDeal(
      {
        id: quoteDocId,
        quoteType: CURTAIN_QUOTE_TYPE,
        ...customerFields,
        quoteNo: newQuoteNo,
        quoteDate: normalizedQuoteDate,
        date: normalizedQuoteDate,
        quotationDate: normalizedQuoteDate,
        rows: rowsForSave,
        repeatSettings,
      fabricSummaryMeta: normalizedFabricSummaryMeta,
      useHalfPieceReuse,
        passHalfPieceSavingsToCustomer,
        ...currentQuoteFullnessFields,
        calculationSnapshot: quoteCalculationSnapshot,
        calculationSettings: currentCurtainCalculationSettings,
        ...currentCalculationMetadata,
        ...legacyRevisionFieldsForSave,
        ...recalculationFieldsForSave,
        subtotal: Number(summaryTotals.subTotal || 0),
        grandTotal: Number(summaryTotals.grandTotal || 0),
        ...currentCurtainDiscountFields,
        ...currentCurtainGstFields,
        status: newStatus,
        workStage: newStageId,
        installationDate: linkedLead?.installationDate || null,
        crm: crmPayload,
      },
      {
        quoteNo: newQuoteNo,
        quoteType: CURTAIN_QUOTE_TYPE,
        totalValue: Number(summaryTotals.grandTotal || 0),
        customerFields,
        linkedLeadId,
        showSaveOutcomeToast: true,
      }
    );
    const finalCrmPayload = crmDealLinkResult?.crmPayload || crmPayload;
    const finalWorkStage = crmDealLinkResult?.stageId || newStageId;
    const finalQuoteStatus = crmDealLinkResult?.status || newStatus;
    const finalInstallationDate =
      finalCrmPayload.installationDate || linkedLead?.installationDate || null;

    await upsertQuoteLocalRecord({
      id: quoteDocId,
      quoteType: CURTAIN_QUOTE_TYPE,
      ...customerFields,
      quoteNo: newQuoteNo,
      quoteDate: normalizedQuoteDate,
      date: normalizedQuoteDate,
      quotationDate: normalizedQuoteDate,
      rows: rowsForSave,
      repeatSettings,
      fabricSummaryMeta: normalizedFabricSummaryMeta,
      useHalfPieceReuse,
      passHalfPieceSavingsToCustomer,
      ...currentQuoteFullnessFields,
      calculationSnapshot: quoteCalculationSnapshot,
      calculationSettings: currentCurtainCalculationSettings,
      ...currentCalculationMetadata,
      ...legacyRevisionFieldsForSave,
      ...recalculationFieldsForSave,
      subtotal: Number(summaryTotals.subTotal || 0),
      grandTotal: Number(summaryTotals.grandTotal || 0),
      ...currentCurtainDiscountFields,
      ...currentCurtainGstFields,
      status: finalQuoteStatus,
      workStage: finalWorkStage,
      installationDate: finalInstallationDate,
      crm: finalCrmPayload,
      createdAt: new Date(),
      updatedAt: new Date(),
    }, { persistIndex: false });
    setQuoteCustomerLink({
      customerId: customerFields.customerId,
      customerKey: customerFields.customerKey,
      customerName: customerFields.customerName,
      phone: customerFields.customerPhone,
      whatsapp: customerFields.customerWhatsapp,
      email: customerFields.customerEmail,
      address: customerFields.customerAddress,
      companyName: customerFields.customerCompanyName,
    });
    if (currentQuoteEditVersionRef.current === saveStartedEditVersion) {
      setSelectedQuote({
        id: quoteDocId,
        quoteType: CURTAIN_QUOTE_TYPE,
        ...customerFields,
        quoteNo: newQuoteNo,
        quoteDate: normalizedQuoteDate,
        date: normalizedQuoteDate,
        quotationDate: normalizedQuoteDate,
        rows: rowsForSave,
        repeatSettings,
        fabricSummaryMeta: normalizedFabricSummaryMeta,
        useHalfPieceReuse,
        passHalfPieceSavingsToCustomer,
        ...currentQuoteFullnessFields,
        calculationSnapshot: quoteCalculationSnapshot,
        calculationSettings: currentCurtainCalculationSettings,
        ...currentCalculationMetadata,
        ...legacyRevisionFieldsForSave,
        ...recalculationFieldsForSave,
        subtotal: Number(summaryTotals.subTotal || 0),
        grandTotal: Number(summaryTotals.grandTotal || 0),
        ...currentCurtainDiscountFields,
        ...currentCurtainGstFields,
        status: finalQuoteStatus,
        workStage: finalWorkStage,
        installationDate: finalInstallationDate,
        crm: finalCrmPayload,
      });
      markCurtainQuoteBaseline(
        {
          ...buildCurtainQuoteDraftData(),
          quoteId: quoteDocId,
          editingQuoteId: quoteDocId,
          quoteNo: newQuoteNo,
          quoteDate: normalizedQuoteDate,
          date: normalizedQuoteDate,
          quotationDate: normalizedQuoteDate,
          selectedQuoteStatus: finalQuoteStatus,
          selectedQuoteWorkStage: finalWorkStage,
          selectedInstallationDate: finalInstallationDate,
        },
        "saved"
      );
      clearCurtainQuoteDraft(previousCurtainDraftKey);
      clearLocalDraft(
        buildLocalDraftKey(
          workspaceUid,
          LOCAL_DRAFT_MODULES.CURTAIN_QUOTE,
          quoteDocId || "new"
        )
      );
    } else if (isQuoteMutationAuditDevMode()) {
      console.warn("SAVE_READBACK_SKIPPED_NEWER_LOCAL_EDITS", {
        source: QUOTE_RAW_FIELD_MUTATION_SOURCES.SAVE_READBACK,
        quoteId: quoteDocId,
        saveStartedEditVersion,
        currentEditVersion: currentQuoteEditVersionRef.current,
      });
    }
    setAutoSaveStatus("saved");
    return true;

  } catch (err) {

    console.error("Save As New failed:", err);

    setAutoSaveStatus("error");

    alert("Failed to save as new quotation");
    return false;

  } finally {

    setIsSaving(false);

  }

};
saveQuotationAsNewRef.current = saveQuotationAsNew;
// ================= SAVE FABRIC STOCK (W1 VERSION  FIXED) =================
// --------------------------- GRAND TOTALS & SUMMARY --------------------------- //

const buildCurtainFabricSummary = useCallback(
  (sourceRows = [], options = {}) => {
    const {
      activeRepeatSettings = repeatSettings,
      halfPieceReuseEnabled = useHalfPieceReuse,
      passSavingsToCustomer = passHalfPieceSavingsToCustomer,
      targetFabricKey = null,
      debugTag = "quote-screen",
      useProductWorkshopRules = false,
      productConfigsForRules = productConfigs,
      fabricCalcSettingsForRules = fabricCalcSettings,
      quoteFullnessOverridesForCalculation = quoteFullnessOverrides,
    } = options;

    const sourceSummaryRows = (Array.isArray(sourceRows) ? sourceRows : [])
      .map((row, index) => withCurtainRowId(row, index))
      .filter((row) => !isDeletedWorkshopExportRow(row))
      .filter(hasMeaningfulWorkshopExportData);

    const plan = buildReuseAwareDemands(sourceSummaryRows, activeRepeatSettings, {
      targetFabricKey,
      enableReuse: halfPieceReuseEnabled,
      debugTag,
      useProductWorkshopRules,
      productConfigsForRules,
      fabricCalcSettingsForRules,
      quoteFullnessOverridesForCalculation,
    });

    const noReusePlan = halfPieceReuseEnabled
      ? buildReuseAwareDemands(sourceSummaryRows, activeRepeatSettings, {
          targetFabricKey,
          enableReuse: false,
          debugTag: `${debugTag}-full-order`,
          useProductWorkshopRules,
          productConfigsForRules,
          fabricCalcSettingsForRules,
          quoteFullnessOverridesForCalculation,
        })
      : plan;

    const sumRowPlanMetersByFabricKey = (rowPlans = [], fieldNames = []) =>
      (Array.isArray(rowPlans) ? rowPlans : []).reduce((acc, rowPlan) => {
        if (!rowPlan) return acc;
        const fabricKey = normalizeFabricKey(rowPlan.fabricKey || "");
        if (!fabricKey) return acc;
        const meters = fieldNames.reduce((resolved, fieldName) => {
          if (Number.isFinite(Number(resolved))) return resolved;
          return rowPlan[fieldName];
        }, undefined);
        acc[fabricKey] = round2(Number(acc[fabricKey] || 0) + Number(meters || 0));
        return acc;
      }, {});

    const fabricToOrderByKey = sumRowPlanMetersByFabricKey(plan.rowPlans, [
      "newFabricNeededMeters",
      "newMeters",
      "orderedFabricMeters",
    ]);
    const fullOrderByKey = sumRowPlanMetersByFabricKey(noReusePlan.rowPlans, [
      "newFabricNeededMeters",
      "newMeters",
      "orderedFabricMeters",
    ]);

    const allFabricKeys = Array.from(
      new Set([
        ...Object.keys(plan.fabricSummaryByKey || {}),
        ...Object.keys(noReusePlan.fabricSummaryByKey || {}),
      ])
    );
    const fabricSummaries = Object.fromEntries(
      allFabricKeys
        .map((fabricKey) => {
          const summary = plan.fabricSummaryByKey?.[fabricKey] || {};
          const fullSummary = noReusePlan.fabricSummaryByKey?.[fabricKey] || summary;
          const normalizedFabricKey = normalizeFabricKey(fabricKey);
          const rowAllocatedFabricToOrderMeters = fabricToOrderByKey[normalizedFabricKey];
          const rowAllocatedFullOrderMeters = fullOrderByKey[normalizedFabricKey];
          const newFabricNeededMeters = round2(
            Number(
              halfPieceReuseEnabled && Number.isFinite(Number(rowAllocatedFabricToOrderMeters))
                ? rowAllocatedFabricToOrderMeters
                : !halfPieceReuseEnabled && Number.isFinite(Number(rowAllocatedFullOrderMeters))
                ? rowAllocatedFullOrderMeters
                : halfPieceReuseEnabled
                ? summary.newFabricNeededMeters ?? summary.orderedFabricMeters ?? 0
                : fullSummary.newFabricNeededMeters ?? fullSummary.orderedFabricMeters ?? 0
            )
          );
          const fullOrderedFabricMeters = round2(
            Number(
              (Number.isFinite(Number(rowAllocatedFullOrderMeters))
                ? rowAllocatedFullOrderMeters
                : undefined) ??
                fullSummary.newFabricNeededMeters ??
                fullSummary.orderedFabricMeters ??
                summary.newFabricNeededMeters ??
                summary.orderedFabricMeters ??
                0
            )
          );
          const workshopFabricRequiredMeters = halfPieceReuseEnabled
            ? newFabricNeededMeters
            : fullOrderedFabricMeters;
          const customerBillableFabricMeters =
            halfPieceReuseEnabled && passSavingsToCustomer
              ? workshopFabricRequiredMeters
              : fullOrderedFabricMeters;
          const cutFabricMeters = round2(
            Number(summary.cutFabricMeters ?? fullSummary.cutFabricMeters ?? 0)
          );
          const halfReusedMeters = halfPieceReuseEnabled
            ? round2(Number(summary.halfReusedMeters || 0))
            : 0;
          if (
            cutFabricMeters <= 0 &&
            fullOrderedFabricMeters <= 0 &&
            workshopFabricRequiredMeters <= 0 &&
            customerBillableFabricMeters <= 0
          ) {
            return null;
          }
          return [
            fabricKey,
            {
              ...summary,
              fabricKey,
              fabricLabel:
                summary.fabricLabel ||
                fullSummary.fabricLabel ||
                summary.displayName ||
                fullSummary.displayName ||
                fabricKey,
              displayName:
                summary.displayName ||
                fullSummary.displayName ||
                summary.fabricLabel ||
                fullSummary.fabricLabel ||
                fabricKey,
              cutFabricMeters,
              halfReusedMeters,
              fabricToOrderMeters: workshopFabricRequiredMeters,
              newFabricNeededMeters,
              fullOrderedFabricMeters,
              customerBillableFabricMeters,
              workshopFabricRequiredMeters,
              meters: workshopFabricRequiredMeters,
              orderedFabricMeters: fullOrderedFabricMeters,
            },
          ];
        })
        .filter(Boolean)
    );

    const productionRows = (plan.rowPlans || []).filter(Boolean).map((rowPlan) => ({
      ...rowPlan,
      item: sourceSummaryRows[rowPlan.rowIndex]?.item || sourceSummaryRows[rowPlan.rowIndex]?.name || "",
      fabric: rowPlan.fabricLabel,
      panels: rowPlan.totalPieces,
      halfPiecesSaved: rowPlan.createdLeftoverId ? 1 : 0,
      halfPiecesReused: Number(rowPlan.reusedHalfCount || 0),
      fabricSource: rowPlan.createdLeftoverId
        ? "new-cut-saved-half"
        : rowPlan.reusedLeftoverId
        ? "reused-half-piece"
        : "new-cut",
    }));

    if (IS_DEV_UI_ENABLED) {
      console.log("HALF_PIECE_SUMMARY_TRACE", {
        enabled: halfPieceReuseEnabled,
        passSavingsToCustomer,
        audit: plan.audit || null,
        fabricSummaries,
        rows: productionRows.map((row) => ({
          item: row.item,
          fabric: row.fabricLabel,
          panels: row.totalPieces,
          cutHeightIn: row.cutHeightIn,
          newFullCuts: row.newFullCuts,
          halfPiecesSaved: row.halfPiecesSaved,
          halfPiecesReused: row.halfPiecesReused,
          cutFabricMeters: row.cutFabricMeters,
          newFabricNeededMeters: row.newFabricNeededMeters,
          fabricSource: row.fabricSource,
        })),
      });
    }

    return {
      fabricSummaries,
      productionRows,
      plan,
      noReusePlan,
    };
  },
  // buildReuseAwareDemands is a function declaration in this component; adding
  // it here would make this callback change every render without changing inputs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [
    calcRow,
    fabricCalcSettings,
    passHalfPieceSavingsToCustomer,
    productConfigs,
    quoteFullnessOverrides,
    repeatSettings,
    useHalfPieceReuse,
  ]
);

const curtainFabricSummaryResult = useMemo(
  () =>
    buildCurtainFabricSummary(rows, {
      activeRepeatSettings: repeatSettings,
      halfPieceReuseEnabled: useHalfPieceReuse,
      passSavingsToCustomer: passHalfPieceSavingsToCustomer,
      debugTag: "quote-screen",
    }),
  [
    rows,
    repeatSettings,
    useHalfPieceReuse,
    passHalfPieceSavingsToCustomer,
    buildCurtainFabricSummary,
  ]
);

const curtainNoReuseBillingResult = useMemo(
  () =>
    buildCurtainFabricSummary(rows, {
      activeRepeatSettings: repeatSettings,
      halfPieceReuseEnabled: false,
      debugTag: "customer-billing-no-reuse",
    }),
  [rows, repeatSettings, buildCurtainFabricSummary]
);

const curtainFabricOrderSummaryResult = useMemo(
  () =>
    buildCurtainFabricSummary(rows, {
      activeRepeatSettings: repeatSettings,
      halfPieceReuseEnabled: useHalfPieceReuse,
      passSavingsToCustomer: passHalfPieceSavingsToCustomer,
      debugTag: "quote-screen-fabric-to-order",
      useProductWorkshopRules: true,
    }),
  [
    rows,
    repeatSettings,
    useHalfPieceReuse,
    passHalfPieceSavingsToCustomer,
    buildCurtainFabricSummary,
  ]
);

const fabricSummaryUI = useMemo(
  () => curtainFabricSummaryResult.fabricSummaries || {},
  [curtainFabricSummaryResult]
);

const fabricOrderSummaryUI = useMemo(
  () => curtainFabricOrderSummaryResult.fabricSummaries || {},
  [curtainFabricOrderSummaryResult]
);

const curtainHalfPieceAudit = useMemo(
  () => curtainFabricSummaryResult?.plan?.audit || null,
  [curtainFabricSummaryResult]
);

const halfPieceAuditResultText = useMemo(() => {
  if (!curtainHalfPieceAudit) return "";
  if (!useHalfPieceReuse) return "Half-piece reuse is off.";
  if (Number(curtainHalfPieceAudit.totalHalfPiecesReused || 0) > 0) {
    return `Half-piece reuse found ${curtainHalfPieceAudit.totalHalfPiecesReused} reused half piece(s), saving ${Number(curtainHalfPieceAudit.totalMetersSaved || 0).toFixed(2)} m for workshop production.`;
  }
  return (
    curtainHalfPieceAudit.reasons?.[0] ||
    "No compatible half pieces were found for the current rows."
  );
}, [curtainHalfPieceAudit, useHalfPieceReuse]);

const canViewHalfPieceAudit =
  IS_DEV_UI_ENABLED;

const copyHalfPieceAuditJson = useCallback(async () => {
  const payload = {
    quoteNo,
    useHalfPieceReuse,
    passHalfPieceSavingsToCustomer,
    audit: curtainHalfPieceAudit,
  };
  const serialized = JSON.stringify(payload, null, 2);
  try {
    await navigator.clipboard.writeText(serialized);
    notify("Half-piece audit JSON copied.", "success");
  } catch (error) {
    console.log("HALF_PIECE_AUDIT_JSON", payload);
    notify("Clipboard unavailable. Half-piece audit JSON logged to console.", "info");
  }
}, [
  curtainHalfPieceAudit,
  notify,
  passHalfPieceSavingsToCustomer,
  quoteNo,
  useHalfPieceReuse,
]);

const getFabricBrandNameById = useCallback(
  (brandId = "") =>
    fabricBrands.find((brand) => brand.id === brandId)?.name || "",
  [fabricBrands]
);

const getCurtainProductionAdjustedRowCalcFromSummary = useCallback(
  (
    row,
    rowIndex = -1,
    activeFabricSummaryResult = null,
    activeNoReuseBillingResult = null
  ) => {
    const calc = calcRow(row, repeatSettings);
    const supportsLining = curtainRowSupportsLining(row, calc);
    if (!calc.isCurtain && !calc.isRoman && !supportsLining) {
      const lining = calculateCurtainRowLining(row, Number(calc.fabricMeters || 0), false);
      return {
        ...calc,
        lining,
        liningAmount: 0,
        customerBillableFabricMeters: Number(calc.fabricMeters || 0),
        workshopNewFabricMeters: Number(calc.fabricMeters || 0),
        cutFabricMeters: Number(calc.cutFabricMeters ?? calc.fabricMetersHalf ?? calc.fabricMeters ?? 0),
      };
    }

    if (calc.usesCalculationSnapshot) {
      const snapshot = calc.calculationSnapshot || row.calculationSnapshot || {};
      const rowPlan =
        Number.isInteger(rowIndex) && rowIndex >= 0
          ? activeFabricSummaryResult?.plan?.rowPlans?.[rowIndex]
          : null;
      const noReuseRowPlan =
        Number.isInteger(rowIndex) && rowIndex >= 0
          ? activeNoReuseBillingResult?.plan?.rowPlans?.[rowIndex]
          : null;
      const orderedFabricMeters = round2(
        Number(
          snapshot.orderedFabricMeters ??
            calc.orderedFabricMeters ??
            snapshot.customerBillableFabricMeters ??
            calc.customerBillableFabricMeters ??
            calc.fabricMeters ??
            0
        )
      );
      const fullOrderedFabricMeters = round2(
        Number(
          noReuseRowPlan?.newFabricNeededMeters ??
            noReuseRowPlan?.newMeters ??
            snapshot.fullOrderedFabricMeters ??
            snapshot.noReuseOrderedFabricMeters ??
            row.fullOrderedFabricMeters ??
            row.noReuseOrderedFabricMeters ??
            orderedFabricMeters
        )
      );
      const hasActualHalfPieceReuse =
        Number(rowPlan?.reusedHalfCount || 0) > 0 ||
        Number(rowPlan?.halfReusedMeters || 0) > 0;
      const workshopFabricRequiredMeters = round2(
        Number(
          rowPlan?.newFabricNeededMeters ??
            rowPlan?.newMeters ??
            snapshot.workshopFabricRequiredMeters ??
            snapshot.workshopNewFabricMeters ??
            row.workshopFabricRequiredMeters ??
            row.workshopNewFabricMeters ??
            row.newFabricNeededMeters ??
            calc.workshopFabricRequiredMeters ??
            calc.workshopNewFabricMeters ??
            calc.newFabricNeededMeters ??
            fullOrderedFabricMeters
        )
      );
      const passSavingsBillableMeters = hasActualHalfPieceReuse
        ? workshopFabricRequiredMeters
        : fullOrderedFabricMeters;
      const customerBillableFabricMeters = passHalfPieceSavingsToCustomer
        ? passSavingsBillableMeters
        : fullOrderedFabricMeters;
      const cutFabricMeters = round2(
        Number(
          rowPlan?.cutFabricMeters ??
            snapshot.cutFabricMeters ??
            calc.cutFabricMeters ??
            calc.fabricMetersHalf ??
            orderedFabricMeters
        )
      );
      const lineTotal = round2(
        Number(snapshot.lineTotal ?? calc.lineTotal ?? calc.rowTotal ?? 0)
      );
      const discountPercent = Math.min(100, num(row.discount));
      const mrpPerMeter = num(row.mrp || row.mrpPerMeter);
      const previousBillableMeters = round2(
        Number(snapshot.customerBillableFabricMeters ?? orderedFabricMeters)
      );
      const computedPreviousFabricGrossAmount = round2(
        previousBillableMeters > 0 && mrpPerMeter > 0
          ? previousBillableMeters * mrpPerMeter
          : 0
      );
      const storedPreviousFabricGrossAmount = Number(snapshot.fabricGrossAmount ?? 0);
      const previousFabricGrossAmount = round2(
        storedPreviousFabricGrossAmount > 0
          ? storedPreviousFabricGrossAmount
          : computedPreviousFabricGrossAmount
      );
      const previousFabricDiscountAmount = round2(
        Number(
          snapshot.fabricDiscountAmount ??
            (previousFabricGrossAmount * discountPercent) / 100
        )
      );
      const computedPreviousFabricCost = round2(
        Math.max(0, previousFabricGrossAmount - previousFabricDiscountAmount)
      );
      const storedPreviousFabricCost = Number(
        snapshot.fabricCost ?? snapshot.fabricAmount ?? calc.fabricCost ?? 0
      );
      const previousFabricCost = round2(
        storedPreviousFabricCost > 0 ? storedPreviousFabricCost : computedPreviousFabricCost
      );
      const fabricGrossAmount = round2(customerBillableFabricMeters * mrpPerMeter);
      const fabricDiscountAmount = round2(fabricGrossAmount * (discountPercent / 100));
      const customerBillableFabricCost = round2(
        Math.max(0, fabricGrossAmount - fabricDiscountAmount)
      );
      const customerBillableRowTotal =
        previousFabricCost > 0
          ? round2(Math.max(0, lineTotal - previousFabricCost + customerBillableFabricCost))
          : lineTotal;
      const lining = calculateCurtainRowLining(
        row,
        customerBillableFabricMeters,
        supportsLining,
        { sqft: calc.adjSqft ?? calc.billableSqft ?? 0 }
      );

      return {
        ...calc,
        lining,
        liningAmount: Number(
          snapshot.liningAmount ?? row.liningAmount ?? calc.liningAmount ?? lining.amount ?? 0
        ),
        fabricMeters: customerBillableFabricMeters || fullOrderedFabricMeters || orderedFabricMeters,
        fabricMetersFull: fullOrderedFabricMeters || orderedFabricMeters,
        fabricMetersHalf: customerBillableFabricMeters || fullOrderedFabricMeters || orderedFabricMeters,
        orderedFabricMeters: customerBillableFabricMeters || fullOrderedFabricMeters || orderedFabricMeters,
        noReuseOrderedFabricMeters: fullOrderedFabricMeters,
        fullOrderedFabricMeters,
        customerBillableFabricMeters,
        workshopNewFabricMeters: workshopFabricRequiredMeters,
        workshopFabricRequiredMeters,
        internalNewFabricNeededMeters: Number(
          row.internalNewFabricNeededMeters ??
            calc.internalNewFabricNeededMeters ??
            workshopFabricRequiredMeters
        ),
        newFabricNeededMeters: Number(
          snapshot.newFabricNeededMeters ??
            row.newFabricNeededMeters ??
            calc.newFabricNeededMeters ??
            workshopFabricRequiredMeters
        ),
        cutFabricMeters,
        fabricCost: customerBillableFabricCost,
        fabricGrossAmount,
        fabricDiscountPercent: discountPercent,
        fabricDiscountAmount,
        fabricNetAmount: customerBillableFabricCost,
        discountScope: "fabric_only",
        discountBase: fabricGrossAmount,
        discountAmount: fabricDiscountAmount,
        totalPieces: passHalfPieceSavingsToCustomer && hasActualHalfPieceReuse
          ? Number(rowPlan?.newFullCuts || 0) + Number(rowPlan?.reusedHalfCount || 0) * 0.5
          : Number(noReuseRowPlan?.newFullCuts ?? noReuseRowPlan?.wholePanels ?? snapshot.pieces ?? calc.pcsFull ?? 0),
        pcs: passHalfPieceSavingsToCustomer && hasActualHalfPieceReuse
          ? Number(rowPlan?.newFullCuts || 0) + Number(rowPlan?.reusedHalfCount || 0) * 0.5
          : Number(noReuseRowPlan?.newFullCuts ?? noReuseRowPlan?.wholePanels ?? snapshot.pieces ?? calc.pcs ?? 0),
        pcsFull: Number(noReuseRowPlan?.newFullCuts ?? noReuseRowPlan?.wholePanels ?? snapshot.pieces ?? calc.pcsFull ?? 0),
        pcsHalf: Number(rowPlan?.totalPieces ?? rowPlan?.halfStepPanels ?? snapshot.pieces ?? calc.pcsHalf ?? 0),
        rawPanels: Number(rowPlan?.rawPanels ?? noReuseRowPlan?.rawPanels ?? snapshot.rawPieces ?? 0),
        halfStepProductionPanels: Number(rowPlan?.totalPieces ?? rowPlan?.halfStepPanels ?? calc.pcsHalf ?? 0),
        wholeBillingPanels: Number(noReuseRowPlan?.newFullCuts ?? noReuseRowPlan?.wholePanels ?? calc.pcsFull ?? 0),
        workshopPanels: Number(rowPlan?.newFullCuts || 0) + Number(rowPlan?.reusedHalfCount || 0) * 0.5,
        customerBillingPanels: passHalfPieceSavingsToCustomer && hasActualHalfPieceReuse
          ? Number(rowPlan?.newFullCuts || 0) + Number(rowPlan?.reusedHalfCount || 0) * 0.5
          : Number(noReuseRowPlan?.newFullCuts ?? noReuseRowPlan?.wholePanels ?? calc.pcsFull ?? 0),
        calculationVersion: snapshot.calculationVersion || calc.calculationVersion || "",
        rowSubtotalBeforeGst: customerBillableRowTotal,
        rowTotal: customerBillableRowTotal,
        lineTotal: customerBillableRowTotal,
        taxableAmount: customerBillableRowTotal,
      };
    }

    const rowPlan =
      Number.isInteger(rowIndex) && rowIndex >= 0
        ? activeFabricSummaryResult?.plan?.rowPlans?.[rowIndex]
        : null;
    const noReuseRowPlan =
      Number.isInteger(rowIndex) && rowIndex >= 0
        ? activeNoReuseBillingResult?.plan?.rowPlans?.[rowIndex]
        : null;
    if (!rowPlan) {
      const fallbackNewFabricMeters = round2(
        Number(
          noReuseRowPlan?.newFabricNeededMeters ??
            noReuseRowPlan?.newMeters ??
            calc.customerBillableFabricMeters ??
            calc.fabricMetersFull ??
            calc.fabricMeters ??
            0
        )
      );
      const fallbackCutFabricMeters = round2(
        Number(noReuseRowPlan?.cutFabricMeters ?? calc.cutFabricMeters ?? calc.fabricMetersHalf ?? calc.fabricMeters ?? 0)
      );
      const customerBillableFabricMeters = fallbackNewFabricMeters;
      const discountPercent = Math.min(100, num(row.discount));
      const mrpPerMeter = num(row.mrp || row.mrpPerMeter);
      const customerBillableFabricCost = round2(
        customerBillableFabricMeters * mrpPerMeter * (1 - discountPercent / 100)
      );
      const fabricGrossAmount = round2(customerBillableFabricMeters * mrpPerMeter);
      const fabricDiscountAmount = round2(fabricGrossAmount * (discountPercent / 100));
      const lining = calculateCurtainRowLining(
        row,
        customerBillableFabricMeters,
        supportsLining,
        { sqft: calc.adjSqft ?? calc.billableSqft ?? 0 }
      );
      const customerBillableRowTotal = round2(
        Number(calc.rowTotal || 0) -
          Number(calc.fabricCost || 0) +
          customerBillableFabricCost +
          Number(lining.amount || 0)
      );
      return {
        ...calc,
        lining,
        liningAmount: Number(lining.amount || 0),
        fabricMeters: customerBillableFabricMeters,
        orderedFabricMeters: customerBillableFabricMeters,
        customerBillableFabricMeters,
        noReuseOrderedFabricMeters: fallbackNewFabricMeters,
        workshopNewFabricMeters: fallbackNewFabricMeters,
        internalNewFabricNeededMeters: fallbackNewFabricMeters,
        newFabricNeededMeters: fallbackNewFabricMeters,
        fullOrderedFabricMeters: fallbackNewFabricMeters,
        workshopFabricRequiredMeters: fallbackNewFabricMeters,
        cutFabricMeters: fallbackCutFabricMeters,
        internalHalfReusedMeters: 0,
        rawPanels: Number(noReuseRowPlan?.rawPanels ?? calc.rawPanels ?? 0),
        halfStepProductionPanels: Number(noReuseRowPlan?.totalPieces ?? noReuseRowPlan?.halfStepPanels ?? calc.pcsHalf ?? 0),
        wholeBillingPanels: Number(noReuseRowPlan?.newFullCuts ?? noReuseRowPlan?.wholePanels ?? calc.pcsFull ?? calc.totalPieces ?? 0),
        workshopPanels: Number(noReuseRowPlan?.newFullCuts ?? noReuseRowPlan?.wholePanels ?? calc.pcsFull ?? calc.totalPieces ?? 0),
        customerBillingPanels: Number(noReuseRowPlan?.newFullCuts ?? noReuseRowPlan?.wholePanels ?? calc.pcsFull ?? calc.totalPieces ?? 0),
        fabricCost: customerBillableFabricCost,
        fabricGrossAmount,
        fabricDiscountPercent: discountPercent,
        fabricDiscountAmount,
        fabricNetAmount: customerBillableFabricCost,
        discountScope: "fabric_only",
        discountBase: fabricGrossAmount,
        discountAmount: fabricDiscountAmount,
        baseLineAmountBeforeDiscount: round2(
          fabricGrossAmount +
            Number(calc.sqftAmount || calc.productCharge || 0) +
            Number(calc.stitchingCost || 0) +
            Number(calc.trackCost || 0) +
            Number(calc.bendAmount || 0) +
            Number(lining.amount || 0)
        ),
        sqftAmount: Number(calc.sqftAmount || calc.productCharge || 0),
        rowSubtotalBeforeGst: customerBillableRowTotal,
        rowTotal: customerBillableRowTotal,
        lineTotal: customerBillableRowTotal,
        taxableAmount: customerBillableRowTotal,
      };
    }

    const workshopNewFabricMeters = round2(
      Number(rowPlan.newFabricNeededMeters ?? rowPlan.newMeters ?? calc.fabricMeters ?? 0)
    );
    const noReuseOrderedFabricMeters = round2(
      Number(
        noReuseRowPlan?.newFabricNeededMeters ??
          noReuseRowPlan?.newMeters ??
          calc.customerBillableFabricMeters ??
          calc.fabricMetersFull ??
          calc.fabricMeters ??
          0
      )
    );
    const cutFabricMeters = round2(
      Number(rowPlan.cutFabricMeters ?? noReuseRowPlan?.cutFabricMeters ?? calc.fabricMetersHalf ?? calc.fabricMeters ?? 0)
    );
    const hasActualHalfPieceReuse =
      Number(rowPlan.reusedHalfCount || 0) > 0 ||
      Number(rowPlan.halfReusedMeters || 0) > 0;
    const passSavingsBillableMeters = hasActualHalfPieceReuse
      ? workshopNewFabricMeters
      : noReuseOrderedFabricMeters;
    const customerBillableFabricMeters = passHalfPieceSavingsToCustomer
      ? passSavingsBillableMeters
      : noReuseOrderedFabricMeters;
    const halfStepProductionPanels = Number(
      rowPlan.totalPieces ?? rowPlan.halfStepPanels ?? calc.pcsHalf ?? calc.totalPieces ?? 0
    );
    const wholeBillingPanels = Number(
      noReuseRowPlan?.newFullCuts ??
        noReuseRowPlan?.wholePanels ??
        calc.pcsFull ??
        calc.totalPieces ??
        0
    );
    const workshopPanels = Number(rowPlan.newFullCuts || 0) + Number(rowPlan.reusedHalfCount || 0) * 0.5;
    const customerBillingPanels =
      passHalfPieceSavingsToCustomer && hasActualHalfPieceReuse
        ? workshopPanels
        : wholeBillingPanels;
    const discountPercent = Math.min(100, num(row.discount));
    const mrpPerMeter = num(row.mrp || row.mrpPerMeter);
    const customerBillableFabricCost = round2(
      customerBillableFabricMeters * mrpPerMeter * (1 - discountPercent / 100)
    );
    const fabricGrossAmount = round2(customerBillableFabricMeters * mrpPerMeter);
    const fabricDiscountAmount = round2(fabricGrossAmount * (discountPercent / 100));
    const lining = calculateCurtainRowLining(
      row,
      customerBillableFabricMeters,
      supportsLining,
      { sqft: calc.adjSqft ?? calc.billableSqft ?? 0 }
    );
    const customerBillableRowTotal = round2(
      Number(calc.rowTotal || 0) -
        Number(calc.fabricCost || 0) +
        customerBillableFabricCost +
        Number(lining.amount || 0)
    );
    const internalHalfPieceSavingMeters = round2(
      Math.max(0, noReuseOrderedFabricMeters - workshopNewFabricMeters)
    );
    const internalHalfPieceSavingValue = round2(
      internalHalfPieceSavingMeters * mrpPerMeter * (1 - discountPercent / 100)
    );

    return {
      ...calc,
      lining,
      liningAmount: Number(lining.amount || 0),
      fabricMeters: customerBillableFabricMeters,
      orderedFabricMeters: customerBillableFabricMeters,
      noReuseOrderedFabricMeters,
      fullOrderedFabricMeters: noReuseOrderedFabricMeters,
      customerBillableFabricMeters,
      workshopNewFabricMeters,
      workshopFabricRequiredMeters: workshopNewFabricMeters,
      internalNewFabricNeededMeters: workshopNewFabricMeters,
      newFabricNeededMeters: workshopNewFabricMeters,
      cutFabricMeters,
      rawPanels: Number(rowPlan.rawPanels ?? noReuseRowPlan?.rawPanels ?? 0),
      halfStepProductionPanels,
      wholeBillingPanels,
      workshopPanels,
      customerBillingPanels,
      halfReusedMeters: Number(rowPlan.halfReusedMeters || 0),
      internalHalfReusedMeters: Number(rowPlan.halfReusedMeters || 0),
      halfPiecesSaved: rowPlan.createdLeftoverId ? 1 : 0,
      halfPiecesReused: Number(rowPlan.reusedHalfCount || 0),
      internalHalfPieceSavingMeters,
      internalHalfPieceSavingValue,
      passHalfPieceSavingsToCustomer: passHalfPieceSavingsToCustomer === true,
      fabricCost: customerBillableFabricCost,
      fabricGrossAmount,
      fabricDiscountPercent: discountPercent,
      fabricDiscountAmount,
      fabricNetAmount: customerBillableFabricCost,
      discountScope: "fabric_only",
      discountBase: fabricGrossAmount,
      discountAmount: fabricDiscountAmount,
      baseLineAmountBeforeDiscount: round2(
        fabricGrossAmount +
          Number(calc.sqftAmount || calc.productCharge || 0) +
          Number(calc.stitchingCost || 0) +
          Number(calc.trackCost || 0) +
          Number(calc.bendAmount || 0) +
          Number(lining.amount || 0)
      ),
      sqftAmount: Number(calc.sqftAmount || calc.productCharge || 0),
      rowSubtotalBeforeGst: customerBillableRowTotal,
      rowTotal: customerBillableRowTotal,
      lineTotal: customerBillableRowTotal,
      taxableAmount: customerBillableRowTotal,
      totalPieces: customerBillingPanels,
      pcsFull: wholeBillingPanels,
      pcsHalf: halfStepProductionPanels,
      newFullCuts: Number(rowPlan.newFullCuts ?? calc.pcsFull ?? 0),
    };
  },
  [
    calcRow,
    passHalfPieceSavingsToCustomer,
    repeatSettings,
  ]
);

const getCurtainProductionAdjustedRowCalc = useCallback(
  (row, rowIndex = -1) =>
    getCurtainProductionAdjustedRowCalcFromSummary(
      row,
      rowIndex,
      curtainFabricSummaryResult,
      curtainNoReuseBillingResult
    ),
  [
    curtainFabricSummaryResult,
    curtainNoReuseBillingResult,
    getCurtainProductionAdjustedRowCalcFromSummary,
  ]
);

/* =============================================
    TOTALS  always recompute using calcRow()
   ============================================= */
const totals = useMemo(() => {
  return round2(
    rows.reduce(
      (sum, r, index) => sum + getCurtainProductionAdjustedRowCalc(r, index).rowTotal,
      0
    )
  );
}, [
  rows,
  getCurtainProductionAdjustedRowCalc,
]);


/* =============================================
    SUMMARY TOTALS  reacts instantly
   ============================================= */
const summaryTotals = useMemo(() => {
  const subTotal = totals;
  const gstTotals = calculateCurtainQuoteTotals({
    rows,
    subtotal: subTotal,
    discountType: getDiscountTypeFromSummaryMode(summaryDiscountMode),
    discountValue: summaryDiscountValue,
    quoteGstRate: curtainGstRate,
    getRowTaxableTotal: (row, index) =>
      getCurtainProductionAdjustedRowCalc(row, index).rowTotal,
    isAccessoryRow: isCurtainAccessoryRowStatic,
  });

  return {
    subTotal: round2(subTotal),
    discountType: gstTotals.discountType,
    discountValue: gstTotals.discountValue,
    discountAmount: gstTotals.discountAmount,
    discountApplication: gstTotals.discountApplication || "after_gst",
    postGstDiscountAmount: gstTotals.postGstDiscountAmount ?? gstTotals.discountAmount,
    preGstGrandTotal: gstTotals.subtotalBeforeGst,
    subtotalBeforeGst: gstTotals.subtotalBeforeGst,
    totalAfterGst: gstTotals.totalAfterGst,
    measurementSubtotalBeforeDiscount: gstTotals.measurementSubtotalBeforeDiscount,
    accessorySubtotalBeforeDiscount: gstTotals.accessorySubtotalBeforeDiscount,
    accessorySubtotalBeforeGst: gstTotals.accessorySubtotalBeforeDiscount,
    measurementDiscountAmount: gstTotals.measurementDiscountAmount,
    accessoryDiscountAmount: gstTotals.accessoryDiscountAmount,
    taxableMeasurementAmount: gstTotals.taxableMeasurementAmount,
    taxableAccessoryAmount: gstTotals.taxableAccessoryAmount,
    taxableAmount: gstTotals.taxableAmount,
    quoteGstRate: gstTotals.quoteGstRate,
    gstRate: gstTotals.gstRate,
    gstRates: gstTotals.gstRates || [gstTotals.gstRate],
    accessoryGstRates: gstTotals.accessoryGstRates || [],
    hasMixedGstRates: gstTotals.hasMixedGstRates === true,
    measurementGstAmount: gstTotals.measurementGstAmount,
    accessoryGstAmount: gstTotals.accessoryGstAmount,
    gstAmount: gstTotals.gstAmount,
    totalGstAmount: gstTotals.totalGstAmount,
    finalTotal: gstTotals.finalTotal,
    grandTotal: gstTotals.grandTotal,
  };
}, [
  curtainGstRate,
  getCurtainProductionAdjustedRowCalc,
  rows,
  totals,
  summaryDiscountMode,
  summaryDiscountValue,
]);

curtainSummaryTotalsRef.current = summaryTotals;

useEffect(() => {
  if (!isDevRuntime) return;
  const totalAfterGst = Number(summaryTotals.totalAfterGst || 0);
  const discountAmount = Number(summaryTotals.discountAmount || 0);
  const expectedGrandTotal = round2(Math.max(0, totalAfterGst - discountAmount));
  const actualGrandTotal = round2(Number(summaryTotals.grandTotal || 0));
  if (discountAmount > 0.005 && Math.abs(expectedGrandTotal - actualGrandTotal) > 0.05) {
    console.error("DISCOUNT_NOT_APPLIED_TO_GRAND_TOTAL", {
      quoteType: CURTAIN_QUOTE_TYPE,
      quoteNo,
      totalAfterGst,
      discountType: summaryTotals.discountType,
      discountValue: summaryTotals.discountValue,
      discountAmount,
      grandTotal: actualGrandTotal,
      expectedGrandTotal,
    });
  }
}, [
  quoteNo,
  summaryTotals.discountAmount,
  summaryTotals.discountType,
  summaryTotals.discountValue,
  summaryTotals.grandTotal,
  summaryTotals.totalAfterGst,
]);

const legacyCurtainCalculationCandidateRows = useMemo(
  () =>
    rows.filter(
      (row) => row.calculationSnapshot?.requiresRecalculation === true
    ),
  [rows]
);
const hasLegacyCurtainCalculationRows = legacyCurtainCalculationCandidateRows.length > 0;
const curtainLegacyVersionStatus = useMemo(() => {
  if (!hasLegacyCurtainCalculationRows) {
    return LEGACY_CALCULATION_VERSION_STATUS.CURRENT;
  }
  if (legacyCurtainCalculationCandidateRows.some((row) => !row.calculationSnapshot)) {
    return LEGACY_CALCULATION_VERSION_STATUS.MISSING_SNAPSHOT;
  }
  if (
    legacyCurtainCalculationCandidateRows.some(
      (row) => !row.calculationSnapshot?.calculationVersion
    )
  ) {
    return LEGACY_CALCULATION_VERSION_STATUS.MISSING_VERSION;
  }
  if (
    legacyCurtainCalculationCandidateRows.some(
      (row) =>
        row.calculationSnapshot?.calculationVersion !==
        CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION
    )
  ) {
    return LEGACY_CALCULATION_VERSION_STATUS.OLDER_VERSION;
  }
  return LEGACY_CALCULATION_VERSION_STATUS.CURRENT;
}, [hasLegacyCurtainCalculationRows, legacyCurtainCalculationCandidateRows]);
const curtainQuoteTotalMode =
  hasLegacyCurtainCalculationRows &&
  !pendingRecalculationAudit &&
  !legacyRecalculationRevisionRequest &&
  !curtainQuoteIsDirty &&
  curtainTotalsSettingsEditVersion <= 0
    ? "savedSnapshot"
    : "liveEditing";
const isFrozenLegacyCurtainQuote = curtainQuoteTotalMode === "savedSnapshot";

const getCurtainSavedTotalsFromQuote = useCallback(
  (quote = selectedQuote) => {
    if (!quote || resolveQuoteType(quote) !== CURTAIN_QUOTE_TYPE) return null;
    const snapshotTotals = quote.calculationSnapshot?.totals || {};
    const storedGrandTotal = Number(
      snapshotTotals.grandTotal ??
        quote.grandTotal ??
        quote.finalTotal ??
        quote.finalAmount ??
        quote.totalAmount ??
        0
    );
    const storedSubtotal = Number(
      snapshotTotals.subtotal ??
        quote.subtotal ??
        quote.subTotal ??
        quote.totalAmount ??
        storedGrandTotal ??
        0
    );
    const storedDiscount = Number(
      snapshotTotals.discount ??
        quote.discountAmount ??
        quote.discount ??
        0
    );
    const storedGst = Number(
      snapshotTotals.gstAmount ??
        quote.totalGstAmount ??
        quote.gstAmount ??
        0
    );
    const storedTaxable = Number(
      snapshotTotals.taxableAmount ??
        quote.taxableAmount ??
        quote.subtotalBeforeGst ??
        quote.preGstGrandTotal ??
        Math.max(0, storedSubtotal - storedDiscount)
    );

    if (
      !Number.isFinite(storedGrandTotal) &&
      !Number.isFinite(storedSubtotal)
    ) {
      return null;
    }

    return {
      subTotal: Number.isFinite(storedSubtotal) ? round2(storedSubtotal) : 0,
      subtotalBeforeGst: Number.isFinite(storedTaxable) ? round2(storedTaxable) : 0,
      taxableAmount: Number.isFinite(storedTaxable) ? round2(storedTaxable) : 0,
      discountAmount: Number.isFinite(storedDiscount) ? round2(storedDiscount) : 0,
      discountApplication: quote.discountApplication || snapshotTotals.discountApplication || "legacy",
      gstAmount: Number.isFinite(storedGst) ? round2(storedGst) : 0,
      totalGstAmount: Number.isFinite(storedGst) ? round2(storedGst) : 0,
      totalAfterGst: Number(
        snapshotTotals.totalAfterGst ??
          quote.totalAfterGst ??
          (Number.isFinite(storedTaxable) || Number.isFinite(storedGst)
            ? round2(Number(storedTaxable || 0) + Number(storedGst || 0))
            : 0)
      ),
      grandTotal: Number.isFinite(storedGrandTotal) ? round2(storedGrandTotal) : 0,
      quoteGstRate: normalizeCurtainGstRate(
        quote.quoteGstRate ?? quote.gstRate ?? snapshotTotals.quoteGstRate
      ),
      source: snapshotTotals.grandTotal != null ? "calculationSnapshot" : "storedQuoteFields",
    };
  },
  [selectedQuote]
);
const curtainDisplaySummaryTotals = useMemo(() => {
  if (!isFrozenLegacyCurtainQuote) return summaryTotals;
  const savedTotals = getCurtainSavedTotalsFromQuote(selectedQuote);
  if (!savedTotals) return summaryTotals;
  return {
    ...summaryTotals,
    subTotal: savedTotals.subTotal,
    subtotalBeforeGst: savedTotals.subtotalBeforeGst,
    preGstGrandTotal: savedTotals.subtotalBeforeGst,
    taxableAmount: savedTotals.taxableAmount,
    discountAmount: savedTotals.discountAmount,
    discountApplication: savedTotals.discountApplication,
    gstAmount: savedTotals.gstAmount,
    totalGstAmount: savedTotals.totalGstAmount,
    totalAfterGst: savedTotals.totalAfterGst,
    measurementGstAmount:
      Number(savedTotals.totalGstAmount || 0) > 0
        ? savedTotals.totalGstAmount
        : summaryTotals.measurementGstAmount,
    grandTotal: savedTotals.grandTotal,
    finalTotal: savedTotals.grandTotal,
    quoteGstRate: savedTotals.quoteGstRate,
    gstRate: savedTotals.quoteGstRate,
    source: savedTotals.source,
  };
}, [
  getCurtainSavedTotalsFromQuote,
  isFrozenLegacyCurtainQuote,
  selectedQuote,
  summaryTotals,
]);

useEffect(() => {
  if (!isDevRuntime) return;
  console.log("DISCOUNT_INPUT_STATE_TRACE", {
    quoteType: CURTAIN_QUOTE_TYPE,
    quoteNo,
    inputRawValue: summaryDiscountValue,
    parsedDiscountValue: num(summaryDiscountValue),
    discountType: getDiscountTypeFromSummaryMode(summaryDiscountMode),
    quoteSettingsDiscountValue: summaryDiscountValue,
    helperDiscountValue: summaryTotals.discountValue,
    helperDiscountType: summaryTotals.discountType,
    liveDiscountAmount: summaryTotals.discountAmount,
    liveTotalAfterGst: summaryTotals.totalAfterGst,
    liveGrandTotal: summaryTotals.grandTotal,
    displayedGrandTotal: curtainDisplaySummaryTotals.grandTotal,
    quoteDisplayMode: curtainQuoteTotalMode,
    totalsSettingsEditVersion: curtainTotalsSettingsEditVersion,
  });
}, [
  curtainDisplaySummaryTotals.grandTotal,
  curtainQuoteTotalMode,
  curtainTotalsSettingsEditVersion,
  quoteNo,
  summaryDiscountMode,
  summaryDiscountValue,
  summaryTotals.discountAmount,
  summaryTotals.discountType,
  summaryTotals.discountValue,
  summaryTotals.grandTotal,
  summaryTotals.totalAfterGst,
]);

useEffect(() => {
  if (!isDevRuntime || !isFrozenLegacyCurtainQuote) return;
  const savedTotals = getCurtainSavedTotalsFromQuote(selectedQuote);
  if (!savedTotals) return;
  const displayedGrandTotal = Number(curtainDisplaySummaryTotals.grandTotal || 0);
  const liveCalculatedGrandTotal = Number(summaryTotals.grandTotal || 0);
  const savedSnapshotGrandTotal = Number(savedTotals.grandTotal || 0);
  if (Math.abs(displayedGrandTotal - savedSnapshotGrandTotal) >= 0.01) {
    console.error("QUOTE_TOTAL_SOURCE_MISMATCH", {
      quoteNo,
      mode: curtainQuoteTotalMode,
      displayedGrandTotal,
      savedSnapshotGrandTotal,
      liveCalculatedGrandTotal,
    });
  }
}, [
  curtainDisplaySummaryTotals.grandTotal,
  curtainQuoteTotalMode,
  getCurtainSavedTotalsFromQuote,
  isFrozenLegacyCurtainQuote,
  quoteNo,
  selectedQuote,
  summaryTotals.grandTotal,
]);

useEffect(() => {
  if (!isDevRuntime || isFrozenLegacyCurtainQuote) return;
  const liveSubtotal = round2(
    rows.reduce(
      (sum, row, index) =>
        sum + Number(getCurtainProductionAdjustedRowCalc(row, index).rowTotal || 0),
      0
    )
  );
  const displayedSubtotal = round2(Number(curtainDisplaySummaryTotals.subTotal || 0));
  if (Math.abs(liveSubtotal - displayedSubtotal) >= 0.01) {
    console.error("QUOTE_TOTAL_STALE_STATE_DETECTED", {
      quoteType: CURTAIN_QUOTE_TYPE,
      quoteNo,
      mode: curtainQuoteTotalMode,
      changedField: "rows",
      liveSubtotal,
      displayedSubtotal,
      lineTotals: rows.map((row, index) => ({
        rowId: getWorkshopRowStableId(row) || index,
        rowTotal: Number(getCurtainProductionAdjustedRowCalc(row, index).rowTotal || 0),
      })),
      subtotal: displayedSubtotal,
      totalGst: Number(curtainDisplaySummaryTotals.totalGstAmount || 0),
      totalAfterGst: Number(curtainDisplaySummaryTotals.totalAfterGst || 0),
      discountAmount: Number(curtainDisplaySummaryTotals.discountAmount || 0),
      grandTotal: Number(curtainDisplaySummaryTotals.grandTotal || 0),
      displayedGrandTotal: curtainDisplaySummaryTotals.grandTotal,
      liveGrandTotal: summaryTotals.grandTotal,
      rowCount: rows.length,
    });
  }
}, [
  curtainDisplaySummaryTotals.grandTotal,
  curtainDisplaySummaryTotals.discountAmount,
  curtainDisplaySummaryTotals.subTotal,
  curtainDisplaySummaryTotals.totalAfterGst,
  curtainDisplaySummaryTotals.totalGstAmount,
  curtainQuoteTotalMode,
  getCurtainProductionAdjustedRowCalc,
  isFrozenLegacyCurtainQuote,
  quoteNo,
  rows,
  summaryTotals.grandTotal,
]);

const legacyPreviewFieldDefinitions = useMemo(() => ({
  pieces: {
    rowFields: ["finalPieces", "totalPieces", "pcsFull", "pcs"],
    snapshotFields: ["pieces"],
  },
  orderedFabric: {
    rowFields: [
      "orderedFabricMeters",
      "customerBillableFabricMeters",
      "fabricMeters",
      "fabricMetersFull",
    ],
    snapshotFields: ["orderedFabricMeters", "customerBillableFabricMeters", "fabricMeters"],
  },
  fabricAmount: {
    rowFields: ["fabricAmount", "fabricCost"],
    snapshotFields: ["fabricAmount", "fabricCost"],
  },
  stitchingTrackAmount: {
    rowFields: [
      "stitchingTrackAmount",
      "stitchTrackAmount",
      "stitchingAndTrackAmount",
    ],
    snapshotFields: ["stitchingTrackAmount", "stitchTrackAmount"],
  },
  liningAmount: {
    rowFields: ["liningAmount"],
    snapshotFields: ["liningAmount"],
  },
  rowTotal: {
    rowFields: ["lineTotal", "rowTotal", "total", "amount", "finalAmount"],
    snapshotFields: ["lineTotal", "rowTotal", "total"],
  },
}), []);

const getLegacyFieldDisplay = useCallback((row = {}, fieldName = "") => {
  const definition = legacyPreviewFieldDefinitions[fieldName] || {
    rowFields: [fieldName],
    snapshotFields: [fieldName],
  };
  const snapshot = row.calculationSnapshot || {};
  const storedMap = snapshot.storedFieldMap || null;
  const isSyntheticLegacySnapshotWithoutMap =
    snapshot.legacyFormula === true &&
    snapshot.requiresRecalculation === true &&
    !storedMap;

  for (const field of definition.rowFields || []) {
    if (hasOwnStoredField(row, field)) {
      const value = Number(row[field]);
      return {
        exists: true,
        value: Number.isFinite(value) ? value : row[field],
        source: field,
      };
    }
  }

  if (storedMap && storedMap[fieldName] === false) {
    return {
      exists: false,
      value: null,
      display: "Not stored in legacy quote",
      source: "",
    };
  }

  if (!isSyntheticLegacySnapshotWithoutMap || fieldName === "rowTotal") {
    for (const field of definition.snapshotFields || []) {
      if (hasOwnStoredField(snapshot, field)) {
        const value = Number(snapshot[field]);
        if (
          isSyntheticLegacySnapshotWithoutMap &&
          fieldName === "rowTotal" &&
          (!Number.isFinite(value) || value <= 0)
        ) {
          continue;
        }
        return {
          exists: true,
          value: Number.isFinite(value) ? value : snapshot[field],
          source: `calculationSnapshot.${field}`,
        };
      }
    }
  }

  return {
    exists: false,
    value: null,
    display: "Not stored in legacy quote",
    source: "",
  };
}, [legacyPreviewFieldDefinitions]);

const rowIsAffectedByLegacyCurtainFormula = useCallback((row = {}, productConfig = null) => {
  const config =
    productConfig || resolveProductConfig(row, productConfigs, fabricCalcSettings);
  return rowIsAffectedByLegacyCurtainFormulaStatic(row, config);
}, [fabricCalcSettings, productConfigs]);

const isLegacyCurtainRecalculationTarget = useCallback((row = {}) => {
  if (row.calculationSnapshot?.requiresRecalculation !== true) return false;
  if (row.__forceCurrentCalculation === true || row.__recalculated === true) {
    return false;
  }
  return rowIsAffectedByLegacyCurtainFormula(row);
}, [rowIsAffectedByLegacyCurtainFormula]);

const buildCurtainSummaryTotalsForRows = useCallback(
  (sourceRows = [], getRowCalc, sourceLabel = "curtain_summary_preview") => {
    const rowCalc =
      typeof getRowCalc === "function"
        ? getRowCalc
        : (row, index) => getCurtainProductionAdjustedRowCalc(row, index);
    const subTotal = round2(
      (Array.isArray(sourceRows) ? sourceRows : []).reduce(
        (sum, row, index) => sum + Number(rowCalc(row, index).rowTotal || 0),
        0
      )
    );
    const gstTotals = calculateCurtainQuoteTotals({
      rows: sourceRows,
      subtotal: subTotal,
      discountType: getDiscountTypeFromSummaryMode(summaryDiscountMode),
      discountValue: summaryDiscountValue,
      quoteGstRate: curtainGstRate,
      getRowTaxableTotal: (row, index) => rowCalc(row, index).rowTotal,
      isAccessoryRow: isCurtainAccessoryRowStatic,
    });

    return {
      subTotal: round2(subTotal),
      discountType: gstTotals.discountType,
      discountValue: gstTotals.discountValue,
      discountAmount: gstTotals.discountAmount,
      discountApplication: gstTotals.discountApplication || "after_gst",
      postGstDiscountAmount: gstTotals.postGstDiscountAmount ?? gstTotals.discountAmount,
      preGstGrandTotal: gstTotals.subtotalBeforeGst,
      subtotalBeforeGst: gstTotals.subtotalBeforeGst,
      totalAfterGst: gstTotals.totalAfterGst,
      measurementSubtotalBeforeDiscount: gstTotals.measurementSubtotalBeforeDiscount,
      accessorySubtotalBeforeDiscount: gstTotals.accessorySubtotalBeforeDiscount,
      accessorySubtotalBeforeGst: gstTotals.accessorySubtotalBeforeDiscount,
      measurementDiscountAmount: gstTotals.measurementDiscountAmount,
      accessoryDiscountAmount: gstTotals.accessoryDiscountAmount,
      taxableMeasurementAmount: gstTotals.taxableMeasurementAmount,
      taxableAccessoryAmount: gstTotals.taxableAccessoryAmount,
      taxableAmount: gstTotals.taxableAmount,
      quoteGstRate: gstTotals.quoteGstRate,
      gstRate: gstTotals.gstRate,
      gstRates: gstTotals.gstRates || [gstTotals.gstRate],
      accessoryGstRates: gstTotals.accessoryGstRates || [],
      hasMixedGstRates: gstTotals.hasMixedGstRates === true,
      measurementGstAmount: gstTotals.measurementGstAmount,
      accessoryGstAmount: gstTotals.accessoryGstAmount,
      gstAmount: gstTotals.gstAmount,
      totalGstAmount: gstTotals.totalGstAmount,
      finalTotal: gstTotals.finalTotal,
      grandTotal: gstTotals.grandTotal,
    };
  },
  [
    curtainGstRate,
    getCurtainProductionAdjustedRowCalc,
    summaryDiscountMode,
    summaryDiscountValue,
  ]
);

const getLegacyRecalculationReasonCodes = useCallback(
  (row = {}, legacyFields = {}, newCalc = {}) => {
    const snapshot = row.calculationSnapshot || {};
    const reasons = ["Legacy formula"];

    if (
      legacyFields.pieces?.exists === false ||
      legacyFields.orderedFabric?.exists === false ||
      legacyFields.fabricAmount?.exists === false ||
      legacyFields.stitchingTrackAmount?.exists === false ||
      legacyFields.liningAmount?.exists === false
    ) {
      reasons.push("Missing old calculation breakdown");
    }

    if (
      Number(snapshot.usableWidthDeductionIn || 0) !==
      Number(newCalc.calculationBreakdown?.curtainPieceCalc?.usableWidthDeductionIn || 0)
    ) {
      reasons.push("Usable width deduction applied");
    }

    const newPieces = Number(newCalc.totalPieces ?? newCalc.pcsFull ?? 0);
    if (
      legacyFields.pieces?.exists === true &&
      Number(legacyFields.pieces.value || 0) !== newPieces
    ) {
      reasons.push("Pieces recalculated");
    } else if (legacyFields.pieces?.exists === false && newPieces > 0) {
      reasons.push("Pieces recalculated");
    }

    if (
      Number(snapshot.fullness || 0) !==
      Number(newCalc.calculationBreakdown?.curtainPieceCalc?.fullness || snapshot.fullness || 0)
    ) {
      reasons.push("Fullness/stitching setting changed");
    }

    const newStitchTrackAmount = round2(
      Number(newCalc.stitchingCost || 0) +
        Number(newCalc.trackCost || 0) +
        Number(newCalc.bendAmount || 0)
    );
    if (
      legacyFields.stitchingTrackAmount?.exists === true &&
      round2(Number(legacyFields.stitchingTrackAmount.value || 0)) !==
        newStitchTrackAmount
    ) {
      reasons.push("Track/stitching recalculated");
    } else if (
      legacyFields.stitchingTrackAmount?.exists === false &&
      newStitchTrackAmount > 0
    ) {
      reasons.push("Track/stitching recalculated");
    }

    const newLiningAmount = round2(Number(newCalc.liningAmount ?? newCalc.lining?.amount ?? 0));
    if (
      legacyFields.liningAmount?.exists === true &&
      round2(Number(legacyFields.liningAmount.value || 0)) !== newLiningAmount
    ) {
      reasons.push("Lining recalculated");
    } else if (legacyFields.liningAmount?.exists === false && newLiningAmount > 0) {
      reasons.push("Lining recalculated");
    }

    return Array.from(new Set(reasons));
  },
  []
);

const buildLegacyCurtainRecalculationPreview = useCallback(() => {
  if (!hasLegacyCurtainCalculationRows) return null;

  const generatedAt = new Date().toISOString();
  const oldRows = rows.map((row, index) =>
    withCurtainRowId({ ...(row || {}) }, index)
  );
  const newRows = oldRows.map((row, index) => {
    if (!isLegacyCurtainRecalculationTarget(row)) {
      return withCurtainRowId({ ...row }, index);
    }

    const nextRow = sanitizeCurtainRowEditableTextFields({
      ...row,
      calculationSnapshot: null,
      calculationSnapshotStatus: "current",
      __forceCurrentCalculation: true,
      __recalculated: true,
      rowUpdatedAt: generatedAt,
    });

    return withCurtainRowId(nextRow, index);
  });
  const previewFabricSummaryResult = buildCurtainFabricSummary(newRows, {
    activeRepeatSettings: repeatSettings,
    halfPieceReuseEnabled: useHalfPieceReuse,
    debugTag: "legacy-recalculation-preview",
  });
  const previewNoReuseBillingResult = buildCurtainFabricSummary(newRows, {
    activeRepeatSettings: repeatSettings,
    halfPieceReuseEnabled: false,
    debugTag: "legacy-recalculation-preview-no-reuse",
  });
  const getPreviewRowCalc = (row, index) =>
    getCurtainProductionAdjustedRowCalcFromSummary(
      row,
      index,
      previewFabricSummaryResult,
      previewNoReuseBillingResult
    );
  const newSummaryTotals = buildCurtainSummaryTotalsForRows(
    newRows,
    getPreviewRowCalc,
    "legacy_recalculation_preview"
  );
  const savedQuoteTotalsForPreview = getCurtainSavedTotalsFromQuote(selectedQuote);
  const oldSummaryTotals = {
    subTotal: Number(
      savedQuoteTotalsForPreview?.subTotal ?? summaryTotals.subTotal ?? 0
    ),
    subtotalBeforeGst: Number(
      savedQuoteTotalsForPreview?.subtotalBeforeGst ??
        summaryTotals.subtotalBeforeGst ??
        summaryTotals.preGstGrandTotal ??
        0
    ),
    gstAmount: Number(
      savedQuoteTotalsForPreview?.totalGstAmount ??
        summaryTotals.totalGstAmount ??
        summaryTotals.gstAmount ??
        0
    ),
    totalGstAmount: Number(
      savedQuoteTotalsForPreview?.totalGstAmount ??
        summaryTotals.totalGstAmount ??
        summaryTotals.gstAmount ??
        0
    ),
    grandTotal: Number(
      savedQuoteTotalsForPreview?.grandTotal ??
        summaryTotals.grandTotal ??
        summaryTotals.finalTotal ??
        0
    ),
    quoteGstRate: Number(
      savedQuoteTotalsForPreview?.quoteGstRate ??
        summaryTotals.quoteGstRate ??
        summaryTotals.gstRate ??
        0
    ),
    discountAmount: Number(
      savedQuoteTotalsForPreview?.discountAmount ??
        summaryTotals.discountAmount ??
        0
    ),
    source: savedQuoteTotalsForPreview?.source || "liveSnapshotTotals",
  };
  const rowCalcsById = {};
  const rowDiffRows = [];
  const changedRows = [];

  oldRows.forEach((oldRow, index) => {
    const rowId = getWorkshopRowStableId(oldRow) || `row-${index}`;
    const newRow = newRows[index] || oldRow;
    const newCalc = getPreviewRowCalc(newRow, index);
    rowCalcsById[getWorkshopRowStableId(newRow) || rowId] = newCalc;

    if (!isLegacyCurtainRecalculationTarget(oldRow)) return;

    const legacyFields = {
      pieces: getLegacyFieldDisplay(oldRow, "pieces"),
      orderedFabric: getLegacyFieldDisplay(oldRow, "orderedFabric"),
      fabricAmount: getLegacyFieldDisplay(oldRow, "fabricAmount"),
      stitchingTrackAmount: getLegacyFieldDisplay(oldRow, "stitchingTrackAmount"),
      liningAmount: getLegacyFieldDisplay(oldRow, "liningAmount"),
      rowTotal: getLegacyFieldDisplay(oldRow, "rowTotal"),
    };
    const newPieces = Number(newCalc.totalPieces ?? newCalc.pcsFull ?? 0);
    const newOrderedFabric = Number(
      newCalc.orderedFabricMeters ??
        newCalc.customerBillableFabricMeters ??
        newCalc.fabricMeters ??
        0
    );
    const newFabricAmount = Number(newCalc.fabricCost || 0);
    const newStitchTrackAmount = round2(
      Number(newCalc.stitchingCost || 0) +
        Number(newCalc.trackCost || 0) +
        Number(newCalc.bendAmount || 0)
    );
    const newLiningAmount = Number(newCalc.liningAmount ?? newCalc.lining?.amount ?? 0);
    const newTotal = Number(newCalc.lineTotal ?? newCalc.rowTotal ?? 0);
    const reasonCodes = getLegacyRecalculationReasonCodes(
      oldRow,
      legacyFields,
      newCalc
    );
    const buildPreviewField = ({ field, type, oldField, newValue, unit = "" }) => {
      const normalizedNewValue = round2(Number(newValue || 0));
      const oldExists = oldField?.exists === true;
      const normalizedOldValue = oldExists
        ? round2(Number(oldField.value || 0))
        : null;
      const difference = oldExists
        ? round2(normalizedNewValue - Number(normalizedOldValue || 0))
        : null;
      const shouldShow = oldExists
        ? Math.abs(Number(difference || 0)) >= 0.01
        : Math.abs(normalizedNewValue) >= 0.01;

      return shouldShow
        ? {
            field,
            type,
            oldValue: normalizedOldValue,
            oldExists,
            oldSource: oldField?.source || "",
            newValue: normalizedNewValue,
            difference,
            unit,
          }
        : null;
    };
    const rowFields = [
      {
        field: "Pieces",
        type: "number",
        oldField: legacyFields.pieces,
        newValue: round2(newPieces),
        unit: "",
      },
      {
        field: "Ordered fabric",
        type: "meters",
        oldField: legacyFields.orderedFabric,
        newValue: round2(newOrderedFabric),
        unit: "m",
      },
      {
        field: "Fabric amount",
        type: "money",
        oldField: legacyFields.fabricAmount,
        newValue: round2(newFabricAmount),
        unit: "",
      },
      {
        field: "Stitching / track amount",
        type: "money",
        oldField: legacyFields.stitchingTrackAmount,
        newValue: round2(newStitchTrackAmount),
        unit: "",
      },
      {
        field: "Lining amount",
        type: "money",
        oldField: legacyFields.liningAmount,
        newValue: round2(newLiningAmount),
        unit: "",
      },
      {
        field: "Row total",
        type: "money",
        oldField: legacyFields.rowTotal,
        newValue: round2(newTotal),
        unit: "",
      },
    ]
      .map(buildPreviewField)
      .filter(Boolean);

    if (!rowFields.length) return;

    const oldPieces = legacyFields.pieces.exists
      ? round2(Number(legacyFields.pieces.value || 0))
      : null;
    const oldOrderedFabric = legacyFields.orderedFabric.exists
      ? round2(Number(legacyFields.orderedFabric.value || 0))
      : null;
    const oldTotal = legacyFields.rowTotal.exists
      ? round2(Number(legacyFields.rowTotal.value || 0))
      : null;

    const rowSummary = {
      rowId,
      itemName: oldRow.item || oldRow.name || oldRow.room || `Row ${index + 1}`,
      product:
        oldRow.productName ||
        oldRow.productType ||
        oldRow.product ||
        oldRow.calculationSnapshot?.productName ||
        "",
      fabric: getCurtainRowFabricLabel(oldRow),
      oldCalculationVersion:
        oldRow.calculationSnapshot?.calculationVersion || "legacy-saved-row-values",
      newCalculationVersion: CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
      oldPieces,
      newPieces: round2(newPieces),
      oldOrderedFabric,
      newOrderedFabric: round2(newOrderedFabric),
      oldTotal,
      oldTotalStored: legacyFields.rowTotal.exists === true,
      newTotal: round2(newTotal),
      difference:
        legacyFields.rowTotal.exists === true
          ? round2(newTotal - Number(legacyFields.rowTotal.value || 0))
          : null,
      reasonCodes,
    };

    changedRows.push(rowSummary);
    rowFields.forEach((field) => {
      rowDiffRows.push({
        ...rowSummary,
        field: field.field,
        type: field.type,
        oldValue: field.oldValue,
        oldExists: field.oldExists,
        oldSource: field.oldSource,
        newValue: field.newValue,
        difference: field.difference,
        unit: field.unit,
      });
    });
  });

  const preview = {
    id: `legacy-recalc-${Date.now()}`,
    quoteId: editingQuoteId || selectedQuote?.id || "",
    quoteNo: quoteNo || selectedQuote?.quoteNo || "",
    customerName,
    generatedAt,
    versionStatus: curtainLegacyVersionStatus,
    legacyCandidateRows: legacyCurtainCalculationCandidateRows.length,
    eligibleLegacyRows: oldRows.filter(isLegacyCurtainRecalculationTarget).length,
    fromCalculationVersion:
      changedRows[0]?.oldCalculationVersion || "legacy-saved-row-values",
    toCalculationVersion: CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
    oldRows: sanitizeLocalDraftValue(oldRows),
    newRows,
    rowCalcsById,
    oldTotals: oldSummaryTotals,
    newTotals: newSummaryTotals,
    difference: {
      subtotal: round2(
        Number(newSummaryTotals.subtotalBeforeGst || 0) -
          Number(oldSummaryTotals.subtotalBeforeGst || 0)
      ),
      gst: round2(
        Number(newSummaryTotals.totalGstAmount || newSummaryTotals.gstAmount || 0) -
          Number(oldSummaryTotals.totalGstAmount || oldSummaryTotals.gstAmount || 0)
      ),
      grandTotal: round2(
        Number(newSummaryTotals.grandTotal || 0) -
          Number(oldSummaryTotals.grandTotal || 0)
      ),
    },
    changedRows,
    rowDiffRows,
    affectedRowCount: changedRows.length,
  };

  const impact = deriveLegacyRecalculationImpact(preview);
  const result = {
    ...preview,
    ...impact,
    affectedRowCount: impact.actualChangedRows,
  };

  if (isDevelopmentMode()) {
    console.log("LEGACY_CALCULATION_CHECK_RESULT", {
      quoteNo: result.quoteNo,
      versionStatus: result.versionStatus,
      legacyCandidateRows: result.legacyCandidateRows,
      actualChangedRows: result.actualChangedRows,
      savedSubtotal: result.oldTotals?.subtotalBeforeGst,
      recalculatedSubtotal: result.newTotals?.subtotalBeforeGst,
      savedGst: result.oldTotals?.totalGstAmount ?? result.oldTotals?.gstAmount,
      recalculatedGst: result.newTotals?.totalGstAmount ?? result.newTotals?.gstAmount,
      savedGrandTotal: result.oldTotals?.grandTotal,
      recalculatedGrandTotal: result.newTotals?.grandTotal,
      totalDifference: result.totalDifference,
      recalculationImpact: result.recalculationImpact,
      shouldShowBanner: result.shouldShowBanner,
      reason: result.reason,
    });
  }

  return result;
}, [
  buildCurtainFabricSummary,
  buildCurtainSummaryTotalsForRows,
  curtainLegacyVersionStatus,
  customerName,
  editingQuoteId,
  getCurtainProductionAdjustedRowCalcFromSummary,
  getCurtainSavedTotalsFromQuote,
  getLegacyFieldDisplay,
  getLegacyRecalculationReasonCodes,
  hasLegacyCurtainCalculationRows,
  isLegacyCurtainRecalculationTarget,
  legacyCurtainCalculationCandidateRows.length,
  quoteNo,
  repeatSettings,
  rows,
  selectedQuote,
  summaryTotals,
  useHalfPieceReuse,
]);

const legacyRecalculationImpactPreview = useMemo(
  () => buildLegacyCurtainRecalculationPreview(),
  [buildLegacyCurtainRecalculationPreview]
);

const shouldShowLegacyCalculationBanner = Boolean(
  legacyRecalculationImpactPreview?.shouldShowBanner === true &&
    !pendingRecalculationAudit &&
    !legacyRecalculationRevisionRequest
);

legacyCurtainRecalculationImpactRef.current = legacyRecalculationImpactPreview || {
  shouldShowBanner: false,
  recalculationImpact: LEGACY_CALCULATION_IMPACT.NO_IMPACT,
};

const buildLegacyRecalculationAuditPackage = useCallback(
  (preview, action = "save_new_revision") => {
    if (!preview) return { audit: null, legacySnapshot: null };
    const recalculatedAt = new Date().toISOString();
    const currentUser = auth.currentUser;
    const audit = {
      recalculatedAt,
      recalculatedBy: currentUser?.uid || "",
      recalculatedByEmail: currentUser?.email || "",
      action,
      versionStatus: preview.versionStatus || LEGACY_CALCULATION_VERSION_STATUS.OLDER_VERSION,
      recalculationImpact:
        preview.recalculationImpact ||
        deriveLegacyRecalculationImpact(preview).recalculationImpact,
      quoteId: preview.quoteId || editingQuoteId || selectedQuote?.id || "",
      quoteNo: preview.quoteNo || quoteNo || selectedQuote?.quoteNo || "",
      fromCalculationVersion: preview.fromCalculationVersion,
      toCalculationVersion: preview.toCalculationVersion,
      oldSubtotal: Number(preview.oldTotals?.subtotalBeforeGst || 0),
      newSubtotal: Number(preview.newTotals?.subtotalBeforeGst || 0),
      oldGst: Number(preview.oldTotals?.totalGstAmount ?? preview.oldTotals?.gstAmount ?? 0),
      newGst: Number(preview.newTotals?.totalGstAmount ?? preview.newTotals?.gstAmount ?? 0),
      oldGrandTotal: Number(preview.oldTotals?.grandTotal || 0),
      newGrandTotal: Number(preview.newTotals?.grandTotal || 0),
      difference: Number(preview.difference?.grandTotal || 0),
      actualChangedRows: Number(preview.actualChangedRows ?? preview.changedRows?.length ?? 0),
      legacyCandidateRows: Number(preview.legacyCandidateRows || 0),
      changedRows: sanitizeLocalDraftValue(preview.changedRows || []),
    };
    const legacySnapshot = {
      capturedAt: recalculatedAt,
      quoteId: preview.quoteId || editingQuoteId || selectedQuote?.id || "",
      quoteNo: preview.quoteNo || quoteNo || selectedQuote?.quoteNo || "",
      subtotal: Number(preview.oldTotals?.subtotalBeforeGst || 0),
      gst: Number(preview.oldTotals?.totalGstAmount ?? preview.oldTotals?.gstAmount ?? 0),
      grandTotal: Number(preview.oldTotals?.grandTotal || 0),
      rows: sanitizeLocalDraftValue(preview.oldRows || []),
    };

    return {
      audit: sanitizeLocalDraftValue(audit),
      legacySnapshot: sanitizeLocalDraftValue(legacySnapshot),
    };
  },
  [editingQuoteId, quoteNo, selectedQuote?.id, selectedQuote?.quoteNo]
);

const resolveCurtainRecalculationFieldsForSave = useCallback(
  (overrides = {}) => {
    const hasAuditOverride = Object.prototype.hasOwnProperty.call(
      overrides,
      "recalculationAudit"
    );
    const hasSnapshotOverride = Object.prototype.hasOwnProperty.call(
      overrides,
      "legacyCalculationSnapshot"
    );
    const auditSource = hasAuditOverride
      ? overrides.recalculationAudit
      : pendingRecalculationAudit === false
      ? null
      : pendingRecalculationAudit || selectedQuote?.recalculationAudit || null;
    const snapshotSource = hasSnapshotOverride
      ? overrides.legacyCalculationSnapshot
      : pendingLegacyCalculationSnapshot === false
      ? null
      : pendingLegacyCalculationSnapshot ||
        selectedQuote?.legacyCalculationSnapshot ||
        null;
    const shouldWrite =
      hasAuditOverride ||
      hasSnapshotOverride ||
      pendingRecalculationAudit !== null ||
      pendingLegacyCalculationSnapshot !== null ||
      Boolean(selectedQuote?.recalculationAudit) ||
      Boolean(selectedQuote?.legacyCalculationSnapshot);

    return shouldWrite
      ? {
          recalculationAudit: auditSource ? sanitizeLocalDraftValue(auditSource) : null,
          legacyCalculationSnapshot: snapshotSource
            ? sanitizeLocalDraftValue(snapshotSource)
            : null,
        }
      : {};
  },
  [
    pendingLegacyCalculationSnapshot,
    pendingRecalculationAudit,
    selectedQuote?.legacyCalculationSnapshot,
    selectedQuote?.recalculationAudit,
  ]
);

const openLegacyRecalculationPreview = useCallback(() => {
  const preview = legacyRecalculationImpactPreview || buildLegacyCurtainRecalculationPreview();
  if (!preview) return;
  setLegacyRecalculationPreview(preview);
  setLegacyRecalculationModalOpen(true);
}, [buildLegacyCurtainRecalculationPreview, legacyRecalculationImpactPreview]);

const saveLegacyRecalculationAsNewRevision = useCallback(
  (preview = legacyRecalculationPreview) => {
    if (!preview) return;
    if (
      preview.recalculationImpact === LEGACY_CALCULATION_IMPACT.NO_IMPACT ||
      deriveLegacyRecalculationImpact(preview).recalculationImpact ===
        LEGACY_CALCULATION_IMPACT.NO_IMPACT
    ) {
      notify("No changes found. This quote already matches the latest calculation output.", "success");
      setLegacyRecalculationModalOpen(false);
      setLegacyRecalculationPreview(null);
      return;
    }
    if (!canCreateQuotes) {
      notify("You do not have permission to create a quotation revision.", "warning");
      return;
    }
    const { audit, legacySnapshot } = buildLegacyRecalculationAuditPackage(
      preview,
      "save_new_revision"
    );
    setPendingRecalculationAudit({
      ...audit,
      revisionOfQuoteId: preview.quoteId || editingQuoteId || selectedQuote?.id || "",
      revisionOfQuoteNo: preview.quoteNo || quoteNo || selectedQuote?.quoteNo || "",
    });
    setPendingLegacyCalculationSnapshot(legacySnapshot);
    commitCurtainRows(
      preview.newRows,
      QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT
    );
    setLegacyRecalculationModalOpen(false);
    setLegacyRecalculationPreview(null);
    setLegacyRecalculationRevisionRequest({
      id: preview.id || `legacy-revision-${Date.now()}`,
      requestedAt: new Date().toISOString(),
    });
    notify("Preparing a recalculated revision from the reviewed changes.", "info");
  },
  [
    buildLegacyRecalculationAuditPackage,
    canCreateQuotes,
    commitCurtainRows,
    editingQuoteId,
    legacyRecalculationPreview,
    notify,
    quoteNo,
    selectedQuote?.id,
    selectedQuote?.quoteNo,
  ]
);

const markLegacyCalculationNoImpactReviewed = useCallback(
  async (preview = legacyRecalculationPreview) => {
    if (!preview) return;
    const impact = deriveLegacyRecalculationImpact(preview);
    if (impact.recalculationImpact !== LEGACY_CALCULATION_IMPACT.NO_IMPACT) {
      notify("This quote has calculation differences and needs a revision review.", "warning");
      return;
    }
    const quoteId = preview.quoteId || editingQuoteId || selectedQuote?.id || "";
    if (!workspaceUid || !quoteId) {
      notify("Could not mark this quote as reviewed because the quote is not saved yet.", "warning");
      return;
    }
    if (!canEditQuotes) {
      notify("You do not have permission to update this quote review status.", "warning");
      return;
    }

    const currentUser = auth.currentUser;
    const reviewedAt = new Date().toISOString();
    const reviewLocal = {
      reviewedAt,
      reviewedBy: currentUser?.uid || "",
      reviewedByEmail: currentUser?.email || "",
      checkedAgainstCalculationVersion: CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
      versionStatus: preview.versionStatus || LEGACY_CALCULATION_VERSION_STATUS.OLDER_VERSION,
      impact: LEGACY_CALCULATION_IMPACT.NO_IMPACT,
      savedGrandTotal: Number(preview.oldTotals?.grandTotal || 0),
      recalculatedGrandTotal: Number(preview.newTotals?.grandTotal || 0),
      difference: 0,
      legacyCandidateRows: Number(preview.legacyCandidateRows || 0),
      actualChangedRows: 0,
    };

    try {
      await updateDoc(doc(db, "users", workspaceUid, "quotations", quoteId), {
        legacyCalculationReview: {
          ...reviewLocal,
          reviewedAt: serverTimestamp(),
        },
        calculationVersion: CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
        formulaCheckStatus: "current",
        updatedAt: serverTimestamp(),
      });
      setSelectedQuote((previous) =>
        previous
          ? {
              ...previous,
              legacyCalculationReview: reviewLocal,
              calculationVersion: CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
              formulaCheckStatus: "current",
            }
          : previous
      );
      setLegacyRecalculationModalOpen(false);
      setLegacyRecalculationPreview(null);
      notify("Calculation check marked as reviewed.", "success");
    } catch (error) {
      console.error("LEGACY_CALCULATION_MARK_REVIEWED_FAILED", {
        code: error?.code,
        message: error?.message,
        quoteId,
        workspaceUid,
      });
      notify("Could not mark calculation check as reviewed.", "error");
    }
  },
  [
    canEditQuotes,
    editingQuoteId,
    legacyRecalculationPreview,
    notify,
    selectedQuote?.id,
    workspaceUid,
  ]
);

const revertLegacyCurtainRecalculation = () => {
  const legacySnapshot =
    pendingLegacyCalculationSnapshot === false
      ? null
      : pendingLegacyCalculationSnapshot || selectedQuote?.legacyCalculationSnapshot;
  if (!legacySnapshot?.rows?.length) {
    notify("No previous calculation snapshot is available to restore.", "warning");
    return;
  }
  const restoredRows = legacySnapshot.rows.map((row, index) =>
    hydrateCurtainRowCalculationSnapshot(
      sanitizeCurtainRowEditableTextFields(withCurtainRowId({ ...(row || {}) }, index))
    )
  );
  commitCurtainRows(restoredRows, QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT);
  setPendingRecalculationAudit(false);
  setPendingLegacyCalculationSnapshot(false);
  setSelectedQuote((previous) =>
    previous
      ? {
          ...previous,
          rows: restoredRows,
          recalculationAudit: null,
          legacyCalculationSnapshot: null,
        }
      : previous
  );
  notify("Previous calculation restored in the editor. Save to persist the revert.", "success");
};

const downloadLegacyRecalculationReport = useCallback(() => {
  const preview = legacyRecalculationPreview;
  if (!preview || typeof document === "undefined") return;
  const blob = new Blob([JSON.stringify(sanitizeLocalDraftValue(preview), null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${preview.quoteNo || "curtain-quote"}-recalculation-diff.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}, [legacyRecalculationPreview]);

const formatLegacyRecalculationValue = useCallback(
  (entry, value) => {
    if (entry?.oldExists === false && value == null) {
      return "Not stored in legacy quote";
    }
    if (value == null || value === "") return "-";
    const amount = Number(value);
    if (!Number.isFinite(amount)) return String(value);
    if (entry?.type === "money") return formatCurrencyDisplay(amount, currency);
    if (entry?.type === "meters") return `${amount.toFixed(2)} m`;
    return Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
  },
  [currency]
);

const activeCurtainRecalculationAudit =
  pendingRecalculationAudit === false
    ? null
    : pendingRecalculationAudit || selectedQuote?.recalculationAudit || null;
const activeCurtainLegacyCalculationSnapshot =
  pendingLegacyCalculationSnapshot === false
    ? null
    : pendingLegacyCalculationSnapshot ||
      selectedQuote?.legacyCalculationSnapshot ||
      null;
const canRevertLegacyCurtainRecalculation =
  canEditQuotes && (isSuperAdmin || isWorkspaceOwner || canAccessSettingsTab);

useEffect(() => {
  if (!legacyRecalculationRevisionRequest) return undefined;
  const requestId = legacyRecalculationRevisionRequest.id;
  const timer = window.setTimeout(async () => {
    if (legacyRecalculationRevisionSaveRef.current === requestId) return;
    legacyRecalculationRevisionSaveRef.current = requestId;
    const saved = await saveQuotationAsNewRef.current?.();
    setLegacyRecalculationRevisionRequest(null);
    if (saved) {
      setPendingRecalculationAudit(null);
      setPendingLegacyCalculationSnapshot(null);
    } else {
      notify(
        "The recalculated revision was prepared but could not be saved. You can try Save As New again.",
        "warning"
      );
    }
    legacyRecalculationRevisionSaveRef.current = "";
  }, 0);

  return () => window.clearTimeout(timer);
}, [legacyRecalculationRevisionRequest, notify]);

useEffect(() => {
  if (!isDevelopmentMode()) return;
  const currentQuoteNo = quoteNo || selectedQuote?.quoteNo || "";
  if (!rows.length) return;

  const traceRows = rows.map((row, index) => {
    const customerCalc = getCurtainProductionAdjustedRowCalc(row, index);
    const baseCalc = calcRow(row, repeatSettings);
    const rowPlan = curtainFabricSummaryResult?.plan?.rowPlans?.[index] || null;
    const workshopNewFabricMeters = round2(
      Number(
        customerCalc.workshopNewFabricMeters ??
          rowPlan?.newFabricNeededMeters ??
          rowPlan?.newMeters ??
          baseCalc.fabricMeters ??
          0
      )
    );
    const discountPercent = Math.min(100, num(row.discount));
    const mrpPerMeter = num(row.mrp || row.mrpPerMeter);
    const workshopFabricCost =
      (baseCalc.isCurtain || baseCalc.isRoman) && rowPlan
        ? round2(workshopNewFabricMeters * mrpPerMeter * (1 - discountPercent / 100))
        : Number(baseCalc.fabricCost || 0);
    const workshopRowTotal =
      (baseCalc.isCurtain || baseCalc.isRoman) && rowPlan
        ? round2(Number(baseCalc.rowTotal || 0) - Number(baseCalc.fabricCost || 0) + workshopFabricCost)
        : Number(customerCalc.rowTotal || baseCalc.rowTotal || 0);

    return {
      item: row.item,
      product: row.product || row.productType || row.productName,
      fabric: row.fabric,
      fabricLabel: getCurtainRowFabricLabel(row),
      stitch: row.stitch || row.stitching || row.stitchLabel,
      mrp: row.mrp || row.mrpPerMeter || "",
      oldOrExpectedOrderedFabric: row.expectedOrderedFabricMeters,
      cutFabricMeters: customerCalc.cutFabricMeters,
      internalNewFabricNeededMeters:
        customerCalc.internalNewFabricNeededMeters ?? workshopNewFabricMeters,
      workshopNewFabricMeters,
      internalHalfReusedMeters:
        customerCalc.internalHalfReusedMeters ?? customerCalc.halfReusedMeters ?? 0,
      noReuseOrderedFabricMeters: customerCalc.noReuseOrderedFabricMeters,
      customerBillableFabricMeters: customerCalc.customerBillableFabricMeters,
      lineTotal: customerCalc.lineTotal ?? customerCalc.rowTotal,
      rowTotal: customerCalc.rowTotal,
      workshopOptimizedRowTotal: workshopRowTotal,
      calculationType: customerCalc.calculationType || row.calculationType,
      lineType: customerCalc.lineType || row.lineType,
      internalHalfPieceSavingMeters: customerCalc.internalHalfPieceSavingMeters || 0,
      internalHalfPieceSavingValue: customerCalc.internalHalfPieceSavingValue || 0,
    };
  });
  const customerBillableSubtotal = round2(
    traceRows.reduce((sum, row) => sum + Number(row.rowTotal || 0), 0)
  );
  const workshopOptimizedSubtotal = round2(
    traceRows.reduce((sum, row) => sum + Number(row.workshopOptimizedRowTotal || 0), 0)
  );
  const affectedRows = traceRows.filter((row) =>
    /living w3 sheer|dining w1 sheer|up bedroom w1 sheer|up bedroom 3-w1 sheer|3rd floor sheer/i.test(
      String(row.item || "")
    )
  );
  const fabricBillingSummaries = Object.values(
    traceRows.reduce((acc, row) => {
      const fabric = row.fabricLabel || row.fabric || "Unknown Fabric";
      if (!acc[fabric]) {
        acc[fabric] = {
          fabricLabel: fabric,
          cutFabricMeters: 0,
          halfReusedMeters: 0,
          newFabricNeededMeters: 0,
          customerBillableFabricMeters: 0,
        };
      }
      acc[fabric].cutFabricMeters = round2(
        acc[fabric].cutFabricMeters + Number(row.cutFabricMeters || 0)
      );
      acc[fabric].halfReusedMeters = round2(
        acc[fabric].halfReusedMeters + Number(row.internalHalfReusedMeters || 0)
      );
      acc[fabric].newFabricNeededMeters = round2(
        acc[fabric].newFabricNeededMeters + Number(row.internalNewFabricNeededMeters || 0)
      );
      acc[fabric].customerBillableFabricMeters = round2(
        acc[fabric].customerBillableFabricMeters + Number(row.customerBillableFabricMeters || 0)
      );
      return acc;
    }, {})
  );
  const totalCutFabricMeters = round2(
    fabricBillingSummaries.reduce((sum, row) => sum + Number(row.cutFabricMeters || 0), 0)
  );
  const totalNewFabricNeededMeters = round2(
    fabricBillingSummaries.reduce((sum, row) => sum + Number(row.newFabricNeededMeters || 0), 0)
  );
  const totalHalfReusedMeters = round2(
    fabricBillingSummaries.reduce((sum, row) => sum + Number(row.halfReusedMeters || 0), 0)
  );
  const totalCustomerBillableFabricMeters = round2(
    fabricBillingSummaries.reduce((sum, row) => sum + Number(row.customerBillableFabricMeters || 0), 0)
  );

  console.log("QUOTE_TOTAL_REGRESSION_TRACE", {
    quoteNo: currentQuoteNo,
    passHalfPieceSavingsToCustomer,
    customerBillableSubtotal,
    workshopOptimizedSubtotal,
    difference: round2(customerBillableSubtotal - workshopOptimizedSubtotal),
    affectedSheerRows: affectedRows,
    rows: traceRows,
  });
  console.log("HALF_PIECE_BILLING_TRACE", {
    quoteNo: currentQuoteNo,
    useHalfPieceReuse,
    passHalfPieceSavingsToCustomer,
    rows: traceRows.map((row) => ({
      item: row.item,
      fabric: row.fabricLabel || row.fabric,
      mrp: row.mrp,
      cutFabricMeters: row.cutFabricMeters,
      internalNewFabricNeededMeters: row.internalNewFabricNeededMeters,
      internalHalfReusedMeters: row.internalHalfReusedMeters,
      noReuseOrderedFabricMeters: row.noReuseOrderedFabricMeters,
      customerBillableFabricMeters: row.customerBillableFabricMeters,
      lineTotal: row.lineTotal,
    })),
    subtotal: customerBillableSubtotal,
  });
  console.log("CUSTOMER_BILLABLE_FABRIC_TRACE", {
    quoteNo: currentQuoteNo,
    cutFabricMeters: totalCutFabricMeters,
    newFabricNeededMeters: totalNewFabricNeededMeters,
    halfReusedMeters: totalHalfReusedMeters,
    customerBillableFabricMeters: totalCustomerBillableFabricMeters,
    subtotal: customerBillableSubtotal,
    grandTotal: summaryTotals.grandTotal,
    expectedTotalForCurrentQuote: 286495.84,
  });
  console.table(
    fabricBillingSummaries.map((fabric) => ({
      fabric: fabric.fabricLabel,
      cut: fabric.cutFabricMeters,
      halfReused: fabric.halfReusedMeters,
      newNeeded: fabric.newFabricNeededMeters,
      billable: fabric.customerBillableFabricMeters,
    }))
  );
  console.log("HALF_PIECE_PASS_SAVINGS_TRACE", {
    quoteNo: currentQuoteNo,
    passHalfPieceSavingsToCustomer,
    fabrics: fabricBillingSummaries.map((fabric) => ({
      fabric: fabric.fabricLabel,
      cutFabricMeters: fabric.cutFabricMeters,
      newFabricNeededMeters: fabric.newFabricNeededMeters,
      customerBillableFabricMeters: fabric.customerBillableFabricMeters,
    })),
  });
  console.log("MIXED_GST_TOTAL_TRACE", {
    quoteGstRate: summaryTotals.quoteGstRate,
    measurementSubtotalBeforeDiscount: summaryTotals.measurementSubtotalBeforeDiscount,
    accessorySubtotalBeforeGst: summaryTotals.accessorySubtotalBeforeGst,
    discountAmount: summaryTotals.discountAmount,
    taxableMeasurementAmount: summaryTotals.taxableMeasurementAmount,
    measurementGstAmount: summaryTotals.measurementGstAmount,
    accessoryGstAmount: summaryTotals.accessoryGstAmount,
    totalGstAmount: summaryTotals.totalGstAmount,
    grandTotal: summaryTotals.grandTotal,
    accessoryRows: rows
      .map((row, index) => ({
        row,
        index,
        calc: getCurtainProductionAdjustedRowCalc(row, index),
      }))
      .filter(({ row }) => isCurtainAccessoryRowStatic(row))
      .map(({ row, calc }) => ({
        product: row.productName || row.productType || row.product || "",
        calculationType: calc.calculationType || row.calculationType || "",
        qty: row.qty ?? row.quantity ?? calc.quantity ?? "",
        amount: row.amount ?? row.manualAmount ?? row.lineAmount ?? "",
        unitRate: row.unitRate ?? calc.unitRate ?? "",
        gstRate: resolveCurtainAccessoryRowGstRate(row),
        taxable: calc.taxableAmount ?? calc.rowTotal ?? 0,
        gstAmount: round2(
          (Number(calc.taxableAmount ?? calc.rowTotal ?? 0) *
            resolveCurtainAccessoryRowGstRate(row)) /
            100
        ),
        total: round2(
          Number(calc.taxableAmount ?? calc.rowTotal ?? 0) +
            (Number(calc.taxableAmount ?? calc.rowTotal ?? 0) *
              resolveCurtainAccessoryRowGstRate(row)) /
              100
        ),
      })),
  });
}, [
  calcRow,
  curtainFabricSummaryResult,
  getCurtainProductionAdjustedRowCalc,
  passHalfPieceSavingsToCustomer,
  quoteNo,
  repeatSettings,
  rows,
  selectedQuote?.quoteNo,
  summaryTotals.accessoryGstAmount,
  summaryTotals.accessorySubtotalBeforeGst,
  summaryTotals.accessorySubtotalBeforeDiscount,
  summaryTotals.discountAmount,
  summaryTotals.grandTotal,
  summaryTotals.measurementGstAmount,
  summaryTotals.measurementSubtotalBeforeDiscount,
  summaryTotals.quoteGstRate,
  summaryTotals.taxableMeasurementAmount,
  summaryTotals.totalGstAmount,
  useHalfPieceReuse,
]);

const generalQuoteDebugSnapshot = useMemo(() => {
  const normalized = syncGeneralQuoteDefaults(generalQuote || {});
  const normalizedTotals = calculateGeneralQuoteTotals(normalized);
  const quoteId =
    generalEditingQuoteId ||
    selectedQuote?.id ||
    normalized.quoteNo ||
    "new";
  const quoteSearchIndex = buildSafeQuoteSearchIndex({
    id: quoteId,
    quoteType: GENERAL_QUOTE_TYPE,
    quoteNo: normalized.quoteNo,
    generalQuote: normalized,
  });
  const normalizedQuoteDate = resolveQuoteDateForInput(
    {
      ...selectedQuote,
      generalQuote: normalized,
    },
    todayISO()
  );
  const rawPayload = {
    quoteType: GENERAL_QUOTE_TYPE,
    quoteNo: normalized.quoteNo || "",
    quoteDate: normalizedQuoteDate,
    date: normalizedQuoteDate,
    quotationDate: normalizedQuoteDate,
    customerName:
      normalized.quoteFor?.company ||
      normalized.quoteFor?.name ||
      normalized.customerName ||
      "",
    generalQuote: normalized,
    subtotal: Number(normalizedTotals.subtotal || 0),
    subtotalBeforeGst: Number(normalizedTotals.subtotalBeforeGst || 0),
    taxableAmount: Number(normalizedTotals.taxableAmount || 0),
    totalGst: Number(normalizedTotals.totalGstAmount ?? normalizedTotals.gstTotal ?? 0),
    totalGstAmount: Number(normalizedTotals.totalGstAmount ?? normalizedTotals.gstTotal ?? 0),
    gstAmount: Number(normalizedTotals.totalGstAmount ?? normalizedTotals.gstTotal ?? 0),
    totalAfterGst: Number(normalizedTotals.totalAfterGst || 0),
    grandTotal: Number(normalizedTotals.grandTotal || 0),
    finalTotal: Number(normalizedTotals.grandTotal || 0),
    totalInWords: normalizedTotals.totalInWords || "",
    discountType: normalizedTotals.discountType || normalized.discountType,
    discountValue: Number(
      normalizedTotals.discountValue ?? normalized.discountValue ?? 0
    ),
    discountAmount: Number(normalizedTotals.discountAmount || 0),
    discountApplication: "after_gst",
    searchText: quoteSearchIndex.searchText,
    searchKeywords: quoteSearchIndex.searchKeywords,
    searchTokens: quoteSearchIndex.searchKeywords,
  };
  const sanitizedPayload = sanitizeGeneralQuotePayload(rawPayload);
  const diagnostics = inspectGeneralQuotePayload(rawPayload);
  const imageMetadataCount = (normalized.lineItems || []).reduce(
    (count, item) => count + normalizeGeneralQuoteItemImages(item).length,
    0
  );

  return {
    workspaceUid,
    uid: auth.currentUser?.uid || user?.uid || "",
    email: auth.currentUser?.email || user?.email || "",
    quoteId,
    quoteNo: normalized.quoteNo || "",
    isEditingExistingQuote: Boolean(generalEditingQuoteId),
    savePath: workspaceUid
      ? `users/${workspaceUid}/quotations/${quoteId}`
      : "",
    payloadSizeBytes: estimateJsonSizeBytes(sanitizedPayload),
    payloadKeys: Object.keys(sanitizedPayload || {}),
    hasInvalidUndefinedFields: diagnostics.hasUndefinedFields,
    hasFileBlobObjects: diagnostics.hasFileBlobObjects,
    hasInvalidNumbers: diagnostics.hasInvalidNumbers,
    searchTextLength: String(sanitizedPayload.searchText || "").length,
    searchKeywordsCount: Array.isArray(sanitizedPayload.searchKeywords)
      ? sanitizedPayload.searchKeywords.length
      : 0,
    discountType: sanitizedPayload.discountType,
    discountValue: sanitizedPayload.discountValue,
    itemCount: normalized.lineItems?.length || 0,
    imageMetadataCount,
  };
}, [
  generalEditingQuoteId,
  generalQuote,
  selectedQuote,
  syncGeneralQuoteDefaults,
  user,
  workspaceUid,
]);

const renderGeneralQuoteDebugPanel = () => {
  if (!IS_DEV_UI_ENABLED) return null;

  const info = generalQuoteDebugSnapshot;
  const latestUpload =
    generalQuoteDebugState.lastUpload ||
    generalQuoteDebugState.directStorageTest ||
    {};
  const debugRow = (label, value) => (
    <div
      key={label}
      style={{
        display: "grid",
        gridTemplateColumns: "190px minmax(0, 1fr)",
        gap: 10,
        fontSize: 12,
        alignItems: "start",
      }}
    >
      <strong style={{ color: isDark ? "#bfdbfe" : "#1d4ed8" }}>{label}</strong>
      <span
        style={{
          color: isDark ? "#e2e8f0" : "#0f172a",
          wordBreak: "break-word",
          whiteSpace: "pre-wrap",
        }}
      >
        {typeof value === "string" || typeof value === "number"
          ? String(value)
          : JSON.stringify(value, null, 2)}
      </span>
    </div>
  );

  return (
    <div
      style={{
        margin: "0 0 16px",
        borderRadius: 18,
        border: isDark
          ? "1px solid rgba(96,165,250,0.35)"
          : "1px solid rgba(37,99,235,0.22)",
        background: isDark ? "rgba(15,23,42,0.92)" : "#eff6ff",
        padding: 14,
      }}
    >
      <button
        type="button"
        onClick={() => setIsGeneralQuoteDebugOpen((prev) => !prev)}
        style={{
          width: "100%",
          border: 0,
          background: "transparent",
          color: isDark ? "#dbeafe" : "#1e3a8a",
          fontWeight: 900,
          fontSize: 14,
          textAlign: "left",
          cursor: "pointer",
        }}
      >
        General Quote Debug {isGeneralQuoteDebugOpen ? "Hide" : "Show"}
      </button>

      {isGeneralQuoteDebugOpen ? (
        <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
          {debugRow("workspaceUid", info.workspaceUid || "(empty)")}
          {debugRow("current user", `${info.uid || "(no uid)"} / ${info.email || "(no email)"}`)}
          {debugRow("quoteId", info.quoteId)}
          {debugRow("quoteNo", info.quoteNo || "(empty)")}
          {debugRow("is editing existing", String(info.isEditingExistingQuote))}
          {debugRow("save path", info.savePath || "(missing workspace)")}
          {debugRow("payload size bytes", info.payloadSizeBytes)}
          {debugRow("payload keys", info.payloadKeys.join(", "))}
          {debugRow("has undefined fields", String(info.hasInvalidUndefinedFields))}
          {debugRow("has File/Blob objects", String(info.hasFileBlobObjects))}
          {debugRow("has NaN/Infinity", String(info.hasInvalidNumbers))}
          {debugRow("searchText length", info.searchTextLength)}
          {debugRow("searchKeywords count", info.searchKeywordsCount)}
          {debugRow("discount", `${info.discountType} / ${info.discountValue}`)}
          {debugRow("item count", info.itemCount)}
          {debugRow("image metadata count", info.imageMetadataCount)}
          {debugRow("selected item id", latestUpload.itemId || "(none)")}
          {debugRow("file", latestUpload.fileName ? `${latestUpload.fileName} / ${latestUpload.fileType || ""} / ${latestUpload.fileSize || 0}` : "(none)")}
          {debugRow("intended storage path", latestUpload.storagePath || "(none)")}
          {debugRow("upload state", generalQuoteDebugState.uploadState || "idle")}
          {debugRow("last upload error", generalQuoteDebugState.lastUploadError || "(none)")}
          {debugRow("last save error", generalQuoteDebugState.lastSaveError || "(none)")}
          {debugRow("direct Firestore test", generalQuoteDebugState.directFirestoreTest || "(not run)")}
          {debugRow("direct Storage test", generalQuoteDebugState.directStorageTest || "(not run)")}

          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 8 }}>
            <Button onClick={testGeneralQuoteFirestoreWrite}>
              Test Firestore General Quote Save
            </Button>
            <label
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "9px 12px",
                borderRadius: 12,
                background: isDark ? "#020617" : "#ffffff",
                border: "1px solid rgba(148,163,184,0.35)",
                cursor: "pointer",
                fontWeight: 800,
              }}
            >
              Choose Test Image
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                style={{ display: "none" }}
                onChange={(event) =>
                  setGeneralQuoteDebugUploadFile(event.target.files?.[0] || null)
                }
              />
            </label>
            <Button onClick={testGeneralQuoteImageUpload}>
              Test General Image Upload
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const generalQuoteTotals = useMemo(() => {
  return calculateGeneralQuoteTotals(
    syncGeneralQuoteDefaults(generalQuote)
  );
}, [generalQuote, syncGeneralQuoteDefaults]);

function buildLiveGeneralQuotePayload({ action = "unknown" } = {}) {
  const quoteState = generalQuoteRef.current || generalQuote || {};
  const normalizedQuote = syncGeneralQuoteDefaults(quoteState);
  const totals = calculateGeneralQuoteTotals(normalizedQuote);
  return {
    action,
    quoteType: GENERAL_QUOTE_TYPE,
    quoteNo: normalizedQuote.quoteNo || quoteNo,
    quote: normalizedQuote,
    items: Array.isArray(normalizedQuote.lineItems)
      ? normalizedQuote.lineItems
      : Array.isArray(normalizedQuote.items)
      ? normalizedQuote.items
      : [],
    summaryTotals: totals,
    totals,
  };
}

useEffect(() => {
  if (
    !IS_DEV_UI_ENABLED ||
    tab !== "quote" ||
    quoteSubTab !== GENERAL_QUOTE_TYPE
  ) {
    return;
  }

  const normalizedDiscount = normalizeGeneralDiscount(
    generalQuote || {},
    "general_discount_input_trace"
  );

  console.log("DISCOUNT_INPUT_STATE_TRACE", {
    quoteType: GENERAL_QUOTE_TYPE,
    quoteNo: generalQuote?.quoteNo || quoteNo,
    inputRawValue: generalQuote?.discountValue ?? "",
    parsedDiscountValue: Number(normalizedDiscount.discountValue || 0),
    discountType: normalizedDiscount.discountType,
    quoteSettingsDiscountValue: generalQuote?.discountValue ?? "",
    helperDiscountValue: generalQuoteTotals?.discountValue,
    helperDiscountType: generalQuoteTotals?.discountType,
    liveDiscountAmount: generalQuoteTotals?.discountAmount,
    liveTotalAfterGst: generalQuoteTotals?.totalAfterGst,
    liveGrandTotal: generalQuoteTotals?.grandTotal,
  });
}, [
  generalQuote,
  generalQuoteTotals?.discountAmount,
  generalQuoteTotals?.discountType,
  generalQuoteTotals?.discountValue,
  generalQuoteTotals?.grandTotal,
  generalQuoteTotals?.totalAfterGst,
  quoteNo,
  quoteSubTab,
  tab,
]);

useEffect(() => {
  if (
    !IS_DEV_UI_ENABLED ||
    tab !== "quote" ||
    quoteSubTab !== GENERAL_QUOTE_TYPE
  ) {
    return;
  }
  const totalAfterGst = Number(generalQuoteTotals?.totalAfterGst || 0);
  const discountAmount = Number(generalQuoteTotals?.discountAmount || 0);
  const expectedGrandTotal = round2(Math.max(0, totalAfterGst - discountAmount));
  const actualGrandTotal = round2(Number(generalQuoteTotals?.grandTotal || 0));
  if (discountAmount > 0.005 && Math.abs(expectedGrandTotal - actualGrandTotal) > 0.05) {
    console.error("DISCOUNT_NOT_APPLIED_TO_GRAND_TOTAL", {
      quoteType: GENERAL_QUOTE_TYPE,
      quoteNo: generalQuote?.quoteNo || quoteNo,
      totalAfterGst,
      discountType: generalQuoteTotals?.discountType,
      discountValue: generalQuoteTotals?.discountValue,
      discountAmount,
      grandTotal: actualGrandTotal,
      expectedGrandTotal,
    });
  }
}, [
  generalQuote?.quoteNo,
  generalQuoteTotals?.discountAmount,
  generalQuoteTotals?.discountType,
  generalQuoteTotals?.discountValue,
  generalQuoteTotals?.grandTotal,
  generalQuoteTotals?.totalAfterGst,
  quoteNo,
  quoteSubTab,
  tab,
]);

useEffect(() => {
  if (
    !IS_DEV_UI_ENABLED ||
    tab !== "quote" ||
    quoteSubTab !== GENERAL_QUOTE_TYPE
  ) {
    return;
  }

  const liveRows = Array.isArray(generalQuote?.lineItems)
    ? generalQuote.lineItems
    : [];
  const legacyAliasRows = Array.isArray(generalQuote?.items)
    ? generalQuote.items
    : [];

  if (!liveRows.length || !legacyAliasRows.length || liveRows === legacyAliasRows) {
    return;
  }

  const buildTotalSignature = (rows) =>
    rows
      .map((item) =>
        [
          item?.id,
          item?.name || item?.itemName,
          item?.quantity,
          item?.rate,
          item?.amount,
          item?.gstPercent,
          item?.gstMode,
        ]
          .map((value) => String(value ?? ""))
          .join("|")
      )
      .join("||");

  const liveSignature = buildTotalSignature(liveRows);
  const aliasSignature = buildTotalSignature(legacyAliasRows);

  if (liveSignature === aliasSignature) {
    return;
  }

  const staleAliasGrandTotal = calcGeneralGrandTotal({
    ...generalQuote,
    lineItems: legacyAliasRows,
    items: legacyAliasRows,
  });
  const liveGrandTotal = Number(generalQuoteTotals?.grandTotal || 0);
  const grandTotalDelta = Math.abs(staleAliasGrandTotal - liveGrandTotal);

  if (grandTotalDelta <= 0.05) {
    return;
  }

  const warningSignature = [
    generalQuote?.quoteNo || "",
    liveSignature,
    aliasSignature,
    liveGrandTotal,
    staleAliasGrandTotal,
  ].join("::");

  if (generalQuoteStaleTotalsWarningRef.current === warningSignature) {
    return;
  }

  generalQuoteStaleTotalsWarningRef.current = warningSignature;
  console.error("QUOTE_TOTAL_STALE_STATE_DETECTED", {
    quoteType: GENERAL_QUOTE_TYPE,
    quoteNo: generalQuote?.quoteNo || quoteNo,
    changedField: "lineItems",
    lineTotals: liveRows.map((item) => ({
      id: item?.id || "",
      total: getGeneralItemAmount(item),
      taxableAmount: getGeneralItemBaseAmount(item),
      gstAmount: getGeneralItemTaxAmount(item),
    })),
    lineItemsCount: liveRows.length,
    itemsAliasCount: legacyAliasRows.length,
    subtotal: Number(generalQuoteTotals?.subtotalBeforeGst ?? generalQuoteTotals?.subtotalExcludingGst ?? 0),
    totalGst: Number(generalQuoteTotals?.totalGstAmount ?? generalQuoteTotals?.gstTotal ?? 0),
    totalAfterGst: Number(generalQuoteTotals?.totalAfterGst || 0),
    discountAmount: Number(generalQuoteTotals?.discountAmount || 0),
    grandTotal: liveGrandTotal,
    displayedGrandTotal: liveGrandTotal,
    staleAliasSubtotal: calcGeneralSubtotal(legacyAliasRows),
    staleAliasGrandTotal,
    grandTotalDelta,
  });
}, [
  generalQuote,
  generalQuoteTotals?.discountAmount,
  generalQuoteTotals?.grandTotal,
  generalQuoteTotals?.gstTotal,
  generalQuoteTotals?.subtotal,
  generalQuoteTotals?.subtotalBeforeGst,
  generalQuoteTotals?.subtotalExcludingGst,
  generalQuoteTotals?.totalAfterGst,
  generalQuoteTotals?.totalGstAmount,
  quoteNo,
  quoteSubTab,
  tab,
]);

/* =============================================
    FABRIC SUMMARY  reacts to repeat + hem
   ============================================= */
// ================= PERIOD FILTER (FIXED & SAFE) =================
function isDateInPeriod(date, type, customRange) {
  if (!date) return false;

  const now = new Date();
  const d = new Date(date);

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (type) {
    case "today":
      return (
        d >= startOfToday &&
        d < new Date(startOfToday.getTime() + 86400000)
      );

    case "thisWeek": {
      const firstDay = new Date(now);
      firstDay.setDate(now.getDate() - now.getDay());
      firstDay.setHours(0, 0, 0, 0);
      const nextWeek = new Date(firstDay);
      nextWeek.setDate(firstDay.getDate() + 7);

      return d >= firstDay && d < nextWeek;
    }

    case "thisMonth":
      return (
        d.getMonth() === now.getMonth() &&
        d.getFullYear() === now.getFullYear()
      );

    case "lastMonth": {
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return (
        d.getMonth() === lastMonth.getMonth() &&
        d.getFullYear() === lastMonth.getFullYear()
      );
    }

    case "thisYear":
      return d.getFullYear() === now.getFullYear();

    case "custom":
      if (!customRange.start || !customRange.end) return false;

      const start = new Date(`${customRange.start}T00:00:00`);
      const end = new Date(`${customRange.end}T23:59:59.999`);
      end.setHours(23, 59, 59, 999);

      return d >= start && d <= end;

    default:
      return true;
  }
}


async function updateWorkStage(quoteId, newStage) {
  try {
    if (!canEditQuotes) {
      throw new Error("You do not have permission to update quotation stages.");
    }

    if (!workspaceUid) throw new Error("User not authenticated");

    const uid = workspaceUid;
    const sourceLead =
      crmLeadRecords.find((item) => item.id === quoteId) || null;

    if (sourceLead) {
      const nextStageMeta = getStageMetaForType(
        sourceLead.pipelineType,
        newStage
      );
      const nextStatus =
        nextStageMeta?.confirmsQuote === true ||
        String(sourceLead?.status || "").toLowerCase() === "confirmed"
          ? "confirmed"
          : "draft";
      const nextChecklistItems = createChecklistFromStage(nextStageMeta);

      if (selectedCrmDealId === quoteId) {
        updateCrmDetailDraft((prev) =>
          prev
            ? {
                ...prev,
                stageId: newStage,
                status: nextStatus,
                checklistItems: cloneChecklistTree(nextChecklistItems, {
                  preserveIds: true,
                  resetState: false,
                  idPrefix: "crm-check",
                }),
              }
            : prev
        );
      }

      await saveCrmFieldsForLead(quoteId, {
        stageId: newStage,
        status: nextStatus,
        checklistItems: nextChecklistItems,
      });
      notify("Stage updated", "success");
      return;
    }

    const quoteRef = doc(db, "users", uid, "quotations", quoteId);
    const sourceQuote = savedQuotes.find((item) => item.id === quoteId) || null;

    await runTransaction(db, async (tx) => {
      const quoteSnap = await tx.get(quoteRef);
      if (!quoteSnap.exists()) throw new Error("Quote not found");

      const quote = quoteSnap.data();
      const quoteType = resolveQuoteType(quote);
      const nextStageMeta = getStageMetaForType(quoteType, newStage);
      const nextStatus =
        nextStageMeta?.confirmsQuote === true ||
        String(quote?.status || "").toLowerCase() === "confirmed"
          ? "confirmed"
          : "draft";
      const nextChecklistItems = createChecklistFromStage(nextStageMeta);
      const crmPayload = buildQuoteCrmData(
        {
          id: quoteId,
          ...quote,
          workStage: newStage,
          status: nextStatus,
          installationDate: quote.installationDate || null,
        },
        {
          stageId: newStage,
          status: nextStatus,
          installationDate: quote.installationDate || null,
          checklistItems: nextChecklistItems,
          expectedValue: Number(quote.grandTotal ?? quote.subtotal ?? 0),
          finalValue: Number(quote.grandTotal ?? quote.subtotal ?? 0),
        }
      );

      tx.update(quoteRef, {
        workStage: newStage,
        workStageUpdatedAt: serverTimestamp(),
        status: nextStatus,
        confirmedAt:
          nextStageMeta?.confirmsQuote === true
            ? quote?.confirmedAt || serverTimestamp()
            : quote?.confirmedAt || deleteField(),
        crm: crmPayload,
      });
    });

    if (sourceQuote) {
      const stageMeta = getStageMetaForType(resolveQuoteType(sourceQuote), newStage);
      const nextStatus =
        stageMeta?.confirmsQuote === true ||
        String(sourceQuote?.status || "").toLowerCase() === "confirmed"
          ? "confirmed"
          : "draft";
      const nextChecklistItems = createChecklistFromStage(stageMeta);
      const crmPayload = buildQuoteCrmData(
        {
          ...sourceQuote,
          workStage: newStage,
          status: nextStatus,
        },
        {
          stageId: newStage,
          status: nextStatus,
          checklistItems: nextChecklistItems,
        }
      );

      if (selectedCrmDealId === quoteId) {
        updateCrmDetailDraft((prev) =>
          prev
            ? {
                ...prev,
                stageId: newStage,
                status: nextStatus,
                checklistItems: cloneChecklistTree(nextChecklistItems, {
                  preserveIds: true,
                  resetState: false,
                  idPrefix: "crm-check",
                }),
              }
            : prev
        );
      }

      applyQuoteLocalPatch(quoteId, {
        workStage: newStage,
        status: nextStatus,
        crm: crmPayload,
        confirmedAt:
          stageMeta?.confirmsQuote === true
            ? sourceQuote.confirmedAt || new Date()
            : sourceQuote.confirmedAt || null,
        updatedAt: new Date(),
      });

      if (sourceQuote?.crm?.leadId) {
        await saveCrmFieldsForLead(
          sourceQuote.crm.leadId,
          {
            stageId: newStage,
            status: nextStatus,
            checklistItems:
              Array.isArray(crmPayload.checklistItems) &&
              crmPayload.checklistItems.length > 0
                ? crmPayload.checklistItems
                : [],
          },
          { syncQuote: false }
        );
      }
    }

    notify("Stage updated", "success");

  } catch (err) {
    console.error(err);
    const errorMessage = String(err?.message || "");
    if (errorMessage.includes("searchText") || errorMessage.includes("1048487")) {
      notify("Could not save changes. Please try again.", "warning");
    } else {
      notify(errorMessage || "Could not update the stage. Please try again.", "error");
    }
  }
}

  const updateInstallationDate = useCallback(
    async (quoteId, value) => {
    if (!canEditQuotes) {
      notify("You do not have permission to edit installation dates.", "warning");
      return;
    }

    if (!workspaceUid || !quoteId) return;

    try {
      const nextDate = parseDateInputValue(value);
      const sourceQuote = savedQuotes.find((item) => item.id === quoteId);
      const quoteRef = doc(
        db,
        "users",
        workspaceUid,
        "quotations",
        quoteId
      );

      await updateDoc(quoteRef, {
        installationDate: nextDate ? Timestamp.fromDate(nextDate) : null,
        crm: sourceQuote
          ? buildQuoteCrmData(sourceQuote, {
              installationDate: nextDate,
            })
          : deleteField(),
        updatedAt: serverTimestamp(),
      });

      applyQuoteLocalPatch(quoteId, {
        installationDate: nextDate,
        crm: sourceQuote
          ? buildQuoteCrmData(sourceQuote, {
              installationDate: nextDate,
            })
          : undefined,
        updatedAt: new Date(),
      });

      if (selectedCrmDealId === quoteId) {
        setCrmDetailDraft((prev) =>
          prev
            ? {
                ...prev,
                installationDate: value || "",
                reminderConfigs: normalizeCrmReminderConfigs(prev.reminderConfigs, {
                  nextActionDate: parseDateInputValue(prev.nextActionDate),
                  installationDate: nextDate,
                }),
              }
            : prev
        );
      }

      if (sourceQuote?.crm?.leadId) {
        await saveCrmFieldsForLead(
          sourceQuote.crm.leadId,
          {
            installationDate: nextDate,
          },
          { syncQuote: false }
        );
        notify("Installation date saved", "success");
        return;
      }

      if (sourceQuote) {
        const nextCrm = buildQuoteCrmData(sourceQuote, {
          installationDate: nextDate,
        });

        await syncReminderDocsForDeal({
          ...sourceQuote,
          id: quoteId,
          crmRecordType: "quote",
          quoteTypeResolved: resolveQuoteType(sourceQuote),
          pipelineType: resolveQuoteType(sourceQuote),
          quoteNo: sourceQuote.quoteNo || "",
          customerLabel:
            sourceQuote.customerName ||
            nextCrm.contact?.name ||
            "",
          crmTitle: nextCrm.title,
          crmContact: nextCrm.contact,
          nextActionDate: nextCrm.nextActionDate,
          installationDate: nextDate,
          reminderConfigs: nextCrm.reminderConfigs,
          followUps: nextCrm.followUps,
          attachments: nextCrm.attachments,
        });
      }

      notify("Installation date saved", "success");
    } catch (error) {
      console.error("Failed to save installation date:", error);
      notify("Could not save the installation date. Please try again.", "error");
    }
  },
  [
    applyQuoteLocalPatch,
    buildQuoteCrmData,
    canEditQuotes,
    notify,
    saveCrmFieldsForLead,
    savedQuotes,
    selectedCrmDealId,
    syncReminderDocsForDeal,
    workspaceUid,
  ]
);

/* ========================= UPDATE INSTALLATION DATE (SAFE) ========================= */

  /* --------------------------- CUTTING OPTIMIZER CORE HELPERS --------------------------- */

const compareOptimizerSolutions = (a, b) => {
  const aRequiresExtra =
    Boolean(a?.requiresExtraCut) ||
    Number(a?.finalRemainingMeters || 0) > 0 ||
    a?.isCompleteFromAvailable === false;
  const bRequiresExtra =
    Boolean(b?.requiresExtraCut) ||
    Number(b?.finalRemainingMeters || 0) > 0 ||
    b?.isCompleteFromAvailable === false;

  if (aRequiresExtra !== bRequiresExtra) {
    return Number(aRequiresExtra) - Number(bRequiresExtra);
  }

  const wasteDiff =
    Number(a?.totalWasteMeters ?? a?.totalWaste ?? 0) -
    Number(b?.totalWasteMeters ?? b?.totalWaste ?? 0);
  if (wasteDiff !== 0) return wasteDiff;

  const rollCountDiff =
    Number(a?.rollCount ?? a?.totalRollsUsed ?? 0) -
    Number(b?.rollCount ?? b?.totalRollsUsed ?? 0);
  if (rollCountDiff !== 0) return rollCountDiff;

  return (
    Number(a?.totalRollLengthMeters ?? a?.totalUsedMeters ?? 0) -
    Number(b?.totalRollLengthMeters ?? b?.totalUsedMeters ?? 0)
  );
};



const optimizeCutsGGMulti = ({ rolls, demands, fabricKey }) => {
  const cleanRolls = (rolls || [])
    .map(num)
    .filter((r) => r > 0)
    .sort((a, b) => b - a);

  const repeatFabric = fabricHasRepeat(fabricKey);

  const vrMeters = repeatFabric
    ? (getVerticalRepeatMeters(fabricKey) || 0)
    : 0;

  if (!cleanRolls.length) {
    alert("Enter at least one roll length.");
    return [];
  }

  const demandMap = {};

  (demands || []).forEach((d) => {
    const len = round2(num(d.length));
    const qty = Math.max(0, Math.floor(num(d.qty)));

    if (len <= 0 || qty <= 0) return;

    const key = len.toFixed(6);

    if (!demandMap[key])
      demandMap[key] = { length: len, qty: 0 };

    demandMap[key].qty += qty;
  });

  const demandList = Object.values(demandMap)
    .sort((a, b) => b.length - a.length);

  if (!demandList.length) {
    alert("No valid demand pieces.");
    return [];
  }

  const toMm = (m) => Math.round(m * 1000);
  
  const bestPatternForRollDP = (rollLenMeters, demandArray) => {

  const rollMm = toMm(rollLenMeters);
  const items = [];

  demandArray.forEach((d) => {
    const lenMm = toMm(d.length);
    let qty = Math.max(0, Math.floor(d.qty));

    let power = 1;

    while (qty > 0) {
      const take = Math.min(power, qty);

      items.push({
        lenMm,
        count: take,
        valueMm: lenMm * take,
        origLen: d.length,
      });

      qty -= take;
      power <<= 1;
    }
  });

  const dp = new Int32Array(rollMm + 1);
  const parent = Array(rollMm + 1).fill(null);

  for (let i = 0; i < items.length; i++) {
    const weight = items[i].valueMm;

    for (let cap = rollMm; cap >= weight; cap--) {
      const candidate = dp[cap - weight] + weight;

      if (candidate > dp[cap]) {
        dp[cap] = candidate;
        parent[cap] = { prev: cap - weight, item: i };
      }
    }
  }

  let bestCap = 0;

  for (let c = 1; c <= rollMm; c++) {
    if (dp[c] > dp[bestCap]) {
      bestCap = c;
    }
  }

  const usedCounts = {};
  let cap = bestCap;

  while (cap > 0 && parent[cap]) {
    const { prev, item } = parent[cap];
    const it = items[item];

    const key = Number(it.origLen).toFixed(6);
    usedCounts[key] = (usedCounts[key] || 0) + (it.count || 0);

    cap = prev;
  }

  const pattern = {};
  Object.entries(usedCounts).forEach(([lenStr, cnt]) => {
    pattern[Number(lenStr)] = cnt;
  });

  const piecesMeters = Object.entries(pattern).reduce(
  (acc, [len, qty]) => acc + (Number(len) * qty),
  0
);

const repeatAdded =
  repeatFabric && Object.keys(pattern).length > 0
    ? vrMeters
    : 0;

const finalUsed = round2(piecesMeters + repeatAdded);

  return {
  pattern,

  piecesMeters: round2(piecesMeters),
  repeatAdded,

  used: finalUsed,
  waste: round2(Math.max(0, rollLenMeters - finalUsed)),
};
};


const generateCombos = (rollsArr) => {
  const combos = [];
  const n = rollsArr.length;

  const dfs = (idx, cur) => {
    if (idx === n) {
      if (cur.length > 0) combos.push(cur.slice());
      return;
    }

    // skip
    dfs(idx + 1, cur);

    // take
    cur.push(rollsArr[idx]);
    dfs(idx + 1, cur);
    cur.pop();
  };

  dfs(0, []);
  return combos;
};

  const evaluateCombo = (combo) => {

    const demandClone = demandList.map((d) => ({
      length: d.length,
      qty: d.qty,
    }));

    const rollResults = [];

    for (let ri = 0; ri < combo.length; ri++) {

      const rollLen = combo[ri];

      const patt = bestPatternForRollDP(rollLen, demandClone, fabricKey);

      Object.entries(patt.pattern || {}).forEach(
        ([lenStr, usedCount]) => {

          const lenNum = Number(lenStr);

          const target = demandClone.find(
            (x) =>
              Number(x.length.toFixed(4)) === Number(lenNum.toFixed(4))
          );

          if (target) {
            target.qty = Math.max(
              0,
              target.qty - usedCount
            );
          }
        }
      );

     rollResults.push({
  rollLen,
  patternInfo: patt,
});
    }

let remaining = demandClone
  .filter(d => d.qty > 0)
  .map(d => ({
    length: d.length,
    qty: d.qty
  }));

let remainingMetersRaw = round2(
  remaining.reduce(
    (s, p) => s + p.length * p.qty,
    0
  )
);
    const isCompleteFromAvailable = remaining.length === 0;
    const maxRoll = cleanRolls.length ? Math.max(...cleanRolls) : 0;
    const remainingRolls =
      remainingMetersRaw > 0 && maxRoll > 0
        ? Math.ceil(remainingMetersRaw / maxRoll)
        : 0;

    const finalRemainingMeters = round2(
      remainingMetersRaw + (repeatFabric ? remainingRolls * vrMeters : 0)
    );

    const totalUsedMeters = round2(
      rollResults.reduce((s, r) => s + Number(r.patternInfo?.used || 0), 0) +
      finalRemainingMeters
    );

    const totalWaste = round2(
      rollResults.reduce(
        (s, r) =>
          s + (r.patternInfo?.waste || 0),
        0
      )
    );
    const availableRollLengthMeters = round2(
      combo.reduce((sum, rollLen) => sum + Number(rollLen || 0), 0)
    );
    const totalRollLengthMeters = round2(
      availableRollLengthMeters + finalRemainingMeters
    );
    const rollCount = combo.length + remainingRolls;

    return {
  comboRolls: combo.slice(),
  rollResults,
  remaining,
  isCompleteFromAvailable,
  requiresExtraCut: finalRemainingMeters > 0,
  finalRemainingMeters,
  totalWaste,
  totalWasteMeters: totalWaste,
  rollCount,
  totalRollLengthMeters,
  availableRollLengthMeters,
  totalRollsUsed: combo.length + remainingRolls,
  totalUsedMeters,
};
  };

  let allEvaluated = [];
  const combos = generateCombos(cleanRolls);

  combos.forEach((combo) => {
    const res = evaluateCombo(combo);
    allEvaluated.push(res);
  });

 allEvaluated.sort(compareOptimizerSolutions);

  const TOP_K = 10;

  const finalList = allEvaluated
    .slice(0, TOP_K)
    .map((s, i) => {

      s.id = `opt-${i + 1}`;

      s.rollResults = s.rollResults.map((r) => ({
        rollLen: r.rollLen,
        patternInfo:
          r.patternInfo || {
            pattern: {},
            used: 0,
            waste: round2(r.rollLen || 0),
          },
      }));

      return s;
    });

  return finalList;
};

 
// === Product type helpers ===
const isCurtainProductConfigOption = (config = {}) => {
  const quoteTypes = Array.isArray(config.usedInQuoteTypes)
    ? config.usedInQuoteTypes
    : [];
  if (quoteTypes.length === 0) return true;

  return quoteTypes.some((type) => {
    const normalizedType = String(type || "").trim().toLowerCase();
    return (
      normalizedType === CURTAIN_QUOTE_TYPE ||
      normalizedType === "curtain" ||
      normalizedType === "curtains"
    );
  });
};

const curtainProductOptions = useMemo(
  () =>
    mergeProductConfigsWithDefaults(productConfigs, fabricCalcSettings)
      .filter(
        (config) =>
          config.active !== false &&
          isCurtainProductConfigOption(config)
      )
      .sort((a, b) => {
        if (a.isDefault && !b.isDefault) return -1;
        if (!a.isDefault && b.isDefault) return 1;
        return String(a.name || "").localeCompare(String(b.name || ""));
      }),
  [fabricCalcSettings, productConfigs]
);

const productConfigById = useMemo(
  () => new Map(curtainProductOptions.map((config) => [config.id, config])),
  [curtainProductOptions]
);

const getCurtainRowActiveProductConfig = useCallback(
  (row = {}) => {
    const candidateIds = [
      row.productId,
      row.productConfigSnapshot?.id,
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean);

    for (const candidateId of candidateIds) {
      const match = productConfigById.get(candidateId);
      if (match) return match;
    }

    const candidateNames = [
      row.productName,
      row.productType,
      row.product,
      row.productConfigSnapshot?.name,
      row.productConfigSnapshot?.normalizedName,
    ]
      .map((value) => normalizeProductName(value || ""))
      .filter(Boolean);

    return (
      curtainProductOptions.find((config) =>
        candidateNames.includes(normalizeProductName(config.normalizedName || config.name))
      ) || null
    );
  },
  [curtainProductOptions, productConfigById]
);

const getCurtainRowSavedProductLabel = useCallback((row = {}) => {
  const label =
    row.productName ||
    row.productType ||
    row.product ||
    row.productConfigSnapshot?.name ||
    row.productConfigSnapshot?.normalizedName ||
    "";
  return String(label || "").trim();
}, []);

const getCurtainRowProductSelectValue = useCallback(
  (row = {}) => {
    const activeConfig = getCurtainRowActiveProductConfig(row);
    if (activeConfig?.id) return activeConfig.id;

    const savedLabel = getCurtainRowSavedProductLabel(row);
    return savedLabel ? `saved:${normalizeProductName(savedLabel) || savedLabel}` : "";
  },
  [getCurtainRowActiveProductConfig, getCurtainRowSavedProductLabel]
);

const getCurtainRowSavedProductOption = useCallback(
  (row = {}) => {
    if (getCurtainRowActiveProductConfig(row)) return null;
    const savedLabel = getCurtainRowSavedProductLabel(row);
    if (!savedLabel) return null;
    return {
      value: getCurtainRowProductSelectValue(row),
      label: `${savedLabel} (saved)`,
    };
  },
  [
    getCurtainRowActiveProductConfig,
    getCurtainRowProductSelectValue,
    getCurtainRowSavedProductLabel,
  ]
);

const getRowProductConfig = useCallback(
  (row = {}) => resolveProductConfig(row, curtainProductOptions, fabricCalcSettings),
  [curtainProductOptions, fabricCalcSettings]
);

const getRowProductRules = useCallback(
  (row = {}) => {
    const config = getRowProductConfig(row);
    return {
      config,
      isLegacy: shouldUseLegacyProductCalculation(row, config),
      requiredFields: config?.requiredFields || {},
      calculationType: config?.calculationType || "legacy",
      workshopRules: config?.workshopRules || {},
      pdfRules: config?.pdfRules || {},
    };
  },
  [getRowProductConfig]
);

const isCurtainAccessoryRow = useCallback(
  (row = {}) => {
    const config = getRowProductConfig(row);
    return (
      row?.lineType === "accessory" ||
      isCurtainAccessoryRowStatic(row) ||
      (config ? isAccessoryProductConfig(config) : false)
    );
  },
  [getRowProductConfig]
);

const curtainMeasurementEntries = useMemo(
  () =>
    rows
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => !isCurtainAccessoryRow(row)),
  [isCurtainAccessoryRow, rows]
);

const curtainAccessoryEntries = useMemo(
  () =>
    rows
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => isCurtainAccessoryRow(row)),
  [isCurtainAccessoryRow, rows]
);

const quoteFullnessOverrideRows = useMemo(() => {
  const usedStitches = new Map();

  curtainMeasurementEntries.forEach(({ row }) => {
    const productConfig = getRowProductConfig(row);
    const productName = normalizeProductName(
      row?.productName || row?.productType || row?.product || productConfig?.name || ""
    );
    const fabricRules = resolveFabricRulesForRow(
      row,
      productConfig && !shouldUseLegacyProductCalculation(row, productConfig)
        ? productConfig
        : null,
      fabricCalcSettings
    );
    const usesStitchFullness =
      productName === "curtains" ||
      (productConfig?.calculationType === "fabric_meter" &&
        fabricRules?.fullnessSource === "stitching");

    if (!usesStitchFullness) return;

    const stitchMatch = findCurtainRowStitchConfig(row, stitchingRates);
    const stitchingLabel =
      getCurtainRowWorkshopStitchLabel(row, stitchingRates, productConfig) ||
      stitchMatch?.key ||
      "";
    const stableStitchingId = sanitizeEditableText(
      row?.stitchingStyleId ||
        row?.stitchingConfigId ||
        row?.stitchingId ||
        row?.stitchId ||
        row?.selectedStitch?.id ||
        row?.selectedStitching?.id ||
        stitchMatch?.config?.id ||
        stitchMatch?.candidate ||
        ""
    );
    const stitchingKey = normalizeStitchingKey(
      stableStitchingId ||
        stitchMatch?.key ||
        row?.stitchingStyle ||
        row?.stitch ||
        stitchingLabel
    );

    if (!stitchingKey) return;

    const defaultFullnessResult = resolveCurtainFullness(
      row,
      productConfig,
      stitchingRates,
      {
        fabricCalcSettings,
        fabricRules,
        quoteFullnessOverrides: {},
      }
    );
    const defaultFullness = Number(
      stitchMatch?.config?.fullness ||
        defaultFullnessResult?.stitchingFullness ||
        defaultFullnessResult?.fullness ||
        0
    );
    const storedOverrideKeys = [
      stitchingKey,
      stableStitchingId,
      stitchMatch?.candidate,
      stitchMatch?.key,
      stitchMatch?.config?.id,
      stitchingLabel,
    ]
      .map((value) => normalizeStitchingKey(value))
      .filter(Boolean);
    const storedOverride =
      storedOverrideKeys
        .map((key) => quoteFullnessOverrides?.[key])
        .find((override) => override && typeof override === "object") || {};
    const existing = usedStitches.get(stitchingKey);

    usedStitches.set(stitchingKey, {
      stitchingKey,
      stitchingStyleId: stableStitchingId || stitchingKey,
      stitchingLabel:
        stitchingLabel && stitchingLabel !== WORKSHOP_EMPTY_VALUE
          ? stitchingLabel
          : stitchMatch?.key || "Selected Stitch",
      defaultFullness:
        existing?.defaultFullness ||
        (Number.isFinite(defaultFullness) && defaultFullness > 0
          ? Number(defaultFullness.toFixed(2))
          : null),
      quoteFullness:
        Number.isFinite(Number(storedOverride?.quoteFullness)) &&
        Number(storedOverride.quoteFullness) > 0
          ? Number(storedOverride.quoteFullness)
          : null,
      rowCount: Number(existing?.rowCount || 0) + 1,
    });
  });

  return Array.from(usedStitches.values()).sort((a, b) =>
    String(a.stitchingLabel || "").localeCompare(String(b.stitchingLabel || ""))
  );
}, [
  curtainMeasurementEntries,
  fabricCalcSettings,
  getRowProductConfig,
  quoteFullnessOverrides,
  stitchingRates,
]);

const updateQuoteFullnessOverride = useCallback(
  (stitchRow = {}, rawValue = "") => {
    const inputValue = String(rawValue ?? "").trim();
    if (!isPermittedDecimalInput(inputValue)) return;

    const stitchingKey = normalizeStitchingKey(
      stitchRow.stitchingKey || stitchRow.stitchingLabel || ""
    );
    if (!stitchingKey) return;

    setQuoteFullnessOverrideInputs((prev) => ({
      ...prev,
      [stitchingKey]: inputValue,
    }));

    const numericValue = Number(inputValue);
    const validQuoteFullness =
      inputValue !== "" &&
      Number.isFinite(numericValue) &&
      numericValue > 0 &&
      numericValue <= 10
        ? numericValue
        : null;
    const nextEntry = buildQuoteFullnessOverrideEntry({
      stitchingKey,
      stitchingStyleId: stitchRow.stitchingStyleId || stitchingKey,
      stitchingLabel: stitchRow.stitchingLabel,
      defaultFullness: stitchRow.defaultFullness,
      quoteFullness: validQuoteFullness,
    });
    if (!nextEntry) return;

    setQuoteFullnessOverrides((prev) => {
      const normalizedPrev = normalizeQuoteFullnessOverrides(prev);
      const next = {
        ...normalizedPrev,
        [stitchingKey]: nextEntry,
      };
      quoteFullnessOverridesRef.current = next;
      return next;
    });
    markCurtainTotalsSettingsEdited(
      "quoteFullnessOverrides",
      `${stitchingKey}:${inputValue}`
    );
  },
  [markCurtainTotalsSettingsEdited]
);

const curtainLineFamilyTotals = useMemo(() => {
  const measurementSubtotal = curtainMeasurementEntries.reduce(
    (sum, { row, index }) =>
      sum + Number(getCurtainProductionAdjustedRowCalc(row, index).rowTotal || 0),
    0
  );
  const accessorySubtotal = curtainAccessoryEntries.reduce(
    (sum, { row, index }) =>
      sum + Number(getCurtainProductionAdjustedRowCalc(row, index).rowTotal || 0),
    0
  );
  return {
    measurementSubtotal: round2(measurementSubtotal),
    accessorySubtotal: round2(accessorySubtotal),
  };
}, [
  curtainAccessoryEntries,
  curtainMeasurementEntries,
  getCurtainProductionAdjustedRowCalc,
]);

const getCurtainOrderedFabricDisplayTrace = useCallback(
  (row = {}, rowIndex = -1, providedCalc = null) => {
    const rowId = getWorkshopRowStableId(row) || `row-${rowIndex}`;
    const productConfig = getRowProductConfig(row);
    const baseCalc = calcRow(row, repeatSettings);
    const adjustedCalc =
      providedCalc || getCurtainProductionAdjustedRowCalc(row, rowIndex);
    const rowPlan =
      Number.isInteger(rowIndex) && rowIndex >= 0
        ? curtainFabricSummaryResult?.plan?.rowPlans?.[rowIndex] || null
        : null;
    const noReuseRowPlan =
      Number.isInteger(rowIndex) && rowIndex >= 0
        ? curtainNoReuseBillingResult?.plan?.rowPlans?.[rowIndex] || null
        : null;
    const fabricRules =
      adjustedCalc?.calculationBreakdown?.fabricRules ||
      baseCalc?.calculationBreakdown?.fabricRules ||
      resolveFabricRulesForRow(
        row,
        productConfig && !shouldUseLegacyProductCalculation(row, productConfig)
          ? productConfig
          : null,
        fabricCalcSettings
      );
    const stitchConfigMatch = findCurtainRowStitchConfig(row, stitchingRates);
    const stitchConfig = stitchConfigMatch?.config || {};
    const fullnessResult =
      adjustedCalc?.calculationBreakdown?.fullness ||
      baseCalc?.calculationBreakdown?.fullness ||
      resolveCurtainFullness(row, productConfig, stitchingRates, {
        fabricCalcSettings,
        fabricRules,
        quoteFullnessOverrides,
      });
    const panelMath = getHalfPieceReusePiecePlan(row, baseCalc, {
      productConfig,
    });
    const fullnessSource = panelMath.fullnessSource || fullnessResult?.source || "";
    const finalFullnessUsed = Number(panelMath.fullness || fullnessResult?.fullness || 0);
    const fullnessApplicable =
      fullnessResult?.applicable !== false &&
      !["none", "not_applicable"].includes(String(fullnessSource || "").trim());
    const fullnessRowOverride =
      fullnessApplicable && Number(fullnessResult?.rowOverride || 0) > 0
        ? Number(fullnessResult.rowOverride)
        : null;
    const fullnessStitchingStyle =
      fullnessApplicable && Number(stitchConfig?.fullness || fullnessResult?.stitchingFullness || 0) > 0
        ? Number(stitchConfig?.fullness || fullnessResult?.stitchingFullness || 0)
        : null;
    const fullnessQuoteOverride =
      fullnessApplicable && Number(fullnessResult?.quoteFullness || 0) > 0
        ? Number(fullnessResult.quoteFullness)
        : null;
    const fullnessProductDefault =
      fullnessApplicable &&
      Number(fullnessResult?.productDefaultFullness ?? fullnessResult?.productFullness ?? 0) > 0
        ? Number(fullnessResult?.productDefaultFullness ?? fullnessResult?.productFullness)
        : null;
    const fullnessLegacyFallback =
      fullnessApplicable &&
      fullnessSource === "legacy_global_fallback" &&
      Number(fullnessResult?.legacyGlobalFallback || 0) > 0
        ? Number(fullnessResult.legacyGlobalFallback)
        : null;
    const fullnessLegacyFallbackIgnored =
      fullnessApplicable &&
      fullnessSource !== "legacy_global_fallback" &&
      Number(fullnessResult?.legacyGlobalFallbackIgnored || 0) > 0
        ? Number(fullnessResult.legacyGlobalFallbackIgnored)
        : null;
    const fullnessAudit = {
      rowOverride: fullnessRowOverride,
      quoteOverride: fullnessQuoteOverride,
      stitchingStyle: fullnessStitchingStyle,
      productDefault: fullnessProductDefault,
      legacyGlobalFallback: fullnessLegacyFallback,
      legacyGlobalFallbackIgnored: Boolean(fullnessLegacyFallbackIgnored),
      finalUsed: fullnessApplicable ? finalFullnessUsed : 1,
      source: fullnessApplicable ? fullnessSource || "unknown" : "not_applicable",
      applicable: Boolean(fullnessApplicable),
    };
    const snapshot = row?.calculationSnapshot || adjustedCalc?.calculationSnapshot || {};
    const savedSnapshotMode = adjustedCalc?.usesCalculationSnapshot === true;
    const legacyMode =
      savedSnapshotMode ||
      row?.calculationSnapshotStatus === "legacy" ||
      snapshot?.legacyFormula === true ||
      snapshot?.requiresRecalculation === true;
    const finalCutHeightIn = round2(
      Number(
        adjustedCalc?.cutCurtain ||
          adjustedCalc?.cutRoman ||
          baseCalc?.cutCurtain ||
          baseCalc?.cutRoman ||
          rowPlan?.cutHeightIn ||
          snapshot?.cutHeightIn ||
          0
      )
    );
    const rawOrderedMeters = round2(
      finalCutHeightIn > 0
        ? (Number(panelMath.rawPanels || 0) * finalCutHeightIn) / 39.37
        : 0
    );
    const wholePanelMeters = round2(
      finalCutHeightIn > 0
        ? (Number(panelMath.wholePanels || 0) * finalCutHeightIn) / 39.37
        : 0
    );
    const halfStepMeters = round2(
      finalCutHeightIn > 0
        ? (Number(panelMath.halfStepPanels || 0) * finalCutHeightIn) / 39.37
        : 0
    );
    const savedOrderedFabricMeters = round2(
      Number(
        snapshot?.orderedFabricMeters ??
          snapshot?.customerBillableFabricMeters ??
          row?.orderedFabricMeters ??
          row?.customerBillableFabricMeters ??
          row?.fabricMeters ??
          0
      )
    );
    const liveCalculatedOrderedFabricMeters = round2(
      Number(
        noReuseRowPlan?.newFabricNeededMeters ??
          noReuseRowPlan?.newMeters ??
          baseCalc?.fabricMetersFull ??
          baseCalc?.fabricMeters ??
          wholePanelMeters ??
          0
      )
    );
    const displayedOrderedFabricMeters = round2(
      Number(
        adjustedCalc?.customerBillableFabricMeters ??
          adjustedCalc?.orderedFabricMeters ??
          adjustedCalc?.fabricMeters ??
          0
      )
    );
    const customerBillableFabricMeters = round2(
      Number(
        adjustedCalc?.customerBillableFabricMeters ??
          displayedOrderedFabricMeters
      )
    );
    const workshopFabricRequiredMeters = round2(
      Number(
        adjustedCalc?.workshopFabricRequiredMeters ??
          adjustedCalc?.workshopNewFabricMeters ??
          rowPlan?.newFabricNeededMeters ??
          rowPlan?.newMeters ??
          0
      )
    );
    const actualReuseOnThisRow =
      Number(rowPlan?.reusedHalfCount || 0) > 0 ||
      Number(rowPlan?.halfReusedMeters || 0) > 0;
    const wholeBillingPanels = Number(
      adjustedCalc?.wholeBillingPanels ??
        noReuseRowPlan?.newFullCuts ??
        noReuseRowPlan?.wholePanels ??
        panelMath.wholePanels ??
        0
    );
    const halfStepProductionPanels = Number(
      adjustedCalc?.halfStepProductionPanels ??
        rowPlan?.totalPieces ??
        rowPlan?.halfStepPanels ??
        panelMath.halfStepPanels ??
        0
    );
    const panelsUsedForWorkshop = Number(
      adjustedCalc?.workshopPanels ??
        (rowPlan
          ? Number(rowPlan?.newFullCuts || 0) + Number(rowPlan?.reusedHalfCount || 0) * 0.5
          : wholeBillingPanels)
    );
    const panelsUsedForCustomerBilling = Number(
      adjustedCalc?.customerBillingPanels ??
        (passHalfPieceSavingsToCustomer && actualReuseOnThisRow
          ? panelsUsedForWorkshop
          : wholeBillingPanels)
    );
    const panelsSource = passHalfPieceSavingsToCustomer && actualReuseOnThisRow
      ? "halfPieceReuse.rowPlan"
      : "noReuseWholePanelBilling";
    const displayedMetersSource = passHalfPieceSavingsToCustomer && actualReuseOnThisRow
      ? "customerBillableFromHalfPieceReuse"
      : "customerBillableFromNoReuseFullOrdered";
    const topAllowanceIn = round2(
      Number(fabricRules?.topHemIn ?? fabricRules?.topHem ?? fabricCalcSettings?.curtain_top_hem ?? 0)
    );
    const bottomAllowanceIn = round2(
      Number(fabricRules?.bottomHemIn ?? fabricRules?.bottomHem ?? fabricCalcSettings?.curtain_bottom_hem ?? 0)
    );
    const totalCutAllowanceIn = round2(
      Number(fabricRules?.dropAllowanceIn ?? topAllowanceIn + bottomAllowanceIn)
    );

    return {
      quoteNo,
      rowId,
      rowIndex,
      itemName: row?.item || row?.name || "",
      product: row?.productType || row?.productName || row?.product || productConfig?.name || "",
      fabricCode: getCurtainRowFabricLabel(row),
      fabricKey: getCurtainRowFabricKey(row),
      selectedStitchLabel: getCurtainRowWorkshopStitchLabel(row, stitchingRates, productConfig),
      selectedStitchId:
        stitchConfigMatch?.key ||
        row?.stitchingStyleId ||
        row?.stitchingStyle ||
        row?.stitch ||
        "",
      selectedStitchRawValue: row?.stitchingStyle || row?.stitchingStyleId || row?.stitch || "",
      resolvedStitchConfig: {
        source: stitchConfigMatch?.source || "",
        key: stitchConfigMatch?.key || "",
        rate: Number(stitchConfig?.rate || 0),
        fullness: Number(stitchConfig?.fullness || 0),
        trackLengthFt: Number(stitchConfig?.trackLengthFt || 0),
        trackRateFt: Number(stitchConfig?.trackRateFt || 0),
        rawConfig: stitchConfig,
      },
      widthInput: row?.width ?? "",
      heightInput: row?.height ?? "",
      measurementUnit: getRowMeasurementUnit(row, unit),
      widthIn: Number(row?.width || 0),
      heightIn: Number(row?.height || 0),
      fabricWidthIn: Number(panelMath.fabricWidthIn || fabricRules?.fabricWidthIn || 0),
      usableWidthDeductionIn: Number(
        panelMath.usableWidthDeductionIn || fabricRules?.usableWidthDeductionIn || 0
      ),
      usableFabricWidthIn: Number(
        panelMath.usableFabricWidthIn || fabricRules?.usableFabricWidthIn || 0
      ),
      fullnessFromRow: fullnessRowOverride,
      fullnessFromQuote: fullnessQuoteOverride,
      fullnessFromStitch: fullnessStitchingStyle,
      fullnessFromProduct: fullnessProductDefault,
      fullnessFromSettings: fullnessLegacyFallback,
      legacyGlobalFullnessFallbackIgnored: Boolean(fullnessLegacyFallbackIgnored),
      finalFullnessUsed: fullnessAudit.finalUsed,
      fullnessSource: fullnessAudit.source,
      fullness: fullnessAudit,
      topAllowanceIn,
      bottomAllowanceIn,
      hemAllowanceIn: totalCutAllowanceIn,
      stitchAllowanceIn: Number(stitchConfig?.cutAllowanceIn || stitchConfig?.stitchAllowanceIn || 0),
      trackAllowanceIn: Number(stitchConfig?.trackAllowanceIn || 0),
      totalCutAllowanceIn,
      finalCutHeightIn,
      cutHeightSource: savedSnapshotMode
        ? "savedSnapshot.cutHeightIn"
        : "liveHeightPlusDropAllowance",
      rawPanels: Number(panelMath.rawPanels || 0),
      wholePanels: Number(panelMath.wholePanels || 0),
      halfStepPanels: Number(panelMath.halfStepPanels || 0),
      halfStepProductionPanels,
      wholeBillingPanels,
      panelsUsedForCustomerBilling,
      panelsUsedForWorkshop,
      workshopPanels: panelsUsedForWorkshop,
      customerBillingPanels: panelsUsedForCustomerBilling,
      panelsSource,
      rawOrderedMeters,
      orderedFabricMeters: liveCalculatedOrderedFabricMeters,
      customerBillableFabricMeters,
      workshopFabricMeters: workshopFabricRequiredMeters,
      workshopFabricRequiredMeters,
      fullOrderedFabricMeters: round2(
        Number(adjustedCalc?.fullOrderedFabricMeters ?? liveCalculatedOrderedFabricMeters)
      ),
      cutFabricMeters: round2(Number(adjustedCalc?.cutFabricMeters || halfStepMeters || 0)),
      wholePanelOrderedMeters: wholePanelMeters,
      halfStepOrderedMeters: halfStepMeters,
      useHalfPieceReuse,
      passHalfPieceSavingsToCustomer,
      halfPieceImpactOnThisRow: {
        createsHalfPiece: Boolean(rowPlan?.createdLeftoverId),
        reusedHalfCount: Number(rowPlan?.reusedHalfCount || 0),
        halfReusedMeters: Number(rowPlan?.halfReusedMeters || 0),
        reusedLeftoverId: rowPlan?.reusedLeftoverId || "",
        createdLeftoverId: rowPlan?.createdLeftoverId || "",
        affectsDisplayedMeters: Boolean(passHalfPieceSavingsToCustomer && actualReuseOnThisRow),
        reasonIfNoReuse: rowPlan?.reasonIfNoReuse || "",
      },
      legacyMode,
      savedSnapshotMode,
      savedOrderedFabricMeters,
      liveCalculatedOrderedFabricMeters,
      displayedOrderedFabricMeters,
      orderedFabricDisplayed: displayedOrderedFabricMeters,
      rowTotalFabricMetersUsed: customerBillableFabricMeters,
      displayedMetersSource,
      displaySource: displayedMetersSource,
      billingSource: displayedMetersSource,
      workshopSource: rowPlan ? "halfPiecePlanner.rowPlan" : "fullOrderedFabric",
      quoteDisplayMode: curtainQuoteTotalMode,
      calculationVersion:
        snapshot?.calculationVersion ||
        adjustedCalc?.calculationVersion ||
        CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
    };
  },
  // getHalfPieceReusePiecePlan is a function declaration in this component; it
  // reads the current quote settings through closure and is intentionally kept
  // out of this dependency list to avoid recreating the audit helper every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [
    calcRow,
    curtainFabricSummaryResult,
    curtainNoReuseBillingResult,
    curtainQuoteTotalMode,
    fabricCalcSettings,
    getCurtainProductionAdjustedRowCalc,
    getRowProductConfig,
    passHalfPieceSavingsToCustomer,
    quoteNo,
    quoteFullnessOverrides,
    repeatSettings,
    stitchingRates,
    unit,
    useHalfPieceReuse,
  ]
);

const compareCurtainRowsForFabricCalculation = useCallback((rowA = {}, rowB = {}) => {
  const trackedFields = [
    ["product", true],
    ["fabricCode", true],
    ["selectedStitchId", false],
    ["selectedStitchLabel", false],
    ["widthIn", true],
    ["heightIn", true],
    ["fabricWidthIn", true],
    ["usableWidthDeductionIn", true],
    ["usableFabricWidthIn", true],
    ["finalFullnessUsed", true],
    ["fullnessSource", true],
    ["totalCutAllowanceIn", true],
    ["finalCutHeightIn", true],
    ["rawPanels", true],
    ["wholePanels", true],
    ["halfStepPanels", true],
    ["panelsUsedForCustomerBilling", true],
    ["panelsUsedForWorkshop", true],
    ["panelsSource", true],
    ["savedSnapshotMode", true],
    ["displayedMetersSource", true],
    ["passHalfPieceSavingsToCustomer", true],
    ["displayedOrderedFabricMeters", false],
  ];
  const valueChanged = (a, b) => {
    const numberA = Number(a);
    const numberB = Number(b);
    if (Number.isFinite(numberA) && Number.isFinite(numberB)) {
      return Math.abs(numberA - numberB) > 0.01;
    }
    return String(a ?? "") !== String(b ?? "");
  };
  const differences = trackedFields
    .filter(([field]) => valueChanged(rowA[field], rowB[field]))
    .map(([field, affectsOrderedFabric]) => ({
      field,
      rowAValue: rowA[field],
      rowBValue: rowB[field],
      affectsOrderedFabric,
    }));
  const outputDiff = Math.abs(
    Number(rowA.displayedOrderedFabricMeters || 0) -
      Number(rowB.displayedOrderedFabricMeters || 0)
  );
  const inputDiffs = differences.filter((diff) => diff.affectsOrderedFabric);
  let conclusion = "Rows use the same ordered-fabric inputs and output.";

  if (inputDiffs.some((diff) => diff.field === "finalFullnessUsed" || diff.field === "fullnessSource")) {
    conclusion = "Different fullness used";
  } else if (
    inputDiffs.some((diff) =>
      ["finalCutHeightIn", "totalCutAllowanceIn"].includes(diff.field)
    )
  ) {
    conclusion = "Different cut height / allowance used";
  } else if (
    rowA.halfPieceImpactOnThisRow?.affectsDisplayedMeters ||
    rowB.halfPieceImpactOnThisRow?.affectsDisplayedMeters
  ) {
    conclusion = "One row affected by half-piece/customer billing setting";
  } else if (
    inputDiffs.some((diff) =>
      ["panelsUsedForCustomerBilling", "panelsUsedForWorkshop", "panelsSource"].includes(diff.field)
    )
  ) {
    conclusion = "Different panel rounding/source used";
  } else if (
    inputDiffs.some((diff) => diff.field === "savedSnapshotMode" || diff.field === "displayedMetersSource")
  ) {
    conclusion = "One row uses saved/stale ordered fabric";
  } else if (outputDiff > 0.01) {
    conclusion = "Bug: same inputs but different output";
  }

  return {
    rowA: {
      rowId: rowA.rowId,
      rowIndex: rowA.rowIndex,
      itemName: rowA.itemName,
      displayedOrderedFabricMeters: rowA.displayedOrderedFabricMeters,
    },
    rowB: {
      rowId: rowB.rowId,
      rowIndex: rowB.rowIndex,
      itemName: rowB.itemName,
      displayedOrderedFabricMeters: rowB.displayedOrderedFabricMeters,
    },
    sameVisibleInputs: inputDiffs.length === 0,
    outputDifferenceMeters: round2(outputDiff),
    differences,
    conclusion,
  };
}, []);

const getFullnessSourceLabel = (source = "") =>
  ({
    row_override: "Row Override",
    quote_stitch_override: "Quote Fullness",
    stitching_style: "Stitching Style",
    row_selected_stitch: "Selected Stitching",
    product_default: "Product Default",
    legacy_global_fallback: "Legacy Global Fallback",
    default_one: "Default 1x",
    not_applicable: "Not Applicable",
    none: "Not Applicable",
  }[String(source || "").trim()] || source || "Unknown");

const getOrderedFabricTooltipText = useCallback((audit = {}) => {
  const fullnessInfo = audit.fullness || {};
  const lines = fullnessInfo.applicable === false
    ? ["Fullness: Not applicable"]
    : [
        `Final fullness used: ${Number(audit.finalFullnessUsed || fullnessInfo.finalUsed || 0).toFixed(2)}`,
        `Source: ${getFullnessSourceLabel(audit.fullnessSource || fullnessInfo.source)}`,
      ];
  if (fullnessInfo.stitchingStyle) {
    lines.push(`Stitching style fullness: ${Number(fullnessInfo.stitchingStyle).toFixed(2)}`);
  }
  if (fullnessInfo.quoteOverride) {
    lines.push(`Quote fullness: ${Number(fullnessInfo.quoteOverride).toFixed(2)}`);
  }
  if (fullnessInfo.rowOverride) {
    lines.push(`Row override fullness: ${Number(fullnessInfo.rowOverride).toFixed(2)}`);
  }
  if (fullnessInfo.productDefault && !fullnessInfo.stitchingStyle) {
    lines.push(`Product default fullness: ${Number(fullnessInfo.productDefault).toFixed(2)}`);
  }
  if (fullnessInfo.legacyGlobalFallback) {
    lines.push(`Legacy global fallback used: ${Number(fullnessInfo.legacyGlobalFallback).toFixed(2)}`);
  }
  if (fullnessInfo.legacyGlobalFallbackIgnored) {
    lines.push("Legacy global fallback: ignored");
  }
  lines.push(
    `Usable fabric width: ${Number(audit.usableFabricWidthIn || 0).toFixed(2)} in`,
    `Raw panels: ${Number(audit.rawPanels || 0).toFixed(2)}`,
    `Whole panels: ${Number(audit.wholePanels || 0).toFixed(2)}`,
    `Half-step panels: ${Number(audit.halfStepPanels || 0).toFixed(2)}`,
    `Customer panels: ${Number(audit.panelsUsedForCustomerBilling || 0).toFixed(2)}`,
    `Workshop panels: ${Number(audit.panelsUsedForWorkshop || 0).toFixed(2)}`,
    `Cut height: ${Number(audit.finalCutHeightIn || 0).toFixed(2)} in`,
    `Displayed ordered fabric: ${Number(audit.displayedOrderedFabricMeters || 0).toFixed(2)} m`,
    `Source: ${audit.displayedMetersSource || "unknown"}`,
  );
  if (audit.savedSnapshotMode) {
    lines.push(`Saved value: ${Number(audit.savedOrderedFabricMeters || 0).toFixed(2)} m`);
  }
  if (audit.halfPieceImpactOnThisRow?.affectsDisplayedMeters) {
    lines.push("Half-piece reuse is affecting this displayed meter value.");
  }
  return lines.join("\n");
}, []);

const curtainOrderedFabricRowAudits = useMemo(
  () =>
    curtainMeasurementEntries.map(({ row, index }) =>
      getCurtainOrderedFabricDisplayTrace(row, index)
    ),
  [curtainMeasurementEntries, getCurtainOrderedFabricDisplayTrace]
);

const curtainOrderedFabricComparisons = useMemo(() => {
  const comparisons = [];
  for (let i = 0; i < curtainOrderedFabricRowAudits.length; i += 1) {
    for (let j = i + 1; j < curtainOrderedFabricRowAudits.length; j += 1) {
      const left = curtainOrderedFabricRowAudits[i];
      const right = curtainOrderedFabricRowAudits[j];
      const sameVisibleMeasurements =
        normalizeProductName(left.product) === normalizeProductName(right.product) &&
        normalizeFabricKey(left.fabricCode) === normalizeFabricKey(right.fabricCode) &&
        Math.abs(Number(left.widthIn || 0) - Number(right.widthIn || 0)) <= 0.01 &&
        Math.abs(Number(left.heightIn || 0) - Number(right.heightIn || 0)) <= 0.01 &&
        Math.abs(Number(left.fabricWidthIn || 0) - Number(right.fabricWidthIn || 0)) <= 0.01;
      const displayedDiff =
        Math.abs(
          Number(left.displayedOrderedFabricMeters || 0) -
            Number(right.displayedOrderedFabricMeters || 0)
        ) > 0.01;

      if (sameVisibleMeasurements || displayedDiff) {
        comparisons.push(compareCurtainRowsForFabricCalculation(left, right));
      }
    }
  }
  return comparisons;
}, [compareCurtainRowsForFabricCalculation, curtainOrderedFabricRowAudits]);

useEffect(() => {
  if (!IS_DEV_UI_ENABLED) return;
  if (!curtainOrderedFabricRowAudits.length) return;
  const signature = JSON.stringify(
    curtainOrderedFabricRowAudits.map((audit) => ({
      rowId: audit.rowId,
      rowIndex: audit.rowIndex,
      itemName: audit.itemName,
      displayedOrderedFabricMeters: audit.displayedOrderedFabricMeters,
      displayedMetersSource: audit.displayedMetersSource,
      finalFullnessUsed: audit.finalFullnessUsed,
      finalCutHeightIn: audit.finalCutHeightIn,
      wholePanels: audit.wholePanels,
      halfStepPanels: audit.halfStepPanels,
    }))
  );
  if (orderedFabricAuditSignatureRef.current === signature) return;
  orderedFabricAuditSignatureRef.current = signature;

  curtainOrderedFabricRowAudits.forEach((audit) => {
    console.log("CUSTOMER_VS_WORKSHOP_FABRIC_TRACE", {
      quoteNo: audit.quoteNo,
      rowId: audit.rowId,
      itemName: audit.itemName,
      rawPanels: audit.rawPanels,
      halfStepProductionPanels: audit.halfStepProductionPanels,
      wholeBillingPanels: audit.wholeBillingPanels,
      fullOrderedFabricMeters: audit.fullOrderedFabricMeters,
      workshopFabricMeters: audit.workshopFabricMeters,
      customerBillableFabricMeters: audit.customerBillableFabricMeters,
      useHalfPieceReuse: audit.useHalfPieceReuse,
      passHalfPieceSavingsToCustomer: audit.passHalfPieceSavingsToCustomer,
      orderedFabricDisplayed: audit.orderedFabricDisplayed,
      rowTotalFabricMetersUsed: audit.rowTotalFabricMetersUsed,
      displaySource: audit.displaySource,
      billingSource: audit.billingSource,
      workshopSource: audit.workshopSource,
    });
    console.log("ORDERED_FABRIC_ROW_AUDIT", audit);
    const fullnessInfo = audit.fullness || {};
    const stitchingStyleFullness = Number(fullnessInfo.stitchingStyle || 0);
    if (
      fullnessInfo.applicable !== false &&
      stitchingStyleFullness > 0 &&
      Math.abs(Number(fullnessInfo.finalUsed || audit.finalFullnessUsed || 0) - stitchingStyleFullness) > 0.01
    ) {
      console.warn("FULLNESS_SOURCE_MISMATCH", {
        rowId: audit.rowId,
        itemName: audit.itemName,
        product: audit.product,
        selectedStitchLabel: audit.selectedStitchLabel,
        rowOverride: fullnessInfo.rowOverride,
        stitchingStyleFullness,
        productDefaultFullness: fullnessInfo.productDefault,
        legacyGlobalFallback: fullnessInfo.legacyGlobalFallback || null,
        legacyGlobalFallbackIgnored: Boolean(fullnessInfo.legacyGlobalFallbackIgnored),
        finalFullnessUsed: fullnessInfo.finalUsed,
        fullnessSource: fullnessInfo.source,
      });
    }
    if (
      fullnessInfo.applicable === false &&
      (audit.fullnessFromProduct !== null || audit.fullnessFromSettings !== null)
    ) {
      console.warn("FULLNESS_SHOULD_NOT_APPLY_TO_PRODUCT", {
        rowId: audit.rowId,
        itemName: audit.itemName,
        product: audit.product,
        fullnessFromProduct: audit.fullnessFromProduct,
        fullnessFromSettings: audit.fullnessFromSettings,
        finalFullnessUsed: audit.finalFullnessUsed,
        fullnessSource: audit.fullnessSource,
      });
    }
    console.log("STITCH_CONFIG_AUDIT", {
      quoteNo: audit.quoteNo,
      rowId: audit.rowId,
      itemName: audit.itemName,
      stitchId: audit.selectedStitchId,
      name: audit.selectedStitchLabel,
      rate: audit.resolvedStitchConfig?.rate,
      fullness: audit.resolvedStitchConfig?.fullness,
      trackLength: audit.resolvedStitchConfig?.trackLengthFt,
      topAllowance: audit.topAllowanceIn,
      bottomAllowance: audit.bottomAllowanceIn,
      hemAllowance: audit.hemAllowanceIn,
      cutAllowance: audit.totalCutAllowanceIn,
      calculationFields: {
        fullnessSource: audit.fullnessSource,
        panelsSource: audit.panelsSource,
        displayedMetersSource: audit.displayedMetersSource,
      },
      rawConfig: audit.resolvedStitchConfig?.rawConfig || {},
    });
    console.log("PANEL_ROUNDING_AUDIT", {
      rowId: audit.rowId,
      itemName: audit.itemName,
      stitch: audit.selectedStitchLabel,
      rawPanels: audit.rawPanels,
      wholePanels: audit.wholePanels,
      halfStepPanels: audit.halfStepPanels,
      panelsUsed: audit.panelsUsedForCustomerBilling,
      panelsUsedSource: audit.panelsSource,
      calculationMode: audit.savedSnapshotMode ? "savedSnapshot" : "live",
    });
    console.log("ROW_DISPLAY_SOURCE_AUDIT", {
      quoteNo: audit.quoteNo,
      rowId: audit.rowId,
      itemName: audit.itemName,
      legacyMode: audit.legacyMode,
      savedSnapshotExists: audit.savedSnapshotMode,
      savedSnapshotOrderedFabric: audit.savedOrderedFabricMeters,
      liveOrderedFabric: audit.liveCalculatedOrderedFabricMeters,
      displayedOrderedFabric: audit.displayedOrderedFabricMeters,
      displayedSource: audit.displayedMetersSource,
    });
  });

  curtainOrderedFabricComparisons.forEach((comparison) => {
    if (
      comparison.conclusion === "Bug: same inputs but different output" ||
      (comparison.sameVisibleInputs && comparison.outputDifferenceMeters > 0.01)
    ) {
      console.error("IDENTICAL_CURTAIN_INPUTS_DIFFERENT_FABRIC_METERS", comparison);
    }
  });
}, [
  curtainOrderedFabricComparisons,
  curtainOrderedFabricRowAudits,
]);

const copyCurtainRowCalculationAudit = useCallback(async () => {
  const payload = {
    quoteNo,
    quoteDisplayMode: curtainQuoteTotalMode,
    useHalfPieceReuse,
    passHalfPieceSavingsToCustomer,
    rows: curtainOrderedFabricRowAudits,
    comparisons: curtainOrderedFabricComparisons,
  };
  try {
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    notify("Row calculation audit copied.", "success");
  } catch (error) {
    console.log("CURTAIN_ROW_CALCULATION_AUDIT", payload);
    notify("Clipboard unavailable. Row calculation audit logged to console.", "info");
  }
}, [
  curtainOrderedFabricComparisons,
  curtainOrderedFabricRowAudits,
  curtainQuoteTotalMode,
  notify,
  passHalfPieceSavingsToCustomer,
  quoteNo,
  useHalfPieceReuse,
]);

const curtainLiningSummaryRows = useMemo(() => {
  const grouped = new Map();

  curtainMeasurementEntries.forEach(({ row, index }) => {
    const calc = getCurtainProductionAdjustedRowCalc(row, index);
    const lining =
      calc.lining ||
      calculateCurtainRowLining(row, 0, false, {
        sqft: calc.adjSqft ?? calc.billableSqft ?? 0,
      });
    if (!curtainRowSupportsLining(row, calc) || !lining.enabled) return;

    const meters = round2(Number(lining.billableMeters || 0));
    const sqft = round2(Number(lining.sqft || 0));
    const amount = round2(Number(lining.amount || 0));
    if (meters <= 0 && amount <= 0) return;

    const label = getCurtainLiningLabel(lining);
    const chargeMethod = lining.chargeMethod === "sqft" ? "sqft" : "meter";
    const fabricCode = sanitizeEditableText(lining.fabricCode || "");
    const brandName =
      sanitizeEditableText(lining.brandName || "") ||
      getFabricBrandNameById(lining.brandId);
    const key = `${chargeMethod}::${normalizeFabricKey(label)}::${normalizeFabricKey(brandName)}::${normalizeFabricKey(fabricCode)}`;
    const previous = grouped.get(key) || {
      key,
      label,
      fabricCode,
      brandName,
      chargeMethod,
      meters: 0,
      sqft: 0,
      amount: 0,
      rowCount: 0,
    };

    grouped.set(key, {
      ...previous,
      meters: round2(previous.meters + meters),
      sqft: round2(previous.sqft + sqft),
      amount: round2(previous.amount + amount),
      rowCount: previous.rowCount + 1,
    });
  });

  return Array.from(grouped.values()).sort((a, b) =>
    String(a.label || "").localeCompare(String(b.label || ""))
  );
}, [curtainMeasurementEntries, getCurtainProductionAdjustedRowCalc, getFabricBrandNameById]);

const curtainLiningTotalAmount = useMemo(
  () =>
    round2(
      curtainLiningSummaryRows.reduce(
        (sum, row) => sum + Number(row.amount || 0),
        0
      )
    ),
  [curtainLiningSummaryRows]
);

const shouldShowFabricInput = useCallback(
  (row = {}, productConfig = null) => {
    const config = productConfig || getRowProductConfig(row);
    const hasExistingFabricReference = Boolean(getEditableFabricValue(row));
    if (hasExistingFabricReference) return true;
    if (!config) return true;
    const displayFields = config.displayFields || {};
    if (typeof displayFields.showFabric === "boolean") {
      return displayFields.showFabric;
    }

    return Boolean(
      config.requiredFields?.fabric ||
        config.fabricRules?.enabled ||
        config.pdfRules?.showFabric ||
        config.workshopRules?.includeFabricSummary
    );
  },
  [getRowProductConfig]
);

const shouldEnableFabricInput = useCallback(
  (row = {}, productConfig = null) => {
    const config = productConfig || getRowProductConfig(row);
    if (config?.displayFields?.fabricDisabled === true) return false;
    return shouldShowFabricInput(row, config);
  },
  [getRowProductConfig, shouldShowFabricInput]
);

const getFabricInputLabel = useCallback((productConfig = null) => {
  const label = productConfig?.displayFields?.fabricLabel || "Fabric";
  return String(label || "Fabric").trim() || "Fabric";
}, []);

const shouldShowRateAmountInput = useCallback(
  (row = {}, productConfig = null) =>
    shouldProductUseRateAmountField(row, productConfig || getRowProductConfig(row)),
  [getRowProductConfig]
);

const getRowRateFieldMeta = useCallback(
  (row = {}) => {
    const { config, calculationType, requiredFields } = getRowProductRules(row);
    if (!shouldShowRateAmountInput(row, config)) {
      return { field: "", label: "", disabled: true, hidden: true };
    }
    if (calculationType === "manual_amount") {
      return { field: "manualAmount", label: "Amount", disabled: false };
    }
    if (calculationType === "quantity_rate") {
      return { field: "unitRate", label: "Unit Rate", disabled: false };
    }
    if (calculationType === "running_length_rate") {
      return { field: "ratePerLength", label: "Rate/length", disabled: false };
    }
    if (requiredFields.ratePerSqft || calculationType === "sqft_only") {
      return { field: "sqftRate", label: "Rate/sqft", disabled: false };
    }
    return { field: "sqftRate", label: "Rate", disabled: true };
  },
  [getRowProductRules, shouldShowRateAmountInput]
);

const getRowSecondaryFieldMeta = useCallback(
  (row = {}) => {
    const { config, calculationType, requiredFields } = getRowProductRules(row);
    if (calculationType === "quantity_rate" || requiredFields.quantity) {
      return { field: "qty", label: "Qty", disabled: false };
    }
    if (
      calculationType === "running_length_rate" ||
      (config?.runningFeetRules?.formula === "manual_feet" && requiredFields.length)
    ) {
      return { field: "length", label: "Length", disabled: false };
    }
    return { field: "customStitchRate", label: "Custom Stitch", disabled: true };
  },
  [getRowProductRules]
);

const applyProductConfigToRow = useCallback(
  (row = {}, productConfig = null) => {
    const config = productConfig ? normalizeProductConfig(productConfig) : null;
    if (!config) return row;

    const nextRow = {
      ...row,
      lineType: isAccessoryProductConfig(config) ? "accessory" : "measurement",
      category: config.category || row.category || "",
      productId: config.id,
      productName: config.name,
      product: config.name,
      productType: config.name,
      calculationType: config.calculationType,
      productConfigSnapshot: buildProductConfigSnapshot(config),
    };
    const canApplyDefault = (fieldName) =>
      (nextRow[fieldName] === "" || nextRow[fieldName] == null) &&
      nextRow.touchedFields?.[fieldName] !== true;

    if (config.requiredFields.fabricWidth && canApplyDefault("fabricWidthIn")) {
      nextRow.fabricWidthIn = config.fabricRules.fabricWidthIn || 54;
    }
    if (config.requiredFields.ratePerSqft && canApplyDefault("sqftRate")) {
      nextRow.sqftRate = config.sqftRules.defaultRate || "";
    }
    if (config.requiredFields.ratePerFeet && canApplyDefault("ratePerFeet")) {
      nextRow.ratePerFeet = config.runningFeetRules.defaultRate || "";
    }
    if (config.requiredFields.ratePerLength && canApplyDefault("ratePerLength")) {
      nextRow.ratePerLength = config.runningFeetRules.defaultRate || "";
    }
    if (config.requiredFields.quantity && canApplyDefault("quantity")) {
      nextRow.quantity = 1;
    }
    if (config.requiredFields.quantity && canApplyDefault("qty")) {
      nextRow.qty = nextRow.quantity || 1;
    }
    if (config.requiredFields.unitRate && canApplyDefault("unitRate")) {
      nextRow.unitRate = config.quantityRules.defaultRate || "";
    }
    if (isAccessoryProductConfig(config)) {
      nextRow.fabric = "";
      nextRow.fabricWidthIn = "";
      nextRow.width = "";
      nextRow.height = "";
      nextRow.mrp = "";
      nextRow.sqftRate = "";
      nextRow.customStitchRate = "";
      nextRow.stitchingStyle = "";
      nextRow.lining = { ...DEFAULT_CURTAIN_LINING };
      nextRow.unit = nextRow.unit || config.quantityRules.defaultUnit || "pcs";
      nextRow.lengthUnit = nextRow.lengthUnit || config.runningFeetRules.defaultUnit || "ft";
      if (config.calculationType === "quantity_rate") {
        nextRow.qty = nextRow.qty || nextRow.quantity || 1;
        nextRow.quantity = nextRow.quantity || nextRow.qty || 1;
        nextRow.unitRate = nextRow.unitRate || config.quantityRules.defaultRate || "";
      }
      if (config.calculationType === "running_length_rate") {
        nextRow.length = nextRow.length || nextRow.runningFeet || "";
        nextRow.ratePerLength =
          nextRow.ratePerLength || nextRow.ratePerFeet || config.runningFeetRules.defaultRate || "";
      }
      if (config.calculationType === "manual_amount") {
        nextRow.manualAmount = nextRow.manualAmount ?? "";
      }
    }
    if (config.requiredFields.manualAmount && nextRow.manualAmount == null) {
      nextRow.manualAmount = "";
    }
    if (
      config.stitchingRules?.defaultStitchingStyleId &&
      !nextRow.stitchingStyle
    ) {
      nextRow.stitchingStyle = config.stitchingRules.defaultStitchingStyleId;
    }

    return sanitizeRowPricingFieldsByProduct(nextRow, config);
  },
  []
);

const isCurtain = (type) =>
  type === "Curtains" ||
  type === "Roman Blind - Normal" ||
  type === "Roman Blind - Clip On";

const isEyeletCustom = (row) =>
  row.stitchingStyle === "Eyelet (Custom)";

const updateField = (rowIdOrIndex, field, value, explicitSource = null) => {
  const source =
    explicitSource ||
    (field === "productType"
      ? QUOTE_RAW_FIELD_MUTATION_SOURCES.PRODUCT_CHANGE
      : QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT);

  commitCurtainRows((prev) => {
    const targetRowId =
      typeof rowIdOrIndex === "number"
        ? getWorkshopRowStableId(prev[rowIdOrIndex] || {})
        : rowIdOrIndex;

    return prev.map((existingRow, index) => {
      const stableExistingRow = withCurtainRowId(existingRow || {}, index);
      if (getWorkshopRowStableId(stableExistingRow) !== targetRowId) {
        return stableExistingRow;
      }

    const nextValue = value;

    let row = {
      ...stableExistingRow,
      [field]: nextValue
    };

    if (field === "stitchingStyle") {
      const stitchConfig = stitchingRates?.[nextValue] || {};
      const stitchSourceRow = {
        ...row,
        stitchingStyle: nextValue,
        stitchingStyleId: nextValue,
      };
      const resolvedStitchLabel = getCurtainRowWorkshopStitchLabel(
        stitchSourceRow,
        stitchingRates,
        getRowProductConfig(stitchSourceRow)
      );
      const safeStitchLabel = isWorkshopResolvedLabel(resolvedStitchLabel)
        ? resolvedStitchLabel
        : "";

      row = {
        ...row,
        stitchingStyle: nextValue,
        stitchingStyleId: nextValue,
        stitch: safeStitchLabel,
        stitchLabel: safeStitchLabel,
        stitchingLabel: safeStitchLabel,
        trackType: getWorkshopFieldLabel(stitchConfig.trackType) || row.trackType || "",
        trackName: getWorkshopFieldLabel(stitchConfig.trackName) || row.trackName || "",
      };

      if (IS_DEV_UI_ENABLED) {
        console.log("STITCH_DROPDOWN_CHANGE", {
          rowId: row.id || row.rowId || row.sourceRowId || "",
          rowIndex: index,
          selectedValue: nextValue,
          selectedOption: stitchConfig,
          resolvedStitchLabel: safeStitchLabel,
          nextRow: row,
        });
      }
    }

    if (field === "productType") {
      const selectedConfig =
        productConfigById.get(String(value || "")) ||
        curtainProductOptions.find(
          (config) => config.name === value || config.normalizedName === normalizeProductName(value)
        );
      row = selectedConfig ? applyProductConfigToRow(stableExistingRow, selectedConfig) : row;
    }

    if (
      [
        "stitchingStyle",
        "stitchingStyleId",
        "customStitchRate",
        "trackConfig",
        "trackLengthFt",
        "trackLength",
      ].includes(field)
    ) {
      row = {
        ...row,
        calculationSnapshot: null,
        calculationSnapshotStatus: "current",
      };
    }

    if (
      source === QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT ||
      source === QUOTE_RAW_FIELD_MUTATION_SOURCES.PRODUCT_CHANGE
    ) {
      row = {
        ...row,
        calculationSnapshot: null,
        calculationSnapshotStatus: "current",
        __forceCurrentCalculation: true,
        __recalculated: true,
      };
    }

    const fabricKey = getCurtainRowFabricKey(row);

    row.repeatSettings =
      repeatSettings?.[fabricKey] || {};

      const touchedFields =
        source === QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT ||
        source === QUOTE_RAW_FIELD_MUTATION_SOURCES.PRODUCT_CHANGE
          ? {
              ...(stableExistingRow.touchedFields || {}),
              [field]: true,
            }
          : stableExistingRow.touchedFields || {};

      return withCurtainRowId(
        {
          ...row,
          touchedFields,
          rowUpdatedAt: new Date().toISOString(),
        },
        index
      );
    });
  }, source);
};

const patchCurtainRowFields = (
  rowIdOrIndex,
  patch = {},
  explicitSource = QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT
) => {
  const source = explicitSource || QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT;
  commitCurtainRows((prev) => {
    const targetRowId =
      typeof rowIdOrIndex === "number"
        ? getWorkshopRowStableId(prev[rowIdOrIndex] || {})
        : rowIdOrIndex;
    const patchKeys = Object.keys(patch || {});

    return prev.map((existingRow, index) => {
      const stableExistingRow = withCurtainRowId(existingRow || {}, index);
      if (getWorkshopRowStableId(stableExistingRow) !== targetRowId) {
        return stableExistingRow;
      }

      let row = {
        ...stableExistingRow,
        ...patch,
      };

      if (source === QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT) {
        row = {
          ...row,
          calculationSnapshot: null,
          calculationSnapshotStatus: "current",
          __forceCurrentCalculation: true,
          __recalculated: true,
        };
      }

      const fabricKey = getCurtainRowFabricKey(row);
      row.repeatSettings = repeatSettings?.[fabricKey] || {};

      return withCurtainRowId(
        {
          ...row,
          touchedFields: {
            ...(stableExistingRow.touchedFields || {}),
            ...Object.fromEntries(patchKeys.map((key) => [key, true])),
          },
          rowUpdatedAt: new Date().toISOString(),
        },
        index
      );
    });
  }, source);
};

const getFabricCatalogueLibraryForBrand = (brandId = "") =>
  fabricCatalogueLibraryRef.current[brandId] ||
  fabricCatalogueLibraryByBrand[brandId] ||
  getEmptyFabricCatalogueLibrary(getFabricBrandById(brandId) || { id: brandId });

const syncFabricSummaryBrandFromRows = useCallback(
  (rowId = "", rowPatch = {}) => {
    if (!rowId) return;
    const nextRows = normalizeRowsForState(rowsRef.current || []).map((row, index) => {
      const stableRow = withCurtainRowId(row || {}, index);
      return getWorkshopRowStableId(stableRow) === rowId
        ? withCurtainRowId({ ...stableRow, ...rowPatch }, index)
        : stableRow;
    });
    const targetRow = nextRows.find((row) => getWorkshopRowStableId(row) === rowId);
    const fabricKey = normalizeFabricKey(getCurtainRowFabricKey(targetRow || {}));
    if (!fabricKey) return;

    const groupRows = nextRows.filter(
      (row) => normalizeFabricKey(getCurtainRowFabricKey(row)) === fabricKey
    );
    if (!groupRows.length) return;

    const brandEntries = groupRows.map((row) => ({
      brandId: sanitizeEditableText(row.brandId || row.fabricBrandId || ""),
      brandName: sanitizeEditableText(row.brandName || row.fabricBrandName || ""),
    }));
    if (brandEntries.some((entry) => !entry.brandId)) return;

    const uniqueBrandIds = Array.from(
      new Set(brandEntries.map((entry) => entry.brandId).filter(Boolean))
    );
    if (uniqueBrandIds.length !== 1) return;

    const [brandId] = uniqueBrandIds;
    const brandName =
      brandEntries.find((entry) => entry.brandId === brandId)?.brandName ||
      getFabricBrandNameById(brandId);
    const catalogueEntries = groupRows
      .map((row) => ({
        catalogueId: sanitizeEditableText(row.catalogueId || ""),
        catalogueName: sanitizeEditableText(row.catalogueName || ""),
        catalogueKey: sanitizeEditableText(row.catalogueKey || ""),
      }))
      .filter((entry) => entry.catalogueId || entry.catalogueKey || entry.catalogueName);
    const uniqueCatalogueKeys = Array.from(
      new Set(
        catalogueEntries
          .map((entry) => entry.catalogueId || entry.catalogueKey || entry.catalogueName)
          .filter(Boolean)
      )
    );
    const cataloguePatch =
      uniqueCatalogueKeys.length === 1
        ? catalogueEntries.find(
            (entry) =>
              (entry.catalogueId || entry.catalogueKey || entry.catalogueName) ===
              uniqueCatalogueKeys[0]
          ) || {}
        : {};
    setFabricSummaryMeta((prev) => {
      const normalized = normalizeFabricSummaryMeta(prev);
      const current = normalized[fabricKey] || {};
      return normalizeFabricSummaryMeta({
        ...normalized,
        [fabricKey]: {
          ...current,
          brandId,
          brandName,
          ...cataloguePatch,
        },
      });
    });
  },
  [getFabricBrandNameById, normalizeRowsForState]
);

const buildFabricCatalogueResolutionPatch = (
  row = {},
  {
    brandId = row.brandId || row.fabricBrandId || "",
    fabricText = getEditableFabricValue(row),
    includeResolvedMrp = true,
  } = {}
) => {
  const brand = getFabricBrandById(brandId);
  if (!brand) {
    return {
      fabricCodeRaw: fabricText,
      brandId: "",
      brandName: "",
      fabricBrandId: "",
      fabricBrandName: "",
      catalogueId: "",
      catalogueName: "",
      catalogueKey: "",
      srNumber: "",
      srLabel: "",
      srPriceRuleId: "",
      srPriceRuleType: "none",
      rrpSource: "unmatched",
    };
  }

  const library = getFabricCatalogueLibraryForBrand(brand.id);
  const resolution = resolveFabricCatalogueRrp({
    brand,
    library,
    fabricText,
    existingMrp: row.mrp,
  });
  const parsed = resolution.parsed || parseFabricCatalogueInput(fabricText);
  const patch = {
    fabricCodeRaw: fabricText,
    brandId: brand.id,
    brandName: brand.name,
    fabricBrandId: brand.id,
    fabricBrandName: brand.name,
    ...buildFabricCatalogueRowPatch(resolution),
    catalogueName:
      resolution.catalogue?.catalogueName || parsed.catalogueName || "",
    catalogueKey:
      resolution.catalogue?.catalogueKey || parsed.catalogueKey || "",
    srNumber: parsed.srNumber || "",
    srLabel: parsed.srLabel || "",
  };

  if (
    includeResolvedMrp &&
    resolution.matched &&
    resolution.rrpPerMeter != null
  ) {
    patch.mrp = String(resolution.rrpPerMeter);
    patch.mrpPerMeter = String(resolution.rrpPerMeter);
  }

  if (IS_DEV_UI_ENABLED) {
    console.log("FABRIC_CATALOGUE_RRP_RESOLVE_TRACE", {
      rowId: row.id || row.rowId || row.sourceRowId || "",
      brandId: brand.id,
      brandName: brand.name,
      fabricText,
      parsed,
      catalogueId: resolution.catalogue?.id || "",
      ruleId: resolution.rule?.id || "",
      ruleType: resolution.rule?.ruleType || "",
      rrpPerMeter: resolution.rrpPerMeter,
      rrpSource: resolution.rrpSource,
    });
  }

  return patch;
};

const handleCurtainRowBrandChange = async (rowId, row = {}, brandId = "") => {
  const brand = getFabricBrandById(brandId);
  const fabricText = getEditableFabricValue(row);
  const brandPatch = {
    brandId: brand?.id || "",
    brandName: brand?.name || "",
    fabricBrandId: brand?.id || "",
    fabricBrandName: brand?.name || "",
    brandSource: brand?.id ? "manual" : "none",
  };
  patchCurtainRowFields(rowId, brandPatch);
  syncFabricSummaryBrandFromRows(rowId, brandPatch);

  if (!brand?.id) return;
  const library = await loadFabricCatalogueLibrary(brand.id);
  const nextRow = {
    ...row,
    brandId: brand.id,
    brandName: brand.name,
    fabricBrandId: brand.id,
    fabricBrandName: brand.name,
    brandSource: "manual",
  };
  if (fabricText) {
    const resolution = resolveFabricCatalogueRrp({
      brand,
      library,
      fabricText,
      existingMrp: row.mrp,
    });
    patchCurtainRowFields(rowId, {
      ...buildFabricCatalogueResolutionPatch(nextRow, {
        brandId: brand.id,
        fabricText,
        includeResolvedMrp: true,
      }),
      brandSource: "manual",
      ...(resolution.matched && resolution.rrpPerMeter != null
        ? {
            mrp: String(resolution.rrpPerMeter),
            mrpPerMeter: String(resolution.rrpPerMeter),
          }
        : {}),
    });
  }
};

const handleCurtainFabricInputChange = async (rowId, row = {}, value = "") => {
  const brandId = row.brandId || row.fabricBrandId || "";
  const brand = getFabricBrandById(brandId);
  const patch = {
    fabric: value,
    fabricName: value,
    fabricCode: value,
    fabricCodeRaw: value,
  };

  if (brand?.id) {
    Object.assign(
      patch,
      buildFabricCatalogueResolutionPatch(row, {
        brandId: brand.id,
        fabricText: value,
        includeResolvedMrp: true,
      })
    );
  }

  patchCurtainRowFields(rowId, patch);

  if (
    brand?.id &&
    !fabricCatalogueLoadedBrandKeysRef.current.has(`${workspaceUid}:${brand.id}`)
  ) {
    await loadFabricCatalogueLibrary(brand.id);
    const refreshedPatch = buildFabricCatalogueResolutionPatch(row, {
      brandId: brand.id,
      fabricText: value,
      includeResolvedMrp: true,
    });
    patchCurtainRowFields(rowId, refreshedPatch);
  }
};

const handleCurtainFabricSrNumberChange = async (rowId, row = {}, value = "") => {
  const brandId = row.brandId || row.fabricBrandId || "";
  const brand = getFabricBrandById(brandId);
  const library = brand?.id
    ? getFabricCatalogueLibraryForBrand(brand.id)
    : getEmptyFabricCatalogueLibrary({});
  const currentFabricText = sanitizeEditableText(
    row.catalogueName || row.fabricCodeRaw || getEditableFabricValue(row)
  );
  const parsedCurrent = parseFabricCatalogueInput(
    currentFabricText,
    library.catalogues || []
  );
  const catalogueName = sanitizeEditableText(
    row.catalogueName || parsedCurrent.catalogueName || currentFabricText
  );
  const srNumber = normalizeSrNumber(value);
  const fabricText = buildFabricCatalogueDisplayCode({
    catalogueName,
    srNumber,
    fallback: currentFabricText,
  });
  const basePatch = {
    fabric: fabricText,
    fabricName: fabricText,
    fabricCode: fabricText,
    fabricCodeRaw: fabricText,
    catalogueName,
    catalogueKey: row.catalogueKey || normalizeCatalogueKey(catalogueName),
    srNumber,
    srLabel: srNumber ? `SR-${srNumber}` : "",
    ...(srNumber
      ? {}
      : {
          srPriceRuleId: "",
          srPriceRuleType: "none",
          srPriceRuleLabel: "",
          rrpSource: row.rrpSource === "manual_override" ? "manual_override" : "unmatched",
        }),
  };

  if (brand?.id && catalogueName) {
    const nextRow = {
      ...row,
      ...basePatch,
      brandId: brand.id,
      brandName: brand.name,
      fabricBrandId: brand.id,
      fabricBrandName: brand.name,
    };
    const resolvedPatch = buildFabricCatalogueResolutionPatch(nextRow, {
      brandId: brand.id,
      fabricText,
      includeResolvedMrp: true,
    });
    const patch = {
      ...basePatch,
      brandId: brand.id,
      brandName: brand.name,
      fabricBrandId: brand.id,
      fabricBrandName: brand.name,
      ...resolvedPatch,
    };
    patchCurtainRowFields(rowId, patch);
    syncFabricSummaryBrandFromRows(rowId, patch);

    if (
      !fabricCatalogueLoadedBrandKeysRef.current.has(`${workspaceUid}:${brand.id}`)
    ) {
      await loadFabricCatalogueLibrary(brand.id);
      const refreshedPatch = buildFabricCatalogueResolutionPatch(nextRow, {
        brandId: brand.id,
        fabricText,
        includeResolvedMrp: true,
      });
      patchCurtainRowFields(rowId, {
        ...basePatch,
        brandId: brand.id,
        brandName: brand.name,
        fabricBrandId: brand.id,
        fabricBrandName: brand.name,
        ...refreshedPatch,
      });
    }
    return;
  }

  patchCurtainRowFields(rowId, basePatch);
};

const createFabricCatalogueFromQuoteRow = async (
  rowId,
  row = {},
  { forceCreateExactRule = false } = {}
) => {
  const brand = getFabricBrandById(row.brandId || row.fabricBrandId || "");
  const fabricText = sanitizeEditableText(
    row.fabricCodeRaw || getEditableFabricValue(row)
  );
  const rrpPerMeter = Number(row.mrp || row.mrpPerMeter || 0);
  if (!workspaceUid || !brand?.id || !fabricText || !(rrpPerMeter > 0)) {
    return null;
  }

  const library = await loadFabricCatalogueLibrary(brand.id);
  const parsed = parseFabricCatalogueInput(fabricText, library.catalogues || []);
  if (!parsed.catalogueName || !parsed.catalogueKey) return null;

  let catalogue = (library.catalogues || []).find(
    (entry) => entry.catalogueKey === parsed.catalogueKey
  );
  let createdCatalogue = false;

  if (!catalogue) {
    const catalogueRef = doc(
      db,
      "users",
      workspaceUid,
      "fabricBrands",
      brand.id,
      "catalogues",
      parsed.catalogueKey
    );
    await setDoc(
      catalogueRef,
      {
        id: parsed.catalogueKey,
        brandId: brand.id,
        brandName: brand.name,
        catalogueName: parsed.catalogueName,
        catalogueKey: parsed.catalogueKey,
        defaultRrpPerMeter: parsed.srNumber ? null : rrpPerMeter,
        currency: "INR",
        active: true,
        archived: false,
        notes: "",
        createdAt: serverTimestamp(),
        createdBy: user?.uid || "",
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || "",
      },
      { merge: true }
    );
    createdCatalogue = true;
    catalogue = normalizeFabricCatalogue({
      id: parsed.catalogueKey,
      brandId: brand.id,
      brandName: brand.name,
      catalogueName: parsed.catalogueName,
      catalogueKey: parsed.catalogueKey,
      defaultRrpPerMeter: parsed.srNumber ? null : rrpPerMeter,
    });
  }

  const currentResolution = resolveFabricCatalogueRrp({
    brand,
    library,
    fabricText,
    existingMrp: row.mrp,
  });

  if (
    parsed.srNumber &&
    (forceCreateExactRule || !currentResolution.rule)
  ) {
    const exactDraft = {
      ruleType: "single",
      srNumber: parsed.srNumber,
      rrpPerMeter,
      notes: "",
    };
    const ruleId = buildSrPriceRuleDocId(exactDraft);
    const rulePayload = buildSrPriceRulePayload({
      draft: exactDraft,
      brand,
      catalogue,
    });
    await setDoc(
      doc(
        db,
        "users",
        workspaceUid,
        "fabricBrands",
        brand.id,
        "catalogues",
        catalogue.id,
        "srPriceRules",
        ruleId
      ),
      {
        id: ruleId,
        ...rulePayload,
        createdAt: serverTimestamp(),
        createdBy: user?.uid || "",
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || "",
      },
      { merge: true }
    );
    await loadFabricCatalogueLibrary(brand.id, { force: true });
    patchCurtainRowFields(rowId, {
      brandId: brand.id,
      brandName: brand.name,
      fabricBrandId: brand.id,
      fabricBrandName: brand.name,
      catalogueId: catalogue.id,
      catalogueName: catalogue.catalogueName,
      catalogueKey: catalogue.catalogueKey,
      srNumber: parsed.srNumber,
      srLabel: parsed.srLabel,
      srPriceRuleId: ruleId,
      srPriceRuleType: "single",
      rrpPerMeter,
      rrpSource: createdCatalogue ? "new_catalogue" : "new_sr_price_rule",
    });
    notify(
      createdCatalogue
        ? `${parsed.catalogueName} ${parsed.srLabel} added under ${brand.name} with RRP ${currencySymbol(currency)}${rrpPerMeter}/m.`
        : `${parsed.srLabel} added under ${catalogue.catalogueName} with RRP ${currencySymbol(currency)}${rrpPerMeter}/m.`,
      "success"
    );
    return { catalogue, ruleId, createdCatalogue };
  }

  if (createdCatalogue) {
    await loadFabricCatalogueLibrary(brand.id, { force: true });
    patchCurtainRowFields(rowId, {
      brandId: brand.id,
      brandName: brand.name,
      fabricBrandId: brand.id,
      fabricBrandName: brand.name,
      catalogueId: catalogue.id,
      catalogueName: catalogue.catalogueName,
      catalogueKey: catalogue.catalogueKey,
      rrpPerMeter,
      rrpSource: "new_catalogue",
    });
    notify(
      `${parsed.catalogueName} added under ${brand.name} with default RRP ${currencySymbol(currency)}${rrpPerMeter}/m.`,
      "success"
    );
    return { catalogue, createdCatalogue };
  }

  return null;
};

const updateFabricSrPriceRuleRrp = async ({
  rowId,
  row = {},
  newRrp,
  rule = null,
  source = "sr_price_rule_updated",
} = {}) => {
  const brand = getFabricBrandById(row.brandId || row.fabricBrandId || "");
  if (!workspaceUid || !brand?.id || !row.catalogueId || !rule?.id) return;
  await updateDoc(
    doc(
      db,
      "users",
      workspaceUid,
      "fabricBrands",
      brand.id,
      "catalogues",
      row.catalogueId,
      "srPriceRules",
      rule.id
    ),
    {
      rrpPerMeter: Number(newRrp),
      updatedAt: serverTimestamp(),
      updatedBy: user?.uid || "",
    }
  );
  await loadFabricCatalogueLibrary(brand.id, { force: true });
  patchCurtainRowFields(rowId, {
    mrp: String(newRrp),
    mrpPerMeter: String(newRrp),
    rrpPerMeter: Number(newRrp),
    rrpSource: source,
  });
  notify("Saved RRP updated for future quotations.", "success");
};

const handleCurtainFabricInputBlur = async (rowId, row = {}) => {
  try {
    await createFabricCatalogueFromQuoteRow(rowId, row);
  } catch (error) {
    console.error("FABRIC_CATALOGUE_ROW_SYNC_FAILED", {
      code: error?.code,
      message: error?.message,
      workspaceUid,
      rowId,
    });
  }
};

const syncFabricCatalogueEntriesForQuoteRows = async (sourceRows = []) => {
  if (!workspaceUid || !Array.isArray(sourceRows) || !sourceRows.length) return;
  const syncedKeys = new Set();

  for (const [index, row] of sourceRows.entries()) {
    const brandId = row?.brandId || row?.fabricBrandId || "";
    const fabricText = sanitizeEditableText(
      row?.fabricCodeRaw || getEditableFabricValue(row)
    );
    const rrpPerMeter = Number(row?.mrp || row?.mrpPerMeter || 0);
    if (!brandId || !fabricText || !(rrpPerMeter > 0)) continue;

    const cachedLibrary = getFabricCatalogueLibraryForBrand(brandId);
    const parsed = parseFabricCatalogueInput(
      fabricText,
      cachedLibrary.catalogues || []
    );
    if (!parsed.catalogueKey) continue;

    const syncKey = [
      brandId,
      parsed.catalogueKey,
      parsed.srNumber || "default",
    ].join(":");
    if (syncedKeys.has(syncKey)) continue;
    syncedKeys.add(syncKey);

    try {
      await createFabricCatalogueFromQuoteRow(
        getWorkshopRowStableId(row) || index,
        row
      );
    } catch (error) {
      console.error("FABRIC_CATALOGUE_SAVE_SYNC_FAILED", {
        code: error?.code,
        message: error?.message,
        workspaceUid,
        brandId,
        fabricText,
      });
    }
  }
};

const getCurtainRowFabricCatalogueSavePatch = (row = {}) => {
  const brandId = row.brandId || row.fabricBrandId || "";
  if (!brandId) return {};
  return buildFabricCatalogueResolutionPatch(row, {
    brandId,
    fabricText: row.fabricCodeRaw || getEditableFabricValue(row),
    includeResolvedMrp: false,
  });
};

const handleCurtainRowMrpBlur = async (rowId, row = {}) => {
  const newRrp = Number(row.mrp || row.mrpPerMeter || 0);
  const savedRrp = Number(row.rrpPerMeter || 0);
  if (!(newRrp > 0)) return;
  if (!row.brandId && !row.fabricBrandId) return;

  if (!savedRrp || Math.abs(newRrp - savedRrp) < 0.01) {
    await handleCurtainFabricInputBlur(rowId, row);
    return;
  }

  const brand = getFabricBrandById(row.brandId || row.fabricBrandId || "");
  if (!brand?.id || !row.catalogueId) return;
  const library = await loadFabricCatalogueLibrary(brand.id);
  const rule = (library.rulesByCatalogueId?.[row.catalogueId] || []).find(
    (entry) => entry.id === row.srPriceRuleId
  );
  const catalogue = (library.catalogues || []).find(
    (entry) => entry.id === row.catalogueId
  );
  const ruleLabel = rule ? getSrPriceRuleLabel(rule) : row.srLabel || "this SR";
  const displayName = [catalogue?.catalogueName || row.catalogueName, row.srLabel]
    .filter(Boolean)
    .join(" ");
  const money = (value) => `${currencySymbol(currency)}${Number(value || 0).toFixed(2)}`;

  const revertRow = () =>
    patchCurtainRowFields(rowId, {
      mrp: String(savedRrp),
      mrpPerMeter: String(savedRrp),
      rrpPerMeter: savedRrp,
      rrpSource:
        row.srPriceRuleId && row.srPriceRuleType !== "default"
          ? "sr_price_rule"
          : "catalogue_default",
    });
  const useOnlyForQuote = () =>
    patchCurtainRowFields(rowId, {
      mrp: String(newRrp),
      mrpPerMeter: String(newRrp),
      rrpPerMeter: newRrp,
      rrpSource: "manual_override",
    });

  if (rule?.ruleType === "single") {
    openConfirmDialog({
      title: "Update saved RRP?",
      description: `${displayName} already exists under ${brand.name} with RRP ${money(savedRrp)}/m. Do you want to update the saved RRP to ${money(newRrp)}/m for future quotations?`,
      confirmLabel: "Update Saved RRP",
      secondaryLabel: `Use ${money(newRrp)} only for this quote`,
      cancelLabel: "Cancel",
      tone: "warning",
      onConfirm: () =>
        updateFabricSrPriceRuleRrp({
          rowId,
          row,
          rule,
          newRrp,
          source: "sr_price_rule_updated",
        }),
      onSecondary: useOnlyForQuote,
      onCancel: revertRow,
    });
    return;
  }

  if (rule?.ruleType === "range" || rule?.ruleType === "list") {
    openConfirmDialog({
      title: "How should this SR price change be saved?",
      description: `${row.srLabel || "This SR"} is currently priced from the ${catalogue?.catalogueName || row.catalogueName} ${ruleLabel} rule at ${money(savedRrp)}/m.`,
      confirmLabel: `Update entire ${ruleLabel} rule`,
      secondaryLabel: `Use ${money(newRrp)} only for this quote`,
      cancelLabel: "Cancel",
      tone: "warning",
      onConfirm: () =>
        updateFabricSrPriceRuleRrp({
          rowId,
          row,
          rule,
          newRrp,
          source: "sr_price_rule_updated",
        }),
      onSecondary: useOnlyForQuote,
      onCancel: revertRow,
      extraActions: [
        {
          label: `Create separate ${row.srLabel || "SR"} price`,
          tone: "primary",
          onClick: async () => {
            await createFabricCatalogueFromQuoteRow(rowId, {
              ...row,
              mrp: String(newRrp),
              mrpPerMeter: String(newRrp),
            }, { forceCreateExactRule: true });
          },
        },
      ],
    });
    return;
  }

  if (row.rrpSource === "catalogue_default" && row.srNumber) {
    openConfirmDialog({
      title: "Create SR-specific RRP?",
      description: `${displayName} is currently using the catalogue default RRP ${money(savedRrp)}/m. Create a separate ${row.srLabel} price at ${money(newRrp)}/m for future quotations?`,
      confirmLabel: `Create ${row.srLabel} price`,
      secondaryLabel: `Use ${money(newRrp)} only for this quote`,
      cancelLabel: "Cancel",
      tone: "warning",
      onConfirm: () =>
        createFabricCatalogueFromQuoteRow(rowId, {
          ...row,
          mrp: String(newRrp),
          mrpPerMeter: String(newRrp),
        }, { forceCreateExactRule: true }),
      onSecondary: useOnlyForQuote,
      onCancel: revertRow,
    });
  }
};

const normalizeFabricSuggestionText = (value = "") =>
  normalizeCatalogueKey(value).replace(/-/g, " ");

const getFabricCatalogueLibrariesForSuggestions = (preferredBrandId = "") => {
  const preferredId = sanitizeEditableText(preferredBrandId);
  const brands = (fabricBrandsRef.current.length ? fabricBrandsRef.current : fabricBrands)
    .filter((brand) => brand?.id);
  const orderedBrands = [
    ...brands.filter((brand) => brand.id === preferredId),
    ...brands.filter((brand) => brand.id !== preferredId),
  ];

  return orderedBrands
    .map((brand) => ({
      brand,
      library: getFabricCatalogueLibraryForBrand(brand.id),
      preferred: preferredId && brand.id === preferredId,
    }))
    .filter(({ library }) => Array.isArray(library.catalogues) && library.catalogues.length);
};

const getFabricSuggestionRuleLabel = (resolution = {}, fallback = "Catalogue") => {
  const ruleType = resolution.rule?.ruleType || resolution.srPriceRuleType || "";
  if (ruleType === "single") return "Exact SR";
  if (ruleType === "list") return "SR group";
  if (ruleType === "range") return "Range rule";
  if (resolution.rrpSource === "catalogue_default") return "Default RRP";
  if (resolution.rrpSource === "manual_override") return "Quote-only RRP";
  return fallback;
};

const getFabricCatalogueSuggestionOptionsForRow = (row = {}) => {
  const preferredBrandId = row.brandId || row.fabricBrandId || "";
  const libraries = getFabricCatalogueLibrariesForSuggestions(preferredBrandId);
  const rawQuery = sanitizeEditableText(row.fabricCodeRaw || getEditableFabricValue(row));
  const currentQuery = normalizeFabricSuggestionText(rawQuery);
  const suggestions = [];
  const seen = new Set();
  const addSuggestion = (option = {}) => {
    const value = sanitizeEditableText(option.value || option.commitValue || "");
    if (!value) return;
    const searchKey = normalizeFabricSuggestionText(
      [
        value,
        option.commitValue,
        option.title,
        option.subtitle,
        option.brandName,
        option.catalogueName,
        option.srLabel,
        option.previewRuleLabel,
      ]
        .filter(Boolean)
        .join(" ")
    );
    const dedupeKey = [
      option.brandId || "",
      option.catalogueId || option.catalogueKey || "",
      option.ruleId || option.previewRuleId || option.srLabel || "catalogue",
      normalizeCatalogueKey(option.commitValue || value),
    ].join(":");
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    suggestions.push({
      ...option,
      id: option.id || dedupeKey,
      value,
      commitValue: option.commitValue || value,
      searchKey,
    });
  };

  libraries.forEach(({ brand, library, preferred }, brandIndex) => {
    const parsed = parseFabricCatalogueInput(rawQuery, library.catalogues || []);
    (library.catalogues || []).forEach((catalogue) => {
      if (!catalogue.active) return;
      const catalogueNameSearch = normalizeFabricSuggestionText(catalogue.catalogueName);
      const baseSearchKey = normalizeFabricSuggestionText(
        [
          catalogue.catalogueName,
          catalogue.catalogueKey,
          brand.name,
          library.brandName,
        ]
          .filter(Boolean)
          .join(" ")
      );
      const parsedMatchesCatalogue =
        parsed.catalogueKey && parsed.catalogueKey === catalogue.catalogueKey;
      const queryMatchesCatalogue =
        !currentQuery ||
        parsedMatchesCatalogue ||
        baseSearchKey.includes(currentQuery) ||
        (catalogueNameSearch && currentQuery.includes(catalogueNameSearch));
      if (!queryMatchesCatalogue) return;

      const commitValue =
        parsedMatchesCatalogue && parsed.srLabel
          ? buildFabricCatalogueDisplayCode({
              catalogueName: catalogue.catalogueName,
              srNumber: parsed.srNumber,
            })
          : catalogue.catalogueName;
      const resolution = resolveFabricCatalogueRrp({
        brand,
        library,
        fabricText: commitValue,
        existingMrp: row.mrp,
      });
      const ruleLabel = resolution.rule
        ? getSrPriceRuleLabel(resolution.rule)
        : parsedMatchesCatalogue
        ? parsed.srLabel
        : "";
      addSuggestion({
        id: `catalogue-${brand.id}-${catalogue.id}-${parsed.srNumber || "default"}`,
        value: catalogue.catalogueName,
        commitValue,
        title: commitValue,
        subtitle: ruleLabel
          ? `${brand.name} - pricing: ${ruleLabel} - ${getFabricSuggestionRuleLabel(resolution, "Catalogue")}`
          : `${brand.name} - ${getFabricSuggestionRuleLabel(resolution, "Catalogue")}`,
        brandId: brand.id,
        brandName: brand.name,
        catalogueId: catalogue.id,
        catalogueName: catalogue.catalogueName,
        catalogueKey: catalogue.catalogueKey,
        srNumber: parsedMatchesCatalogue ? parsed.srNumber : "",
        srLabel: parsedMatchesCatalogue ? parsed.srLabel : "",
        ruleId: resolution.rule?.id || "",
        ruleType:
          resolution.rule?.ruleType ||
          (resolution.rrpSource === "catalogue_default" ? "default" : "none"),
        price: resolution.matched ? resolution.rrpPerMeter : catalogue.defaultRrpPerMeter,
        source: resolution.rrpSource || "catalogue",
        priority: (preferred ? 0 : 10) + (parsedMatchesCatalogue ? 0 : 2) + brandIndex,
      });

      const rules = library.rulesByCatalogueId?.[catalogue.id] || [];
      rules.slice(0, 40).forEach((rule) => {
        if (!rule.active || rule.ruleType === "default") return;
        const priceRuleLabel = getSrPriceRuleLabel(rule);
        const isExactSrRule = rule.ruleType === "single";
        const ruleSrNumber = isExactSrRule ? normalizeSrNumber(rule.srNumber) : "";
        const commitValue = isExactSrRule
          ? buildFabricCatalogueDisplayCode({
              catalogueName: catalogue.catalogueName,
              srNumber: ruleSrNumber,
            })
          : catalogue.catalogueName;
        const value = isExactSrRule
          ? commitValue
          : `${catalogue.catalogueName} pricing ${priceRuleLabel}`;
        const ruleSearchKey = normalizeFabricSuggestionText(
          [
            value,
            catalogue.catalogueName,
            catalogue.catalogueKey,
            priceRuleLabel,
            brand.name,
          ]
            .filter(Boolean)
            .join(" ")
        );
        if (
          currentQuery &&
          !parsedMatchesCatalogue &&
          !ruleSearchKey.includes(currentQuery) &&
          !(catalogueNameSearch && currentQuery.includes(catalogueNameSearch))
        ) {
          return;
        }
        const parsedRuleInput = parseFabricCatalogueInput(value, library.catalogues || []);
        const ruleResolution = resolveFabricCatalogueRrp({
          brand,
          library,
          fabricText: commitValue,
          existingMrp: row.mrp,
        });
        addSuggestion({
          id: `rule-${brand.id}-${catalogue.id}-${rule.id}`,
          value,
          commitValue,
          title: isExactSrRule ? commitValue : catalogue.catalogueName,
          subtitle: `${brand.name} - pricing: ${priceRuleLabel} - ${getFabricSuggestionRuleLabel(
            { rule, rrpSource: "sr_price_rule" },
            "SR price"
          )}`,
          brandId: brand.id,
          brandName: brand.name,
          catalogueId: catalogue.id,
          catalogueName: catalogue.catalogueName,
          catalogueKey: catalogue.catalogueKey,
          srNumber: isExactSrRule ? ruleSrNumber || parsedRuleInput.srNumber || "" : "",
          srLabel: isExactSrRule
            ? parsedRuleInput.srLabel || (ruleSrNumber ? `SR-${ruleSrNumber}` : "")
            : "",
          ruleId: isExactSrRule ? rule.id : "",
          ruleType: isExactSrRule ? rule.ruleType : "none",
          previewRuleId: rule.id,
          previewRuleType: rule.ruleType,
          previewRuleLabel: priceRuleLabel,
          requiresSrNumber: !isExactSrRule,
          price: rule.rrpPerMeter,
          source: ruleResolution.rrpSource || "sr_price_rule",
          priority: (preferred ? 1 : 12) + brandIndex,
        });
      });
    });
  });

  return suggestions
    .sort((left, right) => {
      const priorityDiff = Number(left.priority || 0) - Number(right.priority || 0);
      if (priorityDiff !== 0) return priorityDiff;
      return String(left.title || left.value).localeCompare(String(right.title || right.value));
    })
    .slice(0, 12);
};

const updateFabricSuggestionPosition = (element) => {
  if (!element || typeof element.getBoundingClientRect !== "function") return;
  const rect = element.getBoundingClientRect();
  const viewportHeight =
    typeof window !== "undefined" ? window.innerHeight || 720 : 720;
  setFabricSuggestionPosition({
    left: Math.max(12, rect.left),
    top: rect.bottom + 6,
    width: Math.max(250, rect.width),
    maxHeight: Math.max(150, Math.min(280, viewportHeight - rect.bottom - 18)),
  });
};

const openFabricCatalogueSuggestions = (cellKey, brandId = "", element = null) => {
  setActiveFabricSuggestionCell(cellKey);
  setFabricSuggestionKeyboardIndex(0);
  updateFabricSuggestionPosition(element);
  if (brandId) loadFabricCatalogueLibrary(brandId);
  loadAllFabricCatalogueLibraries();
};

const closeFabricCatalogueSuggestions = () => {
  setActiveFabricSuggestionCell("");
  setFabricSuggestionKeyboardIndex(0);
  setFabricSuggestionPosition(null);
};

const renderFabricCatalogueCombobox = ({
  row = {},
  rowId = "",
  rowIndex = 0,
  label = "Fabric / Code",
  disabled = false,
  compact = false,
} = {}) => {
  const brandId = row.brandId || row.fabricBrandId || "";
  const cellKey = `${compact ? "compact" : "table"}:${rowId}`;
  const isOpen = activeFabricSuggestionCell === cellKey && !disabled;
  const suggestions = getFabricCatalogueSuggestionOptionsForRow(row);
  const highlightedIndex = Math.min(
    Math.max(fabricSuggestionKeyboardIndex, 0),
    Math.max(suggestions.length - 1, 0)
  );
  const listboxId = `fabric-catalogue-combobox-${cellKey.replace(
    /[^a-zA-Z0-9_-]/g,
    "-"
  )}`;
  const fieldHeight = compact ? 34 : 36;
  const compactFieldStyle = {
    ...input,
    minHeight: fieldHeight,
    height: fieldHeight,
    padding: "6px 8px",
    fontSize: 13,
    borderRadius: 9,
  };
  const brandSelectStyle = {
    ...selectInput,
    minHeight: 30,
    height: 30,
    padding: "5px 8px",
    fontSize: 12,
    borderRadius: 9,
  };
  const listPosition = fabricSuggestionPosition || {};
  const specificSrNumber = getSpecificRowSrNumber(row);
  const catalogueInputValue =
    sanitizeEditableText(row.catalogueName || "") &&
    (row.catalogueId || row.catalogueKey || specificSrNumber)
      ? sanitizeEditableText(row.catalogueName || "")
      : getEditableFabricValue(row);
  const getCatalogueInputCommitValue = (value = "") => {
    const catalogueName = sanitizeEditableText(value);
    if (!catalogueName) return "";
    if (/\bS\.?\s*R\.?\b/i.test(catalogueName)) return catalogueName;
    return specificSrNumber
      ? buildFabricCatalogueDisplayCode({
          catalogueName,
          srNumber: specificSrNumber,
        })
      : catalogueName;
  };

  const applySuggestion = (suggestion) => {
    const selectedBrand = getFabricBrandById(suggestion?.brandId || "");
    const commitValue = sanitizeEditableText(
      suggestion?.commitValue || suggestion?.value || ""
    );
    if (!commitValue) return;

    if (selectedBrand?.id) {
      const nextRow = {
        ...row,
        brandId: selectedBrand.id,
        brandName: selectedBrand.name,
        fabricBrandId: selectedBrand.id,
        fabricBrandName: selectedBrand.name,
      };
      const resolutionPatch = suggestion?.requiresSrNumber
        ? {
            fabric: commitValue,
            fabricName: commitValue,
            fabricCode: commitValue,
            fabricCodeRaw: commitValue,
            catalogueId: suggestion.catalogueId || "",
            catalogueName: suggestion.catalogueName || commitValue,
            catalogueKey:
              suggestion.catalogueKey ||
              normalizeCatalogueKey(suggestion.catalogueName || commitValue),
            srNumber: "",
            srLabel: "",
            srPriceRuleId: "",
            srPriceRuleType: "none",
            srPriceRuleLabel: "",
            rrpSource: "needs_sr_number",
          }
        : buildFabricCatalogueResolutionPatch(nextRow, {
            brandId: selectedBrand.id,
            fabricText: commitValue,
            includeResolvedMrp: true,
          });
      const patch = {
        fabric: commitValue,
        fabricName: commitValue,
        fabricCode: commitValue,
        fabricCodeRaw: commitValue,
        brandId: selectedBrand.id,
        brandName: selectedBrand.name,
        fabricBrandId: selectedBrand.id,
        fabricBrandName: selectedBrand.name,
        brandSource: "catalogue_selection",
        ...resolutionPatch,
      };
      patchCurtainRowFields(rowId, patch);
      syncFabricSummaryBrandFromRows(rowId, patch);
      if (suggestion?.requiresSrNumber && typeof window !== "undefined") {
        window.setTimeout(() => {
          fabricSrInputRefs.current[rowId]?.focus?.();
        }, 0);
      }
    } else {
      handleCurtainFabricInputChange(rowId, row, commitValue);
    }

    closeFabricCatalogueSuggestions();
  };

  const handleFabricKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!isOpen) {
        openFabricCatalogueSuggestions(cellKey, brandId, event.currentTarget);
        return;
      }
      setFabricSuggestionKeyboardIndex((current) =>
        suggestions.length ? Math.min(current + 1, suggestions.length - 1) : 0
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setFabricSuggestionKeyboardIndex((current) => Math.max(current - 1, 0));
      return;
    }

    if (event.key === "Enter" && isOpen && suggestions[highlightedIndex]) {
      event.preventDefault();
      applySuggestion(suggestions[highlightedIndex]);
    }
  };

  return (
    <div
      data-fabric-combobox-root="true"
      style={{ display: "grid", gap: compact ? 5 : 6, minWidth: 0 }}
    >
      <select
        style={brandSelectStyle}
        value={brandId}
        onChange={(event) => {
          closeFabricCatalogueSuggestions();
          handleCurtainRowBrandChange(rowId, row, event.target.value);
        }}
        disabled={disabled}
        aria-label={`Select fabric brand for ${row.item || `row ${rowIndex + 1}`}`}
        title="Fabric brand"
      >
        <option value="">No brand</option>
        {fabricBrands.map((brand) => (
          <option key={brand.id} value={brand.id}>
            {brand.name}
          </option>
        ))}
      </select>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: compact ? "minmax(0, 1fr) 64px" : "minmax(0, 1fr) 72px",
          gap: 6,
          alignItems: "start",
          minWidth: 0,
        }}
      >
      <div style={{ position: "relative", minWidth: 0 }}>
        <input
          style={compactFieldStyle}
          title={label}
          placeholder={row.catalogueName ? "Catalogue / Fabric" : label || "Fabric / Code"}
          value={catalogueInputValue}
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          onFocus={(event) =>
            openFabricCatalogueSuggestions(cellKey, brandId, event.currentTarget)
          }
          onChange={(event) => {
            openFabricCatalogueSuggestions(cellKey, brandId, event.currentTarget);
            handleCurtainFabricInputChange(
              rowId,
              row,
              getCatalogueInputCommitValue(event.target.value)
            );
          }}
          onKeyDown={handleFabricKeyDown}
          onBlur={(event) => {
            const blurValue = getCatalogueInputCommitValue(event.target.value);
            const blurRow = {
              ...row,
              fabric: blurValue,
              fabricName: blurValue,
              fabricCode: blurValue,
              fabricCodeRaw: blurValue,
            };
            const blurSuggestions = getFabricCatalogueSuggestionOptionsForRow(blurRow);
            const onlySuggestion = blurSuggestions.length === 1 ? blurSuggestions[0] : null;
            const normalizedBlurValue = normalizeCatalogueKey(blurValue);
            const normalizedSuggestionValue = normalizeCatalogueKey(
              onlySuggestion?.commitValue || onlySuggestion?.value || ""
            );
            const normalizedSuggestionWithoutSrPrefix = normalizeCatalogueKey(
              [
                onlySuggestion?.catalogueName,
                onlySuggestion?.srNumber,
              ]
                .filter(Boolean)
                .join(" ")
            );
            const isHighConfidenceSingleMatch =
              onlySuggestion &&
              (normalizedBlurValue === normalizedSuggestionValue ||
                (normalizedSuggestionWithoutSrPrefix &&
                  normalizedBlurValue === normalizedSuggestionWithoutSrPrefix));

            if (!brandId && isHighConfidenceSingleMatch) {
              applySuggestion(onlySuggestion);
              return;
            }

            handleCurtainFabricInputBlur(rowId, blurRow);
          }}
          disabled={disabled}
        />

        {isOpen ? (
          <div
            id={listboxId}
            role="listbox"
            style={{
              position: "fixed",
              left: listPosition.left || 0,
              top: listPosition.top || 0,
              width: listPosition.width || 260,
              maxHeight: listPosition.maxHeight || 260,
              overflowY: "auto",
              zIndex: 9000,
              padding: 6,
              borderRadius: 14,
              border: isDark
                ? "1px solid rgba(148,163,184,0.24)"
                : "1px solid rgba(203,213,225,0.95)",
              background: isDark ? "#0f172a" : "#ffffff",
              boxShadow: isDark
                ? "0 22px 50px rgba(2,6,23,0.45)"
                : "0 22px 45px rgba(15,23,42,0.16)",
            }}
          >
            {suggestions.length ? (
              suggestions.map((suggestion, suggestionIndex) => {
                const isHighlighted = suggestionIndex === highlightedIndex;
                return (
                  <button
                    key={suggestion.id || suggestion.value}
                    type="button"
                    role="option"
                    aria-selected={isHighlighted}
                    onMouseEnter={() =>
                      setFabricSuggestionKeyboardIndex(suggestionIndex)
                    }
                    onMouseDown={(event) => {
                      event.preventDefault();
                      applySuggestion(suggestion);
                    }}
                    style={{
                      width: "100%",
                      border: "none",
                      borderRadius: 10,
                      padding: "8px 9px",
                      display: "grid",
                      gap: 4,
                      textAlign: "left",
                      cursor: "pointer",
                      background: isHighlighted
                        ? isDark
                          ? "rgba(37,99,235,0.24)"
                          : "#eff6ff"
                        : "transparent",
                      color: isDark ? "#f8fafc" : "#0f172a",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 850,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {suggestion.title || suggestion.value}
                    </span>
                    <span
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 8,
                        fontSize: 11,
                        color: isDark ? "#cbd5e1" : "#64748b",
                      }}
                    >
                      <span>{suggestion.subtitle || "Catalogue"}</span>
                      {Number(suggestion.price || 0) > 0 ? (
                        <span style={{ fontWeight: 850 }}>
                          {currencySymbol(currency)}
                          {Number(suggestion.price || 0).toFixed(2)}/m
                        </span>
                      ) : null}
                    </span>
                  </button>
                );
              })
            ) : (
              <div
                style={{
                  padding: "10px 9px",
                  fontSize: 12,
                  lineHeight: 1.45,
                  color: dashboardMuted,
                }}
              >
                {brandId
                  ? "No saved catalogue match. You can type freely."
                  : fabricCatalogueAllBrandsLoadingWorkspaceRef.current === workspaceUid
                  ? "Loading saved catalogues..."
                  : "No saved catalogue match. You can type freely."}
              </div>
            )}
          </div>
        ) : null}
      </div>

        <input
          ref={(node) => {
            if (node) {
              fabricSrInputRefs.current[rowId] = node;
            } else {
              delete fabricSrInputRefs.current[rowId];
            }
          }}
          type="text"
          style={{
            ...compactFieldStyle,
            padding: "6px 6px",
            textAlign: "center",
          }}
          title="Specific SR number"
          aria-label={`Specific SR number for ${row.item || `row ${rowIndex + 1}`}`}
          placeholder="SR No"
          value={specificSrNumber}
          autoComplete="off"
          onChange={(event) =>
            handleCurtainFabricSrNumberChange(rowId, row, event.target.value)
          }
          onBlur={() => {
            const latestRow =
              (rowsRef.current || []).find(
                (candidate) => getWorkshopRowStableId(candidate) === rowId
              ) || row;
            handleCurtainFabricInputBlur(rowId, latestRow);
          }}
          disabled={disabled}
        />
      </div>

      {row.rrpSource && row.rrpSource !== "unmatched" ? (
        <div style={{ fontSize: 10.5, color: dashboardMuted, lineHeight: 1.35 }}>
          {row.catalogueName || "Catalogue"}
          {row.srLabel ? ` / ${row.srLabel}` : ""} -{" "}
          {row.rrpSource === "needs_sr_number"
            ? "enter selected SR number"
            : row.rrpSource === "manual_override"
            ? "quote-only RRP"
            : "saved RRP"}
        </div>
      ) : null}
      {row.brandSource === "catalogue_selection" ? (
        <div style={{ fontSize: 10.5, color: dashboardMuted, lineHeight: 1.35 }}>
          Brand auto-selected from catalogue.
        </div>
      ) : null}
      {!brandId && getEditableFabricValue(row) && Number(row.mrp || row.mrpPerMeter || 0) > 0 ? (
        <div style={{ fontSize: 10.5, color: dashboardMuted, lineHeight: 1.35 }}>
          Select a brand to save this catalogue for future use.
        </div>
      ) : null}
    </div>
  );
};

const getCurtainRowLiningForUi = (row = {}, calc = {}) =>
  calculateCurtainRowLining(
    row,
    Number(
        calc.customerBillableFabricMeters ??
          calc.orderedFabricMeters ??
          calc.fabricMeters ??
          0
    ),
    curtainRowSupportsLining(row, calc),
    { sqft: calc.adjSqft ?? calc.billableSqft ?? 0 }
  );

const updateCurtainRowLining = (rowId, row = {}, calc = {}, patch = {}) => {
  if (!rowId) return;
  const currentLining = getCurtainRowLiningForUi(row, calc);
  const nextLining = normalizeCurtainRowLining(
    {
      ...currentLining,
      ...patch,
    },
    {
      calculatedMeters: Number(
        calc.customerBillableFabricMeters ??
          calc.orderedFabricMeters ??
          calc.fabricMeters ??
          0
      ),
      sqft: calc.adjSqft ?? calc.billableSqft ?? 0,
      applicable: curtainRowSupportsLining(row, calc),
    }
  );

  updateField(rowId, "lining", nextLining);
};

const getFabricSummaryMetaForKey = useCallback(
  (fabricKey = "") => {
    const normalizedKey = normalizeFabricKey(fabricKey);
    return normalizedKey ? fabricSummaryMeta?.[normalizedKey] || {} : {};
  },
  [fabricSummaryMeta]
);

const updateFabricRepeatSettings = useCallback((fabricKey = "", patch = {}) => {
  const normalizedKey = normalizeFabricKey(fabricKey);
  if (!normalizedKey) return;
  const normalizedRepeatSettings = normalizeCurtainRepeatSettingsMap(repeatSettings);
  const nextRepeat = normalizeCurtainRepeatEntry({
    ...(normalizedRepeatSettings[normalizedKey] || {}),
    ...patch,
  });
  const nextRepeatSettings = {
    ...normalizedRepeatSettings,
    [normalizedKey]: nextRepeat,
  };
  repeatSettingsRef.current = nextRepeatSettings;
  setRepeatSettings(nextRepeatSettings);
  setFabricSummaryMeta((prev) => {
    const normalized = normalizeFabricSummaryMeta(prev);
    const current = normalized[normalizedKey] || {};
    const nextMeta = normalizeFabricSummaryMeta({
      ...normalized,
      [normalizedKey]: {
        ...current,
        repeatSettings: nextRepeat,
      },
    });
    fabricSummaryMetaRef.current = nextMeta;
    return nextMeta;
  });
}, [repeatSettings]);

const getCurtainFabricDisplayWithBrand = useCallback(
  (row = {}, originalRow = {}) => {
    const fabricLabel = getCurtainRowFabricLabel(row, originalRow);
    const fabricKey = getCurtainRowFabricKey(row, originalRow);
    const summaryMeta = getFabricSummaryMetaForKey(fabricKey);
    const brandName =
      sanitizeEditableText(row.fabricBrandName || row.brandName || "") ||
      summaryMeta.brandName ||
      getFabricBrandNameById(summaryMeta.brandId);
    return [brandName, fabricLabel].filter(Boolean).join(" / ");
  },
  [getFabricBrandNameById, getFabricSummaryMetaForKey]
);

function getHalfPieceReusePiecePlan(row = {}, calc = {}, options = {}) {
    const activeQuoteFullnessOverrides = Object.prototype.hasOwnProperty.call(
      options || {},
      "quoteFullnessOverrides"
    )
      ? normalizeQuoteFullnessOverrides(options.quoteFullnessOverrides || {})
      : quoteFullnessOverrides;
    const productConfig =
      options.productConfig ||
      row?.productConfigSnapshot ||
      null;
    const productName = normalizeProductName(
      row?.productName || row?.productType || row?.product || productConfig?.name || ""
    );
    const isRomanRow =
      calc?.isRoman === true ||
      productName.startsWith("roman");
    const fabricRules =
      calc?.calculationBreakdown?.fabricRules ||
      resolveFabricRulesForRow(
        row,
        productConfig && !shouldUseLegacyProductCalculation(row, productConfig)
          ? productConfig
          : null,
        fabricCalcSettings
      );
    const fullnessResult = isRomanRow
      ? {
          fullness: 1,
          source: "roman_half_piece_rule",
          productFullness: 1,
          stitchingFullness: 0,
        }
      : calc?.calculationBreakdown?.fullness ||
        resolveCurtainFullness(row, productConfig, stitchingRates, {
            fabricCalcSettings,
            fabricRules,
            quoteFullnessOverrides: activeQuoteFullnessOverrides,
          });
    const pieceCalc =
      calc?.calculationBreakdown?.curtainPieceCalc ||
      calc?.curtainPieceCalc ||
      calculateCurtainPieces({
        widthIn: row?.width,
        fabricWidthIn: fabricRules.fabricWidthIn,
        usableWidthDeductionIn: fabricRules.usableWidthDeductionIn,
        fullness: fullnessResult.fullness,
      });
    const recomputedRawPieces = Number(pieceCalc.rawPieces || 0);
    const snapshotRawPieces = Number(
      calc?.calculationBreakdown?.curtainPieceCalc?.rawPieces ??
        calc?.curtainPieceCalc?.rawPieces ??
        calc?.rawPieces ??
        0
    );
    const billingPieces = Number(
      calc?.totalPieces ??
        calc?.pcsHalf ??
        calc?.pcsFull ??
        calc?.pcs ??
        pieceCalc.pieces ??
        0
    );
    const safeRawPieces =
      Number.isFinite(recomputedRawPieces) && recomputedRawPieces > 0
        ? recomputedRawPieces
        : Number.isFinite(snapshotRawPieces) && snapshotRawPieces > 0
        ? snapshotRawPieces
        : 0;
    const safeBillingPieces =
      Number.isFinite(billingPieces) && billingPieces > 0
        ? billingPieces
        : Number(pieceCalc.pieces || 0);
    const reuseTotalPieces = safeRawPieces
      ? Math.ceil(Math.max(0, safeRawPieces - HALF_PIECE_EPSILON) * 2) / 2
      : safeBillingPieces;
    const floorPieces = Math.floor(reuseTotalPieces + HALF_PIECE_EPSILON);
    const fractionalPieces = round2(Math.max(0, reuseTotalPieces - floorPieces));
    const hasHalfPiece = fractionalPieces >= 0.5 - HALF_PIECE_EPSILON;
    const wholePanels = safeRawPieces
      ? Math.ceil(Math.max(0, safeRawPieces - HALF_PIECE_EPSILON))
      : safeBillingPieces;

    return {
      width: Number(row?.width || 0),
      fullness: Number(fullnessResult.fullness || 0),
      fullnessSource: fullnessResult.source || "",
      fabricWidth: Number(pieceCalc.fabricWidthIn || fabricRules.fabricWidthIn || 0),
      fabricWidthIn: Number(pieceCalc.fabricWidthIn || fabricRules.fabricWidthIn || 0),
      usableWidthDeduction: Number(
        pieceCalc.usableWidthDeductionIn || fabricRules.usableWidthDeductionIn || 0
      ),
      usableWidthDeductionIn: Number(
        pieceCalc.usableWidthDeductionIn || fabricRules.usableWidthDeductionIn || 0
      ),
      usableFabricWidth: Number(
        pieceCalc.usableFabricWidthIn || fabricRules.usableFabricWidthIn || 0
      ),
      usableFabricWidthIn: Number(
        pieceCalc.usableFabricWidthIn || fabricRules.usableFabricWidthIn || 0
      ),
      rawPieces: Number(pieceCalc.rawPieces || safeRawPieces || 0),
      rawPanels: Number(pieceCalc.rawPieces || safeRawPieces || 0),
      snapshotRawPieces: Number.isFinite(snapshotRawPieces) ? snapshotRawPieces : 0,
      billingPieces: safeBillingPieces,
      wholePanels,
      halfStepPanels: round2(reuseTotalPieces),
      reuseTotalPieces: round2(reuseTotalPieces),
      floorPieces,
      fullCutsWithoutReuse: wholePanels,
      hasHalfPiece,
      hasHalfPanelRequirement: hasHalfPiece,
      fractionalPieces,
      source:
        Number.isFinite(recomputedRawPieces) && recomputedRawPieces > 0
          ? "recomputedRowInputsRoundedToHalf"
          : safeRawPieces
          ? "snapshotRawPiecesRoundedToHalf"
          : "billingPiecesFallback",
    };
}

const getCurtainCustomerBillingFabricTrace = useCallback(
  (row = {}, rowIndex = -1, providedCalc = null) => {
    const calc = providedCalc || getCurtainProductionAdjustedRowCalc(row, rowIndex);
    const productConfig = getRowProductConfig(row);
    const panelPlan = getHalfPieceReusePiecePlan(row, calc, {
      productConfig,
    });
    const cutHeightIn = Number(
      calc.cutCurtain ||
        calc.cutRoman ||
        calc.cutHeightIn ||
        row.calculationSnapshot?.cutHeightIn ||
        row.cutHeightIn ||
        row.height ||
        0
    );
    const cutHeightMeters = cutHeightIn > 0 ? cutHeightIn / 39.3701 : 0;
    const rawPanels = Number(panelPlan.rawPanels || panelPlan.rawPieces || 0);
    const floorPanels = Math.floor(Math.max(0, rawPanels));
    const halfStepProductionPanels = Number(
      panelPlan.halfStepPanels ?? calc.halfStepProductionPanels ?? calc.pcsHalf ?? 0
    );
    const wholeBillingPanels = Number(
      panelPlan.wholePanels ?? calc.wholeBillingPanels ?? calc.pcsFull ?? calc.totalPieces ?? 0
    );
    const computedFullOrderedFabricMeters = round2(wholeBillingPanels * cutHeightMeters);
    const storedFullOrderedFabricMeters = round2(
      Number(
        calc.fullOrderedFabricMeters ??
          calc.noReuseOrderedFabricMeters ??
          calc.orderedFabricMeters ??
          calc.fabricMeters ??
          0
      )
    );
    const fullOrderedFabricMeters =
      computedFullOrderedFabricMeters > 0
        ? computedFullOrderedFabricMeters
        : storedFullOrderedFabricMeters;
    const reducedCustomerMeters = round2(
      Number(
        calc.customerBillableFabricMeters ??
          calc.orderedFabricMeters ??
          calc.fabricMeters ??
          fullOrderedFabricMeters
      )
    );
    const customerBillableFabricMeters = passHalfPieceSavingsToCustomer
      ? reducedCustomerMeters
      : fullOrderedFabricMeters;
    const newFullCuts = Number(calc.newFullCuts ?? panelPlan.floorPieces ?? floorPanels ?? 0);
    const rowNewFabricMeters = round2(newFullCuts * cutHeightMeters);

    return {
      rowId: getWorkshopRowStableId(row) || `row-${rowIndex}`,
      itemName: row.item || row.name || row.room || `Row ${rowIndex + 1}`,
      product: row.productName || row.productType || row.product || productConfig?.name || "",
      fabricCode: getCurtainFabricDisplayWithBrand(row),
      fabricKey: getCurtainRowFabricKey(row),
      widthIn: Number(row.width || 0),
      heightIn: Number(row.height || 0),
      fabricWidthIn: Number(panelPlan.fabricWidthIn || row.fabricWidthIn || 0),
      usableWidthDeductionIn: Number(panelPlan.usableWidthDeductionIn || 0),
      fullness: Number(panelPlan.fullness || 0),
      rawPanels,
      floorPanels,
      halfStepProductionPanels,
      wholeBillingPanels,
      cutHeightIn,
      fullOrderedFabricMeters,
      customerBillableFabricMeters,
      workshopFabricRequiredMeters: round2(
        Number(calc.workshopFabricRequiredMeters ?? calc.workshopNewFabricMeters ?? 0)
      ),
      rowNewFabricMeters,
      newFullCuts,
      cutFabricMeters: round2(Number(calc.cutFabricMeters ?? 0)),
      liveScreenOrderedFabricMeters: customerBillableFabricMeters,
      pdfOrderedFabricPrinted: customerBillableFabricMeters,
      pdfSourceField: passHalfPieceSavingsToCustomer
        ? "customerBillableFabricMeters"
        : "computedWholeBillingPanels * cutHeightMeters",
    };
  },
  // getHalfPieceReusePiecePlan is a hoisted function declaration so render-time
  // memos can call it before this block. It reads current settings through
  // closure and is intentionally omitted from dependencies.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [
    getCurtainFabricDisplayWithBrand,
    getCurtainProductionAdjustedRowCalc,
    getRowProductConfig,
    passHalfPieceSavingsToCustomer,
    quoteFullnessOverrides,
  ]
);

const curtainCanonicalFabricSummaryRows = useMemo(() => {
  const billableMetersByKey = {};
  rows.forEach((row, index) => {
    const calc = getCurtainProductionAdjustedRowCalc(row, index);
    if (!calc?.isCurtain && !calc?.isRoman) return;
    const fabricKey = getCurtainRowFabricKey(row);
    if (!fabricKey) return;
    const billingTrace = getCurtainCustomerBillingFabricTrace(row, index, calc);
    billableMetersByKey[fabricKey] = round2(
      Number(billableMetersByKey[fabricKey] || 0) +
        Number(billingTrace.customerBillableFabricMeters || 0)
    );
  });

  return Object.entries(fabricSummaryUI || {})
    .map(([key, value], index) => {
      const normalizedKey = normalizeFabricKey(key);
      const orderSummary =
        fabricOrderSummaryUI?.[normalizedKey] ||
        fabricOrderSummaryUI?.[key] ||
        value ||
        {};
      const summaryMeta = getFabricSummaryMetaForKey(normalizedKey);
      const brandName =
        sanitizeEditableText(summaryMeta.brandName || "") ||
        getFabricBrandNameById(summaryMeta.brandId);
      const fabricCode =
        sanitizeEditableText(summaryMeta.fabricCode || "") ||
        sanitizeEditableText(value?.fabricLabel || value?.displayName || key);
      const fullOrderedMeters = round2(
        Number(value?.fullOrderedFabricMeters || value?.orderedFabricMeters || 0)
      );
      const newNeededMeters = round2(
        Number(
          orderSummary?.fabricToOrderMeters ??
            orderSummary?.newFabricNeededMeters ??
            orderSummary?.workshopFabricRequiredMeters ??
            value?.fabricToOrderMeters ??
            value?.newFabricNeededMeters ??
            value?.workshopFabricRequiredMeters ??
            value?.meters ??
            0
        )
      );
      const workshopMeters = round2(
        Number(
          orderSummary?.fabricToOrderMeters ??
            orderSummary?.workshopFabricRequiredMeters ??
            orderSummary?.newFabricNeededMeters ??
            newNeededMeters ??
            fullOrderedMeters
        )
      );
      const hasLiveBillableMeters = Object.prototype.hasOwnProperty.call(
        billableMetersByKey,
        normalizedKey
      );
      const fabricGroupCustomerBillableMeters = round2(
        Number(value?.customerBillableFabricMeters || 0)
      );
      const billableMeters = round2(
        Number(
          hasLiveBillableMeters
            ? billableMetersByKey[normalizedKey]
            : fabricGroupCustomerBillableMeters
        )
      );
      const cutMeters = round2(Number(value?.cutFabricMeters || 0));
      const halfReusedMeters = round2(
        Number(orderSummary?.halfReusedMeters ?? value?.halfReusedMeters ?? 0)
      );
      const usedInItems = rows
        .map((sourceRow, sourceIndex) => {
          const sourceFabricKey = normalizeFabricKey(getCurtainRowFabricKey(sourceRow));
          if (sourceFabricKey !== normalizedKey) return "";
          return (
            sanitizeEditableText(sourceRow.item || sourceRow.room || sourceRow.name || "") ||
            `Row ${sourceIndex + 1}`
          );
        })
        .filter(Boolean);
      const groupBrandEntries = rows
        .filter(
          (sourceRow) =>
            normalizeFabricKey(getCurtainRowFabricKey(sourceRow)) === normalizedKey
        )
        .map((sourceRow) => ({
          brandId: sanitizeEditableText(sourceRow.brandId || sourceRow.fabricBrandId || ""),
          brandName: sanitizeEditableText(sourceRow.brandName || sourceRow.fabricBrandName || ""),
        }))
        .filter((entry) => entry.brandId || entry.brandName);
      const uniqueGroupBrands = Array.from(
        new Map(
          groupBrandEntries.map((entry) => [
            entry.brandId || normalizeFabricKey(entry.brandName),
            entry,
          ])
        ).values()
      );
      const hasMixedBrands = uniqueGroupBrands.length > 1;
      const summaryRepeatSettings = normalizeCurtainRepeatEntry(
        repeatSettings?.[normalizedKey] || summaryMeta.repeatSettings || {}
      );
      const repeatSummary = formatRepeatSettings(summaryRepeatSettings);

      if (
        cutMeters <= 0 &&
        fullOrderedMeters <= 0 &&
        newNeededMeters <= 0 &&
        workshopMeters <= 0 &&
        billableMeters <= 0
      ) {
        return null;
      }

      return {
        key,
        normalizedKey,
        displayName: sanitizeEditableText(value?.displayName || value?.fabricLabel || key),
        fabricCode,
        brandId: summaryMeta.brandId || "",
        brandName: hasMixedBrands ? "Mixed brands" : brandName,
        hasMixedBrands,
        mixedBrandNames: uniqueGroupBrands
          .map((entry) => entry.brandName || getFabricBrandNameById(entry.brandId))
          .filter(Boolean),
        usedInItems,
        repeatSettings: summaryRepeatSettings,
        repeatSummary,
        notes: sanitizeEditableText(summaryMeta.notes || ""),
        cutFabricMeters: cutMeters,
        fullOrderedFabricMeters: fullOrderedMeters,
        fabricToOrderMeters: workshopMeters,
        newFabricNeededMeters: newNeededMeters,
        workshopFabricRequiredMeters: workshopMeters,
        customerBillableFabricMeters: billableMeters,
        fabricGroupCustomerBillableFabricMeters: fabricGroupCustomerBillableMeters,
        uiSummaryCustomerBillableFabricMeters: billableMeters,
        uiSummaryBillableSource: hasLiveBillableMeters
          ? "sum(customer PDF billing resolver rows)"
          : "fabricSummaryUI.customerBillableFabricMeters",
        halfReusedMeters,
        color: `hsl(${(index * 37) % 360} 70% 60%)`,
      };
    })
    .filter(Boolean);
}, [
  fabricOrderSummaryUI,
  fabricSummaryUI,
  getCurtainCustomerBillingFabricTrace,
  getCurtainProductionAdjustedRowCalc,
  getFabricBrandNameById,
  getFabricSummaryMetaForKey,
  repeatSettings,
  rows,
]);

function buildCurtainCanonicalFabricSummaryRowsForAction({
  sourceRows = [],
  fabricSummarySource = {},
  fabricOrderSummarySource = {},
  liveFabricSummaryMeta = {},
  liveGetRowCalc,
} = {}) {
  const billableMetersByKey = {};
  const safeRows = Array.isArray(sourceRows) ? sourceRows : [];
  safeRows.forEach((row, index) => {
    const calc =
      typeof liveGetRowCalc === "function"
        ? liveGetRowCalc(row, index)
        : getCurtainProductionAdjustedRowCalc(row, index);
    if (!calc?.isCurtain && !calc?.isRoman) return;
    const fabricKey = getCurtainRowFabricKey(row);
    if (!fabricKey) return;
    const billingTrace = getCurtainCustomerBillingFabricTrace(row, index, calc);
    billableMetersByKey[fabricKey] = round2(
      Number(billableMetersByKey[fabricKey] || 0) +
        Number(billingTrace.customerBillableFabricMeters || 0)
    );
  });

  return Object.entries(fabricSummarySource || {})
    .map(([key, value], index) => {
      const normalizedKey = normalizeFabricKey(key);
      const orderSummary =
        fabricOrderSummarySource?.[normalizedKey] ||
        fabricOrderSummarySource?.[key] ||
        value ||
        {};
      const summaryMeta = normalizedKey
        ? liveFabricSummaryMeta?.[normalizedKey] || {}
        : {};
      const brandName =
        sanitizeEditableText(summaryMeta.brandName || "") ||
        getFabricBrandNameById(summaryMeta.brandId);
      const fabricCode =
        sanitizeEditableText(summaryMeta.fabricCode || "") ||
        sanitizeEditableText(value?.fabricLabel || value?.displayName || key);
      const fullOrderedMeters = round2(
        Number(value?.fullOrderedFabricMeters || value?.orderedFabricMeters || 0)
      );
      const workshopMeters = round2(
        Number(
          orderSummary?.fabricToOrderMeters ??
            orderSummary?.workshopFabricRequiredMeters ??
            orderSummary?.newFabricNeededMeters ??
            value?.fabricToOrderMeters ??
            value?.workshopFabricRequiredMeters ??
            value?.newFabricNeededMeters ??
            0
        )
      );
      const newNeededMeters = workshopMeters;
      const liveBillableMeters = billableMetersByKey[normalizedKey];
      const hasLiveBillableMeters = Number.isFinite(Number(liveBillableMeters));
      const fabricGroupCustomerBillableMeters = round2(
        Number(
          value?.customerBillableFabricMeters ??
            value?.fullOrderedFabricMeters ??
            value?.orderedFabricMeters ??
            0
        )
      );
      const billableMeters = hasLiveBillableMeters
        ? round2(Number(liveBillableMeters || 0))
        : fabricGroupCustomerBillableMeters;
      const cutMeters = round2(Number(value?.cutFabricMeters || 0));
      const halfReusedMeters = round2(Number(value?.halfReusedMeters || 0));
      const usedInItems = safeRows
        .filter((row) => normalizeFabricKey(getCurtainRowFabricKey(row)) === normalizedKey)
        .map((row) => row.item || row.name || row.room || "")
        .filter(Boolean);
      const summaryRepeatSettings = normalizeCurtainRepeatEntry(
        summaryMeta.repeatSettings || {}
      );
      const repeatSummary = formatRepeatSettings(summaryRepeatSettings);
      const groupBrands = usedInItems
        .map((itemName) => {
          const row = safeRows.find((candidate) => {
            if (normalizeFabricKey(getCurtainRowFabricKey(candidate)) !== normalizedKey) {
              return false;
            }
            return (candidate.item || candidate.name || candidate.room || "") === itemName;
          });
          return {
            brandId: row?.brandId || row?.fabricBrandId || "",
            brandName: row?.brandName || row?.fabricBrandName || "",
          };
        })
        .filter((entry) => entry.brandId || entry.brandName);
      const uniqueGroupBrands = Array.from(
        new Map(
          groupBrands.map((entry) => [
            entry.brandId || entry.brandName,
            entry,
          ])
        ).values()
      );
      const hasMixedBrands = uniqueGroupBrands.length > 1;

      if (
        cutMeters <= 0 &&
        fullOrderedMeters <= 0 &&
        workshopMeters <= 0 &&
        billableMeters <= 0
      ) {
        return null;
      }

      return {
        key,
        normalizedKey,
        displayName: sanitizeEditableText(value?.displayName || value?.fabricLabel || key),
        fabricCode,
        brandId: summaryMeta.brandId || "",
        brandName: hasMixedBrands ? "Mixed brands" : brandName,
        hasMixedBrands,
        mixedBrandNames: uniqueGroupBrands
          .map((entry) => entry.brandName || getFabricBrandNameById(entry.brandId))
          .filter(Boolean),
        usedInItems,
        repeatSettings: summaryRepeatSettings,
        repeatSummary,
        notes: sanitizeEditableText(summaryMeta.notes || ""),
        cutFabricMeters: cutMeters,
        fullOrderedFabricMeters: fullOrderedMeters,
        fabricToOrderMeters: workshopMeters,
        newFabricNeededMeters: newNeededMeters,
        workshopFabricRequiredMeters: workshopMeters,
        customerBillableFabricMeters: billableMeters,
        fabricGroupCustomerBillableFabricMeters: fabricGroupCustomerBillableMeters,
        uiSummaryCustomerBillableFabricMeters: billableMeters,
        uiSummaryBillableSource: hasLiveBillableMeters
          ? "sum(customer PDF billing resolver rows)"
          : "fabricSummaryUI.customerBillableFabricMeters",
        halfReusedMeters,
        color: `hsl(${(index * 37) % 360} 70% 60%)`,
      };
    })
    .filter(Boolean);
}

function buildLiveCurtainQuotePayload({ action = "unknown" } = {}) {
  const liveRows = normalizeRowsForState(rowsRef.current || rows);
  const liveRepeatSettings = normalizeCurtainRepeatSettingsMap(
    repeatSettingsRef.current || repeatSettings || {}
  );
  const liveFabricSummaryMeta = normalizeFabricSummaryMetaWithRepeatSettings(
    fabricSummaryMetaRef.current || fabricSummaryMeta || {},
    liveRepeatSettings
  );
  const liveUseHalfPieceReuse = useHalfPieceReuseRef.current !== false;
  const livePassHalfPieceSavingsToCustomer =
    passHalfPieceSavingsToCustomerRef.current === true;
  const liveDiscountMode = summaryDiscountModeRef.current ?? summaryDiscountMode;
  const liveDiscountValue = summaryDiscountValueRef.current ?? summaryDiscountValue;
  const liveGstRate = normalizeCurtainGstRate(
    curtainGstRateRef.current ?? curtainGstRate
  );
  const liveQuoteFullnessOverrides = normalizeQuoteFullnessOverrides(
    quoteFullnessOverridesRef.current || quoteFullnessOverrides || {}
  );

  const liveFabricSummaryResult = buildCurtainFabricSummary(liveRows, {
    activeRepeatSettings: liveRepeatSettings,
    halfPieceReuseEnabled: liveUseHalfPieceReuse,
    passSavingsToCustomer: livePassHalfPieceSavingsToCustomer,
    debugTag: `${action}-live-action`,
    quoteFullnessOverridesForCalculation: liveQuoteFullnessOverrides,
  });
  const liveNoReuseBillingResult = buildCurtainFabricSummary(liveRows, {
    activeRepeatSettings: liveRepeatSettings,
    halfPieceReuseEnabled: false,
    debugTag: `${action}-live-action-no-reuse`,
    quoteFullnessOverridesForCalculation: liveQuoteFullnessOverrides,
  });
  const liveFabricOrderSummaryResult = buildCurtainFabricSummary(liveRows, {
    activeRepeatSettings: liveRepeatSettings,
    halfPieceReuseEnabled: liveUseHalfPieceReuse,
    passSavingsToCustomer: livePassHalfPieceSavingsToCustomer,
    debugTag: `${action}-live-action-fabric-to-order`,
    useProductWorkshopRules: true,
    quoteFullnessOverridesForCalculation: liveQuoteFullnessOverrides,
  });
  const liveGetRowCalc = (row, index) =>
    getCurtainProductionAdjustedRowCalcFromSummary(
      row,
      index,
      liveFabricSummaryResult,
      liveNoReuseBillingResult
    );
  const liveSubtotal = round2(
    liveRows.reduce(
      (sum, row, index) => sum + Number(liveGetRowCalc(row, index).rowTotal || 0),
      0
    )
  );
  const gstTotals = calculateCurtainQuoteTotals({
    rows: liveRows,
    subtotal: liveSubtotal,
    discountType: getDiscountTypeFromSummaryMode(liveDiscountMode),
    discountValue: liveDiscountValue,
    quoteGstRate: liveGstRate,
    getRowTaxableTotal: (row, index) => liveGetRowCalc(row, index).rowTotal,
    isAccessoryRow: isCurtainAccessoryRowStatic,
  });
  const liveSummaryTotals = {
    subTotal: round2(liveSubtotal),
    discountType: gstTotals.discountType,
    discountValue: gstTotals.discountValue,
    discountAmount: gstTotals.discountAmount,
    discountApplication: gstTotals.discountApplication || "after_gst",
    postGstDiscountAmount: gstTotals.postGstDiscountAmount ?? gstTotals.discountAmount,
    preGstGrandTotal: gstTotals.subtotalBeforeGst,
    subtotalBeforeGst: gstTotals.subtotalBeforeGst,
    totalAfterGst: gstTotals.totalAfterGst,
    measurementSubtotalBeforeDiscount: gstTotals.measurementSubtotalBeforeDiscount,
    accessorySubtotalBeforeDiscount: gstTotals.accessorySubtotalBeforeDiscount,
    accessorySubtotalBeforeGst: gstTotals.accessorySubtotalBeforeDiscount,
    measurementDiscountAmount: gstTotals.measurementDiscountAmount,
    accessoryDiscountAmount: gstTotals.accessoryDiscountAmount,
    taxableMeasurementAmount: gstTotals.taxableMeasurementAmount,
    taxableAccessoryAmount: gstTotals.taxableAccessoryAmount,
    taxableAmount: gstTotals.taxableAmount,
    quoteGstRate: gstTotals.quoteGstRate,
    gstRate: gstTotals.gstRate,
    gstRates: gstTotals.gstRates || [gstTotals.gstRate],
    accessoryGstRates: gstTotals.accessoryGstRates || [],
    hasMixedGstRates: gstTotals.hasMixedGstRates === true,
    measurementGstAmount: gstTotals.measurementGstAmount,
    accessoryGstAmount: gstTotals.accessoryGstAmount,
    gstAmount: gstTotals.gstAmount,
    totalGstAmount: gstTotals.totalGstAmount,
    finalTotal: gstTotals.finalTotal,
    grandTotal: gstTotals.grandTotal,
  };
  const liveCurrentCurtainDiscountFields = {
    discountType: liveSummaryTotals.discountType,
    discountValue: liveSummaryTotals.discountValue,
    discountAmount: liveSummaryTotals.discountAmount,
    discountApplication: liveSummaryTotals.discountApplication || "after_gst",
    postGstDiscountAmount:
      liveSummaryTotals.postGstDiscountAmount ?? liveSummaryTotals.discountAmount,
    summaryDiscountMode: getSummaryDiscountModeFromType(liveSummaryTotals.discountType),
    summaryDiscountValue: liveSummaryTotals.discountValue,
  };
  const liveCurrentCurtainGstFields = {
    quoteGstRate: liveSummaryTotals.quoteGstRate,
    gstRate: liveSummaryTotals.gstRate,
    gstAmount: liveSummaryTotals.gstAmount,
    totalGstAmount: liveSummaryTotals.totalGstAmount,
    measurementGstAmount: liveSummaryTotals.measurementGstAmount,
    accessoryGstAmount: liveSummaryTotals.accessoryGstAmount,
    taxableMeasurementAmount: liveSummaryTotals.taxableMeasurementAmount,
    taxableAccessoryAmount: liveSummaryTotals.taxableAccessoryAmount,
    taxableAmount: liveSummaryTotals.taxableAmount,
    subtotalBeforeGst: liveSummaryTotals.subtotalBeforeGst,
    totalAfterGst: liveSummaryTotals.totalAfterGst,
    finalTotal: liveSummaryTotals.finalTotal,
    preGstGrandTotal: liveSummaryTotals.preGstGrandTotal,
    totalInWords: amountToWords(liveSummaryTotals.grandTotal, currency),
  };
  const liveFabricSummaryUI = liveFabricSummaryResult.fabricSummaries || {};
  const liveFabricOrderSummaryUI = liveFabricOrderSummaryResult.fabricSummaries || {};
  const liveCurtainCanonicalFabricSummaryRows =
    buildCurtainCanonicalFabricSummaryRowsForAction({
      sourceRows: liveRows,
      fabricSummarySource: liveFabricSummaryUI,
      fabricOrderSummarySource: liveFabricOrderSummaryUI,
      liveFabricSummaryMeta,
      liveGetRowCalc,
    });
  const liveDisplaySummaryTotals =
    isFrozenLegacyCurtainQuote &&
    !curtainQuoteIsDirty &&
    curtainTotalsSettingsEditVersion <= 0
      ? curtainDisplaySummaryTotals
      : liveSummaryTotals;

  return {
    action,
    quoteType: CURTAIN_QUOTE_TYPE,
    quoteNo,
    quoteDate: date,
    customerName,
    quoteCustomerLink,
    selectedQuote,
    selectedQuoteStatus,
    selectedQuoteWorkStage,
    selectedInstallationDate,
    pendingCrmLeadId,
    rows: liveRows,
    repeatSettings: liveRepeatSettings,
    fabricSummaryMeta: liveFabricSummaryMeta,
    quoteFullnessOverrides: liveQuoteFullnessOverrides,
    fabricSummaryResult: liveFabricSummaryResult,
    fabricOrderSummaryResult: liveFabricOrderSummaryResult,
    fabricSummaryUI: liveFabricSummaryUI,
    fabricOrderSummaryUI: liveFabricOrderSummaryUI,
    curtainCanonicalFabricSummaryRows: liveCurtainCanonicalFabricSummaryRows,
    getRowCalc: liveGetRowCalc,
    summaryTotals: liveSummaryTotals,
    displaySummaryTotals: liveDisplaySummaryTotals,
    currentCurtainDiscountFields: liveCurrentCurtainDiscountFields,
    currentCurtainGstFields: liveCurrentCurtainGstFields,
    summaryDiscountMode: liveDiscountMode,
    summaryDiscountValue: liveDiscountValue,
    curtainGstRate: liveGstRate,
    useHalfPieceReuse: liveUseHalfPieceReuse,
    passHalfPieceSavingsToCustomer: livePassHalfPieceSavingsToCustomer,
  };
}

function getCurtainActionMeterTotals(payload = {}) {
  const summaryRows = payload.curtainCanonicalFabricSummaryRows || [];
  return summaryRows.reduce(
    (acc, summary) => ({
      fabricToOrderTotal: round2(
        acc.fabricToOrderTotal +
          Number(
            summary.fabricToOrderMeters ??
              summary.workshopFabricRequiredMeters ??
              summary.newFabricNeededMeters ??
              0
          )
      ),
      customerBillableTotal: round2(
        acc.customerBillableTotal +
          Number(summary.customerBillableFabricMeters || 0)
      ),
    }),
    { fabricToOrderTotal: 0, customerBillableTotal: 0 }
  );
}

function traceLiveActionPayload(payload = {}) {
  if (!IS_DEV_UI_ENABLED) return;
  const totals = payload.summaryTotals || {};
  const meterTotals = getCurtainActionMeterTotals(payload);
  console.log("LIVE_ACTION_PAYLOAD_TRACE", {
    action: payload.action || "unknown",
    quoteType: payload.quoteType || CURTAIN_QUOTE_TYPE,
    quoteNo: payload.quoteNo || "",
    rowsCount: payload.rows?.length || payload.items?.length || 0,
    discountType: totals.discountType,
    discountValue: totals.discountValue,
    gstRate: totals.quoteGstRate ?? totals.gstRate,
    subtotal:
      totals.subTotal ??
      totals.subtotalBeforeGst ??
      totals.subtotalExcludingGst ??
      totals.subtotal,
    totalAfterGst: totals.totalAfterGst,
    discountAmount: totals.discountAmount,
    grandTotal: totals.grandTotal,
    fabricToOrderTotal: meterTotals.fabricToOrderTotal,
    customerBillableTotal: meterTotals.customerBillableTotal,
    generatedAt: new Date().toISOString(),
  });
}

function guardStaleActionPayload(payload = {}, payloadTotals = null) {
  if (!IS_DEV_UI_ENABLED) return;
  const visibleTotals = payload.summaryTotals || {};
  const actualPayloadTotals = payloadTotals || payload.summaryTotals || {};
  const visibleGrandTotal = round2(Number(visibleTotals.grandTotal || 0));
  const payloadGrandTotal = round2(Number(actualPayloadTotals.grandTotal || 0));
  const visibleDiscountValue = Number(visibleTotals.discountValue || 0);
  const payloadDiscountValue = Number(actualPayloadTotals.discountValue || 0);
  const visibleGstRate = Number(visibleTotals.quoteGstRate ?? visibleTotals.gstRate ?? 0);
  const payloadGstRate = Number(
    actualPayloadTotals.quoteGstRate ?? actualPayloadTotals.gstRate ?? 0
  );
  if (
    Math.abs(visibleGrandTotal - payloadGrandTotal) > 0.05 ||
    Math.abs(visibleDiscountValue - payloadDiscountValue) > 0.05 ||
    Math.abs(visibleGstRate - payloadGstRate) > 0.05
  ) {
    console.error("STALE_ACTION_PAYLOAD_DETECTED", {
      action: payload.action || "unknown",
      visibleGrandTotal,
      payloadGrandTotal,
      visibleDiscountValue,
      payloadDiscountValue,
      visibleGstRate,
      payloadGstRate,
    });
  }
}

const updateFabricSummaryMetaForKey = useCallback((fabricKey, patch = {}) => {
  const normalizedKey = normalizeFabricKey(fabricKey);
  if (!normalizedKey) return;
  setFabricSummaryMeta((prev) => {
    const current = normalizeFabricSummaryMeta(prev)[normalizedKey] || {};
    const nextMeta = normalizeFabricSummaryMeta({
      ...prev,
      [normalizedKey]: {
        ...current,
        ...patch,
      },
    });
    fabricSummaryMetaRef.current = nextMeta;
    return nextMeta;
  });
}, []);

const syncFabricSummaryBrandToRows = useCallback(
  (fabricKey, selectedBrand = null) => {
    const normalizedKey = normalizeFabricKey(fabricKey);
    if (!normalizedKey) return;

    const currentRows = normalizeRowsForState(rowsRef.current || []);
    const targetRowIds = [];
    let skippedManualRows = 0;
    currentRows.forEach((row) => {
      const rowFabricKey = normalizeFabricKey(getCurtainRowFabricKey(row));
      if (rowFabricKey !== normalizedKey) return;
      const currentBrandId = sanitizeEditableText(row.brandId || row.fabricBrandId || "");
      const brandSource = sanitizeEditableText(
        row.brandSource || (currentBrandId ? "legacy" : "none")
      );
      const canUpdate =
        !currentBrandId ||
        brandSource === "fabric_summary" ||
        brandSource === "none";
      if (canUpdate) {
        targetRowIds.push(getWorkshopRowStableId(row));
      } else if (
        selectedBrand?.id &&
        currentBrandId &&
        currentBrandId !== selectedBrand.id
      ) {
        skippedManualRows += 1;
      }
    });

    if (!targetRowIds.length) {
      if (skippedManualRows > 0) {
        notify("Some rows have manually selected brands and were not changed.", "warning");
      }
      return;
    }

    const targetIdSet = new Set(targetRowIds);
    const timestamp = new Date().toISOString();
    commitCurtainRows(
      (previousRows) =>
        previousRows.map((row, index) => {
          const stableRow = withCurtainRowId(row || {}, index);
          if (!targetIdSet.has(getWorkshopRowStableId(stableRow))) return stableRow;
          const nextRow = {
            ...stableRow,
            brandId: selectedBrand?.id || "",
            brandName: selectedBrand?.name || "",
            fabricBrandId: selectedBrand?.id || "",
            fabricBrandName: selectedBrand?.name || "",
            brandSource: selectedBrand?.id ? "fabric_summary" : "none",
            updatedFromFabricSummaryAt: timestamp,
          };
          const rowFabricKey = normalizeFabricKey(getCurtainRowFabricKey(nextRow));
          nextRow.repeatSettings = repeatSettings?.[rowFabricKey] || {};
          return withCurtainRowId(nextRow, index);
        }),
      QUOTE_RAW_FIELD_MUTATION_SOURCES.USER_INPUT
    );

    if (skippedManualRows > 0) {
      notify("Some rows have manually selected brands and were not changed.", "warning");
    }
  },
  [commitCurtainRows, normalizeRowsForState, notify, repeatSettings]
);

const updateFabricSummaryBrand = useCallback(
  (fabricKey, brandId) => {
    const selectedBrand = fabricBrands.find((brand) => brand.id === brandId);
    updateFabricSummaryMetaForKey(fabricKey, {
      brandId: selectedBrand?.id || "",
      brandName: selectedBrand?.name || "",
    });
    syncFabricSummaryBrandToRows(fabricKey, selectedBrand || null);
  },
  [fabricBrands, syncFabricSummaryBrandToRows, updateFabricSummaryMetaForKey]
);

const renderCurtainLiningToggle = (row, calc, rowId) => {
  if (!curtainRowSupportsLining(row, calc)) {
    return (
      <span style={{ color: dashboardMuted, fontSize: 12, opacity: 0.65 }}>
        -
      </span>
    );
  }

  const lining = getCurtainRowLiningForUi(row, calc);

  return (
    <label
      style={{
        display: "grid",
        gap: 5,
        fontSize: 12,
        fontWeight: 800,
        color: isDark ? "#e2e8f0" : "#334155",
        cursor: "pointer",
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
        <input
          type="checkbox"
          checked={lining.enabled}
          onChange={(event) =>
            updateCurtainRowLining(rowId, row, calc, {
              enabled: event.target.checked,
            })
          }
        />
        Add Lining
      </span>
      {lining.enabled ? (
        <span
          style={{
            color: "#2563eb",
            fontSize: 11,
            fontWeight: 900,
            lineHeight: 1.35,
          }}
        >
          {getCurtainLiningLabel(lining)}
          <br />
          {currencySymbol(currency)} {Number(lining.amount || 0).toFixed(2)}
        </span>
      ) : null}
    </label>
  );
};

const renderCurtainLiningPanel = (row, calc, rowId) => {
  if (!curtainRowSupportsLining(row, calc)) return null;

  const lining = getCurtainRowLiningForUi(row, calc);
  const updateLining = (patch) => updateCurtainRowLining(rowId, row, calc, patch);
  const panelInput = {
    ...input,
    minHeight: 34,
    padding: "8px 10px",
  };
  const panelSelect = {
    ...selectInput,
    minHeight: 34,
    padding: "8px 10px",
  };
  const panelLabel = {
    fontSize: 11,
    fontWeight: 800,
    color: dashboardMuted,
    marginBottom: 5,
  };

  return (
    <div
      style={{
        borderRadius: 16,
        border: isDark
          ? "1px solid rgba(96,165,250,0.22)"
          : "1px solid rgba(191,219,254,0.9)",
        background: isDark
          ? "linear-gradient(135deg,rgba(15,23,42,0.94),rgba(30,41,59,0.86))"
          : "linear-gradient(135deg,#ffffff,#eff6ff)",
        padding: 14,
        display: "grid",
        gap: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13,
            fontWeight: 900,
            color: isDark ? "#f8fafc" : "#0f172a",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={lining.enabled}
            onChange={(event) =>
              updateLining({
                enabled: event.target.checked,
              })
            }
          />
          Lining required
        </label>
        {lining.enabled ? (
          <div
            style={{
              borderRadius: 999,
              padding: "5px 10px",
              background: isDark ? "rgba(37,99,235,0.18)" : "#dbeafe",
              color: "#2563eb",
              fontSize: 11,
              fontWeight: 900,
            }}
          >
            Includes lining: {currencySymbol(currency)}{" "}
            {Number(lining.amount || 0).toFixed(2)}
          </div>
        ) : null}
      </div>

      {lining.enabled ? (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr"
                : "repeat(auto-fit, minmax(150px, 1fr))",
              gap: 10,
              alignItems: "end",
            }}
          >
            <div>
              <div style={panelLabel}>Type</div>
              <select
                style={panelSelect}
                value={lining.type}
                onChange={(event) =>
                  updateLining({
                    type: event.target.value,
                  })
                }
              >
                {CURTAIN_LINING_TYPES.map((option) => (
                  <option key={option.type} value={option.type}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div style={panelLabel}>Lining Fabric / Code</div>
              <input
                style={panelInput}
                value={lining.fabricCode ?? ""}
                placeholder="Optional fabric/code"
                onChange={(event) =>
                  updateLining({
                    fabricCode: event.target.value,
                  })
                }
              />
            </div>
            <div>
              <div style={panelLabel}>Brand</div>
              <select
                style={panelSelect}
                value={lining.brandId || ""}
                onChange={(event) => {
                  const selectedBrand = fabricBrands.find(
                    (brand) => brand.id === event.target.value
                  );
                  updateLining({
                    brandId: selectedBrand?.id || "",
                    brandName: selectedBrand?.name || "",
                  });
                }}
              >
                <option value="">No brand selected</option>
                {fabricBrands.map((brand) => (
                  <option key={brand.id} value={brand.id}>
                    {brand.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div style={panelLabel}>Charge Method</div>
              <select
                style={panelSelect}
                value={lining.chargeMethod || "meter"}
                onChange={(event) =>
                  updateLining({
                    chargeMethod: event.target.value,
                  })
                }
              >
                <option value="meter">Per meter</option>
                <option value="sqft">Per sqft</option>
              </select>
            </div>
            <div>
              <div style={panelLabel}>
                {lining.chargeMethod === "sqft" ? "Lining Rate / sqft" : "Lining Rate / m"}
              </div>
              <input
                type="text"
                inputMode="decimal"
                style={panelInput}
                value={
                  lining.chargeMethod === "sqft"
                    ? lining.ratePerSqft ?? ""
                    : lining.ratePerMeter ?? ""
                }
                placeholder="0"
                onChange={(event) =>
                  updateLining({
                    [lining.chargeMethod === "sqft" ? "ratePerSqft" : "ratePerMeter"]:
                      event.target.value,
                  })
                }
              />
            </div>
            {lining.chargeMethod === "meter" ? (
              <div>
                <div style={panelLabel}>Meters</div>
                <select
                  style={panelSelect}
                  value={lining.metersMode}
                  onChange={(event) =>
                    updateLining({
                      metersMode: event.target.value,
                    })
                  }
                >
                  <option value="same_as_curtain">Same as fabric meters</option>
                  <option value="custom_override">Override meters</option>
                </select>
              </div>
            ) : (
              <div>
                <div style={panelLabel}>Calculated Sqft</div>
                <div
                  style={{
                    ...panelInput,
                    display: "flex",
                    alignItems: "center",
                    fontWeight: 900,
                    background: isDark ? "rgba(15,23,42,0.55)" : "#ffffff",
                  }}
                >
                  {Number(lining.sqft || 0).toFixed(2)} sqft
                </div>
              </div>
            )}
            {lining.type === "custom" ? (
              <div style={{ gridColumn: isMobile ? "auto" : "span 2" }}>
                <div style={panelLabel}>Custom Lining Label</div>
                <input
                  style={panelInput}
                  value={lining.customLabel ?? ""}
                  placeholder="Imported blackout lining"
                  onChange={(event) =>
                    updateLining({
                      customLabel: event.target.value,
                    })
                  }
                />
              </div>
            ) : null}
            {lining.chargeMethod === "meter" && lining.metersMode === "custom_override" ? (
              <div>
                <div style={panelLabel}>Override Meters</div>
                <input
                  type="text"
                  inputMode="decimal"
                  style={panelInput}
                  value={lining.overrideMeters ?? ""}
                  placeholder={Number(lining.calculatedMeters || 0).toFixed(2)}
                  onChange={(event) =>
                    updateLining({
                      overrideMeters: event.target.value,
                    })
                  }
                />
              </div>
            ) : null}
            <div>
              <div style={panelLabel}>Lining Amount</div>
              <div
                style={{
                  ...panelInput,
                  display: "flex",
                  alignItems: "center",
                  fontWeight: 900,
                  color: "#2563eb",
                  background: isDark ? "rgba(15,23,42,0.55)" : "#ffffff",
                }}
              >
                {currencySymbol(currency)} {Number(lining.amount || 0).toFixed(2)}
              </div>
            </div>
          </div>
          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              fontSize: 12,
              color: dashboardMuted,
            }}
          >
            <span>
              {lining.chargeMethod === "sqft" ? (
                <>
                  Lining sqft:{" "}
                  <strong style={{ color: isDark ? "#f8fafc" : "#0f172a" }}>
                    {Number(lining.sqft || 0).toFixed(2)} sqft
                  </strong>{" "}
                  charged at rate/sqft.
                </>
              ) : (
                <>
                  Lining meters:{" "}
                  <strong style={{ color: isDark ? "#f8fafc" : "#0f172a" }}>
                    {Number(lining.billableMeters || 0).toFixed(2)} m
                  </strong>{" "}
                  from row fabric requirement{" "}
                  {Number(lining.calculatedMeters || 0).toFixed(2)} m.
                </>
              )}
            </span>
            <button
              type="button"
              onClick={() =>
                updateLining({
                  enabled: false,
                })
              }
              style={{
                border: "none",
                borderRadius: 999,
                padding: "7px 11px",
                background: isDark ? "rgba(239,68,68,0.14)" : "#fee2e2",
                color: "#dc2626",
                fontSize: 12,
                fontWeight: 900,
                cursor: "pointer",
              }}
            >
              Remove lining
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
};

const getCurtainRowSelectedTrackPackage = (row = {}) =>
  findCurtainRowStitchConfig(row, stitchingRates)?.config || null;

const shouldShowCurtainTrackConfigPanel = (row = {}, calc = {}) => {
  if (calc?.isAccessory) return false;
  const selectedTrackPackage = getCurtainRowSelectedTrackPackage(row);
  const normalizedPackage = selectedTrackPackage
    ? normalizeStitchingSettingForState(selectedTrackPackage)
    : null;
  return Boolean(
    normalizedPackage &&
      (normalizedPackage.includesTrack === true ||
        normalizedPackage.supportsBendedTrack === true ||
        row?.trackConfig?.isBended === true)
  );
};

const getCurtainTrackConfigForUi = (row = {}, calc = {}) => {
  const selectedTrackPackage = getCurtainRowSelectedTrackPackage(row) || {};
  const baseTrackLengthFt =
    num(row.trackLengthFt ?? row.trackLength) ||
    num(selectedTrackPackage.trackLengthFt ?? selectedTrackPackage.trackLength);
  return calc?.trackConfig || calculateCurtainTrackConfig(row, selectedTrackPackage, {
    baseTrackLengthFt,
    measurementUnit: getRowMeasurementUnit(row, unit),
  });
};

const updateCurtainTrackConfig = (rowId, row = {}, calc = {}, patch = {}) => {
  if (!rowId) return;
  const selectedTrackPackage = getCurtainRowSelectedTrackPackage(row) || {};
  const currentTrackConfig = getCurtainTrackConfigForUi(row, calc);
  const nextDraft = {
    ...currentTrackConfig,
    ...patch,
  };
  const normalized = calculateCurtainTrackConfig(
    {
      ...row,
      trackConfig: nextDraft,
    },
    selectedTrackPackage,
    {
      baseTrackLengthFt:
        num(row.trackLengthFt ?? row.trackLength) ||
        num(selectedTrackPackage.trackLengthFt ?? selectedTrackPackage.trackLength),
      measurementUnit: getRowMeasurementUnit(row, unit),
    }
  );
  updateField(rowId, "trackConfig", normalized);
};

const renderCurtainTrackConfigPanel = (row, calc, rowId) => {
  if (!shouldShowCurtainTrackConfigPanel(row, calc)) return null;

  const selectedTrackPackage = getCurtainRowSelectedTrackPackage(row) || {};
  const normalizedPackage = normalizeStitchingSettingForState(selectedTrackPackage);
  const trackConfig = getCurtainTrackConfigForUi(row, calc);
  const updateTrack = (patch) => updateCurtainTrackConfig(rowId, row, calc, patch);
  const bendSettings = normalizedPackage.bendSettings || DEFAULT_BEND_SETTINGS;
  const allowedShapes = normalizeAllowedBendShapes(bendSettings.allowedAngles);
  const shapeOptions = normalizedPackage.supportsBendedTrack
    ? BENDED_TRACK_SHAPES.filter(
        (entry) => entry.shape === "straight" || allowedShapes.includes(entry.shape)
      )
    : [BENDED_TRACK_SHAPE_MAP.straight];
  const isBendedShape = trackConfig.shape !== "straight";
  const chargeEditable = trackConfig.pricingMethod === "manual";
  const segmentUnit = trackConfig.segmentUnit || getRowMeasurementUnit(row, unit);
  const segmentUnitLabel = unitLabel(segmentUnit);
  const hasSegmentOverride = Number(trackConfig.totalSegmentLength || 0) > 0;
  const layoutPreview = buildBendedTrackLayoutPreview(trackConfig);
  const panelInput = {
    ...input,
    minHeight: 34,
    padding: "8px 10px",
  };
  const panelSelect = {
    ...selectInput,
    minHeight: 34,
    padding: "8px 10px",
  };
  const panelLabel = {
    fontSize: 11,
    fontWeight: 800,
    color: dashboardMuted,
    marginBottom: 5,
  };

  return (
    <div
      style={{
        borderRadius: 16,
        border: isDark
          ? "1px solid rgba(20,184,166,0.24)"
          : "1px solid rgba(153,246,228,0.95)",
        background: isDark
          ? "linear-gradient(135deg,rgba(15,23,42,0.94),rgba(15,118,110,0.2))"
          : "linear-gradient(135deg,#ffffff,#ecfeff)",
        padding: 14,
        display: "grid",
        gap: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <div>
          <div style={{ fontSize: 13, fontWeight: 900, color: isDark ? "#f8fafc" : "#0f172a" }}>
            Track Shape
          </div>
          <div style={{ marginTop: 3, fontSize: 11.5, color: dashboardMuted }}>
            {normalizedPackage.supportsBendedTrack
              ? "This stitching/track package allows bended track charges."
              : "This package includes track but does not support bended track charges."}
          </div>
        </div>
        {trackConfig.bendAmount > 0 ? (
          <div
            style={{
              borderRadius: 999,
              padding: "5px 10px",
              background: isDark ? "rgba(20,184,166,0.18)" : "#ccfbf1",
              color: "#0f766e",
              fontSize: 11,
              fontWeight: 900,
            }}
          >
            Bend Amount: {currencySymbol(currency)} {Number(trackConfig.bendAmount || 0).toFixed(2)}
          </div>
        ) : null}
      </div>

      {trackConfig.disabledReason ? (
        <div
          style={{
            borderRadius: 12,
            padding: "8px 10px",
            background: isDark ? "rgba(245,158,11,0.12)" : "#fffbeb",
            color: isDark ? "#fde68a" : "#92400e",
            fontSize: 12,
            fontWeight: 800,
          }}
        >
          {trackConfig.disabledReason}
        </div>
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile
            ? "1fr"
            : "minmax(150px, 1fr) minmax(110px, 0.7fr) minmax(110px, 0.7fr) minmax(130px, 0.8fr)",
          gap: 10,
          alignItems: "end",
        }}
      >
        <div>
          <div style={panelLabel}>Track Shape</div>
          <select
            style={panelSelect}
            value={trackConfig.shape}
            onChange={(event) => {
              const shape = normalizeBendedTrackShape(event.target.value);
              const shapeMeta = getBendedTrackShapeMeta(shape);
              updateTrack({
                shape,
                isBended: shape !== "straight",
                bendCount: shapeMeta.defaultBendCount,
                angleDeg: shapeMeta.defaultAngleDeg,
                bendLocations: normalizeBendLocations([], {
                  shape,
                  bendCount: shapeMeta.defaultBendCount,
                  angleDeg: shapeMeta.defaultAngleDeg,
                  unit: segmentUnit,
                }),
              });
            }}
          >
            {shapeOptions.map((option) => (
              <option key={option.shape} value={option.shape}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {isBendedShape ? (
          <>
            <div>
              <div style={panelLabel}>No. of bends</div>
              <input
                type="text"
                inputMode="decimal"
                style={panelInput}
                value={trackConfig.bendCount ?? ""}
                onChange={(event) =>
                  updateTrack({
                    bendCount: event.target.value,
                    isBended: true,
                  })
                }
              />
            </div>
            <div>
              <div style={panelLabel}>Angle degrees</div>
              <input
                type="text"
                inputMode="decimal"
                disabled={trackConfig.shape !== "custom"}
                style={{
                  ...panelInput,
                  opacity: trackConfig.shape === "custom" ? 1 : 0.55,
                }}
                value={trackConfig.angleDeg ?? ""}
                onChange={(event) =>
                  updateTrack({
                    angleDeg: event.target.value,
                    isBended: true,
                  })
                }
              />
            </div>
            <div>
              <div style={panelLabel}>Bend charge / bend</div>
              <input
                type="text"
                inputMode="decimal"
                disabled={!chargeEditable}
                style={{
                  ...panelInput,
                  opacity: chargeEditable ? 1 : 0.62,
                }}
                value={
                  chargeEditable
                    ? trackConfig.manualBendCharge ?? ""
                    : Number(trackConfig.bendChargePerBend || 0).toFixed(2)
                }
                onChange={(event) =>
                  updateTrack({
                    manualBendCharge: event.target.value,
                    isBended: true,
                  })
                }
              />
            </div>
          </>
        ) : null}
      </div>

      {isBendedShape ? (
        <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.5 }}>
          Bend Amount = {Number(trackConfig.bendCount || 0).toFixed(2)} x{" "}
          {currencySymbol(currency)} {Number(trackConfig.bendChargePerBend || 0).toFixed(2)} ={" "}
          <strong style={{ color: isDark ? "#99f6e4" : "#0f766e" }}>
            {currencySymbol(currency)} {Number(trackConfig.bendAmount || 0).toFixed(2)}
          </strong>
        </div>
      ) : null}

      {isBendedShape ? (
        <div
          style={{
            borderRadius: 14,
            padding: 12,
            background: isDark ? "rgba(15,23,42,0.48)" : "rgba(255,255,255,0.78)",
            border: isDark ? "1px solid rgba(148,163,184,0.18)" : "1px solid #dbeafe",
            display: "grid",
            gap: 10,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 900, color: isDark ? "#99f6e4" : "#0f766e" }}>
            Bend Location
          </div>
          {(trackConfig.bendLocations || []).map((location, bendIndex) => (
            <div
              key={`bend-location-${bendIndex}`}
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : "minmax(180px, 1.2fr) minmax(100px, 0.6fr) minmax(180px, 1fr)",
                gap: 10,
                alignItems: "end",
              }}
            >
              <div>
                <div style={panelLabel}>
                  Bend {bendIndex + 1} Location
                </div>
                <select
                  style={panelSelect}
                  value={getBendLocationOption(location)}
                  onChange={(event) => {
                    const nextLocations = normalizeBendLocations(
                      trackConfig.bendLocations,
                      {
                        shape: trackConfig.shape,
                        bendCount: trackConfig.bendCount,
                        angleDeg: trackConfig.angleDeg,
                        unit: segmentUnit,
                      }
                    );
                    nextLocations[bendIndex] = buildBendLocationFromOption(
                      event.target.value,
                      bendIndex + 1,
                      nextLocations[bendIndex]?.angleDeg ?? trackConfig.angleDeg,
                      segmentUnit,
                      nextLocations[bendIndex]
                    );
                    updateTrack({ bendLocations: nextLocations });
                  }}
                >
                  {BEND_LOCATION_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <div style={panelLabel}>Angle</div>
                <input
                  type="text"
                  inputMode="decimal"
                  style={panelInput}
                  value={location.angleDeg ?? trackConfig.angleDeg ?? ""}
                  onChange={(event) => {
                    const nextLocations = normalizeBendLocations(
                      trackConfig.bendLocations,
                      {
                        shape: trackConfig.shape,
                        bendCount: trackConfig.bendCount,
                        angleDeg: trackConfig.angleDeg,
                        unit: segmentUnit,
                      }
                    );
                    nextLocations[bendIndex] = {
                      ...nextLocations[bendIndex],
                      angleDeg: event.target.value,
                    };
                    updateTrack({ bendLocations: nextLocations });
                  }}
                />
              </div>
              <div>
                <div style={panelLabel}>Custom note</div>
                <input
                  style={panelInput}
                  value={location.note || ""}
                  placeholder="Optional bend note"
                  onChange={(event) => {
                    const nextLocations = normalizeBendLocations(
                      trackConfig.bendLocations,
                      {
                        shape: trackConfig.shape,
                        bendCount: trackConfig.bendCount,
                        angleDeg: trackConfig.angleDeg,
                        unit: segmentUnit,
                      }
                    );
                    nextLocations[bendIndex] = {
                      ...nextLocations[bendIndex],
                      note: event.target.value,
                    };
                    updateTrack({ bendLocations: nextLocations });
                  }}
                />
              </div>
            </div>
          ))}
          <div style={{ fontSize: 11.5, color: dashboardMuted, fontWeight: 700 }}>
            {trackConfig.bendLocationSummary || "Choose where each bend happens."}
          </div>
        </div>
      ) : null}

      <details>
        <summary
          style={{
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 900,
            color: isDark ? "#99f6e4" : "#0f766e",
          }}
        >
          Advanced Track Segments
        </summary>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))",
            gap: 10,
            marginTop: 10,
          }}
        >
          {(trackConfig.segments || DEFAULT_TRACK_CONFIG.segments).map((segment, index) => (
            <div key={segment.label || index}>
              <div style={panelLabel}>
                Segment {segment.label || String.fromCharCode(65 + index)} length ({segmentUnitLabel})
              </div>
              <input
                type="text"
                inputMode="decimal"
                style={panelInput}
                value={segment.length || ""}
                onChange={(event) => {
                  const nextSegments = normalizeTrackSegments(trackConfig.segments, segmentUnit);
                  nextSegments[index] = {
                    ...nextSegments[index],
                    length: event.target.value,
                    unit: segmentUnit,
                  };
                  updateTrack({ segmentUnit, segments: nextSegments });
                }}
              />
            </div>
          ))}
        </div>
        <div style={{ marginTop: 8, fontSize: 11.5, color: dashboardMuted }}>
          {hasSegmentOverride ? (
            <>
              Total segment length:{" "}
              <strong>
                {formatLengthWithUnit(trackConfig.totalSegmentLength || 0, segmentUnit)}
              </strong>
              {" "} - Pricing length:{" "}
              <strong>{Number(trackConfig.totalTrackLengthFt || 0).toFixed(2)} running ft</strong>
            </>
          ) : (
            "If no segments are entered, the existing default track length logic is used."
          )}
        </div>
        {layoutPreview ? (
          <div style={{ marginTop: 8, fontSize: 11.5, color: isDark ? "#99f6e4" : "#0f766e", fontWeight: 800 }}>
            Track layout: {layoutPreview}
          </div>
        ) : null}
        {formatTrackSegmentsInline(trackConfig) ? (
          <div style={{ marginTop: 4, fontSize: 11.5, color: dashboardMuted }}>
            Segments: {formatTrackSegmentsInline(trackConfig)}
          </div>
        ) : null}
      </details>

      <div>
        <div style={panelLabel}>Track notes</div>
        <input
          style={panelInput}
          value={trackConfig.notes || ""}
          placeholder="Bay window bend, ceiling bend, site note..."
          onChange={(event) => updateTrack({ notes: event.target.value })}
        />
      </div>
    </div>
  );
};

const startNewProductConfig = () => {
  setEditingProductConfigId("");
  setProductConfigDraft(
    createProductConfigDraft({
      name: "New Product",
      calculationType: "sqft_only",
      isDefault: false,
    })
  );
};

const editProductConfig = (config) => {
  const normalized = normalizeProductConfig(config);
  setEditingProductConfigId(normalized.id);
  setProductConfigDraft(normalized);
};

const duplicateProductConfig = (config) => {
  const normalized = normalizeProductConfig(config);
  const duplicateName = `${normalized.name} Copy`;
  setEditingProductConfigId("");
  setProductConfigDraft(
    createProductConfigDraft({
      ...normalized,
      id: `custom-${normalizeProductName(duplicateName).replace(/\s+/g, "-")}-${Date.now()}`,
      name: duplicateName,
      normalizedName: normalizeProductName(duplicateName),
      isDefault: false,
      createdAt: null,
      updatedAt: null,
    })
  );
};

const updateProductConfigDraftField = (field, value) => {
  setProductConfigDraft((prev) =>
    ({
      ...prev,
      [field]: value,
      ...(field === "name" ? { normalizedName: normalizeProductName(value) } : {}),
    })
  );
};

const updateProductConfigRule = (section, patch) => {
  setProductConfigDraft((prev) =>
    ({
      ...prev,
      [section]: {
        ...(prev?.[section] || {}),
        ...patch,
      },
    })
  );
};

const saveProductConfig = async () => {
  if (!workspaceUid) {
    notify("Workspace is not ready. Please try again.", "warning");
    return;
  }
  if (!canManageProductConfigs) {
    notify("You do not have permission to manage product configs.", "warning");
    return;
  }

  const validation = validateProductConfig(productConfigDraft, productConfigs);
  if (!validation.valid) {
    notify(validation.errors[0] || "Please check product settings.", "error");
    return;
  }

  const normalized = {
    ...validation.normalized,
    updatedAt: serverTimestamp(),
    updatedBy: user?.uid || user?.email || "",
    ...(editingProductConfigId
      ? {}
      : {
          createdAt: serverTimestamp(),
          createdBy: user?.uid || user?.email || "",
        }),
  };

  setSavingProductConfigId(validation.normalized.id);
  try {
    await setDoc(
      doc(
        db,
        "users",
        workspaceUid,
        PRODUCT_CONFIG_COLLECTION,
        validation.normalized.id
      ),
      normalized,
      { merge: true }
    );
    invalidateWorkspaceCollectionCache(workspaceUid, PRODUCT_CONFIG_COLLECTION);
    invalidateFirestoreReadCache(`productConfigs:${workspaceUid}`);
    productConfigsLoadedWorkspaceRef.current = "";
    const localNormalized = normalizeProductConfig({
      ...validation.normalized,
      updatedAt: new Date(),
    });
    setProductConfigs((prev) =>
      mergeProductConfigsWithDefaults(
        [...prev.filter((item) => item.id !== localNormalized.id), localNormalized],
        fabricCalcSettings
      )
    );
    setEditingProductConfigId(localNormalized.id);
    setProductConfigDraft(localNormalized);
    notify("Product configuration saved.", "success");
  } catch (error) {
    console.error("PRODUCT CONFIG SAVE FAILED", {
      code: error?.code,
      message: error?.message,
      productId: validation.normalized.id,
    });
    notify("Could not save product configuration.", "error");
  } finally {
    setSavingProductConfigId("");
  }
};

const setProductConfigActive = async (config, active) => {
  if (!workspaceUid || !config?.id) return;
  if (!canManageProductConfigs) {
    notify("You do not have permission to manage product configs.", "warning");
    return;
  }

  setSavingProductConfigId(config.id);
  try {
    await updateDoc(
      doc(db, "users", workspaceUid, PRODUCT_CONFIG_COLLECTION, config.id),
      {
        active,
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || user?.email || "",
      }
    );
    invalidateWorkspaceCollectionCache(workspaceUid, PRODUCT_CONFIG_COLLECTION);
    invalidateFirestoreReadCache(`productConfigs:${workspaceUid}`);
    productConfigsLoadedWorkspaceRef.current = "";
    setProductConfigs((prev) =>
      mergeProductConfigsWithDefaults(
        prev.map((item) =>
          item.id === config.id ? { ...item, active, updatedAt: new Date() } : item
        ),
        fabricCalcSettings
      )
    );
    notify(active ? "Product activated." : "Product deactivated.", "success");
  } catch (error) {
    console.error("PRODUCT CONFIG ACTIVE TOGGLE FAILED", {
      code: error?.code,
      message: error?.message,
      productId: config.id,
    });
    notify("Could not update product status.", "error");
  } finally {
    setSavingProductConfigId("");
  }
};

const renderProductCalculationsPage = () => {
  if (!canManageProductConfigs) {
    return (
      <div style={{ padding: 24 }}>
        <h2>Products & Calculations</h2>
        <p style={{ color: isDark ? "#94a3b8" : "#64748b" }}>
          You do not have permission to manage product settings.
        </p>
      </div>
    );
  }

  const draft = normalizeProductConfig(productConfigDraft);
  const getDraftFieldValue = (field, fallback = "") => {
    if (productConfigDraft && Object.prototype.hasOwnProperty.call(productConfigDraft, field)) {
      return getInputValue(productConfigDraft[field]);
    }
    return getInputValue(draft[field] ?? fallback);
  };
  const getDraftRuleInputValue = (section, field, fallback = "") => {
    const rawSection = productConfigDraft?.[section] || {};
    if (Object.prototype.hasOwnProperty.call(rawSection, field)) {
      return getInputValue(rawSection[field]);
    }
    return getInputValue(draft?.[section]?.[field] ?? fallback);
  };
  const previewRow = {
    productId: draft.id,
    productType: draft.name,
    productName: draft.name,
    productConfigSnapshot: buildProductConfigSnapshot(draft),
    width: 100,
    height: 100,
    fabricWidthIn: draft.fabricRules.fabricWidthIn || 54,
    mrp: draft.calculationType.includes("fabric") ? 250 : "",
    sqftRate: draft.sqftRules.defaultRate || 100,
    ratePerFeet: draft.runningFeetRules.defaultRate || 100,
    ratePerLength: draft.runningFeetRules.defaultRate || 100,
    length: 10,
    lengthUnit: draft.runningFeetRules.defaultUnit || "ft",
    ratePerPiece: draft.quantityRules.defaultRate || 1000,
    unitRate: draft.quantityRules.defaultRate || 1000,
    qty: 1,
    unit: draft.quantityRules.defaultUnit || "pcs",
    quantity: 1,
    manualAmount: 1000,
    discount: 0,
  };
  const preview = calculateProductRow(previewRow, draft, {
    stitchingRates,
    fabricCalcSettings,
  });
  const previewFabricRules = resolveFabricRulesForRow(
    previewRow,
    draft,
    fabricCalcSettings
  );
  const draftIsAccessory = isAccessoryProductConfig(draft);

  const fieldGrid = {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))",
    gap: 12,
  };
  const cardStyle = {
    borderRadius: 18,
    padding: 18,
    border: isDark ? "1px solid rgba(148,163,184,0.18)" : "1px solid #e2e8f0",
    background: isDark ? "#0f172a" : "#ffffff",
    boxShadow: isDark
      ? "0 18px 40px rgba(2,6,23,0.22)"
      : "0 18px 34px rgba(15,23,42,0.06)",
  };
  const muted = isDark ? "#94a3b8" : "#64748b";

  const renderCheckbox = (label, checked, onChange) => (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: 13,
        fontWeight: 700,
        color: isDark ? "#cbd5e1" : "#334155",
      }}
    >
      <input type="checkbox" checked={Boolean(checked)} onChange={onChange} />
      {label}
    </label>
  );

  return (
    <div style={{ display: "grid", gap: 22 }}>
      <div
        style={{
          ...cardStyle,
          background: isDark
            ? "linear-gradient(135deg,#0f172a,#111827)"
            : "linear-gradient(135deg,#ffffff,#f8fafc)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 16,
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          <div>
            <h2 style={{ margin: 0 }}>Products & Calculations</h2>
            <p style={{ margin: "8px 0 0", color: muted, maxWidth: 760 }}>
              Add custom products and choose safe calculation templates. No raw
              JavaScript formulas are allowed, so quotation totals, PDFs,
              workshop exports, and saved quotes stay stable.
            </p>
          </div>
          <Button icon={Plus} variant="primary" onClick={startNewProductConfig}>
            New Product
          </Button>
        </div>
        <div style={{ marginTop: 12, fontSize: 12, color: muted }}>
          {productConfigsLoadState.loading
            ? "Loading product configs..."
            : productConfigsLoadState.error ||
              `${productConfigs.length} product configs loaded`}
        </div>
      </div>

      <div style={{ ...cardStyle, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 820 }}>
          <thead>
            <tr style={{ color: muted, textAlign: "left", fontSize: 12 }}>
              <th style={{ padding: 10 }}>Product Name</th>
              <th style={{ padding: 10 }}>Category</th>
              <th style={{ padding: 10 }}>Calculation</th>
              <th style={{ padding: 10 }}>Status</th>
              <th style={{ padding: 10 }}>Used In</th>
              <th style={{ padding: 10 }}>Last Updated</th>
              <th style={{ padding: 10 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {productConfigs.map((config) => (
              <tr
                key={config.id}
                style={{
                  borderTop: isDark
                    ? "1px solid rgba(148,163,184,0.12)"
                    : "1px solid #e2e8f0",
                }}
              >
                <td style={{ padding: 10, fontWeight: 800 }}>{config.name}</td>
                <td style={{ padding: 10 }}>
                  {PRODUCT_CATEGORY_LABELS[config.category] || config.category}
                </td>
                <td style={{ padding: 10 }}>
                  {PRODUCT_CALCULATION_LABELS[config.calculationType] ||
                    config.calculationType}
                </td>
                <td style={{ padding: 10 }}>
                  <span
                    style={{
                      padding: "4px 8px",
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 800,
                      background: config.active ? "#dcfce7" : "#fee2e2",
                      color: config.active ? "#166534" : "#991b1b",
                    }}
                  >
                    {config.active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td style={{ padding: 10 }}>
                  {(config.usedInQuoteTypes || ["curtain"]).join(", ")}
                </td>
                <td style={{ padding: 10, color: muted }}>
                  {config.updatedAt?.toDate
                    ? config.updatedAt.toDate().toLocaleDateString()
                    : config.updatedAt instanceof Date
                    ? config.updatedAt.toLocaleDateString()
                    : "-"}
                </td>
                <td style={{ padding: 10 }}>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Button variant="secondary" onClick={() => editProductConfig(config)}>
                      Edit
                    </Button>
                    <Button variant="secondary" onClick={() => duplicateProductConfig(config)}>
                      Duplicate
                    </Button>
                    <Button
                      variant={config.active ? "danger" : "success"}
                      disabled={savingProductConfigId === config.id}
                      onClick={() => setProductConfigActive(config, !config.active)}
                    >
                      {config.active ? "Deactivate" : "Activate"}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ ...cardStyle, display: "grid", gap: 18 }}>
        <div>
          <h3 style={{ margin: 0 }}>
            {editingProductConfigId ? "Edit Product" : "Add Product"}
          </h3>
          <p style={{ margin: "6px 0 0", color: muted }}>
            Configure product behavior using controlled templates. Old quotes
            keep their saved product snapshots.
          </p>
        </div>

        <div style={fieldGrid}>
          <div>
            <label style={{ fontSize: 12, color: muted }}>Product Name</label>
            <input
              style={input}
              value={getDraftFieldValue("name")}
              onChange={(event) =>
                updateProductConfigDraftField("name", event.target.value)
              }
            />
          </div>
          <div>
            <label style={{ fontSize: 12, color: muted }}>Category</label>
            <select
              style={selectInput}
              value={draft.category}
              onChange={(event) =>
                updateProductConfigDraftField("category", event.target.value)
              }
            >
              {PRODUCT_CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {PRODUCT_CATEGORY_LABELS[category] || category}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, color: muted }}>Calculation Type</label>
            <select
              style={selectInput}
              value={draft.calculationType}
              onChange={(event) =>
                setProductConfigDraft((prev) =>
                  createProductConfigDraft({
                    ...prev,
                    calculationType: event.target.value,
                    usesFabric:
                      !isAccessoryCalculationType(event.target.value) &&
                      ["fabric_meter", "sqft_plus_fabric"].includes(event.target.value),
                    supportsLining:
                      !isAccessoryCalculationType(event.target.value) &&
                      ["fabric_meter", "sqft_plus_fabric"].includes(event.target.value),
                    category:
                      isAccessoryCalculationType(event.target.value) &&
                      (!prev?.category || ["curtain", "blind", "soft_furnishing"].includes(prev.category))
                        ? event.target.value === "running_length_rate"
                          ? "track_channel"
                          : "accessory"
                        : prev?.category,
                    requiredFields: {},
                    fabricRules: isAccessoryCalculationType(event.target.value)
                      ? { enabled: false, fullnessSource: "none" }
                      : prev?.fabricRules || {},
                    workshopRules: isAccessoryCalculationType(event.target.value)
                      ? {
                          ...(prev?.workshopRules || {}),
                          includeFabricSummary: false,
                          includeRollOptimizer: false,
                        }
                      : prev?.workshopRules || {},
                    isDefault: prev?.isDefault === true,
                  })
                )
              }
            >
              {PRODUCT_CALCULATION_TYPES.map((type) => (
                <option key={type} value={type}>
                  {PRODUCT_CALCULATION_LABELS[type]}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
          {renderCheckbox("Active", draft.active, (event) =>
            updateProductConfigDraftField("active", event.target.checked)
          )}
          {renderCheckbox("Use in Curtain Quotations", true, () => {})}
          {!draftIsAccessory
            ? renderCheckbox("Show Fabric / Material Field", draft.displayFields.showFabric, (event) =>
                updateProductConfigRule("displayFields", {
                  showFabric: event.target.checked,
                })
              )
            : null}
          {renderCheckbox("Show in Customer PDF", draft.pdfRules.showMeasurements, (event) =>
            updateProductConfigRule("pdfRules", {
              showMeasurements: event.target.checked,
              showFabric: event.target.checked,
              showSqft: event.target.checked,
              showRate: event.target.checked,
            })
          )}
        </div>

        {(draft.calculationType === "sqft_only" ||
          draft.calculationType === "sqft_plus_fabric") && (
          <div style={{ ...cardStyle, boxShadow: "none" }}>
            <h4 style={{ marginTop: 0 }}>Sqft Rules</h4>
            <div style={fieldGrid}>
              <div>
                <label style={{ fontSize: 12, color: muted }}>Default Rate/sqft</label>
                <input
                  type="number"
                  style={input}
                  value={getDraftRuleInputValue("sqftRules", "defaultRate")}
                  onChange={(event) =>
                    updateProductConfigRule("sqftRules", {
                      defaultRate: event.target.value,
                      rateSource: "row_rate",
                    })
                  }
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: muted }}>Minimum Billing Sqft</label>
                <input
                  type="number"
                  style={input}
                  value={getDraftRuleInputValue("sqftRules", "minSqft")}
                  onChange={(event) =>
                    updateProductConfigRule("sqftRules", {
                      minSqft: event.target.value,
                    })
                  }
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: muted }}>Rounding</label>
                <select
                  style={selectInput}
                  value={draft.sqftRules.rounding}
                  onChange={(event) =>
                    updateProductConfigRule("sqftRules", {
                      rounding: event.target.value,
                    })
                  }
                >
                  <option value="none">None</option>
                  <option value="ceil">Round Up</option>
                  <option value="round_2">2 decimals</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {(draft.calculationType === "fabric_meter" ||
          draft.calculationType === "sqft_plus_fabric") && (
          <div style={{ ...cardStyle, boxShadow: "none" }}>
            <h4 style={{ marginTop: 0 }}>Fabric Rules</h4>
            <div style={fieldGrid}>
              <div>
                <label style={{ fontSize: 12, color: muted }}>Fabric Field Label</label>
                <input
                  style={input}
                  value={getDraftRuleInputValue("displayFields", "fabricLabel", "Fabric")}
                  onChange={(event) =>
                    updateProductConfigRule("displayFields", {
                      fabricLabel: event.target.value,
                    })
                  }
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: muted }}>Default Fabric Width</label>
                <input
                  type="number"
                  style={input}
                  value={getDraftRuleInputValue("fabricRules", "fabricWidthIn")}
                  onChange={(event) =>
                    updateProductConfigRule("fabricRules", {
                      fabricWidthIn: event.target.value,
                      enabled: true,
                    })
                  }
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: muted }}>
                  {draft.fabricRules.fullnessSource === "stitching"
                    ? "Fallback Fullness"
                    : "Fullness"}
                </label>
                <input
                  type="number"
                  style={input}
                  value={getDraftRuleInputValue("fabricRules", "fullness")}
                  onChange={(event) =>
                    updateProductConfigRule("fabricRules", {
                      fullness: event.target.value,
                      enabled: true,
                    })
                  }
                />
                <div style={{ marginTop: 6, fontSize: 11, color: muted, lineHeight: 1.45 }}>
                  {draft.fabricRules.fullnessSource === "stitching"
                    ? "Curtain fullness normally comes from the selected stitching style. This fallback is used only if the stitching style has no fullness."
                    : draft.fabricRules.meterFormulaType === "roman_panels"
                    ? "Roman blinds usually use 1x fullness. Change only if your production method requires extra fullness."
                    : "Product-level fullness is used directly for this fabric calculation."}
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, color: muted }}>Fullness Source</label>
                <select
                  style={selectInput}
                  value={draft.fabricRules.fullnessSource || "product"}
                  onChange={(event) =>
                    updateProductConfigRule("fabricRules", {
                      fullnessSource: event.target.value,
                      enabled: true,
                    })
                  }
                >
                  <option value="stitching">Stitching Style</option>
                  <option value="product">Product Config</option>
                  <option value="none">None / 1x</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: muted }}>Top Hem (in)</label>
                <input
                  type="number"
                  style={input}
                  value={getDraftRuleInputValue("fabricRules", "topHemIn")}
                  onChange={(event) =>
                    updateProductConfigRule("fabricRules", {
                      topHemIn: event.target.value,
                      enabled: true,
                    })
                  }
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: muted }}>Bottom Hem (in)</label>
                <input
                  type="number"
                  style={input}
                  value={getDraftRuleInputValue("fabricRules", "bottomHemIn")}
                  onChange={(event) =>
                    updateProductConfigRule("fabricRules", {
                      bottomHemIn: event.target.value,
                      enabled: true,
                    })
                  }
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: muted }}>Side Allowance per Side (in)</label>
                <input
                  type="number"
                  style={input}
                  value={getDraftRuleInputValue("fabricRules", "sideAllowancePerSideIn")}
                  onChange={(event) =>
                    updateProductConfigRule("fabricRules", {
                      sideAllowancePerSideIn: event.target.value,
                      enabled: true,
                    })
                  }
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: muted }}>Panel Rounding</label>
                <select
                  style={selectInput}
                  value={draft.fabricRules.panelRounding}
                  onChange={(event) =>
                    updateProductConfigRule("fabricRules", {
                      panelRounding: event.target.value,
                      enabled: true,
                    })
                  }
                >
                  <option value="ceil_full">Ceil full panel</option>
                  <option value="ceil_half">Ceil half panel</option>
                  <option value="none">None</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: muted }}>Meter Formula</label>
                <select
                  style={selectInput}
                  value={draft.fabricRules.meterFormulaType}
                  onChange={(event) =>
                    updateProductConfigRule("fabricRules", {
                      meterFormulaType: event.target.value,
                      enabled: true,
                    })
                  }
                >
                  <option value="curtain_panels">Curtain panels</option>
                  <option value="roman_panels">Roman panels</option>
                  <option value="simple_length">Simple length</option>
                </select>
              </div>
              <div
                style={{
                  gridColumn: isMobile ? "auto" : "1 / -1",
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "1fr"
                    : "repeat(3, minmax(0, 1fr))",
                  gap: 10,
                  padding: 12,
                  borderRadius: 14,
                  background: isDark ? "rgba(15,23,42,0.7)" : "#f8fafc",
                  border: isDark
                    ? "1px solid rgba(148,163,184,0.18)"
                    : "1px solid #e2e8f0",
                }}
              >
                <div>
                  <div style={{ fontSize: 11, color: muted }}>Total Width Deduction</div>
                  <strong>{previewFabricRules.usableWidthDeductionIn} in</strong>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: muted }}>Usable Fabric Width</div>
                  <strong>{previewFabricRules.usableFabricWidthIn} in</strong>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: muted }}>Total Drop Allowance</div>
                  <strong>{previewFabricRules.dropAllowanceIn} in</strong>
                </div>
              </div>
            </div>
          </div>
        )}

        {draft.calculationType === "running_length_rate" && (
          <div style={{ ...cardStyle, boxShadow: "none" }}>
            <h4 style={{ marginTop: 0 }}>Running Length Rules</h4>
            <div style={fieldGrid}>
              <div>
                <label style={{ fontSize: 12, color: muted }}>Default Rate per Length</label>
                <input
                  type="number"
                  style={input}
                  value={getDraftRuleInputValue("runningFeetRules", "defaultRate")}
                  onChange={(event) =>
                    updateProductConfigRule("runningFeetRules", {
                      defaultRate: event.target.value,
                      enabled: true,
                    })
                  }
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: muted }}>Default Length Unit</label>
                <select
                  style={selectInput}
                  value={draft.runningFeetRules.defaultUnit || "ft"}
                  onChange={(event) =>
                    updateProductConfigRule("runningFeetRules", {
                      defaultUnit: event.target.value,
                      formula: "manual_feet",
                      enabled: true,
                    })
                  }
                >
                  <option value="ft">ft</option>
                  <option value="m">m</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {draft.calculationType === "quantity_rate" && (
          <div style={{ ...cardStyle, boxShadow: "none" }}>
            <h4 style={{ marginTop: 0 }}>Quantity Rules</h4>
            <div style={fieldGrid}>
              <div>
                <label style={{ fontSize: 12, color: muted }}>Default Unit Rate</label>
                <input
                  type="number"
                  style={input}
                  value={getDraftRuleInputValue("quantityRules", "defaultRate")}
                  onChange={(event) =>
                    updateProductConfigRule("quantityRules", {
                      defaultRate: event.target.value,
                      enabled: true,
                    })
                  }
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: muted }}>Default Unit</label>
                <select
                  style={selectInput}
                  value={draft.quantityRules.defaultUnit || "pcs"}
                  onChange={(event) =>
                    updateProductConfigRule("quantityRules", {
                      defaultUnit: event.target.value,
                      enabled: true,
                    })
                  }
                >
                  <option value="pcs">pcs</option>
                  <option value="set">set</option>
                  <option value="nos">nos</option>
                </select>
              </div>
            </div>
          </div>
        )}

        <div style={{ ...cardStyle, boxShadow: "none" }}>
          <h4 style={{ marginTop: 0 }}>Workshop / Optimizer</h4>
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
            {renderCheckbox(
              "Include in Workshop PDF",
              draft.workshopRules.includeInWorkshopPdf,
              (event) =>
                updateProductConfigRule("workshopRules", {
                  includeInWorkshopPdf: event.target.checked,
                })
            )}
            {!draftIsAccessory
              ? renderCheckbox(
                  "Include Fabric Summary",
                  draft.workshopRules.includeFabricSummary,
                  (event) =>
                    updateProductConfigRule("workshopRules", {
                      includeFabricSummary: event.target.checked,
                    })
                )
              : null}
            {!draftIsAccessory
              ? renderCheckbox(
                  "Include in Roll Optimizer",
                  draft.workshopRules.includeRollOptimizer,
                  (event) =>
                    updateProductConfigRule("workshopRules", {
                      includeRollOptimizer: event.target.checked,
                    })
                )
              : null}
          </div>
          {draftIsAccessory ? (
            <div style={{ marginTop: 10, fontSize: 12, color: muted, lineHeight: 1.5 }}>
              Accessory products do not enter fabric summary, half-piece reuse,
              or the roll optimizer.
            </div>
          ) : null}
        </div>

        <div
          style={{
            ...cardStyle,
            boxShadow: "none",
            background: isDark ? "#020617" : "#f8fafc",
          }}
        >
          <h4 style={{ marginTop: 0 }}>Live Preview</h4>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", color: muted }}>
            {!draftIsAccessory ? (
              <>
                <span>Width: 100 in</span>
                <span>Height: 100 in</span>
                {draft.displayFields.showSqft ? (
                  <span>Sqft: {preview.billableSqft.toFixed(2)}</span>
                ) : null}
                {draft.displayFields.showFabricMeters ? (
                  <span>Fabric: {preview.fabricMeters.toFixed(2)} m</span>
                ) : null}
              </>
            ) : draft.calculationType === "running_length_rate" ? (
              <>
                <span>Length: {preview.runningLength || 10} {draft.runningFeetRules.defaultUnit || "ft"}</span>
                <span>Rate: {currencySymbol(currency)} {Number(preview.ratePerLength || 0).toFixed(2)}</span>
              </>
            ) : draft.calculationType === "quantity_rate" ? (
              <>
                <span>Qty: {preview.quantity || 1} {draft.quantityRules.defaultUnit || "pcs"}</span>
                <span>Unit Rate: {currencySymbol(currency)} {Number(preview.unitRate || 0).toFixed(2)}</span>
              </>
            ) : (
              <span>Manual amount product</span>
            )}
            <strong style={{ color: isDark ? "#bfdbfe" : "#1d4ed8" }}>
              Line Total: {currencySymbol(currency)} {preview.rowTotal.toFixed(2)}
            </strong>
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <Button
            icon={Save}
            variant="primary"
            onClick={saveProductConfig}
            disabled={Boolean(savingProductConfigId)}
          >
            {savingProductConfigId ? "Saving..." : "Save Product"}
          </Button>
          <Button variant="secondary" onClick={startNewProductConfig}>
            Clear Form
          </Button>
        </div>
      </div>
    </div>
  );
};

const compactCardStyle = {
  borderRadius: 18,
  border: isDark
    ? "1px solid rgba(148,163,184,0.18)"
    : "1px solid rgba(226,232,240,0.95)",
  background: isDark ? "#0f172a" : "#ffffff",
  boxShadow: isDark
    ? "0 18px 40px rgba(2,6,23,0.25)"
    : "0 18px 36px rgba(15,23,42,0.06)",
  padding: isMobile ? 14 : 18,
};

const sectionLabelStyle = {
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: isDark ? "#94a3b8" : "#64748b",
  marginBottom: 8,
};

const rowActionCellStyle = {
  minWidth: isMobile ? 84 : 76,
  width: isMobile ? 84 : 76,
  display: "flex",
  gap: 6,
  alignItems: "center",
  justifyContent: "center",
  whiteSpace: "nowrap",
};

const renderRowActionIconButton = ({
  label,
  title = label,
  color,
  icon: Icon,
  onClick,
  disabled = false,
}) => {
  const size = isMobile ? 36 : 32;

  return (
    <button
      type="button"
      className="row-action-btn"
      title={title}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      style={{
        width: size,
        height: size,
        minWidth: size,
        borderRadius: 9,
        border: "none",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: color,
        color: "#ffffff",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        lineHeight: 1,
        padding: 0,
        boxShadow: isDark
          ? "0 8px 18px rgba(2,6,23,0.24)"
          : "0 8px 18px rgba(15,23,42,0.12)",
      }}
    >
      <Icon size={isMobile ? 18 : 16} strokeWidth={2.8} aria-hidden="true" />
    </button>
  );
};

const rowSummaryKey = (row, index) =>
  getWorkshopRowStableId(row) || `curtain-row-${index}`;

const renderCurtainCompactRow = (r, i) => {
  const c = getCurtainProductionAdjustedRowCalc(r, i);
  const rowRules = getRowProductRules(r);
  const orderedFabricAudit = getCurtainOrderedFabricDisplayTrace(r, i, c);
  const orderedFabricTooltip = getOrderedFabricTooltipText(orderedFabricAudit);
  const requiredFields = rowRules.requiredFields || {};
  const rateMeta = getRowRateFieldMeta(r);
  const secondaryMeta = getRowSecondaryFieldMeta(r);
  const fabricInputVisible = shouldShowFabricInput(r, rowRules.config);
  const fabricInputEnabled = shouldEnableFabricInput(r, rowRules.config);
  const fabricInputLabel = getFabricInputLabel(rowRules.config);
  const showRepeatControls = Boolean(rowRules.config?.fabricRules?.enabled);
  const rowKey = rowSummaryKey(r, i);
  const rowId = rowKey;
  const isExpanded = expandedCurtainRows[rowKey] ?? i === 0;
  const fabricKey =
    normalizeFabricKey(getCurtainRowFabricKey(r)) ||
    safeFabricKey(r.fabric);
  const currentRepeat = repeatSettings?.[fabricKey] || {};
  const summaryTitle = r.item || `Item ${i + 1}`;
  const stitchLabel = getCurtainRowStitchLabel(r, stitchingRates, rowRules.config);
  const rowTrackConfig = getCurtainTrackConfigForUi(r, c);
  const showTrackConfigPanel = shouldShowCurtainTrackConfigPanel(r, c);
  const rowDiscountScope = c.discountScope || getRowDiscountScope(r, rowRules.config, {
    hasFabricMeterComponent: Boolean(c.isCurtain || c.isRoman),
    fabricGrossAmount: c.fabricGrossAmount || 0,
  });
  const discountInputEnabled = rowDiscountScope !== "none";
  const discountLabel = rowDiscountScope === "fabric_only" ? "Fabric Disc %" : "Disc %";
  const discountTooltip =
    rowDiscountScope === "fabric_only"
      ? "Applies only to fabric amount."
      : rowDiscountScope === "line_total"
      ? "Applies to this line amount."
      : "Discount is disabled for this product.";

  return (
    <div key={rowKey} style={compactCardStyle}>
      <button
        type="button"
        onClick={() => toggleCurtainRowExpanded(rowKey)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
          border: "none",
          background: "transparent",
          padding: 0,
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <div style={{ display: "grid", gap: 6, flex: 1 }}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              alignItems: "center",
            }}
          >
            <span
              style={{
                fontSize: 16,
                fontWeight: 800,
                color: isDark ? "#f8fafc" : "#0f172a",
              }}
            >
              {summaryTitle}
            </span>
            <span
              style={{
                padding: "4px 8px",
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 700,
                background: isDark ? "rgba(37,99,235,0.16)" : "#eff6ff",
                color: "#2563eb",
              }}
            >
              {r.productType || "Curtains"}
            </span>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
              fontSize: 13,
              color: isDark ? "#cbd5e1" : "#475569",
            }}
          >
            <span>
              {displayValue(r.width, unit)} x {displayValue(r.height, unit)} {unitLabel(unit)}
            </span>
            <span>{getDisplayFabricLabel(r)}</span>
            <span>{stitchLabel}</span>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
              fontSize: 12,
              color: isDark ? "#94a3b8" : "#64748b",
            }}
          >
            <span title={orderedFabricTooltip}>
              Ordered Fabric {c.fabricMeters.toFixed(2)} m{" "}
              <span
                aria-hidden="true"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 15,
                  height: 15,
                  borderRadius: "50%",
                  background: isDark ? "rgba(96,165,250,0.2)" : "#dbeafe",
                  color: isDark ? "#bfdbfe" : "#1d4ed8",
                  fontSize: 10,
                  fontWeight: 900,
                }}
              >
                i
              </span>
            </span>
            {c.lining?.enabled ? (
              <span>
                Lining {getCurtainLiningLabel(c.lining)}{" "}
                {Number(c.lining.billableMeters || 0).toFixed(2)} m
              </span>
            ) : null}
            {rowTrackConfig?.isBended ? (
              <span>
                Track {getBendedTrackShapeMeta(rowTrackConfig.shape).label},{" "}
                {Number(rowTrackConfig.bendCount || 0).toFixed(0)} bend
                {Number(rowTrackConfig.bendCount || 0) === 1 ? "" : "s"}
                {rowTrackConfig.bendLocationSummary
                  ? ` - ${rowTrackConfig.bendLocationSummary}`
                  : ""}
                {formatTrackSegmentsInline(rowTrackConfig)
                  ? ` - ${formatTrackSegmentsInline(rowTrackConfig)}`
                  : ""}
              </span>
            ) : null}
            <span>Total {currencySymbol(currency)} {c.rowTotal.toFixed(2)}</span>
          </div>
        </div>

        <div
          style={{
            width: 36,
            height: 36,
            minWidth: 36,
            borderRadius: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: isDark ? "#111827" : "#f1f5f9",
            color: isDark ? "#e2e8f0" : "#334155",
          }}
        >
          {isExpanded ? (
            <ChevronUp size={18} strokeWidth={2.25} />
          ) : (
            <ChevronDown size={18} strokeWidth={2.25} />
          )}
        </div>
      </button>

      {isExpanded ? (
        <div style={{ display: "grid", gap: 18, marginTop: 18 }}>
          <div style={{ display: "grid", gap: 14 }}>
            <div style={sectionLabelStyle}>Product & Item</div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : "minmax(0, 1fr) minmax(180px, 220px)",
                gap: 12,
              }}
            >
              <div>
                <label style={{ fontSize: 12, opacity: 0.75 }}>Item</label>
                <input
                  style={input}
                  value={r.item}
                  onChange={(e) => updateField(rowId, "item", e.target.value)}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, opacity: 0.75 }}>Product</label>
                {(() => {
                  const savedProductOption = getCurtainRowSavedProductOption(r);
                  return (
                <select
                  style={selectInput}
                  value={getCurtainRowProductSelectValue(r)}
                  onChange={(e) => updateField(rowId, "productType", e.target.value)}
                >
                  {savedProductOption ? (
                    <option value={savedProductOption.value}>{savedProductOption.label}</option>
                  ) : null}
                  {curtainProductOptions.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
                  );
                })()}
              </div>
            </div>
          </div>

          <div style={{ display: "grid", gap: 14 }}>
            <div style={sectionLabelStyle}>Dimensions</div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "repeat(2, minmax(0, 1fr))"
                  : "repeat(3, minmax(0, 1fr))",
                gap: 12,
              }}
            >
              <div>
                <label style={{ fontSize: 12, opacity: 0.75 }}>
                  Width ({unitLabel(unit)})
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  step="0.01"
                  style={input}
                  value={getDimensionInputValueForUnit(r.width, unit)}
                  onChange={(e) => {
                    const v = e.target.value;
                    const storedValue = getStoredDimensionValueFromInput(v, unit);
                    if (storedValue == null) return;
                    updateField(rowId, "width", storedValue);
                  }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, opacity: 0.75 }}>
                  Height ({unitLabel(unit)})
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  step="0.01"
                  style={input}
                  value={getDimensionInputValueForUnit(r.height, unit)}
                  onChange={(e) => {
                    const v = e.target.value;
                    const storedValue = getStoredDimensionValueFromInput(v, unit);
                    if (storedValue == null) return;
                    updateField(rowId, "height", storedValue);
                  }}
                />
              </div>
              <div style={{ gridColumn: isMobile ? "1 / -1" : "auto" }}>
                <label style={{ fontSize: 12, opacity: 0.75 }}>Fabric Width (in)</label>
                <input
                  type="text"
                  style={input}
                  value={
                    r.fabricWidthIn === "" || r.fabricWidthIn == null
                      ? ""
                      : r.fabricWidthIn
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "") {
                      updateField(rowId, "fabricWidthIn", "");
                      return;
                    }
                    if (v === "-" || v === "." || v === "-.") return;
                    if (!isPermittedDecimalInput(v)) return;
                    updateField(rowId, "fabricWidthIn", v);
                  }}
                  disabled={!requiredFields.fabricWidth}
                />
              </div>
            </div>
          </div>

          {fabricInputVisible || showRepeatControls ? (
          <div style={{ display: "grid", gap: 14 }}>
            <div style={sectionLabelStyle}>
              {showRepeatControls ? "Fabric & Repeat" : fabricInputLabel}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : showRepeatControls
                  ? "minmax(0, 1fr) minmax(0, 1fr)"
                  : "minmax(0, 1fr)",
                gap: 12,
              }}
            >
              {fabricInputVisible ? (
              <div style={{ display: "grid", gap: 8 }}>
                <label style={{ fontSize: 12, opacity: 0.75 }}>{fabricInputLabel}</label>
                {renderFabricCatalogueCombobox({
                  row: r,
                  rowId,
                  rowIndex: i,
                  label: fabricInputLabel || "Fabric / Code",
                  disabled: !fabricInputEnabled,
                  compact: true,
                })}
              </div>
              ) : null}
              {showRepeatControls ? (
              <div
                style={{
                  borderRadius: 14,
                  border: isDark
                    ? "1px solid rgba(148,163,184,0.16)"
                    : "1px solid #dbe4f0",
                  padding: 12,
                  background: isDark ? "#020617" : "#f8fafc",
                  display: "grid",
                  gap: 10,
                }}
              >
                <label style={{ fontSize: 12, fontWeight: 700 }}>
                  <input
                    type="checkbox"
                    checked={currentRepeat.verticalEnabled || false}
                    onChange={(e) =>
                      updateFabricRepeatSettings(fabricKey, {
                        verticalEnabled: e.target.checked,
                        verticalRepeatInch: currentRepeat.verticalRepeatInch || 0,
                      })
                    }
                  />
                  {" "}Vertical Repeat
                </label>
                {currentRepeat.verticalEnabled ? (
                  <input
                    type="number"
                    style={input}
                    placeholder="Repeat (in)"
                    value={currentRepeat.verticalRepeatInch || ""}
                    onChange={(e) =>
                      updateFabricRepeatSettings(fabricKey, {
                        verticalEnabled: true,
                        verticalRepeatInch: e.target.value,
                      })
                    }
                  />
                ) : null}

                <label style={{ fontSize: 12, fontWeight: 700 }}>
                  <input
                    type="checkbox"
                    checked={currentRepeat.horizontalEnabled || false}
                    onChange={(e) =>
                      updateFabricRepeatSettings(fabricKey, {
                        horizontalEnabled: e.target.checked,
                        horizontalRepeatInch: currentRepeat.horizontalRepeatInch || 0,
                      })
                    }
                  />
                  {" "}Horizontal Repeat
                </label>
                {currentRepeat.horizontalEnabled ? (
                  <input
                    type="number"
                    style={input}
                    placeholder="Repeat (in)"
                    value={currentRepeat.horizontalRepeatInch || ""}
                    onChange={(e) =>
                      updateFabricRepeatSettings(fabricKey, {
                        horizontalEnabled: true,
                        horizontalRepeatInch: e.target.value,
                      })
                    }
                  />
                ) : null}
              </div>
              ) : null}
            </div>
          </div>
          ) : null}

          <div style={{ display: "grid", gap: 14 }}>
            <div style={sectionLabelStyle}>Stitching & Pricing</div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "repeat(2, minmax(0, 1fr))"
                  : "repeat(4, minmax(0, 1fr))",
                gap: 12,
              }}
            >
              {requiredFields.mrpPerMeter ? (
                <div>
                  <label style={{ fontSize: 12, opacity: 0.75 }}>MRP/m</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    style={input}
                    value={r.mrp ?? ""}
                    onChange={(e) => updateField(rowId, "mrp", e.target.value)}
                    onBlur={(e) =>
                      handleCurtainRowMrpBlur(rowId, {
                        ...r,
                        mrp: e.target.value,
                        mrpPerMeter: e.target.value,
                      })
                    }
                  />
                </div>
              ) : null}
              {!rateMeta.hidden ? (
                <div>
                  <label style={{ fontSize: 12, opacity: 0.75 }}>
                    {rateMeta.label}
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    disabled={rateMeta.disabled}
                    style={{
                      ...input,
                      opacity: rateMeta.disabled ? 0.35 : 1,
                    }}
                    value={rateMeta.field ? r[rateMeta.field] ?? "" : ""}
                    onChange={(e) => {
                      if (!rateMeta.field) return;
                      updateField(rowId, rateMeta.field, e.target.value);
                    }}
                  />
                </div>
              ) : null}
              <div>
                <label style={{ fontSize: 12, opacity: 0.75 }}>{discountLabel}</label>
                <input
                  type="text"
                  inputMode="decimal"
                  disabled={!discountInputEnabled}
                  title={discountTooltip}
                  style={{
                    ...input,
                    opacity: discountInputEnabled ? 1 : 0.45,
                  }}
                  value={r.discount ?? ""}
                  onChange={(e) => updateField(rowId, "discount", e.target.value)}
                />
                <div style={{ marginTop: 4, fontSize: 10.5, color: dashboardMuted }}>
                  {discountTooltip}
                </div>
                {!discountInputEnabled ? (
                  <div style={{ marginTop: 4, fontSize: 10.5, color: dashboardMuted }}>
                    Discount disabled
                  </div>
                ) : null}
              </div>
              {requiredFields.stitching ? (
                <div style={{ gridColumn: isMobile ? "1 / -1" : "auto" }}>
                  <label style={{ fontSize: 12, opacity: 0.75 }}>Stitch</label>
                  <select
                    style={selectInput}
                    value={r.stitchingStyle || ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      updateField(rowId, "stitchingStyle", val);
                      if (val !== "Eyelet (Custom)") {
                        updateField(rowId, "customStitchRate", "");
                      }
                    }}
                  >
                    <option value="">Select</option>
                    {Object.keys(stitchingRates).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              {!secondaryMeta.disabled && secondaryMeta.field !== "customStitchRate" ? (
                <div style={{ gridColumn: isMobile ? "1 / -1" : "auto" }}>
                  <label style={{ fontSize: 12, opacity: 0.75 }}>
                    {secondaryMeta.label}
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    style={input}
                    value={r[secondaryMeta.field] ?? ""}
                    onChange={(e) =>
                      updateField(rowId, secondaryMeta.field, e.target.value)
                    }
                  />
                </div>
              ) : isCurtain(r.productType) && isEyeletCustom(r) ? (
                <div style={{ gridColumn: isMobile ? "1 / -1" : "auto" }}>
                  <label style={{ fontSize: 12, opacity: 0.75 }}>Custom Stitch Rate</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    style={input}
                    value={r.customStitchRate}
                    onChange={(e) =>
                      updateField(rowId, "customStitchRate", e.target.value)
                    }
                  />
                </div>
              ) : null}
            </div>
          </div>

          {showTrackConfigPanel ? (
            <div style={{ display: "grid", gap: 14 }}>
              <div style={sectionLabelStyle}>Track Shape</div>
              {renderCurtainTrackConfigPanel(r, c, rowId)}
            </div>
          ) : null}

          {curtainRowSupportsLining(r, c) ? (
            <div style={{ display: "grid", gap: 14 }}>
              <div style={sectionLabelStyle}>Lining</div>
              {renderCurtainLiningPanel(r, c, rowId)}
            </div>
          ) : null}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))",
              gap: 12,
              padding: 14,
              borderRadius: 16,
              background: isDark ? "#020617" : "#f8fafc",
              border: isDark
                ? "1px solid rgba(148,163,184,0.16)"
                : "1px solid #e2e8f0",
            }}
          >
            <div>
              <div style={sectionLabelStyle}>Ordered Fabric</div>
              <div
                title={orderedFabricTooltip}
                style={{
                  fontSize: 16,
                  fontWeight: 800,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span>{c.fabricMeters.toFixed(2)} m</span>
                <span
                  aria-hidden="true"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    background: isDark ? "rgba(96,165,250,0.2)" : "#dbeafe",
                    color: isDark ? "#bfdbfe" : "#1d4ed8",
                    fontSize: 10,
                    fontWeight: 900,
                  }}
                >
                  i
                </span>
              </div>
            </div>
            <div>
              <div style={sectionLabelStyle}>Pieces</div>
              <div style={{ fontSize: 16, fontWeight: 800 }}>
                {Number(c.totalPieces || 0).toFixed(2)}
              </div>
            </div>
            <div>
              <div style={sectionLabelStyle}>Total</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#2563eb" }}>
                {currencySymbol(currency)} {c.rowTotal.toFixed(2)}
              </div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
            }}
          >
            <div style={{ ...rowActionCellStyle, justifyContent: "flex-start" }}>
              {renderRowActionIconButton({
                label: "Add row below",
                title: "Add row below",
                color: "#16a34a",
                icon: Plus,
                onClick: () => insertRowAfter(i),
              })}
              {renderRowActionIconButton({
                label: "Delete row",
                title: "Delete row",
                color: "#dc2626",
                icon: Trash2,
                onClick: () => {
                  removeQuoteRow(getWorkshopRowStableId(r));
                },
              })}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const renderCurtainAccessoryRow = (r, i, displayIndex = 0) => {
  const c = getCurtainProductionAdjustedRowCalc(r, i);
  const rowRules = getRowProductRules(r);
  const calculationType = rowRules.calculationType;
  const isLengthRow = calculationType === "running_length_rate";
  const isManualRow = calculationType === "manual_amount";
  const accessoryUnit =
    r.unit ||
    rowRules.config?.quantityRules?.defaultUnit ||
    (isManualRow ? "nos" : "pcs");
  const lengthUnit =
    r.lengthUnit || rowRules.config?.runningFeetRules?.defaultUnit || "ft";
  const rowId = getWorkshopRowStableId(r) || `accessory-${i}`;
  const rowDiscountScope = c.discountScope || getRowDiscountScope(r, rowRules.config, {
    hasFabricMeterComponent: false,
    fabricGrossAmount: c.fabricGrossAmount || 0,
  });
  const discountInputEnabled = rowDiscountScope !== "none";
  const discountTooltip =
    rowDiscountScope === "fabric_only"
      ? "Applies only to fabric amount."
      : rowDiscountScope === "line_total"
      ? "Applies to this line amount."
      : "Discount is disabled for this product.";

  return (
    <div
      key={getWorkshopRowStableId(r) || `accessory-${i}`}
      style={{
        borderRadius: 18,
        border: isDark
          ? "1px solid rgba(96,165,250,0.22)"
          : "1px solid rgba(191,219,254,0.95)",
        background: isDark
          ? "linear-gradient(135deg,rgba(15,23,42,0.96),rgba(30,41,59,0.86))"
          : "linear-gradient(135deg,#ffffff,#eff6ff)",
        padding: isCompact ? 14 : 16,
        display: "grid",
        gap: 12,
        boxShadow: isDark
          ? "0 16px 34px rgba(2,6,23,0.28)"
          : "0 16px 34px rgba(37,99,235,0.08)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <div>
          <div style={{ fontSize: 11, fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase", color: "#2563eb" }}>
            Add-on #{displayIndex + 1}
          </div>
          <div style={{ fontSize: 18, fontWeight: 900, marginTop: 2 }}>
            {r.item || r.productName || "Accessory"}
          </div>
        </div>
        <div
          style={{
            borderRadius: 999,
            padding: "7px 12px",
            background: isDark ? "rgba(37,99,235,0.18)" : "#dbeafe",
            color: "#2563eb",
            fontSize: 12,
            fontWeight: 900,
          }}
        >
          {PRODUCT_CALCULATION_LABELS[calculationType] || calculationType}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isCompact
            ? "1fr"
            : "minmax(130px,1fr) minmax(160px,1.2fr) minmax(160px,1.4fr)",
          gap: 10,
        }}
      >
        <div>
          <label style={{ fontSize: 12, color: dashboardMuted }}>Item / Location</label>
          <input
            style={input}
            value={r.item ?? ""}
            onChange={(event) => updateField(rowId, "item", event.target.value)}
            placeholder="Living Room"
          />
        </div>
        <div>
          <label style={{ fontSize: 12, color: dashboardMuted }}>Product</label>
          {(() => {
            const savedProductOption = getCurtainRowSavedProductOption(r);
            return (
              <select
                style={selectInput}
                value={getCurtainRowProductSelectValue(r)}
                onChange={(event) => updateField(rowId, "productType", event.target.value)}
              >
                {savedProductOption ? (
                  <option value={savedProductOption.value}>{savedProductOption.label}</option>
                ) : null}
                {curtainProductOptions.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
            );
          })()}
        </div>
        <div>
          <label style={{ fontSize: 12, color: dashboardMuted }}>Description / Model / Code</label>
          <input
            style={input}
            value={r.description ?? r.modelCode ?? ""}
            onChange={(event) => {
              updateField(rowId, "description", event.target.value);
              updateField(rowId, "modelCode", event.target.value);
            }}
            placeholder="Model, code, note"
          />
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isCompact
            ? "repeat(2,minmax(0,1fr))"
            : "repeat(7,minmax(0,1fr))",
          gap: 10,
          alignItems: "end",
        }}
      >
        {isLengthRow ? (
          <>
            <div>
              <label style={{ fontSize: 12, color: dashboardMuted }}>Length</label>
              <input
                type="text"
                inputMode="decimal"
                style={input}
                value={r.length ?? ""}
                onChange={(event) => updateField(rowId, "length", event.target.value)}
                placeholder="30"
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: dashboardMuted }}>Unit</label>
              <select
                style={selectInput}
                value={lengthUnit}
                onChange={(event) => updateField(rowId, "lengthUnit", event.target.value)}
              >
                <option value="ft">ft</option>
                <option value="m">m</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: dashboardMuted }}>Rate</label>
              <input
                type="text"
                inputMode="decimal"
                style={input}
                value={r.ratePerLength ?? ""}
                onChange={(event) => updateField(rowId, "ratePerLength", event.target.value)}
                placeholder="Rate"
              />
            </div>
          </>
        ) : isManualRow ? (
          <>
            <div>
              <label style={{ fontSize: 12, color: dashboardMuted }}>Qty</label>
              <input
                type="text"
                inputMode="decimal"
                style={input}
                value={r.qty ?? r.quantity ?? 1}
                onChange={(event) => {
                  updateField(rowId, "qty", event.target.value);
                  updateField(rowId, "quantity", event.target.value);
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: dashboardMuted }}>Unit</label>
              <input
                style={input}
                value={accessoryUnit}
                onChange={(event) => updateField(rowId, "unit", event.target.value)}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: dashboardMuted }}>Amount</label>
              <input
                type="text"
                inputMode="decimal"
                style={input}
                value={r.manualAmount ?? ""}
                onChange={(event) => updateField(rowId, "manualAmount", event.target.value)}
              />
            </div>
          </>
        ) : (
          <>
            <div>
              <label style={{ fontSize: 12, color: dashboardMuted }}>Qty</label>
              <input
                type="text"
                inputMode="decimal"
                style={input}
                value={r.qty ?? r.quantity ?? ""}
                onChange={(event) => {
                  updateField(rowId, "qty", event.target.value);
                  updateField(rowId, "quantity", event.target.value);
                }}
                placeholder="1"
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: dashboardMuted }}>Unit</label>
              <input
                style={input}
                value={accessoryUnit}
                onChange={(event) => updateField(rowId, "unit", event.target.value)}
                placeholder="pcs"
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: dashboardMuted }}>Unit Rate</label>
              <input
                type="text"
                inputMode="decimal"
                style={input}
                value={r.unitRate ?? ""}
                onChange={(event) => updateField(rowId, "unitRate", event.target.value)}
                placeholder="Rate"
              />
            </div>
          </>
        )}

        <div>
          <label style={{ fontSize: 12, color: dashboardMuted }}>
            {rowDiscountScope === "fabric_only" ? "Fabric Disc %" : "Disc %"}
          </label>
          <input
            type="text"
            inputMode="decimal"
            disabled={!discountInputEnabled}
            title={discountTooltip}
            style={{
              ...input,
              opacity: discountInputEnabled ? 1 : 0.45,
            }}
            value={r.discount ?? ""}
            onChange={(event) => updateField(rowId, "discount", event.target.value)}
            placeholder="0"
          />
          <div style={{ marginTop: 4, fontSize: 10.5, color: dashboardMuted }}>
            {discountTooltip}
          </div>
        </div>
        <div>
          <label style={{ fontSize: 12, color: dashboardMuted }}>GST</label>
          <select
            style={selectInput}
            value={String(resolveCurtainAccessoryRowGstRate(r))}
            onChange={(event) => updateField(rowId, "gstRate", event.target.value)}
          >
            {[0, 5, 12, 18].map((rate) => (
              <option key={rate} value={rate}>
                {rate === 0 ? "No GST" : `${rate}%`}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 12, color: dashboardMuted }}>Total</label>
          <div
            style={{
              ...input,
              minHeight: 36,
              display: "flex",
              alignItems: "center",
              fontWeight: 900,
              color: "#2563eb",
              background: isDark ? "rgba(15,23,42,0.5)" : "#ffffff",
            }}
          >
            {currencySymbol(currency)} {Number(c.rowTotal || 0).toFixed(2)}
          </div>
        </div>
        <div style={{ ...rowActionCellStyle, justifyContent: "flex-start" }}>
          {renderRowActionIconButton({
            label: "Add accessory row",
            title: "Add accessory row",
            color: "#16a34a",
            icon: Plus,
            onClick: addCurtainAccessoryRow,
          })}
          {renderRowActionIconButton({
            label: "Delete row",
            title: "Delete row",
            color: "#dc2626",
            icon: Trash2,
            onClick: () => {
              removeQuoteRow(getWorkshopRowStableId(r));
            },
          })}
        </div>
      </div>
    </div>
  );
};


// === Column Width Presets (global inside component) ===
const colItem     = { width: 120 };
const colProduct  = { width: 130 };
const colFabric   = { width: 210 };
const colXS       = { width: 56 };
const colS        = { width: 80 };
const colWH       = { width: 70 };
const colM        = { width: 70 };
const colStitch   = { width: 130 };
const colLining   = { width: 80 };
const colFabricM  = { width: 80 };
const colTotal    = { width: 95 };
const colActions  = { width: 76 };
const curtainTableMinWidth = 1320;
const stickyTotalHeaderStyle = {
  position: "sticky",
  right: colActions.width,
  zIndex: 6,
  background: isDark ? "#111827" : "#f8fafc",
  boxShadow: isDark
    ? "-10px 0 18px rgba(2,6,23,0.3)"
    : "-10px 0 18px rgba(15,23,42,0.08)",
};
const stickyActionsHeaderStyle = {
  position: "sticky",
  right: 0,
  zIndex: 7,
  background: isDark ? "#111827" : "#f8fafc",
};
const getStickyTotalCellStyle = (background) => ({
  position: "sticky",
  right: colActions.width,
  zIndex: 4,
  background,
  boxShadow: isDark
    ? "-10px 0 18px rgba(2,6,23,0.3)"
    : "-10px 0 18px rgba(15,23,42,0.08)",
});
const getStickyActionsCellStyle = (background) => ({
  position: "sticky",
  right: 0,
  zIndex: 5,
  background,
});


  /* --------------------------- PDF HELPERS / UI COMING NEXT --------------------------- */
  /* --------------------------- PDF HELPERS --------------------------- */

const blobToDataURL = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

const loadPdfImageElement = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = (error) => reject(error);
    img.src = src;
  });

const buildPdfImageAsset = async (
  source,
  {
    maxWidth = 360,
    maxHeight = 360,
    format = "JPEG",
    quality = 0.72,
    backgroundColor = "#ffffff",
  } = {}
) => {
  if (!source) return null;

  try {
    const dataUrl = String(source).startsWith("data:")
      ? source
      : await (async () => {
          const res = await fetch(source);
          if (!res.ok) {
            throw new Error(`Image request failed with status ${res.status}`);
          }
          const blob = await res.blob();
          return blobToDataURL(blob);
        })();

    const img = await loadPdfImageElement(dataUrl);
    const naturalWidth = Number(img.naturalWidth || img.width || 1);
    const naturalHeight = Number(img.naturalHeight || img.height || 1);
    const scale = Math.min(
      1,
      maxWidth / naturalWidth,
      maxHeight / naturalHeight
    );
    const targetWidth = Math.max(1, Math.round(naturalWidth * scale));
    const targetHeight = Math.max(1, Math.round(naturalHeight * scale));

    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext("2d");

    if (!ctx) return null;

    if (String(format).toUpperCase() === "JPEG") {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, targetWidth, targetHeight);
    }

    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

    const mimeType =
      String(format).toUpperCase() === "PNG" ? "image/png" : "image/jpeg";
    const encoded = canvas.toDataURL(
      mimeType,
      mimeType === "image/png" ? undefined : quality
    );

    return {
      dataUrl: encoded,
      format: mimeType === "image/png" ? "PNG" : "JPEG",
      width: targetWidth,
      height: targetHeight,
    };
  } catch (error) {
    console.warn("PDF image preparation failed:", error);
    return null;
  }
};

const fitPdfImageInsideBox = (imageAsset, maxWidth, maxHeight) => {
  const sourceWidth = Number(imageAsset?.width || maxWidth || 1);
  const sourceHeight = Number(imageAsset?.height || maxHeight || 1);
  const ratio = Math.min(
    maxWidth / sourceWidth,
    maxHeight / sourceHeight
  );

  return {
    width: Math.max(1, Number((sourceWidth * ratio).toFixed(2))),
    height: Math.max(1, Number((sourceHeight * ratio).toFixed(2))),
  };
};

// --- Convert Firebase logo URL to optimized dataURL for jsPDF ---
const getLogoDataURL = async (options = {}) => {
  if (!logoURL) return null;

  try {
    const asset = await buildPdfImageAsset(logoURL, {
      maxWidth: 560,
      maxHeight: 260,
      format: "PNG",
      ...options,
    });
    return asset?.dataUrl || null;
  } catch (err) {
    console.warn("Logo conversion failed:", err);
    return null;
  }
};

// --- IMAGE HELPER: load remote product image as optimized dataURL for jsPDF ---
const fetchImageAsDataURL = (url, options = {}) =>
  buildPdfImageAsset(url, {
    maxWidth: 320,
    maxHeight: 320,
    format: "JPEG",
    quality: 0.72,
    backgroundColor: "#ffffff",
    ...options,
  });

const exportGeneralQuotationPDF = async () => {
  if (!canUseCustomerPdf) {
    alert("You do not have permission to export customer PDFs.");
    return;
  }

  try {
    const { jsPDF, autoTable } = await loadPdfRuntime();
    const businessNameLabel = buildBusinessDisplayName(businessName, shopAddress);
    const liveGeneralActionPayload = buildLiveGeneralQuotePayload({
      action: "customerPdf",
    });
    traceLiveActionPayload(liveGeneralActionPayload);
    guardStaleActionPayload(
      liveGeneralActionPayload,
      liveGeneralActionPayload.summaryTotals
    );
    const { cleanedQuote, changed, metadataChanged } =
      await cleanupExpiredGeneralQuoteImages(liveGeneralActionPayload.quote);
    const quoteData = normalizeGeneralQuote(cleanedQuote, {
      shopAddress,
      currency,
      businessName,
      businessPhone,
      businessEmail,
    });
    const totals = calculateGeneralQuoteTotals(quoteData);

    if (changed || metadataChanged) {
      generalQuoteRef.current = quoteData;
      setGeneralQuote(quoteData);
    }

    const doc = new jsPDF({
      unit: "mm",
      format: "a4",
      compress: true,
    });

    const pageW = Number(doc.internal.pageSize.getWidth());
    const pageH = Number(doc.internal.pageSize.getHeight());
    const marginX = 15;
    const curSym = pdfCurrencySymbol(quoteData.currency || currency);
    const safeFileName = (value) =>
      String(value || "quotation").replace(/[^\w-]+/g, "_");

    const logoDataUrl = await getLogoDataURL({
      maxWidth: 560,
      maxHeight: 220,
    });
    const imageMap = {};

    await Promise.all(
      (quoteData.lineItems || []).map(async (item) => {
        const itemImages = normalizeGeneralQuoteItemImages(item);
        if (!itemImages.length) return;

        const renderedImages = await Promise.all(
          itemImages
            .slice(0, 4)
            .map((image) => getGeneralQuoteImageDisplayUrl(image))
            .filter(Boolean)
            .map((imageUrl) =>
              fetchImageAsDataURL(imageUrl, {
                maxWidth: 320,
                maxHeight: 320,
                quality: 0.7,
              })
            )
        );

        imageMap[item.id] = {
          totalCount: itemImages.length,
          assets: renderedImages.filter(Boolean),
        };
      })
    );

    const headerTop = 10;
    const headerWidth = pageW - marginX * 2;
    const headerGap = 6;
    const headerPadX = 5;
    const headerPadY = 5;
    const leftHeaderWidth = 60;
    const rightHeaderWidth = 42;
    const centerHeaderWidth =
      headerWidth - leftHeaderWidth - rightHeaderWidth - headerGap * 2;
    const splitTextBlock = (value, width) => {
      const text = String(value || "").trim();
      if (!text) return [];
      return doc.splitTextToSize(text, Math.max(12, width));
    };
    const wrapTextFields = (values, width) =>
      (Array.isArray(values) ? values : [])
        .flatMap((value) => splitTextBlock(value, width))
        .filter(Boolean);

    let logoW = 0;
    let logoH = 0;

    if (logoDataUrl) {
      try {
        const logoProps = doc.getImageProperties(logoDataUrl);
        const maxW = 24;
        const maxH = 16;
        const ratio = Math.min(
          maxW / (logoProps.width || maxW),
          maxH / (logoProps.height || maxH)
        );

        logoW = (logoProps.width || maxW) * ratio;
        logoH = (logoProps.height || maxH) * ratio;
      } catch (imageError) {
        console.warn("Failed to size business logo for general PDF:", imageError);
      }
    }

    const leftTextWidth = Math.max(22, leftHeaderWidth - headerPadX * 2);
    const rightTextWidth = Math.max(18, rightHeaderWidth - headerPadX * 2);
    const centerTextWidth = Math.max(20, centerHeaderWidth - headerPadX * 2);

    const businessNameLines = splitTextBlock(businessNameLabel, leftTextWidth);
    const businessSupportLines = wrapTextFields(
      [quoteData.quoteFrom?.address || shopAddress || ""],
      leftTextWidth
    );
    const titleLines = splitTextBlock(
      quoteData.title || "Quotation",
      centerTextWidth
    );
    const subtitleLines = splitTextBlock(quoteData.subtitle, centerTextWidth);
    const metaGroups = [
      splitTextBlock(`Quote No: ${quoteData.quoteNo || "Draft"}`, rightTextWidth),
      splitTextBlock(`Date: ${quoteData.quoteDate || "-"}`, rightTextWidth),
      splitTextBlock(`Valid Till: ${quoteData.validTill || "-"}`, rightTextWidth),
    ].filter((lines) => lines.length > 0);

    const leftTextHeight =
      businessNameLines.length * 5 +
      (businessSupportLines.length ? 2 + businessSupportLines.length * 4.2 : 0);
    const leftLogoGap = logoW && logoH ? 4 : 0;
    const leftBlockHeight =
      (logoH ? logoH + leftLogoGap : 0) + leftTextHeight;
    const centerBlockHeight =
      titleLines.length * 6 +
      (subtitleLines.length ? 2 + subtitleLines.length * 4.2 : 0);
    const metaBlockHeight = metaGroups.reduce(
      (sum, lines, index) =>
        sum + lines.length * 4.4 + (index < metaGroups.length - 1 ? 2 : 0),
      0
    );
    const headerInnerHeight = Math.max(
      leftBlockHeight,
      centerBlockHeight,
      metaBlockHeight,
      20
    );
    const headerHeight = headerInnerHeight + headerPadY * 2;

    doc.setDrawColor(226, 232, 240);
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(marginX, headerTop, headerWidth, headerHeight, 4, 4, "F");

    const leftBlockX = marginX + headerPadX;
    const rightBlockX =
      marginX + leftHeaderWidth + headerGap + centerHeaderWidth + headerGap;
    const innerTop = headerTop + headerPadY;

    if (logoDataUrl && logoW && logoH) {
      try {
        const leftContentTop =
          innerTop + Math.max(0, (headerInnerHeight - leftBlockHeight) / 2);
        const logoX =
          leftBlockX + Math.max(0, (leftHeaderWidth - headerPadX * 2 - logoW) / 2);
        doc.addImage(logoDataUrl, "PNG", logoX, leftContentTop, logoW, logoH);
      } catch (imageError) {
        console.warn("Failed to render business logo for general PDF:", imageError);
      }
    }

    const leftContentTop =
      innerTop + Math.max(0, (headerInnerHeight - leftBlockHeight) / 2);
    const businessTextX = leftBlockX + (leftHeaderWidth - headerPadX * 2) / 2;
    const businessTextTop = leftContentTop + (logoH ? logoH + leftLogoGap : 0);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(15, 23, 42);
    if (businessNameLines.length) {
      doc.text(businessNameLines, businessTextX, businessTextTop + 4.5, {
        align: "center",
      });
    }

    if (businessSupportLines.length) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.2);
      doc.setTextColor(71, 85, 105);
      const supportY = businessTextTop + businessNameLines.length * 5 + 6;
      doc.text(businessSupportLines, businessTextX, supportY, {
        align: "center",
      });
    }

    const centerContentTop =
      innerTop + Math.max(0, (headerInnerHeight - centerBlockHeight) / 2);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(15, 23, 42);
    doc.text(titleLines, pageW / 2, centerContentTop + 6, { align: "center" });

    if (subtitleLines.length) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(71, 85, 105);
      doc.text(
        subtitleLines,
        pageW / 2,
        centerContentTop + titleLines.length * 6 + 8,
        { align: "center" }
      );
    }

    const metaTop =
      innerTop + Math.max(0, (headerInnerHeight - metaBlockHeight) / 2) + 4;
    let metaY = metaTop;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(71, 85, 105);
    metaGroups.forEach((lines, index) => {
      doc.text(lines, rightBlockX + rightHeaderWidth - headerPadX, metaY, {
        align: "right",
      });
      metaY += lines.length * 4.4 + (index < metaGroups.length - 1 ? 2 : 0);
    });

    const partyTop = headerTop + headerHeight + 8;
    const partyWidth = (headerWidth - 8) / 2;

    const drawPartyBlock = (x, title, values) => {
      const split = wrapTextFields(values, partyWidth - 8);
      const blockHeight = Math.max(34, 16 + split.length * 4.5 + 6);
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(226, 232, 240);
      doc.roundedRect(x, partyTop, partyWidth, blockHeight, 3, 3, "FD");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(15, 23, 42);
      doc.text(title, x + 4, partyTop + 7);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.5);
      doc.setTextColor(71, 85, 105);
      if (split.length) {
        doc.text(split, x + 4, partyTop + 13);
      }
      return blockHeight;
    };

    const fromBlockHeight = drawPartyBlock(marginX, "Quotation From", [
      quoteData.quoteFrom?.name,
      quoteData.quoteFrom?.address,
      quoteData.quoteFrom?.phone,
      quoteData.quoteFrom?.email,
    ]);

    const toBlockHeight = drawPartyBlock(marginX + partyWidth + 8, "Quotation For", [
      quoteData.quoteFor?.company,
      quoteData.quoteFor?.name,
      quoteData.quoteFor?.address,
      quoteData.quoteFor?.phone,
      quoteData.quoteFor?.email,
    ]);

    const tableStartY = partyTop + Math.max(fromBlockHeight, toBlockHeight) + 8;

    const body = (quoteData.lineItems || []).map((item, index) => [
      String(index + 1),
      "",
      [
        item.itemName || item.name || "-",
        item.description ? `Description: ${item.description}` : "",
        item.imageLinkUrl || item.productUrl
          ? `Link: ${item.imageLinkUrl || item.productUrl}`
          : "",
      ]
        .filter(Boolean)
        .join("\n"),
      Number(item.quantity || 0).toFixed(2),
      item.unit || "-",
      `${curSym} ${Number(item.rate || 0).toFixed(2)}`,
      `${Number(item.gstPercent || 0).toFixed(0)}% ${
        item.gstMode === "exclusive" ? "Exc" : "Inc"
      }`,
      `${curSym} ${getGeneralItemAmount(item).toFixed(2)}`,
    ]);

    autoTable(doc, {
      startY: tableStartY,
      head: [["#", "Image", "Item", "Qty", "Unit", "Rate", "GST", "Amount"]],
      body,
      margin: { left: marginX, right: marginX },
      theme: "grid",
      rowPageBreak: "avoid",
      styles: {
        fontSize: 9,
        cellPadding: 2.8,
        textColor: [15, 23, 42],
        lineColor: [226, 232, 240],
        lineWidth: 0.1,
        valign: "top",
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: [37, 99, 235],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      columnStyles: {
        0: {
          cellWidth: 12,
          halign: "center",
          valign: "middle",
          overflow: "hidden",
          cellPadding: { top: 2.8, right: 1.5, bottom: 2.8, left: 1.5 },
        },
        1: { cellWidth: 24, halign: "center" },
        2: { cellWidth: 53 },
        3: { cellWidth: 14, halign: "right" },
        4: { cellWidth: 14, halign: "center" },
        5: { cellWidth: 21, halign: "right" },
        6: { cellWidth: 18, halign: "center" },
        7: { cellWidth: 24, halign: "right" },
      },
      didParseCell: (data) => {
        if (data.column.index === 0) {
          data.cell.styles.overflow = "hidden";
          data.cell.styles.halign = "center";
          data.cell.styles.valign = "middle";
          data.cell.text = [String(data.cell.raw ?? "").trim()];
        }

        if (data.section !== "body") return;

        const item = quoteData.lineItems?.[data.row.index];
        const imageEntry = imageMap[item?.id];
        const hasThumbnail = Boolean(imageEntry?.assets?.length);
        if (data.column.index === 1) {
          data.cell.text = [""];
          data.cell.styles.minCellHeight = hasThumbnail ? 24 : 10;
        }

        if (hasThumbnail && data.column.index === 2) {
          data.cell.styles.minCellHeight = Math.max(
            Number(data.cell.styles.minCellHeight || 0),
            24
          );
        }
      },
      didDrawCell: (data) => {
        if (data.section !== "body" || data.column.index !== 1) return;

        const item = quoteData.lineItems?.[data.row.index];
        const imageEntry = imageMap[item?.id];
        const imageAssets = imageEntry?.assets || [];
        if (!item || imageAssets.length === 0) return;

        const thumbBox = Math.min(18, Math.max(12, data.cell.width - 4));
        const availableHeight = Math.max(10, Math.min(thumbBox, data.cell.height - 4));
        const boxX =
          data.cell.x + (data.cell.width - Math.min(thumbBox, data.cell.width - 4)) / 2;
        const boxY = data.cell.y + 2;
        const boxWidth = Math.min(thumbBox, data.cell.width - 4);
        const boxHeight = availableHeight;

        doc.setDrawColor(226, 232, 240);
        doc.roundedRect(
          boxX,
          boxY,
          boxWidth,
          boxHeight,
          1.5,
          1.5
        );

        if (imageAssets.length === 1) {
          const fitted = fitPdfImageInsideBox(imageAssets[0], boxWidth, boxHeight);
          const imgX = boxX + (boxWidth - fitted.width) / 2;
          const imgY = boxY + (boxHeight - fitted.height) / 2;

          doc.addImage(
            imageAssets[0].dataUrl,
            imageAssets[0].format,
            imgX,
            imgY,
            fitted.width,
            fitted.height
          );
        } else {
          const columns = 2;
          const rows = imageAssets.length > 2 ? 2 : 1;
          const gap = 0.8;
          const tileWidth = (boxWidth - gap * (columns - 1)) / columns;
          const tileHeight = (boxHeight - gap * (rows - 1)) / rows;

          imageAssets.slice(0, 4).forEach((asset, imageIndex) => {
            const col = imageIndex % columns;
            const row = Math.floor(imageIndex / columns);
            const tileX = boxX + col * (tileWidth + gap);
            const tileY = boxY + row * (tileHeight + gap);
            const fitted = fitPdfImageInsideBox(asset, tileWidth, tileHeight);
            const imgX = tileX + (tileWidth - fitted.width) / 2;
            const imgY = tileY + (tileHeight - fitted.height) / 2;

            doc.addImage(
              asset.dataUrl,
              asset.format,
              imgX,
              imgY,
              fitted.width,
              fitted.height
            );

            if (
              imageIndex === 3 &&
              Number(imageEntry?.totalCount || 0) > imageAssets.length
            ) {
              doc.setFillColor(15, 23, 42);
              doc.setDrawColor(15, 23, 42);
              doc.roundedRect(
                tileX + tileWidth - 7,
                tileY + tileHeight - 5.5,
                6.5,
                4.8,
                1,
                1,
                "F"
              );
              doc.setTextColor(255, 255, 255);
              doc.setFont("helvetica", "bold");
              doc.setFontSize(6.5);
              doc.text(
                `+${Number(imageEntry.totalCount || 0) - imageAssets.length}`,
                tileX + tileWidth - 3.5,
                tileY + tileHeight - 2.1,
                { align: "center" }
              );
              doc.setTextColor(15, 23, 42);
            }
          });
        }

        const primaryImage = normalizeGeneralQuoteItemImages(item)[0];
        const rawItemLink = item.imageLinkUrl || item.productUrl || "";
        const safeItemLink = isFirebaseStorageRestUrl(rawItemLink) ? "" : rawItemLink;
        const href =
          safeItemLink ||
          getGeneralQuoteImageDisplayUrl(primaryImage) ||
          "";
        if (href) {
          doc.link(
            boxX,
            boxY,
            boxWidth,
            boxHeight,
            { url: href }
          );
        }
      },
    });

    let cursorY = Number(doc.lastAutoTable?.finalY || 98) + 12;
    const contentWidth = pageW - marginX * 2;
    const topMargin = 18;
    const bottomMargin = 14;
    const rowHeight = 6;
    const textLineHeight = 4.5;
    const blockPaddingX = 5;
    const blockPaddingY = 6;
    const subtotalExcludingGst = Number(
      totals.subtotalExcludingGst ??
        (quoteData.lineItems || []).reduce(
          (sum, item) => sum + getGeneralItemBaseAmount(item),
          0
        )
    );
    const itemGstTotal = Number(
      (
        Number(totals.itemsGstTotal || 0) ||
        Number(totals.gstTotal || 0) - Number(totals.additionalChargesGst || 0)
      ).toFixed(2)
    );

    const ensurePageSpace = (neededHeight) => {
      if (cursorY + neededHeight > pageH - bottomMargin) {
        doc.addPage();
        cursorY = topMargin;
      }
    };

    const splitPdfBlockText = (value, blockWidth = contentWidth) => {
      const textValue = String(value || "").trim();
      if (!textValue) return [];
      return doc.splitTextToSize(textValue, Math.max(24, blockWidth));
    };

    const specificationShowcaseItems = (quoteData.lineItems || [])
      .map((item, index) => ({
        item,
        index,
        specifications: normalizeGeneralQuoteItemSpecifications(item).filter(
          (specification) =>
            String(specification?.key ?? "").trim() ||
            String(specification?.value ?? "").trim()
        ),
      }))
      .filter((entry) => entry.specifications.length > 0);

    const writeSpecificationShowcase = () => {
      if (!specificationShowcaseItems.length) return;

      ensurePageSpace(18);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.setTextColor(15, 23, 42);
      doc.text("Specifications", marginX, cursorY);
      cursorY += 8;

      const chipGap = 4;
      const chipWidth = (contentWidth - chipGap) / 2;

      specificationShowcaseItems.forEach(({ item, index, specifications }) => {
        const itemNameLines = splitPdfBlockText(
          item.itemName || item.name || `Item ${index + 1}`,
          contentWidth - blockPaddingX * 2
        );
        const metaLabel = [item.specTemplateName, item.specItemType]
          .filter(Boolean)
          .join(" | ");
        const metaLines = splitPdfBlockText(
          metaLabel,
          contentWidth - blockPaddingX * 2
        );
        const descriptionLines = splitPdfBlockText(
          item.description,
          contentWidth - blockPaddingX * 2
        );
        const headerHeight =
          10 +
          itemNameLines.length * 5 +
          (metaLines.length ? metaLines.length * 4 + 2 : 0) +
          (descriptionLines.length ? descriptionLines.length * 4.2 + 4 : 0);

        ensurePageSpace(headerHeight + 10);
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(226, 232, 240);
        doc.roundedRect(marginX, cursorY, contentWidth, headerHeight, 3, 3, "FD");

        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(15, 23, 42);
        doc.text(itemNameLines, marginX + blockPaddingX, cursorY + 7);

        let headerCursorY = cursorY + 7 + itemNameLines.length * 5;

        if (metaLines.length) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.setTextColor(37, 99, 235);
          doc.text(metaLines, marginX + blockPaddingX, headerCursorY + 1.5);
          headerCursorY += metaLines.length * 4 + 2;
        }

        if (descriptionLines.length) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9.5);
          doc.setTextColor(71, 85, 105);
          doc.text(descriptionLines, marginX + blockPaddingX, headerCursorY + 2.5);
        }

        cursorY += headerHeight + 4;

        const needsVerticalLayout =
          specifications.length > 6 ||
          specifications.some(
            (specification) =>
              String(specification?.key ?? "").trim().length > 22 ||
              String(specification?.value ?? "").trim().length > 28
          );

        if (needsVerticalLayout) {
          specifications.forEach((specification) => {
            const label = String(specification?.key || "Specification").trim() || "Specification";
            const value = String(specification?.value || "-").trim() || "-";
            const detailLines = splitPdfBlockText(
              `${label}: ${value}`,
              contentWidth - blockPaddingX * 2
            );
            const rowHeight = Math.max(10, 6 + detailLines.length * 4.2);

            ensurePageSpace(rowHeight + 2);
            doc.setFillColor(248, 250, 252);
            doc.setDrawColor(226, 232, 240);
            doc.roundedRect(marginX, cursorY, contentWidth, rowHeight, 3, 3, "FD");
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9.2);
            doc.setTextColor(30, 41, 59);
            doc.text(detailLines, marginX + blockPaddingX, cursorY + 5.5);
            cursorY += rowHeight + 3;
          });

          cursorY += 5;
          return;
        }

        for (let specIndex = 0; specIndex < specifications.length; specIndex += 2) {
          const rowSpecifications = specifications.slice(specIndex, specIndex + 2);
          const renderedChips = rowSpecifications.map((specification) => {
            const chipText = `${specification.key || "Specification"}: ${
              specification.value || "-"
            }`;
            const chipLines = splitPdfBlockText(
              chipText,
              chipWidth - blockPaddingX * 2
            );

            return {
              chipLines,
              height: Math.max(10, 6 + chipLines.length * 4.2),
            };
          });
          const rowHeight = Math.max(...renderedChips.map((chip) => chip.height));

          ensurePageSpace(rowHeight + 2);
          const currentRowY = cursorY;

          for (let chipIndex = 0; chipIndex < renderedChips.length; chipIndex += 1) {
            const chip = renderedChips[chipIndex];
            const chipX = marginX + chipIndex * (chipWidth + chipGap);
            doc.setFillColor(248, 250, 252);
            doc.setDrawColor(226, 232, 240);
            doc.roundedRect(chipX, currentRowY, chipWidth, rowHeight, 3, 3, "FD");
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9.2);
            doc.setTextColor(30, 41, 59);
            doc.text(chip.chipLines, chipX + blockPaddingX, currentRowY + 5.5);
          }

          cursorY += rowHeight + 3;
        }

        cursorY += 5;
      });
    };

    const writeSummaryBlock = () => {
      const additionalLabel =
        quoteData.additionalChargesLabel || "Additional Charges";
      const discountLabel =
        quoteData.discountType === "percent"
          ? `Post-GST Discount (${Number(quoteData.discountValue || 0)}%)`
          : "Post-GST Discount";
      const summaryRows = [
        ["Subtotal (Excl. GST)", `${curSym} ${subtotalExcludingGst.toFixed(2)}`],
        ["Item GST", `${curSym} ${itemGstTotal.toFixed(2)}`],
        [additionalLabel, `${curSym} ${Number(totals.additionalCharges || 0).toFixed(2)}`],
        [
          `${additionalLabel} GST`,
          `${curSym} ${Number(totals.additionalChargesGst || 0).toFixed(2)}`,
        ],
        [
          "Total After GST",
          `${curSym} ${Number(totals.totalAfterGst ?? totals.subtotal ?? 0).toFixed(2)}`,
        ],
        [discountLabel, `- ${curSym} ${totals.discountAmount.toFixed(2)}`],
      ];
      const blockHeight = 18 + summaryRows.length * rowHeight + 12;

      ensurePageSpace(blockHeight);

      doc.setFillColor(248, 250, 252);
      doc.setDrawColor(226, 232, 240);
      doc.roundedRect(marginX, cursorY, contentWidth, blockHeight, 3, 3, "FD");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(15, 23, 42);
      doc.text("Totals", marginX + blockPaddingX, cursorY + 7);

      let rowY = cursorY + 15;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);

      summaryRows.forEach(([label, value]) => {
        doc.text(label, marginX + blockPaddingX, rowY);
        doc.text(value, pageW - marginX - blockPaddingX, rowY, {
          align: "right",
        });
        rowY += rowHeight;
      });

      doc.setDrawColor(226, 232, 240);
      doc.line(
        marginX + blockPaddingX,
        rowY - 1,
        pageW - marginX - blockPaddingX,
        rowY - 1
      );

      doc.setFont("helvetica", "bold");
      doc.text("Grand Total", marginX + blockPaddingX, rowY + 5);
      doc.text(
        `${curSym} ${totals.grandTotal.toFixed(2)}`,
        pageW - marginX - blockPaddingX,
        rowY + 5,
        { align: "right" }
      );

      cursorY += blockHeight + 8;
    };

    const writeTextBlock = (title, value) => {
      if (!value) return;

      const textValue = String(value || "").trim();
      if (!textValue) return;

      const lines = doc.splitTextToSize(textValue, contentWidth - blockPaddingX * 2);
      let lineIndex = 0;

      while (lineIndex < lines.length) {
        ensurePageSpace(18);

        const availableHeight = pageH - bottomMargin - cursorY;
        const maxLines = Math.max(
          1,
          Math.floor((availableHeight - 14) / textLineHeight)
        );
        const chunk = lines.slice(lineIndex, lineIndex + maxLines);
        const blockHeight = 12 + chunk.length * textLineHeight + blockPaddingY;

        ensurePageSpace(blockHeight);

        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(226, 232, 240);
        doc.roundedRect(marginX, cursorY, contentWidth, blockHeight, 3, 3, "FD");

        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(15, 23, 42);
        doc.text(title, marginX + blockPaddingX, cursorY + 7);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.setTextColor(71, 85, 105);
        doc.text(chunk, marginX + blockPaddingX, cursorY + 13);

        cursorY += blockHeight + 8;
        lineIndex += chunk.length;
      }
    };

    writeSpecificationShowcase();
    writeSummaryBlock();
    writeTextBlock("Total In Words", totals.totalInWords);
    writeTextBlock("Notes", quoteData.notes);
    writeTextBlock("Terms & Conditions", quoteData.terms);

    ensurePageSpace(8);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(
      `Generated by ${businessNameLabel} by using FabriQuote`,
      marginX,
      cursorY
    );

    doc.save(
      `General-Quote-${safeFileName(
        quoteData.quoteFor?.company || quoteData.quoteFor?.name || quoteData.quoteNo
      )}-${safeFileName(quoteData.quoteNo || "draft")}.pdf`
    );
  } catch (error) {
    console.error("Failed to export general quotation PDF:", error);
    alert("Failed to export general quotation PDF");
  }
};

const downloadExecutionWorkshopPdf = async (dealOverride = null, pdfOptions = {}) => {
  if (!canUseWorkshopPdf) {
    notify("You do not have permission to export workshop PDFs.", "warning");
    return;
  }

  const sourceDeal =
    pdfOptions.sourceDeal ||
    dealOverride ||
    selectedCrmDeal ||
    selectedCrmDealCacheRef.current ||
    null;
  const sourceDraft =
    pdfOptions.sourceDraft !== undefined ? pdfOptions.sourceDraft : crmDetailDraft || null;

  if (!sourceDeal && !sourceDraft) {
    notify("Open an execution deal before downloading the workshop PDF.", "warning");
    return;
  }

  const rawExecutionItems = Array.isArray(pdfOptions.executionItems)
    ? pdfOptions.executionItems
    : Array.isArray(sourceDraft?.executionSpecs)
    ? sourceDraft.executionSpecs
    : Array.isArray(sourceDeal?.executionSpecs)
    ? sourceDeal.executionSpecs
    : [];

  const executionItems = normalizeExecutionSpecs(
    (Array.isArray(rawExecutionItems) ? rawExecutionItems : []).map((item) => ({
      ...item,
      attributes: Array.isArray(item?.attributeRows)
        ? item.attributeRows.reduce((accumulator, field) => {
            const key = String(field?.key || "").trim();
            if (!key) return accumulator;
            accumulator[key] = String(field?.value ?? "");
            return accumulator;
          }, {})
        : item?.attributes,
      attachments: normalizeExecutionSpecAttachments(item?.attachments || []),
    }))
  );

  if (executionItems.length === 0) {
    notify("Add execution items before downloading the workshop PDF.", "warning");
    return;
  }

  try {
    const { jsPDF, autoTable } = await loadPdfRuntime();
    const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
    const pageW = Number(doc.internal.pageSize.getWidth());
    const pageH = Number(doc.internal.pageSize.getHeight());
    const marginX = 14;
    const contentWidth = pageW - marginX * 2;
    const businessNameLabel = buildBusinessDisplayName(businessName, shopAddress);
    const generatedAt = new Date();
    const primaryLocation = getPrimarySiteLocation({
      siteLocations: sourceDraft?.siteLocations || sourceDeal?.siteLocations || [],
    });
    const linkedQuote = getPrimaryCrmLinkedQuote(sourceDeal?.linkedQuotes || []) || {};
    const customerNameValue =
      sourceDraft?.contact?.name ||
      sourceDeal?.customerLabel ||
      sourceDeal?.customerName ||
      sourceDeal?.crmContact?.name ||
      sourceDeal?.contact?.name ||
      "Unnamed Customer";
    const customerPhoneValue =
      sourceDraft?.contact?.phone ||
      sourceDraft?.contact?.whatsapp ||
      sourceDeal?.crmContact?.phone ||
      sourceDeal?.crmContact?.whatsapp ||
      sourceDeal?.contact?.phone ||
      sourceDeal?.contact?.whatsapp ||
      sourceDeal?.customerPhone ||
      "";
    const quoteReference =
      linkedQuote.quoteNo ||
      sourceDeal?.quoteNo ||
      sourceDeal?.primaryQuoteNo ||
      sourceDraft?.quoteNo ||
      "-";
    const jobReference =
      sourceDeal?.crmTitle ||
      sourceDeal?.title ||
      sourceDeal?.dealTitle ||
      sourceDeal?.id ||
      "Execution Job";
    const installationDateValue =
      sourceDraft?.installationDate ||
      sourceDeal?.installationDate ||
      sourceDeal?.crm?.installationDate ||
      "";
    const stageLabel =
      sourceDraft?.stageNameSnapshot ||
      sourceDeal?.crmStageLabel ||
      sourceDeal?.stageNameSnapshot ||
      sourceDeal?.status ||
      "-";
    const commercialFieldPattern =
      /(rs\.|₹|\bprice\b|\brate\b|\bmrp\b|\bdiscount\b|\bgst\b|\btax\b|\bmargin\b|\bpayment\b|\badvance\b|\bbalance\b|\bcommercial\b|\bamount\b|\bsubtotal\b|\btotal\b|\bgrand\s*total\b|\bquote\s*value\b|\bfinal\s*value\b|\bexpected\s*value\b|\bcost\b|\bbilling\b)/i;
    const safeFileName = (value) =>
      normalizeFileName(String(value || "execution-workshop").slice(0, 80));
    const formatPdfDate = (value) => {
      const millis = toMillis(value);
      if (!millis) return "";
      return new Date(millis).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    };
    const displayValue = (value, fallback = "-") => {
      const next = String(value ?? "").trim();
      return next || fallback;
    };
    const splitValue = (value, width = contentWidth) =>
      doc.splitTextToSize(displayValue(value, ""), Math.max(20, width));
    const isCommercialField = (label) => commercialFieldPattern.test(String(label || ""));
    const getOperationalAttributeRows = (item) =>
      Object.entries(item?.attributes || {})
        .map(([key, value]) => [String(key || "").trim(), displayValue(value)])
        .filter(([key, value]) => key && !isCommercialField(key) && !isCommercialField(value));
    const sanitizeOperationalNotes = (value) =>
      String(value || "")
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter((line) => line && !commercialFieldPattern.test(line))
        .join("\n");
    const isImageAttachment = (attachment) => {
      const type = String(attachment?.contentType || "").toLowerCase();
      const name = String(attachment?.name || "").toLowerCase();
      return (
        type.startsWith("image/") ||
        /\.(png|jpe?g|webp|gif)$/i.test(name)
      );
    };

    const imageAssetsByItemId = {};
    await Promise.all(
      executionItems.map(async (item) => {
        const imageAttachments = normalizeExecutionSpecAttachments(item.attachments || [])
          .filter((attachment) => attachment.url && isImageAttachment(attachment))
          .slice(0, 2);

        if (!imageAttachments.length) return;

        const assets = await Promise.all(
          imageAttachments.map((attachment) =>
            fetchImageAsDataURL(attachment.url, {
              maxWidth: 360,
              maxHeight: 260,
              quality: 0.72,
            })
          )
        );
        imageAssetsByItemId[item.id] = imageAttachments.map((attachment, index) => ({
          attachment,
          asset: assets[index] || null,
        }));
      })
    );

    const logoDataUrl = await getLogoDataURL({
      maxWidth: 420,
      maxHeight: 160,
    });

    let cursorY = 14;

    const ensurePageSpace = (height = 24) => {
      if (cursorY + height <= pageH - 18) return;
      doc.addPage();
      cursorY = 16;
    };

    const writeHeader = () => {
      const headerHeight = 34;
      const logoBadgeX = marginX + 5;
      const logoBadgeY = cursorY + 5;
      const logoBadgeW = 28;
      const logoBadgeH = 18;
      doc.setFillColor(15, 23, 42);
      doc.roundedRect(marginX, cursorY, contentWidth, headerHeight, 4, 4, "F");

      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(226, 232, 240);
      doc.roundedRect(logoBadgeX, logoBadgeY, logoBadgeW, logoBadgeH, 3, 3, "FD");

      if (logoDataUrl) {
        try {
          const logoProps = doc.getImageProperties(logoDataUrl);
          const fit = fitPdfImageInsideBox(
            {
              width: logoProps.width,
              height: logoProps.height,
            },
            logoBadgeW - 8,
            logoBadgeH - 6
          );
          doc.addImage(
            logoDataUrl,
            "PNG",
            logoBadgeX + (logoBadgeW - fit.width) / 2,
            logoBadgeY + (logoBadgeH - fit.height) / 2,
            fit.width,
            fit.height
          );
        } catch (error) {
          console.warn("Execution workshop PDF logo render failed:", error);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(7.5);
          doc.setTextColor(15, 23, 42);
          doc.text(
            doc.splitTextToSize(businessNameLabel, logoBadgeW - 5).slice(0, 2),
            logoBadgeX + logoBadgeW / 2,
            logoBadgeY + 7,
            { align: "center" }
          );
        }
      } else {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7.5);
        doc.setTextColor(15, 23, 42);
        doc.text(
          doc.splitTextToSize(businessNameLabel, logoBadgeW - 5).slice(0, 2),
          logoBadgeX + logoBadgeW / 2,
          logoBadgeY + 7,
          { align: "center" }
        );
      }

      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.text("WORKSHOP / EXECUTION SHEET", marginX + 34, cursorY + 12);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.5);
      doc.text(
        "Production / installation copy",
        marginX + 34,
        cursorY + 20
      );
      doc.text(businessNameLabel, pageW - marginX - 5, cursorY + 12, {
        align: "right",
      });
      doc.text(
        `Generated: ${generatedAt.toLocaleString("en-IN")}`,
        pageW - marginX - 5,
        cursorY + 20,
        { align: "right" }
      );
      cursorY += headerHeight + 8;
      doc.setTextColor(15, 23, 42);
    };

    const writeSectionTitle = (title, subtitle = "") => {
      ensurePageSpace(14);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(15, 23, 42);
      doc.text(title, marginX, cursorY);
      if (subtitle) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(100, 116, 139);
        doc.text(subtitle, marginX, cursorY + 5);
        cursorY += 10;
      } else {
        cursorY += 6;
      }
    };

    writeHeader();

    const siteValue = [
      primaryLocation?.label,
      primaryLocation?.addressText,
      primaryLocation?.notes,
    ]
      .filter(Boolean)
      .join(" - ");

    autoTable(doc, {
      startY: cursorY,
      head: [["Job", "Customer", "Quote Ref", "Status"]],
      body: [
        [
          displayValue(jobReference),
          displayValue(customerNameValue),
          displayValue(quoteReference),
          displayValue(stageLabel),
        ],
        [
          `Phone: ${displayValue(customerPhoneValue)}`,
          `Site: ${displayValue(siteValue)}`,
          `Installation: ${displayValue(formatPdfDate(installationDateValue))}`,
          `Items: ${executionItems.length}`,
        ],
      ],
      margin: { left: marginX, right: marginX },
      theme: "grid",
      styles: {
        fontSize: 9,
        cellPadding: 2.8,
        lineColor: [226, 232, 240],
        lineWidth: 0.1,
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: [37, 99, 235],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
    });
    cursorY = Number(doc.lastAutoTable?.finalY || cursorY) + 10;

    writeSectionTitle(
      "Execution Items",
      "Manufacturing details, reference photos, and site notes for the execution team."
    );

    executionItems.forEach((item, index) => {
      const attributeRows = getOperationalAttributeRows(item);
      const attachments = normalizeExecutionSpecAttachments(item.attachments || []);
      const attachmentRows = attachments
        .filter((attachment) => attachment.name || attachment.url)
        .map((attachment) => [
          "Attachment",
          displayValue(attachment.name || attachment.url),
        ])
        .filter(([, value]) => !isCommercialField(value));
      const tableRows = [
        ["Item Type", displayValue(item.itemType || "Custom")],
        ["Item / Product", displayValue(item.itemName || `Item ${index + 1}`)],
        ...attributeRows,
        ...attachmentRows,
      ];
      assertNoPricingInWorkshopPdfText(tableRows, {
        label: `executionWorkshopItem:${item.id || index}`,
        product: item.itemType || item.itemName || "",
        rowItem: item.itemName || "",
      });

      ensurePageSpace(26);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(15, 23, 42);
      doc.text(
        `${index + 1}. ${displayValue(item.itemName || item.itemType || "Execution Item")}`,
        marginX,
        cursorY
      );
      cursorY += 4;

      autoTable(doc, {
        startY: cursorY,
        head: [["Field", "Instruction / Detail"]],
        body: tableRows.length ? tableRows : [["Details", "-"]],
        margin: { left: marginX, right: marginX },
        theme: "grid",
        rowPageBreak: "avoid",
        styles: {
          fontSize: 9.2,
          cellPadding: 2.8,
          lineColor: [226, 232, 240],
          lineWidth: 0.1,
          overflow: "linebreak",
          valign: "top",
        },
        headStyles: {
          fillColor: [15, 23, 42],
          textColor: [255, 255, 255],
          fontStyle: "bold",
        },
        columnStyles: {
          0: { cellWidth: 48, fontStyle: "bold" },
          1: { cellWidth: contentWidth - 48 },
        },
      });
      cursorY = Number(doc.lastAutoTable?.finalY || cursorY) + 5;

      const imageEntries = imageAssetsByItemId[item.id] || [];
      if (imageEntries.length) {
        ensurePageSpace(42);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9.5);
        doc.setTextColor(15, 23, 42);
        doc.text("Reference Images", marginX, cursorY);
        cursorY += 4;

        imageEntries.forEach(({ attachment, asset }, imageIndex) => {
          const boxWidth = 42;
          const boxHeight = 30;
          const x = marginX + imageIndex * (boxWidth + 6);
          const imageUrl = String(attachment?.url || "").trim();
          doc.setDrawColor(226, 232, 240);
          doc.roundedRect(x, cursorY, boxWidth, boxHeight, 2, 2, "S");
          if (asset) {
            const fit = fitPdfImageInsideBox(asset, boxWidth, boxHeight);
            try {
              doc.addImage(
                asset.dataUrl,
                asset.format || "JPEG",
                x + (boxWidth - fit.width) / 2,
                cursorY + (boxHeight - fit.height) / 2,
                fit.width,
                fit.height
              );
            } catch (error) {
              console.warn("Execution workshop PDF image render failed:", error);
            }
          } else {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(100, 116, 139);
            doc.text("Image", x + boxWidth / 2, cursorY + boxHeight / 2, {
              align: "center",
            });
          }

          if (imageUrl) {
            try {
              doc.link(x, cursorY, boxWidth, boxHeight, { url: imageUrl });
            } catch (error) {
              console.warn("Execution workshop PDF image link failed:", error);
            }
          }
        });

        const linkY = cursorY + 35;
        imageEntries.forEach(({ attachment }, imageIndex) => {
          const imageUrl = String(attachment?.url || "").trim();
          if (!imageUrl) return;

          const boxWidth = 42;
          const x = marginX + imageIndex * (boxWidth + 6);
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8.2);
          doc.setTextColor(37, 99, 235);
          doc.text("Open full image", x, linkY);
          try {
            doc.link(x, linkY - 3, 25, 5, { url: imageUrl });
          } catch (error) {
            console.warn("Execution workshop PDF text link failed:", error);
          }
        });
        doc.setTextColor(15, 23, 42);
        cursorY += 45;
      }
    });

    const operationalNotes = sanitizeOperationalNotes(
      sourceDraft?.executionNotes ||
        sourceDraft?.internalNotes ||
        sourceDraft?.notes ||
        sourceDeal?.executionNotes ||
        sourceDeal?.internalNotes ||
        sourceDeal?.notes ||
        ""
    );

    if (operationalNotes) {
      writeSectionTitle("Production Notes");
      const noteLines = splitValue(operationalNotes, contentWidth - 8);
      const noteHeight = Math.max(18, 8 + noteLines.length * 4.4);
      ensurePageSpace(noteHeight + 4);
      doc.setFillColor(248, 250, 252);
      doc.setDrawColor(226, 232, 240);
      doc.roundedRect(marginX, cursorY, contentWidth, noteHeight, 3, 3, "FD");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.5);
      doc.setTextColor(30, 41, 59);
      doc.text(noteLines, marginX + 4, cursorY + 7);
      cursorY += noteHeight + 8;
    }

    const pageCount = doc.getNumberOfPages();
    for (let pageIndex = 1; pageIndex <= pageCount; pageIndex += 1) {
      doc.setPage(pageIndex);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(100, 116, 139);
      doc.text(
        "Generated by FabriQuote",
        marginX,
        pageH - 8
      );
      doc.text(`Page ${pageIndex} of ${pageCount}`, pageW - marginX, pageH - 8, {
        align: "right",
      });
    }

    if (isDevRuntime) {
      console.log("Execution workshop PDF generated", {
        dealId: sourceDeal?.id,
        itemCount: executionItems.length,
        quoteReference,
        excludedCommercialFields: true,
      });
    }

    doc.save(
      `Workshop-Execution-${safeFileName(customerNameValue)}-${safeFileName(
        quoteReference || sourceDeal?.id || "job"
      )}.pdf`
    );
  } catch (error) {
    console.error("Failed to export execution workshop PDF:", error);
    notify("Could not download workshop PDF. Please try again.", "error");
  }
};

// ---------- WORKSHOP LOGO PREP ----------
const prepareLogoForWorkshop = async (getLogoDataURLFn, logoURL) => {
  if (!logoURL) return null;
  try {
    const d = await getLogoDataURLFn();
    if (!d) return null;

    const img = new Image();
    img.src = d;
    await img.decode();

    const maxPixels = 400;
    const maxSide = Math.max(img.width, img.height) || 1;
    const scale = Math.min(1, maxPixels / maxSide);

    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(img.width * scale));
    canvas.height = Math.max(1, Math.round(img.height * scale));
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const dataUrl = canvas.toDataURL("image/png");

    const maxWmm = 80;
    const maxHmm = 40;
    const r = canvas.width / canvas.height || 1;

    let logoWmm = maxWmm;
    let logoHmm = maxWmm / r;
    if (logoHmm > maxHmm) {
      logoHmm = maxHmm;
      logoWmm = maxHmm * r;
    }

    return { dataUrl, logoWmm, logoHmm };
  } catch (e) {
    console.warn("Workshop logo prep failed:", e);
    return null;
  }
};

// ---------- COMMON WORKSHOP HEADER ----------
const addWorkshopHeader = (doc, logoMeta, shopAddress) => {
  const pageW = Number(doc.internal.pageSize.getWidth());
  const marginX = 14;
  const topY = 18;
  let headerBottomY = topY;

  if (logoMeta && logoMeta.dataUrl) {
    try {
      doc.addImage(
        logoMeta.dataUrl,
        "PNG",
        marginX,
        topY,
        logoMeta.logoWmm,
        logoMeta.logoHmm
      );
      headerBottomY = topY + logoMeta.logoHmm;
    } catch (e) {
      console.warn("Workshop header logo failed:", e);
    }
  }

  if (shopAddress?.trim()) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const lines = doc.splitTextToSize(shopAddress.trim(), 80);
    const addrY = topY + 4;
    doc.text(lines, pageW - marginX, addrY, { align: "right" });

    const approxLineHeight = 4;
    const addrBottom = addrY + lines.length * approxLineHeight;
    headerBottomY = Math.max(headerBottomY, addrBottom);
  }

  return { marginX, pageW, headerBottomY };
};


/* --------------------------- WORKSHOP FOOTER --------------------------- */
const addWorkshopFooter = (doc) => {
  const pageCount = doc.getNumberOfPages();
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(150);

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.text(
      "Powered by FabriQuote",
      pageW / 2,
      pageH - 10,
      { align: "center" }
    );
  }

  doc.setTextColor(0);
};
/* --------------------------- CUSTOMERPDF (FULL PCS) --------------------------- *//* --------------------------- CUSTOMERPDF (FULL PCS) --------------------------- */

const generateCustomerPDF = async () => {
  const { jsPDF, autoTable } = await loadPdfRuntime();
  const liveActionPayload = buildLiveCurtainQuotePayload({
    action: "customerPdf",
  });
  traceLiveActionPayload(liveActionPayload);
  guardStaleActionPayload(liveActionPayload, liveActionPayload.displaySummaryTotals);
  const {
    rows,
    quoteNo,
    useHalfPieceReuse,
    passHalfPieceSavingsToCustomer,
    getRowCalc: getCurtainProductionAdjustedRowCalc,
    displaySummaryTotals: pdfSummaryTotals,
    curtainCanonicalFabricSummaryRows,
  } = liveActionPayload;
  const itemEntries = rows.map((row, index) => ({ row, index }));
  const measurementEntries = itemEntries.filter(({ row }) => !isCurtainAccessoryRow(row));
  const accessoryEntries = itemEntries.filter(({ row }) => isCurtainAccessoryRow(row));
  const generatedAt = new Date();
  const generatedAtIso = generatedAt.toISOString();
  const generatedStamp = `${generatedAt.getFullYear()}${String(generatedAt.getMonth() + 1).padStart(2, "0")}${String(
    generatedAt.getDate()
  ).padStart(2, "0")}-${String(generatedAt.getHours()).padStart(2, "0")}${String(
    generatedAt.getMinutes()
  ).padStart(2, "0")}`;
  const pdfMode = isFrozenLegacyCurtainQuote ? "saved" : "live";
  const pdfCalculationVersion = CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION;
  const customerPdfMeasurementRows = measurementEntries.map(({ row, index }) => {
    const calc = getCurtainProductionAdjustedRowCalc(row, index);
    const billingTrace = getCurtainCustomerBillingFabricTrace(row, index, calc);
    return {
      row,
      index,
      calc,
      ...billingTrace,
      newFabricNeededMeters: round2(Number(calc.newFabricNeededMeters ?? 0)),
      pdfMeterSource: billingTrace.pdfSourceField,
    };
  });
  const customerPdfBillableMetersByFabricKey = customerPdfMeasurementRows.reduce((acc, entry) => {
    const fabricKey = normalizeFabricKey(entry.fabricKey);
    if (!fabricKey) return acc;
    acc[fabricKey] = round2(
      Number(acc[fabricKey] || 0) + Number(entry.customerBillableFabricMeters || 0)
    );
    return acc;
  }, {});
  const customerPdfFabricSummaryRows = curtainCanonicalFabricSummaryRows.map((summary) => {
    const fabricKey =
      summary.normalizedKey ||
      summary.fabricKey ||
      normalizeFabricKey(summary.fabricCode || summary.displayName || "");
    const rowSumCustomerBillable = round2(Number(customerPdfBillableMetersByFabricKey[fabricKey] || 0));
    const fabricGroupCustomerBillableMeters = round2(
      Number(
        summary.fabricGroupCustomerBillableFabricMeters ??
          summary.customerBillableFabricMeters ??
          0
      )
    );
    const uiSummaryCustomerBillableMeters = round2(
      Number(
        summary.uiSummaryCustomerBillableFabricMeters ??
          summary.customerBillableFabricMeters ??
          0
      )
    );
    const pdfBillableFabricPrinted = uiSummaryCustomerBillableMeters;
    return {
      ...summary,
      fabricKey,
      customerBillableFabricMeters: pdfBillableFabricPrinted,
      pdfBillableFabricPrinted,
      pdfBillableSource: "UI Fabric Summary customerBillableFabricMeters",
      rowSumCustomerBillable,
      fabricGroupCustomerBillableMeters,
      uiSummaryCustomerBillableMeters,
    };
  });

  if (IS_DEV_UI_ENABLED) {
    customerPdfMeasurementRows.forEach((entry) => {
      console.log("CUSTOMER_PDF_FABRIC_ROW_SOURCE_AUDIT", {
        quoteNo,
        rowId: entry.rowId,
        itemName: entry.itemName,
        product: entry.product,
        fabricCode: entry.fabricCode,
        widthIn: entry.widthIn,
        heightIn: entry.heightIn,
        fabricWidthIn: entry.fabricWidthIn,
        usableWidthDeductionIn: entry.usableWidthDeductionIn,
        fullness: entry.fullness,
        rawPanels: entry.rawPanels,
        floorPanels: entry.floorPanels,
        halfStepProductionPanels: entry.halfStepProductionPanels,
        wholeBillingPanels: entry.wholeBillingPanels,
        cutHeightIn: entry.cutHeightIn,
        fullOrderedFabricMeters: entry.fullOrderedFabricMeters,
        customerBillableFabricMeters: entry.customerBillableFabricMeters,
        workshopFabricRequiredMeters: entry.workshopFabricRequiredMeters,
        rowNewFabricMeters: entry.rowNewFabricMeters,
        newFullCuts: entry.newFullCuts,
        cutFabricMeters: entry.cutFabricMeters,
        liveScreenOrderedFabricMeters: entry.liveScreenOrderedFabricMeters,
        pdfOrderedFabricPrinted: entry.pdfOrderedFabricPrinted,
        pdfSourceField: entry.pdfSourceField,
        useHalfPieceReuse,
        passHalfPieceSavingsToCustomer,
      });
      console.log("CUSTOMER_PDF_ROW_METER_SOURCE_TRACE", {
        quoteNo,
        rowId: entry.rowId,
        itemName: entry.itemName,
        fabricCode: entry.fabricCode,
        product: entry.product,
        liveScreenOrderedFabricMeters: entry.liveScreenOrderedFabricMeters,
        customerBillableFabricMeters: entry.customerBillableFabricMeters,
        fullOrderedFabricMeters: entry.fullOrderedFabricMeters,
        workshopFabricRequiredMeters: entry.workshopFabricRequiredMeters,
        newFabricNeededMeters: entry.newFabricNeededMeters,
        cutFabricMeters: entry.cutFabricMeters,
        rowNewFabricMeters: entry.rowNewFabricMeters,
        pdfOrderedFabricPrinted: entry.pdfOrderedFabricPrinted,
        pdfMeterSource: entry.pdfMeterSource,
        useHalfPieceReuse,
        passHalfPieceSavingsToCustomer,
      });
      if (
        passHalfPieceSavingsToCustomer === false &&
        Math.abs(entry.pdfOrderedFabricPrinted - entry.customerBillableFabricMeters) > 0.05
      ) {
        console.error("CUSTOMER_PDF_WRONG_FABRIC_SOURCE", {
          quoteNo,
          rowId: entry.rowId,
          itemName: entry.itemName,
          pdfOrderedFabricPrinted: entry.pdfOrderedFabricPrinted,
          customerBillableFabricMeters: entry.customerBillableFabricMeters,
          pdfMeterSource: entry.pdfMeterSource,
        });
      }
    });

    customerPdfFabricSummaryRows.forEach((summary) => {
      const summaryRows = customerPdfMeasurementRows.filter(
        (entry) => normalizeFabricKey(entry.fabricKey) === summary.fabricKey
      );
      console.log("CUSTOMER_PDF_FABRIC_SUMMARY_TRACE", {
        quoteNo,
        fabricKey: summary.fabricKey,
        fabricCode: summary.fabricCode || summary.displayName || "",
        brandName: summary.brandName || "",
        cutFabricMeters: Number(summary.cutFabricMeters || 0),
        fullOrderedFabricMeters: Number(summary.fullOrderedFabricMeters || 0),
        halfReusedMeters: Number(summary.halfReusedMeters || 0),
        newFabricNeededMeters: Number(summary.newFabricNeededMeters || 0),
        workshopRequiredMeters: Number(summary.workshopFabricRequiredMeters || 0),
        customerBillableFabricMeters: Number(summary.customerBillableFabricMeters || 0),
        pdfBillableFabricPrinted: Number(summary.pdfBillableFabricPrinted || 0),
        sourceUsed: summary.pdfBillableSource,
        rowSumCustomerBillable: summary.rowSumCustomerBillable,
        fabricGroupCustomerBillableMeters: summary.fabricGroupCustomerBillableMeters,
        uiSummaryCustomerBillableMeters: summary.uiSummaryCustomerBillableMeters,
      });
      const printedVsRowDelta = round2(
        Number(summary.pdfBillableFabricPrinted || 0) -
          Number(summary.rowSumCustomerBillable || 0)
      );
      const groupVsRowDelta = round2(
        Number(summary.fabricGroupCustomerBillableMeters || 0) -
          Number(summary.rowSumCustomerBillable || 0)
      );
      const uiVsRowDelta = round2(
        Number(summary.uiSummaryCustomerBillableMeters || 0) -
          Number(summary.rowSumCustomerBillable || 0)
      );
      const groupVsUiDelta = round2(
        Number(summary.fabricGroupCustomerBillableMeters || 0) -
          Number(summary.uiSummaryCustomerBillableMeters || 0)
      );
      if (
        Math.abs(printedVsRowDelta) > 0.05 ||
        Math.abs(groupVsRowDelta) > 0.05 ||
        Math.abs(uiVsRowDelta) > 0.05 ||
        Math.abs(groupVsUiDelta) > 0.05
      ) {
        console.error("CUSTOMER_PDF_FABRIC_SUMMARY_MISMATCH", {
          quoteNo,
          fabricKey: summary.fabricKey,
          fabricCode: summary.fabricCode || summary.displayName || "",
          rowSumCustomerBillable: summary.rowSumCustomerBillable,
          fabricGroupCustomerBillable: summary.fabricGroupCustomerBillableMeters,
          uiSummaryCustomerBillable: summary.uiSummaryCustomerBillableMeters,
          pdfBillableFabricPrinted: summary.pdfBillableFabricPrinted,
          preferredPrintedSource: summary.pdfBillableSource,
          deltas: {
            printedVsRow: printedVsRowDelta,
            fabricGroupVsRow: groupVsRowDelta,
            uiVsRow: uiVsRowDelta,
            fabricGroupVsUi: groupVsUiDelta,
          },
          didPreferUiSummaryValue:
            Math.abs(groupVsUiDelta) > 0.05 || Math.abs(printedVsRowDelta) > 0.05,
          passHalfPieceSavingsToCustomer,
          rowLevelDebug: summaryRows.map((entry) => ({
            rowId: entry.rowId,
            itemName: entry.itemName,
            product: entry.product,
            widthIn: entry.widthIn,
            heightIn: entry.heightIn,
            fabricWidthIn: entry.fabricWidthIn,
            usableWidthDeductionIn: entry.usableWidthDeductionIn,
            fullness: entry.fullness,
            rawPanels: entry.rawPanels,
            floorPanels: entry.floorPanels,
            halfStepProductionPanels: entry.halfStepProductionPanels,
            wholeBillingPanels: entry.wholeBillingPanels,
            cutHeightIn: entry.cutHeightIn,
            pdfOrderedFabricPrinted: entry.pdfOrderedFabricPrinted,
            customerBillableFabricMeters: entry.customerBillableFabricMeters,
            fullOrderedFabricMeters: entry.fullOrderedFabricMeters,
            workshopFabricRequiredMeters: entry.workshopFabricRequiredMeters,
            rowNewFabricMeters: entry.rowNewFabricMeters,
            newFullCuts: entry.newFullCuts,
            cutFabricMeters: entry.cutFabricMeters,
            pdfSourceField: entry.pdfSourceField,
          })),
        });
      }
    });
  }

  //  Detect if ANY row has fabric discount
  const hasAnyFabricDiscount = customerPdfMeasurementRows.some(({ row: r, calc: c }) => {
    return Number(r.discount) > 0 && (c.discountScope || "line_total") !== "none";
  });

  /* ------------ PDF INIT ------------ */
  const doc = new jsPDF({
    unit: "mm",
    format: "a4",
    compress: true,
  });

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const marginX = 14;
  const topY = 18;

  /* ------------ LOGO ------------ */
  let logoWmm = 0;
  let logoHmm = 0;

  try {
    const d = await getLogoDataURL();
    if (d) {
      const img = new Image();
      img.src = d;
      await img.decode();

      const maxPixels = 400;
      const scale = Math.min(
        1,
        maxPixels / Math.max(img.width, img.height)
      );

      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas
        .getContext("2d")
        .drawImage(img, 0, 0, canvas.width, canvas.height);

      const logoData = canvas.toDataURL("image/png");

      const maxWmm = 80;
      const maxHmm = 40;
      const r = canvas.width / canvas.height;

      logoWmm = maxWmm;
      logoHmm = maxWmm / r;
      if (logoHmm > maxHmm) {
        logoHmm = maxHmm;
        logoWmm = maxHmm * r;
      }

      doc.addImage(
        logoData,
        "PNG",
        marginX,
        topY,
        logoWmm,
        logoHmm
      );
    }
  } catch {}

  /* ------------ ADDRESS ------------ */
  let headerBottomY = topY + logoHmm;

  if (shopAddress?.trim()) {
    doc.setFontSize(10);
    const lines = doc.splitTextToSize(shopAddress.trim(), 80);
    const addrY = topY + 12;
    doc.text(lines, pageW - marginX, addrY, {
      align: "right",
    });
    headerBottomY = Math.max(
      headerBottomY,
      addrY + lines.length * 4
    );
  }

  /* ------------ DIVIDER ------------ */
  const lineY = headerBottomY + 10;
  doc.line(marginX, lineY, pageW - marginX, lineY);

  let y = lineY + 8;

  /* ------------ HEADER PANEL ------------ */
  const humanDate = (() => {
    const displayDate = resolveQuoteDateForDisplay(
      {
        ...selectedQuote,
        quoteDate: date || selectedQuote?.quoteDate,
        date: date || selectedQuote?.date,
      },
      todayISO()
    );
    return displayDate || date || todayISO();
  })();

  doc.setFillColor(245, 245, 248);
  doc.roundedRect(
    marginX + 6,
    y,
    pageW - marginX * 2 - 6,
    38,
    3,
    3,
    "F"
  );

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(22, 61, 174);
  doc.text("Customer Quotation", marginX + 10, y + 8);

  doc.setFontSize(10);
  doc.setTextColor(0);

  doc.text("Customer:", marginX + 10, y + 18);
  doc.text(customerName || "", marginX + 40, y + 18);

  doc.text("Quote No:", marginX + 10, y + 25);
  doc.text(quoteNo || "", marginX + 40, y + 25);

  doc.text("Date:", marginX + 10, y + 32);
  doc.text(humanDate, marginX + 40, y + 32);

  y += 46;

  /* ------------ TABLE HEAD ------------ */
  const tableHead = [
    "Item",
    "Product",
    "Fabric / Code",
    "W",
    "H",
    "MRP/m",
  ];

  if (hasAnyFabricDiscount) {
    tableHead.push("Disc. %");
  }

  tableHead.push(
    "Rate/sqft",
    "Stitch",
    "Ordered Fabric (m)",
    "Total"
  );

  /* ------------ TABLE BODY ------------ */
  const tableBody = customerPdfMeasurementRows.map(({ row: r, calc: c, pdfOrderedFabricPrinted }) => {

    const row = [
      r.item || "",
      r.productType || "",
      getCurtainFabricDisplayWithBrand(r) || "",
     // WIDTH shown in selected unit
displayValue(r.width, unit) + " " + unitLabel(unit),

// HEIGHT shown in selected unit
displayValue(r.height, unit) + " " + unitLabel(unit),

      c.isCurtain || c.isRoman
        ? formatPdfMoney(r.mrp || 0, currency)
        : "",
    ];

    if (hasAnyFabricDiscount) {
      row.push(
        Number(r.discount) > 0 && (c.discountScope || "line_total") !== "none"
          ? `${r.discount}%`
          : ""
      );
    }

   row.push(
  (c.isRoman || c.isBlind) ? formatPdfMoney(r.sqftRate || 0, currency) : ""
);

const customerPdfTrackConfig = c.trackConfig || {};
const customerTrackUnit =
  customerPdfTrackConfig.totalSegmentLengthUnit ||
  customerPdfTrackConfig.segmentUnit ||
  getRowMeasurementUnit(r, unit);
const customerTrackSegments = formatTrackSegmentsInline(customerPdfTrackConfig);
const customerTrackLengthLabel =
  Number(customerPdfTrackConfig.totalSegmentLength || 0) > 0
    ? `${formatLengthWithUnit(
        customerPdfTrackConfig.totalSegmentLength || 0,
        customerTrackUnit
      )} / ${Number(customerPdfTrackConfig.totalTrackLengthFt || 0).toFixed(2)} running ft`
    : Number(customerPdfTrackConfig.totalTrackLengthFt || 0) > 0
    ? `${Number(customerPdfTrackConfig.totalTrackLengthFt || 0).toFixed(2)} running ft`
    : "";
const customerPdfTrackParts =
  customerPdfTrackConfig?.isBended
    ? [
        `Shape: ${getBendedTrackShapeMeta(customerPdfTrackConfig.shape).label}`,
        customerPdfTrackConfig.bendLocationSummary
          ? `Bend Location: ${customerPdfTrackConfig.bendLocationSummary}`
          : "",
        customerTrackSegments ? `Segments: ${customerTrackSegments}` : "",
        customerTrackLengthLabel ? `Track Length: ${customerTrackLengthLabel}` : "",
        `${Number(customerPdfTrackConfig.bendCount || 0)} bend${
          Number(customerPdfTrackConfig.bendCount || 0) === 1 ? "" : "s"
        }${
          customerPdfTrackConfig.angleDeg
            ? ` at ${Number(customerPdfTrackConfig.angleDeg).toFixed(0)} deg`
            : ""
        }`,
        Number(customerPdfTrackConfig.bendAmount || 0) > 0
          ? `Bend Charge: ${Number(customerPdfTrackConfig.bendCount || 0)} x ${formatPdfMoney(
              customerPdfTrackConfig.bendChargePerBend || 0,
              currency
            )} = ${formatPdfMoney(customerPdfTrackConfig.bendAmount || 0, currency)}`
          : "",
        customerPdfTrackConfig.notes ? `Track Notes: ${customerPdfTrackConfig.notes}` : "",
      ].filter(Boolean)
    : [];

row.push(
  c.isCurtain || c.trackConfig?.isBended
    ? [r.stitchingStyle || "", ...customerPdfTrackParts].filter(Boolean).join("\n")
    : ""
);

row.push(
  (c.isCurtain || c.isRoman) ? Number(pdfOrderedFabricPrinted || 0).toFixed(2) : ""
);

row.push(
  formatPdfMoney(c.rowTotal, currency)
);

    return row;
  });

  autoTable(doc, {
    startY: y,
    margin: { left: marginX, right: marginX },
    head: [tableHead],
    body: tableBody,
    rowPageBreak: "avoid",
    styles: {
      fontSize: 8.8,
      cellPadding: 2.4,
      textColor: [15, 23, 42],
      lineColor: [226, 232, 240],
      lineWidth: 0.1,
      valign: "middle",
    },
    headStyles: {
      fillColor: [25, 76, 180],
      textColor: 255,
      fontStyle: "bold",
    },
    alternateRowStyles: {
      fillColor: [248, 250, 255],
    },
    columnStyles: {
      3: { halign: "right" },
      4: { halign: "right" },
      5: { halign: "right" },
      ...(hasAnyFabricDiscount ? { 6: { halign: "right" } } : {}),
      [hasAnyFabricDiscount ? 7 : 6]: { halign: "right" },
      [hasAnyFabricDiscount ? 9 : 8]: { halign: "right" },
      [hasAnyFabricDiscount ? 10 : 9]: { halign: "right" },
    },
  });

  const liningDetailRows = measurementEntries
    .map(({ row: r, index }) => {
      const c = getCurtainProductionAdjustedRowCalc(r, index);
      const lining =
        c.lining ||
        calculateCurtainRowLining(r, 0, false, {
          sqft: c.adjSqft ?? c.billableSqft ?? 0,
        });
      if (!curtainRowSupportsLining(r, c) || !lining.enabled) return null;

      const liningBrandName =
        sanitizeEditableText(lining.brandName || "") ||
        getFabricBrandNameById(lining.brandId);
      const liningFabricLabel = [liningBrandName, sanitizeEditableText(lining.fabricCode || "")]
        .filter(Boolean)
        .join(" / ");
      const isSqftLining = lining.chargeMethod === "sqft";
      const qtyLabel = isSqftLining
        ? `${Number(lining.sqft || 0).toFixed(2)} sqft`
        : `${Number(lining.billableMeters || 0).toFixed(2)} m`;
      const rateValue = isSqftLining
        ? parseNonNegativeNumberInput(lining.ratePerSqft, 0)
        : parseNonNegativeNumberInput(lining.ratePerMeter, 0);

      return [
        r.item || "",
        getCurtainFabricDisplayWithBrand(r) || "",
        getCurtainLiningLabel(lining),
        liningFabricLabel,
        isSqftLining ? "Per sqft" : "Per meter",
        qtyLabel,
        formatPdfMoney(rateValue, currency),
        formatPdfMoney(lining.amount || 0, currency),
      ];
    })
    .filter(Boolean);

  if (liningDetailRows.length > 0) {
    y = doc.lastAutoTable.finalY + 10;
    if (y > pageH - 38) {
      doc.addPage();
      y = 18;
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(22, 61, 174);
    doc.text("Lining Details", marginX, y);
    y += 5;

    autoTable(doc, {
      startY: y,
      margin: { left: marginX, right: marginX },
      head: [["Item", "Main Fabric", "Lining Type", "Lining Fabric / Code", "Method", "Qty", "Rate", "Amount"]],
      body: liningDetailRows,
      rowPageBreak: "avoid",
      styles: {
        fontSize: 8.4,
        cellPadding: 2.2,
        textColor: [15, 23, 42],
        lineColor: [226, 232, 240],
        lineWidth: 0.1,
        valign: "middle",
      },
      headStyles: {
        fillColor: [30, 64, 175],
        textColor: 255,
        fontStyle: "bold",
      },
      alternateRowStyles: {
        fillColor: [248, 250, 255],
      },
      columnStyles: {
        5: { halign: "right" },
        6: { halign: "right" },
        7: { halign: "right" },
      },
    });
  }

  if (accessoryEntries.length > 0) {
    y = doc.lastAutoTable.finalY + 10;
    if (y > pageH - 36) {
      doc.addPage();
      y = 18;
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(22, 61, 174);
    doc.text("Motors / Tracks / Accessories", marginX, y);
    y += 5;

    const accessoryHead = [
      "#",
      "Item",
      "Product",
      "Description",
      "Qty / Length",
      "Unit",
      "Rate",
      "Discount",
      "GST",
      "Total",
    ];
    const accessoryBody = accessoryEntries.map(({ row: r, index }, displayIndex) => {
      const c = getCurtainProductionAdjustedRowCalc(r, index);
      const calcType = c.calculationType || r.calculationType || "";
      const isLengthRow = calcType === "running_length_rate";
      const isManualRow = calcType === "manual_amount";
      const qtyValue = isLengthRow
        ? Number(r.length || c.runningLength || 0)
        : isManualRow
        ? Number(r.qty || r.quantity || 1)
        : Number(r.qty || r.quantity || c.quantity || 0);
      const unitValue = isLengthRow
        ? r.lengthUnit || c.lengthUnit || "ft"
        : r.unit || c.unit || "pcs";
      const rateValue = isLengthRow
        ? r.ratePerLength || c.ratePerLength || 0
        : isManualRow
        ? r.manualAmount || 0
        : r.unitRate || c.unitRate || 0;

      return [
        String(displayIndex + 1),
        r.item || "",
        r.productName || r.productType || r.product || "",
        r.description || r.modelCode || r.notes || "",
        Number(qtyValue || 0).toFixed(qtyValue % 1 ? 2 : 0),
        unitValue,
        formatPdfMoney(rateValue || 0, currency),
        Number(r.discount || 0) > 0 && (c.discountScope || "line_total") !== "none"
          ? `${r.discount}%`
          : "",
        `${resolveCurtainAccessoryRowGstRate(r)}%`,
        formatPdfMoney(c.rowTotal || 0, currency),
      ];
    });

    autoTable(doc, {
      startY: y,
      margin: { left: marginX, right: marginX },
      head: [accessoryHead],
      body: accessoryBody,
      rowPageBreak: "avoid",
      styles: {
        fontSize: 8.4,
        cellPadding: 2.2,
        textColor: [15, 23, 42],
        lineColor: [226, 232, 240],
        lineWidth: 0.1,
        valign: "middle",
      },
      headStyles: {
        fillColor: [15, 23, 42],
        textColor: 255,
        fontStyle: "bold",
      },
      alternateRowStyles: {
        fillColor: [248, 250, 255],
      },
      columnStyles: {
        0: { halign: "center", cellWidth: 8 },
        4: { halign: "right" },
        6: { halign: "right" },
        7: { halign: "right" },
        8: { halign: "right" },
        9: { halign: "right" },
      },
    });
  }

  if (customerPdfFabricSummaryRows.length > 0) {
    y = Number(doc.lastAutoTable?.finalY || y) + 10;
    if (y > pageH - 42) {
      doc.addPage();
      y = 18;
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(22, 61, 174);
    doc.text("Fabric Summary", marginX, y);
    y += 5;

    autoTable(doc, {
      startY: y,
      margin: { left: marginX, right: marginX },
      head: [["Brand", "Fabric / SR", "Used in", "Billable Fabric", "Repeat", "Notes"]],
      body: customerPdfFabricSummaryRows.map((summary) => [
        summary.brandName || "",
        summary.fabricCode || summary.displayName || "",
        (summary.usedInItems || []).slice(0, 6).join(", "),
        `${Number(summary.pdfBillableFabricPrinted ?? summary.customerBillableFabricMeters ?? 0).toFixed(2)} m`,
        getRepeatSummaryDisplay(summary),
        [
          summary.notes || "",
          useHalfPieceReuse &&
          passHalfPieceSavingsToCustomer &&
          Number(curtainHalfPieceAudit?.totalHalfPiecesReused || 0) > 0
            ? "Half-piece savings applied"
            : "",
        ]
          .filter(Boolean)
          .join("\n"),
      ]),
      rowPageBreak: "avoid",
      styles: {
        fontSize: 8.4,
        cellPadding: 2.2,
        textColor: [15, 23, 42],
        lineColor: [226, 232, 240],
        lineWidth: 0.1,
        valign: "top",
      },
      headStyles: {
        fillColor: [22, 61, 174],
        textColor: 255,
        fontStyle: "bold",
      },
      alternateRowStyles: {
        fillColor: [248, 250, 255],
      },
      columnStyles: {
        3: { halign: "right" },
      },
    });
  }

  /* ------------ SUMMARY CARD ------------ */
  let cardY = doc.lastAutoTable.finalY + 12;
  const pdfGstRate = normalizeCurtainGstRate(
    pdfSummaryTotals.quoteGstRate ?? pdfSummaryTotals.gstRate
  );
  const measurementSubtotal = round2(
    customerPdfMeasurementRows.reduce(
      (sum, { calc }) =>
        sum + Number(calc.rowTotal || 0),
      0
    )
  );
  const accessorySubtotal = round2(
    accessoryEntries.reduce(
      (sum, { row, index }) =>
        sum + Number(getCurtainProductionAdjustedRowCalc(row, index).rowTotal || 0),
      0
    )
  );
  const sub = Number(pdfSummaryTotals.subTotal || 0).toFixed(2);
  const discVal = Number(pdfSummaryTotals.discountValue || 0);
  const discAmount = Number(pdfSummaryTotals.discountAmount || 0);
  const taxable = Number(pdfSummaryTotals.taxableAmount || 0).toFixed(2);
  const gstAmount = Number(pdfSummaryTotals.gstAmount || 0).toFixed(2);
  const totalAfterGst = Number(
    pdfSummaryTotals.totalAfterGst ??
      Number(pdfSummaryTotals.taxableAmount || 0) + Number(pdfSummaryTotals.gstAmount || 0)
  ).toFixed(2);
  const grand = Number(pdfSummaryTotals.grandTotal || 0).toFixed(2);
  const pdfSubtotal = round2(measurementSubtotal + accessorySubtotal);
  const screenSubtotal = round2(Number(pdfSummaryTotals.subTotal || 0));
  const screenGrandTotal = round2(Number(pdfSummaryTotals.grandTotal || 0));
  const pdfGrandTotal = screenGrandTotal;

  if (IS_DEV_UI_ENABLED) {
    const fabricSummaryTotals = customerPdfFabricSummaryRows.reduce(
      (acc, summary) => ({
        cutFabric: round2(acc.cutFabric + Number(summary.cutFabricMeters || 0)),
        fullOrdered: round2(acc.fullOrdered + Number(summary.fullOrderedFabricMeters || 0)),
        newFabricNeeded: round2(acc.newFabricNeeded + Number(summary.newFabricNeededMeters || 0)),
        workshopRequired: round2(
          acc.workshopRequired + Number(summary.workshopFabricRequiredMeters || 0)
        ),
        customerBillable: round2(
          acc.customerBillable +
            Number(summary.pdfBillableFabricPrinted ?? summary.customerBillableFabricMeters ?? 0)
        ),
      }),
      {
        cutFabric: 0,
        fullOrdered: 0,
        newFabricNeeded: 0,
        workshopRequired: 0,
        customerBillable: 0,
      }
    );
    console.log("CUSTOMER_PDF_GENERATION_TRACE", {
      quoteNo,
      generatedAt: generatedAtIso,
      pdfMode,
      calculationVersion: pdfCalculationVersion,
      useHalfPieceReuse,
      passHalfPieceSavingsToCustomer,
      grandTotal: screenGrandTotal,
      fabricSummaryTotals,
      filenameStamp: generatedStamp,
    });
    console.log("CUSTOMER_PDF_GENERATION_VERSION_TRACE", {
      quoteNo,
      generatedAt: generatedAtIso,
      pdfMode,
      calculationVersion: pdfCalculationVersion,
      useHalfPieceReuse,
      passHalfPieceSavingsToCustomer,
      grandTotal: screenGrandTotal,
      fabricSummaryTotals,
    });
    console.log("CUSTOMER_PDF_TOTAL_SOURCE_AUDIT", {
      quoteNo,
      screenSubtotal,
      pdfSubtotal,
      screenGrandTotal,
      pdfGrandTotal,
      customerBillableFabricTotal: fabricSummaryTotals.customerBillable,
      pdfFabricSummaryTotal: fabricSummaryTotals.customerBillable,
      passHalfPieceSavingsToCustomer,
      source: "curtainDisplaySummaryTotals || summaryTotals",
    });
    if (
      Math.abs(pdfSubtotal - screenSubtotal) > 0.05 ||
      Math.abs(pdfGrandTotal - screenGrandTotal) > 0.05
    ) {
      console.error("CUSTOMER_PDF_TOTAL_MISMATCH", {
        quoteNo,
        screenSubtotal,
        pdfSubtotal,
        screenGrandTotal,
        pdfGrandTotal,
        difference: {
          subtotal: round2(pdfSubtotal - screenSubtotal),
          grandTotal: round2(pdfGrandTotal - screenGrandTotal),
        },
      });
    }
  }
  const hasPdfDiscount = discAmount > 0 || discVal > 0;
  const hasPdfMeasurementGst =
    Number(pdfSummaryTotals.measurementSubtotalBeforeDiscount || 0) > 0;
  const hasPdfAccessoryGst =
    Number(pdfSummaryTotals.accessorySubtotalBeforeDiscount || 0) > 0;
  const hasPdfTotalGst =
    pdfSummaryTotals.hasMixedGstRates ||
    (Number(pdfSummaryTotals.measurementGstAmount || 0) > 0 &&
      Number(pdfSummaryTotals.accessoryGstAmount || 0) > 0);
  const cardH =
    75 +
    (measurementSubtotal > 0 ? 7 : 0) +
    (accessorySubtotal > 0 ? 7 : 0) +
    (hasPdfDiscount ? 7 : 0) +
    (hasPdfMeasurementGst ? 7 : 0) +
    (hasPdfAccessoryGst ? 7 : 0) +
    (hasPdfTotalGst ? 7 : 0);

  if (cardY + cardH > pageH - 20) {
    doc.addPage();
    cardY = 20;
  }

  const cardX = marginX;
  const cardW = pageW - marginX * 2;

  doc.setDrawColor(180);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(cardX, cardY, cardW, cardH, 4, 4, "DF");

  let sy = cardY + 10;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Summary", cardX + 4, sy);

  sy += 8;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);

  if (measurementSubtotal > 0) {
    doc.text("Curtains & Blinds:", cardX + 4, sy);
    doc.text(formatPdfMoney(measurementSubtotal, currency), cardX + cardW - 6, sy, {
      align: "right",
    });
    sy += 7;
  }

  if (accessorySubtotal > 0) {
    doc.text("Accessories:", cardX + 4, sy);
    doc.text(formatPdfMoney(accessorySubtotal, currency), cardX + cardW - 6, sy, {
      align: "right",
    });
    sy += 7;
  }

  doc.text("Subtotal:", cardX + 4, sy);
  doc.text(formatPdfMoney(sub, currency), cardX + cardW - 6, sy, {
    align: "right",
  });

  sy += 7;
  doc.text("Taxable Amount:", cardX + 4, sy);
  doc.text(formatPdfMoney(taxable, currency), cardX + cardW - 6, sy, {
    align: "right",
  });

  if (hasPdfMeasurementGst) {
    sy += 7;
    const measurementGstLabel =
      accessorySubtotal > 0
        ? `GST (Curtains/Blinds @ ${pdfGstRate}%):`
        : `GST @ ${pdfGstRate}%:`;
    doc.text(measurementGstLabel, cardX + 4, sy);
    doc.text(
      formatPdfMoney(pdfSummaryTotals.measurementGstAmount || 0, currency),
      cardX + cardW - 6,
      sy,
      { align: "right" }
    );
  }

  if (hasPdfAccessoryGst) {
    sy += 7;
    const accessoryRates = pdfSummaryTotals.accessoryGstRates || [];
    const accessoryGstLabel =
      accessoryRates.length > 1
        ? "GST (Accessories mixed):"
        : accessoryRates.length === 1
        ? `GST (Accessories @ ${accessoryRates[0]}%):`
        : "GST (Accessories):";
    doc.text(accessoryGstLabel, cardX + 4, sy);
    doc.text(formatPdfMoney(pdfSummaryTotals.accessoryGstAmount || 0, currency), cardX + cardW - 6, sy, {
      align: "right",
    });
  }

  if (hasPdfTotalGst) {
    sy += 7;
    doc.setFont("helvetica", "bold");
    doc.text("Total GST:", cardX + 4, sy);
    doc.text(formatPdfMoney(gstAmount, currency), cardX + cardW - 6, sy, {
      align: "right",
    });
    doc.setFont("helvetica", "normal");
  }

  sy += 7;
  doc.setFont("helvetica", "bold");
  doc.text("Total After GST:", cardX + 4, sy);
  doc.text(formatPdfMoney(totalAfterGst, currency), cardX + cardW - 6, sy, {
    align: "right",
  });
  doc.setFont("helvetica", "normal");

  if (hasPdfDiscount) {
    sy += 7;
    doc.text("Post-GST Discount:", cardX + 4, sy);
    doc.text(
      pdfSummaryTotals.discountType === "percent"
        ? `- ${discVal}% / ${formatPdfMoney(discAmount, currency)}`
        : `- ${formatPdfMoney(discAmount, currency)}`,
      cardX + cardW - 6,
      sy,
      { align: "right" }
    );
  }

  const barY = cardY + cardH - 18;
  doc.setFillColor(37, 99, 235);
  doc.roundedRect(
    cardX + 6,
    barY,
    cardW - 12,
    11,
    3,
    3,
    "F"
  );

  doc.setFont("helvetica", "bold");
  doc.setTextColor(255);
  doc.text(
    Number(pdfSummaryTotals.gstAmount || 0) > 0 ? "Grand Total (Incl. GST):" : "Grand Total:",
    cardX + 10,
    barY + 7
  );
  doc.text(
    formatPdfMoney(grand, currency),
    cardX + cardW - 10,
    barY + 7,
    { align: "right" }
  );

  doc.setTextColor(0);

  /* ------------ FOOTER ------------ */
  doc.setFontSize(9);
  doc.setTextColor(150);
  doc.text(
    "Powered by FabriQuote",
    pageW / 2,
    pageH - 10,
    { align: "center" }
  );

  /* ------------ SAVE ------------ */
  const safeCustomerPdfName = (value) =>
    String(value || "quote")
      .trim()
      .replace(/[^\w-]+/g, "_")
      .replace(/^_+|_+$/g, "") || "quote";
  doc.save(
    `Customer-${safeCustomerPdfName(customerName || "Customer")}-${safeCustomerPdfName(
      quoteNo || "Draft"
    )}-${generatedStamp}.pdf`
  );
};


function resolveHalfPieceReuseSettings(options = {}) {
  return {
    ...DEFAULT_HALF_PIECE_REUSE_SETTINGS,
    ...(options?.halfPieceReuseSettings || {}),
  };
}

function getHalfPieceReuseFabricWidth(calc = {}, row = {}) {
  return round2(
    Number(
      calc?.calculationBreakdown?.curtainPieceCalc?.fabricWidthIn ??
        calc?.curtainPieceCalc?.fabricWidthIn ??
        row?.fabricWidthIn ??
        row?.fabricWidth ??
        0
    )
  );
}

function getHalfPieceReuseMatchKey(fabricKey = "", fabricWidthIn = 0) {
  const safeWidth = Number(fabricWidthIn || 0) > 0 ? round2(Number(fabricWidthIn)) : 0;
  return `${fabricKey || "missing-fabric"}::width:${safeWidth || "unknown"}`;
}

function isHalfPieceLeftoverCompatible(leftover = {}, requiredLengthM = 0, settings = {}) {
  const toleranceM = Math.max(0, Number(settings.heightToleranceInches || 0)) / 39.37;
  const leftoverLengthM = Number(leftover.length || 0);
  const requiredM = Number(requiredLengthM || 0);

  if (settings.heightMatchMode === "exact" || settings.heightMatchMode === "tolerance") {
    return Math.abs(leftoverLengthM - requiredM) <= toleranceM + HALF_PIECE_EPSILON;
  }

  if (settings.allowLargerPieceToBeTrimmed === false) {
    return Math.abs(leftoverLengthM - requiredM) <= toleranceM + HALF_PIECE_EPSILON;
  }

  return leftoverLengthM + toleranceM + HALF_PIECE_EPSILON >= requiredM;
}

function createEmptyHalfPieceAudit(options = {}) {
  return {
    enabled: options.enabled === true,
    settings: resolveHalfPieceReuseSettings(options),
    totalRowsConsidered: 0,
    totalRowsEligible: 0,
    totalHalfPiecesCreated: 0,
    totalHalfPiecesAvailable: 0,
    totalHalfPiecesReusable: 0,
    totalHalfPiecesReused: 0,
    totalHalfPanelNeeds: 0,
    totalPairsCreated: 0,
    totalMetersSaved: 0,
    fabricGroups: {},
    skippedRows: [],
    reasons: [],
  };
}

function getOrCreateHalfPieceAuditGroup(audit, fabricKey, seed = {}) {
  const key = fabricKey || "__missing_fabric__";
  if (!audit.fabricGroups[key]) {
    audit.fabricGroups[key] = {
      fabricKey: key,
      fabricCode: seed.fabricLabel || seed.fabricCode || "",
      brandName: seed.brandName || "",
      fabricWidths: [],
      rowsConsidered: 0,
      rowsEligible: 0,
      rowsIneligible: 0,
      halfPiecesCreated: 0,
      halfPiecesAvailable: 0,
      halfPiecesReusable: 0,
      halfPiecesReused: 0,
      halfPanelNeeds: 0,
      pairsCreated: 0,
      metersSaved: 0,
      reasons: [],
      rows: [],
    };
  }

  const group = audit.fabricGroups[key];
  if (seed.fabricLabel && !group.fabricCode) group.fabricCode = seed.fabricLabel;
  const width = Number(seed.fabricWidth || 0);
  if (width > 0 && !group.fabricWidths.includes(width)) {
    group.fabricWidths.push(width);
  }
  return group;
}

function finalizeHalfPieceAudit(audit = {}) {
  const groups = Object.values(audit.fabricGroups || {}).map((group) => {
    const reasons = Array.from(
      new Set(
        [
          ...(group.reasons || []),
          group.halfPiecesCreated <= 0
            ? "No half-piece rows were created from current row widths."
            : "",
          group.halfPiecesCreated > 0 && group.halfPiecesReused <= 0
            ? "Half pieces were available, but no later compatible row matched the same fabric, width, and cut-height rule."
            : "",
        ].filter(Boolean)
      )
    );
    return {
      ...group,
      fabricWidth:
        group.fabricWidths.length === 1
          ? group.fabricWidths[0]
          : group.fabricWidths.join(", "),
      reasons,
    };
  });

  const globalReasons = Array.from(
    new Set(
      [
        ...(audit.reasons || []),
        audit.enabled ? "" : "Half-piece reuse toggle is off.",
        audit.enabled && audit.totalHalfPiecesCreated <= 0
          ? "No half pieces were created from the current rows."
          : "",
        audit.enabled &&
        audit.totalHalfPiecesCreated > 0 &&
        audit.totalHalfPiecesReused <= 0
          ? "Half pieces were created, but no compatible later rows could reuse them."
          : "",
      ].filter(Boolean)
    )
  );

  return {
    ...audit,
    fabricGroups: groups,
    reasons: globalReasons,
  };
}

function buildReuseAwareDemands(sourceRows, activeRepeatSettings, options = {}) {
  const {
    targetFabricKey = null,
    enableReuse = true,
    debugTag = "",
    useProductWorkshopRules = true,
    getProductRules = null,
    productConfigsForRules = productConfigs,
    fabricCalcSettingsForRules = fabricCalcSettings,
    quoteFullnessOverridesForCalculation = quoteFullnessOverrides,
  } = options;
  const halfPieceReuseSettings = resolveHalfPieceReuseSettings(options);
  const fallbackProductRules = { config: null, workshopRules: {} };
  const resolveProductRulesForPlan =
    typeof getProductRules === "function"
      ? getProductRules
      : (row = {}) => {
          if (!useProductWorkshopRules) return fallbackProductRules;
          const config = resolveProductConfig(
            row,
            mergeProductConfigsWithDefaults(
              productConfigsForRules,
              fabricCalcSettingsForRules
            ),
            fabricCalcSettingsForRules
          );
          return {
            config,
            isLegacy: shouldUseLegacyProductCalculation(row, config),
            requiredFields: config?.requiredFields || {},
            calculationType: config?.calculationType || "legacy",
            workshopRules: config?.workshopRules || {},
            pdfRules: config?.pdfRules || {},
          };
        };

  const rowPlans = [];
  const demandMap = {};
  const fabricMetersByKey = {};
  const fabricSummaryByKey = {};
  const leftoversByKey = {};
  const pool = {};
  const reuseLog = [];
  const skippedReasons = [];
  const audit = createEmptyHalfPieceAudit({
    enabled: enableReuse,
    halfPieceReuseSettings,
  });
  let leftoverSequence = 0;

  const demandKey = (lengthM) =>
    Math.round(lengthM * 1000);
  const poolKey = (fabricMatchKey, lengthM) => `${fabricMatchKey}::${demandKey(lengthM)}`;
  const createLeftoverId = (fabricKey, lengthKey, rowIndex) =>
    `${fabricKey || "fabric"}-${lengthKey}-${rowIndex}-${leftoverSequence++}`;

  const sortedRows = [...sourceRows]
    .map((row, index) => {
      const calc = calcRow(row, activeRepeatSettings, {
        quoteFullnessOverrides: quoteFullnessOverridesForCalculation,
      });
      return { row, index, calc };
    })
    .sort((a, b) => {
      const lenA = Number(a.calc.cutCurtain || a.calc.cutRoman || 0);
      const lenB = Number(b.calc.cutCurtain || b.calc.cutRoman || 0);

      return lenB - lenA;
    });

  sortedRows.forEach(({ row, index, calc }) => {
    const rowIndex = index;
    const rowProductRules = useProductWorkshopRules
      ? resolveProductRulesForPlan(row)
      : fallbackProductRules;
    const rowWorkshopRules = rowProductRules.workshopRules || {};
    const includeFabricDemand =
      debugTag === "optimizer"
        ? rowWorkshopRules.includeRollOptimizer === true
        : rowWorkshopRules.includeInWorkshopPdf !== false &&
          rowWorkshopRules.includeFabricSummary !== false;
    const fabricLabel = getCurtainRowFabricLabel(row);
    const fabricKey = getCurtainRowFabricKey(row);
    const panelMath = getHalfPieceReusePiecePlan(row, calc, {
      productConfig: rowProductRules.config || null,
      quoteFullnessOverrides: quoteFullnessOverridesForCalculation,
    });
    const fabricWidthIn =
      Number(panelMath.fabricWidthIn || panelMath.fabricWidth || 0) ||
      getHalfPieceReuseFabricWidth(calc, row);
    const auditGroup = getOrCreateHalfPieceAuditGroup(audit, fabricKey, {
      fabricLabel,
      fabricWidth: fabricWidthIn,
      brandName: row?.fabricBrandName || row?.brandName || "",
    });
    audit.totalRowsConsidered += 1;
    auditGroup.rowsConsidered += 1;

    const recordSkippedRow = (reason, extra = {}) => {
      const skipped = {
        rowIndex,
        rowId: row?.id || row?.rowId || "",
        item: row?.item || row?.name || "",
        product: row?.productType || row?.product || "",
        fabricLabel,
        fabricKey,
        fabricWidthIn,
        reason,
        ...extra,
      };
      rowPlans[rowIndex] = null;
      skippedReasons.push(skipped);
      audit.skippedRows.push(skipped);
      auditGroup.rowsIneligible += 1;
      if (reason && !auditGroup.reasons.includes(reason)) {
        auditGroup.reasons.push(reason);
      }
      auditGroup.rows.push({
        ...skipped,
        pieces: 0,
        rawPanels: panelMath.rawPanels,
        wholePanels: panelMath.wholePanels,
        halfStepPanels: panelMath.halfStepPanels,
        hasHalfPanelRequirement: panelMath.hasHalfPanelRequirement,
        cutHeight: 0,
        eligibleForReuse: false,
        createsHalfPiece: false,
        halfPieceCreated: false,
        halfPieceReused: false,
        reasonIfNoReuse: reason,
      });
    };

    const logHalfPiecePanelMathTrace = (skipReason = "", eligibleForHalfPieceReuse = false) => {
      if (!IS_DEV_UI_ENABLED) return;
      console.log("HALF_PIECE_FABRIC_KEY_TRACE", {
        quoteNo,
        tag: debugTag || "default",
        rowId: row?.id || row?.rowId || "",
        itemName: row?.item || row?.name || "",
        rawFabricCode: getEditableFabricValue(row),
        brandId: row?.fabricBrandId || row?.brandId || "",
        brandName: row?.fabricBrandName || row?.brandName || "",
        normalizedFabricKey: fabricKey,
        fabricWidth: fabricWidthIn,
        repeatSettings: activeRepeatSettings?.[fabricKey] || {},
      });
      console.log("HALF_PIECE_PANEL_MATH_TRACE", {
        quoteNo,
        tag: debugTag || "default",
        rowId: row?.id || row?.rowId || "",
        itemName: row?.item || row?.name || "",
        product: row?.productType || row?.product || "",
        fabricCode: fabricLabel,
        fabricWidth: fabricWidthIn,
        usableWidthDeduction: panelMath.usableWidthDeductionIn,
        usableFabricWidth: panelMath.usableFabricWidthIn,
        width: panelMath.width,
        fullness: panelMath.fullness,
        fullnessSource: panelMath.fullnessSource,
        rawPanels: panelMath.rawPanels,
        snapshotRawPieces: panelMath.snapshotRawPieces,
        wholePanels: panelMath.wholePanels,
        halfStepPanels: panelMath.halfStepPanels,
        hasHalfPanelRequirement: panelMath.hasHalfPanelRequirement,
        cutHeight: Number(calc.cutCurtain || calc.cutRoman || 0),
        eligibleForHalfPieceReuse,
        skipReason,
      });
    };

    if (rowProductRules.config && !includeFabricDemand) {
      if (IS_DEV_UI_ENABLED) {
        console.log("HALF_PIECE_PANEL_MATH_TRACE", {
          quoteNo,
          tag: debugTag || "default",
          rowId: row?.id || row?.rowId || "",
          itemName: row?.item || row?.name || "",
          product: row?.productType || row?.product || "",
          fabricCode: fabricLabel,
          fabricWidth: fabricWidthIn,
          usableWidthDeduction: panelMath.usableWidthDeductionIn,
          usableFabricWidth: panelMath.usableFabricWidthIn,
          width: panelMath.width,
          fullness: panelMath.fullness,
          fullnessSource: panelMath.fullnessSource,
          rawPanels: panelMath.rawPanels,
          wholePanels: panelMath.wholePanels,
          halfStepPanels: panelMath.halfStepPanels,
          hasHalfPanelRequirement: panelMath.hasHalfPanelRequirement,
          cutHeight: Number(calc.cutCurtain || calc.cutRoman || 0),
          eligibleForHalfPieceReuse: false,
          skipReason:
            debugTag === "optimizer"
              ? "product not enabled for roll optimizer"
              : "product not enabled for fabric workshop demand",
        });
      }
      recordSkippedRow(
        debugTag === "optimizer"
          ? "product not enabled for roll optimizer"
          : "product not enabled for fabric workshop demand"
      );
      return;
    }

    if (!fabricKey) {
      if (IS_DEV_UI_ENABLED) {
        console.log("HALF_PIECE_PANEL_MATH_TRACE", {
          quoteNo,
          tag: debugTag || "default",
          rowId: row?.id || row?.rowId || "",
          itemName: row?.item || row?.name || "",
          product: row?.productType || row?.product || "",
          fabricCode: fabricLabel,
          fabricWidth: fabricWidthIn,
          usableWidthDeduction: panelMath.usableWidthDeductionIn,
          usableFabricWidth: panelMath.usableFabricWidthIn,
          width: panelMath.width,
          fullness: panelMath.fullness,
          rawPanels: panelMath.rawPanels,
          wholePanels: panelMath.wholePanels,
          halfStepPanels: panelMath.halfStepPanels,
          hasHalfPanelRequirement: panelMath.hasHalfPanelRequirement,
          cutHeight: Number(calc.cutCurtain || calc.cutRoman || 0),
          eligibleForHalfPieceReuse: false,
          skipReason: "missing fabric",
        });
      }
      recordSkippedRow("missing fabric");
      return;
    }
    if (targetFabricKey && fabricKey !== targetFabricKey) {
      logHalfPiecePanelMathTrace("fabric mismatch with selected optimizer fabric", false);
      recordSkippedRow("fabric mismatch with selected optimizer fabric");
      return;
    }
    if (!calc.isCurtain && !calc.isRoman) {
      logHalfPiecePanelMathTrace("not a curtain or roman row", false);
      recordSkippedRow("not a curtain or roman row");
      return;
    }

    const cutHeightIn = Number(calc.cutCurtain || calc.cutRoman || 0);
    if (!cutHeightIn || cutHeightIn <= 0) {
      logHalfPiecePanelMathTrace("invalid cut height", false);
      recordSkippedRow("invalid cut height");
      return;
    }
    logHalfPiecePanelMathTrace("", true);

    const cutLengthM =
      Math.round((cutHeightIn / 39.37) * 1000) / 1000;
    const cutLengthKey = demandKey(cutLengthM);
    const piecePlan = panelMath;
    const totalPieces = Number(piecePlan.reuseTotalPieces || 0);
    let fullCuts = Number(piecePlan.floorPieces || 0);
    const hasHalf = piecePlan.hasHalfPiece ? 1 : 0;
    const fabricMatchKey = getHalfPieceReuseMatchKey(fabricKey, fabricWidthIn);
    let reasonIfNoReuse = "";
    let reusedFromRowId = "";
    let reusedFromItem = "";

    audit.totalRowsEligible += 1;
    auditGroup.rowsEligible += 1;

    let reusedHalfCount = 0;
    let createdHalf = false;
    let createdLeftoverId = "";
    let reusedLeftoverId = "";

    if (hasHalf === 1 && enableReuse) {
      const poolEntriesForGroup = Object.entries(pool)
        .filter(([k]) => k.startsWith(fabricMatchKey + "::"))
        .map(([k, v]) => {
          const sep = k.lastIndexOf("::");
          const lenStr = sep >= 0 ? k.slice(sep + 2) : "0";
          return {
            key: k,
            lengthKey: Number(lenStr),
            leftovers: Array.isArray(v) ? v : [],
          };
        })
        .filter((p) => p.leftovers.length > 0);

      const poolEntries = poolEntriesForGroup
        .filter(
          (p) =>
            p.leftovers.some((leftover) =>
              isHalfPieceLeftoverCompatible(
                leftover,
                cutLengthM,
                halfPieceReuseSettings
              )
            )
        )
        .map((entry) => ({
          ...entry,
          leftovers: entry.leftovers.filter((leftover) =>
            isHalfPieceLeftoverCompatible(
              leftover,
              cutLengthM,
              halfPieceReuseSettings
            )
          ),
        }))
        .sort((a, b) => {
          if (a.lengthKey !== b.lengthKey) {
            return a.lengthKey - b.lengthKey;
          }

          return Number(a.leftovers[0]?.sourceRowIndex || 0) -
            Number(b.leftovers[0]?.sourceRowIndex || 0);
        });

      if (poolEntries.length > 0) {
        const best = poolEntries[0];
        const reusedLeftover = best.leftovers.shift() || null;
        const originalPool = pool[best.key] || [];
        pool[best.key] = originalPool.filter(
          (leftover) => leftover?.id !== reusedLeftover?.id
        );
        if (pool[best.key].length <= 0) {
          delete pool[best.key];
        }
        reusedHalfCount = 1;
        reusedLeftoverId = reusedLeftover?.id || "";
        reusedFromRowId = reusedLeftover?.sourceRowId || "";
        reusedFromItem = reusedLeftover?.sourceItem || "";
        Object.values(audit.fabricGroups || {}).forEach((group) => {
          const sourceAuditRow = (group.rows || []).find(
            (auditRow) => auditRow?.createdLeftoverId === reusedLeftoverId
          );
          if (sourceAuditRow) {
            sourceAuditRow.reusedInto = row?.item || row?.name || "";
            sourceAuditRow.reuseTargetRowId = row?.id || row?.rowId || "";
          }
        });
        reuseLog.push({
          rowIndex,
          item: row?.item || row?.name || "",
          fabricLabel,
          action: "reused-half-piece",
          reusedLeftoverId,
          requiredLengthM: cutLengthM,
          leftoverLengthM: Number(reusedLeftover?.length || 0),
        });
      } else {
        fullCuts += 1;
        reasonIfNoReuse =
          poolEntriesForGroup.length > 0
            ? halfPieceReuseSettings.heightMatchMode === "exact"
              ? "existing half pieces did not match the exact cut height"
              : "existing half pieces were shorter than this cut height"
            : "no earlier half-piece available for same fabric and width";
        const newKey = poolKey(fabricMatchKey, cutLengthM);
        const leftover = {
          id: createLeftoverId(fabricKey, cutLengthKey, rowIndex),
          fabricKey,
          fabricMatchKey,
          fabricWidthIn,
          length: cutLengthM,
          lengthKey: cutLengthKey,
          sourceRowIndex: rowIndex,
          sourceRowId: row?.id || row?.rowId || "",
          sourceItem: row?.item || row?.name || "",
          type: "HALF",
        };
        if (!Array.isArray(pool[newKey])) {
          pool[newKey] = [];
        }
        pool[newKey].push(leftover);
        createdHalf = true;
        createdLeftoverId = leftover.id;
        reuseLog.push({
          rowIndex,
          item: row?.item || row?.name || "",
          fabricLabel,
          action: "created-half-leftover",
          createdLeftoverId,
          requiredLengthM: cutLengthM,
          reason: reasonIfNoReuse,
        });
      }
    } else if (hasHalf === 1) {
      fullCuts += 1;
      reasonIfNoReuse = enableReuse ? "half-piece reuse not applicable" : "reuse disabled";
      reuseLog.push({
        rowIndex,
        item: row?.item || row?.name || "",
        fabricLabel,
        action: "rounded-half-to-full-cut",
        requiredLengthM: cutLengthM,
        reason: reasonIfNoReuse,
      });
    } else {
      reasonIfNoReuse = "raw pieces rounded to a whole panel; no half piece needed";
      reuseLog.push({
        rowIndex,
        item: row?.item || row?.name || "",
        fabricLabel,
        action: "full-cuts-only",
        requiredLengthM: cutLengthM,
        reason: reasonIfNoReuse,
      });
    }

    const newMeters = round2(fullCuts * cutLengthM);
    const cutFabricMeters = round2(totalPieces * cutLengthM);
    const halfReusedMeters = round2(reusedHalfCount * cutLengthM);
    const dKey = demandKey(cutLengthM);
    demandMap[dKey] = (demandMap[dKey] || 0) + fullCuts;
    fabricMetersByKey[fabricKey] = round2(
      (fabricMetersByKey[fabricKey] || 0) + newMeters
    );
    if (!fabricSummaryByKey[fabricKey]) {
      fabricSummaryByKey[fabricKey] = {
        fabricKey,
        fabricLabel,
        displayName: fabricLabel,
        cutFabricMeters: 0,
        halfReusedMeters: 0,
        newFabricNeededMeters: 0,
        orderedFabricMeters: 0,
        halfPiecesSaved: 0,
        halfPiecesAvailable: 0,
        halfPiecesReusable: 0,
        halfPiecesReused: 0,
        totalMetersSaved: 0,
      };
    }
    fabricSummaryByKey[fabricKey].cutFabricMeters = round2(
      fabricSummaryByKey[fabricKey].cutFabricMeters + cutFabricMeters
    );
    fabricSummaryByKey[fabricKey].halfReusedMeters = round2(
      fabricSummaryByKey[fabricKey].halfReusedMeters + halfReusedMeters
    );
    fabricSummaryByKey[fabricKey].newFabricNeededMeters = round2(
      fabricSummaryByKey[fabricKey].newFabricNeededMeters + newMeters
    );
    fabricSummaryByKey[fabricKey].orderedFabricMeters =
      fabricSummaryByKey[fabricKey].newFabricNeededMeters;
    fabricSummaryByKey[fabricKey].halfPiecesSaved += createdLeftoverId ? 1 : 0;
    fabricSummaryByKey[fabricKey].halfPiecesAvailable += createdLeftoverId ? 1 : 0;
    fabricSummaryByKey[fabricKey].halfPiecesReusable += createdLeftoverId ? 1 : 0;
    fabricSummaryByKey[fabricKey].halfPiecesReused += Number(reusedHalfCount || 0);
    fabricSummaryByKey[fabricKey].totalMetersSaved = round2(
      fabricSummaryByKey[fabricKey].totalMetersSaved + halfReusedMeters
    );

    const rowAudit = {
      rowIndex,
      rowId: row?.id || row?.rowId || "",
      itemName: row?.item || row?.name || "",
      item: row?.item || row?.name || "",
      product: row?.productType || row?.product || "",
      fabricCode: fabricLabel,
      brandName: row?.fabricBrandName || row?.brandName || "",
      fabricKey,
      fabricMatchKey,
      fabricWidth: fabricWidthIn,
      pieces: totalPieces,
      billingPieces: Number(piecePlan.billingPieces || 0),
      rawPieces: Number(piecePlan.rawPieces || 0),
      rawPanels: Number(piecePlan.rawPanels || 0),
      wholePanels: Number(piecePlan.wholePanels || 0),
      halfStepPanels: Number(piecePlan.halfStepPanels || 0),
      hasHalfPanelRequirement: piecePlan.hasHalfPanelRequirement === true,
      fullness: Number(piecePlan.fullness || 0),
      fullnessSource: piecePlan.fullnessSource || "",
      usableWidthDeduction: Number(piecePlan.usableWidthDeductionIn || 0),
      usableFabricWidth: Number(piecePlan.usableFabricWidthIn || 0),
      cutHeight: cutHeightIn,
      orderedFabricMeters: newMeters,
      cutFabricMeters,
      eligibleForReuse: true,
      createsHalfPiece: hasHalf === 1,
      halfPieceCreated: createdHalf,
      halfPieceSize: hasHalf === 1 ? "0.5 panel" : "",
      halfPieceReused: reusedHalfCount > 0,
      reusedFrom: reusedFromItem,
      reusedFromRowId,
      reusedInto: reusedHalfCount > 0 ? row?.item || row?.name || "" : "",
      reusedLeftoverId,
      createdLeftoverId,
      reasonIfNoReuse: reusedHalfCount > 0 ? "" : reasonIfNoReuse,
    };
    auditGroup.rows.push(rowAudit);
    auditGroup.halfPiecesCreated += createdLeftoverId ? 1 : 0;
    auditGroup.halfPiecesAvailable += createdLeftoverId ? 1 : 0;
    auditGroup.halfPiecesReusable += createdLeftoverId ? 1 : 0;
    auditGroup.halfPiecesReused += Number(reusedHalfCount || 0);
    auditGroup.halfPanelNeeds += hasHalf === 1 ? 1 : 0;
    auditGroup.pairsCreated += Number(reusedHalfCount || 0);
    auditGroup.metersSaved = round2(auditGroup.metersSaved + halfReusedMeters);
    if (reasonIfNoReuse && !auditGroup.reasons.includes(reasonIfNoReuse)) {
      auditGroup.reasons.push(reasonIfNoReuse);
    }
    audit.totalHalfPiecesCreated += createdLeftoverId ? 1 : 0;
    audit.totalHalfPiecesAvailable += createdLeftoverId ? 1 : 0;
    audit.totalHalfPiecesReusable += createdLeftoverId ? 1 : 0;
    audit.totalHalfPiecesReused += Number(reusedHalfCount || 0);
    audit.totalHalfPanelNeeds += hasHalf === 1 ? 1 : 0;
    audit.totalPairsCreated += Number(reusedHalfCount || 0);
    audit.totalMetersSaved = round2(audit.totalMetersSaved + halfReusedMeters);

    rowPlans[rowIndex] = {
      rowIndex,
      fabricKey,
      fabricLabel,
      fabricMatchKey,
      fabricWidthIn,
      cutHeightIn,
      cutLengthM,
      rawPieces: Number(piecePlan.rawPieces || 0),
      rawPanels: Number(piecePlan.rawPanels || 0),
      billingPieces: Number(piecePlan.billingPieces || 0),
      wholePanels: Number(piecePlan.wholePanels || 0),
      halfStepPanels: Number(piecePlan.halfStepPanels || 0),
      hasHalfPanelRequirement: piecePlan.hasHalfPanelRequirement === true,
      fullness: Number(piecePlan.fullness || 0),
      fullnessSource: piecePlan.fullnessSource || "",
      usableWidthDeductionIn: Number(piecePlan.usableWidthDeductionIn || 0),
      usableFabricWidthIn: Number(piecePlan.usableFabricWidthIn || 0),
      reuseTotalPieces: totalPieces,
      totalPieces,
      cutFabricMeters,
      newFullCuts: fullCuts,
      fullCutsWithoutReuse: Number(piecePlan.fullCutsWithoutReuse || 0),
      newMeters,
      newFabricNeededMeters: newMeters,
      halfReusedMeters,
      reusedHalfCount,
      createdHalf,
      createdLeftoverId,
      reusedLeftoverId,
      reusedFromRowId,
      reusedFromItem,
      reasonIfNoReuse,
    };

    if (IS_DEV_UI_ENABLED) {
      console.log("HALF_PIECE_ROW_AUDIT", rowAudit);
    }
  });

  Object.entries(pool).forEach(([k, leftoverList]) => {
    if (!Array.isArray(leftoverList) || leftoverList.length <= 0) return;
    leftoverList.forEach((leftover) => {
      const leftoverFabricKey = leftover?.fabricKey || k;
      if (!leftoversByKey[leftoverFabricKey]) leftoversByKey[leftoverFabricKey] = [];
      leftoversByKey[leftoverFabricKey].push({
        ...leftover,
        fabricKey: leftoverFabricKey,
        length: Number(leftover?.length || 0),
        type: "HALF",
      });
    });
  });

  const demands = Object.entries(demandMap)
    .map(([length, qty]) => ({
      length: Number((Number(length) / 1000).toFixed(3)),
      qty: Math.max(0, Math.floor(Number(qty))),
    }))
    .filter((d) => d.qty > 0)
    .sort((a, b) => b.length - a.length);
  const finalizedAudit = finalizeHalfPieceAudit(audit);

  if (IS_DEV_UI_ENABLED) {
    console.log("REUSE PLAN:", {
      tag: debugTag || "default",
      inputRows: sourceRows.length,
      demands,
      halfPiecesCreated: finalizedAudit.totalHalfPiecesCreated,
      halfPiecesAvailable: finalizedAudit.totalHalfPiecesAvailable,
      reusedHalfCount: rowPlans
        .filter(Boolean)
        .reduce((sum, p) => sum + Number(p.reusedHalfCount || 0), 0),
      metersSaved: finalizedAudit.totalMetersSaved,
      auditReasons: finalizedAudit.reasons,
      reuseLog,
      skippedReasons,
      pool,
    });
  }

  return {
    demands,
    rowPlans,
    fabricMetersByKey,
    fabricSummaryByKey,
    leftoversByKey,
    pool,
    reuseLog,
    skippedReasons,
    audit: finalizedAudit,
  };
}

const calculateCurtainRowProduction = (row = {}, options = {}) => {
  const activeRepeatSettings = options.repeatSettings || repeatSettings;
  const activeQuoteFullnessOverrides = Object.prototype.hasOwnProperty.call(
    options || {},
    "quoteFullnessOverrides"
  )
    ? normalizeQuoteFullnessOverrides(options.quoteFullnessOverrides || {})
    : quoteFullnessOverrides;
  const productConfig =
    options.productConfig ||
    getRowProductConfig(row);
  const calc = calcRow(row, activeRepeatSettings, {
    quoteFullnessOverrides: activeQuoteFullnessOverrides,
  });
  const fullnessResult = resolveCurtainFullness(row, productConfig, stitchingRates, {
    fabricCalcSettings,
    quoteFullnessOverrides: activeQuoteFullnessOverrides,
  });
  const resolvedStitchLabel = getCurtainRowWorkshopStitchLabel(
    row,
    stitchingRates,
    productConfig
  );
  const fabricLabel = getCurtainRowFabricLabel(row);
  const fabricKey = getCurtainRowFabricKey(row);
  const cutFabricMeters = round2(
    Number(calc.fabricMetersHalf ?? calc.cutFabricMeters ?? calc.fabricMeters ?? 0)
  );
  const orderedFabricMeters = round2(Number(calc.fabricMeters || 0));

  const production = {
    widthIn: num(row.width),
    heightIn: num(row.height),
    resolvedStitchLabel,
    resolvedFullness: Number(fullnessResult.fullness || 0),
    fullnessSource: fullnessResult.source || "",
    panels: Number(calc.totalPieces || calc.pcsHalf || calc.pcs || 0),
    cutWidthIn: Number(row.fabricWidthIn || 0),
    cutHeightIn: Number(calc.cutCurtain || calc.cutRoman || 0),
    visibleDropIn: num(row.height),
    fabricMeters: cutFabricMeters,
    cutFabricMeters,
    orderedFabricMeters,
    newFabricMeters: orderedFabricMeters,
    reusedHalfMeters: 0,
    leftoverSaved: 0,
    trackLabel: getCurtainRowDisplay(row?.trackType || row?.trackName || row?.track, ""),
    trackLengthFt: Number(row?.trackLengthFt || 0),
    fabricLabel,
    fabricKey,
    calc,
  };

  if (IS_DEV_UI_ENABLED && calc.isCurtain) {
    console.log("FULLNESS_AND_STITCH_TRACE", {
      item: row?.item || row?.name || "",
      product: row?.productType || row?.product || "",
      visibleStitch: row?.stitchingStyle || row?.stitchingStyleId || row?.stitch || row?.stitching || row?.stitchLabel || "",
      resolvedStitchLabel,
      resolvedFullness: production.resolvedFullness,
      panels: production.panels,
      fabricMeters: production.fabricMeters,
    });
  }

  return production;
};

const getCurtainRowSignature = (
  row = {},
  production = null,
  stitchingStyles = stitchingRates,
  productConfig = null
) => {
  const resolvedProduction =
    production ||
    calculateCurtainRowProduction(row, {
      productConfig,
      repeatSettings,
    });

  return {
    id: getWorkshopRowStableId(row),
    item: getWorkshopRowItemLabel(row),
    product: getWorkshopRowProductLabel(row),
    fabric: getCurtainRowFabricLabel(row),
    stitch: getCurtainRowWorkshopStitchLabel(
      row,
      stitchingStyles,
      productConfig || getRowProductConfig(row)
    ),
    width: Number(row?.width || 0),
    height: Number(row?.height || 0),
    mrp: row?.mrp ?? "",
    rate: row?.rate ?? row?.sqftRate ?? row?.ratePerFeet ?? row?.ratePerPiece ?? "",
    fabricMeters: Number(resolvedProduction?.fabricMeters || 0).toFixed(3),
  };
};

const buildCurrentCurtainQuoteExportPayload = ({
  quoteState = {},
  rows: sourceRows = [],
  productConfigs: activeProductConfigs = productConfigs,
  stitchingStyles = stitchingRates,
  quoteSettings = {},
  halfPieceReuseEnabled = useHalfPieceReuse,
} = {}) => {
  const activeRepeatSettings =
    quoteState.repeatSettings || quoteSettings.repeatSettings || repeatSettings;
  const activeQuoteFullnessOverrides = normalizeQuoteFullnessOverrides(
    quoteState.quoteFullnessOverrides ||
      quoteSettings.quoteFullnessOverrides ||
      quoteFullnessOverrides ||
      {}
  );
  const sourceVisibleRows = (Array.isArray(sourceRows) ? sourceRows : [])
    .map((row, index) => withCurtainRowId(row, index))
    .filter((row) => !isDeletedWorkshopExportRow(row))
    .filter(hasMeaningfulWorkshopExportData);
  const workshopRowsLive = sourceVisibleRows.map((row, index) =>
    createWorkshopSourceRow(row, index, sourceVisibleRows, {
      stitchingStyles,
      productConfig: getRowProductConfig(row),
    })
  );
  const workshopRows = workshopRowsLive
    .map((row) => sanitizeWorkshopRow(row))
    .filter(Boolean);

  const includeRowInWorkshop = (row) =>
    getRowProductRules(row).workshopRules?.includeInWorkshopPdf !== false;
  const getWorkshopRowGroup = (row) => {
    if (isCurtainAccessoryRow(row)) return "accessory";
    const production = calculateCurtainRowProduction(row, {
      repeatSettings: activeRepeatSettings,
      quoteFullnessOverrides: activeQuoteFullnessOverrides,
      productConfig: getRowProductConfig(row),
    });
    const productLabel = normalizeWorkshopProductLabel(
      row.productType || row.productName || row.product || ""
    );
    if (production.calc.isCurtain || productLabel === "curtains") return "curtain";
    if (production.calc.isRoman || productLabel.startsWith("roman blind")) return "roman";
    if (production.calc.isBlind || productLabel.includes("blind")) return "blind";
    return "custom";
  };

  const includedWorkshopRows = workshopRows.filter(includeRowInWorkshop);
  const curtainRows = includedWorkshopRows.filter(
    (row) => getWorkshopRowGroup(row) === "curtain"
  );
  const romanRows = includedWorkshopRows.filter(
    (row) => getWorkshopRowGroup(row) === "roman"
  );
  const blindRows = includedWorkshopRows.filter(
    (row) => getWorkshopRowGroup(row) === "blind"
  );
  const accessoryRows = includedWorkshopRows.filter(
    (row) => getWorkshopRowGroup(row) === "accessory"
  );
  const customRows = includedWorkshopRows.filter(
    (row) => getWorkshopRowGroup(row) === "custom"
  );
  const curtainRomanRows = [...curtainRows, ...romanRows];

  const fabricLabelByKey = {};
  const rowProduction = workshopRows.map((row, index) => {
    const production = calculateCurtainRowProduction(row, {
      repeatSettings: activeRepeatSettings,
      quoteFullnessOverrides: activeQuoteFullnessOverrides,
      productConfig: getRowProductConfig(row),
    });
    const key = production.fabricKey;
    if (key) {
      if (!fabricLabelByKey[key] && production.fabricLabel !== WORKSHOP_EMPTY_VALUE) {
        fabricLabelByKey[key] = production.fabricLabel;
      }
    }
    return {
      row,
      index,
      ...production,
    };
  });

  const workshopPlan = buildReuseAwareDemands(curtainRomanRows, activeRepeatSettings, {
    enableReuse: halfPieceReuseEnabled,
    debugTag: "workshop",
    getProductRules: getRowProductRules,
    quoteFullnessOverridesForCalculation: activeQuoteFullnessOverrides,
  });

  const cutFabricSummary = Object.fromEntries(
    Object.entries(workshopPlan.fabricSummaryByKey || {})
      .filter(([, summary]) => Number(summary?.newFabricNeededMeters || 0) > 0)
      .map(([fabricKey, summary]) => {
        const displayName =
          summary.displayName ||
          summary.fabricLabel ||
          fabricLabelByKey[fabricKey] ||
          fabricKey.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
        if (!fabricLabelByKey[fabricKey]) {
          fabricLabelByKey[fabricKey] = displayName;
        }
        return [
          fabricKey,
          {
            ...summary,
            displayName,
            fabricLabel: summary.fabricLabel || displayName,
            newFabricMeters: Number(summary.newFabricNeededMeters || 0),
            fabricToOrderMeters: Number(summary.fabricToOrderMeters ?? summary.newFabricNeededMeters ?? 0),
            newFabricNeededMeters: Number(summary.newFabricNeededMeters || 0),
            orderedFabricMeters: Number(summary.newFabricNeededMeters || 0),
          },
        ];
      })
  );

  const uiRowSignatures = sourceVisibleRows.map((row) =>
    getCurtainRowSignature(
      row,
      calculateCurtainRowProduction(row, {
        repeatSettings: activeRepeatSettings,
        quoteFullnessOverrides: activeQuoteFullnessOverrides,
        productConfig: getRowProductConfig(row),
      }),
      stitchingStyles,
      getRowProductConfig(row)
    )
  );
  const workshopRowSignatures = workshopRowsLive.map((row) =>
    getCurtainRowSignature(
      row,
      calculateCurtainRowProduction(row, {
        repeatSettings: activeRepeatSettings,
        quoteFullnessOverrides: activeQuoteFullnessOverrides,
        productConfig: getRowProductConfig(row),
      }),
      stitchingStyles,
      getRowProductConfig(row)
    )
  );
  const signatureMismatches = uiRowSignatures
    .map((uiSignature, index) => {
      const workshopSignature = workshopRowSignatures[index];
      if (!workshopSignature) {
        return { type: "missing-workshop-row", index, ui: uiSignature };
      }
      const mismatchFields = ["id", "item", "product", "fabric", "stitch", "width", "height", "fabricMeters"].filter(
        (field) => String(uiSignature[field] ?? "") !== String(workshopSignature[field] ?? "")
      );
      return mismatchFields.length
        ? {
            type: "signature-mismatch",
            index,
            fields: mismatchFields,
            ui: uiSignature,
            workshop: workshopSignature,
          }
        : null;
    })
    .filter(Boolean);

  if (workshopRowSignatures.length > uiRowSignatures.length) {
    workshopRowSignatures.slice(uiRowSignatures.length).forEach((signature, offset) => {
      signatureMismatches.push({
        type: "extra-workshop-row",
        index: uiRowSignatures.length + offset,
        workshop: signature,
      });
    });
  }

  return {
    quoteState,
    activeProductConfigs,
    quoteSettings,
    sourceVisibleRows,
    workshopRowsLive,
    workshopRows,
    includedWorkshopRows,
    curtainRows,
    romanRows,
    blindRows,
    accessoryRows,
    customRows,
    curtainRomanRows,
    workshopPlan,
    cutFabricSummary,
    fabricLabelByKey,
    rowProduction,
    uiRowSignatures,
    workshopRowSignatures,
    signatureMismatches,
  };
};

 /* --------------------------- WORKSHOP ZIP EXPORT (WITH CUTTING) --------------------------- */
/* --------------------------- WORKSHOP ZIP EXPORT (GROUPED BY TYPE) --------------------------- */

/* --------------------------- WORKSHOP ZIP EXPORT (GROUPED BY TYPE) --------------------------- */
/* --------------------------- WORKSHOP ZIP EXPORT (W1 READY) --------------------------- */

const generateWorkshopZIP = async () => {
  try {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      notify(
        "You appear to be offline. Please reconnect and try Workshop ZIP again.",
        "warning"
      );
      return;
    }

    let jsPDF;
    let autoTable;
    let JSZip;
    let saveAs;

    try {
      const [pdfRuntime, zipRuntime] = await Promise.all([
        loadPdfRuntime(),
        loadZipRuntime(),
      ]);
      ({ jsPDF, autoTable } = pdfRuntime);
      ({ JSZip, saveAs } = zipRuntime);
      resetChunkLoadReloadGuard();
    } catch (runtimeError) {
      console.error("WORKSHOP_ZIP_CHUNK_LOAD_FAILED", {
        name: runtimeError?.name || "",
        message: runtimeError?.message || String(runtimeError || ""),
        stack: runtimeError?.stack || "",
        ...getChunkLoadDebugContext(),
      });

      if (
        typeof navigator !== "undefined" &&
        navigator.onLine === false
      ) {
        notify(
          "You appear to be offline. Please reconnect and try Workshop ZIP again.",
          "warning"
        );
        return;
      }

      if (isChunkLoadError(runtimeError)) {
        handleChunkLoadErrorRecovery(runtimeError, {
          source: "generateWorkshopZIP.runtime",
          autoReload: false,
        });
        notify(
          "Workshop exporter could not load. Please refresh the page and try again.",
          "warning",
          7000
        );
        if (
          typeof window !== "undefined" &&
          window.confirm(
            "FabriQuote was updated or the exporter file could not load. Reload now?"
          )
        ) {
          window.location.reload();
        }
        return;
      }

      throw runtimeError;
    }

    const liveActionPayload = buildLiveCurtainQuotePayload({
      action: "workshopZip",
    });
    traceLiveActionPayload(liveActionPayload);
    guardStaleActionPayload(liveActionPayload);
    const {
      rows,
      repeatSettings,
      fabricSummaryUI,
      curtainCanonicalFabricSummaryRows,
      selectedQuote,
      customerName,
      quoteNo,
      quoteDate: date,
      quoteFullnessOverrides,
      useHalfPieceReuse,
      passHalfPieceSavingsToCustomer,
    } = liveActionPayload;

    const buildStitchTrace = (sourceRows = [], stage = "") =>
      (Array.isArray(sourceRows) ? sourceRows : []).map((row, index) =>
        buildWorkshopStitchTraceRow(
          row,
          stitchingRates,
          getRowProductConfig(row),
          { stage, index }
        )
      );

    if (IS_DEV_UI_ENABLED) {
      console.log("STITCH_TRACE_1_UI_ROWS", buildStitchTrace(rows, "ui_rows"));
      console.log("STITCH_TRACE_2_EFFECTIVE_ROWS", buildStitchTrace(effectiveRows, "state_effective_rows"));
      console.log(
        "WORKSHOP_EXPORT_TRACE_UI_ROWS",
        rows.map((r) => {
          const production = calculateCurtainRowProduction(r, {
            repeatSettings,
            productConfig: getRowProductConfig(r),
          });
          return {
            id: r.id,
            item: r.item,
            product: r.product || r.productType || r.productName,
            fabric: r.fabric || r.fabricName || r.materialCode,
            stitch: r.stitch,
            stitchType: r.stitchType,
            stitching: r.stitching,
            stitchingType: r.stitchingType,
            stitchLabel: r.stitchLabel,
            stitchingLabel: r.stitchingLabel,
            selectedStitch: r.selectedStitch,
            stitchingStyleId: r.stitchingStyleId,
            stitchingStyle: r.stitchingStyle,
            width: r.width || r.w,
            height: r.height || r.h,
            mrp: r.mrp,
            rate: r.rate || r.sqftRate || r.ratePerFeet || r.ratePerPiece,
            fabricMeters: production.fabricMeters,
            rowTotal: production.calc?.rowTotal,
          };
        })
      );
    }

    const exportPayload = buildCurrentCurtainQuoteExportPayload({
      quoteState: {
        selectedQuote,
        customerName,
        quoteNo,
        quoteDate: resolveQuoteDateForInput(
          {
            ...selectedQuote,
            quoteDate: date || selectedQuote?.quoteDate,
            date: date || selectedQuote?.date,
          },
          todayISO()
        ),
        date: resolveQuoteDateForInput(
          {
            ...selectedQuote,
            quoteDate: date || selectedQuote?.quoteDate,
            date: date || selectedQuote?.date,
          },
          todayISO()
        ),
        repeatSettings,
        quoteFullnessOverrides,
      },
      rows,
      productConfigs,
      stitchingStyles: stitchingRates,
      quoteSettings: {
        ...fabricCalcSettings,
        repeatSettings,
        quoteFullnessOverrides,
      },
      halfPieceReuseEnabled: useHalfPieceReuse,
    });

    const {
      sourceVisibleRows: activeExportRows,
      workshopRowsLive,
      workshopRows: base,
      includedWorkshopRows,
      curtainRows,
      romanRows,
      blindRows,
      accessoryRows,
      customRows,
      curtainRomanRows,
      workshopPlan,
      cutFabricSummary,
      fabricLabelByKey,
      signatureMismatches,
    } = exportPayload;

    if (IS_DEV_UI_ENABLED) {
      console.log("WORKSHOP_HALF_PIECE_TOGGLE_TRACE", {
        quoteNo,
        useHalfPieceReuse,
        passHalfPieceSavingsToCustomer,
        rowsCount: rows.length,
        fabricGroupsCount: Object.keys(fabricSummaryUI || {}).length,
        workshopAudit: workshopPlan?.audit || null,
      });
      console.log("STITCH_TRACE_2_ACTIVE_EXPORT_ROWS", buildStitchTrace(activeExportRows, "active_export_rows"));
      console.log("STITCH_TRACE_3_WORKSHOP_SOURCE_ROWS", buildStitchTrace(base, "workshop_source_rows"));
      console.log(
        "WORKSHOP_EXPORT_TRACE_FINAL_ROWS",
        curtainRomanRows.map((row, index) => {
          const production = calculateCurtainRowProduction(row, {
            repeatSettings,
            productConfig: getRowProductConfig(row),
          });
          const rowPlan = workshopPlan.rowPlans?.[index] || {};
          return {
            id: row.id,
            item: row.item,
            product: row.product || row.productType || row.productName,
            fabric: production.fabricLabel,
            stitch: production.resolvedStitchLabel,
            width: production.widthIn,
            height: production.heightIn,
            panels: production.panels,
            fabricMeters: production.fabricMeters,
            newFabricMeters: Number(rowPlan.newMeters || production.fabricMeters || 0),
            reusedHalfMeters: Number(rowPlan.halfReusedMeters || 0),
            source: "live-rows",
          };
        })
      );
      console.log("WORKSHOP_EXPORT_FABRIC_TOTAL_COMPARE", {
        uiFabricSummary: Object.fromEntries(
          Object.entries(fabricSummaryUI || {}).map(([key, value]) => [
            key,
            {
              displayName: value.displayName,
              cutFabricMeters: Number(value.cutFabricMeters || 0).toFixed(2),
              halfReusedMeters: Number(value.halfReusedMeters || 0).toFixed(2),
              orderedFabricMeters: Number(value.orderedFabricMeters || value.newFabricNeededMeters || value.meters || 0).toFixed(2),
            },
          ])
        ),
        workshopFabricSummary: Object.fromEntries(
          Object.entries(cutFabricSummary || {}).map(([key, value]) => [
            key,
            {
              displayName: value.displayName,
              cutFabricMeters: Number(value.cutFabricMeters || 0).toFixed(2),
              newFabricMeters: Number(value.newFabricMeters || 0).toFixed(2),
              reusedHalfMeters: Number(value.reusedHalfMeters || 0).toFixed(2),
            },
          ])
        ),
        quoteNo,
        rowCountUi: rows.length,
        rowCountWorkshop: base.length,
      });
    }

    if (!base.length) {
      notify("No current quotation rows are available for workshop export.", "warning");
      return;
    }

    if (IS_DEV_UI_ENABLED) {
      const customerPdfRows = getActiveCurtainRowsForExport(rows).map(
        summarizeWorkshopExportRow
      );
      const workshopSourceRows = base.map(summarizeWorkshopExportRow);
      const staleEffectiveRows = getActiveCurtainRowsForExport(effectiveRows).map(
        summarizeWorkshopExportRow
      );

      console.log("Workshop export source comparison", {
        customerPdfRows,
        workshopSourceRows,
        effectiveRows: staleEffectiveRows,
      });
      console.table([
        ...customerPdfRows.map((row) => ({ source: "customerPdfRows", ...row })),
        ...workshopSourceRows.map((row) => ({ source: "workshopSourceRows", ...row })),
        ...staleEffectiveRows.map((row) => ({ source: "effectiveRows", ...row })),
      ]);
      console.log(
        "Workshop PDF row audit",
        base.map((row) => ({
          item: row.item || row.name,
          product: row.productType || row.product,
          fabric: row.fabric,
          fabricName: row.fabricName,
          fabricLabel: row.fabricLabel,
          resolvedFabric: getCurtainRowFabricLabel(row),
          fabricWidth: row.fabricWidthIn,
          width: row.width,
          height: row.height,
          stitch: row.stitch,
          stitchLabel: row.stitchLabel,
          stitchingStyle: row.stitchingStyle,
          resolvedStitch: getCurtainRowWorkshopStitchLabel(
            row,
            stitchingRates,
            getRowProductConfig(row)
          ),
          sourceRowId: row.sourceRowId,
          sourceIndex: row.sourceIndex,
        }))
      );
      assertNoPricingInWorkshopPdfPayload(base, {
        label: "curtainWorkshopBaseRows",
      });
    }

    if (signatureMismatches.length) {
      console.error("WORKSHOP_EXPORT_STALE_OR_MISMATCHED_ROWS", {
        quoteNo,
        mismatches: signatureMismatches,
        uiSignatures: exportPayload.uiRowSignatures,
        workshopSignatures: exportPayload.workshopRowSignatures,
      });
      if (IS_DEV_UI_ENABLED) {
        notify(
          "Workshop export rows do not match the current visible quotation rows. Check console trace.",
          "error"
        );
        return;
      }
    }

    const exportValidation = validateWorkshopRowsMatchActiveRows(
      activeExportRows,
      workshopRowsLive
    );
    if (!exportValidation.ok) {
      if (IS_DEV_UI_ENABLED) {
        console.error("Workshop export validation failed", exportValidation);
        console.table(exportValidation.mismatches || []);
      }
      notify(
        "Workshop export data does not match the current quotation. Please save and try again.",
        "error"
      );
      return;
    }

    console.time("workshop-zip");

    const zip = new JSZip();
    const logoMeta = await prepareLogoForWorkshop(getLogoDataURL, logoURL);
    const workshopQuoteDate = resolveQuoteDateForInput(
      {
        ...selectedQuote,
        quoteDate: date || selectedQuote?.quoteDate,
        date: date || selectedQuote?.date,
        createdAt: selectedQuote?.createdAt || selectedQuote?.updatedAt,
      },
      todayISO()
    );

    const excludedRows = base
      .filter(
        (row) =>
          !includedWorkshopRows.some(
            (includedRow) =>
              getWorkshopRowStableId(includedRow) === getWorkshopRowStableId(row)
          )
      )
      .map((row) => {
        const rowRules = getRowProductRules(row);
        return {
          item: row.item || row.name || "",
          product: row.productType || row.productName || row.product || "",
          reason: "workshop PDF disabled by product config",
          resolvedProductConfigId: rowRules.config?.id || "",
          workshopRules: rowRules.workshopRules || {},
        };
      });

    if (IS_DEV_UI_ENABLED) {
      console.log("WORKSHOP PDF DEBUG", {
        totalInputRows: rows.length,
        activeExportRows: base.length,
        includedRows: includedWorkshopRows.length,
        excludedRows,
        curtainRows: curtainRows.length,
        romanRows: romanRows.length,
        blindRows: blindRows.length,
        customRows: customRows.length,
        productNames: rows.map((r) => r.product || r.productName || r.productType || ""),
      });
      if (excludedRows.length) {
        console.table(excludedRows);
      }
    }

    if (!includedWorkshopRows.length) {
      notify("No quotation rows are enabled for workshop export.", "warning");
      return;
    }

    const workshopDemandTotal = round2(
      (workshopPlan.demands || []).reduce(
        (sum, d) => sum + Number(d.length || 0) * Number(d.qty || 0),
        0
      )
    );
    if (IS_DEV_UI_ENABLED) {
      console.log("WORKSHOP DEMAND TOTAL:", workshopDemandTotal);
      console.log("Half-piece reuse audit", {
        enabled: useHalfPieceReuse,
        availableLeftovers: workshopPlan.leftoversByKey || {},
        demandRows: (workshopPlan.rowPlans || []).filter(Boolean),
        reuseLog: workshopPlan.reuseLog || [],
        skippedReasons: workshopPlan.skippedReasons || [],
      });
    }

    const realFabricUsage = { ...workshopPlan.fabricMetersByKey };
    const realLeftovers = { ...workshopPlan.leftoversByKey };
    const getWorkshopFabricSummaryRowsForPdf = () =>
      curtainCanonicalFabricSummaryRows.length > 0
        ? curtainCanonicalFabricSummaryRows
        : Object.entries(cutFabricSummary || {}).map(([fabricKey, summary]) => {
            const normalizedKey = normalizeFabricKey(fabricKey);
            const summaryMeta = getFabricSummaryMetaForKey(normalizedKey);
            const summaryRepeatSettings = normalizeCurtainRepeatEntry(
              repeatSettings?.[normalizedKey] || summaryMeta.repeatSettings || {}
            );
            return {
              normalizedKey,
              fabricKey: normalizedKey,
              brandName:
                summaryMeta.brandName || getFabricBrandNameById(summaryMeta.brandId),
              fabricCode:
                summaryMeta.fabricCode ||
                summary.displayName ||
                summary.fabricLabel ||
                fabricLabelByKey[fabricKey] ||
                fabricKey,
              displayName:
                summary.displayName ||
                summary.fabricLabel ||
                fabricLabelByKey[fabricKey] ||
                fabricKey,
              usedInItems: [],
              repeatSettings: summaryRepeatSettings,
              repeatSummary: formatRepeatSettings(summaryRepeatSettings),
              notes: summaryMeta.notes || "",
              cutFabricMeters: Number(summary.cutFabricMeters || 0),
              fullOrderedFabricMeters: Number(summary.fullOrderedFabricMeters || summary.orderedFabricMeters || 0),
              fabricToOrderMeters: Number(summary.fabricToOrderMeters ?? summary.workshopFabricRequiredMeters ?? summary.newFabricMeters ?? summary.newFabricNeededMeters ?? 0),
              newFabricNeededMeters: Number(summary.newFabricMeters || summary.newFabricNeededMeters || 0),
              workshopFabricRequiredMeters: Number(summary.workshopFabricRequiredMeters || summary.newFabricMeters || summary.newFabricNeededMeters || 0),
              halfReusedMeters: Number(summary.reusedHalfMeters || summary.halfReusedMeters || 0),
              customerBillableFabricMeters: Number(summary.customerBillableFabricMeters || summary.fullOrderedFabricMeters || summary.orderedFabricMeters || 0),
            };
          });
    const workshopAllocationByRowId = {};
    const workshopAllocationsByFabricKey = {};
    curtainRomanRows.forEach((row, rowIdx) => {
      const rowId = getWorkshopRowStableId(row) || `workshop-row-${rowIdx}`;
      const rowPlan = workshopPlan.rowPlans?.[rowIdx] || null;
      const fallbackCalc = calcRow(row, repeatSettings);
      const fabricKey = getCurtainRowFabricKey(row);
      const fabricCode = getCurtainFabricDisplayWithBrand(row);
      const cutHeightIn = Number(
        rowPlan?.cutHeightIn || fallbackCalc.cutCurtain || fallbackCalc.cutRoman || row.height || 0
      );
      const cutHeightMeters = cutHeightIn > 0 ? cutHeightIn / 39.3701 : 0;
      const newFullCuts = Number(rowPlan?.newFullCuts ?? fallbackCalc.pcsFull ?? fallbackCalc.pcs ?? 0);
      const expectedNewFabricFromCuts = round2(newFullCuts * cutHeightMeters);
      const rowNewFabricMeters = round2(
        Number.isFinite(Number(rowPlan?.newMeters))
          ? Number(rowPlan?.newMeters || 0)
          : expectedNewFabricFromCuts
      );
      const rowWorkshopRequiredMeters = round2(
        Number.isFinite(Number(rowPlan?.newFabricNeededMeters))
          ? Number(rowPlan?.newFabricNeededMeters || 0)
          : rowNewFabricMeters
      );
      const fabricColorKey = getWorkshopFabricColorKey(row, fabricCode);
      const sourceRowIndex = Number.isInteger(Number(row.sourceIndex))
        ? Number(row.sourceIndex)
        : rowIdx;
      const customerCalc = getCurtainProductionAdjustedRowCalc(row, sourceRowIndex);
      const rowFullOrderedMeters = round2(
        Number(
          customerCalc.fullOrderedFabricMeters ??
            customerCalc.noReuseOrderedFabricMeters ??
            customerCalc.orderedFabricMeters ??
            customerCalc.fabricMeters ??
            0
        )
      );
      const rowCustomerBillableMeters = round2(
        Number(customerCalc.customerBillableFabricMeters ?? rowFullOrderedMeters)
      );
      const reusedHalfCount = Number(rowPlan?.reusedHalfCount || 0);
      const savedHalfCount = rowPlan?.createdLeftoverId ? 1 : 0;
      const fabricSource = rowPlan?.reusedLeftoverId
        ? "Reused Half"
        : rowPlan?.createdLeftoverId
        ? "New Cut + Half Leftover Saved"
        : rowPlan
        ? "New Cut"
        : "Calculated Fallback";
      const allocation = {
        rowId,
        rowIndex: rowIdx,
        fabricKey,
        fabricCode,
        brandName: row.fabricBrandName || row.brandName || "",
        panels: Number(rowPlan?.totalPieces ?? rowPlan?.reuseTotalPieces ?? fallbackCalc.totalPieces ?? 0),
        rawPanels: Number(rowPlan?.rawPanels ?? fallbackCalc.rawPanels ?? 0),
        halfStepProductionPanels: Number(rowPlan?.halfStepPanels ?? fallbackCalc.pcsHalf ?? 0),
        wholeBillingPanels: Number(rowPlan?.wholePanels ?? customerCalc.wholeBillingPanels ?? fallbackCalc.pcsFull ?? 0),
        cutHeightIn,
        cutHeightMeters,
        newFullCuts,
        reusedHalfCount,
        savedHalfCount,
        rowNewFabricMeters,
        rowWorkshopRequiredMeters,
        rowCustomerBillableMeters,
        rowFullOrderedMeters,
        rowCutFabricMeters: round2(Number(rowPlan?.cutFabricMeters ?? fallbackCalc.cutFabricMeters ?? 0)),
        rowHalfReusedMeters: round2(Number(rowPlan?.halfReusedMeters || 0)),
        fabricSource,
        fabricColorKey,
        reusedFromRowId: rowPlan?.reusedFromRowId || "",
        reusedIntoRowId: rowPlan?.reusedLeftoverId ? rowId : "",
        expectedNewFabricFromCuts,
        sourceUsed: rowPlan ? "workshopPlan.rowPlans[rowIndex].newMeters" : "newFullCuts * cutHeightMeters fallback",
      };
      workshopAllocationByRowId[rowId] = allocation;
      if (!workshopAllocationsByFabricKey[fabricKey]) workshopAllocationsByFabricKey[fabricKey] = [];
      workshopAllocationsByFabricKey[fabricKey].push(allocation);
    });

    if (IS_DEV_UI_ENABLED) {
      getWorkshopFabricSummaryRowsForPdf().forEach((summary) => {
        const fabricKey = summary.normalizedKey || summary.fabricKey || normalizeFabricKey(summary.fabricCode || summary.displayName || "");
        const rowsForGroup = workshopAllocationsByFabricKey[fabricKey] || [];
        const sumRowNewFabricMeters = round2(
          rowsForGroup.reduce((sum, row) => sum + Number(row.rowNewFabricMeters || 0), 0)
        );
        const groupNewNeeded = round2(
          Number(summary.newFabricNeededMeters || summary.workshopFabricRequiredMeters || 0)
        );
        const difference = round2(sumRowNewFabricMeters - groupNewNeeded);
        if (Math.abs(difference) > 0.05) {
          console.error("WORKSHOP_PDF_GROUP_ROW_SUM_MISMATCH", {
            quoteNo,
            fabricKey,
            fabricCode: summary.fabricCode || summary.displayName || "",
            sumRowNewFabricMeters,
            groupNewNeeded,
            difference,
            rows: rowsForGroup,
          });
        }
      });
    }

    const blindsByType = {};
    [...blindRows, ...customRows].forEach((r) => {
      const key = r.productType || "Other";
      if (!blindsByType[key]) blindsByType[key] = [];
      blindsByType[key].push(r);
    });

    if (curtainRomanRows.length) {
      const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });

      const { marginX, pageW, headerBottomY } = addWorkshopHeader(
        doc,
        logoMeta,
        shopAddress
      );

      const titleY = headerBottomY + 10;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text("Workshop Sheet - Curtains & Romans", pageW / 2, titleY, {
        align: "center",
      });

      doc.setLineWidth(0.3);
      doc.line(marginX, titleY + 5, pageW - marginX, titleY + 5);

      let y = titleY + 13;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      const customerPhone =
        quoteCustomerLink?.phone ||
        quoteCustomerLink?.whatsapp ||
        selectedQuote?.customerPhone ||
        selectedQuote?.customerWhatsapp ||
        selectedQuote?.crm?.customerPhone ||
        "";
      const installDate =
        toDateInputValue(selectedQuote?.installationDate || selectedQuote?.crm?.installationDate) ||
        "";
      const headerParts = [
        `Customer: ${customerName || WORKSHOP_EMPTY_VALUE}`,
        customerPhone ? `Phone: ${customerPhone}` : "",
        `Quote: ${quoteNo || WORKSHOP_EMPTY_VALUE}`,
        `Date: ${workshopQuoteDate || WORKSHOP_EMPTY_VALUE}`,
        installDate ? `Install: ${installDate}` : "",
      ].filter(Boolean);
      doc.text(headerParts.join("   |   "), marginX, y);
      y += 8;

      const tableRows = [];
      const workshopFabricSummaryRows = getWorkshopFabricSummaryRowsForPdf();
      const fabricColorMap = {};
      const workshopFabricColorKeys = [];
      const workshopFabricColorKeyByNormalizedKey = {};
      const registerWorkshopFabricColorKey = (rawKey = "") => {
        const key = normalizeFabricKey(rawKey);
        if (!key || workshopFabricColorKeys.includes(key)) return key;
        workshopFabricColorKeys.push(key);
        fabricColorMap[key] = getWorkshopPdfFabricTintByIndex(
          workshopFabricColorKeys.length - 1
        );
        return key;
      };
      curtainRomanRows.forEach((row) => {
        const normalizedFabricKey = getCurtainRowFabricKey(row);
        const colorKey = registerWorkshopFabricColorKey(
          getWorkshopFabricColorKey(row, getCurtainFabricDisplayWithBrand(row))
        );
        if (normalizedFabricKey && colorKey && !workshopFabricColorKeyByNormalizedKey[normalizedFabricKey]) {
          workshopFabricColorKeyByNormalizedKey[normalizedFabricKey] = colorKey;
        }
      });
      const getWorkshopSummaryFabricColorKey = (summary = {}) => {
        const summaryNormalizedKey =
          summary.normalizedKey ||
          summary.fabricKey ||
          normalizeFabricKey(summary.fabricCode || summary.displayName || "");
        if (summaryNormalizedKey && workshopFabricColorKeyByNormalizedKey[summaryNormalizedKey]) {
          return workshopFabricColorKeyByNormalizedKey[summaryNormalizedKey];
        }
        return getWorkshopFabricColorKey(
          summary,
          summary.fabricCode || summary.displayName || summaryNormalizedKey || ""
        );
      };
      workshopFabricSummaryRows.forEach((summary) => {
        const summaryNormalizedKey =
          summary.normalizedKey ||
          summary.fabricKey ||
          normalizeFabricKey(summary.fabricCode || summary.displayName || "");
        const colorKey = registerWorkshopFabricColorKey(
          getWorkshopSummaryFabricColorKey(summary)
        );
        if (summaryNormalizedKey && colorKey && !workshopFabricColorKeyByNormalizedKey[summaryNormalizedKey]) {
          workshopFabricColorKeyByNormalizedKey[summaryNormalizedKey] = colorKey;
        }
      });
      const getWorkshopFabricTint = (
        fabricKey = "",
        fallbackLabel = "",
        explicitColorKey = ""
      ) => {
        const normalizedFabricKey = normalizeFabricKey(fabricKey);
        const colorKey =
          normalizeFabricKey(explicitColorKey) ||
          workshopFabricColorKeyByNormalizedKey[normalizedFabricKey] ||
          normalizedFabricKey ||
          normalizeFabricKey(fallbackLabel) ||
          "__workshop_fabric__";
        const registeredKey = registerWorkshopFabricColorKey(colorKey);
        return fabricColorMap[registeredKey] || getWorkshopPdfFabricTintByIndex(0);
      };
      const rowVisuals = [];
      const workshopHalfPieceAudit = workshopPlan.audit || {};
      const availableHalfPieces = Number(
        workshopHalfPieceAudit.totalHalfPiecesAvailable ??
          workshopPlan.rowPlans
            .filter(Boolean)
            .reduce((sum, plan) => sum + (plan?.createdLeftoverId ? 1 : 0), 0)
      );
      const reusedHalfPieces = Number(
        workshopHalfPieceAudit.totalHalfPiecesReused ??
          workshopPlan.rowPlans
            .filter(Boolean)
            .reduce((sum, plan) => sum + Number(plan?.reusedHalfCount || 0), 0)
      );
      const halfPieceMetersSaved = round2(
        Number(workshopHalfPieceAudit.totalMetersSaved || 0)
      );
      const halfPieceReuseResult =
        reusedHalfPieces > 0
          ? "Reuse applied"
          : workshopHalfPieceAudit.reasons?.[0] || "No compatible half pieces found";

      const getWorkshopRowVisual = (rowPlan) => {
        const linkedLeftoverId =
          rowPlan?.createdLeftoverId || rowPlan?.reusedLeftoverId || "";

        if (rowPlan?.reusedLeftoverId) {
          return {
            label: "Reused Half",
            linkedLeftoverId,
          };
        }

        if (rowPlan?.createdLeftoverId) {
          return {
            label: "New Cut + Half Leftover Saved",
            linkedLeftoverId,
          };
        }

        return {
          label: "New Cut",
          linkedLeftoverId: "",
        };
      };

      const summaryBoxW = 88;
      const summaryBoxX = pageW - marginX - summaryBoxW;
      const summaryBoxY = y - 4;
      doc.setFillColor(248, 250, 252);
      doc.setDrawColor(226, 232, 240);
      doc.roundedRect(summaryBoxX, summaryBoxY, summaryBoxW, 36, 3, 3, "FD");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text("Half-Piece Tracking", summaryBoxX + 4, summaryBoxY + 5);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.6);
      doc.text(
        `Half-piece reuse: ${useHalfPieceReuse ? "Enabled" : "Disabled"}`,
        summaryBoxX + 4,
        summaryBoxY + 10
      );
      doc.text(
        `Half Pieces Available: ${availableHalfPieces}`,
        summaryBoxX + 4,
        summaryBoxY + 15
      );
      doc.text(
        `Half Pieces Reused: ${reusedHalfPieces}`,
        summaryBoxX + 4,
        summaryBoxY + 20
      );
      doc.text(
        `Fabric Saved: ${halfPieceMetersSaved.toFixed(2)} m`,
        summaryBoxX + 4,
        summaryBoxY + 25
      );
      const resultLines = doc.splitTextToSize(`Reuse Result: ${halfPieceReuseResult}`, summaryBoxW - 8);
      doc.text(resultLines.slice(0, 2), summaryBoxX + 4, summaryBoxY + 30);
      y = Math.max(y + 2, summaryBoxY + 40);

      if (IS_DEV_UI_ENABLED) {
        console.log(
          "WORKSHOP STITCH DEBUG",
          buildStitchTrace(curtainRomanRows, "curtain_roman_rows")
        );
      }

      const workshopLiningSummaryRows = [];

      curtainRomanRows.forEach((r, rowIdx) => {
        const rowId = getWorkshopRowStableId(r) || `workshop-row-${rowIdx}`;
        const rowPlan = workshopPlan.rowPlans[rowIdx];
        const c = calcRow(r, repeatSettings);
        const effectiveRowPlan = rowPlan || {
          totalPieces: c.totalPieces || c.pcsHalf || c.pcs || 1,
          cutHeightIn: c.cutCurtain || c.cutRoman || Number(r.height || 0),
          newMeters: c.fabricMeters || c.fabricMetersFull || 0,
          newFullCuts: c.pcsFull || c.pcs || 0,
          reusedHalfCount: 0,
          createdLeftoverId: "",
          reusedLeftoverId: "",
        };

        const fabricLabel = getCurtainFabricDisplayWithBrand(r);
        const fabricKey = getCurtainRowFabricKey(r);
        const repeat = repeatSettings?.[fabricKey] || {};

        const repeatText = formatRepeatSettings(repeat);
        const hasRowRepeat = repeatText !== WORKSHOP_EMPTY_VALUE;
        const repeatLabel =
          hasRowRepeat
            ? `Repeat Setting: ${repeatText}`
            : "";

        const item = getCurtainRowDisplay(r.item || r.name, `W${rowIdx + 1}`);
        const product = getCurtainRowDisplay(r.productType || r.product, "Curtains");
        const fabric = fabricLabel;
        const widthIn = num(r.width);
        const heightIn = num(r.height);
        const fabricW = num(r.fabricWidthIn) || 54;
        const totalPieces = Number(effectiveRowPlan.totalPieces || 0);
        const cutHeightIn = Number(effectiveRowPlan.cutHeightIn || 0);
        const allocation =
          workshopAllocationByRowId[rowId] || {
            rowId,
            fabricKey,
            fabricCode: fabric,
            panels: totalPieces,
            rawPanels: 0,
            halfStepProductionPanels: totalPieces,
            wholeBillingPanels: Number(effectiveRowPlan.newFullCuts || 0),
            cutHeightIn,
            cutHeightMeters: cutHeightIn > 0 ? cutHeightIn / 39.3701 : 0,
            newFullCuts: Number(effectiveRowPlan.newFullCuts || 0),
            reusedHalfCount: Number(effectiveRowPlan.reusedHalfCount || 0),
            savedHalfCount: effectiveRowPlan.createdLeftoverId ? 1 : 0,
            rowNewFabricMeters: round2(
              Number(effectiveRowPlan.newMeters || 0) ||
                Number(effectiveRowPlan.newFullCuts || 0) * (cutHeightIn / 39.3701)
            ),
            rowWorkshopRequiredMeters: round2(Number(effectiveRowPlan.newMeters || 0)),
            rowCustomerBillableMeters: 0,
            rowFullOrderedMeters: 0,
            rowCutFabricMeters: 0,
            rowHalfReusedMeters: 0,
            fabricSource: "Calculated Fallback",
            fabricColorKey: getWorkshopFabricColorKey(r, fabric),
            reusedFromRowId: "",
            reusedIntoRowId: "",
            expectedNewFabricFromCuts: round2(
              Number(effectiveRowPlan.newFullCuts || 0) * (cutHeightIn / 39.3701)
            ),
            sourceUsed: "effectiveRowPlan fallback",
          };
        const newMeters = round2(Number(allocation.rowNewFabricMeters || 0));
        const rowVisual = rowPlan
          ? getWorkshopRowVisual(rowPlan)
          : {
              fillColor: [255, 255, 255],
              label: "Measurement Row",
              linkedLeftoverId: "",
            };
        const displayProductConfig = getRowProductConfig(r);
        const stitchConfigMatch = findCurtainRowStitchConfig(r, stitchingRates);
        const stitchCfg = stitchConfigMatch?.config || {};
        const resolvedStitchLabel = getCurtainRowWorkshopStitchLabel(
          r,
          stitchingRates,
          displayProductConfig
        );
        const trackTypeLabel = getCurtainRowDisplay(stitchCfg.trackType, "");
        const workshopStitchLabel = resolvedStitchLabel;
        const trackParts = [
          trackTypeLabel,
          Number(stitchCfg.trackLengthFt || 0) > 0
            ? `${Number(stitchCfg.trackLengthFt)} ft`
            : "",
        ].filter(Boolean);
        const notesLabel = getCurtainRowDisplay(
          r.remarks || r.remark || r.notes || r.note,
          ""
        );
        const sourceRowIndex = Number.isInteger(Number(r.sourceIndex))
          ? Number(r.sourceIndex)
          : rowIdx;
        const adjustedCustomerCalc = getCurtainProductionAdjustedRowCalc(
          r,
          sourceRowIndex
        );
        if (IS_DEV_UI_ENABLED) {
          const fabricSummaryForRow =
            getWorkshopFabricSummaryRowsForPdf().find((summary) => {
              const summaryKey =
                summary.normalizedKey ||
                summary.fabricKey ||
                normalizeFabricKey(summary.fabricCode || summary.displayName || "");
              return summaryKey === fabricKey;
            }) || {};
          const rowTrace = {
            quoteNo,
            rowId,
            itemName: item,
            product,
            fabricCode: fabric,
            fabricKey,
            panels: allocation.panels,
            newFullCuts: allocation.newFullCuts,
            reusedHalfCount: allocation.reusedHalfCount,
            cutHeightIn: allocation.cutHeightIn,
            expectedNewFabricFromCuts: allocation.expectedNewFabricFromCuts,
            rowWorkshopNewFabricMeters: allocation.rowNewFabricMeters,
            rowWorkshopRequiredMeters: allocation.rowWorkshopRequiredMeters,
            rowCustomerBillableMeters: allocation.rowCustomerBillableMeters,
            groupNewFabricNeededMeters: Number(fabricSummaryForRow.newFabricNeededMeters || 0),
            groupWorkshopRequiredMeters: Number(fabricSummaryForRow.workshopFabricRequiredMeters || 0),
            printedNewFabricMeters: newMeters,
            sourceUsed: allocation.sourceUsed,
          };
          console.log("WORKSHOP_PDF_ROW_METER_SOURCE_TRACE", rowTrace);
          if (Math.abs(newMeters - Number(allocation.expectedNewFabricFromCuts || 0)) > 0.05) {
            console.error("WORKSHOP_PDF_ROW_NEW_FABRIC_MISMATCH", rowTrace);
          }
        }
        const lining = adjustedCustomerCalc.lining || calculateCurtainRowLining(
          r,
          Number(
            adjustedCustomerCalc.customerBillableFabricMeters ??
              adjustedCustomerCalc.orderedFabricMeters ??
              adjustedCustomerCalc.fabricMeters ??
              0
          ),
          curtainRowSupportsLining(r, adjustedCustomerCalc),
          {
            sqft:
              adjustedCustomerCalc.adjSqft ??
              adjustedCustomerCalc.billableSqft ??
              0,
          }
        );
        const liningDisplay =
          lining?.enabled
            ? (() => {
                const liningBrandName =
                  sanitizeEditableText(lining.brandName || "") ||
                  getFabricBrandNameById(lining.brandId);
                const liningFabric = [
                  liningBrandName,
                  sanitizeEditableText(lining.fabricCode || ""),
                ]
                  .filter(Boolean)
                  .join(" / ");
                const isSqftLining = lining.chargeMethod === "sqft";
                const liningQuantity = isSqftLining
                  ? `${Number(lining.sqft || 0).toFixed(2)} sqft`
                  : `${Number(lining.billableMeters || 0).toFixed(2)} m`;
                const liningBasis = isSqftLining
                  ? "Sqft"
                  : lining.metersMode === "custom_override"
                  ? "Custom meters"
                  : "Meter";
                const liningLabel = getCurtainLiningLabel(lining);
                return {
                  cellText: [
                    "LINING",
                    liningLabel,
                    liningFabric ? `Fabric: ${liningFabric}` : "",
                    liningQuantity,
                    `Basis: ${liningBasis}`,
                  ]
                    .filter(Boolean)
                    .join("\n"),
                  summaryRow: [
                    item,
                    fabric,
                    liningLabel,
                    liningFabric || WORKSHOP_EMPTY_VALUE,
                    liningBasis,
                    liningQuantity,
                    sanitizeEditableText(lining.notes || "") || WORKSHOP_EMPTY_VALUE,
                  ],
                };
              })()
            : {
                cellText: WORKSHOP_EMPTY_VALUE,
                summaryRow: null,
              };
        if (liningDisplay.summaryRow) {
          workshopLiningSummaryRows.push(liningDisplay.summaryRow);
        }
        const workshopTrackConfig = adjustedCustomerCalc.trackConfig || calculateCurtainTrackConfig(
          r,
          stitchCfg,
          {
            baseTrackLengthFt:
              Number(r.trackLengthFt || r.trackLength || stitchCfg.trackLengthFt || 0),
            measurementUnit: getRowMeasurementUnit(r, unit),
          }
        );
        const segmentParts = getTrackSegmentEntries(workshopTrackConfig).map((segment) =>
          `${segment.label || ""} ${formatLengthWithUnit(
            segment.length || 0,
            segment.unit || workshopTrackConfig.segmentUnit || "in"
          )}`.trim()
        );
        const workshopTrackUnit =
          workshopTrackConfig.totalSegmentLengthUnit ||
          workshopTrackConfig.segmentUnit ||
          getRowMeasurementUnit(r, unit);
        const bendedTrackParts =
          workshopTrackConfig?.isBended
            ? [
                "Track: Bended Track",
                `Shape: ${getBendedTrackShapeMeta(workshopTrackConfig.shape).label}`,
                workshopTrackConfig.bendLocationSummary
                  ? `Bend Location: ${workshopTrackConfig.bendLocationSummary}`
                  : "",
                workshopTrackConfig.angleDeg ? `Angle: ${Number(workshopTrackConfig.angleDeg).toFixed(0)} deg` : "",
                `No. of bends: ${Number(workshopTrackConfig.bendCount || 0)}`,
                segmentParts.length ? `Segments: ${segmentParts.join(" | ")}` : "",
                Number(workshopTrackConfig.totalSegmentLength || 0) > 0
                  ? `Segment length: ${formatLengthWithUnit(
                      workshopTrackConfig.totalSegmentLength || 0,
                      workshopTrackUnit
                    )}`
                  : "",
                Number(workshopTrackConfig.totalTrackLengthFt || 0) > 0
                  ? `Running length: ${Number(workshopTrackConfig.totalTrackLengthFt || 0).toFixed(2)} running ft`
                  : "",
                workshopTrackConfig.notes ? `Track Notes: ${workshopTrackConfig.notes}` : "",
              ].filter(Boolean)
            : [];
        const fabricTint = getWorkshopFabricTint(
          fabricKey,
          fabric,
          allocation.fabricColorKey || getWorkshopFabricColorKey(r, fabric)
        );
        rowVisuals.push({
          ...rowVisual,
          fillColor: fabricTint,
          fabricTint,
          halfPieceLabel: allocation.fabricSource || rowVisual.label,
          hasLining: Boolean(lining?.enabled),
        });

        const displayFabricRules = resolveFabricRulesForRow(
          r,
          shouldUseLegacyProductCalculation(r, displayProductConfig)
            ? null
            : displayProductConfig,
          fabricCalcSettings
        );
        let visibleDropIn = heightIn;
        if (r.productType === "Curtains") {
          visibleDropIn = heightIn + Number(displayFabricRules.dropAllowanceIn || 0);
        } else if (r.productType?.startsWith("Roman")) {
          visibleDropIn = heightIn + Number(displayFabricRules.dropAllowanceIn || 0);
        }

        const cuttingExpr = [
          `Panels: ${totalPieces.toFixed(1)} pcs`,
          `New Full Cuts: ${Number(effectiveRowPlan.newFullCuts || 0)}`,
          Number(effectiveRowPlan.reusedHalfCount || 0) > 0
            ? `Reused Half: ${effectiveRowPlan.reusedHalfCount}`
            : "",
          `Each: ${formatMeasurementWithUnit(fabricW, unit, "W")} x ${formatMeasurementWithUnit(
            cutHeightIn,
            unit,
            "Cut H"
          )}`,
          `Visible Drop: ${formatMeasurementWithUnit(visibleDropIn, unit)}`,
          hasRowRepeat ? "Repeat Cut" : "",
          repeatLabel,
          trackParts.length ? `Track: ${trackParts.join(" | ")}` : "",
          ...bendedTrackParts,
          `Half-piece: [${allocation.fabricSource || rowVisual.label}]`,
          notesLabel ? `Notes: ${notesLabel}` : "",
        ]
          .filter(Boolean)
          .join("\n");

        tableRows.push([
          item,
          product,
          fabric,
          workshopStitchLabel,
          formatMeasurementWithUnit(widthIn, unit, "W"),
          formatMeasurementWithUnit(heightIn, unit, "Finished H"),
          cuttingExpr,
          liningDisplay.cellText,
          newMeters.toFixed(2),
        ]);
      });

      if (IS_DEV_UI_ENABLED) {
        console.log("STITCH_TRACE_4_PDF_TABLE_ROWS", tableRows);
        console.log("CURTAIN_WORKSHOP_TABLE_BODY", tableRows);
      }

      assertNoPricingInWorkshopPdfText(
        {
          head: [
            "Item",
            "Product",
            "Fabric",
            "Stitch",
            "W",
            "H",
            "Cutting",
            "Lining",
            "Fabric to Order (m)",
          ],
          body: tableRows,
          summaryHead: [
            "Brand",
            "Fabric / SR",
            "Used in",
            "Fabric to Order (m)",
            "Half-piece Saving (m)",
            "Repeat",
            "Notes",
          ],
          summaryBody: workshopFabricSummaryRows.map((summary) => [
            summary.brandName || "",
            summary.fabricCode || summary.displayName || "",
            (summary.usedInItems || []).slice(0, 6).join(", "),
            Number(
              summary?.fabricToOrderMeters ??
                summary?.workshopFabricRequiredMeters ??
                summary?.newFabricNeededMeters ??
                0
            ).toFixed(2),
            Number(summary?.halfReusedMeters || 0).toFixed(2),
            getRepeatSummaryDisplay(summary),
            summary.notes || "",
          ]),
          liningHead: [
            "Item",
            "Main Fabric",
            "Lining",
            "Lining Fabric / Code",
            "Basis",
            "Quantity",
            "Notes",
          ],
          liningBody: workshopLiningSummaryRows,
        },
        { label: "curtainWorkshopPdfText" }
      );

      autoTable(doc, {
        startY: y,
        margin: { left: marginX, right: marginX },
        head: [[
          "Item",
          "Product",
          "Fabric",
          "Stitch",
          "W",
          "H",
          "Cutting",
          "Lining",
          "Fabric to Order (m)",
        ]],
        body: tableRows,
        rowPageBreak: "avoid",
        styles: {
          fontSize: 8.7,
          cellPadding: 2.6,
          valign: "top",
          overflow: "linebreak",
          textColor: [15, 23, 42],
          lineColor: [226, 232, 240],
          lineWidth: 0.1,
        },
        headStyles: { fillColor: [15, 23, 42], textColor: 255 },
        columnStyles: {
          0: { cellWidth: 14 },
          1: { cellWidth: 16 },
          2: { cellWidth: 24 },
          3: { cellWidth: 15 },
          4: { cellWidth: 13, halign: "right" },
          5: { cellWidth: 15, halign: "right" },
          6: { cellWidth: 34 },
          7: { cellWidth: 28 },
          8: { halign: "right", cellWidth: 20 },
        },
        didParseCell: (data) => {
          if (data.section !== "body") return;

          const visual = rowVisuals[data.row.index];
          if (visual?.fillColor) {
            data.cell.styles.fillColor = visual.fillColor;
          }

          if (data.column.index === 6) {
            data.cell.styles.minCellHeight = Math.max(
              Number(data.cell.styles.minCellHeight || 0),
              17
            );
          }
          if (data.column.index === 7 && visual?.hasLining) {
            data.cell.styles.fillColor = [236, 253, 245];
            data.cell.styles.textColor = [6, 95, 70];
            data.cell.styles.fontStyle = "bold";
          }
        },
      });

      const afterTableY = Number(doc.lastAutoTable?.finalY || y + 10) + 8;

      autoTable(doc, {
        startY: afterTableY,
        margin: { left: marginX, right: marginX },
        head: [[
          "Brand",
          "Fabric / SR",
          "Used in",
          "Fabric to Order (m)",
          "Half-piece Saving (m)",
          "Repeat",
          "Notes",
        ]],
        body: workshopFabricSummaryRows.map((summary) => [
          summary.brandName || "",
          summary.fabricCode || summary.displayName || "",
          (summary.usedInItems || []).slice(0, 6).join(", "),
          Number(
            summary?.fabricToOrderMeters ??
              summary?.workshopFabricRequiredMeters ??
              summary?.newFabricNeededMeters ??
              0
          ).toFixed(2),
          Number(summary?.halfReusedMeters || 0).toFixed(2),
          getRepeatSummaryDisplay(summary),
          summary.notes || "",
        ]),
        styles: { fontSize: 7.4, cellPadding: 1.6, overflow: "linebreak" },
        headStyles: { fillColor: [37, 99, 235], textColor: 255 },
        columnStyles: {
          3: { halign: "right" },
          4: { halign: "right" },
        },
        didParseCell: (data) => {
          if (data.section !== "body") return;
          const summary = workshopFabricSummaryRows[data.row.index] || {};
          data.cell.styles.fillColor = getWorkshopFabricTint(
            summary.normalizedKey || summary.fabricKey || "",
            summary.fabricCode || summary.displayName || ""
          );
        },
      });

      if (workshopLiningSummaryRows.length > 0) {
        const afterFabricSummaryY = Number(doc.lastAutoTable?.finalY || afterTableY) + 8;
        autoTable(doc, {
          startY: afterFabricSummaryY,
          margin: { left: marginX, right: marginX },
          head: [[
            "Item",
            "Main Fabric",
            "Lining",
            "Lining Fabric / Code",
            "Basis",
            "Quantity",
            "Notes",
          ]],
          body: workshopLiningSummaryRows,
          styles: { fontSize: 7.5, cellPadding: 1.7, overflow: "linebreak" },
          headStyles: { fillColor: [5, 150, 105], textColor: 255 },
          columnStyles: {
            1: { cellWidth: 28 },
            3: { cellWidth: 32 },
            5: { halign: "right" },
          },
        });
      }

      addWorkshopFooter(doc);

      zip.file(
        `Workshop-Curtains-Romans-${customerName || "Customer"}-${quoteNo}.pdf`,
        doc.output("arraybuffer")
      );
    }

    if (accessoryRows.length) {
      const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
      const { marginX, pageW, headerBottomY } = addWorkshopHeader(
        doc,
        logoMeta,
        shopAddress
      );
      const titleY = headerBottomY + 10;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.text("Accessories / Hardware Required", pageW / 2, titleY, {
        align: "center",
      });

      let y = titleY + 14;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(`Customer: ${customerName || WORKSHOP_EMPTY_VALUE}`, marginX, y);
      y += 6;
      doc.text(`Quote: ${quoteNo || WORKSHOP_EMPTY_VALUE}`, marginX, y);
      y += 6;
      doc.text(`Date: ${workshopQuoteDate || WORKSHOP_EMPTY_VALUE}`, marginX, y);
      y += 10;

      const accessoryBody = accessoryRows.map((row, index) => {
        const calc = calcRow(row, repeatSettings);
        const calcType = calc.calculationType || row.calculationType || "";
        const isLengthRow = calcType === "running_length_rate";
        const quantityValue = isLengthRow
          ? row.length || calc.runningLength || ""
          : row.qty || row.quantity || calc.quantity || "";
        const unitValue = isLengthRow
          ? row.lengthUnit || calc.lengthUnit || "ft"
          : row.unit || calc.unit || "pcs";
        return [
          getCurtainRowDisplay(row.item || row.name, `A${index + 1}`),
          getCurtainRowDisplay(row.productName || row.productType || row.product, "Accessory"),
          getCurtainRowDisplay(row.description || row.modelCode || row.materialCode || "", ""),
          String(quantityValue || ""),
          unitValue,
          getCurtainRowDisplay(row.notes || row.remarks || row.note, ""),
        ];
      });

      assertNoPricingInWorkshopPdfText(
        {
          head: ["Item", "Product", "Description / Code", "Qty / Length", "Unit", "Notes"],
          body: accessoryBody,
        },
        { label: "curtainWorkshopAccessoryText" }
      );

      autoTable(doc, {
        startY: y,
        margin: { left: marginX, right: marginX },
        head: [["Item", "Product", "Description / Code", "Qty / Length", "Unit", "Notes"]],
        body: accessoryBody,
        rowPageBreak: "avoid",
        styles: {
          fontSize: 9,
          cellPadding: 2.6,
          textColor: [15, 23, 42],
          lineColor: [226, 232, 240],
          lineWidth: 0.1,
          valign: "top",
        },
        headStyles: { fillColor: [15, 23, 42], textColor: 255 },
        alternateRowStyles: { fillColor: [248, 250, 255] },
        columnStyles: {
          3: { halign: "right" },
        },
      });

      addWorkshopFooter(doc);
      zip.file(
        `Workshop-Accessories-Hardware-${customerName || "Customer"}-${quoteNo}.pdf`,
        doc.output("arraybuffer")
      );
    }

    const safeName = (s) => (s || "item").replace(/[^\w-]+/g, "_");

    for (const [ptype, rowsForType] of Object.entries(blindsByType)) {
      const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });

      const { marginX, pageW, headerBottomY } = addWorkshopHeader(
        doc,
        logoMeta,
        shopAddress
      );

      const titleY = headerBottomY + 10;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.text(`Workshop Sheet - ${ptype}`, pageW / 2, titleY, {
        align: "center",
      });

      let y = titleY + 14;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(`Customer: ${customerName || WORKSHOP_EMPTY_VALUE}`, marginX, y);
      y += 6;
      doc.text(`Quote: ${quoteNo || WORKSHOP_EMPTY_VALUE}`, marginX, y);
      y += 6;
      doc.text(`Date: ${workshopQuoteDate || WORKSHOP_EMPTY_VALUE}`, marginX, y);
      y += 10;

      let totalSqft = 0;

      const body = rowsForType.map((r, idx) => {
        const c = calcRow(r, repeatSettings);
        const sqft = Number(c.billableSqft ?? c.adjSqft ?? 0);
        totalSqft += sqft;
        const quantity = r.quantity ?? r.qty ?? 1;
        const notes = getCurtainRowDisplay(
          r.remarks || r.remark || r.notes || r.note,
          ""
        );

        return [
          getCurtainRowDisplay(r.item || r.name, `W${idx + 1}`),
          getCurtainRowDisplay(r.productType || r.productName || r.product, ptype),
          getCurtainFabricDisplayWithBrand(r),
          `${displayValue(r.width, unit)} ${unitLabel(unit)}`,
          `${displayValue(r.height, unit)} ${unitLabel(unit)}`,
          sqft.toFixed(2),
          String(quantity || ""),
          notes,
        ];
      });

      assertNoPricingInWorkshopPdfText(
        {
          head: [
            "Item",
            "Product",
            "Material / Code",
            `Width (${unitLabel(unit)})`,
            `Height (${unitLabel(unit)})`,
            "Billable Sqft",
            "Qty",
            "Notes",
          ],
          body,
          summaryHead: ["Sqft Covered"],
          summaryBody: [[totalSqft.toFixed(2)]],
        },
        { label: `blindWorkshopPdfText:${ptype}` }
      );

      autoTable(doc, {
        startY: y,
        margin: { left: marginX, right: marginX },
        head: [[
          "Item",
          "Product",
          "Material / Code",
          `Width (${unitLabel(unit)})`,
          `Height (${unitLabel(unit)})`,
          "Billable Sqft",
          "Qty",
          "Notes",
        ]],
        body,
        rowPageBreak: "avoid",
        styles: {
          fontSize: 8.8,
          cellPadding: 2.2,
          textColor: [15, 23, 42],
          lineColor: [226, 232, 240],
          lineWidth: 0.1,
        },
        headStyles: { fillColor: [30, 64, 175], textColor: 255 },
        alternateRowStyles: { fillColor: [248, 250, 255] },
        columnStyles: {
          3: { halign: "right" },
          4: { halign: "right" },
          5: { halign: "right" },
          6: { halign: "right" },
        },
      });

      const afterTableY = Number(doc.lastAutoTable?.finalY || y + 10) + 8;

      autoTable(doc, {
        startY: afterTableY,
        margin: { left: marginX, right: marginX },
        head: [["Sqft Covered"]],
        body: [[totalSqft.toFixed(2)]],
        styles: { fontSize: 10, halign: "right" },
        headStyles: { fillColor: [15, 23, 42], textColor: 255 },
      });

      addWorkshopFooter(doc);
      zip.file(`Workshop-${safeName(ptype)}.pdf`, doc.output("arraybuffer"));
    }

    const zipBlob = await zip.generateAsync({
      type: "blob",
      compression: "DEFLATE",
      compressionOptions: { level: 6 },
    });

    console.timeEnd("workshop-zip");

    saveAs(
      zipBlob,
      `Workshop-${customerName || "Customer"}-${quoteNo || ""}.zip`
    );

    if (selectedQuote && workspaceUid) {
      const qRef = doc(db, "users", workspaceUid, "quotations", selectedQuote.id);

      await updateDoc(qRef, {
        materialsSnapshot: {
          realFabricUsage,
          leftovers: realLeftovers,
          savedToStock: false,
          frozenAt: serverTimestamp(),
        },
        updatedAt: serverTimestamp(),
      });
      applyQuoteLocalPatch(selectedQuote.id, {
        materialsSnapshot: {
          realFabricUsage,
          leftovers: realLeftovers,
          savedToStock: false,
          frozenAt: new Date(),
        },
        updatedAt: new Date(),
      });
    }
  } catch (err) {
    console.error("WORKSHOP_ZIP_FAILED_EXACT", {
      name: err?.name || "",
      message: err?.message || String(err || ""),
      stack: err?.stack || "",
      ...getChunkLoadDebugContext(),
    });

    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      notify(
        "You appear to be offline. Please reconnect and try Workshop ZIP again.",
        "warning"
      );
      return;
    }

    if (isChunkLoadError(err)) {
      handleChunkLoadErrorRecovery(err, {
        source: "generateWorkshopZIP.catch",
        autoReload: false,
      });
      notify(
        "Workshop exporter could not load. Please refresh the page and try again.",
        "warning",
        7000
      );
      return;
    }

    notify("Workshop ZIP failed. Please try again.", "error");
  }
};
const exportCuttingPDF = async (
  solution,
  quote,
  selectedFabric,
  finalDemands = []
) => {
  if (!solution) return alert("No optimization selected.");

  const { jsPDF } = await loadPdfRuntime();
  const doc = new jsPDF("p", "mm", "a4");

  const pageW = Number(doc.internal.pageSize.getWidth());
  const pageH = Number(doc.internal.pageSize.getHeight());
  const marginX = 15;

  const safeCustomer = quote?.customerName?.trim() || "-";
  const safeQuote = quote?.quoteNo?.trim() || "-";
  const quoteRowsForFabric = Array.isArray(quote?.rows)
    ? quote.rows
    : [];
  const safeFabric =
    normalizeWorkshopText(selectedFabric) ||
    quoteRowsForFabric.map((row) => getCurtainRowFabricLabel(row)).find((label) => label && label !== WORKSHOP_EMPTY_VALUE) ||
    WORKSHOP_EMPTY_VALUE;
  const safeDate = new Date().toLocaleDateString();

  const totalRequired = round2(
    (finalDemands || []).reduce(
      (sum, d) => sum + Number(d.length || 0) * Number(d.qty || 0),
      0
    )
  );

  let logoMeta = null;
  try {
    const raw = await prepareLogoForWorkshop(getLogoDataURL, logoURL);

    if (raw?.dataUrl) {
      const BASE_W = 64;
      const originalW = raw.logoWmm || BASE_W;
      const originalH = raw.logoHmm || 12;

      const scale = BASE_W / originalW;
      let w = BASE_W;
      let h = originalH * scale;

      const MAX_H = 32;
      const MIN_H = 16;

      if (h > MAX_H) {
        const s = MAX_H / h;
        h = MAX_H;
        w = w * s;
      }
      if (h < MIN_H) {
        const s = MIN_H / h;
        h = MIN_H;
        w = w * s;
      }

      logoMeta = {
        dataUrl: raw.dataUrl,
        format: "PNG",
        w,
        h,
      };
    }
  } catch (e) {
    console.warn("Logo load failed:", e);
  }

  const headerTop = 12;
  const headerHeight = 24;

  if (logoMeta) {
    const logoY = headerTop + (headerHeight - logoMeta.h) / 2;

    doc.addImage(
      logoMeta.dataUrl,
      logoMeta.format,
      marginX,
      logoY,
      logoMeta.w,
      logoMeta.h
    );
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("ROLL OPTIMIZATION", pageW / 2, 25, {
    align: "center",
  });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(shopAddress || "", pageW - marginX, 20, {
    align: "right",
  });

  const ruleY = headerTop + headerHeight + 4;
  doc.setLineWidth(0.3);
  doc.line(marginX, ruleY, pageW - marginX, ruleY);

  let y = ruleY + 10;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Customer:", marginX, y);
  doc.setFont("helvetica", "normal");
  doc.text(safeCustomer, marginX + 28, y);

  doc.setFont("helvetica", "bold");
  doc.text("Quote No:", pageW / 2 + 10, y);
  doc.setFont("helvetica", "normal");
  doc.text(safeQuote, pageW / 2 + 32, y);

  y += 7;

  doc.setFont("helvetica", "bold");
  doc.text("Fabric:", marginX, y);
  doc.setFont("helvetica", "normal");
  doc.text(safeFabric, marginX + 28, y);

  doc.setFont("helvetica", "bold");
  doc.text("Date:", pageW / 2 + 10, y);
  doc.setFont("helvetica", "normal");
  doc.text(safeDate, pageW / 2 + 32, y);

  y += 7;

  doc.setFont("helvetica", "bold");
  doc.text("Total Required:", marginX, y);
  doc.setFont("helvetica", "normal");
  doc.text(`${Number(totalRequired).toFixed(2)} m`, marginX + 36, y);

  y += 12;

  const contentRightX = pageW - marginX;
  const maxContentY = pageH - 24;
  let currentSectionTitle = "";
  let sectionRowCount = 0;

  const drawSectionTitle = (title) => {
    if (y + 10 > maxContentY) {
      doc.addPage();
      y = 20;
    }

    currentSectionTitle = title;
    sectionRowCount = 0;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text(title, marginX, y);
    y += 7;
  };

  const addSectionPage = (title = currentSectionTitle) => {
    doc.addPage();
    y = 20;
    currentSectionTitle = title || "";
    sectionRowCount = 0;

    if (title) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.text(title, marginX, y);
      y += 7;
    }
  };

  const ensureSectionSpace = (neededHeight, title = currentSectionTitle) => {
    if (
      y + neededHeight > maxContentY ||
      (sectionRowCount > 0 && sectionRowCount % 25 === 0)
    ) {
      addSectionPage(title);
    }
  };

  const ensurePlainSpace = (neededHeight) => {
    if (y + neededHeight > maxContentY) {
      doc.addPage();
      y = 20;
      currentSectionTitle = "";
      sectionRowCount = 0;
    }
  };

  const getCutDisplayMeta = (cutHeight) => {
    const matchedRow = (quote?.rows || []).find((row) => {
      const calc = calcRow(row, repeatSettings);
      const rowCutHeight = Number(calc.cutCurtain || calc.cutRoman || 0);
      return Math.abs(rowCutHeight - Number(cutHeight || 0)) < 0.5;
    });

    let visibleDrop = Number(cutHeight || 0);
    let fabricKey = normalizeFabricKey(safeFabric || selectedFabric || "");

    if (matchedRow) {
      fabricKey = getCurtainRowFabricKey(matchedRow) || fabricKey;

      const isCurtain = matchedRow.productType === "Curtains";
      const matchedProductConfig = getRowProductConfig(matchedRow);
      const matchedFabricRules = resolveFabricRulesForRow(
        matchedRow,
        shouldUseLegacyProductCalculation(matchedRow, matchedProductConfig)
          ? null
          : matchedProductConfig,
        fabricCalcSettings
      );
      if (isCurtain) {
        visibleDrop =
          Number(matchedRow.height || 0) + Number(matchedFabricRules.dropAllowanceIn || 0);
      } else {
        visibleDrop =
          Number(matchedRow.height || 0) + Number(matchedFabricRules.dropAllowanceIn || 0);
      }
    }

    const repeat = repeatSettings?.[fabricKey] || {};
    const repeatDisplay = formatRepeatSettings(repeat);
    const hasRepeat = repeatDisplay !== WORKSHOP_EMPTY_VALUE;

    const displayCutHeightIn = hasRepeat
      ? Number(cutHeight || 0)
      : Number(visibleDrop || 0);

    return {
      repeatDisplay,
      hasRepeat,
      visibleDropIn: Number(visibleDrop || 0),
      displayCutHeightIn,
    };
  };

  const drawCutRow = ({ qty, cutHeight, sectionTitle }) => {
    const { repeatDisplay, hasRepeat, visibleDropIn, displayCutHeightIn } =
      getCutDisplayMeta(cutHeight);

    const rowLines = [
      `${qty} pcs`,
      `Each: ${formatMeasurementWithUnit(54, unit, "W")} x ${formatMeasurementWithUnit(
        displayCutHeightIn,
        unit,
        "Cut H"
      )}`,
      hasRepeat ? `Visible Drop: ${formatMeasurementWithUnit(visibleDropIn, unit)}` : "",
      hasRepeat ? "Repeat Cut" : "Standard Cut",
      hasRepeat ? `Repeat Setting: ${repeatDisplay}` : "",
    ].filter(Boolean);

    const rowHeight = 8 + rowLines.length * 4.5 + 3;

    ensureSectionSpace(rowHeight, sectionTitle);

    doc.setFillColor(
      sectionTitle === "EXTRA CUT FABRIC REQUIRED" ? 254 : 248,
      sectionTitle === "EXTRA CUT FABRIC REQUIRED" ? 249 : 250,
      sectionTitle === "EXTRA CUT FABRIC REQUIRED" ? 195 : 252
    );
    doc.roundedRect(marginX, y - 4, pageW - marginX * 2, rowHeight, 2.5, 2.5, "F");

    rowLines.forEach((line, index) => {
      doc.setFont("helvetica", index === 0 ? "bold" : "normal");
      doc.setFontSize(index === 0 ? 10.5 : 9.5);
      doc.text(line, marginX + 4, y);
      y += 4.5;
    });

    y += 3;
    sectionRowCount += 1;
  };

  drawSectionTitle("ROLL CUTTING");

  (solution.rollResults || []).forEach((rr, i) => {
    const used = Number(rr?.patternInfo?.used || 0).toFixed(2);
    const waste = Number(rr?.patternInfo?.waste || 0).toFixed(2);
    ensureSectionSpace(18, "ROLL CUTTING");

    doc.setFillColor(241, 245, 249);
    doc.roundedRect(marginX, y - 4, pageW - marginX * 2, 16, 3, 3, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(`Roll ${i + 1}`, marginX + 4, y + 1);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.text(
      `Total Length: ${formatMetersLabel(rr?.rollLen || 0)}`,
      marginX + 4,
      y + 7
    );
    doc.text(
      `Used: ${formatMetersLabel(used)} | Waste: ${formatMetersLabel(waste)}`,
      contentRightX - 4,
      y + 7,
      { align: "right" }
    );
    y += 18;

    Object.entries(rr?.patternInfo?.pattern || {}).forEach(([len, qty]) => {
      drawCutRow({
        qty,
        cutHeight: Number(len || 0) * 39.37,
        sectionTitle: "ROLL CUTTING",
      });
    });

    y += 4;
  });

  const totalRollWaste = (solution.rollResults || []).reduce(
    (sum, result) => sum + Number(result?.patternInfo?.waste || 0),
    0
  );

  ensurePlainSpace(8);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(
    `Total Roll Waste: ${formatMetersLabel(totalRollWaste)}`,
    marginX,
    y
  );

  y += 12;

  drawSectionTitle("EXTRA CUT FABRIC REQUIRED");

  if (solution.remaining?.length) {
    solution.remaining.forEach((item) => {
      drawCutRow({
        qty: item.qty,
        cutHeight: Number(item.length || 0) * 39.37,
        sectionTitle: "EXTRA CUT FABRIC REQUIRED",
      });
    });

    y += 4;
    ensurePlainSpace(8);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(
      `Total Cut Purchase Needed: ${formatMetersLabel(
        solution.finalRemainingMeters || 0
      )}`,
      marginX,
      y
    );
  } else {
    ensurePlainSpace(6);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text("No extra cut fabric required.", marginX + 4, y);
  }

  y += 18;
  ensurePlainSpace(14);
  doc.setLineWidth(0.2);
  doc.line(marginX, y, marginX + 60, y);
  doc.line(pageW - marginX - 60, y, pageW - marginX, y);

  doc.setFontSize(10);
  doc.text("Prepared By", marginX, y + 6);
  doc.text("Workshop Approval", pageW - marginX - 60, y + 6);

  const pageCount = doc.getNumberOfPages();
  for (let pageNo = 1; pageNo <= pageCount; pageNo++) {
    doc.setPage(pageNo);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");

    doc.text(
      `Generated on: ${new Date().toLocaleString()}`,
      marginX,
      pageH - 12
    );

    doc.text(`Powered by FabriQuote`, pageW / 2, pageH - 12, {
      align: "center",
    });

    doc.text(`Page ${pageNo} of ${pageCount}`, pageW - marginX, pageH - 12, {
      align: "right",
    });
  }

  doc.save(`Cutting-${safeCustomer}-${safeQuote}-Selected.pdf`);
};

// ================= TRACK OPTIMIZATION PDF =================
const buildRollVisualization = (rollResults) => {
  return (rollResults || []).map((rr, i) => {
    const segments = [];
    let current = 0;

    const pattern = rr?.patternInfo?.pattern || {};

    Object.entries(pattern)
      .sort((a, b) => Number(b[0]) - Number(a[0]))
      .forEach(([length, count]) => {
      const len = Number(length);
      const qty = Number(count) || 0;

      for (let j = 0; j < qty; j++) {
        segments.push({ type: "cut", length: len });
        current += len;
      }
      });

    const rollLength = Number(rr?.rollLength || rr?.rollLen || 0);
    const wasteLength = Math.max(0, rollLength - current);

    if (wasteLength > 0) {
      segments.push({
        type: "waste",
        length: wasteLength,
      });
    }

    return {
      rollIndex: i + 1,
      rollLength,
      segments,
      usedMeters: current,
      wasteMeters: wasteLength,
    };
  });
};

const RollVisualizer = ({ rolls }) => {
  return (
    <div style={{ marginTop: 20 }}>
      {(rolls || []).map((roll, i) => (
        <div key={i} style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12 }}>
            Roll {roll.rollIndex} ({roll.rollLength}m)
          </div>
          <div style={{ fontSize: 11, marginBottom: 4 }}>
            Used: {Number(roll.usedMeters || 0).toFixed(2)}m | Waste: {Number(roll.wasteMeters || 0).toFixed(2)}m
          </div>

          <div
            style={{
              display: "flex",
              height: 20,
              border: "1px solid #e5e7eb",
              overflow: "hidden",
            }}
          >
            {(() => {
              const containerWidth = 400;

              const safeContainerWidth =
                Number.isFinite(containerWidth) && containerWidth > 0
                  ? Math.floor(containerWidth)
                  : 1;

              const segs = Array.isArray(roll?.segments) ? roll.segments : [];
              const n = segs.length;

              const raw = segs.map((seg) => {
                const v = Number(seg?.length);
                return Number.isFinite(v) && v > 0 ? v : 0;
              });

              const total = raw.reduce((a, b) => a + b, 0);

              const norm =
                total > 0
                  ? raw.map((w) => (w / total) * safeContainerWidth)
                  : raw.map(() => safeContainerWidth / Math.max(1, n));

              let widths = norm.map((v) => Math.floor(v));

              let remaining =
                safeContainerWidth - widths.reduce((a, b) => a + b, 0);

              const order = norm
                .map((v, i) => ({ i, frac: v - Math.floor(v) }))
                .sort((a, b) => b.frac - a.frac);

              for (let k = 0; k < order.length && remaining > 0; k++) {
                widths[order[k].i] += 1;
                remaining--;
              }

              if (n > 0 && n <= safeContainerWidth) {
                let deficit = 0;

                for (let i = 0; i < n; i++) {
                  if (widths[i] < 1) {
                    deficit += 1 - widths[i];
                    widths[i] = 1;
                  }
                }

                if (deficit > 0) {
                  let pass = 0;
                  const maxPass = n;

                  while (deficit > 0 && pass < maxPass) {
                    let changed = false;

                    const sorted = [...widths]
                      .map((w, i) => ({ i, w }))
                      .sort((a, b) => b.w - a.w);

                    for (
                      let k = 0;
                      k < sorted.length && deficit > 0;
                      k++
                    ) {
                      const idx = sorted[k].i;

                      if (widths[idx] > 1) {
                        widths[idx] -= 1;
                        deficit--;
                        changed = true;
                      }
                    }

                    if (!changed) break;
                    pass++;
                  }
                }
              }

              return segs.map((seg, j) => {
                const w =
                  Number.isFinite(widths[j]) && widths[j] > 0
                    ? widths[j]
                    : 1;

                const len = Number.isFinite(Number(seg?.length))
                  ? Number(seg.length)
                  : 0;

                return (
                  <div
                    key={`${j}-${seg?.type || "seg"}-${len}`}
                    title={`${seg?.type || "cut"} ${len.toFixed(2)}m`}
                    style={{
                      width: `${w}px`,
                      background:
                        seg?.type === "cut" ? "#22c55e" : "#fecaca",
                      borderRight:
                        j !== segs.length - 1
                          ? "1px solid #fff"
                          : "none",
                    }}
                  />
                );
              });
            })()}
          </div>
        </div>
      ))}
    </div>
  );
};


  /* --------------------------- UI BEGINS (PART 3 CONTINUES) --------------------------- */
   /* ---------------- MAIN PANEL ---------------- */

  const dashboardBorder = isDark
    ? "1px solid rgba(148,163,184,0.14)"
    : "1px solid rgba(226,232,240,0.9)";
  const dashboardSurface = isDark ? "#0f172a" : "#ffffff";
  const dashboardCardBg = isDark ? "rgba(15,23,42,0.86)" : "#ffffff";
  const dashboardMuted = isDark ? "#94a3b8" : "#64748b";
  const dashboardSoftBg = isDark ? "rgba(15,23,42,0.7)" : "#f8fafc";

  const dashboardPanelStyle = {
    background: dashboardSurface,
    padding: isCompact ? 18 : 26,
    borderRadius: 24,
    boxShadow: isDark
      ? "0 18px 50px rgba(2,6,23,0.42)"
      : "0 18px 50px rgba(15,23,42,0.08)",
    border: dashboardBorder,
    marginBottom: 26,
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    overflowX: "hidden",
  };

  const dashboardCardStyle = {
    background: dashboardCardBg,
    borderRadius: 22,
    padding: isCompact ? 16 : 20,
    border: dashboardBorder,
    boxShadow: isDark
      ? "0 10px 30px rgba(2,6,23,0.35)"
      : "0 10px 30px rgba(15,23,42,0.05)",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    overflowWrap: "normal",
    wordBreak: "normal",
  };

  const openDashboardCustomer = useCallback(
    (customerLike = {}) => {
      const directCustomerId = String(customerLike.customerId || customerLike.id || "").trim();
      const normalizedName = normalizeCustomerLedgerName(
        customerLike.name || customerLike.customerName || ""
      );
      const phone = normalizeCustomerLedgerPhone(customerLike.phone || customerLike.customerPhone || "");
      const matchedCustomer =
        (directCustomerId &&
          customerRecords.find((customer) => customer.id === directCustomerId)) ||
        customerRecords.find((customer) => {
          const customerPhone = normalizeCustomerLedgerPhone(
            customer.phone || customer.whatsapp || ""
          );
          if (phone && customerPhone && phone === customerPhone) return true;
          return (
            normalizedName &&
            normalizeCustomerLedgerName(customer.customerName) === normalizedName
          );
        });

      if (matchedCustomer?.id) {
        openCustomerLedger(matchedCustomer.id);
        setCustomerLedgerDetailTab("overview");
        return;
      }

      const fallbackName = String(customerLike.name || customerLike.customerName || "").trim();
      setCustomerLedgerSearch(fallbackName);
      setSelectedCustomerLedgerId("");
      setCustomerLedgerDetailTab("overview");
      setRoutedTab("customerLedger");
    },
    [customerRecords, openCustomerLedger, setRoutedTab]
  );

  const getDashboardMetricRecords = useCallback(
    (metricKey, context = {}) => {
      const revenueQuotes = periodConfirmed.filter((quote) => quote.isRevenueEligible);
      const monthKey = String(context.monthKey || "").trim();
      const quoteType = context.quoteType || "";
      const stageKey = context.stageKey || "";
      const dateKey = String(context.dateKey || "").trim();
      const monthMatches = (millis) => {
        if (!monthKey || !millis) return false;
        const date = new Date(millis);
        if (Number.isNaN(date.getTime())) return false;
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
        return key === monthKey;
      };
      const stageMatches = (quote) => {
        if (!stageKey) return false;
        const resolvedQuoteType = quote.quoteTypeResolved || resolveQuoteType(quote);
        const stageMeta = getCrmStage(
          crmPipelines,
          resolvedQuoteType,
          quote.workStageResolved || quote.workStage
        );
        const quoteStageKey = `${resolvedQuoteType}::${stageMeta?.id || quote.workStageResolved || "quote_given"}`;
        return quoteStageKey === stageKey;
      };
      const make = ({
        title,
        subtitle = "",
        records = [],
        recordType = "quotes",
        summaryValue = null,
        metricValue = null,
        filters = [],
      }) => ({
        key: metricKey,
        title,
        subtitle,
        records,
        recordType,
        summaryValue,
        metricValue: metricValue ?? records.length,
        filters,
      });

      switch (metricKey) {
        case "activeQuotes":
          return make({
            title: "Recent Active Quotes",
            subtitle: "Recent active quotations. Use Saved Quotes search for all historical quotations.",
            records: dashboardAllQuotes,
            filters: ["Recent active quotations"],
          });
        case "quotesThisMonth":
          return make({
            title: `Quotes ${dashboardPeriodLabel}`,
            subtitle: "Quotation count and value from monthly analytics summaries.",
            records: periodQuotes,
            summaryValue: dashboardSummaryAggregate.quotationRevenue,
            metricValue: dashboardSummaryAggregate.quotationCount,
            filters: [dashboardPeriodLabel, "Created date"],
          });
        case "confirmedThisMonth":
          return make({
            title: `Confirmed ${dashboardPeriodLabel}`,
            subtitle: "Confirmed quotations from monthly analytics summaries.",
            records: periodConfirmed,
            summaryValue: dashboardSummaryAggregate.confirmedRevenue,
            metricValue: dashboardSummaryAggregate.confirmedCount,
            filters: [dashboardPeriodLabel, "Confirmed date"],
          });
        case "revenueThisMonth":
          return make({
            title: `Revenue ${dashboardPeriodLabel}`,
            subtitle: "Confirmed revenue from monthly analytics summaries.",
            records: revenueQuotes,
            summaryValue: dashboardSummaryAggregate.confirmedRevenue,
            metricValue: dashboardSummaryAggregate.confirmedCount,
            filters: [dashboardPeriodLabel, "Revenue eligible"],
          });
        case "draftQuotes":
          return make({
            title: "Draft Quotes",
            subtitle: "Active quotations that are still in draft.",
            records: draftQuotes,
            filters: ["Draft", "Not cancelled"],
          });
        case "openPipeline":
          return make({
            title: "Open Pipeline",
            subtitle: "CRM deals that are not cancelled, lost, installed, completed, or closed.",
            records: crmOpenDeals,
            recordType: "deals",
            summaryValue: crmOpenDeals.reduce(
              (sum, deal) => sum + Number(deal.expectedValue || 0),
              0
            ),
            filters: ["Open CRM deals"],
          });
        case "upcomingInstallations":
          return make({
            title: "Upcoming Installations",
            subtitle: "Scheduled installations from today onwards.",
            records: upcomingInstallationQuotes,
            recordType: "installations",
            filters: ["Installation date from today"],
          });
        case "createdQuoteConversion":
          return make({
            title: "Created Quote Conversion",
            subtitle: `${dashboardSummaryAggregate.confirmedCount} of ${dashboardSummaryAggregate.quotationCount} quotes in this period are confirmed.`,
            records: periodQuotes,
            metricValue: conversionRate,
            filters: [dashboardPeriodLabel, "Created quotes with confirmed subset"],
          });
        case "averageQuoteValue":
          return make({
            title: "Average Quote Value",
            subtitle: "Average quote value from monthly analytics summaries.",
            records: periodQuotes,
            summaryValue: dashboardSummaryAggregate.quotationRevenue,
            metricValue: avgQuoteValue,
            filters: [dashboardPeriodLabel, "Created quotes"],
          });
        case "averageConfirmedValue":
          return make({
            title: "Average Confirmed Value",
            subtitle: "Average confirmed value from monthly analytics summaries.",
            records: periodConfirmed,
            summaryValue: dashboardSummaryAggregate.confirmedRevenue,
            metricValue: avgConfirmedValue,
            filters: [dashboardPeriodLabel, "Confirmed quotes"],
          });
        case "quoteType":
          return make({
            title: `${getQuoteTypeLabel(quoteType)} Quotes`,
            subtitle: "Quotations included in this quote type split.",
            records: dashboardAllQuotes.filter(
              (quote) => quote.quoteTypeResolved === quoteType
            ),
            filters: [getQuoteTypeLabel(quoteType)],
          });
        case "stage":
          return make({
            title: context.title || "Stage Distribution",
            subtitle: "Quotations included in this stage distribution bar.",
            records: dashboardAllQuotes.filter(stageMatches),
            filters: [context.stageLabel || "Selected stage"],
          });
        case "monthCreated":
          return make({
            title: `Created Quotes in ${context.monthLabel || "Selected Month"}`,
            subtitle: "Quotations created in the selected trend month.",
            records: dashboardAllQuotes.filter((quote) => monthMatches(quote.createdMillis)),
            filters: [context.monthLabel || monthKey, "Created date"],
          });
        case "monthRevenue":
          const summaryForMonth = dashboardAnalyticsState.monthlySummaries?.[monthKey] || {};
          return make({
            title: `Revenue in ${context.monthLabel || "Selected Month"}`,
            subtitle: "Confirmed revenue from monthly analytics summaries.",
            records: dashboardAllQuotes.filter(
              (quote) =>
                quote.isConfirmed &&
                quote.isRevenueEligible &&
                monthMatches(quote.confirmedMillis)
            ),
            summaryValue: Number(summaryForMonth.confirmedRevenue || 0),
            metricValue: Number(summaryForMonth.confirmedCount || 0),
            filters: [context.monthLabel || monthKey, "Confirmed revenue"],
          });
        case "installationDate":
          return make({
            title: `Installations on ${dateKey || "Selected Date"}`,
            subtitle: "Installation records scheduled for the selected calendar date.",
            records: installationByDate[dateKey] || [],
            recordType: "installations",
            filters: [dateKey],
          });
        case "topCustomer":
          return make({
            title: context.name || "Top Customer",
            subtitle: "Quotations contributing to this customer total.",
            records: context.records || [],
            recordType: "quotes",
            summaryValue: context.value || 0,
            filters: ["Top customer"],
          });
        default:
          return make({
            title: "Dashboard Records",
            records: [],
          });
      }
    },
    [
      avgConfirmedValue,
      avgQuoteValue,
      conversionRate,
      crmOpenDeals,
      crmPipelines,
      dashboardAnalyticsState.monthlySummaries,
      dashboardAllQuotes,
      dashboardPeriodLabel,
      dashboardSummaryAggregate,
      draftQuotes,
      installationByDate,
      periodConfirmed,
      periodQuotes,
      upcomingInstallationQuotes,
    ]
  );

  const dashboardMetricData = useMemo(
    () => ({
      activeQuotes: getDashboardMetricRecords("activeQuotes"),
      quotesThisMonth: getDashboardMetricRecords("quotesThisMonth"),
      confirmedThisMonth: getDashboardMetricRecords("confirmedThisMonth"),
      revenueThisMonth: getDashboardMetricRecords("revenueThisMonth"),
      draftQuotes: getDashboardMetricRecords("draftQuotes"),
      openPipeline: getDashboardMetricRecords("openPipeline"),
      upcomingInstallations: getDashboardMetricRecords("upcomingInstallations"),
      createdQuoteConversion: getDashboardMetricRecords("createdQuoteConversion"),
      averageQuoteValue: getDashboardMetricRecords("averageQuoteValue"),
      averageConfirmedValue: getDashboardMetricRecords("averageConfirmedValue"),
    }),
    [getDashboardMetricRecords]
  );

  const openBusinessDashboardDrilldown = useCallback(
    (key, context = {}) => {
      const nextDrilldown = getDashboardMetricRecords(key, context);
      if (!nextDrilldown) return;
      setBusinessDashboardDrilldownKey("");
      setDashboardDrilldownModal({ key, context });
      setDashboardDrilldownSearch("");
    },
    [getDashboardMetricRecords]
  );

  const clearBusinessDashboardDrilldown = useCallback(() => {
    setBusinessDashboardDrilldownKey("");
  }, []);

  const dashboardKpis = [
    {
      key: "created",
      label: `Quotes ${dashboardPeriodLabel}`,
      value: dashboardAnalyticsNotReady
        ? 0
        : dashboardMetricData.quotesThisMonth.metricValue,
      accent: "#2563eb",
      note: "Created in selected period",
      clickable: true,
      onClick: () => openBusinessDashboardDrilldown("quotesThisMonth"),
    },
    {
      key: "confirmed",
      label: `Confirmed ${dashboardPeriodLabel}`,
      value: dashboardAnalyticsNotReady
        ? 0
        : dashboardMetricData.confirmedThisMonth.metricValue,
      accent: "#10b981",
      note: "Finalized in selected period",
      clickable: true,
      onClick: () => openBusinessDashboardDrilldown("confirmedThisMonth"),
    },
    {
      key: "revenue",
      label: `Revenue ${dashboardPeriodLabel}`,
      value: dashboardAnalyticsNotReady
        ? formatCurrencyDisplay(0, currency)
        : formatCurrencyDisplay(dashboardMetricData.revenueThisMonth.summaryValue || 0, currency),
      accent: "#0ea5e9",
      note: "Confirmed revenue in selected period",
      clickable: true,
      onClick: () => openBusinessDashboardDrilldown("revenueThisMonth"),
    },
    {
      key: "drafts",
      label: "Draft Quotes",
      value: dashboardMetricData.draftQuotes.metricValue,
      accent: "#f59e0b",
      note: "Active quotes still in draft",
      clickable: true,
      onClick: () => openBusinessDashboardDrilldown("draftQuotes"),
    },
    {
      key: "pipeline",
      label: "Open Pipeline",
      value: dashboardMetricData.openPipeline.metricValue,
      accent: "#6366f1",
      note: "Live jobs not yet installed",
      clickable: true,
      onClick: () => openBusinessDashboardDrilldown("openPipeline"),
    },
    {
      key: "upcoming",
      label: "Upcoming Installations",
      value: dashboardAnalyticsNotReady
        ? 0
        : dashboardMetricData.upcomingInstallations.metricValue,
      accent: "#ec4899",
      note: "Scheduled from today onwards",
      clickable: true,
      onClick: () => openBusinessDashboardDrilldown("upcomingInstallations"),
    },
  ];

  const dashboardInsights = [
    {
      key: "conversion",
      label: "Created Quote Conversion",
      value: dashboardAnalyticsNotReady
        ? "0%"
        : `${dashboardMetricData.createdQuoteConversion.metricValue}%`,
      description: "Quotes created in this period that are now confirmed",
      onClick: () => openBusinessDashboardDrilldown("createdQuoteConversion"),
    },
    {
      key: "averageQuote",
      label: "Average Quote Value",
      value: dashboardAnalyticsNotReady
        ? formatCurrencyDisplay(0, currency)
        : formatCurrencyDisplay(dashboardMetricData.averageQuoteValue.metricValue || 0, currency),
      description: "Across created quotes in selected period",
      onClick: () => openBusinessDashboardDrilldown("averageQuoteValue"),
    },
    {
      key: "averageConfirmed",
      label: "Average Confirmed Value",
      value: dashboardAnalyticsNotReady
        ? formatCurrencyDisplay(0, currency)
        : formatCurrencyDisplay(dashboardMetricData.averageConfirmedValue.metricValue || 0, currency),
      description: "Across confirmed quotes in selected period",
      onClick: () => openBusinessDashboardDrilldown("averageConfirmedValue"),
    },
  ];

  const dashboardTooltipStyle = {
    backgroundColor: isDark ? "#0f172a" : "#ffffff",
    border: dashboardBorder,
    borderRadius: 12,
    color: isDark ? "#e2e8f0" : "#0f172a",
    maxWidth: isMobile ? 280 : 360,
    whiteSpace: "normal",
    overflowWrap: "normal",
    wordBreak: "normal",
  };
  const chartShellStyle = {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    height: 220,
    overflow: "hidden",
    position: "relative",
    boxSizing: "border-box",
  };
  const getChartShellStyle = (height) => ({
    ...chartShellStyle,
    height,
  });
  const renderChartEmptyState = (
    title = "No data yet",
    subtitle = "Data will appear as quotations and CRM activity are added."
  ) => (
    <div
      style={{
        height: "100%",
        minHeight: 140,
        display: "grid",
        placeItems: "center",
        borderRadius: 18,
        background: dashboardSoftBg,
        border: dashboardBorder,
        color: dashboardMuted,
        fontSize: 13,
        fontWeight: 700,
        textAlign: "center",
        padding: 18,
      }}
    >
      <div>
        <div style={{ fontSize: 15, fontWeight: 900, color: textColor }}>
          {title}
        </div>
        <div
          style={{
            marginTop: 6,
            fontSize: 12,
            lineHeight: 1.5,
            color: dashboardMuted,
            fontWeight: 650,
          }}
        >
          {subtitle}
        </div>
      </div>
    </div>
  );
  const chartAxisTickStyle = {
    fontSize: isMobile ? 9.5 : 11,
    fontWeight: 700,
  };
  const chartMargin = isMobile
    ? { top: 10, right: 8, left: -18, bottom: 10 }
    : { top: 10, right: 12, left: -10, bottom: 8 };
  const quoteTrendChartHeight = isMobile ? 190 : isCompact ? 205 : 220;
  const revenueChartHeight = isMobile ? 188 : isCompact ? 202 : 214;
  const buildChartData = (rows = [], numericKeys = []) =>
    (Array.isArray(rows) ? rows : []).map((entry) => {
      const next = { ...entry };
      numericKeys.forEach((key) => {
        next[key] = Number.isFinite(Number(entry?.[key]))
          ? Number(entry[key])
          : 0;
      });
      return next;
    });
  const monthlyChartData = useMemo(
    () => buildChartData(monthlyTimeSeries, ["created", "confirmed", "revenue"]),
    [monthlyTimeSeries]
  );
  const stageDistributionChartData = useMemo(
    () => buildChartData(stageDistribution, ["value"]),
    [stageDistribution]
  );
  const crmStageOverviewChartData = useMemo(
    () => buildChartData(crmStageOverview, ["count", "value"]),
    [crmStageOverview]
  );
  const quoteTypeDistributionChartData = useMemo(
    () => buildChartData(quoteTypeDistribution, ["value"]),
    [quoteTypeDistribution]
  );
  const crmChecklistChartData = useMemo(
    () => buildChartData(crmChecklistTrend, ["value"]),
    [crmChecklistTrend]
  );
  const crmPipelineChecklistChartData = useMemo(
    () => buildChartData(crmPipelineChecklistTrend, ["value"]),
    [crmPipelineChecklistTrend]
  );
  const hasQuoteCreationTrendData = monthlyTimeSeries.some(
    (entry) => Number(entry.created || 0) > 0 || Number(entry.confirmed || 0) > 0
  );
  const hasRevenueTrendData = monthlyTimeSeries.some(
    (entry) => Number(entry.revenue || 0) > 0
  );
  const hasQuoteTypeDistributionData = quoteTypeDistribution.some(
    (entry) => Number(entry.value || 0) > 0
  );
  const hasStageDistributionData = stageDistribution.some(
    (entry) => Number(entry.value || 0) > 0
  );
  const hasCrmStageOverviewData = crmStageOverview.some(
    (entry) => Number(entry.count || 0) > 0
  );

  useEffect(() => {
    if (!IS_DEV_UI_ENABLED) return;

    const findLinkedCustomerForDeal = (deal = {}) =>
      customerRecords.find(
        (customer) =>
          customer.id &&
          String(customer.id) === String(deal.customerId || deal.crm?.customerId || "")
      ) || null;

    const firstSearchBlobs = crmDealListBaseDeals.slice(0, 3).map((deal) => ({
      id: deal.id || "",
      customer:
        deal.customerLabel ||
        deal.customerName ||
        deal.crmContact?.name ||
        deal.crmTitle ||
        "",
      blob: buildDealSearchBlob(deal, findLinkedCustomerForDeal(deal)),
    }));

    console.groupCollapsed("[FabriQuote diagnostics] Deal List search");
    console.log("total crm_leads loaded:", debugRawCrmLeadCount);
    console.log("total quotations loaded:", debugRawQuotationCount);
    console.log("total normalized deal records:", crmDeals.length);
    console.log("deal list base records:", crmDealListBaseDeals.length);
    console.log("search term:", deferredCrmSearch);
    console.log("rendered count:", visibleCrmDeals.length);
    console.log("final filtered result count:", crmFilteredDeals.length);
    console.log("first 3 normalized search blobs:", firstSearchBlobs);
    console.groupEnd();

    console.groupCollapsed("[FabriQuote diagnostics] Dashboard charts");
    console.log("raw quotations count:", debugRawQuotationCount);
    console.log("normalized quotations count:", dashboardAllQuotes.length);
    console.log("quote creation trend data array:", monthlyChartData);
    console.log("quote type split data array:", quoteTypeDistributionChartData);
    console.log("stage distribution data array:", stageDistributionChartData);
    console.log(
      "revenue trend data array:",
      monthlyChartData.map((entry) => ({
        month: entry.month,
        revenue: Number(entry.revenue || 0),
      }))
    );
    console.groupEnd();

    console.groupCollapsed("[FabriQuote diagnostics] CRM charts");
    console.log("raw crm_leads count:", debugRawCrmLeadCount);
    console.log("normalized leads count:", crmLeadDeals.length);
    console.log("pipeline stage overview data array:", crmStageOverviewChartData);
    console.log("checklist completion data array:", crmChecklistChartData);
    console.groupEnd();
  }, [
    crmChecklistChartData,
    crmDealListBaseDeals,
    crmDeals.length,
    crmFilteredDeals.length,
    crmLeadDeals.length,
    crmStageOverviewChartData,
    customerRecords,
    dashboardAllQuotes.length,
    debugRawCrmLeadCount,
    debugRawQuotationCount,
    deferredCrmSearch,
    monthlyChartData,
    quoteTypeDistributionChartData,
    stageDistributionChartData,
    visibleCrmDeals.length,
  ]);

  const renderInlineLegend = (items = []) => (
    <div
      style={{
        display: "flex",
        gap: 12,
        flexWrap: "wrap",
        alignItems: "center",
        marginTop: 10,
        fontSize: 12,
        color: dashboardMuted,
        fontWeight: 750,
      }}
    >
      {items.map((item) => (
        <span
          key={item.label}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            whiteSpace: "nowrap",
          }}
        >
          <span
            style={{
              width: 9,
              height: 9,
              borderRadius: 999,
              background: item.color,
              flex: "0 0 auto",
            }}
          />
          {item.label}
        </span>
      ))}
    </div>
  );
  const renderHorizontalMetricBars = (
    items = [],
    {
      valueKey = "value",
      labelKey = "stageLabel",
      valueLabel = "records",
      emptyTitle = "No data yet",
      maxItems = 10,
      onItemClick = null,
    } = {}
  ) => {
    const validItems = (Array.isArray(items) ? items : [])
      .filter((entry) => Number(entry?.[valueKey] || 0) > 0)
      .slice(0, maxItems);
    const maxValue = Math.max(
      1,
      ...validItems.map((entry) => Number(entry?.[valueKey] || 0))
    );

    if (!validItems.length) {
      return renderChartEmptyState(emptyTitle);
    }

    return (
      <div style={{ display: "grid", gap: 12 }}>
        {validItems.map((entry) => {
          const value = Number(entry?.[valueKey] || 0);
          const widthPercent = Math.max(7, Math.round((value / maxValue) * 100));
          const label =
            entry?.[labelKey] ||
            entry?.label ||
            entry?.fullLabel ||
            entry?.name ||
            "Unlabelled";
          const meta = entry?.quoteType ? getQuoteTypeLabel(entry.quoteType) : "";

          const RowTag = onItemClick ? "button" : "div";

          return (
            <RowTag
              key={entry.id || entry.key || label}
              type={onItemClick ? "button" : undefined}
              onClick={onItemClick ? () => onItemClick(entry) : undefined}
              style={{
                display: "grid",
                gap: 6,
                minWidth: 0,
                border: "none",
                padding: 0,
                background: "transparent",
                color: textColor,
                textAlign: "left",
                cursor: onItemClick ? "pointer" : "default",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  gap: 12,
                  minWidth: 0,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 900,
                      color: textColor,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={entry.fullLabel || label}
                  >
                    {label}
                  </div>
                  {meta ? (
                    <div
                      style={{
                        marginTop: 2,
                        fontSize: 10.5,
                        color: dashboardMuted,
                        fontWeight: 750,
                      }}
                    >
                      {meta}
                    </div>
                  ) : null}
                </div>
                <div
                  style={{
                    flex: "0 0 auto",
                    fontSize: 12,
                    fontWeight: 900,
                    color: textColor,
                    whiteSpace: "nowrap",
                  }}
                >
                  {value} {valueLabel}
                </div>
              </div>
              <div
                style={{
                  height: 10,
                  borderRadius: 999,
                  background: dashboardSoftBg,
                  border: dashboardBorder,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${widthPercent}%`,
                    height: "100%",
                    borderRadius: 999,
                    background: entry.color || "#2563eb",
                  }}
                />
              </div>
            </RowTag>
          );
        })}
      </div>
    );
  };

  const renderQuoteTypeSplit = (items = [], onItemClick = null) => {
    const validItems = (Array.isArray(items) ? items : []).filter(
      (entry) => Number(entry?.value || 0) > 0
    );
    const total = validItems.reduce(
      (sum, entry) => sum + Number(entry.value || 0),
      0
    );

    if (!total) {
      return renderChartEmptyState("No quote type data yet");
    }

    return (
      <div style={{ display: "grid", gap: 14 }}>
        <div
          style={{
            height: 18,
            borderRadius: 999,
            background: dashboardSoftBg,
            border: dashboardBorder,
            overflow: "hidden",
            display: "flex",
          }}
        >
          {validItems.map((entry) => {
            const percent = (Number(entry.value || 0) / total) * 100;
            return (
              <div
                key={entry.name}
                title={`${entry.name}: ${entry.value}`}
                style={{
                  width: `${Math.max(4, percent)}%`,
                  background: entry.color || "#2563eb",
                }}
              />
            );
          })}
        </div>
        <div style={{ display: "grid", gap: 10 }}>
          {validItems.map((entry) => {
            const percent = Math.round((Number(entry.value || 0) / total) * 100);
            const RowTag = onItemClick ? "button" : "div";
            return (
              <RowTag
                key={entry.name}
                type={onItemClick ? "button" : undefined}
                onClick={onItemClick ? () => onItemClick(entry) : undefined}
                style={{
                  padding: 12,
                  borderRadius: 15,
                  background: dashboardSoftBg,
                  border: dashboardBorder,
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  alignItems: "center",
                  minWidth: 0,
                  color: textColor,
                  textAlign: "left",
                  cursor: onItemClick ? "pointer" : "default",
                }}
              >
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    minWidth: 0,
                  }}
                >
                  <span
                    style={{
                      width: 9,
                      height: 9,
                      borderRadius: 999,
                      background: entry.color || "#2563eb",
                      flex: "0 0 auto",
                    }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 900,
                        color: textColor,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {entry.name}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: dashboardMuted,
                        marginTop: 2,
                        fontWeight: 750,
                      }}
                    >
                      {percent}% of quotes
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    flex: "0 0 auto",
                    fontSize: 12,
                    fontWeight: 900,
                    color: textColor,
                    textAlign: "right",
                  }}
                >
                  {entry.value} quote{entry.value === 1 ? "" : "s"}
                </div>
              </RowTag>
            );
          })}
        </div>
      </div>
    );
  };

  const renderChecklistProgress = (
    items = [],
    summary = {},
    emptyTitle = "No checklist data yet"
  ) => {
    const completedFromItems = Number(
      (Array.isArray(items) ? items : []).find((entry) => entry.name === "Complete")
        ?.value || 0
    );
    const totalFromItems = (Array.isArray(items) ? items : []).reduce(
      (sum, entry) => sum + Number(entry?.value || 0),
      0
    );
    const completed = Number(summary?.checklistCompleted ?? completedFromItems);
    const total = Number(summary?.checklistTotal ?? totalFromItems);
    const percent = total ? Math.round((completed / total) * 100) : 0;

    if (!total) {
      return renderChartEmptyState(emptyTitle);
    }

    return (
      <div
        style={{
          display: "grid",
          gap: 14,
          padding: 14,
          borderRadius: 18,
          background: dashboardSoftBg,
          border: dashboardBorder,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "end",
            gap: 14,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 34,
                lineHeight: 1,
                letterSpacing: "-0.05em",
                fontWeight: 950,
                color: textColor,
              }}
            >
              {percent}%
            </div>
            <div
              style={{
                marginTop: 6,
                fontSize: 12,
                color: dashboardMuted,
                fontWeight: 750,
              }}
            >
              Checklist completion
            </div>
          </div>
          <div
            style={{
              textAlign: "right",
              fontSize: 12,
              color: dashboardMuted,
              fontWeight: 850,
            }}
          >
            <div style={{ color: textColor }}>{completed} complete</div>
            <div>{Math.max(0, total - completed)} remaining</div>
          </div>
        </div>
        <div
          style={{
            height: 12,
            borderRadius: 999,
            background: isDark ? "rgba(148,163,184,0.18)" : "#e2e8f0",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${Math.max(3, percent)}%`,
              height: "100%",
              borderRadius: 999,
              background: "linear-gradient(90deg, #10b981, #22c55e)",
            }}
          />
        </div>
        <div style={{ fontSize: 12, color: dashboardMuted, fontWeight: 750 }}>
          {completed} / {total} checklist tasks complete.
        </div>
      </div>
    );
  };

  const crmSummaryCards = [
    {
      key: "active",
      label: "Active Deals",
      value: crmSummary.activeDeals,
      note: "Open opportunities across CRM pipelines",
      accent: "#2563eb",
    },
    {
      key: "new",
      label: `New Deals ${dashboardPeriodLabel}`,
      value: crmSummary.newDeals,
      note: "Created in the selected period",
      accent: "#0ea5e9",
    },
    {
      key: "pipelineValue",
      label: "Pipeline Value",
      value: formatCurrencyDisplay(crmSummary.pipelineValue, currency),
      note: "Expected value of all active deals",
      accent: "#6366f1",
    },
    {
      key: "overdue",
      label: "Overdue Follow-ups",
      value: crmSummary.overdueFollowUps,
      note: "Deals with a missed next action",
      accent: "#f97316",
    },
    {
      key: "stalled",
      label: "Stalled Deals",
      value: crmSummary.stalledDeals,
      note: "No meaningful update in the last 10 days",
      accent: "#ef4444",
    },
    {
      key: "installations",
      label: "Upcoming Installations",
      value: upcomingInstallationQuotes.length,
      note: "Scheduled jobs coming up next",
      accent: "#10b981",
    },
  ];

  const crmNavItems = [
    { key: "overview", label: "CRM Overview", icon: LayoutGrid },
    { key: "board", label: "Pipeline Board", icon: Columns3 },
    { key: "leads", label: "Leads", icon: Users },
    { key: "list", label: "Deal List", icon: Users },
    { key: "history", label: "Task History", icon: CheckCircle2 },
    { key: "settings", label: "Pipeline Settings", icon: Settings2 },
  ];

  const openCrmDrilldown = useCallback((key) => {
    if (!crmOverviewDrilldowns[key]) return;
    setCrmDrilldownKey(key);
    setCrmSearch("");
    setCrmStageFilter("all");
    setCrmTypeFilter("all");
    setCrmSourceFilter("all");
    setCrmSortBy("newest");
    setCrmView("list");
  }, [crmOverviewDrilldowns]);

  const clearCrmDrilldown = useCallback(() => {
    setCrmDrilldownKey("");
  }, []);

  const buildExecutionSpecsFromQuote = useCallback((quoteLike) => {
    if (!quoteLike) return [];

    const quoteType = resolveQuoteType(quoteLike);

    if (quoteType === GENERAL_QUOTE_TYPE) {
      const generalQuote = normalizeGeneralQuote(getEmbeddedGeneralQuote(quoteLike));

      return (generalQuote.lineItems || []).map((item, index) =>
        {
          const itemSpecifications = normalizeGeneralQuoteItemSpecifications(item);
          const specificationAttributes = itemSpecifications.reduce(
            (accumulator, specification) => {
              const nextKey = String(specification?.key ?? "").trim();
              if (!nextKey) return accumulator;
              accumulator[nextKey] = specification?.value ?? "";
              return accumulator;
            },
            {}
          );

          return createExecutionSpecItem({
            itemType:
              item.specItemType ||
              item.category ||
              item.specTemplateName ||
              "General Item",
            itemName:
              item.itemName ||
              item.name ||
              `Item ${index + 1}`,
            attributes: pickExecutionSpecAttributes({
              ...(item.specTemplateName
                ? { Template: item.specTemplateName }
                : {}),
              ...(Object.keys(specificationAttributes).length > 0
                ? specificationAttributes
                : {
                    Category: item.category,
                    Description: item.description,
                    Quantity: item.quantity,
                    Unit: item.unit,
                  }),
              ...(item.description ? { Description: item.description } : {}),
            }),
            attachments: normalizeExecutionSpecAttachments(
              normalizeGeneralQuoteItemImages(item).map((image) => ({
                name: image.filename || item.itemName || item.name || "Reference Image",
                url: getGeneralQuoteImageDisplayUrl(image),
                path: getGeneralQuoteImageStoragePath(image),
                uploadedAt: image.uploadedAt || null,
                uploadedBy: "",
              }))
            ),
          });
        }
      );
    }

    return (Array.isArray(quoteLike.rows) ? quoteLike.rows : []).map((row, index) =>
      createExecutionSpecItem({
        itemType: row.productType || "Curtains",
        itemName: row.item || `Item ${index + 1}`,
        attributes: pickExecutionSpecAttributes({
          "Product Type": row.productType,
          Fabric: row.fabric,
          "Fabric Width (in)": row.fabricWidthIn,
          Width: row.width,
          Height: row.height,
          "MRP / m": row.mrp,
          "Discount %": row.discount,
          "Rate / sqft": row.sqftRate,
          "Stitching Style": row.stitchingStyle,
          "Custom Stitch Rate": row.customStitchRate,
        }),
      })
    );
  }, [pickExecutionSpecAttributes]);

  const getGeneralQuotePdfStateSignature = useCallback(() => {
    const currentQuote = generalQuoteRef.current || generalQuote || {};
    return JSON.stringify({
      quoteId: generalEditingQuoteId || "",
      quoteNo: currentQuote.quoteNo || quoteNo || "",
      customerName:
        currentQuote.quoteFor?.name ||
        currentQuote.customerName ||
        "",
      itemCount: Array.isArray(currentQuote.lineItems)
        ? currentQuote.lineItems.length
        : 0,
      grandTotal: Number(generalQuoteTotals?.grandTotal || 0),
    });
  }, [generalEditingQuoteId, generalQuote, generalQuoteTotals?.grandTotal, quoteNo]);

  const exportGeneralExecutionPDF = async () => {
    if (!canUseWorkshopPdf) {
      notify("You do not have permission to export workshop PDFs.", "warning");
      return;
    }

    const beforeSignature = getGeneralQuotePdfStateSignature();
    const currentQuote = generalQuoteRef.current || generalQuote || {};
    const clonedQuote = JSON.parse(JSON.stringify(currentQuote || {}));
    const normalizedQuote = normalizeGeneralQuote(
      {
        ...clonedQuote,
        quoteNo: clonedQuote.quoteNo || quoteNo,
        quoteDate: clonedQuote.quoteDate || date,
      },
      {
        businessName,
        businessPhone,
        businessEmail,
        shopAddress,
        currency: clonedQuote.currency || currency,
      }
    );
    const quoteId = generalEditingQuoteId || selectedQuote?.id || "";
    const customerNameValue =
      normalizedQuote.quoteFor?.name ||
      normalizedQuote.customerName ||
      "Unnamed Customer";
    const quoteLike = {
      id: quoteId,
      quoteType: GENERAL_QUOTE_TYPE,
      quoteNo: normalizedQuote.quoteNo || quoteNo || "-",
      customerName: customerNameValue,
      customerPhone:
        normalizedQuote.quoteFor?.phone ||
        normalizedQuote.customerPhone ||
        "",
      customerWhatsapp:
        normalizedQuote.quoteFor?.whatsapp ||
        normalizedQuote.customerWhatsapp ||
        "",
      generalQuote: normalizedQuote,
      executionSpecs: [],
    };
    const executionItems = buildExecutionSpecsFromQuote(quoteLike);

    if (executionItems.length === 0) {
      notify("Add at least one item before downloading the execution PDF.", "warning");
      return;
    }

    await downloadExecutionWorkshopPdf(null, {
      sourceDeal: {
        id: quoteId || normalizedQuote.quoteNo || "general-quote-draft",
        quoteNo: normalizedQuote.quoteNo || quoteNo || "-",
        customerName: customerNameValue,
        customerLabel: customerNameValue,
        customerPhone: quoteLike.customerPhone || quoteLike.customerWhatsapp || "",
        crmTitle: normalizedQuote.title || normalizedQuote.projectName || "General Quote",
        status: "quotation",
        executionSpecs: executionItems,
        linkedQuotes: [
          {
            quoteId,
            quoteNo: normalizedQuote.quoteNo || quoteNo || "-",
            type: GENERAL_QUOTE_TYPE,
          },
        ],
      },
      sourceDraft: {
        quoteNo: normalizedQuote.quoteNo || quoteNo || "-",
        contact: {
          name: customerNameValue,
          phone: quoteLike.customerPhone || quoteLike.customerWhatsapp || "",
          whatsapp: quoteLike.customerWhatsapp || "",
          email: normalizedQuote.quoteFor?.email || normalizedQuote.customerEmail || "",
          address: normalizedQuote.quoteFor?.address || normalizedQuote.customerAddress || "",
        },
        executionSpecs: executionItems,
        notes: normalizedQuote.notes || "",
      },
      executionItems,
      source: "general_quote_screen",
    });

    const afterSignature = getGeneralQuotePdfStateSignature();
    if (beforeSignature !== afterSignature) {
      console.error("PDF_GENERATION_MUTATED_QUOTE_STATE", {
        beforeSignature,
        afterSignature,
        quoteId,
        quoteNo: normalizedQuote.quoteNo || quoteNo || "",
      });
    }
  };

  const addCrmExecutionSpec = useCallback(() => {
    const nextItem = buildExecutionSpecDraftItem(createExecutionSpecItem());
    setSelectedExecutionItemId(nextItem.id);
    updateCrmDetailDraft((prev) =>
      prev
        ? {
            ...prev,
            executionSpecs: [...(prev.executionSpecs || []), nextItem],
          }
        : prev
    );
  }, [buildExecutionSpecDraftItem, updateCrmDetailDraft]);

  const updateCrmExecutionSpec = useCallback((itemId, updater) => {
    updateCrmDetailDraft((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        executionSpecs: (prev.executionSpecs || []).map((item) => {
          if (item.id !== itemId) return item;
          return typeof updater === "function" ? updater(item) : { ...item, ...updater };
        }),
      };
    });
  }, [updateCrmDetailDraft]);

  const deleteCrmExecutionSpec = useCallback((itemId) => {
    updateCrmDetailDraft((prev) => {
      if (!prev) return prev;

      const nextItems = (prev.executionSpecs || []).filter((item) => item.id !== itemId);
      if (selectedExecutionItemId === itemId) {
        setSelectedExecutionItemId(nextItems[0]?.id || "");
      }

      return {
        ...prev,
        executionSpecs: nextItems,
      };
    });
  }, [selectedExecutionItemId, updateCrmDetailDraft]);

  const syncCrmExecutionSpecType = useCallback(
    (itemId, nextType) => {
      const normalizedType = formatExecutionItemType(nextType);
      const matchingTemplate = getExecutionTemplateForType(normalizedType);

      updateCrmExecutionSpec(itemId, (item) => {
        const nextItem = {
          ...item,
          itemType: normalizedType,
        };

        return matchingTemplate
          ? mergeExecutionTemplateFieldsIntoItem(nextItem, matchingTemplate.fields)
          : nextItem;
      });
    },
      [
        formatExecutionItemType,
        getExecutionTemplateForType,
        mergeExecutionTemplateFieldsIntoItem,
        updateCrmExecutionSpec,
      ]
    );

  const addCrmExecutionSpecField = useCallback((itemId) => {
    updateCrmExecutionSpec(itemId, (item) => ({
      ...item,
      attributeRows: [
        ...(Array.isArray(item.attributeRows) ? item.attributeRows : []),
        {
          id: createClientId("exec-field"),
          key: "New Field",
          value: "",
          type: "text",
        },
      ],
    }));
  }, [updateCrmExecutionSpec]);

  const updateCrmExecutionSpecField = useCallback((itemId, fieldId, patch) => {
    updateCrmExecutionSpec(itemId, (item) => ({
      ...item,
      attributeRows: (Array.isArray(item.attributeRows) ? item.attributeRows : []).map((field) =>
        field.id === fieldId
          ? {
              ...field,
              ...patch,
            }
          : field
      ),
    }));
  }, [updateCrmExecutionSpec]);

  const removeCrmExecutionSpecField = useCallback((itemId, fieldId) => {
    updateCrmExecutionSpec(itemId, (item) => ({
      ...item,
      attributeRows: (Array.isArray(item.attributeRows) ? item.attributeRows : []).filter(
        (field) => field.id !== fieldId
      ),
    }));
  }, [updateCrmExecutionSpec]);

  const applyExecutionTemplateToSpec = useCallback(
    (itemId, templateId) => {
      const template = getExecutionTemplateById(templateId);
      if (!template) return;

      updateCrmExecutionSpec(itemId, (item) =>
        mergeExecutionTemplateFieldsIntoItem(
          {
            ...item,
            itemType: template.itemType || template.name,
          },
          template.fields
        )
      );
      setSelectedExecutionTemplateId(template.id);
    },
    [getExecutionTemplateById, mergeExecutionTemplateFieldsIntoItem, updateCrmExecutionSpec]
  );

  const convertDealQuoteToExecutionSpecs = useCallback(async (deal) => {
    if (!deal?.hasQuote) {
      notify("This lead does not have a linked quotation yet.", "warning");
      return;
    }

    const primaryLinkedQuote = getPrimaryCrmLinkedQuote(deal.linkedQuotes || []);
    const sourceQuote =
      savedQuotes.find((quote) => quote.id === (primaryLinkedQuote?.quoteId || deal.id)) ||
      savedQuotes.find((quote) => String(quote.quoteNo || "") === String(primaryLinkedQuote?.quoteNo || "")) ||
      null;

    if (!sourceQuote) {
      notify("The linked quotation could not be found.", "error");
      return;
    }

    const generatedSpecs = buildExecutionSpecsFromQuote(sourceQuote);
    if (generatedSpecs.length === 0) {
      notify("No quotation items were available to convert.", "warning");
      return;
    }

    const canonicalDeal =
      deal?.crmRecordType === "lead"
        ? deal
        : await ensureCrmLeadForExecutionSpecs(deal);
    const targetDealId = canonicalDeal?.id || deal.id;
    const existingSpecs = serializeExecutionSpecDraftItems(
      crmDetailDraft?.executionSpecs ||
        canonicalDeal?.executionSpecs ||
        deal.executionSpecs ||
        []
    );
    const existingKeys = new Set(
      existingSpecs.map((item) =>
        `${String(item.itemType || "").trim().toLowerCase()}::${String(item.itemName || "").trim().toLowerCase()}`
      )
    );
    const mergedSpecs = stampExecutionSpecs([
      ...existingSpecs,
      ...generatedSpecs.filter((item) => {
        const key = `${String(item.itemType || "").trim().toLowerCase()}::${String(
          item.itemName || ""
        ).trim().toLowerCase()}`;
        return !existingKeys.has(key);
      }),
    ]);

    updateCrmDetailDraft((prev) =>
      prev
        ? {
            ...prev,
            executionSpecs: hydrateExecutionSpecDraftItems(mergedSpecs),
          }
        : prev
    );

    await saveCrmFieldsForDeal(
      targetDealId,
      {
        executionSpecs: mergedSpecs,
      },
      {
        forceLead: canonicalDeal?.crmRecordType === "lead" || targetDealId !== deal.id,
        sourceLead:
          canonicalDeal?.crmRecordType === "lead" || targetDealId !== deal.id
            ? { ...canonicalDeal, id: targetDealId, crmRecordType: "lead" }
            : null,
        createIfMissing: canonicalDeal?.crmRecordType === "lead" || targetDealId !== deal.id,
      }
    );

    if (
      workspaceUid &&
      sourceQuote?.id &&
      resolveQuoteType(sourceQuote) === GENERAL_QUOTE_TYPE
    ) {
      const lockedGeneralQuote = normalizeGeneralQuote(
        getEmbeddedGeneralQuote(sourceQuote),
        {
          currency,
          businessName,
          shopAddress,
          businessEmail,
        }
      );

      const lockedLineItems = (lockedGeneralQuote.lineItems || []).map((item) => ({
        ...item,
        isExecutionLocked:
          item.isExecutionLocked === true ||
          normalizeGeneralQuoteItemSpecifications(item).length > 0 ||
          Boolean(item.specTemplateId || item.specTemplateName),
      }));

      const nextLockedQuote = {
        ...lockedGeneralQuote,
        lineItems: lockedLineItems,
      };

      await updateDoc(doc(db, "users", workspaceUid, "quotations", sourceQuote.id), {
        generalQuote: nextLockedQuote,
        updatedAt: serverTimestamp(),
      });

      applyQuoteLocalPatch(sourceQuote.id, {
        generalQuote: nextLockedQuote,
        updatedAt: new Date(),
      });

      clearLocalDraftRecord(
        buildLocalDraftKey(
          workspaceUid,
          LOCAL_DRAFT_MODULES.GENERAL_QUOTE,
          sourceQuote.id
        )
      );

      if (generalEditingQuoteId === sourceQuote.id) {
        setGeneralQuote(nextLockedQuote);
        markGeneralQuoteBaseline(nextLockedQuote, "saved");
      }
    }
  }, [
    applyQuoteLocalPatch,
    businessEmail,
    businessName,
    buildExecutionSpecsFromQuote,
    currency,
    crmDetailDraft,
    ensureCrmLeadForExecutionSpecs,
    generalEditingQuoteId,
    hydrateExecutionSpecDraftItems,
    markGeneralQuoteBaseline,
    notify,
    saveCrmFieldsForDeal,
    savedQuotes,
    serializeExecutionSpecDraftItems,
    shopAddress,
    stampExecutionSpecs,
    updateCrmDetailDraft,
    workspaceUid,
  ]);

  const sanitizeChecklistItems = useCallback(
    (items = []) =>
      preserveChecklistDraftTree(items, {
        templateMode: false,
        idPrefix: "crm-check",
      }),
    []
  );

  const updateCrmDetailChecklist = useCallback(
    (updater) => {
      markCrmDetailDraftDirty();
      setCrmDetailDraft((prev) => {
        if (!prev) return prev;
        const nextChecklistItems = sanitizeChecklistItems(
          updater(prev.checklistItems || [])
        );
        return {
          ...prev,
          checklistItems: nextChecklistItems,
          checklist: nextChecklistItems,
          stageChecklist: nextChecklistItems,
        };
      });
    },
    [markCrmDetailDraftDirty, sanitizeChecklistItems]
  );

  const addCrmChecklistItem = useCallback(
    (parentId = "") => {
      const actor = getCurrentTaskHistoryActor();
      const createdAt = new Date();
      const nextItem = parentId
        ? createChecklistChildDraft("", {
            createdAt,
            createdBy: actor,
            done: false,
          })
        : createDealChecklistItem("", {
            createdAt,
            createdBy: actor,
            done: false,
          });
      pendingChecklistFocusIdRef.current = nextItem.id;

      updateCrmDetailChecklist((items) => {
        if (!parentId) {
          return [...(items || []), nextItem];
        }

        return insertChecklistChildNode(
          items || [],
          parentId,
          nextItem
        );
      });
    },
    [getCurrentTaskHistoryActor, updateCrmDetailChecklist]
  );

  const updateCrmChecklistItemField = useCallback(
    (itemId, patch) => {
      updateCrmDetailChecklist((items) =>
        updateChecklistNodeById(items || [], itemId, (item) => {
          const nextPatch = {
            ...patch,
            updatedAt: new Date(),
          };

          if (Object.prototype.hasOwnProperty.call(patch, "label")) {
            nextPatch.title = patch.label;
          }

          return {
            ...item,
            ...nextPatch,
          };
        })
      );
    },
    [updateCrmDetailChecklist]
  );

  const toggleCrmChecklistItem = useCallback(
    (itemId, checked) => {
      const actor = getCurrentTaskHistoryActor();
      updateCrmDetailChecklist((items) => {
        const beforeProgress = getChecklistProgress(items || []);
        const nextItems = toggleChecklistNodeById(items || [], itemId, checked, {
          completedAt: new Date(),
          completedBy: actor,
          updatedAt: new Date(),
        });

        if (isDevRuntime) {
          const toggledItem = flattenChecklistNodes(nextItems).find(
            (entry) => String(entry.id || "") === String(itemId || "")
          );
          const progress = getChecklistProgress(nextItems);
          console.log("CHECKLIST CLICK TRACE", {
            selectedCrmDealId,
            itemId,
            label: toggledItem?.label || toggledItem?.title || "",
            checkedFromEvent: checked,
            beforeDraftCompletedCount: beforeProgress.completed,
            afterDraftCompletedCount: progress.completed,
            afterDraftChecklistItems: nextItems,
          });
          console.log("CHECKLIST UI TOGGLE", {
            selectedCrmDealId,
            itemId,
            label: toggledItem?.label || toggledItem?.title || "",
            checked,
            draftChecklist: nextItems,
            draftChecklistItems: nextItems,
            draftStageChecklist: nextItems,
          });
          console.log("CRM checklist toggle", {
            selectedCrmDealId,
            itemId,
            label: toggledItem?.label || toggledItem?.title || "",
            checked,
            draftCompletedCount: progress.completed,
            draftTotalCount: progress.total,
          });
        }

        return nextItems;
      });
    },
    [getCurrentTaskHistoryActor, selectedCrmDealId, updateCrmDetailChecklist]
  );

  const moveCrmChecklistItem = useCallback(
    (itemId, direction) => {
      updateCrmDetailChecklist((items) =>
        moveChecklistNodeById(items || [], itemId, direction)
      );
    },
    [updateCrmDetailChecklist]
  );

  const deleteCrmChecklistItem = useCallback(
    (itemId) => {
      updateCrmDetailChecklist((items) =>
        deleteChecklistNodeById(items || [], itemId)
      );
    },
    [updateCrmDetailChecklist]
  );

  const updateCrmDealAssignment = useCallback(
    async (quoteId, memberId) => {
      const member = getTeamMemberById(memberId);
      const assignmentPatch = memberId
        ? {
            assignedToUserId: memberId,
            assignedToNameSnapshot: member?.name || "",
            assignedTo: member?.name || "",
            assignedAt: new Date(),
            assignedBy: user?.email || user?.uid || "",
          }
        : {
            assignedToUserId: "",
            assignedToNameSnapshot: "",
            assignedTo: "",
            assignedAt: null,
          assignedBy: "",
          };

      try {
        setCrmDetailDraft((prev) =>
          prev
            ? {
                ...prev,
                ...assignmentPatch,
              }
            : prev
        );

        await saveCrmFieldsForDeal(quoteId, assignmentPatch);
        setCrmDetailsSavedAt(new Date());
        notify(memberId ? "Assigned team member saved" : "Assignment cleared", "success");
      } catch (error) {
        console.error("Failed to save CRM assignment:", error);
        notify("Could not save the assignment. Please try again.", "error");
      }
    },
    [getTeamMemberById, notify, saveCrmFieldsForDeal, user]
  );

  const updateCrmChecklistAssignee = useCallback(
    async (quoteId, itemId, memberId) => {
      const member = getTeamMemberById(memberId);
      let nextChecklistItems = [];
      markCrmDetailDraftDirty();

      setCrmDetailDraft((prev) => {
        if (!prev) return prev;

        nextChecklistItems = sanitizeChecklistItems(
          updateChecklistNodeById(prev.checklistItems || [], itemId, (current) => ({
            ...current,
            assignedToUserId: memberId,
            assignedToNameSnapshot: memberId ? member?.name || "" : "",
            assignedAt: memberId ? new Date() : null,
            updatedAt: new Date(),
          }))
        );

        return {
          ...prev,
          checklistItems: nextChecklistItems,
          checklist: nextChecklistItems,
          stageChecklist: nextChecklistItems,
        };
      });

      if (nextChecklistItems.length > 0) {
        const targetDeal =
          selectedCrmDeal?.id === quoteId && selectedCrmDeal.crmRecordType === "quote"
            ? await ensureCrmLeadForExecutionSpecs(selectedCrmDeal)
            : selectedCrmDeal;
        const targetDealId = targetDeal?.id || quoteId;

        if (targetDealId && targetDealId !== quoteId) {
          setSelectedCrmDealId(targetDealId);
        }

        const shouldSaveAsLead =
          targetDeal?.crmRecordType === "lead" || targetDealId !== quoteId;
        await saveCrmFieldsForDeal(
          targetDealId,
          {
            checklistItems: nextChecklistItems,
          },
          {
            forceLead: shouldSaveAsLead,
            sourceLead: shouldSaveAsLead
              ? { ...targetDeal, id: targetDealId, crmRecordType: "lead" }
              : null,
            createIfMissing: shouldSaveAsLead,
          }
        );
        setCrmDetailsSavedAt(new Date());
        notify("Checklist updated", "success");
      }
    },
    [
      ensureCrmLeadForExecutionSpecs,
      getTeamMemberById,
      markCrmDetailDraftDirty,
      notify,
      sanitizeChecklistItems,
      saveCrmFieldsForDeal,
      selectedCrmDeal,
    ]
  );

  const buildCanonicalCrmDetailsPayloadFromDraft = useCallback(
    (draft = {}, currentUser = {}) => {
      const installationDateInput = String(draft.installationDate || "").trim();
      const parsedInstallationDate = parseDateInputValue(installationDateInput);
      const installationDateKey =
        toDateInputValue(parsedInstallationDate) || installationDateInput;

      if (
        installationDateInput &&
        (!parsedInstallationDate ||
          toDateInputValue(parsedInstallationDate) !== installationDateInput)
      ) {
        throw new Error("Please select a valid installation date.");
      }

      const checklistItems = withCanonicalChecklistAliases(
        draft.checklistItems || draft.checklist || draft.stageChecklist || []
      );
      const draftReminderConfigs = normalizeCrmReminderConfigs(draft.reminderConfigs, {
        nextActionDate: parseDateInputValue(draft.nextActionDate),
        installationDate: parsedInstallationDate || installationDateKey || null,
      });
      const rawInstallationReminder =
        draft.installationReminder ||
        draft.reminderConfig ||
        draftReminderConfigs[CRM_REMINDER_TYPE_INSTALLATION] ||
        {};
      const enabled =
        rawInstallationReminder.enabled === true ||
        rawInstallationReminder.reminderEnabled === true;
      const reminderDate = String(
        rawInstallationReminder.reminderDate ||
          rawInstallationReminder.date ||
          installationDateKey ||
          ""
      ).trim();
      const reminderTime = parseTimeInputValue(
        rawInstallationReminder.reminderTime ||
          rawInstallationReminder.time ||
          DEFAULT_CRM_REMINDER_TIME
      );
      const repeat = String(
        rawInstallationReminder.repeat ||
          rawInstallationReminder.repeatKey ||
          "daily"
      ).trim() || "daily";
      const nextNotifyAtDate =
        enabled && reminderDate
          ? combineReminderDateTime(reminderDate, reminderTime)
          : null;
      const actorUid = String(currentUser?.uid || auth.currentUser?.uid || "").trim();
      const actorEmail = String(
        currentUser?.email || auth.currentUser?.email || ""
      ).trim();
      const updatedAtIso = new Date().toISOString();
      const installationReminder = {
        enabled,
        reminderEnabled: enabled,
        reminderDate,
        reminderTime,
        repeat,
        repeatKey: repeat,
        status: enabled ? "pending" : CRM_REMINDER_STATUS_DISABLED,
        nextNotifyAt: nextNotifyAtDate ? nextNotifyAtDate.toISOString() : null,
        updatedAt: updatedAtIso,
        updatedBy: actorUid,
        updatedByEmail: actorEmail,
      };
      const reminderConfigs = {
        ...draftReminderConfigs,
        [CRM_REMINDER_TYPE_INSTALLATION]: {
          ...(draftReminderConfigs[CRM_REMINDER_TYPE_INSTALLATION] || {}),
          ...installationReminder,
        },
      };
      const parsedTags = Array.isArray(draft.tags)
        ? draft.tags
        : String(draft.tags || "")
            .split(",")
            .map((tag) => tag.trim())
            .filter(Boolean);
      const followUps = normalizeCrmFollowUps(draft.followUps || [], {
        nextAction: draft.nextAction || "",
        nextActionDate: parseDateInputValue(draft.nextActionDate),
        reminderConfig: reminderConfigs[CRM_REMINDER_TYPE_TASK_DUE],
      });

      return sanitizeCrmFirestoreValue({
        title: draft.title || "",
        contact: draft.contact || {},
        source: draft.source || draft.contact?.source || "",
        stage: draft.stage || draft.stageId || "",
        stageId: draft.stageId || draft.stage || "",
        status: draft.status || "draft",
        pipelineType: draft.pipelineType || CURTAIN_QUOTE_TYPE,
        quoteType: draft.pipelineType || CURTAIN_QUOTE_TYPE,
        installationDate: installationDateKey || "",
        installationReminder,
        reminderConfig: installationReminder,
        reminderConfigs,
        checklistItems,
        checklist: checklistItems,
        stageChecklist: checklistItems,
        followUps,
        notes: draft.notes || "",
        internalNotes: draft.internalNotes || draft.notes || "",
        tags: parsedTags,
        assignedToUserId: draft.assignedToUserId || "",
        assignedToNameSnapshot: draft.assignedToNameSnapshot || "",
        assignedTo: draft.assignedToNameSnapshot || draft.assignedTo || "",
        assignedToName: draft.assignedToNameSnapshot || draft.assignedTo || "",
        assignedAt: draft.assignedAt || null,
        assignedBy: draft.assignedBy || "",
        updatedAt: serverTimestamp(),
        updatedBy: actorUid,
        updatedByEmail: actorEmail,
        ...(isDevRuntime
          ? {
              _lastWriteSource: "saveCrmDetailsCanonical",
              _lastWriteAt: serverTimestamp(),
            }
          : {}),
      });
    },
    [combineReminderDateTime]
  );

  const verifyCanonicalCrmDetailsSnapshot = useCallback(
    (rawData = {}, expectedPayload = {}) => {
      const expectedChecklistProgress = getChecklistProgress(
        expectedPayload.checklistItems || []
      );
      const savedChecklistItems = withCanonicalChecklistAliases(
        rawData.checklistItems || rawData.checklist || rawData.stageChecklist || []
      );
      const savedChecklistProgress = getChecklistProgress(savedChecklistItems);
      const expectedInstallationDate = String(
        expectedPayload.installationDate || ""
      ).trim();
      const savedInstallationDate =
        toDateInputValue(rawData.installationDate) ||
        String(rawData.installationDate || "").trim();
      const expectedReminder = expectedPayload.installationReminder || {};
      const savedReminder =
        rawData.installationReminder ||
        rawData.reminderConfig ||
        rawData.reminderConfigs?.[CRM_REMINDER_TYPE_INSTALLATION] ||
        {};
      const expectedReminderEnabled = expectedReminder.enabled === true;
      const savedReminderEnabled =
        savedReminder.enabled === true || savedReminder.reminderEnabled === true;
      const mismatches = [];

      if (expectedInstallationDate !== savedInstallationDate) {
        mismatches.push("installationDate");
      }
      if (
        expectedChecklistProgress.total !== savedChecklistProgress.total ||
        expectedChecklistProgress.completed !== savedChecklistProgress.completed
      ) {
        mismatches.push("checklistItems");
      }
      if (expectedReminderEnabled !== savedReminderEnabled) {
        mismatches.push("installationReminder.enabled");
      }
      if (
        expectedReminderEnabled &&
        String(expectedReminder.reminderDate || "") !==
          String(savedReminder.reminderDate || "")
      ) {
        mismatches.push("installationReminder.reminderDate");
      }
      if (
        expectedReminderEnabled &&
        String(expectedReminder.reminderTime || "") !==
          String(savedReminder.reminderTime || "")
      ) {
        mismatches.push("installationReminder.reminderTime");
      }

      return {
        passed: mismatches.length === 0,
        mismatches,
        expectedChecklistProgress,
        savedChecklistProgress,
        expectedInstallationDate,
        savedInstallationDate,
        expectedReminderEnabled,
        savedReminderEnabled,
        lastWriteSource: rawData._lastWriteSource || "",
      };
    },
    []
  );

  const saveCrmDetailsCanonical = useCallback(
    async ({ workspaceUid: targetWorkspaceUid, selectedCrmDealId: targetLeadId, draft, currentUser }) => {
      if (!targetWorkspaceUid || !targetLeadId) {
        throw new Error("Could not save because no deal is selected.");
      }
      if (!draft) {
        throw new Error("CRM details save failed: no draft was available.");
      }

      const leadRef = doc(db, "users", targetWorkspaceUid, "crm_leads", targetLeadId);
      const payload = buildCanonicalCrmDetailsPayloadFromDraft(draft, currentUser);
      const payloadChecklistProgress = getChecklistProgress(payload.checklistItems || []);

      if (isDevRuntime) {
        setCrmSaveDebugState((prev) => ({
          ...prev,
          lastPayload: payload,
          lastImmediateReadback: null,
          lastDelayedReadback: null,
          lastWritePath: `users/${targetWorkspaceUid}/crm_leads/${targetLeadId}`,
          lastVerification: {
            status: "canonical-writing",
            payloadChecklistCompleted: payloadChecklistProgress.completed,
            payloadChecklistTotal: payloadChecklistProgress.total,
            installationDate: payload.installationDate,
            installationReminderEnabled: payload.installationReminder?.enabled === true,
          },
        }));
        console.log("CRM SAVE PAYLOAD FINAL", {
          selectedCrmDealId: targetLeadId,
          path: `users/${targetWorkspaceUid}/crm_leads/${targetLeadId}`,
          checklistItems: payload.checklistItems,
          checklist: payload.checklist,
          stageChecklist: payload.stageChecklist,
          installationDate: payload.installationDate,
          installationReminder: payload.installationReminder,
          reminderConfig: payload.reminderConfig,
          reminderConfigs: payload.reminderConfigs,
          payloadKeys: Object.keys(payload),
        });
        console.log("WRITING CRM DETAILS DOC", leadRef.path, payload);
      }

      await setDoc(leadRef, payload, { merge: true });

      const snap1 = await getDoc(leadRef);
      if (!snap1.exists()) {
        throw new Error("CRM details save verification failed: document missing after write.");
      }
      const data1 = { id: snap1.id, ...snap1.data() };
      const immediateVerification = verifyCanonicalCrmDetailsSnapshot(data1, payload);

      if (isDevRuntime) {
        console.log("CRM SAVE READBACK IMMEDIATE", data1);
        setCrmSaveDebugState((prev) => ({
          ...prev,
          lastImmediateReadback: data1,
          lastVerification: {
            ...(prev.lastVerification || {}),
            status: immediateVerification.passed
              ? "canonical-immediate-ok"
              : "canonical-immediate-mismatch",
            immediateMismatches: immediateVerification.mismatches,
            immediateChecklistCompleted:
              immediateVerification.savedChecklistProgress.completed,
            immediateChecklistTotal: immediateVerification.savedChecklistProgress.total,
            immediateInstallationReminderEnabled:
              immediateVerification.savedReminderEnabled,
            immediateLastWriteSource: immediateVerification.lastWriteSource,
          },
        }));
      }

      await waitFor(2000);

      const snap2 = await getDoc(leadRef);
      if (!snap2.exists()) {
        throw new Error("CRM details save verification failed: document missing after delayed read.");
      }
      const data2 = { id: snap2.id, ...snap2.data() };
      const delayedVerification = verifyCanonicalCrmDetailsSnapshot(data2, payload);

      if (isDevRuntime) {
        console.log("CRM SAVE READBACK DELAYED", data2);
        setCrmSaveDebugState((prev) => ({
          ...prev,
          lastDelayedReadback: data2,
          lastVerification: {
            ...(prev.lastVerification || {}),
            status:
              immediateVerification.passed && delayedVerification.passed
                ? "canonical-verified"
                : "canonical-mismatch",
            immediateMismatches: immediateVerification.mismatches,
            delayedMismatches: delayedVerification.mismatches,
            delayedChecklistCompleted:
              delayedVerification.savedChecklistProgress.completed,
            delayedChecklistTotal: delayedVerification.savedChecklistProgress.total,
            delayedInstallationReminderEnabled:
              delayedVerification.savedReminderEnabled,
            delayedLastWriteSource: delayedVerification.lastWriteSource,
          },
        }));
      }

      if (!immediateVerification.passed || !delayedVerification.passed) {
        if (isDevRuntime) {
          console.error("CRM canonical save verification failed", {
            path: leadRef.path,
            payload,
            immediateVerification,
            delayedVerification,
            immediateReadback: data1,
            delayedReadback: data2,
          });
        }
        throw new Error("CRM details save verification failed.");
      }

      return {
        payload,
        immediateReadback: data1,
        delayedReadback: data2,
        verification: delayedVerification,
      };
    },
    [
      buildCanonicalCrmDetailsPayloadFromDraft,
      verifyCanonicalCrmDetailsSnapshot,
    ]
  );

  const handleSaveCrmDetailsCanonical = useCallback(async () => {
    if (!selectedCrmDealId) {
      notify("Could not save because no deal is selected.", "error");
      return;
    }
    if (!crmDetailDraft) {
      notify("Could not save CRM details. Your changes are still here.", "error");
      return;
    }

    setIsSavingCrmDetails(true);
    try {
      const result = await saveCrmDetailsCanonical({
        workspaceUid,
        selectedCrmDealId,
        draft: crmDetailDraft,
        currentUser: auth.currentUser || user || {},
      });
      const savedAt = new Date();
      const savedRaw = result.delayedReadback || result.immediateReadback || {};
      const savedLead = buildCrmLeadData(savedRaw);
      const savedDraft = {
        ...crmDetailDraft,
        installationDate: String(result.payload.installationDate || ""),
        installationReminder: result.payload.installationReminder,
        reminderConfig: result.payload.installationReminder,
        reminderConfigs: result.payload.reminderConfigs,
        checklistItems: result.payload.checklistItems,
        checklist: result.payload.checklistItems,
        stageChecklist: result.payload.checklistItems,
        followUps: normalizeCrmFollowUps(result.payload.followUps || []).map((followUp) =>
          createCrmFollowUpDraft(followUp)
        ),
      };
      let reminderQueueSyncFailed = false;

      applyCrmLeadLocalPatch(selectedCrmDealId, {
        ...savedLead,
        id: selectedCrmDealId,
        ...savedRaw,
        checklistItems: result.payload.checklistItems,
        checklist: result.payload.checklistItems,
        stageChecklist: result.payload.checklistItems,
        installationDate: result.payload.installationDate,
        installationReminder: result.payload.installationReminder,
        reminderConfig: result.payload.installationReminder,
        reminderConfigs: result.payload.reminderConfigs,
        updatedAt: savedAt,
      });
      await upsertCustomerSearchIndexRecord(
        {
          ...savedLead,
          ...savedRaw,
          id: selectedCrmDealId,
          crmLeadId: selectedCrmDealId,
          leadId: selectedCrmDealId,
          sourceType: "crm_lead",
        },
        { sourceType: "crm_lead" }
      ).catch((error) => {
        if (isDevRuntime) {
          console.error("CUSTOMER SEARCH INDEX CRM DETAILS UPSERT FAILED", {
            leadId: selectedCrmDealId,
            code: error?.code,
            message: error?.message,
          });
        }
      });
      await syncCrmLeadInstallationIndex(
        selectedCrmDealId,
        {
          ...savedLead,
          ...savedRaw,
          id: selectedCrmDealId,
          installationDate: result.payload.installationDate,
          installationReminder: result.payload.installationReminder,
        },
        { source: "saveCrmDetailsCanonical" }
      ).catch((error) => {
        if (isDevRuntime) {
          console.error("CRM installation index sync failed after details save", {
            leadId: selectedCrmDealId,
            code: error?.code,
            message: error?.message,
          });
        }
      });
      await syncReminderDocsForDeal(
        {
          ...savedLead,
          ...savedRaw,
          id: selectedCrmDealId,
          crmRecordType: "lead",
          customerLabel:
            savedLead.customerLabel ||
            savedLead.customerName ||
            savedLead.contact?.name ||
            savedLead.title ||
            "Unnamed Lead",
          crmTitle: savedLead.crmTitle || savedLead.title || "",
          crmContact: savedLead.contact || {},
          installationDate: result.payload.installationDate,
          installationReminder: result.payload.installationReminder,
          reminderConfigs: result.payload.reminderConfigs,
          followUps: result.payload.followUps,
        },
        {
          installationDate: result.payload.installationDate,
          reminderConfigs: result.payload.reminderConfigs,
          followUps: result.payload.followUps,
        }
      ).catch((error) => {
        reminderQueueSyncFailed = true;
        if (isDevRuntime) {
          console.error("CRM due reminder queue sync failed after details save", {
            leadId: selectedCrmDealId,
            code: error?.code,
            message: error?.message,
          });
        }
      });
      selectedCrmDealCacheRef.current = {
        ...(selectedCrmDealCacheRef.current || {}),
        ...savedLead,
        ...savedRaw,
        id: selectedCrmDealId,
        crmRecordType: "lead",
        checklistItems: result.payload.checklistItems,
        checklist: result.payload.checklistItems,
        stageChecklist: result.payload.checklistItems,
        installationDate: result.payload.installationDate,
        installationReminder: result.payload.installationReminder,
        reminderConfig: result.payload.installationReminder,
        reminderConfigs: result.payload.reminderConfigs,
      };
      selectedCrmDealIdRef.current = String(selectedCrmDealId);
      crmDetailDraftDealIdRef.current = String(selectedCrmDealId);
      crmDetailDraftDirtyRef.current = false;
      setCrmDetailDraft(savedDraft);
      setCrmDetailsSavedAt(savedAt);
      markExecutionDetailsBaseline(savedDraft, "saved");
      clearExecutionDetailsDraft();
      notify(
        reminderQueueSyncFailed
          ? "CRM details saved, but reminder notifications could not be queued."
          : "CRM details saved",
        reminderQueueSyncFailed ? "warning" : "success"
      );
    } catch (error) {
      if (isDevRuntime) {
        console.error("Failed to save CRM details canonical pipeline:", error);
        setCrmSaveDebugState((prev) => ({
          ...prev,
          lastVerification: {
            ...(prev.lastVerification || {}),
            status: "canonical-failed",
            error: error?.message || String(error),
          },
        }));
      }
      notify(
        String(error?.message || "").includes("verification")
          ? "Save could not be verified. Your changes are still here."
          : "Could not save CRM details. Your changes are still here.",
        "error"
      );
    } finally {
      setIsSavingCrmDetails(false);
    }
  }, [
    applyCrmLeadLocalPatch,
    buildCrmLeadData,
    clearExecutionDetailsDraft,
    createCrmFollowUpDraft,
    crmDetailDraft,
    markExecutionDetailsBaseline,
    notify,
    saveCrmDetailsCanonical,
    selectedCrmDealId,
    syncCrmLeadInstallationIndex,
    syncReminderDocsForDeal,
    upsertCustomerSearchIndexRecord,
    user,
    workspaceUid,
  ]);

  const testDirectCrmDetailsSave = useCallback(async () => {
    if (!workspaceUid || !selectedCrmDealId) {
      notify("Could not run test because no deal is selected.", "warning");
      return;
    }

    const leadRef = doc(db, "users", workspaceUid, "crm_leads", selectedCrmDealId);
    const debugPayload = sanitizeCrmFirestoreValue({
      installationDate: "2099-01-01",
      installationReminder: {
        enabled: true,
        reminderEnabled: true,
        reminderDate: "2099-01-01",
        reminderTime: "09:00",
        repeat: "daily",
        repeatKey: "daily",
        status: "pending",
      },
      reminderConfig: {
        enabled: true,
        reminderEnabled: true,
        reminderDate: "2099-01-01",
        reminderTime: "09:00",
        repeat: "daily",
        repeatKey: "daily",
        status: "pending",
      },
      reminderConfigs: {
        [CRM_REMINDER_TYPE_INSTALLATION]: {
          enabled: true,
          reminderEnabled: true,
          reminderDate: "2099-01-01",
          reminderTime: "09:00",
          repeat: "daily",
          repeatKey: "daily",
          status: "pending",
        },
      },
      checklistItems: [
        {
          id: "debug-checklist-item",
          label: "Debug checklist item",
          completed: true,
          done: true,
          checked: true,
        },
      ],
      checklist: [
        {
          id: "debug-checklist-item",
          label: "Debug checklist item",
          completed: true,
          done: true,
          checked: true,
        },
      ],
      stageChecklist: [
        {
          id: "debug-checklist-item",
          label: "Debug checklist item",
          completed: true,
          done: true,
          checked: true,
        },
      ],
      updatedAt: serverTimestamp(),
      updatedBy: auth.currentUser?.uid || user?.uid || "",
      updatedByEmail: auth.currentUser?.email || user?.email || "",
      ...(isDevRuntime
        ? {
            _lastWriteSource: "debugDirectCrmDetailsSave",
            _lastWriteAt: serverTimestamp(),
          }
        : {}),
    });

    try {
      console.log("WRITING CRM DETAILS DOC", leadRef.path, debugPayload);
      await setDoc(leadRef, debugPayload, { merge: true });
      const snap1 = await getDoc(leadRef);
      const data1 = snap1.exists() ? { id: snap1.id, ...snap1.data() } : null;
      await waitFor(2000);
      const snap2 = await getDoc(leadRef);
      const data2 = snap2.exists() ? { id: snap2.id, ...snap2.data() } : null;
      console.log("CRM DIRECT TEST READBACK IMMEDIATE", data1);
      console.log("CRM DIRECT TEST READBACK DELAYED", data2);
      setCrmSaveDebugState((prev) => ({
        ...prev,
        lastPayload: debugPayload,
        lastImmediateReadback: data1,
        lastDelayedReadback: data2,
        lastWritePath: `users/${workspaceUid}/crm_leads/${selectedCrmDealId}`,
        lastVerification: {
          status: "direct-test-complete",
          immediateHasReminder: data1?.installationReminder?.enabled === true,
          delayedHasReminder: data2?.installationReminder?.enabled === true,
          delayedLastWriteSource: data2?._lastWriteSource || "",
        },
      }));
      notify("Direct CRM save test completed. Check CRM Save Debug.", "success");
    } catch (error) {
      console.error("Direct CRM details save test failed", error);
      notify("Direct CRM save test failed. Check console.", "error");
    }
  }, [notify, selectedCrmDealId, user, workspaceUid]);

  const persistCrmDetailDraft = async () => {
    const activeCrmDeal = selectedCrmDeal || selectedCrmDealCacheRef.current;
    if (!selectedCrmDealId && !activeCrmDeal?.id) {
      notify("Could not save because no deal is selected.", "error");
      if (isDevRuntime) {
        console.error("Save blocked: selectedCrmDealId missing", {
          selectedCrmDeal,
          cachedDeal: selectedCrmDealCacheRef.current,
        });
      }
      return;
    }

    if (!activeCrmDeal || !crmDetailDraft) {
      notify("Could not save CRM details. Your changes are still here.", "error");
      return;
    }

    const beforeSelectedCrmDealId = selectedCrmDealId;
    const beforeSelectedDealName =
      activeCrmDeal.crmTitle ||
      activeCrmDeal.customerLabel ||
      activeCrmDeal.title ||
      "";
    setIsSavingCrmDetails(true);

    try {
      const parsedTags = String(crmDetailDraft.tags || "")
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean);
      const nextActionDate = parseDateInputValue(crmDetailDraft.nextActionDate);
      const installationDateInput = String(crmDetailDraft.installationDate || "").trim();
      const installationDate = parseDateInputValue(crmDetailDraft.installationDate);
      if (
        installationDateInput &&
        (!installationDate || toDateInputValue(installationDate) !== installationDateInput)
      ) {
        notify("Please select a valid installation date", "warning");
        return;
      }
      const reminderConfigs = normalizeCrmReminderConfigs(
        crmDetailDraft.reminderConfigs,
        {
          nextActionDate,
          installationDate,
        }
      );
      const installationReminder = buildCanonicalInstallationReminder(
        {
          ...crmDetailDraft,
          reminderConfigs,
        },
        {
          installationDate,
          reminderConfigs,
        }
      );
      const reminderConfigsForSave = {
        ...reminderConfigs,
        [CRM_REMINDER_TYPE_INSTALLATION]: {
          ...reminderConfigs[CRM_REMINDER_TYPE_INSTALLATION],
          ...installationReminder,
        },
      };
      const followUps = normalizeCrmFollowUps(crmDetailDraft.followUps || [], {
        nextAction: crmDetailDraft.nextAction || "",
        nextActionDate,
        reminderConfig: reminderConfigsForSave[CRM_REMINDER_TYPE_TASK_DUE],
      });
      const primaryFollowUp = getPrimaryPendingCrmFollowUp(followUps);
      const executionSpecs = stampExecutionSpecs(
        serializeExecutionSpecDraftItems(crmDetailDraft.executionSpecs || [])
      );
      const canonicalDeal =
        activeCrmDeal.crmRecordType === "quote"
          ? await ensureCrmLeadForExecutionSpecs(activeCrmDeal, executionSpecs)
          : activeCrmDeal;
      const targetDealId = canonicalDeal?.id || activeCrmDeal.id;
      if (!targetDealId) {
        throw new Error("CRM details save failed: no CRM lead id was available.");
      }
      const targetDealCrm = canonicalDeal?.crm || activeCrmDeal.crm || {};
      const previousChecklistItems = normalizeChecklistTree(
        canonicalDeal?.checklistItems ||
          targetDealCrm?.checklistItems ||
          activeCrmDeal.checklistItems ||
          []
      );
      const nextChecklistItems = sanitizeChecklistItems(
        crmDetailDraft.checklistItems || []
      );
      const sourceLeadForSave = {
        ...canonicalDeal,
        id: targetDealId,
        crmRecordType: "lead",
      };
      const crmDetailsSavePayload = {
        title: crmDetailDraft.title || canonicalDeal?.crmTitle || activeCrmDeal.crmTitle,
        contact: {
          ...(crmDetailDraft.contact || {}),
        },
        source: crmDetailDraft.contact?.source || canonicalDeal?.source || activeCrmDeal.source || "",
        stageId: crmDetailDraft.stageId || canonicalDeal?.crmStageId || canonicalDeal?.stageId || activeCrmDeal.crmStageId || "",
        status: crmDetailDraft.status || canonicalDeal?.status || activeCrmDeal.status || "draft",
        quoteType: canonicalDeal?.pipelineType || activeCrmDeal.pipelineType || activeCrmDeal.quoteTypeResolved || CURTAIN_QUOTE_TYPE,
        nextAction: primaryFollowUp?.title || crmDetailDraft.nextAction || "",
        nextActionDate: primaryFollowUp?.dueDate
          ? parseDateInputValue(primaryFollowUp.dueDate)
          : nextActionDate,
        installationDate,
        notes: crmDetailDraft.notes || "",
        assignedToUserId: crmDetailDraft.assignedToUserId || "",
        assignedToNameSnapshot: crmDetailDraft.assignedToNameSnapshot || "",
        assignedTo: crmDetailDraft.assignedToNameSnapshot || "",
        assignedAt: crmDetailDraft.assignedToUserId
          ? crmDetailDraft.assignedAt || targetDealCrm.assignedAt || new Date()
          : null,
        assignedBy: crmDetailDraft.assignedToUserId
          ? targetDealCrm.assignedBy || user?.email || user?.uid || ""
          : "",
        tags: parsedTags,
        checklistItems: nextChecklistItems,
        checklist: nextChecklistItems,
        stageChecklist: nextChecklistItems,
        installationReminder,
        reminderConfig: installationReminder,
        reminderConfigs: reminderConfigsForSave,
        followUps,
        attachments: normalizeCrmAttachments(crmDetailDraft.attachments || []),
        executionSpecs,
        siteLocations: normalizeSiteLocations(crmDetailDraft.siteLocations || []),
        expectedValue: Number(canonicalDeal?.expectedValue || activeCrmDeal.expectedValue || 0),
        finalValue: Number(canonicalDeal?.finalValue || activeCrmDeal.finalValue || 0),
      };
      const draftChecklistProgress = getChecklistProgress(
        crmDetailDraft.checklistItems || []
      );
      const payloadChecklistProgress = getChecklistProgress(
        crmDetailsSavePayload.checklistItems || []
      );

      if (isDevRuntime) {
        if (
          draftChecklistProgress.completed > 0 &&
          payloadChecklistProgress.completed < draftChecklistProgress.completed
        ) {
          throw new Error("CRM save payload lost checklistItems before Firestore write");
        }
        if (
          installationReminder?.enabled === true &&
          crmDetailsSavePayload.installationReminder?.enabled !== true
        ) {
          throw new Error("CRM save payload lost installationReminder before Firestore write");
        }
        setCrmSaveDebugState((prev) => ({
          ...prev,
          lastPayload: crmDetailsSavePayload,
          lastImmediateReadback: null,
          lastDelayedReadback: null,
          lastWritePath: `users/${workspaceUid}/crm_leads/${targetDealId}`,
          lastVerification: {
            status: "writing",
            at: new Date().toISOString(),
            draftChecklistCompleted: draftChecklistProgress.completed,
            draftChecklistTotal: draftChecklistProgress.total,
            payloadChecklistCompleted: payloadChecklistProgress.completed,
            payloadChecklistTotal: payloadChecklistProgress.total,
            installationReminderEnabled:
              crmDetailsSavePayload.installationReminder?.enabled === true,
          },
        }));
        console.log("CRM SAVE PAYLOAD FINAL", {
          selectedCrmDealId: targetDealId,
          path: `users/${workspaceUid}/crm_leads/${targetDealId}`,
          checklist: crmDetailsSavePayload.checklist,
          checklistItems: crmDetailsSavePayload.checklistItems,
          stageChecklist: crmDetailsSavePayload.stageChecklist,
          installationDate: crmDetailsSavePayload.installationDate,
          installationReminder: crmDetailsSavePayload.installationReminder,
          reminderConfig: crmDetailsSavePayload.reminderConfig,
          payloadKeys: Object.keys(crmDetailsSavePayload),
        });
        console.log("CRM details save payload audit", {
          workspaceUid,
          selectedCrmDealId,
          selectedCrmDealName: beforeSelectedDealName,
          firestorePath: `users/${workspaceUid}/crm_leads/${targetDealId}`,
          payloadKeys: Object.keys(crmDetailsSavePayload),
          draftChecklistCompleted: draftChecklistProgress.completed,
          draftChecklistTotal: draftChecklistProgress.total,
          payloadChecklistCompleted: payloadChecklistProgress.completed,
          payloadChecklistTotal: payloadChecklistProgress.total,
          installationDate: crmDetailsSavePayload.installationDate,
          followUpsCount: crmDetailsSavePayload.followUps?.length || 0,
          reminderEnabled:
            crmDetailsSavePayload.reminderConfigs?.[CRM_REMINDER_TYPE_INSTALLATION]
              ?.reminderEnabled,
        });
      }

      const saveResult = await saveCrmFieldsForLead(targetDealId, crmDetailsSavePayload, {
        forceLead: true,
        sourceLead: sourceLeadForSave,
        createIfMissing: true,
        requireWrite: true,
        persistFullCrmDetails: true,
        writeSource: "saveCrmDetails",
      });

      if (targetDealId && targetDealId !== activeCrmDeal.id) {
        setSelectedCrmDealId(targetDealId);
      } else if (targetDealId) {
        setSelectedCrmDealId(targetDealId);
      }
      selectedCrmDealIdRef.current = String(targetDealId || activeCrmDeal.id || "");

      const savedLeadForVerification = saveResult?.savedLead || null;
      if (!savedLeadForVerification) {
        throw new Error("CRM details save verification failed: saved lead was not returned.");
      }

      const expectedChecklistProgress = payloadChecklistProgress;
      const savedChecklistItems = normalizeChecklistTree(
        savedLeadForVerification.checklistItems ||
          savedLeadForVerification.checklist ||
          savedLeadForVerification.stageChecklist ||
          []
      );
      const savedChecklistProgress = getChecklistProgress(savedChecklistItems);
      const expectedInstallationDateKey = toDateInputValue(installationDate);
      const savedInstallationDateKey = toDateInputValue(
        savedLeadForVerification.installationDate
      );
      const expectedInstallationReminder = installationReminder;
      const savedInstallationReminder = buildCanonicalInstallationReminder(
        savedLeadForVerification,
        {
          installationDate: savedLeadForVerification.installationDate,
          reminderConfigs: savedLeadForVerification.reminderConfigs,
        }
      );
      const expectedReminderDateKey = String(expectedInstallationReminder?.reminderDate || "");
      const savedReminderDateKey = String(savedInstallationReminder?.reminderDate || "");
      const expectedReminderRepeat = String(
        expectedInstallationReminder?.repeat || expectedInstallationReminder?.repeatKey || ""
      );
      const savedReminderRepeat = String(
        savedInstallationReminder?.repeat || savedInstallationReminder?.repeatKey || ""
      );
      const savedReminderConfig = normalizeCrmReminderConfigs(
        savedLeadForVerification.reminderConfigs,
        {
          nextActionDate: savedLeadForVerification.nextActionDate,
          installationDate: savedLeadForVerification.installationDate,
        }
      )[CRM_REMINDER_TYPE_INSTALLATION];
      const legacyExpectedReminderConfig = normalizeCrmReminderConfigs(reminderConfigsForSave, {
        nextActionDate,
        installationDate,
      })[CRM_REMINDER_TYPE_INSTALLATION];
      const savedRawInstallationReminder =
        saveResult?.savedLeadRaw?.installationReminder || null;
      const savedFollowUps = normalizeCrmFollowUps(savedLeadForVerification.followUps || []);
      const verificationMismatches = [];

      if (expectedInstallationDateKey !== savedInstallationDateKey) {
        verificationMismatches.push("installationDate");
      }
      if (
        expectedChecklistProgress.total !== savedChecklistProgress.total ||
        expectedChecklistProgress.completed !== savedChecklistProgress.completed
      ) {
        verificationMismatches.push("checklistItems");
      }
      if (followUps.length !== savedFollowUps.length) {
        verificationMismatches.push("followUps");
      }
      if (
        expectedInstallationReminder?.enabled !== savedInstallationReminder?.enabled ||
        expectedReminderDateKey !== savedReminderDateKey ||
        expectedInstallationReminder?.reminderTime !== savedInstallationReminder?.reminderTime ||
        expectedReminderRepeat !== savedReminderRepeat ||
        legacyExpectedReminderConfig?.reminderEnabled !== savedReminderConfig?.reminderEnabled ||
        legacyExpectedReminderConfig?.reminderTime !== savedReminderConfig?.reminderTime ||
        legacyExpectedReminderConfig?.repeatKey !== savedReminderConfig?.repeatKey ||
        (expectedInstallationReminder?.enabled === true &&
          savedRawInstallationReminder?.enabled !== true)
      ) {
        verificationMismatches.push("installationReminder");
      }
      if (String(crmDetailsSavePayload.notes || "") !== String(savedLeadForVerification.notes || "")) {
        verificationMismatches.push("notes");
      }

      if (isDevRuntime) {
        const savedLeadRawForDebug = saveResult?.savedLeadRaw || {};
        console.log("CRM SAVE READBACK IMMEDIATE", savedLeadRawForDebug);
        console.log("CRM SAVE READBACK", {
          selectedCrmDealId: targetDealId,
          checklist: savedLeadRawForDebug.checklist,
          checklistItems: savedLeadRawForDebug.checklistItems,
          stageChecklist: savedLeadRawForDebug.stageChecklist,
          installationDate: savedLeadRawForDebug.installationDate,
          installationReminder: savedLeadRawForDebug.installationReminder,
          reminderConfig: savedLeadRawForDebug.reminderConfig,
        });
        console.log("CRM details save readback audit", {
          selectedCrmDealId: targetDealId,
          savedExists: true,
          savedInstallationDate: savedInstallationDateKey,
          savedChecklistCount: savedChecklistProgress.total,
          savedChecklistCompleted: savedChecklistProgress.completed,
          savedFollowUpsCount: savedFollowUps.length,
          verificationPassed: verificationMismatches.length === 0,
          selectedCrmDealIdStillSame:
            String(targetDealId || "") === String(selectedCrmDealIdRef.current || targetDealId || ""),
          verificationMismatches,
        });
        setCrmSaveDebugState((prev) => ({
          ...prev,
          lastImmediateReadback: saveResult?.savedLeadRaw || {},
          lastVerification: {
            ...(prev.lastVerification || {}),
            status: verificationMismatches.length === 0 ? "immediate-ok" : "immediate-mismatch",
            immediateMismatches: verificationMismatches,
            immediateChecklistCompleted: savedChecklistProgress.completed,
            immediateChecklistTotal: savedChecklistProgress.total,
            immediateInstallationReminderEnabled:
              savedInstallationReminder?.enabled === true,
            immediateLastWriteSource:
              saveResult?.savedLeadRaw?._lastWriteSource || "",
            immediateUpdatedAt: saveResult?.savedLeadRaw?.updatedAt || null,
          },
        }));
      }

      await waitFor(2000);
      const delayedLeadRef = doc(db, "users", workspaceUid, "crm_leads", targetDealId);
      const delayedLeadSnap = await getDoc(delayedLeadRef);
      if (!delayedLeadSnap.exists()) {
        throw new Error("CRM details save verification failed: delayed read-back document is missing.");
      }
      const delayedLeadRaw = {
        id: targetDealId,
        ...delayedLeadSnap.data(),
      };
      const delayedLeadForVerification = buildCrmLeadData(delayedLeadRaw);
      const delayedChecklistItems = normalizeChecklistTree(
        delayedLeadForVerification.checklistItems ||
          delayedLeadForVerification.checklist ||
          delayedLeadForVerification.stageChecklist ||
          []
      );
      const delayedChecklistProgress = getChecklistProgress(delayedChecklistItems);
      const delayedInstallationDateKey = toDateInputValue(
        delayedLeadForVerification.installationDate
      );
      const delayedInstallationReminder = buildCanonicalInstallationReminder(
        delayedLeadForVerification,
        {
          installationDate: delayedLeadForVerification.installationDate,
          reminderConfigs: delayedLeadForVerification.reminderConfigs,
        }
      );
      const delayedReminderDateKey = String(delayedInstallationReminder?.reminderDate || "");
      const delayedReminderRepeat = String(
        delayedInstallationReminder?.repeat || delayedInstallationReminder?.repeatKey || ""
      );
      const delayedReminderConfig = normalizeCrmReminderConfigs(
        delayedLeadForVerification.reminderConfigs,
        {
          nextActionDate: delayedLeadForVerification.nextActionDate,
          installationDate: delayedLeadForVerification.installationDate,
        }
      )[CRM_REMINDER_TYPE_INSTALLATION];
      const delayedRawInstallationReminder = delayedLeadRaw.installationReminder || null;
      const delayedFollowUps = normalizeCrmFollowUps(
        delayedLeadForVerification.followUps || []
      );
      const delayedVerificationMismatches = [];

      if (expectedInstallationDateKey !== delayedInstallationDateKey) {
        delayedVerificationMismatches.push("installationDate");
      }
      if (
        expectedChecklistProgress.total !== delayedChecklistProgress.total ||
        expectedChecklistProgress.completed !== delayedChecklistProgress.completed
      ) {
        delayedVerificationMismatches.push("checklistItems");
      }
      if (followUps.length !== delayedFollowUps.length) {
        delayedVerificationMismatches.push("followUps");
      }
      if (
        expectedInstallationReminder?.enabled !== delayedInstallationReminder?.enabled ||
        expectedReminderDateKey !== delayedReminderDateKey ||
        expectedInstallationReminder?.reminderTime !== delayedInstallationReminder?.reminderTime ||
        expectedReminderRepeat !== delayedReminderRepeat ||
        legacyExpectedReminderConfig?.reminderEnabled !== delayedReminderConfig?.reminderEnabled ||
        legacyExpectedReminderConfig?.reminderTime !== delayedReminderConfig?.reminderTime ||
        legacyExpectedReminderConfig?.repeatKey !== delayedReminderConfig?.repeatKey ||
        (expectedInstallationReminder?.enabled === true &&
          delayedRawInstallationReminder?.enabled !== true)
      ) {
        delayedVerificationMismatches.push("installationReminder");
      }
      if (
        String(crmDetailsSavePayload.notes || "") !==
        String(delayedLeadForVerification.notes || "")
      ) {
        delayedVerificationMismatches.push("notes");
      }

      if (isDevRuntime) {
        console.log("CRM SAVE READBACK DELAYED", delayedLeadRaw);
        setCrmSaveDebugState((prev) => ({
          ...prev,
          lastDelayedReadback: delayedLeadRaw,
          lastVerification: {
            ...(prev.lastVerification || {}),
            status:
              verificationMismatches.length === 0 &&
              delayedVerificationMismatches.length === 0
                ? "verified"
                : "mismatch",
            immediateMismatches: verificationMismatches,
            delayedMismatches: delayedVerificationMismatches,
            delayedChecklistCompleted: delayedChecklistProgress.completed,
            delayedChecklistTotal: delayedChecklistProgress.total,
            delayedInstallationReminderEnabled:
              delayedInstallationReminder?.enabled === true,
            delayedLastWriteSource: delayedLeadRaw?._lastWriteSource || "",
            delayedUpdatedAt: delayedLeadRaw?.updatedAt || null,
          },
        }));
      }

      if (
        verificationMismatches.length > 0 ||
        delayedVerificationMismatches.length > 0
      ) {
        if (isDevRuntime) {
          console.error("CRM save verification failed", {
            selectedCrmDealId: targetDealId,
            expectedChecklistProgress,
            savedChecklistProgress,
            expectedInstallationDateKey,
            savedInstallationDateKey,
            expectedFollowUpsCount: followUps.length,
            savedFollowUpsCount: savedFollowUps.length,
            expectedInstallationReminder,
            savedInstallationReminder,
            savedRawInstallationReminder,
            delayedChecklistProgress,
            delayedInstallationDateKey,
            delayedFollowUpsCount: delayedFollowUps.length,
            delayedInstallationReminder,
            delayedRawInstallationReminder,
            verificationMismatches,
            delayedVerificationMismatches,
            lastWriteSource: delayedLeadRaw?._lastWriteSource || "",
          });
        }
        throw new Error(
          `CRM details save verification failed: ${[
            ...verificationMismatches,
            ...delayedVerificationMismatches,
          ].join(", ")} did not match read-back data.`
        );
      }

      const newlyCompletedChecklistItems = getNewlyCompletedChecklistNodes(
        previousChecklistItems,
        nextChecklistItems
      );

      if (newlyCompletedChecklistItems.length > 0) {
        await Promise.all(
          newlyCompletedChecklistItems.map((item) =>
            writeTaskHistoryRecord({
              taskType: TASK_HISTORY_TYPE_CHECKLIST,
              sourceTaskId: item.id,
              dealLike: {
                ...canonicalDeal,
                id: targetDealId,
              },
              taskTitle: item.title || "Checklist Item",
              taskDescription: item.notes || item.pathLabel || "",
              dueDate: null,
              sourceModule:
                tab === "executionSpecs"
                  ? TASK_HISTORY_SOURCE_EXECUTION_DETAILS
                  : TASK_HISTORY_SOURCE_CRM,
              originalPayload: {
                checklistItem: item,
                path: item.path,
                pathLabel: item.pathLabel,
                assignedToUserId: item.assignedToUserId || "",
                assignedToNameSnapshot: item.assignedToNameSnapshot || "",
              },
            })
          )
        );
      }

      const savedAt = new Date();
      const savedDraft = {
        ...crmDetailDraft,
        checklistItems: nextChecklistItems,
        checklist: nextChecklistItems,
        stageChecklist: nextChecklistItems,
        installationReminder,
        reminderConfig: installationReminder,
        reminderConfigs: reminderConfigsForSave,
        followUps: followUps.map((followUp) => createCrmFollowUpDraft(followUp)),
        executionSpecs: hydrateExecutionSpecDraftItems(executionSpecs),
        siteLocations: normalizeSiteLocations(crmDetailDraft.siteLocations || []),
      };
      const cachedSavedDeal = {
        ...activeCrmDeal,
        ...canonicalDeal,
        id: targetDealId || activeCrmDeal.id,
        crmRecordType: "lead",
        crmTitle: crmDetailDraft.title || canonicalDeal?.crmTitle || activeCrmDeal.crmTitle,
        crmContact: crmDetailDraft.contact || {},
        contact: crmDetailDraft.contact || {},
        checklistItems: nextChecklistItems,
        checklist: nextChecklistItems,
        stageChecklist: nextChecklistItems,
        installationReminder,
        reminderConfig: installationReminder,
        reminderConfigs: reminderConfigsForSave,
        followUps,
        attachments: normalizeCrmAttachments(crmDetailDraft.attachments || []),
        executionSpecs,
        siteLocations: normalizeSiteLocations(crmDetailDraft.siteLocations || []),
        notes: crmDetailDraft.notes || "",
        crmNotes: crmDetailDraft.notes || "",
        assignedToUserId: crmDetailDraft.assignedToUserId || "",
        assignedToNameSnapshot: crmDetailDraft.assignedToNameSnapshot || "",
        updatedAt: savedAt,
      };
      selectedCrmDealCacheRef.current = cachedSavedDeal;
      selectedCrmDealIdRef.current = String(targetDealId || activeCrmDeal.id || "");
      crmDetailDraftDealIdRef.current = String(targetDealId || activeCrmDeal.id || "");
      crmDetailDraftDirtyRef.current = false;
      setCrmDetailDraft(savedDraft);
      setCrmDetailsSavedAt(savedAt);
      notify("CRM details saved", "success");
      markExecutionDetailsBaseline(savedDraft, "saved");
      clearExecutionDetailsDraft();
      if (isDevRuntime) {
        const afterSelectedCrmDealId = String(targetDealId || activeCrmDeal.id || "");
        console.log("CRM save selection audit", {
          beforeSelectedCrmDealId,
          beforeSelectedDealName,
          savePayloadLeadId: String(activeCrmDeal.id || ""),
          updatedLeadId: afterSelectedCrmDealId,
          canonicalLeadId:
            canonicalDeal?.crmRecordType === "lead" || targetDealId !== activeCrmDeal.id
              ? afterSelectedCrmDealId
              : "",
          afterSelectedCrmDealId,
          drawerOpen: isCrmDrawerPinned || isCrmDrawerHovered || isCompact,
          foundSelectedAfterRefresh: crmDeals.some(
            (deal) => String(deal.id || "") === afterSelectedCrmDealId
          ),
          crmLeadRecordsCount: crmLeadRecords.length,
        });
      }
    } catch (error) {
      if (isDevRuntime) {
        console.error("Failed to save CRM details:", error);
        setCrmSaveDebugState((prev) => ({
          ...prev,
          lastVerification: {
            ...(prev.lastVerification || {}),
            status: "failed",
            error: error?.message || String(error),
            at: new Date().toISOString(),
          },
        }));
      }
      const errorMessage = String(error?.message || "");
      notify(
        errorMessage.includes("verification failed")
          ? "Save could not be verified. Please try again."
          : "Could not save CRM details. Your changes are still here.",
        "error"
      );
    } finally {
      setIsSavingCrmDetails(false);
    }
  };

  const updateCrmReminderConfig = useCallback((reminderType, patch) => {
    markCrmDetailDraftDirty();
    setCrmDetailDraft((prev) => {
      if (!prev) return prev;

      const nextDate = parseDateInputValue(prev.nextActionDate);
      const installDate = parseDateInputValue(prev.installationDate);
      const currentConfigs = normalizeCrmReminderConfigs(prev.reminderConfigs, {
        nextActionDate: nextDate,
        installationDate: installDate,
      });
      const currentConfig = currentConfigs[reminderType] || {};
      const hasEnabledPatch = Object.prototype.hasOwnProperty.call(patch, "enabled");
      const hasReminderEnabledPatch = Object.prototype.hasOwnProperty.call(
        patch,
        "reminderEnabled"
      );
      const nextEnabled = hasReminderEnabledPatch
        ? patch.reminderEnabled === true
        : hasEnabledPatch
        ? patch.enabled === true
        : currentConfig.enabled === true;
      const nextConfig = {
        ...currentConfig,
        ...patch,
        enabled: nextEnabled,
        reminderEnabled: nextEnabled,
      };
      const nextReminderConfigs = {
        ...currentConfigs,
        [reminderType]: nextConfig,
      };
      const nextDraft = {
        ...prev,
        reminderConfigs: nextReminderConfigs,
      };

      if (reminderType === CRM_REMINDER_TYPE_INSTALLATION) {
        const nextInstallationReminder = buildCanonicalInstallationReminder(
          {
            ...nextDraft,
            installationReminder: nextConfig,
            reminderConfig: nextConfig,
          },
          {
            installationDate: installDate,
            reminderConfigs: nextReminderConfigs,
          }
        );
        nextDraft.installationReminder = nextInstallationReminder;
        nextDraft.reminderConfig = nextInstallationReminder;

        if (isDevRuntime) {
          console.log("INSTALLATION REMINDER TRACE", {
            selectedCrmDealId,
            enabledFromEvent: nextEnabled,
            beforeDraftInstallationReminder: prev.installationReminder,
            afterDraftInstallationReminder: nextDraft.installationReminder,
          });
          console.log("INSTALLATION REMINDER UI CHANGE", {
            selectedCrmDealId,
            installationDate: nextDraft.installationDate,
            installationReminder: nextDraft.installationReminder,
            reminderConfig: nextDraft.reminderConfig,
          });
        }
      }

      return nextDraft;
    });
  }, [
    buildCanonicalInstallationReminder,
    markCrmDetailDraftDirty,
    selectedCrmDealId,
  ]);

  const openCrmFollowUpModal = useCallback(
    (followUp = null) => {
      const actor = getCurrentTaskHistoryActor();
      const currentFollowUps = normalizeCrmFollowUps(
        crmDetailDraft?.followUps || selectedCrmDeal?.followUps || []
      );
      setCrmFollowUpModalState({
        mode: followUp ? "edit" : "create",
        followUp: createCrmFollowUpDraft(
          followUp || {
            title: "",
            description: "",
            order: currentFollowUps.length + 1,
            priority: CRM_FOLLOW_UP_PRIORITY_MEDIUM,
            repeat: "none",
            createdBy: actor,
          }
        ),
      });
    },
    [createCrmFollowUpDraft, crmDetailDraft, getCurrentTaskHistoryActor, selectedCrmDeal]
  );

  const closeCrmFollowUpModal = useCallback(() => {
    setCrmFollowUpModalState(null);
    setCrmFollowUpAttachmentUploadKey("");
  }, []);

  const updateCrmFollowUpModalDraft = useCallback((patch) => {
    setCrmFollowUpModalState((prev) =>
      prev
        ? {
            ...prev,
            followUp: {
              ...prev.followUp,
              ...patch,
            },
          }
        : prev
    );
  }, []);

  const resolveCrmTaskWriteTarget = useCallback(
    (dealLike = {}, fallbackId = "") => {
      const candidateLeadId = String(
        dealLike?.crmRecordType === "lead"
          ? dealLike?.id || fallbackId || ""
          : dealLike?.leadId ||
              dealLike?.crmLeadId ||
              dealLike?.canonicalLeadId ||
              dealLike?.primaryLeadId ||
              dealLike?.crm?.leadId ||
              ""
      ).trim();

      if (candidateLeadId) {
        const sourceLead =
          crmLeadRecords.find(
            (lead) => String(lead.id || "") === String(candidateLeadId)
          ) ||
          (dealLike?.crmRecordType === "lead" && String(dealLike.id || "") === candidateLeadId
            ? dealLike
            : null);

        return {
          targetId: candidateLeadId,
          sourceLead,
          forceLead: true,
          firestorePath: workspaceUid
            ? `users/${workspaceUid}/crm_leads/${candidateLeadId}`
            : "",
        };
      }

      const fallbackDealId = String(
        fallbackId ||
          dealLike?.id ||
          dealLike?.dealId ||
          dealLike?.quoteId ||
          dealLike?.primaryQuoteId ||
          ""
      ).trim();

      return {
        targetId: fallbackDealId,
        sourceLead: null,
        forceLead: false,
        firestorePath:
          workspaceUid && fallbackDealId
            ? `users/${workspaceUid}/quotations/${fallbackDealId}`
            : "",
      };
    },
    [crmLeadRecords, workspaceUid]
  );

  const resolveDailyTaskWriteTarget = useCallback(
    (task = {}) => {
      const explicitLeadId = String(task?.leadId || task?.crmLeadId || "").trim();
      if (explicitLeadId) {
        return resolveCrmTaskWriteTarget(
          { ...(task?.deal || {}), leadId: explicitLeadId },
          explicitLeadId
        );
      }

      return resolveCrmTaskWriteTarget(
        task?.deal || {},
        task?.dealId || task?.quoteId || task?.sourceDealId || ""
      );
    },
    [resolveCrmTaskWriteTarget]
  );

  const persistCrmFollowUpsForDeal = useCallback(
    async (dealLike, nextFollowUps, successMessage = "Follow-up saved") => {
      if (!workspaceUid || !dealLike?.id) {
        throw new Error("Missing workspace or CRM deal for follow-up save.");
      }

      const normalizedFollowUps = normalizeCrmFollowUps(nextFollowUps).map((followUp) =>
        sanitizeCrmFirestoreValue(followUp)
      );
      const canonicalDeal =
        dealLike.crmRecordType === "quote" && normalizedFollowUps.length > 0
          ? await ensureCrmLeadForExecutionSpecs(dealLike)
          : dealLike;
      const targetDealId = canonicalDeal?.id || dealLike.id;
      if (!targetDealId) {
        throw new Error("Missing CRM lead id for follow-up save.");
      }
      const primaryFollowUp = getPrimaryPendingCrmFollowUp(normalizedFollowUps);
      const primaryFollowUpDueDate = primaryFollowUp?.dueDate
        ? parseDateInputValue(primaryFollowUp.dueDate)
        : null;

      setCrmDetailDraft((prev) =>
        prev
          ? {
              ...prev,
              followUps: normalizedFollowUps.map((followUp) =>
                createCrmFollowUpDraft(followUp)
              ),
              nextAction: primaryFollowUp?.title || "",
              nextActionDate: primaryFollowUp?.dueDate || "",
            }
          : prev
      );

      if (
        canonicalDeal?.id &&
        canonicalDeal.id !== dealLike.id &&
        selectedCrmDealId === dealLike.id
      ) {
        setSelectedCrmDealId(canonicalDeal.id);
      }

      await saveCrmFieldsForDeal(targetDealId, {
        followUps: normalizedFollowUps,
        nextAction: primaryFollowUp?.title || "",
        nextActionDate: primaryFollowUpDueDate,
      }, {
        forceLead: canonicalDeal?.crmRecordType === "lead" || targetDealId !== dealLike.id,
        sourceLead:
          canonicalDeal?.crmRecordType === "lead" || targetDealId !== dealLike.id
            ? { ...canonicalDeal, id: targetDealId, crmRecordType: "lead" }
            : null,
        createIfMissing:
          canonicalDeal?.crmRecordType === "lead" || targetDealId !== dealLike.id,
        skipReminderSync: true,
        syncQuote: false,
      });

      let reminderSyncFailed = false;
      try {
        await syncReminderDocsForDeal(
          {
            ...dealLike,
            ...canonicalDeal,
            id: targetDealId,
            crmRecordType: "lead",
            followUps: normalizedFollowUps,
            nextAction: primaryFollowUp?.title || "",
            nextActionDate: primaryFollowUpDueDate,
          },
          {
            followUps: normalizedFollowUps,
            nextActionDate: primaryFollowUpDueDate,
          }
        );
      } catch (error) {
        reminderSyncFailed = true;
        console.error("Follow-up saved but reminder sync failed:", error, {
          workspaceUid,
          leadId: targetDealId,
          followUps: normalizedFollowUps,
        });
      }

      setCrmDetailsSavedAt(new Date());
      notify(
        reminderSyncFailed
          ? "Follow-up saved, but reminder could not be scheduled."
          : successMessage,
        reminderSyncFailed ? "warning" : "success"
      );
      return canonicalDeal || dealLike;
    },
    [
      createCrmFollowUpDraft,
      ensureCrmLeadForExecutionSpecs,
      notify,
      saveCrmFieldsForDeal,
      selectedCrmDealId,
      syncReminderDocsForDeal,
      workspaceUid,
    ]
  );

  const saveCrmFollowUpModal = useCallback(async () => {
    if (!selectedCrmDeal || !crmFollowUpModalState?.followUp) return;

    let resolvedLeadId =
      selectedCrmDeal.crmRecordType === "lead"
        ? String(selectedCrmDeal.id || "")
        : String(selectedCrmDeal.crm?.leadId || selectedCrmDeal.leadId || "");
    let followUpDraftForLog = crmFollowUpModalState.followUp;
    const actor = getCurrentTaskHistoryActor();
    const draft = crmFollowUpModalState.followUp;
    const title = String(draft.title || "").trim();
    const dueDate = String(draft.dueDate || "").trim();
    const dueTimeInput = String(draft.dueTime || "").trim();
    const dueTime = parseTimeInputValue(dueTimeInput);
    const explicitReminderEnabled = normalizeBooleanInput(
      draft.reminderEnabled ?? draft.enabled
    );
    const reminderEnabled =
      explicitReminderEnabled !== undefined ? explicitReminderEnabled : Boolean(dueDate);

    if (!workspaceUid) {
      notify("Please sign in again to save this follow-up.", "warning");
      return;
    }

    if (!title) {
      notify("Please add a follow-up title.", "warning");
      return;
    }

    const parsedDueDate = parseDateInputValue(dueDate);
    if (dueDate && (!parsedDueDate || toDateInputValue(parsedDueDate) !== dueDate)) {
      notify("Please choose a valid due date.", "warning");
      return;
    }

    if (dueTimeInput && !/^\d{2}:\d{2}$/.test(dueTimeInput)) {
      notify("Please choose a valid due time.", "warning");
      return;
    }

    if (reminderEnabled && !dueDate) {
      notify("Choose a due date or turn the reminder off.", "warning");
      return;
    }

    try {
      const currentFollowUps = normalizeCrmFollowUps(
        crmDetailDraft?.followUps || selectedCrmDeal.followUps || [],
        {
          nextAction: crmDetailDraft?.nextAction || selectedCrmDeal.nextAction || "",
          nextActionDate:
            parseDateInputValue(crmDetailDraft?.nextActionDate) ||
            selectedCrmDeal.nextActionDate ||
            null,
          reminderConfig:
            crmDetailDraft?.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] ||
            selectedCrmDeal.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] ||
            null,
        }
      );

      const nextFollowUp = sanitizeCrmFirestoreValue({
        ...draft,
        id: String(draft.id || "").trim() || createClientId("crm-follow-up"),
        title,
        description: String(draft.description || ""),
        dueDate,
        dueTime,
        repeat: String(draft.repeat || "none"),
        reminderEnabled:
          draft.status === CRM_FOLLOW_UP_STATUS_DONE ? false : reminderEnabled,
        order: Number.isFinite(Number(draft.order)) && Number(draft.order) > 0
          ? Number(draft.order)
          : currentFollowUps.length + 1,
        priority: CRM_FOLLOW_UP_PRIORITY_OPTIONS.some(
          (option) => option.key === String(draft.priority || "").trim().toLowerCase()
        )
          ? String(draft.priority || "").trim().toLowerCase()
          : CRM_FOLLOW_UP_PRIORITY_MEDIUM,
        reminderId: String(draft.reminderId || "").trim() || String(draft.id || "").trim(),
        status:
          draft.status === CRM_FOLLOW_UP_STATUS_DONE
            ? CRM_FOLLOW_UP_STATUS_DONE
            : CRM_FOLLOW_UP_STATUS_PENDING,
        createdAt: draft.createdAt || new Date(),
        updatedAt: new Date(),
        createdBy:
          draft.createdBy?.uid || draft.createdBy?.name || draft.createdBy?.email
            ? draft.createdBy
            : actor,
        completedAt:
          draft.status === CRM_FOLLOW_UP_STATUS_DONE
            ? draft.completedAt || new Date()
            : null,
        completedBy:
          draft.status === CRM_FOLLOW_UP_STATUS_DONE
            ? actor
            : buildCrmFollowUpActor({}),
        attachments: normalizeCrmAttachments(draft.attachments || []),
      });
      followUpDraftForLog = nextFollowUp;

      const exists = currentFollowUps.some((followUp) => followUp.id === nextFollowUp.id);
      const nextFollowUps = exists
        ? currentFollowUps.map((followUp) =>
            followUp.id === nextFollowUp.id ? nextFollowUp : followUp
          )
        : [nextFollowUp, ...currentFollowUps];

      const savedDeal = await persistCrmFollowUpsForDeal(
        selectedCrmDeal,
        nextFollowUps,
        exists ? "Follow-up saved" : "Follow-up added"
      );
      resolvedLeadId = savedDeal?.id || resolvedLeadId;
      closeCrmFollowUpModal();
    } catch (error) {
      console.error("Failed to save follow-up", error, {
        workspaceUid,
        leadId: resolvedLeadId,
        dealId: selectedCrmDeal?.id,
        dealRecordType: selectedCrmDeal?.crmRecordType,
        followUpDraft: followUpDraftForLog,
      });
      notify("Could not save follow-up. Please try again.", "error");
    }
  }, [
    closeCrmFollowUpModal,
    crmDetailDraft,
    crmFollowUpModalState,
    buildCrmFollowUpActor,
    getCurrentTaskHistoryActor,
    notify,
    persistCrmFollowUpsForDeal,
    selectedCrmDeal,
    workspaceUid,
  ]);

  const markCrmFollowUpDone = useCallback(
    async (followUpIdentifier, dealOverride = null, options = {}) => {
      const sourceDeal = dealOverride || selectedCrmDeal;
      if (!sourceDeal || !followUpIdentifier) {
        console.error("Failed to mark done: missing follow-up or deal", {
          followUpIdentifier,
          dealId: sourceDeal?.id,
        });
        notify("Could not mark task as done. Please try again.", "error");
        return;
      }
      if (!workspaceUid) {
        console.error("Failed to mark done: missing workspace");
        notify("Please sign in again to mark this task as done.", "error");
        return;
      }

      const actor = getCurrentTaskHistoryActor();
      const reminder = options?.reminder || null;
      const identifier = String(followUpIdentifier || "").trim();
      const useDraft = sourceDeal.id === selectedCrmDeal?.id;
      const currentFollowUps = normalizeCrmFollowUps(
        useDraft
          ? crmDetailDraft?.followUps || sourceDeal.followUps || []
          : sourceDeal.followUps || [],
        {
          nextAction: useDraft
            ? crmDetailDraft?.nextAction || sourceDeal.nextAction || ""
            : sourceDeal.nextAction || "",
          nextActionDate: useDraft
            ? parseDateInputValue(crmDetailDraft?.nextActionDate) ||
              sourceDeal.nextActionDate ||
              null
            : sourceDeal.nextActionDate || null,
          reminderConfig: useDraft
            ? crmDetailDraft?.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] ||
              sourceDeal.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] ||
              null
            : sourceDeal.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] || null,
        }
      );
      const targetFollowUp = currentFollowUps.find(
        (followUp) =>
          String(followUp.id || "") === identifier ||
          String(followUp.reminderId || "") === identifier ||
          (reminder && doesCrmReminderMatchFollowUp(reminder, followUp))
      );

      if (!targetFollowUp) {
        console.error("Failed to mark done: follow-up not found", {
          followUpIdentifier,
          reminderId: reminder?.reminderId,
          dealId: sourceDeal.id,
        });
        notify("We could not find this task. Please open the deal and check it manually.", "error");
        return;
      }

      const nextFollowUps = currentFollowUps.map((followUp) =>
        followUp.id === targetFollowUp.id
          ? {
              ...followUp,
              reminderId: followUp.reminderId || followUp.id,
              reminderEnabled: false,
              status: CRM_FOLLOW_UP_STATUS_DONE,
              completedAt: new Date(),
              completedBy: actor,
              updatedAt: new Date(),
            }
          : followUp
      );

      const doneKey = `followup:${sourceDeal.id || sourceDeal.leadId || "unknown"}:${targetFollowUp.id}`;
      let writeAudit = {
        path: workspaceUid
          ? `users/${workspaceUid}/crm_leads/${sourceDeal.id || sourceDeal.leadId || ""}`
          : "",
        payload: { followUps: nextFollowUps },
        dealId: sourceDeal.id || "",
        followUpId: targetFollowUp.id || "",
      };
      setMarkingDoneKey(doneKey);

      try {
        const canonicalDeal = await persistCrmFollowUpsForDeal(
          sourceDeal,
          nextFollowUps,
          options?.successMessage || "Task marked as done"
        );
        const targetDealId = canonicalDeal?.id || sourceDeal.id;
        const completedFollowUp =
          normalizeCrmFollowUps(nextFollowUps).find(
            (followUp) => followUp.id === targetFollowUp.id
          ) || {
            ...targetFollowUp,
            reminderId: targetFollowUp.reminderId || targetFollowUp.id,
            status: CRM_FOLLOW_UP_STATUS_DONE,
          };
        const canonicalReminderId = buildFollowUpReminderDocId(
          targetDealId,
          completedFollowUp
        );
        const reminderIdsToComplete = Array.from(
          new Set([canonicalReminderId, reminder?.id].filter(Boolean))
        );

        await Promise.all(
          reminderIdsToComplete.map(async (reminderDocId) => {
            const reminderPayload = {
              enabled: false,
              reminderEnabled: false,
              active: false,
              nextTriggerAt: null,
              status: CRM_REMINDER_STATUS_DONE,
              completedAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            };
            writeAudit = {
              path: `users/${workspaceUid}/crm_reminders/${reminderDocId}`,
              payload: reminderPayload,
              dealId: targetDealId,
              followUpId: targetFollowUp.id || "",
            };
            await setDoc(
              doc(db, "users", workspaceUid, "crm_reminders", reminderDocId),
              reminderPayload,
              { merge: true }
            );
            await markDueReminderCompleted({
              leadId: targetDealId,
              reminderType: CRM_REMINDER_TYPE_FOLLOW_UP,
              sourceId: completedFollowUp.id || completedFollowUp.reminderId || targetFollowUp.id || "",
              crmReminderId: reminderDocId,
            });
            applyCrmReminderLocalPatch(reminderDocId, {
              ...(reminderDocId === reminder?.id ? reminder : {}),
              id: reminderDocId,
              enabled: false,
              reminderEnabled: false,
              active: false,
              nextTriggerAt: null,
              status: CRM_REMINDER_STATUS_DONE,
              completedAt: new Date(),
              updatedAt: new Date(),
            });
          })
        );

        await writeTaskHistoryRecord({
          taskType: TASK_HISTORY_TYPE_DEAL_ACTION,
          sourceTaskId: targetFollowUp.id,
          dealLike: canonicalDeal || sourceDeal,
          taskTitle: targetFollowUp.title || "Follow-up",
          taskDescription: targetFollowUp.description || "",
          dueDate:
            combineReminderDateTime(targetFollowUp.dueDate, targetFollowUp.dueTime) ||
            parseDateInputValue(targetFollowUp.dueDate) ||
            null,
          sourceModule: TASK_HISTORY_SOURCE_CRM,
          originalPayload: {
            followUp: targetFollowUp,
            completedBy: actor,
          },
        });
      } catch (error) {
        logCrmTaskDoneFailure("markCrmFollowUpDone", error, {
          ...writeAudit,
          workspaceUid,
          selectedCrmDealId,
        });
        notify("Could not mark task as done. Please try again.", "error");
      } finally {
        setMarkingDoneKey((current) => (current === doneKey ? "" : current));
      }
    },
    [
      applyCrmReminderLocalPatch,
      buildFollowUpReminderDocId,
      combineReminderDateTime,
      crmDetailDraft,
      getCurrentTaskHistoryActor,
      markDueReminderCompleted,
      notify,
      persistCrmFollowUpsForDeal,
      selectedCrmDeal,
      selectedCrmDealId,
      workspaceUid,
      writeTaskHistoryRecord,
    ]
  );

  const deleteCrmFollowUp = useCallback(
    (followUpId) => {
      if (!selectedCrmDeal || !followUpId) return;

      openConfirmDialog({
        title: "Delete follow-up?",
        description: "This follow-up and its reminder will be removed from this deal.",
        confirmLabel: "Delete",
        cancelLabel: "Cancel",
        tone: "danger",
        onConfirm: async () => {
          try {
            const currentFollowUps = normalizeCrmFollowUps(
              crmDetailDraft?.followUps || selectedCrmDeal.followUps || []
            );
            const targetFollowUp = currentFollowUps.find(
              (followUp) => followUp.id === followUpId
            );

            await Promise.all(
              normalizeCrmAttachments(targetFollowUp?.attachments || []).map((attachment) =>
                attachment.fileId
                  ? archiveManagedStorageFile({
                      workspaceUid,
                      fileId: attachment.fileId,
                      removedBy: auth.currentUser?.uid || "",
                      removalReason: "crm_follow_up_deleted",
                      linkedRecords: [],
                    }).catch(() => {})
                  : Promise.resolve()
              )
            );

            const nextFollowUps = currentFollowUps.filter(
              (followUp) => followUp.id !== followUpId
            );
            await persistCrmFollowUpsForDeal(
              selectedCrmDeal,
              nextFollowUps,
              "Follow-up deleted"
            );
          } catch (error) {
            console.error("Failed to delete follow-up:", error);
            notify("Could not delete follow-up. Please try again.", "error");
          }
        },
      });
    },
    [
      crmDetailDraft,
      notify,
      openConfirmDialog,
      persistCrmFollowUpsForDeal,
      selectedCrmDeal,
      workspaceUid,
    ]
  );

  const uploadCrmFollowUpAttachments = useCallback(
    async (filesInput) => {
      const files = Array.isArray(filesInput)
        ? filesInput.filter(Boolean)
        : filesInput
        ? [filesInput]
        : [];

      if (
        !workspaceUid ||
        !selectedCrmDeal ||
        !crmFollowUpModalState?.followUp ||
        files.length === 0
      ) {
        return;
      }

      const followUp = crmFollowUpModalState.followUp;
      const canonicalDeal =
        selectedCrmDeal.crmRecordType === "quote"
          ? await ensureCrmLeadForExecutionSpecs(selectedCrmDeal)
          : selectedCrmDeal;
      const targetDealId = canonicalDeal?.id || selectedCrmDeal.id;
      const uploadKey = `${selectedCrmDeal.id}:${followUp.id}`;
      setCrmFollowUpAttachmentUploadKey(uploadKey);

      try {
        const uploadedAttachments = [];

        for (const file of files) {
          const attachmentId = createClientId("crm-follow-up-attachment");
          const uploadTimestamp = Date.now();
          const storagePath = buildCrmManagedAttachmentStoragePath({
            workspaceUid,
            leadId: targetDealId,
            attachmentId,
            fileName: file.name || "attachment",
            timestamp: uploadTimestamp,
          });
          const managedImage = await uploadManagedImage({
            workspaceUid,
            file,
            ownerType: "crm",
            ownerId: targetDealId,
            leadId: targetDealId,
            imageId: attachmentId,
            fullPath: storagePath,
            uploadedBy: user?.uid || "",
            metadata: {
              dealId: targetDealId,
              followUpId: followUp.id,
              uploadedBy: user?.email || user?.uid || "",
            },
          });
          uploadedAttachments.push(
            normalizeCrmAttachments([
              {
                ...managedImage,
                id: attachmentId,
                name: managedImage.originalFilename || file.name || "Attachment",
                filename: managedImage.filename || file.name || "attachment",
                url: managedImage.downloadURL,
                storagePath: managedImage.fullPath,
                contentType: managedImage.contentType,
                sizeBytes: managedImage.sizeBytes || 0,
                uploadedAt: managedImage.uploadedAt || new Date().toISOString(),
                uploadedByUid: user?.uid || "",
                uploadedByName:
                  currentWorkspaceMember?.name ||
                  user?.displayName ||
                  user?.email ||
                  user?.uid ||
                  "",
              },
            ])[0]
          );
        }

        updateCrmFollowUpModalDraft({
          attachments: normalizeCrmAttachments([
            ...(followUp.attachments || []),
            ...uploadedAttachments,
          ]),
        });
        notify("Attachment added to follow-up", "success");
      } catch (error) {
        console.error("Failed to upload follow-up attachment:", error);
        notify(error?.code === "storage-limit-reached" || error?.code === "invalid-image-file"
          ? error.message
          : "Could not upload follow-up attachment. Please try again.", "error");
      } finally {
        setCrmFollowUpAttachmentUploadKey("");
      }
    },
    [
      crmFollowUpModalState,
      currentWorkspaceMember?.name,
      ensureCrmLeadForExecutionSpecs,
      notify,
      selectedCrmDeal,
      updateCrmFollowUpModalDraft,
      user,
      workspaceUid,
    ]
  );

  const removeCrmFollowUpAttachment = useCallback(
    async (attachmentId) => {
      const followUp = crmFollowUpModalState?.followUp;
      if (!followUp || !attachmentId) return;

      const currentAttachments = normalizeCrmAttachments(followUp.attachments || []);
      const targetAttachment = currentAttachments.find(
        (attachment) => attachment.id === attachmentId
      );

      try {
        if (targetAttachment?.fileId) {
          await archiveManagedStorageFile({
            workspaceUid,
            fileId: targetAttachment.fileId,
            removedBy: auth.currentUser?.uid || "",
            removalReason: "crm_follow_up_attachment_removed",
            linkedRecords: [],
          });
        }

        updateCrmFollowUpModalDraft({
          attachments: currentAttachments.filter(
            (attachment) => attachment.id !== attachmentId
          ),
        });
        notify("Attachment removed", "success");
      } catch (error) {
        console.error("Failed to remove follow-up attachment:", error);
        notify("Could not remove follow-up attachment. Please try again.", "error");
      }
    },
    [crmFollowUpModalState, notify, updateCrmFollowUpModalDraft, workspaceUid]
  );

  const uploadCrmExecutionSpecAttachments = useCallback(
    async (itemId, filesInput) => {
      const files = Array.isArray(filesInput)
        ? filesInput.filter(Boolean)
        : filesInput
        ? [filesInput]
        : [];

      if (!workspaceUid || !selectedCrmDeal || !crmDetailDraft || !itemId || files.length === 0) {
        return;
      }

      const currentSpecs = serializeExecutionSpecDraftItems(
        crmDetailDraft.executionSpecs || []
      );
      const targetSpec = currentSpecs.find((item) => item.id === itemId);
      if (!targetSpec) return;

      const canonicalDeal =
        selectedCrmDeal.crmRecordType === "quote"
          ? await ensureCrmLeadForExecutionSpecs(selectedCrmDeal, currentSpecs)
          : selectedCrmDeal;
      const targetDealId = canonicalDeal?.id || selectedCrmDeal.id;
      if (targetDealId && targetDealId !== selectedCrmDeal.id) {
        setSelectedCrmDealId(targetDealId);
      }
      const uploadKey = `${targetDealId}:${itemId}`;
      setCrmExecutionAttachmentUploadKey(uploadKey);

      try {
        const uploadedAttachments = [];

        for (const file of files) {
          const attachmentId = createClientId("exec-attachment");
          const uploadTimestamp = Date.now();
          const storagePath = buildCrmManagedAttachmentStoragePath({
            workspaceUid,
            leadId: targetDealId,
            attachmentId,
            fileName: file.name || "attachment",
            timestamp: uploadTimestamp,
          });
          const managedImage = await uploadManagedImage({
            workspaceUid,
            file,
            ownerType: "crm",
            ownerId: targetDealId,
            leadId: targetDealId,
            imageId: attachmentId,
            fullPath: storagePath,
            uploadedBy: user?.uid || "",
            metadata: {
              dealId: targetDealId,
              itemId,
              uploadedBy: user?.email || user?.uid || "",
            },
          });
          uploadedAttachments.push(
            normalizeExecutionSpecAttachments([
              {
                ...managedImage,
                id: attachmentId,
                name: managedImage.originalFilename || file.name || "Attachment",
                filename: managedImage.filename || file.name || "attachment",
                url: managedImage.downloadURL,
                path: managedImage.fullPath,
                storagePath: managedImage.fullPath,
                contentType: managedImage.contentType,
                sizeBytes: managedImage.sizeBytes || 0,
                uploadedAt: managedImage.uploadedAt || new Date().toISOString(),
                uploadedBy: user?.uid || "",
                uploadedByName: user?.email || "",
              },
            ])[0]
          );
        }

        const nextSpecs = stampExecutionSpecs(currentSpecs.map((item) =>
          item.id === itemId
            ? {
                ...item,
                attachments: normalizeExecutionSpecAttachments([
                  ...(item.attachments || []),
                  ...uploadedAttachments,
                ]),
              }
            : item
        ));

        setCrmDetailDraft((prev) =>
          prev
            ? {
                ...prev,
                executionSpecs: hydrateExecutionSpecDraftItems(nextSpecs),
              }
            : prev
        );

        await saveCrmFieldsForDeal(
          targetDealId,
          {
            executionSpecs: nextSpecs,
          },
          {
            forceLead: canonicalDeal?.crmRecordType === "lead" || targetDealId !== selectedCrmDeal.id,
            sourceLead:
              canonicalDeal?.crmRecordType === "lead" || targetDealId !== selectedCrmDeal.id
                ? { ...canonicalDeal, id: targetDealId, crmRecordType: "lead" }
                : null,
            createIfMissing:
              canonicalDeal?.crmRecordType === "lead" || targetDealId !== selectedCrmDeal.id,
          }
        );
      } catch (error) {
        console.error("Failed to upload execution spec attachment:", error);
        notify(error?.code === "storage-limit-reached" || error?.code === "invalid-image-file"
          ? error.message
          : "Could not upload execution attachment. Please try again.", "error");
      } finally {
        setCrmExecutionAttachmentUploadKey("");
      }
    },
    [
      crmDetailDraft,
      ensureCrmLeadForExecutionSpecs,
      hydrateExecutionSpecDraftItems,
      saveCrmFieldsForDeal,
      selectedCrmDeal,
      serializeExecutionSpecDraftItems,
      stampExecutionSpecs,
      notify,
      user,
      workspaceUid,
    ]
  );

  const removeCrmExecutionSpecAttachment = useCallback(
    async (itemId, attachmentId) => {
      if (!selectedCrmDeal || !crmDetailDraft) return;

      const currentSpecs = serializeExecutionSpecDraftItems(
        crmDetailDraft.executionSpecs || []
      );
      const targetSpec = currentSpecs.find((item) => item.id === itemId);
      if (!targetSpec) return;

      const targetAttachment = normalizeExecutionSpecAttachments(
        targetSpec.attachments || []
      ).find((attachment) => attachment.id === attachmentId);

      if (!targetAttachment) return;

      if (targetAttachment.fileId) {
        archiveManagedStorageFile({
          workspaceUid,
          fileId: targetAttachment.fileId,
          removedBy: auth.currentUser?.uid || "",
          removalReason: "crm_execution_spec_attachment_removed",
          linkedRecords: [],
        }).catch((error) => {
          if (isDevelopmentMode()) {
            console.warn("CRM_EXECUTION_SPEC_ATTACHMENT_ARCHIVE_FAILED", {
              fileId: targetAttachment.fileId,
              code: error?.code,
              message: error?.message,
            });
          }
        });
      }

      const canonicalDeal =
        selectedCrmDeal.crmRecordType === "quote"
          ? await ensureCrmLeadForExecutionSpecs(selectedCrmDeal, currentSpecs)
          : selectedCrmDeal;
      const targetDealId = canonicalDeal?.id || selectedCrmDeal.id;
      if (targetDealId && targetDealId !== selectedCrmDeal.id) {
        setSelectedCrmDealId(targetDealId);
      }
      const nextSpecs = stampExecutionSpecs(currentSpecs.map((item) =>
        item.id === itemId
          ? {
              ...item,
              attachments: normalizeExecutionSpecAttachments(item.attachments || []).filter(
                (attachment) => attachment.id !== attachmentId
              ),
            }
          : item
      ));

      updateCrmDetailDraft((prev) =>
        prev
          ? {
              ...prev,
              executionSpecs: hydrateExecutionSpecDraftItems(nextSpecs),
            }
          : prev
      );

      await saveCrmFieldsForDeal(
        targetDealId,
        {
          executionSpecs: nextSpecs,
        },
        {
          forceLead: canonicalDeal?.crmRecordType === "lead" || targetDealId !== selectedCrmDeal.id,
          sourceLead:
            canonicalDeal?.crmRecordType === "lead" || targetDealId !== selectedCrmDeal.id
              ? { ...canonicalDeal, id: targetDealId, crmRecordType: "lead" }
              : null,
          createIfMissing:
            canonicalDeal?.crmRecordType === "lead" || targetDealId !== selectedCrmDeal.id,
        }
      );
    },
    [
      crmDetailDraft,
      ensureCrmLeadForExecutionSpecs,
      hydrateExecutionSpecDraftItems,
      saveCrmFieldsForDeal,
      selectedCrmDeal,
      serializeExecutionSpecDraftItems,
      stampExecutionSpecs,
      updateCrmDetailDraft,
      workspaceUid,
    ]
  );

  const uploadCrmDealAttachments = useCallback(
    async (dealId, filesInput) => {
      const files = Array.isArray(filesInput)
        ? filesInput.filter(Boolean)
        : filesInput
        ? [filesInput]
        : [];

      if (!workspaceUid || !dealId || files.length === 0) {
        return;
      }

      const sourceDeal = crmDeals.find((deal) => deal.id === dealId) || selectedCrmDeal;
      if (!sourceDeal) return;

      setCrmAttachmentUploadDealId(dealId);

      try {
        const canonicalDeal =
          sourceDeal.crmRecordType === "quote"
            ? await ensureCrmLeadForExecutionSpecs(sourceDeal)
            : sourceDeal;
        const targetDeal = canonicalDeal || sourceDeal;
        const targetDealId = targetDeal.id || dealId;
        const targetRecordType = targetDeal.crmRecordType || "lead";

        if (targetDealId && targetDealId !== dealId) {
          setSelectedCrmDealId(targetDealId);
        }

        const uploadedAttachments = [];

        for (const file of files) {
          const attachmentId = createClientId("crm-attachment");
          const uploadTimestamp = Date.now();
          const storagePath = buildCrmManagedAttachmentStoragePath({
            workspaceUid,
            leadId: targetDealId,
            attachmentId,
            fileName: file.name || "attachment",
            timestamp: uploadTimestamp,
          });
          const managedImage = await uploadManagedImage({
            workspaceUid,
            file,
            ownerType: "crm",
            ownerId: targetDealId,
            leadId: targetDealId,
            imageId: attachmentId,
            fullPath: storagePath,
            uploadedBy: user?.uid || "",
            metadata: {
              dealId: targetDealId,
              recordType: targetRecordType,
              attachmentId,
              uploadedBy: user?.email || user?.uid || "",
            },
          });
          uploadedAttachments.push(
            normalizeCrmAttachments([
              {
                ...managedImage,
                id: attachmentId,
                name: managedImage.originalFilename || file.name,
                filename: managedImage.filename || file.name,
                url: managedImage.downloadURL,
                storagePath: managedImage.fullPath,
                contentType: managedImage.contentType,
                sizeBytes: managedImage.sizeBytes || 0,
                uploadedAt: managedImage.uploadedAt || new Date().toISOString(),
                uploadedByUid: user?.uid || "",
                uploadedByName:
                  currentWorkspaceMember?.name ||
                  user?.displayName ||
                  user?.email ||
                  user?.uid ||
                  "",
              },
            ])[0]
          );
        }

        const nextAttachments = normalizeCrmAttachments([
          ...(targetDeal.attachments || []),
          ...uploadedAttachments,
        ]);

        setCrmDetailDraft((prev) =>
          (selectedCrmDealId === dealId || selectedCrmDealId === targetDealId) && prev
            ? {
                ...prev,
                attachments: nextAttachments,
              }
            : prev
        );

        await saveCrmFieldsForDeal(
          targetDealId,
          {
            attachments: nextAttachments,
          },
          {
            forceLead: targetRecordType === "lead" || targetDealId !== dealId,
            sourceLead:
              targetRecordType === "lead" || targetDealId !== dealId
                ? { ...targetDeal, id: targetDealId, crmRecordType: "lead" }
                : null,
            createIfMissing: targetRecordType === "lead" || targetDealId !== dealId,
          }
        );
        setCrmDetailsSavedAt(new Date());
        notify("Attachment uploaded", "success");
      } catch (error) {
        console.error("Failed to upload CRM attachment:", error);
        notify(error?.code === "storage-limit-reached" || error?.code === "invalid-image-file"
          ? error.message
          : "Could not upload the attachment. Please try again.", "error");
      } finally {
        setCrmAttachmentUploadDealId("");
      }
    },
    [
      crmDeals,
      currentWorkspaceMember?.name,
      ensureCrmLeadForExecutionSpecs,
      notify,
      saveCrmFieldsForDeal,
      selectedCrmDeal,
      selectedCrmDealId,
      user,
      workspaceUid,
    ]
  );

  const removeCrmDealAttachment = useCallback(
    async (dealId, attachmentId) => {
      const sourceDeal = crmDeals.find((deal) => deal.id === dealId) || selectedCrmDeal;
      if (!workspaceUid || !sourceDeal) return;

      try {
        const canonicalDeal =
          sourceDeal.crmRecordType === "quote"
            ? await ensureCrmLeadForExecutionSpecs(sourceDeal)
            : sourceDeal;
        const targetDeal = canonicalDeal || sourceDeal;
        const targetDealId = targetDeal.id || dealId;
        const targetRecordType = targetDeal.crmRecordType || "lead";
        const currentAttachments = normalizeCrmAttachments(targetDeal.attachments || []);
        const targetAttachment = currentAttachments.find(
          (attachment) => attachment.id === attachmentId
        );
        const nextAttachments = currentAttachments.filter(
          (attachment) => attachment.id !== attachmentId
        );

        if (targetAttachment?.fileId) {
          await archiveManagedStorageFile({
            workspaceUid,
            fileId: targetAttachment.fileId,
            removedBy: auth.currentUser?.uid || "",
            removalReason: "crm_deal_attachment_removed",
            linkedRecords: [],
          });
        }

        setCrmDetailDraft((prev) =>
          (selectedCrmDealId === dealId || selectedCrmDealId === targetDealId) && prev
            ? {
                ...prev,
                attachments: nextAttachments,
              }
            : prev
        );

        if (targetDealId && targetDealId !== dealId) {
          setSelectedCrmDealId(targetDealId);
        }

        await saveCrmFieldsForDeal(
          targetDealId,
          {
            attachments: nextAttachments,
          },
          {
            forceLead: targetRecordType === "lead" || targetDealId !== dealId,
            sourceLead:
              targetRecordType === "lead" || targetDealId !== dealId
                ? { ...targetDeal, id: targetDealId, crmRecordType: "lead" }
                : null,
            createIfMissing: targetRecordType === "lead" || targetDealId !== dealId,
          }
        );
        setCrmDetailsSavedAt(new Date());
        notify("Attachment removed", "success");
      } catch (error) {
        console.error("Failed to remove CRM attachment:", error);
        notify("Could not remove the attachment. Please try again.", "error");
      }
    },
    [
      crmDeals,
      ensureCrmLeadForExecutionSpecs,
      notify,
      saveCrmFieldsForDeal,
      selectedCrmDeal,
      selectedCrmDealId,
      workspaceUid,
    ]
  );

  const dismissCrmReminder = useCallback(
    async (reminder) => {
      if (!workspaceUid || !reminder?.id) return;

      if (reminder.reminderType === CRM_REMINDER_TYPE_FOLLOW_UP) {
        const { followUp } = resolveCrmReminderFollowUp(reminder);
        if (!followUp || followUp.status === CRM_FOLLOW_UP_STATUS_DONE) {
          await setDoc(
            doc(db, "users", workspaceUid, "crm_reminders", reminder.id),
            {
              enabled: false,
              reminderEnabled: false,
              active: false,
              nextTriggerAt: null,
              status: CRM_REMINDER_STATUS_DONE,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          ).catch((error) => {
            console.error("Failed to dismiss stale reminder:", error);
          });
          applyCrmReminderLocalPatch(reminder.id, {
            ...reminder,
            enabled: false,
            reminderEnabled: false,
            active: false,
            nextTriggerAt: null,
            status: CRM_REMINDER_STATUS_DONE,
            updatedAt: new Date(),
          });
          return;
        }
      }

      const repeatMinutes =
        reminder.reminderType === CRM_REMINDER_TYPE_FOLLOW_UP
          ? getCrmFollowUpRepeatMinutes(reminder.repeatKey)
          : getCrmReminderRepeatMinutes(reminder.repeatKey);
      const snoozeMinutes = repeatMinutes > 0 ? repeatMinutes : 30;
      const nextTriggerAt = new Date(Date.now() + snoozeMinutes * 60 * 1000);
      const reminderRef = doc(
        db,
        "users",
        workspaceUid,
        "crm_reminders",
        reminder.id
      );

      await setDoc(
        reminderRef,
        {
          active: true,
          nextTriggerAt: Timestamp.fromDate(nextTriggerAt),
          status: CRM_REMINDER_STATUS_ACTIVE,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      const dueReminderLeadId = String(
        reminder.leadId || reminder.crmLeadId || reminder.dealId || ""
      ).trim();
      const dueReminderId = buildDueReminderDocId(
        dueReminderLeadId,
        reminder.reminderType || CRM_REMINDER_TYPE_TASK_DUE,
        reminder.followUpId || reminder.sourceTaskId || reminder.reminderId || ""
      );
      if (dueReminderLeadId && dueReminderId) {
        await setDoc(
          doc(db, "users", workspaceUid, "due_reminders", dueReminderId),
          {
            id: dueReminderId,
            workspaceUid,
            crmLeadId: dueReminderLeadId,
            crmReminderId: reminder.id || "",
            status: DUE_REMINDER_STATUS_PENDING,
            reminderEnabled: true,
            notificationSent: false,
            nextNotifyAt: Timestamp.fromDate(nextTriggerAt),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }

      applyCrmReminderLocalPatch(reminder.id, {
        ...reminder,
        active: true,
        nextTriggerAt,
        status: CRM_REMINDER_STATUS_ACTIVE,
        updatedAt: new Date(),
      });
      notify("Reminder dismissed for now", "info");
    },
    [
      applyCrmReminderLocalPatch,
      buildDueReminderDocId,
      notify,
      resolveCrmReminderFollowUp,
      workspaceUid,
    ]
  );

  const buildCrmLeadDealFromReminderSnapshot = useCallback((leadId, raw = {}) => {
    const nextActionDate =
      typeof raw.nextActionDate?.toDate === "function"
        ? raw.nextActionDate.toDate()
        : raw.nextActionDate || null;
    const installationDate =
      typeof raw.installationDate?.toDate === "function"
        ? raw.installationDate.toDate()
        : raw.installationDate || null;
    const linkedQuotes = normalizeCrmLinkedQuotes({
      ...raw,
      linkedQuoteDate:
        typeof raw.linkedQuoteDate?.toDate === "function"
          ? raw.linkedQuoteDate.toDate()
          : raw.linkedQuoteDate || null,
    });
    const contact = raw.contact || {};
    const customerLabel =
      raw.customerName ||
      contact.name ||
      raw.companyOrProject ||
      raw.title ||
      "Unnamed Lead";
    const reminderConfigs = normalizeCrmReminderConfigs(raw.reminderConfigs, {
      nextActionDate,
      installationDate,
    });

    return stripLegacyCrmQuoteLinkFields({
      id: leadId,
      ...raw,
      crmRecordType: "lead",
      contact,
      crmContact: contact,
      crmTitle: raw.title || raw.companyOrProject || customerLabel,
      customerLabel,
      customerName: raw.customerName || contact.name || "",
      customerPhone:
        raw.customerPhone ||
        contact.phone ||
        contact.whatsapp ||
        "",
      linkedQuotes,
      hasQuote: raw.hasQuote === true || linkedQuotes.length > 0,
      quoteType: raw.quoteType || raw.pipelineType || "",
      pipelineType: raw.pipelineType || raw.quoteType || "",
      nextActionDate,
      installationDate,
      reminderConfigs,
      followUps: normalizeCrmFollowUps(raw.followUps, {
        nextAction: raw.nextAction,
        nextActionDate,
        reminderConfig: reminderConfigs[CRM_REMINDER_TYPE_TASK_DUE] || null,
      }),
      attachments: normalizeCrmAttachments(raw.attachments),
      checklistItems: Array.isArray(raw.checklistItems)
        ? raw.checklistItems
        : Array.isArray(raw.checklist)
        ? raw.checklist
        : Array.isArray(raw.stageChecklist)
        ? raw.stageChecklist
        : [],
      executionSpecs: normalizeExecutionSpecs(raw.executionSpecs),
    });
  }, []);

  const loadCrmLeadDealForReminder = useCallback(
    async (reminder = {}) => {
      if (!workspaceUid || !reminder) return null;

      const candidateLeadIds = [];
      const pushLeadId = (value) => {
        const id = String(value || "").trim();
        if (id && !candidateLeadIds.includes(id)) {
          candidateLeadIds.push(id);
        }
      };

      pushLeadId(reminder.leadId);
      pushLeadId(reminder.crmLeadId);

      const reminderDealId = String(reminder.dealId || "").trim();
      pushLeadId(reminderDealId);
      if (reminder.dealRecordType === "lead") {
        pushLeadId(reminderDealId);
      }

      const directLocalLead =
        crmLeadRecords.find(
          (lead) =>
            String(lead.id || "") === reminderDealId ||
            String(lead.id || "") === String(reminder.leadId || "") ||
            String(lead.id || "") === String(reminder.crmLeadId || "")
        ) || null;
      if (directLocalLead?.id) {
        pushLeadId(directLocalLead.id);
      }

      const reminderQuoteId = String(
        reminder.quoteId ||
          (reminder.dealRecordType === "quote" ? reminder.dealId : "") ||
          ""
      ).trim();
      const reminderQuoteNo = String(reminder.quoteNo || "").trim();
      const linkedLocalLead =
        crmLeadRecords.find((lead) =>
          normalizeCrmLinkedQuotes(lead).some(
            (linkedQuote) =>
              (reminderQuoteId &&
                String(linkedQuote.quoteId || "") === reminderQuoteId) ||
              (reminderQuoteNo &&
                String(linkedQuote.quoteNo || "") === reminderQuoteNo)
          )
        ) || null;
      if (linkedLocalLead?.id) {
        pushLeadId(linkedLocalLead.id);
      }

      const linkedQuote =
        savedQuotes.find(
          (quote) =>
            (reminderQuoteId && String(quote.id || "") === reminderQuoteId) ||
            (reminderQuoteNo && String(quote.quoteNo || "") === reminderQuoteNo)
        ) || null;
      pushLeadId(linkedQuote?.crm?.leadId || linkedQuote?.leadId);

      const localDealLead =
        crmDeals.find(
          (deal) =>
            deal.crmRecordType === "lead" &&
            candidateLeadIds.includes(String(deal.id || ""))
        ) || null;

      for (const leadId of candidateLeadIds) {
        try {
          const leadSnap = await getDoc(
            doc(db, "users", workspaceUid, "crm_leads", leadId),
            { label: "crmFullLeadLoadById.reminder" }
          );
          if (leadSnap.exists()) {
            return buildCrmLeadDealFromReminderSnapshot(leadId, leadSnap.data() || {});
          }
        } catch (error) {
          console.error("Failed to fetch CRM lead for reminder:", error, {
            reminder,
            workspaceUid,
            leadId,
          });
        }
      }

      const fallbackLead = directLocalLead || linkedLocalLead || localDealLead;
      return fallbackLead?.id
        ? buildCrmLeadDealFromReminderSnapshot(fallbackLead.id, fallbackLead)
        : null;
    },
    [
      buildCrmLeadDealFromReminderSnapshot,
      crmDeals,
      crmLeadRecords,
      savedQuotes,
      workspaceUid,
    ]
  );

  const resolveReminderFollowUpFromDeal = useCallback(
    (reminder = {}, deal = null) => {
      if (!deal) return { deal: null, followUp: null, followUps: [] };

      const followUps = normalizeCrmFollowUps(deal.followUps, {
        nextAction: deal.nextAction,
        nextActionDate: deal.nextActionDate,
        reminderConfig:
          deal.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] ||
          deal.crm?.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] ||
          null,
      });

      const reminderKeys = getCrmReminderFollowUpKeys(reminder);
      const scheduledSource =
        reminder.scheduledAt ||
        reminder.nextTriggerAt ||
        combineReminderDateTime(reminder.reminderDate, reminder.reminderTime);
      const scheduledMillis = safeTimeMillis(scheduledSource);
      const scheduledDateKey =
        toDateInputValue(scheduledSource) ||
        toDateInputValue(reminder.reminderDate) ||
        String(reminder.reminderDate || "");
      const scheduledTimeKey = scheduledMillis
        ? `${String(new Date(scheduledMillis).getHours()).padStart(2, "0")}:${String(
            new Date(scheduledMillis).getMinutes()
          ).padStart(2, "0")}`
        : parseTimeInputValue(reminder.reminderTime || "");
      const reminderTitle = String(
        reminder.taskTitle || reminder.title || reminder.reminderLabel || deal.nextAction || ""
      )
        .trim()
        .toLowerCase();

      let followUp =
        followUps.find((item) => doesCrmReminderMatchFollowUp(reminder, item)) ||
        followUps.find((item) =>
          [item.id, item.reminderId, item.sourceTaskId]
            .map((value) => String(value || "").trim())
            .some((value) => value && reminderKeys.has(value))
        );

      if (!followUp) {
        followUp = followUps.find((item) => {
          if (item.status === CRM_FOLLOW_UP_STATUS_DONE) return false;
          const itemTitle = String(item.title || "").trim().toLowerCase();
          const titleMatches =
            reminderTitle &&
            (itemTitle === reminderTitle ||
              itemTitle.includes(reminderTitle) ||
              reminderTitle.includes(itemTitle));
          const dateMatches = !scheduledDateKey || item.dueDate === scheduledDateKey;
          const timeMatches =
            !scheduledTimeKey ||
            !item.dueTime ||
            item.dueTime === scheduledTimeKey;
          return titleMatches && dateMatches && timeMatches;
        });
      }

      if (!followUp && scheduledDateKey) {
        const dateMatches = followUps.filter(
          (item) =>
            item.status !== CRM_FOLLOW_UP_STATUS_DONE &&
            item.dueDate === scheduledDateKey &&
            (!scheduledTimeKey || !item.dueTime || item.dueTime === scheduledTimeKey)
        );
        if (dateMatches.length === 1) {
          followUp = dateMatches[0];
        }
      }

      if (!followUp) {
        const pendingFollowUps = followUps.filter(
          (item) => item.status !== CRM_FOLLOW_UP_STATUS_DONE
        );
        if (
          reminder.reminderType === CRM_REMINDER_TYPE_TASK_DUE &&
          pendingFollowUps.length === 1
        ) {
          followUp = pendingFollowUps[0];
        }
      }

      return { deal, followUp: followUp || null, followUps };
    },
    [combineReminderDateTime]
  );

  const resolveReminderFollowUpFromLatestSource = useCallback(
    async (reminder = {}) => {
      const localResolved =
        reminder.reminderType === CRM_REMINDER_TYPE_FOLLOW_UP
          ? resolveCrmReminderFollowUp(reminder)
          : { deal: null, followUp: null, followUps: [] };

      if (localResolved.followUp) {
        return localResolved;
      }

      const latestDeal = await loadCrmLeadDealForReminder(reminder);
      return resolveReminderFollowUpFromDeal(reminder, latestDeal);
    },
    [
      loadCrmLeadDealForReminder,
      resolveCrmReminderFollowUp,
      resolveReminderFollowUpFromDeal,
    ]
  );

  const markCrmReminderDone = useCallback(
    async (reminder) => {
      const reminderFollowUpKeys = getCrmReminderFollowUpKeys(reminder);
      if (
        !reminder?.dealId &&
        !reminder?.leadId &&
        !reminder?.quoteId &&
        !reminder?.quoteNo &&
        reminderFollowUpKeys.size === 0
      ) {
        console.error("Failed to mark done: reminder has no source identifiers", reminder);
        notify("Could not mark task as done. Please open the deal and try again.", "error");
        return;
      }
      if (!workspaceUid) {
        console.error("Failed to mark reminder done: missing workspace", reminder);
        notify("Please sign in again to mark this task as done.", "error");
        return;
      }

      const doneKey = `reminder:${reminder.id || reminder.reminderId || reminder.dealId || "unknown"}`;
      let writeAudit = {
        path: workspaceUid && reminder?.id
          ? `users/${workspaceUid}/crm_reminders/${reminder.id}`
          : "",
        payload: {},
        reminderId: reminder?.id || "",
        dealId: reminder?.dealId || reminder?.leadId || "",
      };
      setMarkingDoneKey(doneKey);

      try {
        const markReminderDocumentDoneOnly = async (reason = "reminderOnly") => {
          if (!reminder?.id) {
            throw new Error("Cannot complete reminder without a reminder document id.");
          }
          const reminderPayload = {
            enabled: false,
            reminderEnabled: false,
            active: false,
            nextTriggerAt: null,
            status: CRM_REMINDER_STATUS_DONE,
            completedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          };
          writeAudit = {
            path: `users/${workspaceUid}/crm_reminders/${reminder.id}`,
            payload: reminderPayload,
            reminderId: reminder.id || "",
            dealId: reminder.dealId || reminder.leadId || "",
            reason,
          };
          await setDoc(
            doc(db, "users", workspaceUid, "crm_reminders", reminder.id),
            reminderPayload,
            { merge: true }
          );
          await markDueReminderCompleted({
            leadId: reminder.leadId || reminder.crmLeadId || reminder.dealId || "",
            reminderType: reminder.reminderType || CRM_REMINDER_TYPE_TASK_DUE,
            sourceId:
              reminder.followUpId ||
              reminder.sourceTaskId ||
              reminder.reminderId ||
              "",
            crmReminderId: reminder.id,
          });
          applyCrmReminderLocalPatch(reminder.id, {
            ...reminder,
            enabled: false,
            reminderEnabled: false,
            active: false,
            nextTriggerAt: null,
            status: CRM_REMINDER_STATUS_DONE,
            completedAt: new Date(),
            updatedAt: new Date(),
          });
          notify("Reminder marked done", "success");
        };

        if (
          reminder.reminderType === CRM_REMINDER_TYPE_FOLLOW_UP ||
          reminder.reminderType === CRM_REMINDER_TYPE_TASK_DUE
        ) {
          const { deal: sourceDeal, followUp } =
            await resolveReminderFollowUpFromLatestSource(reminder);

          if (sourceDeal && followUp) {
            await markCrmFollowUpDone(followUp.id, sourceDeal, {
              reminder,
              successMessage: "Task marked as done",
            });
            return;
          }

          if (reminder.reminderType === CRM_REMINDER_TYPE_FOLLOW_UP) {
            console.error("Failed to mark done: follow-up reminder target missing", {
              reminder,
              reminderId: reminder.reminderId,
              followUpId: reminder.followUpId,
              dealId: reminder.dealId,
            });
            await markReminderDocumentDoneOnly("followUpSourceMissing");
            return;
          }
        }

        const sourceDeal =
          crmDeals.find((deal) => deal.id === reminder.dealId) ||
          (await loadCrmLeadDealForReminder(reminder));
        if (!sourceDeal) {
          console.error("Failed to mark done: reminder deal not found", {
            reminder,
            reminderId: reminder.id,
            dealId: reminder.dealId,
          });
          await markReminderDocumentDoneOnly("dealSourceMissing");
          return;
        }

        const nextReminderConfigs = normalizeCrmReminderConfigs(
          sourceDeal.reminderConfigs,
          {
            nextActionDate: sourceDeal.nextActionDate,
            installationDate: sourceDeal.installationDate,
          }
        );

        nextReminderConfigs[reminder.reminderType] = {
          ...nextReminderConfigs[reminder.reminderType],
          enabled: false,
          reminderEnabled: false,
          status: CRM_REMINDER_STATUS_DONE,
          completedAt: new Date().toISOString(),
        };
        const isInstallationReminder =
          reminder.reminderType === CRM_REMINDER_TYPE_INSTALLATION;

        if (selectedCrmDealId === reminder.dealId) {
          setCrmDetailDraft((prev) =>
            prev
              ? {
                  ...prev,
                  reminderConfigs: nextReminderConfigs,
                }
              : prev
          );
        }

        const reminderWriteTarget = resolveCrmTaskWriteTarget(
          sourceDeal,
          reminder.leadId || reminder.crmLeadId || reminder.dealId || sourceDeal.id
        );
        const reminderConfigPayload = {
          reminderConfigs: nextReminderConfigs,
        };
        writeAudit = {
          path: reminderWriteTarget.firestorePath,
          payload: reminderConfigPayload,
          reminderId: reminder.id || "",
          dealId: sourceDeal.id || reminder.dealId || "",
        };

        await saveCrmFieldsForDeal(
          reminderWriteTarget.targetId,
          reminderConfigPayload,
          {
            forceLead: reminderWriteTarget.forceLead,
            sourceLead: reminderWriteTarget.sourceLead,
            createIfMissing: false,
            requireWrite: true,
            skipReminderSync: true,
            syncQuote: false,
            writeSource: "markCrmReminderDone.reminderConfigs",
          }
        );

        const reminderPayload = {
          enabled: false,
          reminderEnabled: false,
          active: false,
          nextTriggerAt: null,
          status: CRM_REMINDER_STATUS_DONE,
          completedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
        writeAudit = {
          path: `users/${workspaceUid}/crm_reminders/${reminder.id}`,
          payload: reminderPayload,
          reminderId: reminder.id || "",
          dealId: sourceDeal.id || reminder.dealId || "",
        };
        await setDoc(
          doc(db, "users", workspaceUid, "crm_reminders", reminder.id),
          reminderPayload,
          { merge: true }
        );
        await markDueReminderCompleted({
          leadId: reminder.leadId || reminder.crmLeadId || sourceDeal.id || reminder.dealId || "",
          reminderType: reminder.reminderType || CRM_REMINDER_TYPE_TASK_DUE,
          sourceId:
            reminder.followUpId ||
            reminder.sourceTaskId ||
            reminder.reminderId ||
            "",
          crmReminderId: reminder.id,
        });
        applyCrmReminderLocalPatch(reminder.id, {
          ...reminder,
          enabled: false,
          reminderEnabled: false,
          active: false,
          nextTriggerAt: null,
          status: CRM_REMINDER_STATUS_DONE,
          completedAt: new Date(),
          updatedAt: new Date(),
        });

        if (isInstallationReminder && sourceDeal.crmRecordType === "lead") {
          const installationPayload = {
            installationReminderDone: true,
            installationReminderCompletedAt: serverTimestamp(),
            installationReminderCompletedBy: getCurrentTaskHistoryActor(),
            ...(isDevRuntime
              ? {
                  _lastWriteSource: "markReminderDone.installation",
                  _lastWriteAt: serverTimestamp(),
                }
              : {}),
            updatedAt: serverTimestamp(),
          };
          writeAudit = {
            path: `users/${workspaceUid}/crm_leads/${sourceDeal.id}`,
            payload: installationPayload,
            reminderId: reminder.id || "",
            dealId: sourceDeal.id || "",
          };
          await setDoc(
            doc(db, "users", workspaceUid, "crm_leads", sourceDeal.id),
            installationPayload,
            { merge: true }
          );
          applyCrmLeadLocalPatch(sourceDeal.id, {
            installationReminderDone: true,
            installationReminderCompletedAt: new Date(),
            installationReminderCompletedBy: getCurrentTaskHistoryActor(),
            updatedAt: new Date(),
          });
        }

        const reminderDueDate =
          reminder.scheduledAt ||
          reminder.nextTriggerAt ||
          combineReminderDateTime(reminder.reminderDate, reminder.reminderTime) ||
          (isInstallationReminder
            ? sourceDeal.installationDate
            : sourceDeal.nextActionDate);

        await writeTaskHistoryRecord({
          taskType: isInstallationReminder
            ? TASK_HISTORY_TYPE_INSTALLATION
            : TASK_HISTORY_TYPE_DEAL_ACTION,
          sourceTaskId: reminder.id,
          dealLike: sourceDeal,
          taskTitle: isInstallationReminder
            ? "Installation Reminder"
            : sourceDeal.nextAction || reminder.reminderLabel || "Deal Action",
          taskDescription: isInstallationReminder
            ? `Installation reminder completed for ${sourceDeal.customerLabel || sourceDeal.crmTitle || "this deal"}.`
            : sourceDeal.nextAction || reminder.reminderLabel || "",
          dueDate: reminderDueDate,
          sourceModule: isInstallationReminder
            ? TASK_HISTORY_SOURCE_INSTALLATION
            : TASK_HISTORY_SOURCE_CRM,
          originalPayload: {
            reminder,
            reminderConfig: nextReminderConfigs[reminder.reminderType],
            dealSnapshot: {
              id: sourceDeal.id,
              crmRecordType: sourceDeal.crmRecordType,
              crmTitle: sourceDeal.crmTitle,
              customerLabel: sourceDeal.customerLabel,
              quoteNo:
                sourceDeal.quoteNo ||
                getPrimaryCrmLinkedQuote(sourceDeal.linkedQuotes || [])?.quoteNo ||
                "",
            },
          },
        });
        notify("Task marked as done", "success");
      } catch (error) {
        logCrmTaskDoneFailure("markCrmReminderDone", error, {
          ...writeAudit,
          reminder,
          workspaceUid,
          resolvedLeadId: reminder?.leadId || reminder?.crmLeadId || reminder?.dealId || "",
          resolvedFollowUpId: reminder?.followUpId || reminder?.reminderId || "",
        });
        notify("Could not mark task as done. Please open the deal and try again.", "error");
      } finally {
        setMarkingDoneKey((current) => (current === doneKey ? "" : current));
      }
    },
    [
      applyCrmLeadLocalPatch,
      applyCrmReminderLocalPatch,
      combineReminderDateTime,
      crmDeals,
      getCurrentTaskHistoryActor,
      loadCrmLeadDealForReminder,
      markDueReminderCompleted,
      markCrmFollowUpDone,
      notify,
      resolveCrmTaskWriteTarget,
      resolveReminderFollowUpFromLatestSource,
      saveCrmFieldsForDeal,
      selectedCrmDealId,
      writeTaskHistoryRecord,
      workspaceUid,
    ]
  );

  const openReminderDeal = useCallback(
    (reminder) => {
      const resolvedFollowUp =
        reminder?.reminderType === CRM_REMINDER_TYPE_FOLLOW_UP
          ? resolveCrmReminderFollowUp(reminder)
          : null;
      const targetDealId = resolvedFollowUp?.deal?.id || reminder?.dealId || "";
      const targetDeal =
        resolvedFollowUp?.deal ||
        crmDeals.find((deal) => String(deal.id || "") === String(targetDealId || "")) ||
        { id: targetDealId, leadId: reminder?.leadId || reminder?.crmLeadId || targetDealId };
      setCrmDrilldownKey("");
      setCrmScope(CRM_SCOPE_ALL);
      setCrmSearch("");
      setCrmStageFilter("all");
      setCrmTypeFilter("all");
      setCrmSortBy("newest");
      setCrmView("list");
      setTab("crm");
      openCrmDealDetails(targetDeal, { crmView: "list" });
    },
    [crmDeals, openCrmDealDetails, resolveCrmReminderFollowUp]
  );

  const openCrmTaskHistoryDeal = useCallback((record) => {
    const targetDealId =
      record?.dealId || record?.leadId || record?.quoteId || "";
    if (!targetDealId) return;
    const targetDeal =
      crmDeals.find((deal) => String(deal.id || "") === String(targetDealId)) ||
      { id: targetDealId, leadId: record?.leadId || record?.dealId || "" };

    setCrmDrilldownKey("");
    setCrmScope(CRM_SCOPE_ALL);
    setCrmSearch("");
    setCrmSourceFilter("all");
    setCrmStageFilter("all");
    setCrmTypeFilter("all");
    setCrmSortBy("newest");
    setCrmView("list");
    setTab("crm");
    openCrmDealDetails(targetDeal, { crmView: "list" });
  }, [crmDeals, openCrmDealDetails]);

  const openDailyTaskDeal = useCallback((task) => {
    const targetDealId = task?.leadId || task?.dealId || task?.quoteId || "";
    if (!targetDealId) return;
    const targetDeal =
      task?.deal ||
      crmDeals.find((deal) => String(deal.id || "") === String(targetDealId)) ||
      { id: targetDealId, leadId: task?.leadId || task?.dealId || "" };

    setCrmDrilldownKey("");
    setCrmScope(CRM_SCOPE_ALL);
    setCrmSearch("");
    setCrmSourceFilter("all");
    setCrmStageFilter("all");
    setCrmTypeFilter("all");
    setCrmSortBy("newest");
    setCrmView("list");
    setTab("crm");
    openCrmDealDetails(targetDeal, { crmView: "list" });
  }, [crmDeals, openCrmDealDetails]);

  const markDailyTaskDone = useCallback(
    async (task) => {
      if (!task?.deal) {
        notify("Could not find that task. Please open the deal and check it manually.", "error");
        return;
      }

      const doneKey = `daily:${task.id || task.sourceType || "task"}:${task.sourceId || task.dealId || ""}`;
      const writeTarget = resolveDailyTaskWriteTarget(task);
      let writeAudit = {
        path: writeTarget.firestorePath,
        payload: {},
        taskId: task.id || "",
        sourceType: task.sourceType || "",
        sourceId: task.sourceId || "",
        dealId: task.dealId || "",
        leadId: task.leadId || "",
      };

      if (!writeTarget.targetId) {
        logCrmTaskDoneFailure("markDailyTaskDone.missingTarget", new Error("Missing CRM task write target"), writeAudit);
        notify("Could not find that task. Please open the deal and check it manually.", "error");
        return;
      }

      setMarkingDoneKey(doneKey);

      try {
        if (task.sourceType === DAILY_TASK_SOURCE_FOLLOW_UP) {
          await markCrmFollowUpDone(task.sourceId, task.deal, {
            successMessage: "Task marked as done",
          });
          return;
        }

        if (task.sourceType === DAILY_TASK_SOURCE_CHECKLIST) {
          const actor = getCurrentTaskHistoryActor();
          const nextChecklistItems = toggleChecklistNodeById(
            task.deal.checklistItems || [],
            task.sourceId,
            true,
            {
              completedAt: new Date(),
              completedBy: actor,
              updatedAt: new Date(),
            }
          );

          const payload = {
            checklistItems: nextChecklistItems,
          };
          writeAudit = {
            ...writeAudit,
            path: writeTarget.firestorePath,
            payload,
          };

          await saveCrmFieldsForDeal(writeTarget.targetId, payload, {
            forceLead: writeTarget.forceLead,
            sourceLead: writeTarget.sourceLead,
            requireWrite: true,
            skipReminderSync: true,
            syncQuote: false,
            writeSource: "markDailyTaskDone.checklist",
          });

          await writeTaskHistoryRecord({
            taskType: TASK_HISTORY_TYPE_CHECKLIST,
            sourceTaskId: task.sourceId || task.id,
            dealLike: task.deal,
            taskTitle: task.title,
            taskDescription: task.description || "",
            dueDate: task.dueDateTime || task.dueDate || null,
            sourceModule: TASK_HISTORY_SOURCE_CRM,
            originalPayload: {
              dailyTask: {
                id: task.id,
                sourceType: task.sourceType,
                sourceId: task.sourceId,
                dueDate: task.dueDateKey,
                dueTime: task.dueTime,
              },
            },
          });

          notify("Checklist item marked done", "success");
          return;
        }

        const reminderType =
          task.sourceType === DAILY_TASK_SOURCE_INSTALLATION
            ? CRM_REMINDER_TYPE_INSTALLATION
            : CRM_REMINDER_TYPE_TASK_DUE;
        const nextReminderConfigs = normalizeCrmReminderConfigs(
          task.deal.reminderConfigs,
          {
            nextActionDate: task.deal.nextActionDate,
            installationDate: task.deal.installationDate,
          }
        );

        nextReminderConfigs[reminderType] = {
          ...nextReminderConfigs[reminderType],
          enabled: false,
          reminderEnabled: false,
          status: CRM_REMINDER_STATUS_DONE,
          completedAt: new Date().toISOString(),
        };

        const payload = {
          reminderConfigs: nextReminderConfigs,
        };
        writeAudit = {
          ...writeAudit,
          path: writeTarget.firestorePath,
          payload,
        };

        await saveCrmFieldsForDeal(writeTarget.targetId, payload, {
          forceLead: writeTarget.forceLead,
          sourceLead: writeTarget.sourceLead,
          requireWrite: true,
          skipReminderSync: true,
          syncQuote: false,
          writeSource: "markDailyTaskDone.reminderConfigs",
        });
        await markDueReminderCompleted({
          leadId: writeTarget.targetId || task.leadId || task.dealId || "",
          reminderType,
          sourceId: task.sourceId || "",
          crmReminderId: task.reminderId || task.id || "",
        });

        await writeTaskHistoryRecord({
          taskType:
            task.sourceType === DAILY_TASK_SOURCE_INSTALLATION
              ? TASK_HISTORY_TYPE_INSTALLATION
              : TASK_HISTORY_TYPE_DEAL_ACTION,
          sourceTaskId: task.sourceId || task.id,
          dealLike: task.deal,
          taskTitle: task.title,
          taskDescription: task.description || "",
          dueDate: task.dueDateTime || task.dueDate || null,
          sourceModule:
            task.sourceType === DAILY_TASK_SOURCE_INSTALLATION
              ? TASK_HISTORY_SOURCE_INSTALLATION
              : TASK_HISTORY_SOURCE_CRM,
          originalPayload: {
            dailyTask: {
              id: task.id,
              sourceType: task.sourceType,
              sourceId: task.sourceId,
              dueDate: task.dueDateKey,
              dueTime: task.dueTime,
            },
          },
        });

        notify(
          task.sourceType === DAILY_TASK_SOURCE_INSTALLATION
            ? "Installation marked done"
            : "Task marked as done",
          "success"
        );
      } catch (error) {
        logCrmTaskDoneFailure("markDailyTaskDone", error, {
          ...writeAudit,
          workspaceUid,
        });
        notify("Could not mark task as done. Please try again.", "error");
      } finally {
        setMarkingDoneKey((current) => (current === doneKey ? "" : current));
      }
    },
    [
      getCurrentTaskHistoryActor,
      markCrmFollowUpDone,
      markDueReminderCompleted,
      notify,
      resolveDailyTaskWriteTarget,
      saveCrmFieldsForDeal,
      writeTaskHistoryRecord,
      workspaceUid,
    ]
  );

  const openDailyTaskReschedule = useCallback((task) => {
    if (!task) return;

    setDailyTaskRescheduleState({
      task,
      dueDate: task.dueDateKey || toDateInputValue(task.dueDate) || dailyTaskDateKey,
      dueTime: task.dueTime || DEFAULT_CRM_REMINDER_TIME,
    });
  }, [dailyTaskDateKey]);

  const saveDailyTaskReschedule = useCallback(async () => {
    const task = dailyTaskRescheduleState?.task;
    if (!task?.deal) {
      notify("Could not find that task. Please open the deal and check it manually.", "error");
      return;
    }
    if (!dailyTaskRescheduleState?.dueDate) {
      notify("Please choose a new task date.", "warning");
      return;
    }

    const nextDueDate = dailyTaskRescheduleState.dueDate;
    const nextDueTime = parseTimeInputValue(dailyTaskRescheduleState.dueTime);
    if (!parseDateInputValue(nextDueDate)) {
      notify("Please choose a valid task date.", "warning");
      return;
    }

    try {
      if (task.sourceType === DAILY_TASK_SOURCE_FOLLOW_UP) {
        const currentFollowUps = normalizeCrmFollowUps(task.deal.followUps || task.deal.crm?.followUps);
        const hasTargetFollowUp = currentFollowUps.some(
          (followUp) =>
            followUp.id === task.sourceId || followUp.reminderId === task.reminderId
        );

        if (!hasTargetFollowUp) {
          notify("Could not find that task. Please open the deal and check it manually.", "error");
          return;
        }

        const nextFollowUps = currentFollowUps.map((followUp) =>
          followUp.id === task.sourceId || followUp.reminderId === task.reminderId
            ? {
                ...followUp,
                dueDate: nextDueDate,
                dueTime: nextDueTime,
                reminderEnabled: true,
                status: CRM_FOLLOW_UP_STATUS_PENDING,
                updatedAt: new Date(),
              }
            : followUp
        );

        await persistCrmFollowUpsForDeal(
          task.deal,
          nextFollowUps,
          "Task rescheduled"
        );
      } else if (task.sourceType === DAILY_TASK_SOURCE_CHECKLIST) {
        const nextChecklistItems = updateChecklistNodeById(
          task.deal.checklistItems || [],
          task.sourceId,
          (node) => ({
            ...node,
            dueDate: nextDueDate,
            dueTime: nextDueTime,
            updatedAt: new Date(),
          })
        );

        await saveCrmFieldsForDeal(task.dealId, {
          checklistItems: nextChecklistItems,
        });
        notify("Task rescheduled", "success");
      } else {
        const reminderType =
          task.sourceType === DAILY_TASK_SOURCE_INSTALLATION
            ? CRM_REMINDER_TYPE_INSTALLATION
            : CRM_REMINDER_TYPE_TASK_DUE;
        const nextReminderConfigs = normalizeCrmReminderConfigs(
          task.deal.reminderConfigs,
          {
            nextActionDate: task.deal.nextActionDate,
            installationDate: task.deal.installationDate,
          }
        );

        nextReminderConfigs[reminderType] = {
          ...nextReminderConfigs[reminderType],
          enabled: true,
          reminderEnabled: true,
          reminderDate: nextDueDate,
          reminderTime: nextDueTime,
          status: CRM_REMINDER_STATUS_ACTIVE,
          completedAt: null,
        };

        const overrides = {
          reminderConfigs: nextReminderConfigs,
        };

        if (task.sourceType === DAILY_TASK_SOURCE_INSTALLATION) {
          overrides.installationDate = combineReminderDateTime(nextDueDate, nextDueTime);
        } else {
          overrides.nextActionDate = parseDateInputValue(nextDueDate);
        }

        await saveCrmFieldsForDeal(task.dealId, overrides);
        notify("Task rescheduled", "success");
      }

      setDailyTaskRescheduleState(null);
    } catch (error) {
      console.error("Failed to reschedule task:", error);
      notify("Could not reschedule task. Please try again.", "error");
    }
  }, [
    combineReminderDateTime,
    dailyTaskRescheduleState,
    notify,
    persistCrmFollowUpsForDeal,
    saveCrmFieldsForDeal,
  ]);

  const getChecklistChildButtonLabel = useCallback((depth) => {
    if (depth === 1) return "Add Sub-checklist";
    if (depth === 2) return "Add Sub-sub-checklist";
    return "";
  }, []);

  const renderPipelineChecklistNode = (stage, item, depth = 1) => {
    const canAddChild = depth < CRM_CHECKLIST_MAX_DEPTH;
    const children = Array.isArray(item.children) ? item.children : [];

    return (
      <div
        key={item.id}
        style={{
          display: "grid",
          gap: 10,
          padding: 12,
          borderRadius: 16,
          background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
          border: dashboardBorder,
          marginLeft: depth > 1 ? (depth - 1) * 18 : 0,
          boxShadow:
            depth > 1
              ? `inset 3px 0 0 ${isDark ? "rgba(96,165,250,0.28)" : "rgba(37,99,235,0.16)"}`
              : "none",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 1fr) auto",
            gap: 10,
            alignItems: "center",
          }}
        >
          <div style={{ display: "grid", gap: 6 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: dashboardMuted,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              {depth === 1
                ? "Checklist"
                : depth === 2
                ? "Sub-checklist"
                : "Sub-sub-checklist"}
            </div>
            <input
              value={item.label || ""}
              onChange={(e) =>
                updatePipelineChecklistItem(
                  activeCrmPipelineType,
                  stage.id,
                  item.id,
                  { label: e.target.value }
                )
              }
              style={input}
              placeholder="Checklist item"
            />
          </div>

          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: isCompact ? "flex-start" : "flex-end",
            }}
          >
            {canAddChild ? (
              <button
                type="button"
                onClick={() =>
                  addPipelineChecklistChild(
                    activeCrmPipelineType,
                    stage.id,
                    item.id
                  )
                }
                style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 10px" }}
              >
                {getChecklistChildButtonLabel(depth)}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() =>
                movePipelineChecklistItem(
                  activeCrmPipelineType,
                  stage.id,
                  item.id,
                  -1
                )
              }
              style={{ ...btn("#64748b"), marginRight: 0, padding: "8px 10px" }}
            >
              Up
            </button>
            <button
              type="button"
              onClick={() =>
                movePipelineChecklistItem(
                  activeCrmPipelineType,
                  stage.id,
                  item.id,
                  1
                )
              }
              style={{ ...btn("#64748b"), marginRight: 0, padding: "8px 10px" }}
            >
              Down
            </button>
            <button
              type="button"
              onClick={() =>
                deletePipelineChecklistItem(
                  activeCrmPipelineType,
                  stage.id,
                  item.id
                )
              }
              style={{ ...btn("#ef4444"), marginRight: 0, padding: "8px 10px" }}
            >
              Delete
            </button>
          </div>
        </div>

        {children.length > 0 ? (
          <div style={{ display: "grid", gap: 10 }}>
            {children.map((child) =>
              renderPipelineChecklistNode(stage, child, depth + 1)
            )}
          </div>
        ) : null}
      </div>
    );
  };

  const renderCrmChecklistNode = (item, depth = 1) => {
    if (!selectedCrmDeal) return null;

    const progress = getChecklistNodeProgress(item);
    const childButtonLabel = getChecklistChildButtonLabel(depth);
    const canAddChild = depth < CRM_CHECKLIST_MAX_DEPTH;
    const children = Array.isArray(item.children) ? item.children : [];

    return (
      <div
        key={item.id}
        style={{
          display: "grid",
          gap: 10,
          padding: 12,
          borderRadius: 16,
          background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
          border: dashboardBorder,
          marginLeft: depth > 1 ? (depth - 1) * 18 : 0,
          boxShadow:
            depth > 1
              ? `inset 3px 0 0 ${isDark ? "rgba(96,165,250,0.28)" : "rgba(37,99,235,0.16)"}`
              : "none",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "auto minmax(0, 1fr)",
            gap: 12,
            alignItems: "start",
          }}
        >
          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontSize: 13,
              fontWeight: 700,
              paddingTop: 8,
            }}
          >
            <input
              type="checkbox"
              checked={progress.done}
              ref={(element) => {
                if (element) {
                  element.indeterminate = progress.partial;
                }
              }}
              onChange={(e) =>
                toggleCrmChecklistItem(item.id, e.target.checked)
              }
            />
            {progress.done ? "Done" : progress.partial ? "Partial" : "Open"}
          </label>

          <div style={{ display: "grid", gap: 8 }}>
            <div style={{ display: "grid", gap: 6 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: dashboardMuted,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                {depth === 1
                  ? "Checklist"
                  : depth === 2
                  ? "Sub-checklist"
                  : "Sub-sub-checklist"}
              </div>
              <textarea
                ref={(element) => {
                  if (element) {
                    checklistInputRefs.current.set(item.id, element);
                  } else {
                    checklistInputRefs.current.delete(item.id);
                  }
                }}
                value={item.label ?? item.title ?? ""}
                onChange={(e) =>
                  updateCrmChecklistItemField(item.id, {
                    label: e.target.value,
                  })
                }
                style={{
                  ...input,
                  minHeight: 62,
                  resize: "vertical",
                  background: isDark ? "#0f172a" : "#ffffff",
                  border: isDark
                    ? "1px solid rgba(148,163,184,0.28)"
                    : "1px solid #cbd5e1",
                }}
                placeholder={
                  depth === 1
                    ? "Type checklist item..."
                    : depth === 2
                    ? "Type sub-checklist item..."
                    : "Type sub-sub-checklist item..."
                }
              />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 10,
              }}
            >
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                  Assigned Team Member
                </div>
                <select
                  value={item.assignedToUserId || ""}
                  onChange={(e) =>
                    updateCrmChecklistAssignee(
                      selectedCrmDeal.id,
                      item.id,
                      e.target.value
                    )
                  }
                  style={selectInput}
                >
                  <option value="">Unassigned</option>
                  {getAssignableTeamMembers(item.assignedToUserId).map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.name || "Unnamed Team Member"}
                      {member.role ? ` | ${member.role}` : ""}
                      {member.isActive === false ? " | Archived" : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                {canAddChild ? (
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      addCrmChecklistItem(item.id);
                    }}
                    style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 10px" }}
                  >
                    {childButtonLabel}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    moveCrmChecklistItem(item.id, -1);
                  }}
                  style={{ ...btn("#64748b"), marginRight: 0, padding: "8px 10px" }}
                >
                  Up
                </button>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    moveCrmChecklistItem(item.id, 1);
                  }}
                  style={{ ...btn("#64748b"), marginRight: 0, padding: "8px 10px" }}
                >
                  Down
                </button>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    deleteCrmChecklistItem(item.id);
                  }}
                  style={{ ...btn("#ef4444"), marginRight: 0, padding: "8px 10px" }}
                >
                  Delete
                </button>
                <div
                  style={{
                    fontSize: 11,
                    color: dashboardMuted,
                    fontWeight: 700,
                    marginLeft: "auto",
                  }}
                >
                  {progress.completed}/{progress.total} complete
                </div>
              </div>
            </div>
          </div>
        </div>

        {children.length > 0 ? (
          <div style={{ display: "grid", gap: 10 }}>
            {children.map((child) => renderCrmChecklistNode(child, depth + 1))}
          </div>
        ) : null}
      </div>
    );
  };

  const renderCrmStageSelect = (deal, extraStyle = {}) => {
    const stageOptions = getStageSelectOptions(
      crmPipelines,
      deal.pipelineType,
      deal.crmStageId
    );

    return (
      <select
        value={deal.crmStageId}
        onChange={(e) => updateWorkStage(deal.id, e.target.value)}
        style={{
          ...selectInput,
          fontSize: 12,
          fontWeight: 700,
          borderRadius: 12,
          background: deal.crmStageColor,
          color: "#ffffff",
          WebkitTextFillColor: "#ffffff",
          border: "none",
          ...extraStyle,
        }}
      >
        {stageOptions.map((stage) => (
          <option key={stage.id} value={stage.id}>
            {stage.label}
          </option>
        ))}
      </select>
    );
  };

  const getCanonicalCrmLeadForCard = (cardRecord = {}) => {
    const directLeadIds = [
      cardRecord.canonicalLeadId,
      cardRecord.crmLeadId,
      cardRecord.leadId,
      cardRecord.crm?.leadId,
      cardRecord.primaryLeadId,
      cardRecord.crmRecordType === "lead" ? cardRecord.id : "",
      cardRecord.id,
    ]
      .map((value) => String(value || "").trim())
      .filter((value, index, source) => value && source.indexOf(value) === index);

    for (const leadId of directLeadIds) {
      const lead = crmLeadRecords.find((item) => String(item.id || "") === leadId);
      if (lead) return lead;
    }

    const quoteReference = getCrmLeadQuoteReferenceSummary(cardRecord);
    const quoteId =
      quoteReference.primaryQuoteId ||
      cardRecord.quoteId ||
      cardRecord.primaryQuoteId ||
      cardRecord.primaryLinkedQuote?.quoteId ||
      (cardRecord.crmRecordType === "quote" ? cardRecord.id : "");
    const quoteNo =
      quoteReference.primaryQuoteNo ||
      cardRecord.quoteNo ||
      cardRecord.primaryQuoteNo ||
      cardRecord.primaryLinkedQuote?.quoteNo ||
      "";

    return (
      crmLeadRecords.find((lead) =>
        doesCrmLeadReferenceQuote(lead, quoteId, quoteNo)
      ) || null
    );
  };

  const getLatestCrmLeadForCard = (cardRecord = {}) => {
    const canonicalLead = getCanonicalCrmLeadForCard(cardRecord);
    if (!canonicalLead?.id) {
      return {
        ...cardRecord,
        canonicalLeadId: cardRecord.canonicalLeadId || cardRecord.crmLeadId || cardRecord.leadId || "",
      };
    }

    const latestDeal = buildCrmLeadDetailsRecord(canonicalLead) || canonicalLead;
    return {
      ...cardRecord,
      ...latestDeal,
      id: latestDeal.id || canonicalLead.id,
      canonicalLeadId: latestDeal.id || canonicalLead.id,
      crmLeadId: latestDeal.id || canonicalLead.id,
      leadId: latestDeal.id || canonicalLead.id,
      crmRecordType: "lead",
    };
  };

  const renderCrmDealCard = (
    cardRecord,
    { dense = false, overview = false, renderSource = "DealList" } = {}
  ) => {
    const deal = getLatestCrmLeadForCard(cardRecord);
    const checklistSummary = getChecklistSummary(deal);
    const followUpSummary = getFollowUpSummary(deal, { nowMillis: Date.now() });
    const installationSummary = getInstallationSummary(deal);
    const hasOverdueTasks = Number(followUpSummary.overdue || 0) > 0;
    const installationLabel = installationSummary.installationLabel || "";
    const nextFollowUpLabel = followUpSummary.nextFollowUpLabel || "No follow-up set";
    const shouldTraceDealListCard =
      isDevRuntime &&
      renderSource === "DealList" &&
      (String(deal.id || "") === String(selectedCrmDealId || "") ||
        String(cardRecord?.id || "") === String(selectedCrmDealId || "") ||
        String(deal.id || "") === String(crmDetailsOpenDebug.lastResolvedLeadId || "") ||
        String(cardRecord?.id || "") === String(crmDetailsOpenDebug.lastRecordId || ""));

    if (shouldTraceDealListCard) {
      const canonical = getCanonicalCrmLeadForCard(cardRecord);
      console.log("DEAL LIST CARD DATA TRACE", {
        renderSource: "DealList",
        cardId: cardRecord?.id,
        canonicalLeadId: deal.canonicalLeadId,
        quoteId: cardRecord?.quoteId || cardRecord?.primaryLinkedQuote?.quoteId || "",
        quoteNo: cardRecord?.quoteNo || cardRecord?.primaryLinkedQuote?.quoteNo || "",
        customerName:
          cardRecord?.customerName ||
          cardRecord?.customerLabel ||
          cardRecord?.crmContact?.name ||
          "",
        rawChecklistItems: cardRecord?.checklistItems,
        rawChecklist: cardRecord?.checklist,
        rawStageChecklist: cardRecord?.stageChecklist,
        rawFollowUps: cardRecord?.followUps,
        rawInstallationDate: cardRecord?.installationDate,
        rawInstallationReminder: cardRecord?.installationReminder,
        checklistCompleted: cardRecord?.checklistCompleted,
        checklistTotal: cardRecord?.checklistTotal,
        followUpSummary: cardRecord?.followUpSummary,
        installationDisplay: cardRecord?.installationDisplay,
        checklistSummary,
        followUpSummaryFromHelper: followUpSummary,
        installationSummary,
      });
      console.log("DEAL LIST CANONICAL LEAD TRACE", {
        cardId: cardRecord?.id,
        canonicalFound: Boolean(canonical),
        canonicalId: canonical?.id,
        canonicalChecklistItems: canonical?.checklistItems,
        canonicalFollowUps: canonical?.followUps,
        canonicalInstallationDate: canonical?.installationDate,
        canonicalInstallationReminder: canonical?.installationReminder,
        canonicalChecklistSummary: canonical ? getChecklistSummary(canonical) : null,
      });
    }

    if (overview) {
      return (
        <div
          key={deal.id}
          style={{
            padding: 12,
            borderRadius: 16,
            border:
              selectedCrmDealId === deal.id
                ? "1px solid rgba(37,99,235,0.4)"
                : dashboardBorder,
            background:
              selectedCrmDealId === deal.id
                ? isDark
                  ? "rgba(37,99,235,0.12)"
                  : "#eff6ff"
                : dashboardSoftBg,
            display: "grid",
            gap: 10,
            minWidth: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 10,
              alignItems: "flex-start",
              minWidth: 0,
            }}
          >
            <button
              type="button"
              onClick={() =>
                openCrmDealDetails(
                  deal,
                  overview ? "crm_overview_card_title" : "crm_deal_list_card_title"
                )
              }
              style={{
                border: "none",
                background: "transparent",
                padding: 0,
                cursor: "pointer",
                textAlign: "left",
                color: textColor,
                minWidth: 0,
                flex: "1 1 auto",
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 900,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {deal.crmTitle}
              </div>
              <div style={{ fontSize: 11, color: dashboardMuted, marginTop: 3 }}>
                {deal.hasQuote
                  ? `#${deal.primaryLinkedQuote?.quoteNo || deal.quoteNo || "-"}`
                  : "Lead"}{" "}
                | {deal.customerLabel || "Unnamed Customer"}
              </div>
            </button>
            <span
              style={{
                flex: "0 0 auto",
                maxWidth: 118,
                padding: "5px 9px",
                borderRadius: 999,
                background: deal.crmStageColor || "#64748b",
                color: "#ffffff",
                fontSize: 10.5,
                fontWeight: 900,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {deal.crmStageLabel || "Stage"}
            </span>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 8,
              fontSize: 11,
            }}
          >
            {[
              ["Value", formatCurrencyDisplay(deal.expectedValue || 0, currency)],
              ["Tasks", followUpSummary.label],
              ["Checklist", checklistSummary.label],
              ["Install", installationLabel || "-"],
            ].map(([label, value]) => (
              <div
                key={label}
                style={{
                  padding: "8px 9px",
                  borderRadius: 12,
                  background: isDark ? "rgba(15,23,42,0.55)" : "#ffffff",
                  border: dashboardBorder,
                  minWidth: 0,
                }}
              >
                <div style={{ color: dashboardMuted, fontWeight: 800 }}>
                  {label}
                </div>
                <div
                  style={{
                    marginTop: 3,
                    color:
                      label === "Tasks" && hasOverdueTasks ? "#be123c" : textColor,
                    fontWeight: 900,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {value}
                </div>
              </div>
            ))}
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              alignItems: "center",
            }}
          >
            <button
              type="button"
              onClick={() =>
                openCrmDealDetails(
                  deal,
                  overview ? "crm_overview_card_details" : "crm_deal_list_card_details"
                )
              }
              style={{
                ...btn("#0f172a"),
                marginRight: 0,
                padding: "8px 12px",
                background: isDark ? "#1e293b" : "#0f172a",
              }}
            >
              Details
            </button>
            {(deal.hasQuote ? canViewQuotes : canCreateQuotes) ? (
              <button
                type="button"
                onClick={() =>
                  deal.hasQuote
                    ? openCrmQuoteForDeal(deal)
                    : startQuoteFromCrmLead(deal)
                }
                style={{
                  ...btn("#2563eb"),
                  marginRight: 0,
                  padding: "8px 12px",
                }}
              >
                {deal.hasQuote ? "Open Quote" : "Create Quote"}
              </button>
            ) : null}
          </div>
        </div>
      );
    }

    return (
    <div
      key={deal.id}
      style={{
        padding: dense ? 14 : 16,
        borderRadius: 18,
        border:
          selectedCrmDealId === deal.id
            ? "1px solid rgba(37,99,235,0.4)"
            : dashboardBorder,
        background:
          selectedCrmDealId === deal.id
            ? isDark
              ? "rgba(37,99,235,0.12)"
              : "#eff6ff"
            : dashboardSoftBg,
        display: "grid",
        gap: 12,
        boxShadow:
          selectedCrmDealId === deal.id
            ? "0 14px 28px rgba(37,99,235,0.12)"
            : "none",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "flex-start",
        }}
      >
        <button
          type="button"
          onClick={() => openCrmDealDetails(deal, "crm_deal_list_card_title")}
          style={{
            border: "none",
            background: "transparent",
            padding: 0,
            cursor: "pointer",
            textAlign: "left",
            color: textColor,
            flex: 1,
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 800 }}>{deal.crmTitle}</div>
          <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
            {deal.hasQuote
              ? `#${deal.primaryLinkedQuote?.quoteNo || deal.quoteNo || "-"} | ${deal.customerLabel}`
              : `Lead | ${deal.customerLabel}`}
          </div>
        </button>

        <div
          style={{
            padding: "6px 10px",
            borderRadius: 999,
            background:
              deal.quoteTypeResolved === GENERAL_QUOTE_TYPE ? "#dbeafe" : "#ede9fe",
            color:
              deal.quoteTypeResolved === GENERAL_QUOTE_TYPE ? "#1d4ed8" : "#7c3aed",
            fontSize: 11,
            fontWeight: 800,
            whiteSpace: "nowrap",
          }}
        >
          {getQuoteTypeLabel(deal.quoteTypeResolved)}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: dense || isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))",
          gap: 10,
        }}
      >
        <div>
          <div style={{ fontSize: 11, color: dashboardMuted, marginBottom: 4 }}>
            {deal.hasQuote ? "Deal Value" : "Expected Value"}
          </div>
          <div style={{ fontSize: 18, fontWeight: 900 }}>
            {formatCurrencyDisplay(deal.expectedValue || 0, currency)}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: dashboardMuted, marginBottom: 4 }}>
            Checklist
          </div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>
            {checklistSummary.label}
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted }}>
        <div>
          Assigned:{" "}
          <strong style={{ color: textColor }}>
            {getTeamMemberLabel(deal.assignedToUserId, deal.assignedToNameSnapshot)}
          </strong>
        </div>
        <div>
          Next Action:{" "}
          <strong style={{ color: textColor }}>
            {nextFollowUpLabel}
          </strong>
        </div>
        <div>
          Tasks:{" "}
          <strong style={{ color: hasOverdueTasks ? "#be123c" : textColor }}>
            {followUpSummary.label}
          </strong>
        </div>
        <div>
          Source:{" "}
          <strong style={{ color: textColor }}>
            {deal.source || deal.crmContact?.source || "-"}
          </strong>
        </div>
        <div>
          {deal.hasQuote ? "Installation:" : "Quote:"}{" "}
          <strong style={{ color: textColor }}>
            {deal.hasQuote
              ? installationLabel || "-"
              : "No Quote Yet"}
          </strong>
          {deal.hasQuote && installationSummary.reminderEnabled ? (
            <span style={{ color: "#16a34a", fontWeight: 800 }}> | Reminder on</span>
          ) : null}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          alignItems: "center",
        }}
      >
        {renderCrmStageSelect(deal, {
          minWidth: dense ? "100%" : 180,
          flex: dense ? "1 1 100%" : "0 1 200px",
        })}

        <button
          type="button"
          onClick={() => openCrmDealDetails(deal, "crm_deal_list_card_details")}
          style={{
            ...btn("#0f172a"),
            marginRight: 0,
            padding: "9px 14px",
            background: isDark ? "#1e293b" : "#0f172a",
          }}
        >
          Details
        </button>

        {(deal.hasQuote ? canViewQuotes : canCreateQuotes) ? (
          <button
            type="button"
            onClick={() =>
              deal.hasQuote ? openCrmQuoteForDeal(deal) : startQuoteFromCrmLead(deal)
            }
            style={{
              ...btn("#2563eb"),
              marginRight: 0,
              padding: "9px 14px",
            }}
          >
            {deal.hasQuote ? "Open Quote" : "Create Quote"}
          </button>
        ) : null}
      </div>
    </div>
  );
  };

  const renderCrmDetailPanel = () => {
    const renderDetailsDebugPanel = () => {
      if (!IS_DEV_UI_ENABLED) return null;

      const cachedDeal = selectedCrmDealCacheRef.current;
      const selectedExistsInRecords = crmLeadRecords.some(
        (lead) => String(lead.id || "") === String(selectedCrmDealId || "")
      );
      const debugRows = [
        ["drawerOpen", String(isCrmDrawerPinned || isCrmDrawerHovered || isCompact)],
        ["selectedCrmDealId", selectedCrmDealId || "-"],
        ["selectedCrmDealCache.id", cachedDeal?.id || "-"],
        ["selectedCrmDeal.id", selectedCrmDeal?.id || "-"],
        [
          "selectedCrmDeal.customerName",
          selectedCrmDeal?.customerName ||
            selectedCrmDeal?.customerLabel ||
            selectedCrmDeal?.crmContact?.name ||
            "-",
        ],
        ["crmLeadRecords count", String(crmLeadRecords.length)],
        ["exists in crmLeadRecords", String(selectedExistsInRecords)],
        ["lastDetailsOpenSource", crmDetailsOpenDebug.lastSource || "-"],
        ["lastDetailsOpenRecordId", crmDetailsOpenDebug.lastRecordId || "-"],
        ["lastDetailsResolvedLeadId", crmDetailsOpenDebug.lastResolvedLeadId || "-"],
        ["lastDetailsResolveReason", crmDetailsOpenDebug.lastResolveReason || "-"],
        ["lastDetailsError", crmDetailsOpenDebug.lastError || "-"],
      ];

      return (
        <div
          style={{
            padding: 12,
            borderRadius: 16,
            border: "1px solid rgba(59,130,246,0.35)",
            background: isDark ? "rgba(30,64,175,0.16)" : "#eff6ff",
            display: "grid",
            gap: 8,
            fontSize: 12,
            color: dashboardMuted,
          }}
        >
          <button
            type="button"
            onClick={() => setCrmDetailsDebugExpanded((prev) => !prev)}
            style={{
              ...btn(isDark ? "#1e3a8a" : "#2563eb", "#ffffff"),
              marginRight: 0,
              justifyContent: "space-between",
            }}
          >
            Details Debug {crmDetailsDebugExpanded ? "Hide" : "Show"}
          </button>
          {crmDetailsDebugExpanded ? (
            <div style={{ display: "grid", gap: 4 }}>
              {debugRows.map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(120px, 0.45fr) minmax(0, 1fr)",
                    gap: 8,
                    minWidth: 0,
                  }}
                >
                  <strong style={{ color: textColor }}>{label}</strong>
                  <span style={{ wordBreak: "break-word" }}>{value}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      );
    };

    if (!selectedCrmDeal || !crmDetailDraft) {
      if (selectedCrmDealId || selectedCrmDealCacheRef.current) {
        return (
          <div style={{ display: "grid", gap: 12 }}>
            <div style={{ color: dashboardMuted, fontSize: 13, lineHeight: 1.7 }}>
              Loading the selected deal details...
            </div>
            {renderDetailsDebugPanel()}
          </div>
        );
      }

      return (
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ color: dashboardMuted, fontSize: 13, lineHeight: 1.7 }}>
            Select a CRM deal to view contact details, checklist progress, notes, and next actions.
          </div>
          {renderDetailsDebugPanel()}
        </div>
      );
    }

    const checklistProgress = getChecklistProgress(crmDetailDraft.checklistItems || []);
    const nextActionDate = parseDateInputValue(crmDetailDraft.nextActionDate);
    const installationDate = parseDateInputValue(crmDetailDraft.installationDate);
    const reminderConfigs = normalizeCrmReminderConfigs(
      crmDetailDraft.reminderConfigs,
      {
        nextActionDate,
        installationDate,
      }
    );
      const attachments = normalizeCrmAttachments(crmDetailDraft.attachments || []);
      const followUps = normalizeCrmFollowUps(crmDetailDraft.followUps || [], {
        nextAction: crmDetailDraft.nextAction || "",
        nextActionDate,
        reminderConfig: reminderConfigs[CRM_REMINDER_TYPE_TASK_DUE],
      });
      const linkedQuote = findLinkedQuoteForLead(selectedCrmDeal);
      const selectedPrimaryLinkedQuote = getPrimaryCrmLinkedQuote(
        selectedCrmDeal.linkedQuotes || []
      );
      const linkedQuoteMeta = linkedQuote
        ? getQuoteLinkDisplayData(linkedQuote)
        : {
            quoteNo: selectedPrimaryLinkedQuote?.quoteNo || selectedCrmDeal.quoteNo || "",
            quoteType: selectedPrimaryLinkedQuote?.type || selectedCrmDeal.quoteTypeResolved || selectedCrmDeal.pipelineType || "",
            quoteTypeLabel:
              selectedPrimaryLinkedQuote?.type === GENERAL_QUOTE_TYPE
                ? "General"
                : selectedPrimaryLinkedQuote?.type === CURTAIN_QUOTE_TYPE
                ? "Curtain"
                : selectedCrmDeal.pipelineType === GENERAL_QUOTE_TYPE
                ? "General"
                : "Curtain",
            quoteDateLabel: "-",
            totalValue: Number(
              selectedPrimaryLinkedQuote?.value ||
                selectedCrmDeal.finalValue ||
                selectedCrmDeal.expectedValue ||
                0
            ),
          };
      const executionSpecs = Array.isArray(crmDetailDraft.executionSpecs)
        ? crmDetailDraft.executionSpecs
        : [];
    const activeStageId = crmDetailDraft.stageId || selectedCrmDeal.crmStageId;
    const stageOptions = getStageSelectOptions(
      crmPipelines,
      selectedCrmDeal.pipelineType,
      activeStageId
    );
    const stageTemplate = getCrmStage(
      crmPipelines,
      selectedCrmDeal.pipelineType,
      activeStageId
    );
    const sectionCardStyle = {
      padding: 16,
      borderRadius: 18,
      background: dashboardSoftBg,
      border: dashboardBorder,
      display: "grid",
      gap: 12,
    };
    const siteLocations = normalizeSiteLocations(crmDetailDraft.siteLocations || []);
    const selectedCustomerId = String(
      selectedCrmDeal.customerId || selectedCrmDeal.crm?.customerId || ""
    ).trim();
    const resetCrmSiteLocationDraft = () =>
      setCrmSiteLocationDraft(createSiteLocationDraft());
    const persistCrmSiteLocations = async (nextLocations) => {
      const normalizedLocations = normalizeSiteLocations(nextLocations);
      updateCrmDetailDraft((prev) => ({
        ...prev,
        siteLocations: normalizedLocations,
      }));

      const leadId =
        selectedCrmDeal.crmRecordType === "lead"
          ? selectedCrmDeal.id
          : String(selectedCrmDeal.crm?.leadId || selectedCrmDeal.leadId || "").trim();

      try {
        if (leadId) {
          await saveCrmFieldsForLead(
            leadId,
            { siteLocations: normalizedLocations },
            {
              sourceLead:
                selectedCrmDeal.crmRecordType === "lead" ? selectedCrmDeal : null,
            }
          );
        } else {
          await saveCrmFieldsForDeal(selectedCrmDeal.id, {
            siteLocations: normalizedLocations,
          });
        }
        notify("Site location saved", "success");
      } catch (error) {
        console.error("Failed to save CRM site location:", error, {
          dealId: selectedCrmDeal.id,
          leadId,
        });
        notify("Could not save site location. Please try again.", "error");
      }
    };
    const saveCrmSiteLocationDraft = async () => {
      const hasLocationContent = [
        crmSiteLocationDraft.label,
        crmSiteLocationDraft.addressText,
        crmSiteLocationDraft.googleMapsUrl,
        crmSiteLocationDraft.notes,
      ].some((value) => String(value || "").trim());

      if (!hasLocationContent) {
        notify("Add a label, address, or map link before saving.", "warning");
        return;
      }

      const nowIso = new Date().toISOString();
      const userId = user?.uid || user?.email || "";
      const nextLocation = normalizeSiteLocation(
        {
          ...crmSiteLocationDraft,
          id: crmSiteLocationDraft.id || createClientId("site-location"),
          createdAt: crmSiteLocationDraft.createdAt || nowIso,
          createdBy: crmSiteLocationDraft.createdBy || userId,
          updatedAt: nowIso,
          updatedBy: userId,
        },
        siteLocations.length
      );
      const nextLocations = siteLocations.some(
        (location) => location.id === nextLocation.id
      )
        ? siteLocations.map((location) =>
            location.id === nextLocation.id
              ? nextLocation
              : nextLocation.isPrimary
              ? { ...location, isPrimary: false }
              : location
          )
        : [
            ...siteLocations.map((location) =>
              nextLocation.isPrimary ? { ...location, isPrimary: false } : location
            ),
            nextLocation,
          ];

      await persistCrmSiteLocations(nextLocations);
      resetCrmSiteLocationDraft();
    };
    const editCrmSiteLocation = (location) => {
      setCrmSiteLocationDraft(createSiteLocationDraft(location));
    };
    const deleteCrmSiteLocation = async (locationId) => {
      await persistCrmSiteLocations(
        siteLocations.filter((location) => location.id !== locationId)
      );
    };
    const copyCrmLocationToCustomerLedger = async (location) => {
      if (!selectedCustomerId) {
        notify("Link this deal to a customer before syncing the location.", "info");
        return;
      }
      const customer = customerRecords.find(
        (entry) => entry.id === selectedCustomerId
      );
      const currentLocations = normalizeSiteLocations(customer?.siteLocations || []);
      const nextLocation = normalizeSiteLocation({
        ...location,
        id: createClientId("customer-site-location"),
        isPrimary: currentLocations.length === 0 || location.isPrimary === true,
        createdAt: new Date().toISOString(),
        createdBy: user?.uid || user?.email || "",
        updatedAt: new Date().toISOString(),
        updatedBy: user?.uid || user?.email || "",
      });
      const saved = await saveCustomerSiteLocations(selectedCustomerId, [
        ...currentLocations,
        nextLocation,
      ]);
      if (saved) notify("Location saved to customer ledger", "success");
    };

    const renderReminderManager = (
      reminderType,
      {
        dueFieldLabel,
        dueValue,
        helperText,
      }
    ) => {
      const rawConfig = reminderConfigs[reminderType];
      const config =
        reminderType === CRM_REMINDER_TYPE_INSTALLATION
          ? buildCanonicalInstallationReminder(
              {
                ...crmDetailDraft,
                installationReminder:
                  crmDetailDraft.installationReminder ||
                  crmDetailDraft.reminderConfig ||
                  rawConfig,
                reminderConfig: crmDetailDraft.reminderConfig || rawConfig,
              },
              {
                installationDate: parseDateInputValue(crmDetailDraft.installationDate),
                reminderConfigs,
              }
            )
          : rawConfig;

      return (
        <div
          style={{
            padding: 14,
            borderRadius: 16,
            background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
            border: dashboardBorder,
            display: "grid",
            gap: 10,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div>
              <div style={{ fontSize: 14, fontWeight: 800 }}>
                {getCrmReminderTypeLabel(reminderType)}
              </div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.5 }}>
                {helperText}
              </div>
            </div>
            <label
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              <input
                type="checkbox"
                checked={
                  reminderType === CRM_REMINDER_TYPE_INSTALLATION
                    ? Boolean(crmDetailDraft.installationReminder?.enabled)
                    : config?.reminderEnabled === true
                }
                onChange={(event) =>
                  updateCrmReminderConfig(reminderType, {
                    enabled: event.target.checked,
                    reminderEnabled: event.target.checked,
                    status: event.target.checked
                      ? CRM_REMINDER_STATUS_ACTIVE
                      : CRM_REMINDER_STATUS_DISABLED,
                    completedAt: event.target.checked ? null : config?.completedAt || null,
                  })
                }
              />
              Reminder Enabled
            </label>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact
                ? "1fr"
                : "repeat(auto-fit, minmax(160px, 1fr))",
              gap: 10,
            }}
          >
            <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
              <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                Reminder Date
              </div>
              <input
                type="date"
                value={config?.reminderDate ?? ""}
                onChange={(event) =>
                  updateCrmReminderConfig(reminderType, {
                    reminderDate: event.target.value,
                    status:
                      config?.reminderEnabled === true
                        ? CRM_REMINDER_STATUS_ACTIVE
                        : config?.status,
                    completedAt: null,
                  })
                }
                style={{ ...input, width: "100%", minWidth: 0 }}
              />
            </div>
            <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
              <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                Reminder Time
              </div>
              <input
                type="time"
                value={config?.reminderTime ?? DEFAULT_CRM_REMINDER_TIME}
                onChange={(event) =>
                  updateCrmReminderConfig(reminderType, {
                    reminderTime: parseTimeInputValue(event.target.value),
                    status:
                      config?.reminderEnabled === true
                        ? CRM_REMINDER_STATUS_ACTIVE
                        : config?.status,
                    completedAt: null,
                  })
                }
                style={{ ...input, width: "100%", minWidth: 0 }}
              />
            </div>
            <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
              <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                Repeat
              </div>
              <select
                value={config?.repeatKey ?? "daily"}
                onChange={(event) =>
                  updateCrmReminderConfig(reminderType, {
                    repeatKey: event.target.value,
                    status:
                      config?.reminderEnabled === true
                        ? CRM_REMINDER_STATUS_ACTIVE
                        : config?.status,
                    completedAt: null,
                  })
                }
                style={{ ...selectInput, width: "100%", minWidth: 0 }}
              >
                {CRM_REMINDER_REPEAT_OPTIONS.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              alignItems: "center",
              fontSize: 12,
              color: dashboardMuted,
            }}
          >
            <span>
              {dueFieldLabel}: <strong style={{ color: textColor }}>{dueValue || "-"}</strong>
            </span>
            <span>
              Reminder fires at{" "}
              <strong style={{ color: textColor }}>
                {config?.reminderDate
                  ? formatReminderDateTime(
                      combineReminderDateTime(
                        config?.reminderDate,
                        config?.reminderTime
                      )
                    )
                  : "-"}
              </strong>
            </span>
            {config?.status === CRM_REMINDER_STATUS_DONE ? (
              <span
                style={{
                  padding: "4px 8px",
                  borderRadius: 999,
                  background: isDark ? "rgba(22,163,74,0.18)" : "#dcfce7",
                  color: isDark ? "#86efac" : "#166534",
                  fontWeight: 700,
                }}
              >
                Marked Done
              </span>
            ) : null}
          </div>
        </div>
      );
    };

    const debugStringify = (value) => {
      try {
        return JSON.stringify(
          value ?? null,
          (_key, item) => {
            if (item instanceof Date) return item.toISOString();
            if (item && typeof item.toDate === "function") {
              try {
                return item.toDate().toISOString();
              } catch (_error) {
                return item;
              }
            }
            return item;
          },
          2
        );
      } catch (error) {
        return `Could not render debug JSON: ${error?.message || error}`;
      }
    };
    const getDebugChecklistItems = (source) =>
      source?.checklistItems || source?.checklist || source?.stageChecklist || [];
    const renderDebugJson = (value) => (
      <pre
        style={{
          margin: 0,
          padding: 10,
          borderRadius: 12,
          background: isDark ? "rgba(15,23,42,0.78)" : "#f8fafc",
          border: dashboardBorder,
          color: isDark ? "#e2e8f0" : "#0f172a",
          fontSize: 11,
          lineHeight: 1.45,
          maxHeight: 180,
          overflow: "auto",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {debugStringify(value)}
      </pre>
    );
    const renderCrmSaveDebugPanel = () => {
      if (!IS_DEV_UI_ENABLED) return null;

      const draftChecklistProgress = getChecklistProgress(
        crmDetailDraft?.checklistItems || []
      );
      const payloadChecklistProgress = getChecklistProgress(
        getDebugChecklistItems(crmSaveDebugState.lastPayload)
      );
      const immediateChecklistProgress = getChecklistProgress(
        getDebugChecklistItems(crmSaveDebugState.lastImmediateReadback)
      );
      const delayedChecklistProgress = getChecklistProgress(
        getDebugChecklistItems(crmSaveDebugState.lastDelayedReadback)
      );
      const selectedCrmDealCache = selectedCrmDealCacheRef.current;

      return (
        <div
          style={{
            padding: 14,
            borderRadius: 18,
            border: "1px solid rgba(245,158,11,0.35)",
            background: isDark ? "rgba(120,53,15,0.18)" : "#fffbeb",
            display: "grid",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => setCrmSaveDebugExpanded((prev) => !prev)}
              style={{
                ...btn(isDark ? "#78350f" : "#f59e0b", isDark ? "#fde68a" : "#111827"),
                marginRight: 0,
                justifyContent: "space-between",
              }}
            >
              CRM Save Debug {crmSaveDebugExpanded ? "Hide" : "Show"}
            </button>
            <button
              type="button"
              onClick={testDirectCrmDetailsSave}
              style={{
                ...btn(isDark ? "#0f172a" : "#111827", "#ffffff"),
                marginRight: 0,
              }}
            >
              Test Direct Save
            </button>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))",
              gap: 10,
              fontSize: 12,
              color: dashboardMuted,
            }}
          >
            <div>
              <strong style={{ color: textColor }}>Selected identity</strong>
              <div>workspaceUid: {workspaceUid || "-"}</div>
              <div>selectedCrmDealId: {selectedCrmDealId || "-"}</div>
              <div>selectedCrmDealCache.id: {selectedCrmDealCache?.id || "-"}</div>
              <div>selectedCrmDeal.id: {selectedCrmDeal?.id || "-"}</div>
              <div>
                path:{" "}
                {selectedCrmDealId
                  ? `users/${workspaceUid}/crm_leads/${selectedCrmDealId}`
                  : "-"}
              </div>
            </div>
            <div>
              <strong style={{ color: textColor }}>Verification</strong>
              <div>status: {crmSaveDebugState.lastVerification?.status || "-"}</div>
              <div>last write path: {crmSaveDebugState.lastWritePath || "-"}</div>
              <div>
                immediate source:{" "}
                {crmSaveDebugState.lastVerification?.immediateLastWriteSource || "-"}
              </div>
              <div>
                delayed source:{" "}
                {crmSaveDebugState.lastVerification?.delayedLastWriteSource || "-"}
              </div>
            </div>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact ? "1fr" : "repeat(4, minmax(0, 1fr))",
              gap: 8,
            }}
          >
            {[
              ["Draft", draftChecklistProgress],
              ["Payload", payloadChecklistProgress],
              ["Read-back", immediateChecklistProgress],
              ["Delayed", delayedChecklistProgress],
            ].map(([label, progress]) => (
              <div
                key={label}
                style={{
                  padding: 10,
                  borderRadius: 14,
                  background: isDark ? "rgba(255,255,255,0.05)" : "#ffffff",
                  border: dashboardBorder,
                  fontSize: 12,
                }}
              >
                <div style={{ color: dashboardMuted, fontWeight: 800 }}>{label}</div>
                <div style={{ marginTop: 4, color: textColor, fontWeight: 900 }}>
                  {progress.completed}/{progress.total} checklist
                </div>
              </div>
            ))}
          </div>
          {crmSaveDebugExpanded ? (
            <div style={{ display: "grid", gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>
                  Draft values
                </div>
                {renderDebugJson({
                  checklist: crmDetailDraft?.checklist,
                  checklistItems: crmDetailDraft?.checklistItems,
                  stageChecklist: crmDetailDraft?.stageChecklist,
                  installationDate: crmDetailDraft?.installationDate,
                  installationReminder: crmDetailDraft?.installationReminder,
                  reminderConfig: crmDetailDraft?.reminderConfig,
                  reminderConfigsInstallation:
                    crmDetailDraft?.reminderConfigs?.[CRM_REMINDER_TYPE_INSTALLATION],
                })}
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>
                  Last save payload
                </div>
                {renderDebugJson({
                  checklist: crmSaveDebugState.lastPayload?.checklist,
                  checklistItems: crmSaveDebugState.lastPayload?.checklistItems,
                  stageChecklist: crmSaveDebugState.lastPayload?.stageChecklist,
                  installationReminder:
                    crmSaveDebugState.lastPayload?.installationReminder,
                  reminderConfig: crmSaveDebugState.lastPayload?.reminderConfig,
                  reminderConfigsInstallation:
                    crmSaveDebugState.lastPayload?.reminderConfigs?.[
                      CRM_REMINDER_TYPE_INSTALLATION
                    ],
                })}
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>
                  Immediate Firestore read-back
                </div>
                {renderDebugJson({
                  checklist: crmSaveDebugState.lastImmediateReadback?.checklist,
                  checklistItems:
                    crmSaveDebugState.lastImmediateReadback?.checklistItems,
                  stageChecklist:
                    crmSaveDebugState.lastImmediateReadback?.stageChecklist,
                  installationDate:
                    crmSaveDebugState.lastImmediateReadback?.installationDate,
                  installationReminder:
                    crmSaveDebugState.lastImmediateReadback?.installationReminder,
                  reminderConfig:
                    crmSaveDebugState.lastImmediateReadback?.reminderConfig,
                  reminderConfigsInstallation:
                    crmSaveDebugState.lastImmediateReadback?.reminderConfigs?.[
                      CRM_REMINDER_TYPE_INSTALLATION
                    ],
                  updatedAt: crmSaveDebugState.lastImmediateReadback?.updatedAt,
                  _lastWriteSource:
                    crmSaveDebugState.lastImmediateReadback?._lastWriteSource,
                })}
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>
                  Delayed Firestore read-back
                </div>
                {renderDebugJson({
                  checklist: crmSaveDebugState.lastDelayedReadback?.checklist,
                  checklistItems:
                    crmSaveDebugState.lastDelayedReadback?.checklistItems,
                  stageChecklist:
                    crmSaveDebugState.lastDelayedReadback?.stageChecklist,
                  installationDate:
                    crmSaveDebugState.lastDelayedReadback?.installationDate,
                  installationReminder:
                    crmSaveDebugState.lastDelayedReadback?.installationReminder,
                  reminderConfig:
                    crmSaveDebugState.lastDelayedReadback?.reminderConfig,
                  reminderConfigsInstallation:
                    crmSaveDebugState.lastDelayedReadback?.reminderConfigs?.[
                      CRM_REMINDER_TYPE_INSTALLATION
                    ],
                  updatedAt: crmSaveDebugState.lastDelayedReadback?.updatedAt,
                  _lastWriteSource:
                    crmSaveDebugState.lastDelayedReadback?._lastWriteSource,
                })}
              </div>
            </div>
          ) : null}
        </div>
      );
    };

    return (
      <div style={{ display: "grid", gap: 18 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            flexDirection: isCompact ? "column" : "row",
          }}
        >
          <div>
            <div style={{ fontSize: 20, fontWeight: 900 }}>
              {selectedCrmDeal.crmTitle}
            </div>
            <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
              {selectedCrmDeal.hasQuote
                ? `#${selectedPrimaryLinkedQuote?.quoteNo || selectedCrmDeal.quoteNo || "-"} | ${selectedCrmDeal.customerLabel}`
                : `Lead | ${selectedCrmDeal.customerLabel}`}
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <button
              type="button"
              onClick={() => {
                if (selectedCrmDeal.customerId) {
                  openCustomerLedger(selectedCrmDeal.customerId);
                  return;
                }
                crmContactSectionRef.current?.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
              }}
              style={{
                ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"),
                marginRight: 0,
              }}
            >
              View Customer
            </button>
            {(selectedCrmDeal.hasQuote ? canViewQuotes : canCreateQuotes) ? (
              <button
                type="button"
                onClick={() =>
                  selectedCrmDeal.hasQuote
                    ? openCrmQuoteForDeal(selectedCrmDeal)
                    : startQuoteFromCrmLead(selectedCrmDeal)
                }
                style={{ ...btn("#2563eb"), marginRight: 0 }}
              >
                {selectedCrmDeal.hasQuote ? "Open Quote" : "Create Quote"}
              </button>
            ) : null}
            <button
              type="button"
              onClick={handleSaveCrmDetailsCanonical}
              disabled={isSavingCrmDetails}
              style={{ ...btn("#16a34a"), marginRight: 0 }}
            >
              {isSavingCrmDetails ? "Saving..." : "Save CRM Details"}
            </button>
            {crmDetailsSavedAt ? (
              <div style={{ fontSize: 12, color: dashboardMuted, fontWeight: 700 }}>
                Saved {crmDetailsSavedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </div>
            ) : null}
          </div>
        </div>

        {renderDetailsDebugPanel()}

        {renderCrmSaveDebugPanel()}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: 14,
          }}
        >
          <div style={{ ...sectionCardStyle, gridTemplateColumns: "1fr", gap: 14 }}>
            <div style={{ display: "grid", gap: 14 }}>
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontSize: 12, color: dashboardMuted, fontWeight: 700 }}>
                  Current Stage
                </div>
                <select
                  value={activeStageId}
                  onChange={(e) => updateWorkStage(selectedCrmDeal.id, e.target.value)}
                  style={selectInput}
                >
                  {stageOptions.map((stage) => (
                    <option key={stage.id} value={stage.id}>
                      {stage.label}
                    </option>
                  ))}
                </select>
                <div style={{ fontSize: 11, color: dashboardMuted }}>
                  Stage changes now refresh the stage checklist and progress in this panel immediately.
                </div>
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontSize: 12, color: dashboardMuted, fontWeight: 700 }}>
                  Assigned Team Member
                </div>
                <select
                  value={crmDetailDraft.assignedToUserId || ""}
                  onChange={(e) => updateCrmDealAssignment(selectedCrmDeal.id, e.target.value)}
                  style={selectInput}
                >
                  <option value="">Unassigned</option>
                  {getAssignableTeamMembers(crmDetailDraft.assignedToUserId).map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.name || "Unnamed Team Member"}
                      {member.role ? ` | ${member.role}` : ""}
                      {member.isActive === false ? " | Archived" : ""}
                    </option>
                  ))}
                </select>
                <div style={{ fontSize: 11, color: dashboardMuted, marginTop: 2 }}>
                  {teamMembers.length === 0
                    ? "Manage assignable staff from Team & Access."
                    : `Current assignee: ${getTeamMemberLabel(
                        crmDetailDraft.assignedToUserId,
                        crmDetailDraft.assignedToNameSnapshot
                      )}`}
                </div>
              </div>
            </div>
          </div>

          <div style={sectionCardStyle}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                flexDirection: isCompact ? "column" : "row",
                alignItems: isCompact ? "stretch" : "center",
              }}
            >
              <div>
                <div style={{ fontSize: 16, fontWeight: 800 }}>Linked Quote</div>
                <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                  Connect this lead to an existing curtain or general quotation.
                </div>
              </div>
              {selectedCrmDeal.crmRecordType === "lead" ? (
                <button
                  type="button"
                  onClick={() => openQuoteLinkModal(selectedCrmDeal)}
                  style={{ ...btn("#2563eb"), marginRight: 0 }}
                >
                  <Link2 size={14} style={{ marginRight: 6 }} />
                  {selectedCrmDeal.hasQuote ? "Change Quote" : "Link Quote"}
                </button>
              ) : null}
            </div>

            {selectedCrmDeal.hasQuote ? (
              <div
                style={{
                  padding: 14,
                  borderRadius: 18,
                  background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                  border: dashboardBorder,
                  display: "grid",
                  gap: 12,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                    alignItems: "flex-start",
                  }}
                >
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 900 }}>
                      #{linkedQuoteMeta.quoteNo || "-"}
                    </div>
                    <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                      {linkedQuoteMeta.quoteTypeLabel || "Quotation"} |{" "}
                      {currencySymbol(currency)}{" "}
                      {Number(linkedQuoteMeta.totalValue || 0).toFixed(2)}
                    </div>
                  </div>
                  <div
                    style={{
                      padding: "7px 11px",
                      borderRadius: 999,
                      background: isDark ? "rgba(37,99,235,0.18)" : "#dbeafe",
                      color: isDark ? "#bfdbfe" : "#1d4ed8",
                      fontSize: 11,
                      fontWeight: 800,
                    }}
                  >
                    Linked
                  </div>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isCompact ? "1fr" : "repeat(3, minmax(0, 1fr))",
                    gap: 10,
                  }}
                >
                  {[
                    ["Quote Date", linkedQuoteMeta.quoteDateLabel || "-"],
                    ["Quote Type", linkedQuoteMeta.quoteTypeLabel || "-"],
                    [
                      "Quote Value",
                      `${currencySymbol(currency)} ${Number(linkedQuoteMeta.totalValue || 0).toFixed(2)}`,
                    ],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      style={{
                        padding: 12,
                        borderRadius: 16,
                        background: dashboardSoftBg,
                        border: dashboardBorder,
                      }}
                    >
                      <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                        {label}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 800, marginTop: 6 }}>
                        {value}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {canViewQuotes ? (
                    <button
                      type="button"
                      onClick={() => openCrmQuoteForDeal(selectedCrmDeal)}
                      style={{ ...btn("#0f172a"), marginRight: 0 }}
                    >
                      <ExternalLink size={14} style={{ marginRight: 6 }} />
                      Open Quote
                    </button>
                  ) : null}
                  {selectedCrmDeal.crmRecordType === "lead" ? (
                    <>
                      <button
                        type="button"
                        onClick={() => openQuoteLinkModal(selectedCrmDeal)}
                        style={{ ...btn("#2563eb"), marginRight: 0 }}
                      >
                        Change Quote
                      </button>
                      <button
                        type="button"
                        onClick={() => requestUnlinkQuoteFromLead(selectedCrmDeal)}
                        disabled={isLinkingQuote}
                        style={{ ...btn("#ef4444"), marginRight: 0 }}
                      >
                        <Unlink size={14} style={{ marginRight: 6 }} />
                        Unlink Quote
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            ) : (
              <div
                style={{
                  padding: 14,
                  borderRadius: 18,
                  background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                  border: dashboardBorder,
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  flexDirection: isCompact ? "column" : "row",
                  alignItems: isCompact ? "stretch" : "center",
                }}
              >
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800 }}>No quote linked yet</div>
                  <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                    Link an existing quotation or create a new one from this lead.
                  </div>
                </div>
                {selectedCrmDeal.crmRecordType === "lead" ? (
                  <button
                    type="button"
                    onClick={() => openQuoteLinkModal(selectedCrmDeal)}
                    style={{ ...btn("#2563eb"), marginRight: 0 }}
                  >
                    <Link2 size={14} style={{ marginRight: 6 }} />
                    Link Quote
                  </button>
                ) : null}
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: 16,
          }}
        >
          <div
            ref={crmContactSectionRef}
            style={sectionCardStyle}
          >
            <div style={{ fontSize: 16, fontWeight: 800 }}>Contact</div>
            <input
              value={crmDetailDraft.contact?.name || ""}
              onChange={(e) =>
                updateCrmDetailDraft((prev) => ({
                  ...prev,
                  contact: { ...prev.contact, name: e.target.value },
                }))
              }
              style={input}
              placeholder="Client / company name"
            />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))",
                gap: 10,
              }}
            >
              <div style={{ position: "relative", minWidth: 0 }}>
                <Phone
                  size={14}
                  style={{
                    position: "absolute",
                    left: 10,
                    top: 10,
                    color: dashboardMuted,
                  }}
                />
                <input
                  value={crmDetailDraft.contact?.phone || ""}
                  onChange={(e) =>
                    updateCrmDetailDraft((prev) => ({
                      ...prev,
                      contact: { ...prev.contact, phone: e.target.value },
                    }))
                  }
                  style={{ ...input, paddingLeft: 32 }}
                  placeholder="Phone"
                />
              </div>
              <div style={{ position: "relative", minWidth: 0 }}>
                <Phone
                  size={14}
                  style={{
                    position: "absolute",
                    left: 10,
                    top: 10,
                    color: dashboardMuted,
                  }}
                />
                <input
                  value={crmDetailDraft.contact?.whatsapp || ""}
                  onChange={(e) =>
                    updateCrmDetailDraft((prev) => ({
                      ...prev,
                      contact: { ...prev.contact, whatsapp: e.target.value },
                    }))
                  }
                  style={{ ...input, paddingLeft: 32 }}
                  placeholder="WhatsApp"
                />
              </div>
            </div>
            <div style={{ position: "relative", minWidth: 0 }}>
              <Mail
                size={14}
                style={{
                  position: "absolute",
                  left: 10,
                  top: 10,
                  color: dashboardMuted,
                }}
              />
              <input
                value={crmDetailDraft.contact?.email || ""}
                onChange={(e) =>
                  updateCrmDetailDraft((prev) => ({
                    ...prev,
                    contact: { ...prev.contact, email: e.target.value },
                  }))
                }
                style={{ ...input, paddingLeft: 32 }}
                placeholder="Email"
              />
            </div>
            <textarea
              value={crmDetailDraft.contact?.address || ""}
              onChange={(e) =>
                updateCrmDetailDraft((prev) => ({
                  ...prev,
                  contact: { ...prev.contact, address: e.target.value },
                }))
              }
              style={{ ...input, minHeight: 82, resize: "vertical" }}
              placeholder="Address"
            />
            <input
              value={crmDetailDraft.contact?.source || ""}
              onChange={(e) =>
                updateCrmDetailDraft((prev) => ({
                  ...prev,
                  contact: { ...prev.contact, source: e.target.value },
                }))
              }
              style={input}
              placeholder="Lead source"
            />
          </div>

          <div style={sectionCardStyle}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                flexDirection: isCompact ? "column" : "row",
                alignItems: isCompact ? "stretch" : "center",
              }}
            >
              <div>
                <div style={{ fontSize: 16, fontWeight: 800 }}>Site Location</div>
                <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                  Save Google Maps or WhatsApp location links so your team can find the site later.
                </div>
              </div>
              {siteLocations.length ? (
                <button
                  type="button"
                  onClick={resetCrmSiteLocationDraft}
                  style={{ ...btn("#2563eb"), marginRight: 0 }}
                >
                  Add Another Location
                </button>
              ) : null}
            </div>

            {siteLocations.length === 0 ? (
              <div
                style={{
                  padding: 14,
                  borderRadius: 16,
                  background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                  border: dashboardBorder,
                  color: dashboardMuted,
                  fontSize: 12,
                  lineHeight: 1.6,
                }}
              >
                <strong style={{ color: textColor }}>No site location saved yet.</strong>
                <br />
                Paste a Google Maps or WhatsApp location link, or just save the address/landmark.
              </div>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {siteLocations.map((location) => (
                  <div
                    key={location.id}
                    style={{
                      padding: 14,
                      borderRadius: 18,
                      background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                      border: dashboardBorder,
                      display: "grid",
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 10,
                        flexWrap: "wrap",
                        alignItems: "flex-start",
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                          <span style={{ fontSize: 14, fontWeight: 900 }}>
                            {location.label || "Site Location"}
                          </span>
                          {location.isPrimary ? (
                            <span
                              style={{
                                padding: "4px 8px",
                                borderRadius: 999,
                                background: isDark ? "rgba(16,185,129,0.18)" : "#dcfce7",
                                color: isDark ? "#86efac" : "#166534",
                                fontSize: 11,
                                fontWeight: 800,
                              }}
                            >
                              Primary
                            </span>
                          ) : null}
                        </div>
                        {location.addressText ? (
                          <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 5, lineHeight: 1.5 }}>
                            {location.addressText}
                          </div>
                        ) : null}
                        {location.notes ? (
                          <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.5 }}>
                            Notes: {location.notes}
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        onClick={() => openSiteLocation(location)}
                        style={{ ...btn("#0f172a"), marginRight: 0 }}
                      >
                        Open Map
                      </button>
                      <button
                        type="button"
                        onClick={() => copySiteLocationLink(location)}
                        style={{ ...btn("#2563eb"), marginRight: 0 }}
                      >
                        Copy Link
                      </button>
                      <button
                        type="button"
                        onClick={() => editCrmSiteLocation(location)}
                        style={{ ...btn("#64748b"), marginRight: 0 }}
                      >
                        Edit
                      </button>
                      {selectedCustomerId ? (
                        <button
                          type="button"
                          onClick={() => copyCrmLocationToCustomerLedger(location)}
                          style={{ ...btn("#10b981"), marginRight: 0 }}
                        >
                          Save to Customer Ledger
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => deleteCrmSiteLocation(location.id)}
                        style={{ ...btn("#ef4444"), marginRight: 0 }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div
              style={{
                padding: 14,
                borderRadius: 18,
                background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                border: dashboardBorder,
                display: "grid",
                gap: 10,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 850 }}>
                {crmSiteLocationDraft.id ? "Edit Location" : "Add Location"}
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))",
                  gap: 10,
                }}
              >
                <input
                  value={crmSiteLocationDraft.label}
                  onChange={(event) =>
                    setCrmSiteLocationDraft((prev) => ({
                      ...prev,
                      label: event.target.value,
                    }))
                  }
                  style={input}
                  placeholder="Location label, e.g. Villa Site"
                />
                <input
                  value={crmSiteLocationDraft.googleMapsUrl}
                  onChange={(event) =>
                    setCrmSiteLocationDraft((prev) => ({
                      ...prev,
                      googleMapsUrl: event.target.value,
                    }))
                  }
                  style={input}
                  placeholder="Google Maps / WhatsApp location link"
                />
              </div>
              <textarea
                value={crmSiteLocationDraft.addressText}
                onChange={(event) =>
                  setCrmSiteLocationDraft((prev) => ({
                    ...prev,
                    addressText: event.target.value,
                  }))
                }
                style={{ ...input, minHeight: 72, resize: "vertical" }}
                placeholder="Address / landmark"
              />
              <textarea
                value={crmSiteLocationDraft.notes}
                onChange={(event) =>
                  setCrmSiteLocationDraft((prev) => ({
                    ...prev,
                    notes: event.target.value,
                  }))
                }
                style={{ ...input, minHeight: 62, resize: "vertical" }}
                placeholder="Notes for installers or measurement team"
              />
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 12,
                  fontWeight: 800,
                }}
              >
                <input
                  type="checkbox"
                  checked={crmSiteLocationDraft.isPrimary === true}
                  onChange={(event) =>
                    setCrmSiteLocationDraft((prev) => ({
                      ...prev,
                      isPrimary: event.target.checked,
                    }))
                  }
                />
                Primary Location
              </label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={saveCrmSiteLocationDraft}
                  style={{ ...btn("#2563eb"), marginRight: 0 }}
                >
                  Save Location
                </button>
                <button
                  type="button"
                  onClick={resetCrmSiteLocationDraft}
                  style={{ ...btn("#64748b"), marginRight: 0 }}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>

          <div
            style={sectionCardStyle}
          >
            <div style={{ fontSize: 16, fontWeight: 800 }}>Deal Actions</div>
            <input
              value={crmDetailDraft.title || ""}
              onChange={(e) =>
                updateCrmDetailDraft((prev) => ({
                  ...prev,
                  title: e.target.value,
                }))
              }
              style={input}
              placeholder="Deal title"
            />
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontSize: 12, color: dashboardMuted, fontWeight: 700 }}>
                  Installation Date
                </div>
                <div style={{ position: "relative", minWidth: 0 }}>
                  <CalendarDays
                    size={14}
                    style={{
                      position: "absolute",
                      left: 10,
                      top: 10,
                      color: dashboardMuted,
                    }}
                  />
                  <input
                    type="date"
                    value={crmDetailDraft.installationDate || ""}
                    onChange={(e) => {
                      const nextInstallationDate = e.target.value;
                      updateCrmDetailDraft((prev) => {
                        const nextReminderConfigs = normalizeCrmReminderConfigs(
                          prev?.reminderConfigs,
                          {
                            nextActionDate: parseDateInputValue(prev?.nextActionDate),
                            installationDate: parseDateInputValue(nextInstallationDate),
                          }
                        );
                        const nextInstallationReminder =
                          buildCanonicalInstallationReminder(
                            {
                              ...prev,
                              installationDate: nextInstallationDate,
                              reminderConfigs: nextReminderConfigs,
                            },
                            {
                              installationDate: parseDateInputValue(nextInstallationDate),
                              reminderConfigs: nextReminderConfigs,
                            }
                          );

                        if (isDevRuntime) {
                          console.log("INSTALLATION REMINDER TRACE", {
                            selectedCrmDealId,
                            enabledFromEvent: nextInstallationReminder?.enabled === true,
                            beforeDraftInstallationReminder:
                              prev.installationReminder,
                            afterDraftInstallationReminder:
                              nextInstallationReminder,
                          });
                          console.log("INSTALLATION REMINDER UI CHANGE", {
                            selectedCrmDealId,
                            installationDate: nextInstallationDate,
                            installationReminder: nextInstallationReminder,
                            reminderConfig: nextInstallationReminder,
                          });
                        }

                        return {
                          ...prev,
                          installationDate: nextInstallationDate,
                          reminderConfigs: nextReminderConfigs,
                          installationReminder: nextInstallationReminder,
                          reminderConfig: nextInstallationReminder,
                        };
                      });
                    }}
                    style={{ ...input, paddingLeft: 32 }}
                  />
                </div>
                <div style={{ fontSize: 11, color: dashboardMuted }}>
                  Use this for installation scheduling and installation reminders.
                </div>
              </div>
            </div>
            <div
              style={{
                padding: 12,
                borderRadius: 16,
                background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                border: dashboardBorder,
                display: "grid",
                gap: 4,
              }}
            >
              <div style={{ fontSize: 11, color: dashboardMuted }}>Follow-up Summary</div>
              <div style={{ fontWeight: 800 }}>
                {followUps.length > 0
                  ? `${followUps.filter((item) => item.status !== CRM_FOLLOW_UP_STATUS_DONE).length} pending`
                  : "No follow-ups yet"}
              </div>
              <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                Add, schedule, and complete tasks from the Follow-ups section below.
              </div>
            </div>
            <input
              value={crmDetailDraft.tags || ""}
              onChange={(e) =>
                updateCrmDetailDraft((prev) => ({
                  ...prev,
                  tags: e.target.value,
                }))
              }
              style={input}
              placeholder="Tags, comma separated"
            />
            <textarea
              value={crmDetailDraft.notes || ""}
              onChange={(e) =>
                updateCrmDetailDraft((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))
              }
              style={{ ...input, minHeight: 120, resize: "vertical" }}
              placeholder="Internal notes"
            />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: 10,
              }}
            >
              <div
                style={{
                  padding: 12,
                  borderRadius: 16,
                  background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                  border: dashboardBorder,
                }}
              >
                <div style={{ fontSize: 11, color: dashboardMuted }}>Expected</div>
                <div style={{ fontWeight: 800, marginTop: 4 }}>
                  {currencySymbol(currency)} {Number(selectedCrmDeal.expectedValue || 0).toFixed(2)}
                </div>
              </div>
              <div
                style={{
                  padding: 12,
                  borderRadius: 16,
                  background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                  border: dashboardBorder,
                }}
              >
                <div style={{ fontSize: 11, color: dashboardMuted }}>Final</div>
                <div style={{ fontWeight: 800, marginTop: 4 }}>
                  {currencySymbol(currency)} {Number(selectedCrmDeal.finalValue || 0).toFixed(2)}
                </div>
              </div>
              <div
                style={{
                  padding: 12,
                  borderRadius: 16,
                  background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                  border: dashboardBorder,
                }}
              >
                <div style={{ fontSize: 11, color: dashboardMuted }}>Progress</div>
                <div style={{ fontWeight: 800, marginTop: 4 }}>
                  {checklistProgress.completed}/{checklistProgress.total || 0}
                </div>
              </div>
            </div>
            <div
              style={{
                padding: 12,
                borderRadius: 16,
                background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                border: dashboardBorder,
                display: "grid",
                gap: 4,
              }}
            >
              <div style={{ fontSize: 11, color: dashboardMuted }}>Assigned Team Member</div>
              <div style={{ fontWeight: 800 }}>
                {getTeamMemberLabel(
                  crmDetailDraft.assignedToUserId,
                  crmDetailDraft.assignedToNameSnapshot
                )}
              </div>
            </div>
          </div>
        </div>

        <div style={sectionCardStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 16, fontWeight: 800 }}>Follow-ups / Tasks</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                Track multiple callbacks, visits, approvals, and delivery tasks for this lead without squeezing everything into one reminder.
              </div>
            </div>
            <button
              type="button"
              onClick={() => openCrmFollowUpModal()}
              style={{ ...btn("#2563eb"), marginRight: 0 }}
            >
              + Add Follow-up
            </button>
          </div>

          {followUps.length === 0 ? (
            <div
              style={{
                padding: 16,
                borderRadius: 18,
                background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                border: dashboardBorder,
                display: "grid",
                gap: 8,
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 800 }}>No follow-ups added yet</div>
              <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                Add your first follow-up to schedule callbacks, approvals, site work, or delivery tasks for this deal.
              </div>
            </div>
          ) : (
            <div style={{ display: "grid", gap: 12 }}>
              {followUps.map((followUp, index) => {
                const isDone = followUp.status === CRM_FOLLOW_UP_STATUS_DONE;
                const dueDateTime = formatReminderDateTime(
                  combineReminderDateTime(followUp.dueDate, followUp.dueTime) ||
                    parseDateInputValue(followUp.dueDate)
                );
                const dueMillis = safeTimeMillis(
                  combineReminderDateTime(followUp.dueDate, followUp.dueTime) ||
                    parseDateInputValue(followUp.dueDate)
                );
                const isOverdue = !isDone && dueMillis > 0 && dueMillis < Date.now();
                const attachmentCount = normalizeCrmAttachments(
                  followUp.attachments || []
                ).length;
                const priorityOption =
                  CRM_FOLLOW_UP_PRIORITY_OPTIONS.find(
                    (option) => option.key === followUp.priority
                  ) || CRM_FOLLOW_UP_PRIORITY_OPTIONS.find(
                    (option) => option.key === CRM_FOLLOW_UP_PRIORITY_MEDIUM
                  );
                const priorityAccent =
                  followUp.priority === "high"
                    ? "#dc2626"
                    : followUp.priority === "low"
                    ? "#2563eb"
                    : "#d97706";

                return (
                  <div
                    key={followUp.id}
                    style={{
                      padding: 14,
                      borderRadius: 18,
                      background: isOverdue
                        ? isDark
                          ? "rgba(127,29,29,0.24)"
                          : "#fff1f2"
                        : isDark
                        ? "rgba(255,255,255,0.03)"
                        : "#ffffff",
                      border: isOverdue
                        ? `1px solid ${isDark ? "rgba(248,113,113,0.45)" : "#fecdd3"}`
                        : dashboardBorder,
                      display: "grid",
                      gap: 12,
                      opacity: isDone ? 0.72 : 1,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 12,
                        alignItems: "flex-start",
                        flexDirection: isCompact ? "column" : "row",
                      }}
                    >
                      <div style={{ display: "flex", gap: 10, alignItems: "flex-start", minWidth: 0 }}>
                        <input
                          type="checkbox"
                          checked={isDone}
                          disabled={isDone || Boolean(markingDoneKey)}
                          onChange={(event) => {
                            if (event.target.checked) {
                              markCrmFollowUpDone(followUp.id);
                            }
                          }}
                          style={{ marginTop: 4 }}
                          aria-label={`Mark ${followUp.title || "follow-up"} as done`}
                        />
                        <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
                          <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                            Follow-up {index + 1}
                          </div>
                          <div style={{ fontSize: 15, fontWeight: 900, wordBreak: "break-word" }}>
                            {followUp.title || "Untitled follow-up"}
                          </div>
                          {followUp.description ? (
                            <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                              {followUp.description}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          flexWrap: "wrap",
                          justifyContent: isCompact ? "flex-start" : "flex-end",
                        }}
                      >
                        {isOverdue ? (
                          <div
                            style={{
                              padding: "6px 10px",
                              borderRadius: 999,
                              background: isDark
                                ? "rgba(248,113,113,0.2)"
                                : "#ffe4e6",
                              color: isDark ? "#fecdd3" : "#be123c",
                              fontSize: 11,
                              fontWeight: 900,
                            }}
                          >
                            Overdue
                          </div>
                        ) : null}
                        <div
                          style={{
                            padding: "6px 10px",
                            borderRadius: 999,
                            background: `${priorityAccent}18`,
                            color: priorityAccent,
                            fontSize: 11,
                            fontWeight: 900,
                          }}
                        >
                          {priorityOption?.label || "Medium"} Priority
                        </div>
                        <div
                          style={{
                          padding: "6px 10px",
                          borderRadius: 999,
                          background: isDone
                            ? isDark
                              ? "rgba(22,163,74,0.18)"
                              : "#dcfce7"
                            : isDark
                            ? "rgba(59,130,246,0.18)"
                            : "#dbeafe",
                          color: isDone
                            ? isDark
                              ? "#86efac"
                              : "#166534"
                            : isDark
                            ? "#bfdbfe"
                            : "#1d4ed8",
                          fontSize: 11,
                          fontWeight: 800,
                        }}
                      >
                        {isDone ? "Done" : "Pending"}
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isCompact
                          ? "1fr"
                          : "repeat(auto-fit, minmax(120px, 1fr))",
                        gap: 10,
                      }}
                    >
                      {[
                        ["Order", Number(followUp.order || 0) > 0 ? followUp.order : index + 1],
                        ["Due", dueDateTime],
                        [
                          "Repeat",
                          CRM_FOLLOW_UP_REPEAT_OPTIONS.find(
                            (option) => option.key === followUp.repeat
                          )?.label || "No repeat",
                        ],
                        ["Reminder", followUp.reminderEnabled ? "On" : "Off"],
                        ["Priority", priorityOption?.label || "Medium"],
                        ["Attachments", `${attachmentCount} file${attachmentCount === 1 ? "" : "s"}`],
                      ].map(([label, value]) => (
                        <div
                          key={label}
                          style={{
                            padding: 12,
                            borderRadius: 16,
                            background: dashboardSoftBg,
                            border: dashboardBorder,
                          }}
                        >
                          <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                            {label}
                          </div>
                          <div style={{ fontSize: 13, fontWeight: 800, marginTop: 6 }}>
                            {value}
                          </div>
                        </div>
                      ))}
                    </div>

                    {isDone ? (
                      <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                        Completed by{" "}
                        <strong style={{ color: textColor }}>
                          {followUp.completedBy?.name ||
                            followUp.completedBy?.email ||
                            "Workspace User"}
                        </strong>
                        {followUp.completedAt
                          ? ` on ${new Date(followUp.completedAt).toLocaleString("en-IN")}`
                          : ""}
                      </div>
                    ) : null}

                    {attachmentCount > 0 ? (
                      <div style={{ display: "grid", gap: 8 }}>
                        {normalizeCrmAttachments(followUp.attachments || []).map((attachment) => {
                          const isImage = String(attachment.contentType || "").startsWith("image/");
                          return (
                            <div
                              key={attachment.id}
                              style={{
                                display: "flex",
                                gap: 10,
                                alignItems: "center",
                                flexWrap: "wrap",
                                padding: 10,
                                borderRadius: 14,
                                background: dashboardSoftBg,
                                border: dashboardBorder,
                              }}
                            >
                              {isImage && attachment.url ? (
                                <img
                                  src={attachment.url}
                                  alt={attachment.name}
                                  style={{
                                    width: 56,
                                    height: 56,
                                    objectFit: "cover",
                                    borderRadius: 12,
                                    border: dashboardBorder,
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: 12,
                                    display: "grid",
                                    placeItems: "center",
                                    background: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc",
                                    border: dashboardBorder,
                                  }}
                                >
                                  <Paperclip size={16} />
                                </div>
                              )}
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={{ fontSize: 13, fontWeight: 800, wordBreak: "break-word" }}>
                                  {attachment.name}
                                </div>
                                <div style={{ fontSize: 11, color: dashboardMuted, marginTop: 4 }}>
                                  {attachment.uploadedAt
                                    ? `Uploaded ${new Date(attachment.uploadedAt).toLocaleDateString("en-IN")}`
                                    : "Uploaded"}
                                </div>
                              </div>
                              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                <a
                                  href={attachment.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{
                                    ...btn("#0f172a"),
                                    marginRight: 0,
                                    textDecoration: "none",
                                  }}
                                >
                                  View
                                </a>
                                <a
                                  href={attachment.url}
                                  download={attachment.filename || attachment.name}
                                  style={{
                                    ...btn("#2563eb"),
                                    marginRight: 0,
                                    textDecoration: "none",
                                  }}
                                >
                                  Download
                                </a>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : null}

                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                      <button
                        type="button"
                        onClick={() => openCrmFollowUpModal(followUp)}
                        style={{ ...btn("#0f172a"), marginRight: 0 }}
                      >
                        Edit
                      </button>
                      {!isDone ? (
                        <button
                          type="button"
                          disabled={Boolean(markingDoneKey)}
                          onClick={() => markCrmFollowUpDone(followUp.id)}
                          style={{
                            ...btn("#16a34a"),
                            marginRight: 0,
                            opacity: markingDoneKey ? 0.7 : 1,
                          }}
                        >
                          {markingDoneKey ? "Saving..." : "Mark as Done"}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => deleteCrmFollowUp(followUp.id)}
                        style={{ ...btn("#ef4444"), marginRight: 0 }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={sectionCardStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 16, fontWeight: 800 }}>Execution Details</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                Capture what will actually be delivered or manufactured using flexible fields per execution item.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              {selectedCrmDeal.hasQuote ? (
                <button
                  type="button"
                  onClick={() => convertDealQuoteToExecutionSpecs(selectedCrmDeal)}
                  style={{ ...btn("#2563eb"), marginRight: 0 }}
                >
                  Convert Quote to Execution Specs
                </button>
              ) : null}
              {canUseWorkshopPdf && executionSpecs.length > 0 ? (
                <button
                  type="button"
                  onClick={() => downloadExecutionWorkshopPdf(selectedCrmDeal)}
                  style={{
                    ...btn("#7c3aed"),
                    marginRight: 0,
                    display: "grid",
                    gap: 3,
                    justifyItems: "start",
                    textAlign: "left",
                  }}
                >
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <Download size={14} />
                    Download Workshop PDF
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 600, opacity: 0.86 }}>
                    Production / installation copy
                  </span>
                </button>
              ) : null}
              <button
                type="button"
                onClick={addCrmExecutionSpec}
                style={{ ...btn("#0f172a"), marginRight: 0 }}
              >
                + Add Item
              </button>
            </div>
          </div>

          {executionSpecs.length === 0 ? (
            <div
              style={{
                padding: 14,
                borderRadius: 16,
                background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                border: dashboardBorder,
                fontSize: 13,
                color: dashboardMuted,
                lineHeight: 1.7,
              }}
            >
              No execution items added yet. Open the full execution workspace to add structured job specs, dynamic fields, and attachments without working inside this drawer.
            </div>
          ) : (
            <div style={{ display: "grid", gap: 12 }}>
              {executionSpecs.slice(0, 3).map((item, index) => {
                const normalizedAttachments = normalizeExecutionSpecAttachments(
                  item.attachments || []
                );
                const fieldCount = Array.isArray(item.attributeRows)
                  ? item.attributeRows.length
                  : 0;

                return (
                  <div
                    key={item.id}
                    style={{
                      padding: 14,
                      borderRadius: 18,
                      background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                      border: dashboardBorder,
                      display: "grid",
                      gap: 8,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                      <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                        Execution Item {index + 1}
                      </div>
                      <div
                        style={{
                          padding: "4px 8px",
                          borderRadius: 999,
                          background: dashboardSoftBg,
                          color: dashboardMuted,
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        {item.itemType || "Custom"}
                      </div>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 800 }}>
                      {item.itemName || "Untitled execution item"}
                    </div>
                    <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                      {fieldCount} field{fieldCount === 1 ? "" : "s"} | {normalizedAttachments.length} attachment
                      {normalizedAttachments.length === 1 ? "" : "s"}
                    </div>
                  </div>
                );
              })}
              {executionSpecs.length > 3 ? (
                <div style={{ fontSize: 12, color: dashboardMuted }}>
                  +{executionSpecs.length - 3} more execution item{executionSpecs.length - 3 === 1 ? "" : "s"} in the full workspace
                </div>
              ) : null}
            </div>
          )}

          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: dashboardSoftBg,
              border: dashboardBorder,
              display: "grid",
              gap: 10,
            }}
          >
            <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
              Execution Specs now have a dedicated full-page workspace for General Quotations. Use it for larger item sets, deep attribute editing, and attachment review.
            </div>
            {isGeneralExecutionEligibleDeal(selectedCrmDeal) ? (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <button
                  type="button"
                  onClick={() => openExecutionWorkspaceForDeal(selectedCrmDeal)}
                  style={{ ...btn("#2563eb"), marginRight: 0 }}
                >
                  {executionWorkspaceOpeningDealId === selectedCrmDeal.id
                    ? "Opening..."
                    : "Manage Execution Details"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    addCrmExecutionSpec();
                    openExecutionWorkspaceForDeal(selectedCrmDeal);
                  }}
                  style={{ ...btn("#0f172a"), marginRight: 0 }}
                >
                  Add Item in Workspace
                </button>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                Execution Details is available for General Quotations only.
              </div>
            )}
          </div>
        </div>

        <div style={sectionCardStyle}>
          <div style={{ display: "grid", gap: 4 }}>
            <div style={{ fontSize: 16, fontWeight: 800 }}>Reminders</div>
            <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
              Installation reminders stay inside FabriQuote until they are dismissed or marked done. Follow-up task reminders are managed from the Follow-ups section above.
            </div>
          </div>
          <div style={{ display: "grid", gap: 12 }}>
            {renderReminderManager(CRM_REMINDER_TYPE_INSTALLATION, {
              dueFieldLabel: "Installation date",
              dueValue: crmDetailDraft.installationDate || "-",
              helperText:
                "Defaults to the Installation Date so the team keeps seeing pending installation alerts until the work is done.",
            })}
          </div>
        </div>

        <div style={sectionCardStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 16, fontWeight: 800 }}>Attachments</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                Upload site photos, PDFs, measurements, or reference documents directly against this deal.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <input
                ref={crmAttachmentInputRef}
                type="file"
                multiple
                style={{ display: "none" }}
                onChange={(event) => {
                  const files = Array.from(event.target.files || []);
                  uploadCrmDealAttachments(selectedCrmDeal.id, files);
                  event.target.value = "";
                }}
              />
              <button
                type="button"
                onClick={() => crmAttachmentInputRef.current?.click()}
                style={{ ...btn("#0f172a"), marginRight: 0 }}
              >
                <UploadCloud size={14} style={{ marginRight: 6 }} />
                Add Attachment
              </button>
            </div>
          </div>

          {crmAttachmentUploadDealId === selectedCrmDeal.id ? (
            <div style={{ fontSize: 12, color: dashboardMuted }}>
              Uploading attachment...
            </div>
          ) : null}

          {attachments.length === 0 ? (
            <div style={{ fontSize: 13, color: dashboardMuted }}>
              No attachments added yet for this deal.
            </div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {attachments.map((attachment) => {
                const visual = getAttachmentVisual(attachment);
                const Icon = visual.icon;

                return (
                  <div
                    key={attachment.id}
                    style={{
                      padding: 12,
                      borderRadius: 16,
                      background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                      border: dashboardBorder,
                      display: "grid",
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 12,
                        alignItems: "flex-start",
                        flexDirection: isCompact ? "column" : "row",
                      }}
                    >
                      <div style={{ display: "flex", gap: 10, minWidth: 0 }}>
                        <div
                          style={{
                            width: 40,
                            height: 40,
                            minWidth: 40,
                            borderRadius: 14,
                            display: "grid",
                            placeItems: "center",
                            background: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc",
                            color: visual.accent,
                          }}
                        >
                          <Icon size={18} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 800, wordBreak: "break-word" }}>
                            {attachment.name}
                          </div>
                          <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                            {visual.label}
                            {formatAttachmentSize(attachment.sizeBytes)
                              ? ` | ${formatAttachmentSize(attachment.sizeBytes)}`
                              : ""}
                            {attachment.uploadedAt
                              ? ` | Uploaded ${new Date(attachment.uploadedAt).toLocaleDateString("en-IN")}`
                              : ""}
                            {attachment.uploadedByName
                              ? ` | By ${attachment.uploadedByName}`
                              : ""}
                          </div>
                        </div>
                      </div>

                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                        <button
                          type="button"
                          onClick={() => {
                            if (attachment.url) {
                              window.open(attachment.url, "_blank", "noopener,noreferrer");
                            }
                          }}
                          style={{ ...btn("#2563eb"), marginRight: 0, padding: "8px 10px" }}
                        >
                          <ExternalLink size={14} style={{ marginRight: 6 }} />
                          View
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (!attachment.url) return;
                            const anchor = document.createElement("a");
                            anchor.href = attachment.url;
                            anchor.download = attachment.filename || attachment.name || "attachment";
                            anchor.target = "_blank";
                            anchor.rel = "noopener noreferrer";
                            anchor.click();
                          }}
                          style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 10px" }}
                        >
                          Download
                        </button>
                        <button
                          type="button"
                          onClick={() => removeCrmDealAttachment(selectedCrmDeal.id, attachment.id)}
                          style={{ ...btn("#ef4444"), marginRight: 0, padding: "8px 10px" }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div
          style={sectionCardStyle}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 16, fontWeight: 800 }}>Checklist</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                Stage template: {stageTemplate?.label || "Custom"}
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  addCrmChecklistItem();
                }}
                style={{ ...btn("#0f172a"), marginRight: 0 }}
              >
                Add Item
              </button>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  updateCrmDetailChecklist(() => createChecklistFromStage(stageTemplate));
                }}
                style={{ ...btn("#2563eb"), marginRight: 0 }}
              >
                Reset from Stage Template
              </button>
            </div>
          </div>

          <div style={{ display: "grid", gap: 10 }}>
            {(crmDetailDraft.checklistItems || []).length === 0 ? (
              <div style={{ fontSize: 13, color: dashboardMuted }}>
                No checklist items yet for this deal. Add a top-level item or reset from the stage template.
              </div>
            ) : (
              (crmDetailDraft.checklistItems || []).map((item) =>
                renderCrmChecklistNode(item, 1)
              )
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCrmDetailDrawer = () => {
    const drawerExpanded = !isCompact && (isCrmDrawerHovered || isCrmDrawerPinned);
    const collapsedWidth = 12;
    const expandedWidth = isTablet ? 360 : 420;
    const drawerTop = isTablet ? 108 : 118;

    return (
      <div
        onMouseEnter={() => {
          if (!isCompact) {
            setIsCrmDrawerHovered(true);
          }
        }}
        onMouseLeave={() => {
          if (!isCompact && !isCrmDrawerPinned) {
            setIsCrmDrawerHovered(false);
          }
        }}
        style={{
          width: isCompact ? "100%" : drawerExpanded ? expandedWidth : collapsedWidth,
          minWidth: isCompact ? "100%" : drawerExpanded ? expandedWidth : collapsedWidth,
          position: isCompact ? "static" : "fixed",
          top: isCompact ? "auto" : drawerTop,
          right: isCompact ? "auto" : 10,
          bottom: isCompact ? "auto" : 16,
          zIndex: isCompact ? "auto" : 90,
          overflow: "visible",
          transition: "width 0.24s ease, min-width 0.24s ease",
          pointerEvents: "auto",
        }}
      >
        <div
          style={{
            ...dashboardCardStyle,
            minHeight: isCompact ? "auto" : `calc(100vh - ${drawerTop + 16}px)`,
            height: isCompact ? "auto" : `calc(100vh - ${drawerTop + 16}px)`,
            padding: 0,
            display: "grid",
            overflow: "hidden",
            borderRadius: 24,
            boxShadow: drawerExpanded
              ? isDark
                ? "0 24px 60px rgba(2,6,23,0.7)"
                : "0 24px 60px rgba(15,23,42,0.18)"
              : "0 8px 18px rgba(15,23,42,0.08)",
          }}
        >
          <div
            style={{
              width: isCompact ? "100%" : expandedWidth,
              height: "100%",
              transform: "translateX(0)",
              padding: drawerExpanded || isCompact ? 18 : 6,
              display: "grid",
              gap: 14,
              minHeight: "100%",
            }}
          >
            {drawerExpanded || isCompact ? (
              <div style={{ display: "grid", gap: 14, minHeight: "100%" }}>
                {!isCompact ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 12,
                      paddingBottom: 10,
                      borderBottom: dashboardBorder,
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                        CRM Details
                      </div>
                      <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                        {selectedCrmDeal
                          ? selectedCrmDeal.crmStageLabel
                          : selectedCrmDealId || selectedCrmDealCacheRef.current
                          ? "Loading selected deal details"
                          : "Select a deal to view details"}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setIsCrmDrawerPinned(false);
                        setIsCrmDrawerHovered(false);
                      }}
                      style={{
                        ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"),
                        marginRight: 0,
                        padding: "8px 12px",
                      }}
                    >
                      Close
                    </button>
                  </div>
                ) : null}
                <div style={{ overflowY: "auto", paddingRight: isCompact ? 0 : 4 }}>
                  {renderCrmDetailPanel()}
                </div>
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gap: 12,
                  alignContent: "start",
                  justifyItems: "center",
                  minHeight: "100%",
                  background: isDark
                    ? "linear-gradient(180deg, rgba(15,23,42,0.96), rgba(15,23,42,0.88))"
                    : "linear-gradient(180deg, #ffffff, #f8fafc)",
                }}
              >
                <div
                  style={{
                    width: 8,
                    height: 120,
                    borderRadius: 16,
                    background: selectedCrmDeal?.crmStageColor || "#2563eb",
                    marginTop: 8,
                    boxShadow: "0 12px 24px rgba(37,99,235,0.18)",
                  }}
                />

                <div
                  style={{
                    writingMode: "vertical-rl",
                    transform: "rotate(180deg)",
                    fontSize: 10,
                    fontWeight: 800,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: dashboardMuted,
                  }}
                >
                  CRM Drawer
                </div>

                <div style={{ fontSize: 11, color: dashboardMuted, textAlign: "center" }}>
                  {selectedCrmDeal
                    ? "Hover to expand"
                    : selectedCrmDealId || selectedCrmDealCacheRef.current
                    ? "Loading selected record"
                    : "Select a customer or lead to view details"}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCrmOverview = () => {
    const overviewCards = [
      {
        key: "active",
        label: "Active Leads",
        value: crmOverviewDrilldowns.active.deals.length,
        note:
          crmScope === CRM_SCOPE_ALL
            ? "Live opportunities across curtains and general quotations"
            : "Live opportunities in the selected pipeline",
        accent: "#2563eb",
      },
      {
        key: "new",
        label: `New Leads ${dashboardPeriodLabel}`,
        value: crmOverviewDrilldowns.new.deals.length,
        note: "Created in the selected period",
        accent: "#0ea5e9",
      },
      {
        key: "value",
        label: "Pipeline Value",
        value: formatCurrencyDisplay(crmOverviewDrilldowns.value.totalValue || 0, currency),
        note: "Expected value across active customer and lead records",
        accent: "#6366f1",
      },
      {
        key: "overdue",
        label: "Overdue Follow-ups",
        value: crmOverviewDrilldowns.overdue.deals.length,
        note: "Missed next actions that need attention",
        accent: "#f97316",
      },
      {
        key: "stalled",
        label: "Stalled Leads",
        value: crmOverviewDrilldowns.stalled.deals.length,
        note: "No updates in the last 10 days",
        accent: "#ef4444",
      },
      {
        key: "installs",
        label: "Upcoming Installations",
        value: crmOverviewDrilldowns.installs.deals.length,
        note:
          crmScope === CRM_SCOPE_ALL
            ? "Upcoming scheduled jobs across the CRM workspace"
            : "Upcoming scheduled jobs for this pipeline",
        accent: "#10b981",
      },
    ];

    return (
      <div style={{ display: "grid", gap: 16 }}>
        <div
          style={{
            ...dashboardCardStyle,
            display: "flex",
            justifyContent: "space-between",
            gap: 14,
            flexDirection: isCompact ? "column" : "row",
            alignItems: isCompact ? "stretch" : "center",
          }}
        >
          <div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>Lead Management</div>
            <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
              Search customers, leads, saved quote contacts, and quote-linked jobs without loading the whole CRM workspace.
            </div>
          </div>
          <button
            type="button"
            onClick={() => openNewCrmLeadModal()}
            style={{ ...btn("#2563eb"), marginRight: 0 }}
          >
            + New Lead
          </button>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact
              ? "1fr"
              : isTablet
              ? "repeat(2, minmax(0, 1fr))"
              : "repeat(3, minmax(0, 1fr))",
            gap: 16,
          }}
        >
          {overviewCards.map((card) => (
            <div
              key={card.key}
              onClick={() => openCrmDrilldown(card.key)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = isDark
                  ? "0 24px 48px rgba(2,6,23,0.44)"
                  : "0 24px 48px rgba(15,23,42,0.12)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = isDark
                  ? "0 18px 40px rgba(2,6,23,0.35)"
                  : "0 18px 40px rgba(15,23,42,0.08)";
              }}
              style={{
                ...dashboardCardStyle,
                position: "relative",
                overflow: "hidden",
                cursor: "pointer",
                transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
                boxShadow: isDark
                  ? "0 18px 40px rgba(2,6,23,0.35)"
                  : "0 18px 40px rgba(15,23,42,0.08)",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: `linear-gradient(135deg, ${card.accent}16, transparent 58%)`,
                }}
              />
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: 22,
                  boxShadow: `inset 0 0 0 1px ${card.accent}22`,
                  pointerEvents: "none",
                }}
              />
              <div style={{ position: "relative", display: "grid", gap: 8 }}>
                <div style={{ fontSize: 13, color: dashboardMuted, fontWeight: 600 }}>
                  {card.label}
                </div>
                <div style={{ fontSize: isCompact ? 26 : 30, fontWeight: 900 }}>
                  {typeof card.value === "number" ? (
                    <CountUp end={card.value} duration={1.1} />
                  ) : (
                    card.value
                  )}
                </div>
                <div style={{ fontSize: 12, color: dashboardMuted }}>{card.note}</div>
                <div style={{ fontSize: 11, color: card.accent, fontWeight: 700 }}>
                  Click to view records
                </div>
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 1.25fr) minmax(320px, 0.95fr)",
            gap: 16,
            alignItems: "start",
          }}
        >
          <div style={dashboardCardStyle}>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 18, fontWeight: 800 }}>Pipeline Stage Overview</div>
              <div style={{ fontSize: 12, color: dashboardMuted }}>
                {crmScope === CRM_SCOPE_ALL
                  ? "Stage occupancy across both CRM pipelines"
                  : `Occupancy across the ${getQuoteTypeLabel(activeCrmPipelineType).toLowerCase()} pipeline`}
              </div>
            </div>
            <div
              style={{
                width: "100%",
                minWidth: 0,
              }}
            >
              {hasCrmStageOverviewData ? (
                renderHorizontalMetricBars(crmStageOverviewChartData, {
                  valueKey: "count",
                  valueLabel: "deals",
                  emptyTitle: "No pipeline stage data yet",
                })
              ) : (
                renderChartEmptyState("No pipeline stage data yet")
              )}
            </div>
          </div>

          <div style={{ display: "grid", gap: 16 }}>
            <div style={dashboardCardStyle}>
              <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 12 }}>
                Checklist Completion
              </div>
              {renderChecklistProgress(
                crmPipelineChecklistChartData,
                crmPipelineSummary,
                "No checklist data yet"
              )}
              <div style={{ marginTop: 10, fontSize: 12, color: dashboardMuted }}>
                {crmPipelineSummary.checklistCompletion}% of checklist tasks are complete for open deals in the selected CRM scope.
              </div>
            </div>

            {crmScope === CRM_SCOPE_ALL ? (
              <div
                onClick={() => openCrmDrilldown("quoteTypeMix")}
                style={{ ...dashboardCardStyle, cursor: "pointer" }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "center",
                    marginBottom: 12,
                  }}
                >
                  <div style={{ fontSize: 18, fontWeight: 800 }}>
                    Quote Type Mix
                  </div>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      openCrmDrilldown("quoteTypeMix");
                    }}
                    style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 12px" }}
                  >
                    View All
                  </button>
                </div>
                <div style={{ display: "grid", gap: 10 }}>
                  {crmQuoteTypeMix.map((entry) => (
                    <div
                      key={entry.name}
                      role="button"
                      tabIndex={0}
                      onClick={(event) => {
                        event.stopPropagation();
                        openCrmDrilldown(entry.key);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          event.stopPropagation();
                          openCrmDrilldown(entry.key);
                        }
                      }}
                      style={{
                        padding: 14,
                        borderRadius: 16,
                        background: dashboardSoftBg,
                        border: dashboardBorder,
                        display: "grid",
                        gap: 6,
                        cursor: "pointer",
                        transition: "transform 0.16s ease, border-color 0.16s ease",
                        minWidth: 0,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 12,
                          alignItems: "center",
                          flexWrap: isMobile ? "wrap" : "nowrap",
                          minWidth: 0,
                        }}
                      >
                        <div
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            fontWeight: 800,
                            minWidth: 0,
                            overflowWrap: "anywhere",
                          }}
                        >
                          <span
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: 999,
                              background: entry.color,
                              display: "inline-block",
                            }}
                          />
                          {entry.name}
                        </div>
                        <div style={{ fontWeight: 800, minWidth: 0 }}>{entry.count} deals</div>
                      </div>
                      <div style={{ fontSize: 12, color: dashboardMuted }}>
                        {formatCurrencyDisplay(entry.value || 0, currency)} in pipeline value
                      </div>
                      <div style={{ fontSize: 11, color: entry.color, fontWeight: 800 }}>
                        Open {entry.name} drill-down
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div
              onClick={() => openCrmDrilldown("topClients")}
              style={{ ...dashboardCardStyle, cursor: "pointer" }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  alignItems: "center",
                  marginBottom: 12,
                }}
              >
                <div style={{ fontSize: 18, fontWeight: 800 }}>
                  Top Clients
                </div>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    openCrmDrilldown("topClients");
                  }}
                  style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 12px" }}
                >
                  View All
                </button>
              </div>
              <div style={{ display: "grid", gap: 10 }}>
                {crmTopClients.slice(0, 5).map((client, index) => (
                  <div
                    key={client.name}
                    role="button"
                    tabIndex={0}
                    onClick={(event) => {
                      event.stopPropagation();
                      openCrmDrilldown(`client:${client.name}`);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        event.stopPropagation();
                        openCrmDrilldown(`client:${client.name}`);
                      }
                    }}
                    style={{
                      padding: 14,
                      borderRadius: 16,
                      background: dashboardSoftBg,
                      border: dashboardBorder,
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 12,
                      cursor: "pointer",
                      transition: "transform 0.16s ease, border-color 0.16s ease",
                      minWidth: 0,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                        {index + 1}. {client.name}
                      </div>
                      <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                        {client.count} linked quote{client.count === 1 ? "" : "s"}
                      </div>
                      <div style={{ fontSize: 11, color: "#2563eb", fontWeight: 800, marginTop: 4 }}>
                        Open client drill-down
                      </div>
                    </div>
                    <div style={{ fontWeight: 900, flex: "0 0 auto", whiteSpace: "nowrap", textAlign: "right", fontSize: isMobile ? 13 : 14 }}>
                      {formatCurrencyDisplay(client.value || 0, currency)}
                    </div>
                  </div>
                ))}
                {crmTopClients.length === 0 && (
                  <div style={{ fontSize: 13, color: dashboardMuted }}>
                    No customer concentration data is available yet.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact ? "1fr" : "repeat(3, minmax(0, 1fr))",
            gap: 16,
          }}
        >
          <div style={dashboardCardStyle}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 12 }}>
              Overdue Follow-ups
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {crmOverdueDeals.length === 0 ? (
                <div style={{ fontSize: 13, color: dashboardMuted }}>
                  No overdue follow-ups in this pipeline.
                </div>
              ) : (
                crmOverdueDeals.slice(0, 5).map((deal) =>
                  renderCrmDealCard(deal, {
                    dense: true,
                    overview: true,
                    renderSource: "CRMOverview",
                  })
                )
              )}
            </div>
          </div>

          <div style={dashboardCardStyle}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 12 }}>
              Upcoming Installations
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {crmUpcomingDeals.length === 0 ? (
                <div style={{ fontSize: 13, color: dashboardMuted }}>
                  No upcoming installations scheduled in this pipeline.
                </div>
              ) : (
                crmUpcomingDeals.map((deal) =>
                  renderCrmDealCard(deal, {
                    dense: true,
                    overview: true,
                    renderSource: "CRMOverview",
                  })
                )
              )}
            </div>
          </div>

          <div style={dashboardCardStyle}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 12 }}>
              Recent Activity
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {crmRecentActivityDeals.length === 0 ? (
                <div style={{ fontSize: 13, color: dashboardMuted }}>
                  No recent CRM activity yet.
                </div>
              ) : (
                crmRecentActivityDeals.map((deal) =>
                  renderCrmDealCard(deal, {
                    dense: true,
                    overview: true,
                    renderSource: "CRMOverview",
                  })
                )
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCrmBoard = () => (
    <div
      style={{
        display: "grid",
        gap: 16,
        alignItems: "start",
      }}
    >
      <div style={{ display: "grid", gap: 16 }}>
        <div
          style={{
            ...dashboardCardStyle,
            display: "grid",
            gap: 12,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>Pipeline Board</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                Move live leads and quote-linked deals through the selected CRM pipeline.
              </div>
            </div>
            <button
              type="button"
              onClick={() => openNewCrmLeadModal(activeCrmPipelineType)}
              style={{ ...btn("#2563eb"), marginRight: 0 }}
            >
              + New Lead
            </button>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact
                ? "1fr"
                : "minmax(220px, 1fr) minmax(220px, 260px) minmax(220px, 260px)",
              gap: 12,
            }}
          >
            <input
              value={crmSearch}
              onChange={(e) => setCrmSearch(e.target.value)}
              style={input}
              placeholder="Search customers, leads, phone, quote number, or source"
            />
            <select
              value={crmStageFilter}
              onChange={(e) => setCrmStageFilter(e.target.value)}
              style={selectInput}
            >
              <option value="all">All Stages</option>
              {crmBoardFilterOptions.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
            <select
              value={crmSourceFilter}
              onChange={(e) => setCrmSourceFilter(e.target.value)}
              style={selectInput}
            >
              <option value="all">All Sources</option>
              {crmSourceOptions.map((source) => (
                <option key={source} value={source}>
                  {source}
                </option>
              ))}
            </select>
          </div>
          {deferredCrmSearch.trim() ? (
            <div style={{ fontSize: 12, color: dashboardMuted }}>
              Search checks all CRM customer and lead records, including older records.
              {globalSearchStatus.crmDealSearch?.loading ? " Searching..." : ""}
              {globalSearchStatus.crmDealSearch?.error ? (
                <span style={{ color: "#dc2626", fontWeight: 800 }}>
                  {" "}{globalSearchStatus.crmDealSearch.error}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact
              ? "1fr"
              : `repeat(${crmBoardStages.length}, minmax(280px, 1fr))`,
            gap: 14,
            overflowX: isCompact ? "visible" : "auto",
            alignItems: "start",
          }}
        >
          {crmBoardStages.map((stage) => {
            const stageDeals = crmBoardDeals.filter((deal) => deal.crmStageId === stage.id);

            return (
              <div
                key={stage.id}
                style={{
                  ...dashboardCardStyle,
                  minWidth: isCompact ? "auto" : 280,
                  display: "grid",
                  gap: 14,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "center",
                  }}
                >
                  <div>
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        fontSize: 16,
                        fontWeight: 800,
                      }}
                    >
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: 999,
                          background: stage.color,
                          display: "inline-block",
                        }}
                      />
                      {stage.label}
                    </div>
                    <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                      {stageDeals.length} lead{stageDeals.length === 1 ? "" : "s"} |{" "}
                      {formatCurrencyDisplay(
                        stageDeals.reduce(
                          (sum, deal) => sum + Number(deal.expectedValue || 0),
                          0
                        ),
                        currency
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ display: "grid", gap: 12 }}>
                  {stageDeals.length === 0 ? (
                    <div
                      style={{
                        padding: 16,
                        borderRadius: 16,
                        border: dashboardBorder,
                        background: dashboardSoftBg,
                        color: dashboardMuted,
                        fontSize: 13,
                      }}
                    >
                      No leads in this stage.
                    </div>
                  ) : (
                    stageDeals.map((deal) =>
                      renderCrmDealCard(deal, {
                        dense: true,
                        renderSource: "PipelineBoard",
                      })
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {isCompact ? renderCrmDetailPanel() : null}
    </div>
  );

  const renderCrmList = () => (
    <div
      style={{
        display: "grid",
        gap: 16,
        alignItems: "start",
      }}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {crmActiveDrilldown ? (
          <div
            style={{
              ...dashboardCardStyle,
              display: "flex",
              justifyContent: "space-between",
              gap: 14,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
  <div
    style={{
      fontSize: 12,
      fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "#2563eb",
                  marginBottom: 6,
                }}
              >
                Showing
              </div>
              <div style={{ fontSize: 20, fontWeight: 900 }}>
                {crmActiveDrilldown.label}
              </div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 6 }}>
                {crmDealListBaseDeals.length} matching customer/lead
                {crmDealListBaseDeals.length === 1 ? "" : "s"}
                {" | "}
                {formatCurrencyDisplay(
                  crmActiveDrilldown.totalValue ??
                    crmDealListBaseDeals.reduce(
                      (sum, deal) => sum + Number(deal.expectedValue || 0),
                      0
                    ),
                  currency
                )}{" "}
                total value
                {crmActiveDrilldown.key === "value"
                  ? ` | ${formatCurrencyDisplay(
                      crmActiveDrilldown.totalValue || 0,
                      currency
                    )} total pipeline value`
                  : ""}
              </div>
              {crmActiveDrilldown.description ? (
                <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 6, lineHeight: 1.6 }}>
                  {crmActiveDrilldown.description}
                </div>
              ) : null}
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <button
                type="button"
                onClick={clearCrmDrilldown}
                style={{ ...btn("#0f172a"), marginRight: 0 }}
              >
                Clear Filter
              </button>
            </div>
          </div>
        ) : null}

        <div style={{ ...dashboardCardStyle, display: "grid", gap: 12 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>Customer / Lead List</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                Review customers, leads, and quote-linked jobs in one simple workspace.
              </div>
            </div>
            <button
              type="button"
              onClick={() => openNewCrmLeadModal(activeCrmPipelineType)}
              style={{ ...btn("#2563eb"), marginRight: 0 }}
            >
              + New Lead
            </button>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact
                ? "1fr"
                : "minmax(220px, 1fr) repeat(5, minmax(150px, 210px))",
              gap: 12,
            }}
          >
            <input
              value={crmSearch}
              onChange={(e) => setCrmSearch(e.target.value)}
              style={input}
              placeholder="Search customers, leads, phone, quote number, or source"
            />
            <select
              value={crmStageFilter}
              onChange={(e) => setCrmStageFilter(e.target.value)}
              style={selectInput}
            >
              <option value="all">All Stages</option>
              {crmStageFilterOptions.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
            <select
              value={crmTypeFilter}
              onChange={(e) => setCrmTypeFilter(e.target.value)}
              style={selectInput}
            >
              <option value="all">All Types</option>
              {canAccessCurtainQuotes ? (
                <option value={CURTAIN_QUOTE_TYPE}>Curtains</option>
              ) : null}
              {canAccessGeneralQuotes ? (
                <option value={GENERAL_QUOTE_TYPE}>General Quotation</option>
              ) : null}
            </select>
            <select
              value={crmSourceFilter}
              onChange={(e) => setCrmSourceFilter(e.target.value)}
              style={selectInput}
            >
              <option value="all">All Sources</option>
              {crmSourceOptions.map((source) => (
                <option key={source} value={source}>
                  {source}
                </option>
              ))}
            </select>
            <select
              value={crmSortBy}
              onChange={(e) => setCrmSortBy(e.target.value)}
              style={selectInput}
            >
              <option value="newest">Newest Updated</option>
              <option value="oldest">Oldest Updated</option>
              <option value="highest">Highest Value</option>
              <option value="client">Client Name</option>
            </select>
            <div
              style={{
                padding: "10px 14px",
                borderRadius: 14,
                border: dashboardBorder,
                background: dashboardSoftBg,
                fontSize: 13,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Showing {visibleCrmDeals.length} of {crmFilteredDeals.length} record{crmFilteredDeals.length === 1 ? "" : "s"}
              {crmActiveDrilldown &&
              crmFilteredDeals.length !== crmDealListBaseDeals.length
                ? ` | ${crmDealListBaseDeals.length} in drill-down`
                : ""}
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gap: 12 }}>
          {crmFilteredDeals.length === 0 ? (
            <div style={{ ...dashboardCardStyle, color: dashboardMuted }}>
              No customers or leads match the current filters.
            </div>
          ) : (
            visibleCrmDeals.map((deal) => renderCrmDealCard(deal))
          )}
          {hasMoreCrmDeals ? (
            <button
              type="button"
              onClick={() =>
                setCrmDealsVisibleCount((prev) => prev + CRM_DEALS_PAGE_SIZE)
              }
              style={{
                ...btn(deferredCrmSearch ? "#0f172a" : "#2563eb"),
                marginRight: 0,
                justifySelf: "center",
                minWidth: 220,
              }}
            >
              {deferredCrmSearch ? "Search More Records" : "Load More Records"}
            </button>
          ) : crmFilteredDeals.length > CRM_DEALS_PAGE_SIZE ? (
            <div style={{ textAlign: "center", color: dashboardMuted, fontSize: 12 }}>
              All matching records are shown.
            </div>
          ) : null}
        </div>
      </div>

      {isCompact ? renderCrmDetailPanel() : null}
    </div>
  );

  const renderCrmSettings = () => {
    const pipeline = getCrmPipeline(crmPipelines, activeCrmPipelineType);
    const hasConfirmationStage = pipeline.stages.some((stage) => stage.confirmsQuote);

    return (
      <div style={{ display: "grid", gap: 16 }}>
        <div style={dashboardCardStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>
                {getQuoteTypeLabel(activeCrmPipelineType)} Pipeline Settings
              </div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                Stage names, confirmation rules, completion flags, and default checklists are fully customizable.
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <button
                type="button"
                onClick={() => addPipelineStage(activeCrmPipelineType)}
                style={{ ...btn("#0f172a"), marginRight: 0 }}
              >
                Add Stage
              </button>
              <button
                type="button"
                onClick={saveCrmPipelinesSettings}
                style={{ ...btn("#16a34a"), marginRight: 0 }}
              >
                Save Pipeline Settings
              </button>
            </div>
          </div>

          <div
            style={{
              marginTop: 16,
              display: "grid",
              gridTemplateColumns: isCompact ? "1fr" : "minmax(260px, 1fr) auto",
              gap: 12,
              alignItems: "center",
            }}
          >
            <div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginBottom: 6 }}>
                Pipeline Label
              </div>
              <input
                value={pipeline.label || ""}
                onChange={(e) =>
                  setCrmPipelines((prev) => ({
                    ...normalizeCrmPipelines(prev),
                    [activeCrmPipelineType]: {
                      ...getCrmPipeline(prev, activeCrmPipelineType),
                      label: e.target.value,
                    },
                  }))
                }
                style={input}
                placeholder="Pipeline name"
              />
            </div>
            <div
              style={{
                padding: "10px 14px",
                borderRadius: 14,
                background: hasConfirmationStage ? "#dcfce7" : "#fef3c7",
                color: hasConfirmationStage ? "#166534" : "#92400e",
                fontSize: 12,
                fontWeight: 800,
              }}
            >
              {hasConfirmationStage
                ? "A stage is set to confirm the quote."
                : "Choose a confirmation stage to mark quotes as confirmed orders."}
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gap: 14 }}>
          {pipeline.stages.map((stage, index) => (
            <div
              key={stage.id}
              style={{
                ...dashboardCardStyle,
                display: "grid",
                gap: 16,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  flexDirection: isCompact ? "column" : "row",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    flexWrap: "wrap",
                    flex: 1,
                  }}
                >
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 12,
                      background: dashboardSoftBg,
                      border: dashboardBorder,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <GripVertical size={16} color={dashboardMuted} />
                  </div>
                  <div style={{ minWidth: 90, fontSize: 13, fontWeight: 800 }}>
                    Stage {index + 1}
                  </div>
                  <input
                    value={stage.label}
                    onChange={(e) =>
                      updatePipelineStage(activeCrmPipelineType, stage.id, {
                        label: e.target.value,
                      })
                    }
                    style={{ ...input, flex: 1 }}
                    placeholder="Stage label"
                  />
                  <input
                    type="color"
                    value={stage.color}
                    onChange={(e) =>
                      updatePipelineStage(activeCrmPipelineType, stage.id, {
                        color: e.target.value,
                      })
                    }
                    style={{
                      width: 54,
                      height: 40,
                      padding: 4,
                      borderRadius: 12,
                      border: "1px solid rgba(148,163,184,0.35)",
                      background: "transparent",
                    }}
                  />
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <button
                    type="button"
                    onClick={() => movePipelineStage(activeCrmPipelineType, stage.id, -1)}
                    style={{ ...btn("#64748b"), marginRight: 0, padding: "8px 10px" }}
                  >
                    Up
                  </button>
                  <button
                    type="button"
                    onClick={() => movePipelineStage(activeCrmPipelineType, stage.id, 1)}
                    style={{ ...btn("#64748b"), marginRight: 0, padding: "8px 10px" }}
                  >
                    Down
                  </button>
                  <button
                    type="button"
                    onClick={() => deletePipelineStage(activeCrmPipelineType, stage.id)}
                    style={{ ...btn("#ef4444"), marginRight: 0, padding: "8px 10px" }}
                  >
                    Delete
                  </button>
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))",
                  gap: 10,
                }}
              >
                {[
                  { key: "confirmsQuote", label: "Marks quote as confirmed order" },
                  { key: "marksComplete", label: "Marks the deal as complete" },
                  { key: "isCancelled", label: "Marks the deal as cancelled" },
                ].map((toggle) => (
                  <label
                    key={toggle.key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "10px 12px",
                      borderRadius: 14,
                      background: dashboardSoftBg,
                      border: dashboardBorder,
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={stage[toggle.key] === true}
                      onChange={(e) =>
                        updatePipelineStage(activeCrmPipelineType, stage.id, {
                          [toggle.key]: e.target.checked,
                        })
                      }
                    />
                    {toggle.label}
                  </label>
                ))}
              </div>

              <div
                style={{
                  padding: 16,
                  borderRadius: 18,
                  background: dashboardSoftBg,
                  border: dashboardBorder,
                  display: "grid",
                  gap: 12,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    flexDirection: isCompact ? "column" : "row",
                    alignItems: isCompact ? "stretch" : "center",
                  }}
                  >
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 800 }}>Default Checklist</div>
                    <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                      These tasks are copied into each deal that enters this stage.
                    </div>
                  </div>

                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                    <button
                      type="button"
                      onClick={() => addPipelineChecklistItem(activeCrmPipelineType, stage.id)}
                      style={{ ...btn("#0f172a"), marginRight: 0 }}
                    >
                      Add Checklist Item
                    </button>
                  </div>
                </div>

                <div style={{ display: "grid", gap: 10 }}>
                  {(stage.defaultChecklist || []).length === 0 ? (
                    <div style={{ fontSize: 13, color: dashboardMuted }}>
                      No default checklist items for this stage yet.
                    </div>
                  ) : (
                    (stage.defaultChecklist || []).map((item) =>
                      renderPipelineChecklistNode(stage, item, 1)
                    )
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ ...dashboardCardStyle, display: "grid", gap: 16 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>Execution Templates</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                Execution templates are now managed from their own dedicated workspace so teams can create, edit, search,
                and reuse them without crowding the CRM settings screen.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setRoutedTab("executionTemplates")}
              style={{ ...btn("#2563eb"), marginRight: 0 }}
            >
              Open Execution Templates
            </button>
          </div>

          <div
            style={{
              padding: 18,
              borderRadius: 18,
              border: dashboardBorder,
              background: dashboardSoftBg,
              display: "grid",
              gap: 10,
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 800 }}>
              Dedicated template manager
            </div>
            <div style={{ fontSize: 13, color: dashboardMuted, lineHeight: 1.7 }}>
              Create template names, normalized item types, and default field/value pairs from the dedicated
              <strong style={{ color: textColor }}> Execution Templates</strong> page. Those templates then appear in the
              Execution Details workspace and auto-populate item fields the moment a matching template is selected or the
              item type matches automatically.
            </div>
            <div
              style={{
                fontSize: 12,
                color: dashboardMuted,
                padding: "10px 12px",
                borderRadius: 14,
                background: isDark ? "rgba(37,99,235,0.08)" : "#eff6ff",
                border: "1px solid rgba(37,99,235,0.18)",
              }}
            >
              Current templates available: {executionTemplateCatalog.length}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCrmLeadModal = () => {
    if (!isCrmLeadModalOpen || !crmLeadDraft) return null;

    const stageOptions = getStageSelectOptions(
      crmPipelines,
      crmLeadDraft.quoteType,
      crmLeadDraft.stageId
    );
    const leadCustomerMatch = findCustomerLedgerMatch(
      getCustomerIdentityFromLeadDraft(crmLeadDraft)
    );

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 120,
          background: "rgba(15,23,42,0.45)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isCompact ? 12 : 24,
        }}
        onClick={closeNewCrmLeadModal}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          onBlurCapture={() => persistCrmLeadDraftLocally("blur")}
          style={{
            width: "100%",
            maxWidth: 880,
            maxHeight: "90vh",
            overflowY: "auto",
            ...dashboardCardStyle,
            display: "grid",
            gap: 18,
            padding: isCompact ? 18 : 24,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#2563eb", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                CRM
              </div>
              <div style={{ fontSize: 24, fontWeight: 900, marginTop: 6 }}>
                New Lead
              </div>
              <div style={{ fontSize: 13, color: dashboardMuted, marginTop: 8 }}>
                Create a customer or project in CRM first, then create a curtain or general quotation only when the lead is ready.
              </div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 8 }}>
                {getLocalDraftStatusText(LOCAL_DRAFT_MODULES.CRM_LEAD)}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <button
                type="button"
                onClick={closeNewCrmLeadModal}
                style={{
                  ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"),
                  marginRight: 0,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveNewCrmLead}
                disabled={isSavingCrmLead}
                style={{ ...btn("#2563eb"), marginRight: 0, opacity: isSavingCrmLead ? 0.7 : 1 }}
              >
                {isSavingCrmLead ? "Saving..." : "Create Lead"}
              </button>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))",
              gap: 16,
            }}
          >
            <div style={{ display: "grid", gap: 14 }}>
              <div style={{ ...dashboardCardStyle, background: dashboardSoftBg, padding: 16, display: "grid", gap: 12 }}>
                <div style={{ fontSize: 16, fontWeight: 800 }}>Customer / Contact</div>
                {leadCustomerMatch.customer ? (
                  <div
                    style={{
                      padding: 12,
                      borderRadius: 16,
                      background: isDark ? "rgba(37,99,235,0.12)" : "#eff6ff",
                      border: "1px solid rgba(37,99,235,0.20)",
                      display: "grid",
                      gap: 4,
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 900, color: "#2563eb" }}>
                      Existing Customer Found
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 800 }}>
                      {leadCustomerMatch.customer.customerName}
                    </div>
                    <div style={{ fontSize: 12, color: dashboardMuted }}>
                      {[leadCustomerMatch.customer.phone, leadCustomerMatch.customer.address]
                        .filter(Boolean)
                        .join(" | ") || "Linked by customer history"}
                    </div>
                  </div>
                ) : leadCustomerMatch.possibleMatches?.length ? (
                  <div
                    style={{
                      padding: 12,
                      borderRadius: 16,
                      background: "#fef3c7",
                      border: "1px solid rgba(245,158,11,0.30)",
                      color: "#92400e",
                      fontSize: 12,
                      fontWeight: 800,
                    }}
                  >
                    Possible repeat customer found. Check Customer Ledger before creating a separate record.
                  </div>
                ) : null}
                <input
                  value={crmLeadDraft.contact?.name || ""}
                  onChange={(e) =>
                    setCrmLeadDraft((prev) => ({
                      ...prev,
                      contact: { ...prev.contact, name: e.target.value },
                    }))
                  }
                  style={input}
                  placeholder="Contact Name"
                />
                <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "1fr 1fr", gap: 10 }}>
                  <input
                    value={crmLeadDraft.contact?.phone || ""}
                    onChange={(e) =>
                      setCrmLeadDraft((prev) => ({
                        ...prev,
                        contact: { ...prev.contact, phone: e.target.value },
                      }))
                    }
                    style={input}
                    placeholder="Phone"
                  />
                  <input
                    value={crmLeadDraft.contact?.whatsapp || ""}
                    onChange={(e) =>
                      setCrmLeadDraft((prev) => ({
                        ...prev,
                        contact: { ...prev.contact, whatsapp: e.target.value },
                      }))
                    }
                    style={input}
                    placeholder="WhatsApp"
                  />
                </div>
                <input
                  value={crmLeadDraft.contact?.email || ""}
                  onChange={(e) =>
                    setCrmLeadDraft((prev) => ({
                      ...prev,
                      contact: { ...prev.contact, email: e.target.value },
                    }))
                  }
                  style={input}
                  placeholder="Email"
                />
                <textarea
                  value={crmLeadDraft.contact?.address || ""}
                  onChange={(e) =>
                    setCrmLeadDraft((prev) => ({
                      ...prev,
                      contact: { ...prev.contact, address: e.target.value },
                    }))
                  }
                  style={{ ...input, minHeight: 84, resize: "vertical" }}
                  placeholder="Address"
                />
              </div>

              <div style={{ ...dashboardCardStyle, background: dashboardSoftBg, padding: 16, display: "grid", gap: 12 }}>
                <div style={{ fontSize: 16, fontWeight: 800 }}>Deal / Project</div>
                <input
                  value={crmLeadDraft.title || ""}
                  onChange={(e) =>
                    setCrmLeadDraft((prev) => ({
                      ...prev,
                      title: e.target.value,
                    }))
                  }
                  style={input}
                  placeholder="Project / Deal Title"
                />
                <input
                  value={crmLeadDraft.companyOrProject || ""}
                  onChange={(e) =>
                    setCrmLeadDraft((prev) => ({
                      ...prev,
                      companyOrProject: e.target.value,
                    }))
                  }
                  style={input}
                  placeholder="Company / Project Name"
                />
                <input
                  value={crmLeadDraft.source || ""}
                  onChange={(e) =>
                    setCrmLeadDraft((prev) => ({
                      ...prev,
                      source: e.target.value,
                    }))
                  }
                  style={input}
                  placeholder="Source"
                />
                <input
                  value={crmLeadDraft.tags || ""}
                  onChange={(e) =>
                    setCrmLeadDraft((prev) => ({
                      ...prev,
                      tags: e.target.value,
                    }))
                  }
                  style={input}
                  placeholder="Tags (comma separated)"
                />
                <textarea
                  value={crmLeadDraft.notes || ""}
                  onChange={(e) =>
                    setCrmLeadDraft((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                  style={{ ...input, minHeight: 110, resize: "vertical" }}
                  placeholder="Notes"
                />
              </div>
            </div>

            <div style={{ display: "grid", gap: 14 }}>
              <div style={{ ...dashboardCardStyle, background: dashboardSoftBg, padding: 16, display: "grid", gap: 12 }}>
                <div style={{ fontSize: 16, fontWeight: 800 }}>CRM Routing</div>
                <div style={{ display: "grid", gap: 6 }}>
                  <div style={{ fontSize: 12, color: dashboardMuted }}>Quote Type</div>
                  <select
                    value={crmLeadDraft.quoteType}
                    onChange={(e) => {
                      const nextType =
                        e.target.value === GENERAL_QUOTE_TYPE
                          ? GENERAL_QUOTE_TYPE
                          : CURTAIN_QUOTE_TYPE;
                      setCrmLeadDraft((prev) => ({
                        ...prev,
                        quoteType: nextType,
                        pipelineType: nextType,
                        stageId: getDefaultCrmStageId(crmPipelines, nextType),
                      }));
                    }}
                    style={selectInput}
                  >
                    {canAccessCurtainQuotes ? (
                      <option value={CURTAIN_QUOTE_TYPE}>Curtains</option>
                    ) : null}
                    {canAccessGeneralQuotes ? (
                      <option value={GENERAL_QUOTE_TYPE}>General Quotation</option>
                    ) : null}
                  </select>
                </div>
                <div style={{ display: "grid", gap: 6 }}>
                  <div style={{ fontSize: 12, color: dashboardMuted }}>Pipeline</div>
                  <div
                    style={{
                      padding: "10px 12px",
                      borderRadius: 12,
                      border: dashboardBorder,
                      background: isDark ? "rgba(255,255,255,0.04)" : "#ffffff",
                      fontSize: 13,
                      fontWeight: 700,
                    }}
                  >
                    {getCrmPipeline(crmPipelines, crmLeadDraft.quoteType).label}
                  </div>
                </div>
                <div style={{ display: "grid", gap: 6 }}>
                  <div style={{ fontSize: 12, color: dashboardMuted }}>Initial Stage</div>
                  <select
                    value={crmLeadDraft.stageId}
                    onChange={(e) =>
                      setCrmLeadDraft((prev) => ({
                        ...prev,
                        stageId: e.target.value,
                      }))
                    }
                    style={selectInput}
                  >
                    {stageOptions.map((stage) => (
                      <option key={stage.id} value={stage.id}>
                        {stage.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ ...dashboardCardStyle, background: dashboardSoftBg, padding: 16, display: "grid", gap: 12 }}>
                <div style={{ fontSize: 16, fontWeight: 800 }}>Business Details</div>
                <input
                  type="number"
                  value={crmLeadDraft.expectedValue || ""}
                  onChange={(e) =>
                    setCrmLeadDraft((prev) => ({
                      ...prev,
                      expectedValue: e.target.value,
                    }))
                  }
                  style={input}
                  placeholder="Expected Value"
                />
                <input
                  value={crmLeadDraft.nextAction || ""}
                  onChange={(e) =>
                    setCrmLeadDraft((prev) => ({
                      ...prev,
                      nextAction: e.target.value,
                    }))
                  }
                  style={input}
                  placeholder="Next Action"
                />
                <input
                  type="date"
                  value={crmLeadDraft.nextActionDate || ""}
                  onChange={(e) =>
                    setCrmLeadDraft((prev) => ({
                      ...prev,
                      nextActionDate: e.target.value,
                    }))
                  }
                  style={input}
                />
                <select
                  value={crmLeadDraft.assignedToUserId || ""}
                  onChange={(e) => {
                    const member = getTeamMemberById(e.target.value);
                    setCrmLeadDraft((prev) => ({
                      ...prev,
                      assignedToUserId: e.target.value,
                      assignedToNameSnapshot: member?.name || "",
                    }));
                  }}
                  style={selectInput}
                >
                  <option value="">Unassigned</option>
                  {getAssignableTeamMembers(crmLeadDraft.assignedToUserId).map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.name || "Unnamed Team Member"}
                      {member.role ? ` | ${member.role}` : ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDraftRecoveryModal = () => {
    if (!draftRecoveryPrompt) return null;

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 140,
          background: "rgba(15,23,42,0.48)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isCompact ? 12 : 24,
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 560,
            ...dashboardCardStyle,
            display: "grid",
            gap: 16,
            padding: isCompact ? 18 : 24,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#2563eb",
                marginBottom: 8,
              }}
            >
              Unsaved work found
            </div>
            <div style={{ fontSize: 24, fontWeight: 900 }}>
              {draftRecoveryPrompt.title}
            </div>
            <div
              style={{
                fontSize: 13,
                color: dashboardMuted,
                marginTop: 10,
                lineHeight: 1.7,
              }}
            >
              {draftRecoveryPrompt.description}
            </div>
          </div>

          <div
            style={{
              padding: 14,
              borderRadius: 18,
              background: dashboardSoftBg,
              border: dashboardBorder,
              display: "grid",
              gap: 8,
            }}
          >
            <div style={{ fontSize: 12, color: dashboardMuted }}>
              Draft saved
              {draftRecoveryPrompt.savedAt
                ? ` ${new Date(draftRecoveryPrompt.savedAt).toLocaleString("en-IN")}`
                : " recently"}
            </div>
            {draftRecoveryPrompt.serverIsNewer ? (
              <div
                style={{
                  fontSize: 12,
                  color: isDark ? "#fde68a" : "#92400e",
                  padding: "10px 12px",
                  borderRadius: 14,
                  background: isDark ? "rgba(245,158,11,0.12)" : "#fffbeb",
                  border: "1px solid rgba(245,158,11,0.18)",
                }}
              >
                {draftRecoveryPrompt.serverNewerDescription}
              </div>
            ) : null}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              onClick={draftRecoveryPrompt.onDiscard}
              style={{
                ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"),
                marginRight: 0,
              }}
            >
              {draftRecoveryPrompt.discardLabel || "Discard"}
            </button>
            <button
              type="button"
              onClick={draftRecoveryPrompt.onRestore}
              style={{ ...btn("#2563eb"), marginRight: 0 }}
            >
              {draftRecoveryPrompt.restoreLabel || "Restore"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderConfirmDialog = () => {
    if (!confirmDialog) return null;

    const toneColor =
      confirmDialog.tone === "danger"
        ? "#dc2626"
        : confirmDialog.tone === "warning"
        ? "#f59e0b"
        : "#2563eb";

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 145,
          background: "rgba(15,23,42,0.48)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isCompact ? 12 : 24,
        }}
      >
        <div
          style={{
            width: "min(520px, 100%)",
            borderRadius: 26,
            background: dashboardCardBg,
            border: dashboardBorder,
            boxShadow: isDark
              ? "0 24px 60px rgba(2,6,23,0.65)"
              : "0 24px 60px rgba(15,23,42,0.16)",
            padding: isCompact ? 20 : 26,
            display: "grid",
            gap: 18,
          }}
        >
          <div style={{ display: "grid", gap: 8 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: toneColor,
              }}
            >
              Please Confirm
            </div>
            <div style={{ fontSize: 24, fontWeight: 900 }}>{confirmDialog.title}</div>
            <div style={{ fontSize: 13, color: dashboardMuted, lineHeight: 1.7 }}>
              {confirmDialog.description}
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={async () => {
                const action = confirmDialog.onCancel;
                setConfirmDialog(null);
                if (typeof action === "function") {
                  await action();
                }
              }}
              style={{
                ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"),
                marginRight: 0,
              }}
            >
              {confirmDialog.cancelLabel || "Cancel"}
            </button>
            {(confirmDialog.extraActions || []).map((action, index) => (
              <button
                key={`${action.label || "action"}-${index}`}
                type="button"
                onClick={async () => {
                  setConfirmDialog(null);
                  if (typeof action.onClick === "function") {
                    await action.onClick();
                  }
                }}
                style={{
                  ...btn(action.tone === "danger" ? "#dc2626" : "#475569"),
                  marginRight: 0,
                }}
              >
                {action.label || "Action"}
              </button>
            ))}
            {confirmDialog.secondaryLabel ? (
              <button
                type="button"
                onClick={async () => {
                  const action = confirmDialog.onSecondary;
                  setConfirmDialog(null);
                  if (typeof action === "function") {
                    await action();
                  }
                }}
                style={{
                  ...btn(isDark ? "#334155" : "#64748b"),
                  marginRight: 0,
                }}
              >
                {confirmDialog.secondaryLabel}
              </button>
            ) : null}
            <button
              type="button"
              onClick={async () => {
                const action = confirmDialog.onConfirm;
                setConfirmDialog(null);
                if (typeof action === "function") {
                  await action();
                }
              }}
              style={{
                ...btn(confirmDialog.tone === "danger" ? "#dc2626" : "#2563eb"),
                marginRight: 0,
              }}
            >
              {confirmDialog.confirmLabel || "Confirm"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderDashboardDrilldownModal = () => {
    if (!dashboardDrilldownModal) return null;

    const drilldown = getDashboardMetricRecords(
      dashboardDrilldownModal.key,
      dashboardDrilldownModal.context || {}
    );
    const records = Array.isArray(drilldown.records) ? drilldown.records : [];
    const query = normalizeSearchText(dashboardDrilldownSearch);
    const filteredRecords = records.filter((record) => {
      if (!query) return true;
      if (drilldown.recordType === "deals") {
        return doesSearchBlobMatch(buildDealSearchBlob(record), query);
      }
      if (drilldown.recordType === "customers") {
        return buildCustomerLedgerSearchText(record).includes(query);
      }
      return doesSearchBlobMatch(
        [
          record.customerLabel,
          record.customerName,
          record.quoteNo,
          record.status,
          record.workStageResolved,
          record.source,
          getQuoteTypeLabel(record.quoteTypeResolved || resolveQuoteType(record)),
          formatCurrencyDisplay(record.totalValue || record.expectedValue || 0, currency),
        ].join(" "),
        query
      );
    });

    const closeModal = () => {
      setDashboardDrilldownModal(null);
      setDashboardDrilldownSearch("");
    };
    const openQuoteRecord = (quote) => {
      if (!quote?.id) return;
      closeModal();
      loadFullQuote(quote);
    };
    const openDealRecord = (deal) => {
      if (!deal?.id) return;
      closeModal();
      setCrmView("list");
      setRoutedTab("crm");
      openCrmDealDetails(deal, { crmView: "list" });
    };
    const openQuoteDeal = (quote) => {
      const linkedLead =
        findLinkedCrmLeadForQuote(
          quote,
          quote?.crm?.leadId || quote?.linkedLeadId || ""
        ) || null;
      if (linkedLead?.id) {
        openDealRecord(linkedLead);
      } else {
        notify("This quote is not linked to a CRM deal yet.", "info");
      }
    };
    const openRecordCustomer = (record = {}) => {
      closeModal();
      openDashboardCustomer({
        customerId: record.customerId || record.crm?.customerId || record.customer?.id,
        name: record.customerLabel || record.customerName || record.crmContact?.name,
        phone: record.customerPhone || record.crm?.customerPhone || record.crmContact?.phone,
      });
    };
    const renderQuoteLikeRecord = (record, index, isInstallation = false) => (
      <div
        key={`${record.id || record.quoteNo || index}-${isInstallation ? "install" : "quote"}`}
        style={{
          padding: 14,
          borderRadius: 18,
          border: dashboardBorder,
          background: dashboardSoftBg,
          display: "grid",
          gap: 10,
          minWidth: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "flex-start",
            flexWrap: isMobile ? "wrap" : "nowrap",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 900, overflowWrap: "anywhere" }}>
              {record.customerLabel || record.customerName || "Unnamed Customer"}
            </div>
            <div style={{ marginTop: 4, fontSize: 12, color: dashboardMuted }}>
              #{record.quoteNo || "-"} |{" "}
              {getQuoteTypeLabel(record.quoteTypeResolved || resolveQuoteType(record))}
              {isInstallation && record.installationMillis
                ? ` | Install ${new Date(record.installationMillis).toLocaleDateString("en-IN")}`
                : ""}
            </div>
          </div>
          <div style={{ fontWeight: 950, whiteSpace: "nowrap" }}>
            {formatCurrencyDisplay(record.totalValue || 0, currency)}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <span
            style={{
              padding: "4px 9px",
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 850,
              background:
                (record.quoteTypeResolved || resolveQuoteType(record)) === GENERAL_QUOTE_TYPE
                  ? "#dbeafe"
                  : "#ede9fe",
              color:
                (record.quoteTypeResolved || resolveQuoteType(record)) === GENERAL_QUOTE_TYPE
                  ? "#1d4ed8"
                  : "#7c3aed",
            }}
          >
            {getQuoteTypeLabel(record.quoteTypeResolved || resolveQuoteType(record))}
          </span>
          <span
            style={{
              padding: "4px 9px",
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 850,
              background: record.isConfirmed ? "#dcfce7" : "#fef3c7",
              color: record.isConfirmed ? "#166534" : "#92400e",
            }}
          >
            {record.isConfirmed ? "Confirmed" : record.status || "Draft"}
          </span>
          {record.workStageResolved ? (
            <span
              style={{
                padding: "4px 9px",
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 850,
                background: isDark ? "rgba(148,163,184,0.16)" : "#e2e8f0",
                color: isDark ? "#cbd5e1" : "#475569",
              }}
            >
              {getStageLabelForQuote(record)}
            </span>
          ) : null}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => openQuoteRecord(record)}
            style={{ ...btn("#2563eb"), marginRight: 0, padding: "8px 11px" }}
          >
            Open Quote
          </button>
          <button
            type="button"
            onClick={() => openQuoteDeal(record)}
            style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 11px" }}
          >
            Open CRM Deal
          </button>
          <button
            type="button"
            onClick={() => openRecordCustomer(record)}
            style={{ ...btn("#10b981"), marginRight: 0, padding: "8px 11px" }}
          >
            Open Customer
          </button>
        </div>
      </div>
    );
    const renderDealRecord = (deal, index) => {
      const primaryQuote = getPrimaryCrmLinkedQuote(normalizeCrmLinkedQuotes(deal));
      return (
        <div
          key={`${deal.id || index}-deal`}
          style={{
            padding: 14,
            borderRadius: 18,
            border: dashboardBorder,
            background: dashboardSoftBg,
            display: "grid",
            gap: 10,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 900, overflowWrap: "anywhere" }}>
                {deal.crmTitle || deal.customerLabel || deal.crmContact?.name || "Unnamed Deal"}
              </div>
              <div style={{ marginTop: 4, fontSize: 12, color: dashboardMuted }}>
                {primaryQuote?.quoteNo ? `#${primaryQuote.quoteNo} | ` : ""}
                {deal.crmStageLabel || "CRM Deal"}
              </div>
            </div>
            <div style={{ fontWeight: 950, whiteSpace: "nowrap" }}>
              {formatCurrencyDisplay(deal.expectedValue || 0, currency)}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => openDealRecord(deal)}
              style={{ ...btn("#2563eb"), marginRight: 0, padding: "8px 11px" }}
            >
              Details
            </button>
            {primaryQuote?.quoteId ? (
              <button
                type="button"
                onClick={() => {
                  closeModal();
                  loadFullQuote(primaryQuote.quoteId);
                }}
                style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 11px" }}
              >
                Open Quote
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => openRecordCustomer(deal)}
              style={{ ...btn("#10b981"), marginRight: 0, padding: "8px 11px" }}
            >
              Open Customer
            </button>
          </div>
        </div>
      );
    };

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 155,
          background: "rgba(15,23,42,0.56)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isCompact ? 12 : 24,
        }}
      >
        <div
          style={{
            width: "min(980px, 100%)",
            maxHeight: "calc(100vh - 32px)",
            overflow: "hidden",
            borderRadius: 28,
            background: dashboardCardBg,
            border: dashboardBorder,
            boxShadow: isDark
              ? "0 24px 70px rgba(2,6,23,0.72)"
              : "0 24px 70px rgba(15,23,42,0.2)",
            display: "grid",
            gridTemplateRows: "auto auto minmax(0, 1fr)",
          }}
        >
          <div style={{ padding: isCompact ? 18 : 24, borderBottom: dashboardBorder }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 900,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "#2563eb",
                  }}
                >
                  Dashboard Drill-down
                </div>
                <div style={{ marginTop: 6, fontSize: isCompact ? 22 : 28, fontWeight: 950 }}>
                  {drilldown.title}
                </div>
                {drilldown.subtitle ? (
                  <div style={{ marginTop: 6, fontSize: 13, color: dashboardMuted, lineHeight: 1.6 }}>
                    {drilldown.subtitle}
                  </div>
                ) : null}
              </div>
              <button
                type="button"
                onClick={closeModal}
                style={{
                  ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"),
                  marginRight: 0,
                  alignSelf: "start",
                }}
              >
                Close
              </button>
            </div>
          </div>
          <div
            style={{
              padding: isCompact ? 14 : 18,
              borderBottom: dashboardBorder,
              display: "grid",
              gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 1fr) auto",
              gap: 12,
              alignItems: "center",
            }}
          >
            <input
              value={dashboardDrilldownSearch}
              onChange={(event) => setDashboardDrilldownSearch(event.target.value)}
              style={input}
              placeholder="Search within these records..."
            />
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ fontSize: 12, color: dashboardMuted, fontWeight: 850 }}>
                {filteredRecords.length} of {records.length} shown
              </span>
              {drilldown.summaryValue != null ? (
                <span style={{ fontSize: 12, color: textColor, fontWeight: 950 }}>
                  Total {formatCurrencyDisplay(drilldown.summaryValue || 0, currency)}
                </span>
              ) : null}
            </div>
          </div>
          <div style={{ overflowY: "auto", padding: isCompact ? 14 : 18 }}>
            {drilldown.filters?.length ? (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
                {drilldown.filters.map((filter) => (
                  <span
                    key={filter}
                    style={{
                      padding: "5px 10px",
                      borderRadius: 999,
                      background: isDark ? "rgba(37,99,235,0.18)" : "#dbeafe",
                      color: "#2563eb",
                      fontSize: 11,
                      fontWeight: 850,
                    }}
                  >
                    {filter}
                  </span>
                ))}
              </div>
            ) : null}
            {filteredRecords.length === 0 ? (
              <div
                style={{
                  padding: 28,
                  borderRadius: 20,
                  background: dashboardSoftBg,
                  border: dashboardBorder,
                  textAlign: "center",
                  color: dashboardMuted,
                  fontWeight: 800,
                }}
              >
                No records found for this drill-down.
              </div>
            ) : (
              <div style={{ display: "grid", gap: 12 }}>
                {filteredRecords.map((record, index) =>
                  drilldown.recordType === "deals"
                    ? renderDealRecord(record, index)
                    : renderQuoteLikeRecord(
                        record,
                        index,
                        drilldown.recordType === "installations"
                      )
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCrmFollowUpModal = () => {
    if (!crmFollowUpModalState?.followUp) return null;

    const followUp = crmFollowUpModalState.followUp;
    const attachmentUploadKey = `${selectedCrmDeal?.id || ""}:${followUp.id}`;

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 150,
          background: "rgba(15,23,42,0.56)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isCompact ? 12 : 24,
        }}
      >
        <div
          style={{
            width: "min(760px, 100%)",
            maxHeight: "calc(100vh - 32px)",
            overflowY: "auto",
            borderRadius: 26,
            background: dashboardCardBg,
            border: dashboardBorder,
            boxShadow: isDark
              ? "0 24px 60px rgba(2,6,23,0.65)"
              : "0 24px 60px rgba(15,23,42,0.16)",
            padding: isCompact ? 20 : 26,
            display: "grid",
            gap: 18,
          }}
        >
          <div style={{ display: "grid", gap: 8 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#2563eb",
              }}
            >
              Follow-up
            </div>
            <div style={{ fontSize: 24, fontWeight: 900 }}>
              {crmFollowUpModalState.mode === "edit" ? "Edit Follow-up" : "Add Follow-up"}
            </div>
            <div style={{ fontSize: 13, color: dashboardMuted, lineHeight: 1.7 }}>
              Keep each task focused with its own due date, repeat cadence, notes, and attachments.
            </div>
          </div>

          <div style={{ display: "grid", gap: 14 }}>
            <div style={{ display: "grid", gap: 6 }}>
              <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>Title</div>
              <input
                value={followUp.title ?? ""}
                onChange={(event) =>
                  updateCrmFollowUpModalDraft({ title: event.target.value })
                }
                style={input}
                placeholder="Client callback, site visit, material approval..."
              />
            </div>

            <div style={{ display: "grid", gap: 6 }}>
              <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                Description
              </div>
              <textarea
                value={followUp.description ?? ""}
                onChange={(event) =>
                  updateCrmFollowUpModalDraft({ description: event.target.value })
                }
                style={{ ...input, minHeight: 110, resize: "vertical" }}
                placeholder="Add context, promised work, or what the team needs to do next."
              />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isCompact
                  ? "1fr"
                  : "repeat(auto-fit, minmax(150px, 1fr))",
                gap: 12,
              }}
            >
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                  Due Date
                </div>
                <input
                  type="date"
                  value={followUp.dueDate ?? ""}
                  onChange={(event) =>
                    updateCrmFollowUpModalDraft({
                      dueDate: event.target.value,
                      reminderEnabled: event.target.value
                        ? followUp.reminderEnabled !== false
                        : false,
                    })
                  }
                  style={{ ...input, width: "100%", minWidth: 0 }}
                />
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                  Due Time
                </div>
                <input
                  type="time"
                  value={followUp.dueTime ?? DEFAULT_CRM_REMINDER_TIME}
                  onChange={(event) =>
                    updateCrmFollowUpModalDraft({
                      dueTime: parseTimeInputValue(event.target.value),
                    })
                  }
                  style={{ ...input, width: "100%", minWidth: 0 }}
                />
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                  Repeat
                </div>
                <select
                  value={followUp.repeat ?? "none"}
                  onChange={(event) =>
                    updateCrmFollowUpModalDraft({ repeat: event.target.value })
                  }
                  style={{ ...selectInput, width: "100%", minWidth: 0 }}
                >
                  {CRM_FOLLOW_UP_REPEAT_OPTIONS.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                  Priority
                </div>
                <select
                  value={followUp.priority ?? CRM_FOLLOW_UP_PRIORITY_MEDIUM}
                  onChange={(event) =>
                    updateCrmFollowUpModalDraft({ priority: event.target.value })
                  }
                  style={{ ...selectInput, width: "100%", minWidth: 0 }}
                >
                  {CRM_FOLLOW_UP_PRIORITY_OPTIONS.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                  Order
                </div>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={followUp.order || ""}
                  onChange={(event) =>
                    updateCrmFollowUpModalDraft({ order: event.target.value })
                  }
                  style={{ ...input, width: "100%", minWidth: 0 }}
                  placeholder="1"
                />
              </div>
            </div>

            <label
              style={{
                padding: 14,
                borderRadius: 18,
                background: isDark ? "rgba(37,99,235,0.10)" : "#eff6ff",
                border: "1px solid rgba(37,99,235,0.18)",
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={followUp.reminderEnabled === true}
                onChange={(event) =>
                  updateCrmFollowUpModalDraft({
                    reminderEnabled: event.target.checked,
                  })
                }
                style={{ marginTop: 3 }}
              />
              <span style={{ display: "grid", gap: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 900 }}>
                  Reminder Enabled
                </span>
                <span style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                  When enabled, this task can show in-app reminders until it is marked done.
                </span>
              </span>
            </label>

            <div
              style={{
                padding: 16,
                borderRadius: 18,
                background: dashboardSoftBg,
                border: dashboardBorder,
                display: "grid",
                gap: 12,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  flexDirection: isCompact ? "column" : "row",
                  alignItems: isCompact ? "stretch" : "center",
                }}
              >
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800 }}>Attachments</div>
                  <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                    Add photos, PDFs, or site notes directly to this follow-up.
                  </div>
                </div>
                <label
                  style={{
                    ...btn("#0f172a"),
                    marginRight: 0,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  <UploadCloud size={14} style={{ marginRight: 6 }} />
                  Add Attachment
                  <input
                    type="file"
                    multiple
                    style={{ display: "none" }}
                    onChange={(event) => {
                      const files = Array.from(event.target.files || []);
                      uploadCrmFollowUpAttachments(files);
                      event.target.value = "";
                    }}
                  />
                </label>
              </div>

              {crmFollowUpAttachmentUploadKey === attachmentUploadKey ? (
                <div style={{ fontSize: 12, color: dashboardMuted }}>Uploading attachment...</div>
              ) : null}

              {normalizeCrmAttachments(followUp.attachments || []).length === 0 ? (
                <div style={{ fontSize: 12, color: dashboardMuted }}>
                  No attachments added yet for this follow-up.
                </div>
              ) : (
                <div style={{ display: "grid", gap: 10 }}>
                  {normalizeCrmAttachments(followUp.attachments || []).map((attachment) => {
                    const isImage = String(attachment.contentType || "").startsWith("image/");
                    return (
                      <div
                        key={attachment.id}
                        style={{
                          display: "flex",
                          gap: 12,
                          alignItems: "center",
                          flexWrap: "wrap",
                          padding: 12,
                          borderRadius: 16,
                          background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                          border: dashboardBorder,
                        }}
                      >
                        {isImage && attachment.url ? (
                          <img
                            src={attachment.url}
                            alt={attachment.name}
                            style={{
                              width: 64,
                              height: 64,
                              borderRadius: 14,
                              objectFit: "cover",
                              border: dashboardBorder,
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: 64,
                              height: 64,
                              borderRadius: 14,
                              display: "grid",
                              placeItems: "center",
                              background: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc",
                              border: dashboardBorder,
                            }}
                          >
                            <Paperclip size={16} />
                          </div>
                        )}
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 800, wordBreak: "break-word" }}>
                            {attachment.name}
                          </div>
                          <div style={{ fontSize: 11, color: dashboardMuted, marginTop: 4 }}>
                            {attachment.uploadedAt
                              ? `Uploaded ${new Date(attachment.uploadedAt).toLocaleString("en-IN")}`
                              : "Uploaded"}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <a
                            href={attachment.url}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              ...btn("#2563eb"),
                              marginRight: 0,
                              textDecoration: "none",
                            }}
                          >
                            View
                          </a>
                          <a
                            href={attachment.url}
                            download={attachment.filename || attachment.name}
                            style={{
                              ...btn("#0f172a"),
                              marginRight: 0,
                              textDecoration: "none",
                            }}
                          >
                            Download
                          </a>
                          <button
                            type="button"
                            onClick={() => removeCrmFollowUpAttachment(attachment.id)}
                            style={{ ...btn("#ef4444"), marginRight: 0 }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={closeCrmFollowUpModal}
              style={{
                ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"),
                marginRight: 0,
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveCrmFollowUpModal}
              style={{ ...btn("#16a34a"), marginRight: 0 }}
            >
              Save Follow-up
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderQuoteDealChoiceModal = () => {
    if (!quoteDealChoiceState) return null;
    const recommendedDeal = quoteDealChoiceState.openDeals?.[0] || null;

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15,23,42,.38)",
          backdropFilter: "blur(8px)",
          zIndex: 10040,
          display: "grid",
          placeItems: "center",
          padding: 18,
        }}
      >
        <div
          style={{
            width: "min(560px, 100%)",
            borderRadius: 28,
            background: isDark ? "#0f172a" : "#fff",
            border: dashboardBorder,
            boxShadow: "0 28px 80px rgba(15,23,42,.28)",
            padding: 24,
            color: isDark ? "#f8fafc" : "#0f172a",
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 900, color: "#2563eb", letterSpacing: ".08em", textTransform: "uppercase" }}>
            CRM deal already open
          </div>
          <h3 style={{ margin: "8px 0 8px", fontSize: 22, lineHeight: 1.15 }}>
            This customer already has an open deal. What do you want to do?
          </h3>
          <p style={{ margin: 0, color: dashboardMuted, lineHeight: 1.55 }}>
            {quoteDealChoiceState.customerName}
            {quoteDealChoiceState.quoteNo ? ` | Quote #${quoteDealChoiceState.quoteNo}` : ""}.
            Linking to the existing deal keeps the customer pipeline clean.
          </p>

          {recommendedDeal ? (
            <div
              style={{
                marginTop: 16,
                padding: 14,
                borderRadius: 18,
                background: dashboardSoftBg,
                border: dashboardBorder,
                display: "grid",
                gap: 6,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                <strong>{recommendedDeal.title || recommendedDeal.customerName || "Open CRM Deal"}</strong>
                <span style={{ color: "#2563eb", fontSize: 12, fontWeight: 900 }}>
                  Recommended
                </span>
              </div>
              <div style={{ color: dashboardMuted, fontSize: 12 }}>
                {getQuoteTypeLabel(recommendedDeal.pipelineType || recommendedDeal.quoteType)} | {recommendedDeal.stageNameSnapshot || recommendedDeal.stageId || "Open"} | {formatCurrencyDisplay(recommendedDeal.expectedValue || recommendedDeal.finalValue || 0, currency)}
              </div>
            </div>
          ) : null}

          <div style={{ display: "grid", gap: 10, marginTop: 20 }}>
            <button
              type="button"
              onClick={() => resolveQuoteDealChoice("linkExisting")}
              disabled={!recommendedDeal}
              style={{
                ...btn("#2563eb"),
                marginRight: 0,
                justifyContent: "center",
                opacity: recommendedDeal ? 1 : 0.55,
              }}
            >
              Link this quote to existing deal
            </button>
            <button
              type="button"
              onClick={() => resolveQuoteDealChoice("create")}
              style={{
                ...btn(isDark ? "#1e293b" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"),
                marginRight: 0,
                justifyContent: "center",
              }}
            >
              Create separate new deal
            </button>
            <button
              type="button"
              onClick={() => resolveQuoteDealChoice("saveOnly")}
              style={{
                ...btn("transparent", dashboardMuted),
                border: dashboardBorder,
                marginRight: 0,
                justifyContent: "center",
              }}
            >
              Save quote only
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderQuoteLinkModal = () => {
    if (!quoteLinkModalState || !quoteLinkTargetLead) return null;

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 152,
          background: "rgba(15,23,42,0.56)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isCompact ? 12 : 24,
        }}
      >
        <div
          style={{
            width: "min(980px, 100%)",
            maxHeight: "calc(100vh - 32px)",
            overflowY: "auto",
            borderRadius: 26,
            background: dashboardCardBg,
            border: dashboardBorder,
            boxShadow: isDark
              ? "0 24px 60px rgba(2,6,23,0.65)"
              : "0 24px 60px rgba(15,23,42,0.16)",
            padding: isCompact ? 20 : 26,
            display: "grid",
            gap: 18,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 14,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "flex-start",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "#2563eb",
                }}
              >
                Link Quote
              </div>
              <div style={{ fontSize: 24, fontWeight: 900, marginTop: 6 }}>
                Link Existing Quote
              </div>
              <div style={{ fontSize: 13, color: dashboardMuted, marginTop: 6, lineHeight: 1.7 }}>
                {quoteLinkTargetLead.customerLabel || quoteLinkTargetLead.crmTitle || "Selected Lead"}
              </div>
            </div>
            <button
              type="button"
              onClick={closeQuoteLinkModal}
              style={{
                width: 34,
                height: 34,
                borderRadius: 12,
                border: "none",
                display: "grid",
                placeItems: "center",
                background: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                color: textColor,
                cursor: "pointer",
                justifySelf: isCompact ? "end" : "auto",
              }}
              title="Close"
            >
              <X size={16} />
            </button>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact
                ? "1fr"
                : "minmax(220px, 1.3fr) minmax(150px, 0.8fr) repeat(2, minmax(130px, 0.7fr))",
              gap: 12,
            }}
          >
            <input
              value={quoteLinkSearch}
              onChange={(event) => setQuoteLinkSearch(event.target.value)}
              style={{ ...input, width: "100%", minWidth: 0 }}
              placeholder="Search customer, phone, quote no, or status"
            />
            <select
              value={quoteLinkTypeFilter}
              onChange={(event) => setQuoteLinkTypeFilter(event.target.value)}
              style={{ ...selectInput, width: "100%", minWidth: 0 }}
            >
              <option value="all">All Types</option>
              <option value={GENERAL_QUOTE_TYPE}>General</option>
              <option value={CURTAIN_QUOTE_TYPE}>Curtain</option>
            </select>
            <input
              type="number"
              min="0"
              value={quoteLinkMinAmount}
              onChange={(event) => setQuoteLinkMinAmount(event.target.value)}
              style={{ ...input, width: "100%", minWidth: 0 }}
              placeholder="Min amount"
            />
            <input
              type="number"
              min="0"
              value={quoteLinkMaxAmount}
              onChange={(event) => setQuoteLinkMaxAmount(event.target.value)}
              style={{ ...input, width: "100%", minWidth: 0 }}
              placeholder="Max amount"
            />
          </div>

          <div style={{ fontSize: 12, color: dashboardMuted }}>
            Showing {quoteLinkResults.length} matching quotation
            {quoteLinkResults.length === 1 ? "" : "s"}
          </div>

          {quoteLinkResults.length === 0 ? (
            <div
              style={{
                padding: 20,
                borderRadius: 18,
                background: dashboardSoftBg,
                border: dashboardBorder,
                display: "grid",
                gap: 8,
              }}
            >
              <div style={{ fontSize: 16, fontWeight: 800 }}>No quotes found</div>
              <div style={{ fontSize: 13, color: dashboardMuted, lineHeight: 1.7 }}>
                Try a customer name, phone number, quote number, or wider amount range.
              </div>
            </div>
          ) : (
            <div style={{ display: "grid", gap: 12 }}>
              {quoteLinkResults.map(({ quote, meta }) => {
                const linkedLead = findLinkedCrmLeadForQuote(quote);
                const linkedToThisLead = linkedLead?.id === quoteLinkTargetLead.id;
                const linkedElsewhere = linkedLead?.id && !linkedToThisLead;
                const badgeColor = linkedToThisLead
                  ? "#16a34a"
                  : linkedElsewhere
                  ? "#f59e0b"
                  : "#2563eb";

                return (
                  <div
                    key={quote.id}
                    style={{
                      padding: 16,
                      borderRadius: 20,
                      background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                      border: dashboardBorder,
                      display: "grid",
                      gap: 12,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 12,
                        flexDirection: isCompact ? "column" : "row",
                        alignItems: isCompact ? "stretch" : "flex-start",
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 18, fontWeight: 900 }}>
                          #{meta.quoteNo || "-"} | {meta.customerName || "Unnamed Customer"}
                        </div>
                        <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 5, lineHeight: 1.6 }}>
                          {meta.phone || "No phone"} | {meta.quoteDateLabel || "-"} |{" "}
                          {currencySymbol(currency)} {Number(meta.totalValue || 0).toFixed(2)}
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          flexWrap: "wrap",
                          alignItems: "center",
                        }}
                      >
                        <span
                          style={{
                            padding: "7px 11px",
                            borderRadius: 999,
                            background: `${badgeColor}18`,
                            color: badgeColor,
                            fontSize: 11,
                            fontWeight: 800,
                          }}
                        >
                          {linkedToThisLead
                            ? "Linked to this lead"
                            : linkedElsewhere
                            ? "Linked elsewhere"
                            : "Unlinked"}
                        </span>
                        <span
                          style={{
                            padding: "7px 11px",
                            borderRadius: 999,
                            background: isDark ? "rgba(148,163,184,0.18)" : "#e2e8f0",
                            color: isDark ? "#e2e8f0" : "#334155",
                            fontSize: 11,
                            fontWeight: 800,
                          }}
                        >
                          {meta.quoteTypeLabel}
                        </span>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isCompact ? "1fr" : "repeat(4, minmax(0, 1fr))",
                        gap: 10,
                      }}
                    >
                      {[
                        ["Customer", meta.customerName || "-"],
                        ["Phone", meta.phone || "-"],
                        ["Status", meta.status || "-"],
                        [
                          "Linked To",
                          linkedLead
                            ? linkedLead.title || linkedLead.contact?.name || linkedLead.id
                            : "Not linked",
                        ],
                      ].map(([label, value]) => (
                        <div
                          key={label}
                          style={{
                            padding: 11,
                            borderRadius: 16,
                            background: dashboardSoftBg,
                            border: dashboardBorder,
                          }}
                        >
                          <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                            {label}
                          </div>
                          <div style={{ fontSize: 13, fontWeight: 800, marginTop: 6, wordBreak: "break-word" }}>
                            {value}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        onClick={() => requestLinkExistingQuote(quoteLinkTargetLead, quote)}
                        disabled={isLinkingQuote || linkedToThisLead}
                        style={{
                          ...btn(linkedToThisLead ? "#94a3b8" : "#16a34a"),
                          marginRight: 0,
                        }}
                      >
                        <Link2 size={14} style={{ marginRight: 6 }} />
                        {linkedToThisLead ? "Already Linked" : "Link this Quote"}
                      </button>
                      {canViewQuotes ? (
                        <button
                          type="button"
                          onClick={() => openQuoteFromLinkModal(quote)}
                          style={{ ...btn("#2563eb"), marginRight: 0 }}
                        >
                          <ExternalLink size={14} style={{ marginRight: 6 }} />
                          Open Quote
                        </button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCustomerCreateModal = () => {
    if (!customerCreateState) return null;

    const draft = customerCreateState.draft || {};
    const updateDraft = (field, value) => {
      setCustomerCreateState((prev) => ({
        ...prev,
        draft: {
          ...(prev?.draft || {}),
          [field]: value,
        },
        duplicateWarning: false,
      }));
    };

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 120,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 18,
          background: "rgba(15,23,42,0.46)",
          backdropFilter: "blur(10px)",
        }}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) setCustomerCreateState(null);
        }}
      >
        <div
          style={{
            width: "min(680px, 100%)",
            maxHeight: "92vh",
            overflowY: "auto",
            borderRadius: 28,
            background: dashboardCardBg,
            border: dashboardBorder,
            boxShadow: isDark
              ? "0 30px 90px rgba(2,6,23,0.62)"
              : "0 30px 90px rgba(15,23,42,0.22)",
            padding: 22,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 14,
              alignItems: "flex-start",
              marginBottom: 18,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 900,
                  letterSpacing: "-0.04em",
                  color: textColor,
                }}
              >
                Create Customer
              </div>
              <div style={{ marginTop: 5, color: dashboardMuted, fontSize: 13 }}>
                Add this customer to the ledger and link it to the quotation.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setCustomerCreateState(null)}
              style={{
                border: "none",
                width: 38,
                height: 38,
                borderRadius: 14,
                background: dashboardSoftBg,
                color: textColor,
                cursor: "pointer",
                fontWeight: 900,
              }}
            >
              x
            </button>
          </div>

          {customerCreateState.duplicateWarning ? (
            <div
              style={{
                display: "grid",
                gap: 10,
                padding: 14,
                borderRadius: 20,
                border: isDark
                  ? "1px solid rgba(251,191,36,0.32)"
                  : "1px solid rgba(245,158,11,0.32)",
                background: isDark
                  ? "rgba(120,53,15,0.22)"
                  : "rgba(255,251,235,0.95)",
                marginBottom: 16,
              }}
            >
              <div style={{ fontSize: 15, fontWeight: 900, color: textColor }}>
                Possible existing customer found
              </div>
              <div style={{ fontSize: 13, color: dashboardMuted }}>
                Use an existing customer if this is the same person. Create a new
                customer only when it is genuinely separate.
              </div>
              {(customerCreateState.duplicateMatches || []).map(({ customer, reason }) => (
                <div
                  key={customer.id}
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 10,
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: 12,
                    borderRadius: 16,
                    background: isDark ? "rgba(15,23,42,0.72)" : "#ffffff",
                    border: dashboardBorder,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 900,
                        color: textColor,
                        overflowWrap: "anywhere",
                      }}
                    >
                      {customer.customerName || "Unnamed Customer"}
                    </div>
                    <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 3 }}>
                      {[customer.phone || customer.whatsapp, customer.address]
                        .filter(Boolean)
                        .join(" | ") || "No contact details"}{" "}
                      - {reason} - {Number(customer.totals?.totalQuotes || 0)} quotes
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      applyCustomerToQuoteContext(customerCreateState.context, customer);
                      setCustomerCreateState(null);
                      notify("Customer selected", "success");
                    }}
                    style={{
                      ...btn("#2563eb"),
                      minHeight: 36,
                      padding: "8px 12px",
                    }}
                  >
                    Use Existing
                  </button>
                </div>
              ))}
            </div>
          ) : null}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr"
                : "repeat(2, minmax(0, 1fr))",
              gap: 14,
            }}
          >
            <div style={{ gridColumn: isMobile ? "auto" : "1 / -1" }}>
              <label style={{ fontSize: 12, fontWeight: 800, color: dashboardMuted }}>
                Customer Name
              </label>
              <input
                style={{ ...input, marginTop: 6 }}
                value={draft.customerName ?? ""}
                onChange={(event) => updateDraft("customerName", event.target.value)}
                placeholder="Customer name"
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 800, color: dashboardMuted }}>
                Phone
              </label>
              <input
                style={{ ...input, marginTop: 6 }}
                value={draft.phone ?? ""}
                onChange={(event) => updateDraft("phone", event.target.value)}
                placeholder="Phone number"
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 800, color: dashboardMuted }}>
                WhatsApp
              </label>
              <input
                style={{ ...input, marginTop: 6 }}
                value={draft.whatsapp ?? ""}
                onChange={(event) => updateDraft("whatsapp", event.target.value)}
                placeholder="WhatsApp number"
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 800, color: dashboardMuted }}>
                Email
              </label>
              <input
                style={{ ...input, marginTop: 6 }}
                value={draft.email ?? ""}
                onChange={(event) => updateDraft("email", event.target.value)}
                placeholder="Email address"
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 800, color: dashboardMuted }}>
                Company / Project
              </label>
              <input
                style={{ ...input, marginTop: 6 }}
                value={draft.companyName ?? ""}
                onChange={(event) => updateDraft("companyName", event.target.value)}
                placeholder="Optional company or project"
              />
            </div>
            <div style={{ gridColumn: isMobile ? "auto" : "1 / -1" }}>
              <label style={{ fontSize: 12, fontWeight: 800, color: dashboardMuted }}>
                Address
              </label>
              <textarea
                style={{ ...input, marginTop: 6, minHeight: 86, resize: "vertical" }}
                value={draft.address ?? ""}
                onChange={(event) => updateDraft("address", event.target.value)}
                placeholder="Address or location"
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 800, color: dashboardMuted }}>
                Source
              </label>
              <input
                style={{ ...input, marginTop: 6 }}
                value={draft.source ?? ""}
                onChange={(event) => updateDraft("source", event.target.value)}
                placeholder="Walk-in, Instagram, Referral..."
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 800, color: dashboardMuted }}>
                Tags
              </label>
              <input
                style={{ ...input, marginTop: 6 }}
                value={draft.tags ?? ""}
                onChange={(event) => updateDraft("tags", event.target.value)}
                placeholder="VIP, repeat, priority"
              />
            </div>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "flex-end",
              gap: 10,
              marginTop: 20,
            }}
          >
            <button
              type="button"
              onClick={() => setCustomerCreateState(null)}
              style={{
                ...btn(isDark ? "#1e293b" : "#e2e8f0", textColor),
                minHeight: 42,
              }}
            >
              Cancel
            </button>
            {customerCreateState.duplicateWarning ? (
              <button
                type="button"
                onClick={() => createCustomerFromQuote({ force: true })}
                disabled={isCreatingCustomerFromQuote}
                style={{
                  ...btn("#f59e0b"),
                  minHeight: 42,
                  opacity: isCreatingCustomerFromQuote ? 0.72 : 1,
                }}
              >
                {isCreatingCustomerFromQuote ? "Creating..." : "Create New Anyway"}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => createCustomerFromQuote()}
              disabled={isCreatingCustomerFromQuote}
              style={{
                ...btn("#2563eb"),
                minHeight: 42,
                opacity: isCreatingCustomerFromQuote ? 0.72 : 1,
              }}
            >
              {isCreatingCustomerFromQuote ? "Creating..." : "Create Customer"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAppToasts = () => {
    if (appToasts.length === 0) return null;

    const toneStyles = {
      success: {
        accent: "#16a34a",
        background: isDark ? "rgba(22,163,74,0.16)" : "#ecfdf5",
        color: isDark ? "#bbf7d0" : "#166534",
      },
      error: {
        accent: "#dc2626",
        background: isDark ? "rgba(220,38,38,0.18)" : "#fef2f2",
        color: isDark ? "#fecaca" : "#991b1b",
      },
      warning: {
        accent: "#f59e0b",
        background: isDark ? "rgba(245,158,11,0.16)" : "#fffbeb",
        color: isDark ? "#fde68a" : "#92400e",
      },
      info: {
        accent: "#2563eb",
        background: isDark ? "rgba(37,99,235,0.16)" : "#eff6ff",
        color: isDark ? "#bfdbfe" : "#1d4ed8",
      },
    };

    return (
      <div
        style={{
          position: "fixed",
          top: isCompact ? 12 : 18,
          right: isCompact ? 12 : 18,
          zIndex: 150,
          width: isCompact ? "calc(100vw - 24px)" : 360,
          maxWidth: "calc(100vw - 24px)",
          display: "grid",
          gap: 10,
        }}
      >
        {appToasts.map((toast) => {
          const tone = toneStyles[toast.tone] || toneStyles.info;

          return (
            <div
              key={toast.id}
              style={{
                borderRadius: 18,
                border: dashboardBorder,
                background: dashboardCardBg,
                boxShadow: isDark
                  ? "0 18px 40px rgba(2,6,23,0.55)"
                  : "0 18px 40px rgba(15,23,42,0.12)",
                padding: 14,
                display: "grid",
                gridTemplateColumns: "auto 1fr auto",
                gap: 12,
                alignItems: "start",
              }}
            >
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 999,
                  background: tone.accent,
                  marginTop: 5,
                }}
              />
              <div style={{ fontSize: 13, lineHeight: 1.6, color: tone.color }}>
                {toast.message}
              </div>
              <button
                type="button"
                onClick={() => dismissToast(toast.id)}
                style={{
                  border: "none",
                  background: tone.background,
                  color: tone.color,
                  width: 28,
                  height: 28,
                  borderRadius: 999,
                  cursor: "pointer",
                  display: "grid",
                  placeItems: "center",
                  padding: 0,
                }}
                aria-label="Dismiss notification"
              >
                <X size={14} />
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  const renderExecutionWorkspace = () => {
    const selectedDeal = selectedCrmDeal && isGeneralExecutionEligibleDeal(selectedCrmDeal)
      ? selectedCrmDeal
      : null;
    const executionItems = Array.isArray(crmDetailDraft?.executionSpecs)
      ? crmDetailDraft.executionSpecs
      : [];
    const selectedExecutionItem =
      executionItems.find((item) => item.id === selectedExecutionItemId) ||
      executionItems[0] ||
      null;
    const selectedItemAttachments = normalizeExecutionSpecAttachments(
      selectedExecutionItem?.attachments || []
    );
    const selectedItemUploadKey = selectedDeal && selectedExecutionItem
      ? `${selectedDeal.id}:${selectedExecutionItem.id}`
      : "";
    const selectedItemMatchingTemplate = selectedExecutionItem
      ? getExecutionTemplateForType(selectedExecutionItem.itemType)
      : null;
    const hasExecutionFilters =
      executionWorkspaceSearch.trim() ||
      executionWorkspaceSourceFilter !== "all" ||
      executionWorkspaceStageFilter !== "all" ||
      executionWorkspaceItemTypeFilter !== "all" ||
      executionWorkspaceAttachmentFilter !== "all" ||
      executionWorkspaceSortBy !== "updated";
    const finderResults = executionWorkspaceFilteredDeals.slice(0, 18);
    const executionWorkspaceItemTypeListId = "execution-workspace-item-type-options";
    const executionWorkspaceSelectedAttachmentCount = executionItems.reduce(
      (sum, item) =>
        sum +
        normalizeExecutionSpecAttachments(item.attachments || []).length,
      0
    );

    return (
      <div
        style={dashboardPanelStyle}
        onBlurCapture={() => persistExecutionDetailsDraftLocally("blur")}
      >
        <div
          style={{
            ...dashboardCardStyle,
            marginBottom: 18,
            background: isDark
              ? "linear-gradient(135deg, rgba(15,23,42,0.96), rgba(30,41,59,0.96))"
              : "linear-gradient(135deg, #eff6ff, #ffffff)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 18,
              flexDirection: isCompact ? "column" : "row",
            }}
          >
            <div style={{ maxWidth: 760 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "#2563eb",
                  marginBottom: 10,
                }}
              >
                Execution Workspace
              </div>
              <h2
                style={{
                  margin: 0,
                  fontSize: isCompact ? 24 : 30,
                  fontWeight: 900,
                  letterSpacing: "-0.04em",
                  color: isDark ? "#f8fafc" : "#0f172a",
                }}
              >
                Manage real job specs, files, and production-ready details outside the CRM drawer
              </h2>
              <div
                style={{
                  marginTop: 10,
                  fontSize: 13,
                  lineHeight: 1.6,
                  color: dashboardMuted,
                }}
              >
                Execution Specs now have a dedicated operating surface for General Quotations. This workspace keeps the
                live job details on the linked CRM lead while letting the original quotation seed the first version when
                needed.
              </div>
              <div style={{ marginTop: 10, fontSize: 12, color: dashboardMuted }}>
                {getLocalDraftStatusText(LOCAL_DRAFT_MODULES.EXECUTION_DETAILS)}
              </div>
            </div>

            <div
              style={{
                minWidth: isCompact ? "100%" : 320,
                display: "grid",
                gap: 10,
                alignContent: "start",
              }}
            >
              <button
                type="button"
                onClick={() => returnToCrmFromExecutionWorkspace()}
                style={{
                  ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"),
                  marginRight: 0,
                  justifySelf: "stretch",
                  width: "100%",
                }}
              >
                Back to CRM
              </button>
              {selectedDeal && (selectedDeal.hasQuote ? canViewQuotes : canCreateQuotes) ? (
                <button
                  type="button"
                  onClick={() =>
                    selectedDeal.hasQuote
                      ? openCrmQuoteForDeal(selectedDeal)
                      : startQuoteFromCrmLead(selectedDeal)
                  }
                  style={{
                    ...btn("#2563eb"),
                    marginRight: 0,
                    justifySelf: "stretch",
                    width: "100%",
                  }}
                >
                  {selectedDeal.hasQuote ? "Open Quote" : "Create Quote"}
                </button>
              ) : null}
              {selectedDeal ? (
                <button
                  type="button"
                  onClick={persistCrmDetailDraft}
                  style={{
                    ...btn("#16a34a"),
                    marginRight: 0,
                    justifySelf: "stretch",
                    width: "100%",
                  }}
                >
                  Save Execution Details
                </button>
              ) : null}
              {selectedDeal && canUseWorkshopPdf ? (
                <button
                  type="button"
                  onClick={() => downloadExecutionWorkshopPdf(selectedDeal)}
                  style={{
                    ...btn("#7c3aed"),
                    marginRight: 0,
                    justifySelf: "stretch",
                    width: "100%",
                    display: "grid",
                    gap: 3,
                    justifyItems: "start",
                    textAlign: "left",
                  }}
                >
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <Download size={15} />
                    Download Workshop PDF
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 600, opacity: 0.86 }}>
                    Production / installation copy
                  </span>
                </button>
              ) : null}
            </div>
          </div>
        </div>

        <div style={{ ...dashboardCardStyle, display: "grid", gap: 14, marginBottom: 18 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 14,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>Deal Finder</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                Search general-work deals by client, source, quote number, stage, item type, or attachment coverage
                before opening a full execution workspace.
              </div>
            </div>
            <div
              style={{
                padding: "10px 14px",
                borderRadius: 16,
                border: dashboardBorder,
                background: dashboardSoftBg,
                fontSize: 12,
                color: dashboardMuted,
                fontWeight: 700,
              }}
            >
              {executionWorkspaceFilteredDeals.length} matching deal
              {executionWorkspaceFilteredDeals.length === 1 ? "" : "s"}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact ? "1fr" : "minmax(220px, 1.2fr) repeat(5, minmax(120px, 1fr))",
              gap: 10,
            }}
          >
            <input
              value={executionWorkspaceSearch}
              onChange={(event) => setExecutionWorkspaceSearch(event.target.value)}
              style={input}
              placeholder="Search all execution deals by client, source, quote number, stage..."
            />
            <select
              value={executionWorkspaceSourceFilter}
              onChange={(event) => setExecutionWorkspaceSourceFilter(event.target.value)}
              style={selectInput}
            >
              <option value="all">All Sources</option>
              {executionWorkspaceSourceOptions.map((source) => (
                <option key={source} value={source}>
                  {source}
                </option>
              ))}
            </select>
            <select
              value={executionWorkspaceStageFilter}
              onChange={(event) => setExecutionWorkspaceStageFilter(event.target.value)}
              style={selectInput}
            >
              <option value="all">All Stages</option>
              {executionWorkspaceStageOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <select
              value={executionWorkspaceItemTypeFilter}
              onChange={(event) => setExecutionWorkspaceItemTypeFilter(event.target.value)}
              style={selectInput}
            >
              <option value="all">All Item Types</option>
              {executionTemplateItemTypeOptions.map((itemType) => (
                <option key={itemType} value={itemType}>
                  {itemType}
                </option>
              ))}
            </select>
            <select
              value={executionWorkspaceAttachmentFilter}
              onChange={(event) => setExecutionWorkspaceAttachmentFilter(event.target.value)}
              style={selectInput}
            >
              <option value="all">All Attachments</option>
              <option value="with">With Attachments</option>
              <option value="without">Without Attachments</option>
            </select>
            <select
              value={executionWorkspaceSortBy}
              onChange={(event) => setExecutionWorkspaceSortBy(event.target.value)}
              style={selectInput}
            >
              <option value="updated">Newest Updated</option>
              <option value="oldest">Oldest Updated</option>
              <option value="client">Client Name</option>
            </select>
          </div>
          {executionWorkspaceSearch.trim() ? (
            <div style={{ fontSize: 12, color: dashboardMuted }}>
              Search checks all CRM execution deals, including older records.
              {globalSearchStatus.executionWorkspaceSearch?.loading ? " Searching..." : ""}
              {globalSearchStatus.executionWorkspaceSearch?.error ? (
                <span style={{ color: "#dc2626", fontWeight: 800 }}>
                  {" "}{globalSearchStatus.executionWorkspaceSearch.error}
                </span>
              ) : null}
            </div>
          ) : null}

          {hasExecutionFilters ? (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => {
                  setExecutionWorkspaceSearch("");
                  setExecutionWorkspaceSourceFilter("all");
                  setExecutionWorkspaceStageFilter("all");
                  setExecutionWorkspaceItemTypeFilter("all");
                  setExecutionWorkspaceAttachmentFilter("all");
                  setExecutionWorkspaceSortBy("updated");
                }}
                style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 12px" }}
              >
                Clear Filters
              </button>
            </div>
          ) : null}

          {finderResults.length === 0 ? (
            <div
              style={{
                padding: 18,
                borderRadius: 18,
                border: dashboardBorder,
                background: dashboardSoftBg,
                color: dashboardMuted,
                fontSize: 13,
              }}
            >
              No deals match the current execution filters.
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isCompact ? "1fr" : "repeat(3, minmax(0, 1fr))",
                gap: 12,
              }}
            >
              {finderResults.map((deal) => {
                const executionItemsForDeal = normalizeExecutionSpecs(deal.executionSpecs || []);
                const dealAttachmentCount = executionItemsForDeal.reduce(
                  (sum, item) => sum + normalizeExecutionSpecAttachments(item.attachments || []).length,
                  0
                );
                const opening = executionWorkspaceOpeningDealId === deal.id;
                const active = selectedDeal?.id === deal.id;

                return (
                  <button
                    key={`execution-deal-${deal.id}`}
                    type="button"
                    onClick={() => openExecutionWorkspaceForDeal(deal)}
                    disabled={opening}
                    style={{
                      ...dashboardCardStyle,
                      textAlign: "left",
                      width: "100%",
                      cursor: opening ? "progress" : "pointer",
                      padding: 16,
                      display: "grid",
                      gap: 10,
                      border:
                        active
                          ? "1px solid rgba(37,99,235,0.4)"
                          : dashboardBorder,
                      background:
                        active
                          ? isDark
                            ? "rgba(37,99,235,0.12)"
                            : "#eff6ff"
                          : dashboardCardBg,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                      <div
                        style={{
                          padding: "4px 8px",
                          borderRadius: 999,
                          background: isDark ? "rgba(255,255,255,0.06)" : "#f8fafc",
                          color: dashboardMuted,
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        {getQuoteTypeLabel(deal.pipelineType)}
                      </div>
                      <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                        {opening ? "Opening..." : active ? "Selected" : "Open"}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 800 }}>
                        {deal.crmTitle || deal.customerLabel}
                      </div>
                      <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                        {deal.hasQuote
                          ? `#${deal.primaryLinkedQuote?.quoteNo || deal.quoteNo || "-"}`
                          : "Lead only"}
                        {" | "}
                        {deal.customerLabel || "Unnamed Client"}
                      </div>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, fontSize: 11, color: dashboardMuted }}>
                      <span>{deal.crmStageLabel}</span>
                      <span>{executionItemsForDeal.length} item{executionItemsForDeal.length === 1 ? "" : "s"}</span>
                      <span>{dealAttachmentCount} attachment{dealAttachmentCount === 1 ? "" : "s"}</span>
                      <span>{deal.source || deal.crmContact?.source || "No source"}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {!selectedDeal || !crmDetailDraft ? (
          <div
            style={{
              ...dashboardCardStyle,
              display: "grid",
              gap: 10,
              textAlign: "center",
              padding: isCompact ? 24 : 36,
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 900 }}>Select a general quotation deal to view execution details.</div>
            <div style={{ fontSize: 13, color: dashboardMuted, lineHeight: 1.7, maxWidth: 720, margin: "0 auto" }}>
              Once a General Quotation lead is selected, this page opens a full execution workspace for structured job
              specs, dynamic fields, and attachments without forcing your team to work inside the CRM drawer.
            </div>
          </div>
        ) : selectedDeal.crmRecordType === "quote" ? (
          <div
            style={{
              ...dashboardCardStyle,
              display: "grid",
              gap: 12,
              padding: isCompact ? 22 : 28,
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 900 }}>
              Promote this quote-backed record into the canonical execution workspace
            </div>
            <div style={{ fontSize: 13, color: dashboardMuted, lineHeight: 1.7, maxWidth: 760 }}>
              Execution Specs stay canonical on CRM lead documents. This quotation still uses the legacy quote-backed
              CRM record, so opening the full workspace will create or link the lead-backed execution owner first and
              then continue from there.
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <button
                type="button"
                onClick={() => openExecutionWorkspaceForDeal(selectedDeal)}
                style={{ ...btn("#2563eb"), marginRight: 0 }}
              >
                {executionWorkspaceOpeningDealId === selectedDeal.id
                  ? "Preparing Workspace..."
                  : "Open Execution Workspace"}
              </button>
              {selectedDeal.hasQuote && canViewQuotes ? (
                <button
                  type="button"
                  onClick={() => openCrmQuoteForDeal(selectedDeal)}
                  style={{ ...btn("#0f172a"), marginRight: 0 }}
                >
                  Open Quote
                </button>
              ) : null}
            </div>
          </div>
        ) : (
          <>
            <div style={{ ...dashboardCardStyle, display: "grid", gap: 16, marginBottom: 18 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 16,
                  flexDirection: isCompact ? "column" : "row",
                }}
              >
                <div style={{ display: "grid", gap: 8 }}>
                  <div style={{ fontSize: 24, fontWeight: 900 }}>
                    {selectedDeal.crmTitle || selectedDeal.customerLabel}
                  </div>
                  <div style={{ fontSize: 13, color: dashboardMuted, lineHeight: 1.6 }}>
                    {selectedDeal.customerLabel || "Unnamed Client"}
                    {selectedDeal.hasQuote
                      ? ` | Quote #${selectedDeal.primaryLinkedQuote?.quoteNo || selectedDeal.quoteNo || "-"}`
                      : " | No quote linked yet"}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <button
                    type="button"
                    onClick={() => returnToCrmFromExecutionWorkspace(selectedDeal.id)}
                    style={{
                      ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"),
                      marginRight: 0,
                    }}
                  >
                    Open CRM Deal
                  </button>
                  {selectedDeal.hasQuote ? (
                    <button
                      type="button"
                      onClick={() => openCrmQuoteForDeal(selectedDeal)}
                      style={{ ...btn("#2563eb"), marginRight: 0 }}
                    >
                      Open Quote
                    </button>
                  ) : canCreateQuotes ? (
                    <button
                      type="button"
                      onClick={() => startQuoteFromCrmLead(selectedDeal)}
                      style={{ ...btn("#2563eb"), marginRight: 0 }}
                    >
                      Create Quote
                    </button>
                  ) : null}
                  {selectedDeal.hasQuote ? (
                    <button
                      type="button"
                      onClick={() => convertDealQuoteToExecutionSpecs(selectedDeal)}
                      style={{ ...btn("#0f172a"), marginRight: 0 }}
                    >
                      Convert Quote to Execution Specs
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={persistCrmDetailDraft}
                    style={{ ...btn("#16a34a"), marginRight: 0 }}
                  >
                    Save Execution Details
                  </button>
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isCompact ? "1fr" : "repeat(5, minmax(0, 1fr))",
                  gap: 10,
                }}
              >
                {[
                  ["Stage", selectedDeal.crmStageLabel || "-"],
                  ["Source", selectedDeal.source || selectedDeal.crmContact?.source || "-"],
                  [
                    "Assigned",
                    getTeamMemberLabel(
                      crmDetailDraft.assignedToUserId,
                      crmDetailDraft.assignedToNameSnapshot
                    ),
                  ],
                  [
                    "Execution Items",
                    `${executionItems.length} item${executionItems.length === 1 ? "" : "s"}`,
                  ],
                  [
                    "Execution Attachments",
                    `${executionWorkspaceSelectedAttachmentCount} file${
                      executionWorkspaceSelectedAttachmentCount === 1 ? "" : "s"
                    }`,
                  ],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    style={{
                      padding: 14,
                      borderRadius: 16,
                      background: dashboardSoftBg,
                      border: dashboardBorder,
                      display: "grid",
                      gap: 6,
                    }}
                  >
                    <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>{label}</div>
                    <div style={{ fontSize: 14, fontWeight: 800 }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isCompact ? "1fr" : "minmax(300px, 0.9fr) minmax(0, 1.5fr)",
                gap: 16,
                alignItems: "start",
              }}
            >
              <div style={{ ...dashboardCardStyle, display: "grid", gap: 14 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    flexDirection: isCompact ? "column" : "row",
                    alignItems: isCompact ? "stretch" : "center",
                  }}
                >
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 800 }}>Execution Items</div>
                    <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                      Select an item to manage dynamic fields and execution attachments.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={addCrmExecutionSpec}
                    style={{ ...btn("#0f172a"), marginRight: 0 }}
                  >
                    + Add Item
                  </button>
                </div>

                {executionItems.length === 0 ? (
                  <div
                    style={{
                      padding: 18,
                      borderRadius: 18,
                      background: dashboardSoftBg,
                      border: dashboardBorder,
                      display: "grid",
                      gap: 8,
                    }}
                  >
                    <div style={{ fontSize: 14, fontWeight: 800 }}>No execution items yet</div>
                    <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                      Add an item manually or seed the workspace from the linked quotation.
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "grid", gap: 10 }}>
                    {executionItems.map((item, index) => {
                      const active = selectedExecutionItem?.id === item.id;
                      const attachmentCount = normalizeExecutionSpecAttachments(item.attachments || []).length;
                      const fieldCount = Array.isArray(item.attributeRows) ? item.attributeRows.length : 0;

                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => setSelectedExecutionItemId(item.id)}
                          style={{
                            padding: 14,
                            borderRadius: 18,
                            border: active
                              ? "1px solid rgba(37,99,235,0.38)"
                              : dashboardBorder,
                            background: active
                              ? isDark
                                ? "rgba(37,99,235,0.12)"
                                : "#eff6ff"
                              : dashboardSoftBg,
                            display: "grid",
                            gap: 8,
                            textAlign: "left",
                            cursor: "pointer",
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                            <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                              Item {index + 1}
                            </div>
                            <div
                              style={{
                                padding: "4px 8px",
                                borderRadius: 999,
                                background: isDark ? "rgba(255,255,255,0.06)" : "#ffffff",
                                color: dashboardMuted,
                                fontSize: 11,
                                fontWeight: 700,
                              }}
                            >
                              {item.itemType || "Custom"}
                            </div>
                          </div>
                          <div style={{ fontSize: 15, fontWeight: 800 }}>
                            {item.itemName || "Untitled execution item"}
                          </div>
                          <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                            {fieldCount} field{fieldCount === 1 ? "" : "s"} | {attachmentCount} attachment
                            {attachmentCount === 1 ? "" : "s"}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div style={{ ...dashboardCardStyle, display: "grid", gap: 16 }}>
                {!selectedExecutionItem ? (
                  <div
                    style={{
                      padding: 24,
                      borderRadius: 18,
                      border: dashboardBorder,
                      background: dashboardSoftBg,
                      color: dashboardMuted,
                      fontSize: 13,
                    }}
                  >
                    Select an execution item to edit its fields and attachments.
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 14,
                        flexDirection: isCompact ? "column" : "row",
                        alignItems: isCompact ? "stretch" : "flex-start",
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#2563eb", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                          Selected Item
                        </div>
                        <div style={{ fontSize: 22, fontWeight: 900, marginTop: 6 }}>
                          {selectedExecutionItem.itemName || "Untitled execution item"}
                        </div>
                        <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 6, lineHeight: 1.6 }}>
                          {selectedExecutionItem.itemType || "Custom type"} |{" "}
                          {(selectedExecutionItem.attributeRows || []).length} field
                          {(selectedExecutionItem.attributeRows || []).length === 1 ? "" : "s"} |{" "}
                          {selectedItemAttachments.length} attachment
                          {selectedItemAttachments.length === 1 ? "" : "s"}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => deleteCrmExecutionSpec(selectedExecutionItem.id)}
                        style={{ ...btn("#ef4444"), marginRight: 0 }}
                      >
                        Delete Item
                      </button>
                    </div>

                    <datalist id={executionWorkspaceItemTypeListId}>
                        {executionTemplateItemTypeOptions.map((option) => (
                          <option key={option} value={option} />
                        ))}
                    </datalist>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isCompact ? "1fr" : "minmax(180px, 0.9fr) minmax(220px, 1.1fr)",
                        gap: 12,
                      }}
                    >
                      <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
                        <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                          Item Type
                        </div>
                        <input
                          value={selectedExecutionItem.itemType || ""}
                          onChange={(event) =>
                            syncCrmExecutionSpecType(selectedExecutionItem.id, event.target.value)
                          }
                          style={{ ...input, width: "100%", minWidth: 0 }}
                          placeholder="Sofa, Wardrobe, Interior..."
                          list={executionWorkspaceItemTypeListId}
                        />
                      </div>
                      <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
                        <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                          Item Name
                        </div>
                        <input
                          value={selectedExecutionItem.itemName || ""}
                          onChange={(event) =>
                            updateCrmExecutionSpec(selectedExecutionItem.id, {
                              itemName: event.target.value,
                            })
                          }
                          style={{ ...input, width: "100%", minWidth: 0 }}
                          placeholder="L Shape Sofa, Bedroom Wardrobe..."
                        />
                      </div>
                    </div>

                    <div
                      style={{
                        padding: 16,
                        borderRadius: 18,
                        background: dashboardSoftBg,
                        border: dashboardBorder,
                        display: "grid",
                        gap: 12,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 12,
                          flexDirection: isCompact ? "column" : "row",
                          alignItems: isCompact ? "stretch" : "center",
                        }}
                      >
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 800 }}>Template Actions</div>
                          <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                            Matching item types can auto-populate standard fields, while still keeping every deal fully customizable.
                          </div>
                          {selectedItemMatchingTemplate ? (
                            <div style={{ fontSize: 12, color: "#2563eb", marginTop: 8, fontWeight: 700 }}>
                              Suggested template: {selectedItemMatchingTemplate.name} ({selectedItemMatchingTemplate.itemType})
                            </div>
                          ) : null}
                        </div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                          <button
                            type="button"
                            onClick={() => setRoutedTab("executionTemplates")}
                            style={{
                              ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"),
                              marginRight: 0,
                              padding: "8px 12px",
                            }}
                          >
                            Manage Templates
                          </button>
                          {selectedItemMatchingTemplate ? (
                            <button
                              type="button"
                              onClick={() =>
                                applyExecutionTemplateToSpec(
                                  selectedExecutionItem.id,
                                  selectedItemMatchingTemplate.id
                                )
                              }
                              style={{ ...btn("#2563eb"), marginRight: 0, padding: "8px 12px" }}
                            >
                              Apply Suggested Template
                            </button>
                          ) : null}
                        </div>
                      </div>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 1fr) auto",
                          gap: 10,
                          alignItems: "center",
                        }}
                      >
                        <select
                          value={selectedExecutionTemplateId ?? ""}
                          onChange={(event) => {
                            const nextTemplateId = event.target.value;
                            setSelectedExecutionTemplateId(nextTemplateId);
                            if (!nextTemplateId) return;
                            applyExecutionTemplateToSpec(
                              selectedExecutionItem.id,
                              nextTemplateId
                            );
                          }}
                          style={{ ...selectInput, width: "100%", minWidth: 0 }}
                        >
                          <option value="">Select a template...</option>
                          {executionWorkspaceTemplateOptions.map((template) => (
                            <option key={template.id} value={template.id}>
                              {template.name} | {template.itemType}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => {
                            if (!selectedExecutionTemplateId) return;
                            applyExecutionTemplateToSpec(
                              selectedExecutionItem.id,
                              selectedExecutionTemplateId
                            );
                          }}
                          disabled={!selectedExecutionTemplateId}
                          style={{
                            ...btn(selectedExecutionTemplateId ? "#2563eb" : "#94a3b8"),
                            marginRight: 0,
                            padding: "8px 12px",
                            opacity: selectedExecutionTemplateId ? 1 : 0.7,
                          }}
                        >
                          Apply Again
                        </button>
                      </div>
                      <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                        Selecting a template fills in matching fields right away. Reapplying stays safe and avoids repeating the same field twice.
                      </div>
                    </div>

                    <div
                      style={{
                        padding: 16,
                        borderRadius: 18,
                        background: dashboardSoftBg,
                        border: dashboardBorder,
                        display: "grid",
                        gap: 12,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 12,
                          flexDirection: isCompact ? "column" : "row",
                          alignItems: isCompact ? "stretch" : "center",
                        }}
                      >
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 800 }}>Dynamic Fields</div>
                          <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                            Add or edit any structured key/value fields for the actual execution plan.
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => addCrmExecutionSpecField(selectedExecutionItem.id)}
                          style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 12px" }}
                        >
                          + Add Field
                        </button>
                      </div>

                      {(selectedExecutionItem.attributeRows || []).length === 0 ? (
                        <div style={{ fontSize: 13, color: dashboardMuted }}>
                          No fields yet for this item. Add a field or apply a template.
                        </div>
                      ) : (
                        <div style={{ display: "grid", gap: 10 }}>
                          {(selectedExecutionItem.attributeRows || []).map((field) => (
                            <div
                              key={field.id}
                              style={{
                                display: "grid",
                                gridTemplateColumns: isCompact
                                  ? "1fr"
                                  : "minmax(180px, 0.9fr) minmax(220px, 1.1fr) auto",
                                gap: 10,
                                alignItems: "center",
                              }}
                            >
                              <input
                                value={field.key ?? ""}
                                onChange={(event) =>
                                  updateCrmExecutionSpecField(selectedExecutionItem.id, field.id, {
                                    key: event.target.value,
                                  })
                                }
                                style={{ ...input, minWidth: 0 }}
                                placeholder="Field name"
                              />
                              <input
                                value={field.value ?? ""}
                                onChange={(event) =>
                                  updateCrmExecutionSpecField(selectedExecutionItem.id, field.id, {
                                    value: event.target.value,
                                  })
                                }
                                style={{ ...input, minWidth: 0 }}
                                placeholder="Field value"
                              />
                              <button
                                type="button"
                                onClick={() => removeCrmExecutionSpecField(selectedExecutionItem.id, field.id)}
                                style={{ ...btn("#ef4444"), marginRight: 0, padding: "8px 10px" }}
                              >
                                Delete
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div
                      style={{
                        padding: 16,
                        borderRadius: 18,
                        background: dashboardSoftBg,
                        border: dashboardBorder,
                        display: "grid",
                        gap: 12,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 12,
                          flexDirection: isCompact ? "column" : "row",
                          alignItems: isCompact ? "stretch" : "center",
                        }}
                      >
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 800 }}>Attachments</div>
                          <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                            Upload production references, site photos, measurements, PDFs, and vendor documents for this execution item.
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                          <input
                            id={`execution-workspace-attachment-${selectedExecutionItem.id}`}
                            type="file"
                            multiple
                            style={{ display: "none" }}
                            onChange={(event) => {
                              const files = Array.from(event.target.files || []);
                              uploadCrmExecutionSpecAttachments(selectedExecutionItem.id, files);
                              event.target.value = "";
                            }}
                          />
                          <button
                            type="button"
                            onClick={() =>
                              document
                                .getElementById(`execution-workspace-attachment-${selectedExecutionItem.id}`)
                                ?.click()
                            }
                            style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 12px" }}
                          >
                            <UploadCloud size={14} style={{ marginRight: 6 }} />
                            Upload File
                          </button>
                        </div>
                      </div>

                      {crmExecutionAttachmentUploadKey === selectedItemUploadKey ? (
                        <div style={{ fontSize: 12, color: dashboardMuted }}>
                          Uploading execution attachment...
                        </div>
                      ) : null}

                      {selectedItemAttachments.length === 0 ? (
                        <div style={{ fontSize: 13, color: dashboardMuted }}>
                          No execution attachments added yet for this item.
                        </div>
                      ) : (
                        <div style={{ display: "grid", gap: 10 }}>
                          {selectedItemAttachments.map((attachment) => {
                            const visual = getAttachmentVisual(attachment);
                            const Icon = visual.icon;

                            return (
                              <div
                                key={attachment.id}
                                style={{
                                  padding: 12,
                                  borderRadius: 16,
                                  background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                                  border: dashboardBorder,
                                  display: "grid",
                                  gap: 10,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: 12,
                                    alignItems: "flex-start",
                                    flexDirection: isCompact ? "column" : "row",
                                  }}
                                >
                                  <div style={{ display: "flex", gap: 10, minWidth: 0 }}>
                                    <div
                                      style={{
                                        width: 40,
                                        height: 40,
                                        minWidth: 40,
                                        borderRadius: 14,
                                        display: "grid",
                                        placeItems: "center",
                                        background: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc",
                                        color: visual.accent,
                                      }}
                                    >
                                      <Icon size={18} />
                                    </div>
                                    <div style={{ minWidth: 0 }}>
                                      <div style={{ fontSize: 14, fontWeight: 800, wordBreak: "break-word" }}>
                                        {attachment.name}
                                      </div>
                                      <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                                        {visual.label}
                                        {formatAttachmentSize(attachment.sizeBytes)
                                          ? ` | ${formatAttachmentSize(attachment.sizeBytes)}`
                                          : ""}
                                        {attachment.uploadedAt
                                          ? ` | Uploaded ${new Date(attachment.uploadedAt).toLocaleDateString("en-IN")}`
                                          : ""}
                                        {attachment.uploadedByName
                                          ? ` | By ${attachment.uploadedByName}`
                                          : ""}
                                      </div>
                                    </div>
                                  </div>

                                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (attachment.url) {
                                          window.open(attachment.url, "_blank", "noopener,noreferrer");
                                        }
                                      }}
                                      style={{ ...btn("#2563eb"), marginRight: 0, padding: "8px 10px" }}
                                    >
                                      <ExternalLink size={14} style={{ marginRight: 6 }} />
                                      View
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (!attachment.url) return;
                                        const anchor = document.createElement("a");
                                        anchor.href = attachment.url;
                                        anchor.download = attachment.name || "execution-attachment";
                                        anchor.target = "_blank";
                                        anchor.rel = "noopener noreferrer";
                                        anchor.click();
                                      }}
                                      style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 10px" }}
                                    >
                                      Download
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeCrmExecutionSpecAttachment(
                                          selectedExecutionItem.id,
                                          attachment.id
                                        )
                                      }
                                      style={{ ...btn("#ef4444"), marginRight: 0, padding: "8px 10px" }}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderExecutionTemplatesPage = () => {
    const executionTemplatesReady =
      executionTemplatesLoadState.loaded &&
      executionTemplatesLoadState.workspaceUid === workspaceUid;
    const executionTemplatesLoading =
      executionTemplatesLoadState.loading &&
      executionTemplatesLoadState.workspaceUid === workspaceUid;
    const selectedTemplate =
      executionTemplateCatalog.find(
        (template) => template.id === selectedExecutionTemplateManagerId
      ) || null;
    if (isDevRuntime) {
      executionTemplatesRenderCountRef.current += 1;
      console.count("ExecutionTemplates render");
      if (
        executionTemplatesRenderCountRef.current <= 10 ||
        executionTemplatesRenderCountRef.current % 25 === 0
      ) {
        console.log("EXECUTION TEMPLATE RENDER AUDIT", {
          renderCount: executionTemplatesRenderCountRef.current,
          workspaceUid,
          loadedWorkspace: executionTemplatesLoadedWorkspaceRef.current,
          loadingWorkspace: executionTemplatesLoadingWorkspaceRef.current,
          ready: executionTemplatesReady,
          loading: executionTemplatesLoading,
          localTemplateCount: crmExecutionTemplates.length,
          catalogCount: executionTemplateCatalog.length,
          filteredCount: filteredExecutionTemplates.length,
          selectedExecutionTemplateManagerId,
          hasSelectedTemplate: Boolean(selectedTemplate),
        });
      }
    }

    return (
      <div
        style={dashboardPanelStyle}
        onBlurCapture={() => persistExecutionTemplatesDraftLocally("blur")}
      >
        <div
          style={{
            ...dashboardCardStyle,
            marginBottom: 18,
            background: isDark
              ? "linear-gradient(135deg, rgba(15,23,42,0.96), rgba(30,41,59,0.96))"
              : "linear-gradient(135deg, #eff6ff, #ffffff)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 18,
              flexDirection: isCompact ? "column" : "row",
            }}
          >
            <div style={{ maxWidth: 760 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "#2563eb",
                  marginBottom: 10,
                }}
              >
                Execution Templates
              </div>
              <h2
                style={{
                  margin: 0,
                  fontSize: isCompact ? 24 : 30,
                  fontWeight: 900,
                  letterSpacing: "-0.04em",
                  color: isDark ? "#f8fafc" : "#0f172a",
                }}
              >
                Build reusable execution field packs for sofas, wardrobes, kitchens, custom interiors, and other general work
              </h2>
              <div
                style={{
                  marginTop: 10,
                  fontSize: 13,
                  lineHeight: 1.6,
                  color: dashboardMuted,
                }}
              >
                Templates stay shared across your workspace and are ready to use in Execution Details whenever a matching item type is selected.
              </div>
              <div style={{ marginTop: 10, fontSize: 12, color: dashboardMuted }}>
                {getLocalDraftStatusText(LOCAL_DRAFT_MODULES.EXECUTION_TEMPLATE)}
              </div>
            </div>

            <div
              style={{
                minWidth: isCompact ? "100%" : 320,
                display: "grid",
                gap: 10,
                alignContent: "start",
              }}
            >
              <button
                type="button"
                onClick={addExecutionTemplate}
                disabled={
                  !executionTemplatesReady ||
                  executionTemplatesLoading ||
                  isSavingExecutionTemplates
                }
                style={{
                  ...btn("#2563eb"),
                  marginRight: 0,
                  justifySelf: "stretch",
                  width: "100%",
                  opacity:
                    !executionTemplatesReady ||
                    executionTemplatesLoading ||
                    isSavingExecutionTemplates
                      ? 0.62
                      : 1,
                  cursor:
                    !executionTemplatesReady ||
                    executionTemplatesLoading ||
                    isSavingExecutionTemplates
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                {executionTemplatesLoading ? "Loading Templates..." : "+ Create Template"}
              </button>
              <button
                type="button"
                onClick={saveCrmExecutionTemplates}
                disabled={
                  !executionTemplatesReady ||
                  executionTemplatesLoading ||
                  isSavingExecutionTemplates
                }
                style={{
                  ...btn("#16a34a"),
                  marginRight: 0,
                  justifySelf: "stretch",
                  width: "100%",
                  opacity:
                    !executionTemplatesReady ||
                    executionTemplatesLoading ||
                    isSavingExecutionTemplates
                      ? 0.62
                      : 1,
                  cursor:
                    !executionTemplatesReady ||
                    executionTemplatesLoading ||
                    isSavingExecutionTemplates
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                {isSavingExecutionTemplates ? "Saving Templates..." : "Save Template Changes"}
              </button>
              <button
                type="button"
                onClick={() => setRoutedTab("executionSpecs")}
                style={{
                  ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"),
                  marginRight: 0,
                  justifySelf: "stretch",
                  width: "100%",
                }}
              >
                Open Execution Details
              </button>
            </div>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact ? "1fr" : "minmax(320px, 0.92fr) minmax(0, 1.48fr)",
            gap: 16,
            alignItems: "start",
          }}
        >
          <div style={{ ...dashboardCardStyle, display: "grid", gap: 14 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                flexDirection: isCompact ? "column" : "row",
                alignItems: isCompact ? "stretch" : "center",
              }}
            >
              <div>
                <div style={{ fontSize: 18, fontWeight: 800 }}>Template Library</div>
                <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                  Search, sort, and choose a reusable template to edit.
                </div>
              </div>
              <div
                style={{
                  padding: "8px 12px",
                  borderRadius: 999,
                  background: dashboardSoftBg,
                  border: dashboardBorder,
                  fontSize: 12,
                  fontWeight: 700,
                  color: dashboardMuted,
                }}
              >
                {executionTemplateCatalog.length} template{executionTemplateCatalog.length === 1 ? "" : "s"}
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 1fr) minmax(160px, 0.55fr)",
                gap: 10,
              }}
            >
              <input
                value={executionTemplateSearch}
                onChange={(event) => setExecutionTemplateSearch(event.target.value)}
                style={input}
                placeholder="Search name, item type, field labels, defaults..."
              />
              <select
                value={executionTemplateSortBy}
                onChange={(event) => setExecutionTemplateSortBy(event.target.value)}
                style={selectInput}
              >
                <option value="name">Sort by Name</option>
                <option value="updated">Newest Updated</option>
                <option value="oldest">Oldest Created</option>
              </select>
            </div>

            {filteredExecutionTemplates.length === 0 ? (
              <div
                style={{
                  padding: 18,
                  borderRadius: 18,
                  background: dashboardSoftBg,
                  border: dashboardBorder,
                  color: dashboardMuted,
                  fontSize: 13,
                  lineHeight: 1.7,
                }}
              >
                {executionTemplateCatalog.length === 0
                  ? executionTemplatesLoading
                    ? "Loading execution templates..."
                    : executionTemplatesLoadState.error
                    ? "Could not load execution templates. Please try again."
                    : "No execution templates yet. Create one to start auto-populating execution item fields."
                  : "No templates match the current search."}
              </div>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {filteredExecutionTemplates.map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() => setSelectedExecutionTemplateManagerId(template.id)}
                    style={{
                      padding: 14,
                      borderRadius: 18,
                      border:
                        selectedExecutionTemplateManagerId === template.id
                          ? "1px solid rgba(37,99,235,0.38)"
                          : dashboardBorder,
                      background:
                        selectedExecutionTemplateManagerId === template.id
                          ? isDark
                            ? "rgba(37,99,235,0.12)"
                            : "#eff6ff"
                          : dashboardSoftBg,
                      display: "grid",
                      gap: 8,
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                      <div style={{ fontSize: 14, fontWeight: 800 }}>
                        {template.name || "Untitled Template"}
                      </div>
                      <div
                        style={{
                          padding: "4px 8px",
                          borderRadius: 999,
                          background: isDark ? "rgba(255,255,255,0.05)" : "#ffffff",
                          color: dashboardMuted,
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        {template.itemType || "no type"}
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                      {(template.fields || []).length} field{(template.fields || []).length === 1 ? "" : "s"}
                      {template.updatedAt
                        ? ` | Updated ${new Date(template.updatedAt).toLocaleDateString("en-IN")}`
                        : ""}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div style={{ ...dashboardCardStyle, display: "grid", gap: 16 }}>
            {!selectedTemplate ? (
              <div
                style={{
                  padding: 24,
                  borderRadius: 18,
                  background: dashboardSoftBg,
                  border: dashboardBorder,
                  color: dashboardMuted,
                  fontSize: 13,
                }}
              >
                Select a template to edit its name, normalized item type, and reusable fields.
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 14,
                    flexDirection: isCompact ? "column" : "row",
                    alignItems: isCompact ? "stretch" : "center",
                  }}
                >
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#2563eb", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                      Template Editor
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 900, marginTop: 6 }}>
                      {selectedTemplate.name || "Untitled Template"}
                    </div>
                    <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 6, lineHeight: 1.6 }}>
                      Item type <strong style={{ color: textColor }}>{selectedTemplate.itemType || "-"}</strong> will be used for auto-matching inside Execution Details.
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => deleteExecutionTemplate(selectedTemplate.id)}
                    style={{ ...btn("#ef4444"), marginRight: 0 }}
                  >
                    Delete Template
                  </button>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isCompact ? "1fr" : "minmax(220px, 1fr) minmax(220px, 1fr)",
                    gap: 12,
                  }}
                >
                  <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
                    <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                      Template Name
                    </div>
                    <input
                      value={selectedTemplate.name ?? ""}
                      onChange={(event) =>
                        updateExecutionTemplate(selectedTemplate.id, {
                          name: event.target.value,
                        })
                      }
                      style={{ ...input, width: "100%", minWidth: 0 }}
                      placeholder="Sofa, Wardrobe, Interior..."
                    />
                  </div>
                  <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
                    <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                      Item Type
                    </div>
                    <input
                      value={selectedTemplate.itemType ?? ""}
                      onChange={(event) =>
                        updateExecutionTemplate(selectedTemplate.id, {
                          itemType: event.target.value,
                        })
                      }
                      style={{ ...input, width: "100%", minWidth: 0 }}
                      placeholder="sofa, wardrobe, interior..."
                    />
                  </div>
                </div>

                <div
                  style={{
                    padding: 16,
                    borderRadius: 18,
                    background: dashboardSoftBg,
                    border: dashboardBorder,
                    display: "grid",
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      flexDirection: isCompact ? "column" : "row",
                      alignItems: isCompact ? "stretch" : "center",
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 800 }}>Template Fields</div>
                      <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                        Every field stays generic and reusable. Default values are optional and will auto-fill when the template is applied.
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => addExecutionTemplateField(selectedTemplate.id)}
                      style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 12px" }}
                    >
                      + Add Field
                    </button>
                  </div>

                  {(selectedTemplate.fields || []).length === 0 ? (
                    <div style={{ fontSize: 13, color: dashboardMuted }}>
                      No fields yet for this template.
                    </div>
                  ) : (
                    <div style={{ display: "grid", gap: 10 }}>
                      {(selectedTemplate.fields || []).map((field) => (
                        <div
                          key={field.id}
                          style={{
                            display: "grid",
                            gridTemplateColumns: isCompact
                              ? "1fr"
                              : "minmax(220px, 0.95fr) minmax(220px, 1.05fr) auto",
                            gap: 10,
                            alignItems: "center",
                          }}
                        >
                          <input
                            value={field.key ?? ""}
                            onChange={(event) =>
                              updateExecutionTemplateField(selectedTemplate.id, field.id, {
                                key: event.target.value,
                              })
                            }
                            style={{ ...input, width: "100%", minWidth: 0 }}
                            placeholder="Field label"
                          />
                          <input
                            value={field.defaultValue ?? ""}
                            onChange={(event) =>
                              updateExecutionTemplateField(selectedTemplate.id, field.id, {
                                defaultValue: event.target.value,
                              })
                            }
                            style={{ ...input, width: "100%", minWidth: 0 }}
                            placeholder="Optional default value"
                          />
                          <button
                            type="button"
                            onClick={() => removeExecutionTemplateField(selectedTemplate.id, field.id)}
                            style={{ ...btn("#ef4444"), marginRight: 0, padding: "8px 10px" }}
                          >
                            Delete
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderMaintenancePage = () => {
    if (!IS_DEV_UI_ENABLED) {
      return null;
    }
    if (!isSuperAdmin) {
      return (
        <div style={{ ...dashboardCardStyle, padding: 24 }}>
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 900 }}>
            Page unavailable
          </h2>
          <p style={{ margin: "10px 0 0", color: dashboardMuted }}>
            This area is reserved for platform administrators.
          </p>
        </div>
      );
    }

    const maintenanceActions = [
      {
        key: "repairQuoteSearch",
        title: "Repair Saved Quote Search",
        description:
          "Use this when saved quote search is missing older quotations for this workspace.",
        buttonLabel: quoteSearchRepairState.running
          ? `Repairing ${quoteSearchRepairState.processed}`
          : "Repair Saved Quote Search",
        color: "#475569",
        disabled: quoteSearchRepairState.running,
        confirmTitle: "Repair Saved Quote Search?",
        confirmDescription:
          "This will repair quote search/index metadata for this workspace. It may take some time. Continue?",
        confirmLabel: "Repair Search",
        onConfirm: repairQuotationSearchIndex,
      },
      {
        key: "rebuildAnalytics",
        title: "Rebuild Dashboard & Calendar Data",
        description:
          "Use this when dashboard totals, top clients, or installation calendar dates are incorrect.",
        buttonLabel: analyticsRebuildState.running
          ? `Rebuilding ${analyticsRebuildState.scanned}`
          : "Rebuild Dashboard & Calendar Data",
        color: "#0f766e",
        disabled: analyticsRebuildState.running,
        confirmTitle: "Rebuild Dashboard & Calendar Data?",
        confirmDescription:
          "This will rebuild dashboard and calendar summary indexes for this workspace. Continue?",
        confirmLabel: "Rebuild Data",
        onConfirm: rebuildAnalyticsAndCalendarIndexes,
      },
      {
        key: "auditWorkspaceData",
        title: "Audit Workspace Data",
        description:
          "Run a diagnostic report when search or dashboard data looks inconsistent.",
        buttonLabel: adminDataAuditState.running ? "Auditing Data..." : "Audit Workspace Data",
        color: "#7c3aed",
        disabled: adminDataAuditState.running,
        confirmTitle: "Audit Workspace Data?",
        confirmDescription: "This will run a diagnostic audit. Continue?",
        confirmLabel: "Run Audit",
        onConfirm: runTemporaryFullDataAudit,
      },
    ];

    return (
      <div
        style={{
          ...dashboardCardStyle,
          padding: isCompact ? 18 : 24,
          display: "grid",
          gap: 18,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "#b45309",
            }}
          >
            Superadmin Maintenance
          </div>
          <h2
            style={{
              margin: "6px 0 4px",
              fontSize: isCompact ? 24 : 30,
              fontWeight: 900,
              letterSpacing: "-0.04em",
            }}
          >
            Admin Tools
          </h2>
          <p style={{ margin: 0, color: dashboardMuted, fontSize: 13, lineHeight: 1.6 }}>
            These tools are for system maintenance only. Use them only when search,
            dashboard totals, or quote indexes are incorrect.
          </p>
        </div>

        <div
          style={{
            padding: 14,
            borderRadius: 16,
            background: isDark ? "rgba(120,53,15,0.24)" : "#fffbeb",
            border: "1px solid rgba(245,158,11,0.32)",
            color: isDark ? "#fde68a" : "#92400e",
            fontSize: 13,
            lineHeight: 1.55,
            fontWeight: 750,
          }}
        >
          Maintenance actions may take time and should only be run when the workspace
          needs repair. Normal quotation search, loading, deletion, and stage updates
          do not require these tools.
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact ? "1fr" : "repeat(3, minmax(0, 1fr))",
            gap: 12,
          }}
        >
          {maintenanceActions.map((action) => (
            <div
              key={action.key}
              style={{
                padding: 16,
                borderRadius: 18,
                background: dashboardSoftBg,
                border: dashboardBorder,
                display: "grid",
                gap: 12,
                alignContent: "space-between",
              }}
            >
              <div>
                <div style={{ fontSize: 16, fontWeight: 900, color: textColor }}>
                  {action.title}
                </div>
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 12.5,
                    color: dashboardMuted,
                    lineHeight: 1.55,
                  }}
                >
                  {action.description}
                </div>
              </div>
              <button
                type="button"
                disabled={action.disabled}
                onClick={() =>
                  openConfirmDialog({
                    title: action.confirmTitle,
                    description: action.confirmDescription,
                    confirmLabel: action.confirmLabel,
                    onConfirm: action.onConfirm,
                  })
                }
                style={{
                  ...btn(action.color),
                  marginRight: 0,
                  opacity: action.disabled ? 0.72 : 1,
                  cursor: action.disabled ? "not-allowed" : "pointer",
                }}
              >
                {action.buttonLabel}
              </button>
            </div>
          ))}
        </div>

        {(analyticsRebuildState.running ||
          analyticsRebuildState.scanned > 0 ||
          analyticsRebuildState.error) ? (
          <div
            style={{
              padding: "12px 14px",
              borderRadius: 14,
              background: isDark ? "rgba(15,118,110,0.16)" : "#ecfdf5",
              border: "1px solid rgba(15,118,110,0.22)",
              color: textColor,
              fontSize: 12,
              lineHeight: 1.6,
            }}
          >
            <strong>Dashboard rebuild:</strong>{" "}
            {analyticsRebuildState.running ? "running" : "idle"} | scanned{" "}
            {analyticsRebuildState.scanned} | indexed {analyticsRebuildState.indexedQuotes} |
            monthly updates {analyticsRebuildState.monthlyUpdated} | client updates{" "}
            {analyticsRebuildState.clientUpdated} | installation records{" "}
            {analyticsRebuildState.installationsUpdated} | stale cleared{" "}
            {analyticsRebuildState.staleCleared || 0} | skipped {analyticsRebuildState.skipped} |
            failed {analyticsRebuildState.failed}
            {analyticsRebuildState.error ? (
              <span style={{ color: "#dc2626", fontWeight: 800 }}>
                {" "}Error: {analyticsRebuildState.error}
              </span>
            ) : null}
          </div>
        ) : null}

        {(adminDataAuditState.running ||
          adminDataAuditState.checkedAt ||
          adminDataAuditState.error) ? (
          <div
            style={{
              padding: "12px 14px",
              borderRadius: 14,
              background: isDark ? "rgba(124,58,237,0.14)" : "#f5f3ff",
              border: "1px solid rgba(124,58,237,0.22)",
              color: textColor,
              fontSize: 12,
              lineHeight: 1.55,
            }}
          >
            <div style={{ fontWeight: 900, marginBottom: 8 }}>
              Workspace Data Audit{" "}
              {adminDataAuditState.running
                ? "(running)"
                : adminDataAuditState.checkedAt
                ? `(${new Date(adminDataAuditState.checkedAt).toLocaleString()})`
                : ""}
            </div>
            {adminDataAuditState.progress?.collection && adminDataAuditState.running ? (
              <div style={{ marginBottom: 8, color: dashboardMuted }}>
                Scanning {adminDataAuditState.progress.collection}:{" "}
                {adminDataAuditState.progress.scanned} documents checked
              </div>
            ) : null}
            {adminDataAuditState.error ? (
              <div style={{ marginBottom: 8, color: "#dc2626", fontWeight: 800 }}>
                Audit error: {adminDataAuditState.error}
              </div>
            ) : null}
            {adminDataAuditState.report ? (
              <div
                style={{
                  marginBottom: 10,
                  padding: 10,
                  borderRadius: 12,
                  background: isDark ? "rgba(15,23,42,0.72)" : "#ffffff",
                  border: dashboardBorder,
                }}
              >
                <div style={{ fontWeight: 900, marginBottom: 4 }}>
                  Source documents: quotations{" "}
                  {adminDataAuditState.report.sourceDocumentCounts?.quotations || 0},
                  CRM leads {adminDataAuditState.report.sourceDocumentCounts?.crm_leads || 0},
                  customers {adminDataAuditState.report.sourceDocumentCounts?.customers || 0}
                </div>
                <div>
                  Derived docs: quote_index{" "}
                  {adminDataAuditState.report.derivedDocumentCounts?.quote_index || 0},
                  analytics_monthly{" "}
                  {adminDataAuditState.report.derivedDocumentCounts?.analytics_monthly || 0},
                  client_monthly{" "}
                  {adminDataAuditState.report.derivedDocumentCounts?.client_monthly || 0},
                  installation_index{" "}
                  {adminDataAuditState.report.derivedDocumentCounts?.installation_index || 0}
                </div>
                <div style={{ marginTop: 4 }}>
                  Mismatches / repair issues:{" "}
                  <strong>
                    {adminDataAuditState.report.mismatchSummary?.totalMismatchCount || 0}
                  </strong>{" "}
                  | invalid amounts{" "}
                  {adminDataAuditState.report.issueCounts?.invalidAmountCount || 0}
                  {" "} | confirmed without confirmedAt{" "}
                  {adminDataAuditState.report.issueCounts?.confirmedWithoutConfirmedAtCount || 0}
                </div>
                {adminDataAuditState.report.mismatchSummary?.rootCauseHints?.length ? (
                  <div style={{ marginTop: 6, color: dashboardMuted }}>
                    {adminDataAuditState.report.mismatchSummary.rootCauseHints.join(" ")}
                  </div>
                ) : null}
                <button
                  type="button"
                  onClick={downloadAdminDataAuditReport}
                  style={{ ...btn("#6d28d9"), marginTop: 8 }}
                >
                  Download Audit JSON
                </button>
              </div>
            ) : null}
            <div style={{ display: "grid", gap: 8 }}>
              {Object.entries(adminDataAuditState.collections || {}).map(([name, result]) => (
                <div
                  key={name}
                  style={{
                    padding: 10,
                    borderRadius: 12,
                    background: isDark ? "rgba(15,23,42,0.72)" : "#ffffff",
                    border: dashboardBorder,
                  }}
                >
                  <strong>{name}</strong>:{" "}
                  {result.ok ? (
                    <>
                      total {result.totalCount} | batches {result.batches || 0} |
                      sample docs {result.sampleCount}
                      {Array.isArray(result.samples) && result.samples.length ? (
                        <div style={{ marginTop: 6, color: dashboardMuted }}>
                          Samples:{" "}
                          {result.samples
                            .map((sample) =>
                              [
                                sample.id,
                                sample.quoteNo ? `#${sample.quoteNo}` : "",
                                sample.customerName,
                                sample.monthKey,
                                sample.installationDateKey,
                              ]
                                .filter(Boolean)
                                .join(" / ")
                            )
                            .join(" | ")}
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <span style={{ color: "#dc2626", fontWeight: 800 }}>
                      failed {result.errorCode ? `(${result.errorCode})` : ""}:{" "}
                      {result.errorMessage || "Unknown error"}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const renderAccountSecurityPage = () => {
    const currentUser = auth.currentUser || user;
    const providerSummary = getSecurityProviderSummary(currentUser);
    const passwordStrength = getPasswordStrength(passwordForm.newPassword);
    const lastPasswordChange = securityActivity.find(
      (activity) => activity.type === "password_changed"
    );
    const currentSession = securitySessions.find(
      (session) => session.id === securitySessionId || session.sessionId === securitySessionId
    );
    const otherSessionCount = securitySessions.filter(
      (session) => session.id !== securitySessionId && session.sessionId !== securitySessionId
    ).length;
    const securityCard = {
      ...dashboardCardStyle,
      padding: isCompact ? 18 : 22,
      display: "grid",
      gap: 14,
    };
    const sectionTitleStyle = {
      fontSize: 18,
      fontWeight: 900,
      margin: 0,
      letterSpacing: "-0.02em",
    };
    const tinyLabelStyle = {
      fontSize: 11,
      fontWeight: 850,
      color: dashboardMuted,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
    };
    const securityPill = (background, color) => ({
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "6px 10px",
      borderRadius: 999,
      background,
      color,
      fontSize: 11,
      fontWeight: 900,
      whiteSpace: "nowrap",
    });
    const passwordInputType = passwordForm.showPasswords ? "text" : "password";
    const activityLabels = {
      password_changed: "Password changed",
      session_created: "New device signed in",
      session_revoked: "Session revoked",
      session_logged_out: "Session logged out",
      logout_everywhere: "Logged out everywhere",
    };

    return (
      <div
        style={{
          display: "grid",
          gap: 18,
          width: "100%",
          maxWidth: 1180,
          margin: "0 auto",
        }}
      >
        <div
          style={{
            borderRadius: 28,
            padding: isCompact ? 20 : 28,
            color: "#ffffff",
            background:
              "radial-gradient(circle at 15% 20%, rgba(56,189,248,0.30), transparent 28%), linear-gradient(135deg, #0f172a 0%, #1e3a8a 52%, #0f766e 100%)",
            boxShadow: "0 24px 70px rgba(15,23,42,0.24)",
            display: "grid",
            gap: 18,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 16,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div style={{ display: "grid", gap: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: "0.12em", textTransform: "uppercase", color: "#bfdbfe" }}>
                Account Security
              </div>
              <h1 style={{ fontSize: isCompact ? 28 : 38, lineHeight: 1.05, margin: 0, fontWeight: 950, letterSpacing: "-0.055em" }}>
                Protect your FabriQuote login
              </h1>
              <div style={{ fontSize: 14, lineHeight: 1.7, color: "#dbeafe", maxWidth: 720 }}>
                Change your password, review active devices, and sign out sessions you do not recognize. Sensitive writes go through secure Cloud Functions.
              </div>
            </div>

            <div
              style={{
                padding: "10px 14px",
                borderRadius: 999,
                background: "rgba(255,255,255,0.14)",
                border: "1px solid rgba(255,255,255,0.22)",
                fontSize: 12,
                fontWeight: 900,
                alignSelf: isCompact ? "flex-start" : "center",
              }}
            >
              Security status: Active
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact ? "1fr" : "repeat(3, minmax(0, 1fr))",
              gap: 12,
            }}
          >
            {[
              ["Email", currentUser?.email || "Not available"],
              ["Provider", providerSummary.label],
              ["Last sign-in", currentUser?.metadata?.lastSignInTime || "Not available"],
            ].map(([label, value]) => (
              <div
                key={label}
                style={{
                  padding: 16,
                  borderRadius: 20,
                  background: "rgba(255,255,255,0.12)",
                  border: "1px solid rgba(255,255,255,0.18)",
                  backdropFilter: "blur(16px)",
                  minWidth: 0,
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 900, color: "#bfdbfe", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  {label}
                </div>
                <div style={{ fontSize: 15, fontWeight: 850, marginTop: 8, overflowWrap: "anywhere" }}>
                  {value}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 0.95fr) minmax(0, 1.05fr)",
            gap: 18,
            alignItems: "start",
          }}
        >
          <div style={{ display: "grid", gap: 18 }}>
            <div style={securityCard}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "start" }}>
                <div>
                  <div style={tinyLabelStyle}>Overview</div>
                  <h2 style={sectionTitleStyle}>Account details</h2>
                </div>
                <span style={securityPill(isDark ? "rgba(22,163,74,0.18)" : "#dcfce7", isDark ? "#86efac" : "#166534")}>
                  Protected
                </span>
              </div>

              {[
                ["Email address", currentUser?.email || "Not available"],
                ["Sign-in provider", providerSummary.label],
                ["Last password change", lastPasswordChange ? formatSecurityRelativeTime(lastPasswordChange.createdAt) : "Not recorded yet"],
                ["Current device", currentSession?.display?.label || "This browser"],
              ].map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 14,
                    borderTop: dashboardBorder,
                    paddingTop: 12,
                    fontSize: 13,
                  }}
                >
                  <span style={{ color: dashboardMuted, fontWeight: 750 }}>{label}</span>
                  <span style={{ fontWeight: 850, textAlign: "right", overflowWrap: "anywhere" }}>{value}</span>
                </div>
              ))}
            </div>

            <div style={securityCard}>
              <div>
                <div style={tinyLabelStyle}>Password</div>
                <h2 style={sectionTitleStyle}>Change password</h2>
                <div style={{ fontSize: 13, color: dashboardMuted, lineHeight: 1.7, marginTop: 6 }}>
                  Choose a strong password you do not use elsewhere.
                </div>
              </div>

              {!providerSummary.hasPassword ? (
                <div
                  style={{
                    padding: 14,
                    borderRadius: 16,
                    background: isDark ? "rgba(37,99,235,0.14)" : "#eff6ff",
                    color: isDark ? "#bfdbfe" : "#1d4ed8",
                    border: isDark ? "1px solid rgba(96,165,250,0.22)" : "1px solid rgba(37,99,235,0.16)",
                    fontSize: 13,
                    lineHeight: 1.7,
                  }}
                >
                  Your account uses {providerSummary.label} sign-in. Password changes are managed by that provider.
                </div>
              ) : (
                <div style={{ display: "grid", gap: 12 }}>
                  {[
                    ["Current password", "currentPassword"],
                    ["New password", "newPassword"],
                    ["Confirm new password", "confirmPassword"],
                  ].map(([label, key]) => (
                    <label key={key} style={{ display: "grid", gap: 6 }}>
                      <span style={{ fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>{label}</span>
                      <input
                        type={passwordInputType}
                        value={passwordForm[key] || ""}
                        onChange={(event) =>
                          setPasswordForm((prev) => ({
                            ...prev,
                            [key]: event.target.value,
                            error: "",
                            message: "",
                          }))
                        }
                        style={{ ...input, padding: "11px 12px", borderRadius: 14 }}
                        autoComplete={key === "currentPassword" ? "current-password" : "new-password"}
                      />
                    </label>
                  ))}

                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                    <div style={{ flex: "1 1 180px", minWidth: 160 }}>
                      <div style={{ height: 8, borderRadius: 999, background: isDark ? "rgba(148,163,184,0.18)" : "#e2e8f0", overflow: "hidden" }}>
                        <div
                          style={{
                            width: `${Math.min(100, Math.max(8, (passwordStrength.score / 5) * 100))}%`,
                            height: "100%",
                            borderRadius: 999,
                            background: passwordStrength.color,
                          }}
                        />
                      </div>
                      <div style={{ marginTop: 6, fontSize: 12, color: passwordStrength.color, fontWeight: 850 }}>
                        Strength: {passwordStrength.label}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setPasswordForm((prev) => ({
                          ...prev,
                          showPasswords: !prev.showPasswords,
                        }))
                      }
                      style={{
                        ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"),
                        marginRight: 0,
                      }}
                    >
                      {passwordForm.showPasswords ? "Hide" : "Show"}
                    </button>
                  </div>

                  <label style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, color: dashboardMuted, lineHeight: 1.5 }}>
                    <input
                      type="checkbox"
                      checked={passwordForm.logoutOtherSessions}
                      onChange={(event) =>
                        setPasswordForm((prev) => ({
                          ...prev,
                          logoutOtherSessions: event.target.checked,
                        }))
                      }
                      style={{ marginTop: 3 }}
                    />
                    <span>Sign out other app sessions after password change. Recommended.</span>
                  </label>

                  {passwordForm.error ? (
                    <div style={{ color: "#dc2626", fontSize: 13, fontWeight: 750 }}>
                      {passwordForm.error}
                    </div>
                  ) : null}
                  {passwordForm.message ? (
                    <div style={{ color: "#16a34a", fontSize: 13, fontWeight: 750 }}>
                      {passwordForm.message}
                    </div>
                  ) : null}

                  <button
                    type="button"
                    onClick={handleChangePassword}
                    disabled={securityActionState.password}
                    style={{
                      ...btn("#2563eb"),
                      marginRight: 0,
                      opacity: securityActionState.password ? 0.72 : 1,
                    }}
                  >
                    {securityActionState.password ? "Updating password..." : "Update Password"}
                  </button>
                </div>
              )}
            </div>
          </div>

          <div style={{ display: "grid", gap: 18 }}>
            <div style={securityCard}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  flexDirection: isCompact ? "column" : "row",
                  alignItems: isCompact ? "stretch" : "center",
                }}
              >
                <div>
                  <div style={tinyLabelStyle}>Active sessions</div>
                  <h2 style={sectionTitleStyle}>Signed-in devices</h2>
                  <div style={{ fontSize: 13, color: dashboardMuted, lineHeight: 1.7, marginTop: 6 }}>
                    These are recent app sessions for your account. Only safe device labels are stored.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={loadAccountSecurityData}
                  disabled={securityCenterState.loading}
                  style={{
                    ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"),
                    marginRight: 0,
                    opacity: securityCenterState.loading ? 0.72 : 1,
                  }}
                >
                  {securityCenterState.loading ? "Refreshing..." : "Refresh"}
                </button>
              </div>

              {securityCenterState.error ? (
                <div style={{ color: "#dc2626", fontSize: 13, fontWeight: 750 }}>
                  {securityCenterState.error}
                </div>
              ) : null}

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={() =>
                    openConfirmDialog({
                      title: "Log out this device?",
                      description: "This browser will be signed out immediately.",
                      confirmLabel: "Log Out This Device",
                      tone: "danger",
                      onConfirm: handleRevokeCurrentSession,
                    })
                  }
                  disabled={securityActionState.currentSession}
                  style={{
                    ...btn("#dc2626"),
                    marginRight: 0,
                    opacity: securityActionState.currentSession ? 0.72 : 1,
                  }}
                >
                  {securityActionState.currentSession ? "Signing out..." : "Log out this device"}
                </button>

                <button
                  type="button"
                  onClick={() =>
                    openConfirmDialog({
                      title: "Log out other app sessions?",
                      description:
                        "Other FabriQuote browser sessions will sign out when they receive the session update. This does not expose or store tokens.",
                      confirmLabel: "Log Out Other Devices",
                      tone: "warning",
                      onConfirm: () => handleRevokeOtherSessions(),
                    })
                  }
                  disabled={securityActionState.otherSessions || otherSessionCount === 0}
                  style={{
                    ...btn("#f59e0b"),
                    marginRight: 0,
                    opacity: securityActionState.otherSessions || otherSessionCount === 0 ? 0.62 : 1,
                  }}
                >
                  {securityActionState.otherSessions ? "Signing out..." : "Log out other devices"}
                </button>

                <button
                  type="button"
                  onClick={() =>
                    openConfirmDialog({
                      title: "Log out everywhere?",
                      description:
                        "This is the strongest option. Firebase refresh tokens are revoked and you will need to sign in again.",
                      confirmLabel: "Log Out Everywhere",
                      tone: "danger",
                      onConfirm: handleRevokeAllSessions,
                    })
                  }
                  disabled={securityActionState.allSessions}
                  style={{
                    ...btn("#7f1d1d"),
                    marginRight: 0,
                    opacity: securityActionState.allSessions ? 0.72 : 1,
                  }}
                >
                  {securityActionState.allSessions ? "Securing..." : "Log out everywhere"}
                </button>
              </div>

              {securitySessions.length === 0 && !securityCenterState.loading ? (
                <div
                  style={{
                    padding: 16,
                    borderRadius: 18,
                    border: "1px dashed rgba(148,163,184,0.42)",
                    color: dashboardMuted,
                    fontSize: 13,
                    lineHeight: 1.7,
                  }}
                >
                  No active sessions are listed yet. Refresh once if you just signed in.
                </div>
              ) : (
                <div style={{ display: "grid", gap: 12 }}>
                  {securitySessions.map((session) => {
                    const sessionId = session.sessionId || session.id;
                    const isCurrent =
                      sessionId === securitySessionId || session.id === securitySessionId;
                    const isTargetLoading =
                      securityActionState.targetSessionId &&
                      securityActionState.targetSessionId === sessionId;
                    return (
                      <div
                        key={session.id || sessionId}
                        style={{
                          padding: 16,
                          borderRadius: 20,
                          border: isCurrent
                            ? "1px solid rgba(37,99,235,0.42)"
                            : dashboardBorder,
                          background: isCurrent
                            ? isDark
                              ? "rgba(37,99,235,0.12)"
                              : "#eff6ff"
                            : dashboardSoftBg,
                          display: "grid",
                          gap: 12,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 12,
                            flexDirection: isCompact ? "column" : "row",
                            alignItems: isCompact ? "stretch" : "center",
                          }}
                        >
                          <div>
                            <div style={{ fontSize: 15, fontWeight: 900 }}>
                              {session.display?.label || "Browser session"}
                            </div>
                            <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 5 }}>
                              {session.device?.deviceType || "unknown"} - {session.device?.os || "Unknown OS"}
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            {isCurrent ? (
                              <span style={securityPill(isDark ? "rgba(37,99,235,0.20)" : "#dbeafe", isDark ? "#bfdbfe" : "#1d4ed8")}>
                                Current device
                              </span>
                            ) : null}
                            <span style={securityPill(isDark ? "rgba(148,163,184,0.14)" : "#e2e8f0", isDark ? "#cbd5e1" : "#475569")}>
                              {formatSecurityRelativeTime(session.lastSeenAt)}
                            </span>
                          </div>
                        </div>

                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: isCompact ? "1fr" : "repeat(3, minmax(0, 1fr))",
                            gap: 10,
                            fontSize: 12,
                            color: dashboardMuted,
                          }}
                        >
                          <div>First signed in: <strong style={{ color: textColor }}>{formatSecurityRelativeTime(session.createdAt)}</strong></div>
                          <div>Location: <strong style={{ color: textColor }}>{session.display?.approximateLocation || "Not stored"}</strong></div>
                          <div>Browser: <strong style={{ color: textColor }}>{session.device?.browser || "Browser"}</strong></div>
                        </div>

                        {!isCurrent ? (
                          <div>
                            <button
                              type="button"
                              onClick={() =>
                                openConfirmDialog({
                                  title: "Log out this device?",
                                  description: "This app session will be marked revoked. The device will sign out when it next checks its session.",
                                  confirmLabel: "Log Out Device",
                                  tone: "warning",
                                  onConfirm: () => handleRevokeOtherSessions(sessionId),
                                })
                              }
                              disabled={Boolean(isTargetLoading)}
                              style={{
                                ...btn("#f59e0b"),
                                marginRight: 0,
                                opacity: isTargetLoading ? 0.72 : 1,
                              }}
                            >
                              {isTargetLoading ? "Signing out..." : "Log out device"}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={securityCard}>
              <div>
                <div style={tinyLabelStyle}>Security activity</div>
                <h2 style={sectionTitleStyle}>Recent events</h2>
              </div>

              {securityActivity.length === 0 ? (
                <div
                  style={{
                    padding: 16,
                    borderRadius: 18,
                    border: "1px dashed rgba(148,163,184,0.42)",
                    color: dashboardMuted,
                    fontSize: 13,
                    lineHeight: 1.7,
                  }}
                >
                  Security events such as new sessions, password changes, and session revokes will appear here.
                </div>
              ) : (
                <div style={{ display: "grid", gap: 10 }}>
                  {securityActivity.map((activity) => {
                    const tone =
                      activity.severity === "critical"
                        ? { bg: isDark ? "rgba(127,29,29,0.34)" : "#fee2e2", color: "#dc2626" }
                        : activity.severity === "warning"
                        ? { bg: isDark ? "rgba(245,158,11,0.16)" : "#fffbeb", color: "#d97706" }
                        : { bg: isDark ? "rgba(37,99,235,0.16)" : "#eff6ff", color: "#2563eb" };
                    return (
                      <div
                        key={activity.id}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "auto 1fr",
                          gap: 12,
                          padding: 12,
                          borderRadius: 16,
                          border: dashboardBorder,
                          background: dashboardSoftBg,
                        }}
                      >
                        <div
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: 999,
                            background: tone.color,
                            marginTop: 5,
                            boxShadow: `0 0 0 6px ${tone.bg}`,
                          }}
                        />
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 900 }}>
                            {activityLabels[activity.type] || "Security event"}
                          </div>
                          <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                            {activity.deviceLabel || "FabriQuote"} - {formatSecurityRelativeTime(activity.createdAt)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTeamMemberInviteModal = () => {
    if (!isTeamMemberModalOpen) return null;

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 140,
          background: "rgba(15,23,42,0.48)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isCompact ? 12 : 24,
        }}
        onClick={closeTeamMemberInviteModal}
      >
        <div
          onClick={(event) => event.stopPropagation()}
          style={{
            width: "100%",
            maxWidth: 920,
            maxHeight: "90vh",
            overflowY: "auto",
            ...dashboardCardStyle,
            padding: isCompact ? 18 : 24,
            display: "grid",
            gap: 18,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#2563eb", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                Team Members
              </div>
              <div style={{ fontSize: 24, fontWeight: 900, marginTop: 6 }}>
                Invite Team Member
              </div>
              <div style={{ fontSize: 13, color: dashboardMuted, marginTop: 8 }}>
                Invite staff into this shared FabriQuote workspace with their own login and tailored feature access.
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <button
                type="button"
                onClick={closeTeamMemberInviteModal}
                style={{
                  ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"),
                  marginRight: 0,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => sendTeamMemberInvite()}
                disabled={isSendingTeamInvite}
                style={{
                  ...btn("#2563eb"),
                  marginRight: 0,
                  opacity: isSendingTeamInvite ? 0.7 : 1,
                }}
              >
                {isSendingTeamInvite ? "Sending Invite..." : "Send Invite"}
              </button>
              {isDevRuntime ? (
                <button
                  type="button"
                  onClick={sendTeamInviteTestEmail}
                  disabled={isTestingTeamInviteEmail || isSendingTeamInvite}
                  style={{
                    ...btn("#f59e0b"),
                    marginRight: 0,
                    opacity: isTestingTeamInviteEmail ? 0.7 : 1,
                  }}
                >
                  {isTestingTeamInviteEmail ? "Testing..." : "Test Invite Email"}
                </button>
              ) : null}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 1fr) minmax(0, 1.15fr)",
              gap: 16,
            }}
          >
            <div style={{ ...dashboardCardStyle, background: dashboardSoftBg, padding: 16, display: "grid", gap: 12 }}>
              <div style={{ fontSize: 16, fontWeight: 800 }}>Team Member Details</div>
              <input
                value={teamMemberDraft.name || ""}
                onChange={(event) => updateTeamMemberDraftField("name", event.target.value)}
                style={input}
                placeholder="Full name"
              />
              <input
                value={teamMemberDraft.email || ""}
                onChange={(event) => updateTeamMemberDraftField("email", event.target.value)}
                style={input}
                placeholder="Email address"
                type="email"
              />
              <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "1fr 1fr", gap: 10 }}>
                <input
                  value={teamMemberDraft.phone || ""}
                  onChange={(event) => updateTeamMemberDraftField("phone", event.target.value)}
                  style={input}
                  placeholder="Phone"
                />
                <input
                  value={teamMemberDraft.role || ""}
                  onChange={(event) => updateTeamMemberDraftField("role", event.target.value)}
                  style={input}
                  placeholder="Role"
                />
              </div>
              <div
                style={{
                  padding: "12px 14px",
                  borderRadius: 14,
                  border: dashboardBorder,
                  background: isDark ? "rgba(255,255,255,0.04)" : "#ffffff",
                  fontSize: 13,
                  color: dashboardMuted,
                  lineHeight: 1.6,
                }}
              >
                {remainingStaffSlots > 0
                  ? `${remainingStaffSlots} of ${accountStaffLimit} staff slots remaining in this workspace.`
                  : "You have reached your staff limit."}
              </div>
              {teamMemberModalError ? (
                <div style={{ color: "#dc2626", fontSize: 13, fontWeight: 600 }}>
                  {teamMemberModalError}
                </div>
              ) : null}
            </div>

            <div style={{ ...dashboardCardStyle, background: dashboardSoftBg, padding: 16, display: "grid", gap: 12 }}>
              <div style={{ fontSize: 16, fontWeight: 800 }}>Permissions</div>
              <div style={{ display: "grid", gap: 10 }}>
                {TEAM_PERMISSION_DEFINITIONS.map((permission) => (
                  <label
                    key={permission.key}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "auto 1fr",
                      gap: 12,
                      alignItems: "flex-start",
                      padding: 12,
                      borderRadius: 14,
                      border: dashboardBorder,
                      background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={teamMemberDraft.permissions?.[permission.key] === true}
                      onChange={(event) =>
                        updateTeamMemberDraftPermission(permission.key, event.target.checked)
                      }
                      style={{ marginTop: 3 }}
                    />
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>{permission.label}</div>
                      <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.5 }}>
                        {permission.description}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTeamMembersManager = () => (
    <div
      style={{
        padding: 20,
        borderRadius: 20,
        background: isDark ? "#1b1b27" : "#f8fafc",
        border: "1px solid rgba(148,163,184,0.16)",
        display: "grid",
        gap: 16,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexDirection: isCompact ? "column" : "row",
          alignItems: isCompact ? "stretch" : "center",
        }}
      >
        <div>
          <h3 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>
            Workspace Team Members
          </h3>
          <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 6, lineHeight: 1.6 }}>
            Manage invites, feature permissions, and CRM assignment availability for this shared FabriQuote workspace.
          </div>
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            alignItems: "center",
          }}
        >
          <div
            style={{
              padding: "8px 12px",
              borderRadius: 999,
              background: isDark ? "rgba(148,163,184,0.14)" : "#e2e8f0",
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {occupiedStaffSlots}/{accountStaffLimit} staff slots used
          </div>
          {isWorkspaceOwner ? (
            <Button
              icon={Plus}
              variant="primary"
              onClick={openTeamMemberInviteModal}
              disabled={remainingStaffSlots <= 0}
            >
              Invite Team Member
            </Button>
          ) : null}
        </div>
      </div>

      {teamMembers.length === 0 ? (
        <div
          style={{
            padding: 16,
            borderRadius: 16,
            background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
            border: "1px dashed rgba(148,163,184,0.4)",
            fontSize: 13,
            color: dashboardMuted,
            lineHeight: 1.7,
          }}
        >
          No team members have been invited yet. Add staff here to share this workspace, unlock CRM assignments, and control what each person can access.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {teamMembers.map((member) => (
            <div
              key={member.id}
              style={{
                padding: 16,
                borderRadius: 18,
                background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                border: "1px solid rgba(148,163,184,0.18)",
                display: "grid",
                gap: 12,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  flexDirection: isCompact ? "column" : "row",
                  alignItems: isCompact ? "stretch" : "center",
                }}
              >
                <div>
                  <div style={{ fontSize: 16, fontWeight: 800 }}>
                    {member.name || "Unnamed Team Member"}
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.72, marginTop: 6 }}>
                    {member.email || "No email added yet"}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <div
                    style={{
                      padding: "8px 12px",
                      borderRadius: 999,
                      background:
                        member.status === "active"
                          ? isDark
                            ? "rgba(16,185,129,0.16)"
                            : "#dcfce7"
                          : member.status === "invited"
                          ? isDark
                            ? "rgba(37,99,235,0.18)"
                            : "#dbeafe"
                          : isDark
                          ? "rgba(148,163,184,0.14)"
                          : "#e2e8f0",
                      color:
                        member.status === "active"
                          ? "#166534"
                          : member.status === "invited"
                          ? "#1d4ed8"
                          : isDark
                          ? "#cbd5e1"
                          : "#475569",
                      fontSize: 12,
                      fontWeight: 800,
                    }}
                  >
                    {getTeamMemberStatusLabel(member)}
                  </div>
                  <div
                    style={{
                      padding: "8px 12px",
                      borderRadius: 999,
                      background: isDark ? "rgba(148,163,184,0.14)" : "#e2e8f0",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    {member.role || "Team Member"}
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isCompact
                    ? "1fr"
                    : "minmax(0, 1.1fr) minmax(0, 1fr) minmax(0, 0.8fr)",
                  gap: 12,
                }}
              >
                <input
                  style={input}
                  value={member.name}
                  onChange={(e) =>
                    setTeamMembers((prev) =>
                      prev.map((current) =>
                        current.id === member.id
                          ? { ...current, name: e.target.value }
                          : current
                      )
                    )
                  }
                  placeholder="Team member name"
                  disabled={!isWorkspaceOwner}
                />
                <input
                  style={input}
                  value={member.phone}
                  onChange={(e) =>
                    setTeamMembers((prev) =>
                      prev.map((current) =>
                        current.id === member.id
                          ? { ...current, phone: e.target.value }
                          : current
                      )
                    )
                  }
                  placeholder="Phone"
                  disabled={!isWorkspaceOwner}
                />
                <input
                  style={input}
                  value={member.role}
                  onChange={(e) =>
                    setTeamMembers((prev) =>
                      prev.map((current) =>
                        current.id === member.id
                          ? { ...current, role: e.target.value }
                          : current
                      )
                    )
                  }
                  placeholder="Role"
                  disabled={!isWorkspaceOwner}
                />
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 1fr) auto",
                  gap: 12,
                  alignItems: "start",
                }}
              >
                <div style={{ display: "grid", gap: 10 }}>
                  <input
                    style={input}
                    value={member.email}
                    onChange={(e) =>
                      setTeamMembers((prev) =>
                        prev.map((current) =>
                          current.id === member.id && !current.uid
                            ? { ...current, email: e.target.value }
                            : current
                        )
                      )
                    }
                    placeholder="Email"
                    disabled={!isWorkspaceOwner || Boolean(member.uid)}
                  />

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))",
                      gap: 10,
                    }}
                  >
                    {TEAM_PERMISSION_DEFINITIONS.map((permission) => (
                      <label
                        key={`${member.id}-${permission.key}`}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "auto 1fr",
                          gap: 10,
                          alignItems: "start",
                          padding: 12,
                          borderRadius: 14,
                          border: "1px solid rgba(148,163,184,0.18)",
                          background: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={member.permissions?.[permission.key] === true}
                          onChange={(e) =>
                            updateSavedTeamMemberPermission(
                              member.id,
                              permission.key,
                              e.target.checked
                            )
                          }
                          disabled={!isWorkspaceOwner}
                          style={{ marginTop: 3 }}
                        />
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700 }}>
                            {permission.label}
                          </div>
                          <div style={{ fontSize: 11, color: dashboardMuted, marginTop: 4, lineHeight: 1.5 }}>
                            {permission.description}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    alignItems: isCompact ? "stretch" : "flex-end",
                  }}
                >
                  <label
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: 13,
                      fontWeight: 700,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={member.status !== "disabled" && member.isActive !== false}
                      disabled={!isWorkspaceOwner}
                      onChange={(e) =>
                        setTeamMembers((prev) =>
                          normalizeTeamMembers(
                            prev.map((current) =>
                              current.id === member.id
                                ? {
                                    ...current,
                                    isActive: e.target.checked,
                                    status: e.target.checked
                                      ? current.uid
                                        ? "active"
                                        : "invited"
                                      : "disabled",
                                  }
                                : current
                            )
                          )
                        )
                      }
                    />
                    {member.status === "disabled" ? "Disabled" : "Enabled"}
                  </label>

                  {member.status === "invited" && member.email && isWorkspaceOwner ? (
                    <Button
                      icon={Mail}
                      variant="secondary"
                      onClick={() =>
                        sendTeamMemberInvite(member, {
                          closeAfter: false,
                          resend: true,
                        })
                      }
                      disabled={isSendingTeamInvite || Boolean(resendingTeamInviteId)}
                    >
                      {resendingTeamInviteId === member.id ? "Sending..." : "Resend Invite"}
                    </Button>
                  ) : null}

                  <Button
                    icon={Trash2}
                    variant="danger"
                    onClick={() =>
                      setTeamMembers((prev) =>
                        prev.filter((current) => current.id !== member.id)
                      )
                    }
                    disabled={!isWorkspaceOwner}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "center",
          flexWrap: "wrap",
          paddingTop: 4,
        }}
      >
        <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
          Permission, status, and profile edits are saved back to this workspace when you save team changes.
        </div>
        {isWorkspaceOwner ? (
          <Button
            icon={Save}
            variant="primary"
            onClick={saveSettings}
            disabled={isSavingSettings}
          >
            {isSavingSettings ? "Saving..." : "Save Team Changes"}
          </Button>
        ) : null}
      </div>
    </div>
  );

  const renderTodayTasks = () => {
    const summaryCards = [
      { label: "Total Tasks", value: dailyTaskSummary.total, accent: "#2563eb" },
      { label: "Pending", value: dailyTaskSummary.pending, accent: "#0f172a" },
      { label: "Overdue", value: dailyTaskSummary.overdue, accent: "#ef4444" },
      { label: "Installations", value: dailyTaskSummary.installations, accent: "#f97316" },
      { label: "Completed", value: dailyTaskSummary.completed, accent: "#16a34a" },
    ];
    const dateModeOptions = [
      { key: "today", label: "Today" },
      { key: "tomorrow", label: "Tomorrow" },
      { key: "week", label: "This Week" },
      { key: "custom", label: "Custom Range" },
      { key: "overdue", label: "Overdue" },
    ];
    const groupDefinitions = [
      ["overdue", "Overdue", "Pending work already past its due date or time.", dailyTaskGroups.overdue, "#ef4444"],
      ["today", dailyTaskDateMode === "week" ? "This Week" : dailyTaskDateMode === "tomorrow" ? "Tomorrow" : dailyTaskDateMode === "custom" ? "Selected Range" : "Today", "Follow-ups, checklist items, and deal actions scheduled for the selected window.", dailyTaskGroups.today, "#2563eb"],
      ["installations", "Installations", "Installation work scheduled for this date.", dailyTaskGroups.installations, "#f97316"],
      ["completed", "Completed", "Tasks completed on or scheduled for this date.", dailyTaskGroups.completed, "#16a34a"],
    ];

    const renderTaskCard = (task) => {
      const isSelected = selectedDailyTask?.id === task.id;
      const priorityColor =
        task.priority === CRM_FOLLOW_UP_PRIORITY_HIGH
          ? "#ef4444"
          : task.priority === CRM_FOLLOW_UP_PRIORITY_MEDIUM
          ? "#f97316"
          : "#64748b";
      const typeColor =
        task.sourceType === DAILY_TASK_SOURCE_INSTALLATION
          ? "#f97316"
          : task.sourceType === DAILY_TASK_SOURCE_FOLLOW_UP
          ? "#2563eb"
          : "#0f172a";

      return (
        <div
          key={task.id}
          onClick={() => setSelectedDailyTaskId(task.id)}
          style={{
            padding: 16,
            borderRadius: 20,
            border: isSelected ? "1px solid rgba(37,99,235,0.42)" : dashboardBorder,
            background: isSelected
              ? isDark
                ? "rgba(37,99,235,0.12)"
                : "#eff6ff"
              : dashboardCardBg,
            boxShadow: isDark ? "none" : "0 12px 26px rgba(15,23,42,0.06)",
            display: "grid",
            gap: 12,
            cursor: "pointer",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                <span style={{ padding: "4px 8px", borderRadius: 999, background: `${typeColor}18`, color: typeColor, fontSize: 11, fontWeight: 800 }}>
                  {task.typeLabel}
                </span>
                <span style={{ padding: "4px 8px", borderRadius: 999, background: `${priorityColor}16`, color: priorityColor, fontSize: 11, fontWeight: 800, textTransform: "capitalize" }}>
                  {task.priority || "medium"}
                </span>
                {task.isOverdue && task.status !== "completed" ? (
                  <span style={{ padding: "4px 8px", borderRadius: 999, background: "rgba(239,68,68,0.12)", color: "#ef4444", fontSize: 11, fontWeight: 800 }}>
                    Overdue
                  </span>
                ) : null}
                {task.status === "completed" ? (
                  <span style={{ padding: "4px 8px", borderRadius: 999, background: "rgba(22,163,74,0.12)", color: "#16a34a", fontSize: 11, fontWeight: 800 }}>
                    Done
                  </span>
                ) : null}
              </div>
              <div style={{ fontSize: 16, fontWeight: 900, color: textColor }}>{task.title}</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 5, lineHeight: 1.6 }}>
                {task.customerName}
                {task.quoteNo ? ` | #${task.quoteNo}` : ""}
                {task.source ? ` | ${task.source}` : ""}
              </div>
            </div>
            <div style={{ minWidth: 82, textAlign: "right", color: task.isOverdue ? "#ef4444" : dashboardMuted, fontSize: 12, fontWeight: 800 }}>
              {task.dueTime || "-"}
            </div>
          </div>

          {task.description ? (
            <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>{task.description}</div>
          ) : null}
          {task.locationLabel || task.locationAddress ? (
            <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
              Location: <strong style={{ color: textColor }}>{task.locationLabel || "Site"}</strong>
              {task.locationAddress ? ` | ${task.locationAddress}` : ""}
            </div>
          ) : null}

          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, fontSize: 11, color: dashboardMuted }}>
            <span>{formatReminderDateTime(task.dueDateTime || task.dueDate)}</span>
            <span>{task.assignedTo || "Unassigned"}</span>
            {task.stageLabel ? <span>{task.stageLabel}</span> : null}
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" onClick={(event) => { event.stopPropagation(); openDailyTaskDeal(task); }} style={{ ...btn("#0f172a"), padding: "8px 12px", marginRight: 0 }}>
              Open Deal
            </button>
            {task.locationUrl || task.siteLocation ? (
              <button type="button" onClick={(event) => { event.stopPropagation(); openSiteLocation(task.siteLocation || task); }} style={{ ...btn("#2563eb"), padding: "8px 12px", marginRight: 0 }}>
                Navigate
              </button>
            ) : null}
            {task.status !== "completed" ? (
              <>
                <button
                  type="button"
                  disabled={Boolean(markingDoneKey)}
                  onClick={(event) => { event.stopPropagation(); markDailyTaskDone(task); }}
                  style={{
                    ...btn("#16a34a"),
                    padding: "8px 12px",
                    marginRight: 0,
                    opacity: markingDoneKey ? 0.7 : 1,
                  }}
                >
                  {markingDoneKey ? "Saving..." : "Mark as Done"}
                </button>
                <button
                  type="button"
                  onClick={(event) => { event.stopPropagation(); openDailyTaskReschedule(task); }}
                  style={{ ...btn(isDark ? "#1e293b" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"), padding: "8px 12px", marginRight: 0 }}
                >
                  Snooze / Reschedule
                </button>
              </>
            ) : null}
          </div>
        </div>
      );
    };

    return (
      <div style={dashboardPanelStyle}>
        <div style={{ ...dashboardCardStyle, marginBottom: 18, background: isDark ? "linear-gradient(135deg, rgba(15,23,42,0.98), rgba(30,41,59,0.96))" : "linear-gradient(135deg, #eff6ff, #ffffff)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 18, flexDirection: isCompact ? "column" : "row", alignItems: isCompact ? "stretch" : "flex-start" }}>
            <div style={{ maxWidth: 760 }}>
              <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#2563eb", marginBottom: 10 }}>
                Today's Tasks
              </div>
              <h2 style={{ margin: 0, fontSize: isCompact ? 24 : 32, fontWeight: 900, letterSpacing: "-0.04em", color: isDark ? "#f8fafc" : "#0f172a" }}>
                Start the day with every follow-up, action, and installation in one place
              </h2>
              <div style={{ marginTop: 10, fontSize: 13, color: dashboardMuted, lineHeight: 1.7 }}>
                This dashboard combines CRM follow-ups, legacy deal actions, overdue work, and installation dates from the same CRM records your team already uses.
              </div>
            </div>
            <div style={{ minWidth: isCompact ? "100%" : 340, display: "grid", gap: 10 }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {dateModeOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => {
                      setDailyTaskDateMode(option.key);
                      if (option.key === "today") setDailyTaskDate(todayISO());
                      if (option.key === "tomorrow") {
                        const tomorrow = parseDateInputValue(todayISO()) || new Date();
                        tomorrow.setDate(tomorrow.getDate() + 1);
                        setDailyTaskDate(toDateInputValue(tomorrow));
                      }
                    }}
                    style={{
                      ...btn(
                        dailyTaskDateMode === option.key
                          ? "#2563eb"
                          : isDark
                          ? "#1e293b"
                          : "#e2e8f0",
                        dailyTaskDateMode === option.key || isDark ? "#fff" : "#0f172a"
                      ),
                      marginRight: 0,
                      padding: "8px 10px",
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              {dailyTaskDateMode === "custom" ? (
                <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "1fr 1fr", gap: 8 }}>
                  <input type="date" value={dailyTaskRangeStart} onChange={(event) => setDailyTaskRangeStart(event.target.value || todayISO())} style={{ ...input, width: "100%", minWidth: 0 }} />
                  <input type="date" value={dailyTaskRangeEnd} onChange={(event) => setDailyTaskRangeEnd(event.target.value || todayISO())} style={{ ...input, width: "100%", minWidth: 0 }} />
                </div>
              ) : (
                <input type="date" value={dailyTaskDateKey} onChange={(event) => { setDailyTaskDate(event.target.value || todayISO()); setDailyTaskDateMode("custom"); setDailyTaskRangeStart(event.target.value || todayISO()); setDailyTaskRangeEnd(event.target.value || todayISO()); }} style={{ ...input, width: "100%", minWidth: 0 }} />
              )}
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(5, minmax(0, 1fr))", gap: 12, marginBottom: 18 }}>
          {summaryCards.map((card) => (
            <div key={card.label} style={{ ...dashboardCardStyle, display: "grid", gap: 6, border: `1px solid ${card.accent}22` }}>
              <div style={{ fontSize: 12, color: dashboardMuted, fontWeight: 800 }}>{card.label}</div>
              <div style={{ fontSize: 28, fontWeight: 900, color: card.accent }}>{card.value}</div>
            </div>
          ))}
        </div>

        <div style={{ ...dashboardCardStyle, display: "grid", gap: 12, marginBottom: 18 }}>
          <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "minmax(220px, 1.3fr) repeat(6, minmax(120px, 1fr))", gap: 10 }}>
            <input value={dailyTaskSearch} onChange={(event) => setDailyTaskSearch(event.target.value)} style={{ ...input, width: "100%", minWidth: 0 }} placeholder="Search all tasks by customer, quote no, task, source..." />
            <select value={dailyTaskAssignedFilter} onChange={(event) => setDailyTaskAssignedFilter(event.target.value)} style={{ ...selectInput, width: "100%", minWidth: 0 }}>
              <option value="all">All Assignees</option>
              {dailyTaskAssignedOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
            <select value={dailyTaskTypeFilter} onChange={(event) => setDailyTaskTypeFilter(event.target.value)} style={{ ...selectInput, width: "100%", minWidth: 0 }}>
              <option value="all">All Types</option>
              <option value={DAILY_TASK_SOURCE_FOLLOW_UP}>Follow-ups</option>
              <option value={DAILY_TASK_SOURCE_DEAL_ACTION}>Deal Actions</option>
              <option value={DAILY_TASK_SOURCE_CHECKLIST}>Checklist</option>
              <option value={DAILY_TASK_SOURCE_INSTALLATION}>Installations</option>
            </select>
            <select value={dailyTaskPriorityFilter} onChange={(event) => setDailyTaskPriorityFilter(event.target.value)} style={{ ...selectInput, width: "100%", minWidth: 0 }}>
              <option value="all">All Priority</option>
              {CRM_FOLLOW_UP_PRIORITY_OPTIONS.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
            </select>
            <select value={dailyTaskStatusFilter} onChange={(event) => setDailyTaskStatusFilter(event.target.value)} style={{ ...selectInput, width: "100%", minWidth: 0 }}>
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="overdue">Overdue</option>
              <option value="completed">Completed</option>
            </select>
            <select value={dailyTaskSourceFilter} onChange={(event) => setDailyTaskSourceFilter(event.target.value)} style={{ ...selectInput, width: "100%", minWidth: 0 }}>
              <option value="all">All Sources</option>
              {dailyTaskSourceOptions.map((source) => <option key={source} value={source}>{source}</option>)}
            </select>
            <select value={dailyTaskQuoteTypeFilter} onChange={(event) => setDailyTaskQuoteTypeFilter(event.target.value)} style={{ ...selectInput, width: "100%", minWidth: 0 }}>
              <option value="all">All Quote Types</option>
              <option value={CURTAIN_QUOTE_TYPE}>Curtains</option>
              <option value={GENERAL_QUOTE_TYPE}>General</option>
            </select>
          </div>
          {deferredDailyTaskSearch.trim() ? (
            <div style={{ fontSize: 12, color: dashboardMuted }}>
              Search hydrates matching CRM records from all leads, then filters tasks for the selected date/range.
              {globalSearchStatus.todayTaskSearch?.loading ? " Searching..." : ""}
              {globalSearchStatus.todayTaskSearch?.error ? (
                <span style={{ color: "#dc2626", fontWeight: 800 }}>
                  {" "}{globalSearchStatus.todayTaskSearch.error}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        {filteredDailyTasks.length === 0 ? (
          <div style={{ ...dashboardCardStyle, textAlign: "center", padding: isCompact ? 26 : 42, display: "grid", gap: 10 }}>
            <div style={{ fontSize: 22, fontWeight: 900 }}>Nothing scheduled for this day</div>
            <div style={{ fontSize: 13, color: dashboardMuted, lineHeight: 1.7 }}>Your follow-ups and installations will appear here.</div>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 1.35fr) minmax(300px, 0.65fr)", gap: 16, alignItems: "start" }}>
            <div style={{ display: "grid", gap: 16 }}>
              {groupDefinitions.filter(([, , , tasks]) => tasks.length > 0).map(([key, title, helper, tasks, accent]) => (
                <section key={key} style={{ ...dashboardCardStyle, display: "grid", gap: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                    <div>
                      <div style={{ fontSize: 18, fontWeight: 900, color: accent }}>{title}</div>
                      <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>{helper}</div>
                    </div>
                    <div style={{ padding: "6px 10px", borderRadius: 999, background: `${accent}14`, color: accent, fontSize: 12, fontWeight: 900 }}>{tasks.length}</div>
                  </div>
                  <div style={{ display: "grid", gap: 10 }}>{tasks.map(renderTaskCard)}</div>
                </section>
              ))}
            </div>

            <aside style={{ ...dashboardCardStyle, position: isCompact ? "static" : "sticky", top: 16, display: "grid", gap: 12 }}>
              {selectedDailyTask ? (
                <>
                  <div style={{ fontSize: 12, color: "#2563eb", fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase" }}>Selected Task</div>
                  <div style={{ fontSize: 22, fontWeight: 900 }}>{selectedDailyTask.title}</div>
                  <div style={{ fontSize: 13, color: dashboardMuted, lineHeight: 1.7 }}>
                    {selectedDailyTask.customerName}{selectedDailyTask.quoteNo ? ` | Quote #${selectedDailyTask.quoteNo}` : ""}
                  </div>
                  <div style={{ display: "grid", gap: 8 }}>
                    {[
                      ["Type", selectedDailyTask.typeLabel],
                      ["Due", formatReminderDateTime(selectedDailyTask.dueDateTime || selectedDailyTask.dueDate)],
                      ["Assigned", selectedDailyTask.assignedTo || "Unassigned"],
                      ["Source", selectedDailyTask.source || "-"],
                      ["Stage", selectedDailyTask.stageLabel || "-"],
                      ["Location", selectedDailyTask.locationLabel || selectedDailyTask.locationAddress || "-"],
                    ].map(([label, value]) => (
                      <div key={label} style={{ padding: 12, borderRadius: 14, background: dashboardSoftBg, border: dashboardBorder, display: "flex", justifyContent: "space-between", gap: 12, fontSize: 12 }}>
                        <span style={{ color: dashboardMuted, fontWeight: 700 }}>{label}</span>
                        <span style={{ fontWeight: 800, textAlign: "right" }}>{value}</span>
                      </div>
                    ))}
                  </div>
                  {selectedDailyTask.description ? <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.7 }}>{selectedDailyTask.description}</div> : null}
                  <div style={{ display: "grid", gap: 8 }}>
                    <button type="button" onClick={() => openDailyTaskDeal(selectedDailyTask)} style={{ ...btn("#0f172a"), width: "100%", justifySelf: "stretch", marginRight: 0 }}>Open Deal</button>
                    {selectedDailyTask.locationUrl || selectedDailyTask.siteLocation ? (
                      <button type="button" onClick={() => openSiteLocation(selectedDailyTask.siteLocation || selectedDailyTask)} style={{ ...btn("#2563eb"), width: "100%", justifySelf: "stretch", marginRight: 0 }}>Navigate</button>
                    ) : null}
                    {selectedDailyTask.status !== "completed" ? (
                      <>
                        <button
                          type="button"
                          disabled={Boolean(markingDoneKey)}
                          onClick={() => markDailyTaskDone(selectedDailyTask)}
                          style={{
                            ...btn("#16a34a"),
                            width: "100%",
                            justifySelf: "stretch",
                            marginRight: 0,
                            opacity: markingDoneKey ? 0.7 : 1,
                          }}
                        >
                          {markingDoneKey ? "Saving..." : "Mark as Done"}
                        </button>
                        <button type="button" onClick={() => openDailyTaskReschedule(selectedDailyTask)} style={{ ...btn(isDark ? "#1e293b" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"), width: "100%", justifySelf: "stretch", marginRight: 0 }}>Snooze / Reschedule</button>
                      </>
                    ) : null}
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 13, color: dashboardMuted, lineHeight: 1.7 }}>Select a task to see more details and quick actions.</div>
              )}
            </aside>
          </div>
        )}
      </div>
    );
  };

  const renderDailyTaskRescheduleModal = () => {
    if (!dailyTaskRescheduleState?.task) return null;

    const task = dailyTaskRescheduleState.task;

    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 190, display: "grid", placeItems: "center", padding: 18 }}>
        <div style={{ ...dashboardCardStyle, width: "min(520px, 100%)", display: "grid", gap: 14, boxShadow: "0 28px 80px rgba(15,23,42,0.28)" }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 900 }}>Reschedule Task</div>
            <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>Update the original CRM source for {task.title}.</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "minmax(150px, 1fr) minmax(150px, 1fr)", gap: 12 }}>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 700 }}>
              Date
              <input type="date" value={dailyTaskRescheduleState.dueDate} onChange={(event) => setDailyTaskRescheduleState((prev) => prev ? { ...prev, dueDate: event.target.value } : prev)} style={{ ...input, width: "100%", minWidth: 0 }} />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, color: dashboardMuted, fontWeight: 700 }}>
              Time
              <input type="time" value={dailyTaskRescheduleState.dueTime} onChange={(event) => setDailyTaskRescheduleState((prev) => prev ? { ...prev, dueTime: event.target.value } : prev)} style={{ ...input, width: "100%", minWidth: 0 }} />
            </label>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
            <button type="button" onClick={() => setDailyTaskRescheduleState(null)} style={{ ...btn(isDark ? "#1e293b" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"), marginRight: 0 }}>Cancel</button>
            <button type="button" onClick={saveDailyTaskReschedule} style={{ ...btn("#2563eb"), marginRight: 0 }}>Save New Time</button>
          </div>
        </div>
      </div>
    );
  };

  const renderCrmReminderPopups = () => {
    if (!canAccessCrm || dueCrmReminders.length === 0) return null;

    const visibleReminders = dueCrmReminders.slice(0, 3);

    return (
      <div
        style={{
          position: "fixed",
          right: isCompact ? 12 : 20,
          bottom: isCompact ? 12 : 20,
          zIndex: 160,
          width: isCompact ? "calc(100vw - 24px)" : 380,
          maxWidth: "calc(100vw - 24px)",
          display: "grid",
          gap: 12,
        }}
        >
          {visibleReminders.map((reminder) => {
            const isFollowUpReminder =
              reminder.reminderType === CRM_REMINDER_TYPE_FOLLOW_UP;
            const headline =
              (isFollowUpReminder
                ? reminder.taskTitle
                : reminder.dealTitle || reminder.clientName) || "CRM Deal";
            const secondaryLine = [
              reminder.clientName || "",
              reminder.quoteNo ? `#${reminder.quoteNo}` : "",
            ]
              .filter(Boolean)
              .join(" | ");
            const repeatLabel = isFollowUpReminder
              ? CRM_FOLLOW_UP_REPEAT_OPTIONS.find(
                  (option) => option.key === reminder.repeatKey
                )?.label
              : CRM_REMINDER_REPEAT_OPTIONS.find(
                  (option) => option.key === reminder.repeatKey
                )?.label;

            return (
              <div
                key={reminder.id}
                style={{
                  ...dashboardCardStyle,
                  padding: 16,
                  borderRadius: 20,
                  background: isDark
                    ? "linear-gradient(135deg, rgba(15,23,42,0.96), rgba(30,41,59,0.96))"
                    : "linear-gradient(135deg, #ffffff, #eff6ff)",
                  border: "1px solid rgba(37,99,235,0.18)",
                  display: "grid",
                  gap: 12,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "flex-start",
                  }}
                >
                  <div style={{ display: "flex", gap: 10 }}>
                    <div
                      style={{
                        width: 42,
                        height: 42,
                        minWidth: 42,
                        borderRadius: 14,
                        display: "grid",
                        placeItems: "center",
                        background: isDark ? "rgba(37,99,235,0.18)" : "#dbeafe",
                        color: "#2563eb",
                      }}
                    >
                      <BellRing size={18} />
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 800,
                          color: "#2563eb",
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                        }}
                      >
                        {reminder.reminderLabel || "Reminder"}
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 900, marginTop: 4 }}>
                        {headline}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: dashboardMuted,
                          marginTop: 4,
                          lineHeight: 1.6,
                        }}
                      >
                        {secondaryLine || "Unknown client"}
                      </div>
                      {isFollowUpReminder && reminder.taskDescription ? (
                        <div
                          style={{
                            fontSize: 12,
                            color: dashboardMuted,
                            marginTop: 4,
                            lineHeight: 1.6,
                          }}
                        >
                          {reminder.taskDescription}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => dismissCrmReminder(reminder)}
                    style={{
                      width: 32,
                      height: 32,
                      minWidth: 32,
                      borderRadius: 10,
                      border: "none",
                      display: "grid",
                      placeItems: "center",
                      background: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                      color: textColor,
                      cursor: "pointer",
                    }}
                    title="Dismiss reminder"
                  >
                    <X size={15} />
                  </button>
                </div>

                <div
                  style={{
                    padding: 12,
                    borderRadius: 16,
                    background: isDark ? "rgba(255,255,255,0.04)" : "#ffffff",
                    border: dashboardBorder,
                    display: "grid",
                    gap: 6,
                  }}
                >
                  <div style={{ fontSize: 12, color: dashboardMuted, fontWeight: 700 }}>
                    Due Date & Time
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 800 }}>
                    {formatReminderDateTime(reminder.scheduledAt || reminder.nextTriggerAt)}
                  </div>
                  <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                    {reminder.repeatKey === "none"
                      ? "This reminder will stay active until you mark it done."
                      : `Repeats ${repeatLabel?.toLowerCase() || "daily"} until you mark it done.`}
                  </div>
                </div>

                {reminder.locationLabel || reminder.locationAddress || reminder.locationUrl ? (
                  <div
                    style={{
                      padding: 12,
                      borderRadius: 16,
                      background: isDark ? "rgba(255,255,255,0.04)" : "#ffffff",
                      border: dashboardBorder,
                      display: "grid",
                      gap: 8,
                    }}
                  >
                    <div style={{ fontSize: 12, color: dashboardMuted, fontWeight: 700 }}>
                      Site Location
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 800 }}>
                      {reminder.locationLabel || "Site"}
                    </div>
                    {reminder.locationAddress ? (
                      <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                        {reminder.locationAddress}
                      </div>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => openSiteLocation(reminder.siteLocation || reminder)}
                      style={{ ...btn("#2563eb"), marginRight: 0, justifySelf: "start" }}
                    >
                      Open Map
                    </button>
                  </div>
                ) : null}

                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => openReminderDeal(reminder)}
                    style={{ ...btn("#2563eb"), marginRight: 0 }}
                  >
                    Open Deal
                  </button>
                  <button
                    type="button"
                    onClick={() => dismissCrmReminder(reminder)}
                    style={{ ...btn("#0f172a"), marginRight: 0 }}
                  >
                    Close / Dismiss
                  </button>
                  <button
                    type="button"
                    disabled={Boolean(markingDoneKey)}
                    onClick={() => markCrmReminderDone(reminder)}
                    style={{
                      ...btn("#16a34a"),
                      marginRight: 0,
                      opacity: markingDoneKey ? 0.7 : 1,
                    }}
                  >
                    <CheckCircle2 size={14} style={{ marginRight: 6 }} />
                    {markingDoneKey ? "Saving..." : "Mark as Done"}
                  </button>
                </div>
              </div>
            );
          })}

        {dueCrmReminders.length > visibleReminders.length ? (
          <div
            style={{
              padding: "10px 14px",
              borderRadius: 16,
              background: isDark ? "rgba(15,23,42,0.92)" : "#ffffff",
              border: dashboardBorder,
              fontSize: 12,
              color: dashboardMuted,
            }}
          >
            {dueCrmReminders.length - visibleReminders.length} more reminder
            {dueCrmReminders.length - visibleReminders.length === 1 ? "" : "s"} waiting.
          </div>
        ) : null}
      </div>
    );
  };

  const renderCrmLeads = () => (
    <div
      style={{
        display: "grid",
        gap: 16,
        alignItems: "start",
      }}
    >
      <div style={{ display: "grid", gap: 16 }}>
        <div style={{ ...dashboardCardStyle, display: "grid", gap: 12 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>Leads</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                A clean lead-first workspace for customer follow-ups, assignment, and quote creation without mixing everything into the pipeline board.
              </div>
            </div>
            <button
              type="button"
              onClick={() => openNewCrmLeadModal(activeCrmPipelineType)}
              style={{ ...btn("#2563eb"), marginRight: 0 }}
            >
              + New Lead
            </button>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact
                ? "1fr"
                : "minmax(240px, 1.3fr) repeat(5, minmax(140px, 1fr))",
              gap: 12,
            }}
          >
            <input
              value={crmLeadSearch}
              onChange={(event) => setCrmLeadSearch(event.target.value)}
              style={{ ...input, width: "100%", minWidth: 0 }}
              placeholder="Search all leads by customer, phone, quote no, or source"
            />
            <select
              value={crmLeadSourceFilter}
              onChange={(event) => setCrmLeadSourceFilter(event.target.value)}
              style={{ ...selectInput, width: "100%", minWidth: 0 }}
            >
              <option value="all">All Sources</option>
              {crmLeadSourceOptions.map((source) => (
                <option key={source} value={source}>
                  {source}
                </option>
              ))}
            </select>
            <select
              value={crmLeadStageFilter}
              onChange={(event) => setCrmLeadStageFilter(event.target.value)}
              style={{ ...selectInput, width: "100%", minWidth: 0 }}
            >
              <option value="all">All Stages / Status</option>
              {crmLeadStageOptions.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
            <select
              value={crmLeadAssignedFilter}
              onChange={(event) => setCrmLeadAssignedFilter(event.target.value)}
              style={{ ...selectInput, width: "100%", minWidth: 0 }}
            >
              <option value="all">All Assignees</option>
              {crmLeadAssignedOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
            <input
              type="date"
              value={crmLeadFromDate}
              onChange={(event) => setCrmLeadFromDate(event.target.value)}
              style={{ ...input, width: "100%", minWidth: 0 }}
            />
            <input
              type="date"
              value={crmLeadToDate}
              onChange={(event) => setCrmLeadToDate(event.target.value)}
              style={{ ...input, width: "100%", minWidth: 0 }}
            />
          </div>
          {deferredCrmLeadSearch.trim() ? (
            <div style={{ fontSize: 12, color: dashboardMuted }}>
              Search checks all CRM lead records, including older leads.
              {globalSearchStatus.crmLeadSearch?.loading ? " Searching..." : ""}
              {globalSearchStatus.crmLeadSearch?.error ? (
                <span style={{ color: "#dc2626", fontWeight: 800 }}>
                  {" "}{globalSearchStatus.crmLeadSearch.error}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        <div style={{ ...dashboardCardStyle, display: "grid", gap: 12 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div style={{ fontSize: 13, color: dashboardMuted }}>
              Showing {visibleCrmLeadDeals.length} of {crmFilteredLeadDeals.length} lead
              {crmFilteredLeadDeals.length === 1 ? "" : "s"}
            </div>
            <div style={{ fontSize: 12, color: dashboardMuted }}>
              Click a lead to open the details panel and keep follow-ups moving.
            </div>
          </div>

          {crmFilteredLeadDeals.length === 0 ? (
            <div
              style={{
                padding: 22,
                borderRadius: 20,
                background: dashboardSoftBg,
                border: dashboardBorder,
                display: "grid",
                gap: 8,
                justifyItems: "start",
              }}
            >
              <div style={{ fontSize: 16, fontWeight: 800 }}>No leads match these filters</div>
              <div style={{ fontSize: 13, color: dashboardMuted, lineHeight: 1.7 }}>
                Try widening the date range or clearing a filter to bring more leads back into view.
              </div>
            </div>
          ) : (
            visibleCrmLeadDeals.map((cardRecord) => {
              const deal = getLatestCrmLeadForCard(cardRecord);
              const active = selectedCrmDealId === deal.id;
              const stageColor = deal.crmStageColor || "#64748b";
              const checklistSummary = getChecklistSummary(deal);
              const followUpSummary = getFollowUpSummary(deal, { nowMillis: Date.now() });
              const installationSummary = getInstallationSummary(deal);
              const hasOverdueTasks = Number(followUpSummary.overdue || 0) > 0;

              return (
                <div
                  key={deal.id}
                  onClick={() => {
                    openCrmDealDetails(deal, { crmView: "leads" });
                  }}
                  style={{
                    padding: 16,
                    borderRadius: 20,
                    border: active
                      ? `1px solid ${isDark ? "rgba(96,165,250,0.6)" : "#93c5fd"}`
                      : dashboardBorder,
                    background: active
                      ? isDark
                        ? "rgba(37,99,235,0.1)"
                        : "#eff6ff"
                      : dashboardSoftBg,
                    display: "grid",
                    gap: 14,
                    cursor: "pointer",
                    transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
                    boxShadow: active
                      ? isDark
                        ? "0 18px 32px rgba(2,6,23,0.35)"
                        : "0 18px 32px rgba(37,99,235,0.08)"
                      : "none",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 14,
                      flexDirection: isCompact ? "column" : "row",
                    }}
                  >
                    <div style={{ display: "grid", gap: 6 }}>
                      <div style={{ fontSize: 18, fontWeight: 900 }}>
                        {deal.customerLabel || deal.crmTitle}
                      </div>
                      <div style={{ fontSize: 13, color: dashboardMuted, lineHeight: 1.6 }}>
                        {deal.crmTitle}
                        {deal.companyOrProject ? ` | ${deal.companyOrProject}` : ""}
                      </div>
                      <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                        {[deal.crmContact?.phone, deal.crmContact?.email]
                          .filter(Boolean)
                          .join(" | ") || "No phone or email added yet"}
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-start" }}>
                      <div
                        style={{
                          padding: "7px 11px",
                          borderRadius: 999,
                          background: `${stageColor}18`,
                          color: stageColor,
                          fontSize: 11,
                          fontWeight: 800,
                        }}
                      >
                        {deal.crmStageLabel}
                      </div>
                      <div
                        style={{
                          padding: "7px 11px",
                          borderRadius: 999,
                          background: isDark ? "rgba(148,163,184,0.18)" : "#e2e8f0",
                          color: isDark ? "#e2e8f0" : "#334155",
                          fontSize: 11,
                          fontWeight: 800,
                        }}
                      >
                        {deal.source || "No Source"}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isCompact
                        ? "1fr"
                        : "repeat(auto-fit, minmax(130px, 1fr))",
                      gap: 12,
                    }}
                    >
                      {[
                        ["Assigned", getTeamMemberLabel(deal.assignedToUserId, deal.assignedToNameSnapshot)],
                        ["Next Action", followUpSummary.nextFollowUpLabel || "Not scheduled"],
                        ["Checklist", checklistSummary.label],
                        [
                          "Install",
                          installationSummary.installationLabel || "-",
                        ],
                        ["Last Updated", deal.updatedMillis ? new Date(deal.updatedMillis).toLocaleString() : "-"],
                      ].map(([label, value]) => (
                        <div
                          key={label}
                        style={{
                          padding: 12,
                          borderRadius: 16,
                          background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                          border: dashboardBorder,
                        }}
                      >
                        <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                          {label}
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 800, marginTop: 6 }}>
                          {value}
                        </div>
                      </div>
                    ))}
                    <div
                      style={{
                        padding: 12,
                        borderRadius: 16,
                        background: hasOverdueTasks
                          ? isDark
                            ? "rgba(127,29,29,0.2)"
                            : "#fff1f2"
                          : isDark
                          ? "rgba(255,255,255,0.03)"
                          : "#ffffff",
                        border: hasOverdueTasks
                          ? `1px solid ${isDark ? "rgba(248,113,113,0.42)" : "#fecdd3"}`
                          : dashboardBorder,
                      }}
                    >
                      <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                        Tasks
                      </div>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 900,
                          marginTop: 6,
                          color: hasOverdueTasks ? (isDark ? "#fecdd3" : "#be123c") : textColor,
                        }}
                      >
                        {followUpSummary.label}
                      </div>
                    </div>
                    <div
                      style={{
                        padding: 12,
                        borderRadius: 16,
                        background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                        border: dashboardBorder,
                        display: "grid",
                        gap: 8,
                        alignContent: "space-between",
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                          Linked Quote
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 800, marginTop: 6 }}>
                          {deal.hasQuote
                            ? `#${deal.primaryLinkedQuote?.quoteNo || deal.quoteNo || "-"}`
                            : "No quote yet"}
                        </div>
                      </div>
                      {deal.crmRecordType === "lead" ? (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            openQuoteLinkModal(deal);
                          }}
                          style={{
                            ...btn(deal.hasQuote ? "#0f172a" : "#2563eb"),
                            marginRight: 0,
                            justifySelf: "start",
                            padding: "7px 10px",
                          }}
                        >
                          <Link2 size={13} style={{ marginRight: 6 }} />
                          {deal.hasQuote ? "Change" : "Link Quote"}
                        </button>
                      ) : null}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      flexDirection: isCompact ? "column" : "row",
                      alignItems: isCompact ? "stretch" : "center",
                    }}
                  >
                    <div style={{ fontSize: 13, color: dashboardMuted }}>
                      Value: {currencySymbol(currency)} {Number(deal.expectedValue || 0).toFixed(2)}
                    </div>

                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <select
                        value={deal.assignedToUserId || ""}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) => {
                          event.stopPropagation();
                          updateCrmDealAssignment(deal.id, event.target.value);
                        }}
                        style={{
                          ...selectInput,
                          minWidth: 170,
                        }}
                      >
                        <option value="">Assign user</option>
                        {getAssignableTeamMembers(deal.assignedToUserId).map((member) => (
                          <option key={member.id} value={member.id}>
                            {member.name || "Unnamed Team Member"}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          openCrmDealDetails(deal, { crmView: "leads" });
                        }}
                        style={{ ...btn(isDark ? "#1e293b" : "#0f172a", "#fff"), marginRight: 0 }}
                      >
                        Details
                      </button>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          openCrmDealDetails(deal, { crmView: "leads" });
                        }}
                        style={{ ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"), marginRight: 0 }}
                      >
                        Mark Follow-up
                      </button>
                      {(deal.hasQuote ? canViewQuotes : canCreateQuotes) ? (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            if (deal.hasQuote) {
                              openCrmQuoteForDeal(deal);
                            } else {
                              startQuoteFromCrmLead(deal);
                            }
                          }}
                          style={{ ...btn("#2563eb"), marginRight: 0 }}
                        >
                          {deal.hasQuote ? "Open Quote" : "Create Quote"}
                        </button>
                      ) : null}
                      {deal.crmRecordType === "lead" && deal.hasQuote ? (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            openQuoteLinkModal(deal);
                          }}
                          style={{ ...btn("#0f172a"), marginRight: 0 }}
                        >
                          Change Quote
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })
          )}

          {hasMoreCrmLeadDeals ? (
            <div style={{ display: "flex", justifyContent: "center", paddingTop: 4 }}>
              <button
                type="button"
                onClick={() =>
                  setCrmLeadsVisibleCount((prev) => prev + CRM_LEADS_PAGE_SIZE)
                }
                style={{ ...btn("#2563eb"), marginRight: 0 }}
              >
                Load More Leads
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );

  const renderCustomerLedger = () => {
    const selected = selectedCustomerLedger;
    const selectedQuotes = selected?.quotes || [];
    const selectedLeads = selected?.leads || [];
    const selectedFollowUps = selectedLeads.flatMap((lead) =>
      normalizeCrmFollowUps(lead.followUps).map((followUp) => ({
        ...followUp,
        lead,
      }))
    );
    const selectedInstallations = selectedLeads.filter((lead) => lead.installationDate);
    const selectedSiteLocations = normalizeSiteLocations(selected?.siteLocations || []);
    const selectedLeadSiteLocations = selectedLeads.flatMap((lead) =>
      normalizeSiteLocations(lead.siteLocations || []).map((location) => ({
        ...location,
        leadId: lead.id,
        leadTitle: lead.title || lead.customerName || "CRM Deal",
      }))
    );
    const detailTabs = [
      { key: "overview", label: "Overview" },
      { key: "quotations", label: "Quotations" },
      { key: "deals", label: "CRM Deals" },
      { key: "tasks", label: "Follow-ups" },
      { key: "locations", label: "Locations" },
      { key: "installations", label: "Installations" },
      { key: "payments", label: "Payments" },
      { key: "notes", label: "Notes & Activity" },
    ];
    const statCards = selected
      ? [
          ["Total Quotations", selected.totals?.totalQuotes || 0],
          [
            "Total Quoted Value",
            `${currencySymbol(currency)} ${Number(selected.totals?.totalQuoteValue || 0).toFixed(2)}`,
          ],
          ["Confirmed Quotes", selected.totals?.confirmedQuotes || 0],
          ["Open Deals", selected.openDealsCount || 0],
          ["Completed Jobs", selected.completedJobsCount || 0],
          ["Pending Follow-ups", selected.pendingFollowUpsCount || 0],
        ]
      : [];

    const createQuoteForCustomer = (quoteType = GENERAL_QUOTE_TYPE) => {
      if (!selected) return;
      if (quoteType === GENERAL_QUOTE_TYPE && canAccessGeneralQuotes) {
        setGeneralQuote((prev) =>
          normalizeGeneralQuote({
            ...prev,
            customerId: selected.id || "",
            customerKey: selected.customerKey || selected.id || "",
            customerName: selected.customerName || "",
            customerPhone: selected.phone || selected.whatsapp || "",
            customerWhatsapp: selected.whatsapp || "",
            customerEmail: selected.email || "",
            customerAddress: selected.address || "",
            customerCompanyName: selected.companyName || "",
            customerLedgerLinked: Boolean(selected.id),
            quoteFor: {
              ...(prev.quoteFor || {}),
              name: selected.customerName || "",
              company: selected.companyName || "",
              phone: selected.phone || selected.whatsapp || "",
              email: selected.email || "",
              address: selected.address || "",
            },
          })
        );
        setQuoteSubTab(GENERAL_QUOTE_TYPE);
        setRoutedTab("quote", GENERAL_QUOTE_TYPE);
        return;
      }

      setCustomerName(selected.customerName || "");
      setQuoteCustomerLink({
        customerId: selected.id || "",
        customerKey: selected.customerKey || selected.id || "",
        customerName: selected.customerName || "",
        phone: selected.phone || "",
        whatsapp: selected.whatsapp || "",
        email: selected.email || "",
        address: selected.address || "",
        companyName: selected.companyName || "",
      });
      setQuoteSubTab(CURTAIN_QUOTE_TYPE);
      setRoutedTab("quote", CURTAIN_QUOTE_TYPE);
    };

    const createLeadForCustomer = () => {
      if (!selected) return;
      const nextDraft = createCrmLeadDraftState(GENERAL_QUOTE_TYPE);
      setCrmLeadDraft({
        ...nextDraft,
        customerId: selected.id || "",
        customerKey: selected.customerKey || selected.id || "",
        customerName: selected.customerName || "",
        customerPhone: selected.phone || selected.whatsapp || "",
        customerWhatsapp: selected.whatsapp || "",
        customerEmail: selected.email || "",
        customerAddress: selected.address || "",
        customerCompanyName: selected.companyName || "",
        title: selected.companyName || selected.customerName || "",
        companyOrProject: selected.companyName || "",
        source: selected.source || "",
        tags: (selected.tags || []).join(", "),
        contact: {
          ...nextDraft.contact,
          name: selected.customerName || "",
          phone: selected.phone || "",
          whatsapp: selected.whatsapp || "",
          email: selected.email || "",
          address: selected.address || "",
        },
        siteLocations: normalizeSiteLocations(selected.siteLocations || []),
      });
      setIsCrmLeadModalOpen(true);
    };

    const createLeadForCustomerLocation = (location) => {
      if (!selected) return;
      const nextDraft = createCrmLeadDraftState(GENERAL_QUOTE_TYPE);
      setCrmLeadDraft({
        ...nextDraft,
        customerId: selected.id || "",
        customerKey: selected.customerKey || selected.id || "",
        customerName: selected.customerName || "",
        customerPhone: selected.phone || selected.whatsapp || "",
        customerWhatsapp: selected.whatsapp || "",
        customerEmail: selected.email || "",
        customerAddress: location.addressText || selected.address || "",
        customerCompanyName: selected.companyName || "",
        title: selected.companyName || selected.customerName || location.label || "",
        companyOrProject: selected.companyName || "",
        source: selected.source || "",
        tags: (selected.tags || []).join(", "),
        contact: {
          ...nextDraft.contact,
          name: selected.customerName || "",
          phone: selected.phone || "",
          whatsapp: selected.whatsapp || "",
          email: selected.email || "",
          address: location.addressText || selected.address || "",
        },
        siteLocations: normalizeSiteLocations([{ ...location, isPrimary: true }]),
      });
      setIsCrmLeadModalOpen(true);
    };

    const saveCustomerLocationDraft = async () => {
      if (!selected?.id) return;
      const hasLocationContent = [
        customerLocationDraft.label,
        customerLocationDraft.addressText,
        customerLocationDraft.googleMapsUrl,
        customerLocationDraft.notes,
      ].some((value) => String(value || "").trim());

      if (!hasLocationContent) {
        notify("Add a label, address, or map link before saving.", "warning");
        return;
      }

      const nextLocation = normalizeSiteLocation({
        ...customerLocationDraft,
        id: customerLocationDraft.id || createClientId("customer-site-location"),
        createdAt: customerLocationDraft.createdAt || new Date().toISOString(),
        createdBy: customerLocationDraft.createdBy || user?.uid || user?.email || "",
        updatedAt: new Date().toISOString(),
        updatedBy: user?.uid || user?.email || "",
      });
      const nextLocations = selectedSiteLocations.some(
        (location) => location.id === nextLocation.id
      )
        ? selectedSiteLocations.map((location) =>
            location.id === nextLocation.id
              ? nextLocation
              : nextLocation.isPrimary
              ? { ...location, isPrimary: false }
              : location
          )
        : [
            ...selectedSiteLocations.map((location) =>
              nextLocation.isPrimary ? { ...location, isPrimary: false } : location
            ),
            nextLocation,
          ];
      const saved = await saveCustomerSiteLocations(selected.id, nextLocations);
      if (saved) {
        notify("Site location saved", "success");
        setCustomerLocationDraft(createSiteLocationDraft());
      }
    };

    const editCustomerLocation = (location) => {
      setCustomerLocationDraft(createSiteLocationDraft(location));
      setCustomerLedgerDetailTab("locations");
    };

    const deleteCustomerLocation = async (locationId) => {
      if (!selected?.id) return;
      const saved = await saveCustomerSiteLocations(
        selected.id,
        selectedSiteLocations.filter((location) => location.id !== locationId)
      );
      if (saved) notify("Site location deleted", "success");
    };

    const copyLeadLocationToCustomer = async (location) => {
      if (!selected?.id) return;
      const saved = await saveCustomerSiteLocations(selected.id, [
        ...selectedSiteLocations,
        normalizeSiteLocation({
          ...location,
          id: createClientId("customer-site-location"),
          isPrimary: selectedSiteLocations.length === 0 || location.isPrimary === true,
          createdAt: new Date().toISOString(),
          createdBy: user?.uid || user?.email || "",
          updatedAt: new Date().toISOString(),
          updatedBy: user?.uid || user?.email || "",
        }),
      ]);
      if (saved) notify("Location saved to customer ledger", "success");
    };

    const createOrLinkDealFromQuote = async (quote, mode = "link") => {
      if (!quote?.id || !selected) return;
      try {
        await ensureSavedQuoteCrmDeal(
          {
            ...quote,
            customerId: selected.id,
            customerKey: selected.customerKey || selected.id || "",
            customerName: selected.customerName,
            customerPhone: selected.phone || selected.whatsapp || "",
            customerWhatsapp: selected.whatsapp || "",
            customerEmail: selected.email || "",
            customerAddress: selected.address || "",
            customerCompanyName: selected.companyName || "",
          },
          {
            quoteNo: quote.quoteNo || quote.id,
            quoteType: resolveQuoteType(quote),
            totalValue: Number(getNormalizedQuoteTotal(quote) || 0),
            customerFields: {
              customerId: selected.id,
              customerKey: selected.customerKey || selected.id || "",
              customerName: selected.customerName,
              customerPhone: selected.phone || selected.whatsapp || "",
              customerWhatsapp: selected.whatsapp || "",
              customerEmail: selected.email || "",
              customerAddress: selected.address || "",
              customerCompanyName: selected.companyName || "",
            },
            forceCreate: mode === "create",
            forceLinkExisting: mode === "link",
            respectSaveQuoteOnly: false,
            showToast: true,
          }
        );
        setCustomerLedgerDetailTab("deals");
        Object.keys(customerLedgerCrmDealsLoadRef.current || {}).forEach((key) => {
          if (key.includes(`:${selected.id}:`)) {
            delete customerLedgerCrmDealsLoadRef.current[key];
          }
        });
        await loadCustomerLedgerCrmDeals(selected.id);
      } catch (error) {
        console.error("Failed to create or link CRM deal from quote:", error);
        notify("Could not create CRM deal from quote. Please try again.", "error");
      }
    };

    return (
      <div style={{ display: "grid", gap: 18 }}>
        <div
          style={{
            ...dashboardCardStyle,
            background: isDark
              ? "linear-gradient(135deg, rgba(15,23,42,0.98), rgba(30,41,59,0.94))"
              : "linear-gradient(135deg, #f8fafc, #ffffff)",
            display: "grid",
            gap: 16,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 14,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#2563eb" }}>
                CRM Customer Ledger
              </div>
              <div style={{ fontSize: isCompact ? 24 : 32, fontWeight: 900, letterSpacing: "-0.04em", marginTop: 6 }}>
                One customer, every quote and job history.
              </div>
              <div style={{ fontSize: 13, color: dashboardMuted, marginTop: 8, lineHeight: 1.7 }}>
                Permanent customer records connect quotations, CRM deals, tasks, installations, payments, and activity without creating duplicate leads for repeat customers.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button type="button" onClick={() => selected && createQuoteForCustomer(GENERAL_QUOTE_TYPE)} style={{ ...btn("#2563eb"), marginRight: 0 }}>
                Create Quote
              </button>
              <button type="button" onClick={createLeadForCustomer} style={{ ...btn("#16a34a"), marginRight: 0 }}>
                Create Lead
              </button>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact ? "1fr" : "minmax(220px, 1fr) 180px 180px",
              gap: 10,
            }}
          >
            <input
              value={customerLedgerSearch}
              onChange={(event) => setCustomerLedgerSearch(event.target.value)}
              style={input}
              placeholder="Search all customers by name, phone, email, source, tag, or location"
            />
            <select
              value={customerLedgerSourceFilter}
              onChange={(event) => setCustomerLedgerSourceFilter(event.target.value)}
              style={selectInput}
            >
              <option value="all">All Sources</option>
              {customerLedgerSourceOptions.map((source) => (
                <option key={source} value={source}>
                  {source}
                </option>
              ))}
            </select>
            <select
              value={customerLedgerSortBy}
              onChange={(event) => setCustomerLedgerSortBy(event.target.value)}
              style={selectInput}
            >
              <option value="latest">Latest activity</option>
              <option value="value">Highest value</option>
              <option value="quotes">Most quotes</option>
              <option value="name">Name</option>
            </select>
          </div>
          {deferredCustomerLedgerSearch.trim() ? (
            <div style={{ fontSize: 12, color: dashboardMuted }}>
              Search checks all customer ledger records, including older customers.
              {globalSearchStatus.customerLedgerSearch?.loading ? " Searching..." : ""}
              {globalSearchStatus.customerLedgerSearch?.error ? (
                <span style={{ color: "#dc2626", fontWeight: 800 }}>
                  {" "}{globalSearchStatus.customerLedgerSearch.error}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact ? "1fr" : "minmax(280px, 0.85fr) minmax(0, 1.6fr)",
            gap: 16,
            alignItems: "start",
          }}
        >
          <div style={{ display: "grid", gap: 10, minWidth: 0 }}>
            {filteredCustomerLedgerSummaries.length === 0 ? (
              <div style={{ ...dashboardCardStyle, color: dashboardMuted }}>
                No customers yet. Create a quotation or lead to start building your customer ledger.
              </div>
            ) : (
              filteredCustomerLedgerSummaries.map((customer) => (
                <button
                  key={customer.id}
                  type="button"
                  onClick={() => {
                    setSelectedCustomerLedgerId(customer.id);
                    setCustomerLedgerDetailTab("overview");
                    loadCustomerLedgerQuotes(customer.id);
                    loadCustomerLedgerCrmDeals(customer.id);
                  }}
                  style={{
                    ...dashboardCardStyle,
                    textAlign: "left",
                    cursor: "pointer",
                    border:
                      customer.id === selected?.id
                        ? "1px solid rgba(37,99,235,0.45)"
                        : dashboardBorder,
                    boxShadow:
                      customer.id === selected?.id
                        ? "0 18px 42px rgba(37,99,235,0.12)"
                        : dashboardCardStyle.boxShadow,
                    display: "grid",
                    gap: 10,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 16, fontWeight: 900, overflowWrap: "anywhere" }}>
                        {customer.customerName}
                      </div>
                      <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, overflowWrap: "anywhere" }}>
                        {[customer.phone || customer.whatsapp, customer.email, customer.address].filter(Boolean).join(" | ") || "No contact details yet"}
                      </div>
                    </div>
                    {customer.duplicateWarning ? (
                      <span style={{ fontSize: 11, fontWeight: 800, color: "#b45309", background: "#fef3c7", borderRadius: 999, padding: "4px 8px", alignSelf: "flex-start" }}>
                        Possible duplicate
                      </span>
                    ) : null}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8 }}>
                    {[
                      ["Quotes", customer.totals?.totalQuotes || 0],
                      ["Value", `${currencySymbol(currency)} ${Number(customer.totals?.totalQuoteValue || 0).toFixed(0)}`],
                      ["Open Deals", customer.openDealsCount || 0],
                    ].map(([label, value]) => (
                      <div key={label} style={{ padding: 10, borderRadius: 14, background: dashboardSoftBg, border: dashboardBorder, minWidth: 0 }}>
                        <div style={{ fontSize: 11, color: dashboardMuted }}>{label}</div>
                        <div style={{ fontSize: 14, fontWeight: 900, overflowWrap: "anywhere" }}>{value}</div>
                      </div>
                    ))}
                  </div>
                </button>
              ))
            )}
          </div>

          <div style={{ minWidth: 0 }}>
            {!selected ? (
              <div style={{ ...dashboardCardStyle, color: dashboardMuted }}>
                Select a customer to view the full ledger.
              </div>
            ) : (
              <div style={{ display: "grid", gap: 14 }}>
                <div style={{ ...dashboardCardStyle, display: "grid", gap: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexDirection: isCompact ? "column" : "row" }}>
                    <div>
                      <div style={{ fontSize: 26, fontWeight: 900, letterSpacing: "-0.04em" }}>
                        {selected.customerName}
                      </div>
                      <div style={{ fontSize: 13, color: dashboardMuted, marginTop: 6, lineHeight: 1.7 }}>
                        {[selected.phone || selected.whatsapp, selected.email, selected.address].filter(Boolean).join(" | ") || "No contact details yet"}
                      </div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
                        {(selected.tags || []).map((tag) => (
                          <span key={tag} style={{ padding: "4px 9px", borderRadius: 999, background: dashboardSoftBg, border: dashboardBorder, fontSize: 11, fontWeight: 800 }}>
                            {tag}
                          </span>
                        ))}
                        {selected.source ? (
                          <span style={{ padding: "4px 9px", borderRadius: 999, background: "#eff6ff", color: "#1d4ed8", fontSize: 11, fontWeight: 800 }}>
                            {selected.source}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignSelf: isCompact ? "stretch" : "flex-start" }}>
                      <button type="button" onClick={() => createQuoteForCustomer(GENERAL_QUOTE_TYPE)} style={{ ...btn("#2563eb"), marginRight: 0 }}>
                        General Quote
                      </button>
                      <button type="button" onClick={() => createQuoteForCustomer(CURTAIN_QUOTE_TYPE)} style={{ ...btn("#0f172a"), marginRight: 0 }}>
                        Curtain Quote
                      </button>
                      <button type="button" onClick={createLeadForCustomer} style={{ ...btn("#16a34a"), marginRight: 0 }}>
                        Create Lead
                      </button>
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr 1fr" : "repeat(6, minmax(0, 1fr))", gap: 10 }}>
                    {statCards.map(([label, value]) => (
                      <div key={label} style={{ padding: 14, borderRadius: 18, background: dashboardSoftBg, border: dashboardBorder, minWidth: 0 }}>
                        <div style={{ fontSize: 11, color: dashboardMuted }}>{label}</div>
                        <div style={{ fontSize: 18, fontWeight: 900, marginTop: 5, overflowWrap: "anywhere" }}>{value}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {detailTabs.map((detailTab) => (
                      <button
                        key={detailTab.key}
                        type="button"
                        onClick={() => {
                          setCustomerLedgerDetailTab(detailTab.key);
                          if (detailTab.key === "quotations") {
                            loadCustomerLedgerQuotes(selected.id);
                          }
                          if (["deals", "tasks", "installations"].includes(detailTab.key)) {
                            loadCustomerLedgerCrmDeals(selected.id);
                          }
                        }}
                        style={{
                          ...btn(
                            customerLedgerDetailTab === detailTab.key ? "#2563eb" : isDark ? "#0f172a" : "#e2e8f0",
                            customerLedgerDetailTab === detailTab.key || isDark ? "#fff" : "#0f172a"
                          ),
                          marginRight: 0,
                          padding: "8px 11px",
                        }}
                      >
                        {detailTab.label}
                      </button>
                    ))}
                  </div>
                </div>

                {customerLedgerDetailTab === "overview" ? (
                  <div style={{ ...dashboardCardStyle, display: "grid", gap: 10 }}>
                    <div style={{ fontSize: 17, fontWeight: 900 }}>Customer Timeline</div>
                    {[...selectedQuotes, ...selectedLeads]
                      .sort((a, b) => safeTimeMillis(b.updatedAt || b.createdAt) - safeTimeMillis(a.updatedAt || a.createdAt))
                      .slice(0, 12)
                      .map((record) => (
                        <div key={`${record.quoteNo || record.id}-${record.crmRecordType || "record"}`} style={{ padding: 12, borderRadius: 16, background: dashboardSoftBg, border: dashboardBorder }}>
                          <div style={{ fontWeight: 800 }}>
                            {record.quoteNo ? `Quote #${record.quoteNo}` : record.title || record.crmTitle || "CRM Deal"}
                          </div>
                          <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                            {record.quoteNo ? getQuoteTypeLabel(resolveQuoteType(record)) : record.stageNameSnapshot || record.status || "Lead"} | {new Date(safeTimeMillis(record.updatedAt || record.createdAt) || Date.now()).toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                  </div>
                ) : null}

                {customerLedgerDetailTab === "quotations" ? (
                  <div style={{ ...dashboardCardStyle, display: "grid", gap: 10 }}>
                    <div style={{ fontSize: 17, fontWeight: 900 }}>Quotations</div>
                    {customerLedgerQuotesLoadingId === selected.id ? (
                      <div style={{ color: dashboardMuted }}>Loading quotations for this customer...</div>
                    ) : null}
                    {selectedQuotes.length === 0 && customerLedgerQuotesLoadingId !== selected.id ? (
                      <div style={{ color: dashboardMuted }}>No linked quotations yet.</div>
                    ) : selectedQuotes.map((quote) => (
                      <div key={quote.id} style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: 12, borderRadius: 16, background: dashboardSoftBg, border: dashboardBorder }}>
                        <div>
                          <div style={{ fontWeight: 900 }}>#{quote.quoteNo || quote.id}</div>
                          <div style={{ fontSize: 12, color: dashboardMuted }}>
                            {getQuoteTypeLabel(resolveQuoteType(quote))} | {resolveQuoteDateForInput(quote) || toDateInputValue(quote.createdAt) || "-"} | {quote.status || "draft"}
                          </div>
                          <div style={{ fontSize: 11, color: quote.crmLeadId || quote.leadId || quote.crm?.leadId ? "#16a34a" : "#f97316", fontWeight: 800, marginTop: 5 }}>
                            {quote.crmLeadId || quote.leadId || quote.crm?.leadId ? "Linked to CRM deal" : "Quote only - not in pipeline yet"}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                          <strong>{currencySymbol(currency)} {Number(getNormalizedQuoteTotal(quote) || 0).toFixed(2)}</strong>
                          <button type="button" onClick={() => loadFullQuote(quote)} style={{ ...btn("#2563eb"), marginRight: 0, padding: "8px 10px" }}>
                            Open Quote
                          </button>
                          {!(quote.crmLeadId || quote.leadId || quote.crm?.leadId) ? (
                            <>
                              <button
                                type="button"
                                onClick={() => createOrLinkDealFromQuote(quote, "link")}
                                style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 10px" }}
                              >
                                Link to Deal
                              </button>
                              <button
                                type="button"
                                onClick={() => createOrLinkDealFromQuote(quote, "create")}
                                style={{ ...btn("#16a34a"), marginRight: 0, padding: "8px 10px" }}
                              >
                                Create CRM Deal
                              </button>
                            </>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}

                {customerLedgerDetailTab === "deals" ? (
                  <div style={{ ...dashboardCardStyle, display: "grid", gap: 10 }}>
                    <div style={{ fontSize: 17, fontWeight: 900 }}>CRM Deals</div>
                    {customerLedgerCrmDealsLoadingId === selected.id ? (
                      <div style={{ color: dashboardMuted }}>Loading CRM deals for this customer...</div>
                    ) : null}
                    {selectedLeads.length === 0 && customerLedgerCrmDealsLoadingId !== selected.id ? (
                      <div style={{ color: dashboardMuted }}>No CRM deals linked yet.</div>
                    ) : selectedLeads.map((leadRecord) => {
                      const lead = getLatestCrmLeadForCard(leadRecord);
                      const checklistSummary = getChecklistSummary(lead);
                      const followUpSummary = getFollowUpSummary(lead, { nowMillis: Date.now() });
                      const installationSummary = getInstallationSummary(lead);

                      return (
                        <div key={lead.id} style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: 12, borderRadius: 16, background: dashboardSoftBg, border: dashboardBorder }}>
                          <div style={{ display: "grid", gap: 5 }}>
                            <div style={{ fontWeight: 900 }}>{lead.title || lead.customerName || "CRM Lead"}</div>
                            <div style={{ fontSize: 12, color: dashboardMuted }}>
                              {getQuoteTypeLabel(lead.pipelineType)} | {lead.stageNameSnapshot || lead.status || "-"} | {lead.assignedToNameSnapshot || "Unassigned"}
                            </div>
                            <div style={{ fontSize: 12, color: dashboardMuted }}>
                              Checklist {checklistSummary.label} | Tasks {followUpSummary.label} | Install {installationSummary.installationLabel || "-"}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setRoutedTab("crm");
                              openCrmDealDetails(lead, { crmView: "leads" });
                            }}
                            style={{ ...btn("#2563eb"), marginRight: 0, padding: "8px 10px" }}
                          >
                            Open Details
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : null}

                {customerLedgerDetailTab === "tasks" ? (
                  <div style={{ ...dashboardCardStyle, display: "grid", gap: 10 }}>
                    <div style={{ fontSize: 17, fontWeight: 900 }}>Follow-ups / Tasks</div>
                    {selectedFollowUps.length === 0 ? (
                      <div style={{ color: dashboardMuted }}>No follow-ups yet.</div>
                    ) : selectedFollowUps.map((followUp) => (
                      <div key={`${followUp.lead.id}-${followUp.id}`} style={{ padding: 12, borderRadius: 16, background: dashboardSoftBg, border: dashboardBorder }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                          <strong>{followUp.title || "Follow-up"}</strong>
                          <span style={{ fontSize: 12, fontWeight: 800, color: followUp.status === CRM_FOLLOW_UP_STATUS_DONE ? "#16a34a" : "#f97316" }}>
                            {followUp.status === CRM_FOLLOW_UP_STATUS_DONE ? "Done" : "Pending"}
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 5 }}>
                          {followUp.lead.title || followUp.lead.customerName || "CRM Deal"} | Due {followUp.dueDate || "-"} {followUp.dueTime || ""}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}

                {customerLedgerDetailTab === "locations" ? (
                  <div style={{ ...dashboardCardStyle, display: "grid", gap: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexDirection: isCompact ? "column" : "row" }}>
                      <div>
                        <div style={{ fontSize: 17, fontWeight: 900 }}>Locations</div>
                        <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                          Customer-level site links stay available across future quotes, leads, and installations.
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setCustomerLocationDraft(createSiteLocationDraft())}
                        style={{ ...btn("#2563eb"), marginRight: 0 }}
                      >
                        Add Location
                      </button>
                    </div>

                    {selectedSiteLocations.length === 0 ? (
                      <div
                        style={{
                          padding: 16,
                          borderRadius: 18,
                          background: dashboardSoftBg,
                          border: dashboardBorder,
                          color: dashboardMuted,
                          lineHeight: 1.7,
                        }}
                      >
                        <strong style={{ color: textColor }}>No customer locations saved yet.</strong>
                        <br />
                        Add a location here, or copy one from a linked CRM deal below.
                      </div>
                    ) : (
                      <div style={{ display: "grid", gap: 10 }}>
                        {selectedSiteLocations.map((location) => (
                          <div
                            key={location.id}
                            style={{
                              padding: 14,
                              borderRadius: 18,
                              background: dashboardSoftBg,
                              border: dashboardBorder,
                              display: "grid",
                              gap: 10,
                            }}
                          >
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                              <div>
                                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                                  <strong>{location.label || "Site Location"}</strong>
                                  {location.isPrimary ? (
                                    <span style={{ padding: "4px 8px", borderRadius: 999, background: "#dcfce7", color: "#166534", fontSize: 11, fontWeight: 800 }}>
                                      Primary
                                    </span>
                                  ) : null}
                                </div>
                                {location.addressText ? (
                                  <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 5, lineHeight: 1.6 }}>
                                    {location.addressText}
                                  </div>
                                ) : null}
                                {location.notes ? (
                                  <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                                    Notes: {location.notes}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                              <button type="button" onClick={() => openSiteLocation(location)} style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 10px" }}>
                                Open Map
                              </button>
                              <button type="button" onClick={() => copySiteLocationLink(location)} style={{ ...btn("#2563eb"), marginRight: 0, padding: "8px 10px" }}>
                                Copy Link
                              </button>
                              <button type="button" onClick={() => editCustomerLocation(location)} style={{ ...btn("#64748b"), marginRight: 0, padding: "8px 10px" }}>
                                Edit
                              </button>
                              <button type="button" onClick={() => createLeadForCustomerLocation(location)} style={{ ...btn("#16a34a"), marginRight: 0, padding: "8px 10px" }}>
                                Create Lead Using This Location
                              </button>
                              <button type="button" onClick={() => deleteCustomerLocation(location.id)} style={{ ...btn("#ef4444"), marginRight: 0, padding: "8px 10px" }}>
                                Delete
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div
                      style={{
                        padding: 14,
                        borderRadius: 18,
                        background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                        border: dashboardBorder,
                        display: "grid",
                        gap: 10,
                      }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 850 }}>
                        {customerLocationDraft.id ? "Edit Customer Location" : "Add Customer Location"}
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 10 }}>
                        <input
                          value={customerLocationDraft.label}
                          onChange={(event) =>
                            setCustomerLocationDraft((prev) => ({
                              ...prev,
                              label: event.target.value,
                            }))
                          }
                          style={input}
                          placeholder="Location label"
                        />
                        <input
                          value={customerLocationDraft.googleMapsUrl}
                          onChange={(event) =>
                            setCustomerLocationDraft((prev) => ({
                              ...prev,
                              googleMapsUrl: event.target.value,
                            }))
                          }
                          style={input}
                          placeholder="Google Maps / WhatsApp location link"
                        />
                      </div>
                      <textarea
                        value={customerLocationDraft.addressText}
                        onChange={(event) =>
                          setCustomerLocationDraft((prev) => ({
                            ...prev,
                            addressText: event.target.value,
                          }))
                        }
                        style={{ ...input, minHeight: 72, resize: "vertical" }}
                        placeholder="Address / landmark"
                      />
                      <textarea
                        value={customerLocationDraft.notes}
                        onChange={(event) =>
                          setCustomerLocationDraft((prev) => ({
                            ...prev,
                            notes: event.target.value,
                          }))
                        }
                        style={{ ...input, minHeight: 62, resize: "vertical" }}
                        placeholder="Notes"
                      />
                      <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 800 }}>
                        <input
                          type="checkbox"
                          checked={customerLocationDraft.isPrimary === true}
                          onChange={(event) =>
                            setCustomerLocationDraft((prev) => ({
                              ...prev,
                              isPrimary: event.target.checked,
                            }))
                          }
                        />
                        Primary Location
                      </label>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button type="button" onClick={saveCustomerLocationDraft} style={{ ...btn("#2563eb"), marginRight: 0 }}>
                          Save Location
                        </button>
                        <button type="button" onClick={() => setCustomerLocationDraft(createSiteLocationDraft())} style={{ ...btn("#64748b"), marginRight: 0 }}>
                          Clear
                        </button>
                      </div>
                    </div>

                    {selectedLeadSiteLocations.length > 0 ? (
                      <div style={{ display: "grid", gap: 10 }}>
                        <div style={{ fontSize: 13, fontWeight: 850 }}>
                          Locations saved on linked CRM deals
                        </div>
                        {selectedLeadSiteLocations.map((location) => (
                          <div key={`${location.leadId}-${location.id}`} style={{ padding: 12, borderRadius: 16, background: dashboardSoftBg, border: dashboardBorder, display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                            <div>
                              <strong>{location.label || "Site Location"}</strong>
                              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                                {location.leadTitle} {location.addressText ? `| ${location.addressText}` : ""}
                              </div>
                            </div>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                              <button type="button" onClick={() => openSiteLocation(location)} style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 10px" }}>
                                Open Map
                              </button>
                              <button type="button" onClick={() => copyLeadLocationToCustomer(location)} style={{ ...btn("#10b981"), marginRight: 0, padding: "8px 10px" }}>
                                Save to Customer Ledger
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {customerLedgerDetailTab === "installations" ? (
                  <div style={{ ...dashboardCardStyle, display: "grid", gap: 10 }}>
                    <div style={{ fontSize: 17, fontWeight: 900 }}>Installations</div>
                    {selectedInstallations.length === 0 ? (
                      <div style={{ color: dashboardMuted }}>No installation dates linked yet.</div>
                    ) : selectedInstallations.map((lead) => {
                      const installationLocation =
                        getPrimarySiteLocation(lead) || getPrimarySiteLocation(selected);
                      return (
                        <div key={lead.id} style={{ padding: 12, borderRadius: 16, background: dashboardSoftBg, border: dashboardBorder, display: "grid", gap: 8 }}>
                          <strong>{lead.title || lead.customerName || "Installation"}</strong>
                          <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
                            {toDateInputValue(lead.installationDate) || "-"} | {installationLocation?.addressText || lead.contact?.address || lead.crmContact?.address || selected.address || "No address"}
                          </div>
                          {installationLocation ? (
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                              <button type="button" onClick={() => openSiteLocation(installationLocation)} style={{ ...btn("#0f172a"), marginRight: 0, padding: "8px 10px" }}>
                                Navigate
                              </button>
                              <button type="button" onClick={() => copySiteLocationLink(installationLocation)} style={{ ...btn("#2563eb"), marginRight: 0, padding: "8px 10px" }}>
                                Copy Link
                              </button>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : null}

                {customerLedgerDetailTab === "payments" ? (
                  <div style={{ ...dashboardCardStyle, display: "grid", gap: 12 }}>
                    <div style={{ fontSize: 17, fontWeight: 900 }}>Payments / Ledger</div>
                    <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "1fr 160px 2fr auto", gap: 10, alignItems: "center" }}>
                      <input type="number" value={customerLedgerPaymentDraft.amount} onChange={(event) => setCustomerLedgerPaymentDraft((prev) => ({ ...prev, amount: event.target.value }))} style={input} placeholder="Payment amount" />
                      <input type="date" value={customerLedgerPaymentDraft.date} onChange={(event) => setCustomerLedgerPaymentDraft((prev) => ({ ...prev, date: event.target.value }))} style={input} />
                      <input value={customerLedgerPaymentDraft.notes} onChange={(event) => setCustomerLedgerPaymentDraft((prev) => ({ ...prev, notes: event.target.value }))} style={input} placeholder="Notes" />
                      <button type="button" onClick={addCustomerLedgerPayment} disabled={isSavingCustomerPayment} style={{ ...btn("#16a34a"), marginRight: 0 }}>
                        {isSavingCustomerPayment ? "Saving..." : "Add Payment"}
                      </button>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(3, minmax(0, 1fr))", gap: 10 }}>
                      <div style={{ padding: 12, borderRadius: 16, background: dashboardSoftBg, border: dashboardBorder }}>
                        <div style={{ fontSize: 12, color: dashboardMuted }}>Quoted</div>
                        <strong>{currencySymbol(currency)} {Number(selected.totals?.totalQuoteValue || 0).toFixed(2)}</strong>
                      </div>
                      <div style={{ padding: 12, borderRadius: 16, background: dashboardSoftBg, border: dashboardBorder }}>
                        <div style={{ fontSize: 12, color: dashboardMuted }}>Received</div>
                        <strong>{currencySymbol(currency)} {Number(selected.totals?.receivedAmount || 0).toFixed(2)}</strong>
                      </div>
                      <div style={{ padding: 12, borderRadius: 16, background: dashboardSoftBg, border: dashboardBorder }}>
                        <div style={{ fontSize: 12, color: dashboardMuted }}>Pending</div>
                        <strong>{currencySymbol(currency)} {Number(selected.totals?.pendingAmount || 0).toFixed(2)}</strong>
                      </div>
                    </div>
                  </div>
                ) : null}

                {customerLedgerDetailTab === "notes" ? (
                  <div style={{ ...dashboardCardStyle, display: "grid", gap: 10 }}>
                    <div style={{ fontSize: 17, fontWeight: 900 }}>Notes & Activity</div>
                    <div style={{ color: dashboardMuted, lineHeight: 1.7 }}>
                      {selected.notes || "No customer notes yet. Quote, lead, task, and installation activity appears across the other tabs."}
                    </div>
                    {selected.duplicateWarning ? (
                      <div style={{ padding: 12, borderRadius: 16, background: "#fef3c7", color: "#92400e", fontWeight: 700 }}>
                        Possible duplicate customer detected by {selected.duplicateWarning}. Open matching records before merging manually.
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCrmTaskHistory = () => {
    const formatTaskHistoryDateTime = (value) => {
      const millis = safeTimeMillis(value);
      if (!millis) return "-";
      return new Date(millis).toLocaleString();
    };

    const selectedRecord = selectedCrmTaskHistoryRecord;

    return (
      <div style={{ display: "grid", gap: 16 }}>
        <div style={{ ...dashboardCardStyle, display: "grid", gap: 14 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexDirection: isCompact ? "column" : "row",
              alignItems: isCompact ? "stretch" : "center",
            }}
          >
            <div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>Task History</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.6 }}>
                Completed reminders, checklist items, and installation follow-ups stay permanently recorded here for this workspace.
              </div>
            </div>
            <div style={{ fontSize: 12, color: dashboardMuted }}>
              {crmFilteredTaskHistoryRecords.length} visible
              {" / "}
              {crmTaskHistoryRecords.length} total completed item
              {crmTaskHistoryRecords.length === 1 ? "" : "s"}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact
                ? "1fr"
                : "minmax(240px, 1.4fr) repeat(4, minmax(140px, 1fr))",
              gap: 12,
            }}
          >
            <input
              value={crmTaskHistorySearch}
              onChange={(event) => setCrmTaskHistorySearch(event.target.value)}
              style={{ ...input, width: "100%", minWidth: 0 }}
              placeholder="Search all task history by task, customer, quote no, or completed by"
            />
            <select
              value={crmTaskHistoryTypeFilter}
              onChange={(event) => setCrmTaskHistoryTypeFilter(event.target.value)}
              style={{ ...selectInput, width: "100%", minWidth: 0 }}
            >
              <option value="all">All Task Types</option>
              {Object.entries(TASK_HISTORY_TYPE_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
            <select
              value={crmTaskHistoryCompletedByFilter}
              onChange={(event) =>
                setCrmTaskHistoryCompletedByFilter(event.target.value)
              }
              style={{ ...selectInput, width: "100%", minWidth: 0 }}
            >
              <option value="all">All Team Members</option>
              {crmTaskHistoryCompletedByOptions.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
            <input
              type="date"
              value={crmTaskHistoryFromDate}
              onChange={(event) => setCrmTaskHistoryFromDate(event.target.value)}
              style={{ ...input, width: "100%", minWidth: 0 }}
            />
            <input
              type="date"
              value={crmTaskHistoryToDate}
              onChange={(event) => setCrmTaskHistoryToDate(event.target.value)}
              style={{ ...input, width: "100%", minWidth: 0 }}
            />
          </div>
          {deferredCrmTaskHistorySearch.trim() ? (
            <div style={{ fontSize: 12, color: dashboardMuted }}>
              Search checks indexed task history beyond the recent page.
              {globalSearchStatus.crmTaskHistorySearch?.loading ? " Searching..." : ""}
              {globalSearchStatus.crmTaskHistorySearch?.error ? (
                <span style={{ color: "#dc2626", fontWeight: 800 }}>
                  {" "}{globalSearchStatus.crmTaskHistorySearch.error}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact ? "1fr" : "minmax(320px, 0.95fr) minmax(0, 1.25fr)",
            gap: 16,
            alignItems: "start",
          }}
        >
          <div style={{ ...dashboardCardStyle, display: "grid", gap: 12 }}>
            {crmFilteredTaskHistoryRecords.length === 0 ? (
              <div
                style={{
                  padding: 18,
                  borderRadius: 18,
                  background: dashboardSoftBg,
                  border: dashboardBorder,
                  fontSize: 13,
                  color: dashboardMuted,
                  lineHeight: 1.7,
                }}
              >
                No completed task history matches these filters yet.
              </div>
            ) : (
              crmFilteredTaskHistoryRecords.map((record) => {
                const active = selectedRecord?.id === record.id;
                const typeLabel =
                  TASK_HISTORY_TYPE_LABELS[record.taskType] || "Completed Task";

                return (
                  <button
                    key={record.id}
                    type="button"
                    onClick={() => setSelectedCrmTaskHistoryId(record.id)}
                    style={{
                      ...dashboardCardStyle,
                      padding: 16,
                      background: active
                        ? isDark
                          ? "rgba(37,99,235,0.14)"
                          : "#eff6ff"
                        : dashboardSoftBg,
                      border: active
                        ? `1px solid ${isDark ? "rgba(96,165,250,0.6)" : "#93c5fd"}`
                        : dashboardBorder,
                      textAlign: "left",
                      display: "grid",
                      gap: 10,
                      cursor: "pointer",
                      boxShadow: active
                        ? isDark
                          ? "0 16px 30px rgba(2,6,23,0.35)"
                          : "0 16px 30px rgba(37,99,235,0.08)"
                        : "none",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 10,
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <div style={{ fontSize: 15, fontWeight: 800 }}>{record.taskTitle}</div>
                      <div
                        style={{
                          padding: "5px 9px",
                          borderRadius: 999,
                          background: isDark ? "rgba(22,163,74,0.2)" : "#dcfce7",
                          color: isDark ? "#86efac" : "#166534",
                          fontSize: 11,
                          fontWeight: 800,
                        }}
                      >
                        {typeLabel}
                      </div>
                    </div>
                    <div style={{ fontSize: 13, color: dashboardMuted }}>
                      {record.customerName || "Unnamed Customer"}
                      {record.quoteNo ? ` | Quote #${record.quoteNo}` : ""}
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))",
                        gap: 8,
                        fontSize: 12,
                        color: dashboardMuted,
                      }}
                    >
                      <div>Due: {formatTaskHistoryDateTime(record.dueDate)}</div>
                      <div>Completed: {formatTaskHistoryDateTime(record.completedAt)}</div>
                      <div>
                        By:{" "}
                        {record.completedByName ||
                          record.completedByEmail ||
                          record.completedByUid ||
                          "Workspace User"}
                      </div>
                      <div>Deal: {record.dealId || record.leadId || record.quoteId || "-"}</div>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          <div style={{ ...dashboardCardStyle, display: "grid", gap: 14 }}>
            {!selectedRecord ? (
              <div
                style={{
                  padding: 18,
                  borderRadius: 18,
                  background: dashboardSoftBg,
                  border: dashboardBorder,
                  fontSize: 13,
                  color: dashboardMuted,
                  lineHeight: 1.7,
                }}
              >
                Select a completed task to review the full audit trail and jump back into the related CRM deal.
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    flexDirection: isCompact ? "column" : "row",
                    alignItems: isCompact ? "stretch" : "center",
                  }}
                >
                  <div>
                    <div style={{ fontSize: 12, color: dashboardMuted, fontWeight: 700 }}>
                      {TASK_HISTORY_TYPE_LABELS[selectedRecord.taskType] || "Completed Task"}
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 900, marginTop: 4 }}>
                      {selectedRecord.taskTitle}
                    </div>
                    <div style={{ fontSize: 13, color: dashboardMuted, marginTop: 6, lineHeight: 1.7 }}>
                      {selectedRecord.taskDescription || "No extra description was stored for this completion."}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => openCrmTaskHistoryDeal(selectedRecord)}
                    style={{ ...btn("#2563eb"), marginRight: 0 }}
                  >
                    Open Deal
                  </button>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))",
                    gap: 12,
                  }}
                >
                  {[
                    ["Customer / Deal", selectedRecord.customerName || "Unnamed Customer"],
                    ["Quote No", selectedRecord.quoteNo || "No linked quote"],
                    ["Due Date", formatTaskHistoryDateTime(selectedRecord.dueDate)],
                    ["Completed At", formatTaskHistoryDateTime(selectedRecord.completedAt)],
                    [
                      "Completed By",
                      selectedRecord.completedByName ||
                        selectedRecord.completedByEmail ||
                        selectedRecord.completedByUid ||
                        "Workspace User",
                    ],
                    [
                      "Area",
                      TASK_HISTORY_SOURCE_LABELS[selectedRecord.sourceModule] ||
                        TASK_HISTORY_SOURCE_LABELS[TASK_HISTORY_SOURCE_CRM],
                    ],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      style={{
                        padding: 14,
                        borderRadius: 16,
                        background: dashboardSoftBg,
                        border: dashboardBorder,
                      }}
                    >
                      <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 700 }}>
                        {label}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 800, marginTop: 6 }}>
                        {value}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCrmWorkspace = () => (
    <div style={dashboardPanelStyle}>
      <div
        style={{
          ...dashboardCardStyle,
          marginBottom: 18,
          background: isDark
            ? "linear-gradient(135deg, rgba(15,23,42,0.96), rgba(30,41,59,0.92))"
            : "linear-gradient(135deg, #eff6ff, #ffffff)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 18,
            flexDirection: isCompact ? "column" : "row",
          }}
        >
          <div style={{ maxWidth: 700 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#2563eb",
                marginBottom: 10,
              }}
            >
              CRM Workspace
            </div>
            <h2
              style={{
                margin: 0,
                fontSize: isCompact ? 24 : 30,
                fontWeight: 900,
                letterSpacing: "-0.04em",
                color: isDark ? "#f8fafc" : "#0f172a",
              }}
            >
              Tailored sales, production, and installation operations for furnishing projects
            </h2>
            <div
              style={{
                marginTop: 10,
                fontSize: 13,
                lineHeight: 1.6,
                color: dashboardMuted,
              }}
            >
              Curtains and general quotations stay on their own pipelines, while Saved Quotes, checklist progress, and installation scheduling all remain connected to the same CRM state.
            </div>
          </div>

          <div
            style={{
              minWidth: isCompact ? "100%" : 320,
              display: "grid",
              gap: 10,
            }}
          >
            <button
              type="button"
              onClick={() => openNewCrmLeadModal()}
              style={{
                ...btn("#2563eb"),
                marginRight: 0,
                justifySelf: "stretch",
                width: "100%",
              }}
            >
              + New Lead
            </button>
            <div
              style={{
                padding: 16,
                borderRadius: 18,
                background: isDark ? "rgba(15,23,42,0.75)" : "#ffffff",
                border: dashboardBorder,
              }}
            >
              <div style={{ fontSize: 12, color: dashboardMuted, marginBottom: 6 }}>
                CRM Scope
              </div>
              <div style={{ fontSize: 20, fontWeight: 900 }}>
                {crmScopeLabel}
              </div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                {crmScopeSubtitle}
              </div>
            </div>
            <div
              style={{
                padding: 16,
                borderRadius: 18,
                background: isDark ? "rgba(15,23,42,0.75)" : "#ffffff",
                border: dashboardBorder,
              }}
            >
              <div style={{ fontSize: 12, color: dashboardMuted, marginBottom: 6 }}>
                {crmScope === CRM_SCOPE_ALL ? "Deals in CRM" : "Deals in Pipeline"}
              </div>
              <div style={{ fontSize: 20, fontWeight: 900 }}>{crmScopeDeals.length}</div>
              <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                {crmOpenDeals.length} open | {crmPipelineSummary.completed} complete | {crmPipelineSummary.cancelled} cancelled
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
        {[CRM_SCOPE_ALL, ...CRM_PIPELINE_TYPES.filter((type) =>
          type === GENERAL_QUOTE_TYPE ? canAccessGeneralQuotes : canAccessCurtainQuotes
        )].map((type) => (
          <button
            key={type}
            type="button"
            onClick={() => {
              setCrmScope(type);
              if (type !== CRM_SCOPE_ALL) {
                setCrmPipelineType(type);
              }
              setCrmStageFilter("all");
              setCrmSourceFilter("all");
            }}
            style={{
              padding: "8px 16px",
              borderRadius: 999,
              border: dashboardBorder,
              background:
                crmScope === type
                  ? "#2563eb"
                  : isDark
                  ? "rgba(255,255,255,0.05)"
                  : "#ffffff",
              color:
                crmScope === type
                  ? "#ffffff"
                  : isDark
                  ? "#cbd5e1"
                  : "#334155",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {type === CRM_SCOPE_ALL ? "All" : getQuoteTypeLabel(type)}
          </button>
        ))}
      </div>

      {(crmView === "board" || crmView === "settings") && crmScope === CRM_SCOPE_ALL ? (
        <div
          style={{
            ...dashboardCardStyle,
            marginBottom: 16,
            padding: isCompact ? 16 : 18,
            display: "flex",
            justifyContent: "space-between",
            gap: 14,
            flexDirection: isCompact ? "column" : "row",
            alignItems: isCompact ? "stretch" : "center",
          }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 4 }}>
              Choose the pipeline to manage
            </div>
            <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
              The combined CRM overview stays available in All mode, while the pipeline board and pipeline settings remain type-specific for cleaner operations.
            </div>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            {CRM_PIPELINE_TYPES.filter((type) =>
              type === GENERAL_QUOTE_TYPE ? canAccessGeneralQuotes : canAccessCurtainQuotes
            ).map((type) => (
              <button
                key={`pipeline-${type}`}
                type="button"
                onClick={() => {
                  setCrmPipelineType(type);
                  setCrmStageFilter("all");
                  setCrmSourceFilter("all");
                }}
                style={{
                  padding: "8px 16px",
                  borderRadius: 999,
                  border: dashboardBorder,
                  background:
                    crmPipelineType === type
                      ? isDark
                        ? "rgba(37,99,235,0.22)"
                        : "#dbeafe"
                      : isDark
                      ? "rgba(255,255,255,0.04)"
                      : "#ffffff",
                  color:
                    crmPipelineType === type
                      ? "#2563eb"
                      : isDark
                      ? "#cbd5e1"
                      : "#334155",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                {getQuoteTypeLabel(type)}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 20 }}>
        {crmNavItems.map((item) => {
          const Icon = item.icon;
          const active = crmView === item.key;

          return (
            <button
              key={item.key}
              type="button"
              onClick={() => setCrmView(item.key)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 14px",
                borderRadius: 14,
                border: dashboardBorder,
                background: active
                  ? isDark
                    ? "rgba(37,99,235,0.2)"
                    : "#dbeafe"
                  : isDark
                  ? "rgba(255,255,255,0.04)"
                  : "#ffffff",
                color: active
                  ? "#2563eb"
                  : isDark
                  ? "#cbd5e1"
                  : "#334155",
                cursor: "pointer",
                fontWeight: 700,
              }}
            >
              <Icon size={16} strokeWidth={2} />
              <span>{item.label}</span>
            </button>
          );
        })}
      </div>

      {crmView === "overview"
        ? renderCrmOverview()
        : crmView === "board"
        ? renderCrmBoard()
        : crmView === "leads"
        ? renderCrmLeads()
        : crmView === "list"
        ? renderCrmList()
        : crmView === "history"
        ? renderCrmTaskHistory()
        : renderCrmSettings()}

      {!isCompact && (crmView === "board" || crmView === "list" || crmView === "leads")
        ? renderCrmDetailDrawer()
        : null}

      {renderCrmLeadModal()}
    </div>
  );



  if (workspaceBootstrapError) {
    const debugText = JSON.stringify(workspaceBootstrapDebug || {}, null, 2);
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: pageBg,
          color: textColor,
          padding: 24,
        }}
      >
        <div
          style={{
            ...dashboardCardStyle,
            maxWidth: 640,
            textAlign: "center",
            display: "grid",
            gap: 14,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#dc2626" }}>
            Workspace Load Error
          </div>
          <div style={{ fontSize: 24, fontWeight: 900 }}>
            We could not load your workspace
          </div>
          <div style={{ fontSize: 14, color: dashboardMuted, lineHeight: 1.65 }}>
            {workspaceBootstrapError.message ||
              "Please refresh the page. If this continues, contact support."}
          </div>
          {IS_DEV_UI_ENABLED ? (
            <details
              style={{
                textAlign: "left",
                background: isDark ? "rgba(15,23,42,0.58)" : "#f8fafc",
                border: dashboardBorder,
                borderRadius: 14,
                padding: 14,
                fontSize: 12,
                lineHeight: 1.6,
                color: isDark ? "#cbd5e1" : "#334155",
              }}
            >
              <summary style={{ cursor: "pointer", fontWeight: 900 }}>
                Startup debug details
              </summary>
              <pre style={{ whiteSpace: "pre-wrap", margin: "10px 0 0" }}>
                {debugText}
              </pre>
            </details>
          ) : null}
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <Button
              icon={RotateCcw}
              variant="primary"
              onClick={() => window.location.reload()}
            >
              Reload FabriQuote
            </Button>
            {IS_DEV_UI_ENABLED ? (
              <Button
                icon={FileText}
                variant="secondary"
                onClick={() => {
                  if (navigator.clipboard?.writeText) {
                    navigator.clipboard.writeText(debugText);
                  }
                }}
              >
                Copy Error Details
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  if (isResolvingWorkspace || accountLoading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: pageBg,
          color: textColor,
          padding: 24,
        }}
      >
        <div
          style={{
            ...dashboardCardStyle,
            maxWidth: 520,
            textAlign: "center",
            display: "grid",
            gap: 12,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#2563eb" }}>
            Workspace
          </div>
          <div style={{ fontSize: 24, fontWeight: 900 }}>
            Loading your FabriQuote workspace
          </div>
          <div style={{ fontSize: 14, color: dashboardMuted, lineHeight: 1.6 }}>
            We&apos;re connecting your login to the correct company data, permissions, and shared team workspace.
          </div>
        </div>
      </div>
    );
  }

  if (!hasActiveSubscription || accessibleTabs.length === 0) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: pageBg,
          color: textColor,
          padding: 24,
        }}
      >
        <div
          style={{
            ...dashboardCardStyle,
            maxWidth: 560,
            textAlign: "center",
            display: "grid",
            gap: 12,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#2563eb" }}>
            Subscription Access
          </div>
          <div style={{ fontSize: 24, fontWeight: 900 }}>
            This FabriQuote account is not active
          </div>
          <div style={{ fontSize: 14, color: dashboardMuted, lineHeight: 1.6 }}>
            {account?.name || "This workspace"} is currently {account?.subscriptionStatus || "inactive"}. Plan-based access is locked until the subscription is active.
          </div>
          <div style={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
            <Button icon={RotateCcw} variant="secondary" onClick={() => signOut(auth)}>
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (isWorkspaceAccessRevoked) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: pageBg,
          color: textColor,
          padding: 24,
        }}
      >
        <div
          style={{
            ...dashboardCardStyle,
            maxWidth: 560,
            textAlign: "center",
            display: "grid",
            gap: 12,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#2563eb" }}>
            Team Access
          </div>
          <div style={{ fontSize: 24, fontWeight: 900 }}>
            Your workspace access is currently disabled
          </div>
          <div style={{ fontSize: 14, color: dashboardMuted, lineHeight: 1.6 }}>
            Please contact the workspace owner if you need access restored. Your sign-in still works, but FabriQuote is respecting the latest team permissions for this company.
          </div>
          <div style={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
            <Button icon={RotateCcw} variant="secondary" onClick={() => signOut(auth)}>
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div
      className="fabriquote-main-shell"
      style={{
        minHeight: "100vh",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        display: "flex",
        background: pageBg,
        color: textColor,
        fontFamily:
          "Inter, system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      <Sidebar
        activeTab={tab}
        onTabChange={(nextTab) => {
          setRoutedTab(nextTab);
          if (isMobile) {
            setIsNavOpen(false);
          }
        }}
        isDark={isDark}
        onToggleDarkMode={() => setIsDark((value) => !value)}
        onSignOut={() => signOut(auth)}
        isMobile={isMobile}
        mobileOpen={isNavOpen}
        onClose={() => setIsNavOpen(false)}
        forceExpanded={isTablet}
        brandName={businessName || "FabriQuote"}
        visibleItemIds={accessibleTabs}
      />

      {/* ---------------- MAIN PANEL ---------------- */}
      <main
        className="fabriquote-main-content"
        style={{
          flex: 1,
          minWidth: 0,
          width: "100%",
          maxWidth: "100%",
          padding: isMobile ? 14 : isTablet ? 18 : 24,
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
          gap: 18,
        }}
      >
        {/* Top bar */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            position: isMobile ? "sticky" : "static",
            top: isMobile ? 0 : "auto",
            zIndex: 10,
            background: isMobile ? pageBg : "transparent",
            paddingBottom: isMobile ? 8 : 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {isMobile ? (
              <button
                type="button"
                onClick={() => setIsNavOpen(true)}
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 14,
                  border: "1px solid rgba(148,163,184,0.25)",
                  background: isDark ? "#0f172a" : "#ffffff",
                  color: textColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  boxShadow: "0 10px 24px rgba(15,23,42,0.08)",
                }}
              >
                <Menu size={18} strokeWidth={2.25} />
              </button>
            ) : null}
            {logoURL ? (
              <img
                src={logoURL}
                alt="logo"
                style={{
                  height: isMobile ? 48 : isTablet ? 72 : 130,
                  width: "auto",
                  objectFit: "contain",
                }}
              />
            ) : (
              <div
                style={{
                  fontSize: isMobile ? 14 : 18,
                  fontWeight: 700,
                  opacity: 0.5,
                }}
              >
                {businessName || "Upload Logo"}
              </div>
            )}
          </div>
        </div>

        {IS_DEV_UI_ENABLED && firebaseHealthWarning ? (
          <div
            style={{
              border: "1px solid rgba(245, 158, 11, 0.45)",
              background: isDark ? "rgba(120, 53, 15, 0.28)" : "#fffbeb",
              color: isDark ? "#fde68a" : "#78350f",
              borderRadius: 16,
              padding: "12px 14px",
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              alignItems: "flex-start",
              boxShadow: "0 12px 28px rgba(15,23,42,0.08)",
            }}
          >
            <div>
              <div style={{ fontWeight: 800, marginBottom: 4 }}>
                Firebase quota/billing issue detected
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.45 }}>
                {firebaseHealthWarning.message} Check Firebase Usage and Billing.
              </div>
              <div style={{ fontSize: 11, opacity: 0.8, marginTop: 4 }}>
                {firebaseHealthWarning.code || "no-code"} ·{" "}
                {firebaseHealthWarning.detectedAt}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => setFirebaseHealthWarning(null)}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "inherit",
                  cursor: "pointer",
                  fontWeight: 800,
                }}
              >
                Dismiss
              </button>
              <button
                type="button"
                onClick={() => {
                  clearFirestoreReadBackoffState();
                  setFirebaseHealthWarning(null);
                  notify("Firebase quota warning state cleared for this session.", "success");
                }}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "inherit",
                  cursor: "pointer",
                  fontWeight: 800,
                }}
              >
                Clear warning state
              </button>
            </div>
          </div>
        ) : null}

        {IS_DEV_UI_ENABLED ? (() => {
          const monitor =
            typeof window !== "undefined"
              ? window.__FQ_READ_MONITOR__ || window.__FQ_READ_COUNT__
              : null;
          const startupTrace =
            monitor?.startupTrace ||
            (typeof window !== "undefined"
              ? window.__FQ_STARTUP_READ_TRACE__ || []
              : []);
          const byPath = monitor?.byPath || {};
          const activeListeners = monitor?.activeListeners || {};
          const topEntries = Object.entries(byPath)
            .sort(([, a], [, b]) => Number(b.reads || 0) - Number(a.reads || 0))
            .slice(0, 10);
          const activeListenerCount = Object.keys(activeListeners).length;

          return (
            <details
              style={{
                border: "1px solid rgba(59,130,246,0.24)",
                background: isDark ? "rgba(15,23,42,0.8)" : "#ffffff",
                borderRadius: 16,
                padding: 12,
                boxShadow: "0 12px 28px rgba(15,23,42,0.06)",
              }}
            >
              <summary style={{ cursor: "pointer", fontWeight: 800 }}>
                Firebase Read Health · {Number(monitor?.total || 0)} reads ·{" "}
                {activeListenerCount} active listener
                {activeListenerCount === 1 ? "" : "s"}
              </summary>
              <div
                style={{
                  marginTop: 10,
                  display: "grid",
                  gap: 8,
                  fontSize: 12,
                  color: isDark ? "#cbd5e1" : "#475569",
                }}
              >
                <div>
                  Startup trace rows: {startupTrace.length} · refresh tick:{" "}
                  {firebaseReadHealthTick}
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => {
                      clearFirestoreReadBackoffState();
                      setFirebaseHealthWarning(null);
                      notify("Firebase quota warning state cleared for this session.", "success");
                    }}
                    style={{
                      border: dashboardBorder,
                      background: dashboardSoftBg,
                      color: textColor,
                      borderRadius: 10,
                      padding: "7px 10px",
                      cursor: "pointer",
                      fontWeight: 800,
                    }}
                  >
                    Clear Firebase quota warning state
                  </button>
                </div>
                {topEntries.length === 0 ? (
                  <div>No tracked Firestore reads in this session yet.</div>
                ) : (
                  topEntries.map(([path, entry]) => (
                    <div
                      key={path}
                      style={{
                        display: "grid",
                        gridTemplateColumns: isCompact ? "1fr" : "1fr auto",
                        gap: 8,
                        padding: 8,
                        borderRadius: 10,
                        background: isDark ? "rgba(30,41,59,0.75)" : "#f8fafc",
                      }}
                    >
                      <span style={{ wordBreak: "break-word" }}>{path}</span>
                      <span>
                        {Number(entry.reads || 0)} reads ·{" "}
                        {Number(entry.cacheHits || 0)} cache ·{" "}
                        {Number(entry.listenerEvents || 0)} snapshots
                      </span>
                    </div>
                  ))
                )}
                {activeListenerCount > 0 ? (
                  <div>
                    Active listeners: {Object.keys(activeListeners).join(", ")}
                  </div>
                ) : null}
              </div>
            </details>
          );
        })() : null}

              {/* ---------------- DASHBOARD TAB ---------------- */}
{tab === "dashboard" && (
  <>
    <div style={dashboardPanelStyle}>
      <div
        style={{
          ...dashboardCardStyle,
          marginBottom: 20,
          background: isDark
            ? "linear-gradient(135deg, rgba(30,41,59,0.95), rgba(15,23,42,0.95))"
            : "linear-gradient(135deg, #eff6ff, #ffffff)",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: isCompact ? "column" : "row",
            justifyContent: "space-between",
            gap: 18,
          }}
        >
          <div style={{ maxWidth: 620 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#2563eb",
                marginBottom: 10,
              }}
            >
              Executive Dashboard
            </div>
            <h2
              style={{
                margin: 0,
                fontSize: isCompact ? 24 : 32,
                fontWeight: 900,
                letterSpacing: "-0.04em",
                color: isDark ? "#f8fafc" : "#0f172a",
              }}
            >
              Trusted quote intelligence for curtains and general quotations
            </h2>
          </div>

          <div
            style={{
              minWidth: isCompact ? "100%" : 280,
              display: "grid",
              gap: 10,
              alignSelf: "stretch",
            }}
          >
            <button
              type="button"
              onClick={() => openBusinessDashboardDrilldown("activeQuotes")}
              style={{
                width: "100%",
                padding: 16,
                borderRadius: 18,
                background: isDark ? "rgba(15,23,42,0.8)" : "#ffffff",
                border: dashboardBorder,
                cursor: "pointer",
                textAlign: "left",
                transition: "transform 0.18s ease, box-shadow 0.18s ease",
              }}
            >
              <div style={{ fontSize: 12, color: dashboardMuted, marginBottom: 6 }}>
                Recent Active Quotes
              </div>
              <div style={{ fontSize: 28, fontWeight: 900 }}>
                <CountUp end={dashboardMetricData.activeQuotes.metricValue} duration={1.2} />
              </div>
            </button>
            <div
              style={{
                padding: 16,
                borderRadius: 18,
                background: isDark ? "rgba(15,23,42,0.8)" : "#ffffff",
                border: dashboardBorder,
              }}
            >
              <div style={{ fontSize: 12, color: dashboardMuted, marginBottom: 6 }}>
                Selected Period
              </div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>{dashboardPeriodLabel}</div>
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
          marginBottom: 16,
        }}
      >
        {[
          { key: "business", label: "Business Overview" },
          { key: "crm", label: "CRM Overview" },
        ].map((mode) => (
          <button
            key={mode.key}
            type="button"
            onClick={() => setDashboardMode(mode.key)}
            style={{
              padding: "8px 16px",
              borderRadius: 999,
              border: dashboardBorder,
              background:
                dashboardMode === mode.key
                  ? isDark
                    ? "rgba(37,99,235,0.2)"
                    : "#dbeafe"
                  : isDark
                  ? "rgba(255,255,255,0.05)"
                  : "#ffffff",
              color:
                dashboardMode === mode.key
                  ? "#2563eb"
                  : isDark
                  ? "#cbd5e1"
                  : "#334155",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {mode.label}
          </button>
        ))}
      </div>

      {dashboardMode === "business" ? (
      <>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
          marginBottom: 16,
        }}
      >
        {[
          { k: "today", l: "Today" },
          { k: "thisWeek", l: "This Week" },
          { k: "thisMonth", l: "This Month" },
          { k: "lastMonth", l: "Last Month" },
          { k: "thisYear", l: "This Year" },
          { k: "custom", l: "Custom" },
        ].map((period) => (
          <button
            key={period.k}
            onClick={() => setPeriodType(period.k)}
            style={{
              padding: "8px 16px",
              borderRadius: 999,
              border: dashboardBorder,
              background:
                periodType === period.k
                  ? "#2563eb"
                  : isDark
                  ? "rgba(255,255,255,0.05)"
                  : "#ffffff",
              color:
                periodType === period.k
                  ? "#ffffff"
                  : isDark
                  ? "#cbd5e1"
                  : "#334155",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {period.l}
          </button>
        ))}
      </div>

      {periodType === "custom" && (
        <div
          style={{
            ...dashboardCardStyle,
            marginBottom: 18,
            display: "grid",
            gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))",
            gap: 12,
          }}
        >
          <input
            type="date"
            value={customRange.start || ""}
            onChange={(e) =>
              setCustomRange((prev) => ({
                ...prev,
                start: e.target.value,
              }))
            }
            style={input}
          />
          <input
            type="date"
            value={customRange.end || ""}
            onChange={(e) =>
              setCustomRange((prev) => ({
                ...prev,
                end: e.target.value,
              }))
            }
            style={input}
          />
        </div>
      )}

      {IS_DEV_UI_ENABLED ? (
        <div
          style={{
            ...dashboardCardStyle,
            marginBottom: 18,
            fontSize: 12,
            lineHeight: 1.6,
            background: isDark ? "rgba(15,23,42,0.92)" : "#f8fafc",
          }}
        >
          <div style={{ fontWeight: 900, marginBottom: 6 }}>
            Dashboard Analytics Diagnostics
          </div>
          <div>
            KPI source: <strong>{dashboardAnalyticsState.diagnostics?.source || "unknown"}</strong>
            {" "} | monthKey: <strong>{dashboardAnalyticsState.diagnostics?.monthKey || "-"}</strong>
            {" "} | calendar: <strong>{dashboardAnalyticsState.diagnostics?.calendarMonthKey || "-"}</strong>
          </div>
          <div>
            Reads: analytics_monthly{" "}
            {dashboardAnalyticsState.diagnostics?.reads?.analyticsMonthly ?? 0},
            client_monthly {dashboardAnalyticsState.diagnostics?.reads?.clientMonthly ?? 0},
            installation_index {dashboardAnalyticsState.diagnostics?.reads?.installationIndex ?? 0}
          </div>
          <div>
            Loaded: {dashboardAnalyticsState.diagnostics?.loadedAt || "-"} | summary updated:{" "}
            {dashboardAnalyticsState.diagnostics?.lastUpdated
              ? new Date(dashboardAnalyticsState.diagnostics.lastUpdated).toLocaleString()
              : "-"}
          </div>
          {dashboardAnalyticsState.diagnostics?.source !== "analytics_indexes" ? (
            <div style={{ color: "#dc2626", fontWeight: 900 }}>
              Warning: dashboard KPI source is not analytics/index documents.
            </div>
          ) : null}
          {adminDataAuditState.checkedAt || adminDataAuditState.running ? (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontWeight: 900 }}>Workspace Data Audit</div>
              {adminDataAuditState.report?.mismatchSummary ? (
                <div>
                  Mismatches:{" "}
                  <strong>
                    {adminDataAuditState.report.mismatchSummary.totalMismatchCount}
                  </strong>{" "}
                  | source quotes:{" "}
                  <strong>{adminDataAuditState.report.sourceDocumentCounts?.quotations || 0}</strong>
                </div>
              ) : adminDataAuditState.progress?.collection ? (
                <div>
                  Scanning {adminDataAuditState.progress.collection}:{" "}
                  {adminDataAuditState.progress.scanned}
                </div>
              ) : null}
              {Object.entries(adminDataAuditState.collections || {}).map(([name, result]) => (
                <div key={name}>
                  {name}:{" "}
                  {result.ok
                    ? `total ${result.totalCount}, sample ${result.sampleCount}`
                    : `failed ${result.errorCode || ""} ${result.errorMessage || ""}`}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {dashboardAnalyticsError ? (
        <div
          style={{
            ...dashboardCardStyle,
            marginBottom: 18,
            background: isDark ? "rgba(220,38,38,0.14)" : "#fef2f2",
            border: "1px solid rgba(220,38,38,0.24)",
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 6 }}>
            Could not load dashboard right now
          </div>
          <div style={{ fontSize: 13, color: dashboardMuted, lineHeight: 1.6 }}>
            Try again in a moment. Your quotations and customer data are still safe.
          </div>
          {IS_DEV_UI_ENABLED ? (
            <div style={{ marginTop: 8, fontSize: 12, color: "#dc2626", fontWeight: 800 }}>
              {dashboardAnalyticsState.diagnostics?.errorCode || "error"}:{" "}
              {dashboardAnalyticsState.diagnostics?.errorMessage || "Unknown dashboard analytics error"}
            </div>
          ) : null}
        </div>
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isCompact
            ? "1fr"
            : isTablet
            ? "repeat(2, minmax(0, 1fr))"
            : "repeat(3, minmax(0, 1fr))",
          gap: 16,
          marginBottom: 20,
        }}
      >
        {dashboardKpis.map((kpi) => (
          <button
            key={kpi.key}
            type="button"
            onClick={kpi.clickable ? kpi.onClick : undefined}
            style={{
              ...dashboardCardStyle,
              width: "100%",
              cursor: kpi.clickable ? "pointer" : "default",
              position: "relative",
              overflow: "hidden",
              textAlign: "left",
              transition: "transform 0.18s ease, box-shadow 0.18s ease",
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: `linear-gradient(135deg, ${kpi.accent}18, transparent 58%)`,
                pointerEvents: "none",
              }}
            />
            <div
              style={{
                position: "relative",
                display: "grid",
                gap: 10,
              }}
            >
              <div style={{ fontSize: 13, color: dashboardMuted, fontWeight: 600 }}>
                {kpi.label}
              </div>
              <div
                style={{
                  fontSize: isCompact ? 26 : 30,
                  fontWeight: 900,
                  letterSpacing: "-0.03em",
                  color: isDark ? "#f8fafc" : "#0f172a",
                }}
              >
                {typeof kpi.value === "number" ? (
                  <CountUp end={kpi.value} duration={1.1} />
                ) : (
                  kpi.value
                )}
              </div>
              <div style={{ fontSize: 12, color: dashboardMuted }}>{kpi.note}</div>
            </div>
          </button>
        ))}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 1.65fr) minmax(280px, 0.95fr)",
          gap: 16,
          marginBottom: 20,
          alignItems: "start",
        }}
      >
        <div style={dashboardCardStyle}>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 18, fontWeight: 800 }}>Quote Creation Trend</div>
            <div style={{ fontSize: 12, color: dashboardMuted }}>
              Created vs confirmed over the last 6 months
            </div>
          </div>
          <MeasuredChartFrame
            active={hasQuoteCreationTrendData}
            height={quoteTrendChartHeight}
            style={getChartShellStyle(quoteTrendChartHeight)}
            fallback={renderChartEmptyState("No quote trend data yet")}
          >
            {({ width, height }) => (
                <AreaChart
                  width={width}
                  height={height}
                  data={monthlyChartData}
                  margin={chartMargin}
                  onClick={(state) => {
                    const entry = state?.activePayload?.[0]?.payload;
                    if (entry?.key) {
                      openBusinessDashboardDrilldown("monthCreated", {
                        monthKey: entry.key,
                        monthLabel: entry.month,
                      });
                    }
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <defs>
                    <linearGradient id="createdFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2563eb" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#2563eb" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="confirmedFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#243041" : "#e2e8f0"} />
                  <XAxis
                    dataKey="month"
                    stroke={dashboardMuted}
                    tick={chartAxisTickStyle}
                    tickLine={false}
                    axisLine={false}
                    minTickGap={8}
                  />
                  <YAxis
                    allowDecimals={false}
                    stroke={dashboardMuted}
                    tick={chartAxisTickStyle}
                    width={isMobile ? 32 : 45}
                  />
                  <Tooltip contentStyle={dashboardTooltipStyle} />
                  <Area
                    type="monotone"
                    dataKey="created"
                    stroke="#2563eb"
                    fill="url(#createdFill)"
                    strokeWidth={2.5}
                    name="Created"
                  />
                  <Area
                    type="monotone"
                    dataKey="confirmed"
                    stroke="#10b981"
                    fill="url(#confirmedFill)"
                    strokeWidth={2.5}
                    name="Confirmed"
                  />
                </AreaChart>
            )}
          </MeasuredChartFrame>
          {hasQuoteCreationTrendData
            ? renderInlineLegend([
                { label: "Created", color: "#2563eb" },
                { label: "Confirmed", color: "#10b981" },
              ])
            : null}
        </div>

        <div style={{ display: "grid", gap: 16 }}>
          <div style={dashboardCardStyle}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 10 }}>
              Business Health
            </div>
            <div style={{ display: "grid", gap: 12 }}>
              {dashboardInsights.map((insight) => (
                <button
                  key={insight.key}
                  type="button"
                  onClick={insight.onClick}
                  style={{
                    padding: 14,
                    borderRadius: 16,
                    background: dashboardSoftBg,
                    border: dashboardBorder,
                    color: textColor,
                    textAlign: "left",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ fontSize: 12, color: dashboardMuted, marginBottom: 6 }}>
                    {insight.label}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 900 }}>{insight.value}</div>
                  <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                    {insight.description}
                  </div>
                  <div style={{ fontSize: 11, color: "#2563eb", fontWeight: 800, marginTop: 8 }}>
                    Click to view records
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div style={dashboardCardStyle}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 10 }}>
              Quote Type Split
            </div>
            {hasQuoteTypeDistributionData
              ? renderQuoteTypeSplit(quoteTypeDistributionChartData, (entry) =>
                  openBusinessDashboardDrilldown("quoteType", {
                    quoteType:
                      entry.name === "General Quotation"
                        ? GENERAL_QUOTE_TYPE
                        : CURTAIN_QUOTE_TYPE,
                  })
                )
              : renderChartEmptyState("No quote type data yet")}
          </div>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 1.2fr) minmax(0, 1fr)",
          gap: 16,
          marginBottom: 20,
          alignItems: "start",
        }}
      >
        <div style={dashboardCardStyle}>
          <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 10 }}>
            Stage Distribution
          </div>
          <div
            style={{
              width: "100%",
              minWidth: 0,
            }}
          >
            {hasStageDistributionData ? (
              renderHorizontalMetricBars(stageDistributionChartData, {
                valueKey: "value",
                valueLabel: "quotes",
                emptyTitle: "No stage data yet",
                onItemClick: (entry) =>
                  openBusinessDashboardDrilldown("stage", {
                    stageKey: entry.key,
                    stageLabel: entry.stageLabel,
                    title: entry.fullLabel,
                  }),
              })
            ) : (
              renderChartEmptyState("No stage data yet")
            )}
          </div>
        </div>

        <div style={dashboardCardStyle}>
          <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 10 }}>
            Revenue Trend
          </div>
          <MeasuredChartFrame
            active={hasRevenueTrendData}
            height={revenueChartHeight}
            style={getChartShellStyle(revenueChartHeight)}
            fallback={renderChartEmptyState("No revenue data yet")}
          >
            {({ width, height }) => (
                <BarChart
                  width={width}
                  height={height}
                  data={monthlyChartData}
                  margin={chartMargin}
                  onClick={(state) => {
                    const entry = state?.activePayload?.[0]?.payload;
                    if (entry?.key) {
                      openBusinessDashboardDrilldown("monthRevenue", {
                        monthKey: entry.key,
                        monthLabel: entry.month,
                      });
                    }
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#243041" : "#e2e8f0"} />
                  <XAxis
                    dataKey="month"
                    stroke={dashboardMuted}
                    tick={chartAxisTickStyle}
                    tickLine={false}
                    axisLine={false}
                    minTickGap={8}
                  />
                  <YAxis
                    stroke={dashboardMuted}
                    tick={chartAxisTickStyle}
                    width={isMobile ? 34 : 58}
                    tickFormatter={(value) =>
                      Number(value) >= 100000
                        ? `${Math.round(Number(value) / 100000)}L`
                        : Number(value) >= 1000
                        ? `${Math.round(Number(value) / 1000)}k`
                        : value
                    }
                  />
                  <Tooltip
                    contentStyle={dashboardTooltipStyle}
                    formatter={(value) => [formatCurrencyDisplay(value, currency), "Revenue"]}
                  />
                  <Bar
                    dataKey="revenue"
                    fill="#2563eb"
                    name="Revenue"
                    radius={[8, 8, 0, 0]}
                    maxBarSize={42}
                    minPointSize={3}
                  />
                </BarChart>
            )}
          </MeasuredChartFrame>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isCompact
            ? "1fr"
            : isTablet
            ? "repeat(2, minmax(0, 1fr))"
            : "1.15fr 0.95fr 0.9fr",
          gap: 16,
          marginBottom: 20,
        }}
      >
        <div style={dashboardCardStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>Recent Activity</div>
              <div style={{ fontSize: 12, color: dashboardMuted }}>
                Most recently updated quotations
              </div>
            </div>
            <button
              onClick={() => setTab("saved")}
              style={{
                border: "none",
                background: "transparent",
                color: "#2563eb",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              View All
            </button>
          </div>
          <div style={{ display: "grid", gap: 10 }}>
            {recentQuotes.map((quote) => (
              <button
                key={quote.id}
                type="button"
                onClick={() => loadFullQuote(quote)}
                style={{
                  border: dashboardBorder,
                  background: dashboardSoftBg,
                  borderRadius: 16,
                  padding: 14,
                  textAlign: "left",
                  cursor: "pointer",
                  minWidth: 0,
                }}
              >
                <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", gap: 12, minWidth: 0 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 800, overflowWrap: "anywhere" }}>{quote.customerLabel}</div>
                    <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                      #{quote.quoteNo || "-"} | {getStageLabelForQuote(quote)}
                    </div>
                  </div>
                  <div style={{ textAlign: isMobile ? "left" : "right", minWidth: 0 }}>
                    <div style={{ fontWeight: 800 }}>
                      {formatCurrencyDisplay(quote.totalValue || 0, currency)}
                    </div>
                    <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                      {quote.updatedMillis
                        ? new Date(quote.updatedMillis).toLocaleDateString()
                        : "-"}
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <span
                    style={{
                      flex: "0 0 auto",
                      padding: "4px 10px",
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 700,
                      background:
                        quote.quoteTypeResolved === GENERAL_QUOTE_TYPE ? "#dbeafe" : "#ede9fe",
                      color:
                        quote.quoteTypeResolved === GENERAL_QUOTE_TYPE ? "#1d4ed8" : "#7c3aed",
                    }}
                  >
                    {quote.quoteTypeResolved === GENERAL_QUOTE_TYPE
                      ? "General Quotation"
                      : "Curtains"}
                  </span>
                  <span
                    style={{
                      flex: "0 0 auto",
                      padding: "4px 10px",
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 700,
                      background: quote.isConfirmed ? "#dcfce7" : "#fef3c7",
                      color: quote.isConfirmed ? "#166534" : "#92400e",
                    }}
                  >
                    {quote.isConfirmed ? "Confirmed" : "Draft"}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div style={dashboardCardStyle}>
          <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 12 }}>
            Top Customers
          </div>
          <div style={{ display: "grid", gap: 10 }}>
            {dashboardAnalyticsNotReady ? (
              <div style={{ fontSize: 13, color: dashboardMuted }}>
                No customers found for this period.
              </div>
            ) : topCustomers.length === 0 ? (
              <div style={{ fontSize: 13, color: dashboardMuted }}>
                No customers found for this period.
              </div>
            ) : (
            topCustomers.map((customer, index) => (
              <button
                key={customer.name}
                type="button"
                onClick={() => openDashboardCustomer(customer)}
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 12,
                  padding: 14,
                  borderRadius: 16,
                  background: dashboardSoftBg,
                  border: dashboardBorder,
                  minWidth: 0,
                  color: textColor,
                  textAlign: "left",
                  cursor: "pointer",
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                    {index + 1}. {customer.name}
                  </div>
                  <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                    {customer.count} quote{customer.count === 1 ? "" : "s"}
                  </div>
                  <div style={{ fontSize: 11, color: "#2563eb", fontWeight: 800, marginTop: 4 }}>
                    Open Customer
                  </div>
                </div>
                <div style={{ fontWeight: 900, flex: "0 0 auto", textAlign: "right", whiteSpace: "nowrap", fontSize: isMobile ? 13 : 14 }}>
                  {formatCurrencyDisplay(customer.value || 0, currency)}
                </div>
              </button>
            ))
            )}
          </div>
        </div>

        <div style={dashboardCardStyle}>
          <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 12 }}>
            Upcoming Installations
          </div>
          <div style={{ display: "grid", gap: 10 }}>
            {dashboardAnalyticsNotReady ? (
              <div style={{ fontSize: 13, color: dashboardMuted }}>
                No upcoming installations.
              </div>
            ) : upcomingInstallations.length === 0 ? (
              <div style={{ fontSize: 13, color: dashboardMuted }}>
                No upcoming installations.
              </div>
            ) : (
              upcomingInstallations.map((quote) => (
                <button
                  key={quote.id}
                  type="button"
                  onClick={() => loadFullQuote(quote)}
                  style={{
                    textAlign: "left",
                    border: dashboardBorder,
                    background: dashboardSoftBg,
                    borderRadius: 16,
                    padding: 14,
                    cursor: "pointer",
                  }}
                >
                  <div style={{ fontWeight: 800 }}>{quote.customerLabel}</div>
                  <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                    #{quote.quoteNo || "-"}
                  </div>
                  <div style={{ marginTop: 8, fontSize: 13 }}>
                    {new Date(quote.installationMillis).toLocaleDateString()}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      </div>

      <div style={dashboardCardStyle}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: isCompact ? "flex-start" : "center",
            flexDirection: isCompact ? "column" : "row",
            gap: 12,
            marginBottom: 16,
          }}
        >
          <div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>Installation Calendar</div>
            <div style={{ fontSize: 12, color: dashboardMuted }}>
              Scheduled jobs remain connected to the same live saved-quote data.
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              type="button"
              onClick={() => changeCalendarMonth(-1)}
              style={{
                border: dashboardBorder,
                background: dashboardSoftBg,
                borderRadius: 12,
                padding: "8px 12px",
                cursor: "pointer",
                color: textColor,
              }}
            >
              Prev
            </button>
            <div style={{ fontWeight: 700, minWidth: 150, textAlign: "center" }}>
              {MONTH_NAMES[calendarMonth.month]} {calendarMonth.year}
            </div>
            <button
              type="button"
              onClick={() => changeCalendarMonth(1)}
              style={{
                border: dashboardBorder,
                background: dashboardSoftBg,
                borderRadius: 12,
                padding: "8px 12px",
                cursor: "pointer",
                color: textColor,
              }}
            >
              Next
            </button>
          </div>
        </div>

        {(() => {
          const { year, month } = calendarMonth;
          const firstDay = new Date(year, month, 1);
          const daysInMonth = new Date(year, month + 1, 0).getDate();
          const startWeekday = firstDay.getDay();
          const weeks = [];
          let current = 1 - startWeekday;

          for (let w = 0; w < 6; w++) {
            const row = [];
            for (let d = 0; d < 7; d++, current++) {
              if (current < 1 || current > daysInMonth) {
                row.push(null);
              } else {
                const cellDate = new Date(year, month, current);
                const key =
                  cellDate.getFullYear() +
                  "-" +
                  String(cellDate.getMonth() + 1).padStart(2, "0") +
                  "-" +
                  String(cellDate.getDate()).padStart(2, "0");

                row.push({
                  day: current,
                  key,
                  installs: installationByDate[key] || [],
                });
              }
            }
            weeks.push(row);
          }

          return (
            <div style={{ display: "grid", gap: 8 }}>
              {weeks.map((row, rowIndex) => (
                <div
                  key={rowIndex}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                    gap: 8,
                  }}
                >
                  {row.map((cell, cellIndex) =>
                    cell ? (
                      <button
                        key={cellIndex}
                        type="button"
                        onClick={() => {
                          if (!cell.installs.length) return;
                          setSelectedInstallDate(cell.key);
                          openBusinessDashboardDrilldown("installationDate", {
                            dateKey: cell.key,
                          });
                        }}
                        style={{
                          minHeight: isCompact ? 62 : 76,
                          padding: 10,
                          borderRadius: 16,
                          border:
                            cell.key === selectedInstallDate
                              ? "2px solid #2563eb"
                              : dashboardBorder,
                          background:
                            cell.key === selectedInstallDate
                              ? "rgba(37,99,235,0.16)"
                              : cell.installs.length > 0
                              ? isDark
                                ? "rgba(16,185,129,0.14)"
                                : "#ecfdf5"
                              : dashboardSoftBg,
                          color: textColor,
                          cursor: cell.installs.length ? "pointer" : "default",
                          textAlign: "left",
                        }}
                      >
                        <div style={{ fontWeight: 800 }}>{cell.day}</div>
                        <div style={{ fontSize: 11, color: dashboardMuted, marginTop: 8 }}>
                          {cell.installs.length
                            ? `${cell.installs.length} install${cell.installs.length === 1 ? "" : "s"}`
                            : "No jobs"}
                        </div>
                      </button>
                    ) : (
                      <div key={cellIndex} />
                    )
                  )}
                </div>
              ))}
            </div>
          );
        })()}

        {false && selectedInstallDate &&
          installationByDate[selectedInstallDate]?.length > 0 && (
            <div
              style={{
                marginTop: 20,
                paddingTop: 16,
                borderTop: dashboardBorder,
                display: "grid",
                gap: 10,
              }}
            >
              <div style={{ fontSize: 15, fontWeight: 800 }}>
                Installations on {selectedInstallDate}
              </div>
              {installationByDate[selectedInstallDate].map((quote) => (
                <button
                  key={quote.id}
                  type="button"
                  onClick={() => loadFullQuote(quote)}
                  style={{
                    border: dashboardBorder,
                    background: dashboardSoftBg,
                    borderRadius: 16,
                    padding: 14,
                    textAlign: "left",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "center",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 800 }}>{quote.customerLabel || "Unnamed Site"}</div>
                    <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                      #{quote.quoteNo || "-"} | {getStageLabelForQuote(quote)}
                    </div>
                  </div>
                  <div style={{ fontWeight: 800 }}>
                      {formatCurrencyDisplay(quote.totalValue || 0, currency)}
                  </div>
                </button>
              ))}
            </div>
          )}
        {selectedInstallDate &&
          installationByDate[selectedInstallDate]?.length > 0 && (
            <div
              style={{
                marginTop: 20,
                paddingTop: 16,
                borderTop: dashboardBorder,
                display: "grid",
                gap: 10,
              }}
            >
              <div style={{ fontSize: 15, fontWeight: 800 }}>
                Installations on {selectedInstallDate}
              </div>
              {installationByDate[selectedInstallDate].map((quote) => (
                <button
                  key={`${quote.id}-clean`}
                  type="button"
                  onClick={() => loadFullQuote(quote)}
                  style={{
                    border: dashboardBorder,
                    background: dashboardSoftBg,
                    borderRadius: 16,
                    padding: 14,
                    textAlign: "left",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "center",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 800 }}>
                      {quote.customerLabel || "Unnamed Site"}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: dashboardMuted,
                        marginTop: 4,
                      }}
                    >
                      #{quote.quoteNo || "-"} |{" "}
                      {getStageLabelForQuote(quote)}
                    </div>
                  </div>
                  <div style={{ fontWeight: 800 }}>
                    {formatCurrencyDisplay(quote.totalValue || 0, currency)}
                  </div>
                </button>
              ))}
            </div>
          )}
      </div>
      </>
    ) : (
      <>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact
              ? "1fr"
              : isTablet
              ? "repeat(2, minmax(0, 1fr))"
              : "repeat(3, minmax(0, 1fr))",
            gap: 16,
            marginBottom: 20,
          }}
        >
          {crmSummaryCards.map((card) => (
            <div
              key={card.key}
              style={{
                ...dashboardCardStyle,
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: `linear-gradient(135deg, ${card.accent}16, transparent 58%)`,
                }}
              />
              <div style={{ position: "relative", display: "grid", gap: 8 }}>
                <div style={{ fontSize: 13, color: dashboardMuted, fontWeight: 600 }}>
                  {card.label}
                </div>
                <div style={{ fontSize: isCompact ? 26 : 30, fontWeight: 900 }}>
                  {typeof card.value === "number" ? (
                    <CountUp end={card.value} duration={1.1} />
                  ) : (
                    card.value
                  )}
                </div>
                <div style={{ fontSize: 12, color: dashboardMuted }}>{card.note}</div>
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact
              ? "1fr"
              : "minmax(0, 1.4fr) minmax(320px, 1fr)",
            gap: 16,
            marginBottom: 20,
            alignItems: "start",
          }}
        >
          <div style={dashboardCardStyle}>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 18, fontWeight: 800 }}>CRM Pipeline Health</div>
              <div style={{ fontSize: 12, color: dashboardMuted }}>
                Stage occupancy for the selected {getQuoteTypeLabel(crmPipelineType).toLowerCase()} pipeline
              </div>
            </div>
            {hasCrmStageOverviewData
              ? renderHorizontalMetricBars(crmStageOverviewChartData, {
                  valueKey: "count",
                  valueLabel: "deals",
                  emptyTitle: "No pipeline stage data yet",
                })
              : renderChartEmptyState("No pipeline stage data yet")}
          </div>

          <div style={{ display: "grid", gap: 16 }}>
            <div style={dashboardCardStyle}>
              <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 12 }}>
                Checklist Completion
              </div>
              {renderChecklistProgress(
                crmChecklistChartData,
                crmSummary,
                "No checklist data yet"
              )}
              <div style={{ marginTop: 10, fontSize: 12, color: dashboardMuted }}>
                {crmSummary.checklistCompletion}% of tracked checklist items are complete across open deals.
              </div>
            </div>

            <div style={dashboardCardStyle}>
              <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 12 }}>
                Top CRM Clients
              </div>
              <div style={{ display: "grid", gap: 10 }}>
                {crmTopClients.map((client, index) => (
                  <div
                    key={client.name}
                    style={{
                      padding: 14,
                      borderRadius: 16,
                      background: dashboardSoftBg,
                      border: dashboardBorder,
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 12,
                      minWidth: 0,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                        {index + 1}. {client.name}
                      </div>
                      <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                        {client.count} active quote{client.count === 1 ? "" : "s"}
                      </div>
                    </div>
                    <div style={{ fontWeight: 900, flex: "0 0 auto", whiteSpace: "nowrap", textAlign: "right", fontSize: isMobile ? 13 : 14 }}>
                      {formatCurrencyDisplay(client.value || 0, currency)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))",
            gap: 16,
          }}
        >
          <div style={dashboardCardStyle}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 12 }}>
              Overdue Follow-ups
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {crmDeals
                .filter(
                  (deal) =>
                    deal.nextActionMillis &&
                    deal.nextActionMillis < Date.now() &&
                    !deal.isCrmClosed &&
                    !deal.isCrmCancelled
                )
                .slice(0, 5)
                .map((deal) => (
                  <button
                    key={deal.id}
                    type="button"
                    onClick={() => {
                      setCrmView("list");
                      setTab("crm");
                      openCrmDealDetails(deal, { crmView: "list" });
                    }}
                    style={{
                      border: dashboardBorder,
                      background: dashboardSoftBg,
                      borderRadius: 16,
                      padding: 14,
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ fontWeight: 800 }}>{deal.crmTitle}</div>
                    <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                      {deal.nextAction || "Follow-up pending"} |{" "}
                      {toDateInputValue(deal.nextActionDate) || "-"}
                    </div>
                  </button>
                ))}
              {crmSummary.overdueFollowUps === 0 && (
                <div style={{ fontSize: 13, color: dashboardMuted }}>
                  No overdue follow-ups right now.
                </div>
              )}
            </div>
          </div>

          <div style={dashboardCardStyle}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 12 }}>
              Upcoming Installations
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {upcomingInstallations.map((deal) => (
                <button
                  key={`${deal.id}-crm-overview`}
                  type="button"
                  onClick={() => {
                    setCrmView("list");
                    setTab("crm");
                    openCrmDealDetails(deal, { crmView: "list" });
                  }}
                  style={{
                    border: dashboardBorder,
                    background: dashboardSoftBg,
                    borderRadius: 16,
                    padding: 14,
                    textAlign: "left",
                    cursor: "pointer",
                  }}
                  >
                  <div style={{ fontWeight: 800 }}>{deal.customerLabel}</div>
                  <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
                    {deal.hasQuote
                      ? `#${deal.primaryLinkedQuote?.quoteNo || deal.quoteNo || "-"}`
                      : "Lead"}{" "}
                    | {new Date(deal.installationMillis).toLocaleDateString()}
                  </div>
                </button>
              ))}
              {upcomingInstallations.length === 0 && (
                <div style={{ fontSize: 13, color: dashboardMuted }}>
                  No upcoming installations scheduled.
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    )}
    </div>
  </>
)}

{/* ================= DASHBOARD LIST ================= */}
{tab === "dashboardList" && (
  <div
    style={{
      ...dashboardPanelStyle,
      minHeight: "80vh",
    }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: isCompact ? "flex-start" : "center",
        flexDirection: isCompact ? "column" : "row",
        gap: 12,
        marginBottom: 20,
      }}
    >
      <div>
        <h2 style={{ fontSize: 22, fontWeight: 900, margin: 0 }}>
          {activeDashboardListView?.label || `Quotes ${dashboardPeriodLabel}`}
        </h2>
        <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 6 }}>
          {dashboardListQuotes.length} quotation{dashboardListQuotes.length === 1 ? "" : "s"} in this view
          {activeDashboardListView?.summaryValue != null
            ? ` | Total ${formatCurrencyDisplay(
                activeDashboardListView.summaryValue || 0,
                currency
              )}`
            : ""}
        </div>
      </div>

      <button
        onClick={() => {
          setDashView("home");
          setTab("dashboard");
        }}
        style={{
          padding: "8px 16px",
          borderRadius: 10,
          border: "none",
          background: "#2563eb",
          color: "#fff",
          cursor: "pointer",
        }}
      >
         Back to Dashboard
      </button>
    </div>

    <div style={{ display: "grid", gap: 12 }}>
      {dashboardListQuotes.map((q) => (
        <div
          key={q.id}
          onClick={() => {
            if (!q?.id) {
              console.error("Invalid quotation reference:", q);
              return;
            }

            try {
              loadFullQuote(q);

            } catch (err) {
              console.error("Dashboard load error:", err);
            }
          }}
          style={{
            ...dashboardCardStyle,
            cursor: "pointer",
            display: "flex",
            flexDirection: isCompact ? "column" : "row",
            justifyContent: "space-between",
            gap: 14,
          }}
        >
          <div>
            <strong>{q.customerLabel || "-"}</strong>
            <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
              #{q.quoteNo || "-"} | {getStageLabelForQuote(q)}
            </div>
            <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
              <span
                style={{
                  padding: "4px 10px",
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 700,
                  background:
                    q.quoteTypeResolved === GENERAL_QUOTE_TYPE ? "#dbeafe" : "#ede9fe",
                  color:
                    q.quoteTypeResolved === GENERAL_QUOTE_TYPE ? "#1d4ed8" : "#7c3aed",
                }}
              >
                {q.quoteTypeResolved === GENERAL_QUOTE_TYPE
                  ? "General Quotation"
                  : "Curtains"}
              </span>
              <span
                style={{
                  padding: "4px 10px",
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 700,
                  background: q.isConfirmed ? "#dcfce7" : "#fef3c7",
                  color: q.isConfirmed ? "#166534" : "#92400e",
                }}
              >
                {q.isConfirmed ? "Confirmed" : "Draft"}
              </span>
            </div>
          </div>

          <div style={{ textAlign: isCompact ? "left" : "right" }}>
            <strong>
              {formatCurrencyDisplay(q.totalValue || 0, currency)}
            </strong>
            <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 6 }}>
              Created {q.createdMillis ? new Date(q.createdMillis).toLocaleDateString() : "-"}
            </div>
            <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 2 }}>
              Updated {q.updatedMillis ? new Date(q.updatedMillis).toLocaleDateString() : "-"}
            </div>
          </div>
        </div>
      ))}
      {dashboardListQuotes.length === 0 && (
        <div style={{ ...dashboardCardStyle, color: dashboardMuted }}>
          No quotations found for this dashboard view.
        </div>
      )}
    </div>
  </div>
)}


{/* ================= CRM TAB ================= */}
{tab === "crm" && renderCrmWorkspace()}
{tab === "customerLedger" && renderCustomerLedger()}
{tab === "todayTasks" && renderTodayTasks()}
{tab === "executionSpecs" && renderExecutionWorkspace()}
{tab === "executionTemplates" && renderExecutionTemplatesPage()}
{tab === "products" && renderProductCalculationsPage()}
{tab === "accountSecurity" && renderAccountSecurityPage()}
{IS_DEV_UI_ENABLED && tab === "maintenance" && renderMaintenancePage()}

        {/* === QUOTE TAB === */}
      {/* === QUOTE TAB === */}
      {tab === "teamAccess" && canAccessTeamAccess ? (
        <div
          style={{
            ...dashboardCardStyle,
            padding: isCompact ? 18 : 24,
            display: "grid",
            gap: 18,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 16,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <div>
              <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#2563eb" }}>
                Team & Access
              </div>
              <h2 style={{ margin: "6px 0 4px", fontSize: isCompact ? 24 : 30, fontWeight: 900, letterSpacing: "-0.04em" }}>
                Manage account members and feature visibility
              </h2>
              <p style={{ margin: 0, color: dashboardMuted, fontSize: 13, lineHeight: 1.6 }}>
                Super admins, owners, and account admins can review the active plan, member status, and feature toggles. Existing quotation data remains under the legacy workspace path.
              </p>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {isSuperAdmin ? (
                <a href="/super-admin" style={{ ...btn("#2563eb"), textDecoration: "none", marginRight: 0 }}>
                  Open Super Admin
                </a>
              ) : null}
              <button
                type="button"
                style={{ ...btn("#0f172a"), marginRight: 0 }}
                onClick={openTeamMemberInviteModal}
              >
                Add team member
              </button>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact ? "1fr" : "repeat(3, minmax(0, 1fr))",
              gap: 12,
            }}
          >
            {[
              ["Account", account?.name || "Not linked"],
              ["Plan", accountUser?.plan || account?.plan || "full"],
              ["Status", account?.isActive ? "active" : "inactive"],
              ...(isTrialAccount
                ? [[
                    "Free Trial",
                    `${trialQuotationsUsed || 0} / ${trialQuotationLimit || 10} used${
                      Number(trialQuotationsRemaining || 0) > 0
                        ? ` | ${trialQuotationsRemaining} left`
                        : ""
                    }`,
                  ]]
                : []),
            ].map(([label, value]) => (
              <div key={label} style={{ ...dashboardCardStyle, background: dashboardSoftBg, padding: 16 }}>
                <div style={{ fontSize: 12, color: dashboardMuted, marginBottom: 6 }}>{label}</div>
                <div style={{ fontSize: 20, fontWeight: 900 }}>{value}</div>
              </div>
            ))}
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0 10px", minWidth: 860 }}>
              <thead>
                <tr>
                  {["Name", "Email", "Role", "Plan", "Status", "Account", "Actions"].map((heading) => (
                    <th key={heading} style={{ textAlign: "left", fontSize: 12, color: dashboardMuted, padding: "0 12px" }}>
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {teamAccessRows.map((member) => (
                  <tr key={member.id}>
                    <td style={teamCellStyle}>{member.name}</td>
                    <td style={teamCellStyle}>{member.email || "No email"}</td>
                    <td style={teamCellStyle}>{member.role}</td>
                    <td style={teamCellStyle}>{member.plan}</td>
                    <td style={teamCellStyle}>
                      <span style={{ ...statusPillStyle, background: member.isActive ? "#dcfce7" : "#fee2e2", color: member.isActive ? "#166534" : "#991b1b" }}>
                        {member.isActive ? "active" : "inactive"}
                      </span>
                    </td>
                    <td style={teamCellStyle}>{member.accountName}</td>
                    <td style={teamCellStyle}>
                      {isSuperAdmin ? (
                        <a href="/super-admin" style={{ color: "#2563eb", fontWeight: 800, textDecoration: "none" }}>
                          Edit access
                        </a>
                      ) : (
                        <span style={{ color: dashboardMuted }}>Owner settings</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ ...dashboardCardStyle, background: dashboardSoftBg, padding: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <UserCog size={20} color="#2563eb" />
              <div style={{ fontSize: 18, fontWeight: 900 }}>Current enabled features</div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 10 }}>
              {accessFeatureLabels.map(([key, label]) => (
                <div
                  key={key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    border: dashboardBorder,
                    borderRadius: 14,
                    padding: "12px 14px",
                    background: isDark ? "rgba(15,23,42,0.65)" : "#ffffff",
                  }}
                >
                  <span style={{ fontWeight: 800 }}>{label}</span>
                  <span style={{ ...statusPillStyle, background: features?.[key] ? "#dbeafe" : "#f1f5f9", color: features?.[key] ? "#1d4ed8" : "#64748b" }}>
                    {features?.[key] ? "enabled" : "locked"}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {renderTeamMembersManager()}
        </div>
      ) : null}

      <div
        style={{
          display: tab === "quote" ? "block" : "none",
    background: isDark ? "#151520" : "#ffffff",
    padding: 24,
    borderRadius: 16,
    boxShadow: isDark
      ? "0px 0px 20px rgba(0,0,0,0.4)"
      : "0px 4px 18px rgba(0,0,0,0.06)",
  }}
>
  <div
    style={{
      display: "inline-flex",
      gap: 8,
      padding: 6,
      borderRadius: 18,
      marginBottom: 22,
      background: isDark ? "#0f172a" : "#f8fafc",
      border: isDark
        ? "1px solid rgba(148,163,184,0.14)"
        : "1px solid rgba(226,232,240,0.9)",
    }}
  >
    {[
      { key: CURTAIN_QUOTE_TYPE, label: "Curtains" },
      { key: GENERAL_QUOTE_TYPE, label: "General Quotation" },
    ]
      .filter((item) =>
        item.key === CURTAIN_QUOTE_TYPE
          ? canAccessCurtainQuotes
          : canAccessGeneralQuotes
      )
      .map((item) => {
      const active = quoteSubTab === item.key;
      return (
        <button
          key={item.key}
          type="button"
          onClick={() => setRoutedTab("quote", item.key)}
          style={{
            border: "none",
            cursor: "pointer",
            borderRadius: 14,
            padding: "10px 16px",
            fontSize: 14,
            fontWeight: 700,
            background: active
              ? isDark
                ? "rgba(37,99,235,0.22)"
                : "#dbeafe"
              : "transparent",
            color: active
              ? "#2563eb"
              : isDark
              ? "#cbd5e1"
              : "#475569",
            transition: "all 0.2s ease",
          }}
        >
          {item.label}
        </button>
      );
    })}
  </div>

  {quoteEditorReadOnly ? (
    <div
      style={{
        marginBottom: 16,
        padding: "12px 14px",
        borderRadius: 14,
        background: isDark ? "rgba(37,99,235,0.14)" : "#eff6ff",
        border: "1px solid rgba(37,99,235,0.18)",
        fontSize: 13,
        fontWeight: 700,
        color: isDark ? "#bfdbfe" : "#1d4ed8",
      }}
    >
      View-only access: you can review quotations in this workspace, but saving changes is disabled for your role.
    </div>
  ) : null}

  <div
    style={{
      pointerEvents: quoteEditorReadOnly ? "none" : "auto",
      opacity: quoteEditorReadOnly ? 0.84 : 1,
      transition: "opacity 0.2s ease",
    }}
  >
  {quoteSubTab === CURTAIN_QUOTE_TYPE ? (

          <div
            onBlurCapture={() => persistCurtainQuoteDraft("blur")}
            style={{
              background: isDark ? "#151520" : "#ffffff",
              padding: 24,
              borderRadius: 16,
              boxShadow: isDark
                ? "0px 0px 20px rgba(0,0,0,0.4)"
                : "0px 4px 18px rgba(0,0,0,0.06)",
            }}
          >
{!selectedQuote && (
  <div style={{ padding: 20, opacity: 0.7 }}>
   
  </div>
)}

            {legacyRecalculationModalOpen && legacyRecalculationPreview ? (
              <div
                role="dialog"
                aria-modal="true"
                aria-label="Review calculation changes"
                style={{
                  position: "fixed",
                  inset: 0,
                  zIndex: 9999,
                  background: "rgba(15,23,42,0.58)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: isCompact ? 12 : 24,
                }}
              >
                <div
                  style={{
                    width: "min(1100px, 96vw)",
                    maxHeight: "90vh",
                    overflow: "auto",
                    borderRadius: 24,
                    background: isDark ? "#111827" : "#ffffff",
                    color: isDark ? "#f8fafc" : "#111827",
                    boxShadow: "0 28px 90px rgba(15,23,42,0.38)",
                    border: isDark
                      ? "1px solid rgba(148,163,184,0.24)"
                      : "1px solid rgba(226,232,240,0.9)",
                  }}
                >
                  <div
                    style={{
                      padding: isCompact ? 18 : 24,
                      borderBottom: isDark
                        ? "1px solid rgba(148,163,184,0.18)"
                        : "1px solid #e2e8f0",
                      display: "flex",
                      flexDirection: isCompact ? "column" : "row",
                      justifyContent: "space-between",
                      gap: 14,
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 900,
                          letterSpacing: "0.09em",
                          textTransform: "uppercase",
                          color:
                            legacyRecalculationPreview.recalculationImpact ===
                            LEGACY_CALCULATION_IMPACT.NO_IMPACT
                              ? isDark
                                ? "#86efac"
                                : "#15803d"
                              : isDark
                              ? "#fbbf24"
                              : "#b45309",
                        }}
                      >
                        {legacyRecalculationPreview.recalculationImpact ===
                        LEGACY_CALCULATION_IMPACT.NO_IMPACT
                          ? "Calculation check"
                          : "Legacy curtain calculation"}
                      </div>
                      <h2 style={{ margin: "4px 0 6px", fontSize: isCompact ? 22 : 28 }}>
                        {legacyRecalculationPreview.recalculationImpact ===
                        LEGACY_CALCULATION_IMPACT.NO_IMPACT
                          ? "Calculation Check"
                          : "Review Calculation Changes"}
                      </h2>
                      <div style={{ fontSize: 13, color: dashboardMuted, fontWeight: 700 }}>
                        Quote {legacyRecalculationPreview.quoteNo || quoteNo || "-"} ·{" "}
                        {legacyRecalculationPreview.customerName || customerName || "Customer"} ·{" "}
                        rows changed: {legacyRecalculationPreview.affectedRowCount}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setLegacyRecalculationModalOpen(false);
                        setLegacyRecalculationPreview(null);
                      }}
                      style={{ ...btn(isDark ? "#1f2937" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"), marginRight: 0 }}
                    >
                      Cancel
                    </button>
                  </div>

                  <div style={{ padding: isCompact ? 18 : 24, display: "grid", gap: 18 }}>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isCompact
                          ? "1fr"
                          : "repeat(3, minmax(0, 1fr))",
                        gap: 12,
                      }}
                    >
                      {[
                        {
                          label: "Saved subtotal",
                          oldValue: legacyRecalculationPreview.oldTotals?.subtotalBeforeGst,
                          newValue: legacyRecalculationPreview.newTotals?.subtotalBeforeGst,
                          diff: legacyRecalculationPreview.difference?.subtotal,
                        },
                        {
                          label: "GST",
                          oldValue:
                            legacyRecalculationPreview.oldTotals?.totalGstAmount ??
                            legacyRecalculationPreview.oldTotals?.gstAmount,
                          newValue:
                            legacyRecalculationPreview.newTotals?.totalGstAmount ??
                            legacyRecalculationPreview.newTotals?.gstAmount,
                          diff: legacyRecalculationPreview.difference?.gst,
                        },
                        {
                          label: "Grand total",
                          oldValue: legacyRecalculationPreview.oldTotals?.grandTotal,
                          newValue: legacyRecalculationPreview.newTotals?.grandTotal,
                          diff: legacyRecalculationPreview.difference?.grandTotal,
                          highlight: true,
                        },
                      ].map((card) => (
                        <div
                          key={card.label}
                          style={{
                            padding: 16,
                            borderRadius: 18,
                            background: card.highlight
                              ? isDark
                                ? "linear-gradient(135deg, rgba(30,64,175,0.5), rgba(15,23,42,0.96))"
                                : "linear-gradient(135deg, #eff6ff, #ffffff)"
                              : isDark
                              ? "rgba(15,23,42,0.72)"
                              : "#f8fafc",
                            border: isDark
                              ? "1px solid rgba(148,163,184,0.18)"
                              : "1px solid #e2e8f0",
                          }}
                        >
                          <div style={{ fontSize: 12, fontWeight: 900, color: dashboardMuted }}>
                            {card.label}
                          </div>
                          <div style={{ display: "grid", gap: 5, marginTop: 8 }}>
                            <strong>Saved: {formatCurrencyDisplay(card.oldValue || 0, currency)}</strong>
                            <strong>New: {formatCurrencyDisplay(card.newValue || 0, currency)}</strong>
                            <span
                              style={{
                                color: Number(card.diff || 0) >= 0 ? "#16a34a" : "#dc2626",
                                fontWeight: 900,
                              }}
                            >
                              Difference: {Number(card.diff || 0) >= 0 ? "+" : ""}
                              {formatCurrencyDisplay(card.diff || 0, currency)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div
                      style={{
                        padding: 14,
                        borderRadius: 16,
                        background:
                          legacyRecalculationPreview.recalculationImpact ===
                          LEGACY_CALCULATION_IMPACT.NO_IMPACT
                            ? isDark
                              ? "rgba(22,101,52,0.2)"
                              : "#f0fdf4"
                            : isDark
                            ? "rgba(120,53,15,0.24)"
                            : "#fffbeb",
                        color:
                          legacyRecalculationPreview.recalculationImpact ===
                          LEGACY_CALCULATION_IMPACT.NO_IMPACT
                            ? isDark
                              ? "#bbf7d0"
                              : "#166534"
                            : isDark
                            ? "#fde68a"
                            : "#78350f",
                        border:
                          legacyRecalculationPreview.recalculationImpact ===
                          LEGACY_CALCULATION_IMPACT.NO_IMPACT
                            ? isDark
                              ? "1px solid rgba(74,222,128,0.25)"
                              : "1px solid #bbf7d0"
                            : isDark
                            ? "1px solid rgba(245,158,11,0.28)"
                            : "1px solid #fde68a",
                        fontSize: 13,
                        fontWeight: 750,
                        lineHeight: 1.55,
                      }}
                    >
                      {legacyRecalculationPreview.recalculationImpact ===
                      LEGACY_CALCULATION_IMPACT.NO_IMPACT
                        ? "No changes found. Saved values and the latest formula match, so no updated revision is needed."
                        : "This will not change the original quote unless you save a new revision. Customer and workshop PDFs continue to use the saved values."}
                    </div>

                    <div style={{ overflowX: "auto" }}>
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "separate",
                          borderSpacing: 0,
                          minWidth: 860,
                          fontSize: 13,
                        }}
                      >
                        <thead>
                          <tr>
                            {[
                              "Item",
                              "Product",
                              "Fabric / Code",
                              "Field changed",
                              "Old value",
                              "New value",
                              "Difference",
                              "Reason",
                            ].map((heading) => (
                              <th
                                key={heading}
                                style={{
                                  textAlign: "left",
                                  padding: "10px 12px",
                                  background: isDark ? "#1f2937" : "#f1f5f9",
                                  color: isDark ? "#cbd5e1" : "#475569",
                                  fontSize: 11,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.06em",
                                }}
                              >
                                {heading}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {(legacyRecalculationPreview.rowDiffRows || []).map((entry, index) => (
                            <tr key={`${entry.rowId}-${entry.field}-${index}`}>
                              <td style={{ padding: "10px 12px", borderTop: isDark ? "1px solid #1f2937" : "1px solid #e2e8f0", fontWeight: 800 }}>
                                {entry.itemName}
                              </td>
                              <td style={{ padding: "10px 12px", borderTop: isDark ? "1px solid #1f2937" : "1px solid #e2e8f0" }}>
                                {entry.product || "-"}
                              </td>
                              <td style={{ padding: "10px 12px", borderTop: isDark ? "1px solid #1f2937" : "1px solid #e2e8f0" }}>
                                {entry.fabric || "-"}
                              </td>
                              <td style={{ padding: "10px 12px", borderTop: isDark ? "1px solid #1f2937" : "1px solid #e2e8f0", fontWeight: 900 }}>
                                {entry.field}
                              </td>
                              <td style={{ padding: "10px 12px", borderTop: isDark ? "1px solid #1f2937" : "1px solid #e2e8f0" }}>
                                {formatLegacyRecalculationValue(entry, entry.oldValue)}
                              </td>
                              <td style={{ padding: "10px 12px", borderTop: isDark ? "1px solid #1f2937" : "1px solid #e2e8f0" }}>
                                {formatLegacyRecalculationValue(entry, entry.newValue)}
                              </td>
                              <td
                                style={{
                                  padding: "10px 12px",
                                  borderTop: isDark ? "1px solid #1f2937" : "1px solid #e2e8f0",
                                  color:
                                    entry.difference == null
                                      ? dashboardMuted
                                      : Number(entry.difference || 0) >= 0
                                      ? "#16a34a"
                                      : "#dc2626",
                                  fontWeight: 900,
                                }}
                              >
                                {entry.difference == null ? (
                                  "Not comparable"
                                ) : (
                                  <>
                                    {Number(entry.difference || 0) >= 0 ? "+" : ""}
                                    {formatLegacyRecalculationValue(
                                      { ...entry, oldExists: true },
                                      entry.difference
                                    )}
                                  </>
                                )}
                              </td>
                              <td style={{ padding: "10px 12px", borderTop: isDark ? "1px solid #1f2937" : "1px solid #e2e8f0", maxWidth: 260 }}>
                                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                  {(entry.reasonCodes || []).map((reason) => (
                                    <span
                                      key={reason}
                                      style={{
                                        padding: "3px 7px",
                                        borderRadius: 999,
                                        background: isDark
                                          ? "rgba(148,163,184,0.14)"
                                          : "#f1f5f9",
                                        color: isDark ? "#e2e8f0" : "#334155",
                                        fontSize: 11,
                                        fontWeight: 800,
                                      }}
                                    >
                                      {reason}
                                    </span>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          ))}
                          {!legacyRecalculationPreview.rowDiffRows?.length ? (
                            <tr>
                              <td colSpan={8} style={{ padding: 18, textAlign: "center", color: dashboardMuted }}>
                                No row-level numeric differences were found.
                              </td>
                            </tr>
                          ) : null}
                        </tbody>
                      </table>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "flex-end",
                        gap: 10,
                      }}
                    >
                      <button
                        type="button"
                        onClick={downloadLegacyRecalculationReport}
                        style={{ ...btn(isDark ? "#334155" : "#e2e8f0", isDark ? "#f8fafc" : "#0f172a"), marginRight: 0 }}
                      >
                        Download Difference Report
                      </button>
                      {legacyRecalculationPreview.recalculationImpact ===
                        LEGACY_CALCULATION_IMPACT.NO_IMPACT && canEditQuotes ? (
                        <button
                          type="button"
                          onClick={() => markLegacyCalculationNoImpactReviewed()}
                          style={{ ...btn("#16a34a", "#ffffff"), marginRight: 0 }}
                        >
                          Mark as Reviewed
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => {
                          setLegacyRecalculationModalOpen(false);
                          setLegacyRecalculationPreview(null);
                        }}
                        style={{ ...btn(isDark ? "#1f2937" : "#cbd5e1", isDark ? "#f8fafc" : "#0f172a"), marginRight: 0 }}
                      >
                        {legacyRecalculationPreview.recalculationImpact ===
                        LEGACY_CALCULATION_IMPACT.NO_IMPACT
                          ? "Close"
                          : "Cancel"}
                      </button>
                      {legacyRecalculationPreview.recalculationImpact !==
                      LEGACY_CALCULATION_IMPACT.NO_IMPACT ? (
                        <button
                          type="button"
                          disabled={!canCreateQuotes || isSaving}
                          onClick={() => saveLegacyRecalculationAsNewRevision()}
                          style={{
                            ...btn("#2563eb", "#ffffff"),
                            marginRight: 0,
                            opacity: !canCreateQuotes || isSaving ? 0.65 : 1,
                          }}
                        >
                          Create Updated Revision
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {activeCurtainRecalculationAudit ? (
              <div
                style={{
                  marginBottom: 18,
                  padding: isCompact ? 14 : 16,
                  borderRadius: 18,
                  border: isDark
                    ? "1px solid rgba(59,130,246,0.35)"
                    : "1px solid rgba(37,99,235,0.18)",
                  background: isDark
                    ? "linear-gradient(135deg, rgba(30,64,175,0.28), rgba(15,23,42,0.82))"
                    : "linear-gradient(135deg, #eff6ff, #ffffff)",
                  display: "flex",
                  flexDirection: isCompact ? "column" : "row",
                  justifyContent: "space-between",
                  alignItems: isCompact ? "stretch" : "center",
                  gap: 12,
                }}
              >
                <div style={{ display: "grid", gap: 5 }}>
                  <div style={{ fontSize: 13, fontWeight: 900, color: isDark ? "#93c5fd" : "#1d4ed8" }}>
                    Calculation updated from older formula
                  </div>
                  <div style={{ fontSize: 13, color: dashboardMuted, fontWeight: 700 }}>
                    Previous grand total{" "}
                    {formatCurrencyDisplay(activeCurtainRecalculationAudit.oldGrandTotal || 0, currency)}
                    {" "}→{" "}
                    {formatCurrencyDisplay(activeCurtainRecalculationAudit.newGrandTotal || 0, currency)}
                    {" "}· rows changed: {activeCurtainRecalculationAudit.changedRows?.length || 0}
                  </div>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => {
                      const preview = {
                        id: `audit-${Date.now()}`,
                        quoteNo,
                        customerName,
                        oldTotals: {
                          subtotalBeforeGst: activeCurtainRecalculationAudit.oldSubtotal,
                          totalGstAmount: activeCurtainRecalculationAudit.oldGst,
                          grandTotal: activeCurtainRecalculationAudit.oldGrandTotal,
                        },
                        newTotals: {
                          subtotalBeforeGst: activeCurtainRecalculationAudit.newSubtotal,
                          totalGstAmount: activeCurtainRecalculationAudit.newGst,
                          grandTotal: activeCurtainRecalculationAudit.newGrandTotal,
                        },
                        difference: {
                          subtotal: round2(
                            Number(activeCurtainRecalculationAudit.newSubtotal || 0) -
                              Number(activeCurtainRecalculationAudit.oldSubtotal || 0)
                          ),
                          gst: round2(
                            Number(activeCurtainRecalculationAudit.newGst || 0) -
                              Number(activeCurtainRecalculationAudit.oldGst || 0)
                          ),
                          grandTotal: Number(activeCurtainRecalculationAudit.difference || 0),
                        },
                        changedRows: activeCurtainRecalculationAudit.changedRows || [],
                        rowDiffRows: (activeCurtainRecalculationAudit.changedRows || []).map((row) => ({
                          ...row,
                          field: "Row total",
                          type: "money",
                          oldValue: row.oldTotal,
                          newValue: row.newTotal,
                          difference: row.difference,
                        })),
                        affectedRowCount:
                          activeCurtainRecalculationAudit.changedRows?.length || 0,
                      };
                      setLegacyRecalculationPreview(preview);
                      setLegacyRecalculationModalOpen(true);
                    }}
                    style={{ ...btn("#2563eb"), marginRight: 0 }}
                  >
                    View Recalculation Details
                  </button>
                  {canRevertLegacyCurtainRecalculation &&
                  activeCurtainLegacyCalculationSnapshot?.rows?.length ? (
                    <button
                      type="button"
                      onClick={revertLegacyCurtainRecalculation}
                      style={{ ...btn("#dc2626"), marginRight: 0 }}
                    >
                      Revert to Previous Calculation
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}

            {shouldShowLegacyCalculationBanner ? (
              <div
                style={{
                  marginBottom: 18,
                  padding: isCompact ? 14 : 16,
                  borderRadius: 18,
                  border: isDark
                    ? "1px solid rgba(245,158,11,0.38)"
                    : "1px solid rgba(245,158,11,0.42)",
                  background: isDark
                    ? "linear-gradient(135deg, rgba(120,53,15,0.28), rgba(15,23,42,0.78))"
                    : "linear-gradient(135deg, #fffbeb, #ffffff)",
                  boxShadow: isDark
                    ? "0 18px 34px rgba(2,6,23,0.24)"
                    : "0 16px 32px rgba(245,158,11,0.12)",
                  display: "flex",
                  flexDirection: isCompact ? "column" : "row",
                  justifyContent: "space-between",
                  alignItems: isCompact ? "stretch" : "center",
                  gap: 14,
                }}
              >
                <div style={{ display: "grid", gap: 5 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 900,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      color: isDark ? "#fbbf24" : "#92400e",
                    }}
                  >
                    Older calculation version detected
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      lineHeight: 1.6,
                      color: isDark ? "#fde68a" : "#78350f",
                    }}
                  >
                    This quote was created with an older calculation version. We are showing
                    the saved values exactly as originally saved. To update it with the latest
                    formula, create a new revision.
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 10,
                      fontSize: 12,
                      color: dashboardMuted,
                      fontWeight: 700,
                    }}
                  >
                    <span>
                      Rows affected:{" "}
                      {legacyRecalculationImpactPreview?.actualChangedRows || 0}
                    </span>
                    <button
                      type="button"
                      onClick={openLegacyRecalculationPreview}
                      style={{
                        border: "none",
                        background: "transparent",
                        color: isDark ? "#fbbf24" : "#b45309",
                        fontWeight: 900,
                        padding: 0,
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      View Changes
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  disabled={!canCreateQuotes || isSaving}
                  onClick={() => {
                    const preview =
                      legacyRecalculationImpactPreview ||
                      buildLegacyCurtainRecalculationPreview();
                    if (!preview) return;
                    saveLegacyRecalculationAsNewRevision(preview);
                  }}
                  style={{
                    ...btn("#f59e0b", "#111827"),
                    marginRight: 0,
                    whiteSpace: "nowrap",
                    opacity: !canCreateQuotes || isSaving ? 0.65 : 1,
                  }}
                >
                  Create New Revision
                </button>
              </div>
            ) : null}

            {/* CUSTOMER INFO */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isCompact
                  ? "1fr"
                  : "repeat(3, 1fr)",
                gap: 16,
                marginBottom: 16,
              }}
            >
              {renderCustomerLedgerCombobox({
                context: CURTAIN_QUOTE_TYPE,
                label: "Customer Name",
                value: customerName,
                selectedCustomerId:
                  quoteCustomerLink.customerId ||
                  selectedQuote?.customerId ||
                  selectedQuote?.crm?.customerId ||
                  "",
                seed: quoteCustomerLink,
                onTextChange: (nextValue) => {
                  setCustomerName(nextValue);
                  setQuoteCustomerLink((prev) => ({
                    ...prev,
                    customerId: "",
                    customerName: nextValue,
                  }));
                },
              })}

            <div>
  <label style={{ fontSize: 13, opacity: 0.7 }}>
  Quote Number
</label>

<div style={{ position: "relative", width: "100%" }}>
  <input
    style={{
      ...input,
      paddingRight: 0, // space for refresh button
    }}
    value={quoteNo}
    onChange={(e) => setQuoteNo(e.target.value)}
    placeholder="Quote number will be generated when saving"
    pattern=".*[0-9].*"   // must contain a number
  />

 {/*  Quote number generated automatically on save */}
<button
  type="button"
  title="Quote number will be generated when saving"
  disabled
  style={{
    position: "absolute",
    right: 8,
    top: "50%",
    transform: "translateY(-50%)",

    width: 28,
    height: 28,
    borderRadius: 8,

    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    color: "#94a3b8",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "not-allowed",
    fontSize: 14,
  }}
>

</button>
</div>
</div>


              <div>
                <label style={{ fontSize: 13, opacity: 0.7 }}>Date</label>
                <input
                  style={input}
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>
            </div>

    

            {/* TABLE */}
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                margin: "6px 0 14px",
              }}
            >
              <div>
                <div style={{ fontSize: 16, fontWeight: 900 }}>Curtains & Blinds</div>
                <div style={{ fontSize: 12, color: dashboardMuted }}>
                  Measurement rows use width, height, fabric, stitching, and fabric demand.
                </div>
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <Button icon={Plus} variant="success" onClick={addCurtainMeasurementRow}>
                  Add Curtain/Blind Row
                </Button>
                <Button icon={Plus} variant="primary" onClick={addCurtainAccessoryRow}>
                  Add Accessory Row
                </Button>
              </div>
            </div>
            {isCompact ? (
              <div style={{ display: "grid", gap: 14 }}>
                {curtainMeasurementEntries.map(({ row: r, index: i }) =>
                  renderCurtainCompactRow(r, i)
                )}
                {curtainMeasurementEntries.length === 0 ? (
                  <div style={{ ...compactCardStyle, color: dashboardMuted }}>
                    No curtain/blind measurement rows yet. Add a measurement row or use
                    accessories only for hardware/service quotations.
                  </div>
                ) : null}
              </div>
            ) : (
            <div className="quote-table-wrapper" style={{ overflowX: "auto", paddingBottom: 8 }}>
    <table
  style={{
    width: "max-content",
    minWidth: curtainTableMinWidth,
    borderCollapse: "collapse",
    tableLayout: "fixed",
  }}
>

  {/* COLUMN WIDTH CONTROL */}
  <colgroup>
    <col style={colItem} />
    <col style={colProduct} />
    <col style={colFabric} />

    <col style={colXS} />   {/* Fab W */}
    <col style={colWH} />   {/* W */}
    <col style={colWH} />   {/* H */}
    <col style={colM} />    {/* MRP/m */}
    <col style={colXS} />   {/* Disc */}
    <col style={colS} />    {/* Rate/sqft */}

    <col style={colStitch} />   {/* Stitch */}
    <col style={colLining} />   {/* Lining */}

    <col style={colFabricM} />  {/* Ordered Fabric (m) */}
    <col style={colTotal} />    {/* Total */}

    <col style={colActions} />  {/* Actions */}
  </colgroup>

  <thead>
    <tr>
      <th>Item</th>
      <th>Product</th>
      <th>Fabric / Code</th>
     <th>Fab W (in)</th>

<th>W ({unitLabel(unit)})</th>
<th>H ({unitLabel(unit)})</th>
      <th>MRP/m</th>
      <th>Disc %</th>
      <th>Rate</th>
      <th>Stitch</th>
      <th>Lining</th>
      <th>Fabric (m)</th>
      <th style={stickyTotalHeaderStyle}>Total</th>
      <th style={stickyActionsHeaderStyle}>Actions</th>
    </tr>
  </thead>


                <tbody>
                  {curtainMeasurementEntries.map(({ row: r, index: i }) => {
                    const c = getCurtainProductionAdjustedRowCalc(r, i);
                    const rowRules = getRowProductRules(r);
                    const orderedFabricAudit = getCurtainOrderedFabricDisplayTrace(r, i, c);
                    const orderedFabricTooltip = getOrderedFabricTooltipText(orderedFabricAudit);
                    const requiredFields = rowRules.requiredFields || {};
                    const rateMeta = getRowRateFieldMeta(r);
                    const fabricInputVisible = shouldShowFabricInput(r, rowRules.config);
                    const fabricInputEnabled = shouldEnableFabricInput(r, rowRules.config);
                    const fabricInputLabel = getFabricInputLabel(rowRules.config);
                    const rowId = getWorkshopRowStableId(r) || `measurement-${i}`;
                    const rowSurface =
                      i % 2 === 0
                        ? isDark
                          ? "#1e1e26"
                          : "#ffffff"
                        : isDark
                        ? "#2a2a34"
                        : "#f9fafb";
                    const rowTrackConfig = getCurtainTrackConfigForUi(r, c);
                    const showTrackConfigPanel = shouldShowCurtainTrackConfigPanel(r, c);
                    const rowDiscountScope = c.discountScope || getRowDiscountScope(r, rowRules.config, {
                      hasFabricMeterComponent: Boolean(c.isCurtain || c.isRoman),
                      fabricGrossAmount: c.fabricGrossAmount || 0,
                    });
                    const discountInputEnabled = rowDiscountScope !== "none";
                    const discountTooltip =
                      rowDiscountScope === "fabric_only"
                        ? "Applies only to fabric amount."
                        : rowDiscountScope === "line_total"
                        ? "Applies to this line amount."
                        : "Discount is disabled for this product.";

    // --- RAW INPUT SYNC (fix typing issues) ---

                    return (
                      <React.Fragment key={getWorkshopRowStableId(r) || `measurement-${i}`}>
                      <tr
                        style={{
                          background: rowSurface,
                        }}
                      >
                       {/* ITEM */}
<td style={{ padding: 8, ...colItem }}>
  <input
    style={input}
    value={r.item}
    onChange={(e) => updateField(rowId, "item", e.target.value)}
  />
</td>

{/* PRODUCT */}
<td style={{ padding: 8, ...colProduct }}>
  {(() => {
    const savedProductOption = getCurtainRowSavedProductOption(r);
    return (
  <select
    style={selectInput}
    value={getCurtainRowProductSelectValue(r)}
    onChange={(e) => updateField(rowId, "productType", e.target.value)}
  >
    {savedProductOption ? (
      <option value={savedProductOption.value}>{savedProductOption.label}</option>
    ) : null}
    {curtainProductOptions.map((product) => (
      <option key={product.id} value={product.id}>
        {product.name}
      </option>
    ))}
  </select>
    );
  })()}
</td>

{/* FABRIC */}
<td style={{ padding: 8, ...colFabric }}>
  {fabricInputVisible ? (
    renderFabricCatalogueCombobox({
      row: r,
      rowId,
      rowIndex: i,
      label: fabricInputLabel || "Fabric / Code",
      disabled: !fabricInputEnabled,
    })
  ) : null}
</td>


{/* FAB W (in) */}
<td style={{ padding: 8, ...colXS }}>
  <input
    type="text"
    style={input}
    placeholder="in"   //  ghost text when empty
    value={
      r.fabricWidthIn === "" || r.fabricWidthIn == null
        ? ""
        : r.fabricWidthIn
    }
    onChange={(e) => {
      const v = e.target.value;

      if (v === "") {
        updateField(rowId, "fabricWidthIn", "");
        return;
      }

      if (v === "-" || v === "." || v === "-.") return;

      if (!isPermittedDecimalInput(v)) return;

      updateField(rowId, "fabricWidthIn", v);
    }}
    disabled={!requiredFields.fabricWidth}
  />
</td>


{/* WIDTH */}
<td style={{ padding: 8, ...colWH }}>
  <input
    type="text"
    inputMode="decimal"
    step="0.01"
    style={input}
    placeholder={unitLabel(unit)}
    value={getDimensionInputValueForUnit(r.width, unit)}
    onChange={(e) => {
      const v = e.target.value;

      const storedValue = getStoredDimensionValueFromInput(v, unit);
      if (storedValue == null) return;

      updateField(rowId, "width", storedValue);
    }}
  />
</td>


{/* HEIGHT */}
<td style={{ padding: 8, ...colWH }}>
  <input
    type="text"
    inputMode="decimal"
    step="0.01"
    style={input}
    placeholder={unitLabel(unit)}
    value={getDimensionInputValueForUnit(r.height, unit)}
    onChange={(e) => {
      const v = e.target.value;

      const storedValue = getStoredDimensionValueFromInput(v, unit);
      if (storedValue == null) return;

      updateField(rowId, "height", storedValue);
    }}
  />
</td>


{/* MRP/m */}
<td style={{ padding: 8, ...colM }}>
  <input
    type="text"
    inputMode="decimal"
    disabled={!requiredFields.mrpPerMeter}
    style={{
      ...input,
      opacity: requiredFields.mrpPerMeter ? 1 : 0.35,
    }}
    value={r.mrp ?? ""}
    onChange={(e) => updateField(rowId, "mrp", e.target.value)}
    onBlur={(e) =>
      handleCurtainRowMrpBlur(rowId, {
        ...r,
        mrp: e.target.value,
        mrpPerMeter: e.target.value,
      })
    }
  />
</td>

{/* DISCOUNT */}
<td style={{ padding: 8, ...colXS }}>
  <input
    type="text"
    inputMode="decimal"
    disabled={!discountInputEnabled}
    title={discountTooltip}
    style={{
      ...input,
      opacity: discountInputEnabled ? 1 : 0.45,
    }}
    value={r.discount}
    onChange={(e) => updateField(rowId, "discount", e.target.value)}
  />
</td>

{/* RATE/SQFT */}
<td style={{ padding: 8, ...colS }}>
  {rateMeta.hidden ? (
    <div
      title="Not applicable"
      style={{
        minHeight: 36,
        borderRadius: input.borderRadius,
        border: input.border,
        background: isDark ? "rgba(15,23,42,0.42)" : "#f8fafc",
        opacity: 0.45,
      }}
    />
  ) : (
    <input
      type="text"
      inputMode="decimal"
      disabled={rateMeta.disabled}
      title={rateMeta.label}
      placeholder={rateMeta.label}
      style={{ ...input, opacity: rateMeta.disabled ? 0.4 : 1 }}
      value={rateMeta.field ? r[rateMeta.field] ?? "" : ""}
      onChange={(e) => {
        if (!rateMeta.field) return;
        updateField(rowId, rateMeta.field, e.target.value);
      }}
    />
  )}
</td>

{/* STITCH */}
{/* STITCH */}
{requiredFields.stitching ? (
  <td style={{ padding: 8, ...colStitch }}>
    <select
      style={selectInput}
      value={r.stitchingStyle || ""}
      onChange={(e) => {
        const val = e.target.value;
        updateField(rowId, "stitchingStyle", val);

        //  Clear custom rate if not custom
        if (val !== "Eyelet (Custom)") {
          updateField(rowId, "customStitchRate", "");
        }
      }}
    >
      <option value="">Select</option>
      {Object.keys(stitchingRates).map((s) => (
        <option key={s} value={s}>
          {s}
        </option>
      ))}
    </select>
    {isCurtain(r.productType) && isEyeletCustom(r) ? (
      <input
        type="text"
        inputMode="decimal"
        style={{ ...input, marginTop: 6 }}
        value={r.customStitchRate ?? ""}
        placeholder="Custom stitch rate"
        onChange={(e) => updateField(rowId, "customStitchRate", e.target.value)}
      />
    ) : null}
  </td>
) : (
  // keep column alignment without showing UI
  <td style={{ padding: 8 }}></td>
)}


{/* LINING */}
<td style={{ padding: 8, ...colLining }}>
  {renderCurtainLiningToggle(r, c, rowId)}
</td>

{/* FABRIC (m) */}
<td style={{ padding: 8, textAlign: "center", ...colFabricM }}>
  <span
    title={orderedFabricTooltip}
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 5,
      whiteSpace: "nowrap",
    }}
  >
    <span>{c.fabricMeters.toFixed(2)}</span>
    <span
      aria-hidden="true"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 15,
        height: 15,
        borderRadius: "50%",
        background: isDark ? "rgba(96,165,250,0.2)" : "#dbeafe",
        color: isDark ? "#bfdbfe" : "#1d4ed8",
        fontSize: 10,
        fontWeight: 900,
      }}
    >
      i
    </span>
  </span>
</td>

{/* TOTAL */}
<td style={{
  padding: 8,
  textAlign: "center",
  fontWeight: 700,
  color: isDark ? "#93c5fd" : "#1d4ed8",
  ...getStickyTotalCellStyle(rowSurface),
  ...colTotal
}}>
  <div>{currencySymbol(currency)} {c.rowTotal.toFixed(2)}</div>
  {c.lining?.enabled ? (
    <div
      style={{
        marginTop: 4,
        fontSize: 10.5,
        fontWeight: 800,
        color: dashboardMuted,
        lineHeight: 1.25,
      }}
    >
      Includes lining {currencySymbol(currency)}{" "}
      {Number(c.lining.amount || 0).toFixed(2)}
    </div>
  ) : null}
  {rowTrackConfig?.bendAmount > 0 ? (
    <div
      style={{
        marginTop: 4,
        fontSize: 10.5,
        fontWeight: 800,
        color: dashboardMuted,
        lineHeight: 1.25,
      }}
    >
      Bend {currencySymbol(currency)}{" "}
      {Number(rowTrackConfig.bendAmount || 0).toFixed(2)}
    </div>
  ) : null}
</td>


                        {/* ACTIONS */}
              {/* ACTIONS */}
<td style={{ padding: 6, ...getStickyActionsCellStyle(rowSurface), ...colActions }}>
  <div style={rowActionCellStyle}>
    {renderRowActionIconButton({
      label: "Add row below",
      title: "Add row below",
      color: "#16a34a",
      icon: Plus,
      onClick: () => insertRowAfter(i),
    })}
    {renderRowActionIconButton({
      label: "Delete row",
      title: "Delete row",
      color: "#dc2626",
      icon: Trash2,
      onClick: () => {
        removeQuoteRow(getWorkshopRowStableId(r));
      },
    })}
  </div>
</td>

                      </tr>
                      {(showTrackConfigPanel || (curtainRowSupportsLining(r, c) && c.lining?.enabled)) ? (
                        <tr>
                          <td
                            colSpan={14}
                            style={{
                              padding: "0 8px 12px",
                              background: rowSurface,
                            }}
                          >
                            <div style={{ display: "grid", gap: 12 }}>
                              {showTrackConfigPanel ? renderCurtainTrackConfigPanel(r, c, rowId) : null}
                              {curtainRowSupportsLining(r, c) && c.lining?.enabled
                                ? renderCurtainLiningPanel(r, c, rowId)
                                : null}
                            </div>
                          </td>
                        </tr>
                      ) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            )}

            <div style={{ marginTop: 26, display: "grid", gap: 14 }}>
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <div style={{ fontSize: 16, fontWeight: 900 }}>Add-ons / Accessories</div>
                  <div style={{ fontSize: 12, color: dashboardMuted }}>
                    Motors, tracks, remotes, brackets, freight, and installation
                    charges stay out of fabric summary and optimizer.
                  </div>
                </div>
                <Button icon={Plus} variant="primary" onClick={addCurtainAccessoryRow}>
                  Add Accessory Row
                </Button>
              </div>
              {curtainAccessoryEntries.length > 0 ? (
                <div style={{ display: "grid", gap: 12 }}>
                  {curtainAccessoryEntries.map(({ row: accessoryRow, index }, displayIndex) =>
                    renderCurtainAccessoryRow(accessoryRow, index, displayIndex)
                  )}
                </div>
              ) : (
                <div
                  style={{
                    borderRadius: 18,
                    border: isDark
                      ? "1px dashed rgba(96,165,250,0.35)"
                      : "1px dashed rgba(37,99,235,0.35)",
                    background: isDark ? "rgba(15,23,42,0.5)" : "#eff6ff",
                    color: dashboardMuted,
                    padding: 16,
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  No accessories added yet. Add motors, tracks, remotes, or service
                  charges here when the project needs hardware.
                </div>
              )}
            </div>

            {/* Curtain Quote bottom flow: actions, summaries, totals */}
<div
  style={{
    marginTop: 30,
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 18,
    alignItems: "stretch",
  }}
>

  {/* Save/PDF actions immediately after Accessories */}
  <div
    style={{
      display: "flex",
      gap: 14,
      alignItems: "center",
      flexWrap: "wrap",
      width: "100%",
      position: isMobile ? "sticky" : "static",
      bottom: isMobile ? 12 : "auto",
      zIndex: isMobile ? 8 : "auto",
      background: isMobile
        ? isDark
          ? "rgba(2,6,23,0.92)"
          : "rgba(255,255,255,0.96)"
        : isDark
        ? "rgba(15,23,42,0.66)"
        : "rgba(248,250,252,0.96)",
      padding: isMobile ? 12 : 16,
      borderRadius: 18,
      border: isDark
        ? "1px solid rgba(148,163,184,0.2)"
        : "1px solid rgba(226,232,240,0.95)",
      boxShadow: isMobile
        ? "0 14px 28px rgba(15,23,42,0.16)"
        : "0 12px 28px rgba(15,23,42,0.06)",
    }}
  >
    {(canEditQuotes || canCreateQuotes) ? (
      <Button
        icon={Save}
        variant="success"
        onClick={saveQuotation}
        disabled={isSaving || (quoteSubTab === GENERAL_QUOTE_TYPE && Boolean(generalUploadingItemId))}
      >
        {isSaving
          ? "Saving..."
          : quoteSubTab === GENERAL_QUOTE_TYPE && generalUploadingItemId
          ? "Uploading..."
          : "Save"}
      </Button>
    ) : null}

    {canCreateQuotes ? (
      <Button
        icon={Save}
        variant="primary"
        onClick={saveQuotationAsNew}
        disabled={isSaving || (quoteSubTab === GENERAL_QUOTE_TYPE && Boolean(generalUploadingItemId))}
      >
        Save As New
      </Button>
    ) : null}



{/* Apple-like autosave indicator */}
  <div
    style={{
      fontSize: 12,
      fontWeight: 600,
      color:
        autoSaveStatus === "saving"
          ? "#2563eb"
          : autoSaveStatus === "saved"
          ? "#16a34a"
          : autoSaveStatus === "error"
          ? "#dc2626"
          : "#64748b",
      transition: "all 0.25s ease",
    }}
  >
    {autoSaveStatus === "saving" && "Saving..."}
    {autoSaveStatus === "error" && "Save failed"}
    {autoSaveStatus !== "saving" &&
      autoSaveStatus !== "error" &&
      getLocalDraftStatusText(LOCAL_DRAFT_MODULES.CURTAIN_QUOTE)}
  </div>

    {isQuoteMutationAuditDevMode() ? (
      <div
        style={{
          borderRadius: 14,
          border: quoteDataIntegrityWatch?.suspicious
            ? "1px solid rgba(239,68,68,0.55)"
            : "1px solid rgba(245,158,11,0.35)",
          background: quoteDataIntegrityWatch?.suspicious
            ? isDark
              ? "rgba(127,29,29,0.38)"
              : "rgba(254,242,242,0.98)"
            : isDark
            ? "rgba(120,53,15,0.28)"
            : "rgba(255,251,235,0.95)",
          color: isDark ? "#fde68a" : "#92400e",
          padding: "9px 11px",
          fontSize: 11,
          lineHeight: 1.45,
          maxWidth: 420,
        }}
      >
        <strong>Data Integrity Watch</strong>
        <div>
          Last mutation:{" "}
          {quoteDataIntegrityWatch
            ? `${quoteDataIntegrityWatch.source} | ${quoteDataIntegrityWatch.field} | ${String(
                quoteDataIntegrityWatch.previousValue ?? ""
              )} -> ${String(quoteDataIntegrityWatch.nextValue ?? "")}`
            : "none"}
        </div>
        <div>Row: {quoteDataIntegrityWatch?.rowId || "-"}</div>
        <div>Autosave: {autoSaveStatus || "idle"} | Saving: {isSaving ? "yes" : "no"}</div>
        <div style={{ opacity: 0.75 }}>
          Signature:{" "}
          {typeof window !== "undefined"
            ? String(window.__FQ_CURRENT_QUOTE_ROW_SIGNATURE__ || "").slice(0, 90)
            : "-"}
        </div>
      </div>
    ) : null}

    {canUseCustomerPdf ? (
      <Button icon={Download} variant="primary" onClick={exportCustomerPDF}>
        Customer PDF
      </Button>
    ) : null}

    {canUseWorkshopPdf ? (
      <Button
        icon={Download}
        variant="primary"
        onClick={exportWorkshopPDF}
        onMouseEnter={preloadWorkshopZipRuntime}
        onFocus={preloadWorkshopZipRuntime}
        style={{ background: "#7c3aed" }}
      >
        Workshop ZIP
      </Button>
    ) : null}

 

    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        flex: isCompact ? "1 1 100%" : "0 1 auto",
        fontSize: 13,
        fontWeight: 600,
        color: isDark ? "#cbd5e1" : "#334155",
      }}
    >
      <input
        type="checkbox"
        checked={useHalfPieceReuse}
        onChange={(e) => {
          useHalfPieceReuseRef.current = e.target.checked;
          setUseHalfPieceReuse(e.target.checked);
        }}
      />
      Use half-piece reuse in workshop and optimizer
    </label>

    <label
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        flex: isCompact ? "1 1 100%" : "1 1 360px",
        fontSize: 13,
        fontWeight: 600,
        color: isDark ? "#cbd5e1" : "#334155",
        maxWidth: 430,
      }}
    >
      <input
        type="checkbox"
        checked={passHalfPieceSavingsToCustomer}
        onChange={(event) => {
          passHalfPieceSavingsToCustomerRef.current = event.target.checked;
          setPassHalfPieceSavingsToCustomer(event.target.checked);
        }}
        style={{ marginTop: 2 }}
      />
      <span>
        Pass half-piece savings to customer
        <span
          style={{
            display: "block",
            marginTop: 3,
            fontSize: 11,
            fontWeight: 500,
            color: dashboardMuted,
            lineHeight: 1.35,
          }}
        >
          {passHalfPieceSavingsToCustomer
            ? "Customer billable fabric and customer PDF total use the reduced fabric requirement."
            : "Customer is billed using full ordered fabric. Half-piece reuse remains internal workshop information."}
        </span>
      </span>
    </label>

  </div>

{quoteFullnessOverrideRows.length > 0 ? (
  <div
    style={{
      marginTop: 18,
      padding: isCompact ? "18px 16px" : "22px 24px",
      borderRadius: 20,
      background: isDark
        ? "linear-gradient(135deg,rgba(15,23,42,0.88),rgba(30,41,59,0.76))"
        : "linear-gradient(135deg,#ffffff,#f8fafc)",
      border: isDark
        ? "1px solid rgba(45,212,191,0.24)"
        : "1px solid rgba(125,211,252,0.65)",
      boxShadow: isDark
        ? "0 18px 42px rgba(2,6,23,0.28)"
        : "0 18px 36px rgba(14,165,233,0.08)",
      display: "grid",
      gap: 16,
    }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        flexDirection: isCompact ? "column" : "row",
        alignItems: isCompact ? "stretch" : "flex-start",
      }}
    >
      <div>
        <div
          style={{
            fontSize: 18,
            fontWeight: 900,
            letterSpacing: "-0.2px",
            color: isDark ? "#f8fafc" : "#0f172a",
          }}
        >
          Fullness Settings for This Quote
        </div>
        <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4, lineHeight: 1.45 }}>
          Leave blank to use the default fullness from Stitching & Tracks settings.
        </div>
      </div>
      <div
        style={{
          borderRadius: 999,
          padding: "7px 12px",
          background: isDark ? "rgba(45,212,191,0.12)" : "#ecfeff",
          color: isDark ? "#5eead4" : "#0f766e",
          fontSize: 12,
          fontWeight: 900,
          whiteSpace: "nowrap",
          alignSelf: isCompact ? "flex-start" : "center",
        }}
      >
        {quoteFullnessOverrideRows.length} stitch type
        {quoteFullnessOverrideRows.length === 1 ? "" : "s"}
      </div>
    </div>

    <div
      style={{
        overflowX: "auto",
        borderRadius: 16,
        border: "1px solid rgba(148,163,184,0.22)",
        background: isDark ? "rgba(2,6,23,0.38)" : "rgba(255,255,255,0.8)",
      }}
    >
      <div style={{ minWidth: 620 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(220px,1.3fr) minmax(130px,0.7fr) minmax(170px,0.8fr) minmax(120px,0.6fr)",
            gap: 12,
            padding: "10px 14px",
            borderBottom: "1px solid rgba(148,163,184,0.2)",
            fontSize: 11,
            fontWeight: 900,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: dashboardMuted,
          }}
        >
          <div>Stitch / Track Type</div>
          <div>Default Fullness</div>
          <div>Quote Fullness</div>
          <div>Status</div>
        </div>
        {quoteFullnessOverrideRows.map((stitchRow, index) => {
          const inputValue =
            quoteFullnessOverrideInputs[stitchRow.stitchingKey] ??
            (stitchRow.quoteFullness ? String(stitchRow.quoteFullness) : "");
          const parsedInput = Number(inputValue);
          const isInvalidFullness =
            inputValue !== "" &&
            (!Number.isFinite(parsedInput) || parsedInput <= 0 || parsedInput > 10);
          const usingQuoteOverride =
            !isInvalidFullness &&
            inputValue !== "" &&
            Number.isFinite(parsedInput) &&
            parsedInput > 0;

          return (
            <div
              key={stitchRow.stitchingKey}
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(220px,1.3fr) minmax(130px,0.7fr) minmax(170px,0.8fr) minmax(120px,0.6fr)",
                gap: 12,
                alignItems: "center",
                padding: "12px 14px",
                borderBottom:
                  index === quoteFullnessOverrideRows.length - 1
                    ? "none"
                    : "1px solid rgba(148,163,184,0.14)",
              }}
            >
              <div>
                <div style={{ fontSize: 13, fontWeight: 900, color: isDark ? "#f8fafc" : "#0f172a" }}>
                  {stitchRow.stitchingLabel}
                </div>
                <div style={{ fontSize: 11, color: dashboardMuted, marginTop: 2 }}>
                  Used in {stitchRow.rowCount} row{stitchRow.rowCount === 1 ? "" : "s"}
                </div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 800, color: isDark ? "#cbd5e1" : "#334155" }}>
                {stitchRow.defaultFullness
                  ? Number(stitchRow.defaultFullness).toFixed(2)
                  : "-"}
              </div>
              <div>
                <input
                  type="text"
                  inputMode="decimal"
                  style={{
                    ...input,
                    minHeight: 38,
                    borderColor: isInvalidFullness
                      ? "#ef4444"
                      : input.borderColor || "rgba(148,163,184,0.35)",
                  }}
                  value={inputValue}
                  placeholder={
                    stitchRow.defaultFullness
                      ? `Default ${Number(stitchRow.defaultFullness).toFixed(2)}`
                      : "Use default"
                  }
                  onChange={(event) =>
                    updateQuoteFullnessOverride(stitchRow, event.target.value)
                  }
                />
                {isInvalidFullness ? (
                  <div style={{ marginTop: 5, fontSize: 11, fontWeight: 700, color: "#dc2626" }}>
                    Enter a fullness between 0 and 10.
                  </div>
                ) : null}
              </div>
              <div
                style={{
                  justifySelf: "start",
                  borderRadius: 999,
                  padding: "6px 10px",
                  background: usingQuoteOverride
                    ? isDark
                      ? "rgba(34,197,94,0.13)"
                      : "#ecfdf5"
                    : isDark
                    ? "rgba(148,163,184,0.12)"
                    : "#f1f5f9",
                  color: usingQuoteOverride
                    ? isDark
                      ? "#86efac"
                      : "#047857"
                    : dashboardMuted,
                  fontSize: 11,
                  fontWeight: 900,
                  whiteSpace: "nowrap",
                }}
              >
                {usingQuoteOverride ? "Quote override" : "Using default"}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </div>
) : null}

 {/* FABRIC SUMMARY UI */}

{fabricSummaryUI &&
  typeof fabricSummaryUI === "object" &&
  Object.keys(fabricSummaryUI).length > 0 &&
  (() => {
    const fabricSummaryRows = curtainCanonicalFabricSummaryRows.map((row) => ({
      ...row,
      usedIn: row.usedInItems || [],
      fabricToOrderMeters: Number(
        row.fabricToOrderMeters ??
          row.workshopFabricRequiredMeters ??
          row.newFabricNeededMeters ??
          0
      ),
      halfReusedMeters: row.halfReusedMeters,
      billableMeters: row.customerBillableFabricMeters,
    }));

    if (fabricSummaryRows.length === 0) return null;

    const totalFabricToOrderMeters = fabricSummaryRows.reduce(
      (acc, row) => acc + Number(row.fabricToOrderMeters || 0),
      0
    );
    const totalBillableMeters = fabricSummaryRows.reduce(
      (acc, row) => acc + Number(row.billableMeters || 0),
      0
    );
    const totalHalfPieceSavingMeters = fabricSummaryRows.reduce(
      (acc, row) => acc + Number(row.halfReusedMeters || 0),
      0
    );
    const showWorkshopSavingsNote =
      useHalfPieceReuse &&
      !passHalfPieceSavingsToCustomer &&
      totalHalfPieceSavingMeters > 0.005;
    return (
      <div
        className="shimmer-bg"
        style={{
          marginTop: 0,
          padding: isCompact ? "20px 18px" : "26px 28px",
          borderRadius: "20px",
          background: isDark ? "rgba(15,23,42,0.72)" : "rgba(255,255,255,0.72)",
          backdropFilter: "blur(18px) saturate(180%)",
          WebkitBackdropFilter: "blur(18px) saturate(180%)",
          border: isDark
            ? "1px solid rgba(148,163,184,0.24)"
            : "1px solid rgba(255,255,255,0.55)",
          boxShadow:
            "0 12px 36px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.25)",
          animation: "fadeSlideUp 0.45s ease both",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: isCompact ? "flex-start" : "center",
            flexDirection: isCompact ? "column" : "row",
            gap: 10,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              fontSize: 19,
              fontWeight: 800,
              letterSpacing: "-0.35px",
              color: isDark ? "#f8fafc" : "#0f172a",
            }}
          >
            Fabric Summary
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {canViewHalfPieceAudit ? (
              <button
                type="button"
                onClick={() => setHalfPieceAuditOpen((open) => !open)}
                style={{
                  padding: "7px 12px",
                  borderRadius: "999px",
                  border: isDark
                    ? "1px solid rgba(251,191,36,0.32)"
                    : "1px solid rgba(217,119,6,0.22)",
                  background: isDark ? "rgba(251,191,36,0.12)" : "#fffbeb",
                  color: isDark ? "#fde68a" : "#92400e",
                  fontSize: 12,
                  fontWeight: 900,
                  cursor: "pointer",
                }}
              >
                Half-Piece Audit
              </button>
            ) : null}
            {canViewHalfPieceAudit ? (
              <button
                type="button"
                onClick={copyCurtainRowCalculationAudit}
                style={{
                  padding: "7px 12px",
                  borderRadius: "999px",
                  border: isDark
                    ? "1px solid rgba(96,165,250,0.32)"
                    : "1px solid rgba(37,99,235,0.18)",
                  background: isDark ? "rgba(59,130,246,0.14)" : "#eff6ff",
                  color: isDark ? "#bfdbfe" : "#1d4ed8",
                  fontSize: 12,
                  fontWeight: 900,
                  cursor: "pointer",
                }}
              >
                Copy Row Calculation Audit
              </button>
            ) : null}
            <div
              style={{
                padding: "7px 14px",
                borderRadius: "999px",
                background: isDark ? "rgba(59,130,246,0.16)" : "rgba(15,23,42,0.06)",
                border: isDark ? "1px solid rgba(96,165,250,0.18)" : "1px solid rgba(15,23,42,0.06)",
                fontSize: 12.5,
                fontWeight: 800,
                color: isDark ? "#bfdbfe" : "#334155",
                whiteSpace: "nowrap",
              }}
            >
              Fabric to Order {totalFabricToOrderMeters.toFixed(2)} m
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "10px 12px",
            marginBottom: 16,
            fontSize: 12.5,
            color: isDark ? "#cbd5e1" : "#334155",
          }}
        >
          <span
            style={{
              padding: "7px 10px",
              borderRadius: 10,
              background: isDark ? "rgba(16,185,129,0.14)" : "rgba(236,253,245,0.94)",
              border: "1px solid rgba(16,185,129,0.22)",
              whiteSpace: "nowrap",
            }}
          >
            Fabric to Order: <strong>{totalFabricToOrderMeters.toFixed(2)} m</strong>
          </span>
          <span
            style={{
              padding: "7px 10px",
              borderRadius: 10,
              background: isDark ? "rgba(59,130,246,0.14)" : "rgba(239,246,255,0.94)",
              border: "1px solid rgba(59,130,246,0.22)",
              whiteSpace: "nowrap",
            }}
          >
            Customer Billable: <strong>{totalBillableMeters.toFixed(2)} m</strong>
          </span>
          <span
            style={{
              padding: "7px 10px",
              borderRadius: 10,
              background: isDark ? "rgba(251,191,36,0.14)" : "rgba(254,243,199,0.94)",
              border: "1px solid rgba(251,191,36,0.28)",
              whiteSpace: "nowrap",
            }}
          >
            Half-piece Saving: <strong>{totalHalfPieceSavingMeters.toFixed(2)} m</strong>
          </span>
        </div>
        {showWorkshopSavingsNote ? (
          <div
            style={{
              marginBottom: 14,
              borderRadius: 12,
              padding: "9px 11px",
              background: isDark
                ? "rgba(148,163,184,0.1)"
                : "rgba(248,250,252,0.9)",
              color: isDark ? "#cbd5e1" : "#475569",
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            Customer is billed full fabric. Half-piece saving is used only for workshop/order planning.
            {halfPieceAuditResultText ? (
              <div style={{ marginTop: 6, fontWeight: 800 }}>
                Reuse result: {halfPieceAuditResultText}
              </div>
            ) : null}
          </div>
        ) : null}

        {canViewHalfPieceAudit && halfPieceAuditOpen && curtainHalfPieceAudit ? (
          <div
            style={{
              display: "grid",
              gap: 12,
              marginBottom: 14,
              padding: 14,
              borderRadius: 16,
              background: isDark ? "rgba(15,23,42,0.66)" : "#fff7ed",
              border: isDark
                ? "1px solid rgba(251,191,36,0.22)"
                : "1px solid rgba(251,146,60,0.25)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <div>
                <div style={{ fontSize: 13, fontWeight: 950, color: isDark ? "#f8fafc" : "#7c2d12" }}>
                  Half-Piece Reuse Audit
                </div>
                <div style={{ marginTop: 3, fontSize: 12, color: dashboardMuted }}>
                  Same canonical workshop fabric plan used by the quote screen, optimizer, and Workshop PDF.
                </div>
              </div>
              <button
                type="button"
                onClick={copyHalfPieceAuditJson}
                style={{
                  padding: "7px 11px",
                  borderRadius: 10,
                  border: 0,
                  background: "#ea580c",
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 900,
                  cursor: "pointer",
                }}
              >
                Copy Half-Piece Audit JSON
              </button>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isCompact
                  ? "repeat(2, minmax(0, 1fr))"
                  : "repeat(4, minmax(0, 1fr))",
                gap: 8,
              }}
            >
              {[
                ["Reuse", curtainHalfPieceAudit.enabled ? "Enabled" : "Disabled"],
                ["Half Needs", `${Number(curtainHalfPieceAudit.totalHalfPanelNeeds || 0)} row(s)`],
                ["Pairs", `${Number(curtainHalfPieceAudit.totalPairsCreated || 0)} pair(s)`],
                ["Reused", `${Number(curtainHalfPieceAudit.totalHalfPiecesReused || 0)} half piece(s)`],
                ["Fabric Saved", `${Number(curtainHalfPieceAudit.totalMetersSaved || 0).toFixed(2)} m`],
              ].map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    padding: "9px 10px",
                    borderRadius: 12,
                    background: isDark ? "rgba(2,6,23,0.44)" : "rgba(255,255,255,0.72)",
                    border: "1px solid rgba(148,163,184,0.18)",
                  }}
                >
                  <div style={{ fontSize: 10, fontWeight: 900, color: dashboardMuted, textTransform: "uppercase" }}>
                    {label}
                  </div>
                  <div style={{ marginTop: 3, fontSize: 13, fontWeight: 900, color: isDark ? "#f8fafc" : "#0f172a" }}>
                    {value}
                  </div>
                </div>
              ))}
            </div>

            {(curtainHalfPieceAudit.reasons || []).length > 0 ? (
              <div style={{ fontSize: 12, fontWeight: 800, color: isDark ? "#fed7aa" : "#9a3412" }}>
                {(curtainHalfPieceAudit.reasons || []).join(" ")}
              </div>
            ) : null}

            <div style={{ display: "grid", gap: 8 }}>
              {(curtainHalfPieceAudit.fabricGroups || []).map((group) => (
                <div
                  key={group.fabricKey}
                  style={{
                    padding: 10,
                    borderRadius: 12,
                    background: isDark ? "rgba(2,6,23,0.34)" : "rgba(255,255,255,0.68)",
                    border: "1px solid rgba(148,163,184,0.16)",
                    fontSize: 12,
                    color: isDark ? "#cbd5e1" : "#334155",
                  }}
                >
                  <div style={{ fontWeight: 950, color: isDark ? "#f8fafc" : "#111827" }}>
                    {group.fabricCode || group.fabricKey}
                  </div>
                  <div style={{ marginTop: 4 }}>
                    Rows {group.rowsEligible}/{group.rowsConsidered} eligible | Half needs {group.halfPanelNeeds || 0} | Pairs {group.pairsCreated || 0} | Reused {group.halfPiecesReused} | Saved {Number(group.metersSaved || 0).toFixed(2)} m
                  </div>
                  {(group.reasons || []).length > 0 ? (
                    <div style={{ marginTop: 4, color: dashboardMuted }}>
                      Reason: {(group.reasons || []).join(" ")}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact
              ? "1fr"
              : "repeat(auto-fit, minmax(420px, 1fr))",
            gap: 14,
          }}
        >
          {fabricSummaryRows.map((row) => {
            const current = repeatSettings?.[row.normalizedKey] || {};
            const usedInLabel =
              row.usedIn?.length > 0
                ? `${row.usedIn.slice(0, 5).join(", ")}${
                    row.usedIn.length > 5 ? ` +${row.usedIn.length - 5} more` : ""
                  }`
                : "Not linked to visible rows";

            return (
              <div
                key={row.normalizedKey || row.key}
                style={{
                  display: "grid",
                  gap: 14,
                  padding: isCompact ? 14 : 16,
                  borderRadius: 18,
                  border: isDark
                    ? "1px solid rgba(148,163,184,0.2)"
                    : "1px solid rgba(226,232,240,0.95)",
                  background: isDark ? "rgba(2,6,23,0.36)" : "rgba(255,255,255,0.88)",
                  boxShadow: isDark
                    ? "inset 0 1px 0 rgba(255,255,255,0.04)"
                    : "0 10px 24px rgba(15,23,42,0.05)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 14,
                    flexDirection: isCompact ? "column" : "row",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                      minWidth: 0,
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        width: 12,
                        height: 12,
                        flex: "0 0 12px",
                        marginTop: 4,
                        borderRadius: "50%",
                        background: row.color,
                        boxShadow: "0 0 8px rgba(0,0,0,0.16)",
                      }}
                    />
                    <div style={{ minWidth: 0 }}>
                      <div
                        title={row.displayName}
                        style={{
                          minWidth: 0,
                          overflowWrap: "anywhere",
                          fontWeight: 900,
                          fontSize: 16,
                          lineHeight: 1.35,
                          color: isDark ? "#f8fafc" : "#0f172a",
                        }}
                      >
                        {row.displayName}
                      </div>
                      <div
                        style={{
                          marginTop: 5,
                          fontSize: 12,
                          color: dashboardMuted,
                          lineHeight: 1.45,
                        }}
                      >
                        Used in: {usedInLabel}
                      </div>
                    </div>
                  </div>

                  <select
                    style={{
                      ...selectInput,
                      width: isCompact ? "100%" : 180,
                      minHeight: 34,
                      padding: "7px 9px",
                      fontSize: 12,
                    }}
                    value={row.brandId || ""}
                    onChange={(event) =>
                      updateFabricSummaryBrand(row.normalizedKey, event.target.value)
                    }
                    aria-label={`Select brand for ${row.displayName}`}
                  >
                    <option value="">Select brand</option>
                    {fabricBrands.map((brand) => (
                      <option key={brand.id} value={brand.id}>
                        {brand.name}
                      </option>
                    ))}
                  </select>
                </div>

                {row.brandName ? (
                  <div
                    style={{
                      marginTop: -6,
                      fontSize: 11,
                      fontWeight: 800,
                      color: row.hasMixedBrands
                        ? isDark
                          ? "#fde68a"
                          : "#92400e"
                        : isDark
                        ? "#bfdbfe"
                        : "#1d4ed8",
                    }}
                  >
                    {row.hasMixedBrands
                      ? `Mixed brands: ${(row.mixedBrandNames || []).join(", ")}`
                      : `Brand: ${row.brandName}`}
                  </div>
                ) : null}

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isCompact
                      ? "repeat(2, minmax(0, 1fr))"
                      : "repeat(4, minmax(0, 1fr))",
                    gap: 10,
                  }}
                >
                  {[
                    ["Fabric to Order", `${row.fabricToOrderMeters.toFixed(2)} m`],
                    ["Customer Billable", `${row.billableMeters.toFixed(2)} m`],
                    ["Half-piece Saving", `${row.halfReusedMeters.toFixed(2)} m`],
                  ].map(([label, value]) => {
                    const isCustomerBillableMetric = label === "Customer Billable";
                    const isFabricToOrderMetric = label === "Fabric to Order";
                    return (
                    <div
                      key={label}
                      style={{
                        borderRadius: 14,
                        padding: "10px 11px",
                        background:
                          isFabricToOrderMetric
                            ? isDark
                              ? "rgba(16,185,129,0.2)"
                              : "#ecfdf5"
                            : isCustomerBillableMetric
                            ? isDark
                              ? "rgba(37,99,235,0.2)"
                              : "#eff6ff"
                            : isDark
                            ? "rgba(15,23,42,0.52)"
                            : "#f8fafc",
                        border:
                          isFabricToOrderMetric
                            ? "1px solid rgba(16,185,129,0.26)"
                            : isCustomerBillableMetric
                            ? "1px solid rgba(37,99,235,0.24)"
                            : "1px solid rgba(148,163,184,0.18)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 10.5,
                          fontWeight: 900,
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                          color: dashboardMuted,
                          marginBottom: 4,
                        }}
                      >
                        {label}
                      </div>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 900,
                          color:
                            isFabricToOrderMetric
                              ? "#059669"
                              : isCustomerBillableMetric
                              ? "#2563eb"
                              : isDark
                              ? "#f8fafc"
                              : "#0f172a",
                        }}
                      >
                        {value}
                      </div>
                    </div>
                    );
                  })}
                </div>

                <div
                  style={{
                    padding: 12,
                    borderRadius: 16,
                    background: isDark ? "rgba(15,23,42,0.5)" : "rgba(248,250,252,0.9)",
                    border: "1px solid rgba(148,163,184,0.18)",
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 900,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: dashboardMuted,
                      marginBottom: 10,
                    }}
                  >
                    Repeat Settings
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))",
                      gap: 10,
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 7,
                          fontSize: 12,
                          fontWeight: 700,
                          color: isDark ? "#cbd5e1" : "#334155",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={current.verticalEnabled || false}
                          onChange={(e) =>
                            updateFabricRepeatSettings(row.normalizedKey, {
                              verticalEnabled: e.target.checked,
                              verticalRepeatInch: current.verticalRepeatInch || 0,
                            })
                          }
                        />
                        Vertical Repeat
                      </label>

                      {current.verticalEnabled && (
                        <input
                          type="number"
                          placeholder="Repeat (in)"
                          style={{ ...input, marginTop: 7 }}
                          value={current.verticalRepeatInch || ""}
                          onChange={(e) =>
                            updateFabricRepeatSettings(row.normalizedKey, {
                              verticalEnabled: true,
                              verticalRepeatInch: e.target.value,
                            })
                          }
                        />
                      )}
                    </div>

                    <div>
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 7,
                          fontSize: 12,
                          fontWeight: 700,
                          color: isDark ? "#cbd5e1" : "#334155",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={current.horizontalEnabled || false}
                          onChange={(e) =>
                            updateFabricRepeatSettings(row.normalizedKey, {
                              horizontalEnabled: e.target.checked,
                              horizontalRepeatInch: current.horizontalRepeatInch || 0,
                            })
                          }
                        />
                        Horizontal Repeat
                      </label>

                      {current.horizontalEnabled && (
                        <input
                          type="number"
                          placeholder="Repeat (in)"
                          style={{ ...input, marginTop: 7 }}
                          value={current.horizontalRepeatInch || ""}
                          onChange={(e) =>
                            updateFabricRepeatSettings(row.normalizedKey, {
                              horizontalEnabled: true,
                              horizontalRepeatInch: e.target.value,
                            })
                          }
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
        </div>

        <div
          style={{
            marginTop: 22,
            height: 3,
            borderRadius: 4,
            background: "linear-gradient(90deg,transparent,#94a3b8,transparent)",
            opacity: 0.35,
            animation: "glowLine 5s ease-in-out infinite",
          }}
        />
      </div>
    );
  })()}

{curtainLiningSummaryRows.length > 0 ? (
  <div
    style={{
      marginTop: 18,
      padding: isCompact ? "18px 16px" : "22px 24px",
      borderRadius: 20,
      background: isDark
        ? "linear-gradient(135deg,rgba(15,23,42,0.88),rgba(30,41,59,0.76))"
        : "linear-gradient(135deg,#ffffff,#f8fafc)",
      border: isDark
        ? "1px solid rgba(96,165,250,0.22)"
        : "1px solid rgba(191,219,254,0.85)",
      boxShadow: isDark
        ? "0 18px 40px rgba(2,6,23,0.24)"
        : "0 18px 34px rgba(37,99,235,0.07)",
      display: "grid",
      gap: 14,
    }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        flexDirection: isCompact ? "column" : "row",
        alignItems: isCompact ? "stretch" : "center",
      }}
    >
      <div>
        <div
          style={{
            fontSize: 18,
            fontWeight: 900,
            color: isDark ? "#f8fafc" : "#0f172a",
          }}
        >
          Lining Summary
        </div>
        <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
          Separate from main curtain fabric demand and roll optimizer quantities.
        </div>
      </div>
      <div
        style={{
          borderRadius: 999,
          padding: "7px 12px",
          background: isDark ? "rgba(37,99,235,0.18)" : "#dbeafe",
          color: "#2563eb",
          fontSize: 12,
          fontWeight: 900,
          whiteSpace: "nowrap",
        }}
      >
        Total {currencySymbol(currency)} {curtainLiningTotalAmount.toFixed(2)}
      </div>
    </div>
    <div
      style={{
        overflowX: "auto",
        borderRadius: 16,
        border: "1px solid rgba(148,163,184,0.22)",
        background: isDark ? "rgba(2,6,23,0.42)" : "rgba(255,255,255,0.82)",
      }}
    >
      <div style={{ minWidth: 680 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(150px,1fr) minmax(180px,1fr) 100px 100px 110px",
            gap: 12,
            padding: "10px 14px",
            borderBottom: "1px solid rgba(148,163,184,0.2)",
            fontSize: 11,
            fontWeight: 900,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: dashboardMuted,
          }}
        >
          <div>Type</div>
          <div>Fabric / Code</div>
          <div>Method</div>
          <div style={{ textAlign: "right" }}>Qty</div>
          <div style={{ textAlign: "right" }}>Amount</div>
        </div>
        {curtainLiningSummaryRows.map((row, index) => (
          <div
            key={row.key}
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(150px,1fr) minmax(180px,1fr) 100px 100px 110px",
              gap: 12,
              alignItems: "center",
              padding: "12px 14px",
              borderBottom:
                index === curtainLiningSummaryRows.length - 1
                  ? "none"
                  : "1px solid rgba(148,163,184,0.14)",
              fontSize: 13,
            }}
          >
            <div style={{ fontWeight: 900 }}>{row.label}</div>
            <div style={{ color: dashboardMuted }}>
              {[row.brandName, row.fabricCode].filter(Boolean).join(" / ") || "-"}
            </div>
            <div style={{ fontWeight: 800 }}>
              {row.chargeMethod === "sqft" ? "Per sqft" : "Per meter"}
            </div>
            <div style={{ textAlign: "right", fontWeight: 800 }}>
              {row.chargeMethod === "sqft"
                ? `${Number(row.sqft || 0).toFixed(2)} sqft`
                : `${Number(row.meters || 0).toFixed(2)} m`}
            </div>
            <div style={{ textAlign: "right", fontWeight: 900, color: "#2563eb" }}>
              {currencySymbol(currency)} {Number(row.amount || 0).toFixed(2)}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
) : null}

              <div
                style={{
                  maxWidth: isCompact ? "100%" : 420,
                  width: "100%",
                  marginLeft: isCompact ? 0 : "auto",
                  background: isDark ? "#0f172a" : "#f9fafb",
                  borderRadius: 14,
                  padding: 16,
                  border: "1px solid rgba(148,163,184,0.4)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                {/* Subtotal */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ fontSize: 14, opacity: 0.8 }}>Subtotal</span>
                  <strong style={{ fontSize: 16 }}>
                    {currencySymbol(currency)}{" "}
                    {curtainDisplaySummaryTotals.subTotal.toFixed(2)}
                  </strong>
                </div>

                {curtainLineFamilyTotals.accessorySubtotal > 0 ? (
                  <div
                    style={{
                      display: "grid",
                      gap: 6,
                      padding: "10px 0 2px",
                      borderTop: "1px solid rgba(148,163,184,0.18)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 13,
                        opacity: 0.78,
                      }}
                    >
                      <span>Curtains & Blinds</span>
                      <span>
                        {currencySymbol(currency)}{" "}
                        {curtainLineFamilyTotals.measurementSubtotal.toFixed(2)}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 13,
                        opacity: 0.78,
                      }}
                    >
                      <span>Accessories</span>
                      <span>
                        {currencySymbol(currency)}{" "}
                        {curtainLineFamilyTotals.accessorySubtotal.toFixed(2)}
                      </span>
                    </div>
                  </div>
                ) : null}

{/* Discount label */}
<div style={{ fontSize: 14, opacity: 0.7, marginTop: 6 }}>
  {summaryDiscountMode === "%" ? "Quotation Discount (%)" : "Quotation Discount (Rs.)"}
  <div style={{ fontSize: 12, opacity: 0.72, marginTop: 3 }}>
    Applied after GST to adjust the final payable amount.
  </div>
</div>

                {/* Discount input */}
                <div
                  style={{
                    background: isDark ? "#020617" : "#ffffff",
                    borderRadius: 10,
                    padding: 10,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    border: "1px solid rgba(148,163,184,0.4)",
                  }}
                >
                  <select
                    style={{ ...selectInput, width: 120 }}
                    value={summaryDiscountMode}
                    onChange={(e) => {
                      const nextMode = e.target.value;
                      summaryDiscountModeRef.current = nextMode;
                      markCurtainTotalsSettingsEdited("summaryDiscountMode", nextMode);
                      setSummaryDiscountMode(nextMode);
                    }}
                  >
                    <option value="%">Percent</option>
                    <option value="">Flat</option>
                  </select>

                  <input
                    type="number"
                    style={{ ...input, flex: 1 }}
                    value={summaryDiscountValue}
                    onChange={(e) => {
                      summaryDiscountValueRef.current = e.target.value;
                      markCurtainTotalsSettingsEdited(
                        "summaryDiscountValue",
                        e.target.value
                      );
                      setSummaryDiscountValue(e.target.value);
                    }}
                    placeholder={
                      summaryDiscountMode === "%"
                        ? "Enter % for full quote"
                        : "Enter flat amount"
                    }
                  />
                </div>

                {/* GST selector */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isCompact ? "1fr" : "1fr 160px",
                    gap: 10,
                    alignItems: "center",
                  }}
                >
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>GST</div>
                    <div style={{ fontSize: 12, opacity: 0.65 }}>
                      Calculated before the quotation discount.
                    </div>
                  </div>
                  <select
                    style={{ ...selectInput, width: "100%" }}
                    value={curtainGstRate}
                    onChange={(e) => {
                      const nextGstRate = normalizeCurtainGstRate(e.target.value);
                      curtainGstRateRef.current = nextGstRate;
                      markCurtainTotalsSettingsEdited("curtainGstRate", nextGstRate);
                      setCurtainGstRate(nextGstRate);
                    }}
                  >
                    <option value={0}>No GST / 0%</option>
                    <option value={5}>5%</option>
                    <option value={12}>12%</option>
                    <option value={18}>18%</option>
                  </select>
                </div>

                <div
                  style={{
                    borderTop: "1px solid rgba(148,163,184,0.28)",
                    paddingTop: 10,
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: 13,
                      opacity: 0.82,
                    }}
                  >
                    <span>Taxable Amount</span>
                    <strong>
                      {currencySymbol(currency)}{" "}
                      {curtainDisplaySummaryTotals.taxableAmount.toFixed(2)}
                    </strong>
                  </div>
                  {curtainDisplaySummaryTotals.measurementSubtotalBeforeDiscount > 0 ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 13,
                        opacity: 0.82,
                      }}
                    >
                      <span>Curtains/Blinds GST @ {curtainDisplaySummaryTotals.quoteGstRate}%</span>
                      <span>
                        {currencySymbol(currency)}{" "}
                        {curtainDisplaySummaryTotals.measurementGstAmount.toFixed(2)}
                      </span>
                    </div>
                  ) : null}
                  {curtainDisplaySummaryTotals.accessorySubtotalBeforeDiscount > 0 ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 13,
                        opacity: 0.82,
                      }}
                    >
                      <span>
                        Accessories GST
                        {curtainDisplaySummaryTotals.accessoryGstRates?.length > 1
                          ? " (mixed)"
                          : curtainDisplaySummaryTotals.accessoryGstRates?.length === 1
                          ? ` @ ${curtainDisplaySummaryTotals.accessoryGstRates[0]}%`
                          : ""}
                      </span>
                      <span>
                        {currencySymbol(currency)}{" "}
                        {curtainDisplaySummaryTotals.accessoryGstAmount.toFixed(2)}
                      </span>
                    </div>
                  ) : null}
                  {curtainDisplaySummaryTotals.hasMixedGstRates ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 13,
                        opacity: 0.9,
                        fontWeight: 800,
                      }}
                    >
                      <span>Total GST</span>
                      <span>
                        {currencySymbol(currency)}{" "}
                        {curtainDisplaySummaryTotals.totalGstAmount.toFixed(2)}
                      </span>
                    </div>
                  ) : null}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: 13,
                      opacity: 0.92,
                      fontWeight: 800,
                    }}
                  >
                    <span>Total After GST</span>
                    <span>
                      {currencySymbol(currency)}{" "}
                      {Number(
                        curtainDisplaySummaryTotals.totalAfterGst ??
                          curtainDisplaySummaryTotals.finalTotal ??
                          0
                      ).toFixed(2)}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: 13,
                      opacity: 0.82,
                    }}
                  >
                    <span>Post-GST Discount</span>
                    <span>
                      - {currencySymbol(currency)}{" "}
                      {curtainDisplaySummaryTotals.discountAmount.toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Final total */}
                <div
                  style={{
                    marginTop: 4,
                    background: isDark ? "#1d4ed8" : "#2563eb",
                    borderRadius: 10,
                    padding: 12,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    color: "white",
                    fontWeight: 700,
                  }}
                >
                  <span>GRAND TOTAL</span>
                  <span>
                    {currencySymbol(currency)}{" "}
                    {curtainDisplaySummaryTotals.grandTotal.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
          {renderGeneralQuoteDebugPanel()}
          <GeneralQuotationEditor
            quote={generalQuote}
            setQuote={setGeneralQuoteLive}
            totals={generalQuoteTotals}
            isDark={isDark}
            businessName={businessName}
            logoURL={logoURL}
            shopAddress={shopAddress}
            currencySymbol={currencySymbol(generalQuote.currency || currency)}
            isSaving={isSaving}
            autoSaveStatus={autoSaveStatus}
            localDraftStatus={localDraftStatuses[LOCAL_DRAFT_MODULES.GENERAL_QUOTE]}
            onSave={saveQuotation}
            onSaveAsNew={saveQuotationAsNew}
            onExportPdf={exportGeneralQuotationPDF}
            onExportExecutionPdf={exportGeneralExecutionPDF}
            canSave={canEditQuotes || canCreateQuotes}
            canSaveAsNew={canCreateQuotes}
            canExportPdf={canUseCustomerPdf}
            canExportExecutionPdf={canUseWorkshopPdf}
            onUploadItemImage={uploadGeneralQuoteItemImage}
            onRemoveItemImage={removeGeneralQuoteItemImage}
            onRepairItemImage={
              IS_DEV_UI_ENABLED && !generalRepairingImageKey ? repairGeneralQuoteItemImage : null
            }
            onReplaceItemImage={
              generalRepairingImageKey ? null : replaceGeneralQuoteItemImage
            }
            onCopyImageDebug={IS_DEV_UI_ENABLED ? copyGeneralQuoteImageDebugJson : null}
            imageRepairState={IS_DEV_UI_ENABLED ? generalImageRepairState : {}}
            imageRepairCandidates={IS_DEV_UI_ENABLED ? generalImageRepairCandidates : {}}
            onSelectRepairCandidate={
              IS_DEV_UI_ENABLED && !generalRepairingImageKey
                ? selectGeneralQuoteImageRepairCandidate
                : null
            }
            onRemoveItem={removeGeneralQuoteItem}
            onDraftBlur={() => persistGeneralQuoteDraft("blur")}
            uploadingItemId={generalUploadingItemId}
            executionTemplates={executionTemplateCatalog}
            customerSelector={renderCustomerLedgerCombobox({
              context: GENERAL_QUOTE_TYPE,
              label: "Contact Name",
              value: generalQuote.quoteFor?.name || generalQuote.customerName || "",
              selectedCustomerId:
                generalQuote.customerId ||
                selectedQuote?.customerId ||
                selectedQuote?.crm?.customerId ||
                "",
              seed: {
                customerName: generalQuote.quoteFor?.name || generalQuote.customerName || "",
                phone: generalQuote.quoteFor?.phone || generalQuote.customerPhone || "",
                email: generalQuote.quoteFor?.email || generalQuote.customerEmail || "",
                address: generalQuote.quoteFor?.address || generalQuote.customerAddress || "",
                companyName:
                  generalQuote.quoteFor?.company ||
                  generalQuote.customerCompanyName ||
                  "",
              },
              onTextChange: (nextValue) => {
                setGeneralQuote((prev) => ({
                  ...prev,
                  customerId: "",
                  customerName: nextValue,
                  customerPhone: "",
                  customerWhatsapp: "",
                  customerEmail: "",
                  customerAddress: "",
                  customerCompanyName: prev.customerCompanyName || "",
                  customerLedgerLinked: false,
                  quoteFor: {
                    ...(prev.quoteFor || {}),
                    name: nextValue,
                  },
                }));
                setCustomerName(nextValue);
              },
            })}
          />
          </>
        )}
      </div>
  </div>


{/* ================= SAVED QUOTES TAB ================= */}
{tab === "saved" && (
  <div>
    <h2 style={{ fontSize: 22, fontWeight: 900, marginBottom: 20 }}>
      Saved Quotations
    </h2>

    {/* ================= FILTER BAR ================= */}
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 14,
        alignItems: "center",
        marginBottom: 22,
        padding: 16,
        borderRadius: 16,
        background: isDark ? "#111827" : "#f8fafc",
        boxShadow: "0 6px 20px rgba(0,0,0,0.05)",
      }}
    >
      <input
        style={{ ...input, minWidth: isCompact ? "100%" : 220, flex: isCompact ? "1 1 100%" : "0 1 220px" }}
        placeholder="Search all quotations by customer, quote no, phone..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <select
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
        style={selectInput}
      >
        <option value="all">All Status</option>
        <option value="confirmed">Confirmed</option>
        <option value="draft">Draft</option>
      </select>

      <select
        value={stageFilter}
        onChange={(e) => setStageFilter(e.target.value)}
        style={selectInput}
      >
        <option value="all">All Stages</option>
        {savedStageOptions.map((opt) => (
          <option key={opt.key} value={opt.key}>
            {opt.label}
          </option>
        ))}
      </select>

      <select
        value={quoteTypeFilter}
        onChange={(e) => setQuoteTypeFilter(e.target.value)}
        style={selectInput}
      >
        <option value="all">All Types</option>
        <option value={CURTAIN_QUOTE_TYPE}>Curtains Only</option>
        <option value={GENERAL_QUOTE_TYPE}>General Quotations Only</option>
      </select>

      <select
        value={sortBy}
        onChange={(e) => setSortBy(e.target.value)}
        style={selectInput}
      >
        <option value="newest">Newest First</option>
        <option value="recently_edited">Recently Edited</option>
        <option value="oldest">Oldest First</option>
        <option value="highest">Highest Value</option>
        <option value="lowest">Lowest Value</option>
      </select>
    </div>

    {savedQuotesSearchText ? (
      <div style={{ marginBottom: 14, fontSize: 12, color: dashboardMuted }}>
        Search checks all saved quotations, including older records.
      </div>
    ) : null}

    {loadingSaved && <div>Loading...</div>}
    {savedQuotesAccessError ? (
      <div
        style={{
          marginBottom: 16,
          padding: "16px 18px",
          borderRadius: 16,
          background: isDark ? "rgba(127,29,29,0.22)" : "#fef2f2",
          border: "1px solid rgba(220,38,38,0.28)",
          color: isDark ? "#fecaca" : "#991b1b",
          fontSize: 13,
          lineHeight: 1.65,
        }}
      >
        <div style={{ fontWeight: 900, marginBottom: 4 }}>
          Saved quotes cannot be loaded
        </div>
        <div>{savedQuotesAccessError.message}</div>
        {isDevRuntime ? (
          <div style={{ marginTop: 8, fontFamily: "monospace", fontSize: 11 }}>
            {savedQuotesAccessError.code || "error"} | {savedQuotesAccessError.path || ""}
          </div>
        ) : null}
      </div>
    ) : null}
    {savedQuoteOpenError ? (
      <div
        style={{
          marginBottom: 16,
          padding: "16px 18px",
          borderRadius: 16,
          background: isDark ? "rgba(120,53,15,0.22)" : "#fff7ed",
          border: "1px solid rgba(249,115,22,0.28)",
          color: isDark ? "#fed7aa" : "#9a3412",
          fontSize: 13,
          lineHeight: 1.65,
          display: "grid",
          gap: 10,
        }}
      >
        <div>
          <div style={{ fontWeight: 900, marginBottom: 4 }}>
            This quotation could not be opened
          </div>
          <div>
            {savedQuoteOpenError.message ||
              "Please try again. If the issue continues, contact your admin."}
          </div>
          {savedQuoteOpenError.quoteNo ? (
            <div style={{ marginTop: 4, opacity: 0.86 }}>
              Quote #{savedQuoteOpenError.quoteNo}
            </div>
          ) : null}
          {Array.isArray(savedQuoteOpenError.candidates) &&
          savedQuoteOpenError.candidates.length ? (
            <div style={{ marginTop: 6 }}>
              Matching candidates:{" "}
              {savedQuoteOpenError.candidates
                .map((candidate) =>
                  [candidate.quoteNo ? `#${candidate.quoteNo}` : candidate.id, candidate.customerName]
                    .filter(Boolean)
                    .join(" / ")
                )
                .join(" | ")}
            </div>
          ) : null}
          {isDevRuntime ? (
            <div style={{ marginTop: 8, fontFamily: "monospace", fontSize: 11 }}>
              {savedQuoteOpenError.result || "error"}
              {savedQuoteOpenError.code ? ` | ${savedQuoteOpenError.code}` : ""}
              {Array.isArray(savedQuoteOpenError.attemptedPaths) &&
              savedQuoteOpenError.attemptedPaths.length
                ? ` | ${savedQuoteOpenError.attemptedPaths.join(", ")}`
                : ""}
            </div>
          ) : null}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {savedQuoteOpenError.result !== "not-found" ? (
            <button
              type="button"
              onClick={() => loadFullQuote(savedQuoteOpenError.quoteIndex)}
              style={{ ...btn("#2563eb"), marginRight: 0 }}
            >
              Try Open Again
            </button>
          ) : null}
          {isDevRuntime ? (
            <button
              type="button"
              onClick={() => {
                console.log("SAVED_QUOTE_FAILED_OPEN_DETAILS", savedQuoteOpenError);
                notify("Saved quote open details were logged to the console.", "info");
              }}
              style={{ ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#fff" : "#0f172a"), marginRight: 0 }}
            >
              Log Details
            </button>
          ) : null}
          {savedQuoteOpenError.result === "not-found" ? (
            <button
              type="button"
              onClick={() => {
                const errorKeys = new Set(
                  [
                    savedQuoteOpenError.indexId,
                    savedQuoteOpenError.quoteIndex?.indexId,
                    savedQuoteOpenError.quoteIndex?.quoteIndexId,
                    savedQuoteOpenError.quoteIndex?.id,
                    savedQuoteOpenError.quoteId,
                    savedQuoteOpenError.sourceQuoteId,
                  ]
                    .filter(Boolean)
                    .map(String)
                );
                setSavedQuotes((prev) =>
                  (prev || []).filter((quote) =>
                    [
                      quote.id,
                      quote.quoteId,
                      quote.sourceQuoteId,
                      quote.indexId,
                      quote.quoteIndexId,
                    ]
                      .filter(Boolean)
                      .every((key) => !errorKeys.has(String(key)))
                  )
                );
                setSavedQuoteOpenError(null);
              }}
              style={{ ...btn(isDark ? "#0f172a" : "#e2e8f0", isDark ? "#fff" : "#0f172a"), marginRight: 0 }}
            >
              Remove From View
            </button>
          ) : null}
        </div>
      </div>
    ) : null}
    {activeBusinessDashboardDrilldown ? (
      <div
        style={{
          marginBottom: 16,
          padding: "14px 16px",
          borderRadius: 16,
          background: isDark ? "rgba(37,99,235,0.18)" : "#eff6ff",
          border: "1px solid rgba(37,99,235,0.22)",
          display: "flex",
          flexDirection: isCompact ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isCompact ? "stretch" : "center",
          gap: 12,
        }}
      >
        <div>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#2563eb" }}>
            Showing: {activeBusinessDashboardDrilldown.label}
          </div>
          <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 4 }}>
            {filteredSavedQuotesForDisplay.length} quotation
            {filteredSavedQuotesForDisplay.length === 1 ? "" : "s"}
            {activeBusinessDashboardDrilldown.summaryValue != null
              ? ` | Total ${currencySymbol(currency)} ${Number(
                  activeBusinessDashboardDrilldown.summaryValue || 0
                ).toFixed(2)}`
              : ""}
          </div>
        </div>

        <button
          type="button"
          onClick={clearBusinessDashboardDrilldown}
          style={btn("#2563eb")}
        >
          Clear Filter
        </button>
      </div>
    ) : null}
    <>
      {/* ================= ACTIVE QUOTES ================= */}
      <div
        style={{
          borderRadius: 18,
          overflow: "hidden",
          background: isDark ? "#151520" : "#ffffff",
          boxShadow: "0 12px 32px rgba(0,0,0,0.08)",
        }}
      >
        {filteredSavedQuotesForDisplay.length === 0 && (
          <div style={{ padding: 24, opacity: 0.6 }}>
            {savedQuotesAccessError ? "Fix workspace permissions, then refresh Saved Quotes." : "No matching quotations"}
          </div>
        )}

        {filteredSavedQuotesForDisplay.length > 0 && (
          <div
            style={{
              padding: "12px 20px",
              borderBottom: "1px solid rgba(148,163,184,0.16)",
              fontSize: 12,
              fontWeight: 700,
              color: dashboardMuted,
              background: isDark ? "rgba(15,23,42,0.62)" : "#f8fafc",
            }}
          >
            Showing {visibleSavedQuotesDisplay.length} of{" "}
            {filteredSavedQuotesForDisplay.length} quotations
          </div>
        )}

        {visibleSavedQuotesDisplay.map((q) => {
    const computedTotals = getSavedQuoteTotals(q);
    const amount = Number(
      q.grandTotal ??
      q.total ??
      q.finalTotal ??
      q.totalAmount ??
      computedTotals.grandTotal ??
      q.subtotal ??
      0
    ).toFixed(2);
    const quoteType = resolveQuoteType(q);
    const typeLabel =
      quoteType === GENERAL_QUOTE_TYPE ? "General Quotation" : "Curtains";
    const stageLabel = getStageLabelForQuote(q);
    const stageColor = getStageColorForQuote(q);
    const checklistProgress = getChecklistProgress(
      buildQuoteCrmData(q).checklistItems
    );
    const stageOptions = getStageSelectOptions(
      crmPipelines,
      quoteType,
      q.workStage ?? q.crm?.stageId
    );

          return (
            <div
              key={q.id}
              style={{
                padding: 24,
                borderBottom: "1px solid rgba(0,0,0,0.05)",
                display: "flex",
                flexDirection: isCompact ? "column" : "row",
                justifyContent: "space-between",
                alignItems: isCompact ? "stretch" : "flex-start",
                transition: "all 0.2s ease",
                gap: 16,
              }}
            >
        {/* LEFT SIDE */}
        <div>
          <div style={{ fontSize: 17, fontWeight: 800 }}>
            {q.customerName} - #{q.quoteNo}
          </div>

          <div style={{ fontSize: 12, opacity: 0.6, marginTop: 4, display: "grid", gap: 4 }}>
            <span>
              Created: {getCreatedMillis(q) ? new Date(getCreatedMillis(q)).toLocaleDateString() : "-"}
            </span>
            <span>
              Updated: {getUpdatedMillis(q) ? new Date(getUpdatedMillis(q)).toLocaleDateString() : "-"}
            </span>
            <span>Stage: {stageLabel}</span>
            <span>
              Checklist: {checklistProgress.completed}/{checklistProgress.total || 0}
            </span>
          </div>

          <div
            style={{
              fontSize: 16,
              fontWeight: 900,
              marginTop: 8,
              color: "#2563eb",
            }}
          >
            {currencySymbol(currency)} {amount}
          </div>

          {/* STATUS BADGE */}
          <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <span
              style={{
                padding: "5px 12px",
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 700,
                color: "#fff",
                background:
                  q.status === "confirmed"
                    ? "#16a34a"
                    : "#f59e0b",
              }}
            >
              {q.status === "confirmed" ? "Final" : "Draft"}
            </span>
            <span
              style={{
                padding: "5px 12px",
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 700,
                color: quoteType === GENERAL_QUOTE_TYPE ? "#1d4ed8" : "#7c3aed",
                background:
                  quoteType === GENERAL_QUOTE_TYPE ? "#dbeafe" : "#ede9fe",
              }}
            >
              {typeLabel}
            </span>
          </div>
        </div>

        {/* RIGHT SIDE */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            alignItems: isCompact ? "stretch" : "flex-end",
            minWidth: isCompact ? "100%" : 260,
          }}
        >
          {/* CONFIRM / UNCONFIRM */}
          {canEditQuotes && q.status !== "confirmed" ? (
                        <Button
              icon={Check}
              variant="success"
              onClick={async () => {
                try {
                  if (!workspaceUid) return;

                  const ref = doc(
                    db,
                    "users",
                    workspaceUid,
                    "quotations",
                    q.id
                  );

                  const totals = getSavedQuoteTotals(q);

                  await updateDoc(ref, {
                    status: "confirmed",
                    subtotal: Number(totals.subtotal || 0),
                    grandTotal: Number(totals.grandTotal || 0),
                    confirmedAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                  });

                  applyQuoteLocalPatch(q.id, {
                    status: "confirmed",
                    subtotal: Number(totals.subtotal || 0),
                    grandTotal: Number(totals.grandTotal || 0),
                    confirmedAt: new Date(),
                    updatedAt: new Date(),
                  });
                } catch (err) {
                  console.error(err);
                  alert("Failed to confirm quote");
                }
              }}
            >
              Confirm
            </Button>
          ) : canEditQuotes ? (
                        <Button
              icon={RotateCcw}
              variant="warning"
              onClick={async () => {
                try {
                  if (!workspaceUid) return;

                  const ref = doc(
                    db,
                    "users",
                    workspaceUid,
                    "quotations",
                    q.id
                  );

                  await updateDoc(ref, {
                    status: "draft",
                    updatedAt: serverTimestamp(),
                  });

                  applyQuoteLocalPatch(q.id, {
                    status: "draft",
                    updatedAt: new Date(),
                  });
                } catch (err) {
                  console.error(err);
                  alert("Failed to revert quote");
                }
              }}
            >
              Unconfirm
            </Button>
          ) : null}

          {/* LOAD + DELETE */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", width: isCompact ? "100%" : "auto" }}>
            {canViewQuotes ? (
              <button
                style={btn("#1d4ed8")}
                onClick={() => {
                  loadFullQuote(q);
                }}
              >
                Load
              </button>
            ) : null}

            {canEditQuotes ? (
              <button
                style={btn("#ef4444")}
                onClick={() => deleteQuotation(q.id)}
              >
                Delete
              </button>
            ) : null}
          </div>

          {/* WORK STAGE */}
          {canEditQuotes ? (
            <select
              value={q.workStage ?? "quote_given"}
              onChange={(e) =>
                updateWorkStage(q.id, e.target.value)
              }
              style={{
                ...selectInput,
                padding: "6px 14px",
                borderRadius: 999,
                border: "1px solid rgba(148,163,184,0.6)",
                fontSize: 12,
                fontWeight: 700,
                background: stageColor,
                color: "#fff",
                WebkitTextFillColor: "#fff",
                cursor: "pointer",
                width: isCompact ? "100%" : "auto",
              }}
            >
              {stageOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>
          ) : (
            <div
              style={{
                padding: "8px 14px",
                borderRadius: 999,
                background: stageColor,
                color: "#fff",
                fontSize: 12,
                fontWeight: 700,
                alignSelf: isCompact ? "stretch" : "auto",
              }}
            >
              {stageLabel}
            </div>
          )}

          {/* INSTALLATION DATE (Editable Anytime) */}
          {canEditQuotes ? (
            <input
              type="date"
              value={toDateInputValue(q.installationDate)}
              onChange={async (e) => {
                try {
                  await updateInstallationDate(q.id, e.target.value);
                } catch (error) {
                  console.error("Failed to update installation date:", error);
                  alert("Failed to save installation date");
                }
              }}
              style={{
                padding: "6px 10px",
                borderRadius: 8,
                border: "1px solid rgba(148,163,184,0.4)",
                fontSize: 12,
                width: isCompact ? "100%" : "auto",
              }}
            />
          ) : (
            <div
              style={{
                fontSize: 12,
                color: dashboardMuted,
                fontWeight: 700,
              }}
            >
              Installation: {toDateInputValue(q.installationDate) || "-"}
            </div>
          )}
        </div>
            </div>
          );
        })}

        {hasMoreSavedQuotesDisplay || canLoadMoreSavedQuoteIndex ? (
          <div
            style={{
              padding: 18,
              display: "flex",
              justifyContent: "center",
              background: isDark ? "#151520" : "#ffffff",
            }}
          >
            <button
              type="button"
              style={btn("#0f172a")}
              disabled={loadingMoreSavedQuotes}
              onClick={() => {
                if (hasMoreSavedQuotesDisplay) {
                  setSavedQuotesVisibleCount((prev) => prev + SAVED_QUOTES_PAGE_SIZE);
                  return;
                }
                loadMoreSavedQuoteIndex();
              }}
            >
              {loadingMoreSavedQuotes ? "Loading..." : "Load More"}
            </button>
          </div>
        ) : null}
        </div>

          {/* ================= RECYCLE BIN ================= */}
          {!activeBusinessDashboardDrilldown ? (
            <div
              style={{
                marginTop: 24,
                display: "flex",
                flexDirection: isCompact ? "column" : "row",
                alignItems: isCompact ? "stretch" : "center",
                justifyContent: "space-between",
                gap: 12,
                padding: 14,
                borderRadius: 14,
                background: isDark ? "#111827" : "#f8fafc",
                border: isDark
                  ? "1px solid rgba(148,163,184,0.18)"
                  : "1px solid #e2e8f0",
              }}
            >
              <div>
                <div style={{ fontSize: 14, fontWeight: 900, color: isDark ? "#f8fafc" : "#0f172a" }}>
                  Recycle Bin
                </div>
                <div style={{ fontSize: 12, color: dashboardMuted }}>
                  Deleted quotations are loaded only when you open this section.
                </div>
              </div>
              <button
                type="button"
                style={btn(recycleBinOpen ? "#64748b" : "#b91c1c")}
                disabled={loadingRecycleBinQuotes}
                onClick={() => {
                  if (recycleBinOpen) {
                    setRecycleBinOpen(false);
                    return;
                  }
                  openRecycleBin();
                }}
              >
                {loadingRecycleBinQuotes
                  ? "Loading Recycle Bin..."
                  : recycleBinOpen
                  ? "Hide Recycle Bin"
                  : "Open Recycle Bin"}
              </button>
            </div>
          ) : null}

          {recycleBinOpen && !loadingRecycleBinQuotes && recycleBinQuotesForDisplay.length === 0 ? (
            <div
              style={{
                marginTop: 12,
                padding: 14,
                borderRadius: 14,
                background: isDark ? "#0f172a" : "#fff",
                border: isDark
                  ? "1px solid rgba(148,163,184,0.14)"
                  : "1px solid #e2e8f0",
                color: dashboardMuted,
                fontSize: 13,
              }}
            >
              No deleted quotations found in the first recycle-bin page.
            </div>
          ) : null}

          {recycleBinQuotesForDisplay.length > 0 ? (
            <>
              <h3
                style={{
                  fontSize: 16,
                  fontWeight: 900,
                  marginTop: 36,
                  marginBottom: 12,
                  color: "#b91c1c",
                }}
              >
                 Recycle Bin
              </h3>

              {recycleBinQuotesForDisplay.map((q) => (
            <div
              key={q.id}
              style={{
                padding: 16,
                borderRadius: 14,
                background: "#fff1f2",
                border: "1px solid #fecdd3",
                marginBottom: 10,
                display: "flex",
                flexDirection: isCompact ? "column" : "row",
                justifyContent: "space-between",
                alignItems: isCompact ? "stretch" : "center",
                gap: 12,
              }}
            >
              <div>
                {q.customerName} - #{q.quoteNo}
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {canEditQuotes ? (
                  <>
                    <button
                      style={btn("#16a34a")}
                      onClick={() => restoreQuotation(q.id)}
                    >
                      Restore
                    </button>

                    <button
                      style={btn("#dc2626")}
                      onClick={() => {
                        openConfirmDialog({
                          title: "Delete quotation permanently?",
                          description:
                            "This action cannot be undone. The quotation will be removed from Recycle Bin permanently.",
                          confirmLabel: "Delete Forever",
                          cancelLabel: "Cancel",
                          tone: "danger",
                          onConfirm: async () => {
                            await deleteDoc(
                              doc(
                                db,
                                "users",
                                workspaceUid,
                                "quotations",
                                q.id
                              )
                            );
                            invalidateWorkspaceCollectionCache(workspaceUid, "quotations");
                            setSavedQuotes((prev) =>
                              prev.filter((item) => item.id !== q.id)
                            );
                            notify("Quotation deleted permanently.", "success");
                          },
                        });
                      }}
                    >
                      Delete Forever
                    </button>
                  </>
                ) : null}
              </div>
            </div>
              ))}
            </>
          ) : null}
    </>
  </div>
)}

       {/* ================= SETTINGS TAB ================= */}
{tab === "settings" && (
  <div
    style={{
      background: isDark ? "#151520" : "#ffffff",
      padding: 32,
      borderRadius: 20,
      maxWidth: 900,
      margin: "0 auto",
      boxShadow: isDark
        ? "0px 0px 25px rgba(0,0,0,0.45)"
        : "0px 6px 20px rgba(0,0,0,0.08)",
    }}
  >
    <h2
      style={{
        fontSize: 24,
        fontWeight: 800,
        marginBottom: 24,
        letterSpacing: "-0.3px",
      }}
    >
      Settings
    </h2>

    {/* ================= SECTION WRAPPER ================= */}
    <div style={{ display: "grid", gap: 28 }}>

      {/* ------------------ Currency & Units ------------------ */}
      <div
        style={{
          padding: 20,
          borderRadius: 16,
          background: isDark ? "#1b1b27" : "#f8fafc",
          border: "1px solid rgba(0,0,0,0.08)",
        }}
      >
        <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 14 }}>
          General Settings
        </h3>

        {/* CURRENCY */}
        <label style={{ fontSize: 13, opacity: 0.7 }}>Currency</label>
        <select
          style={{ ...selectInput, marginBottom: 16 }}
          value={currency}
          onChange={(e) => setCurrency(e.target.value)}
        >
          {CURRENCY_LIST.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>

        {/* MEASUREMENT UNIT */}
        <label style={{ fontSize: 13, opacity: 0.7 }}>Measurement Unit</label>
        <select
          style={{ ...selectInput, marginBottom: 8 }}
          value={unit}
          onChange={(e) => setUnit(e.target.value)}
        >
          <option value="in">Inches</option>
          <option value="cm">Centimeters</option>
        </select>
      </div>

     

     {/* ------------------ Blind Calculation Settings ------------------ */}
<div
  style={{
    padding: 20,
    borderRadius: 16,
    background: isDark ? "#111827" : "#f8fafc",
    border: "1px solid rgba(0,0,0,0.08)",
  }}
>
  <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 12 }}>
    Blind Calculation Settings
  </h3>
  <label
    style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 10,
      cursor: "pointer",
      fontSize: 14,
      fontWeight: 700,
      color: isDark ? "#e5e7eb" : "#111827",
    }}
  >
    <input
      type="checkbox"
      checked={
        resolveBlindSqftRoundingMode(fabricCalcSettings, "exact") === "ceil"
      }
      onChange={(e) =>
        setFabricCalcSettings((prev) =>
          normalizeFabricCalcSettingsForState({
            ...prev,
            blindSqftRoundingMode: e.target.checked ? "ceil" : "exact",
          })
        )
      }
      style={{ marginTop: 2 }}
    />
    <span>
      Round blind sqft up to next full sqft
      <span
        style={{
          display: "block",
          marginTop: 4,
          fontSize: 12,
          lineHeight: 1.5,
          fontWeight: 500,
          color: isDark ? "#94a3b8" : "#64748b",
        }}
      >
        When enabled, 14.09 sqft will be billed as 15 sqft. When disabled,
        exact sqft will be used.
      </span>
    </span>
  </label>
</div>

     {/* ------------------ Minimum Sqft ------------------ */}
<div
  style={{
    padding: 20,
    borderRadius: 16,
    background: isDark ? "#1b1b27" : "#f8fafc",
    border: "1px solid rgba(0,0,0,0.08)",
  }}
>
  <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 16 }}>
    Minimum SQFT for Each Blind Type
  </h3>

  {Object.keys(minSqft).map((k) => (
    <div
      key={k}
      style={{
        display: "flex",
        justifyContent: "space-between",
        marginBottom: 12,
        alignItems: "center",
      }}
    >
      <span style={{ fontSize: 14 }}>{k}</span>

      <input
        type="number"
        style={{ ...input, width: 120 }}
        value={minSqft[k]}
        onChange={(e) =>
          setMinSqft({ ...minSqft, [k]: e.target.value })
        }
      />
    </div>
  ))}
</div>

{/* ------------------ Product Calculation Handoff ------------------ */}
<div
  style={{
    padding: 20,
    borderRadius: 16,
    background: isDark ? "#101827" : "#eef6ff",
    border: isDark
      ? "1px solid rgba(96,165,250,0.24)"
      : "1px solid rgba(37,99,235,0.18)",
  }}
>
  <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 8 }}>
    Fabric calculation rules moved
  </h3>
  <p
    style={{
      margin: "0 0 14px",
      color: isDark ? "#bfdbfe" : "#1e3a8a",
      fontSize: 13,
      lineHeight: 1.55,
    }}
  >
    Curtain and blind hems, side allowances, usable fabric width, fullness, and
    meter formulas are now managed per product in Products & Calculations.
    Legacy values remain available only as fallback data for old quotations.
  </p>
  <button
    type="button"
    onClick={() => setRoutedTab("products")}
    disabled={!canManageProductConfigs}
    style={{
      padding: "10px 14px",
      borderRadius: 12,
      border: 0,
      background: canManageProductConfigs ? "#2563eb" : "#94a3b8",
      color: "#fff",
      fontWeight: 800,
      cursor: canManageProductConfigs ? "pointer" : "not-allowed",
    }}
  >
    Manage Product Calculations
  </button>
  {!canManageProductConfigs ? (
    <div style={{ marginTop: 10, fontSize: 12, color: isDark ? "#94a3b8" : "#64748b" }}>
      You need product/settings access to edit calculation rules.
    </div>
  ) : null}
</div>

{/* ------------------ Fabric Brands ------------------ */}
<div
  style={{
    padding: 20,
    borderRadius: 16,
    background: isDark ? "#111827" : "#f8fafc",
    border: isDark ? "1px solid rgba(148,163,184,0.18)" : "1px solid #e2e8f0",
    display: "grid",
    gap: 16,
  }}
>
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 12,
      flexWrap: "wrap",
    }}
  >
    <div>
      <h3 style={{ fontSize: 17, fontWeight: 800, margin: 0 }}>
        Fabric Brands
      </h3>
      <div style={{ marginTop: 6, fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
        Manage reusable fabric brand names for the Curtain Quotation fabric summary.
      </div>
    </div>
    <Button
      icon={RotateCcw}
      variant="secondary"
      onClick={() => loadFabricBrands({ force: true })}
      disabled={fabricBrandsLoading}
    >
      {fabricBrandsLoading ? "Loading..." : "Refresh"}
    </Button>
  </div>

  <div
    style={{
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "1.1fr 0.9fr",
      gap: 12,
    }}
  >
    <label style={{ display: "grid", gap: 6 }}>
      <span style={{ fontSize: 12, fontWeight: 800, color: dashboardMuted }}>
        Brand Name
      </span>
      <input
        style={input}
        value={fabricBrandDraft.name}
        placeholder="D'Decor, GM Fabrics, Vista"
        onChange={(event) =>
          setFabricBrandDraft((prev) => ({ ...prev, name: event.target.value }))
        }
      />
    </label>
    <label style={{ display: "grid", gap: 6 }}>
      <span style={{ fontSize: 12, fontWeight: 800, color: dashboardMuted }}>
        Supplier / Notes
      </span>
      <input
        style={input}
        value={fabricBrandDraft.supplier}
        placeholder="Optional supplier"
        onChange={(event) =>
          setFabricBrandDraft((prev) => ({
            ...prev,
            supplier: event.target.value,
          }))
        }
      />
    </label>
  </div>

  <label style={{ display: "grid", gap: 6 }}>
    <span style={{ fontSize: 12, fontWeight: 800, color: dashboardMuted }}>
      Notes
    </span>
    <input
      style={input}
      value={fabricBrandDraft.notes}
      placeholder="Optional brand note"
      onChange={(event) =>
        setFabricBrandDraft((prev) => ({ ...prev, notes: event.target.value }))
      }
    />
  </label>

  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
    <Button icon={Plus} variant="success" onClick={saveFabricBrandDraft}>
      Add Fabric Brand
    </Button>
    <span style={{ alignSelf: "center", fontSize: 12, color: dashboardMuted }}>
      Brands are loaded once per workspace and reused in quotation summaries.
    </span>
  </div>

  <div style={{ display: "grid", gap: 8 }}>
    {fabricBrands.length ? (
      fabricBrands.map((brand) => {
        const isExpanded = expandedFabricBrandId === brand.id;
        return (
          <div
            key={brand.id}
            style={{
              display: "grid",
              gap: 10,
              padding: "12px 12px",
              borderRadius: 14,
              background: isDark ? "#0f172a" : "#ffffff",
              border: isDark
                ? "1px solid rgba(148,163,184,0.14)"
                : "1px solid #e2e8f0",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 900 }}>{brand.name}</div>
                {brand.supplier || brand.notes ? (
                  <div style={{ marginTop: 3, fontSize: 11, color: dashboardMuted }}>
                    {[brand.supplier, brand.notes].filter(Boolean).join(" | ")}
                  </div>
                ) : null}
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={async () => {
                    if (isExpanded) {
                      setExpandedFabricBrandId("");
                      return;
                    }
                    setExpandedFabricBrandId(brand.id);
                    await loadFabricCatalogueLibrary(brand.id);
                  }}
                  style={{
                    border: 0,
                    background: isDark ? "#1e293b" : "#dbeafe",
                    color: isDark ? "#bfdbfe" : "#1d4ed8",
                    fontSize: 12,
                    fontWeight: 900,
                    cursor: "pointer",
                    padding: "7px 10px",
                    borderRadius: 10,
                  }}
                >
                  {isExpanded ? "Hide catalogues" : "Catalogues & SR RRP"}
                </button>
                <button
                  type="button"
                  onClick={() => archiveFabricBrand(brand.id)}
                  style={{
                    border: 0,
                    background: "transparent",
                    color: "#dc2626",
                    fontSize: 12,
                    fontWeight: 900,
                    cursor: "pointer",
                    padding: "7px 0",
                  }}
                >
                  Archive
                </button>
              </div>
            </div>
            {isExpanded ? renderFabricBrandCatalogueManager(brand) : null}
          </div>
        );
      })
    ) : (
      <div style={{ fontSize: 12, color: dashboardMuted }}>
        No fabric brands added yet.
      </div>
    )}
  </div>
</div>
          

      

      {/* ------------------ Shop Address + Logo ------------------ */}
      <div
        style={{
          padding: 20,
          borderRadius: 16,
          background: isDark ? "#1b1b27" : "#f8fafc",
          border: "1px solid rgba(0,0,0,0.08)",
        }}
      >
        <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 16 }}>
          Business Details
        </h3>

        <label style={{ fontSize: 13, opacity: 0.7 }}>Business Name</label>
        <input
          style={{ ...input, marginBottom: 12 }}
          value={businessName}
          onChange={(e) => setBusinessName(e.target.value)}
          placeholder="Enter your business name"
        />

        {/* SHOP ADDRESS */}
        <label style={{ fontSize: 13, opacity: 0.7 }}>Shop Address</label>
        <textarea
          style={{ ...input, marginBottom: 16, height: 80 }}
          value={shopAddress}
          onChange={(e) => setShopAddress(e.target.value)}
        />

        <label style={{ fontSize: 13, opacity: 0.7 }}>Business Phone</label>
        <input
          style={{ ...input, marginBottom: 12 }}
          value={businessPhone}
          onChange={(e) => setBusinessPhone(e.target.value)}
          placeholder="Enter business phone"
        />

        <label style={{ fontSize: 13, opacity: 0.7 }}>Business Email</label>
        <input
          style={{ ...input, marginBottom: 16 }}
          value={businessEmail}
          onChange={(e) => setBusinessEmail(e.target.value)}
          placeholder="Enter business email"
        />

        {/* LOGO UPLOAD */}
        <label style={{ fontSize: 13, opacity: 0.7 }}>Brand Logo</label>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ marginBottom: 16 }}
          onChange={(e) => setSelectedLogoFile(e.target.files[0])}
        />

        {logoURL && (
          <img
            src={logoURL}
            alt="logo"
            style={{
              height: 70,
              marginBottom: 16,
              borderRadius: 10,
              objectFit: "contain",
              }}
            />
        )}
      </div>

      <div
        style={{
          padding: 20,
          borderRadius: 16,
          background: isDark ? "#111827" : "#f8fafc",
          border: isDark ? "1px solid rgba(148,163,184,0.18)" : "1px solid #e2e8f0",
          display: "grid",
          gap: 16,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          <div>
            <h3 style={{ fontSize: 17, fontWeight: 800, margin: 0 }}>
              Storage Manager
            </h3>
            <div style={{ fontSize: 12, color: dashboardMuted, marginTop: 6, lineHeight: 1.6 }}>
              Review optimized uploads from this workspace. Files are archived first;
              permanent deletion is admin-only and requires confirmation.
            </div>
          </div>
          <Button
            icon={FileImage}
            variant="secondary"
            onClick={loadStorageManagerFiles}
            disabled={storageManagerState.loading}
          >
            {storageManagerState.loading ? "Loading..." : "Refresh Storage"}
          </Button>
        </div>

        {storageManagerState.usage ? (() => {
          const usedBytes = Number(storageManagerState.usage.usedBytes || 0);
          const includedBytes = Number(
            storageManagerState.usage.includedBytes || 1024 * 1024 * 1024
          );
          const percent = includedBytes
            ? Math.min(100, Math.round((usedBytes / includedBytes) * 100))
            : 0;
          return (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                gap: 10,
              }}
            >
              {[
                ["Used", formatStorageBytes(usedBytes)],
                ["Included", formatStorageBytes(includedBytes)],
                ["Remaining", formatStorageBytes(Math.max(0, includedBytes - usedBytes))],
                ["Images", Number(storageManagerState.usage.imageCount || 0).toLocaleString("en-IN")],
              ].map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    padding: 12,
                    borderRadius: 14,
                    background: isDark ? "#0f172a" : "#ffffff",
                    border: isDark ? "1px solid rgba(148,163,184,0.15)" : "1px solid #e2e8f0",
                  }}
                >
                  <div style={{ fontSize: 11, color: dashboardMuted, fontWeight: 800 }}>
                    {label}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 17, fontWeight: 900 }}>
                    {value}
                  </div>
                </div>
              ))}
              <div
                style={{
                  gridColumn: "1 / -1",
                  height: 8,
                  borderRadius: 999,
                  background: isDark ? "#1f2937" : "#e2e8f0",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${percent}%`,
                    height: "100%",
                    background: percent > 85 ? "#dc2626" : percent > 65 ? "#f59e0b" : "#16a34a",
                  }}
                />
              </div>
            </div>
          );
        })() : (
          <div style={{ fontSize: 12, color: dashboardMuted }}>
            Click Refresh Storage to load the manifest. This does not scan Firebase Storage.
          </div>
        )}

        {storageManagerState.error ? (
          <div style={{ color: "#dc2626", fontSize: 12, fontWeight: 800 }}>
            {storageManagerState.error}
          </div>
        ) : null}

        {storageManagerState.files.length ? (
          <div style={{ display: "grid", gap: 10 }}>
            {storageManagerState.files.map((file) => {
              const previewUrl =
                file.thumbnailDownloadURL || file.downloadURL || file.url || "";
              const linkedRecord =
                (Array.isArray(file.linkedRecords) && file.linkedRecords[0]) || {};
              return (
                <div
                  key={file.fileId || file.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "56px minmax(0, 1fr)",
                    gap: 12,
                    padding: 12,
                    borderRadius: 16,
                    background: isDark ? "#0f172a" : "#ffffff",
                    border: isDark ? "1px solid rgba(148,163,184,0.15)" : "1px solid #e2e8f0",
                  }}
                >
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 12,
                      overflow: "hidden",
                      background: isDark ? "#1f2937" : "#e2e8f0",
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    {previewUrl ? (
                      <img
                        src={previewUrl}
                        alt={file.filename || "Stored file"}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <FileImage size={20} color={isDark ? "#94a3b8" : "#64748b"} />
                    )}
                  </div>
                  <div style={{ minWidth: 0, display: "grid", gap: 8 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 10,
                        flexWrap: "wrap",
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 900,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={file.filename || file.fullPath}
                        >
                          {file.originalFilename || file.filename || "Stored image"}
                        </div>
                        <div style={{ fontSize: 11, color: dashboardMuted, marginTop: 3 }}>
                          {file.ownerType || "image"} · {formatStorageBytes(
                            Number(file.sizeBytes || 0) + Number(file.thumbnailSizeBytes || 0)
                          )} · {file.compressionStatus || "legacy"}
                        </div>
                        <div style={{ fontSize: 11, color: dashboardMuted, marginTop: 3 }}>
                          Linked: {linkedRecord.type || file.ownerType || "-"}{" "}
                          {linkedRecord.id || file.ownerId || file.quoteId || file.leadId || ""}
                        </div>
                      </div>
                      <span
                        style={{
                          alignSelf: "flex-start",
                          padding: "5px 8px",
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 900,
                          background: file.deleted
                            ? "#fee2e2"
                            : file.archived
                            ? "#fef3c7"
                            : "#dcfce7",
                          color: file.deleted
                            ? "#991b1b"
                            : file.archived
                            ? "#92400e"
                            : "#166534",
                        }}
                      >
                        {file.deleted ? "Deleted" : file.archived ? "Archived" : "Active"}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {file.downloadURL || file.url ? (
                        <a
                          href={file.downloadURL || file.url}
                          target="_blank"
                          rel="noreferrer"
                          style={{ fontSize: 12, fontWeight: 800, color: "#2563eb" }}
                        >
                          View
                        </a>
                      ) : null}
                      {!file.archived && !file.deleted ? (
                        <button
                          type="button"
                          onClick={() => archiveStorageManagerFile(file)}
                          style={{
                            border: 0,
                            background: "transparent",
                            color: "#b45309",
                            fontSize: 12,
                            fontWeight: 900,
                            cursor: "pointer",
                            padding: 0,
                          }}
                        >
                          Archive
                        </button>
                      ) : null}
                      {canManageStorageFiles && !file.deleted ? (
                        <button
                          type="button"
                          onClick={() => permanentlyDeleteStorageManagerFile(file)}
                          style={{
                            border: 0,
                            background: "transparent",
                            color: "#dc2626",
                            fontSize: 12,
                            fontWeight: 900,
                            cursor: "pointer",
                            padding: 0,
                          }}
                        >
                          Delete permanently
                        </button>
                      ) : null}
                      {IS_DEV_UI_ENABLED ? (
                        <button
                          type="button"
                          onClick={() => copyStorageManagerFileDebug(file)}
                          style={{
                            border: 0,
                            background: "transparent",
                            color: isDark ? "#93c5fd" : "#1d4ed8",
                            fontSize: 12,
                            fontWeight: 900,
                            cursor: "pointer",
                            padding: 0,
                          }}
                        >
                          Copy JSON
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : storageManagerState.loadedAt && !storageManagerState.loading ? (
          <div style={{ fontSize: 12, color: dashboardMuted }}>
            No storage manifest files found yet. New uploads will appear here.
          </div>
        ) : null}
      </div>

      <div
        style={{
          padding: 20,
          borderRadius: 16,
          background: isDark ? "#1b1b27" : "#f8fafc",
          border: "1px solid rgba(0,0,0,0.08)",
          display: "grid",
          gap: 12,
        }}
      >
        <div style={{ display: "grid", gap: 6 }}>
          <h3 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>
            Team members moved
          </h3>
          <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.7 }}>
            Team invites, permissions, and assignment access now live only in the dedicated Team &amp; Access tab so there is a single place to manage staff.
          </div>
        </div>
        {canAccessTeamAccess ? (
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <Button
              icon={Users}
              variant="secondary"
              onClick={() => setRoutedTab("teamAccess")}
            >
              Open Team &amp; Access
            </Button>
          </div>
        ) : null}
      </div>

      <div
        style={{
          padding: 20,
          borderRadius: 16,
          background: isDark ? "#101827" : "#eff6ff",
          border: isDark
            ? "1px solid rgba(96,165,250,0.24)"
            : "1px solid rgba(37,99,235,0.18)",
          display: "grid",
          gap: 12,
        }}
      >
        <div style={{ display: "grid", gap: 6 }}>
          <h3 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>
            Account Security
          </h3>
          <div style={{ fontSize: 12, color: dashboardMuted, lineHeight: 1.7 }}>
            Change your password, review signed-in devices, and secure your account from one place.
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-start" }}>
          <Button
            icon={UserCog}
            variant="secondary"
            onClick={() => setRoutedTab("accountSecurity")}
          >
            Open Account Security
          </Button>
        </div>
      </div>
    </div>

    {/* ------------------ SINGLE UNIVERSAL SAVE BUTTON ------------------ */}
        <Button
      icon={Save}
      variant="primary"
      onClick={saveSettings}
      disabled={isSavingSettings}
      style={{ marginLeft: "auto", marginTop: 30 }}
    >
      {isSavingSettings ? "Saving..." : "Save All Settings"}
    </Button>
  </div>
)}


        {/* ================= STITCHING TAB ================= */}
        {/* ================= STITCHING TAB ================= */}
{tab === "stitching" && (
  <div
    style={{
      display: "grid",
      gap: 18,
      maxWidth: 1120,
      margin: "0 auto",
    }}
  >
    <div
      style={{
        background: isDark
          ? "linear-gradient(135deg, rgba(15,23,42,0.96), rgba(30,64,175,0.42))"
          : "linear-gradient(135deg, #eff6ff, #ffffff)",
        padding: 24,
        borderRadius: 22,
        border: isDark ? "1px solid rgba(96,165,250,0.25)" : "1px solid #dbeafe",
        boxShadow: isDark
          ? "0 24px 60px rgba(0,0,0,0.34)"
          : "0 18px 45px rgba(15,23,42,0.08)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h2 style={{ fontSize: 24, fontWeight: 900, margin: 0 }}>
            Stitching & Track Pricing
          </h2>
          <div style={{ marginTop: 8, fontSize: 13, color: dashboardMuted, lineHeight: 1.7 }}>
            Set per-running-foot rates, fullness, and default track length for each
            stitching/track style.
          </div>
        </div>
        {isStitchingSettingsDirty ? (
          <div
            style={{
              padding: "8px 12px",
              borderRadius: 999,
              background: isDark ? "rgba(245,158,11,0.18)" : "#fffbeb",
              border: isDark ? "1px solid rgba(251,191,36,0.3)" : "1px solid #fde68a",
              color: isDark ? "#fde68a" : "#92400e",
              fontSize: 12,
              fontWeight: 800,
            }}
          >
            Unsaved changes
          </div>
        ) : null}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isCompact ? "1fr" : "repeat(3, minmax(0, 1fr))",
          gap: 12,
          marginTop: 18,
        }}
      >
        {[
          ["Rate / Running ft", STITCHING_SETTING_HELP.rate],
          ["Fullness Multiplier", STITCHING_SETTING_HELP.fullness],
          ["Default Track Length", STITCHING_SETTING_HELP.trackLength],
        ].map(([label, copy]) => (
          <div
            key={label}
            style={{
              padding: 14,
              borderRadius: 16,
              background: isDark ? "rgba(15,23,42,0.62)" : "rgba(255,255,255,0.82)",
              border: isDark ? "1px solid rgba(148,163,184,0.18)" : "1px solid #e2e8f0",
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 900 }}>{label}</div>
            <div style={{ marginTop: 5, color: dashboardMuted, fontSize: 12, lineHeight: 1.55 }}>
              {copy}
            </div>
          </div>
        ))}
      </div>
    </div>

    <div
      style={{
        background: isDark ? "#151520" : "#ffffff",
        padding: 22,
        borderRadius: 20,
        border: isDark ? "1px solid rgba(148,163,184,0.16)" : "1px solid #e2e8f0",
        boxShadow: isDark
          ? "0px 18px 40px rgba(0,0,0,0.35)"
          : "0px 14px 32px rgba(15,23,42,0.06)",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isCompact
            ? "1fr"
            : "minmax(180px, 1.2fr) minmax(150px, 1fr) minmax(150px, 1fr) minmax(170px, 1fr) 96px",
          gap: 12,
          alignItems: "center",
          color: dashboardMuted,
          fontWeight: 900,
          fontSize: 12,
          letterSpacing: "0.02em",
          textTransform: "uppercase",
          padding: "0 4px 10px",
        }}
      >
        <span>Style Name</span>
        <span>Rate / Running ft (₹)</span>
        <span>Fullness Multiplier</span>
        <span>Default Track Length (ft)</span>
        <span>Actions</span>
    </div>

    {Object.entries(stitchingRates || {}).map(([name, cfg]) => {
      const settingCfg = normalizeStitchingSettingForState(cfg);
      const bendSettings = settingCfg.bendSettings || DEFAULT_BEND_SETTINGS;
      const rateValue = parseStitchingSettingNumber(settingCfg?.rate, 0);
      const trackLengthValue = parseStitchingSettingNumber(settingCfg?.trackLengthFt, 0);
      const exampleCharge = Number.isFinite(rateValue * trackLengthValue)
        ? rateValue * trackLengthValue
        : 0;

      return (
        <div
          key={name}
          style={{
            display: "grid",
            gridTemplateColumns: isCompact
              ? "1fr"
              : "minmax(180px, 1.2fr) minmax(150px, 1fr) minmax(150px, 1fr) minmax(170px, 1fr) 96px",
            gap: 12,
            alignItems: "start",
            padding: 14,
            marginBottom: 10,
            borderRadius: 16,
            background: isDark ? "rgba(15,23,42,0.72)" : "#f8fafc",
            border: isDark ? "1px solid rgba(148,163,184,0.16)" : "1px solid #e2e8f0",
          }}
        >
          <div>
            <div style={{ fontSize: 15, fontWeight: 900, color: textColor }}>
              {name}
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: dashboardMuted, lineHeight: 1.5 }}>
              Example: {trackLengthValue || 0} ft x{" "}
              {formatCurrencyDisplay(rateValue || 0, currency)} ={" "}
              {formatCurrencyDisplay(exampleCharge, currency)} track/stitch charge
            </div>
          </div>

          <label style={{ display: "grid", gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 800 }}>Rate / Running ft (₹)</span>
            <input
              style={{ ...input, padding: "10px 12px", borderRadius: 12 }}
              placeholder="200"
              type="number"
              min="0"
              step="0.01"
              value={getInputValue(settingCfg.rate)}
              onChange={(e) => {
                const val = e.target.value;
                setStitchingRates((prev) => ({
                  ...prev,
                  [name]: {
                    ...prev[name],
                    rate: val,
                  },
                }));
                setIsStitchingSettingsDirty(true);
              }}
            />
            <span style={{ fontSize: 11, color: dashboardMuted, lineHeight: 1.45 }}>
              {STITCHING_SETTING_HELP.rate}
            </span>
          </label>

          <label style={{ display: "grid", gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 800 }}>Fullness Multiplier</span>
            <input
              style={{ ...input, padding: "10px 12px", borderRadius: 12 }}
              placeholder="3"
              type="number"
              min="0.01"
              step="0.01"
              value={getInputValue(settingCfg.fullness)}
              onChange={(e) => {
                const val = e.target.value;
                setStitchingRates((prev) => ({
                  ...prev,
                  [name]: {
                    ...prev[name],
                    fullness: val,
                  },
                }));
                setIsStitchingSettingsDirty(true);
              }}
            />
            <span style={{ fontSize: 11, color: dashboardMuted, lineHeight: 1.45 }}>
              {STITCHING_SETTING_HELP.fullness}
            </span>
          </label>

          <label style={{ display: "grid", gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 800 }}>Default Track Length (ft)</span>
            <input
              style={{ ...input, padding: "10px 12px", borderRadius: 12 }}
              placeholder="16"
              type="number"
              min="0"
              step="0.01"
              value={getInputValue(settingCfg.trackLengthFt)}
              onChange={(e) => {
                const val = e.target.value;
                setStitchingRates((prev) => ({
                  ...prev,
                  [name]: {
                    ...prev[name],
                    trackLengthFt: val,
                  },
                }));
                setIsStitchingSettingsDirty(true);
              }}
            />
            <span style={{ fontSize: 11, color: dashboardMuted, lineHeight: 1.45 }}>
              {STITCHING_SETTING_HELP.trackLength}
            </span>
          </label>

          <details
            style={{
              gridColumn: isCompact ? "auto" : "1 / -1",
              borderRadius: 14,
              border: isDark ? "1px solid rgba(20,184,166,0.18)" : "1px solid #ccfbf1",
              background: isDark ? "rgba(15,118,110,0.08)" : "#f0fdfa",
              padding: 12,
            }}
          >
            <summary
              style={{
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 900,
                color: isDark ? "#99f6e4" : "#0f766e",
              }}
            >
              Bended Track Settings
            </summary>
            <div style={{ display: "grid", gap: 14, marginTop: 12 }}>
              <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 800 }}>
                  <input
                    type="checkbox"
                    checked={settingCfg.includesTrack === true}
                    onChange={(event) => {
                      setStitchingRates((prev) => ({
                        ...prev,
                        [name]: {
                          ...prev[name],
                          includesTrack: event.target.checked,
                        },
                      }));
                      setIsStitchingSettingsDirty(true);
                    }}
                  />
                  This rate includes track/channel
                </label>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 800 }}>
                  <input
                    type="checkbox"
                    checked={settingCfg.supportsBendedTrack === true}
                    onChange={(event) => {
                      setStitchingRates((prev) => ({
                        ...prev,
                        [name]: {
                          ...prev[name],
                          includesTrack: event.target.checked ? true : prev[name]?.includesTrack,
                          supportsBendedTrack: event.target.checked,
                          bendSettings: {
                            ...(prev[name]?.bendSettings || DEFAULT_BEND_SETTINGS),
                            enabled: event.target.checked,
                          },
                        },
                      }));
                      setIsStitchingSettingsDirty(true);
                    }}
                  />
                  Allow bended track for this package
                </label>
              </div>

              {settingCfg.supportsBendedTrack ? (
                <>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isCompact ? "1fr" : "repeat(4, minmax(0, 1fr))",
                      gap: 10,
                    }}
                  >
                    <label style={{ display: "grid", gap: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 800 }}>Bend Pricing Method</span>
                      <select
                        style={selectInput}
                        value={bendSettings.pricingMethod}
                        onChange={(event) => {
                          setStitchingRates((prev) => ({
                            ...prev,
                            [name]: {
                              ...prev[name],
                              bendSettings: {
                                ...(prev[name]?.bendSettings || DEFAULT_BEND_SETTINGS),
                                pricingMethod: event.target.value,
                                enabled: true,
                              },
                            },
                          }));
                          setIsStitchingSettingsDirty(true);
                        }}
                      >
                        {BENDED_TRACK_PRICING_METHODS.map((method) => (
                          <option key={method.value} value={method.value}>
                            {method.label}
                          </option>
                        ))}
                      </select>
                    </label>

                    {bendSettings.pricingMethod === "fixed_per_bend" ? (
                      <label style={{ display: "grid", gap: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 800 }}>Default bend charge / bend</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          style={input}
                          value={getInputValue(bendSettings.defaultBendCharge)}
                          onChange={(event) => {
                            setStitchingRates((prev) => ({
                              ...prev,
                              [name]: {
                                ...prev[name],
                                bendSettings: {
                                  ...(prev[name]?.bendSettings || DEFAULT_BEND_SETTINGS),
                                  defaultBendCharge: event.target.value,
                                  enabled: true,
                                },
                              },
                            }));
                            setIsStitchingSettingsDirty(true);
                          }}
                        />
                      </label>
                    ) : null}

                    {bendSettings.pricingMethod === "by_angle" ? (
                      ["90", "120", "135", "custom"].map((angle) => (
                        <label key={angle} style={{ display: "grid", gap: 6 }}>
                          <span style={{ fontSize: 12, fontWeight: 800 }}>
                            {angle === "custom" ? "Custom angle default" : `${angle} deg bend charge`}
                          </span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            style={input}
                            value={getInputValue(bendSettings.angleCharges?.[angle])}
                            onChange={(event) => {
                              setStitchingRates((prev) => ({
                                ...prev,
                                [name]: {
                                  ...prev[name],
                                  bendSettings: {
                                    ...(prev[name]?.bendSettings || DEFAULT_BEND_SETTINGS),
                                    angleCharges: {
                                      ...((prev[name]?.bendSettings || DEFAULT_BEND_SETTINGS).angleCharges || {}),
                                      [angle]: event.target.value,
                                    },
                                    enabled: true,
                                  },
                                },
                              }));
                              setIsStitchingSettingsDirty(true);
                            }}
                          />
                        </label>
                      ))
                    ) : null}
                  </div>

                  <div>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 8 }}>
                      Allowed Bend Types / Angles
                    </div>
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                      {BENDED_TRACK_SHAPES.filter((shape) => shape.shape !== "straight").map((shape) => (
                        <label
                          key={shape.shape}
                          style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 800 }}
                        >
                          <input
                            type="checkbox"
                            checked={normalizeAllowedBendShapes(bendSettings.allowedAngles).includes(shape.shape)}
                            onChange={(event) => {
                              const current = normalizeAllowedBendShapes(
                                (stitchingRates[name]?.bendSettings || DEFAULT_BEND_SETTINGS).allowedAngles
                              );
                              const nextAllowed = event.target.checked
                                ? Array.from(new Set([...current, shape.shape]))
                                : current.filter((entry) => entry !== shape.shape);
                              setStitchingRates((prev) => ({
                                ...prev,
                                [name]: {
                                  ...prev[name],
                                  bendSettings: {
                                    ...(prev[name]?.bendSettings || DEFAULT_BEND_SETTINGS),
                                    allowedAngles: nextAllowed,
                                    enabled: true,
                                  },
                                },
                              }));
                              setIsStitchingSettingsDirty(true);
                            }}
                          />
                          {shape.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  <label style={{ display: "grid", gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 800 }}>Optional notes</span>
                    <input
                      style={input}
                      value={bendSettings.notes || ""}
                      placeholder="Charge extra for ceiling bend / bay window bend."
                      onChange={(event) => {
                        setStitchingRates((prev) => ({
                          ...prev,
                          [name]: {
                            ...prev[name],
                            bendSettings: {
                              ...(prev[name]?.bendSettings || DEFAULT_BEND_SETTINGS),
                              notes: event.target.value,
                              enabled: true,
                            },
                          },
                        }));
                        setIsStitchingSettingsDirty(true);
                      }}
                    />
                  </label>
                </>
              ) : null}
            </div>
          </details>

          <Button
            icon={Trash2}
            variant="danger"
            disabled={isSavingSettings}
            onClick={async () => {
              if (!workspaceUid) {
                notify("Workspace is not ready. Please refresh and try again.", "warning");
                return;
              }

              try {
                const ref = doc(db, "users", workspaceUid, "settings", "main");

                await updateDoc(ref, {
                  [`stitching.${name}`]: deleteField(),
                });

                const updated = { ...stitchingRates };
                delete updated[name];
                setStitchingRates(updated);
                invalidateFirestoreReadCache(ref);
                mainSettingsLoadedWorkspaceRef.current = "";
                commitCurtainRows(
                  (prevRows) => prevRows.map((row) => ({ ...row })),
                  QUOTE_RAW_FIELD_MUTATION_SOURCES.PRODUCT_DEFAULT_APPLY
                );
                setEffectiveRows((prevRows) => prevRows.map((row) => ({ ...row })));
                notify("Stitching / track style deleted.", "success");
              } catch (err) {
                console.error("Delete stitching style failed:", err);
                notify("Failed to delete stitching / track style.", "error", 5200);
              }
            }}
          >
            Delete
          </Button>
        </div>
      );
    })}
    </div>

    <div
      style={{
        background: isDark ? "#151520" : "#ffffff",
        padding: 22,
        borderRadius: 20,
        border: isDark ? "1px solid rgba(148,163,184,0.16)" : "1px solid #e2e8f0",
        boxShadow: isDark
          ? "0px 18px 40px rgba(0,0,0,0.35)"
          : "0px 14px 32px rgba(15,23,42,0.06)",
      }}
    >
      <h3 style={{ margin: 0, fontSize: 18, fontWeight: 900 }}>
        Add New Stitching / Track Style
      </h3>
      <div style={{ marginTop: 6, fontSize: 12, color: dashboardMuted, lineHeight: 1.6 }}>
        Add a reusable style with its running-foot rate, fabric fullness, and fallback
        track length.
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isCompact ? "1fr" : "repeat(4, minmax(0, 1fr))",
          gap: 12,
          marginTop: 16,
        }}
      >
        <label style={{ display: "grid", gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 800 }}>Style Name</span>
          <input
            style={{ ...input, padding: "10px 12px", borderRadius: 12 }}
            placeholder="Ripple - Motorized"
            value={newStitch}
            onChange={(e) => setNewStitch(e.target.value)}
          />
        </label>

        <label style={{ display: "grid", gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 800 }}>Rate / Running ft (₹)</span>
          <input
            style={{ ...input, padding: "10px 12px", borderRadius: 12 }}
            placeholder="200"
            type="number"
            min="0"
            step="0.01"
            value={newRate === null || newRate === undefined ? "" : newRate}
            onChange={(e) => setNewRate(e.target.value)}
          />
        </label>

        <label style={{ display: "grid", gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 800 }}>Fullness Multiplier</span>
          <input
            style={{ ...input, padding: "10px 12px", borderRadius: 12 }}
            placeholder="3"
            type="number"
            min="0.01"
            step="0.01"
            value={
              newFullness === null || newFullness === undefined
                ? ""
                : newFullness
            }
            onChange={(e) => setNewFullness(e.target.value)}
          />
        </label>

        <label style={{ display: "grid", gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 800 }}>Default Track Length (ft)</span>
          <input
            style={{ ...input, padding: "10px 12px", borderRadius: 12 }}
            placeholder="16"
            type="number"
            min="0"
            step="0.01"
            value={
              newTrackLengthFt === null || newTrackLengthFt === undefined
                ? ""
                : newTrackLengthFt
            }
            onChange={(e) => setNewTrackLengthFt(e.target.value)}
          />
        </label>
      </div>

      <div
        style={{
          display: "flex",
          gap: 12,
          marginTop: 18,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <Button
          icon={Plus}
          variant="success"
          disabled={isSavingSettings}
          onClick={() => {
            const name = String(newStitch || "").trim();
            const rate = parseStitchingSettingNumber(newRate, 0);
            const fullness = parseStitchingSettingNumber(newFullness, Number.NaN);
            const trackLengthFt = parseStitchingSettingNumber(newTrackLengthFt, 0);

            if (!name) {
              notify("Style name is required.", "error");
              return;
            }
            if (!Number.isFinite(rate) || rate < 0) {
              notify("Rate / Running ft must be 0 or greater.", "error");
              return;
            }
            if (!Number.isFinite(fullness) || fullness <= 0) {
              notify("Fullness Multiplier must be greater than 0.", "error");
              return;
            }
            if (!Number.isFinite(trackLengthFt) || trackLengthFt < 0) {
              notify("Default Track Length must be 0 or greater.", "error");
              return;
            }

            setStitchingRates((prev) => ({
              ...prev,
              [name]: {
                ...(prev[name] || {}),
                rate,
                fullness,
                trackLengthFt,
                trackRateFt: parseStitchingSettingNumber(prev[name]?.trackRateFt, 0),
              },
            }));
            setIsStitchingSettingsDirty(true);
            setNewStitch("");
            setNewRate("");
            setNewFullness("3");
            setNewTrackLengthFt("16");
          }}
        >
          + Add Stitching / Track Style
        </Button>

        <Button
          icon={Save}
          variant="primary"
          disabled={isSavingSettings}
          onClick={() =>
            saveSettings({ successMessage: "Stitching & track settings saved." })
          }
        >
          {isSavingSettings ? "Saving..." : "Save Stitching & Track Settings"}
        </Button>
      </div>
    </div>
  </div>
)}

{tab === "fco" && (
  <div
    style={{
      background: isDark ? "#0f172a" : "#ffffff",
      padding: 26,
      borderRadius: 16,
      maxWidth: 1100,
      margin: "0 auto",
      boxShadow: isDark
        ? "0 12px 40px rgba(0,0,0,0.6)"
        : "0 10px 30px rgba(2,6,23,0.06)",
    }}
  >

    {/* ================= HEADER ================= */}

    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 20,
      }}
    >
      <div>
        <div style={{ fontSize: 22, fontWeight: 800 }}>
          Roll Optimizer
        </div>
        <div style={{ fontSize: 13, opacity: 0.6 }}>
          Panel-based fabric cutting optimization
        </div>
      </div>

            <div style={{ display: "flex", gap: 10 }}>
        <Button
          icon={RotateCcw}
          variant="ghost"
          onClick={() => {
            setOptimizerQuoteId("");
            setOptimizerQuote(null);
            setOptimizerFabric("");
            setRollOptions([]);
            setCutPlan([]);
            setOptimizerFinalDemands([]);
            setSelectedSolutionIndex(0);
            setOptimizerQuoteSearch("");
            setOptimizerQuoteSearchState({
              loading: false,
              error: "",
              results: [],
              searched: false,
            });
          }}
        >
          Reset
        </Button>

        <Button
          icon={Download}
          variant="success"
          disabled={
            !cutPlan.length ||
            !cutPlan[0]?.solutions?.[selectedSolutionIndex]
          }
          onClick={() => {
            const sol =
              cutPlan?.[0]?.solutions?.[selectedSolutionIndex];

            if (!sol) return;

            exportCuttingPDF(
              sol,
              optimizerQuote,
              optimizerFabric,
              optimizerFinalDemands
            );
          }}
        >
          Download Selected
        </Button>
      </div>
    </div>

    <div
      style={{
        padding: 20,
        borderRadius: 22,
        marginBottom: 20,
        background: isDark
          ? "radial-gradient(circle at top left, rgba(37,99,235,0.24), transparent 34%), linear-gradient(135deg, rgba(15,23,42,0.98), rgba(30,41,59,0.94))"
          : "radial-gradient(circle at top left, rgba(37,99,235,0.13), transparent 34%), linear-gradient(135deg, #ffffff, #f8fafc)",
        border: isDark
          ? "1px solid rgba(148,163,184,0.18)"
          : "1px solid rgba(37,99,235,0.12)",
        boxShadow: isDark
          ? "0 22px 55px rgba(0,0,0,0.38)"
          : "0 22px 55px rgba(15,23,42,0.08)",
      }}
    >
      <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "1.1fr 1fr", gap: 18 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 900, color: "#2563eb", letterSpacing: "0.08em", textTransform: "uppercase" }}>
            Start by loading a quotation
          </div>
          <div style={{ marginTop: 8, fontSize: isCompact ? 21 : 26, fontWeight: 950, letterSpacing: "-0.04em" }}>
            Optimize from a real saved curtain quote
          </div>
          <div style={{ marginTop: 8, fontSize: 13, lineHeight: 1.7, color: isDark ? "#cbd5e1" : "#475569" }}>
            To optimize fabric cutting, first open the quotation in the editor, then come here. You can also search below and load a saved quotation directly.
          </div>
        </div>
        <div style={{ display: "grid", gap: 8 }}>
          {[
            "Search customer / quote number",
            "Select the correct quote",
            "Choose fabric",
            "Add available roll lengths",
            "Optimize cutting",
          ].map((step, index) => (
            <div
              key={step}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "9px 11px",
                borderRadius: 14,
                background: isDark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.72)",
                border: isDark
                  ? "1px solid rgba(148,163,184,0.12)"
                  : "1px solid rgba(15,23,42,0.06)",
              }}
            >
              <span
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 999,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#2563eb",
                  color: "#ffffff",
                  fontSize: 12,
                  fontWeight: 900,
                }}
              >
                {index + 1}
              </span>
              <span style={{ fontSize: 13, fontWeight: 750 }}>{step}</span>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* ================= MAIN GRID ================= */}

    <div
      style={{
        display: "grid",
        gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 1fr) 360px",
        gap: 20,
      }}
    >

      {/* ================= LEFT COLUMN ================= */}

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

        {/* Quote Search */}

        <div
          style={{
            padding: 18,
            borderRadius: 18,
            background: isDark
              ? "linear-gradient(135deg, rgba(15,23,42,0.96), rgba(30,41,59,0.96))"
              : "linear-gradient(135deg, #f8fafc, #ffffff)",
            border: isDark
              ? "1px solid rgba(148,163,184,0.18)"
              : "1px solid rgba(15,23,42,0.08)",
            boxShadow: isDark
              ? "0 18px 45px rgba(0,0,0,0.35)"
              : "0 18px 45px rgba(15,23,42,0.08)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 850 }}>Find a saved quotation</div>
              <div style={{ fontSize: 12, color: isDark ? "#94a3b8" : "#64748b", marginTop: 4 }}>
                Search is debounced and only loads full quotation details after you select a result.
              </div>
            </div>
            <div
              style={{
                padding: "6px 10px",
                borderRadius: 999,
                background: isDark ? "rgba(37,99,235,0.14)" : "#eff6ff",
                color: "#2563eb",
                fontSize: 11,
                fontWeight: 800,
                alignSelf: "start",
                whiteSpace: "nowrap",
              }}
            >
              Low-read search
            </div>
          </div>

          {currentEditorOptimizerQuote ? (
            <div
              style={{
                padding: 14,
                borderRadius: 16,
                background: isDark ? "rgba(34,197,94,0.10)" : "#ecfdf5",
                border: isDark
                  ? "1px solid rgba(34,197,94,0.24)"
                  : "1px solid rgba(34,197,94,0.18)",
                marginBottom: 12,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 800, color: "#16a34a", marginBottom: 5 }}>
                Currently loaded quotation
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                <div>
                  <div style={{ fontWeight: 850 }}>
                    #{currentEditorOptimizerQuote.quoteNo || currentEditorOptimizerQuote.id} · {currentEditorOptimizerQuote.customerName || "Customer"}
                  </div>
                  <div style={{ fontSize: 12, color: isDark ? "#cbd5e1" : "#475569", marginTop: 3 }}>
                    {Array.isArray(currentEditorOptimizerQuote.rows)
                      ? `${currentEditorOptimizerQuote.rows.length} row${currentEditorOptimizerQuote.rows.length === 1 ? "" : "s"} ready`
                      : "Quotation rows ready"}
                  </div>
                </div>
                <Button
                  variant="success"
                  onClick={() => {
                    const nextQuote = cloneOptimizerQuoteForState(currentEditorOptimizerQuote);
                    setOptimizerQuote(nextQuote);
                    setOptimizerQuoteId(nextQuote.id || "");
                    setOptimizerFabric("");
                    setRollOptions([]);
                    setCutPlan([]);
                    setOptimizerFinalDemands([]);
                    setSelectedSolutionIndex(0);
                  }}
                >
                  Use Loaded Quote
                </Button>
              </div>
            </div>
          ) : null}

          <input
            value={optimizerQuoteSearch}
            onChange={(event) => setOptimizerQuoteSearch(event.target.value)}
            style={{ ...input, width: "100%" }}
            placeholder="Search customer, quote number, phone, or project..."
          />

          <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
            {optimizerQuoteSearch.trim().length > 0 && optimizerQuoteSearch.trim().length < 2 ? (
              <div style={{ fontSize: 12, color: isDark ? "#94a3b8" : "#64748b" }}>
                Type at least 2 characters to search saved quotations.
              </div>
            ) : null}

            {optimizerQuoteSearchState.loading ? (
              <div style={{ fontSize: 13, color: isDark ? "#cbd5e1" : "#475569" }}>
                Searching quotations...
              </div>
            ) : null}

            {optimizerQuoteSearchState.error ? (
              <div style={{ fontSize: 13, color: "#ef4444" }}>
                {optimizerQuoteSearchState.error}
              </div>
            ) : null}

            {!optimizerQuoteSearchState.loading &&
            optimizerQuoteSearchState.searched &&
            optimizerQuoteSearchState.results.length === 0 ? (
              <div
                style={{
                  padding: 14,
                  borderRadius: 14,
                  background: isDark ? "rgba(148,163,184,0.08)" : "#f8fafc",
                  border: isDark
                    ? "1px solid rgba(148,163,184,0.15)"
                    : "1px solid rgba(15,23,42,0.08)",
                  color: isDark ? "#cbd5e1" : "#475569",
                  fontSize: 13,
                }}
              >
                No curtain quotations found for this search. Try quote number, customer name, or phone.
              </div>
            ) : null}

            {optimizerQuoteSearchState.results.map((result) => (
              <button
                key={result.id}
                type="button"
                onClick={() => loadOptimizerQuoteById(result.id)}
                disabled={optimizerQuoteLoadingId === result.id}
                style={{
                  padding: 14,
                  borderRadius: 16,
                  border:
                    optimizerQuoteId === result.id
                      ? "1px solid rgba(37,99,235,0.45)"
                      : isDark
                      ? "1px solid rgba(148,163,184,0.15)"
                      : "1px solid rgba(15,23,42,0.08)",
                  background:
                    optimizerQuoteId === result.id
                      ? isDark
                        ? "rgba(37,99,235,0.18)"
                        : "#eff6ff"
                      : isDark
                      ? "rgba(15,23,42,0.72)"
                      : "#ffffff",
                  color: isDark ? "#f8fafc" : "#0f172a",
                  textAlign: "left",
                  cursor: optimizerQuoteLoadingId === result.id ? "wait" : "pointer",
                  display: "grid",
                  gap: 7,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <strong>#{result.quoteNo || result.id}</strong>
                  <span style={{ fontSize: 12, color: "#2563eb", fontWeight: 800 }}>
                    {optimizerQuoteLoadingId === result.id ? "Loading..." : getQuoteTypeLabel(result.quoteType)}
                  </span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 750 }}>
                  {result.customerName || "Customer"}
                </div>
                <div style={{ fontSize: 12, color: isDark ? "#94a3b8" : "#64748b" }}>
                  {[result.customerPhone, result.projectName, result.date]
                    .filter(Boolean)
                    .join(" · ") || "No extra details"}
                </div>
                {result.grandTotal ? (
                  <div style={{ fontSize: 12, color: isDark ? "#cbd5e1" : "#334155" }}>
                    Total: {currency} {Number(result.grandTotal || 0).toLocaleString("en-IN")}
                  </div>
                ) : null}
              </button>
            ))}
          </div>
        </div>

        {optimizerQuote ? (
          <div
            style={{
              padding: 16,
              borderRadius: 18,
              background: isDark ? "rgba(37,99,235,0.10)" : "#eff6ff",
              border: isDark
                ? "1px solid rgba(37,99,235,0.24)"
                : "1px solid rgba(37,99,235,0.16)",
              display: "grid",
              gap: 7,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 900, color: "#2563eb", textTransform: "uppercase", letterSpacing: "0.08em" }}>
              Selected quotation
            </div>
            <div style={{ fontSize: 18, fontWeight: 900 }}>
              #{optimizerQuote.quoteNo || optimizerQuote.id} · {optimizerQuote.customerName || "Customer"}
            </div>
            <div style={{ fontSize: 12, color: isDark ? "#cbd5e1" : "#475569" }}>
              {[
                optimizerQuote.date || optimizerQuote.quoteDate,
                optimizerQuote.customerPhone || optimizerQuote.customerWhatsapp,
                Array.isArray(optimizerQuote.rows)
                  ? `${optimizerQuote.rows.length} row${optimizerQuote.rows.length === 1 ? "" : "s"}`
                  : "",
              ]
                .filter(Boolean)
                .join(" · ")}
            </div>
          </div>
        ) : (
          <div
            style={{
              padding: 16,
              borderRadius: 18,
              background: isDark ? "rgba(148,163,184,0.08)" : "#f8fafc",
              border: isDark
                ? "1px solid rgba(148,163,184,0.14)"
                : "1px solid rgba(15,23,42,0.08)",
              color: isDark ? "#cbd5e1" : "#475569",
              fontSize: 13,
              lineHeight: 1.7,
            }}
          >
            No quotation selected yet. Use the loaded editor quote or search a saved curtain quotation above.
          </div>
        )}

        {/* Fabric Selector */}

        <div>
          <label>Select Fabric</label>

          <select
            value={optimizerFabric || ""}
            disabled={!optimizerQuote}
            onChange={(e) => {
              setOptimizerFabric(e.target.value);
              setCutPlan([]);
              setOptimizerFinalDemands([]);
            }}
            style={{ ...selectInput, marginTop: 6 }}
          >

            <option value="">Choose fabric...</option>

            {optimizerQuote &&
              Array.from(
                new Set(
                  (optimizerQuote.rows || [])
                    .map((r) => r.fabric)
                    .filter(Boolean)
                )
              ).map((fab) => (
                <option key={fab} value={fab}>
                  {fab}
                </option>
              ))}

          </select>
          {optimizerQuote &&
          !Array.from(
            new Set(
              (optimizerQuote.rows || []).map((r) => r.fabric).filter(Boolean)
            )
          ).length ? (
            <div style={{ marginTop: 8, fontSize: 12, color: "#ef4444" }}>
              This quotation has no fabric demand rows for optimizer yet.
            </div>
          ) : null}
        </div>

        {/* Rolls */}

        <div>

          <div style={{ fontWeight: 700, marginBottom: 8 }}>
            Rolls (meters)
          </div>

          {rollOptions.map((len, i) => (

            <div
              key={i}
              style={{
                display: "flex",
                gap: 8,
                marginBottom: 8
              }}
            >

              <input
                type="number"
                step="0.01"
                value={len}
                onChange={(e) => {

                  const v = [...rollOptions];
                  v[i] = e.target.value;

                  setRollOptions(v);
                  setCutPlan([]);
                  setOptimizerFinalDemands([]);

                }}
                style={{ ...input, flex: 1 }}
              />

              <Button
                icon={Trash2}
                variant="danger"
                onClick={() => {
                  setRollOptions(
                    rollOptions.filter((_, idx) => idx !== i)
                  );

                  setCutPlan([]);
                  setOptimizerFinalDemands([]);
                }}
              >
                Remove
              </Button>

            </div>

          ))}

          <Button
            icon={Plus}
            variant="primary"
            onClick={() => {
              setRollOptions([...rollOptions, ""]);
              setCutPlan([]);
              setOptimizerFinalDemands([]);
            }}
          >
            Add Roll
          </Button>

        </div>

    {/* ================= OPTIMIZE BUTTON ================= */}
<Button
  icon={Scissors}
  variant="success"
  style={{ marginTop: 10 }}
  onClick={() => {

    if (!optimizerQuote || !optimizerFabric) {
      alert("Select quote and fabric");
      return;
    }

    const fabricKey = normalizeFabricKey(optimizerFabric || "");

    const plan = buildReuseAwareDemands(
      optimizerQuote.rows || [],
      optimizerQuote?.repeatSettings || repeatSettings,
      {
        targetFabricKey: fabricKey,
        enableReuse: useHalfPieceReuse,
        debugTag: "optimizer",
        getProductRules: getRowProductRules,
        quoteFullnessOverridesForCalculation:
          optimizerQuote?.quoteFullnessOverrides || quoteFullnessOverrides,
      }
    );

    const demands = plan.demands;
    const repeat =
      optimizerQuote?.repeatSettings?.[fabricKey] || {};
    const vr =
      repeat.verticalEnabled
        ? Number(repeat.verticalRepeatInch || 0) / 39.37
        : 0;

    if (!demands.length) {
      alert("No panels found for this fabric.");
      return;
    }

    setOptimizerFinalDemands(demands);

    const cleanRolls =
      rollOptions
        .map((x) => Number(x))
        .filter((n) => Number.isFinite(n) && n > 0)
        .map((n) => Math.round(n * 100) / 100);

    if (!cleanRolls.length) {
      alert("Enter valid roll lengths.");
      return;
    }

    const result =
      optimizeCutsGGMulti({
        rolls: cleanRolls,
        demands,
        fabricKey,
      });

    console.log("DEMANDS:", demands);
    console.log("ROLLS:", cleanRolls);
    console.log("OPTIMIZER RESULT:", result);

    const rawSolutions =
      Array.isArray(result)
        ? result
        : result?.solutions || [];

    const normalizedSolutions =
      rawSolutions
        .filter(Boolean)
        .map((sol) => {

          if (sol?.rollResults) return sol;

          if (
            sol?.patternInfo &&
            Array.isArray(sol?.comboRolls) &&
            sol.comboRolls.length
          ) {

            const rollResults =
              sol.comboRolls.map((rollLen) => ({
                rollLen,
                patternInfo: {
                  pattern:
                    sol.patternInfo?.pattern || {},
                  used:
                    sol.patternInfo.used || 0,
                  waste:
                    sol.patternInfo.waste || 0
                }
              }));

            return {
              ...sol,
              rollResults
            };
          }

          return {
            ...sol,
            rollResults: []
          };

        })
        .map((sol) => {
          let extraMeters = 0;
          const rollResults = Array.isArray(sol.rollResults) ? sol.rollResults : [];

          const rollCount = Array.isArray(rollResults)
            ? rollResults.filter(
                (r) =>
                  r &&
                  r.patternInfo &&
                  Object.keys(r.patternInfo.pattern || {}).length > 0
              ).length
            : 0;

          if (vr > 0 && rollCount > 0) {
            extraMeters = round2(rollCount * vr);
          }

          const vrPenalty = vr > 0 ? extraMeters : 0;
          const effectiveUsed =
            Number(sol.totalUsedMeters || 0) + vrPenalty;

          return {
            ...sol,
            optimizerExtraVrMeters: vrPenalty,
            effectiveUsedMeters: effectiveUsed,
            requiresExtraCut:
              Boolean(sol?.requiresExtraCut) ||
              Number(sol?.finalRemainingMeters || 0) > 0,
            isCompleteFromAvailable:
              sol?.isCompleteFromAvailable === true &&
              Number(sol?.finalRemainingMeters || 0) <= 0,
            totalWasteMeters: Number(sol?.totalWasteMeters ?? sol?.totalWaste ?? 0),
            rollCount: Number(sol?.rollCount ?? sol?.totalRollsUsed ?? 0),
            totalRollLengthMeters: Number(
              sol?.totalRollLengthMeters ??
              (Array.isArray(sol?.comboRolls)
                ? sol.comboRolls.reduce(
                    (sum, rollLen) => sum + Number(rollLen || 0),
                    0
                  ) + Number(sol?.finalRemainingMeters || 0)
                : 0)
            ),
          };
        });

    //  REMOVE DUPLICATES (STABLE KEY)
    const seen = new Set();

    const uniqueSolutions = normalizedSolutions.filter((s) => {
      const key = JSON.stringify({
        rolls: s.comboRolls,
        waste: Number(s.totalWaste || 0).toFixed(2),
        remaining: Number(s.finalRemainingMeters || 0).toFixed(2),
      });

      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    uniqueSolutions.sort(compareOptimizerSolutions);

    setSelectedSolutionIndex(0);

    setCutPlan([
      {
        solutions: uniqueSolutions
      }
    ]);

  }}
>
  Optimize Cutting
</Button>

      </div>

      {/* ================= RIGHT COLUMN ================= */}

     <div
  style={{
    padding: 16,
    borderRadius: 12,
    background: isDark ? "#0b1220" : "#f8fafc",
  }}
>

  <div style={{ fontWeight: 800, marginBottom: 8 }}>
    Demand Summary
  </div>

  {optimizerQuote && optimizerFabric ? (

    (() => {

      let total = 0;
      let panels = 0;

      const fabricKey = normalizeFabricKey(optimizerFabric || "");

      const plan = buildReuseAwareDemands(
        optimizerQuote.rows || [],
        optimizerQuote?.repeatSettings || repeatSettings,
        {
          targetFabricKey: fabricKey,
          enableReuse: useHalfPieceReuse,
          getProductRules: getRowProductRules,
          quoteFullnessOverridesForCalculation:
            optimizerQuote?.quoteFullnessOverrides || quoteFullnessOverrides,
        }
      );

      total = round2(
        (plan.demands || []).reduce(
          (sum, d) => sum + Number(d.length || 0) * Number(d.qty || 0),
          0
        )
      );

      panels = plan.rowPlans
        .filter(Boolean)
        .reduce((sum, p) => sum + Number(p.totalPieces || 0), 0);

      const reusedPieces = plan.rowPlans
        .filter(Boolean)
        .reduce((sum, p) => sum + Number(p.reusedHalfCount || 0), 0);

      return (
        <>
          <div>
            Total Required: {total.toFixed(2)} m
          </div>
          <div>
            Panels: {panels}
          </div>
          <div>
            Reused pieces: {reusedPieces}
          </div>
        </>
      );

    })()

  ) : (
    <div>Select quote and fabric</div>
  )}

</div>
    </div>
{/* ================= RESULTS ================= */}

{cutPlan?.[0]?.solutions?.length > 0 && (

  <div style={{ marginTop: 30 }}>

    <h3 style={{ marginBottom: 12 }}>
      Optimization Results  {optimizerFabric}
    </h3>

    {[...cutPlan[0].solutions]
      .slice(0, 3)
      .map((sol, idx) => {

        const rollResults = sol.rollResults || [];
        const requiresExtraCut =
          Boolean(sol?.requiresExtraCut) ||
          Number(sol?.finalRemainingMeters || 0) > 0;
        const rollCount = Number(sol?.rollCount ?? sol?.totalRollsUsed ?? 0);
        const totalRollLengthMeters = Number(
          sol?.totalRollLengthMeters ??
          (Array.isArray(sol?.comboRolls)
            ? sol.comboRolls.reduce(
                (sum, rollLen) => sum + Number(rollLen || 0),
                0
              ) + Number(sol?.finalRemainingMeters || 0)
            : 0)
        );

        //  SAFE USED CALCULATION (from pattern, not stored value)
        const getUsed = (pattern = {}) =>
          Object.entries(pattern).reduce(
            (s, [len, qty]) => s + Number(len) * Number(qty),
            0
          );

        const totalWaste = rollResults.reduce(
          (s, r) => s + (r?.patternInfo?.waste || 0),
          0
        );

        const totalUsed = rollResults.reduce(
          (s, r) =>
            s + getUsed(r?.patternInfo?.pattern || {}),
          0
        );

        const remainingMeters =
          sol.finalRemainingMeters || 0;

        return (

          <div
            key={idx}
            style={{
              border:
                selectedSolutionIndex === idx
                  ? "2px solid #22c55e"
                  : "1px solid #ddd",
              padding: 14,
              borderRadius: 10,
              marginBottom: 14,
              background:
                selectedSolutionIndex === idx
                  ? "rgba(34,197,94,0.06)"
                  : "transparent",
            }}
          >

            {/* HEADER */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >

              <strong>Option {idx + 1}</strong>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    padding: "4px 10px",
                    borderRadius: 999,
                    background: requiresExtraCut ? "#fef3c7" : "#dcfce7",
                    color: requiresExtraCut ? "#92400e" : "#166534",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {requiresExtraCut ? "Requires Extra Cut" : "Complete From Available Rolls"}
                </span>

              <button
                style={btn(
                  selectedSolutionIndex === idx
                    ? "#22c55e"
                    : "#9ca3af"
                )}
                onClick={() => setSelectedSolutionIndex(idx)}
              >
                {selectedSolutionIndex === idx
                  ? "Selected"
                  : "Select"}
              </button>
              </div>

            </div>

            {/* ROLLS */}
            {rollResults.map((rr, i) => {

              const pattern =
                rr?.patternInfo?.pattern || {};

              const used = getUsed(pattern);
              const waste =
                rr?.patternInfo?.waste || 0;

              return (

                <div key={i} style={{ marginBottom: 6 }}>

                  <strong>
                    Roll {rr?.rollLen}m | Used{" "}
                    {used.toFixed(2)}m | Waste{" "}
                    {waste.toFixed(2)}m
                  </strong>

                  <div style={{ marginLeft: 12 }}>

                    {Object.entries(pattern).map(
                      ([len, qty]) => (

                        <div key={len}>
                           {Number(len).toFixed(2)}m  {qty}
                        </div>

                      )
                    )}

                  </div>

                </div>

              );

            })}

            {/* SUMMARY */}
            <div style={{ marginTop: 10, fontSize: 13 }}>

              <div>
                <strong>Total Used:</strong>{" "}
                {totalUsed.toFixed(2)} m
              </div>

              <div>
                <strong>Total Waste:</strong>{" "}
                {totalWaste.toFixed(2)} m
              </div>

              <div>
                <strong>Roll Count:</strong>{" "}
                {rollCount}
              </div>

              <div>
                <strong>Total Roll Length:</strong>{" "}
                {totalRollLengthMeters.toFixed(2)} m
              </div>

              <div>
                <strong>{requiresExtraCut ? "Extra Cut Required" : "Remaining" }:</strong>{" "}
                {remainingMeters.toFixed(2)} m
              </div>

            </div>

          </div>

        );

      })}

    {(() => {
      const selectedSolution =
        cutPlan?.[0]?.solutions?.[selectedSolutionIndex] || null;
      const visualRolls = buildRollVisualization(
        selectedSolution?.rollResults
      );

      return <RollVisualizer rolls={visualRolls} />;
    })()}

  </div>

)}

 </div> )}


{/* ====================== STOCK TAB ====================== */}
{tab === "stock" && (
  <div
    style={{
      background: isDark ? "#0f172a" : "#ffffff",
      padding: 28,
      borderRadius: 18,
      maxWidth: 1150,
      margin: "0 auto",
      boxShadow: isDark
        ? "0 14px 50px rgba(0,0,0,0.65)"
        : "0 12px 32px rgba(2,6,23,0.08)",
    }}
  >
    {/* ================= HEADER ================= */}
    <div style={{ marginBottom: 26 }}>
      <div style={{ fontSize: 24, fontWeight: 800 }}>
        Fabric Stock
      </div>
      <div style={{ fontSize: 13, color: "#6b7280" }}>
        Purchase, consume and audit fabric inventory
      </div>
    </div>

    {/* ================= ADD STOCK CARD ================= */}
    <div
      style={{
        background: isDark ? "#0b1220" : "#f9fafb",
        borderRadius: 16,
        padding: 22,
        marginBottom: 30,
        boxShadow: isDark
          ? "inset 0 0 0 1px rgba(255,255,255,0.06)"
          : "inset 0 0 0 1px rgba(0,0,0,0.06)",
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 14 }}>
        Add Fabric to Stock
      </div>

      {/* QUOTATION SELECT */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontWeight: 600 }}>
          Quotation / Stock Purpose
        </label>
        <select
          value={stockQuoteId}
          onChange={(e) => setStockQuoteId(e.target.value)}
          style={{ ...selectInput, marginTop: 6 }}
        >
          <option value="">Select quotation</option>
          <option value="__GENERAL__">
            General / Bulk Stock
          </option>
          {savedQuotes
            .filter((q) => resolveQuoteType(q) !== GENERAL_QUOTE_TYPE)
            .map((q) => (
            <option key={q.id} value={q.id}>
              {q.customerName} - #{q.quoteNo}
            </option>
          ))}
        </select>
      </div>

      {/* FABRIC NAME */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontWeight: 600 }}>Fabric Name</label>
        <input
          type="text"
          value={stockFabricName}
          onChange={(e) => setStockFabricName(e.target.value)}
          placeholder="Eg: Chroma Aqua, Milano Sheer"
          style={{ ...input, marginTop: 6 }}
        />
      </div>

      {/* METERS */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontWeight: 600 }}>
          Purchased Fabric
        </label>
        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            marginTop: 6,
          }}
        >
          <input
            type="text"
            placeholder="Rolls (e.g. 84, 42)"
            value={stockRollMeters}
            onChange={(e) => setStockRollMeters(e.target.value)}
            style={{ ...input, maxWidth: 260 }}
          />
          <input
            type="number"
            step="0.01"
            placeholder="Loose (m)"
            value={stockLooseMeters}
            onChange={(e) => setStockLooseMeters(e.target.value)}
            style={{ ...input, maxWidth: 160 }}
          />
          <input
            type="number"
            step="0.01"
            placeholder="Wastage (m)"
            value={stockWastageMeters}
            onChange={(e) => setStockWastageMeters(e.target.value)}
            style={{ ...input, maxWidth: 160 }}
          />
        </div>
      </div>

      <div style={{ textAlign: "right" }}>
        <button
          style={{
            ...btn("#2563eb"),
            padding: "10px 20px",
            fontWeight: 700,
          }}
onClick={async () => {
  try {
    if (!user?.uid) throw new Error("Not logged in");
    if (!stockFabricName.trim())
      throw new Error("Enter fabric name");

    const fabricKey = stockFabricName
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");

    const rolls = String(stockRollMeters || "")
      .split(",")
      .map((r) => Number(r.trim()))
      .filter((n) => n > 0);

    const loose = Number(stockLooseMeters) || 0;
    const wastage = Number(stockWastageMeters) || 0;

    const totalMeters =
      rolls.reduce((a, b) => a + b, 0) +
      loose -
      wastage;

    if (totalMeters <= 0)
      throw new Error("Enter valid fabric meters");

    await applyStockTransaction({
      uid: workspaceUid,
      fabricKey,
      meters: totalMeters,
      type: "PURCHASE",
      quoteId:
        stockQuoteId === "__GENERAL__"
          ? null
          : stockQuoteId,
    });

    setStockFabricName("");
    setStockRollMeters("");
    setStockLooseMeters("");
    setStockWastageMeters("");
    setStockQuoteId("");

    alert("Stock added successfully");
  } catch (err) {
    alert(err.message);
  }
}}


        >
           Add to Stock
        </button>
      </div>
    </div>

   {/* ================= FABRIC STOCK VIEWER ================= */}
<div
  style={{
    background: "#ffffff",
    borderRadius: 16,
    padding: 20,
    marginBottom: 30,
    border: "1px solid #e5e7eb",
  }}
>
  {/* Header */}
  <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12 }}>
    Current Stock
  </div>

  {/*  Search */}
  <input
    type="text"
    placeholder="Search fabric name"
    value={stockSearch}
    onChange={(e) => setStockSearch(e.target.value)}
    style={{
      width: "100%",
      padding: "10px 12px",
      borderRadius: 10,
      border: "1px solid #d1d5db",
      marginBottom: 16,
      fontSize: 14,
    }}
  />

  {loadingFabricStock ? (
    <div style={{ opacity: 0.6 }}>Loading stock</div>
  ) : filteredFabricStock.length === 0 ? (
    <div style={{ opacity: 0.6 }}>No matching fabrics found.</div>
  ) : (
    filteredFabricStock.map((fab) => {

      const buckets = Array.isArray(fab.buckets)
        ? fab.buckets.filter((b) => Number(b.length) !== 0)
        : [];

      if (!buckets.length) return null;

      const rolls = buckets.filter((b) => b.type === "ROLL");
      const leftovers = buckets.filter((b) => b.type === "LEFTOVER");
      const negatives = buckets.filter((b) => b.type === "NEGATIVE");

      const totalRolls = rolls.reduce((a, b) => a + Number(b.length || 0), 0);
      const totalLeftovers = leftovers.reduce((a, b) => a + Number(b.length || 0), 0);
      const totalNegative = negatives.reduce((a, b) => a + Number(b.length || 0), 0);

      const totalAvailable = totalRolls + totalLeftovers;
      const netStock = totalAvailable + totalNegative;

      return (
        <div
          key={fab.fabricKey}
          style={{
            marginBottom: 22,
            padding: 18,
            borderRadius: 16,
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
          }}
        >
          {/* Fabric Name */}
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>
              {(fab.displayName || fab.fabricKey)
                .toString()
                .replace(/_/g, " ")}
            </div>

            <div style={{ fontSize: 13, marginTop: 4 }}>
              Available:{" "}
              <strong>{totalAvailable.toFixed(2)} m</strong>
              &nbsp; | &nbsp;
              Negative:{" "}
              <strong style={{ color: "#dc2626" }}>
                {totalNegative.toFixed(2)} m
              </strong>
              &nbsp; | &nbsp;
              Net:{" "}
              <strong>{netStock.toFixed(2)} m</strong>
            </div>
          </div>

          {/* Rolls */}
          {rolls.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>
                Rolls
              </div>
              {rolls.map((r, i) => (
                <div key={i} style={{ fontSize: 13 }}>
                   {Number(r.length).toFixed(2)} m
                </div>
              ))}
            </div>
          )}

          {/* Leftovers */}
          {leftovers.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>
                Leftovers
              </div>
              {leftovers.map((l, i) => (
                <div key={i} style={{ fontSize: 13 }}>
                   {Number(l.length).toFixed(2)} m
                </div>
              ))}
            </div>
          )}

          {/* Negative */}
          {negatives.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: 13,
                  color: "#dc2626",
                }}
              >
                Negative Balance
              </div>
              {negatives.map((n, i) => (
                <div
                  key={i}
                  style={{ fontSize: 13, color: "#dc2626" }}
                >
                   {Number(n.length).toFixed(2)} m
                </div>
              ))}
            </div>
          )}
        </div>
      );
    })
  )}
</div>

    {/* ================= STOCK HISTORY (LEDGER STYLE) ================= */}
    <div
      style={{
        background: "#ffffff",
        borderRadius: 18,
        padding: 20,
        border: "1px solid #e5e7eb",
      }}
    >
      <div style={{ fontSize: 18, fontWeight: 700 }}>
        Stock History
      </div>
      <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 14 }}>
        Immutable audit trail
      </div>

      <input
        type="text"
        placeholder="Search fabric / customer / quote"
        value={stockHistorySearch}
        onChange={(e) => setStockHistorySearch(e.target.value)}
        style={{
          width: "100%",
          padding: "10px 12px",
          borderRadius: 10,
          border: "1px solid #d1d5db",
          marginBottom: 14,
        }}
      />

      {loadingStockHistory ? (
        <div>Loading history</div>
      ) : (
        <table style={{ width: "100%", fontSize: 14 }}>
          <thead>
            <tr style={{ color: "#6b7280", textAlign: "left" }}>
              <th>Date</th>
              <th>Fabric</th>
              <th>Action</th>
              <th>Meters</th>
              <th>Customer</th>
              <th>Quote</th>
            </tr>
          </thead>
          <tbody>
            {filteredStockHistory.flatMap((h) =>
              (h.fabrics || []).map((f, i) => (
                <tr key={`${h.id}-${i}`}>
                  <td>{h.triggeredAt?.toDate?.().toLocaleString()}</td>
                  <td>{f.fabricKey.replace(/_/g, " ")}</td>
                  <td
                    style={{
                      fontWeight: 700,
                      color:
                        h.direction === "DEDUCT"
                          ? "#dc2626"
                          : h.direction === "REVERSE"
                          ? "#16a34a"
                          : "#2563eb",
                    }}
                  >
                    {h.direction}
                  </td>
                  <td>{Number(f.meters).toFixed(2)}</td>
                  <td>{h.customerName || "-"}</td>
                  <td>{h.quoteNo || h.quoteId || "-"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
    </div>
  </div>
)}

      </main>
      {renderDraftRecoveryModal()}
      {renderConfirmDialog()}
      {renderDashboardDrilldownModal()}
      {renderQuoteDealChoiceModal()}
      {renderCrmFollowUpModal()}
      {renderDailyTaskRescheduleModal()}
      {renderQuoteLinkModal()}
      {renderCustomerCreateModal()}
      {renderAppToasts()}
      {renderCrmReminderPopups()}
      {renderTeamMemberInviteModal()}
    </div>
  );
};











